// Sistema — gestão de gestão de gorjetas e equipe para restaurantes
import React, { useState, useEffect, useMemo, useRef, Component } from "react";
import { db } from "./firebase";
import { doc, getDoc, setDoc, onSnapshot } from "firebase/firestore";

const APP_VERSION = "8.31.0";

// v8.11.1: comparação de versão semver-like (8.11.1 > 8.10.7 > 8.9.4)
function compareVersions(a, b) {
  const pa = String(a || "0").split(".").map(n => parseInt(n, 10) || 0);
  const pb = String(b || "0").split(".").map(n => parseInt(n, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const da = pa[i] || 0;
    const db = pb[i] || 0;
    if (da > db) return 1;
    if (da < db) return -1;
  }
  return 0;
}

// v8.14.0: visibilidade de reuniões. Owner e DP sempre veem.
// Default: comportamento atual (admin geral + líder com participante na sua área — filtro feito pelo chamador).
// Shared: criador + sharedWith + Owner/DP + admin geral.
// Private: só criador + Owner + DP.
function canSeeMeeting({ item, currentUserId, isOwner, isDP, isAdminGeneral }) {
  if (!item) return false;
  if (isOwner) return true;
  if (isDP) return true;
  const v = item.visibility || { mode: "default", sharedWith: [] };
  const isCreator = item.createdById && item.createdById === currentUserId;
  if (v.mode === "private") return isCreator;
  if (v.mode === "shared") {
    if (isCreator) return true;
    return Array.isArray(v.sharedWith) && v.sharedWith.includes(currentUserId);
  }
  // default — admin ou criador
  return isAdminGeneral || isCreator;
}

// v8.14.0: ocorrências são privadas por natureza. Apenas criador + Owner + DP.
// Legacy: ocorrências sem createdById (criadas antes de v8.14.0) ficam visíveis pra todos
// para não esconder dados antigos de gestores.
function canSeeOccurrence({ item, currentUserId, isOwner, isDP }) {
  if (!item) return false;
  if (isOwner) return true;
  if (isDP) return true;
  if (!item.createdById) return true; // legacy
  return item.createdById === currentUserId;
}

const DEFAULT_ADMISSION = () => `${new Date().getFullYear()}-01-01`;
const round2 = (v) => Math.round(v * 100) / 100;

// Error Boundary — evita tela branca mostrando mensagem de erro
class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error("Sistema ErrorBoundary:", error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{minHeight:"100vh",background:"#1a1510",display:"flex",alignItems:"center",justifyContent:"center",padding:32}}>
          <div style={{background:"#211c16",borderRadius:20,padding:32,maxWidth:480,width:"100%",border:"1px solid #3d3325",textAlign:"center",fontFamily:"'DM Sans',sans-serif"}}>
            <div style={{fontSize:40,marginBottom:16}}>⚠️</div>
            <h2 style={{color:"#f0ece4",margin:"0 0 8px",fontSize:18}}>Algo deu errado</h2>
            <p style={{color:"#7a6e5a",fontSize:13,marginBottom:16}}>{this.state.error?.message || "Erro desconhecido"}</p>
            <button onClick={()=>{ this.setState({hasError:false,error:null}); window.location.reload(); }}
              style={{padding:"10px 24px",borderRadius:10,background:"#d4a017",border:"none",color:"#1a1510",fontWeight:700,cursor:"pointer",fontSize:14}}>
              Recarregar
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
//
// ── localStorage cache helpers ──
function cacheSet(key, value) {
  try { localStorage.setItem("apptip_cache_" + key, JSON.stringify(value)); } catch { /* quota */ }
}
function cacheGet(key) {
  try {
    const raw = localStorage.getItem("apptip_cache_" + key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

// ═══════════════════════════════════════════════════════════════
// ── CONFIRM MODAL — substitui window.confirm com modal customizado
// ═══════════════════════════════════════════════════════════════
// API: appConfirm("Apagar?") ou appConfirm({ title, message, danger, confirmLabel, cancelLabel })
// Retorna Promise<boolean>. Caller deve estar em função async.
// O <ConfirmHost/> precisa estar montado em algum ponto da árvore (App raiz).
let _setConfirmModal = null;
function appConfirm(opts) {
  if (typeof opts === "string") opts = { message: opts };
  // Fallback se host não estiver montado (defesa, não deveria acontecer em prod).
  // Usa window["confirm"] (bracket access) pra escapar do find/replace que mira "window.confirm(".
  if (!_setConfirmModal) return Promise.resolve(window["confirm"](opts.message));
  return new Promise(resolve => {
    _setConfirmModal({ ...opts, resolve });
  });
}

function ConfirmHost() {
  const [modal, setModal] = useState(null);
  useEffect(() => {
    _setConfirmModal = setModal;
    return () => { _setConfirmModal = null; };
  }, []);

  if (!modal) return null;

  function close(result) {
    modal.resolve(result);
    setModal(null);
  }

  const danger = !!modal.danger;
  const confirmLabel = modal.confirmLabel || (danger ? "Apagar" : "Confirmar");
  const cancelLabel = modal.cancelLabel || "Cancelar";
  const title = modal.title || "Confirmar?";

  return (
    <div onClick={() => close(false)}
      style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:16,fontFamily:"'DM Sans',sans-serif"}}>
      <div onClick={e => e.stopPropagation()}
        style={{background:"var(--bg1)",borderRadius:14,maxWidth:440,width:"100%",boxShadow:"0 10px 40px rgba(0,0,0,0.3)",overflow:"hidden"}}>
        <div style={{padding:"18px 20px 12px",borderBottom:"1px solid var(--border)"}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:20}}>{danger ? "⚠️" : "❓"}</span>
            <h3 style={{margin:0,fontSize:16,fontWeight:700,color:"var(--text)",lineHeight:1.3}}>{title}</h3>
          </div>
        </div>
        {modal.message && (
          <div style={{padding:"14px 20px",color:"var(--text2)",fontSize:13,lineHeight:1.6,whiteSpace:"pre-wrap",maxHeight:"50vh",overflowY:"auto"}}>
            {modal.message}
          </div>
        )}
        <div style={{padding:"12px 20px 18px",display:"flex",gap:10,justifyContent:"flex-end",borderTop:"1px solid var(--border)",background:"var(--bg2)"}}>
          <button onClick={() => close(false)}
            style={{padding:"9px 18px",borderRadius:9,border:"1px solid var(--border)",background:"var(--bg1)",color:"var(--text2)",cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:"'DM Sans',sans-serif"}}>
            {cancelLabel}
          </button>
          <button onClick={() => close(true)} autoFocus
            style={{padding:"9px 20px",borderRadius:9,border:"none",background:danger?"#dc2626":"var(--ac)",color:"#fff",cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"'DM Sans',sans-serif"}}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

async function load(key, retries = 3) {
  for (let i = 0; i < retries; i++) {
    try {
      const snap = await getDoc(doc(db, "appdata", key));
      const val = snap.exists() ? snap.data().value : null;
      if (val !== null) cacheSet(key, val);
      return val;
    } catch (e) {
      console.error(`load error (tentativa ${i+1}/${retries}):`, key, e);
      if (i < retries - 1) await new Promise(r => setTimeout(r, 800 * (i + 1)));
    }
  }
  console.warn(`load FAILED after ${retries} retries — usando cache local:`, key);
  return cacheGet(key);
}
// Remove campos `undefined` recursivamente — Firestore rejeita undefined em qualquer nível
// (inclusive dentro de arrays) e quebra o save inteiro. Bugs de dados (workSchedules, etc.)
// costumam introduzir undefined silenciosamente; sanitizar aqui é a defesa mais robusta.
// Estratégia:
//  - undefined em chave de objeto → remove a chave
//  - undefined em elemento de array → vira null (preserva índice)
//  - undefined no topo → null
function sanitizeForFirestore(v) {
  if (v === undefined) return null;
  if (v === null) return null;
  if (typeof v === "function") return null; // funções nunca devem ir pro Firestore
  if (v instanceof Date) return v.toISOString();
  if (Array.isArray(v)) return v.map(item => sanitizeForFirestore(item));
  if (typeof v === "object") {
    const out = {};
    for (const [k, val] of Object.entries(v)) {
      if (val === undefined) continue; // pula a chave inteira
      out[k] = sanitizeForFirestore(val);
    }
    return out;
  }
  return v;
}

// Debug helper: encontra caminho do primeiro undefined num objeto (pra logs)
function findUndefinedPath(v, path = "") {
  if (v === undefined) return path || "(root)";
  if (v === null) return null;
  if (typeof v !== "object") return null;
  if (Array.isArray(v)) {
    for (let i = 0; i < v.length; i++) {
      const r = findUndefinedPath(v[i], `${path}[${i}]`);
      if (r) return r;
    }
    return null;
  }
  for (const [k, val] of Object.entries(v)) {
    if (val === undefined) return `${path}.${k}`;
    const r = findUndefinedPath(val, `${path}.${k}`);
    if (r) return r;
  }
  return null;
}

async function save(key, value, retries = 3) {
  const undefPath = findUndefinedPath(value);
  if (undefPath) console.warn(`[save] removendo undefined em ${key}${undefPath}`);
  const cleanValue = sanitizeForFirestore(value);
  // v8.32 — métricas de tamanho ajudam a diagnosticar erro de limite (1 MiB)
  let approxBytes = 0;
  try { approxBytes = JSON.stringify(cleanValue).length; } catch (_) {}
  let lastErr = null;
  for (let i = 0; i < retries; i++) {
    try {
      await setDoc(doc(db, "appdata", key), { value: cleanValue });
      cacheSet(key, cleanValue);
      return true;
    } catch (e) {
      lastErr = e;
      console.error(`save error (tentativa ${i+1}/${retries}):`, key, `${approxBytes}B`, e?.code, e?.message, e);
      if (i < retries - 1) await new Promise(r => setTimeout(r, 1000 * (i + 1)));
    }
  }
  console.error(`save FAILED after ${retries} retries:`, key, `${approxBytes}B`, lastErr);
  // Expõe erro detalhado pro caller via window pra alert mostrar
  if (typeof window !== "undefined" && lastErr) {
    window.__lastSaveError = {
      key,
      bytes: approxBytes,
      code: lastErr?.code || "(sem code)",
      message: lastErr?.message || String(lastErr),
    };
  }
  return false;
}
// Flag global para detectar se o load principal conseguiu conectar
let _loadSuccess = false;

//
const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v ?? 0);
const fmtBR = (v) => new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v ?? 0);
const fmtDate = (d) => d ? new Date(d + "T12:00:00").toLocaleDateString("pt-BR") : "—";
const today = () => new Date().toISOString().slice(0, 10);
const maskCpf = (v) => { const d = (v ?? "").replace(/\D/g,"").slice(0,11); if(d.length<=3) return d; if(d.length<=6) return `${d.slice(0,3)}.${d.slice(3)}`; if(d.length<=9) return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6)}`; return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9,11)}`; };
// v8.21.0: nome curto = primeiro nome + último sobrenome (desambigua quando 2 pessoas têm o mesmo primeiro nome)
const shortName = (name) => {
  if (!name) return "";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1]}`;
};
const monthKey = (y, m) => `${y}-${String(m + 1).padStart(2, "0")}`;

// v8.32 — Defesa em profundidade: limita os arrays passados pra EmployeePortal ao restaurante
// do empregado logado. Não impede ataque sério (a leitura no Firestore continua aberta sem
// rules), mas reduz vazamento via React DevTools e bate olho casual.
// Mantém estruturas {[rid]:{...}} intactas (já são indexadas e o componente acessa só pela rid certa).
function filterDataForRestaurant(data, rid) {
  if (!data || !rid) return data;
  const inRest = (item) => !item || item.restaurantId === rid;
  const restaurantHasRid = (p) => Array.isArray(p?.restaurantIds) ? p.restaurantIds.includes(rid) : false;
  return {
    ...data,
    restaurants: (data.restaurants || []).filter(r => r.id === rid),
    employees:   (data.employees || []).filter(inRest),
    roles:       (data.roles || []).filter(inRest),
    tips:        (data.tips || []).filter(inRest),
    incidents:   (data.incidents || []).filter(inRest),
    feedbacks:   (data.feedbacks || []).filter(inRest),
    meetingPlans:       (data.meetingPlans || []).filter(inRest),
    meetingIdeas:       (data.meetingIdeas || []).filter(inRest),
    meetingAgendas:     (data.meetingAgendas || []).filter(inRest),
    meetingActions:     (data.meetingActions || []).filter(inRest),
    meetingOccurrences: (data.meetingOccurrences || []).filter(inRest),
    meetingPendencias:  (data.meetingPendencias || []).filter(inRest),
    communications:     (data.communications || []).filter(inRest),
    dpMessages:         (data.dpMessages || []).filter(inRest),
    notifications:      (data.notifications || []).filter(inRest),
    inbox:              (data.inbox || []).filter(inRest),
    scheduleSwaps:      (data.scheduleSwaps || []).filter(inRest),
    dayInversions:      (data.dayInversions || []).filter(inRest),
    recursoFolders:     (data.recursoFolders || []).filter(inRest),
    recursos:           (data.recursos || []).filter(inRest),
    reservas:           (data.reservas || []).filter(inRest),
    reservasSalas:      (data.reservasSalas || []).filter(inRest),
    reservasTiposMesa:  (data.reservasTiposMesa || []).filter(inRest),
    reservasClientes:   (data.reservasClientes || []).filter(inRest),
    freelaShifts:       (data.freelaShifts || []).filter(inRest),
    freelaPagamentos:   (data.freelaPagamentos || []).filter(inRest),
    pessoas:    (data.pessoas || []).filter(restaurantHasRid),
    managers:   (data.managers || []).filter(m => !m.restaurantIds || restaurantHasRid(m)),
  };
}
// Soma N dias (pode ser negativo) a uma data YYYY-MM-DD e retorna nova string YYYY-MM-DD
const addDaysISO = (dateStr, n) => {
  if (!dateStr) return dateStr;
  const d = new Date(dateStr + "T12:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

// Converte URLs do Google Drive/Docs/Forms pra versões que permitem iframe.
// Drive/Docs/Sheets/Slides bloqueiam X-Frame em /view e /edit, mas /preview e /embed funcionam.
const embeddableUrl = (url) => {
  if (!url) return url;
  try {
    const u = new URL(url);
    const host = u.hostname;
    // Drive: /file/d/ID/view → /file/d/ID/preview
    if (host === "drive.google.com") {
      const m = u.pathname.match(/^\/file\/d\/([^/]+)/);
      if (m) return `https://drive.google.com/file/d/${m[1]}/preview`;
      // Pasta compartilhada: /drive/folders/ID → embed
      const f = u.pathname.match(/^\/drive\/folders\/([^/?#]+)/);
      if (f) return `https://drive.google.com/embeddedfolderview?id=${f[1]}#list`;
    }
    if (host === "docs.google.com") {
      // /document /spreadsheets /presentation /forms /d/ID/(edit|view) → /preview
      const m = u.pathname.match(/^\/(document|spreadsheets|presentation|forms)\/d\/([^/]+)/);
      if (m) {
        const kind = m[1];
        const id = m[2];
        if (kind === "forms") return `https://docs.google.com/forms/d/e/${id}/viewform?embedded=true`;
        return `https://docs.google.com/${kind}/d/${id}/preview`;
      }
      // Forms já no formato /e/ID/viewform
      const fe = u.pathname.match(/^\/forms\/d\/e\/([^/]+)\/viewform/);
      if (fe) return `https://docs.google.com/forms/d/e/${fe[1]}/viewform?embedded=true`;
    }
    return url;
  } catch { return url; }
};

// v8.9.1 — Sanitiza key pra Firestore (que rejeita campos com __ início+fim).
// IDs do FAQ usam padrão __gorjeta__/__sistema__/etc. Convertemos pra "gorjeta", "sistema", etc.
// Usado tanto em escrita quanto leitura no tabsGestor.faqAuto pra manter consistência.
const safeFaqKey = (id) => String(id || "").replace(/^_+|_+$/g, "") || "_";

// v8.0 — Default inteligente de mês pra telas que tipicamente envolvem planejamento/visualização forward.
// Se hoje é dia ≥ 20, o usuário provavelmente quer ver o PRÓXIMO mês (estamos no fim do mês atual,
// planejando o que vem). Senão, default = mês atual.
function smartDefaultMonth() {
  const d = new Date();
  if (d.getDate() >= 20) {
    const next = new Date(d.getFullYear(), d.getMonth() + 1, 1);
    return { year: next.getFullYear(), month: next.getMonth() };
  }
  return { year: d.getFullYear(), month: d.getMonth() };
}
const monthLabel = (y, m) => new Date(y, m, 1).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
const getWeekMonday = (dateStr) => { const d = new Date(dateStr + "T12:00:00"); const day = d.getDay(); const diff = d.getDate() - day + (day === 0 ? -6 : 1); d.setDate(diff); return d.toISOString().slice(0, 10); };

// ─────────── HELPERS DE ESCALA ALTERNADA (A/B) ───────────
// Retorna o tipo de semana ("A" | "B") para uma data, considerando overrides e re-anchors.
// Para schedules type !== "alternating" retorna null (chamador usa schedule.days direto).
function weekTypeForDate(schedule, dateStr) {
  if (!schedule || schedule.type !== "alternating") return null;
  if (!dateStr) return null;
  const monday = getWeekMonday(dateStr);
  // 1) Override pontual sobrepõe tudo
  if (schedule.overrides && schedule.overrides[monday]) return schedule.overrides[monday];
  // 2) Encontra o anchor ativo: o mais recente cuja date ≤ monday (anchor original ou re-anchor)
  const allAnchors = [
    schedule.anchor,
    ...((schedule.reanchors || []).map(r => ({ date: r.date, week: r.week }))),
  ].filter(a => a && a.date && a.week)
   .sort((a, b) => a.date.localeCompare(b.date));
  let active = allAnchors[0];
  for (const a of allAnchors) {
    if (a.date <= monday) active = a;
    else break;
  }
  if (!active) return (schedule.pattern || ["A", "B"])[0];
  // 3) Calcula paridade em semanas desde o anchor ativo
  const t1 = new Date(active.date + "T12:00:00").getTime();
  const t2 = new Date(monday + "T12:00:00").getTime();
  const diffWeeks = Math.floor((t2 - t1) / (7 * 24 * 60 * 60 * 1000));
  const pattern = schedule.pattern || ["A", "B"];
  const startIdx = pattern.indexOf(active.week);
  if (startIdx < 0) return pattern[0];
  const idx = (((startIdx + diffWeeks) % pattern.length) + pattern.length) % pattern.length;
  return pattern[idx];
}

// Retorna o objeto `days` efetivo (compatível com schedule.days antigo) para uma data específica.
// Para "single" → schedule.days; para "alternating" → schedule.weeks[weekType].days.
function getEffectiveDays(schedule, dateStr) {
  if (!schedule) return null;
  if (schedule.type !== "alternating") return schedule.days || null;
  const wk = weekTypeForDate(schedule, dateStr || today());
  return schedule.weeks?.[wk]?.days || null;
}

// Retorna totalContract efetivo para uma data (pode variar entre A e B)
function getEffectiveTotalContract(schedule, dateStr) {
  if (!schedule) return 0;
  if (schedule.type !== "alternating") return schedule.totalContract || 0;
  const wk = weekTypeForDate(schedule, dateStr || today());
  return schedule.weeks?.[wk]?.totalContract || 0;
}

// Expõe helpers pra acesso nas próximas fases (escala mensal, VT, gorjeta) — silencia eslint
// eslint-disable-next-line no-unused-expressions
[getEffectiveDays, getEffectiveTotalContract];

// ─────────── HELPERS DE CICLO DE DOMINGOS (v8.11.0) ───────────
// sundayCycle = { workCount: N, offCount: M, refDate: "YYYY-MM-DD" }  → trabalha N domingos, folga M, ciclo (N+M).
// refDate é o PRIMEIRO domingo de FOLGA da sequência. A função retorna se uma data específica
// (que precisa ser domingo) cai como folga no ciclo. Datas que não são domingo retornam false.
function getSundayCycleFromSchedule(schedule, dateStr) {
  // schedule pode ser single (sundayCycle no topo) ou alternating (sundayCycle por semana A/B).
  if (!schedule) return null;
  if (schedule.type !== "alternating") return schedule.sundayCycle || null;
  const wk = weekTypeForDate(schedule, dateStr || today());
  return schedule.weeks?.[wk]?.sundayCycle || schedule.sundayCycle || null;
}
function isSundayOffByCycle(cycle, dateStr) {
  if (!cycle || !cycle.refDate || !dateStr) return false;
  const work = parseInt(cycle.workCount, 10);
  const off = parseInt(cycle.offCount, 10);
  if (!Number.isFinite(work) || work < 0) return false;
  if (!Number.isFinite(off) || off <= 0) return false;
  // Ambos devem ser domingo
  const d = new Date(dateStr + "T12:00:00");
  if (d.getDay() !== 0) return false;
  const ref = new Date(cycle.refDate + "T12:00:00");
  if (ref.getDay() !== 0) return false;
  const diffMs = d.getTime() - ref.getTime();
  const diffWeeks = Math.round(diffMs / (7 * 24 * 60 * 60 * 1000));
  // Posição no ciclo. Refdate é a 1ª folga (pos 0). Posições 0..off-1 = folga, off..off+work-1 = trabalho.
  const cycleLen = work + off;
  const pos = ((diffWeeks % cycleLen) + cycleLen) % cycleLen;
  return pos < off;
}
// Retorna a versão de workSchedule vigente em uma data — pega a entry com maior validFrom <= date.
function getActiveWorkSchedule(empSchedules, dateStr) {
  if (!Array.isArray(empSchedules) || empSchedules.length === 0) return null;
  const sorted = [...empSchedules].filter(s => !s.validFrom || s.validFrom <= dateStr)
    .sort((a, b) => (a.validFrom || "").localeCompare(b.validFrom || ""));
  return sorted[sorted.length - 1] || null;
}

// Deriva a escala de UM empregado pra um mês a partir do horário cadastrado + ciclo de domingos.
// Mesma lógica de derivedScheduleByEmp do RestaurantPanel (v8.12.0). Usado quando não há dados
// salvos pro mês — em particular meses futuros que ninguém ainda preencheu.
function deriveScheduleForEmp(emp, year, month, workSchedules, restaurant) {
  if (!emp) return {};
  const empWS = workSchedules?.[emp.restaurantId]?.[emp.id] ?? [];
  if (empWS.length === 0) return {};
  const dim = new Date(year, month + 1, 0).getDate();
  const out = {};
  for (let d = 1; d <= dim; d++) {
    const dateStr = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
    if (restaurant?.serviceStartDate && dateStr < restaurant.serviceStartDate) continue;
    if (emp.demitidoEm && dateStr >= emp.demitidoEm) continue;
    const ws = getActiveWorkSchedule(empWS, dateStr);
    if (!ws) continue;
    if (ws.validFrom && dateStr < ws.validFrom) continue;
    const days = getEffectiveDays(ws, dateStr);
    if (!days) continue;
    const dow = new Date(dateStr+"T12:00:00").getDay();
    const dayCfg = days[dow];
    if (!dayCfg || dayCfg.active === false) { out[dateStr] = DAY_OFF; continue; }
    if (dow === 0) {
      const cycle = getSundayCycleFromSchedule(ws, dateStr);
      if (cycle && cycle.refDate && isSundayOffByCycle(cycle, dateStr)) {
        out[dateStr] = DAY_OFF;
      }
    }
  }
  return out;
}
// eslint-disable-next-line no-unused-expressions
[getSundayCycleFromSchedule, isSundayOffByCycle, getActiveWorkSchedule];
const getWeeksInMonth = (y, m) => { const daysInM = new Date(y, m + 1, 0).getDate(); const weeks = new Map(); for (let d = 1; d <= daysInM; d++) { const ds = `${y}-${String(m+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`; const mon = getWeekMonday(ds); if (!weeks.has(mon)) { const sunD = new Date(mon + "T12:00:00"); sunD.setDate(sunD.getDate() + 6); weeks.set(mon, { monday: mon, sunday: sunD.toISOString().slice(0,10), daysInMonth: [] }); } weeks.get(mon).daysInMonth.push(d); } return [...weeks.values()]; };
const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const AREAS = ["Bar", "Cozinha", "Salão", "Limpeza"];
const AREA_COLORS = { Bar: "#3b82f6", Cozinha: "#f59e0b", Salão: "#10b981", Limpeza: "#8b5cf6" };
const DEFAULT_SPLIT = { Bar: 12, Cozinha: 40, Salão: 40, Limpeza: 8 };
const TAX = 0.33;
const DAY_OFF       = "off";      // folga programada
const DAY_COMP      = "comp";     // folga por compensação (banco de horas — folgando)
const DAY_COMP_TRAB = "comptrab"; // trabalho por compensação (deve horas — trabalhando)
const DAY_VACATION  = "vac";      // ferias
const DAY_FAULT_J   = "faultj";   // falta justificada
const DAY_FAULT_U   = "faultu";   // falta injustificada
const DAY_FREELA    = "freela";   // freela — presente mas sem gorjeta


const STATUS_SHORT = {
  [DAY_OFF]:"F",[DAY_FREELA]:"FL",[DAY_COMP]:"FC",[DAY_COMP_TRAB]:"TC",
  [DAY_VACATION]:"Fér",[DAY_FAULT_J]:"FJ",[DAY_FAULT_U]:"FI",
};
// v8.3 — paleta alinhada ao redesign do v8.2 (CalendarGrid). Cores distintas pra
// pares conflitantes (Folga vs Falta Injust; FC vs TC; Freela vs TC).
const STATUS_COLORS = {
  [DAY_OFF]:"#dc2626",[DAY_FREELA]:"#14b8a6",[DAY_COMP]:"#2563eb",
  [DAY_COMP_TRAB]:"#06b6d4",[DAY_VACATION]:"#7c3aed",
  [DAY_FAULT_J]:"#f59e0b",[DAY_FAULT_U]:"#7f1d1d",
};

// Division mode constants
const MODE_AREA_POINTS = "area_points"; // default: split by area % then by points within area
const MODE_GLOBAL_POINTS = "global_points"; // split only by total points across all employees

// ─── Versioned splits + variable areas (perEmployee) ───
// splits[restaurantId] aceita 2 formatos:
//   LEGADO:  { [monthKey]: { Bar: 12, Cozinha: 40, ... } }
//   NOVO:    Array<{ id, effectiveFrom: "YYYY-MM-DD", percentages, mode, status, ata, proposedBy, ... }>
//
// Funções abaixo absorvem ambos transparentemente — backend prepara, UI virá depois.

// Retorna a regra vigente numa data específica.
// percentages pode ser obj com números (legado) ou obj com {type, value/valuePerEmp} (novo).
function getSplitForDate(splitsForRid, date) {
  if (!splitsForRid) return null;
  // Formato novo: array de versões
  if (Array.isArray(splitsForRid)) {
    const active = splitsForRid
      .filter(v => v.status === "active" && v.effectiveFrom && v.effectiveFrom <= date)
      .sort((a, b) => (b.effectiveFrom || "").localeCompare(a.effectiveFrom || ""));
    if (active[0]) return active[0];
    return null;
  }
  // Formato legado: { monthKey: percentages }
  if (typeof splitsForRid === "object") {
    const td = new Date(date + "T12:00:00");
    const mk = monthKey(td.getFullYear(), td.getMonth());
    if (splitsForRid[mk]) {
      return { percentages: splitsForRid[mk], mode: null, status: "active", _legacy: true };
    }
  }
  return null;
}

// Verifica se um tip está publicado (visível pro empregado).
// Modelo novo (Fase D): cada tip tem `publishedAt` (ISO string ou null=despublicado).
// Modelo legado: aprovação semanal via `tipApprovals[rid][weekMonday]`.
// Helper aceita ambos transparentemente.
function isTipPublished(tip, ridApprovals) {
  if (tip?.publishedAt === null) return false; // explicitamente despublicado pelo gestor
  if (tip?.publishedAt) return true;            // novo: publicado por dia
  if (!tip?.date) return false;
  const monday = getWeekMonday(tip.date);
  return !!ridApprovals?.[monday];               // legado: cai no approval semanal
}

// Conta empregados ATIVOS numa área em uma data específica (per-day, conforme decisão).
// Filtros: não freela, admitido ≤ date, demitido > date (ou nunca demitido), não inativo.
function countActiveEmployeesInArea(employees, restRoles, areaName, date, restaurantId) {
  return (employees || []).filter(e => {
    if (e.restaurantId !== restaurantId) return false;
    if (e.isFreela) return false;
    const r = (restRoles || []).find(r => r.id === e.roleId);
    if (!r || r.area !== areaName) return false;
    if (e.admission && e.admission > date) return false;
    if (e.demitidoEm && e.demitidoEm <= date) return false; // demitido conta ATÉ o dia da demissão (estrito menor)
    if (e.inactive && e.inactiveFrom && e.inactiveFrom <= date) return false;
    return true;
  }).length;
}

// Calcula percentuais finais por área aplicando regras fixas + perEmployee.
// Retorna { Bar: 12, Cozinha: 40.5, ... } sempre como número (totalizando ~100%).
//
// percentages aceita:
//   Legado: { Bar: 12, Cozinha: 40 }
//   Novo:   { Bar: { type: "fixed", value: 12 }, Limpeza: { type: "perEmployee", valuePerEmp: 1.5 } }
function computeAreaPercentages(percentages, employeesPerArea) {
  if (!percentages) return {};
  // Normaliza pra novo formato internamente
  const normalized = {};
  Object.entries(percentages).forEach(([area, val]) => {
    if (typeof val === "number") {
      normalized[area] = { type: "fixed", value: val };
    } else if (val && typeof val === "object" && val.type) {
      normalized[area] = val;
    } else {
      normalized[area] = { type: "fixed", value: 0 };
    }
  });

  const finalPct = {};
  let dynamicTotal = 0;
  // 1) Áreas perEmployee comem primeiro
  for (const [area, cfg] of Object.entries(normalized)) {
    if (cfg.type === "perEmployee") {
      const n = (employeesPerArea && employeesPerArea[area]) || 0;
      const pct = n * (cfg.valuePerEmp || 0);
      finalPct[area] = pct;
      dynamicTotal += pct;
    }
  }
  // Cap em 100 (caso patológico de muitas pessoas em área variável)
  if (dynamicTotal > 100) {
    const scale = 100 / dynamicTotal;
    Object.keys(finalPct).forEach(k => { finalPct[k] = finalPct[k] * scale; });
    dynamicTotal = 100;
  }
  // 2) Saldo restante distribuído entre áreas fixas, re-normalizado pra somar (100 - dinâmica)
  const remaining = Math.max(0, 100 - dynamicTotal);
  let fixedSum = 0;
  for (const cfg of Object.values(normalized)) {
    if (cfg.type === "fixed") fixedSum += (cfg.value || 0);
  }
  for (const [area, cfg] of Object.entries(normalized)) {
    if (cfg.type === "fixed") {
      finalPct[area] = fixedSum > 0 ? ((cfg.value || 0) / fixedSum) * remaining : 0;
    }
  }
  return finalPct;
}

//
function nextEmpSeq(employees, restaurantCode) {
  // Find all seqs ever used for this restaurant (including deleted)
  // Set lookup é O(1) — antes era includes() em array (O(n)) num while loop = O(n²)
  const used = new Set(
    employees
      .filter(e => e.restaurantId && e.empCode && e.empCode.startsWith(restaurantCode))
      .map(e => parseInt(e.empCode.slice(restaurantCode.length)) || 0)
  );
  let seq = 1;
  while (used.has(seq)) seq++;
  return seq;
}
function makeEmpCode(restaurantCode, seq) {
  return restaurantCode.toUpperCase() + String(seq).padStart(4, "0");
}

const K = {
  owners: "v4:owners",
  managers:      "v4:managers",
  restaurants:   "v4:restaurants",
  employees:     "v4:employees",
  roles:         "v4:roles",
  tips:          "v4:tips",
  splits:        "v4:splits",
  schedules:     "v4:schedules",
  communications:"v4:communications",
  commAcks:      "v4:commAcks",
  faq:           "v4:faq",
  dpMessages:    "v4:dpMessages",
  workSchedules: "v4:workSchedules",
  notifications: "v4:notifications",
  noTipDays:     "v4:noTipDays",
  trash:         "v4:trash",           // {restaurants:[], managers:[], employees:[]}
  schedTemplates:"v4:schedTemplates",  // {[restaurantId]: [{id,name,days}]}
  schedDrafts:   "v4:schedDrafts",     // {[restaurantId]: {[empId]: {days,savedAt}}}
  scheduleVersions: "v4:scheduleVersions", // {[restaurantId]: {[monthKey]: [{id,ts,author,reason,snapshot}]}}
  tipVersions:      "v4:tipVersions",      // {[restaurantId]: {[monthKey]: [{id,ts,author,reason,snapshot}]}}
  vtConfig:         "v4:vtConfig",          // {[restaurantId]: {[employeeId]: {dailyRate: number}}}
  vtMonthly:        "v4:vtMonthly",         // {[restaurantId]: {[monthKey]: {[employeeId]: {adjustOverride: number|null, manualDiscount: number, manualBonus: number}}}}
  vtPayments:       "v4:vtPayments",        // {[restaurantId]: {[monthKey]: {paidAt: ISO, paidBy: string, snapshot: [{empId,name,role,dailyRate,plannedDays,grossVT,autoAdjust,manualBonus,manualDiscount,totalPaid}]}}}
  incidents:        "v4:incidents",         // [{id, restaurantId, employeeIds:[], type, severity, description, date, createdAt, createdBy, createdById, visibility:"internal"}]
  feedbacks:        "v4:feedbacks",         // [{id, restaurantId, employeeId, quarter, year, rating, strengths, improvements, internalNotes, goal, targetRoleId, devChecklist:[{title,link,type,done}], createdAt, createdBy}]
  devChecklists:    "v4:devChecklists",     // {[roleId]: [{title, link, type:"livro"|"video"|"curso"|"pratica"}]}
  scheduleAdjustments: "v4:scheduleAdjustments", // {[rid]: {[mk]: [{id, empId, date, from, to, author, timestamp}]}}
  scheduleStatus:      "v4:scheduleStatus",      // {[rid]: {[mk]: {status:"open"|"review"|"closed", closedAt, closedBy, lastPontoImport, pontoSystem, missingFromPonto:[], importHistory:[], lastImportSummary}}}
  schedulePrevista:    "v4:schedulePrevista",     // {[rid]: {[mk]: frozen snapshot of schedules at first edit/VT payment}}
  scheduleSwaps:       "v4:scheduleSwaps",        // [{id, restaurantId, date, empAId, empBId, note?, registeredAt, registeredBy}] — inversões informais (v8.11.0)
  dayInversions:       "v4:dayInversions",        // [{id, restaurantId, dateA, dateB, reason?, createdAt, createdBy}] — inversões de dia inteiro (feriados)
  recursoFolders:      "v4:recursoFolders",       // [{id, restaurantId, name, icon, order, createdAt, createdBy, deletedAt?}]  (v8.15.0)
  recursos:            "v4:recursos",             // [{id, restaurantId, folderId, name, type:"link"|"nota", url?, content?, tags:[], visibility:{mode,sharedWith}, editores:[pessoaId], createdById, createdBy, createdAt, updatedAt?, updatedBy?, deletedAt?}]  (v8.15.1: editores delegados pelo Owner; responsavelId removido)
  recursosInitialized: "v4:recursosInitialized",  // {[restaurantId]: true} — evita recriar pastas exemplo
  employeeGoals:       "v4:employeeGoals",        // {[empId]: [{id, type, title, targetRoleId?, topic?, materials:[], metas:[], createdAt, createdBy, status:"active"|"completed"|"cancelled"}]}
  delays:              "v4:delays",               // {[rid]: {[mk]: {[empId]: {[day]: minutes}}}}
  tipApprovals:        "v4:tipApprovals",         // {[rid]: {[weekMonday]: {approvedAt, approvedBy, approvedByName}}}
  meetingPlans:        "v4:meetingPlans",         // [{id, restaurantId, type:"alinhamento"|"avaliação", employeeIds:[], plannedDate, note, createdBy, createdAt, completedFeedbackIds:{[empId]:fbId}}]
  meetingIdeas:        "v4:meetingIdeas",         // [{id, restaurantId, title, description, area, createdBy, createdAt, priority:bool, status:"nova"|"na_pauta"|"descartada"}]
  meetingAgendas:      "v4:meetingAgendas",       // [{id, restaurantId, title, templateType, date, items:[], createdBy, createdAt, status:"aberta"|"em_andamento"|"encerrada"}]
  meetingActions:      "v4:meetingActions",       // [{id, restaurantId, pautaId, text, responsible, deadline, done:bool, createdAt}]
  meetingOccurrences:  "v4:meetingOccurrences",   // [{id, restaurantId, title, description, type:"cliente"|"estrutura"|"operacao"|"outro", areas:[], createdBy, createdAt, status:"nova"|"na_pauta"|"resolvida"|"descartada"}]
  meetingPendencias:   "v4:meetingPendencias",    // [{id, restaurantId, text, reason, type:"pendente"|"sem_resolucao", fromPautaId, fromItemId, createdAt, status:"ativa"|"na_pauta"|"resolvida"}]
  inbox:               "v4:inbox",                // [{id, tipo, de:{id,nome,role}, para:{id?,role?,restaurantId?}, restaurantId?, assunto, corpo, lido:bool, criadoEm, metadata:{}, folder:null|string, deletedAt:null|iso}]
  inboxFolders:        "v4:inboxFolders",         // {userId: [{id, nome, cor, ordem}]}
  // ═══ Operacional — Contagens (estoques por perfil de usuário) ═══
  miseCategories:      "v4:miseCategories",       // [{id, restaurantId, name}]   ex: Vinhos, Bar, Cozinha
  miseStocks:          "v4:miseStocks",           // [{id, restaurantId, name, location?}]   ex: Estoque 1, Adega
  miseAssignments:     "v4:miseAssignments",      // [{id, restaurantId, categoryId, stockId, userId}]  unicidade por tripla
  miseItems:           "v4:miseItems",            // [{id, restaurantId, categoryId?, name, unit, expectedQty?, idealQtys?:{0..6:number}, pkgSize?:number, stockIds?:[stockId], suppliers?:[{supplierId,preferred:bool}]}]
  // idealQtys: estoque ideal por dia da semana (índice 0=Dom..6=Sáb).
  //   Pedido sugerido = ceil((idealQtys[diaSeguinte] - contagem) / pkgSize) * pkgSize
  // stockIds: produto pode existir em múltiplos estoques físicos — contagem por estoque, soma vai pro pedido.
  // suppliers: atalho com fornecedor preferencial + alternativos (até 3). Tabela miseProductSuppliers continua válida pra dados extras (conversionFactor/price).
  miseCycles:          "v4:miseCycles",           // [{id, restaurantId, name, startDate, endDate?, status:"open"|"closed", closedAt?, closedBy?}]
  miseCounts:          "v4:miseCounts",           // [{id, restaurantId, cycleId, itemId, stockId, userId, qty, countedAt, note?}]
  miseSuppliers:       "v4:miseSuppliers",        // [{id, restaurantId, name, nomeContato?, whatsapp, notes}]
  // nomeContato: usado na mensagem WhatsApp ("Olá [nomeContato]..."). Junto com whatsapp são obrigatórios pra gerar pedido.
  miseProductSuppliers:"v4:miseProductSuppliers", // [{id, restaurantId, productId, supplierId, conversionFactor, price?, preferred}]
  miseSupplierOrders:  "v4:miseSupplierOrders",   // [{id, restaurantId, cycleId, supplierId, status, items:[{productId,qtySuggested,qtyApproved?,qtyReceived?,notes?}], createdAt, approvedAt?, sentAt?, receivedAt?, receivedBy?, receivedByName?, messageText?, history:[{status,ts,by,note}]}]
  miseChecklistTemplates: "v4:miseChecklistTemplates", // [{id, restaurantId, name, description?, items:[{id,text,order}], active}]
  miseChecklistRuns:      "v4:miseChecklistRuns",      // [{id, restaurantId, templateId, userId, userName, date, items:[{itemId,done,doneAt?,note?}], completedAt?}]
  // ═══ Pessoas (entidade unificada: empregados + gestores → uma só) ═══
  pessoas:             "v4:pessoas",              // [{id, restaurantIds:[], name, cpf, pin, mustChangePin, email?, whatsapp?, isTeam:{[rid]:bool}, teamData:{[rid]:{empCode,roleId,admission,...}}, linkedEmployeeId?, linkedManagerId?, permissions:{[rid]:{operational:{},admin:{},special:{}}}}]
  pessoasMigratedAt:   "v4:pessoasMigratedAt",    // ISO date da última migração (para idempotência)
  permsV2MigratedAt:   "v4:permsV2MigratedAt",    // v8.17.0 — flag idempotente da migração isDP → perms granulares
  freelasFecharOpMigratedAt: "v4:freelasFecharOpMigratedAt", // v8.30.0 — flag da migração admin.freelasFechar → operational.freelasFechar
  // ═══ Operacional — Fichas Técnicas (portado do projeto fichastecnicas-c3829) ═══
  miseFtInsumos:       "v4:miseFtInsumos",        // [{id, restaurantId, name, unit, price}]
  miseFtEquipamentos:  "v4:miseFtEquipamentos",   // {[restaurantId]: [string]}
  miseFtDishes:        "v4:miseFtDishes",         // [{id, restaurantId, name, description, louca, equipamentos:[], markup, sub_fichas:[{id,name,rendimento,rendimento_qty,rendimento_unit,modo_preparo,subproduto?:{name},ingredientes:[{insumo_id?,insumo_name?,subref_id?,qty,unit,qty_raw?,is_qb?,fc?}]}], photos:[]}]
  // ═══ Operacional — Temperaturas (sensores Tuya/SmartLife) ═══
  tuyaLinks:           "v4:tuyaLinks",            // {[restaurantId]: {uid, accessToken?, refreshToken?, expiresAt?, linkedAt, linkedBy, accountEmail?}}
  tempSensors:         "v4:tempSensors",          // [{id, restaurantId, tuyaDeviceId, name, equipmentId?, location?, minTemp, maxTemp, alertMinutes, alertWhatsapp?, alertPessoaId?, createdAt, active}]
  tempReadings:        "v4:tempReadings",         // [{id, sensorId, restaurantId, timestamp, temp, battery?, online:bool}]   // histórico — pode ser truncado/arquivado
  tempAlerts:          "v4:tempAlerts",           // [{id, sensorId, restaurantId, openedAt, closedAt?, firstTemp, peakTemp, notifiedAt?, acknowledgedBy?, acknowledgedAt?, note?}]
  tempBackfillState:   "v4:tempBackfillState",    // {[restaurantId]: { lastBackfillAt: ISO, lastBackfillBy?: name }} — auto-backfill a cada 3 dias
  // ═══ Permissões — perfis customizados por restaurante ═══
  permProfiles:        "v4:permProfiles",          // {[restaurantId]: [{id, label, icon, color, desc, keys:["operational.contagens", ...]}]}
  // ═══ Freelas ═══
  freelaShifts:        "v4:freelaShifts",          // [{id, restaurantId, pessoaId, date, entrada, saida, intervalo, area, valorTipo:"hora"|"diaria", valorUnit, totalCalc, status:"aberto"|"fechamento"|"pago", lotePagamentoId?, lancadoPor, lancadoEm, observacao}]
  freelaPagamentos:    "v4:freelaPagamentos",      // [{id, restaurantId, criadoEm, criadoPor, shiftIds:[], totalGeral, status:"em_fechamento"|"pago", pagoEm?, pagoPor?}]

  // ═══ Reservas (módulo de reservas de mesa) ═══
  reservasConfig:      "v4:reservasConfig",         // {[rid]: { turnos:[{id,nome,diasSemana:[],horarios:[],capacidadePessoas,slotMinutes,ativo}], duracaoMediaMin, antecedenciaMinHoras, antecedenciaMaxDias, mensagemConfirmacao, mensagemLembrete, autoNoShowAposMin }}
  reservasSalas:       "v4:reservasSalas",          // [{id, restaurantId, nome, ordem, ativa, descricao?}]
  reservasTiposMesa:   "v4:reservasTiposMesa",      // [{id, restaurantId, salaId, label, capacidade, quantidade, descricao?, ativa, ordem}]
  reservasClientes:    "v4:reservasClientes",       // [{id, restaurantId, nome, telefone, email?, aniversario?, observacoes?, tags:[], visitas:0, ultimaVisita?, noShows:0, blacklisted:false, createdAt, createdBy}]
  reservas:            "v4:reservas",               // [{id, restaurantId, dateTime, party, turnoId, salaId?, tipoMesaId?, mesaIds:[], clienteId?, nomeCliente, telCliente, emailCliente?, status:"feita"|"confirmada"|"sentou"|"finalizada"|"no-show"|"cancelada", ocasiao?, observacoesCliente?, observacoesInternas?, fonte:"widget"|"backoffice", forcada:false, criadaPor, criadaPorId, confirmadaEm?, confirmadaPor?, sentouEm?, sentouPor?, finalizouEm?, noShowEm?, canceladaEm?, canceladaPor?, createdAt}]
};

// ── Inbox helpers ──
// tipos: "solicitacao" | "comunicado" | "sistema" | "reuniao" | "acao_reuniao"
function createInboxMessage({ tipo, de, para, restaurantId, assunto, corpo, metadata = {} }) {
  return {
    id: Date.now().toString() + "_" + Math.random().toString(36).slice(2, 8),
    tipo,
    de,           // {id, nome, role} — quem enviou
    para,         // {id?, role?, restaurantId?} — destinatário (userId ou role+restaurantId)
    restaurantId: restaurantId || null,
    assunto,
    corpo,
    lido: false,
    criadoEm: new Date().toISOString(),
    metadata,     // dados extras (ex: meetingId, actionIds, prazo, etc.)
    folder: null,     // null = Entrada, string = id da pasta custom
    deletedAt: null,  // null = ativa, ISO string = na lixeira (purge após 90 dias)
  };
}

// 90 dias em ms — usado em purge automático de inbox excluído
const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;

// Filtrar mensagens do inbox para um destinatário específico
// folder: undefined = todas (exceto excluídas), null = Entrada, "__trash" = excluídas, string = id da pasta
function getInboxForUser({ inbox, userId, userRole, restaurantId, includeRead = true, folder }) {
  const now = Date.now();
  return (inbox ?? []).filter(m => {
    // Purge: mensagens excluídas há mais de 90 dias são invisíveis
    if (m.deletedAt && (now - new Date(m.deletedAt).getTime()) > NINETY_DAYS_MS) return false;
    // Filtro de pasta
    if (folder === "__trash") { if (!m.deletedAt) return false; }
    else if (folder === null || folder === undefined) { if (m.deletedAt) return false; }
    else { if (m.deletedAt) return false; if (m.folder !== folder) return false; }
    // Filtro pasta específica (quando folder é null = Entrada, mostra só sem pasta)
    if (folder === null && m.folder) return false;
    if (!includeRead && m.lido) return false;
    // Destinatário
    if (m.para?.id === userId) return true;
    if (m.para?.role === userRole && m.para?.restaurantId === restaurantId) return true;
    if (m.para?.role === userRole && !m.para?.restaurantId) return true;
    if (m.para?.role === "all" && m.para?.restaurantId === restaurantId) return true;
    return false;
  }).sort((a, b) => b.criadoEm.localeCompare(a.criadoEm));
}

// Auto-purge: remove mensagens excluídas há mais de 90 dias do array (chamado no load)
function purgeExpiredInbox(inbox) {
  const now = Date.now();
  const before = (inbox ?? []).length;
  const cleaned = (inbox ?? []).filter(m => !m.deletedAt || (now - new Date(m.deletedAt).getTime()) <= NINETY_DAYS_MS);
  return cleaned.length < before ? cleaned : null; // null = nada mudou
}

// Criar notificações automáticas de reunião agendada
function createMeetingNotifications({ meetingType, meetingDate, restaurantId, participants, createdBy }) {
  const tipoLabel = meetingType === "equipe" ? "Reunião de Equipe" : "Reunião de Liderança";
  const dataFmt = meetingDate ? new Date(meetingDate + "T12:00:00").toLocaleDateString("pt-BR") : "em breve";
  return participants.map(p => createInboxMessage({
    tipo: "reuniao",
    de: createdBy,
    para: { id: p.id },
    restaurantId,
    assunto: `📅 ${tipoLabel} agendada`,
    corpo: `Nova ${tipoLabel.toLowerCase()} marcada para ${dataFmt}. Sua presença é esperada.`,
    metadata: { meetingType, meetingDate },
  }));
}

// Criar notificações de ações pós-reunião de liderança
function createActionNotifications({ actions, restaurantId, meetingDate, createdBy }) {
  // Agrupar ações por responsável
  const byResponsible = {};
  actions.forEach(a => {
    const key = a.responsibleId || a.responsible;
    if (!key) return;
    if (!byResponsible[key]) byResponsible[key] = { responsible: a.responsible, responsibleId: a.responsibleId, direct: [], involved: [] };
    byResponsible[key].direct.push(a);
  });
  // Também marcar envolvidos (quem não é responsável direto mas está na lista de envolvidos)
  actions.forEach(a => {
    (a.involvedIds ?? []).forEach(iid => {
      if (iid === (a.responsibleId || a.responsible)) return; // já é responsável direto
      if (!byResponsible[iid]) byResponsible[iid] = { responsible: null, responsibleId: iid, direct: [], involved: [] };
      byResponsible[iid].involved.push(a);
    });
  });

  const dataFmt = meetingDate ? new Date(meetingDate + "T12:00:00").toLocaleDateString("pt-BR") : "recente";
  return Object.entries(byResponsible).map(([key, { responsibleId, direct, involved }]) => {
    let corpo = `**Ações da Reunião de Liderança — ${dataFmt}**\n\n`;
    if (direct.length > 0) {
      corpo += `🔴 Você é responsável:\n`;
      direct.forEach(a => {
        corpo += `• ${a.text}${a.deadline ? ` (prazo: ${new Date(a.deadline + "T12:00:00").toLocaleDateString("pt-BR")})` : ""}\n`;
      });
      corpo += "\n";
    }
    if (involved.length > 0) {
      corpo += `🟡 Você está envolvido:\n`;
      involved.forEach(a => {
        corpo += `• ${a.text} (responsável: ${a.responsible || "—"}${a.deadline ? `, prazo: ${new Date(a.deadline + "T12:00:00").toLocaleDateString("pt-BR")}` : ""})\n`;
      });
    }
    return createInboxMessage({
      tipo: "acao_reuniao",
      de: createdBy,
      para: { id: responsibleId || key },
      restaurantId,
      assunto: `📋 Ações da reunião de liderança — ${dataFmt}`,
      corpo: corpo.trim(),
      metadata: { meetingDate, actionCount: direct.length + involved.length },
    });
  });
}

// ── Version retention ──
// Reduzido de 30 → 5 pra evitar estouro do limite de 1 MB do Firestore. Cada (rid, mk) tem até 5 snapshots.
const MAX_VERSIONS = 5;
const VERSION_DEBOUNCE_MS = 30000; // 30s
// Pending debounced snapshots: key = `${kind}:${rid}:${mk}`, value = { timer, firstSnapshot, author, reason }
const _pendingSnapshots = {};

// Format relative time in pt-BR ("há 5 min", "há 2h", "ontem às 14:30")
function fmtRelTime(iso) {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "agora há pouco";
  if (diffMin < 60) return `há ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `há ${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD === 1) return `ontem às ${d.toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"})}`;
  if (diffD < 7) return `há ${diffD} dias`;
  return d.toLocaleDateString("pt-BR") + " " + d.toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"});
}

// Create a snapshot of schedules for a specific month (deep copy)
function snapshotSchedulesMonth(schedules, rid, mk) {
  return JSON.parse(JSON.stringify(schedules?.[rid]?.[mk] ?? {}));
}
// Create a snapshot of tips for a specific month (deep copy)
function snapshotTipsMonth(tips, rid, mk) {
  return JSON.parse(JSON.stringify((tips ?? []).filter(t => t.restaurantId === rid && t.monthKey === mk)));
}

// Enqueue or save a version entry. `kind` = "schedules" | "tips".
// Bulk = true salva imediatamente (sem debounce). Bulk = false aguarda VERSION_DEBOUNCE_MS.
function saveVersion(kind, rid, mk, currentVersions, snapshot, author, reason, onUpdate, bulk = false) {
  const versionKey = kind === "schedules" ? "scheduleVersions" : "tipVersions";
  const pendKey = `${kind}:${rid}:${mk}`;

  function commit(snap, reas) {
    const entry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
      ts: new Date().toISOString(),
      author: author || "Gestor Adm.",
      reason: reas,
      snapshot: snap,
    };
    const byRest = { ...(currentVersions ?? {}) };
    const byMonth = { ...(byRest[rid] ?? {}) };
    const list = [entry, ...(byMonth[mk] ?? [])].slice(0, MAX_VERSIONS);
    byMonth[mk] = list;
    byRest[rid] = byMonth;
    // Poda TODAS as listas (de todos os restaurantes/meses) pra MAX_VERSIONS — protege contra
    // crescimento histórico que pode estourar 1 MB no Firestore.
    Object.keys(byRest).forEach(r => {
      const months = byRest[r];
      if (!months || typeof months !== "object") return;
      const trimmedMonths = { ...months };
      Object.keys(trimmedMonths).forEach(m => {
        if (Array.isArray(trimmedMonths[m]) && trimmedMonths[m].length > MAX_VERSIONS) {
          trimmedMonths[m] = trimmedMonths[m].slice(0, MAX_VERSIONS);
        }
      });
      byRest[r] = trimmedMonths;
    });
    onUpdate(versionKey, byRest);
  }

  if (bulk) {
    // clear pending (bulk supersedes debounced edits)
    if (_pendingSnapshots[pendKey]) { clearTimeout(_pendingSnapshots[pendKey].timer); delete _pendingSnapshots[pendKey]; }
    commit(snapshot, reason);
    return;
  }

  // Debounced: if already pending, reset timer (keep original pre-edit snapshot)
  if (_pendingSnapshots[pendKey]) {
    clearTimeout(_pendingSnapshots[pendKey].timer);
    _pendingSnapshots[pendKey].editCount = (_pendingSnapshots[pendKey].editCount ?? 1) + 1;
    _pendingSnapshots[pendKey].timer = setTimeout(() => {
      const p = _pendingSnapshots[pendKey];
      if (!p) return;
      commit(p.preSnapshot, `Edição manual (${p.editCount} alteração${p.editCount>1?"ões":""})`);
      delete _pendingSnapshots[pendKey];
    }, VERSION_DEBOUNCE_MS);
    return;
  }

  // New pending: capture the PRE-edit state (snapshot passed in should be pre-edit)
  _pendingSnapshots[pendKey] = {
    preSnapshot: snapshot,
    editCount: 1,
    timer: setTimeout(() => {
      commit(snapshot, "Edição manual (1 alteração)");
      delete _pendingSnapshots[pendKey];
    }, VERSION_DEBOUNCE_MS),
  };
}

//
const ac = "#d4a017";
// ── Shared styles ──
const S = {
  input: { width:"100%", boxSizing:"border-box", padding:"11px 14px", borderRadius:10, border:"1px solid var(--border)", background:"var(--input-bg)", color:"var(--text)", fontSize:14, fontFamily:"'DM Sans',sans-serif", outline:"none" },
  btnPrimary: { width:"100%", padding:"12px", borderRadius:12, background:ac, border:"none", color:"var(--text)", fontWeight:700, fontSize:14, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" },
  btnSecondary: { padding:"8px 18px", borderRadius:10, border:"1px solid var(--border)", background:"transparent", color:"var(--text2)", cursor:"pointer", fontFamily:"'DM Sans',sans-serif", fontSize:13 },
  // Small action button with minimum touch target (44px)
  btnSmall: { padding:"8px 14px", borderRadius:8, border:"1px solid var(--border)", background:"transparent", cursor:"pointer", fontFamily:"'DM Sans',sans-serif", fontSize:12, minHeight:44, display:"inline-flex", alignItems:"center", justifyContent:"center" },
  card: { background:"var(--card-bg)", borderRadius:16, padding:"18px 20px", border:"1px solid var(--border)" },
  label: { color:"var(--text3)", fontSize:12, marginBottom:4, display:"block", fontWeight:500 },
  mono: { fontFamily:"'DM Mono',monospace" },
};

//
function Toast({ msg, onClose }) {
  useEffect(() => { if (msg) { const t = setTimeout(onClose, 3200); return () => clearTimeout(t); } }, [msg, onClose]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!msg) return null;
  return <div role="status" aria-live="polite" style={{ position:"fixed", bottom:32, left:"50%", transform:"translateX(-50%)", background:"var(--text)", color:"var(--bg)", padding:"12px 28px", borderRadius:40, fontFamily:"'DM Sans',sans-serif", fontWeight:600, fontSize:14, boxShadow:"0 8px 32px rgba(0,0,0,.15)", zIndex:9999, whiteSpace:"nowrap" }}>{msg}</div>;
}

function PrivacyModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div role="dialog" aria-modal="true" aria-label="Política de Privacidade" style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{background:"var(--card-bg)",borderRadius:16,padding:28,maxWidth:480,width:"100%",maxHeight:"85vh",overflowY:"auto",border:"1px solid var(--border)",fontFamily:"'DM Sans',sans-serif",boxShadow:"0 20px 60px rgba(0,0,0,.2)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <h2 style={{color:"var(--text)",margin:0,fontSize:18,fontWeight:700}}>🔒 Política de Privacidade</h2>
          <button onClick={onClose} style={{background:"none",border:"none",color:"var(--text3)",cursor:"pointer",fontSize:20}}>✕</button>
        </div>
        <div style={{color:"var(--text2)",fontSize:13,lineHeight:1.8,display:"flex",flexDirection:"column",gap:12}}>
          <p style={{color:"var(--text3)",fontSize:11}}>Última atualização: {new Date().toLocaleDateString("pt-BR")}</p>
          <p><strong style={{color:"var(--text)"}}>1. Quem somos</strong><br/>Esta é uma plataforma de gestão de gorjetas para restaurantes, operada pelo estabelecimento ao qual você está vinculado.</p>
          <p><strong style={{color:"var(--text)"}}>2. Dados coletados</strong><br/>Coletamos: nome completo, CPF, cargo, data de admissão e PIN de acesso. Esses dados são necessários para identificação e distribuição de gorjetas.</p>
          <p><strong style={{color:"var(--text)"}}>3. Finalidade</strong><br/>Seus dados são usados exclusivamente para: controle de acesso ao sistema, cálculo e distribuição de gorjetas, gestão de escala e comunicados internos.</p>
          <p><strong style={{color:"var(--text)"}}>4. Armazenamento</strong><br/>Os dados são armazenados no Google Firebase (servidores na América do Sul) com acesso restrito ao seu restaurante. Não compartilhamos seus dados com terceiros.</p>
          <p><strong style={{color:"var(--text)"}}>5. Seus direitos (LGPD)</strong><br/>Você tem direito a: acessar seus dados, corrigir informações incorretas, solicitar a exclusão dos seus dados e revogar o consentimento a qualquer momento.</p>
          <p><strong style={{color:"var(--text)"}}>6. Solicitação de exclusão</strong><br/>Para solicitar a exclusão dos seus dados, entre em contato com o gestor do seu restaurante através da função "Fale com DP" no aplicativo.</p>
          <p><strong style={{color:"var(--text)"}}>7. Contato</strong><br/>Dúvidas sobre privacidade devem ser direcionadas ao gestor responsável pelo restaurante.</p>
          <p style={{color:"var(--text3)",fontSize:11,borderTop:"1px solid var(--border)",paddingTop:12}}>Esta política está em conformidade com a Lei Geral de Proteção de Dados (LGPD — Lei nº 13.709/2018).</p>
        </div>
        <button onClick={onClose} style={{...S.btnPrimary,marginTop:16}}>Entendi</button>
      </div>
    </div>
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div role="dialog" aria-modal="true" aria-label={title} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.4)", zIndex:8000, display:"flex", alignItems:"center", justifyContent:"center", padding:16, overflowY:"auto" }} onKeyDown={e=>{if(e.key==="Escape")onClose();}}>
      <div style={{ background:"var(--card-bg)", borderRadius:20, padding:28, width:"100%", maxWidth:wide?680:480, border:"1px solid var(--border)", maxHeight:"92vh", overflowY:"auto", boxShadow:"0 20px 60px rgba(0,0,0,.15)" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
          <h3 style={{ color:"var(--text)", margin:0, fontFamily:"'DM Sans',sans-serif", fontSize:17, fontWeight:700 }}>{title}</h3>
          <button onClick={onClose} aria-label="Fechar" style={{ background:"none", border:"none", color:"var(--text3)", fontSize:20, cursor:"pointer" }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── PDF Preview Modal ──
function PDFPreviewModal({ pdfDoc, fileName, onClose, title }) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    if (!pdfDoc) return;
    const blob = pdfDoc.output("blob");
    const url = URL.createObjectURL(blob);
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pdfDoc]);
  return (
    <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.7)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{background:"var(--bg2)",borderRadius:16,display:"flex",flexDirection:"column",width:"95vw",maxWidth:900,height:"90vh",border:"1px solid var(--border)",overflow:"hidden"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 20px",borderBottom:"1px solid var(--border)",flexShrink:0}}>
          <span style={{color:"var(--text)",fontWeight:700,fontSize:15,fontFamily:"'DM Sans',sans-serif"}}>{title || "Pré-visualização do PDF"}</span>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>{ if(pdfDoc) pdfDoc.save(fileName || "documento.pdf"); }} style={{padding:"6px 16px",borderRadius:8,border:"none",background:"var(--ac)",color:"#fff",fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
              Baixar PDF
            </button>
            <button onClick={onClose} style={{padding:"6px 16px",borderRadius:8,border:"1px solid var(--border)",background:"transparent",color:"var(--text3)",fontSize:13,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
              Fechar
            </button>
          </div>
        </div>
        <div style={{flex:1,overflow:"hidden",background:"#525659"}}>
          {blobUrl ? (
            <iframe src={blobUrl} title="PDF Preview" style={{width:"100%",height:"100%",border:"none"}} />
          ) : (
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100%",color:"#fff",fontSize:14}}>Carregando...</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Version History Modal (shared by Escala + Gorjetas) ──
function VersionHistoryModal({ title, versions, currentSnapshot, onRestore, onClose, restoreLabel = "Restaurar esta versão", emptyMsg = "Nenhuma versão salva ainda. Qualquer alteração gera automaticamente um ponto de histórico." }) {
  const list = versions ?? [];
  return (
    <Modal title={title} onClose={onClose} wide>
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <p style={{color:"var(--text3)",fontSize:13,margin:0,lineHeight:1.6}}>
          As últimas {MAX_VERSIONS} versões deste mês são mantidas automaticamente. Ao restaurar, a versão atual também vira um ponto no histórico — você pode desfazer se quiser.
        </p>
        {list.length === 0 && (
          <div style={{...S.card,textAlign:"center",padding:"32px 20px"}}>
            <div style={{fontSize:32,marginBottom:10}}>📂</div>
            <p style={{color:"var(--text3)",fontSize:13,margin:0}}>{emptyMsg}</p>
          </div>
        )}
        {list.map((v, idx) => (
          <div key={v.id} style={{...S.card,padding:"14px 18px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
              <div style={{flex:1,minWidth:200}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                  <span style={{color:"var(--text)",fontWeight:700,fontSize:14}}>{v.reason || "Alteração"}</span>
                  {idx === 0 && <span style={{background:"#10b98122",color:"var(--green)",fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:6}}>MAIS RECENTE</span>}
                </div>
                <div style={{color:"var(--text3)",fontSize:12,display:"flex",gap:8,flexWrap:"wrap"}}>
                  <span>👤 {v.author || "Gestor"}</span>
                  <span>·</span>
                  <span title={new Date(v.ts).toLocaleString("pt-BR")}>🕐 {fmtRelTime(v.ts)}</span>
                </div>
              </div>
              <button onClick={async ()=>{
                if (!await appConfirm(`Restaurar esta versão?\n\n"${v.reason}" · ${fmtRelTime(v.ts)}\n\nA versão atual será salva como uma nova entrada no histórico — você pode desfazer depois.`)) return;
                onRestore(v);
              }} style={{...S.btnSecondary,fontSize:12,padding:"8px 16px",color:"var(--ac-text)",borderColor:"var(--ac)44"}}>
                ♻️ {restoreLabel}
              </button>
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}

function AreaBadge({ area }) {
  return <span style={{ background:AREA_COLORS[area]+"22", color:AREA_COLORS[area], borderRadius:6, padding:"2px 9px", fontSize:11, fontWeight:700 }}>{area}</span>;
}

function PillBar({ options, value, onChange }) {
  return (
    <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
      {options.map(o => (
        <button key={o} onClick={() => onChange(o)} style={{ padding:"6px 16px", borderRadius:20, border:`1px solid ${value===o?ac:"var(--border)"}`, background:value===o?ac:"transparent", color:value===o?"#fff":"var(--text3)", cursor:"pointer", fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:value===o?700:500 }}>{o}</button>
      ))}
    </div>
  );
}

function MonthNav({ year, month, onChange }) {
  const prev = () => { let m=month-1, y=year; if(m<0){m=11;y--;} onChange(y,m); };
  const next = () => { let m=month+1, y=year; if(m>11){m=0;y++;} onChange(y,m); };
  return (
    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
      <button onClick={prev} style={{ ...S.btnSecondary, padding:"6px 12px" }}>‹</button>
      <span style={{ color:"var(--text)", fontFamily:"'DM Mono',monospace", fontSize:13, minWidth:140, textAlign:"center", textTransform:"capitalize" }}>{monthLabel(year,month)}</span>
      <button onClick={next} style={{ ...S.btnSecondary, padding:"6px 12px" }}>›</button>
    </div>
  );
}


//
function CalendarGrid({ year, month, dayMap, onDayClick, readOnly, delayMap, dayInversionsByDate }) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++)
    cells.push(`${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`);

  // v8.2 — paleta sólida com cores distintas para pares conflitantes (Folga vs Falta Injust; FC vs TC)
  // Cada célula: bg sólido + texto branco + número do dia + código bold no centro
  const styleOf = (dateStr) => {
    if (!dateStr) return null;
    const s = dayMap?.[dateStr];
    if (s === DAY_OFF)       return { bg: "#dc2626", code: "F",   label: "Folga" };
    if (s === DAY_FREELA)    return { bg: "#14b8a6", code: "FL",  label: "Freela" };
    if (s === DAY_COMP)      return { bg: "#2563eb", code: "FC",  label: "Folga Comp." };
    if (s === DAY_COMP_TRAB) return { bg: "#06b6d4", code: "TC",  label: "Trab. Comp." };
    if (s === DAY_VACATION)  return { bg: "#7c3aed", code: "Fér", label: "Férias" };
    if (s === DAY_FAULT_J)   return { bg: "#f59e0b", code: "FJ",  label: "Falta Just." };
    if (s === DAY_FAULT_U)   return { bg: "#7f1d1d", code: "FI",  label: "Falta Injust." }; // vermelho mais escuro pra distinguir de Folga
    return { bg: "#16a34a", code: "T", label: "Trabalho" };
  };

  // Lista enxuta pra legenda — somente status que aparecem ou são frequentes
  const LEGEND_ALL = [
    { bg: "#16a34a", code: "T",   label: "Trabalho" },
    { bg: "#dc2626", code: "F",   label: "Folga" },
    { bg: "#7f1d1d", code: "FI",  label: "Falta Injust." },
    { bg: "#f59e0b", code: "FJ",  label: "Falta Just." },
    { bg: "#2563eb", code: "FC",  label: "Folga Comp." },
    { bg: "#06b6d4", code: "TC",  label: "Trab. Comp." },
    { bg: "#7c3aed", code: "Fér", label: "Férias" },
    { bg: "#14b8a6", code: "FL",  label: "Freela" },
  ];

  return (
    <div>
      {/* v8.2 — Legenda no topo, mini-células com mesmo visual da grid */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ color: "var(--text3)", fontSize: 11, fontFamily: "'DM Sans',sans-serif", marginRight: 4 }}>Legenda:</span>
        {LEGEND_ALL.map(s => (
          <div key={s.code} style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{
              minWidth: 26, height: 22, borderRadius: 5, background: s.bg, color: "#fff",
              fontSize: 10, fontWeight: 700, fontFamily: "'DM Mono',monospace",
              display: "flex", alignItems: "center", justifyContent: "center", padding: "0 6px",
            }}>{s.code}</div>
            <span style={{ color: "var(--text2)", fontSize: 11, fontFamily: "'DM Sans',sans-serif" }}>{s.label}</span>
          </div>
        ))}
      </div>

      {/* Cabeçalho dias da semana */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 4 }}>
        {WEEKDAYS.map(w => <div key={w} style={{ textAlign: "center", color: "var(--text3)", fontSize: 11, fontFamily: "'DM Sans',sans-serif", fontWeight: 600, padding: "4px 0", letterSpacing: 0.3 }}>{w}</div>)}
      </div>

      {/* Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
        {cells.map((dateStr, idx) => {
          if (!dateStr) return <div key={`e-${idx}`} />;
          const d = parseInt(dateStr.slice(-2));
          const st = styleOf(dateStr);
          const hasDelay = delayMap && (delayMap[String(d)] > 0);
          const inv = dayInversionsByDate?.[dateStr];
          const invTip = inv ? `↔️ Dia invertido com ${new Date(inv.otherDate+"T12:00:00").toLocaleDateString("pt-BR")}${inv.reason?` · Motivo: ${inv.reason}`:""}` : "";
          return (
            <button key={dateStr} onClick={() => !readOnly && onDayClick && onDayClick(dateStr)}
              title={`${st.label}${hasDelay ? ` · Atraso ${delayMap[String(d)]} min` : ""}${invTip ? `\n${invTip}` : ""}`}
              style={{
                aspectRatio: "1", borderRadius: 8, border: "none",
                background: st.bg, color: "#fff",
                cursor: readOnly ? "default" : "pointer",
                fontFamily: "'DM Sans',sans-serif", padding: 0, position: "relative",
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                transition: "transform .1s",
              }}
              onMouseEnter={e => { if (!readOnly) e.currentTarget.style.transform = "scale(1.03)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}
            >
              {/* Número do dia no canto superior, sutil */}
              <span style={{ position: "absolute", top: 4, left: 6, fontSize: 10, fontWeight: 600, opacity: 0.85, fontFamily: "'DM Mono',monospace" }}>{d}</span>
              {/* Código do status no centro, bold */}
              <span style={{ fontSize: 16, fontWeight: 800, fontFamily: "'DM Mono',monospace", letterSpacing: 0.5, lineHeight: 1 }}>{st.code}</span>
              {hasDelay && (
                <span style={{ position: "absolute", top: 3, right: 4, fontSize: 8, lineHeight: 1, background: "#fff", color: "#f59e0b", borderRadius: 3, padding: "1px 3px", fontWeight: 800 }}>⏰</span>
              )}
              {inv && (
                <span style={{ position: "absolute", bottom: 3, right: 4, fontSize: 8, lineHeight: 1, background: "#fff", color: "#f59e0b", borderRadius: 3, padding: "1px 4px", fontWeight: 800 }}>↔</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Cycle order: work > off > freela > comp > faultJ > faultU > work (férias só via formulário)
const DAY_CYCLE = [DAY_OFF, DAY_FREELA, DAY_COMP, DAY_COMP_TRAB, DAY_FAULT_J, DAY_FAULT_U];

//
function loadScript(src) {
  return new Promise((res, rej) => {
    if (document.querySelector(`script[src="${src}"]`)) { res(); return; }
    const s = document.createElement("script");
    s.src = src; s.onload = res; s.onerror = rej;
    document.head.appendChild(s);
  });
}

//
function ExportModal({ onClose, employees, roles, tips, restaurant }) {
  const now = new Date();
  const [dateFrom, setDateFrom] = useState(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-01`);
  const [dateTo, setDateTo] = useState(today());
  const [status, setStatus] = useState("");
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewFileName, setPreviewFileName] = useState("");
  function buildMatrix() {
    const filtered = tips.filter(t => t.restaurantId === restaurant.id && t.date >= dateFrom && t.date <= dateTo);
    const dates = [...new Set(filtered.map(t => t.date))].sort();
    const rows = employees.filter(e => e.restaurantId === restaurant.id).map(emp => {
      const role = roles.find(r => r.id === emp.roleId);
      const byDay = {};
      dates.forEach(d => { const t = filtered.find(t => t.employeeId === emp.id && t.date === d); byDay[d] = t ? t.myShare : 0; });
      const totalBruto = Object.values(byDay).reduce((a, v) => a + v, 0);
      return { name: emp.name, role: role?.name ?? "—", area: role?.area ?? "—", byDay, totalBruto, deducao: totalBruto * TAX, liquido: totalBruto * (1 - TAX) };
    });
    const dayTotals = {};
    dates.forEach(d => { dayTotals[d] = rows.reduce((a, r) => a + (r.byDay[d] ?? 0), 0); });
    const grandBruto = rows.reduce((a, r) => a + r.totalBruto, 0);
    return { dates, rows, dayTotals, grandBruto, grandDeducao: grandBruto * TAX, grandLiquido: grandBruto * (1 - TAX) };
  }

  async function exportExcel() {
    setStatus("loading");
    try {
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js");
      const XLSX = window.XLSX;
      const { dates, rows, dayTotals, grandBruto, grandDeducao, grandLiquido } = buildMatrix();
      const ds = (d) => new Date(d + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
      const header = ["Nome", "Cargo", "Área", ...dates.map(ds), "Total Bruto", "Dedução (33%)", "Líquido"];
      const wsData = [
        [`${restaurant.name} — Relatório de Gorjetas: ${fmtDate(dateFrom)} a ${fmtDate(dateTo)}`],
        [],
        header,
        ...rows.map(r => [r.name, r.role, r.area, ...dates.map(d => r.byDay[d] ?? 0), r.totalBruto, r.deducao, r.liquido]),
        [],
        ["TOTAL DO DIA", "", "", ...dates.map(d => dayTotals[d]), grandBruto, grandDeducao, grandLiquido],
      ];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws["!cols"] = [{ wch: 28 }, { wch: 18 }, { wch: 12 }, ...dates.map(() => ({ wch: 10 })), { wch: 14 }, { wch: 14 }, { wch: 14 }];
      ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: header.length - 1 } }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Gorjetas");
      XLSX.writeFile(wb, `gorjetas_${restaurant.name}_${dateFrom}_${dateTo}.xlsx`);
      setStatus("done");
    } catch (e) { console.error(e); setStatus("error"); }
  }

  async function exportPDF() {
    setStatus("loading");
    try {
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js");
      const { jsPDF } = window.jspdf;
      const { dates, rows, dayTotals, grandBruto, grandDeducao, grandLiquido } = buildMatrix();
      const ds = (d) => new Date(d + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

      // A4 landscape = 297mm wide, margins 10mm each side = 277mm usable
      const pageW = 277;
      // Fixed columns: Nome(32) Cargo(28) Área(16) TotalBruto(22) Deducao(20) Liquido(22) = 140mm
      const fixedW = 140;
      // Remaining for day columns
      const dayW = Math.max(10, Math.floor((pageW - fixedW) / Math.max(dates.length, 1)));

      doc.setFontSize(13); doc.setFont("helvetica", "bold");
      doc.text(restaurant.name, 10, 12);
      doc.setFontSize(9); doc.setFont("helvetica", "normal");
      doc.text(`Relatório de Gorjetas: ${fmtDate(dateFrom)} a ${fmtDate(dateTo)}   |   Gerado em: ${new Date().toLocaleDateString("pt-BR")}`, 10, 19);

      const head = [["Nome", "Cargo", "Área", ...dates.map(ds), "Total Bruto", "Dedução", "Líquido"]];
      const body = [
        ...rows.map(r => [r.name, r.role, r.area, ...dates.map(d => r.byDay[d] ? fmtBR(r.byDay[d]) : "–"), fmtBR(r.totalBruto), fmtBR(r.deducao), fmtBR(r.liquido)]),
        ["TOTAL", "", "", ...dates.map(d => dayTotals[d] ? fmtBR(dayTotals[d]) : "–"), fmtBR(grandBruto), fmtBR(grandDeducao), fmtBR(grandLiquido)],
      ];

      doc.autoTable({
        head, body,
        startY: 23,
        margin: { left: 10, right: 10 },
        styles: {
          fontSize: dates.length > 20 ? 6 : 7,
          font: "helvetica",
          cellPadding: { top: 2, bottom: 2, left: 2, right: 2 },
          overflow: "linebreak",  // allow name/role to wrap
          lineColor: [200, 200, 200],
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: [30, 30, 30],
          textColor: [245, 200, 66],
          fontStyle: "bold",
          halign: "center",
          overflow: "hidden",
        },
        alternateRowStyles: { fillColor: [248, 248, 248] },
        columnStyles: {
          0: { cellWidth: 32, overflow: "linebreak" },   // Nome — pode quebrar
          1: { cellWidth: 28, overflow: "linebreak" },   // Cargo — pode quebrar
          2: { cellWidth: 16, halign: "center" },        // Área
          // Day columns — fixed width, no wrap
          ...Object.fromEntries(dates.map((_, i) => [i + 3, { cellWidth: dayW, halign: "right", overflow: "hidden" }])),
          [dates.length + 3]: { cellWidth: 22, halign: "right", fontStyle: "bold", overflow: "hidden" },
          [dates.length + 4]: { cellWidth: 20, halign: "right", textColor: [180, 40, 40], overflow: "hidden" },
          [dates.length + 5]: { cellWidth: 22, halign: "right", textColor: [10, 130, 60], fontStyle: "bold", overflow: "hidden" },
        },
        didParseCell: (data) => {
          if (data.row.index === body.length - 1) {
            data.cell.styles.fillColor = [30, 30, 30];
            data.cell.styles.textColor = [245, 200, 66];
            data.cell.styles.fontStyle = "bold";
          }
        },
      });

      doc.setFontSize(7); doc.setTextColor(150);
      doc.text("* Valores brutos sem dedução fiscal. Documento gerado pelo sistema.", 10, doc.lastAutoTable.finalY + 5);
      setPreviewDoc(doc); setPreviewFileName(`gorjetas_${restaurant.name}_${dateFrom}_${dateTo}.pdf`);
      setStatus("done");
    } catch (e) { console.error(e); setStatus("error"); }
  }

  const preview = dateFrom && dateTo && dateFrom <= dateTo ? (() => {
    const f = tips.filter(t => t.restaurantId === restaurant.id && t.date >= dateFrom && t.date <= dateTo);
    return { dias: [...new Set(f.map(t => t.date))].length, emps: [...new Set(f.map(t => t.employeeId))].length, total: f.reduce((a, t) => a + t.myShare, 0) };
  })() : null;

  return (
    <Modal title={`📤 Exportar — ${restaurant.name}`} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div><label style={S.label}>Data Inicial</label><input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={S.input} /></div>
          <div><label style={S.label}>Data Final</label><input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={S.input} /></div>
        </div>
        {preview && (
          <div style={{ background: "var(--bg1)", borderRadius: 10, padding: 12, fontSize: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", color: "var(--text2)", marginBottom: 3 }}><span>Dias com gorjeta</span><span style={{ color: "var(--text)" }}>{preview.dias}</span></div>
            <div style={{ display: "flex", justifyContent: "space-between", color: "var(--text2)", marginBottom: 3 }}><span>Empregados</span><span style={{ color: "var(--text)" }}>{preview.emps}</span></div>
            <div style={{ display: "flex", justifyContent: "space-between", color: "var(--text2)" }}><span>Total bruto</span><span style={{ color: "var(--ac)", fontWeight: 700 }}>{fmt(preview.total)}</span></div>
          </div>
        )}
        {status === "loading" && <p style={{ color: "var(--ac)", textAlign: "center", fontSize: 13 }}>⏳ Gerando arquivo…</p>}
        {status === "done"    && <p style={{ color: "var(--green)", textAlign: "center", fontSize: 13 }}>✅ Arquivo salvo nos seus downloads!</p>}
        {status === "error"   && <p style={{ color: "var(--red)", textAlign: "center", fontSize: 13 }}>❌ Erro ao gerar. Tente novamente.</p>}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <button onClick={exportExcel} disabled={status === "loading" || !preview} style={{ ...S.btnPrimary, background: "#217346", color: "var(--text)", opacity: !preview ? 0.5 : 1 }}>📊 Excel</button>
          <button onClick={exportPDF}   disabled={status === "loading" || !preview} style={{ ...S.btnPrimary, background: "#c0392b", color: "var(--text)", opacity: !preview ? 0.5 : 1 }}>📄 PDF</button>
        </div>
        <button onClick={onClose} style={{ ...S.btnSecondary, textAlign: "center" }}>Fechar</button>
      </div>
      {previewDoc && <PDFPreviewModal pdfDoc={previewDoc} fileName={previewFileName} title="Relatório de Gorjetas" onClose={()=>setPreviewDoc(null)} />}
    </Modal>
  );
}

//
// EMPLOYEE PORTAL
//
//
// COMUNICADOS TAB (employee view)
//
function ComunicadosTab({ empId, restaurantId, communications, commAcks, onUpdate, roles, emp }) {
  const empRole = roles?.find(r => r.id === emp?.roleId);
  const myComms = communications.filter(c => {
    if (c.restaurantId !== restaurantId || c.deleted) return false;
    if (!c.target || c.target === "all") return true;
    if (c.target.startsWith("emps:")) return c.target.replace("emps:","").split(",").includes(empId);
    if (c.target.startsWith("areas:")) return empRole && c.target.replace("areas:","").split(",").includes(empRole.area);
    if (c.target === `emp:${empId}`) return true;
    if (c.target.startsWith("area:") && empRole) return c.target === `area:${empRole.area}`;
    return false;
  }).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const pending = myComms.filter(c => !commAcks?.[c.id]?.[empId]);
  const done    = myComms.filter(c =>  commAcks?.[c.id]?.[empId]);
  const [tab, setTab] = useState("pending");
  const ac = "var(--ac)";

  function ack(commId) {
    const now = new Date().toISOString();
    onUpdate("commAcks", {
      ...commAcks,
      [commId]: { ...(commAcks?.[commId] ?? {}), [empId]: now }
    });
  }

  const list = tab === "pending" ? pending : done;

  return (
    <div>
      {pending.length > 0 && (
        <div style={{ background: "#e74c3c22", border: "1px solid #e74c3c44", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: "var(--red)", fontFamily: "'DM Mono',monospace" }}>
          ⚠️ Você tem <strong>{pending.length}</strong> comunicado{pending.length > 1 ? "s" : ""} pendente{pending.length > 1 ? "s" : ""} de ciência.
        </div>
      )}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {[["pending", `Pendentes (${pending.length})`], ["done", `Lidos (${done.length})`]].map(([id, lbl]) => (
          <button key={id} onClick={() => setTab(id)} style={{ flex: 1, padding: "8px", borderRadius: 10, border: `1px solid ${tab === id ? ac : "var(--border)"}`, background: tab === id ? ac + "22" : "transparent", color: tab === id ? ac : "#555", cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 12 }}>{lbl}</button>
        ))}
      </div>
      {list.length === 0 && <p style={{ color: "var(--text3)", textAlign: "center", fontSize: 14 }}>Nenhum comunicado {tab === "pending" ? "pendente" : "lido"}.</p>}
      {list.map(c => (
        <div key={c.id} style={{ background: "var(--card-bg)", borderRadius: 14, padding: 16, marginBottom: 12, border: `1px solid ${!commAcks?.[c.id]?.[empId] ? "#e74c3c44" : "var(--border)"}` }}>
          <div style={{ color: "var(--text)", fontWeight: 700, fontSize: 15, marginBottom: 6 }}>{c.title}</div>
          <div style={{ color: "var(--text2)", fontSize: 13, marginBottom: 12, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{c.body}</div>
          <div style={{ color: "var(--text3)", fontSize: 11, marginBottom: commAcks?.[c.id]?.[empId] ? 0 : 12 }}>
            Publicado em {fmtDate(c.createdAt?.slice(0,10))}
            {commAcks?.[c.id]?.[empId] && <span style={{ color: "var(--green)", marginLeft: 12 }}>✓ Ciência em {new Date(commAcks[c.id][empId]).toLocaleString("pt-BR")}</span>}
          </div>
          {!commAcks?.[c.id]?.[empId] && (
            <button onClick={() => ack(c.id)} style={{ width: "100%", padding: "11px", borderRadius: 10, background: "var(--ac)", border: "none", color: "#111", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "'DM Mono',monospace" }}>
              ✓ Dar Ciência
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

//
// FAQ TAB (employee view)
//
function FaqTab({ restaurantId, faq, emp, roles, restaurants, splits, employees }) {
  const items = (faq?.[restaurantId] ?? []).filter(item => item.visible !== false);
  const [openSys, setOpenSys] = useState(null);
  const [openRest, setOpenRest] = useState(null);

  const rest = restaurants?.find(r => r.id === restaurantId);
  const empRole = roles?.find(r => r.id === emp?.roleId);
  const restRolesComGorjeta = (roles ?? []).filter(r => r.restaurantId === restaurantId && !r.inactive && !r.noTip);
  const restRolesSemGorjeta = (roles ?? []).filter(r => r.restaurantId === restaurantId && !r.inactive && r.noTip);
  const taxRate = rest?.taxRate ?? TAX;
  const taxLabel = taxRate === 0.20 ? "20% (Simples Nacional)" : "33% (Lucro Real/Presumido)";
  const splitType = (rest?.divisionMode ?? MODE_AREA_POINTS) === MODE_AREA_POINTS ? "area" : "points";
  const ac = "var(--ac)";

  const now = new Date();
  // v7.9: lê a regra ativa via getSplitForDate (suporta formato versionado novo + legacy).
  // Antes lia direto splits[rid][mk], que só funcionava no legacy → caía no DEFAULT_SPLIT
  // ignorando a regra configurada no Wizard de Regra de Divisão.
  const todayStr = now.toISOString().slice(0, 10);
  const activeSplitVersion = getSplitForDate(splits?.[restaurantId], todayStr);
  // Normaliza percentages — cobre tipo "perEmployee" usando contagem real de empregados ativos.
  const empsPerAreaForFaq = {};
  (employees || [])
    .filter(e => e.restaurantId === restaurantId && !(e.inactive && e.inactiveFrom && e.inactiveFrom <= todayStr) && !e.isFreela)
    .forEach(e => {
      const r = (roles || []).find(x => x.id === e.roleId);
      const area = r?.area;
      if (area) empsPerAreaForFaq[area] = (empsPerAreaForFaq[area] || 0) + 1;
    });
  const curSplit = activeSplitVersion?.percentages
    ? computeAreaPercentages(activeSplitVersion.percentages, empsPerAreaForFaq)
    : DEFAULT_SPLIT;

  const pontosCargo = parseFloat(empRole?.points) || 0;
  const totalPontos = restRolesComGorjeta.reduce((s,r) => s+(parseFloat(r.points)||0), 0);
  const empArea = emp?.area ?? empRole?.area ?? "—";
  const cargosEmpArea = restRolesComGorjeta.filter(r => r.area === empArea);
  const totalPtsArea = cargosEmpArea.reduce((s,r) => s+(parseFloat(r.points)||0), 0);
  const pctArea = curSplit[empArea] ?? 0;
  const EX = 1000;

  function fmtR(n) { return n.toLocaleString("pt-BR",{minimumFractionDigits:2,maximumFractionDigits:2}); }

  const FAQ_SISTEMA = [
    {
      id:"__gorjeta__",
      q:"💸 Como é calculada a minha gorjeta?",
      a:(() => {
        if (!empRole) return "Cargo não identificado. Fale com o gestor.";
        if (empRole.noTip) return `Seu cargo (${empRole.name}) não participa da distribuição de gorjetas.`;
        if (splitType==="area") {
          const pool = EX*(pctArea/100);
          const bruto = totalPtsArea>0 ? (pool/totalPtsArea)*pontosCargo : 0;
          const liq = bruto * (1-taxRate);
          return `Modo atual: Área + Pontos
A gorjeta total do dia é primeiro dividida pelo percentual de cada área. Depois, dentro da sua área, é dividida pelos pontos dos cargos.

📌 Seu cargo: ${empRole.name} — ${pontosCargo} ponto${pontosCargo!==1?"s":""}
📌 Sua área: ${empArea} — recebe ${pctArea}% do total
📌 Dedução: ${taxLabel}

Passo a passo:
1. Gestor lança o valor total do dia
2. Sistema separa o valor por área conforme os percentuais configurados
3. Sua área (${empArea}) recebe ${pctArea}% do total
4. Dentro da área, divide pelos pontos de quem trabalhou
5. Multiplica pelos seus pontos → bruto
6. Deduz ${(taxRate*100).toFixed(0)}% → líquido

Exemplo (gorjeta R$${fmtR(EX)}):
• ${empArea} recebe ${pctArea}% → R$${fmtR(pool)}
• Pontos na área: ${totalPtsArea}pt · Seus: ${pontosCargo}pt
• Seu bruto: R$${fmtR(bruto)}
• Após ${(taxRate*100).toFixed(0)}%: R$${fmtR(liq)} líquido`;
        } else {
          const vpp = totalPontos > 0 ? EX/totalPontos : 0;
          const bruto = vpp * pontosCargo;
          const liq = bruto * (1-taxRate);
          return `Modo atual: Pontos Global
A gorjeta total do dia é somada e dividida diretamente pelos pontos de todos os empregados do restaurante que trabalharam no dia, sem separação por área.

📌 Seu cargo: ${empRole.name} — ${pontosCargo} ponto${pontosCargo!==1?"s":""}
📌 Dedução: ${taxLabel}

Passo a passo:
1. Gestor lança o valor total do dia
2. Sistema soma os pontos de todos que trabalharam no dia
3. Divide o total pelos pontos → valor por ponto
4. Multiplica pelos seus pontos → bruto
5. Deduz ${(taxRate*100).toFixed(0)}% → líquido

Exemplo (gorjeta R$${fmtR(EX)}, ${totalPontos}pt no total):
• Valor por ponto: R$${fmtR(vpp)}
• Seus ${pontosCargo}pt → R$${fmtR(bruto)} bruto
• Após ${(taxRate*100).toFixed(0)}%: R$${fmtR(liq)} líquido`;
        }
      })(),
    },
    {
      id:"__sistema__",
      q: splitType==="area" ? "🏢 Como funciona a divisão por área e pontos?" : "📊 Como funciona a tabela de pontos?",
      a:(() => {
        if (splitType==="area") {
          const AREAS = ["Bar","Cozinha","Salão","Limpeza"];
          const ativas = AREAS.filter(a => (curSplit[a]??0)>0);
          const linhas = ativas.map(a => {
            const pct = curSplit[a]??0;
            const cargos = restRolesComGorjeta.filter(r=>r.area===a);
            const pts = cargos.reduce((s,r)=>s+(parseFloat(r.points)||0),0);
            const cs = cargos.length>0 ? cargos.map(r=>`   • ${r.name}: ${r.points}pt`).join("\n") : "   (sem cargos)";
            return `${a} — ${pct}%\n${cs}\n   Total: ${pts}pt`;
          }).join("\n\n");
          const pool = EX*(pctArea/100);
          const bruto = totalPtsArea>0 ? (pool/totalPtsArea)*pontosCargo : 0;
          return `Sistema atual: Área + Pontos\n\nO valor total da gorjeta do dia é primeiro dividido pelo percentual configurado de cada área. Depois, dentro de cada área, o valor é dividido pelos pontos dos cargos dos empregados presentes.\n\nDistribuição por área:\n${linhas}${restRolesSemGorjeta.length>0?"\n\nCargos sem gorjeta: "+restRolesSemGorjeta.map(r=>r.name).join(", "):""}\n\nSua situação (área: ${empArea}):\n• ${empArea} recebe ${pctArea}% → R$${fmtR(pool)} (de R$${fmtR(EX)})\n• Você tem ${pontosCargo}pt de ${totalPtsArea}pt da área\n• Bruto: R$${fmtR(bruto)} → Líquido: R$${fmtR(bruto*(1-taxRate))}`;
        } else {
          const linhas = restRolesComGorjeta
            .sort((a,b)=>(parseFloat(b.points)||0)-(parseFloat(a.points)||0))
            .map(r=>{
              const pct = totalPontos>0 ? ((parseFloat(r.points)||0)/totalPontos*100).toFixed(1):"0";
              const d = r.id===empRole?.id?" ◄ você":"";
              return `• ${r.name}: ${r.points}pt (${pct}%)${d}`;
            }).join("\n");
          return `Sistema atual: Pontos Global\n\nTodos os pontos de todos os empregados do restaurante que trabalharam no dia são somados. O valor total da gorjeta é dividido por essa soma, e cada empregado recebe proporcionalmente aos pontos do seu cargo. Não há separação por área.\n\nTabela de cargos (${totalPontos}pt total):\n${linhas}${restRolesSemGorjeta.length>0?"\n\nCargos sem gorjeta: "+restRolesSemGorjeta.map(r=>r.name).join(", "):""}\n\nQuanto maior a pontuação, maior a fatia da gorjeta.`;
        }
      })(),
    },
    {
      id:"__escala__",
      tabKey: "escala",
      q:"📅 Como funciona a escala e por que ela importa?",
      a:"A escala registra sua presença em cada dia e define diretamente se você recebe gorjeta.\n\nVocê recebe gorjeta quando:\n✅ Trabalhando normalmente\n✅ Compensação de banco de horas (C)\n\nVocê NÃO recebe gorjeta quando:\n❌ Folga\n❌ Freela (FL) — presente cobrindo a equipe, mas sem gorjeta\n❌ Falta injustificada (F) — além de não receber, pode haver penalidade\n❌ Falta justificada (FJ)\n❌ Atestado médico (A)\n❌ Férias (V) — nenhum empregado recebe gorjeta durante férias, incluindo produção\n\nExceções:\n• Empregados de produção (🏭) recebem gorjeta todos os dias, exceto férias\n• Empregados freela (🎯) nunca participam do rateio de gorjeta\n\nSe notar algum erro na sua escala, avise o gestor o quanto antes — erros afetam diretamente o valor que você recebe.",
    },
    {
      id:"__producao__",
      tabKey: null,
      q:"🏭 O que é empregado de produção?",
      a:(() => {
        const penU = rest?.producaoPenaltyU ?? 6.66;
        const penJ = rest?.producaoPenaltyJ ?? 3.33;
        const isEmpProd = emp?.isProducao;
        return `Empregados marcados como "Produção" têm regras especiais de gorjeta:\n\n✅ Recebem gorjeta TODOS os dias (trabalhando, folga, compensação, etc.)\n❌ NÃO recebem gorjeta durante férias — assim como qualquer outro empregado\n✅ A distribuição segue os pontos do cargo normalmente\n\n⚠️ Penalidades por falta (sobre o pool mensal, por dia):\n• Falta injustificada: ${penU}% por dia\n• Falta justificada: ${penJ}% por dia\n\nExemplo: pool mensal de R$${fmtR(10000)}, 2 faltas injustificadas + 1 justificada → desconto de ${penU*2}% + ${penJ}% = ${penU*2+penJ}% = R$${fmtR(10000*(penU*2+penJ)/100)}\n\n${isEmpProd ? "📌 Você está marcado como empregado de produção. Essas regras se aplicam a você." : "Essas regras só se aplicam a empregados marcados como produção pelo gestor."}\n\nO status de produção é definido pelo gestor na aba Equipe e pode ser atribuído a empregados de qualquer área.`;
      })(),
    },
    {
      id:"__freela__",
      tabKey: null,
      q:"🎯 O que é empregado freela?",
      a:(() => {
        const isEmpFreela = emp?.isFreela;
        return `Empregados marcados como "Freela" são colaboradores que cobrem a equipe esporadicamente:\n\n✅ Aparecem normalmente na escala\n❌ Nunca participam do rateio de gorjeta, independente do status na escala\n✅ O cargo e área são atribuídos normalmente\n\nAlém disso, qualquer empregado pode receber o status "Freela" (FL) em um dia específico da escala. Nesse caso, ele está presente mas não recebe gorjeta naquele dia — útil para quem estava de folga e resolveu cobrir.\n\n${isEmpFreela ? "📌 Você está marcado como empregado freela. Você não participa do rateio de gorjeta." : "Essas regras só se aplicam a empregados marcados como freela pelo gestor."}\n\nO status de freela é definido pelo gestor na aba Equipe.`;
      })(),
    },
    {
      id:"__dp__",
      tabKey: "dp",
      q:"💬 Para que serve o Fale com DP?",
      a:"Canal direto entre você e o departamento pessoal do restaurante. Use para:\n\n• Dúvidas trabalhistas (férias, horas extras, INSS, FGTS...)\n• Entrega de atestados e justificativas de falta\n• Solicitação de documentos (holerite, declaração de vínculo...)\n• Sugestões e elogios\n• Denúncias — você pode enviar de forma totalmente anônima\n\nO gestor responsável pelo DP responde diretamente pelo aplicativo.",
    },
    {
      id:"__comunicados__",
      tabKey: "comunicados",
      q:"📢 Como funcionam os comunicados?",
      a:"Avisos enviados pelo gestor para a equipe ou grupos específicos.\n\nQuando chegar um comunicado novo:\n• Você recebe uma notificação\n• Leia o comunicado completo\n• Confirme clicando em \"Li e entendi\"\n\nO gestor acompanha quem confirmou. É importante confirmar — comunicados podem conter informações sobre escalas, regras e avisos importantes do restaurante.",
    },
    {
      id:"__pin__",
      tabKey: null,
      q:"🔐 O que é o PIN e como trocar?",
      a:"O PIN é sua senha de acesso ao sistema — um código de 4 dígitos numéricos.\n\nPara fazer login use:\n• Seu ID de empregado (ex: LBZ0005) ou CPF\n• Seu PIN de 4 dígitos\n\nNo primeiro acesso o sistema pedirá que você crie um PIN pessoal.\n\nPara trocar o PIN depois: solicite ao seu gestor que faça o reset. Após o reset, você deverá criar um novo PIN no próximo acesso.\n\nNunca compartilhe seu PIN com ninguém.",
    },
  ].filter(item => {
    // v8.9.4: visibilidade do item de FAQ é INDEPENDENTE da aba (tab pode estar oculta e o FAQ
    // continuar visível, ou tab visível e o FAQ ocultado individualmente).
    if (rest?.tabsGestor?.faqAuto?.[safeFaqKey(item.id)] === false) return false;
    return true;
  });

  return (
    <div style={{paddingBottom:20}}>

      <div style={{padding:"12px 16px 8px"}}>
        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
          <span style={{fontSize:14}}>📐</span>
          <span style={{color:ac,fontSize:12,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5}}>Regras do sistema</span>
        </div>
        <p style={{color:"var(--text3)",fontSize:11,margin:"0 0 8px"}}>Geradas automaticamente com as regras do seu restaurante</p>
      </div>
      {FAQ_SISTEMA.map((item,i) => (
        <div key={item.id} style={{background:"var(--card-bg)",borderRadius:12,marginBottom:6,border:"1px solid var(--ac)22",overflow:"hidden",marginInline:8}}>
          <button onClick={()=>setOpenSys(openSys===i?null:i)}
            style={{width:"100%",padding:"12px 14px",background:openSys===i?"var(--ac-bg)":"transparent",border:"none",color:openSys===i?ac:"var(--text)",textAlign:"left",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:600,display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
            <span>{item.q}</span>
            <span style={{fontSize:16,color:"var(--text3)",flexShrink:0}}>{openSys===i?"−":"+"}</span>
          </button>
          {openSys===i && (
            <div style={{padding:"10px 14px 14px",color:"var(--text2)",fontSize:12,lineHeight:1.75,borderTop:"1px solid var(--ac)22",whiteSpace:"pre-wrap",fontFamily:"'DM Mono',monospace"}}>
              {item.a}
              <div style={{marginTop:10,fontSize:10,color:"var(--text3)",fontStyle:"italic",fontFamily:"'DM Sans',sans-serif"}}>Atualizado automaticamente com as regras do restaurante.</div>
            </div>
          )}
        </div>
      ))}

      <div style={{padding:"20px 16px 8px"}}>
        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
          <span style={{fontSize:14}}>🏢</span>
          <span style={{color:"var(--text2)",fontSize:12,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5}}>Sobre o restaurante</span>
        </div>
        <p style={{color:"var(--text3)",fontSize:11,margin:"0 0 8px"}}>Perguntas e respostas cadastradas pelo gestor</p>
      </div>
      {items.length===0
        ? <p style={{color:"var(--text3)",textAlign:"center",fontSize:13,padding:"12px 0"}}>Nenhuma pergunta cadastrada pelo gestor ainda.</p>
        : items.map((item,i) => (
          <div key={item.id??i} style={{background:"var(--card-bg)",borderRadius:12,marginBottom:6,border:"1px solid var(--border)",overflow:"hidden",marginInline:8}}>
            <button onClick={()=>setOpenRest(openRest===i?null:i)}
              style={{width:"100%",padding:"12px 14px",background:"none",border:"none",color:openRest===i?ac:"var(--text)",textAlign:"left",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:600,display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
              <span>{item.q}</span>
              <span style={{fontSize:16,color:"var(--text3)",flexShrink:0}}>{openRest===i?"−":"+"}</span>
            </button>
            {openRest===i && (
              <div style={{padding:"10px 14px 14px",color:"var(--text2)",fontSize:13,lineHeight:1.7,borderTop:"1px solid var(--border)",whiteSpace:"pre-wrap"}}>
                {item.a}
              </div>
            )}
          </div>
        ))
      }
    </div>
  );
}

//
// FALE COM DP TAB (employee view)
//
function FaleDpTab({ empId, emp, restaurantId, dpMessages, onUpdate }) {
  const [category, setCategory] = useState("sugestao");
  const [body, setBody] = useState("");
  const [anon, setAnon] = useState(false);
  const [sent, setSent] = useState(false);
  const CATS = [["sugestao","💡 Sugestão"],["elogio","👏 Elogio"],["reclamacao","⚠️ Reclamação"],["denuncia","🚨 Denúncia"]];
  const ac = "var(--ac)";

  function send() {
    if (!body.trim()) return;
    const msg = {
      id: Date.now().toString(),
      restaurantId,
      empId: anon ? null : empId,
      empName: anon ? "Anônimo" : (emp?.name ?? "—"),
      category,
      body: body.trim(),
      date: new Date().toISOString(),
      read: false,
    };
    onUpdate("dpMessages", [...dpMessages, msg]);
    setBody(""); setSent(true);
    setTimeout(() => setSent(false), 3000);
  }

  return (
    <div>
      <p style={{ color: "var(--text3)", fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}>
        Envie sugestões, elogios, reclamações ou denúncias ao Departamento Pessoal. Você pode escolher se quer se identificar ou enviar de forma anônima.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
        {CATS.map(([val, lbl]) => (
          <button key={val} onClick={() => setCategory(val)} style={{ padding: "10px", borderRadius: 10, border: `1px solid ${category === val ? ac : "var(--border)"}`, background: category === val ? ac + "22" : "transparent", color: category === val ? ac : "#555", cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 12, fontWeight: category === val ? 700 : 400 }}>{lbl}</button>
        ))}
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={S.label}>Mensagem</label>
        <textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Escreva sua mensagem aqui…" rows={5} style={{ ...S.input, resize: "vertical", lineHeight: 1.5 }} />
      </div>
      <div style={{ marginBottom: 14 }}>
        <button onClick={() => setAnon(!anon)} style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer", fontFamily: "'DM Mono',monospace", color: anon ? ac : "#555", fontSize: 13, padding: 0 }}>
          <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${anon ? ac : "#555"}`, background: anon ? ac : "transparent", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#111" }}>{anon ? "✓" : ""}</div>
          Enviar de forma anônima
        </button>
        {!anon && <p style={{ color: "var(--text3)", fontSize: 11, marginTop: 4, marginLeft: 26 }}>Identificado como: <strong style={{ color: "var(--text2)" }}>{emp?.name}</strong></p>}
      </div>
      {sent && <p style={{ color: "var(--green)", fontSize: 13, marginBottom: 10 }}>✅ Mensagem enviada com sucesso!</p>}
      <button onClick={send} disabled={!body.trim()} style={{ width: "100%", padding: "12px", borderRadius: 12, background: body.trim() ? ac : "var(--border)", border: "none", color: "#111", fontWeight: 700, fontSize: 14, cursor: body.trim() ? "pointer" : "default", fontFamily: "'DM Mono',monospace" }}>
        Enviar Mensagem
      </button>

      {/* Direitos LGPD */}
      <div style={{marginTop:24,padding:"16px",borderRadius:12,border:"1px solid var(--border)",background:"var(--bg1)"}}>
        <p style={{color:"var(--text3)",fontSize:12,fontWeight:700,margin:"0 0 8px"}}>🔒 Seus direitos (LGPD)</p>
        <p style={{color:"var(--text3)",fontSize:11,margin:"0 0 12px",lineHeight:1.6}}>
          Pela Lei Geral de Proteção de Dados você pode solicitar acesso, correção ou exclusão dos seus dados pessoais a qualquer momento.
        </p>
        <button onClick={async ()=>{
          if(!await appConfirm("Confirma a solicitação de exclusão dos seus dados? O gestor receberá sua solicitação.")) return;
          const msg = {
            id: Date.now().toString(),
            restaurantId,
            empId,
            empName: emp?.name ?? "—",
            category: "denuncia",
            body: `[SOLICITAÇÃO LGPD] ${emp?.name} (${emp?.empCode}) solicita a exclusão de todos os seus dados pessoais do sistema, conforme direito garantido pela Lei nº 13.709/2018 (LGPD).`,
            date: new Date().toISOString(),
            read: false,
          };
          onUpdate("dpMessages", [...dpMessages, msg]);
          onUpdate("_toast", "✅ Solicitação enviada ao gestor.");
        }} style={{padding:"8px 16px",borderRadius:8,border:"1px solid #ef444433",background:"transparent",color:"var(--red)",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12}}>
          Solicitar exclusão dos meus dados
        </button>
        <button onClick={()=>window.__showPrivacy?.()} style={{display:"block",marginTop:8,background:"none",border:"none",color:"var(--text3)",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:11,padding:0,textDecoration:"underline"}}>
          Ver Política de Privacidade completa
        </button>
      </div>
    </div>
  );
}

//
// COMUNICADOS MANAGER TAB (manager/super view)
//
function ComunicadosManagerTab({ restaurantId, communications, commAcks, employees, onUpdate, currentManagerName, isOwner, trash }) {
  const myComms = communications.filter(c => c.restaurantId === restaurantId && !c.autoSchedule && !c.deleted)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const trashedComms = communications.filter(c => c.restaurantId === restaurantId && !c.autoSchedule && c.deleted)
    .sort((a, b) => (b.deletedAt||b.createdAt).localeCompare(a.deletedAt||a.createdAt));
  const [showTrash, setShowTrash] = useState(false);
  const restEmps = employees.filter(e => e.restaurantId === restaurantId && !(e.inactive && e.inactiveFrom && e.inactiveFrom <= today()));
  const [showNew, setShowNew] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody]   = useState("");
  const [selComm, setSelComm] = useState(null);
  const [selAreas, setSelAreas] = useState([]);
  const [selEmps, setSelEmps] = useState([]);
  const [aiInput, setAiInput] = useState("");
  const { generate: aiGenerate, aiLoading, aiError, setAiError } = useAiGenerate();

  async function handleAiSuggest() {
    if (!aiInput.trim()) return;
    const result = await aiGenerate(
      `Você é um assistente de comunicação interna de restaurantes. O gestor quer enviar um comunicado para a equipe. Redija de forma profissional, clara e direta. Responda APENAS com JSON: {"titulo": "título objetivo", "corpo": "texto completo do comunicado"}`,
      aiInput.trim()
    );
    if (result) { setTitle(result.titulo); setBody(result.corpo); setAiInput(""); }
  }

  // target logic: "all" | "areas:[Bar,Cozinha]" | "emps:[id1,id2]"
  function toggleArea(a) { setSelAreas(p => p.includes(a) ? p.filter(x=>x!==a) : [...p,a]); setSelEmps([]); }
  function toggleEmp(id) { setSelEmps(p => p.includes(id) ? p.filter(x=>x!==id) : [...p,id]); setSelAreas([]); }
  function selectAll() { setSelAreas([]); setSelEmps([]); }
  const isAll = selAreas.length===0 && selEmps.length===0;

  function publish() {
    if (!title.trim() || !body.trim()) return;
    let target = "all";
    if (selAreas.length > 0) target = `areas:${selAreas.join(",")}`;
    else if (selEmps.length > 0) target = `emps:${selEmps.join(",")}`;
    const c = { id: Date.now().toString(), restaurantId, title: title.trim(), body: body.trim(), createdAt: new Date().toISOString(), createdBy: currentManagerName, target };
    onUpdate("communications", [...communications, c]);
    setTitle(""); setBody(""); setSelAreas([]); setSelEmps([]); setShowNew(false);
  }

  async function softDelete(id) {
    if(!await appConfirm("Mover este comunicado para a lixeira?")) return;
    onUpdate("communications", communications.map(c => c.id === id ? { ...c, deleted: true, deletedAt: new Date().toISOString() } : c));
    onUpdate("_toast", "🗑️ Comunicado movido para a lixeira");
  }

  async function permanentDelete(id) {
    if(!await appConfirm("Apagar permanentemente este comunicado? Esta ação não pode ser desfeita.")) return;
    onUpdate("communications", communications.filter(c => c.id !== id));
    const newAcks = { ...commAcks };
    delete newAcks[id];
    onUpdate("commAcks", newAcks);
    onUpdate("_toast", "🗑️ Comunicado apagado permanentemente");
  }

  function restoreFromTrash(id) {
    onUpdate("communications", communications.map(c => c.id === id ? { ...c, deleted: false, deletedAt: undefined } : c));
    onUpdate("_toast", "♻️ Comunicado restaurado");
  }

  if (selComm) {
    const c = myComms.find(x => x.id === selComm);
    return (
      <div>
        <button onClick={() => setSelComm(null)} style={{ ...S.btnSecondary, marginBottom: 16 }}>← Voltar</button>
        <div style={{ ...S.card, marginBottom: 16 }}>
          <div style={{ color: "var(--text)", fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{c.title}</div>
          <div style={{ color: "var(--text3)", fontSize: 12, marginBottom: 4 }}>Publicado em {new Date(c.createdAt).toLocaleString("pt-BR")} por {c.createdBy}</div>
          <div style={{fontSize:11,color:"var(--text3)",marginBottom:12}}>→ {!c.target||c.target==="all"?"Todos":c.target.startsWith("emps:")?`${c.target.replace("emps:","").split(",").length} empregado(s)`:c.target.startsWith("areas:")?`Áreas: ${c.target.replace("areas:","").replace(/,/g,", ")}`:c.target.startsWith("emp:")?shortName(employees.find(e=>e.id===c.target.replace("emp:",""))?.name)||"":`Área ${c.target.replace("area:","")}`}</div>
          <div style={{ color: "var(--text2)", fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap", marginBottom: 12 }}>{c.body}</div>
        </div>
        {(() => {
          const targetLabel = !c.target || c.target==="all" ? "Todos os empregados"
            : c.target.startsWith("emps:") ? `Empregados: ${c.target.replace("emps:","").split(",").map(id=>shortName(employees.find(e=>e.id===id)?.name)||"").filter(Boolean).join(", ")}`
            : c.target.startsWith("areas:") ? `Áreas: ${c.target.replace("areas:","").replace(/,/g,", ")}`
            : c.target.startsWith("emp:") ? `Empregado: ${employees.find(e=>e.id===c.target.replace("emp:",""))?.name??""}`
            : `Área: ${c.target.replace("area:","")}`;
          return (
            <>
              <p style={{ color: "var(--text3)", fontSize: 12, marginBottom: 4 }}>Destinatário: <strong style={{color:"var(--text2)"}}>{targetLabel}</strong></p>
              <p style={{ color: "var(--text3)", fontSize: 12, marginBottom: 10 }}>Tabela de ciências</p>
              <div style={{ ...S.card }}>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 6, marginBottom: 8, paddingBottom: 8, borderBottom: "1px solid var(--border)" }}>
                  {["Empregado", "Envio", "Ciência"].map(h => <div key={h} style={{ color: "var(--text3)", fontSize: 11 }}>{h}</div>)}
                </div>
                {restEmps.map(e => {
                  const ackDate = commAcks?.[c.id]?.[e.id];
                  return (
                    <div key={e.id} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 6, padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
                      <div style={{ color: "var(--text)", fontSize: 13 }}>{e.name}</div>
                      <div style={{ color: "var(--text3)", fontSize: 11 }}>{fmtDate(c.createdAt?.slice(0,10))}</div>
                      <div style={{ color: ackDate ? "var(--green)" : "var(--red)", fontSize: 11 }}>{ackDate ? new Date(ackDate).toLocaleDateString("pt-BR") : "Pendente"}</div>
                    </div>
                  );
                })}
                <div style={{ marginTop: 12, color: "var(--text3)", fontSize: 12 }}>
                  ✓ {restEmps.filter(e => commAcks?.[c.id]?.[e.id]).length} de {restEmps.length} confirmados
                </div>
              </div>
            </>
          );
        })()}
      </div>
    );
  }

  return (
    <div>
      <button onClick={() => { setShowNew(!showNew); setAiInput(""); setAiError(""); }} style={{ ...S.btnPrimary, marginBottom: 16 }}>
        {showNew ? "Cancelar" : "+ Novo Comunicado"}
      </button>
      {showNew && (
        <div style={{ ...S.card, marginBottom: 20 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {/* Destinatários */}
            <div>
              <label style={S.label}>Destinatários</label>
              {/* Todos button */}
              <div style={{marginBottom:8}}>
                <button onClick={selectAll} style={{padding:"5px 14px",borderRadius:20,border:`1px solid ${isAll?"var(--ac)":"var(--border)"}`,background:isAll?"var(--ac)22":"transparent",color:isAll?"var(--ac)":"var(--text3)",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12,fontWeight:isAll?700:400}}>
                  👥 Todos
                </button>
              </div>
              {/* Areas */}
              <div style={{marginBottom:6}}>
                <div style={{color:"var(--text3)",fontSize:10,marginBottom:4}}>ÁREAS</div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {AREAS.map(a=>{
                    const on = selAreas.includes(a);
                    return <button key={a} onClick={()=>toggleArea(a)} style={{padding:"4px 12px",borderRadius:20,border:`1px solid ${on?AREA_COLORS[a]??"#555":"var(--border)"}`,background:on?(AREA_COLORS[a]??"#555")+"22":"transparent",color:on?AREA_COLORS[a]??"var(--text)":"var(--text3)",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12,fontWeight:on?700:400}}>{a}</button>;
                  })}
                </div>
              </div>
              {/* Employees */}
              <div>
                <div style={{color:"var(--text3)",fontSize:10,marginBottom:4}}>EMPREGADOS ESPECÍFICOS</div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {restEmps.sort((a,b)=>a.name.localeCompare(b.name)).map(e=>{
                    const on = selEmps.includes(e.id);
                    return <button key={e.id} onClick={()=>toggleEmp(e.id)} style={{padding:"4px 12px",borderRadius:20,border:`1px solid ${on?"var(--green)":"var(--border)"}`,background:on?"#10b98122":"transparent",color:on?"var(--green)":"var(--text3)",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12,fontWeight:on?700:400}}>{shortName(e.name)}</button>;
                  })}
                </div>
              </div>
              {/* Summary */}
              <div style={{marginTop:8,fontSize:11,color:"var(--text3)"}}>
                {isAll ? "→ Enviando para todos os empregados"
                  : selAreas.length>0 ? `→ Áreas: ${selAreas.join(", ")}`
                  : `→ ${selEmps.length} empregado(s) selecionado(s)`}
              </div>
            </div>
            {/* Assistente IA */}
            <div style={{padding:"12px 14px",borderRadius:10,background:"var(--ac-bg)",border:"1px solid var(--ac)33"}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                <span style={{fontSize:14}}>✨</span>
                <span style={{color:"var(--ac-text)",fontWeight:700,fontSize:13}}>Assistente IA</span>
              </div>
              <p style={{color:"var(--text3)",fontSize:12,margin:"0 0 8px"}}>Descreva informalmente o que quer comunicar. A IA redige o título e o texto de forma profissional.</p>
              <textarea value={aiInput} onChange={e=>setAiInput(e.target.value)}
                placeholder='Ex: "lembrar a equipe que a escala de fim de semana mudou, sábado agora começa às 11h"'
                rows={2} style={{...S.input,resize:"vertical",marginBottom:8,fontSize:13}}/>
              {aiError && <p style={{color:"var(--red)",fontSize:12,margin:"0 0 8px"}}>{aiError}</p>}
              <button onClick={handleAiSuggest} disabled={!aiInput.trim()||aiLoading}
                style={{...S.btnPrimary,width:"auto",padding:"7px 18px",fontSize:13,opacity:(!aiInput.trim()||aiLoading)?0.6:1}}>
                {aiLoading?"✨ Gerando...":"✨ Sugerir com IA"}
              </button>
              {title && <p style={{color:"var(--text3)",fontSize:11,marginTop:8,marginBottom:0}}>↓ Sugestão gerada abaixo — edite à vontade antes de publicar</p>}
            </div>
            <div><label style={S.label}>Título</label><input value={title} onChange={e => setTitle(e.target.value)} placeholder="Título do comunicado" style={S.input} /></div>
            <div><label style={S.label}>Conteúdo</label><textarea value={body} onChange={e => setBody(e.target.value)} rows={5} placeholder="Texto do comunicado…" style={{ ...S.input, resize: "vertical" }} /></div>
            <button onClick={publish} style={{ ...S.btnPrimary }}>Publicar</button>
          </div>
        </div>
      )}
      {myComms.length === 0 && !showTrash && <p style={{ color: "var(--text3)", textAlign: "center" }}>Nenhum comunicado publicado.</p>}
      {!showTrash && myComms.map(c => {
        const ackCount = restEmps.filter(e => commAcks?.[c.id]?.[e.id]).length;
        return (
          <div key={c.id} style={{ ...S.card, marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ flex: 1 }}>
                <div style={{ color: "var(--text)", fontWeight: 600, marginBottom: 4 }}>{c.title}</div>
                <div style={{ color: "var(--text3)", fontSize: 12 }}>{fmtDate(c.createdAt?.slice(0,10))} · {ackCount}/{restEmps.length} ciências</div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setSelComm(c.id)} style={{ ...S.btnSecondary, fontSize: 12 }}>Ver ciências</button>
                <button onClick={() => softDelete(c.id)} style={{ background: "none", border: "1px solid #e74c3c33", borderRadius: 8, color: "var(--red)", cursor: "pointer", fontSize: 12, padding: "6px 12px", fontFamily: "'DM Mono',monospace" }}>🗑️</button>
              </div>
            </div>
          </div>
        );
      })}

      {/* Trash toggle */}
      {trashedComms.length > 0 && (
        <button onClick={() => setShowTrash(!showTrash)} style={{ ...S.btnSecondary, fontSize: 12, marginTop: 12, display: "flex", alignItems: "center", gap: 6 }}>
          🗑️ Lixeira ({trashedComms.length}) {showTrash ? "▲" : "▼"}
        </button>
      )}

      {/* Trash view */}
      {showTrash && trashedComms.map(c => (
        <div key={c.id} style={{ ...S.card, marginBottom: 10, marginTop: 10, opacity: 0.6, borderColor: "var(--border)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: "var(--text)", fontWeight: 600, marginBottom: 4 }}>{c.title}</div>
              <div style={{ color: "var(--text3)", fontSize: 12 }}>Apagado em {c.deletedAt ? new Date(c.deletedAt).toLocaleDateString("pt-BR") : "—"}</div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => restoreFromTrash(c.id)} style={{ ...S.btnSecondary, fontSize: 12 }}>♻️ Restaurar</button>
              {isOwner && <button onClick={() => permanentDelete(c.id)} style={{ background: "none", border: "1px solid #e74c3c33", borderRadius: 8, color: "var(--red)", cursor: "pointer", fontSize: 12, padding: "6px 12px", fontFamily: "'DM Mono',monospace" }}>✕ Apagar</button>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

//
// ── AI helper (Groq — Llama 3 via Vercel function) ───────────────────────────
// Antes (antes de v8.32) chamava direto a API do Groq com REACT_APP_GROQ_KEY no
// bundle público, expondo a chave. Agora vai por /api/ai/generate (server-side).
async function groqGenerate(systemPrompt, userInput) {
  let res;
  try {
    res = await fetch("/api/ai/generate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ systemPrompt, userInput }),
    });
  } catch (netErr) {
    console.error("[AI] Erro de rede:", netErr);
    throw new Error("Erro de conexão com a IA. Verifique sua internet e tente novamente.");
  }

  let payload;
  try {
    payload = await res.json();
  } catch (parseErr) {
    console.error("[AI] Resposta inválida do servidor:", res.status);
    throw new Error(`Resposta inválida do servidor (HTTP ${res.status}).`);
  }

  if (!res.ok || !payload.success) {
    const msg = payload?.error || `HTTP ${res.status}`;
    console.error("[AI] Erro:", { status: res.status, msg });
    if (res.status === 429) throw new Error("Limite de requisições da IA excedido. Aguarde alguns instantes.");
    throw new Error(`IA retornou erro: ${msg}`);
  }

  return payload.data;
}

// ── useMobile hook — unified mobile detection ──
function useMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return isMobile;
}

// ── useAiGenerate hook — wraps groqGenerate with loading/error state ──
function useAiGenerate() {
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");
  async function generate(systemPrompt, userInput) {
    setAiLoading(true); setAiError("");
    try {
      const result = await groqGenerate(systemPrompt, userInput);
      return result;
    } catch (e) {
      setAiError(e.message || "Não foi possível gerar sugestão. Tente novamente.");
      return null;
    } finally {
      setAiLoading(false);
    }
  }
  return { generate, aiLoading, aiError, setAiError };
}

// FAQ MANAGER TAB
//

function FaqManagerTab({ restaurantId, faq, onUpdate }) {
  const items = faq?.[restaurantId] ?? [];
  const [editIdx, setEditIdx] = useState(null);
  const [form, setForm] = useState({ q: "", a: "" });
  const [aiInput, setAiInput] = useState("");
  const { generate: aiGenerate, aiLoading, aiError, setAiError } = useAiGenerate();
  const ac = "var(--ac)";

  function saveItem() {
    if (!form.q.trim() || !form.a.trim()) return;
    const newItem = {
      id: editIdx === "new" ? Date.now().toString() : (items[editIdx]?.id ?? Date.now().toString()),
      q: form.q.trim(),
      a: form.a.trim(),
      visible: editIdx === "new" ? true : (items[editIdx]?.visible ?? true),
    };
    const newItems = editIdx === "new"
      ? [...items, newItem]
      : items.map((x, i) => i === editIdx ? { ...x, ...newItem } : x);
    onUpdate("faq", { ...faq, [restaurantId]: newItems });
    setEditIdx(null); setForm({ q: "", a: "" }); setAiInput(""); setAiError("");
  }

  function removeItem(i) {
    onUpdate("faq", { ...faq, [restaurantId]: items.filter((_, idx) => idx !== i) });
  }

  function toggleVisible(i) {
    const updated = items.map((x, idx) => idx === i ? { ...x, visible: x.visible === false ? true : false } : x);
    onUpdate("faq", { ...faq, [restaurantId]: updated });
  }

  async function handleAiSuggest() {
    if (!aiInput.trim()) return;
    const result = await aiGenerate(
      `Você é um assistente especializado em restaurantes. O gestor descreveu informalmente uma pergunta e resposta para o FAQ dos empregados. Reformule de forma clara, profissional e empática. Responda APENAS com JSON: {"q": "pergunta clara", "a": "resposta profissional"}`,
      aiInput.trim()
    );
    if (result) { setForm({ q: result.q, a: result.a }); setAiInput(""); }
  }

  const isEditing = editIdx !== null;

  return (
    <div>
      <button onClick={() => { setEditIdx("new"); setForm({ q: "", a: "" }); setAiInput(""); setAiError(""); }}
        style={{ ...S.btnPrimary, marginBottom: 16 }}>+ Nova Pergunta</button>

      {isEditing && (
        <div style={{ ...S.card, marginBottom: 16, border: "1px solid var(--ac)33" }}>
          {/* Assistente IA */}
          <div style={{ marginBottom: 14, padding: "12px 14px", borderRadius: 10, background: "var(--ac-bg)", border: "1px solid var(--ac)33" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
              <span style={{ fontSize: 16 }}>✨</span>
              <span style={{ color: "var(--ac-text)", fontWeight: 700, fontSize: 13 }}>Assistente IA</span>
            </div>
            <p style={{ color: "var(--text3)", fontSize: 12, margin: "0 0 8px" }}>
              Descreva informalmente a pergunta e resposta que quer criar. A IA redige de forma profissional.
            </p>
            <textarea
              value={aiInput}
              onChange={e => setAiInput(e.target.value)}
              placeholder='Ex: "quando o empregado falta sem avisar ele perde a gorjeta do dia e precisa justificar para o gestor"'
              rows={3}
              style={{ ...S.input, resize: "vertical", marginBottom: 8, fontSize: 13 }}
            />
            {aiError && <p style={{ color: "var(--red)", fontSize: 12, margin: "0 0 8px" }}>{aiError}</p>}
            <button onClick={handleAiSuggest} disabled={!aiInput.trim() || aiLoading}
              style={{ ...S.btnPrimary, width: "auto", padding: "8px 20px", fontSize: 13, opacity: (!aiInput.trim() || aiLoading) ? 0.6 : 1 }}>
              {aiLoading ? "✨ Gerando..." : "✨ Sugerir com IA"}
            </button>
            {form.q && <p style={{ color: "var(--text3)", fontSize: 11, marginTop: 8, marginBottom: 0 }}>↓ Sugestão gerada abaixo — edite à vontade antes de salvar</p>}
          </div>

          {/* Formulário */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div>
              <label style={S.label}>Pergunta</label>
              <input value={form.q} onChange={e => setForm(p => ({ ...p, q: e.target.value }))}
                placeholder="Ex: Como funciona o rateio de gorjeta?" style={S.input} />
            </div>
            <div>
              <label style={S.label}>Resposta</label>
              <textarea value={form.a} onChange={e => setForm(p => ({ ...p, a: e.target.value }))}
                rows={5} placeholder="Resposta detalhada…" style={{ ...S.input, resize: "vertical" }} />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={saveItem} style={{ ...S.btnPrimary, flex: 1 }}>Salvar</button>
              <button onClick={() => { setEditIdx(null); setAiInput(""); setAiError(""); }} style={S.btnSecondary}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {items.length === 0 && !isEditing && (
        <p style={{ color: "var(--text3)", textAlign: "center", padding: "20px 0" }}>Nenhuma pergunta cadastrada.</p>
      )}

      {items.map((item, i) => {
        const isVisible = item.visible !== false;
        return (
          <div key={item.id ?? i} style={{ ...S.card, marginBottom: 8, opacity: isVisible ? 1 : 0.6, border: `1px solid ${isVisible ? "var(--border)" : "var(--border)"}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: isVisible ? ac : "var(--text3)", fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{item.q}</div>
                <div style={{ color: "var(--text3)", fontSize: 12 }}>{item.a.slice(0, 80)}{item.a.length > 80 ? "…" : ""}</div>
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0, alignItems: "center" }}>
                {/* Toggle visibilidade */}
                <button onClick={() => toggleVisible(i)}
                  style={{ padding: "5px 12px", borderRadius: 20, border: "none", background: isVisible ? "var(--green)" : "var(--border)", color: isVisible ? "#fff" : "var(--text3)", fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans',sans-serif", fontSize: 11, whiteSpace: "nowrap" }}>
                  {isVisible ? "👁 Exibindo" : "🚫 Oculto"}
                </button>
                <button onClick={() => { setEditIdx(i); setForm({ q: item.q, a: item.a }); setAiInput(""); setAiError(""); }}
                  style={{ ...S.btnSecondary, fontSize: 12 }}>Editar</button>
                <button onClick={() => removeItem(i)}
                  style={{ background: "none", border: "1px solid #e74c3c33", borderRadius: 8, color: "var(--red)", cursor: "pointer", fontSize: 12, padding: "6px 10px", fontFamily: "'DM Mono',monospace" }}>✕</button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

//
// DP MESSAGES MANAGER TAB
//
// ── Notificações Tab (DP only) ────────────────────────────────────────────────
function NotificacoesTab({ restaurantId, dpMessages, notifications, onUpdate, isOwner, inbox, managerId, managerRole, inboxFolders }) {
  const ac = "#3b82f6";
  const [activeFolder, setActiveFolder] = useState(null); // null=Entrada, "__trash"=Excluídas, string=folderId
  const [ntFolderModal, setNtFolderModal] = useState(null);
  const [ntFolderForm, setNtFolderForm] = useState({ nome: "", cor: "#3498db" });
  const [ntMoveModal, setNtMoveModal] = useState(null);
  const FOLDER_COLORS = ["#3498db","#e74c3c","#2ecc71","#f39c12","#9b59b6","#1abc9c","#e67e22","#34495e"];

  const myFolders = managerId ? ((inboxFolders ?? {})[managerId] ?? []) : [];
  const isTrash = activeFolder === "__trash";

  // All DP messages for this restaurant (exclude soft-deleted)
  const dpMsgs = (dpMessages ?? [])
    .filter(m => m.restaurantId === restaurantId && !m.deleted)
    .map(m => ({ ...m, _kind: "dp" }));

  // System notifications — exclude admin-only and soft-deleted
  const sysNots = (notifications ?? [])
    .filter(n => n.restaurantId === restaurantId && n.targetRole !== "admin" && n.type !== "upgrade_request" && !n.deleted)
    .map(n => ({ ...n, _kind: "sys" }));

  // New inbox messages filtered by folder
  const inboxMsgs = managerId ? getInboxForUser({ inbox, userId: managerId, userRole: managerRole || "manager", restaurantId, folder: activeFolder })
    .map(m => ({ ...m, _kind: "inbox", read: m.lido, body: m.corpo, date: m.criadoEm })) : [];

  // Trashed items (legacy)
  const trashedDp = (dpMessages ?? []).filter(m => m.restaurantId === restaurantId && m.deleted).map(m => ({ ...m, _kind: "dp" }));
  const trashedSys = (notifications ?? []).filter(n => n.restaurantId === restaurantId && n.targetRole !== "admin" && n.type !== "upgrade_request" && n.deleted).map(n => ({ ...n, _kind: "sys" }));
  // Trash inbox msgs
  const trashedInbox = managerId ? getInboxForUser({ inbox, userId: managerId, userRole: managerRole || "manager", restaurantId, folder: "__trash" })
    .map(m => ({ ...m, _kind: "inbox", read: m.lido, body: m.corpo, date: m.criadoEm })) : [];

  // Build final list based on active folder
  const all = isTrash
    ? [...trashedDp, ...trashedSys, ...trashedInbox].sort((a, b) => (b.deletedAt || b.date || "").localeCompare(a.deletedAt || a.date || ""))
    : (activeFolder === null
      ? [...dpMsgs, ...sysNots, ...inboxMsgs]
      : inboxMsgs // custom folders only have inbox messages
    ).sort((a, b) => (b.date || b.createdAt || "").localeCompare(a.date || a.createdAt || ""));

  const unread = all.filter(m => !m.read).length;
  // Counts for tabs
  const entradaUnread = dpMsgs.filter(m=>!m.read).length + sysNots.filter(m=>!m.read).length
    + (managerId ? getInboxForUser({ inbox, userId: managerId, userRole: managerRole || "manager", restaurantId, folder: null, includeRead: false }).length : 0);
  const trashCount = trashedDp.length + trashedSys.length + trashedInbox.length;
  const folderUnread = (fid) => managerId ? getInboxForUser({ inbox, userId: managerId, userRole: managerRole || "manager", restaurantId, folder: fid, includeRead: false }).length : 0;

  function markRead(item) {
    if (item._kind === "dp") {
      onUpdate("dpMessages", dpMessages.map(m => m.id === item.id ? { ...m, read: true } : m));
    } else if (item._kind === "inbox") {
      onUpdate("inbox", (inbox ?? []).map(m => m.id === item.id ? { ...m, lido: true } : m));
    } else {
      onUpdate("notifications", notifications.map(n => n.id === item.id ? { ...n, read: true } : n));
    }
  }

  function markAllRead() {
    if (activeFolder === null) {
      onUpdate("dpMessages", dpMessages.map(m => m.restaurantId === restaurantId ? { ...m, read: true } : m));
      onUpdate("notifications", notifications.map(n => n.restaurantId === restaurantId ? { ...n, read: true } : n));
    }
    if (inboxMsgs.length > 0) {
      const ids = new Set(inboxMsgs.map(m => m.id));
      onUpdate("inbox", (inbox ?? []).map(m => ids.has(m.id) ? { ...m, lido: true } : m));
    }
  }

  function softDeleteItem(item) {
    if (item._kind === "dp") {
      onUpdate("dpMessages", dpMessages.map(m => m.id === item.id ? { ...m, deleted: true, deletedAt: new Date().toISOString(), read: true } : m));
    } else if (item._kind === "inbox") {
      onUpdate("inbox", (inbox ?? []).map(m => m.id === item.id ? { ...m, deletedAt: new Date().toISOString(), lido: true, folder: null } : m));
    } else {
      onUpdate("notifications", notifications.map(n => n.id === item.id ? { ...n, deleted: true, deletedAt: new Date().toISOString(), read: true } : n));
    }
    onUpdate("_toast", "🗑️ Movido para Excluídas");
  }

  function restoreItem(item) {
    if (item._kind === "dp") {
      onUpdate("dpMessages", dpMessages.map(m => m.id === item.id ? { ...m, deleted: false, deletedAt: undefined } : m));
    } else if (item._kind === "inbox") {
      onUpdate("inbox", (inbox ?? []).map(m => m.id === item.id ? { ...m, deletedAt: null } : m));
    } else {
      onUpdate("notifications", notifications.map(n => n.id === item.id ? { ...n, deleted: false, deletedAt: undefined } : n));
    }
    onUpdate("_toast", "📥 Restaurado para Entrada");
  }

  async function permDeleteItem(item) {
    if (!await appConfirm("Excluir definitivamente? Esta ação não pode ser desfeita.")) return;
    if (item._kind === "dp") onUpdate("dpMessages", (dpMessages??[]).filter(m=>m.id!==item.id));
    else if (item._kind === "inbox") onUpdate("inbox", (inbox??[]).filter(m=>m.id!==item.id));
    else onUpdate("notifications", (notifications??[]).filter(n=>n.id!==item.id));
    onUpdate("_toast", "❌ Excluído definitivamente");
  }

  function moveToFolder(msgId, folderId) {
    onUpdate("inbox", (inbox ?? []).map(m => m.id === msgId ? {...m, folder: folderId, deletedAt: null} : m));
    setNtMoveModal(null);
    onUpdate("_toast", folderId ? `📁 Movido para ${myFolders.find(f=>f.id===folderId)?.nome || "pasta"}` : "📥 Movido para Entrada");
  }

  function saveFolder() {
    if (!ntFolderForm.nome.trim() || !managerId) return;
    const allF = { ...(inboxFolders ?? {}) };
    const mine = [...(allF[managerId] ?? [])];
    if (ntFolderModal === "new") {
      mine.push({ id: Date.now().toString(36), nome: ntFolderForm.nome.trim(), cor: ntFolderForm.cor, ordem: mine.length });
    } else if (ntFolderModal?.id) {
      const idx = mine.findIndex(f => f.id === ntFolderModal.id);
      if (idx >= 0) mine[idx] = { ...mine[idx], nome: ntFolderForm.nome.trim(), cor: ntFolderForm.cor };
    }
    allF[managerId] = mine;
    onUpdate("inboxFolders", allF);
    setNtFolderModal(null);
    onUpdate("_toast", "📁 Pasta salva");
  }

  async function deleteFolder(fid) {
    if (!await appConfirm("Excluir esta pasta? As mensagens serão movidas para Entrada.")) return;
    onUpdate("inbox", (inbox ?? []).map(x => x.folder === fid ? {...x, folder: null} : x));
    const allF = { ...(inboxFolders ?? {}) };
    allF[managerId] = (allF[managerId] ?? []).filter(f => f.id !== fid);
    onUpdate("inboxFolders", allF);
    if (activeFolder === fid) setActiveFolder(null);
    onUpdate("_toast", "📁 Pasta excluída");
  }

  const CATS = { sugestao:"💡 Sugestão", elogio:"👏 Elogio", reclamacao:"⚠️ Reclamação", denuncia:"🚨 Denúncia" };
  const tabSt = (active, cor) => ({padding:"5px 12px",borderRadius:20,border:`1px solid ${active?(cor||ac)+"66":"var(--border)"}`,background:active?(cor||ac)+"15":"transparent",color:active?(cor||ac):"var(--text3)",cursor:"pointer",fontSize:11,fontFamily:"'DM Sans',sans-serif",fontWeight:active?700:500,display:"flex",alignItems:"center",gap:4});

  return (
    <div style={{fontFamily:"'DM Mono',monospace"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div>
          <p style={{color:ac,fontSize:15,fontWeight:700,margin:0}}>📬 Caixa</p>
          {unread > 0 && !isTrash && <p style={{color:"var(--text3)",fontSize:12,margin:"2px 0 0"}}>{unread} não lida{unread>1?"s":""}</p>}
        </div>
        {unread > 0 && !isTrash && <button onClick={markAllRead} style={{...S.btnSecondary,fontSize:12}}>Marcar todas lidas</button>}
      </div>

      {/* Folder tabs */}
      <div style={{display:"flex",gap:5,marginBottom:12,flexWrap:"wrap",alignItems:"center"}}>
        <button onClick={()=>setActiveFolder(null)} style={tabSt(activeFolder===null)}>📥 Entrada{entradaUnread>0?` (${entradaUnread})`:""}</button>
        {myFolders.map(f => {
          const fu = folderUnread(f.id);
          return <button key={f.id} onClick={()=>setActiveFolder(f.id)} style={tabSt(activeFolder===f.id,f.cor)}><span style={{width:7,height:7,borderRadius:"50%",background:f.cor||ac}}></span>{f.nome}{fu>0?` (${fu})`:""}</button>;
        })}
        <button onClick={()=>setActiveFolder("__trash")} style={tabSt(isTrash)}>🗑️ Excluídas{trashCount>0?` (${trashCount})`:""}</button>
        <button onClick={()=>{setNtFolderModal("new");setNtFolderForm({nome:"",cor:"#3498db"});}} style={{background:"none",border:"1px dashed var(--border)",borderRadius:20,padding:"5px 10px",color:"var(--text3)",cursor:"pointer",fontSize:11,fontFamily:"'DM Sans',sans-serif"}} title="Nova pasta">+</button>
      </div>

      {/* Edit/delete current custom folder */}
      {activeFolder && activeFolder !== "__trash" && (() => {
        const cf = myFolders.find(f=>f.id===activeFolder);
        return cf ? (
          <div style={{display:"flex",gap:8,marginBottom:10}}>
            <button onClick={()=>{setNtFolderModal(cf);setNtFolderForm({nome:cf.nome,cor:cf.cor||"#3498db"});}} style={{...S.btnSecondary,fontSize:10,padding:"3px 8px"}}>Renomear</button>
            <button onClick={()=>deleteFolder(cf.id)} style={{...S.btnSecondary,fontSize:10,padding:"3px 8px",color:"#e74c3c",borderColor:"#e74c3c44"}}>Excluir pasta</button>
          </div>
        ) : null;
      })()}

      {isTrash && <p style={{color:"var(--text3)",fontSize:11,marginBottom:10}}>Mensagens excluídas são removidas automaticamente após 90 dias.</p>}

      {all.length === 0 && <p style={{color:"var(--text3)",textAlign:"center",marginTop:40}}>{isTrash?"Nenhuma mensagem excluída.":"Nenhuma notificação."}</p>}

      {all.map(item => {
        const date = item.date || item.createdAt || "";
        const isDP = item._kind === "dp";
        const isSys = item._kind === "sys";
        const isInbox = item._kind === "inbox";
        return (
          <div key={item.id} style={{...S.card,marginBottom:10,opacity:item.read?0.65:1,borderColor:item.read?"var(--border)":isDP?"var(--ac)44":"#3b82f644"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
              <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                {isDP && <span style={{background:"var(--ac)22",color:"var(--ac)",borderRadius:6,padding:"2px 8px",fontSize:10,fontWeight:700}}>💬 Fale com DP</span>}
                {isSys && <span style={{background:"#3b82f622",color:"#3b82f6",borderRadius:6,padding:"2px 8px",fontSize:10,fontWeight:700}}>⚙️ Sistema</span>}
                {isInbox && <span style={{background:item.tipo==="reuniao"?"#10b98122":item.tipo==="acao_reuniao"?"#f59e0b22":"#8b5cf622",color:item.tipo==="reuniao"?"#10b981":item.tipo==="acao_reuniao"?"#f59e0b":"#8b5cf6",borderRadius:6,padding:"2px 8px",fontSize:10,fontWeight:700}}>{{reuniao:"📅 Reunião",acao_reuniao:"📋 Ação",comunicado:"📨 Comunicado",solicitacao:"📩 Solicitação",sistema:"⚙️ Sistema"}[item.tipo]??item.tipo}</span>}
                {isInbox && item.assunto && <span style={{color:"var(--text)",fontSize:12,fontWeight:600}}>{item.assunto}</span>}
                {isDP && item.category && <span style={{color:"var(--text3)",fontSize:11}}>{CATS[item.category]??item.category}</span>}
                {!item.read && <span style={{background:isDP?"var(--ac)":"#3b82f6",color:"var(--text)",borderRadius:4,padding:"1px 6px",fontSize:10,fontWeight:700}}>Novo</span>}
              </div>
              <span style={{color:"var(--text3)",fontSize:11,whiteSpace:"nowrap"}}>{date ? new Date(date).toLocaleString("pt-BR") : ""}</span>
            </div>
            {isDP && <div style={{color:"var(--text2)",fontSize:12,marginBottom:6}}>De: <span style={{color:item.empName==="Anônimo"?"#8b5cf6":"var(--text)"}}>{item.empName}</span></div>}
            {isInbox && item.de && <div style={{color:"var(--text2)",fontSize:12,marginBottom:6}}>De: <span style={{color:"var(--text)"}}>{item.de.nome || item.de.role}</span></div>}
            <div style={{color:"var(--text)",fontSize:13,lineHeight:1.6,whiteSpace:"pre-wrap",marginBottom:8}}>{item.body}</div>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              {!isTrash && !item.read && <button onClick={()=>markRead(item)} style={{...S.btnSecondary,fontSize:11,padding:"4px 12px"}}>Marcar como lida</button>}
              {!isTrash && isInbox && myFolders.length > 0 && <button onClick={()=>setNtMoveModal(item.id)} style={{background:"none",border:"1px solid var(--border)",borderRadius:8,color:"var(--text3)",cursor:"pointer",fontSize:11,padding:"4px 10px",fontFamily:"'DM Mono',monospace"}}>📁</button>}
              {isTrash ? (
                <>
                  <button onClick={()=>restoreItem(item)} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px"}}>📥 Restaurar</button>
                  {isOwner && <button onClick={()=>permDeleteItem(item)} style={{background:"none",border:"1px solid #e74c3c33",borderRadius:8,color:"#e74c3c",cursor:"pointer",fontSize:11,padding:"4px 10px",fontFamily:"'DM Mono',monospace"}}>❌ Excluir</button>}
                </>
              ) : (
                <button onClick={()=>softDeleteItem(item)} aria-label="Excluir" style={{background:"none",border:"1px solid #e74c3c22",borderRadius:8,color:"var(--text3)",cursor:"pointer",fontSize:11,padding:"4px 10px",fontFamily:"'DM Mono',monospace"}}>🗑️</button>
              )}
            </div>
          </div>
        );
      })}

      {/* Modal mover para pasta */}
      {ntMoveModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999,padding:16}} onClick={()=>setNtMoveModal(null)}>
          <div style={{background:"var(--card-bg)",borderRadius:16,padding:20,maxWidth:320,width:"100%",border:"1px solid var(--border)"}} onClick={e=>e.stopPropagation()}>
            <h3 style={{color:"var(--text)",fontSize:14,fontWeight:700,margin:"0 0 12px"}}>📁 Mover para pasta</h3>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              <button onClick={()=>moveToFolder(ntMoveModal, null)} style={{...S.btnSecondary,fontSize:12,textAlign:"left"}}>📥 Entrada</button>
              {myFolders.map(f => (
                <button key={f.id} onClick={()=>moveToFolder(ntMoveModal, f.id)} style={{...S.btnSecondary,fontSize:12,textAlign:"left",display:"flex",alignItems:"center",gap:6}}>
                  <span style={{width:8,height:8,borderRadius:"50%",background:f.cor||ac}}></span> {f.nome}
                </button>
              ))}
            </div>
            <div style={{marginTop:10,textAlign:"right"}}>
              <button onClick={()=>setNtMoveModal(null)} style={{...S.btnSecondary,fontSize:11}}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal criar/editar pasta */}
      {ntFolderModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999,padding:16}} onClick={()=>setNtFolderModal(null)}>
          <div style={{background:"var(--card-bg)",borderRadius:16,padding:20,maxWidth:360,width:"100%",border:"1px solid var(--border)"}} onClick={e=>e.stopPropagation()}>
            <h3 style={{color:"var(--text)",fontSize:14,fontWeight:700,margin:"0 0 12px"}}>{ntFolderModal==="new"?"📁 Nova pasta":"📁 Editar pasta"}</h3>
            <div style={{marginBottom:10}}>
              <label style={{color:"var(--text3)",fontSize:11,fontWeight:600,display:"block",marginBottom:4}}>Nome</label>
              <input value={ntFolderForm.nome} onChange={e=>setNtFolderForm(p=>({...p,nome:e.target.value}))} style={{...S.input,width:"100%"}} placeholder="Nome da pasta" maxLength={30}/>
            </div>
            <div style={{marginBottom:14}}>
              <label style={{color:"var(--text3)",fontSize:11,fontWeight:600,display:"block",marginBottom:4}}>Cor</label>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {FOLDER_COLORS.map(c => (
                  <button key={c} onClick={()=>setNtFolderForm(p=>({...p,cor:c}))} style={{width:24,height:24,borderRadius:"50%",background:c,border:ntFolderForm.cor===c?"3px solid var(--text)":"2px solid transparent",cursor:"pointer"}}/>
                ))}
              </div>
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
              <button onClick={()=>setNtFolderModal(null)} style={{...S.btnSecondary,fontSize:12}}>Cancelar</button>
              <button onClick={saveFolder} disabled={!ntFolderForm.nome.trim()} style={{...S.btnPrimary,fontSize:12,opacity:!ntFolderForm.nome.trim()?0.5:1}}>Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Work Schedule helpers ──────────────────────────────────────────────────────
const WEEK_DAYS_LABEL = ["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"];
// Ordem visual de exibição dos 7 dias (Seg→Dom), padronizando com a escala mensal.
// Os índices internos seguem JS padrão (0=Dom, 6=Sáb) em validações, calcDayHours, etc.
// Esta constante é usada APENAS pra renderização e exibição em listas/PDFs/notificações.
const WEEK_DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

function timeToMin(t) {
  if (!t) return null;
  const [h, m] = t.split(":").map(Number);
  return h * 60 + (m || 0);
}
function fmtHHMM(totalMin) {
  if (!totalMin && totalMin !== 0) return "—";
  const sign = totalMin < 0 ? "-" : "";
  const abs = Math.abs(totalMin);
  return `${sign}${String(Math.floor(abs/60)).padStart(2,"0")}:${String(abs%60).padStart(2,"0")}`;
}

// Calcula horas do dia respeitando hora ficta noturna (CLT Art. 73)
// Método padrão folha: intervalo descontado das horas diurnas primeiro,
// horas noturnas mantidas intactas, ficta aplicada sobre noturnas reais.
// Retorna {worked, diurnal, nocturnal, nocturnalFicta, totalContract, error}
function calcDayHours(inTime, outTime, breakMin) {
  if (!inTime || !outTime) return { worked: 0, diurnal: 0, nocturnal: 0, nocturnalFicta: 0, totalContract: 0 };
  let inM = timeToMin(inTime);
  let outM = timeToMin(outTime);
  // Handle overnight (e.g. 22:00 -> 06:00)
  if (outM <= inM) outM += 24 * 60;

  const totalPeriod = outM - inM;
  const bk = breakMin || 0;
  const worked = totalPeriod - bk;
  if (worked <= 0) return { worked: 0, diurnal: 0, nocturnal: 0, nocturnalFicta: 0, totalContract: 0, error: "Horário inválido" };

  // Count raw nocturnal minutes (22:00-05:00, CLT Art. 73)
  let noctRaw = 0;
  for (let t = inM; t < outM; t++) {
    const tMod = t % (24 * 60);
    if (tMod >= 22 * 60 || tMod < 5 * 60) noctRaw++;
  }
  const diurnRaw = totalPeriod - noctRaw;

  // Desconta intervalo das horas diurnas primeiro; se sobrar, desconta das noturnas
  let diurnAfterBreak, noctAfterBreak;
  if (bk <= diurnRaw) {
    diurnAfterBreak = diurnRaw - bk;
    noctAfterBreak = noctRaw;
  } else {
    diurnAfterBreak = 0;
    noctAfterBreak = noctRaw - (bk - diurnRaw);
  }

  // Hora ficta: 52min30s reais = 60min contratuais (CLT Art. 73 §1)
  const nocturnalFicta = Math.round(noctAfterBreak * (60 / 52.5));

  return {
    worked,
    diurnal: diurnAfterBreak,
    nocturnal: noctAfterBreak,
    nocturnalFicta,
    totalContract: diurnAfterBreak + nocturnalFicta,
  };
}

// Validate a full week schedule (CLT compliant)
function validateWeekSchedule(days) {
  const errors = [];
  const activeDays = Object.entries(days).filter(([,d]) => d && d.in && d.out);
  // ── Per day validations ──
  activeDays.forEach(([dayIdx, d]) => {
    const label = WEEK_DAYS_LABEL[parseInt(dayIdx)];
    const calc = calcDayHours(d.in, d.out, d.break || 0);
    if (calc.error) { errors.push(`${label}: ${calc.error}`); return; }

    // Jornada máxima: 10h contratuais/dia (Art. 59 CLT)
    if (calc.totalContract > 10 * 60) {
      errors.push(`${label}: jornada contratual de ${fmtHHMM(calc.totalContract)} ultrapassa o máximo de 10h (${fmtHHMM(calc.diurnal)} diurnas + ${fmtHHMM(calc.nocturnalFicta)} noturnas fictas).`);
    }

    // Intervalo intrajornada (Art. 71 CLT)
    const bk = d.break || 0;
    if (calc.worked > 6 * 60 && bk < 60) {
      errors.push(`${label}: jornada de ${fmtHHMM(calc.worked)} exige intervalo mínimo de 60 minutos (atual: ${bk}min). Art. 71 CLT.`);
    } else if (calc.worked > 4 * 60 && calc.worked <= 6 * 60 && bk < 15) {
      errors.push(`${label}: jornada de ${fmtHHMM(calc.worked)} exige intervalo mínimo de 15 minutos (atual: ${bk}min). Art. 71 §1 CLT.`);
    }
  });

  // ── Interjornada ≥ 11h (Art. 66 CLT) — com wrap-around (último→primeiro) ──
  const sorted = [...activeDays].sort((a, b) => parseInt(a[0]) - parseInt(b[0]));
  for (let i = 0; i < sorted.length; i++) {
    const curIdx = parseInt(sorted[i][0]);
    const nxtI = (i + 1) % sorted.length;
    const nxtIdx = parseInt(sorted[nxtI][0]);
    const [, curD] = sorted[i];
    const [, nxtD] = sorted[nxtI];

    // Calendar days between (wrap around week)
    const daysBetween = nxtIdx > curIdx ? nxtIdx - curIdx : nxtIdx + 7 - curIdx;

    const curIn = timeToMin(curD.in);
    const curOut = timeToMin(curD.out);
    const nxtIn = timeToMin(nxtD.in);
    const isOvernight = curOut <= curIn;

    // Gap = (minutes remaining in day after out) + (full days between -1) * 1440 + nxtIn
    // For overnight shifts, out is actually next calendar morning
    let gap;
    if (isOvernight) {
      // Shift ends at curOut on the NEXT calendar day
      gap = (24 * 60 - curOut) + (daysBetween - 2) * 24 * 60 + nxtIn;
    } else {
      gap = (24 * 60 - curOut) + (daysBetween - 1) * 24 * 60 + nxtIn;
    }

    if (gap < 11 * 60) {
      errors.push(`Interjornada entre ${WEEK_DAYS_LABEL[curIdx]} e ${WEEK_DAYS_LABEL[nxtIdx]} é de ${fmtHHMM(gap)}, mínimo exigido é 11h. Art. 66 CLT.`);
    }
  }

  // ── DSR: mínimo 1 folga por semana (Art. 67 CLT) ──
  if (activeDays.length >= 7) {
    errors.push("Sem dia de folga na semana. O empregado deve ter pelo menos 1 descanso semanal remunerado. Art. 67 CLT.");
  }

  // ── Carga semanal: 43:55 a 44:00 (Art. 58 CLT + Art. 7 XIV CF) ──
  const totalContract = activeDays.reduce((sum, [,d]) => {
    const c = calcDayHours(d.in, d.out, d.break || 0);
    return sum + (c.totalContract || 0);
  }, 0);
  const MIN_WEEK = 43 * 60 + 55;
  const MAX_WEEK = 44 * 60;
  if (activeDays.length > 0 && (totalContract < MIN_WEEK || totalContract > MAX_WEEK)) {
    errors.push(`Carga semanal de ${fmtHHMM(totalContract)} fora do intervalo permitido (43:55 a 44:00).`);
  }

  return { errors, totalContract };
}

// ── Work Schedule Manager Tab ─────────────────────────────────────────────────
function WorkScheduleManagerTab({ restaurantId, employees, roles, workSchedules, notifications, managers, currentManagerName, onUpdate, communications, isOwner, mobileOnly, forcedEmpId }) {
  const ac = "var(--ac)";
  const restEmps = employees.filter(e => e.restaurantId === restaurantId && !e.inactive);

  // ── State ──
  // Se `forcedEmpId` vier (uso embutido em detalhe do empregado), pré-seleciona e esconde dropdown
  const [selEmpId, setSelEmpId]             = useState(forcedEmpId || null);
  const [editDays, setEditDays]             = useState({});   // semana A (ou única se single)
  const [editDaysB, setEditDaysB]           = useState({});   // semana B (só usado se alternating)
  const [scheduleType, setScheduleType]     = useState("single"); // "single" | "alternating"
  const [editWeek, setEditWeek]             = useState("A");  // qual semana está sendo editada (A|B)
  const [anchorDate, setAnchorDate]         = useState(() => getWeekMonday(today())); // segunda da semana de referência
  const [anchorWeek, setAnchorWeek]         = useState("A");  // qual semana é o anchor inicial
  const [errors, setErrors]                 = useState([]);
  const [showValidFrom, setShowValidFrom]   = useState(false);
  const [validFrom, setValidFrom]           = useState(today());
  const [selectedSchedIds, setSelectedSchedIds] = useState(new Set());
  const [, setSaveMode]                     = useState(null); // saveMode read elsewhere via effects
  const [validated, setValidated]           = useState(false); // true após validar horários com sucesso
  // Modal de inversão de semana (só faz sentido pra schedules alternating)
  const [invertingMonday, setInvertingMonday] = useState(null); // segunda da semana sendo invertida ou null
  const [invertScope, setInvertScope]       = useState("once");  // "once" (override) | "forever" (re-anchor)
  // v8.11.0: ciclo de domingos pra empregado que não folga aos domingos
  const [sundayCycle, setSundayCycle]       = useState({ workCount: 3, offCount: 1, refDate: "" });

  const selEmp = restEmps.find(e => e.id === selEmpId);
  const empSchedules = workSchedules?.[restaurantId]?.[selEmpId] ?? [];

  // ── Aliases dinâmicos: "days" e "setDays" sempre apontam pra semana sendo editada agora ──
  // No modo single, apontam sempre pra editDays. No alternating, alternam entre A e B
  // conforme o sub-tab selecionado (editWeek). Permite que toggleDay/handleDayChange/etc
  // continuem funcionando com mínimo de mudanças no código existente.
  const isAlternating = scheduleType === "alternating";
  const days = isAlternating && editWeek === "B" ? editDaysB : editDays;
  const setDays = isAlternating && editWeek === "B" ? setEditDaysB : setEditDays;

  // Sincroniza com forcedEmpId externo (modo embutido em detalhe do empregado)
  // v8.11.4: SEMPRE chama loadEmp quando forcedEmpId muda (ou no mount inicial), pra popular
  // editDays/sundayCycle a partir do horário vigente. Antes só setSelEmpId era chamado, deixando
  // o form vazio mesmo com forcedEmpId já setado no useState inicial.
  useEffect(() => {
    if (forcedEmpId) loadEmp(forcedEmpId);
  }, [forcedEmpId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helpers: convert between internal format (with active flag) and storage format ──
  function toInternal(storedEntry) {
    const storedDays = storedEntry?.days;
    const out = {};
    for (let i = 0; i < 7; i++) {
      const d = storedDays?.[i];
      if (d && (d.in || d.out)) {
        out[i] = { active: true, in: d.in || "", out: d.out || "", break: d.break ?? 0 };
      } else if (d && d.active) {
        // days-only save: active but no hours
        out[i] = { active: true, in: "", out: "", break: 0 };
      } else if (storedDays && !d) {
        // missing from stored = folga
        out[i] = { active: false };
      } else {
        out[i] = { active: true }; // fresh default
      }
    }
    return out;
  }

  function toStorage(internalDays, daysOnly) {
    const out = {};
    Object.entries(internalDays).forEach(([idx, d]) => {
      if (d.active) {
        if (!daysOnly && d.in && d.out) {
          out[idx] = { in: d.in, out: d.out, break: d.break ?? 0 };
        } else {
          // days-only: store just active flag so we know it's a work day
          out[idx] = { active: true };
        }
      }
      // inactive days (folga) → not stored (absence = folga)
    });
    return out;
  }

  // ── Check if hours are filled for all active days ──
  function hasAllHours(days) {
    return Object.values(days).every(d => !d.active || (d.in && d.out));
  }


  // ── Load employee ──
  function loadEmp(empId) {
    setSelEmpId(empId);
    setErrors([]);
    setShowValidFrom(false);
    setSaveMode(null);
    setValidated(false);
    setCopyPickerOpen(false);
    setEditWeek("A");
    const sched = (workSchedules?.[restaurantId]?.[empId] ?? []);
    const cur = sched[sched.length - 1];
    if (cur && cur.type === "alternating") {
      // Alternating: popula A e B + anchor
      setScheduleType("alternating");
      setEditDays(toInternal(cur.weeks?.A || {}));
      setEditDaysB(toInternal(cur.weeks?.B || {}));
      setAnchorDate(cur.anchor?.date || getWeekMonday(today()));
      setAnchorWeek(cur.anchor?.week || "A");
    } else if (cur) {
      // Single (atual): popula editDays e mantém B vazio até user trocar pra alternating
      setScheduleType("single");
      setEditDays(toInternal(cur));
      const freshB = {};
      for (let i = 0; i < 7; i++) freshB[i] = { active: true };
      setEditDaysB(freshB);
      setAnchorDate(getWeekMonday(today()));
      setAnchorWeek("A");
    } else {
      // New employee: all days active, no hours
      setScheduleType("single");
      const fresh = {};
      for (let i = 0; i < 7; i++) fresh[i] = { active: true };
      setEditDays(fresh);
      const freshB = {};
      for (let i = 0; i < 7; i++) freshB[i] = { active: true };
      setEditDaysB(freshB);
      setAnchorDate(getWeekMonday(today()));
      setAnchorWeek("A");
    }
    // v8.11.0: carrega ciclo de domingos da versão atual (se houver)
    if (cur?.sundayCycle) {
      setSundayCycle({
        workCount: cur.sundayCycle.workCount ?? 3,
        offCount: cur.sundayCycle.offCount ?? 1,
        refDate: cur.sundayCycle.refDate ?? "",
      });
    } else {
      setSundayCycle({ workCount: 3, offCount: 1, refDate: "" });
    }
  }

  // ── Toggle work/folga ──
  function toggleDay(dayIdx) {
    setDays(prev => {
      const cur = prev[dayIdx] ?? { active: true };
      return { ...prev, [dayIdx]: cur.active ? { active: false } : { active: true, in: "", out: "", break: 0 } };
    });
    setErrors([]);
    setValidated(false);
  }

  // ── Update time/break field ──
  function handleDayChange(dayIdx, field, val) {
    setDays(prev => ({
      ...prev,
      [dayIdx]: { ...(prev[dayIdx] ?? { active: true }), [field]: val }
    }));
    setErrors([]);
    setValidated(false);
  }


  // ── "Validar Horários" (só valida, não salva) ──
  // Suporta single (valida editDays) e alternating (valida editDays + editDaysB independentemente)
  function tryValidateFull() {
    const semanas = isAlternating
      ? [{ label: "Semana A", d: editDays }, { label: "Semana B", d: editDaysB }]
      : [{ label: "", d: editDays }];
    const allErrors = [];
    for (const { label, d } of semanas) {
      const prefix = label ? `[${label}] ` : "";
      const activeDays = Object.values(d).filter(x => x.active);
      if (activeDays.length === 0) { allErrors.push(`${prefix}Selecione pelo menos um dia de trabalho.`); continue; }
      if (!hasAllHours(d)) { allErrors.push(`${prefix}Preencha os horários de todos os dias marcados como trabalho.`); continue; }
      if (!selEmp?.isFreela) {
        const storageDays = toStorage(d, false);
        const { errors: errs } = validateWeekSchedule(storageDays);
        errs.forEach(e => allErrors.push(`${prefix}${e}`));
      }
    }
    setErrors(allErrors);
    setValidated(allErrors.length === 0);
    if (allErrors.length === 0) {
      const msg = selEmp?.isFreela
        ? "✅ Horário registrado (freela — sem validação CLT)"
        : (isAlternating ? "✅ Semanas A e B validadas — clique em Salvar para confirmar" : "✅ Horário validado — clique em Salvar para confirmar");
      onUpdate("_toast", msg);
    }
  }

  // ── "Prosseguir para salvar" (abre modal de vigência — só após validar) ──
  function proceedToSave() {
    setSaveMode("full");
    setShowValidFrom(true);
  }

  // ── Save schedule ──
  function saveSchedule() {
    const isFreela = selEmp?.isFreela;

    // Helper: valida + calcula totalContract pra um conjunto de dias
    function validateAndCalc(daysObj, label) {
      const storageDays = toStorage(daysObj, false);
      let totalContract = 0;
      if (!isFreela) {
        const { errors: errs, totalContract: tc } = validateWeekSchedule(storageDays);
        if (errs.length > 0) {
          const prefix = label ? `[${label}] ` : "";
          return { errors: errs.map(e => prefix + e), storageDays, totalContract: tc };
        }
        totalContract = tc;
      } else {
        const activeDays = Object.entries(storageDays).filter(([,d]) => d && d.in && d.out);
        totalContract = activeDays.reduce((sum, [,d]) => sum + (calcDayHours(d.in, d.out, d.break || 0).totalContract || 0), 0);
      }
      return { errors: [], storageDays, totalContract };
    }

    let newEntry;
    let storageDaysForNotif; // pra mensagem de notificação (no alternating, usa Semana A como representativa)
    // v8.11.0: ciclo de domingos só vale se domingo for ativo em alguma semana E o usuário preencheu refDate.
    function cleanSundayCycle(daysObj, daysObjB) {
      const sundayActiveA = daysObj?.[0]?.active === true;
      const sundayActiveB = daysObjB ? daysObjB?.[0]?.active === true : false;
      if (!sundayActiveA && !sundayActiveB) return null;
      const work = parseInt(sundayCycle.workCount, 10);
      const off  = parseInt(sundayCycle.offCount, 10);
      if (!Number.isFinite(work) || work < 0) return null;
      if (!Number.isFinite(off) || off <= 0) return null;
      if (!sundayCycle.refDate) return null;
      // Valida que refDate é domingo
      const ref = new Date(sundayCycle.refDate + "T12:00:00");
      if (ref.getDay() !== 0) return null;
      return { workCount: work, offCount: off, refDate: sundayCycle.refDate };
    }
    if (isAlternating) {
      const a = validateAndCalc(editDays, "Semana A");
      const b = validateAndCalc(editDaysB, "Semana B");
      const allErrs = [...a.errors, ...b.errors];
      if (allErrs.length > 0) { setErrors(allErrs); return; }
      const cycle = cleanSundayCycle(editDays, editDaysB);
      newEntry = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2,5)}`,
        type: "alternating",
        pattern: ["A", "B"],
        weeks: {
          A: { days: a.storageDays, totalContract: a.totalContract, hoursComplete: true },
          B: { days: b.storageDays, totalContract: b.totalContract, hoursComplete: true },
        },
        anchor: { date: anchorDate, week: anchorWeek },
        reanchors: [],
        overrides: {},
        validFrom,
        createdBy: currentManagerName,
        createdAt: new Date().toISOString(),
        // Fallback `days` pra código legado que ainda lê schedule.days direto.
        // Aponta pra Semana A — funciona aproximadamente até o consumidor usar getEffectiveDays.
        days: a.storageDays,
        // totalContract: média das duas (pra compatibilidade com código legado que lê esse campo)
        totalContract: Math.round((a.totalContract + b.totalContract) / 2),
        hoursComplete: true,
        ...(cycle ? { sundayCycle: cycle } : {}),
        ...(isFreela ? { freela: true } : {}),
      };
      storageDaysForNotif = a.storageDays;
    } else {
      const r = validateAndCalc(editDays, "");
      if (r.errors.length > 0) { setErrors(r.errors); return; }
      const cycle = cleanSundayCycle(editDays, null);
      newEntry = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2,5)}`,
        type: "single",
        days: r.storageDays,
        validFrom,
        createdBy: currentManagerName,
        createdAt: new Date().toISOString(),
        totalContract: r.totalContract,
        hoursComplete: true,
        ...(cycle ? { sundayCycle: cycle } : {}),
        ...(isFreela ? { freela: true } : {}),
      };
      storageDaysForNotif = r.storageDays;
    }

    onUpdate("workSchedules", prev => {
      const empScheds = [...(prev?.[restaurantId]?.[selEmpId] ?? []), newEntry];
      return { ...prev, [restaurantId]: { ...(prev?.[restaurantId] ?? {}), [selEmpId]: empScheds } };
    });

    // Format schedule description for notifications (no alternating: ambas as semanas)
    function fmtSchedLineFor(daysObj, i) {
      const d = daysObj[i];
      if (!d) return `${WEEK_DAYS_LABEL[i]}: Folga`;
      if (d.in && d.out) return `${WEEK_DAYS_LABEL[i]}: ${d.in} - ${d.out} (intervalo ${d.break??0}min)`;
      return `${WEEK_DAYS_LABEL[i]}: Trabalha (horario a definir)`;
    }
    function fmtSchedLine(i) { return fmtSchedLineFor(storageDaysForNotif, i); }

    // Body do horário (single = lista única, alternating = duas semanas)
    function fmtBodyDoHorario() {
      if (isAlternating) {
        const linhasA = WEEK_DISPLAY_ORDER.map(i => fmtSchedLineFor(newEntry.weeks.A.days, i)).join("\n");
        const linhasB = WEEK_DISPLAY_ORDER.map(i => fmtSchedLineFor(newEntry.weeks.B.days, i)).join("\n");
        return `Tipo: Escala alternada A/B\nReferência: semana de ${fmtDate(anchorDate)} é Semana ${anchorWeek}\n\nSEMANA A:\n${linhasA}\n\nSEMANA B:\n${linhasB}`;
      }
      return WEEK_DISPLAY_ORDER.map(fmtSchedLine).join("\n");
    }

    // Notify DP managers
    const dpMgrs = managers.filter(m => m.isDP && (m.restaurantIds ?? []).includes(restaurantId));
    if (dpMgrs.length > 0) {
      const body = `Horario alterado\n\nEmpregado: ${selEmp?.name}\nAlterado por: ${currentManagerName}\nVigencia a partir de: ${fmtDate(validFrom)}\n\nNovo horario:\n${fmtBodyDoHorario()}`;
      const notif = {
        id: `${Date.now()}-notif-${Math.random().toString(36).slice(2,5)}`,
        restaurantId, type: "horario", body,
        date: new Date().toISOString(), read: false, targetRole: "dp",
      };
      onUpdate("notifications", [...(notifications ?? []), notif]);
    }

    // Comunicado for employee
    const schedBody = `Seu horario de trabalho foi atualizado por ${currentManagerName}.\nVigencia a partir de: ${fmtDate(validFrom)}\n\nNovo horario:\n${fmtBodyDoHorario()}`;
    const commForEmp = {
      id: `${Date.now()}-comm-${Math.random().toString(36).slice(2,5)}`,
      restaurantId,
      title: `Novo horario - vigencia ${fmtDate(validFrom)}`,
      body: schedBody,
      createdAt: new Date().toISOString(),
      createdBy: currentManagerName,
      target: `emp:${selEmpId}`,
      autoSchedule: true,
    };
    onUpdate("communications", [...(communications ?? []), commForEmp]);

    setShowValidFrom(false);
    setSaveMode(null);
    setValidated(false);
    setErrors([]);
    onUpdate("_toast", `Horario de ${selEmp?.name} salvo com vigencia a partir de ${fmtDate(validFrom)}`);
  }

  // ── INVERSÃO de semana (só schedules alternating) ──
  // monday: data ISO da segunda-feira da semana sendo invertida
  // scope: "once" → cria override pontual; "forever" → cria re-anchor (inverte sequência daqui em diante)
  function applyInversion(monday, scope) {
    const cur = empSchedules[empSchedules.length - 1];
    if (!cur || cur.type !== "alternating") return;
    const wkAtual = weekTypeForDate(cur, monday);
    const wkNovo = wkAtual === "A" ? "B" : "A";
    let updated;
    if (scope === "once") {
      // Override: marca essa segunda específica como wkNovo
      updated = {
        ...cur,
        overrides: { ...(cur.overrides || {}), [monday]: wkNovo },
      };
    } else {
      // Re-anchor: adiciona à lista, sequência inverte daquela semana em diante
      const novoReanchor = { date: monday, week: wkNovo };
      const filtered = (cur.reanchors || []).filter(r => r.date !== monday); // sobrescreve se já existe
      updated = {
        ...cur,
        reanchors: [...filtered, novoReanchor].sort((a,b) => a.date.localeCompare(b.date)),
      };
    }
    onUpdate("workSchedules", prev => {
      const list = (prev?.[restaurantId]?.[selEmpId] ?? []).slice();
      list[list.length - 1] = updated;
      return { ...prev, [restaurantId]: { ...(prev?.[restaurantId] ?? {}), [selEmpId]: list } };
    });
    setInvertingMonday(null);
    onUpdate("_toast", scope === "once"
      ? `🔄 Semana de ${fmtDate(monday)} marcada como Semana ${wkNovo} (apenas esta)`
      : `🔄 Sequência invertida a partir de ${fmtDate(monday)} (Semana ${wkNovo} daqui em diante)`);
  }

  // ── Exporta PDF do horário (single ou alternating) ──
  async function exportarHorarioPDF() {
    const cur = empSchedules[empSchedules.length - 1];
    if (!cur) { onUpdate("_toast", "Sem horário cadastrado pra exportar"); return; }
    const jsPDFCtor = window.jspdf?.jsPDF || window.jsPDF;
    if (!jsPDFCtor) { alert("Biblioteca PDF carregando — tente novamente em 2s."); return; }
    const doc = new jsPDFCtor("p", "mm", "a4");
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();

    // Cabeçalho
    doc.setFont("helvetica","bold"); doc.setFontSize(16);
    doc.text("Horário de Trabalho", 14, 16);
    doc.setFontSize(11); doc.setFont("helvetica","normal");
    doc.text(`Empregado: ${selEmp?.name || "—"}`, 14, 24);
    const role = roles.find(r => r.id === selEmp?.roleId);
    if (role) doc.text(`Cargo: ${role.name}`, 14, 30);
    doc.text(`Vigente desde: ${fmtDate(cur.validFrom)}`, 14, 36);
    if (cur.type === "alternating") {
      doc.text(`Tipo: Escala alternada A/B`, 14, 42);
      doc.setFontSize(9);
      doc.text(`Referência: semana de ${fmtDate(cur.anchor?.date)} é Semana ${cur.anchor?.week}`, 14, 47);
    }

    function buildBody(daysObj) {
      return WEEK_DISPLAY_ORDER.map(i => {
        const d = daysObj?.[i];
        if (!d) return [WEEK_DAYS_LABEL[i], "Folga", "—", "—"];
        if (d.in && d.out) return [WEEK_DAYS_LABEL[i], `${d.in} – ${d.out}`, `${d.break||0}min`, fmtHHMM(calcDayHours(d.in,d.out,d.break||0).totalContract)];
        return [WEEK_DAYS_LABEL[i], "Trabalha", "—", "—"];
      });
    }

    if (cur.type === "alternating") {
      // Duas tabelas lado a lado: Semana A à esquerda, Semana B à direita
      const startY = 54;
      const colW = (W - 14*2 - 6) / 2;
      const wkHoje = weekTypeForDate(cur, today()) || "A";
      // Semana A
      doc.autoTable({
        head: [[`Semana A${wkHoje==="A"?" ★ esta semana":""}`,"Horário","Interv.","Carga"]],
        body: buildBody(cur.weeks?.A?.days),
        foot: cur.weeks?.A?.totalContract > 0 ? [["Total/sem","","",fmtHHMM(cur.weeks.A.totalContract)]] : null,
        startY, theme: "grid",
        styles: { fontSize: 8 },
        headStyles: { fillColor: wkHoje==="A" ? [212,160,23] : [120,120,120], textColor: 255 },
        footStyles: { fillColor: [240,235,210], fontStyle:"bold", textColor: 0 },
        margin: { left: 14, right: W - 14 - colW },
        tableWidth: colW,
      });
      doc.autoTable({
        head: [[`Semana B${wkHoje==="B"?" ★ esta semana":""}`,"Horário","Interv.","Carga"]],
        body: buildBody(cur.weeks?.B?.days),
        foot: cur.weeks?.B?.totalContract > 0 ? [["Total/sem","","",fmtHHMM(cur.weeks.B.totalContract)]] : null,
        startY, theme: "grid",
        styles: { fontSize: 8 },
        headStyles: { fillColor: wkHoje==="B" ? [212,160,23] : [120,120,120], textColor: 255 },
        footStyles: { fillColor: [240,235,210], fontStyle:"bold", textColor: 0 },
        margin: { left: 14 + colW + 6, right: 14 },
        tableWidth: colW,
      });
      // Próximas 8 semanas
      const monHoje = getWeekMonday(today());
      const linhasSeq = [];
      for (let i = 0; i < 8; i++) {
        const d = new Date(monHoje + "T12:00:00");
        d.setDate(d.getDate() + i * 7);
        const ds = d.toISOString().slice(0,10);
        const wk = weekTypeForDate(cur, ds);
        linhasSeq.push([fmtDate(ds), `Semana ${wk}`, i===0?"esta":""]);
      }
      const yAfter = Math.max(doc.lastAutoTable.finalY + 8, startY + 80);
      doc.setFontSize(11); doc.setFont("helvetica","bold");
      doc.text("Próximas 8 semanas (calculadas automaticamente)", 14, yAfter);
      doc.autoTable({
        head: [["Início","Tipo","Obs"]],
        body: linhasSeq,
        startY: yAfter + 3,
        theme: "striped",
        styles: { fontSize: 9 },
        headStyles: { fillColor: [212,160,23], textColor: 255 },
      });
      // Inversões existentes
      const inv = [
        ...((cur.reanchors || []).map(r => [fmtDate(r.date), "Re-anchor", `Daqui em diante = Semana ${r.week}`])),
        ...(Object.entries(cur.overrides || {}).map(([dt, wk]) => [fmtDate(dt), "Override", `Apenas esta semana = Semana ${wk}`])),
      ].sort((a,b) => a[0].localeCompare(b[0]));
      if (inv.length > 0) {
        const yInv = doc.lastAutoTable.finalY + 8;
        doc.setFontSize(11); doc.setFont("helvetica","bold");
        doc.text("Inversões registradas", 14, yInv);
        doc.autoTable({
          head: [["Data","Tipo","Detalhe"]],
          body: inv,
          startY: yInv + 3,
          theme: "grid",
          styles: { fontSize: 9 },
          headStyles: { fillColor: [139,92,246], textColor: 255 },
        });
      }
    } else {
      // Single — uma tabela só
      doc.autoTable({
        head: [["Dia","Horário","Intervalo","Carga contratual"]],
        body: buildBody(cur.days),
        foot: cur.totalContract > 0 ? [["Total semanal","","",fmtHHMM(cur.totalContract)]] : null,
        startY: 44,
        theme: "grid",
        styles: { fontSize: 10 },
        headStyles: { fillColor: [212,160,23], textColor: 255 },
        footStyles: { fillColor: [240,235,210], fontStyle:"bold", textColor: 0 },
      });
    }

    // Rodapé
    doc.setFontSize(8); doc.setTextColor(150);
    doc.text(`Gerado em ${new Date().toLocaleString("pt-BR")}`, 14, H - 8);
    const safeName = (selEmp?.name || "horario").replace(/[^a-zA-Z0-9]/g,"_");
    doc.save(`horario_${safeName}.pdf`);
    onUpdate("_toast", "📄 PDF do horário gerado");
  }

  function removeInversion(kind, key) {
    // kind: "override" | "reanchor"; key: data ISO
    const cur = empSchedules[empSchedules.length - 1];
    if (!cur) return;
    let updated;
    if (kind === "override") {
      const next = { ...(cur.overrides || {}) };
      delete next[key];
      updated = { ...cur, overrides: next };
    } else {
      updated = { ...cur, reanchors: (cur.reanchors || []).filter(r => r.date !== key) };
    }
    onUpdate("workSchedules", prev => {
      const list = (prev?.[restaurantId]?.[selEmpId] ?? []).slice();
      list[list.length - 1] = updated;
      return { ...prev, [restaurantId]: { ...(prev?.[restaurantId] ?? {}), [selEmpId]: list } };
    });
    onUpdate("_toast", `Inversão de ${fmtDate(key)} removida`);
  }

  // ── Calculated values (sempre da SEMANA SENDO EDITADA — `days` aponta pra A ou B) ──
  const activeDayCount = Object.values(days).filter(d => d.active).length;
  const folgaDayCount = 7 - activeDayCount;
  const allHoursFilled = hasAllHours(days);
  const storageDaysCalc = allHoursFilled && activeDayCount > 0 ? toStorage(days, false) : {};
  const { totalContract } = allHoursFilled && activeDayCount > 0 ? validateWeekSchedule(storageDaysCalc) : { totalContract: 0 };
  const MIN_WEEK = 43*60+55, MAX_WEEK = 44*60;
  const isEmpFreela = selEmp?.isFreela;
  const weekOk = allHoursFilled && activeDayCount > 0 && (isEmpFreela || (totalContract >= MIN_WEEK && totalContract <= MAX_WEEK));
  // No alternating: também checa carga da OUTRA semana pra mostrar status global "ambas OK"
  const otherDays = isAlternating ? (editWeek === "A" ? editDaysB : editDays) : null;
  const otherActive = otherDays ? Object.values(otherDays).filter(d => d.active).length : 0;
  const otherFilled = otherDays ? hasAllHours(otherDays) : true;
  const otherTC = (otherDays && otherFilled && otherActive > 0)
    ? validateWeekSchedule(toStorage(otherDays, false)).totalContract : 0;
  const otherOk = !otherDays || (otherFilled && otherActive > 0 && (isEmpFreela || (otherTC >= MIN_WEEK && otherTC <= MAX_WEEK)));
  // bothWeeksOk pode ser usado em fases futuras pra mostrar status global
  // eslint-disable-next-line no-unused-vars
  const bothWeeksOk = isAlternating ? (weekOk && otherOk) : weekOk;

  // ── IA Assistant state ──
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiResult, setAiResult] = useState(null); // { days: {}, message: "" }
  const [aiLoading, setAiLoading] = useState(false);

  // ── Copy from another employee ──
  const [copyPickerOpen, setCopyPickerOpen] = useState(false);
  // Empregados que têm horário cadastrado (exceto o atual)
  const empsWithSched = restEmps.filter(e => e.id !== selEmpId && (workSchedules?.[restaurantId]?.[e.id]?.length ?? 0) > 0);

  function copyFromEmployee(srcEmpId) {
    const srcScheds = workSchedules?.[restaurantId]?.[srcEmpId] ?? [];
    const src = srcScheds[srcScheds.length - 1];
    if (!src) { onUpdate("_toast","⚠️ Empregado de origem sem horário cadastrado"); return; }

    // Validação de compatibilidade: só copia entre tipos iguais (single↔single OU alternating↔alternating)
    const srcIsAlt = src.type === "alternating";
    const dstIsAlt = isAlternating;
    if (srcIsAlt !== dstIsAlt) {
      const srcLabel = srcIsAlt ? "Alternada A/B" : "Fixa";
      const dstLabel = dstIsAlt ? "Alternada A/B" : "Fixa";
      const srcEmp = restEmps.find(e => e.id === srcEmpId);
      alert(
        `⚠️ Tipos de escala incompatíveis\n\n`
        + `Origem (${srcEmp?.name || "?"}): ${srcLabel}\n`
        + `Destino atual: ${dstLabel}\n\n`
        + `Pra copiar, troque o tipo do horário do empregado destino pra "${srcLabel}" antes — usando o toggle "📅 Fixo / 🔄 Alternado A/B" no topo.`
      );
      return;
    }

    const srcEmp = restEmps.find(e => e.id === srcEmpId);
    if (srcIsAlt) {
      // Copia AMBAS as semanas A e B do source pro destino
      const daysA = src.weeks?.A?.days || {};
      const daysB = src.weeks?.B?.days || {};
      setEditDays(toInternal({ days: daysA }));
      setEditDaysB(toInternal({ days: daysB }));
      // Mantém anchor e inversões do destino — só copia os horários (decisão de design: anchor é algo pessoal)
      onUpdate("_toast", `📋 Semanas A e B copiadas de ${srcEmp?.name ?? "outro empregado"}`);
    } else {
      // Single → single: copia o days
      setDays(toInternal({ days: src.days || {} }));
      onUpdate("_toast", `📋 Horário copiado de ${srcEmp?.name ?? "outro empregado"}`);
    }
    setErrors([]);
    setValidated(false);
    setCopyPickerOpen(false);
  }

  function parseAiCommand(text) {
    const DIAS_FULL = [
      {names:["dom","domingo"], idx:0}, {names:["seg","segunda"], idx:1},
      {names:["ter","terca","terça"], idx:2}, {names:["qua","quarta"], idx:3},
      {names:["qui","quinta"], idx:4}, {names:["sex","sexta"], idx:5},
      {names:["sab","sabado","sábado"], idx:6},
    ];
    const normText = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,"").replace(/\s+/g," ").trim();
    // Normaliza: lowercase, remove acentos, normaliza espaços
    const lower = normText(text);
    // parseAi opera SEMPRE na semana sendo editada agora (`days`, alias dinâmico A ou B)
    const newDays = JSON.parse(JSON.stringify(days));
    const changes = [];

    // Helper: extrai indices de dias de um trecho (sem duplicar)
    function extractDays(txt) {
      const found = new Set();
      const rangeMatch = txt.match(/(\w+)\s+a\s+(\w+)/);
      if (rangeMatch) {
        const fromD = DIAS_FULL.find(d => d.names.some(n => rangeMatch[1].includes(n)));
        const toD = DIAS_FULL.find(d => d.names.some(n => rangeMatch[2].includes(n)));
        if (fromD && toD) {
          for (let i = fromD.idx; i !== (toD.idx + 1) % 7; i = (i + 1) % 7) found.add(i);
          return [...found];
        }
      }
      DIAS_FULL.forEach(d => {
        const sorted = [...d.names].sort((a,b)=>b.length-a.length);
        for (const n of sorted) { if (txt.includes(n)) { found.add(d.idx); break; } }
      });
      return [...found];
    }

    // Helper: formata hora "HH:MM" a partir de captura (h, m opcional); aceita "24" como "00:00" (fim de jornada)
    function fmtTime(h, m) {
      const hi = parseInt(h);
      if (isNaN(hi)) return null;
      const mi = m ? String(parseInt(m)).padStart(2,"0") : "00";
      if (hi === 24) return "00:00"; // cruza meia-noite, calcDayHours lida
      if (hi < 0 || hi > 23) return null;
      return `${String(hi).padStart(2,"0")}:${mi}`;
    }

    // ═══ 0. MODO MULTI-GRUPO ═══
    // Detecta listas como:
    //   "Quarta e Quinta: das 15h às 24h com 1h de intervalo"
    //   "Sexta e Sábado: das 12:20h às 24h com 2h de intervalo"
    //   "Domingo: das 11h às 19:30h com 1h de intervalo"
    // Separadores suportados: linha nova, •, ·, ;, - inicial
    const rawSegs = text.split(/[\n•·;]|(?:^\s*[-*]\s)/m).map(s => s.trim()).filter(s => s.length > 2);
    const groupSegs = rawSegs.map(seg => {
      const segLow = normText(seg);
      const dayIds = extractDays(segLow);
      // Precisa ter pelo menos um dia E algum horário (d{1,2}h ou d{1,2}:d{1,2})
      const hasTime = /\b\d{1,2}\s*[:.h]/.test(segLow) || /das?\s+\d/.test(segLow);
      return { seg, segLow, dayIds, hasTime };
    }).filter(g => g.dayIds.length > 0 && g.hasTime);

    if (groupSegs.length >= 2) {
      const mentioned = new Set();
      groupSegs.forEach(({ seg, segLow, dayIds }) => {
        // "das 15h às 24h", "das 12:20 às 24h", "12:20 às 24:00", "das 11h as 19h30"
        const rangeMatch = segLow.match(/(?:das?\s+)?(\d{1,2})\s*[:.h]?\s*(\d{2})?\s*(?:h\s*)?(?:as?|até|ate|a)\s+(\d{1,2})\s*[:.h]?\s*(\d{2})?/);
        let inTime = null, outTime = null;
        if (rangeMatch) {
          inTime = fmtTime(rangeMatch[1], rangeMatch[2]);
          outTime = fmtTime(rangeMatch[3], rangeMatch[4]);
        } else {
          const entM = segLow.match(/(?:entra(?:da|ndo)?|inicia\w*)\s*(?:as?\s*)?(\d{1,2})\s*[:.h]?\s*(\d{2})?/);
          const saiM = segLow.match(/(?:sai(?:da|ndo)?|termin\w*)\s*(?:as?\s*)?(\d{1,2})\s*[:.h]?\s*(\d{2})?/);
          if (entM) inTime = fmtTime(entM[1], entM[2]);
          if (saiM) outTime = fmtTime(saiM[1], saiM[2]);
        }
        // Intervalo: "1h de intervalo", "2h de intervalo", "30 min de intervalo", "intervalo 60min", "com 1h"
        let breakMin = null;
        const intHoraM = segLow.match(/(\d+)\s*h(?:ora)?s?\s*(?:de\s+)?intervalo|(?:com\s+)?intervalo\s*(?:de\s+)?(\d+)\s*h(?:ora)?s?/);
        const intMinM = segLow.match(/(\d+)\s*(?:min|minutos?)\s*(?:de\s+)?intervalo|intervalo\s*(?:de\s+)?(\d+)\s*(?:min|minutos?)/);
        if (intHoraM) breakMin = parseInt(intHoraM[1] || intHoraM[2]) * 60;
        else if (intMinM) breakMin = parseInt(intMinM[1] || intMinM[2]);

        dayIds.forEach(idx => {
          mentioned.add(idx);
          newDays[idx] = {
            active: true,
            in: inTime || newDays[idx]?.in || "",
            out: outTime || newDays[idx]?.out || "",
            break: breakMin != null ? breakMin : (newDays[idx]?.break || 0),
          };
        });
        const label = dayIds.sort().map(i => WEEK_DAYS_LABEL[i]).join("/");
        const parts = [];
        if (inTime && outTime) parts.push(`${inTime}-${outTime}`);
        else { if (inTime) parts.push(`ent ${inTime}`); if (outTime) parts.push(`sai ${outTime}`); }
        if (breakMin != null) parts.push(`int ${breakMin}min`);
        changes.push(`${label}: ${parts.join(", ") || "(sem horário)"}`);
      });
      // Dias não mencionados em nenhum grupo = folga
      for (let i = 0; i < 7; i++) {
        if (!mentioned.has(i)) {
          newDays[i] = { active: false };
          changes.push(`${WEEK_DAYS_LABEL[i]}: Folga`);
        }
      }
      // Validação CLT
      const warnings = [];
      const activeDays = Object.values(newDays).filter(d => d.active);
      if (activeDays.length === 0) warnings.push("Nenhum dia de trabalho definido.");
      if (activeDays.length > 6) warnings.push("Mais de 6 dias pode violar a CLT.");
      const allHaveHours = activeDays.every(d => d.in && d.out);
      if (allHaveHours && activeDays.length > 0) {
        const stDays = {};
        Object.entries(newDays).forEach(([idx, d]) => { if (d.active && d.in && d.out) stDays[idx] = { in:d.in, out:d.out, break:d.break??0 }; });
        const { errors: errs, totalContract: tc } = validateWeekSchedule(stDays);
        if (tc > 0) changes.push(`Carga semanal: ${fmtHHMM(tc)}`);
        errs.forEach(e => warnings.push(e));
      }
      return { days: newDays, message: changes.join("\n"), warnings };
    }

    // ═══ 1. Parse carga horária semanal ═══
    // Aceita: "divida/dividindo/divide 44h", "44 horas semanais", "44 semanis", "44h semanais"
    const cargaMatch = lower.match(/(?:divid\w*\s+(?:as?\s+)?)?(\d{2,3})\s*(?:h(?:oras?)?|hrs?)?\s*semana\w*/);
    let cargaSemanalMin = 0;
    let numDiasTrab = 0;
    if (cargaMatch) {
      cargaSemanalMin = parseInt(cargaMatch[1]) * 60;
    }
    // "em X dias"
    const emDiasMatch = lower.match(/em\s+(\d)\s*dias/);
    if (emDiasMatch) numDiasTrab = parseInt(emDiasMatch[1]);

    // ═══ 2. Parse folga ═══
    // Aceita: folga/folgue/folgando/folgar/folgou + dias
    const folgaDays = new Set();
    const folgaMatch = lower.match(/folg\w*\s+(?:esse\s+empregado\s+|para\s+(?:ele\s+)?|(?:nos?\s+)?(?:dias?\s+)?(?:de?\s+)?)?(.+?)(?:\s+e\s+trabalh|\s+(?:divid|entrand|entrad|horari|interval|escala)\w*|\s*[,.]|$)/);
    if (folgaMatch) {
      extractDays(folgaMatch[1]).forEach(idx => {
        folgaDays.add(idx);
        newDays[idx] = { active: false };
        changes.push(`${WEEK_DAYS_LABEL[idx]}: Folga`);
      });
    }

    // ═══ 3. Parse "trabalha/trabalhe nos outros dias" ═══
    const trabOutros = /trabalh\w*\s+(?:n[oa]s?\s+)?outr\w*\s+dias?/.test(lower);
    let trabExplicitoCount = 0; // rastreia se houve menção explícita de dias de trabalho
    if (trabOutros) {
      for (let i = 0; i < 7; i++) {
        if (!folgaDays.has(i)) {
          newDays[i] = { active: true, in: newDays[i]?.in||"", out: newDays[i]?.out||"", break: newDays[i]?.break||0 };
          trabExplicitoCount++;
        }
      }
      changes.push(`Trabalha: ${7 - folgaDays.size} dias`);
    } else {
      // Parse dias específicos de trabalho
      const trabMatch = lower.match(/trabalh\w*\s+(?:n[oa]s?\s+)?(?:dias?\s+)?(.+?)(?:\s+(?:folg|divid|entrand|entrad|horari|interval|escala)\w*|\s*[,.]|$)/);
      if (trabMatch && !trabOutros) {
        extractDays(trabMatch[1]).forEach(idx => {
          if (!folgaDays.has(idx)) {
            newDays[idx] = { active: true, in: newDays[idx]?.in||"", out: newDays[idx]?.out||"", break: newDays[idx]?.break||0 };
            changes.push(`${WEEK_DAYS_LABEL[idx]}: Trabalha`);
            trabExplicitoCount++;
          }
        });
      }
    }

    // ═══ 4. Parse "escala 6x1" ═══
    const escalaMatch = lower.match(/escala\s+(\d)x(\d)/);
    if (escalaMatch) {
      const trab = parseInt(escalaMatch[1]), folg = parseInt(escalaMatch[2]);
      let count = 0;
      for (let i = 1; i <= 6; i++) {
        if (count < trab) { newDays[i] = { active: true, in: newDays[i]?.in||"", out: newDays[i]?.out||"", break: newDays[i]?.break||0 }; count++; }
        else { newDays[i] = { active: false }; folgaDays.add(i); }
      }
      newDays[0] = { active: false }; folgaDays.add(0);
      changes.push(`Escala ${trab}x${folg}: ${trab} dias trabalho, ${folg} folga (dom folga)`);
    }

    // ═══ 5. Se carga horária + folgas, inferir dias de trabalho ═══
    if (cargaSemanalMin > 0 && folgaDays.size > 0 && numDiasTrab === 0) {
      numDiasTrab = 7 - folgaDays.size;
    }
    if (cargaSemanalMin > 0 && numDiasTrab > 0) {
      const trabIds = [];
      for (let i = 0; i < 7; i++) {
        if (!folgaDays.has(i) && trabIds.length < numDiasTrab) {
          trabIds.push(i);
          newDays[i] = { active: true, in: newDays[i]?.in||"", out: newDays[i]?.out||"", break: newDays[i]?.break||0 };
        } else if (!folgaDays.has(i)) {
          newDays[i] = { active: false }; folgaDays.add(i);
          changes.push(`${WEEK_DAYS_LABEL[i]}: Folga (excedente)`);
        }
      }
      changes.push(`Carga: ${Math.round(cargaSemanalMin/60)}h semanais em ${numDiasTrab} dias`);
    }

    // ═══ 6. Parse horários ═══
    // Aceita: entra/entrada/entrando + as/a/s + hora
    // Também "horario de entrada as 10", "entrandoa s 10"
    const entradaMatch = lower.match(/(?:entrand\w*\s*a?\s*s?\s*|entra(?:da)?\s*(?:as?\s*)?|horario\s+de\s+entrada\s*(?:as?\s*)?)(\d{1,2})\s*[:.h]?\s*(\d{2})?/);
    const saidaMatch = lower.match(/(?:saind\w*\s*a?\s*s?\s*|sa[ii](?:da)?\s*(?:as?\s*)?|horario\s+de\s+saida\s*(?:as?\s*)?)(\d{1,2})\s*[:.h]?\s*(\d{2})?/);
    const intervaloMatch = lower.match(/intervalo\s*(?:de\s+)?(\d+)\s*(?:min|minutos?|h|hora)/);

    const entH = entradaMatch ? `${String(parseInt(entradaMatch[1])).padStart(2,"0")}:${entradaMatch[2]||"00"}` : null;
    const saiH = saidaMatch ? `${String(parseInt(saidaMatch[1])).padStart(2,"0")}:${saidaMatch[2]||"00"}` : null;
    const breakMin = intervaloMatch ? parseInt(intervaloMatch[1]) * (intervaloMatch[0].includes("h")?60:1) : null;

    // ═══ 7. Se tem carga + entrada (sem saída), calcular saída ═══
    let calcSaiH = saiH;
    if (cargaSemanalMin > 0 && entH && !saiH && numDiasTrab > 0) {
      const dailyMin = Math.round(cargaSemanalMin / numDiasTrab);
      const bk = breakMin ?? 60;
      const totalMin = dailyMin + bk;
      const entMin = parseInt(entH.split(":")[0]) * 60 + parseInt(entH.split(":")[1]);
      const saiMin = entMin + totalMin;
      calcSaiH = `${String(Math.floor(saiMin / 60) % 24).padStart(2,"0")}:${String(saiMin % 60).padStart(2,"0")}`;
      const jornH = Math.floor(dailyMin / 60);
      const jornM = dailyMin % 60;
      changes.push(`Jornada: ${jornH}h${jornM > 0 ? String(jornM).padStart(2,"0") + "min" : ""}/dia`);
      changes.push(`Entrada: ${entH} → Saída: ${calcSaiH} (intervalo ${bk}min)`);
    } else {
      if (entH) changes.push(`Entrada: ${entH}`);
      if (saiH) changes.push(`Saída: ${saiH}`);
    }
    if (breakMin !== null && !(cargaSemanalMin > 0 && entH && !saiH)) changes.push(`Intervalo: ${breakMin}min`);

    // ═══ 7.5. INFERÊNCIA DE COMPLEMENTO ═══
    // Caso "folga sábado e domingo, entra 09:00 sai 18:48 intervalo 60min":
    // user só citou folgas + um horário, sem dizer "trabalha em X". Assumir que TODOS
    // os dias não-mencionados como folga trabalham com o horário fornecido.
    // Importante: SOBRESCREVE active=false residual de versão anterior (loadEmp pode ter trazido
    // folgas antigas que o user agora quer substituir).
    const temHorario = !!(entH || saiH || calcSaiH);
    if (folgaDays.size > 0 && trabExplicitoCount === 0 && temHorario && !escalaMatch && cargaSemanalMin === 0) {
      const inferidos = [];
      for (let i = 0; i < 7; i++) {
        if (!folgaDays.has(i)) {
          newDays[i] = { active: true, in: newDays[i]?.in || "", out: newDays[i]?.out || "", break: newDays[i]?.break || 0 };
          inferidos.push(WEEK_DAYS_LABEL[i]);
        }
      }
      if (inferidos.length > 0) {
        changes.push(`Inferido — Trabalha em ${inferidos.join(", ")} com o horário fornecido`);
      }
    }

    // Apply horários aos dias ativos
    const finalEnt = entH;
    const finalSai = calcSaiH || saiH;
    const finalBreak = breakMin ?? (cargaSemanalMin > 0 && entH && !saiH ? 60 : null);
    if (finalEnt || finalSai || finalBreak !== null) {
      const targetDays = [0,1,2,3,4,5,6].filter(i => newDays[i]?.active);
      targetDays.forEach(idx => {
        if (finalEnt) newDays[idx] = { ...newDays[idx], in: finalEnt };
        if (finalSai) newDays[idx] = { ...newDays[idx], out: finalSai };
        if (finalBreak !== null) newDays[idx] = { ...newDays[idx], break: finalBreak };
      });
    }

    // ═══ 8. Validação CLT ═══
    const warnings = [];
    const activeDays = Object.values(newDays).filter(d => d.active);
    if (activeDays.length === 0) warnings.push("Nenhum dia de trabalho definido.");
    if (activeDays.length > 6) warnings.push("Mais de 6 dias de trabalho pode violar a CLT.");

    const allHaveHours = activeDays.every(d => d.in && d.out);
    if (allHaveHours && activeDays.length > 0) {
      const stDays = {};
      Object.entries(newDays).forEach(([idx, d]) => { if (d.active && d.in && d.out) stDays[idx] = { in:d.in, out:d.out, break:d.break??0 }; });
      const { errors: errs, totalContract: tc } = validateWeekSchedule(stDays);
      if (tc > 0) changes.push(`Carga semanal: ${fmtHHMM(tc)}`);
      errs.forEach(e => warnings.push(e));
    }

    if (changes.length === 0) return { days: null, message: "Não consegui entender. Tente algo como:\n• \"folga segunda e terça, entrada 10:00\"\n• \"entra 08:00 sai 17:00 intervalo 60min\"\n• \"escala 6x1\"\n• \"folgue seg e ter, trabalhe nos outros dias, 44h semanais entrando as 10\"", warnings: [] };
    return { days: newDays, message: changes.join("\n"), warnings };
  }

  function handleAiSubmit() {
    if (!aiPrompt.trim()) return;
    setAiLoading(true);
    setTimeout(() => {
      const result = parseAiCommand(aiPrompt);
      setAiResult(result);
      setAiLoading(false);
    }, 300);
  }

  function applyAiSuggestion() {
    if (aiResult?.days) {
      // Aplica na semana sendo editada (A ou B no alternating, única no single)
      setDays(aiResult.days);
      setErrors([]);
      setAiResult(null);
      setAiPrompt("");
      setAiOpen(false);
    }
  }

  // ── Styles ──
  const cardS = { ...S.card, marginBottom: mobileOnly ? 8 : 12, padding: mobileOnly ? "10px 12px" : undefined };
  const toggleOn = { width: 44, height: 24, borderRadius: 12, border: "none", cursor: "pointer", position: "relative", background: "var(--ac)", transition: "background .2s" };
  const toggleOff = { ...toggleOn, background: "var(--border)" };
  const toggleDot = (on) => ({ position: "absolute", top: 2, left: on ? 22 : 2, width: 20, height: 20, borderRadius: 10, background: "#fff", transition: "left .2s", boxShadow: "0 1px 3px rgba(0,0,0,.2)" });

  // ═══ LIST VIEW (employee selection) — grouped by area ═══
  if (!selEmpId) {
    const restRoles = roles?.filter(r => r.restaurantId === restaurantId) ?? [];
    const byArea = {}; AREAS.forEach(a => { byArea[a] = []; });
    const noArea = [];
    restEmps.forEach(emp => {
      const role = restRoles.find(r => r.id === emp.roleId);
      const a = role?.area;
      if (a && byArea[a]) byArea[a].push(emp);
      else noArea.push(emp);
    });

    const renderEmpCard = (emp) => {
      const sched = workSchedules?.[restaurantId]?.[emp.id] ?? [];
      const cur = sched[sched.length - 1];
      const hasHoursComplete = cur?.hoursComplete !== false;
      const workDays = cur ? Object.keys(cur.days).length : 0;
      const folgaDays = cur ? 7 - workDays : 0;
      const role = restRoles.find(r => r.id === emp.roleId);
      const areaColor = role?.area ? AREA_COLORS[role.area] : "var(--text3)";
      const initial = emp.name?.trim()?.[0]?.toUpperCase() ?? "?";

      // Status compacto (lado direito)
      let statusBadge = null;
      if (!cur) {
        statusBadge = <span style={{background:"var(--bg1)",color:"var(--text3)",fontSize:mobileOnly?10:11,padding:mobileOnly?"3px 8px":"5px 12px",borderRadius:8,fontWeight:600,border:"1px solid var(--border)"}}>Sem horário</span>;
      } else if (!hasHoursComplete) {
        statusBadge = <span style={{background:"#f59e0b18",color:"#b45309",fontSize:mobileOnly?10:11,padding:mobileOnly?"3px 8px":"5px 12px",borderRadius:8,fontWeight:700,border:"1px solid #f59e0b33"}}>⏳ {mobileOnly?"Pendente":"Horários pendentes"}</span>;
      } else {
        statusBadge = <span style={{background:"#10b98118",color:"#047857",fontSize:mobileOnly?10:11,padding:mobileOnly?"3px 8px":"5px 12px",borderRadius:8,fontWeight:700,border:"1px solid #10b98133",fontFamily:"'DM Mono',monospace"}}>{fmtHHMM(cur.totalContract)}/sem</span>;
      }

      if (mobileOnly) {
        // Mobile: layout compacto existente
        return (
          <div key={emp.id} onClick={()=>loadEmp(emp.id)} style={{...S.card,marginBottom:6,padding:"8px 10px",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{color:"var(--text)",fontWeight:600,fontSize:13,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                {emp.name}
                {cur && !hasHoursComplete && <span style={{background:"#f59e0b22",color:"#f59e0b",fontSize:9,padding:"2px 6px",borderRadius:6,fontWeight:700}}>PENDENTE</span>}
              </div>
              <div style={{color:"var(--text3)",fontSize:10}}>
                {cur
                  ? (hasHoursComplete ? `${fmtHHMM(cur.totalContract)}/sem` : `${workDays} dias, ${folgaDays} folga(s)`)
                  : "Sem horario"}
              </div>
            </div>
            <span style={{color:ac,fontSize:16,fontWeight:700,flexShrink:0,paddingLeft:6}}>&rsaquo;</span>
          </div>
        );
      }

      // Desktop: layout espaçoso com avatar, grid, status e seta
      return (
        <div key={emp.id} onClick={()=>loadEmp(emp.id)} onMouseEnter={e=>e.currentTarget.style.borderColor="var(--ac)66"} onMouseLeave={e=>e.currentTarget.style.borderColor="var(--border)"}
          style={{...S.card,marginBottom:10,padding:"16px 20px",cursor:"pointer",display:"grid",gridTemplateColumns:"48px 1fr auto 20px",gap:16,alignItems:"center",transition:"border-color .15s"}}>
          {/* Avatar com inicial */}
          <div style={{width:48,height:48,borderRadius:12,background:`${areaColor}15`,border:`2px solid ${areaColor}33`,display:"flex",alignItems:"center",justifyContent:"center",color:areaColor,fontWeight:800,fontSize:18,letterSpacing:-0.5}}>
            {initial}
          </div>
          {/* Nome + cargo + vigência */}
          <div style={{minWidth:0}}>
            <div style={{color:"var(--text)",fontWeight:700,fontSize:15,marginBottom:3,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{emp.name}</div>
            <div style={{color:"var(--text3)",fontSize:12,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
              {role && <span>{role.name}</span>}
              {cur && hasHoursComplete && <span>· Vigente desde {fmtDate(cur.validFrom)}</span>}
              {cur && !hasHoursComplete && <span>· {workDays} dias de trabalho</span>}
            </div>
          </div>
          {/* Status badge */}
          <div style={{flexShrink:0}}>{statusBadge}</div>
          {/* Seta */}
          <span style={{color:"var(--text3)",fontSize:18,fontWeight:600,textAlign:"right"}}>›</span>
        </div>
      );
    };

    return (
      <div>
        <p style={{color:"var(--text3)",fontSize:mobileOnly?11:14,marginBottom:mobileOnly?10:20}}>
          Selecione um empregado para cadastrar ou editar o horário de trabalho:
        </p>
        {AREAS.map(area => {
          const emps = byArea[area];
          if (emps.length === 0) return null;
          return (
            <div key={area} style={{marginBottom:mobileOnly?16:24}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:mobileOnly?8:12,paddingBottom:mobileOnly?4:6,borderBottom:`2px solid ${AREA_COLORS[area]}33`}}>
                <span style={{width:mobileOnly?8:10,height:mobileOnly?8:10,borderRadius:"50%",background:AREA_COLORS[area],display:"inline-block"}}></span>
                <span style={{color:AREA_COLORS[area],fontSize:mobileOnly?12:13,fontWeight:700,letterSpacing:1}}>{area.toUpperCase()}</span>
                <span style={{color:"var(--text3)",fontSize:mobileOnly?11:12,marginLeft:4}}>({emps.length})</span>
              </div>
              {emps.map(renderEmpCard)}
            </div>
          );
        })}
        {noArea.length > 0 && (
          <div style={{marginBottom:mobileOnly?16:24}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:mobileOnly?8:12,paddingBottom:mobileOnly?4:6,borderBottom:"2px solid var(--border)"}}>
              <span style={{color:"var(--text3)",fontSize:mobileOnly?12:13,fontWeight:700,letterSpacing:1}}>SEM ÁREA</span>
              <span style={{color:"var(--text3)",fontSize:mobileOnly?11:12,marginLeft:4}}>({noArea.length})</span>
            </div>
            {noArea.map(renderEmpCard)}
          </div>
        )}
      </div>
    );
  }

  // ═══ EDIT VIEW (single employee) ═══
  return (
    <div>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",gap:mobileOnly?6:10,marginBottom:mobileOnly?10:16,flexWrap:"wrap"}}>
        {!forcedEmpId && (
          <button onClick={()=>{setSelEmpId(null);setErrors([]);setShowValidFrom(false);setSaveMode(null);setValidated(false);}} style={{...S.btnSecondary,fontSize:mobileOnly?11:12,padding:mobileOnly?"5px 10px":"6px 14px"}}>← Voltar</button>
        )}
        <div style={{flex:1,minWidth:0}}>
          {!forcedEmpId && <div style={{color:"var(--text)",fontWeight:700,fontSize:mobileOnly?14:15}}>{selEmp?.name}</div>}
          <div style={{color:"var(--text3)",fontSize:mobileOnly?10:11}}>{mobileOnly?`${activeDayCount} dias · ${folgaDayCount} folga(s)`:`Horario semanal · ${activeDayCount} dias · ${folgaDayCount} folga(s)`}</div>
        </div>
        {empSchedules.length > 0 && (
          <button onClick={async ()=>{
            if (!await appConfirm(`Resetar o horário de ${selEmp?.name}?\n\nTodas as versões (${empSchedules.length}) serão apagadas permanentemente. O empregado ficará sem horário cadastrado.`)) return;
            onUpdate("workSchedules", prev => {
              const w = { ...(prev ?? {}) };
              if (w[restaurantId]) {
                const emp = { ...w[restaurantId] };
                delete emp[selEmpId];
                w[restaurantId] = emp;
              }
              return w;
            });
            // Reset edit state
            const fresh = {};
            for (let i = 0; i < 7; i++) fresh[i] = { active: true };
            setEditDays(fresh);
            setErrors([]);
            setShowValidFrom(false);
            setSaveMode(null);
            onUpdate("_toast", `🔄 Horário de ${selEmp?.name} resetado`);
          }} style={{...S.btnSecondary,fontSize:mobileOnly?11:12,padding:mobileOnly?"5px 10px":"6px 14px",color:"var(--red)",borderColor:"var(--red)44"}}>
            🔄 {mobileOnly?"Resetar":"Resetar horário"}
          </button>
        )}
        {empSchedules.length > 0 && (
          <button onClick={exportarHorarioPDF}
            style={{...S.btnSecondary,fontSize:mobileOnly?11:12,padding:mobileOnly?"5px 10px":"6px 14px",color:"var(--ac-text)",borderColor:"var(--ac)44"}}>
            📄 {mobileOnly?"PDF":"Imprimir horário"}
          </button>
        )}
      </div>

      {/* ═══ Horário ATIVO — só pra quem já tem horário cadastrado ═══ */}
      {empSchedules.length > 0 && (() => {
        const current = empSchedules[empSchedules.length - 1];
        if (!current) return null;
        const isCurAlt = current.type === "alternating";
        // Helper: renderiza um bloco de "dias" (semana única OU semana A/B)
        function renderDaysBlock(daysObj, opts = {}) {
          const { highlight, totalContract: tc, hoursComplete: hc, label } = opts;
          return (
            <div style={{
              ...(label?{
                background: highlight ? "var(--ac-bg)" : "var(--bg1)",
                border: `2px solid ${highlight ? "var(--ac)" : "var(--border)"}`,
                borderRadius: 8, padding: mobileOnly?"8px 10px":"10px 14px",
              }:{}),
            }}>
              {label && (
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6,flexWrap:"wrap",gap:4}}>
                  <span style={{fontSize:11,fontWeight:700,letterSpacing:0.3,color:highlight?"var(--ac-text)":"var(--text2)"}}>
                    {label} {highlight && "← esta semana"}
                  </span>
                  {tc > 0 && <span style={{fontFamily:"'DM Mono',monospace",fontSize:11,color:highlight?"var(--ac-text)":"var(--text3)",fontWeight:600}}>{fmtHHMM(tc)}/sem</span>}
                </div>
              )}
              {hc === false && (
                <div style={{background:"#f59e0b15",borderRadius:6,padding:"4px 8px",marginBottom:6,fontSize:10,color:"#f59e0b",fontWeight:600}}>⚠️ Horários pendentes</div>
              )}
              {WEEK_DISPLAY_ORDER.map(i => {
                const d = daysObj?.[i];
                const hasShift = d?.in && d?.out;
                const isWorkDay = !!d;
                const isWeekend = i === 0 || i === 6;
                return (
                  <div key={i} style={{
                    padding:mobileOnly?"4px 8px":"5px 10px",
                    marginBottom:2,
                    borderRadius:5,
                    background:isWorkDay && !label ? "var(--bg1)" : "transparent",
                    border:`1px solid ${isWorkDay && !label ? "var(--border)" : "transparent"}`,
                    opacity:isWorkDay?1:0.55,
                    fontSize:mobileOnly?11:12,
                  }}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                      <span style={{color:isWeekend?"#f59e0b":"var(--text)",fontWeight:700,minWidth:30}}>{WEEK_DAYS_LABEL[i]}</span>
                      {hasShift
                        ? <span style={{color:"var(--text2)",fontFamily:"'DM Mono',monospace"}}>{d.in}–{d.out}{d.break>0?` (${d.break}m)`:""}</span>
                        : isWorkDay
                          ? <span style={{color:"#f59e0b",fontSize:10}}>Trab. (pendente)</span>
                          : <span style={{color:"var(--text3)",fontSize:10}}>Folga</span>
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          );
        }
        // Cabeçalho: tipo + vigência
        return (
          <div style={{...S.card, marginBottom: mobileOnly?10:14, padding: mobileOnly?"12px 14px":"16px 20px", borderColor: "var(--ac)33", background: "var(--ac-bg)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,flexWrap:"wrap",gap:6}}>
              <span style={{color:"var(--ac-text)",fontSize:mobileOnly?12:13,fontWeight:700,letterSpacing:0.3}}>
                📋 Horário ativo {isCurAlt && "(Escala alternada A/B)"}
              </span>
              <span style={{color:"var(--text3)",fontSize:11}}>Vigente desde {fmtDate(current.validFrom)}</span>
            </div>
            {!isCurAlt && current.totalContract > 0 && (
              <div style={{background:"var(--bg1)",borderRadius:6,padding:"6px 10px",marginBottom:10,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{color:"var(--text3)",fontSize:11}}>Carga semanal</span>
                <span style={{color:"var(--ac-text)",fontWeight:700,fontSize:13,fontFamily:"'DM Mono',monospace"}}>{fmtHHMM(current.totalContract)}/sem</span>
              </div>
            )}
            {/* Render single ou alternating */}
            {isCurAlt ? (() => {
              const wkHoje = weekTypeForDate(current, today()) || "A";
              const monHoje = getWeekMonday(today());
              // Próximas 4 semanas previstas
              const futuras = [];
              for (let i = 1; i <= 4; i++) {
                const d = new Date(monHoje + "T12:00:00");
                d.setDate(d.getDate() + i * 7);
                const ds = d.toISOString().slice(0,10);
                futuras.push({ monday: ds, week: weekTypeForDate(current, ds) });
              }
              return (
                <>
                  <div style={{background:"var(--bg1)",borderRadius:8,padding:"8px 12px",marginBottom:10,fontSize:12,textAlign:"center"}}>
                    Esta semana ({fmtDate(monHoje)} a {fmtDate((() => { const d = new Date(monHoje+"T12:00:00"); d.setDate(d.getDate()+6); return d.toISOString().slice(0,10); })())}): <strong style={{color:"var(--ac-text)",fontSize:14}}>Semana {wkHoje}</strong>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"1fr 1fr",gap:10,marginBottom:10}}>
                    {renderDaysBlock(current.weeks?.A?.days || {}, {
                      label: "Semana A",
                      highlight: wkHoje === "A",
                      totalContract: current.weeks?.A?.totalContract || 0,
                      hoursComplete: current.weeks?.A?.hoursComplete,
                    })}
                    {renderDaysBlock(current.weeks?.B?.days || {}, {
                      label: "Semana B",
                      highlight: wkHoje === "B",
                      totalContract: current.weeks?.B?.totalContract || 0,
                      hoursComplete: current.weeks?.B?.hoursComplete,
                    })}
                  </div>
                  <div style={{fontSize:10,color:"var(--text3)",padding:"6px 0",borderTop:"1px dashed var(--border)"}}>
                    <strong>Próximas:</strong> {futuras.map((f, i) => (
                      <span key={i} style={{marginRight:8}}>{fmtDate(f.monday).slice(0,5)}: <strong style={{color:f.week==="A"?"var(--ac-text)":"#8b5cf6"}}>{f.week}</strong></span>
                    ))}
                  </div>
                  {/* Botão inverter + lista de inversões existentes */}
                  <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid var(--border)",display:"flex",flexDirection:"column",gap:8}}>
                    <button onClick={()=>{ setInvertingMonday(monHoje); setInvertScope("once"); }}
                      style={{background:"transparent",color:"#8b5cf6",border:"1px dashed #8b5cf6",borderRadius:6,padding:"6px 12px",fontSize:11,fontWeight:600,cursor:"pointer",alignSelf:"flex-start"}}>
                      🔄 Inverter esta semana
                    </button>
                    {((current.overrides && Object.keys(current.overrides).length > 0) || (current.reanchors && current.reanchors.length > 0)) && (
                      <div style={{fontSize:10,color:"var(--text3)"}}>
                        <strong>Inversões registradas:</strong>
                        {(current.reanchors || []).map(r => (
                          <div key={"r"+r.date} style={{display:"flex",alignItems:"center",gap:6,padding:"3px 0"}}>
                            <span style={{flex:1}}>🔁 {fmtDate(r.date)} → re-anchor (sequência inverteu, daqui em diante semana = {r.week})</span>
                            <button onClick={()=>removeInversion("reanchor", r.date)} title="Desfazer" style={{background:"none",border:"none",color:"var(--red)",cursor:"pointer",fontSize:11}}>✕</button>
                          </div>
                        ))}
                        {Object.entries(current.overrides || {}).sort(([a],[b])=>a.localeCompare(b)).map(([dt, wk]) => (
                          <div key={"o"+dt} style={{display:"flex",alignItems:"center",gap:6,padding:"3px 0"}}>
                            <span style={{flex:1}}>📌 {fmtDate(dt)} → override (apenas essa semana = {wk})</span>
                            <button onClick={()=>removeInversion("override", dt)} title="Desfazer" style={{background:"none",border:"none",color:"var(--red)",cursor:"pointer",fontSize:11}}>✕</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              );
            })() : (
              renderDaysBlock(current.days || {}, {})
            )}
            <div style={{fontSize:10,color:"var(--text3)",marginTop:8,fontStyle:"italic",textAlign:"center"}}>
              Edite abaixo pra criar uma nova vigência. Esse card mostra o que está em vigor.
            </div>
          </div>
        );
      })()}

      {/* ═══ Modal de inversão (override vs re-anchor) ═══ */}
      {invertingMonday && (() => {
        const cur = empSchedules[empSchedules.length - 1];
        if (!cur || cur.type !== "alternating") return null;
        const wkAtual = weekTypeForDate(cur, invertingMonday) || "A";
        const wkNovo = wkAtual === "A" ? "B" : "A";
        // Preview das próximas 3 semanas em cada cenário
        const previewOnce = [];
        const previewForever = [];
        for (let i = 0; i < 4; i++) {
          const d = new Date(invertingMonday + "T12:00:00");
          d.setDate(d.getDate() + i * 7);
          const ds = d.toISOString().slice(0,10);
          // Once: só essa semana inverte; demais seguem padrão original
          const onceWk = i === 0 ? wkNovo : weekTypeForDate(cur, ds);
          previewOnce.push({ date: ds, week: onceWk });
          // Forever: a partir desta semana, sequência inverte
          const tmpCur = { ...cur, reanchors: [...(cur.reanchors||[]).filter(r=>r.date!==invertingMonday), { date: invertingMonday, week: wkNovo }].sort((a,b)=>a.date.localeCompare(b.date)) };
          previewForever.push({ date: ds, week: weekTypeForDate(tmpCur, ds) });
        }
        return (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={()=>setInvertingMonday(null)}>
            <div onClick={e=>e.stopPropagation()}
              style={{background:"var(--card-bg)",borderRadius:12,padding:mobileOnly?"18px 16px":"24px 28px",maxWidth:540,width:"100%",maxHeight:"90vh",overflowY:"auto",border:"1px solid var(--border)"}}>
              <div style={{fontSize:16,fontWeight:700,color:"var(--text)",marginBottom:6}}>🔄 Inverter semana</div>
              <div style={{fontSize:12,color:"var(--text3)",marginBottom:14}}>
                Semana de {fmtDate(invertingMonday)} a {fmtDate((() => { const d = new Date(invertingMonday+"T12:00:00"); d.setDate(d.getDate()+6); return d.toISOString().slice(0,10); })())}
              </div>
              <div style={{padding:"10px 12px",background:"var(--bg2)",borderRadius:8,marginBottom:14,fontSize:12}}>
                Hoje essa semana é <strong>Semana {wkAtual}</strong>. Você quer marcar como <strong>Semana {wkNovo}</strong>.
              </div>
              {/* Opção 1: only */}
              <label style={{display:"block",padding:12,borderRadius:8,border:`2px solid ${invertScope==="once"?"var(--ac)":"var(--border)"}`,marginBottom:10,cursor:"pointer",background:invertScope==="once"?"var(--ac-bg)":"transparent"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                  <input type="radio" checked={invertScope==="once"} onChange={()=>setInvertScope("once")}/>
                  <strong style={{color:"var(--text)",fontSize:13}}>Apenas esta semana</strong>
                </div>
                <div style={{fontSize:11,color:"var(--text3)",fontFamily:"'DM Mono',monospace",lineHeight:1.6,paddingLeft:24}}>
                  {previewOnce.map((p, i) => (
                    <div key={i}>
                      • {fmtDate(p.date).slice(0,5)} – {fmtDate((()=>{const d=new Date(p.date+"T12:00:00");d.setDate(d.getDate()+6);return d.toISOString().slice(0,10);})()).slice(0,5)}: <strong style={{color:p.week===wkNovo?"var(--ac-text)":"var(--text2)"}}>Semana {p.week}</strong>{i===0?" (invertida)":i>0?" (padrão)":""}
                    </div>
                  ))}
                </div>
              </label>
              {/* Opção 2: forever */}
              <label style={{display:"block",padding:12,borderRadius:8,border:`2px solid ${invertScope==="forever"?"var(--ac)":"var(--border)"}`,marginBottom:14,cursor:"pointer",background:invertScope==="forever"?"var(--ac-bg)":"transparent"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                  <input type="radio" checked={invertScope==="forever"} onChange={()=>setInvertScope("forever")}/>
                  <strong style={{color:"var(--text)",fontSize:13}}>A partir desta semana, pra sempre</strong>
                </div>
                <div style={{fontSize:11,color:"var(--text3)",fontFamily:"'DM Mono',monospace",lineHeight:1.6,paddingLeft:24}}>
                  {previewForever.map((p, i) => (
                    <div key={i}>
                      • {fmtDate(p.date).slice(0,5)} – {fmtDate((()=>{const d=new Date(p.date+"T12:00:00");d.setDate(d.getDate()+6);return d.toISOString().slice(0,10);})()).slice(0,5)}: <strong style={{color:"var(--ac-text)"}}>Semana {p.week}</strong>
                    </div>
                  ))}
                </div>
              </label>
              <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                <button onClick={()=>setInvertingMonday(null)} style={{...S.btnSecondary,fontSize:13,padding:"8px 16px"}}>Cancelar</button>
                <button onClick={()=>applyInversion(invertingMonday, invertScope)}
                  style={{background:"var(--ac)",color:"#fff",border:"none",borderRadius:8,padding:"8px 18px",fontSize:13,fontWeight:700,cursor:"pointer"}}>
                  🔄 Confirmar
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* History */}
      {empSchedules.length > 1 && (
        <details style={{marginBottom:12}}>
          <summary style={{color:"var(--text3)",fontSize:12,cursor:"pointer",padding:"8px 12px",background:"var(--bg1)",borderRadius:8}}>
            Historico ({empSchedules.length} versoes)
          </summary>
          <div style={{paddingTop:8}}>
            {isOwner && (
              <div style={{padding:"6px 12px 10px",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                <span style={{color:"var(--text3)",fontSize:11}}>Selecione versoes para apagar:</span>
                {selectedSchedIds.size > 0 && (
                  <button onClick={async ()=>{
                    if(!await appConfirm(`Apagar ${selectedSchedIds.size} versao(oes)?`)) return;
                    onUpdate("workSchedules", prev => {
                      const remaining = (prev?.[restaurantId]?.[selEmpId] ?? []).filter(s => !selectedSchedIds.has(s.id));
                      return { ...prev, [restaurantId]: { ...(prev?.[restaurantId]??{}), [selEmpId]: remaining } };
                    });
                    setSelectedSchedIds(new Set());
                    onUpdate("_toast", `${selectedSchedIds.size} versao(oes) apagada(s)`);
                  }} style={{padding:"5px 12px",borderRadius:8,border:"none",background:"var(--red)",color:"#fff",fontWeight:700,cursor:"pointer",fontSize:11}}>
                    Apagar ({selectedSchedIds.size})
                  </button>
                )}
              </div>
            )}
            {[...empSchedules].reverse().slice(1).map(s => (
              <div key={s.id} style={{padding:"8px 12px",borderBottom:"1px solid var(--border)",fontSize:12,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                {isOwner && (
                  <input type="checkbox" checked={selectedSchedIds.has(s.id)}
                    onChange={e=>{ const next = new Set(selectedSchedIds); e.target.checked ? next.add(s.id) : next.delete(s.id); setSelectedSchedIds(next); }}
                    style={{accentColor:"var(--red)",cursor:"pointer",width:14,height:14}}
                  />
                )}
                <span style={{color:"var(--text2)",flex:1,minWidth:140}}>Vigente de {fmtDate(s.validFrom)}</span>
                <span style={{color:"var(--text3)"}}>por {s.createdBy}</span>
                <span style={{color:"var(--text3)"}}>{s.hoursComplete !== false ? `${fmtHHMM(s.totalContract)}/sem` : "dias apenas"}</span>
                <button onClick={async ()=>{
                  if(!await appConfirm(`Reativar esta versão do horário?\n\nOs dias e horários dessa versão serão copiados como nova vigência (a mais recente fica preservada no histórico).`)) return;
                  // Cria uma nova entrada baseada nessa versão antiga, como a mais recente
                  const reactivated = {
                    ...s,
                    id: `${Date.now()}-${Math.random().toString(36).slice(2,5)}`,
                    validFrom: today(),
                    createdBy: currentManagerName,
                    createdAt: new Date().toISOString(),
                    reactivatedFrom: s.id,
                  };
                  onUpdate("workSchedules", prev => {
                    const newList = [...(prev?.[restaurantId]?.[selEmpId] ?? []), reactivated];
                    return { ...prev, [restaurantId]: { ...(prev?.[restaurantId]??{}), [selEmpId]: newList } };
                  });
                  // Carrega a versão reativada no editor
                  setEditDays(toInternal(reactivated));
                  onUpdate("_toast", `♻️ Horário de ${fmtDate(s.validFrom)} reativado como vigência a partir de hoje`);
                }} title="Copiar esta versão como nova vigência" style={{padding:"4px 10px",borderRadius:6,border:"1px solid var(--ac)44",background:"transparent",color:"var(--ac-text)",cursor:"pointer",fontSize:11,fontWeight:600}}>♻️ Reativar</button>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* ═══ COPY FROM ANOTHER EMPLOYEE ═══ */}
      {empsWithSched.length > 0 && (
        <div style={{...S.card,marginBottom:mobileOnly?8:14,padding:mobileOnly?"12px 14px":"18px 22px",border:copyPickerOpen?"2px solid #06b6d444":"1px solid var(--border)",background:copyPickerOpen?"#06b6d408":"var(--card-bg)"}}>
          <div onClick={()=>setCopyPickerOpen(!copyPickerOpen)} style={{display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",gap:12}}>
            <div style={{display:"flex",alignItems:"center",gap:mobileOnly?10:14}}>
              <span style={{fontSize:mobileOnly?18:22,lineHeight:1}}>📋</span>
              <div>
                <div style={{color:"#06b6d4",fontWeight:700,fontSize:mobileOnly?13:15,letterSpacing:-0.2}}>Copiar horário de outro empregado</div>
                {!mobileOnly && <div style={{color:"var(--text3)",fontSize:12,marginTop:2}}>Traga os dias e horários de um colega como ponto de partida</div>}
              </div>
            </div>
            <span style={{color:"var(--text3)",fontSize:14,flexShrink:0}}>{copyPickerOpen?"▲":"▼"}</span>
          </div>
          {copyPickerOpen && (
            <div style={{marginTop:mobileOnly?12:16,paddingTop:mobileOnly?12:16,borderTop:"1px solid var(--border)"}}>
              <p style={{color:"var(--text3)",fontSize:mobileOnly?11:12,margin:"0 0 12px",lineHeight:1.6}}>
                Seleciona um empregado — os dias e horários serão copiados para edição (nada é salvo automaticamente).
              </p>
              <select defaultValue="" onChange={e=>{ if(e.target.value) copyFromEmployee(e.target.value); e.target.value=""; }}
                style={{...S.input,width:"100%",fontSize:mobileOnly?13:14,padding:mobileOnly?undefined:"12px 14px",cursor:"pointer"}}>
                <option value="" disabled>Selecionar empregado...</option>
                {(() => {
                  const restRoles = roles?.filter(r => r.restaurantId === restaurantId) ?? [];
                  const byArea = {};
                  AREAS.forEach(a => { byArea[a] = []; });
                  const noArea = [];
                  empsWithSched.forEach(emp => {
                    const role = restRoles.find(r => r.id === emp.roleId);
                    const a = role?.area;
                    if (a && byArea[a]) byArea[a].push({ emp, role });
                    else noArea.push({ emp, role });
                  });
                  const fmtOpt = ({emp, role}) => {
                    const scheds = workSchedules?.[restaurantId]?.[emp.id] ?? [];
                    const cur = scheds[scheds.length - 1];
                    const hasHoursComplete = cur?.hoursComplete !== false;
                    const isAlt = cur?.type === "alternating";
                    const workDays = cur ? Object.keys(cur.days || {}).length : 0;
                    const tipoLabel = isAlt ? " 🔄A/B" : " 📅Fixa";
                    // Marca incompatibilidade com o tipo do destino atual pra o user ver de cara
                    const incompat = (isAlt !== isAlternating) ? " ⚠ tipo diferente" : "";
                    const suf = hasHoursComplete && cur?.totalContract
                      ? ` —${tipoLabel} ${fmtHHMM(cur.totalContract)}/sem${incompat}`
                      : ` —${tipoLabel} ${workDays} dias (pendente)${incompat}`;
                    return <option key={emp.id} value={emp.id}>{emp.name}{role ? ` (${role.name})` : ""}{suf}</option>;
                  };
                  const groups = [];
                  AREAS.forEach(area => {
                    const list = byArea[area];
                    if (list.length === 0) return;
                    groups.push(<optgroup key={area} label={area}>{list.sort((a,b)=>a.emp.name.localeCompare(b.emp.name)).map(fmtOpt)}</optgroup>);
                  });
                  if (noArea.length > 0) {
                    groups.push(<optgroup key="sem" label="Sem área">{noArea.sort((a,b)=>a.emp.name.localeCompare(b.emp.name)).map(fmtOpt)}</optgroup>);
                  }
                  return groups;
                })()}
              </select>
            </div>
          )}
        </div>
      )}

      {/* ═══ AI ASSISTANT ═══ */}
      <div style={{...S.card,marginBottom:mobileOnly?8:14,padding:mobileOnly?"12px 14px":"18px 22px",border:aiOpen?"2px solid #8b5cf644":"1px solid var(--border)",background:aiOpen?"#8b5cf608":"var(--card-bg)"}}>
        <div onClick={()=>setAiOpen(!aiOpen)} style={{display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",gap:12}}>
          <div style={{display:"flex",alignItems:"center",gap:mobileOnly?10:14}}>
            <span style={{fontSize:mobileOnly?18:22,lineHeight:1}}>🤖</span>
            <div>
              <div style={{color:"#8b5cf6",fontWeight:700,fontSize:mobileOnly?13:15,letterSpacing:-0.2}}>Assistente de Horários</div>
              {!mobileOnly && <div style={{color:"var(--text3)",fontSize:12,marginTop:2}}>Descreva o horário em linguagem natural — a IA preenche os campos</div>}
            </div>
          </div>
          <span style={{color:"var(--text3)",fontSize:14,flexShrink:0}}>{aiOpen?"▲":"▼"}</span>
        </div>
        {aiOpen && (
          <div style={{marginTop:mobileOnly?12:16,paddingTop:mobileOnly?12:16,borderTop:"1px solid var(--border)"}}>
            <p style={{color:"var(--text3)",fontSize:mobileOnly?11:12,margin:"0 0 12px",lineHeight:1.6}}>
              {mobileOnly
                ? "Ex: \"folga dom e seg, 44h semanais entrando às 10\""
                : "Exemplos: \"folga domingo e segunda, entra 08:00 sai 17:00 intervalo 60min\" · \"escala 6x1\" · \"Qua e Qui: 15h às 24h com 1h de intervalo\""}
            </p>
            <div style={{display:"flex",flexDirection:mobileOnly?"column":"row",gap:mobileOnly?8:10}}>
              <input
                type="text" value={aiPrompt}
                onChange={e=>setAiPrompt(e.target.value)}
                onKeyDown={e=>{ if(e.key==="Enter") handleAiSubmit(); }}
                placeholder="Descreva o horário..."
                style={{...S.input,flex:1,fontSize:mobileOnly?13:14,padding:mobileOnly?undefined:"12px 14px",boxSizing:"border-box"}}
              />
              <button onClick={handleAiSubmit} disabled={aiLoading||!aiPrompt.trim()}
                style={{...S.btnPrimary,padding:mobileOnly?"10px 16px":"12px 28px",fontSize:mobileOnly?13:14,fontWeight:700,whiteSpace:"nowrap",opacity:aiLoading||!aiPrompt.trim()?0.5:1,background:"#8b5cf6",width:mobileOnly?"100%":"auto",minWidth:mobileOnly?undefined:140}}>
                {aiLoading ? "..." : "✨ Sugerir"}
              </button>
            </div>
            {aiResult && (
              <div style={{marginTop:mobileOnly?12:16,padding:mobileOnly?"12px 14px":"16px 18px",borderRadius:12,background:aiResult.days?"#f0fdf4":"#fff7ed",border:`1px solid ${aiResult.days?"#10b98133":"#f59e0b33"}`}}>
                <p style={{color:aiResult.days?"var(--green)":"#f59e0b",fontSize:mobileOnly?12:13,fontWeight:700,margin:"0 0 10px"}}>
                  {aiResult.days ? "✅ Sugestão do assistente:" : "⚠️ Não entendido"}
                </p>
                <pre style={{color:"var(--text2)",fontSize:mobileOnly?11:12,margin:0,whiteSpace:"pre-wrap",fontFamily:"'DM Mono',monospace",lineHeight:1.6}}>{aiResult.message}</pre>
                {aiResult.warnings?.length > 0 && (
                  <div style={{marginTop:10,padding:"10px 12px",borderRadius:8,background:"#fff7ed",border:"1px solid #f59e0b33"}}>
                    <p style={{color:"#f59e0b",fontSize:mobileOnly?11:12,fontWeight:700,margin:"0 0 4px"}}>⚠️ Avisos:</p>
                    {aiResult.warnings.map((w,i) => <p key={i} style={{color:"#92400e",fontSize:mobileOnly?11:12,margin:"2px 0"}}>{w}</p>)}
                  </div>
                )}
                {aiResult.days && (
                  <div style={{display:"flex",gap:10,marginTop:14,flexWrap:"wrap"}}>
                    <button onClick={applyAiSuggestion} style={{...S.btnPrimary,padding:mobileOnly?"8px 16px":"10px 20px",fontSize:mobileOnly?12:13,fontWeight:700,background:"#8b5cf6"}}>✓ Aplicar sugestão</button>
                    <button onClick={()=>setAiResult(null)} style={{...S.btnSecondary,padding:mobileOnly?"8px 16px":"10px 20px",fontSize:mobileOnly?12:13}}>Descartar</button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ═══ DAY CARDS (mobile-first) ═══ */}
      <div style={{background:"var(--bg1)",borderRadius:10,padding:mobileOnly?"8px 10px":"10px 14px",marginBottom:mobileOnly?8:12}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{color:"var(--green)",fontSize:11,fontWeight:600}}>ON = Trabalha</span>
          <span style={{color:"var(--text3)",fontSize:11,fontWeight:600}}>OFF = Folga</span>
        </div>
        {!mobileOnly && <p style={{color:"var(--text3)",fontSize:11,margin:"6px 0 0"}}>Defina os dias de trabalho e folga. Horarios podem ser preenchidos agora ou depois.</p>}
      </div>

      {/* ═══ TIPO DE HORÁRIO: Fixo (single) vs Alternado (A/B) ═══ */}
      <div style={{...S.card,marginBottom:mobileOnly?8:12,padding:mobileOnly?"10px 12px":"14px 18px"}}>
        <div style={{display:"flex",gap:8,marginBottom:isAlternating?12:0,flexWrap:"wrap"}}>
          <button onClick={()=>{ if (isAlternating) { setScheduleType("single"); setEditWeek("A"); setValidated(false); setErrors([]); } }}
            style={{
              flex:1, minWidth:140, padding:mobileOnly?"10px 12px":"12px 14px", borderRadius:8,
              border:`2px solid ${!isAlternating?"var(--ac)":"var(--border)"}`,
              background:!isAlternating?"var(--ac-bg)":"transparent",
              cursor:"pointer", fontSize:mobileOnly?12:13, fontWeight:!isAlternating?700:500,
              color:!isAlternating?"var(--ac-text)":"var(--text2)",
            }}>
            📅 Fixo<div style={{fontSize:10,fontWeight:400,marginTop:2,opacity:0.7}}>Mesma semana sempre</div>
          </button>
          <button onClick={()=>{ if (!isAlternating) { setScheduleType("alternating"); setEditWeek("A"); setValidated(false); setErrors([]); } }}
            style={{
              flex:1, minWidth:140, padding:mobileOnly?"10px 12px":"12px 14px", borderRadius:8,
              border:`2px solid ${isAlternating?"var(--ac)":"var(--border)"}`,
              background:isAlternating?"var(--ac-bg)":"transparent",
              cursor:"pointer", fontSize:mobileOnly?12:13, fontWeight:isAlternating?700:500,
              color:isAlternating?"var(--ac-text)":"var(--text2)",
            }}>
            🔄 Alternado A/B<div style={{fontSize:10,fontWeight:400,marginTop:2,opacity:0.7}}>Duas semanas que se revezam</div>
          </button>
        </div>

        {/* Sub-tabs A | B + anchor (só aparecem no modo alternating) */}
        {isAlternating && (
          <>
            <div style={{display:"flex",gap:4,borderBottom:"1px solid var(--border)",marginBottom:10}}>
              {["A","B"].map(w => {
                const isCurrent = editWeek === w;
                const dArr = w === "A" ? editDays : editDaysB;
                const cnt = Object.values(dArr).filter(d => d.active).length;
                const filled = hasAllHours(dArr) && cnt > 0;
                return (
                  <button key={w} onClick={()=>{ setEditWeek(w); setValidated(false); setErrors([]); }}
                    style={{
                      flex:1, padding:mobileOnly?"10px 12px":"10px 14px", border:"none",
                      borderBottom:`3px solid ${isCurrent?"var(--ac)":"transparent"}`,
                      background:"none", cursor:"pointer",
                      fontSize:mobileOnly?13:14, fontWeight:isCurrent?700:500,
                      color:isCurrent?"var(--text)":"var(--text3)",
                    }}>
                    Semana {w}
                    <span style={{fontSize:10,marginLeft:6,padding:"1px 6px",borderRadius:4,background:filled?"#10b98122":cnt>0?"#f59e0b22":"var(--bg2)",color:filled?"#15803d":cnt>0?"#92400e":"var(--text3)"}}>
                      {cnt} dias{filled?" ✓":cnt>0?" ⚠":""}
                    </span>
                  </button>
                );
              })}
            </div>
            <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"1.4fr 1fr",gap:8,marginBottom:6}}>
              <div>
                <label style={{...S.label,fontSize:11}}>Esta segunda-feira (referência)</label>
                <input type="date" value={anchorDate} onChange={e=>setAnchorDate(getWeekMonday(e.target.value))} style={{...S.input,fontSize:13}}/>
              </div>
              <div>
                <label style={{...S.label,fontSize:11}}>É a Semana</label>
                <select value={anchorWeek} onChange={e=>setAnchorWeek(e.target.value)} style={{...S.input,fontSize:13,cursor:"pointer"}}>
                  <option value="A">A</option>
                  <option value="B">B</option>
                </select>
              </div>
            </div>
            <div style={{fontSize:10,color:"var(--text3)",fontStyle:"italic"}}>
              A semana do dia {fmtDate(anchorDate)} é considerada Semana {anchorWeek}. As próximas alternam: {anchorWeek === "A" ? "B → A → B → A..." : "A → B → A → B..."}
            </div>
          </>
        )}
      </div>

      {/* ═══ DAY ROWS ═══ */}
      {mobileOnly ? (
        /* ── MOBILE: single card with all days as compact rows ── */
        <div style={{...S.card,padding:0,marginBottom:8,overflow:"hidden"}}>
          {WEEK_DISPLAY_ORDER.map(dayIdx => {
            const d = days[dayIdx] ?? { active: true };
            const isActive = d.active;
            const hasHours = isActive && d.in && d.out;
            const calc = hasHours ? calcDayHours(d.in, d.out, parseInt(d.break)||0) : null;
            const isWeekend = dayIdx === 0 || dayIdx === 6;
            const contractOver = calc && calc.totalContract > 10*60;
            return (
              <div key={dayIdx} style={{
                padding:"10px 12px",
                borderBottom: dayIdx < 6 ? "1px solid var(--border)" : "none",
                opacity: isActive ? 1 : 0.5,
                background: isActive ? "var(--card-bg)" : "var(--bg1)",
              }}>
                {/* Row: day + toggle */}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:isActive?8:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{color:isWeekend?"#f59e0b":"var(--text)",fontWeight:700,fontSize:14,minWidth:30}}>{WEEK_DAYS_LABEL[dayIdx]}</span>
                    {!isActive && <span style={{color:"var(--text3)",fontSize:11}}>Folga</span>}
                    {isActive && hasHours && calc && (
                      <span style={{color:contractOver?"var(--red)":ac,fontSize:10,fontWeight:700,fontFamily:"'DM Mono',monospace"}}>{fmtHHMM(calc.totalContract)}</span>
                    )}
                  </div>
                  <button onClick={()=>toggleDay(dayIdx)} style={isActive ? toggleOn : toggleOff}>
                    <div style={toggleDot(isActive)} />
                  </button>
                </div>
                {/* Inputs inline */}
                {isActive && (
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 52px",gap:6,alignItems:"center"}}>
                    <input type="time" value={d.in||""} onChange={e=>handleDayChange(dayIdx,"in",e.target.value)}
                      placeholder="Entrada" style={{...S.input,fontSize:13,padding:"7px 4px",textAlign:"center",width:"100%",boxSizing:"border-box"}}/>
                    <input type="time" value={d.out||""} onChange={e=>handleDayChange(dayIdx,"out",e.target.value)}
                      placeholder="Saída" style={{...S.input,fontSize:13,padding:"7px 4px",textAlign:"center",width:"100%",boxSizing:"border-box"}}/>
                    <input type="number" min="0" max="120" value={d.break||""} onChange={e=>handleDayChange(dayIdx,"break",parseInt(e.target.value)||0)}
                      placeholder="Int" style={{...S.input,fontSize:13,padding:"7px 2px",textAlign:"center",width:"100%",boxSizing:"border-box"}}/>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* ── DESKTOP: linhas espaçosas com grid generoso ── */
        <div style={{...S.card,padding:0,marginBottom:16,overflow:"hidden"}}>
          {WEEK_DISPLAY_ORDER.map(dayIdx => {
            const d = days[dayIdx] ?? { active: true };
            const isActive = d.active;
            const hasHours = isActive && d.in && d.out;
            const calc = hasHours ? calcDayHours(d.in, d.out, parseInt(d.break)||0) : null;
            const isWeekend = dayIdx === 0 || dayIdx === 6;
            const contractOver = calc && calc.totalContract > 10*60;
            return (
              <div key={dayIdx} style={{
                padding:"16px 20px",
                borderBottom: dayIdx < 6 ? "1px solid var(--border)" : "none",
                background: isActive ? (hasHours && contractOver ? "#fef2f2" : "transparent") : "var(--bg1)",
                opacity: isActive ? 1 : 0.55,
                display:"grid",
                gridTemplateColumns:"120px 1fr auto",
                gap:20,
                alignItems:"center",
              }}>
                {/* Coluna 1: Dia + status */}
                <div style={{display:"flex",flexDirection:"column",gap:2}}>
                  <span style={{color:isWeekend?"#f59e0b":"var(--text)",fontWeight:700,fontSize:16,letterSpacing:0.3}}>{WEEK_DAYS_LABEL[dayIdx]}</span>
                  <span style={{color: isActive ? (hasHours ? "var(--green)" : "var(--text3)") : "var(--text3)", fontSize:11, fontWeight: isActive ? 600 : 500, letterSpacing:0.3}}>
                    {isActive ? (hasHours ? "Trabalha" : "Sem horário") : "Folga"}
                  </span>
                </div>

                {/* Coluna 2: Inputs + métricas */}
                {isActive ? (
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 110px",gap:12,alignItems:"end"}}>
                      <div>
                        <label style={{color:"var(--text3)",fontSize:10,display:"block",marginBottom:4,letterSpacing:0.5,textTransform:"uppercase",fontWeight:600}}>Entrada</label>
                        <input type="time" value={d.in||""} onChange={e=>handleDayChange(dayIdx,"in",e.target.value)}
                          style={{...S.input,fontSize:15,padding:"11px 12px",textAlign:"center",width:"100%",boxSizing:"border-box"}}/>
                      </div>
                      <div>
                        <label style={{color:"var(--text3)",fontSize:10,display:"block",marginBottom:4,letterSpacing:0.5,textTransform:"uppercase",fontWeight:600}}>Saída</label>
                        <input type="time" value={d.out||""} onChange={e=>handleDayChange(dayIdx,"out",e.target.value)}
                          style={{...S.input,fontSize:15,padding:"11px 12px",textAlign:"center",width:"100%",boxSizing:"border-box"}}/>
                      </div>
                      <div>
                        <label style={{color:"var(--text3)",fontSize:10,display:"block",marginBottom:4,letterSpacing:0.5,textTransform:"uppercase",fontWeight:600}}>Intervalo (min)</label>
                        <input type="number" min="0" max="120" value={d.break||""} onChange={e=>handleDayChange(dayIdx,"break",parseInt(e.target.value)||0)}
                          placeholder="30" style={{...S.input,fontSize:15,padding:"11px 10px",textAlign:"center",width:"100%",boxSizing:"border-box"}}/>
                      </div>
                    </div>
                    {calc && (
                      <div style={{display:"flex",gap:14,flexWrap:"wrap",fontSize:11,fontFamily:"'DM Mono',monospace",paddingTop:4}}>
                        <span style={{color:"var(--text3)"}}>Real <strong style={{color:"var(--text2)"}}>{fmtHHMM(calc.worked)}</strong></span>
                        <span style={{color:"var(--text3)"}}>Diurna <strong style={{color:"var(--text2)"}}>{fmtHHMM(calc.diurnal)}</strong></span>
                        {calc.nocturnal > 0 && <span style={{color:"#8b5cf6"}}>Not.real <strong>{fmtHHMM(calc.nocturnal)}</strong></span>}
                        {calc.nocturnalFicta > 0 && <span style={{color:"#ec4899"}}>Not.ficta <strong>{fmtHHMM(calc.nocturnalFicta)}</strong></span>}
                        <span style={{color:contractOver?"var(--red)":ac,fontWeight:700,marginLeft:"auto"}}>Contratual {fmtHHMM(calc.totalContract)}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{color:"var(--text3)",fontSize:12,fontStyle:"italic"}}>— dia não trabalhado</div>
                )}

                {/* Coluna 3: Toggle */}
                <button onClick={()=>toggleDay(dayIdx)} style={isActive ? toggleOn : toggleOff}>
                  <div style={toggleDot(isActive)} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Weekly total (only if all hours filled) */}
      {allHoursFilled && activeDayCount > 0 && (
        <div style={{
          ...cardS,
          padding: mobileOnly ? "14px 16px" : "20px 24px",
          display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",
          gap: mobileOnly ? 10 : 16,
          borderColor:weekOk?"var(--green)33":"var(--red)33",
          background: weekOk ? "#10b98108" : "#ef444408",
        }}>
          <div style={{display:"flex",alignItems:"center",gap:mobileOnly?8:12}}>
            <span style={{fontSize:mobileOnly?18:22}}>{weekOk?"✅":"⚠️"}</span>
            <div>
              <div style={{color:"var(--text3)",fontSize:mobileOnly?11:12,fontWeight:500,letterSpacing:0.3,textTransform:"uppercase"}}>{mobileOnly?"Total semanal":"Total semanal contratual"}</div>
              <div style={{color:weekOk?"var(--green)":"var(--red)",fontSize:mobileOnly?11:12,fontWeight:600,marginTop:2}}>
                {isEmpFreela ? "Freela — sem validação CLT" : weekOk ? "Dentro do limite CLT" : "Fora do limite — deve estar entre 43:55 e 44:00"}
              </div>
            </div>
          </div>
          <div style={{display:"flex",alignItems:"baseline",gap:6}}>
            <span style={{color:weekOk?"var(--green)":"var(--red)",fontWeight:800,fontSize:mobileOnly?22:28,fontFamily:"'DM Mono',monospace",letterSpacing:-0.5}}>{fmtHHMM(totalContract)}</span>
            <span style={{color:"var(--text3)",fontSize:mobileOnly?11:13,fontWeight:500}}>/ sem</span>
          </div>
        </div>
      )}

      {/* v8.11.0: Ciclo de domingos — só aparece se domingo for dia de trabalho em alguma semana */}
      {(() => {
        const sundayActiveSingle = !isAlternating && editDays?.[0]?.active === true;
        const sundayActiveAlt    = isAlternating && (editDays?.[0]?.active === true || editDaysB?.[0]?.active === true);
        const showCycle = !isEmpFreela && (sundayActiveSingle || sundayActiveAlt);
        if (!showCycle) return null;
        // v8.11.3: refDate é o PRIMEIRO domingo de FOLGA. O empregado trabalha workCount domingos
        // antes da primeira folga, então a sugestão = primeiro domingo on/after validFrom + (workCount × 7 dias).
        // Ex: 3 trab + 1 folga, vigência 01/04 → 1º domingo (05/04) trab, +21 dias = 26/04 (4º domingo, folga).
        const suggestRef = () => {
          const base = new Date((validFrom || today()) + "T12:00:00");
          while (base.getDay() !== 0) base.setDate(base.getDate() + 1);
          const w = parseInt(sundayCycle.workCount, 10);
          if (Number.isFinite(w) && w > 0) base.setDate(base.getDate() + 7 * w);
          return base.toISOString().slice(0, 10);
        };
        const refIsValid = sundayCycle.refDate && new Date(sundayCycle.refDate + "T12:00:00").getDay() === 0;
        const work = parseInt(sundayCycle.workCount, 10);
        const off  = parseInt(sundayCycle.offCount, 10);
        const cycleLen = (Number.isFinite(work) && Number.isFinite(off)) ? (work + off) : 0;
        return (
          <div style={{...cardS,borderColor:"#f59e0b44",background:"#f59e0b08",padding:mobileOnly?"14px 16px":"18px 22px",marginBottom:12}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
              <span style={{fontSize:mobileOnly?18:22}}>🌞</span>
              <div>
                <div style={{color:"var(--text)",fontWeight:700,fontSize:mobileOnly?13:14}}>Ciclo de domingos</div>
                <div style={{color:"var(--text3)",fontSize:mobileOnly?10:11,marginTop:2}}>Define quantos domingos seguidos trabalha antes de folgar 1. Aplicado pelo botão "Sugerir folgas de domingo" na Escala.</div>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"1fr 1fr 1.5fr",gap:10,marginTop:8}}>
              <div>
                <label style={{...S.label,fontSize:11}}>Trabalha (domingos)</label>
                <input type="number" min="0" value={sundayCycle.workCount} onChange={e=>setSundayCycle({...sundayCycle,workCount:e.target.value})} style={{...S.input,fontFamily:"'DM Mono',monospace",textAlign:"center"}} />
              </div>
              <div>
                <label style={{...S.label,fontSize:11}}>Folga (domingos)</label>
                <input type="number" min="1" value={sundayCycle.offCount} onChange={e=>setSundayCycle({...sundayCycle,offCount:e.target.value})} style={{...S.input,fontFamily:"'DM Mono',monospace",textAlign:"center"}} />
              </div>
              <div>
                <label style={{...S.label,fontSize:11}}>1º domingo de folga (referência)</label>
                <div style={{display:"flex",gap:6}}>
                  <input type="date" value={sundayCycle.refDate} onChange={e=>setSundayCycle({...sundayCycle,refDate:e.target.value})} style={{...S.input,flex:1}} />
                  {!sundayCycle.refDate && <button type="button" onClick={()=>setSundayCycle({...sundayCycle,refDate:suggestRef()})} style={{...S.btnSecondary,fontSize:11,padding:"6px 10px",whiteSpace:"nowrap"}} title="Próximo domingo a partir da vigência">Sugerir</button>}
                </div>
                {sundayCycle.refDate && !refIsValid && <div style={{color:"var(--red)",fontSize:10,marginTop:4}}>⚠️ A data precisa ser um domingo</div>}
              </div>
            </div>
            {cycleLen > 0 && refIsValid && (
              <div style={{marginTop:10,padding:"8px 12px",background:"var(--bg2)",borderRadius:8,fontSize:mobileOnly?11:12,color:"var(--text2)",lineHeight:1.5}}>
                💡 {work === 1 && off === 1 ? "Domingos alternados (sim/não)." : `Folga ${off} a cada ${cycleLen} domingos. Trabalha ${work} seguidos, folga ${off}.`}
                {" "}Próxima folga: <strong>{new Date(sundayCycle.refDate+"T12:00:00").toLocaleDateString("pt-BR")}</strong>.
              </div>
            )}
          </div>
        );
      })()}

      {/* Validation errors */}
      {errors.length > 0 && (
        <div style={{background:"#e74c3c11",border:"1px solid #e74c3c44",borderRadius:10,padding:"12px 16px",marginBottom:12}}>
          <p style={{color:"var(--red)",fontWeight:700,fontSize:13,margin:"0 0 8px"}}>Corrija antes de salvar:</p>
          {errors.map((e,i)=><div key={i} style={{color:"var(--red)",fontSize:12,marginBottom:4}}>• {e}</div>)}
        </div>
      )}

      {/* Valid from confirmation */}
      {showValidFrom && (
        <div style={{...cardS,border:"1px solid var(--ac)44"}}>
          <p style={{color:ac,fontWeight:700,fontSize:mobileOnly?13:14,margin:"0 0 8px"}}>
            Salvar horário
          </p>
          <p style={{color:"var(--text3)",fontSize:mobileOnly?11:12,marginBottom:8}}>A partir de quando entra em vigor?</p>
          <input type="date" value={validFrom} onChange={e=>setValidFrom(e.target.value)} style={{...S.input,marginBottom:10,fontSize:mobileOnly?14:15,padding:mobileOnly?"10px 10px":"12px 14px",width:"100%",boxSizing:"border-box"}}/>
          <p style={{color:"var(--text3)",fontSize:12,marginBottom:12}}>Todos os gestores DP receberão uma notificação com esta alteração.</p>
          <div style={{display:"flex",gap:mobileOnly?10:12,flexWrap:"wrap"}}>
            <button onClick={saveSchedule} style={{...S.btnPrimary,flex:1,minWidth:mobileOnly?140:200,padding:mobileOnly?undefined:"14px 24px",fontSize:mobileOnly?undefined:14,fontWeight:700}}>✓ Confirmar e Salvar</button>
            <button onClick={()=>{setShowValidFrom(false);setSaveMode(null);}} style={{...S.btnSecondary,flex:1,minWidth:mobileOnly?100:140,padding:mobileOnly?undefined:"14px 24px",fontSize:mobileOnly?undefined:14}}>Voltar</button>
          </div>
        </div>
      )}

      {/* Action buttons */}
      {!showValidFrom && (
        <div style={{display:"flex",flexDirection:"column",gap:10,marginTop:mobileOnly?4:8}}>
          {/* Validado com sucesso — mostra aviso e libera Salvar */}
          {validated && (
            <div style={{...S.card,padding:mobileOnly?"10px 14px":"14px 18px",background:"#10b98108",borderColor:"#10b98144",display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:mobileOnly?18:22}}>✅</span>
              <div style={{flex:1,minWidth:0}}>
                <div style={{color:"var(--green)",fontWeight:700,fontSize:mobileOnly?13:14}}>Horário validado</div>
                <div style={{color:"var(--text3)",fontSize:mobileOnly?11:12,marginTop:2}}>{isEmpFreela ? "Freela — horário registrado sem validação CLT." : "Todas as regras da CLT estão atendidas. Pode salvar com segurança."}</div>
              </div>
            </div>
          )}
          <div style={{display:"flex",gap:mobileOnly?8:12,flexWrap:"wrap"}}>
            {validated ? (
              <button onClick={proceedToSave}
                style={{...S.btnPrimary,flex:1,minWidth:mobileOnly?120:220,fontSize:mobileOnly?13:15,padding:mobileOnly?"12px 10px":"14px 24px",fontWeight:700,background:"var(--green)"}}>
                💾 Salvar Horário
              </button>
            ) : (
              <button onClick={tryValidateFull} disabled={!allHoursFilled || activeDayCount === 0}
                style={{...S.btnPrimary,flex:1,minWidth:mobileOnly?120:220,fontSize:mobileOnly?13:15,padding:mobileOnly?"12px 10px":"14px 24px",fontWeight:700,opacity:allHoursFilled&&activeDayCount>0?1:0.4}}>
                ✓ Validar Horários
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Work Schedule Employee Tab ────────────────────────────────────────────────
function WorkScheduleEmployeeTab({ empId, restaurantId, workSchedules }) {
  const ac = "var(--ac)";
  const empScheds = [...(workSchedules?.[restaurantId]?.[empId] ?? [])].sort((a,b)=>a.validFrom.localeCompare(b.validFrom));
  const current = empScheds[empScheds.length - 1];

  // Helper genérico: renderiza um bloco de days a partir de objeto {0:..., 1:..., ...}
  function daysBlock(daysObj) {
    return (
      <div>
        {WEEK_DISPLAY_ORDER.map(i => {
          const d = daysObj?.[i];
          const hasShift = d?.in && d?.out;
          const isWorkDay = !!d;
          const isWeekend = i === 0 || i === 6;
          const calc = hasShift ? calcDayHours(d.in, d.out, parseInt(d.break)||0) : null;
          return (
            <div key={i} style={{
              padding:"10px 14px",marginBottom:6,borderRadius:10,
              background:isWorkDay?"var(--card-bg)":"var(--bg1)",
              border:`1px solid ${isWorkDay?"var(--border)":"transparent"}`,
              opacity:isWorkDay?1:0.5,
            }}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{color:isWeekend?"#f59e0b":"var(--text)",fontWeight:700,fontSize:14,minWidth:36}}>{WEEK_DAYS_LABEL[i]}</span>
                {hasShift
                  ? <span style={{color:"var(--text2)",fontSize:13,fontFamily:"'DM Mono',monospace"}}>{d.in} - {d.out}</span>
                  : isWorkDay
                    ? <span style={{color:"#f59e0b",fontSize:12}}>Trabalha (horario pendente)</span>
                    : <span style={{color:"var(--text3)",fontSize:12}}>Folga</span>
                }
              </div>
              {hasShift && calc && (
                <div style={{display:"flex",gap:8,marginTop:4,fontSize:11,fontFamily:"'DM Mono',monospace",color:"var(--text3)",flexWrap:"wrap"}}>
                  <span>Intervalo: {d.break||0}min</span>
                  <span>Contratual: <strong style={{color:ac}}>{fmtHHMM(calc.totalContract)}</strong></span>
                  {calc.nocturnal > 0 && <span style={{color:"#8b5cf6"}}>Noturna: {fmtHHMM(calc.nocturnalFicta)}</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  function scheduleBlock(s, validUntil) {
    // Se for alternating, mostra Semana A + Semana B + indicador da semana atual
    if (s?.type === "alternating") {
      const wkHoje = weekTypeForDate(s, today()) || "A";
      return (
        <div>
          <div style={{background:"var(--bg1)",borderRadius:8,padding:"8px 12px",marginBottom:10,fontSize:12,textAlign:"center"}}>
            Esta semana: <strong style={{color:ac,fontSize:14}}>Semana {wkHoje}</strong>
          </div>
          {["A","B"].map(wk => {
            const wkData = s.weeks?.[wk];
            const isCurrent = wkHoje === wk;
            return (
              <div key={wk} style={{marginBottom:14,border:`2px solid ${isCurrent?ac:"var(--border)"}`,borderRadius:10,padding:"10px 12px",background:isCurrent?"var(--ac-bg)":"transparent"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <strong style={{color:isCurrent?ac:"var(--text2)",fontSize:13}}>Semana {wk} {isCurrent && "← esta semana"}</strong>
                  {wkData?.totalContract > 0 && <span style={{fontSize:11,fontFamily:"'DM Mono',monospace",color:"var(--text3)"}}>{fmtHHMM(wkData.totalContract)}/sem</span>}
                </div>
                {daysBlock(wkData?.days)}
              </div>
            );
          })}
          {validUntil && <p style={{color:"var(--text3)",fontSize:11,marginTop:6,textAlign:"right"}}>Vigente ate {fmtDate(validUntil)}</p>}
        </div>
      );
    }
    // Single (legado): renderização original
    return (
      <div>
        {WEEK_DISPLAY_ORDER.map(i => {
          const d = s.days?.[i];
          const hasShift = d?.in && d?.out;
          const isWorkDay = !!d; // day exists in storage = work day
          const isWeekend = i === 0 || i === 6;
          const calc = hasShift ? calcDayHours(d.in, d.out, parseInt(d.break)||0) : null;
          return (
            <div key={i} style={{
              padding:"10px 14px",marginBottom:6,borderRadius:10,
              background:isWorkDay?"var(--card-bg)":"var(--bg1)",
              border:`1px solid ${isWorkDay?"var(--border)":"transparent"}`,
              opacity:isWorkDay?1:0.5,
            }}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{color:isWeekend?"#f59e0b":"var(--text)",fontWeight:700,fontSize:14,minWidth:36}}>{WEEK_DAYS_LABEL[i]}</span>
                {hasShift
                  ? <span style={{color:"var(--text2)",fontSize:13,fontFamily:"'DM Mono',monospace"}}>{d.in} - {d.out}</span>
                  : isWorkDay
                    ? <span style={{color:"#f59e0b",fontSize:12}}>Trabalha (horario pendente)</span>
                    : <span style={{color:"var(--text3)",fontSize:12}}>Folga</span>
                }
              </div>
              {hasShift && calc && (
                <div style={{display:"flex",gap:8,marginTop:4,fontSize:11,fontFamily:"'DM Mono',monospace",color:"var(--text3)",flexWrap:"wrap"}}>
                  <span>Intervalo: {d.break||0}min</span>
                  <span>Contratual: <strong style={{color:ac}}>{fmtHHMM(calc.totalContract)}</strong></span>
                  {calc.nocturnal > 0 && <span style={{color:"#8b5cf6"}}>Noturna: {fmtHHMM(calc.nocturnalFicta)}</span>}
                </div>
              )}
            </div>
          );
        })}
        {validUntil && (
          <p style={{color:"var(--text3)",fontSize:11,marginTop:6,textAlign:"right"}}>Vigente ate {fmtDate(validUntil)}</p>
        )}
      </div>
    );
  }

  if (!current) return (
    <div style={{textAlign:"center",marginTop:40}}>
      <div style={{fontSize:32,marginBottom:12}}>🕐</div>
      <p style={{color:"var(--text3)",fontSize:14}}>Nenhum horario cadastrado ainda.</p>
    </div>
  );

  return (
    <div>
      {/* Current schedule */}
      <div style={{...S.card,marginBottom:20,borderColor:"var(--ac)33"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:6}}>
          <span style={{color:ac,fontSize:14,fontWeight:700}}>Horario atual</span>
          <span style={{color:"var(--text3)",fontSize:12}}>Desde {fmtDate(current.validFrom)}</span>
        </div>
        {current.hoursComplete === false && (
          <div style={{background:"#f59e0b11",borderRadius:8,padding:"8px 12px",marginBottom:12,border:"1px solid #f59e0b33"}}>
            <span style={{color:"#f59e0b",fontSize:12,fontWeight:600}}>Dias de trabalho e folga definidos. Horarios ainda nao preenchidos pelo gestor.</span>
          </div>
        )}
        {current.totalContract > 0 && (
          <div style={{background:"var(--bg1)",borderRadius:8,padding:"8px 12px",marginBottom:12,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{color:"var(--text3)",fontSize:12}}>Carga semanal</span>
            <span style={{color:ac,fontWeight:700,fontSize:14,fontFamily:"'DM Mono',monospace"}}>{fmtHHMM(current.totalContract)}/sem</span>
          </div>
        )}
        {scheduleBlock(current, null)}
      </div>

      {/* Previous schedules */}
      {empScheds.length > 1 && (
        <details>
          <summary style={{color:"var(--text3)",fontSize:12,cursor:"pointer",padding:"8px 12px",background:"var(--bg1)",borderRadius:8,marginBottom:8}}>
            Horarios anteriores ({empScheds.length - 1})
          </summary>
          <div style={{paddingTop:4}}>
            {[...empScheds].reverse().slice(1).map((s, idx) => {
              const newerSched = [...empScheds].reverse()[idx];
              const validUntil = newerSched?.validFrom
                ? (() => { const d = new Date(newerSched.validFrom+"T12:00:00"); d.setDate(d.getDate()-1); return d.toISOString().slice(0,10); })()
                : null;
              return (
                <div key={s.id} style={{...S.card,marginBottom:10,opacity:0.7}}>
                  <p style={{color:"var(--text3)",fontSize:12,fontWeight:700,margin:"0 0 10px"}}>Vigencia a partir de {fmtDate(s.validFrom)}</p>
                  {scheduleBlock(s, validUntil)}
                </div>
              );
            })}
          </div>
        </details>
      )}
    </div>
  );
}

function DpManagerTab({ restaurantId, dpMessages, onUpdate, isOwner }) {
  const msgs = dpMessages.filter(m => m.restaurantId === restaurantId && !m.deleted)
    .sort((a, b) => b.date.localeCompare(a.date));
  const trashedMsgs = dpMessages.filter(m => m.restaurantId === restaurantId && m.deleted)
    .sort((a, b) => (b.deletedAt||b.date).localeCompare(a.deletedAt||a.date));
  const [filter, setFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showTrash, setShowTrash] = useState(false);
  const CATS = { all: "Todos", sugestao: "💡 Sugestões", elogio: "👏 Elogios", reclamacao: "⚠️ Reclamações", denuncia: "🚨 Denúncias" };
  const filtered = filter === "all" ? msgs : msgs.filter(m => m.category === filter);
  const unread = msgs.filter(m => !m.read).length;
  const ac = "var(--ac)";

  function markRead(id) {
    onUpdate("dpMessages", dpMessages.map(m => m.id === id ? { ...m, read: true } : m));
  }

  function toggleSelect(id) {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  }

  async function deleteSelected() {
    if (isOwner) {
      if(!await appConfirm(`Apagar ${selectedIds.size} mensagem(ns) permanentemente? Esta ação não pode ser desfeita.`)) return;
      onUpdate("dpMessages", dpMessages.filter(m => !selectedIds.has(m.id)));
      setSelectedIds(new Set());
      onUpdate("_toast", `🗑️ ${selectedIds.size} mensagem(ns) apagada(s) permanentemente`);
    } else {
      if(!await appConfirm(`Mover ${selectedIds.size} mensagem(ns) para a lixeira?`)) return;
      onUpdate("dpMessages", dpMessages.map(m => selectedIds.has(m.id) ? { ...m, deleted: true, deletedAt: new Date().toISOString(), read: true } : m));
      setSelectedIds(new Set());
      onUpdate("_toast", `🗑️ ${selectedIds.size} mensagem(ns) movida(s) para a lixeira`);
    }
  }

  function restoreFromTrash(id) {
    onUpdate("dpMessages", dpMessages.map(m => m.id === id ? { ...m, deleted: false, deletedAt: undefined } : m));
    onUpdate("_toast", "♻️ Mensagem restaurada");
  }

  async function permanentDeleteFromTrash(id) {
    if(!await appConfirm("Apagar permanentemente? Esta ação não pode ser desfeita.")) return;
    onUpdate("dpMessages", dpMessages.filter(m => m.id !== id));
    onUpdate("_toast", "🗑️ Mensagem apagada permanentemente");
  }

  return (
    <div>
      {unread > 0 && <div style={{ background: "var(--ac)22", border: "1px solid var(--ac)44", borderRadius: 10, padding: "8px 14px", marginBottom: 12, fontSize: 12, color: ac, fontFamily: "'DM Mono',monospace" }}>📬 {unread} mensagem{unread > 1 ? "s" : ""} não lida{unread > 1 ? "s" : ""}</div>}

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8,marginBottom:16}}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {Object.entries(CATS).map(([val, lbl]) => (
            <button key={val} onClick={() => setFilter(val)} style={{ padding: "6px 12px", borderRadius: 20, border: `1px solid ${filter === val ? ac : "var(--border)"}`, background: filter === val ? ac + "22" : "transparent", color: filter === val ? ac : "#555", cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 11 }}>{lbl}</button>
          ))}
        </div>
        {selectedIds.size > 0 && (
          <button onClick={deleteSelected} style={{padding:"6px 14px",borderRadius:8,border:"none",background:"var(--red)",color:"var(--text)",fontWeight:700,cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12}}>
            🗑️ {isOwner ? "Apagar" : "Mover p/ lixeira"} ({selectedIds.size})
          </button>
        )}
      </div>

      {filtered.length === 0 && !showTrash && <p style={{ color: "var(--text3)", textAlign: "center" }}>Nenhuma mensagem.</p>}
      {!showTrash && filtered.map(m => (
        <div key={m.id} style={{ ...S.card, marginBottom: 10, opacity: m.read ? 0.7 : 1, borderColor: selectedIds.has(m.id) ? "#ef444488" : m.read ? "var(--border)" : "var(--ac)44", background: selectedIds.has(m.id) ? "#1a0808" : undefined }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, alignItems:"flex-start" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input type="checkbox" checked={selectedIds.has(m.id)} onChange={()=>toggleSelect(m.id)}
                style={{accentColor:"var(--red)",cursor:"pointer",width:14,height:14,flexShrink:0}}
              />
              <span style={{ color: "var(--text2)", fontSize: 12 }}>{CATS[m.category] ?? m.category}</span>
              {!m.read && <span style={{ background: ac, color: "#111", borderRadius: 4, padding: "1px 6px", fontSize: 10, fontWeight: 700 }}>Novo</span>}
            </div>
            <span style={{ color: "var(--text3)", fontSize: 11 }}>{new Date(m.date).toLocaleString("pt-BR")}</span>
          </div>
          <div style={{ color: "var(--text2)", fontSize: 12, marginBottom: 8 }}>De: <span style={{ color: m.empName === "Anônimo" ? "#8b5cf6" : "var(--text)" }}>{m.empName}</span></div>
          <div style={{ color: "var(--text)", fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap", marginBottom: 8 }}>{m.body}</div>
          {!m.read && <button onClick={() => markRead(m.id)} style={{ ...S.btnSecondary, fontSize: 12 }}>Marcar como lida</button>}
        </div>
      ))}

      {/* Trash toggle */}
      {trashedMsgs.length > 0 && (
        <button onClick={() => setShowTrash(!showTrash)} style={{ ...S.btnSecondary, fontSize: 12, marginTop: 12, display: "flex", alignItems: "center", gap: 6 }}>
          🗑️ Lixeira ({trashedMsgs.length}) {showTrash ? "▲" : "▼"}
        </button>
      )}

      {/* Trash view */}
      {showTrash && trashedMsgs.map(m => (
        <div key={m.id} style={{...S.card,marginBottom:10,marginTop:10,opacity:0.5,borderColor:"var(--border)"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
            <div style={{flex:1}}>
              <div style={{color:"var(--text3)",fontSize:11,marginBottom:4}}>{CATS[m.category]??m.category} · Apagado em {m.deletedAt ? new Date(m.deletedAt).toLocaleDateString("pt-BR") : "—"}</div>
              <div style={{color:"var(--text2)",fontSize:12,marginBottom:2}}>De: {m.empName}</div>
              <div style={{color:"var(--text2)",fontSize:13,lineHeight:1.5}}>{m.body?.slice(0,100)}{(m.body?.length??0)>100?"…":""}</div>
            </div>
            <div style={{display:"flex",gap:6,flexShrink:0}}>
              <button onClick={()=>restoreFromTrash(m.id)} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px"}}>♻️</button>
              {isOwner && <button onClick={()=>permanentDeleteFromTrash(m.id)} style={{background:"none",border:"1px solid #e74c3c33",borderRadius:8,color:"var(--red)",cursor:"pointer",fontSize:11,padding:"4px 10px",fontFamily:"'DM Mono',monospace"}}>✕</button>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmployeePortal({ employees, roles, tips, schedules, splits, restaurants, communications, commAcks, faq, dpMessages, workSchedules, incidents, feedbacks, devChecklists, onBack, onUpdateEmployee, onUpdate, toggleTheme, theme, onSwitchToManager, onSwitchToOperational, employeeGoals, tipApprovals, delays, meetingPlans, inbox, inboxFolders, hideHeader, hideTabNav, forceTab, forceEmpId, pessoas, managers, dayInversions }) {
  const [empId, setEmpId] = useState(() => forceEmpId || localStorage.getItem("apptip_empid") || null);
  // Sincroniza forceEmpId vindo do shell
  useEffect(() => {
    if (forceEmpId && forceEmpId !== empId) setEmpId(forceEmpId);
  }, [forceEmpId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (empId) localStorage.setItem("apptip_empid", empId);
    else localStorage.removeItem("apptip_empid");
  }, [empId]);

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [tab, setTab] = useState(forceTab || "comunicados");
  useEffect(() => { if (forceTab && forceTab !== tab) setTab(forceTab); }, [forceTab]); // eslint-disable-line react-hooks/exhaustive-deps
  const [firstCpf, setFirstCpf] = useState("");
  const [firstPin, setFirstPin] = useState("");
  const [firstPin2, setFirstPin2] = useState("");
  const [firstErr, setFirstErr] = useState("");
  const [empSchedView, setEmpSchedView] = useState("mine");
  // Inbox folder state (must be at component level, not in IIFE)
  const [eFolder, setEFolder] = useState(null);
  const [eFolderModal, setEFolderModal] = useState(null);
  const [eFolderForm, setEFolderForm] = useState({ nome: "", cor: "#3498db" });
  const [eMoveModal, setEMoveModal] = useState(null);

  const emp = employees.find(e => e.id === empId);
  const role = emp ? roles.find(r => r.id === emp.roleId) : null;
  const restaurant = emp ? restaurants.find(r => r.id === emp.restaurantId) : null;
  const empNumericPin = (emp?.empCode ?? "").replace(/\D/g, "").padStart(4, "0"); // eslint-disable-line no-unused-vars
  // v7.6: só dispara primeiro-acesso pelo flag mustChangePin. Os triggers antigos por
  // "pin == empCode" causavam loop de pedir PIN novo em cada portal porque os 3 records
  // (pessoa/employee/manager) ficavam dessincronizados.
  const isFirstAccess = emp && emp.mustChangePin === true;
  const needsCpf = emp && !emp.cpf;

  // Verificação em tempo real — empregado inativado ou não encontrado → logout
  useEffect(() => {
    if (!empId) return;
    if (employees.length === 0) return; // dados ainda não carregaram
    if (!emp) { onBack(); return; } // empregado não existe mais
    if (emp.inactive && emp.inactiveFrom && emp.inactiveFrom <= today()) {
      alert("Seu acesso foi desativado. Entre em contato com o gestor.");
      onBack();
    }
  }, [emp, empId, employees.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const mk = monthKey(year, month);
  const ridApprovals = tipApprovals?.[emp?.restaurantId] ?? {};
  // Fase D: filtra por publishedAt (novo) com fallback para tipApprovals semanal (legado)
  const myTips = tips
    .filter(t => t.employeeId === empId && t.monthKey === mk)
    .filter(t => isTipPublished(t, ridApprovals));
  const grossTotal = myTips.reduce((a, t) => a + (t.myShare ?? 0), 0);
  const taxTotal   = myTips.reduce((a, t) => a + (t.myTax   ?? 0), 0);
  const netTotal   = myTips.reduce((a, t) => a + (t.myNet   ?? 0), 0);
  // Se o mês ainda não foi preenchido pelo gestor, deriva a escala do horário cadastrado + ciclo de domingos
  // (mesmo comportamento do gestor no RestaurantPanel). Sem isso, meses futuros apareciam como "trabalha todo dia".
  const dayMap = (() => {
    if (!emp) return {};
    const saved = schedules?.[emp.restaurantId]?.[mk]?.[empId];
    if (saved && Object.keys(saved).length > 0) return saved;
    const restObj = restaurants?.find(r => r.id === emp.restaurantId);
    return deriveScheduleForEmp(emp, year, month, workSchedules, restObj);
  })();

  // Inversões de dia inteiro deste mês (pra exibir badge nas células)
  const dayInversionsByDate = (() => {
    const map = {};
    if (!emp) return map;
    (dayInversions ?? []).forEach(inv => {
      if (inv.restaurantId !== emp.restaurantId) return;
      if (inv.dateA?.slice(0,7) === mk) map[inv.dateA] = { otherDate: inv.dateB, reason: inv.reason, createdBy: inv.createdBy };
      if (inv.dateB?.slice(0,7) === mk) map[inv.dateB] = { otherDate: inv.dateA, reason: inv.reason, createdBy: inv.createdBy };
    });
    return map;
  })();

  // Início de vigência da escala do empregado: validFrom mais antigo entre os horários cadastrados.
  // Se o mês exibido é totalmente anterior, escondemos o calendário (ele ainda não estava trabalhando).
  const empValidFrom = (() => {
    if (!emp) return null;
    const empWS = workSchedules?.[emp.restaurantId]?.[emp.id] ?? [];
    const dates = empWS.map(s => s.validFrom).filter(Boolean).sort();
    return dates[0] || null; // null = sem horário cadastrado → não bloqueia
  })();
  const lastDayOfDisplayedMonth = `${year}-${String(month+1).padStart(2,"0")}-${String(new Date(year, month+1, 0).getDate()).padStart(2,"0")}`;
  const monthBeforeVigencia = !!(empValidFrom && lastDayOfDisplayedMonth < empValidFrom);

  // Pending communications
  const empRole = roles?.find(r => r.id === emp?.roleId);
  const myComms = emp ? communications.filter(c => {
    if (c.restaurantId !== emp.restaurantId) return false;
    if (c.autoSchedule || c.deleted) return false;
    if (!c.target || c.target === "all") return true;
    if (c.target.startsWith("emps:")) return c.target.replace("emps:","").split(",").includes(empId);
    if (c.target.startsWith("areas:")) return empRole && c.target.replace("areas:","").split(",").includes(empRole.area);
    if (c.target === `emp:${empId}`) return true;
    if (c.target.startsWith("area:") && empRole) return c.target === `area:${empRole.area}`;
    return false;
  }) : [];
  const pendingComms = myComms.filter(c => !commAcks?.[c.id]?.[empId]);
  const hasPending = pendingComms.length > 0;

  // Abas do empregado — respeita config do admin E escolha do gestor
  const empTabVisible = (key) => restaurant?.tabsConfig?.[key] !== false && restaurant?.tabsGestor?.[key] !== false;

  function handleTabChange(id) {
    if (hasPending && id !== "comunicados") {
      return; // block navigation while pending comms exist
    }
    setTab(id);
  }

  // Auto-switch to comunicados if pending
  useEffect(() => {
    if (hasPending && tab !== "comunicados") setTab("comunicados");
  }, [hasPending, tab]);

  function completeFirstAccess() {
    if (needsCpf && !firstCpf.trim()) { setFirstErr("Informe seu CPF."); return; }
    if (firstPin.length !== 4 || !/^\d{4}$/.test(firstPin)) { setFirstErr("PIN deve ter exatamente 4 dígitos numéricos."); return; }
    if (firstPin !== firstPin2) { setFirstErr("PINs não coincidem."); return; }
    const newCpf = firstCpf.trim() || emp.cpf;
    // v7.6: além de atualizar o employee, sincroniza pessoa/manager linkados
    const updated = { ...emp, pin: firstPin, cpf: newCpf, mustChangePin: false };
    onUpdateEmployee(updated);
    // Sincroniza os 3 records (pessoa/employee/manager) — usa CPF como chave de identidade
    const linkedPessoa = (pessoas || []).find(p =>
      p.linkedEmployeeId === emp.id ||
      ((p.cpf || "").replace(/\D/g, "") === (newCpf || "").replace(/\D/g, "") && (newCpf || "").replace(/\D/g, "").length > 0)
    );
    syncPinAcrossLinkedRecords({
      newPin: firstPin,
      identityCpf: newCpf,
      pessoa: linkedPessoa,
      pessoas: pessoas || [],
      employees: employees || [],
      managers: managers || [],
      onUpdate,
    });
  }

  // Se não tem empId — redireciona para login unificado
  if (!empId) { onBack(); return null; }

  if (empId && isFirstAccess) return (
    <div style={{ minHeight:"100vh", background:"var(--bg)", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'DM Sans',sans-serif", padding:24 }}>
      <div style={{ ...S.card, maxWidth:380, width:"100%", boxShadow:"0 8px 32px rgba(0,0,0,0.08)" }}>
        <div style={{ textAlign:"center", marginBottom:24 }}>
          <div style={{ fontSize:36, marginBottom:12 }}>🔑</div>
          <h2 style={{ color:"var(--text)", margin:"0 0 8px", fontSize:22, fontWeight:800 }}>
            {emp?.mustChangePin ? "Novo PIN" : "Primeiro Acesso"}
          </h2>
          <p style={{ color:"var(--text3)", fontSize:14, lineHeight:1.5 }}>
            {emp?.mustChangePin
              ? `PIN resetado pelo gestor. Defina um novo PIN, ${emp?.name}.`
              : `Bem-vindo, ${emp?.name}! ${needsCpf ? "Complete seu cadastro e defina seu PIN." : "Defina seu PIN de acesso."}`}
          </p>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {needsCpf && (
            <div>
              <label style={S.label}>Seu CPF</label>
              <input value={firstCpf} onChange={e=>setFirstCpf(maskCpf(e.target.value))} placeholder="000.000.000-00" style={S.input} inputMode="numeric"/>
            </div>
          )}
          <div>
            <label style={S.label}>Novo PIN (4 dígitos)</label>
            <input type="password" inputMode="numeric" maxLength={4} value={firstPin} onChange={e=>setFirstPin(e.target.value)} placeholder="••••" style={{ ...S.input, letterSpacing:8, fontSize:22, textAlign:"center", fontFamily:"'DM Mono',monospace" }}/>
          </div>
          <div>
            <label style={S.label}>Confirmar PIN</label>
            <input type="password" inputMode="numeric" maxLength={4} value={firstPin2} onChange={e=>setFirstPin2(e.target.value)} placeholder="••••" style={{ ...S.input, letterSpacing:8, fontSize:22, textAlign:"center", fontFamily:"'DM Mono',monospace" }} onKeyDown={e=>e.key==="Enter"&&completeFirstAccess()}/>
          </div>
          {firstErr && <div style={{background:"var(--red-bg)",border:"1px solid var(--red)33",borderRadius:8,padding:"10px 12px",color:"var(--red)",fontSize:13}}>{firstErr}</div>}
          <button onClick={completeFirstAccess} style={{...S.btnPrimary,marginTop:4}}>Confirmar e Entrar →</button>
          <button onClick={()=>{ setEmpId(null); onBack(); }} style={{background:"none",border:"none",color:"var(--text3)",cursor:"pointer",fontSize:13,marginTop:4,padding:8,fontFamily:"'DM Sans',sans-serif"}}>← Voltar ao login</button>
        </div>
      </div>
    </div>
  );

  // Bottom nav config: icon + short label
  const empInbox = getInboxForUser({ inbox: inbox ?? [], userId: empId, userRole: "employee", restaurantId: emp?.restaurantId, includeRead: false });
  const empInboxCount = empInbox.length;
  // v8.10: TODAS as abas do empregado respeitam empTabVisible (gorjeta combina com showTipsToEmployee).
  const NAV = [
    empTabVisible("comunicados") && ["comunicados","📢","Avisos"],
    empTabVisible("escala")      && ["escala","📅","Escala"],
    (restaurant?.showTipsToEmployee) && ["extrato","💸","Gorjeta"],
    empTabVisible("trilha")   && ["trilha","📈","Trilha"],
    empTabVisible("horarios") && ["horarios","🕐","Horários"],
    empTabVisible("faq")      && ["faq","❓","FAQ"],
    empTabVisible("dp")       && ["dp","💬","Fale DP"],
    empTabVisible("empInbox") && ["empInbox","📬",empInboxCount > 0 ? `Caixa (${empInboxCount})` : "Caixa"],
  ].filter(Boolean);

  return (
    <div style={{ minHeight:hideHeader?"auto":"100vh", background:"var(--bg)", fontFamily:"'DM Sans',sans-serif", paddingBottom:hideHeader?0:76 }}>
      {/* Header */}
      {!hideHeader && (
      <div style={{ background:"var(--header-bg)", borderBottom:"1px solid var(--border)", padding:"12px 16px", display:"flex", justifyContent:"space-between", alignItems:"center", boxShadow:"0 1px 4px rgba(0,0,0,0.04)", gap:8 }}>
        <div style={{minWidth:0,flex:1}}>
          <div style={{ color:"var(--text)", fontWeight:700, fontSize:15, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{emp?.name}</div>
          <div style={{ color:"var(--text3)", fontSize:11, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{role?.name} · {restaurant?.name}</div>
        </div>
        <div style={{display:"flex",gap:6,alignItems:"center",flexShrink:0}}>
          <button onClick={toggleTheme} style={{background:"none",border:"1px solid var(--border)",borderRadius:20,padding:"5px 8px",cursor:"pointer",fontSize:14,color:"var(--text2)"}}>
            {theme==="dark"?"☀️":"🌙"}
          </button>
          {onSwitchToOperational && <button onClick={onSwitchToOperational} title="Portal do Gestor Operacional" style={{...S.btnSecondary,fontSize:11,padding:"5px 10px",color:"#7c9e5e",borderColor:"#7c9e5e"}}>⚙️</button>}
          {onSwitchToManager && <button onClick={onSwitchToManager} style={{...S.btnSecondary,fontSize:11,padding:"5px 10px",color:"var(--ac)",borderColor:"var(--ac)"}}>📊</button>}
          <button onClick={() => { setEmpId(null); onBack(); }} style={{ ...S.btnSecondary, fontSize:11, padding:"5px 10px" }}>Sair</button>
        </div>
      </div>
      )}

      {hasPending && !hideHeader && (
        <div style={{ background:"var(--red-bg)", padding:"8px 16px", fontSize:13, color:"var(--red)", textAlign:"center", fontWeight:500 }}>
          ⚠️ Dê ciência nos comunicados para acessar as outras abas.
        </div>
      )}

      {/* Content */}
      <div style={{ padding: hideHeader?"0 8px":"16px 16px", maxWidth: hideHeader?780:600, margin: "0 auto" }}>

        {tab === "extrato" && (
          <div>
            <div style={{ marginBottom: 20 }}><MonthNav year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); }} /></div>
            {/* Aviso permanente de transparência — valores podem ser recalculados até o fechamento do mês */}
            <div style={{ background: "#f59e0b15", border: "1px solid #f59e0b44", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: "var(--text2)", lineHeight: 1.55, display:"flex", gap:10, alignItems:"flex-start" }}>
              <span style={{fontSize:18,lineHeight:1,marginTop:1}}>⚠️</span>
              <div>
                <strong style={{ color: "#b45309" }}>Valores em fase aberta —</strong> Os números mostrados aqui são <strong>para transparência</strong> e <strong>podem ser recalculados a qualquer momento</strong> até o fechamento do mês. O documento oficial e definitivo é o <strong>recibo de pagamento gerado no quinto dia útil</strong>.
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
              {[["Bruto", grossTotal, "var(--text)"], ["Imposto", taxTotal, "var(--red)"], ["Líquido", netTotal, ac]].map(([lbl, val, col]) => (
                <div key={lbl} style={{ ...S.card, textAlign: "center" }}>
                  <div style={{ color: "var(--text3)", fontSize: 10, marginBottom: 4 }}>{lbl}</div>
                  <div style={{ color: col, fontWeight: 700, fontSize: 14 }}>{fmt(val)}</div>
                </div>
              ))}
            </div>
            {myTips.length === 0 && <p style={{ color: "var(--text3)", textAlign: "center" }}>Nenhuma gorjeta registrada neste mês.</p>}
            {myTips.length > 0 && (() => {
              const sorted = [...myTips].sort((a,b) => a.date.localeCompare(b.date));
              let running = 0;
              return (
                <div>
                  {/* Table header */}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:4,padding:"6px 8px",background:"var(--bg1)",borderRadius:"8px 8px 0 0",marginBottom:2}}>
                    {["Data","Bruto","Desconto","Líquido"].map(h=><div key={h} style={{color:"var(--text3)",fontSize:10,fontWeight:700}}>{h}</div>)}
                  </div>
                  {sorted.map(t => {
                    running += t.myNet;
                    const statusOfDay = schedules?.[emp?.restaurantId]?.[mk]?.[empId]?.[t.date];
                    const statusLabels = {off:"Folga",freela:"Freela",vac:"Férias",faultj:"Falta Just.",faultu:"Falta Injust.",comp:"Compensação"};
                    return (
                      <div key={t.id} style={{background:"var(--card-bg)",borderBottom:"1px solid var(--border)",padding:"8px",borderRadius:0}}>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:4,marginBottom: statusOfDay||t.note?4:0}}>
                          <div style={{color:"var(--text2)",fontSize:12}}>{fmtDate(t.date)}</div>
                          <div style={{color:"var(--text)",fontSize:12}}>{fmt(t.myShare)}</div>
                          <div style={{color:"var(--red)",fontSize:12}}>-{fmt(t.myTax)}</div>
                          <div style={{color:ac,fontSize:12,fontWeight:700}}>{fmt(t.myNet)}</div>
                        </div>
                        {(statusOfDay || t.note) && (
                          <div style={{color:"var(--text3)",fontSize:10}}>
                            {statusOfDay && <span style={{marginRight:8}}>{statusLabels[statusOfDay]??statusOfDay}</span>}
                            {t.note && <span>📝 {t.note}</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {/* Running total */}
                  <div style={{background:"var(--bg1)",borderRadius:"0 0 8px 8px",padding:"10px 8px",display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:4}}>
                    <div style={{color:"var(--text3)",fontSize:11,fontWeight:700}}>Acumulado</div>
                    <div style={{color:"var(--text)",fontSize:11}}>{fmt(myTips.reduce((a,t)=>a+t.myShare,0))}</div>
                    <div style={{color:"var(--red)",fontSize:11}}>-{fmt(myTips.reduce((a,t)=>a+t.myTax,0))}</div>
                    <div style={{color:ac,fontSize:13,fontWeight:700}}>{fmt(running)}</div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {tab === "escala" && (
          <div>
            <div style={{ marginBottom: 20 }}><MonthNav year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); }} /></div>
            {monthBeforeVigencia ? (
              <div style={{...S.card,padding:24,textAlign:"center"}}>
                <div style={{fontSize:36,marginBottom:8}}>📅</div>
                <div style={{color:"var(--text)",fontWeight:700,fontSize:14}}>Não há escalas cadastradas para você neste mês.</div>
              </div>
            ) : (<>
            <div style={{display:"flex",alignItems:"flex-start",gap:8,padding:"10px 12px",background:"#f59e0b14",border:"1px solid #f59e0b44",borderRadius:10,marginBottom:14,fontSize:12,color:"var(--text2)",lineHeight:1.5}}>
              <span style={{fontSize:14,lineHeight:1}}>ℹ️</span>
              <span>A escala apresentada é <strong>prevista</strong> e pode sofrer alterações. Mudanças serão comunicadas conforme o contrato de trabalho vigente.</span>
            </div>
            <div style={{display:"flex",gap:8,marginBottom:14}}>
              <button onClick={()=>setEmpSchedView("mine")} style={{flex:1,padding:"8px",borderRadius:10,border:`1px solid ${empSchedView==="mine"?ac:"var(--border)"}`,background:empSchedView==="mine"?ac+"22":"transparent",color:empSchedView==="mine"?ac:"#555",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12}}>Minha Escala</button>
              <button onClick={()=>setEmpSchedView("area")} style={{flex:1,padding:"8px",borderRadius:10,border:`1px solid ${empSchedView==="area"?ac:"var(--border)"}`,background:empSchedView==="area"?ac+"22":"transparent",color:empSchedView==="area"?ac:"#555",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12}}>Escala da Área</button>
            </div>

            {empSchedView === "mine" && (
              <div>
                <p style={{ color: "var(--text3)", fontSize: 13, marginBottom: 16, textTransform: "capitalize" }}>Sua escala em {monthLabel(year, month)}</p>
                <CalendarGrid year={year} month={month} dayMap={dayMap} readOnly delayMap={delays?.[emp?.restaurantId]?.[mk]?.[empId] ?? {}} dayInversionsByDate={dayInversionsByDate} />
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginTop: 20 }}>
                  {(() => {
                    const dim = new Date(year, month + 1, 0).getDate();
                    const empDelayMap = delays?.[emp?.restaurantId]?.[mk]?.[empId] ?? {};
                    const counts = { work: 0, off: 0, freela: 0, comp: 0, comptrab: 0, vac: 0, fj: 0, fu: 0, delays: 0 };
                    for (let d = 1; d <= dim; d++) {
                      const k = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
                      const s = dayMap[k];
                      if (s === DAY_OFF) counts.off++;
                      else if (s === DAY_FREELA) counts.freela++;
                      else if (s === DAY_COMP) counts.comp++;
                      else if (s === DAY_COMP_TRAB) counts.comptrab++;
                      else if (s === DAY_VACATION) counts.vac++;
                      else if (s === DAY_FAULT_J) counts.fj++;
                      else if (s === DAY_FAULT_U) counts.fu++;
                      else counts.work++;
                      if (empDelayMap[String(d)] > 0) counts.delays++;
                    }
                    return [
                      ["Trabalho", counts.work, "var(--green)"], ["Folga", counts.off, "var(--red)"],
                      ["Freela", counts.freela, "#06b6d4"],
                      ["Folga Comp.", counts.comp, "#3b82f6"], ["Trab. Comp.", counts.comptrab, "#0ea5e9"],
                      ["Férias", counts.vac, "#8b5cf6"],
                      ["Falta Just.", counts.fj, "#f59e0b"], ["Falta Injust.", counts.fu, "var(--red)"],
                      ["Atrasos", counts.delays, "#f59e0b"],
                    ].filter(([,val]) => val > 0).map(([lbl, val, col]) => (
                      <div key={lbl} style={{ ...S.card, textAlign: "center", padding: "12px 8px" }}>
                        <div style={{ color: "var(--text3)", fontSize: 9, marginBottom: 4 }}>{lbl}</div>
                        <div style={{ color: col, fontWeight: 700, fontSize: 20 }}>{val}</div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}

            {empSchedView === "area" && (() => {
              const empRole = roles.find(r => r.id === emp?.roleId);
              const empArea = empRole?.area;
              const areaEmpsList = employees.filter(e => {
                const r = roles.find(r => r.id === e.roleId);
                return r?.area === empArea && e.restaurantId === emp?.restaurantId &&
                  !(e.inactive && e.inactiveFrom && e.inactiveFrom <= today());
              }).sort((a,b) => a.name.localeCompare(b.name));

              const dim = new Date(year, month+1, 0).getDate();
              const LEGEND = [
                ["var(--green)","Trabalho"],["var(--red)","Folga"],["#06b6d4","Freela"],
                ["#3b82f6","FC"],["#0ea5e9","TC"],
                ["#8b5cf6","Férias"],["#f59e0b","F.Just."],["var(--red)","F.Injust."],
              ];

              return (
                <div>
                  <p style={{color:"var(--text3)",fontSize:12,marginBottom:10}}>
                    Área <span style={{color:AREA_COLORS[empArea]??ac,fontWeight:700}}>{empArea}</span> — {monthLabel(year,month)}
                  </p>

                  {/* Legenda */}
                  <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:12}}>
                    {LEGEND.map(([c,lbl])=>(
                      <div key={lbl} style={{display:"flex",alignItems:"center",gap:3}}>
                        <div style={{width:10,height:10,borderRadius:3,background:c+"33",border:`1px solid ${c}`,flexShrink:0}}/>
                        <span style={{color:"var(--text3)",fontSize:9,fontFamily:"'DM Mono',monospace"}}>{lbl}</span>
                      </div>
                    ))}
                  </div>

                  {/* Tabela com scroll horizontal */}
                  <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch",borderRadius:10,border:"1px solid var(--border)"}}>
                    <table style={{borderCollapse:"collapse",fontFamily:"'DM Mono',monospace",fontSize:10,minWidth:"100%"}}>
                      <thead>
                        <tr style={{background:"var(--bg1)"}}>
                          <th style={{position:"sticky",left:0,background:"var(--bg1)",zIndex:2,padding:"6px 8px",textAlign:"left",color:"var(--text3)",fontSize:10,borderBottom:"1px solid var(--border)",whiteSpace:"nowrap",minWidth:90}}>
                            Empregado
                          </th>
                          {Array.from({length:dim},(_,i)=>{
                            const d = i+1;
                            const date = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
                            const wd = new Date(date+"T12:00:00").getDay();
                            const isWe = wd===0||wd===6;
                            const isToday = date === today();
                            return (
                              <th key={d} style={{padding:"3px 1px",textAlign:"center",color:isToday?ac:isWe?"#f59e0b":"var(--text3)",fontSize:9,borderBottom:"1px solid var(--border)",minWidth:22,width:22,background:isToday?"var(--ac)11":"transparent"}}>
                                <div style={{fontWeight:isToday?700:400}}>{d}</div>
                                <div style={{fontSize:7,opacity:0.7}}>{["D","S","T","Q","Q","S","S"][wd]}</div>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {areaEmpsList.map((e, ei) => {
                          const isMe = e.id === empId;
                          const savedDm = schedules?.[emp?.restaurantId]?.[mk]?.[e.id];
                          const restObj = restaurants?.find(r => r.id === emp?.restaurantId);
                          const dm = (savedDm && Object.keys(savedDm).length > 0) ? savedDm : deriveScheduleForEmp(e, year, month, workSchedules, restObj);
                          const role = roles.find(r=>r.id===e.roleId);
                          return (
                            <tr key={e.id} style={{background:isMe?"var(--ac-bg)":ei%2===0?"var(--bg1)":"var(--bg2)"}}>
                              <td style={{position:"sticky",left:0,background:isMe?"var(--ac-bg)":ei%2===0?"var(--bg1)":"var(--bg2)",zIndex:1,padding:"5px 8px",borderRight:"1px solid var(--border)",minWidth:90}}>
                                <div style={{color:isMe?ac:"var(--text)",fontSize:10,fontWeight:isMe?700:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:85}}>
                                  {e.name.split(" ")[0]}{isMe?" ✦":""}
                                </div>
                                <div style={{color:"var(--text3)",fontSize:8,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:85}}>{role?.name}</div>
                              </td>
                              {Array.from({length:dim},(_,i)=>{
                                const d = i+1;
                                const date = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
                                const status = dm[date];
                                const color = STATUS_COLORS[status] ?? "var(--green)";
                                const label = STATUS_SHORT[status] ?? "•";
                                const wd = new Date(date+"T12:00:00").getDay();
                                const isWe = wd===0||wd===6;
                                const isToday = date === today();
                                const delayMin = delays?.[emp?.restaurantId]?.[mk]?.[e.id]?.[String(d)] ?? 0;
                                return (
                                  <td key={d} title={delayMin > 0 ? `⏰ ${delayMin}min atraso` : undefined} style={{
                                    textAlign:"center",padding:"3px 1px",
                                    // v8.3: bg sólido em exceções, neutro em work
                                    background: status ? color : (isToday ? "var(--ac)11" : (isWe ? "var(--bg1)" : "transparent")),
                                    borderRight:`1px solid ${delayMin > 0 ? "#f59e0b" : "var(--border)"}`,
                                    borderBottom: delayMin > 0 ? "2px solid #f59e0b" : undefined,
                                    width:22,outline:isToday?`1px solid ${ac}44`:undefined
                                  }}>
                                    <span style={{color: status ? "#fff" : color, fontSize: status?8:9, fontWeight:700}}>{label}</span>
                                    {delayMin > 0 && <div style={{fontSize:6,color:status?"#fff":"#f59e0b",lineHeight:1}}>⏰</div>}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <p style={{color:"var(--text3)",fontSize:10,marginTop:8,textAlign:"center"}}>
                    ✦ você · role na coluna da esquerda
                  </p>
                </div>
              );
            })()}
            </>)}
          </div>
        )}

        {tab === "comunicados" && (
          <ComunicadosTab empId={empId} restaurantId={emp?.restaurantId} communications={communications} commAcks={commAcks} onUpdate={onUpdate} roles={roles} emp={emp} />
        )}

        {tab === "faq" && (
          <FaqTab restaurantId={emp?.restaurantId} faq={faq} emp={emp} roles={roles} restaurants={restaurants} splits={splits} employees={employees} />
        )}

        {tab === "trilha" && emp && (
          <EmpTrilhaView empId={empId} employees={employees} roles={roles} schedules={schedules} incidents={incidents??[]} feedbacks={feedbacks??[]} devChecklists={devChecklists??{}} restaurantId={emp.restaurantId} onUpdate={onUpdate} employeeGoals={employeeGoals??{}} meetingPlans={meetingPlans??[]}/>
        )}

        {tab === "dp" && (
          <FaleDpTab empId={empId} emp={emp} restaurantId={emp?.restaurantId} dpMessages={dpMessages} onUpdate={onUpdate} />
        )}

        {tab === "horarios" && (
          <WorkScheduleEmployeeTab empId={empId} restaurantId={emp?.restaurantId} workSchedules={workSchedules ?? {}} />
        )}

        {tab === "empInbox" && (() => {
          const ac2 = "#3b82f6";
          const eFolders = (inboxFolders ?? {})[empId] ?? [];
          const FOLDER_COLORS = ["#3498db","#e74c3c","#2ecc71","#f39c12","#9b59b6","#1abc9c","#e67e22","#34495e"];
          const isTrash = eFolder === "__trash";

          const myMsgs = getInboxForUser({ inbox: inbox ?? [], userId: empId, userRole: "employee", restaurantId: emp?.restaurantId, folder: eFolder });
          const entradaUn = getInboxForUser({ inbox: inbox ?? [], userId: empId, userRole: "employee", restaurantId: emp?.restaurantId, folder: null, includeRead: false }).length;
          const trashCt = getInboxForUser({ inbox: inbox ?? [], userId: empId, userRole: "employee", restaurantId: emp?.restaurantId, folder: "__trash" }).length;
          const fUnread = (fid) => getInboxForUser({ inbox: inbox ?? [], userId: empId, userRole: "employee", restaurantId: emp?.restaurantId, folder: fid, includeRead: false }).length;

          function softDel(m) {
            onUpdate("inbox", (inbox??[]).map(x => x.id===m.id ? {...x, deletedAt: new Date().toISOString(), folder: null} : x));
            onUpdate("_toast","🗑️ Movido para Excluídas");
          }
          function restore(m) {
            onUpdate("inbox", (inbox??[]).map(x => x.id===m.id ? {...x, deletedAt: null} : x));
            onUpdate("_toast","📥 Restaurado");
          }
          function moveMsg(msgId, folderId) {
            onUpdate("inbox", (inbox??[]).map(x => x.id===msgId ? {...x, folder: folderId, deletedAt: null} : x));
            setEMoveModal(null);
            onUpdate("_toast", folderId ? `📁 Movido` : "📥 Movido para Entrada");
          }
          function saveFld() {
            if (!eFolderForm.nome.trim()) return;
            const allF = { ...(inboxFolders ?? {}) };
            const mine = [...(allF[empId] ?? [])];
            if (eFolderModal === "new") mine.push({ id: Date.now().toString(36), nome: eFolderForm.nome.trim(), cor: eFolderForm.cor, ordem: mine.length });
            else if (eFolderModal?.id) { const idx = mine.findIndex(f=>f.id===eFolderModal.id); if (idx>=0) mine[idx]={...mine[idx],nome:eFolderForm.nome.trim(),cor:eFolderForm.cor}; }
            allF[empId] = mine;
            onUpdate("inboxFolders", allF);
            setEFolderModal(null);
          }
          async function delFld(fid) {
            if (!await appConfirm("Excluir esta pasta?")) return;
            onUpdate("inbox", (inbox??[]).map(x => x.folder===fid?{...x,folder:null}:x));
            const allF = { ...(inboxFolders ?? {}) };
            allF[empId] = (allF[empId] ?? []).filter(f => f.id !== fid);
            onUpdate("inboxFolders", allF);
            if (eFolder === fid) setEFolder(null);
          }

          const tabSt = (active, cor) => ({padding:"5px 10px",borderRadius:16,border:`1px solid ${active?(cor||ac2)+"66":"var(--border)"}`,background:active?(cor||ac2)+"15":"transparent",color:active?(cor||ac2):"var(--text3)",cursor:"pointer",fontSize:11,fontFamily:"'DM Sans',sans-serif",fontWeight:active?700:500,display:"flex",alignItems:"center",gap:4});
          const ICONS = { reuniao:"📅", acao_reuniao:"📋", comunicado:"📨", sistema:"⚙️" };
          const LABELS = { reuniao:"Reunião", acao_reuniao:"Ação", comunicado:"Comunicado", sistema:"Sistema" };

          return (
            <div>
              <h3 style={{color:"var(--text)",fontSize:16,fontWeight:700,margin:"0 0 12px"}}>📬 Caixa de entrada</h3>

              {/* Folder tabs */}
              <div style={{display:"flex",gap:5,marginBottom:12,flexWrap:"wrap",alignItems:"center"}}>
                <button onClick={()=>setEFolder(null)} style={tabSt(eFolder===null)}>📥 Entrada{entradaUn>0?` (${entradaUn})`:""}</button>
                {eFolders.map(f => {
                  const fu = fUnread(f.id);
                  return <button key={f.id} onClick={()=>setEFolder(f.id)} style={tabSt(eFolder===f.id,f.cor)}><span style={{width:7,height:7,borderRadius:"50%",background:f.cor||ac2}}></span>{f.nome}{fu>0?` (${fu})`:""}</button>;
                })}
                <button onClick={()=>setEFolder("__trash")} style={tabSt(isTrash)}>🗑️ Excluídas{trashCt>0?` (${trashCt})`:""}</button>
                <button onClick={()=>{setEFolderModal("new");setEFolderForm({nome:"",cor:"#3498db"});}} style={{background:"none",border:"1px dashed var(--border)",borderRadius:16,padding:"5px 8px",color:"var(--text3)",cursor:"pointer",fontSize:11}}>+</button>
              </div>

              {/* Edit/delete custom folder */}
              {eFolder && eFolder !== "__trash" && (() => {
                const cf = eFolders.find(f=>f.id===eFolder);
                return cf ? (
                  <div style={{display:"flex",gap:6,marginBottom:10}}>
                    <button onClick={()=>{setEFolderModal(cf);setEFolderForm({nome:cf.nome,cor:cf.cor||"#3498db"});}} style={{...S.btnSecondary,fontSize:10,padding:"3px 8px"}}>Renomear</button>
                    <button onClick={()=>delFld(cf.id)} style={{...S.btnSecondary,fontSize:10,padding:"3px 8px",color:"#e74c3c"}}>Excluir pasta</button>
                  </div>
                ) : null;
              })()}

              {isTrash && <p style={{color:"var(--text3)",fontSize:11,marginBottom:8}}>Excluídas são removidas após 90 dias.</p>}

              {myMsgs.length === 0 && (
                <div style={{...S.card,textAlign:"center",padding:40}}>
                  <div style={{fontSize:36,marginBottom:12}}>{isTrash?"🗑️":"📭"}</div>
                  <p style={{color:"var(--text3)",fontSize:14}}>{isTrash?"Nenhuma mensagem excluída.":"Nenhuma mensagem."}</p>
                </div>
              )}
              {myMsgs.map(m => (
                <div key={m.id} style={{...S.card,marginBottom:10,border:`1px solid ${m.lido?"var(--border)":"#3b82f633"}`,background:m.lido?"var(--card-bg)":"#3b82f608"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                    <div style={{flex:1}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                        <span style={{fontSize:14}}>{ICONS[m.tipo]??"💬"}</span>
                        <span style={{color:"var(--text)",fontWeight:700,fontSize:13}}>{m.assunto || LABELS[m.tipo] || "Mensagem"}</span>
                        {!m.lido && <span style={{background:"#3b82f6",color:"#fff",borderRadius:20,padding:"1px 8px",fontSize:10,fontWeight:700}}>Novo</span>}
                      </div>
                      {m.de && <div style={{color:"var(--text3)",fontSize:11,marginBottom:4}}>De: {m.de.nome || m.de.role}</div>}
                      <div style={{color:"var(--text2)",fontSize:13,lineHeight:1.6,whiteSpace:"pre-line",marginBottom:6}}>{m.corpo}</div>
                      <span style={{color:"var(--text3)",fontSize:11}}>{m.criadoEm ? fmtRelTime(m.criadoEm) : ""}</span>
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:4,flexShrink:0}}>
                      {!m.lido && !isTrash && <button onClick={()=>onUpdate("inbox",(inbox??[]).map(x=>x.id===m.id?{...x,lido:true}:x))} style={{background:"none",border:"none",color:"var(--text3)",cursor:"pointer",fontSize:16,padding:4}} title="Marcar como lida">✓</button>}
                      {!isTrash && eFolders.length > 0 && <button onClick={()=>setEMoveModal(m.id)} style={{background:"none",border:"1px solid var(--border)",borderRadius:6,color:"var(--text3)",cursor:"pointer",fontSize:11,padding:"2px 6px"}}>📁</button>}
                      {isTrash ? (
                        <button onClick={()=>restore(m)} style={{background:"none",border:"1px solid var(--border)",borderRadius:6,color:"var(--text3)",cursor:"pointer",fontSize:11,padding:"2px 6px"}}>📥</button>
                      ) : (
                        <button onClick={()=>softDel(m)} style={{background:"none",border:"1px solid #e74c3c22",borderRadius:6,color:"var(--text3)",cursor:"pointer",fontSize:11,padding:"2px 6px"}}>🗑️</button>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Move modal */}
              {eMoveModal && (
                <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999,padding:16}} onClick={()=>setEMoveModal(null)}>
                  <div style={{background:"var(--card-bg)",borderRadius:16,padding:20,maxWidth:300,width:"100%",border:"1px solid var(--border)"}} onClick={e=>e.stopPropagation()}>
                    <h3 style={{color:"var(--text)",fontSize:14,fontWeight:700,margin:"0 0 12px"}}>📁 Mover</h3>
                    <div style={{display:"flex",flexDirection:"column",gap:6}}>
                      <button onClick={()=>moveMsg(eMoveModal,null)} style={{...S.btnSecondary,fontSize:12,textAlign:"left"}}>📥 Entrada</button>
                      {eFolders.map(f => (
                        <button key={f.id} onClick={()=>moveMsg(eMoveModal,f.id)} style={{...S.btnSecondary,fontSize:12,textAlign:"left",display:"flex",alignItems:"center",gap:6}}>
                          <span style={{width:8,height:8,borderRadius:"50%",background:f.cor||ac2}}></span> {f.nome}
                        </button>
                      ))}
                    </div>
                    <div style={{marginTop:10,textAlign:"right"}}><button onClick={()=>setEMoveModal(null)} style={{...S.btnSecondary,fontSize:11}}>Cancelar</button></div>
                  </div>
                </div>
              )}

              {/* Folder modal */}
              {eFolderModal && (
                <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999,padding:16}} onClick={()=>setEFolderModal(null)}>
                  <div style={{background:"var(--card-bg)",borderRadius:16,padding:20,maxWidth:340,width:"100%",border:"1px solid var(--border)"}} onClick={e=>e.stopPropagation()}>
                    <h3 style={{color:"var(--text)",fontSize:14,fontWeight:700,margin:"0 0 12px"}}>{eFolderModal==="new"?"Nova pasta":"Editar pasta"}</h3>
                    <div style={{marginBottom:10}}>
                      <input value={eFolderForm.nome} onChange={e=>setEFolderForm(p=>({...p,nome:e.target.value}))} style={{...S.input,width:"100%"}} placeholder="Nome" maxLength={30}/>
                    </div>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
                      {FOLDER_COLORS.map(c => (
                        <button key={c} onClick={()=>setEFolderForm(p=>({...p,cor:c}))} style={{width:24,height:24,borderRadius:"50%",background:c,border:eFolderForm.cor===c?"3px solid var(--text)":"2px solid transparent",cursor:"pointer"}}/>
                      ))}
                    </div>
                    <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
                      <button onClick={()=>setEFolderModal(null)} style={{...S.btnSecondary,fontSize:12}}>Cancelar</button>
                      <button onClick={saveFld} disabled={!eFolderForm.nome.trim()} style={{...S.btnPrimary,fontSize:12,opacity:!eFolderForm.nome.trim()?0.5:1}}>Salvar</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

      </div>

      {/* Bottom navigation bar — omitida quando embedado no shell */}
      {!hideTabNav && (
      <div style={{ position:"fixed", bottom:0, left:0, right:0, background:"var(--header-bg)", borderTop:"1px solid var(--border)", display:"flex", zIndex:100, paddingBottom:"env(safe-area-inset-bottom, 0px)", boxShadow:"0 -2px 12px rgba(0,0,0,0.06)" }}>
        {NAV.map(([id, icon, label]) => {
          const blocked = hasPending && id !== "comunicados";
          const isActive = tab === id;
          const hasBadge = id === "comunicados" && pendingComms.length > 0;
          return (
            <button key={id} onClick={() => !blocked && handleTabChange(id)}
              style={{ flex:1, background:"none", border:"none", padding:"10px 4px 8px", cursor:blocked?"not-allowed":"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:3, opacity:blocked?0.3:1, position:"relative" }}>
              {hasBadge && (
                <span style={{ position:"absolute", top:6, right:"calc(50% - 14px)", background:"var(--red)", color:"var(--text)", borderRadius:10, padding:"1px 5px", fontSize:9, fontWeight:700, lineHeight:1.4 }}>{pendingComms.length}</span>
              )}
              <span style={{ fontSize:22, lineHeight:1 }}>{icon}</span>
              <span style={{ fontSize:9, fontFamily:"'DM Sans',sans-serif", color:isActive?ac:"var(--text3)", fontWeight:isActive?700:500 }}>{label}</span>
              {isActive && <div style={{ position:"absolute", bottom:0, left:"20%", right:"20%", height:2, background:ac, borderRadius:2 }}/>}
            </button>
          );
        })}
      </div>
      )}
    </div>
  );
}
//
//
function RoleSpreadsheet({ restRoles, rid, roles, employees, onUpdate }) {
  const blank = () => ({ id: null, name: "", area: "Bar", points: "1", restaurantId: rid });
  const [newRow, setNewRow] = useState(blank());
  const [editRows, setEditRows] = useState({});
  const [aiInput, setAiInput] = useState("");
  const { generate: aiGenerate, aiLoading, aiError, setAiError } = useAiGenerate();
  const [showAi, setShowAi] = useState(false);
  const [pendingNoTip, setPendingNoTip] = useState({}); // track noTip changes locally

  const [aiPreview, setAiPreview] = useState(null); // {criar:[], modificar:[], inativar:[]}

  async function handleAiCargos() {
    if (!aiInput.trim()) return;
    setAiPreview(null);
    const existingList = restRoles.map(r => `- id:"${r.id}" nome:"${r.name}" area:"${r.area}" pontos:${r.points} semGorjeta:${!!r.noTip} inativo:${!!r.inactive}`).join("\n");
    const result = await aiGenerate(
        `Você é um assistente de gestão de restaurantes. O usuário pode pedir para CRIAR novos cargos, MODIFICAR cargos existentes (renomear, mudar pontos, área, sem gorjeta) ou INATIVAR cargos.

Cargos existentes:
${existingList || "(nenhum)"}

Regras:
- "area" deve ser: Bar, Cozinha, Salão ou Limpeza
- "pontos": 0 a 20 (0 = sem gorjeta). Se não informado ao criar, estime pela hierarquia (Gerente=10, Subchef=9, Garçom=6, Auxiliar=3)
- Se não informar área ao criar, deduza pelo nome do cargo
- Para modificar, use o "id" do cargo existente e inclua APENAS os campos que mudam
- Para inativar, use o "id" do cargo existente

Responda com JSON:
{
  "criar": [{"nome":"...", "area":"...", "pontos":6, "semGorjeta":false}],
  "modificar": [{"id":"...", "nome":"...", "area":"...", "pontos":6, "semGorjeta":false}],
  "inativar": ["id1", "id2"]
}
Inclua apenas as ações solicitadas. Arrays vazios se não houver ação daquele tipo.`,
      aiInput.trim()
    );
    if (!result) return;
    // Prepare preview
    const preview = {
      criar: (result.criar ?? []).map(c => ({
        id: `role-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
        name: c.nome, area: c.area, points: c.semGorjeta ? 0 : (c.pontos ?? 1), noTip: c.semGorjeta ?? false,
      })),
      modificar: (result.modificar ?? []).map(m => {
        const existing = restRoles.find(r => r.id === m.id);
        if (!existing) return null;
        return {
          id: m.id, oldName: existing.name,
          name: m.nome ?? existing.name,
          area: m.area ?? existing.area,
          points: m.semGorjeta ? 0 : (m.pontos !== undefined ? m.pontos : existing.points),
          noTip: m.semGorjeta !== undefined ? m.semGorjeta : !!existing.noTip,
        };
      }).filter(Boolean),
      inativar: (result.inativar ?? []).map(id => restRoles.find(r => r.id === id)).filter(Boolean),
    };
    if (!preview.criar.length && !preview.modificar.length && !preview.inativar.length) {
      setAiError("A IA não identificou nenhuma ação. Tente reformular.");
    } else {
      setAiPreview(preview);
    }
  }

  function confirmAiChanges() {
    if (!aiPreview) return;
    let updated = [...roles];
    // Create new roles
    aiPreview.criar.forEach(c => {
      updated.push({ id: c.id, restaurantId: rid, name: c.name, area: c.area, points: c.points, noTip: c.noTip, inactive: false });
    });
    // Modify existing
    aiPreview.modificar.forEach(m => {
      updated = updated.map(r => r.id === m.id ? { ...r, name: m.name, area: m.area, points: m.points, noTip: m.noTip } : r);
    });
    // Inactivate
    aiPreview.inativar.forEach(r => {
      updated = updated.map(x => x.id === r.id ? { ...x, inactive: true } : x);
    });
    onUpdate("roles", updated);
    const total = aiPreview.criar.length + aiPreview.modificar.length + aiPreview.inativar.length;
    onUpdate("_toast", `✨ ${total} ação(ões) aplicada(s) pela IA!`);
    setAiPreview(null); setAiInput(""); setShowAi(false);
  }

  const ROLE_COLS = "minmax(80px,2fr) 60px 100px auto";

  function getRow(r) { return editRows[r.id] ?? { name: r.name, area: r.area ?? "Bar", points: r.points === 0 ? "0" : String(r.points || "") }; }
  function setRow(role, field, val) { setEditRows(prev => ({ ...prev, [role.id]: { ...getRow(role), [field]: val } })); }
  function getNoTip(r) { return pendingNoTip[r.id] !== undefined ? pendingNoTip[r.id] : !!r.noTip; }

  // Check if any rows have unsaved changes
  const isDirty = Object.keys(editRows).length > 0 || Object.keys(pendingNoTip).length > 0;

  function saveAll() {
    let updated = [...roles];
    // Apply all editRows and pendingNoTip changes
    for (const r of restRoles) {
      const row = editRows[r.id];
      const noTipChanged = pendingNoTip[r.id] !== undefined;
      if (!row && !noTipChanged) continue;
      const merged = row ?? { name: r.name, area: r.area ?? "Bar", points: r.points === 0 ? "0" : String(r.points || "") };
      if (!merged.name.trim()) continue;
      const noTip = noTipChanged ? pendingNoTip[r.id] : !!r.noTip;
      const pts = noTip ? 0 : (merged.points === "" ? 0 : (Number(merged.points) || 0));
      updated = updated.map(x => x.id === r.id ? { ...x, name: merged.name.trim(), area: merged.area, points: pts, noTip } : x);
    }
    onUpdate("roles", updated);
    setEditRows({});
    setPendingNoTip({});
    onUpdate("_toast", "✅ Cargos salvos!");
  }

  function discardAll() {
    setEditRows({});
    setPendingNoTip({});
  }

  function saveNew() {
    if (!newRow.name.trim()) return;
    const pts = newRow.points === "" ? 0 : (Number(newRow.points) || 0);
    const r = { ...newRow, id: Date.now().toString(), points: pts };
    onUpdate("roles", [...roles, r]);
    setNewRow(blank());
  }

  function inactivateRole(id) { onUpdate("roles", roles.map(x => x.id === id ? {...x, inactive: true} : x)); }
  function reactivateRole(id) { onUpdate("roles", roles.map(x => x.id === id ? {...x, inactive: false} : x)); }

  async function deleteRole(id) {
    const r = roles.find(x => x.id === id);
    const empCount = (employees ?? []).filter(e => e.roleId === id && e.restaurantId === rid).length;
    if (empCount > 0) {
      onUpdate("_toast", `⚠️ Não é possível apagar: ${empCount} empregado(s) vinculado(s) a este cargo`);
      return;
    }
    if (!await appConfirm(`Apagar permanentemente o cargo "${r?.name ?? ""}"?`)) return;
    onUpdate("roles", roles.filter(x => x.id !== id));
    onUpdate("_toast", "🗑️ Cargo apagado");
  }

  const inStyle = { background: "var(--bg1)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", fontFamily: "'DM Mono',monospace", fontSize: 12, padding: "6px 8px", outline: "none", width: "100%", boxSizing:"border-box" };
  const sel = { ...inStyle, cursor: "pointer" };
  const ac = "var(--ac)";

  // Agrupar por área
  const activeByArea = {};
  const inactiveList = [];
  AREAS.forEach(a => { activeByArea[a] = []; });
  [...restRoles].sort((a,b) => a.name.localeCompare(b.name)).forEach(r => {
    if (r.inactive) inactiveList.push(r);
    else if (activeByArea[r.area]) activeByArea[r.area].push(r);
    else activeByArea[r.area] = [r];
  });

  const renderRow = (r) => {
    const row = getRow(r);
    const noTip = getNoTip(r);
    const hasEdit = !!editRows[r.id] || pendingNoTip[r.id] !== undefined;
    return (
      <div key={r.id} style={{ display:"grid", gridTemplateColumns:ROLE_COLS, gap:6, marginBottom:4, background:hasEdit?"var(--ac-bg)":"var(--card-bg)", borderRadius:10, padding:"6px 8px", border:`1px solid ${hasEdit?"var(--ac)44":r.inactive?"#8b5cf622":"var(--border)"}`, opacity:r.inactive?0.6:1, alignItems:"center" }}>
        <input value={row.name} onChange={e => setRow(r, "name", e.target.value)} style={inStyle} />
        <input type="number" min="0" step="0.5" value={noTip ? 0 : row.points} disabled={noTip} onChange={e => setRow(r, "points", e.target.value)} style={{...inStyle, opacity: noTip ? 0.4 : 1, textAlign:"center"}} />
        <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",color:"var(--text2)",fontSize:12,fontFamily:"'DM Mono',monospace",whiteSpace:"nowrap"}}>
          <input type="checkbox" checked={noTip} onChange={e=>{setPendingNoTip(p=>({...p,[r.id]:e.target.checked}));if(e.target.checked) setRow(r,"points","0");}} style={{width:14,height:14,cursor:"pointer",accentColor:ac}}/>
          Sem gorjeta
        </label>
        <div style={{display:"flex",gap:4,justifyContent:"flex-end"}}>
          {r.inactive
            ? <>
                <button onClick={()=>reactivateRole(r.id)} style={{padding:"5px 10px",borderRadius:7,border:"1px solid #10b98144",background:"transparent",color:"var(--green)",cursor:"pointer",fontSize:11,fontFamily:"'DM Mono',monospace",whiteSpace:"nowrap"}}>Reativar</button>
                <button onClick={()=>deleteRole(r.id)} style={{padding:"5px 10px",borderRadius:7,border:"1px solid #e74c3c33",background:"transparent",color:"var(--red)",cursor:"pointer",fontSize:11,fontFamily:"'DM Mono',monospace",whiteSpace:"nowrap"}}>✕</button>
              </>
            : <button onClick={()=>inactivateRole(r.id)} style={{padding:"5px 10px",borderRadius:7,border:"1px solid #f59e0b44",background:"transparent",color:"#f59e0b",cursor:"pointer",fontSize:11,fontFamily:"'DM Mono',monospace",whiteSpace:"nowrap"}}>Inativar</button>
          }
        </div>
      </div>
    );
  };

  return (
    <div style={{ fontFamily: "'DM Mono',monospace" }}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <p style={{ color: "var(--text3)", fontSize: 12, margin:0 }}>Edite inline. Salve tudo ao terminar.</p>
        {isDirty && (
          <div style={{display:"flex",gap:8}}>
            <button onClick={discardAll} style={{...S.btnSecondary,fontSize:12,padding:"5px 12px"}}>Descartar</button>
            <button onClick={saveAll} style={{padding:"5px 14px",borderRadius:8,border:"none",background:ac,color:"var(--text)",fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"'DM Mono',monospace"}}>💾 Salvar tudo</button>
          </div>
        )}
      </div>

      {/* Unsaved changes banner */}
      {isDirty && (
        <div style={{background:"#f59e0b22",border:"1px solid #f59e0b44",borderRadius:10,padding:"8px 14px",marginBottom:12,fontSize:12,color:"#f59e0b",fontFamily:"'DM Mono',monospace"}}>
          ⚠️ Alterações não salvas — clique "Salvar tudo" para confirmar
        </div>
      )}

      {/* Assistente IA */}
      <div style={{marginBottom:14}}>
        <button onClick={()=>{setShowAi(!showAi);setAiError("");setAiPreview(null);}}
          style={{...S.btnSecondary,fontSize:12,display:"inline-flex",alignItems:"center",gap:6,padding:"7px 14px",
            background:showAi?"var(--ac-bg)":undefined,borderColor:showAi?"var(--ac)":undefined,color:showAi?"var(--ac-text)":undefined}}>
          ✨ Gerenciar cargos com IA
        </button>
        {showAi && (
          <div style={{marginTop:10,padding:"14px",borderRadius:12,background:"var(--ac-bg)",border:"1px solid var(--ac)33"}}>
            <p style={{color:"var(--text2)",fontSize:13,margin:"0 0 6px",fontWeight:600}}>✨ Assistente de cargos</p>
            <p style={{color:"var(--text3)",fontSize:12,margin:"0 0 6px",lineHeight:1.5}}>Crie, modifique, inative ou ajuste pontos dos cargos. A IA entende linguagem natural.</p>
            <p style={{color:"var(--text3)",fontSize:11,margin:"0 0 10px",fontStyle:"italic"}}>Ex: "Adicione Garçom 6pts no Salão" · "Mude o Barman para 8 pontos" · "Inative o cargo Auxiliar" · "Cozinheiro agora é sem gorjeta"</p>
            <textarea value={aiInput} onChange={e=>setAiInput(e.target.value)}
              placeholder="Descreva o que quer fazer com os cargos..." rows={3}
              style={{...S.input,resize:"vertical",marginBottom:8,fontSize:13}}/>
            {aiError && <p style={{color:"var(--red)",fontSize:12,margin:"0 0 8px"}}>{aiError}</p>}

            {/* AI Preview/Confirmation */}
            {aiPreview && (
              <div style={{background:"var(--bg1)",border:"1px solid var(--border)",borderRadius:10,padding:"12px",marginBottom:10}}>
                <p style={{color:"var(--text)",fontWeight:700,fontSize:13,margin:"0 0 10px"}}>Confirme as alterações:</p>
                {aiPreview.criar.length > 0 && (
                  <div style={{marginBottom:8}}>
                    <span style={{color:"var(--green)",fontSize:11,fontWeight:700}}>+ CRIAR</span>
                    {aiPreview.criar.map(c => (
                      <div key={c.id} style={{padding:"4px 8px",marginTop:4,borderRadius:6,background:"#10b98111",fontSize:12,color:"var(--text2)"}}>
                        {c.name} · {c.area} · {c.noTip ? "Sem gorjeta" : `${c.points} pts`}
                      </div>
                    ))}
                  </div>
                )}
                {aiPreview.modificar.length > 0 && (
                  <div style={{marginBottom:8}}>
                    <span style={{color:"#3b82f6",fontSize:11,fontWeight:700}}>✏️ MODIFICAR</span>
                    {aiPreview.modificar.map(m => (
                      <div key={m.id} style={{padding:"4px 8px",marginTop:4,borderRadius:6,background:"#3b82f611",fontSize:12,color:"var(--text2)"}}>
                        {m.oldName !== m.name ? <><s>{m.oldName}</s> → {m.name}</> : m.name} · {m.area} · {m.noTip ? "Sem gorjeta" : `${m.points} pts`}
                      </div>
                    ))}
                  </div>
                )}
                {aiPreview.inativar.length > 0 && (
                  <div style={{marginBottom:8}}>
                    <span style={{color:"#f59e0b",fontSize:11,fontWeight:700}}>⏸ INATIVAR</span>
                    {aiPreview.inativar.map(r => (
                      <div key={r.id} style={{padding:"4px 8px",marginTop:4,borderRadius:6,background:"#f59e0b11",fontSize:12,color:"var(--text2)"}}>
                        {r.name} · {r.area}
                      </div>
                    ))}
                  </div>
                )}
                <div style={{display:"flex",gap:8,marginTop:10}}>
                  <button onClick={confirmAiChanges} style={{...S.btnPrimary,flex:1,fontSize:13}}>✅ Confirmar e aplicar</button>
                  <button onClick={()=>setAiPreview(null)} style={S.btnSecondary}>Cancelar</button>
                </div>
              </div>
            )}

            {!aiPreview && (
              <div style={{display:"flex",gap:8}}>
                <button onClick={handleAiCargos} disabled={!aiInput.trim()||aiLoading}
                  style={{...S.btnPrimary,flex:1,fontSize:13,opacity:(!aiInput.trim()||aiLoading)?0.6:1}}>
                  {aiLoading?"✨ Processando...":"✨ Processar com IA"}
                </button>
                <button onClick={()=>{setShowAi(false);setAiInput("");setAiError("");setAiPreview(null);}} style={S.btnSecondary}>Cancelar</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Cabeçalho */}
      <div style={{ display:"grid", gridTemplateColumns:ROLE_COLS, gap:6, marginBottom:6, padding:"0 8px" }}>
        {["Nome do Cargo","Pontos","",""].map((h,i) => <div key={i} style={{color:"var(--text3)",fontSize:10,fontWeight:700}}>{h}</div>)}
      </div>

      {/* Nova linha */}
      <div style={{ display:"grid", gridTemplateColumns:ROLE_COLS, gap:6, marginBottom:16, background:"#f0fdf4", borderRadius:10, padding:"6px 8px", border:"1px solid #10b98144", alignItems:"center" }}>
        <input value={newRow.name} onChange={e => setNewRow(p => ({ ...p, name: e.target.value }))} placeholder="Nome do cargo…" style={inStyle} />
        <input type="number" min="0.5" step="0.5" value={newRow.noTip ? 0 : newRow.points} disabled={newRow.noTip} onChange={e => setNewRow(p => ({ ...p, points: e.target.value }))} style={{...inStyle, opacity: newRow.noTip ? 0.4 : 1, textAlign:"center"}} />
        <div style={{display:"flex",alignItems:"center",gap:6}}>
          <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",color:"var(--text2)",fontSize:12,whiteSpace:"nowrap"}}>
            <input type="checkbox" checked={!!newRow.noTip} onChange={e=>setNewRow(p=>({...p,noTip:e.target.checked,points:e.target.checked?"0":p.points}))} style={{width:14,height:14,cursor:"pointer",accentColor:ac}}/>
            Sem gorjeta
          </label>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:6}}>
          <select value={newRow.area} onChange={e => setNewRow(p => ({ ...p, area: e.target.value }))} style={{...sel,flex:1}}>
            {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <button onClick={saveNew} style={{padding:"6px 10px",borderRadius:8,border:"none",background:"var(--green)",color:"var(--text)",fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"'DM Mono',monospace",whiteSpace:"nowrap"}}>+ Add</button>
        </div>
      </div>

      {/* Agrupado por área */}
      {AREAS.map(area => {
        const areaRoles = activeByArea[area] ?? [];
        if (!areaRoles.length) return null;
        return (
          <div key={area} style={{marginBottom:16}}>
            <div style={{color:AREA_COLORS[area]??"#888",fontSize:11,fontWeight:700,padding:"6px 8px 4px",borderBottom:`1px solid ${AREA_COLORS[area]??"var(--bg4)"}33`,marginBottom:4,letterSpacing:1}}>
              {area.toUpperCase()} · {areaRoles.length} cargo{areaRoles.length!==1?"s":""}
            </div>
            {areaRoles.map(renderRow)}
          </div>
        );
      })}

      {/* Inativos */}
      {inactiveList.length > 0 && (
        <div style={{marginTop:8}}>
          <div style={{color:"var(--text3)",fontSize:11,fontWeight:700,padding:"6px 8px 4px",borderBottom:"1px solid var(--border)",marginBottom:4,letterSpacing:1}}>
            INATIVOS · {inactiveList.length}
          </div>
          {inactiveList.map(renderRow)}
        </div>
      )}

      {/* Bottom save bar */}
      {isDirty && (
        <div style={{position:"sticky",bottom:0,background:"var(--bg)",borderTop:"1px solid var(--ac)44",padding:"10px 0",marginTop:16,display:"flex",justifyContent:"flex-end",gap:8}}>
          <button onClick={discardAll} style={{...S.btnSecondary,fontSize:12,padding:"6px 14px"}}>Descartar</button>
          <button onClick={saveAll} style={{padding:"6px 16px",borderRadius:8,border:"none",background:ac,color:"var(--text)",fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:"'DM Mono',monospace"}}>💾 Salvar tudo</button>
        </div>
      )}
    </div>
  );
}



function EmployeeSpreadsheet({ restEmps, restRoles, rid, employees, pessoas, onUpdate, restCode: restCode_, isOwner, restaurant, notifications, privacyMask, onGenerateDismissalReport, incidents, feedbacks, devChecklists, schedules, currentUser, isLider, mobileOnly: mobileOnlyProp, roles, vtPayments, vtConfig, scheduleStatus, employeeGoals, delays, meetingPlans, meetingOccurrences, resetSignal, workSchedules, managers, communications }) {
  const mobileOnly = mobileOnlyProp ?? false; // eslint-disable-line no-unused-vars
  const PLANOS = [
    { id:"pIsento", empMax:999 },
    { id:"p10",  empMax:10  },
    { id:"p20",  empMax:20  },
    { id:"p50",  empMax:50  },
    { id:"p999", empMax:100 },
    { id:"pOrc", empMax:999 },
  ];
  const plano = PLANOS.find(p=>p.id===(restaurant?.planoId??"p10")) ?? PLANOS[0];
  const activeCount = restEmps.filter(e=>!e.inactive).length;
  // (blank/newRow removidos — criação direta descontinuada)
  const [editRows, setEditRows] = useState({});
  const [saved, setSaved] = useState({});
  const [showInactive, setShowInactive] = useState(false);
  // (IA "Gerenciar empregados" removida — adição em lote agora vive em Pessoas via IA)
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewFileName, setPreviewFileName] = useState("");
  const [showDismissalChecklist, setShowDismissalChecklist] = useState(false);
  const [dismissalCheckEmp, setDismissalCheckEmp] = useState(null);
  // Modais com date picker (substitui window.prompt)
  const [dismissForm, setDismissForm] = useState(null); // null | { emp, lastWorkedDay }
  const [promoteForm, setPromoteForm] = useState(null); // null | { emp, newRoleId, lastDayOldRole, reason }
  const [detailEmp, setDetailEmp] = useState(null); // empId for detail view
  const [detailTab, setDetailTab] = useState("cadastro"); // cadastro | acoes | trilha
  // Reset detail view when parent signals (e.g. re-clicking "Equipe" tab)
  useEffect(() => { if (resetSignal > 0) { setDetailEmp(null); setDetailTab("cadastro"); } }, [resetSignal]); // eslint-disable-line react-hooks/exhaustive-deps
  // showIncForm removido — ocorrências cadastradas pela aba Reuniões
  // showFbForm removed — meetings now created only in Reuniões tab
  const [expandedJornada, setExpandedJornada] = useState(null); // id of expanded event
  // ── Vincular pessoa como empregado (refactor "Pessoas é fonte única") ──
  const [vincularPessoaOpen, setVincularPessoaOpen] = useState(false);
  const [vincularPessoaSelected, setVincularPessoaSelected] = useState(null); // pessoa
  const [vincularPessoaRole, setVincularPessoaRole] = useState("");
  const [vincularPessoaAdmission, setVincularPessoaAdmission] = useState("");
  const [vincularPessoaFilter, setVincularPessoaFilter] = useState("");

  // Pessoas do restaurante atual que AINDA não estão marcadas como equipe
  const pessoasNaoEquipe = (pessoas || []).filter(p =>
    (p.restaurantIds || []).includes(rid) && !p.isTeam?.[rid]
  );

  function vincularPessoaComoEmpregado() {
    if (!vincularPessoaSelected) return;
    const p = vincularPessoaSelected;
    const admission = vincularPessoaAdmission || today();
    const updatedPessoa = {
      ...p,
      isTeam: { ...(p.isTeam || {}), [rid]: true },
      teamData: {
        ...(p.teamData || {}),
        [rid]: {
          ...(p.teamData?.[rid] || {}),
          roleId: vincularPessoaRole || null,
          admission,
        },
      },
    };
    // Cria/atualiza o employee record correspondente — fonte única
    let emp = (employees || []).find(e =>
      e.restaurantId === rid && (e.linkedPessoaId === p.id || e.id === p.linkedEmployeeId)
    );
    let nextEmployees;
    let employeeId;
    if (emp) {
      const updated = {
        ...emp,
        name: p.name,
        cpf: p.cpf || emp.cpf,
        roleId: vincularPessoaRole || emp.roleId || null,
        admission,
        phone: p.whatsapp || emp.phone || null,
        email: p.email || emp.email || null,
        emergencyName: p.emergencyName || emp.emergencyName || null,
        emergencyPhone: p.emergencyPhone || emp.emergencyPhone || null,
        inactive: false,
        inactiveFrom: null,
        linkedPessoaId: p.id,
      };
      nextEmployees = (employees || []).map(e => e.id === emp.id ? updated : e);
      employeeId = emp.id;
    } else {
      const code = restCode_ || "XXX";
      const seq = nextEmpSeq(employees || [], code);
      const empCode = makeEmpCode(code, seq);
      employeeId = `emp_${Date.now().toString(36)}${Math.random().toString(36).slice(2,5)}`;
      const newEmp = {
        id: employeeId,
        restaurantId: rid,
        empCode,
        name: p.name,
        cpf: p.cpf || "",
        pin: p.pin || (p.cpf ? p.cpf.slice(0,4).padEnd(4,"0") : "0000"),
        mustChangePin: !!p.mustChangePin,
        roleId: vincularPessoaRole || null,
        admission,
        phone: p.whatsapp || null,
        email: p.email || null,
        emergencyName: p.emergencyName || null,
        emergencyPhone: p.emergencyPhone || null,
        linkedPessoaId: p.id,
        createdAt: new Date().toISOString(),
        createdViaPessoa: true,
      };
      nextEmployees = [...(employees || []), newEmp];
    }
    updatedPessoa.linkedEmployeeId = employeeId;
    onUpdate("employees", nextEmployees);
    onUpdate("pessoas", (pessoas || []).map(x => x.id === p.id ? updatedPessoa : x));
    onUpdate("_toast", `✅ ${p.name} vinculada como empregado`);
    setVincularPessoaOpen(false);
    setVincularPessoaSelected(null);
    setVincularPessoaRole("");
    setVincularPessoaAdmission("");
    setVincularPessoaFilter("");
  }

  // (handleAiEmpregados + confirmAiEmpChanges removidos — adição em lote agora vive em Pessoas via IA)

  const sorted = [...restEmps].sort((a,b) => {
    const rA = restRoles.find(r=>r.id===a.roleId);
    const rB = restRoles.find(r=>r.id===b.roleId);
    return (rA?.area??"z").localeCompare(rB?.area??"z") || a.name.localeCompare(b.name);
  });
  const activeEmps   = sorted.filter(e => !e.inactive || (e.inactiveFrom && e.inactiveFrom > today()));
  const inactiveEmps = sorted.filter(e => e.inactive && e.inactiveFrom && e.inactiveFrom <= today());
  const list = showInactive ? inactiveEmps : activeEmps;

  function getRow(emp) {
    return { name:emp.name||"", cpf:emp.cpf||"", admission:emp.admission||"", roleId:emp.roleId||"", inactiveFrom:emp.inactiveFrom||"", phone:emp.phone||"", email:emp.email||"", emergencyName:emp.emergencyName||"", emergencyPhone:emp.emergencyPhone||"", codigoContabil:emp.codigoContabil||"", ...(editRows[emp.id]??{}) };
  }

  function setField(id, field, val) {
    setEditRows(prev => ({ ...prev, [id]: { ...(prev[id]??{}), [field]: val } }));
  }

  function saveEmp(emp) {
    setEditRows(prev => {
      const row = { name:emp.name||"", cpf:emp.cpf||"", admission:emp.admission||"", roleId:emp.roleId||"", inactiveFrom:emp.inactiveFrom||"", phone:emp.phone||"", email:emp.email||"", emergencyName:emp.emergencyName||"", emergencyPhone:emp.emergencyPhone||"", codigoContabil:emp.codigoContabil||"", ...(prev[emp.id]??{}) };
      if (!row.name.trim()) return prev;
      onUpdate("employees", employees.map(x => x.id===emp.id ? {...emp, name:row.name.trim(), cpf:row.cpf, admission:row.admission, roleId:row.roleId, inactiveFrom:row.inactiveFrom, phone:row.phone, email:row.email, emergencyName:row.emergencyName, emergencyPhone:row.emergencyPhone, codigoContabil:(row.codigoContabil||"").trim()} : x));
      setSaved(p=>({...p,[emp.id]:true}));
      setTimeout(()=>setSaved(p=>({...p,[emp.id]:false})),1500);
      return prev;
    });
  }

  // (saveNew removido — empregados agora são criados em Pessoas e vinculados aqui via "🔗 Vincular pessoa")

  function toggleInactive(emp) {
    const row = getRow(emp);
    onUpdate("employees", employees.map(x => x.id===emp.id ? {...emp, inactive:!emp.inactive, inactiveFrom:row.inactiveFrom||today()} : x));
  }

  function dismissEmp(emp) {
    setDismissForm({ emp, lastWorkedDay: today() });
  }
  function confirmDismiss() {
    const { emp, lastWorkedDay } = dismissForm || {};
    if (!emp || !lastWorkedDay) return;
    const demitidoEm = addDaysISO(lastWorkedDay, 1);
    const dataStr = new Date(lastWorkedDay+"T12:00:00").toLocaleDateString("pt-BR");
    onUpdate("employees", employees.map(x => x.id===emp.id ? {...x, demitidoEm, demitidoPor: isOwner ? "Master" : "Gestor Adm.", inactive: true, inactiveFrom: demitidoEm} : x));
    onUpdate("_toast", `📋 ${emp.name} demitido — último dia ${dataStr}`);
    setDismissForm(null);
  }

  async function undoDismiss(emp) {
    if (!await appConfirm(`Reverter demissão de "${emp.name}"?`)) return;
    onUpdate("employees", employees.map(x => {
      if (x.id !== emp.id) return x;
      const copy = {...x};
      delete copy.demitidoEm;
      delete copy.demitidoPor;
      return copy;
    }));
    onUpdate("_toast", `↩️ Demissão de ${emp.name} revertida`);
  }

  function promoteEmp(emp) {
    const available = restRoles.filter(r => !r.inactive && r.id !== emp.roleId);
    if (!available.length) { onUpdate("_toast", "Não há outros cargos ativos disponíveis."); return; }
    setPromoteForm({ emp, newRoleId: "", lastDayOldRole: today(), reason: "" });
  }
  function confirmPromote() {
    const { emp, newRoleId, lastDayOldRole, reason } = promoteForm || {};
    if (!emp || !newRoleId || !lastDayOldRole) return;
    const newRole = restRoles.find(r => r.id === newRoleId);
    if (!newRole) return;
    const oldRole = restRoles.find(r => r.id === emp.roleId);
    const effectiveDate = addDaysISO(lastDayOldRole, 1);
    const changedBy = isOwner ? "Master" : "Gestor Adm.";
    const dataStr = new Date(lastDayOldRole+"T12:00:00").toLocaleDateString("pt-BR");
    if (effectiveDate <= today()) {
      const historyEntry = { fromRoleId: emp.roleId, toRoleId: newRole.id, date: effectiveDate, reason, changedBy };
      const history = [...(emp.roleHistory ?? []), historyEntry];
      onUpdate("employees", employees.map(e => e.id === emp.id ? { ...e, roleId: newRole.id, roleHistory: history } : e));
      onUpdate("_toast", `⬆️ ${emp.name}: ${oldRole?.name ?? "—"} → ${newRole.name}`);
    } else {
      const pending = { newRoleId: newRole.id, effectiveDate, reason, changedBy };
      onUpdate("employees", employees.map(e => e.id === emp.id ? { ...e, pendingRoleChange: pending } : e));
      onUpdate("_toast", `📅 Promoção de ${emp.name} agendada — último dia como ${oldRole?.name ?? "cargo atual"} ${dataStr}`);
    }
    setPromoteForm(null);
  }

  async function deleteEmp(emp) {
    if (!await appConfirm(`Excluir permanentemente "${emp.name}"? Esta ação não pode ser desfeita.`)) return;
    onUpdate("employees", employees.filter(x => x.id !== emp.id));
  }

  // v8.10.4: resetPin movido para PessoasAdmin. Função aqui foi removida.

  const detailEmpObj = detailEmp ? restEmps.find(e => e.id === detailEmp) : null;
  const detailRole = detailEmpObj ? restRoles.find(r => r.id === detailEmpObj.roleId) : null;

  // ── Status badge helper ──
  const statusBadge = (emp) => {
    const isDem = emp.demitidoEm && emp.demitidoEm <= today();
    const isInact = emp.inactive && emp.inactiveFrom && emp.inactiveFrom <= today();
    const hasPending = !!emp.pendingRoleChange;
    if (isDem) return { label:"Demitido", bg:"#e74c3c22", color:"var(--red)" };
    if (isInact) return { label:"Inativo", bg:"#8b5cf622", color:"#8b5cf6" };
    if (hasPending) return { label:"Promoção agendada", bg:"#3b82f622", color:"#3b82f6" };
    return { label:"Ativo", bg:"#10b98122", color:"var(--green)" };
  };

  // ── Detail tab pill style ──
  const dtPill = (tab, active) => ({
    padding:"7px 16px", borderRadius:10, border:`1px solid ${active?"var(--accent)":"var(--border)"}`,
    background:active?"var(--accent)11":"transparent", color:active?"var(--accent)":"var(--text3)",
    cursor:"pointer", fontFamily:"'DM Mono',monospace", fontSize:12, fontWeight:active?700:400
  });

  return (
    <div style={{fontFamily:"'DM Mono',monospace"}}>

      {/* ═══════════════════════ DETAIL VIEW ═══════════════════════ */}
      {detailEmp && detailEmpObj && (() => {
        const emp = detailEmpObj;
        const role = detailRole;
        const empIncidents = (incidents??[]).filter(i => i.restaurantId === rid && (i.employeeIds??[]).includes(emp.id) && !i.deletedAt);
        const empFeedbacks = (feedbacks??[]).filter(f => f.restaurantId === rid && f.employeeId === emp.id && !f.deletedAt);
        const negCount = empIncidents.filter(i => { const t = INCIDENT_TYPES.find(x=>x.id===i.type); return t?.negative; }).length;
        const posCount = empIncidents.filter(i => { const t = INCIDENT_TYPES.find(x=>x.id===i.type); return !t?.negative; }).length;
        const avgStars = empFeedbacks.length > 0 ? (empFeedbacks.reduce((a,f)=>a+(f.stars??0),0)/empFeedbacks.length).toFixed(1) : "—";
        const badge = statusBadge(emp);
        const isDemitido = emp.demitidoEm && emp.demitidoEm <= today();
        const isInactive = emp.inactive && emp.inactiveFrom && emp.inactiveFrom <= today();
        const row = getRow(emp);
        return (
          <div>
            {/* Back */}
            <button onClick={()=>{setDetailEmp(null);setDetailTab("cadastro");}} style={{background:"none",border:"none",color:"var(--accent)",cursor:"pointer",fontSize:13,fontFamily:"'DM Mono',monospace",padding:"4px 0",marginBottom:12,display:"flex",alignItems:"center",gap:4}}>
              ← Voltar para equipe
            </button>

            {/* Nome da pessoa */}
            <div style={{fontSize:15,fontWeight:700,color:"var(--text)",marginBottom:8}}>{emp.name}</div>
            {/* Tab pills */}
            <div style={{display:"flex",gap:6,marginBottom:16,flexWrap:"wrap"}}>
              <button onClick={()=>setDetailTab("cadastro")} style={dtPill("cadastro",detailTab==="cadastro")}>Cadastro</button>
              <button onClick={()=>setDetailTab("horario")} style={dtPill("horario",detailTab==="horario")}>🕐 Horário</button>
              <button onClick={()=>setDetailTab("acoes")} style={dtPill("acoes",detailTab==="acoes")}>Ações</button>
              <button onClick={()=>setDetailTab("trilha")} style={dtPill("trilha",detailTab==="trilha")}>Trilha</button>
            </div>

            {/* ── TAB: Cadastro ── */}
            {detailTab === "cadastro" && (
              <div style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:12,padding:16}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                  <div>
                    <label style={{fontSize:10,color:"var(--text3)",fontWeight:700,display:"block",marginBottom:4}}>Nome completo</label>
                    <input value={row.name} onChange={ev=>setField(emp.id,"name",ev.target.value)} style={{...S.input,fontSize:13}}/>
                  </div>
                  <div>
                    <label style={{fontSize:10,color:"var(--text3)",fontWeight:700,display:"block",marginBottom:4}}>CPF</label>
                    {privacyMask
                      ? <div style={{...S.input,fontSize:13,display:"flex",alignItems:"center",color:"var(--text3)"}}>•••.•••.•••-••</div>
                      : <input value={row.cpf||""} onChange={ev=>setField(emp.id,"cpf",maskCpf(ev.target.value))} placeholder="000.000.000-00" style={{...S.input,fontSize:13}} inputMode="numeric"/>
                    }
                  </div>
                  <div>
                    <label style={{fontSize:10,color:"var(--text3)",fontWeight:700,display:"block",marginBottom:4}}>Data de admissão</label>
                    <input type="date" value={row.admission||""} onChange={ev=>setField(emp.id,"admission",ev.target.value)} style={{...S.input,fontSize:13}}/>
                  </div>
                  <div>
                    <label style={{fontSize:10,color:"var(--text3)",fontWeight:700,display:"block",marginBottom:4}}>Cargo</label>
                    <select value={row.roleId||""} onChange={ev=>setField(emp.id,"roleId",ev.target.value)} style={{...S.input,fontSize:13,cursor:"pointer"}}>
                      <option value="">Selecionar…</option>
                      {AREAS.map(a=>(
                        <optgroup key={a} label={a}>
                          {restRoles.filter(r=>r.area===a&&!r.inactive).map(r=><option key={r.id} value={r.id}>{r.name} ({r.points}pt)</option>)}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{fontSize:10,color:"var(--text3)",fontWeight:700,display:"block",marginBottom:4}}>Código</label>
                    <div style={{...S.input,fontSize:13,background:"var(--bg3)",color:"var(--accent)",fontWeight:700}}>{emp.empCode ?? "—"}</div>
                  </div>
                  <div>
                    <label style={{fontSize:10,color:"var(--text3)",fontWeight:700,display:"block",marginBottom:4}} title="Usado na exportação Excel da contabilidade. Pode ficar vazio.">Código contábil</label>
                    <input value={row.codigoContabil||""} onChange={ev=>setField(emp.id,"codigoContabil",ev.target.value.replace(/\D/g,""))} placeholder="ex: 000045" style={{...S.input,fontSize:13,fontFamily:"'DM Mono',monospace"}} inputMode="numeric"/>
                  </div>
                  <div>
                    <label style={{fontSize:10,color:"var(--text3)",fontWeight:700,display:"block",marginBottom:4}}>Telefone</label>
                    <input value={row.phone||""} onChange={ev=>setField(emp.id,"phone",ev.target.value)} placeholder="(00) 00000-0000" style={{...S.input,fontSize:13}} inputMode="tel"/>
                  </div>
                  <div>
                    <label style={{fontSize:10,color:"var(--text3)",fontWeight:700,display:"block",marginBottom:4}}>E-mail</label>
                    <input value={row.email||""} onChange={ev=>setField(emp.id,"email",ev.target.value)} placeholder="email@exemplo.com" style={{...S.input,fontSize:13}} type="email"/>
                  </div>
                  <div>
                    <label style={{fontSize:10,color:"var(--text3)",fontWeight:700,display:"block",marginBottom:4}}>Nome do contato de emergência</label>
                    <input value={row.emergencyName||""} onChange={ev=>setField(emp.id,"emergencyName",ev.target.value)} placeholder="Nome completo" style={{...S.input,fontSize:13}}/>
                  </div>
                  <div>
                    <label style={{fontSize:10,color:"var(--text3)",fontWeight:700,display:"block",marginBottom:4}}>Telefone de emergência</label>
                    <input value={row.emergencyPhone||""} onChange={ev=>setField(emp.id,"emergencyPhone",ev.target.value)} placeholder="(00) 00000-0000" style={{...S.input,fontSize:13}} inputMode="tel"/>
                  </div>
                  <div>
                    <label style={{fontSize:10,color:"var(--text3)",fontWeight:700,display:"block",marginBottom:4}}>Flags</label>
                    <div style={{display:"flex",gap:8}}>
                      <button onClick={()=>onUpdate("employees",employees.map(x=>x.id===emp.id?{...x,isProducao:!x.isProducao}:x))} style={{padding:"6px 12px",borderRadius:8,border:`1px solid ${emp.isProducao?"#ec4899":"var(--border)"}`,background:emp.isProducao?"#ec489922":"transparent",color:emp.isProducao?"#ec4899":"var(--text3)",cursor:"pointer",fontSize:11,fontFamily:"'DM Mono',monospace"}}>
                        {emp.isProducao ? "🏭 Produção ✓" : "🏭 Produção"}
                      </button>
                      <button onClick={()=>onUpdate("employees",employees.map(x=>x.id===emp.id?{...x,isFreela:!x.isFreela}:x))} style={{padding:"6px 12px",borderRadius:8,border:`1px solid ${emp.isFreela?"#06b6d4":"var(--border)"}`,background:emp.isFreela?"#06b6d422":"transparent",color:emp.isFreela?"#06b6d4":"var(--text3)",cursor:"pointer",fontSize:11,fontFamily:"'DM Mono',monospace"}}>
                        {emp.isFreela ? "🎯 Freela ✓" : "🎯 Freela"}
                      </button>
                    </div>
                  </div>
                </div>
                <div style={{marginTop:14,display:"flex",gap:8}}>
                  <button onClick={()=>saveEmp(emp)} style={{...S.btnPrimary,fontSize:13}}>
                    {saved[emp.id] ? "✓ Salvo" : "Salvar alterações"}
                  </button>
                </div>
              </div>
            )}

            {/* ── TAB: Ações ── */}
            {detailTab === "horario" && (
              <div style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:12,padding:mobileOnly?12:16}}>
                <WorkScheduleManagerTab
                  restaurantId={rid}
                  employees={employees}
                  roles={roles}
                  workSchedules={workSchedules}
                  notifications={notifications}
                  managers={managers}
                  currentManagerName={currentUser?.name ?? (isOwner?"Master":"Gestor Adm.")}
                  onUpdate={onUpdate}
                  communications={communications}
                  isOwner={isOwner}
                  mobileOnly={mobileOnly}
                  forcedEmpId={emp.id}
                />
              </div>
            )}

            {detailTab === "acoes" && (
              <div>
                {/* Resumo da pessoa */}
                <div style={{background:"var(--bg2)",border:"1px solid var(--border)",borderRadius:12,padding:14,marginBottom:12,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
                  <div>
                    <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                      {(() => {
                        const scheds = workSchedules?.[rid]?.[emp.id] || [];
                        const validos = scheds.filter(s =>
                          s && s.days && Object.values(s.days).some(d => d && d.in && d.out)
                        );
                        if (validos.length === 0) return null;
                        return (
                          <span title={`${validos.length} horário${validos.length>1?"s":""} cadastrado${validos.length>1?"s":""}`}
                            style={{fontSize:13,lineHeight:1,color:"#0284c7"}}>⏰</span>
                        );
                      })()}
                      <span style={{fontWeight:700,color:"var(--text)",fontSize:15}}>{emp.name}</span>
                      <span style={{fontSize:10,padding:"2px 8px",borderRadius:6,background:badge.bg,color:badge.color,fontWeight:700}}>{badge.label}</span>
                      {emp.isProducao && <span style={{fontSize:10,padding:"2px 8px",borderRadius:6,background:"#ec489922",color:"#ec4899",fontWeight:700}}>Produção</span>}
                      {emp.isFreela && <span style={{fontSize:10,padding:"2px 8px",borderRadius:6,background:"#06b6d422",color:"#06b6d4",fontWeight:700}}>Freela</span>}
                    </div>
                    <div style={{fontSize:12,color:"var(--text3)",marginTop:3}}>{role?.name ?? "Sem cargo"} · {role?.area ?? "Sem área"} · {emp.empCode ?? "—"}</div>
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    <div style={{textAlign:"center",padding:"4px 10px",background:"var(--bg3)",borderRadius:8}}>
                      <div style={{fontSize:14,fontWeight:700,color:"var(--red)"}}>{negCount}</div>
                      <div style={{fontSize:8,color:"var(--text3)"}}>Ocorrências</div>
                    </div>
                    <div style={{textAlign:"center",padding:"4px 10px",background:"var(--bg3)",borderRadius:8}}>
                      <div style={{fontSize:14,fontWeight:700,color:"var(--green)"}}>{posCount}</div>
                      <div style={{fontSize:8,color:"var(--text3)"}}>Elogios</div>
                    </div>
                    <div style={{textAlign:"center",padding:"4px 10px",background:"var(--bg3)",borderRadius:8}}>
                      <div style={{fontSize:14,fontWeight:700,color:"#f59e0b"}}>{avgStars}</div>
                      <div style={{fontSize:8,color:"var(--text3)"}}>Avaliação</div>
                    </div>
                  </div>
                </div>
                {/* Ações */}
                <div style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:12,padding:16}}>
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {/* Promoção */}
                  {!isDemitido && !isInactive && (
                    <button onClick={()=>promoteEmp(emp)} style={{display:"flex",alignItems:"center",gap:8,padding:"12px 16px",borderRadius:10,border:"1px solid #3b82f633",background:"#3b82f609",color:"var(--text)",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:13,textAlign:"left"}}>
                      <span style={{fontSize:18}}>⬆️</span>
                      <div><div style={{fontWeight:700,color:"#3b82f6"}}>Promover / Mudar cargo</div><div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>Alterar cargo com data efetiva imediata ou agendada</div></div>
                    </button>
                  )}
                  {emp.pendingRoleChange && (
                    <div style={{padding:"10px 16px",borderRadius:10,background:"#3b82f611",border:"1px solid #3b82f633",fontSize:12,color:"#3b82f6"}}>
                      📅 Promoção agendada → <strong>{restRoles.find(r=>r.id===emp.pendingRoleChange.newRoleId)?.name}</strong> em {new Date(emp.pendingRoleChange.effectiveDate+"T12:00:00").toLocaleDateString("pt-BR")}
                      {emp.pendingRoleChange.reason && <span style={{color:"var(--text3)",marginLeft:8}}>({emp.pendingRoleChange.reason})</span>}
                    </div>
                  )}
                  {/* Demitir */}
                  {!isDemitido && !isInactive && (
                    <button onClick={()=>dismissEmp(emp)} style={{display:"flex",alignItems:"center",gap:8,padding:"12px 16px",borderRadius:10,border:"1px solid #e74c3c33",background:"#e74c3c09",color:"var(--text)",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:13,textAlign:"left"}}>
                      <span style={{fontSize:18}}>📋</span>
                      <div><div style={{fontWeight:700,color:"var(--red)"}}>Demitir</div><div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>Registrar desligamento com data</div></div>
                    </button>
                  )}
                  {/* Reverter demissão */}
                  {isDemitido && (
                    <>
                      <div style={{padding:"10px 16px",borderRadius:10,background:"#e74c3c11",border:"1px solid #e74c3c33",fontSize:12,color:"var(--red)"}}>
                        Último dia trabalhado: {new Date(addDaysISO(emp.demitidoEm,-1)+"T12:00:00").toLocaleDateString("pt-BR")} · registrado por {emp.demitidoPor ?? "—"}
                      </div>
                      <button onClick={()=>undoDismiss(emp)} style={{display:"flex",alignItems:"center",gap:8,padding:"12px 16px",borderRadius:10,border:"1px solid #10b98133",background:"#10b98109",color:"var(--text)",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:13,textAlign:"left"}}>
                        <span style={{fontSize:18}}>↩️</span>
                        <div><div style={{fontWeight:700,color:"var(--green)"}}>Reverter demissão</div><div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>Retornar empregado ao status anterior</div></div>
                      </button>
                      {onGenerateDismissalReport && (
                        <button onClick={()=>{
                          // Pre-dismissal checklist (#71)
                          setDismissalCheckEmp(emp);
                          setShowDismissalChecklist(true);
                        }} style={{display:"flex",alignItems:"center",gap:8,padding:"12px 16px",borderRadius:10,border:"1px solid #3b82f633",background:"#3b82f609",color:"var(--text)",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:13,textAlign:"left"}}>
                          <span style={{fontSize:18}}>📄</span>
                          <div><div style={{fontWeight:700,color:"#3b82f6"}}>Relatório de desligamento</div><div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>Exportar PDF com dados e gorjetas</div></div>
                        </button>
                      )}
                      {/* VT balance card for dismissed employee (#70) */}
                      {(() => {
                        // Usa o mês/dia do ÚLTIMO DIA TRABALHADO (= demitidoEm - 1) como referência.
                        const lastWorked2 = emp.demitidoEm ? addDaysISO(emp.demitidoEm, -1) : "";
                        const demMk2 = lastWorked2.slice(0,7);
                        if (!demMk2) return null;
                        const [demY2, demM2] = demMk2.split("-").map(Number);
                        const demDay2 = parseInt(lastWorked2.slice(8,10) ?? "0");
                        const daysInDemMonth2 = new Date(demY2, demM2, 0).getDate();
                        const vtPay2 = vtPayments?.[rid]?.[demMk2];
                        const vtSnap2 = vtPay2?.snapshot?.find(s => s.empId === emp.id);
                        const vtCfg2 = vtConfig?.[rid]?.[emp.id];
                        const rate2 = vtSnap2?.dailyRate ?? vtCfg2?.dailyRate ?? 0;
                        if (rate2 === 0) return null;
                        const schedMap2 = schedules?.[rid]?.[demMk2]?.[emp.id] ?? {};
                        let planned2 = vtSnap2?.plannedDays ?? 0;
                        let worked2 = 0;
                        if (!vtSnap2) {
                          for (let dd = 1; dd <= daysInDemMonth2; dd++) {
                            const ds = `${demY2}-${String(demM2).padStart(2,"0")}-${String(dd).padStart(2,"0")}`;
                            if (!schedMap2[ds] || schedMap2[ds] === "comptrab") planned2++;
                          }
                        }
                        // Conta dias trabalhados ATÉ E INCLUINDO o último dia trabalhado.
                        for (let dd = 1; dd <= demDay2 && dd <= daysInDemMonth2; dd++) {
                          const ds = `${demY2}-${String(demM2).padStart(2,"0")}-${String(dd).padStart(2,"0")}`;
                          if (!schedMap2[ds] || schedMap2[ds] === "comptrab") worked2++;
                        }
                        const vtPaid2 = round2(rate2 * planned2);
                        const vtOwed2 = round2(rate2 * worked2);
                        const toReturn2 = Math.max(0, round2(vtPaid2 - vtOwed2));
                        // Previous month pending adjust
                        const prevDemDate2 = new Date(demY2, demM2 - 2, 1);
                        const prevDemMk2 = `${prevDemDate2.getFullYear()}-${String(prevDemDate2.getMonth()+1).padStart(2,"0")}`;
                        const prevPay2 = vtPayments?.[rid]?.[prevDemMk2];
                        let pendAdj2 = 0;
                        if (prevPay2?.snapshot) {
                          const ps2 = prevPay2.snapshot.find(s => s.empId === emp.id);
                          if (ps2) {
                            const pLastDay = new Date(prevDemDate2.getFullYear(), prevDemDate2.getMonth()+1, 0).getDate();
                            const pMap = schedules?.[rid]?.[prevDemMk2]?.[emp.id] ?? {};
                            let pActual = 0;
                            for (let dd = 1; dd <= pLastDay; dd++) {
                              const ds = `${prevDemDate2.getFullYear()}-${String(prevDemDate2.getMonth()+1).padStart(2,"0")}-${String(dd).padStart(2,"0")}`;
                              if (!pMap[ds] || pMap[ds] === "comptrab") pActual++;
                            }
                            pendAdj2 = round2((pActual - ps2.plannedDays) * ps2.dailyRate);
                          }
                        }
                        return (
                          <div style={{padding:"12px 16px",borderRadius:10,background:"#10b98109",border:"1px solid #10b98133"}}>
                            <div style={{fontWeight:700,color:"var(--green)",fontSize:12,marginBottom:8}}>{"🚌"} Saldo VT no desligamento</div>
                            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4,fontSize:11,color:"var(--text2)"}}>
                              <span>VT pago (mês):</span><span style={{textAlign:"right",fontFamily:"'DM Mono',monospace"}}>R$ {vtPaid2.toFixed(2).replace(".",",")}</span>
                              <span>Dias trabalhados:</span><span style={{textAlign:"right",fontFamily:"'DM Mono',monospace"}}>{worked2}d</span>
                              <span>A restituir:</span><span style={{textAlign:"right",fontFamily:"'DM Mono',monospace",color:"var(--red)",fontWeight:700}}>R$ {toReturn2.toFixed(2).replace(".",",")}</span>
                              {pendAdj2 !== 0 && <><span>Ajuste pendente ant.:</span><span style={{textAlign:"right",fontFamily:"'DM Mono',monospace",color:pendAdj2>0?"var(--green)":"var(--red)"}}>R$ {pendAdj2.toFixed(2).replace(".",",")}</span></>}
                            </div>
                          </div>
                        );
                      })()}
                    </>
                  )}
                  {/* Inativar/Reativar */}
                  {!isDemitido && (
                    <button onClick={()=>toggleInactive(emp)} style={{display:"flex",alignItems:"center",gap:8,padding:"12px 16px",borderRadius:10,border:`1px solid ${isInactive?"#10b98133":"#f59e0b33"}`,background:isInactive?"#10b98109":"#f59e0b09",color:"var(--text)",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:13,textAlign:"left"}}>
                      <span style={{fontSize:18}}>{isInactive ? "↑" : "↓"}</span>
                      <div><div style={{fontWeight:700,color:isInactive?"var(--green)":"#f59e0b"}}>{isInactive ? "Reativar empregado" : "Inativar empregado"}</div><div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>{isInactive ? "Mover de volta para ativos" : "Mover para inativos temporariamente"}</div></div>
                    </button>
                  )}
                  {/* v8.10.4: Reset PIN saiu da Equipe — agora é exclusivo da aba Pessoas. */}
                  {/* Excluir */}
                  {isOwner && isInactive && (
                    <button onClick={()=>deleteEmp(emp)} style={{display:"flex",alignItems:"center",gap:8,padding:"12px 16px",borderRadius:10,border:"1px solid #e74c3c33",background:"#e74c3c09",color:"var(--text)",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:13,textAlign:"left"}}>
                      <span style={{fontSize:18}}>🗑️</span>
                      <div><div style={{fontWeight:700,color:"var(--red)"}}>Excluir permanentemente</div><div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>Remove o empregado do sistema (irreversível)</div></div>
                    </button>
                  )}
                </div>
              </div>
              </div>
            )}

            {/* ── TAB: Trilha ── */}
            {detailTab === "trilha" && (() => {
              // ── Compute shared data for trilha sections ──
              const admDate = emp.admission ? new Date(emp.admission+"T12:00:00") : null;
              const daysInCompany = admDate ? Math.floor((new Date() - admDate) / 86400000) : 0;
              const areaColor = AREA_COLORS[role?.area] ?? "var(--ac)";
              const myFeedbacksAll = (feedbacks??[]).filter(f => f.restaurantId === rid && f.employeeId === emp.id);
              const myFeedbacks = myFeedbacksAll.filter(f => !f.deletedAt).sort((a,b)=>(b.createdAt??"").localeCompare(a.createdAt??""));
              const latestFb = myFeedbacks[0];
              const lastMeetingDate = latestFb?.meetingDate ? new Date(latestFb.meetingDate+"T12:00:00").toLocaleDateString("pt-BR") : (latestFb?.createdAt ? new Date(latestFb.createdAt).toLocaleDateString("pt-BR") : null);
              const lastRating = latestFb?.rating ? RATING_LABELS[latestFb.rating - 1] : null;
              const lastRatingColor = latestFb?.rating ? RATING_COLORS[latestFb.rating - 1] : "var(--text3)";
              // Faltas 6 meses
              const now2 = new Date();
              const months6 = [];
              for (let i = 0; i < 6; i++) {
                const d = new Date(now2.getFullYear(), now2.getMonth() - i, 1);
                const mKey = monthKey(d.getFullYear(), d.getMonth());
                const lab = d.toLocaleDateString("pt-BR", { month:"short" }).replace(".","");
                const empSched = schedules?.[rid]?.[mKey]?.[emp.id] ?? {};
                const empDel = delays?.[rid]?.[mKey]?.[emp.id] ?? {};
                const faltasI = Object.values(empSched).filter(s => s === DAY_FAULT_U).length;
                const faltasJ = Object.values(empSched).filter(s => s === DAY_FAULT_J).length;
                const delayDays = Object.values(empDel).filter(v => v > 0).length;
                const delayMin = Object.values(empDel).filter(v => v > 0).reduce((s,v) => s+v, 0);
                const hasDays = Object.keys(empSched).length > 0;
                months6.push({ mKey, label:lab, faltasI, faltasJ, delayDays, delayMin, hasDays });
              }
              months6.reverse();
              const totFI = months6.reduce((s,m) => s+m.faltasI, 0);
              const totFJ = months6.reduce((s,m) => s+m.faltasJ, 0);
              const totDelDays = months6.reduce((s,m) => s+m.delayDays, 0);
              const totDelMin = months6.reduce((s,m) => s+m.delayMin, 0);
              // Badges
              const myGoalsAll = (employeeGoals ?? {})[emp.id] ?? [];
              const completedGoals = myGoalsAll.filter(g => g.status === "completed");
              const prevMk = monthKey(new Date(now2.getFullYear(), now2.getMonth()-1, 1).getFullYear(), new Date(now2.getFullYear(), now2.getMonth()-1, 1).getMonth());
              const prevMyDays = schedules?.[rid]?.[prevMk]?.[emp.id] ?? {};
              const prevFaults = Object.values(prevMyDays).filter(s => s === DAY_FAULT_U).length;
              const hasPrevData = Object.keys(prevMyDays).length > 0;
              const badgesNew = [];
              if (daysInCompany >= 365) badgesNew.push({ icon:"🏆", label:"1 Ano", desc:"Completou 1 ano na empresa" });
              else if (daysInCompany >= 180) badgesNew.push({ icon:"⭐", label:"6 Meses", desc:"6 meses de dedicação" });
              else if (daysInCompany >= 90) badgesNew.push({ icon:"🌟", label:"3 Meses", desc:"Primeiros 90 dias concluídos" });
              if ((emp?.roleHistory ?? []).length > 0) badgesNew.push({ icon:"⬆️", label:"Promovido", desc:"Já recebeu uma promoção" });
              if (latestFb?.rating === 5) badgesNew.push({ icon:"💎", label:"Excelência", desc:"Avaliação Excepcional" });
              if (completedGoals.length > 0) badgesNew.push({ icon:"🎯", label:"Objetivo Concluído", desc:`${completedGoals.length} objetivo${completedGoals.length>1?"s":""} finalizado${completedGoals.length>1?"s":""}` });
              if (hasPrevData && prevFaults === 0) badgesNew.push({ icon:"🔥", label:"Sem Faltas", desc:"0 faltas injustificadas no mês anterior" });
              const conhecGoals = completedGoals.filter(g => g.type === "conhecimento" && (g.metas??[]).length > 0 && (g.metas??[]).every(m => m.done));
              if (conhecGoals.length > 0) badgesNew.push({ icon:"📚", label:"Estudioso", desc:"Completou objetivo de conhecimento" });
              // Meeting plans
              const empPlans = (meetingPlans??[]).filter(p => p.restaurantId === rid && (p.employeeIds??[]).includes(emp.id)).sort((a,b)=>a.plannedDate.localeCompare(b.plannedDate));
              const upcomingPlans = empPlans.filter(p => p.plannedDate >= today()).slice(0,3);
              // overduePlan / nextPlan removed (unused)
              // Jornada events
              const jornadaEvents = (() => {
                if (!admDate) return [];
                const events = [];
                events.push({ date: admDate, label: "Admissão", icon: "🚀" });
                (emp.roleHistory ?? []).forEach(rh => {
                  if (rh.date) {
                    const newRole = (roles??restRoles).find(r => r.id === rh.newRoleId);
                    events.push({ date: new Date(rh.date + "T12:00:00"), label: `Promovido a ${newRole?.name ?? "novo cargo"}`, icon: "⬆️" });
                  }
                });
                [[90,"🌟","3 Meses"],[180,"⭐","6 Meses"],[365,"🏆","1 Ano"]].forEach(([days,ic,lb]) => {
                  if (daysInCompany >= days) {
                    const dEv = new Date(admDate.getTime() + days * 86400000);
                    events.push({ date: dEv, label: lb, icon: ic });
                  }
                });
                myFeedbacks.forEach(f => {
                  const isAval = f.meetingType === "avaliação" || (!f.meetingType && f.rating > 0);
                  events.push({ date: new Date(f.meetingDate ? f.meetingDate+"T12:00:00" : f.createdAt), label: isAval ? "Conversa de avaliação" : "Conversa de alinhamento", icon: isAval ? "📋" : "💬", type:"feedback", fb: f });
                });
                // Incidents
                const empIncsAll = (incidents??[]).filter(i => i.restaurantId === rid && (i.employeeIds??[]).includes(emp.id) && !i.deletedAt);
                empIncsAll.forEach(inc => {
                  const incType = INCIDENT_TYPES.find(x => x.id === inc.type);
                  const isNeg = incType?.negative;
                  events.push({ date: new Date(inc.date+"T12:00:00"), label: incType?.label ?? inc.type, icon: isNeg ? "🔴" : "🟢", type:"incident", inc, incNeg: isNeg });
                });
                // All meetingPlans (upcoming = future, past = realized or overdue)
                empPlans.forEach(p => {
                  const isAval = p.type === "avaliação";
                  const isFuture = p.plannedDate >= today();
                  const isDone = p.status === "encerrada";
                  const isInProgress = p.status === "em_andamento";
                  if (isFuture && !isDone) {
                    events.push({ date: new Date(p.plannedDate+"T12:00:00"), label: isAval ? "Avaliação prevista" : "Alinhamento previsto", icon: "📅", future: true });
                  } else {
                    events.push({ date: new Date((p.endedAt ?? p.startedAt ?? p.plannedDate+"T12:00:00")), label: isDone ? (isAval ? "Avaliação realizada" : "Alinhamento realizado") : isInProgress ? (isAval ? "Avaliação em andamento" : "Alinhamento em andamento") : (isAval ? "Avaliação (não realizada)" : "Alinhamento (não realizado)"), icon: isDone ? "✅" : isInProgress ? "▶️" : "⏳", type:"meetingPlan", plan: p });
                  }
                });
                // Unified meetingOccurrences
                const empMeetingOccs = (meetingOccurrences??[]).filter(o => o.restaurantId === rid && (o.occEmployeeIds??o.employeeIds??[]).includes(emp.id) && !o.deletedAt);
                const OCC_TYPES_TIMELINE = [["cliente","🧑‍🍳","Cliente"],["estrutura","🏗️","Estrutura"],["operacao","⚙️","Operação"],["pessoal","👤","Pessoal"],["outro","📝","Outro"]];
                empMeetingOccs.forEach(o => {
                  const occT = OCC_TYPES_TIMELINE.find(([t])=>t===o.type);
                  const isResolved = o.status === "resolvida";
                  events.push({ date: new Date(o.createdAt ?? o.date+"T12:00:00"), label: `Ocorrência: ${o.title || occT?.[2] || o.type}`, icon: isResolved ? "✓" : (occT?.[1] ?? "📝"), type:"meetingOcc", occ: o, occResolved: isResolved });
                });
                completedGoals.forEach(g => {
                  events.push({ date: new Date(g.createdAt), label: `Objetivo: ${g.title}`, icon: "🎯" });
                });
                events.sort((a,b) => a.date - b.date);
                return events;
              })();
              const gradients = [
                "linear-gradient(135deg,#fef3c7,#fde68a)",
                "linear-gradient(135deg,#dbeafe,#bfdbfe)",
                "linear-gradient(135deg,#d1fae5,#a7f3d0)",
                "linear-gradient(135deg,#ede9fe,#ddd6fe)",
                "linear-gradient(135deg,#fee2e2,#fecaca)",
                "linear-gradient(135deg,#ffedd5,#fed7aa)",
                "linear-gradient(135deg,#f0fdf4,#dcfce7)",
                "linear-gradient(135deg,#fdf4ff,#f5d0fe)",
              ];
              return (
              <div>
                {/* ── 1. Card do empregado com métricas ── */}
                <div style={{...S.card,marginBottom:16,padding:"20px"}}>
                  <div style={{display:"flex",gap:16,alignItems:"center",flexWrap:"wrap"}}>
                    <div style={{width:56,height:56,borderRadius:28,background:`linear-gradient(135deg,${areaColor}cc,${areaColor})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,fontWeight:700,color:"#fff",flexShrink:0}}>{(emp.name??"?").charAt(0)}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{color:"var(--text)",fontWeight:700,fontSize:18}}>{emp.name}</div>
                      <div style={{color:areaColor,fontSize:13,fontWeight:600}}>{role?.name ?? "—"}</div>
                      <div style={{color:"var(--text3)",fontSize:11}}>{role?.area ?? "—"} · {daysInCompany} dias na empresa{emp.admission ? ` · Admissão ${new Date(emp.admission+"T12:00:00").toLocaleDateString("pt-BR")}` : ""}</div>
                    </div>
                  </div>
                  {/* Mini-métricas */}
                  <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginTop:14}}>
                    <div style={{padding:"8px 6px",borderRadius:10,background:totFI>0?"#ef444409":"#10b98109",textAlign:"center"}}>
                      <div style={{color:totFI>0?"#ef4444":"#10b981",fontWeight:700,fontSize:18}}>{totFI}</div>
                      <div style={{color:"var(--text3)",fontSize:9}}>Faltas 6m</div>
                    </div>
                    <div style={{padding:"8px 6px",borderRadius:10,background:"var(--border)22",textAlign:"center"}}>
                      <div style={{color:"var(--text)",fontWeight:700,fontSize:12,lineHeight:1.3}}>{lastMeetingDate ?? "—"}</div>
                      <div style={{color:"var(--text3)",fontSize:9}}>Última reunião</div>
                    </div>
                    <div style={{padding:"8px 6px",borderRadius:10,background:lastRating?lastRatingColor+"09":"var(--border)22",textAlign:"center"}}>
                      <div style={{color:lastRatingColor,fontWeight:700,fontSize:12,lineHeight:1.3}}>{lastRating ?? "—"}</div>
                      <div style={{color:"var(--text3)",fontSize:9}}>Última nota</div>
                    </div>
                  </div>
                </div>

                {/* ── Action buttons ── */}
                <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
                  <button onClick={async()=>{
                    const dateFrom = window.prompt("Data início (DD/MM/AAAA):", emp.admission ? new Date(emp.admission+"T12:00:00").toLocaleDateString("pt-BR") : "01/01/2026");
                    if (!dateFrom) return;
                    const dateTo = window.prompt("Data fim (DD/MM/AAAA):", new Date().toLocaleDateString("pt-BR"));
                    if (!dateTo) return;
                    const parseDate = (s) => { const p=s.split("/"); return p.length===3?`${p[2]}-${p[1].padStart(2,"0")}-${p[0].padStart(2,"0")}`:null; };
                    const fromISO = parseDate(dateFrom);
                    const toISO = parseDate(dateTo);
                    if (!fromISO || !toISO) { window.alert("Data inválida. Use DD/MM/AAAA."); return; }
                    try {
                      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
                      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js");
                      const { jsPDF } = window.jspdf;
                      const doc = new jsPDF({ orientation:"portrait", unit:"mm", format:"a4" });
                      const W = doc.internal.pageSize.getWidth();
                      let y = 15;
                      doc.setFontSize(16); doc.setTextColor(30,30,30);
                      doc.text("RELATÓRIO DA TRILHA DO EMPREGADO", W/2, y, {align:"center"}); y += 7;
                      doc.setFontSize(10); doc.setTextColor(100,100,100);
                      doc.text(`Período: ${dateFrom} a ${dateTo} — Gerado em ${new Date().toLocaleDateString("pt-BR")}`, W/2, y, {align:"center"}); y += 10;
                      doc.setFontSize(12); doc.setTextColor(30,30,30); doc.text("DADOS DO EMPREGADO", 14, y); y += 2;
                      doc.autoTable({ startY:y, head:[], body:[["Nome",emp.name],["Cargo",role?.name??"—"],["Área",role?.area??"—"],["Código",emp.empCode??"—"],["Admissão",emp.admission?new Date(emp.admission+"T12:00:00").toLocaleDateString("pt-BR"):"—"],["Status",emp.demitidoEm?`Demitido — último dia trabalhado em ${new Date(addDaysISO(emp.demitidoEm,-1)+"T12:00:00").toLocaleDateString("pt-BR")}`:emp.inactive?"Inativo":"Ativo"]], theme:"grid", styles:{fontSize:10,cellPadding:3}, columnStyles:{0:{fontStyle:"bold",cellWidth:45}}, margin:{left:14,right:14} });
                      y = doc.lastAutoTable.finalY + 8;
                      const empIncs = (incidents??[]).filter(i => i.restaurantId===rid && (i.employeeIds??[]).includes(emp.id) && !i.deletedAt && i.date>=fromISO && i.date<=toISO);
                      if (empIncs.length > 0) { if (y > 240) { doc.addPage(); y = 15; } doc.setFontSize(12); doc.text("OCORRÊNCIAS", 14, y); y += 2; doc.autoTable({ startY:y, head:[["Data","Tipo","Gravidade","Descrição"]], body:empIncs.sort((a,b)=>a.date.localeCompare(b.date)).map(inc => { const t = INCIDENT_TYPES.find(x=>x.id===inc.type); const sev = SEVERITY_OPTIONS.find(s=>s.id===inc.severity); return [new Date(inc.date+"T12:00:00").toLocaleDateString("pt-BR"),t?.label??inc.type,sev?.label??inc.severity??"—",(inc.description??"").slice(0,80)]; }), theme:"striped", styles:{fontSize:9,cellPadding:2}, headStyles:{fillColor:[59,130,246]}, columnStyles:{3:{cellWidth:70}}, margin:{left:14,right:14} }); y = doc.lastAutoTable.finalY + 8; }
                      const empFbsPdf = (feedbacks??[]).filter(f => f.restaurantId===rid && f.employeeId===emp.id && !f.deletedAt && ((f.meetingDate??f.createdAt??"").slice(0,10))>=fromISO && ((f.meetingDate??f.createdAt??"").slice(0,10))<=toISO);
                      if (empFbsPdf.length > 0) { if (y > 240) { doc.addPage(); y = 15; } doc.setFontSize(12); doc.text("REUNIÕES", 14, y); y += 2; doc.autoTable({ startY:y, head:[["Data","Tipo","Avaliação","Anotações","Pontos Positivos"]], body:empFbsPdf.sort((a,b)=>(a.meetingDate??a.createdAt??"").localeCompare(b.meetingDate??b.createdAt??"")).map(fb => { const isAv = fb.meetingType==="avaliação"||(!fb.meetingType&&fb.rating>0); return [fb.meetingDate?new Date(fb.meetingDate+"T12:00:00").toLocaleDateString("pt-BR"):(fb.createdAt?new Date(fb.createdAt).toLocaleDateString("pt-BR"):"—"),isAv?"Avaliação":"Alinhamento",fb.rating?(RATING_LABELS[fb.rating-1]??"—"):"—",(fb.notes??"").slice(0,50)||(fb.internalNotes??"").slice(0,50),(fb.strengths??"").slice(0,50)]; }), theme:"striped", styles:{fontSize:9,cellPadding:2}, headStyles:{fillColor:[59,130,246]}, margin:{left:14,right:14} }); y = doc.lastAutoTable.finalY + 8; }
                      const roleHist = (emp.roleHistory??[]).filter(rh=>rh.date>=fromISO && rh.date<=toISO);
                      if (roleHist.length > 0) { if (y > 250) { doc.addPage(); y = 15; } doc.setFontSize(12); doc.text("MUDANÇAS DE CARGO", 14, y); y += 2; doc.autoTable({ startY:y, head:[["Data","De","Para","Motivo"]], body:roleHist.map(rh => [new Date(rh.date+"T12:00:00").toLocaleDateString("pt-BR"),(roles??restRoles).find(r=>r.id===rh.fromRoleId)?.name??"—",(roles??restRoles).find(r=>r.id===rh.toRoleId)?.name??"—",rh.reason||"—"]), theme:"striped", styles:{fontSize:9,cellPadding:2}, headStyles:{fillColor:[139,92,246]}, margin:{left:14,right:14} }); y = doc.lastAutoTable.finalY + 8; }
                      if (y > 250) { doc.addPage(); y = 15; } doc.setFontSize(12); doc.text("RESUMO", 14, y); y += 2;
                      const negIncs = empIncs.filter(i => { const t=INCIDENT_TYPES.find(x=>x.id===i.type); return t?.negative; }); const posIncs = empIncs.filter(i => { const t=INCIDENT_TYPES.find(x=>x.id===i.type); return !t?.negative; });
                      doc.autoTable({ startY:y, head:[], body:[["Ocorrências negativas",String(negIncs.length)],["Ocorrências positivas",String(posIncs.length)],["Reuniões realizadas",String(empFbsPdf.length)],["Mudanças de cargo",String(roleHist.length)],["Avaliação média",empFbsPdf.length>0?(empFbsPdf.reduce((a,f)=>a+(f.rating||0),0)/empFbsPdf.length).toFixed(1):"—"]], theme:"grid", styles:{fontSize:10,cellPadding:3}, columnStyles:{0:{fontStyle:"bold",cellWidth:60}}, margin:{left:14,right:14} });
                      const pages = doc.internal.getNumberOfPages(); for (let i=1;i<=pages;i++) { doc.setPage(i); doc.setFontSize(8); doc.setTextColor(150,150,150); doc.text(`Trilha de ${emp.name} · ${dateFrom} a ${dateTo} · Página ${i}/${pages}`, W/2, doc.internal.pageSize.getHeight()-8, {align:"center"}); }
                      const safeName = emp.name.replace(/[^a-zA-Z0-9]/g,"_").toLowerCase();
                      setPreviewDoc(doc); setPreviewFileName(`trilha_${safeName}_${fromISO}_${toISO}.pdf`);
                      onUpdate("_toast", `📄 Trilha de ${emp.name} exportada!`);
                    } catch(err) { console.error("Erro ao exportar trilha:", err); onUpdate("_toast", "⚠️ Erro ao exportar: " + (err.message || "desconhecido")); }
                  }} style={{padding:"8px 16px",borderRadius:10,border:"1px solid #3b82f644",background:"transparent",color:"#3b82f6",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12}}>
                    📄 PDF
                  </button>
                </div>
                {/* IncidentForm removido — ocorrências cadastradas pela aba Reuniões */}

                {/* Meeting alert removed — info now in Jornada timeline */}

                {/* ── 2. Conquistas / Badges ── */}
                {badgesNew.length > 0 && (
                  <div style={{marginBottom:16}}>
                    <h4 style={{color:"var(--text)",margin:"0 0 10px",fontSize:14}}>🏅 Conquistas</h4>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                      {badgesNew.map((b,i) => (
                        <div key={i} style={{borderRadius:12,padding:"14px 12px",background:gradients[i % gradients.length],display:"flex",alignItems:"center",gap:10,boxShadow:"0 1px 3px rgba(0,0,0,0.06)"}}>
                          <div style={{width:40,height:40,borderRadius:20,background:"rgba(255,255,255,0.7)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>{b.icon}</div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{color:"#1f2937",fontWeight:700,fontSize:12,lineHeight:1.2}}>{b.label}</div>
                            <div style={{color:"#6b7280",fontSize:10,lineHeight:1.3,marginTop:1}}>{b.desc}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── 3. Próximas reuniões planejadas ── */}
                {upcomingPlans.length > 0 && (
                  <div style={{marginBottom:16}}>
                    <h4 style={{color:"var(--text)",margin:"0 0 10px",fontSize:14}}>📅 Próximas reuniões</h4>
                    {upcomingPlans.map(p => {
                      const pDate = new Date(p.plannedDate+"T12:00:00");
                      const todayD = new Date(); todayD.setHours(0,0,0,0);
                      const diff = Math.round((pDate - todayD) / 86400000);
                      const isAval = p.type === "avaliação";
                      const pColor = isAval ? "#8b5cf6" : "#3b82f6";
                      return (
                        <div key={p.id} style={{...S.card,padding:"14px 16px",marginBottom:8,display:"flex",alignItems:"center",gap:10,borderLeft:`4px solid ${pColor}`}}>
                          <span style={{fontSize:20}}>📅</span>
                          <div style={{flex:1}}>
                            <div style={{color:"var(--text)",fontWeight:600,fontSize:13}}>{isAval ? "Conversa de avaliação" : "Conversa de alinhamento"}</div>
                            <div style={{color:"var(--text3)",fontSize:12}}>{diff === 0 ? "Hoje" : diff === 1 ? "Amanhã" : `Em ${diff} dia${diff!==1?"s":""}`} · {pDate.toLocaleDateString("pt-BR")}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* ── 3b. Reuniões — all meetings for this employee ── */}
                {(() => {
                  const allEmpPlans = (meetingPlans??[]).filter(p => p.restaurantId === rid && (p.employeeIds??[]).includes(emp.id)).sort((a,b)=>(b.plannedDate??"").localeCompare(a.plannedDate??""));
                  const empCompletedFbs = (feedbacks??[]).filter(f => f.restaurantId === rid && f.employeeId === emp.id && !f.deletedAt).sort((a,b)=>(b.meetingDate??b.createdAt??"").localeCompare(a.meetingDate??a.createdAt??""));
                  const allMeetingItems = [
                    ...allEmpPlans.map(p => ({ kind:"plan", id:"p-"+p.id, date:p.plannedDate, item:p })),
                    ...empCompletedFbs.map(f => ({ kind:"feedback", id:"f-"+f.id, date:f.meetingDate??(f.createdAt??"").slice(0,10), item:f }))
                  ].sort((a,b)=>(b.date??"").localeCompare(a.date??""));
                  if (allMeetingItems.length === 0) return null;
                  const MAX_VISIBLE = 5;
                  const visibleItems = allMeetingItems.slice(0, MAX_VISIBLE);
                  const hasMore = allMeetingItems.length > MAX_VISIBLE;
                  return (
                    <div style={{marginBottom:16}}>
                      <h4 style={{color:"var(--text)",margin:"0 0 10px",fontSize:14}}>📋 Reuniões</h4>
                      {visibleItems.map(mi => {
                        if (mi.kind === "plan") {
                          const p = mi.item;
                          const isAval = p.type === "avaliação";
                          const pColor = isAval ? "#8b5cf6" : "#3b82f6";
                          const pIcon = isAval ? "📋" : "💬";
                          const statusBadge = p.status === "encerrada"
                            ? {bg:"#10b98118",color:"#10b981",label:"✓ Encerrada"}
                            : p.status === "em_andamento"
                            ? {bg:"#f59e0b18",color:"#f59e0b",label:"Em andamento"}
                            : {bg:"var(--border)33",color:"var(--text3)",label:"Agendada"};
                          const fortes = (p.pontosFortes??[]).length;
                          const melhorar = (p.pontosMelhorar??[]).length;
                          const notas = (p.notasInternas??[]).length;
                          const topics = (p.topics??[]).length;
                          const topicsDone = (p.topics??[]).filter(t=>t.done).length;
                          return (
                            <div key={mi.id} style={{...S.card,padding:"10px 14px",marginBottom:6,borderLeft:`3px solid ${pColor}`}}>
                              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                                <span style={{fontSize:16}}>{pIcon}</span>
                                <span style={{color:pColor,fontWeight:600,fontSize:12}}>{isAval?"Avaliação":"Alinhamento"}</span>
                                <span style={{fontSize:10,padding:"2px 6px",borderRadius:4,background:statusBadge.bg,color:statusBadge.color,fontWeight:600}}>{statusBadge.label}</span>
                                <span style={{marginLeft:"auto",color:"var(--text3)",fontSize:11}}>{fmtDate(p.plannedDate)}</span>
                              </div>
                              {isAval && (fortes>0||melhorar>0||notas>0) && (
                                <div style={{display:"flex",gap:8,marginTop:4,fontSize:10,color:"var(--text3)"}}>
                                  {fortes>0&&<span>💪 {fortes} forte{fortes>1?"s":""}</span>}
                                  {melhorar>0&&<span>📈 {melhorar} melhoria{melhorar>1?"s":""}</span>}
                                  {notas>0&&<span>📝 {notas} nota{notas>1?"s":""}</span>}
                                </div>
                              )}
                              {!isAval && topics>0 && (
                                <div style={{fontSize:10,color:"var(--text3)",marginTop:4}}>📝 {topicsDone}/{topics} tópico{topics>1?"s":""} concluído{topicsDone>1?"s":""}</div>
                              )}
                            </div>
                          );
                        } else {
                          const f = mi.item;
                          const isAval = f.meetingType === "avaliação" || (!f.meetingType && f.rating > 0);
                          const fColor = isAval ? "#8b5cf6" : "#3b82f6";
                          return (
                            <div key={mi.id} style={{...S.card,padding:"10px 14px",marginBottom:6,borderLeft:`3px solid ${fColor}`,opacity:0.85}}>
                              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                                <span style={{fontSize:16}}>{isAval?"📋":"💬"}</span>
                                <span style={{color:fColor,fontWeight:600,fontSize:12}}>{isAval?"Avaliação":"Alinhamento"} (feedback)</span>
                                {f.rating > 0 && <span style={{fontSize:10,padding:"2px 6px",borderRadius:4,background:(RATING_COLORS[f.rating-1]??"var(--text3)")+"18",color:RATING_COLORS[f.rating-1]??"var(--text3)",fontWeight:700}}>{RATING_LABELS[f.rating-1]}</span>}
                                <span style={{marginLeft:"auto",color:"var(--text3)",fontSize:11}}>{fmtDate(f.meetingDate??(f.createdAt??"").slice(0,10))}</span>
                              </div>
                            </div>
                          );
                        }
                      })}
                      {hasMore && <div style={{textAlign:"center",marginTop:4}}><span style={{color:"var(--text3)",fontSize:11}}>+{allMeetingItems.length - MAX_VISIBLE} mais</span></div>}
                    </div>
                  );
                })()}

                {/* ── 3c. Ocorrências — from both incident systems ── */}
                {(() => {
                  const OCC_TYPES_LOCAL = [["cliente","🧑‍🍳","Cliente"],["estrutura","🏗️","Estrutura"],["operacao","⚙️","Operação"],["pessoal","👤","Pessoal"],["outro","📝","Outro"]];
                  // Old incidents system
                  const empIncidents2 = (incidents??[]).filter(i => i.restaurantId === rid && (i.employeeIds??[]).includes(emp.id) && !i.deletedAt);
                  // Unified meetingOccurrences system
                  const empMeetingOccs = (meetingOccurrences??[]).filter(o => o.restaurantId === rid && (o.employeeIds??[]).includes(emp.id) && !o.deletedAt);
                  // Merge into unified list
                  const allOccs = [
                    ...empIncidents2.map(i => {
                      const incType = INCIDENT_TYPES.find(x=>x.id===i.type);
                      return { id:"inc-"+i.id, date:i.date??"", type:incType?.label??i.type, icon:incType?.negative?"🔴":"🟢", desc:(i.description??"").slice(0,120), severity:SEVERITY_OPTIONS.find(s=>s.id===i.severity)?.label??"", resolved:false, source:"incident", negative:!!incType?.negative, action:i.action??"" };
                    }),
                    ...empMeetingOccs.map(o => {
                      const occT = OCC_TYPES_LOCAL.find(([t])=>t===o.type);
                      return { id:"occ-"+o.id, date:(o.createdAt??"").slice(0,10), type:occT?.[2]??o.type, icon:occT?.[1]??"📝", desc:(o.description??"").slice(0,120), severity:o.priority?"Prioridade":"", resolved:o.status==="resolvida", source:"meetingOcc", resolvedText:o.resolvedText??"", title:o.title??"" };
                    })
                  ].sort((a,b)=>(b.date??"").localeCompare(a.date??""));
                  if (allOccs.length === 0) return null;
                  const abertas = allOccs.filter(o => !o.resolved);
                  const resolvidas = allOccs.filter(o => o.resolved);
                  const MAX_OCC = 5;
                  return (
                    <div style={{marginBottom:16}}>
                      <h4 style={{color:"var(--text)",margin:"0 0 10px",fontSize:14}}>🚨 Ocorrências</h4>
                      {abertas.length > 0 && (
                        <div style={{marginBottom:8}}>
                          <div style={{fontSize:11,fontWeight:700,color:"#ef4444",marginBottom:6}}>Abertas ({abertas.length})</div>
                          {abertas.slice(0, MAX_OCC).map(o => (
                            <div key={o.id} style={{...S.card,padding:"10px 14px",marginBottom:6,borderLeft:"3px solid #ef4444"}}>
                              <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                                <span style={{fontSize:14}}>{o.icon}</span>
                                <span style={{color:"var(--text)",fontWeight:600,fontSize:12}}>{o.title||o.type}</span>
                                {o.severity && <span style={{fontSize:10,padding:"2px 6px",borderRadius:4,background:"#f59e0b18",color:"#f59e0b",fontWeight:600}}>{o.severity}</span>}
                                <span style={{marginLeft:"auto",color:"var(--text3)",fontSize:11}}>{fmtDate(o.date)}</span>
                              </div>
                              {o.desc && <div style={{color:"var(--text3)",fontSize:11,marginTop:3}}>{o.desc}</div>}
                              {o.action && <div style={{color:"var(--accent)",fontSize:11,marginTop:2}}>Ação: {o.action}</div>}
                            </div>
                          ))}
                          {abertas.length > MAX_OCC && <div style={{textAlign:"center",marginTop:2}}><span style={{color:"var(--text3)",fontSize:11}}>+{abertas.length - MAX_OCC} mais</span></div>}
                        </div>
                      )}
                      {resolvidas.length > 0 && (
                        <div>
                          <div style={{fontSize:11,fontWeight:700,color:"#10b981",marginBottom:6}}>Resolvidas ({resolvidas.length})</div>
                          {resolvidas.slice(0, MAX_OCC).map(o => (
                            <div key={o.id} style={{...S.card,padding:"10px 14px",marginBottom:6,borderLeft:"3px solid #10b981",opacity:0.75}}>
                              <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                                <span style={{fontSize:14}}>✓</span>
                                <span style={{color:"var(--text3)",fontWeight:600,fontSize:12,textDecoration:"line-through"}}>{o.title||o.type}</span>
                                <span style={{marginLeft:"auto",color:"var(--text3)",fontSize:11}}>{fmtDate(o.date)}</span>
                              </div>
                              {o.resolvedText && <div style={{color:"#10b981",fontSize:11,marginTop:3}}>Resolução: {o.resolvedText}</div>}
                            </div>
                          ))}
                          {resolvidas.length > MAX_OCC && <div style={{textAlign:"center",marginTop:2}}><span style={{color:"var(--text3)",fontSize:11}}>+{resolvidas.length - MAX_OCC} mais</span></div>}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* ── 4. Objetivos ── */}
                <GoalsManager empId={emp.id} employeeGoals={employeeGoals} roles={roles??restRoles} restaurantId={rid} onUpdate={onUpdate} currentUser={currentUser} isOwner={isOwner} schedules={schedules??{}} feedbacks={feedbacks??[]} employees={employees}/>

                {/* ── 5. Presença & Pontualidade — 6 meses ── */}
                {(() => {
                  const hasAny = totFI > 0 || totFJ > 0 || totDelDays > 0;
                  return (
                    <div style={{...S.card,marginBottom:16,padding:"14px"}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                        <span style={{fontSize:16}}>📊</span>
                        <span style={{color:"var(--text)",fontWeight:700,fontSize:14}}>Presença & Pontualidade</span>
                        <span style={{color:"var(--text3)",fontSize:11,marginLeft:"auto"}}>últimos 6 meses</span>
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:12}}>
                        <div style={{padding:"10px 8px",borderRadius:10,background:totFI>0?"#ef444411":"#10b98111",textAlign:"center"}}>
                          <div style={{color:totFI>0?"#ef4444":"#10b981",fontWeight:700,fontSize:20}}>{totFI}</div>
                          <div style={{color:"var(--text3)",fontSize:10}}>Faltas injust.</div>
                        </div>
                        <div style={{padding:"10px 8px",borderRadius:10,background:totFJ>0?"#f59e0b11":"#10b98111",textAlign:"center"}}>
                          <div style={{color:totFJ>0?"#f59e0b":"#10b981",fontWeight:700,fontSize:20}}>{totFJ}</div>
                          <div style={{color:"var(--text3)",fontSize:10}}>Faltas just.</div>
                        </div>
                        <div style={{padding:"10px 8px",borderRadius:10,background:totDelDays>0?"#f59e0b11":"#10b98111",textAlign:"center"}}>
                          <div style={{color:totDelDays>0?"#f59e0b":"#10b981",fontWeight:700,fontSize:20}}>{totDelDays}</div>
                          <div style={{color:"var(--text3)",fontSize:10}}>Dias c/ atraso</div>
                          {totDelMin > 0 && <div style={{color:"var(--text3)",fontSize:9}}>{totDelMin}min ({(totDelMin/60).toFixed(1)}h)</div>}
                        </div>
                      </div>
                      {hasAny && (
                        <div style={{overflowX:"auto"}}>
                          <table style={{width:"100%",borderCollapse:"collapse",fontSize:11,fontFamily:"'DM Mono',monospace"}}>
                            <thead><tr>
                              <th style={{textAlign:"left",padding:"4px 6px",borderBottom:"1px solid var(--border)",color:"var(--text3)",fontSize:10,fontWeight:600}}>Mês</th>
                              <th style={{textAlign:"center",padding:"4px 4px",borderBottom:"1px solid var(--border)",color:"#ef4444",fontSize:10,fontWeight:600}}>FI</th>
                              <th style={{textAlign:"center",padding:"4px 4px",borderBottom:"1px solid var(--border)",color:"#f59e0b",fontSize:10,fontWeight:600}}>FJ</th>
                              <th style={{textAlign:"center",padding:"4px 4px",borderBottom:"1px solid var(--border)",color:"#f59e0b",fontSize:10,fontWeight:600}}>Atrasos</th>
                              <th style={{textAlign:"center",padding:"4px 4px",borderBottom:"1px solid var(--border)",color:"var(--text3)",fontSize:10,fontWeight:600}}>Min</th>
                            </tr></thead>
                            <tbody>
                              {months6.map(m => {
                                if (!m.hasDays && m.delayDays === 0) return null;
                                return (
                                  <tr key={m.mKey}>
                                    <td style={{padding:"4px 6px",borderBottom:"1px solid var(--border)22",textTransform:"capitalize",color:"var(--text2)",fontWeight:600}}>{m.label}</td>
                                    <td style={{textAlign:"center",padding:"4px",borderBottom:"1px solid var(--border)22",color:m.faltasI>0?"#ef4444":"var(--text3)",fontWeight:m.faltasI>0?700:400}}>{m.faltasI}</td>
                                    <td style={{textAlign:"center",padding:"4px",borderBottom:"1px solid var(--border)22",color:m.faltasJ>0?"#f59e0b":"var(--text3)",fontWeight:m.faltasJ>0?700:400}}>{m.faltasJ}</td>
                                    <td style={{textAlign:"center",padding:"4px",borderBottom:"1px solid var(--border)22",color:m.delayDays>0?"#f59e0b":"var(--text3)",fontWeight:m.delayDays>0?700:400}}>{m.delayDays}</td>
                                    <td style={{textAlign:"center",padding:"4px",borderBottom:"1px solid var(--border)22",color:"var(--text3)"}}>{m.delayMin>0?m.delayMin:"—"}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {!hasAny && <div style={{textAlign:"center",color:"#10b981",fontSize:12,padding:"8px 0"}}>✅ Nenhuma falta ou atraso nos últimos 6 meses</div>}
                    </div>
                  );
                })()}

                {/* ── 6. Jornada — timeline clicável ── */}
                {jornadaEvents.length > 0 && (
                  <div style={{marginBottom:16}}>
                    <h4 style={{color:"var(--text)",margin:"0 0 12px",fontSize:14}}>🗓️ Jornada</h4>
                    <div style={{position:"relative",paddingLeft:24}}>
                      <div style={{position:"absolute",left:7,top:4,bottom:4,width:2,background:"var(--border)",borderRadius:1}}/>
                      {jornadaEvents.map((ev,i) => {
                        const evId = ev.fb?.id || ev.inc?.id || ev.plan?.id || ev.occ?.id || `ev-${i}`;
                        const isClickable = ev.type === "feedback" || ev.type === "incident" || ev.type === "meetingPlan" || ev.type === "meetingOcc";
                        const isExpanded = expandedJornada === evId;
                        const dotColor = ev.future ? "#3b82f6" : ev.type === "incident" ? (ev.incNeg ? "#ef4444" : "#10b981") : ev.type === "feedback" ? "#8b5cf6" : ev.type === "meetingPlan" ? "#6366f1" : ev.type === "meetingOcc" ? (ev.occResolved ? "#10b981" : "#f59e0b") : i === jornadaEvents.length-1 ? "var(--ac)" : "var(--border)";
                        const canDeleteFb = !isLider;
                        const canDeleteInc = !isLider;
                        return (
                          <div key={evId} style={{position:"relative",marginBottom:i<jornadaEvents.length-1?4:0}}>
                            <div onClick={isClickable ? ()=>setExpandedJornada(isExpanded ? null : evId) : undefined} style={{display:"flex",alignItems:"flex-start",gap:10,cursor:isClickable?"pointer":"default",padding:"8px 0",borderRadius:8,transition:"background 0.15s"}} onMouseEnter={e=>{if(isClickable)e.currentTarget.style.background="var(--border)22"}} onMouseLeave={e=>{e.currentTarget.style.background="transparent"}}>
                              <div style={{position:"absolute",left:-20,top:11,width:12,height:12,borderRadius:6,background:dotColor,border:"2px solid var(--bg)",flexShrink:0,zIndex:1}}/>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{display:"flex",alignItems:"center",gap:6}}>
                                  <span style={{fontSize:13}}>{ev.icon}</span>
                                  <span style={{color:ev.future?"#3b82f6":"var(--text)",fontSize:12,fontWeight:600,fontStyle:ev.future?"italic":"normal",flex:1}}>{ev.label}</span>
                                  {isClickable && <span style={{color:"var(--text3)",fontSize:10,flexShrink:0}}>{isExpanded ? "▲" : "▼"}</span>}
                                </div>
                                <div style={{color:"var(--text3)",fontSize:10,marginTop:1}}>
                                  {ev.date.toLocaleDateString("pt-BR",{day:"2-digit",month:"short",year:"numeric"})}
                                  {ev.fb?.createdBy ? ` · ${ev.fb.createdBy}` : ""}
                                </div>
                              </div>
                            </div>
                            {/* Expanded feedback detail */}
                            {isExpanded && ev.type === "feedback" && ev.fb && (
                              <div style={{marginLeft:4,marginBottom:8,padding:"10px 14px",borderRadius:10,background:"var(--card-bg)",border:"1px solid var(--border)",borderLeft:`3px solid ${(ev.fb.meetingType==="avaliação"||(!ev.fb.meetingType&&ev.fb.rating>0))?"#8b5cf6":"#3b82f6"}`}}>
                                {ev.fb.rating > 0 && <div style={{marginBottom:6}}><span style={{fontSize:11,padding:"2px 8px",borderRadius:6,background:(RATING_COLORS[ev.fb.rating-1]??"var(--text3)")+"18",color:RATING_COLORS[ev.fb.rating-1]??"var(--text3)",fontWeight:700}}>{RATING_LABELS[ev.fb.rating-1]}</span></div>}
                                {ev.fb.notes && <div style={{fontSize:12,color:"var(--text2)",marginBottom:4}}>{ev.fb.notes}</div>}
                                {ev.fb.strengths && <div style={{fontSize:12,color:"var(--text2)",marginBottom:4}}><strong style={{color:"#10b981"}}>Pontos positivos:</strong> {ev.fb.strengths}</div>}
                                {ev.fb.improvements && <div style={{fontSize:12,color:"var(--text2)",marginBottom:4}}><strong style={{color:"#f59e0b"}}>Melhorias:</strong> {ev.fb.improvements}</div>}
                                {ev.fb.internalNotes && <div style={{fontSize:11,color:"var(--text3)",fontStyle:"italic",marginBottom:4}}>📝 {ev.fb.internalNotes}</div>}
                                {!ev.fb.notes && !ev.fb.strengths && !ev.fb.improvements && !ev.fb.internalNotes && !ev.fb.rating && <div style={{fontSize:11,color:"var(--text3)"}}>Sem anotações registradas</div>}
                                {canDeleteFb && <div style={{marginTop:6,display:"flex",justifyContent:"flex-end"}}><button onClick={async (e)=>{
                                  e.stopPropagation();
                                  const dateStr = ev.fb.meetingDate ? new Date(ev.fb.meetingDate+"T12:00:00").toLocaleDateString("pt-BR") : "";
                                  if (!await appConfirm(`Excluir reunião de ${dateStr}?\n\nO registro ficará na lixeira por 90 dias.`)) return;
                                  const updated = (feedbacks??[]).map(f => f.id === ev.fb.id ? {...f, deletedAt: new Date().toISOString(), deletedBy: currentUser?.name || (isOwner ? "Master" : "Gestor Adm.")} : f);
                                  onUpdate("feedbacks", updated);
                                  setExpandedJornada(null);
                                }} style={{padding:"3px 10px",borderRadius:6,border:"1px solid var(--red)33",background:"transparent",color:"var(--red)",cursor:"pointer",fontSize:10,fontFamily:"'DM Mono',monospace"}}>🗑️ Excluir</button></div>}
                              </div>
                            )}
                            {/* Expanded incident detail */}
                            {isExpanded && ev.type === "incident" && ev.inc && (
                              <div style={{marginLeft:4,marginBottom:8,padding:"10px 14px",borderRadius:10,background:"var(--card-bg)",border:"1px solid var(--border)",borderLeft:`3px solid ${ev.incNeg?"#ef4444":"#10b981"}`}}>
                                {(() => {
                                  const sev = SEVERITY_OPTIONS.find(s=>s.id===ev.inc.severity);
                                  return sev ? <div style={{marginBottom:4}}><span style={{fontSize:11,padding:"2px 8px",borderRadius:6,background:ev.incNeg?"#ef444418":"#10b98118",color:ev.incNeg?"#ef4444":"#10b981",fontWeight:700}}>{sev.label}</span></div> : null;
                                })()}
                                {ev.inc.description && <div style={{fontSize:12,color:"var(--text2)",marginBottom:4}}>{ev.inc.description}</div>}
                                {ev.inc.action && <div style={{fontSize:12,color:"var(--text2)",marginBottom:4}}><strong style={{color:"var(--ac)"}}>Ação:</strong> {ev.inc.action}</div>}
                                <div style={{fontSize:10,color:"var(--text3)"}}>{ev.inc.createdBy ?? ""}</div>
                                {canDeleteInc && <div style={{marginTop:6,display:"flex",justifyContent:"flex-end"}}><button onClick={async (e)=>{
                                  e.stopPropagation();
                                  if (!await appConfirm(`Excluir ocorrência?\n\nO registro ficará na lixeira por 90 dias.`)) return;
                                  const updated = (incidents??[]).map(inc => inc.id === ev.inc.id ? {...inc, deletedAt: new Date().toISOString(), deletedBy: currentUser?.name || (isOwner ? "Master" : "Gestor Adm.")} : inc);
                                  onUpdate("incidents", updated);
                                  setExpandedJornada(null);
                                }} style={{padding:"3px 10px",borderRadius:6,border:"1px solid var(--red)33",background:"transparent",color:"var(--red)",cursor:"pointer",fontSize:10,fontFamily:"'DM Mono',monospace"}}>🗑️ Excluir</button></div>}
                              </div>
                            )}
                            {/* Expanded meetingPlan detail */}
                            {isExpanded && ev.type === "meetingPlan" && ev.plan && (
                              <div style={{marginLeft:4,marginBottom:8,padding:"10px 14px",borderRadius:10,background:"var(--card-bg)",border:"1px solid var(--border)",borderLeft:"3px solid #6366f1"}}>
                                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                                  <span style={{fontSize:11,padding:"2px 8px",borderRadius:6,background:ev.plan.status==="encerrada"?"#10b98118":ev.plan.status==="em_andamento"?"#3b82f618":"#f59e0b18",color:ev.plan.status==="encerrada"?"#10b981":ev.plan.status==="em_andamento"?"#3b82f6":"#f59e0b",fontWeight:700}}>{ev.plan.status==="encerrada"?"Encerrada":ev.plan.status==="em_andamento"?"Em andamento":"Aberta"}</span>
                                  <span style={{fontSize:11,color:"var(--text3)"}}>{ev.plan.type === "avaliação" ? "Avaliação" : "Alinhamento"}</span>
                                </div>
                                {ev.plan.type === "avaliação" && (
                                  <div>
                                    {(ev.plan.pontosFortes??[]).length > 0 && <div style={{fontSize:12,color:"var(--text2)",marginBottom:4}}><strong style={{color:"#10b981"}}>Pontos fortes:</strong> {(ev.plan.pontosFortes??[]).filter(x=>x.text).map(x=>x.text).join(", ")}</div>}
                                    {(ev.plan.pontosMelhorar??[]).length > 0 && <div style={{fontSize:12,color:"var(--text2)",marginBottom:4}}><strong style={{color:"#f59e0b"}}>Melhorias:</strong> {(ev.plan.pontosMelhorar??[]).filter(x=>x.text).map(x=>x.text).join(", ")}</div>}
                                    {(ev.plan.notasInternas??[]).length > 0 && <div style={{fontSize:11,color:"var(--text3)",fontStyle:"italic",marginBottom:4}}>Notas: {(ev.plan.notasInternas??[]).filter(x=>x.text).map(x=>x.text).join(", ")}</div>}
                                  </div>
                                )}
                                {ev.plan.type === "alinhamento" && (ev.plan.topics??[]).length > 0 && (
                                  <div style={{fontSize:12,color:"var(--text2)",marginBottom:4}}><strong>Temas:</strong> {(ev.plan.topics??[]).filter(x=>x.text).map(x=>x.text).join(", ")}</div>
                                )}
                                {ev.plan.meetingNotes && <div style={{fontSize:11,color:"var(--text3)",fontStyle:"italic",marginTop:4}}>Notas da reunião: {ev.plan.meetingNotes}</div>}
                                {ev.plan.createdBy && <div style={{fontSize:10,color:"var(--text3)",marginTop:4}}>{ev.plan.createdBy}</div>}
                              </div>
                            )}
                            {/* Expanded meetingOcc detail */}
                            {isExpanded && ev.type === "meetingOcc" && ev.occ && (
                              <div style={{marginLeft:4,marginBottom:8,padding:"10px 14px",borderRadius:10,background:"var(--card-bg)",border:"1px solid var(--border)",borderLeft:`3px solid ${ev.occResolved?"#10b981":"#f59e0b"}`}}>
                                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                                  <span style={{fontSize:11,padding:"2px 8px",borderRadius:6,background:ev.occResolved?"#10b98118":"#f59e0b18",color:ev.occResolved?"#10b981":"#f59e0b",fontWeight:700}}>{ev.occResolved?"Resolvida":ev.occ.status==="na_pauta"?"Na pauta":"Nova"}</span>
                                  {ev.occ.priority && <span style={{fontSize:10,padding:"2px 6px",borderRadius:4,background:"#ef444418",color:"#ef4444",fontWeight:600}}>Prioridade</span>}
                                </div>
                                {ev.occ.description && <div style={{fontSize:12,color:"var(--text2)",marginBottom:4}}>{ev.occ.description}</div>}
                                {ev.occ.resolvedText && <div style={{fontSize:12,color:"#10b981",marginBottom:4}}>Resolução: {ev.occ.resolvedText}</div>}
                                {ev.occ.resolvedAt && <div style={{fontSize:10,color:"var(--text3)"}}>Resolvida em {new Date(ev.occ.resolvedAt).toLocaleDateString("pt-BR")}{ev.occ.resolvedBy ? ` por ${ev.occ.resolvedBy}` : ""}</div>}
                                {ev.occ.createdBy && <div style={{fontSize:10,color:"var(--text3)",marginTop:2}}>Criada por {ev.occ.createdBy}</div>}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {/* Lixeira de feedbacks e ocorrências */}
                    {(() => {
                      const deletedFbs = myFeedbacksAll.filter(f => f.deletedAt && (Date.now() - new Date(f.deletedAt).getTime()) < 90 * 86400000).sort((a,b) => (b.deletedAt??"").localeCompare(a.deletedAt??""));
                      const deletedIncs = (incidents??[]).filter(i => i.restaurantId === rid && (i.employeeIds??[]).includes(emp.id) && i.deletedAt && (Date.now() - new Date(i.deletedAt).getTime()) < 90 * 86400000);
                      if (deletedFbs.length === 0 && deletedIncs.length === 0) return null;
                      return (
                        <div style={{marginTop:8,padding:"10px 12px",borderRadius:8,background:"var(--red)08",border:"1px solid var(--red)22"}}>
                          <div style={{fontSize:11,color:"var(--red)",fontWeight:700,marginBottom:6}}>🗑️ Lixeira ({deletedFbs.length + deletedIncs.length})</div>
                          {deletedFbs.map(fb => {
                            const daysLeft = Math.max(0, 90 - Math.floor((Date.now() - new Date(fb.deletedAt).getTime()) / 86400000));
                            const dateStr = fb.meetingDate ? new Date(fb.meetingDate+"T12:00:00").toLocaleDateString("pt-BR") : "";
                            return (
                              <div key={fb.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"4px 0",fontSize:10}}>
                                <span style={{color:"var(--text3)",flex:1,minWidth:0}}>💬 Reunião {dateStr} — {fb.deletedBy} ({daysLeft}d)</span>
                                <div style={{display:"flex",gap:4,flexShrink:0}}>
                                  <button onClick={()=>{
                                    const updated = (feedbacks??[]).map(f => f.id === fb.id ? (()=>{ const {deletedAt, deletedBy, ...rest} = f; return rest; })() : f);
                                    onUpdate("feedbacks", updated);
                                  }} style={{padding:"2px 8px",borderRadius:6,border:"1px solid var(--ac)44",background:"transparent",color:"var(--ac-text)",cursor:"pointer",fontSize:10,fontFamily:"'DM Mono',monospace"}}>Restaurar</button>
                                  {isOwner && <button onClick={async ()=>{
                                    if(!await appConfirm("Excluir definitivamente esta reunião? Esta ação não pode ser desfeita.")) return;
                                    onUpdate("feedbacks", (feedbacks??[]).filter(f => f.id !== fb.id));
                                  }} style={{padding:"2px 8px",borderRadius:6,border:"1px solid var(--red)44",background:"transparent",color:"var(--red)",cursor:"pointer",fontSize:10,fontFamily:"'DM Mono',monospace"}}>Excluir</button>}
                                </div>
                              </div>
                            );
                          })}
                          {deletedIncs.map(inc => {
                            const daysLeft = Math.max(0, 90 - Math.floor((Date.now() - new Date(inc.deletedAt).getTime()) / 86400000));
                            const t = INCIDENT_TYPES.find(x=>x.id===inc.type);
                            return (
                              <div key={inc.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"4px 0",fontSize:10}}>
                                <span style={{color:"var(--text3)",flex:1,minWidth:0}}>{t?.negative?"🔴":"🟢"} {t?.label??inc.type} — {inc.deletedBy} ({daysLeft}d)</span>
                                <div style={{display:"flex",gap:4,flexShrink:0}}>
                                  <button onClick={()=>{
                                    const updated = (incidents??[]).map(i => i.id === inc.id ? (()=>{ const {deletedAt, deletedBy, ...rest} = i; return rest; })() : i);
                                    onUpdate("incidents", updated);
                                  }} style={{padding:"2px 8px",borderRadius:6,border:"1px solid var(--ac)44",background:"transparent",color:"var(--ac-text)",cursor:"pointer",fontSize:10,fontFamily:"'DM Mono',monospace"}}>Restaurar</button>
                                  {isOwner && <button onClick={async ()=>{
                                    if(!await appConfirm("Excluir definitivamente esta ocorrência? Esta ação não pode ser desfeita.")) return;
                                    onUpdate("incidents", (incidents??[]).filter(i => i.id !== inc.id));
                                  }} style={{padding:"2px 8px",borderRadius:6,border:"1px solid var(--red)44",background:"transparent",color:"var(--red)",cursor:"pointer",fontSize:10,fontFamily:"'DM Mono',monospace"}}>Excluir</button>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
              );
            })()}
          </div>
        );
      })()}

      {/* ═══════════════════════ LIST VIEW ═══════════════════════ */}
      {!detailEmp && <>

      {/* Toggle Ativos / Inativos + Vincular pessoa */}
      <div style={{display:"flex",gap:8,marginBottom:12,alignItems:"center",flexWrap:"wrap"}}>
        <button onClick={()=>setShowInactive(false)} style={{flex:1,padding:"8px",borderRadius:10,border:`1px solid ${!showInactive?"var(--green)":"var(--border)"}`,background:!showInactive?"#10b98122":"transparent",color:!showInactive?"var(--green)":"var(--text3)",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:13}}>
          Ativos ({activeEmps.length})
        </button>
        <button onClick={()=>setShowInactive(true)} style={{flex:1,padding:"8px",borderRadius:10,border:`1px solid ${showInactive?"#8b5cf6":"var(--border)"}`,background:showInactive?"#8b5cf622":"transparent",color:showInactive?"#8b5cf6":"var(--text3)",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:13}}>
          Inativos ({inactiveEmps.length})
        </button>
        {!showInactive && (
          <button onClick={()=>setVincularPessoaOpen(true)}
            title="Cadastros novos vão na aba Pessoas. Aqui você marca uma pessoa existente como membro da equipe."
            style={{padding:"8px 14px",borderRadius:10,border:"1px solid var(--ac)44",background:"var(--ac)11",color:"var(--ac)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:700,whiteSpace:"nowrap"}}>
            🔗 Vincular pessoa {pessoasNaoEquipe.length > 0 && <span style={{opacity:0.6,fontWeight:400}}>({pessoasNaoEquipe.length})</span>}
          </button>
        )}
      </div>

      {/* MODAL: Vincular pessoa como empregado */}
      {vincularPessoaOpen && (() => {
        const q = vincularPessoaFilter.trim().toLowerCase();
        const filtered = q
          ? pessoasNaoEquipe.filter(p => p.name.toLowerCase().includes(q) || (p.cpf || "").includes(q))
          : pessoasNaoEquipe;
        const sortedList = [...filtered].sort((a,b) => a.name.localeCompare(b.name));
        return (
          <div onClick={()=>{setVincularPessoaOpen(false);setVincularPessoaSelected(null);setVincularPessoaFilter("");}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:9500,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
            <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",borderRadius:14,maxWidth:600,width:"100%",maxHeight:"85vh",display:"flex",flexDirection:"column",boxShadow:"0 10px 40px rgba(0,0,0,0.3)"}}>
              <div style={{padding:"16px 20px",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div>
                  <div style={{fontSize:11,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Equipe</div>
                  <div style={{fontSize:16,color:"var(--text)",fontWeight:700,marginTop:2}}>🔗 Vincular pessoa como empregado</div>
                </div>
                <button onClick={()=>{setVincularPessoaOpen(false);setVincularPessoaSelected(null);setVincularPessoaFilter("");}} style={{background:"none",border:"none",fontSize:22,color:"var(--text3)",cursor:"pointer",padding:"0 4px"}}>×</button>
              </div>

              {!vincularPessoaSelected ? (
                <>
                  <div style={{padding:"12px 20px",borderBottom:"1px solid var(--border)"}}>
                    <p style={{color:"var(--text3)",fontSize:12,margin:"0 0 10px",lineHeight:1.5}}>
                      Selecione uma pessoa cadastrada deste restaurante pra marcar como equipe (entra na escala e gorjeta). Se a pessoa ainda não existe, cadastre primeiro na aba <strong>Pessoas</strong>.
                    </p>
                    <input value={vincularPessoaFilter} onChange={e=>setVincularPessoaFilter(e.target.value)} placeholder="🔍 Buscar por nome ou CPF..." style={S.input} autoFocus/>
                  </div>
                  <div style={{padding:"10px 16px",overflowY:"auto",flex:1}}>
                    {sortedList.length === 0 ? (
                      <div style={{padding:"30px 16px",textAlign:"center",color:"var(--text3)",fontSize:13}}>
                        {pessoasNaoEquipe.length === 0
                          ? "Todas as pessoas deste restaurante já estão marcadas como equipe. Cadastre uma nova em Pessoas."
                          : "Nenhuma pessoa encontrada."}
                      </div>
                    ) : (
                      <div style={{display:"flex",flexDirection:"column",gap:6}}>
                        {sortedList.map(p => {
                          const cpfMasked = p.cpf ? p.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4") : "—";
                          const initial = (p.name || "?").trim().charAt(0).toUpperCase();
                          return (
                            <div key={p.id} onClick={()=>{setVincularPessoaSelected(p);setVincularPessoaAdmission(today());}}
                              style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",background:"var(--card-bg)",borderRadius:10,border:"1px solid var(--border)",cursor:"pointer",transition:"background 0.12s"}}
                              onMouseEnter={e=>e.currentTarget.style.background="var(--bg2)"}
                              onMouseLeave={e=>e.currentTarget.style.background="var(--card-bg)"}>
                              <div style={{flexShrink:0,width:36,height:36,borderRadius:18,background:"var(--ac)22",color:"var(--ac)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:700}}>{initial}</div>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>{p.name}</div>
                                <div style={{fontSize:11,color:"var(--text3)",fontFamily:"'DM Mono',monospace"}}>{cpfMasked}</div>
                              </div>
                              <span style={{color:"var(--ac)",fontSize:14}}>›</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div style={{padding:"16px 20px",overflowY:"auto",flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:"var(--ac)11",border:"1px solid var(--ac)44",borderRadius:10,marginBottom:14}}>
                    <div style={{flexShrink:0,width:36,height:36,borderRadius:18,background:"var(--ac)22",color:"var(--ac)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:700}}>{(vincularPessoaSelected.name||"?").trim().charAt(0).toUpperCase()}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:14,fontWeight:700,color:"var(--text)"}}>{vincularPessoaSelected.name}</div>
                      <div style={{fontSize:11,color:"var(--text3)",fontFamily:"'DM Mono',monospace"}}>{vincularPessoaSelected.cpf ? vincularPessoaSelected.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4") : "—"}</div>
                    </div>
                    <button onClick={()=>setVincularPessoaSelected(null)} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px"}}>← Trocar</button>
                  </div>
                  <p style={{color:"var(--text3)",fontSize:12,lineHeight:1.5,marginBottom:14}}>
                    Defina cargo e data de admissão no <strong>{restaurant?.name || "restaurante"}</strong>. Esses dados ficam por restaurante — a pessoa pode ter cargos diferentes em outros.
                  </p>
                  <div style={{display:"grid",gridTemplateColumns:"1.5fr 1fr",gap:10,marginBottom:10}}>
                    <div>
                      <label style={{...S.label,fontSize:11}}>Cargo</label>
                      <select value={vincularPessoaRole} onChange={e=>setVincularPessoaRole(e.target.value)} style={{...S.input,cursor:"pointer"}}>
                        <option value="">— Selecione (pode definir depois) —</option>
                        {AREAS.map(a=>(<optgroup key={a} label={a}>{restRoles.filter(r=>r.area===a&&!r.inactive).map(r=><option key={r.id} value={r.id}>{r.name}{r.points?` (${r.points}pt)`:""}</option>)}</optgroup>))}
                      </select>
                    </div>
                    <div>
                      <label style={{...S.label,fontSize:11}}>Data de admissão</label>
                      <input type="date" value={vincularPessoaAdmission} onChange={e=>setVincularPessoaAdmission(e.target.value)} style={S.input}/>
                    </div>
                  </div>
                </div>
              )}

              <div style={{padding:"12px 20px",borderTop:"1px solid var(--border)",display:"flex",gap:8,justifyContent:"flex-end",background:"var(--bg2)"}}>
                <button onClick={()=>{setVincularPessoaOpen(false);setVincularPessoaSelected(null);setVincularPessoaFilter("");}} style={{...S.btnSecondary,fontSize:12,padding:"8px 16px"}}>Cancelar</button>
                {vincularPessoaSelected && (
                  <button onClick={vincularPessoaComoEmpregado}
                    style={{background:"var(--ac)",color:"#fff",border:"none",borderRadius:8,padding:"8px 18px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                    🔗 Vincular como empregado
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Plan limits */}
      {activeCount >= plano.empMax && (
        <div style={{background:"var(--red-bg)",border:"1px solid var(--red)33",borderRadius:12,padding:"14px 16px",marginBottom:12}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
            <div>
              <div style={{color:"var(--red)",fontSize:13,fontWeight:700,marginBottom:2}}>⚠️ Limite do plano atingido — {activeCount}/{plano.empMax}</div>
              <div style={{color:"var(--text3)",fontSize:12}}>Solicite upgrade para adicionar mais.</div>
            </div>
            <button onClick={()=>{
              const PLANOS_LABEL = { pIsento:"Isento", p10:"Starter (10)", p20:"Básico (20)", p50:"Profissional (50)", p999:"Enterprise (51-100)", pOrc:"On Demand (+100)" };
              const PROXIMO = { p10:"p20", p20:"p50", p50:"p999", p999:"pOrc" };
              const planoAtual = PLANOS_LABEL[restaurant?.planoId??"p10"] ?? "Starter";
              const planoProx = PLANOS_LABEL[PROXIMO[restaurant?.planoId??"p10"]] ?? "Enterprise";
              const restNome = restaurant?.name ?? "Restaurante";
              if (onUpdate) { onUpdate("notifications", [...(notifications ?? []), { id:Date.now().toString(), restaurantId:rid, type:"upgrade_request", body:`📦 Upgrade — ${restNome}: ${planoAtual} → ${planoProx}. Ativos: ${activeCount}/${plano.empMax}.`, date:new Date().toISOString(), read:false, targetRole:"admin" }]); }
              const msg = encodeURIComponent(`Olá! Sou gestor do restaurante *${restNome}*.\nGostaria de solicitar upgrade do plano:\n• Atual: *${planoAtual}*\n• Desejado: *${planoProx}*\n• Empregados: ${activeCount}/${plano.empMax}\nObrigado!`);
              window.open(`https://wa.me/5511985499821?text=${msg}`, "_blank");
              onUpdate("_toast", "✅ Solicitação enviada!");
            }} style={{padding:"10px 18px",borderRadius:10,border:"none",background:"var(--red)",color:"#fff",fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,whiteSpace:"nowrap"}}>
              📲 Solicitar upgrade
            </button>
          </div>
        </div>
      )}
      {activeCount > 0 && activeCount < plano.empMax && activeCount >= plano.empMax * 0.8 && (
        <div style={{background:"#f59e0b11",border:"1px solid #f59e0b33",borderRadius:10,padding:"8px 14px",marginBottom:12}}>
          <span style={{color:"#f59e0b",fontSize:12}}>⚡ {activeCount}/{plano.empMax} — próximo do limite</span>
        </div>
      )}

      {/* ── Employee cards by area ── */}
      {(() => {
        const groups = {};
        list.forEach(emp => {
          const role = restRoles.find(r=>r.id===emp.roleId);
          const area = role?.area ?? "Sem área";
          if (!groups[area]) groups[area] = [];
          groups[area].push(emp);
        });
        return Object.entries(groups).map(([area, emps]) => (
          <div key={area} style={{marginBottom:14}}>
            <div style={{color:AREA_COLORS[area]??"#888",fontSize:11,fontWeight:700,padding:"8px 8px 4px",borderBottom:`1px solid ${AREA_COLORS[area]??"var(--bg4)"}33`,marginBottom:6,letterSpacing:1}}>
              {area.toUpperCase()} · {emps.length}
            </div>
            {emps.map(emp => {
              const role = restRoles.find(r=>r.id===emp.roleId);
              const badge = statusBadge(emp);
              return (
                <div key={emp.id} onClick={()=>{setDetailEmp(emp.id);setDetailTab("cadastro");}} style={{
                  display:"flex", alignItems:"center", gap:10, padding:"10px 14px", marginBottom:4,
                  background:"var(--card-bg)", borderRadius:10, border:"1px solid var(--border)",
                  cursor:"pointer", transition:"background 0.15s"
                }}
                onMouseEnter={e=>e.currentTarget.style.background="var(--bg2)"}
                onMouseLeave={e=>e.currentTarget.style.background="var(--card-bg)"}>
                  {/* Avatar circle */}
                  <div style={{width:36,height:36,borderRadius:"50%",background:AREA_COLORS[role?.area]??"var(--bg3)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:700,color:"#fff",flexShrink:0}}>
                    {(emp.name||"?").charAt(0).toUpperCase()}
                  </div>
                  {/* Info */}
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                      {(() => {
                        // Só exibe se há pelo menos UM schedule completo (com algum dia ativo + in/out preenchidos)
                        const scheds = workSchedules?.[rid]?.[emp.id] || [];
                        const validos = scheds.filter(s =>
                          s && s.days && Object.values(s.days).some(d => d && d.in && d.out)
                        );
                        if (validos.length === 0) return null;
                        return (
                          <span title={`${validos.length} horário${validos.length>1?"s":""} cadastrado${validos.length>1?"s":""}`}
                            style={{fontSize:11,lineHeight:1,color:"#0284c7",flexShrink:0}}>⏰</span>
                        );
                      })()}
                      <span style={{fontSize:13,fontWeight:700,color:"var(--text)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{emp.name}</span>
                      <span style={{fontSize:9,padding:"1px 6px",borderRadius:4,background:badge.bg,color:badge.color,fontWeight:700}}>{badge.label}</span>
                      {emp.isProducao && <span style={{fontSize:9,padding:"1px 5px",borderRadius:4,background:"#ec489922",color:"#ec4899"}}>Prod</span>}
                      {emp.isFreela && <span style={{fontSize:9,padding:"1px 5px",borderRadius:4,background:"#06b6d422",color:"#06b6d4"}}>Freela</span>}
                    </div>
                    <div style={{fontSize:11,color:"var(--text3)",marginTop:1}}>{role?.name ?? "Sem cargo"}</div>
                  </div>
                  {/* Arrow */}
                  <span style={{color:"var(--text3)",fontSize:16,flexShrink:0}}>›</span>
                </div>
              );
            })}
          </div>
        ));
      })()}
      </>}

      {/* Modal: Demissão com date picker */}
      {dismissForm && (() => {
        const { emp, lastWorkedDay } = dismissForm;
        const valid = !!lastWorkedDay && !isNaN(new Date(lastWorkedDay+"T12:00:00").getTime());
        const lwdBR = valid ? new Date(lastWorkedDay+"T12:00:00").toLocaleDateString("pt-BR") : "—";
        const nextDayBR = valid ? new Date(addDaysISO(lastWorkedDay,1)+"T12:00:00").toLocaleDateString("pt-BR") : "—";
        return (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={()=>setDismissForm(null)}>
            <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:14,maxWidth:480,width:"100%",padding:24,boxShadow:"0 20px 50px rgba(0,0,0,0.3)"}}>
              <h3 style={{margin:"0 0 8px",color:"var(--red)",fontSize:16,fontWeight:700}}>📋 Demitir {emp.name}</h3>
              <p style={{color:"var(--text2)",fontSize:13,margin:"0 0 14px",lineHeight:1.5}}>
                Selecione o <strong>último dia trabalhado</strong>. Esse dia ainda conta na escala e na gorjeta. A partir do dia seguinte, {emp.name} sai dos cálculos.
              </p>
              <div style={{marginBottom:14}}>
                <label style={{...S.label,fontSize:11}}>Último dia trabalhado</label>
                <input type="date" value={lastWorkedDay} onChange={e=>setDismissForm({...dismissForm, lastWorkedDay:e.target.value})} style={S.input}/>
              </div>
              {valid && (
                <div style={{padding:"10px 12px",background:"var(--bg2)",borderRadius:8,fontSize:12,color:"var(--text2)",lineHeight:1.6,marginBottom:14}}>
                  <div>Último dia: <strong>{lwdBR}</strong> (conta normal)</div>
                  <div style={{color:"var(--red)",marginTop:4}}>A partir de <strong>{nextDayBR}</strong>: sai do cálculo de gorjeta, consta como "DEM" na escala.</div>
                </div>
              )}
              <div style={{display:"flex",justifyContent:"flex-end",gap:10}}>
                <button onClick={()=>setDismissForm(null)} style={{...S.btnSecondary,padding:"8px 14px",fontSize:13}}>Cancelar</button>
                <button onClick={confirmDismiss} disabled={!valid}
                  style={{...S.btnPrimary,padding:"8px 14px",fontSize:13,fontWeight:700,background:"var(--red)",borderColor:"var(--red)",opacity:valid?1:0.5}}>
                  Confirmar demissão
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal: Promoção / Mudança de cargo */}
      {promoteForm && (() => {
        const { emp, newRoleId, lastDayOldRole, reason } = promoteForm;
        const oldRole = restRoles.find(r => r.id === emp.roleId);
        const newRole = restRoles.find(r => r.id === newRoleId);
        const available = restRoles.filter(r => !r.inactive && r.id !== emp.roleId).sort((a,b) => a.name.localeCompare(b.name));
        const valid = !!newRoleId && !!lastDayOldRole && !isNaN(new Date(lastDayOldRole+"T12:00:00").getTime());
        const lwdBR = valid ? new Date(lastDayOldRole+"T12:00:00").toLocaleDateString("pt-BR") : "—";
        const nextDayBR = valid ? new Date(addDaysISO(lastDayOldRole,1)+"T12:00:00").toLocaleDateString("pt-BR") : "—";
        const isFuture = valid && addDaysISO(lastDayOldRole, 1) > today();
        return (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={()=>setPromoteForm(null)}>
            <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:14,maxWidth:520,width:"100%",padding:24,boxShadow:"0 20px 50px rgba(0,0,0,0.3)"}}>
              <h3 style={{margin:"0 0 8px",color:"#3b82f6",fontSize:16,fontWeight:700}}>⬆️ Mudar cargo de {emp.name}</h3>
              <p style={{color:"var(--text2)",fontSize:13,margin:"0 0 14px",lineHeight:1.5}}>
                Cargo atual: <strong>{oldRole?.name ?? "—"}</strong>
              </p>
              <div style={{marginBottom:12}}>
                <label style={{...S.label,fontSize:11}}>Novo cargo</label>
                <select value={newRoleId} onChange={e=>setPromoteForm({...promoteForm, newRoleId:e.target.value})} style={{...S.input,cursor:"pointer"}}>
                  <option value="">Selecione...</option>
                  {available.map(r => <option key={r.id} value={r.id}>{r.name} · {r.area} · {r.points}pt{r.noTip?" · sem gorjeta":""}</option>)}
                </select>
              </div>
              <div style={{marginBottom:12}}>
                <label style={{...S.label,fontSize:11}}>Último dia no cargo atual</label>
                <input type="date" value={lastDayOldRole} onChange={e=>setPromoteForm({...promoteForm, lastDayOldRole:e.target.value})} style={S.input}/>
                <div style={{fontSize:10,color:"var(--text3)",marginTop:4,lineHeight:1.4}}>Esse dia ainda conta como {oldRole?.name ?? "cargo atual"}. A partir do dia seguinte passa a ser {newRole?.name ?? "novo cargo"}.</div>
              </div>
              <div style={{marginBottom:14}}>
                <label style={{...S.label,fontSize:11}}>Motivo (opcional)</label>
                <input value={reason} onChange={e=>setPromoteForm({...promoteForm, reason:e.target.value})} placeholder="ex: promoção por tempo de casa" style={S.input}/>
              </div>
              {valid && newRole && (
                <div style={{padding:"10px 12px",background:"var(--bg2)",borderRadius:8,fontSize:12,color:"var(--text2)",lineHeight:1.6,marginBottom:14}}>
                  <div><strong>{oldRole?.name ?? "—"} → {newRole.name}</strong></div>
                  <div style={{marginTop:4}}>Último dia como {oldRole?.name ?? "cargo atual"}: <strong>{lwdBR}</strong></div>
                  <div style={{color:"#3b82f6",marginTop:4}}>{newRole.name} a partir de <strong>{nextDayBR}</strong>{isFuture?" (agendada — aplicada automaticamente)":""}</div>
                </div>
              )}
              <div style={{display:"flex",justifyContent:"flex-end",gap:10}}>
                <button onClick={()=>setPromoteForm(null)} style={{...S.btnSecondary,padding:"8px 14px",fontSize:13}}>Cancelar</button>
                <button onClick={confirmPromote} disabled={!valid}
                  style={{...S.btnPrimary,padding:"8px 14px",fontSize:13,fontWeight:700,background:"#3b82f6",borderColor:"#3b82f6",opacity:valid?1:0.5}}>
                  {isFuture ? "Agendar promoção" : "Confirmar mudança"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Pre-dismissal checklist modal (#71) */}
      {showDismissalChecklist && dismissalCheckEmp && (() => {
        const dce = dismissalCheckEmp;
        // Mês de referência = mês do ÚLTIMO DIA TRABALHADO (= demitidoEm - 1)
        const lastWorked3 = dce.demitidoEm ? addDaysISO(dce.demitidoEm, -1) : "";
        const demMk3 = lastWorked3.slice(0,7);
        const [demY3, demM3] = demMk3 ? demMk3.split("-").map(Number) : [0,0];
        const demDay3 = parseInt(lastWorked3.slice(8,10) ?? "0");
        const daysInDemMonth3 = demY3 ? new Date(demY3, demM3, 0).getDate() : 0;
        // VT status
        const vtPaid3 = !!vtPayments?.[rid]?.[demMk3];
        // Previous month schedule status
        const prevDem3 = demY3 ? new Date(demY3, demM3 - 2, 1) : null;
        const prevDemMk3 = prevDem3 ? `${prevDem3.getFullYear()}-${String(prevDem3.getMonth()+1).padStart(2,"0")}` : "";
        const prevSchedClosed3 = prevDemMk3 ? scheduleStatus?.[rid]?.[prevDemMk3]?.status === "closed" : false;
        // Dias trabalhados ATÉ E INCLUINDO o último dia trabalhado
        const schedMap3 = schedules?.[rid]?.[demMk3]?.[dce.id] ?? {};
        let workDays3 = 0;
        for (let dd = 1; dd <= demDay3 && dd <= daysInDemMonth3; dd++) {
          const ds = `${demY3}-${String(demM3).padStart(2,"0")}-${String(dd).padStart(2,"0")}`;
          if (!schedMap3[ds] || schedMap3[ds] === "comptrab") workDays3++;
        }
        // Tips check - find last date with tips up to dismissal
        const hasWarnings = !vtPaid3 || !prevSchedClosed3;
        const checks = [
          { label: "VT do mês de demissão", ok: vtPaid3, okText: "Pago", warnText: "Não pago — valores provisórios" },
          { label: "Escala do mês anterior", ok: prevSchedClosed3, okText: "Fechada — ajuste definitivo", warnText: "Não fechada — ajuste provisório" },
          { label: `Dias trabalhados até demissão (${demMk3})`, ok: true, okText: `${workDays3} dias de trabalho computados`, warnText: "" },
        ];
        return (
          <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={()=>setShowDismissalChecklist(false)}>
            <div onClick={e=>e.stopPropagation()} style={{background:"var(--bg2)",borderRadius:16,padding:24,maxWidth:480,width:"100%",maxHeight:"80vh",overflowY:"auto",border:"1px solid var(--border)"}}>
              <h3 style={{color:"var(--text)",margin:"0 0 16px",fontSize:16}}>Checklist de desligamento</h3>
              <p style={{color:"var(--text2)",fontSize:13,margin:"0 0 12px"}}>{dce.name} — último dia trabalhado em {dce.demitidoEm ? new Date(addDaysISO(dce.demitidoEm,-1)+"T12:00:00").toLocaleDateString("pt-BR") : "—"}</p>
              <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
                {checks.map((c,i) => (
                  <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:10,background:c.ok?"#10b98109":"#f59e0b09",border:`1px solid ${c.ok?"#10b98133":"#f59e0b33"}`}}>
                    <span style={{fontSize:16}}>{c.ok ? "\u2705" : "\u26A0\uFE0F"}</span>
                    <div>
                      <div style={{color:"var(--text)",fontSize:12,fontWeight:600}}>{c.label}</div>
                      <div style={{color:c.ok?"var(--green)":"#f59e0b",fontSize:11}}>{c.ok ? c.okText : c.warnText}</div>
                    </div>
                  </div>
                ))}
              </div>
              {hasWarnings && (
                <p style={{color:"#f59e0b",fontSize:11,margin:"0 0 12px"}}>Existem itens pendentes. O relatório pode conter valores provisórios.</p>
              )}
              <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                <button onClick={()=>setShowDismissalChecklist(false)} style={S.btnSecondary}>Cancelar</button>
                <button onClick={()=>{
                  setShowDismissalChecklist(false);
                  onGenerateDismissalReport(dce);
                }} style={{...S.btnPrimary,fontSize:13}}>
                  {hasWarnings ? "Gerar mesmo assim" : "Gerar PDF"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {previewDoc && <PDFPreviewModal pdfDoc={previewDoc} fileName={previewFileName} title="Trilha do Empregado" onClose={()=>setPreviewDoc(null)} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// VALE TRANSPORTE TAB
// ═══════════════════════════════════════════════════════════════════
function ValeTransporteTab({ restaurantId, employees, roles, workSchedules, schedules, vtConfig, vtMonthly, vtPayments, onUpdate, currentUser, isOwner, mobileOnly, schedulePrevista, scheduleStatus, scheduleVersions, onExit }) {
  // v8.0 — default inteligente: dia ≥ 20 → próximo mês (planejamento de fechamento)
  const _smartDefVT = smartDefaultMonth();
  const [year, setYear] = useState(_smartDefVT.year);
  const [month, setMonth] = useState(_smartDefVT.month);
  // v8.4 — gate de seleção de mês
  const [vtMonthConfirmed, setVtMonthConfirmed] = useState(false);
  const mk = monthKey(year, month);
  const ac = "var(--accent)";
  const [showPayDateModal, setShowPayDateModal] = useState(false);
  const [payDate, setPayDate] = useState(today());
  const [versionWarning, setVersionWarning] = useState(null);
  const [vtSavedAt, setVtSavedAt] = useState(null); // v8.1 — timestamp do último auto-save

  // v8.10.7: filtro alinhado com a Escala — demitido em mês anterior ao visualizado some,
  // demitido NESTE mês continua aparecendo (pra fechar VT do mês de saída). Inativo sem
  // demissão sai se inactiveFrom <= today.
  const restEmps = (employees ?? []).filter(e => {
    if (e.restaurantId !== restaurantId) return false;
    if (e.isFreela) return false;
    // demitidoEm = primeiro dia FORA. O mês relevante pra filtro é o do ÚLTIMO DIA TRABALHADO (= demitidoEm - 1)
    // assim demissão em 30/04 (demitidoEm=01/05) some já em maio, não aparece como "demitido neste mês" em maio.
    const demMonth = e.demitidoEm ? addDaysISO(e.demitidoEm, -1).slice(0, 7) : null;
    if (demMonth && demMonth < mk) return false; // último dia trabalhado num mês anterior — fora sempre
    const isInactiveNow = !!(e.inactive && e.inactiveFrom && e.inactiveFrom <= today());
    if (isInactiveNow && demMonth !== mk) return false; // inativo e não demitido neste mês — fora
    return true;
  });
  const restRoles = (roles ?? []).filter(r => r.restaurantId === restaurantId);

  // ── BR currency input helpers ──
  const toBR = (v) => { const n = parseFloat(v); return isNaN(n) || n === 0 ? "" : n.toFixed(2).replace(".", ","); };
  const fromBR = (s) => { if (!s) return 0; return parseFloat(s.replace(/\./g, "").replace(",", ".")) || 0; };

  // ── Editable local state ──
  const [localOverrides, setLocalOverrides] = useState({});
  const [localRates, setLocalRates] = useState({}); // display strings in BR format
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const monthData = vtMonthly?.[restaurantId]?.[mk] ?? {};
    // v7.2: pre-fill da coluna Ajuda de Custo (manualBonus) com o valor do MESMO empregado no mês anterior.
    // Fallback: vtMonthly do prevMk → snapshot pago em vtPayments do prevMk → 0.
    const prevDateLocal = new Date(year, month - 1, 1);
    const prevMkLocal = monthKey(prevDateLocal.getFullYear(), prevDateLocal.getMonth());
    const prevMonthData = vtMonthly?.[restaurantId]?.[prevMkLocal] ?? {};
    const prevPay = vtPayments?.[restaurantId]?.[prevMkLocal];
    const prevBonusFor = (empId) => {
      if (prevMonthData[empId]?.manualBonus !== undefined && prevMonthData[empId]?.manualBonus !== null) {
        return prevMonthData[empId].manualBonus;
      }
      const ps = prevPay?.snapshot?.find(s => s.empId === empId);
      if (ps?.manualBonus !== undefined && ps?.manualBonus !== null) return ps.manualBonus;
      return 0;
    };
    const overrides = {};
    // Pré-popula TODOS os empregados (mesmo os sem registro no mês atual) pra puxar o bônus.
    restEmps.forEach(emp => {
      const v = monthData[emp.id] || {};
      const bonus = (v.manualBonus !== undefined && v.manualBonus !== null) ? v.manualBonus : prevBonusFor(emp.id);
      overrides[emp.id] = {
        adjustOverride: v.adjustOverride ?? null,
        adjustDisplay: toBR(v.adjustOverride),
        manualDiscount: v.manualDiscount ?? 0,
        discountDisplay: toBR(v.manualDiscount),
        manualBonus: bonus,
        bonusDisplay: toBR(bonus),
      };
    });
    setLocalOverrides(overrides);

    const rates = {};
    const cfgRest = vtConfig?.[restaurantId] ?? {};
    restEmps.forEach(emp => {
      rates[emp.id] = toBR(cfgRest[emp.id]?.dailyRate ?? 0);
    });
    setLocalRates(rates);
    setDirty(false);
  }, [mk, restaurantId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Previous month payment snapshot ──
  const prevDate = new Date(year, month - 1, 1);
  const prevMk = monthKey(prevDate.getFullYear(), prevDate.getMonth());
  const prevPayment = vtPayments?.[restaurantId]?.[prevMk] ?? null;
  const currentPayment = vtPayments?.[restaurantId]?.[mk] ?? null;

  // ── Count planned working days (alinhado com visual da escala) ──
  function countPlannedDays(empId) {
    const lastDay = new Date(year, month+1, 0).getDate();
    const schedDayMap = schedules?.[restaurantId]?.[mk]?.[empId] ?? {};
    let count = 0;
    for (let d = 1; d <= lastDay; d++) {
      const dateStr = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
      const escalaSt = schedDayMap[dateStr];
      if (!escalaSt || escalaSt === "comptrab") { count++; }
      // off, comp, vac, faultj, faultu, freela = não conta
    }
    return count;
  }

  // ── Count actual working days (for auto-adjust, alinhado com visual) ──
  function countActualDays(empId, targetMk, targetYear, targetMonth) {
    const lastDay = new Date(targetYear, targetMonth+1, 0).getDate();
    const schedDayMap = schedules?.[restaurantId]?.[targetMk]?.[empId] ?? {};
    let count = 0;
    for (let d = 1; d <= lastDay; d++) {
      const dateStr = `${targetYear}-${String(targetMonth+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
      const escalaSt = schedDayMap[dateStr];
      if (!escalaSt || escalaSt === "comptrab") { count++; }
    }
    return count;
  }

  function calcAutoAdjust(empId) {
    if (!prevPayment?.snapshot) return 0;
    const prevClosed = scheduleStatus?.[restaurantId]?.[prevMk]?.status === "closed";
    if (!prevClosed) return 0; // adjustment deferred until previous month is closed
    const prevSnap = prevPayment.snapshot.find(s => s.empId === empId);
    if (!prevSnap) return 0;
    const actualDays = countActualDays(empId, prevMk, prevDate.getFullYear(), prevDate.getMonth());
    const diff = actualDays - prevSnap.plannedDays;
    return round2(diff * prevSnap.dailyRate);
  }

  // ── Build rows ──
  const rows = restEmps.map(emp => {
    const role = restRoles.find(r => r.id === emp.roleId);
    const area = role?.area ?? "Outros";
    const dailyRate = fromBR(localRates[emp.id]);
    const plannedDays = countPlannedDays(emp.id);
    const grossVT = round2(dailyRate * plannedDays);
    const suggestedAdjust = calcAutoAdjust(emp.id);
    const overrides = localOverrides[emp.id] ?? {};
    const autoAdjust = overrides.adjustOverride !== null && overrides.adjustOverride !== undefined ? overrides.adjustOverride : suggestedAdjust;
    const manualDiscount = overrides.manualDiscount ?? 0;
    const manualBonus = overrides.manualBonus ?? 0;
    const totalPaid = round2(grossVT + autoAdjust + manualBonus - manualDiscount);
    // Previous month actual days (only when closed)
    const prevClosed = scheduleStatus?.[restaurantId]?.[prevMk]?.status === "closed";
    let prevActualDays = null;
    if (prevClosed && prevPayment?.snapshot) {
      const prevSnap = prevPayment.snapshot.find(s => s.empId === emp.id);
      if (prevSnap) {
        prevActualDays = countActualDays(emp.id, prevMk, prevDate.getFullYear(), prevDate.getMonth());
      }
    }
    return { emp, role, area, dailyRate, plannedDays, grossVT, suggestedAdjust, autoAdjust, manualBonus, manualDiscount, totalPaid, prevActualDays };
  }).sort((a,b) => (a.emp.name??"").localeCompare(b.emp.name??""));

  // ── Group by area ──
  const areaOrder = [...AREAS, "Outros"];
  const byArea = {};
  areaOrder.forEach(a => { byArea[a] = []; });
  rows.forEach(r => { const a = areaOrder.includes(r.area) ? r.area : "Outros"; byArea[a].push(r); });
  const activeAreas = areaOrder.filter(a => byArea[a].length > 0);

  const grandTotal = rows.reduce((s,r) => s + Math.max(0, r.totalPaid), 0);

  // ── Auto-save on blur ──
  function persistAll() {
    const newCfg = { ...(vtConfig ?? {}), [restaurantId]: { ...(vtConfig?.[restaurantId] ?? {}) } };
    Object.entries(localRates).forEach(([empId, rateStr]) => {
      newCfg[restaurantId][empId] = { dailyRate: fromBR(rateStr) };
    });
    onUpdate("vtConfig", newCfg);

    const newMonthly = { ...(vtMonthly ?? {}), [restaurantId]: { ...(vtMonthly?.[restaurantId] ?? {}), [mk]: {} } };
    Object.entries(localOverrides).forEach(([empId, v]) => {
      newMonthly[restaurantId][mk][empId] = { adjustOverride: v.adjustOverride ?? null, manualDiscount: v.manualDiscount ?? 0, manualBonus: v.manualBonus ?? 0 };
    });
    onUpdate("vtMonthly", newMonthly);
    setDirty(false);
    setVtSavedAt(Date.now()); // v8.1 — atualiza indicador de save
  }


  // ── Mark as paid (modal flow with date selector) ──
  function openPayModal() {
    setPayDate(today());
    setVersionWarning(null);
    setShowPayDateModal(true);
  }

  function checkVersionAndPay() {
    const chosenDate = payDate;
    const todayStr = today();
    if (chosenDate >= todayStr) {
      // Date is today or future — pay with current values, no version check needed
      confirmPay(chosenDate, false);
      return;
    }
    // Date is in the past — check scheduleVersions for edits between chosenDate and now
    const versions = scheduleVersions?.[restaurantId]?.[mk] ?? [];
    const editsAfterPayDate = versions.filter(v => v.ts && v.ts.slice(0, 10) > chosenDate);
    if (editsAfterPayDate.length > 0) {
      setVersionWarning({
        edits: editsAfterPayDate,
        payDateStr: chosenDate,
        oldestEditSnapshot: editsAfterPayDate[editsAfterPayDate.length - 1]?.snapshot ?? null,
      });
    } else {
      confirmPay(chosenDate, false);
    }
  }

  function confirmPay(chosenDate, useOldSnapshot) {
    if (dirty) persistAll();
    let snapshot;
    if (useOldSnapshot && versionWarning?.oldestEditSnapshot) {
      // Reconstruct rows from the version snapshot closest to the payment date
      const oldSched = versionWarning.oldestEditSnapshot;
      // Build rows using old schedule data but current VT config
      snapshot = rows.map(r => {
        const empDays = oldSched?.[r.emp.id] ? Object.values(oldSched[r.emp.id]).filter(s => s === "work" || s === null || s === undefined).length : r.plannedDays;
        const gross = round2(empDays * r.dailyRate);
        const total = round2(gross + r.autoAdjust + (r.manualBonus ?? 0) - (r.manualDiscount ?? 0));
        return {
          empId: r.emp.id, name: r.emp.name, role: r.role?.name ?? "—", area: r.area,
          dailyRate: r.dailyRate, plannedDays: empDays, grossVT: gross,
          autoAdjust: r.autoAdjust, manualBonus: r.manualBonus ?? 0, manualDiscount: r.manualDiscount, totalPaid: Math.max(0, total),
        };
      });
    } else {
      snapshot = rows.map(r => ({
        empId: r.emp.id, name: r.emp.name, role: r.role?.name ?? "—", area: r.area,
        dailyRate: r.dailyRate, plannedDays: r.plannedDays, grossVT: r.grossVT,
        autoAdjust: r.autoAdjust, manualBonus: r.manualBonus ?? 0, manualDiscount: r.manualDiscount, totalPaid: Math.max(0, r.totalPaid),
      }));
    }
    const paidAtISO = new Date(chosenDate + "T12:00:00").toISOString();
    const total = useOldSnapshot ? round2(snapshot.reduce((s, r) => s + r.totalPaid, 0)) : round2(grandTotal);
    const newPayments = { ...(vtPayments ?? {}), [restaurantId]: { ...(vtPayments?.[restaurantId] ?? {}), [mk]: { paidAt: paidAtISO, paidBy: currentUser?.name ?? "Gestor Adm.", snapshot, grandTotal: total } } };
    onUpdate("vtPayments", newPayments);
    // Freeze prevista when VT is paid
    if (!schedulePrevista?.[restaurantId]?.[mk]) {
      const frozenPrevista = JSON.parse(JSON.stringify(schedules?.[restaurantId]?.[mk] ?? {}));
      const newPrev = { ...(schedulePrevista ?? {}) };
      if (!newPrev[restaurantId]) newPrev[restaurantId] = {};
      newPrev[restaurantId][mk] = frozenPrevista;
      onUpdate("schedulePrevista", newPrev);
    }
    setShowPayDateModal(false);
    setVersionWarning(null);
    onUpdate("_toast", `💰 VT marcado como pago em ${chosenDate.split("-").reverse().join("/")}!`);
  }

  // ── Export CSV (grouped) ──
  function exportCSV() {
    const header = "Área;Empregado;Cargo;VT Diário;Dias;VT Bruto;Ajuste Mês Ant.;Ajuda de Custo;Desconto;Total";
    const csvRows = [];
    activeAreas.forEach(area => {
      const areaRows = byArea[area];
      areaRows.forEach(r => { csvRows.push(`${area};${r.emp.name};${r.role?.name??"—"};${fmtBR(r.dailyRate)};${r.plannedDays};${fmtBR(r.grossVT)};${fmtBR(r.autoAdjust)};${fmtBR(r.manualBonus ?? 0)};${fmtBR(r.manualDiscount)};${fmtBR(Math.max(0,r.totalPaid))}`); });
      const sub = areaRows.reduce((s,r) => s + Math.max(0, r.totalPaid), 0);
      csvRows.push(`${area};;;;;;;;Subtotal;${fmtBR(sub)}`);
    });
    csvRows.push(`;;;;;;;;;TOTAL;${fmtBR(grandTotal)}`);
    const blob = new Blob(["\uFEFF" + header + "\n" + csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `VT_${mk}.csv`; a.click(); URL.revokeObjectURL(url);
    onUpdate("_toast", "📊 CSV exportado");
  }

  // ── Export PDF (grouped) ──
  function exportPDF() {
    const w = window.open("", "_blank");
    if (!w) { onUpdate("_toast", "⚠️ Permita pop-ups para exportar PDF"); return; }
    const paidInfo = currentPayment ? `<p style="color:green;font-weight:700">✅ Pago em ${new Date(currentPayment.paidAt).toLocaleString("pt-BR")} por ${currentPayment.paidBy}</p>` : `<p style="color:#b45309;font-weight:700">⏳ Ainda não marcado como pago</p>`;
    let tableBody = "";
    activeAreas.forEach(area => {
      const color = AREA_COLORS[area] ?? "#666";
      tableBody += `<tr><td colspan="9" style="background:${color}18;color:${color};font-weight:700;padding:10px 12px;font-size:14px;border-left:4px solid ${color}">${area}</td></tr>`;
      byArea[area].forEach(r => {
        const bonusVal = r.manualBonus ?? 0;
        tableBody += `<tr><td>${r.emp.name}</td><td>${r.role?.name??"—"}</td><td style="text-align:right">${fmt(r.dailyRate)}</td><td style="text-align:center">${r.plannedDays}</td><td style="text-align:right">${fmt(r.grossVT)}</td><td style="text-align:right;color:${r.autoAdjust>=0?"green":"#b45309"}">${r.autoAdjust>=0?"+":""}${fmt(r.autoAdjust)}</td><td style="text-align:right;color:${bonusVal?"green":"#999"}">${bonusVal?"+"+fmt(bonusVal):"—"}</td><td style="text-align:right;color:#b45309">${r.manualDiscount?"-"+fmt(r.manualDiscount):"—"}</td><td style="text-align:right;font-weight:700">${fmt(Math.max(0,r.totalPaid))}</td></tr>`;
      });
      const sub = byArea[area].reduce((s,r) => s + Math.max(0, r.totalPaid), 0);
      tableBody += `<tr style="background:#f9f9f9"><td colspan="8" style="text-align:right;font-weight:600;font-size:12px;color:${color}">Subtotal ${area}</td><td style="text-align:right;font-weight:700;color:${color}">${fmt(sub)}</td></tr>`;
    });
    tableBody += `<tr style="font-weight:700;background:#e8e8e8"><td colspan="8" style="text-align:right;font-size:15px">TOTAL GERAL</td><td style="text-align:right;font-size:15px">${fmt(grandTotal)}</td></tr>`;
    w.document.write(`<!DOCTYPE html><html><head><title>VT ${mk}</title><style>body{font-family:Arial,sans-serif;margin:24px}table{border-collapse:collapse;width:100%}th,td{border:1px solid #ccc;padding:8px 12px;font-size:13px}th{background:#f5f5f5;font-weight:700}h1{font-size:20px}</style></head><body><h1>🚌 Vale Transporte — ${monthLabel(year,month)}</h1>${paidInfo}<table><thead><tr><th>Empregado</th><th>Cargo</th><th>VT Diário</th><th>Dias</th><th>VT Bruto</th><th>Ajuste Mês Ant.</th><th>Ajuda de Custo</th><th>Desconto</th><th>Total</th></tr></thead><tbody>${tableBody}</tbody></table><p style="font-size:11px;color:#999;margin-top:24px">Gerado em em ${new Date().toLocaleString("pt-BR")}</p></body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 500);
    onUpdate("_toast", "🖨️ PDF pronto para impressão");
  }

  // v8.12.6: goMonth removido — VT usa gate de seleção de mês via "↺ Trocar mês"
  // eslint-disable-next-line no-unused-vars
  const goMonth = (dir) => { const d = new Date(year, month + dir, 1); setYear(d.getFullYear()); setMonth(d.getMonth()); };

  // ── Render helpers ──
  // v7.2: width reduzida pra caber tudo sem scroll horizontal no desktop
  const inputStyle = { ...S.input, width: mobileOnly ? 80 : 80, textAlign: "right", padding: "6px 8px", fontSize: 13 };
  const cellPad = mobileOnly ? "8px 6px" : "9px 10px";

  // ── BR money input helpers (inline, not a component — avoids focus loss) ──
  const moneyOnChange = (e, setter, empId, field) => {
    const raw = e.target.value.replace(/[^0-9,.\u002D]/g, "");
    if (field === "rate") { setter(prev => ({ ...prev, [empId]: raw })); }
    else if (field === "adjust") { setter(prev => ({ ...prev, [empId]: { ...(prev[empId] ?? {}), adjustOverride: fromBR(raw), adjustDisplay: raw } })); }
    else if (field === "discount") { setter(prev => ({ ...prev, [empId]: { ...(prev[empId] ?? {}), manualDiscount: fromBR(raw), discountDisplay: raw } })); }
    else if (field === "bonus") { setter(prev => ({ ...prev, [empId]: { ...(prev[empId] ?? {}), manualBonus: fromBR(raw), bonusDisplay: raw } })); }
    setDirty(true);
  };
  const moneyOnBlur = (e, setter, empId, field) => {
    const n = fromBR(e.target.value);
    const formatted = n ? n.toFixed(2).replace(".", ",") : "";
    if (field === "rate") { setter(prev => ({ ...prev, [empId]: formatted })); }
    else if (field === "adjust") { setter(prev => ({ ...prev, [empId]: { ...(prev[empId] ?? {}), adjustOverride: n || null, adjustDisplay: formatted } })); }
    else if (field === "discount") { setter(prev => ({ ...prev, [empId]: { ...(prev[empId] ?? {}), manualDiscount: n || 0, discountDisplay: formatted } })); }
    else if (field === "bonus") { setter(prev => ({ ...prev, [empId]: { ...(prev[empId] ?? {}), manualBonus: n || 0, bonusDisplay: formatted } })); }
    if (dirty) persistAll();
  };

  // ── Area section renderer ──
  const renderAreaSection = (area, areaRows) => {
    const areaColor = AREA_COLORS[area] ?? "var(--text3)";
    const areaSubtotal = areaRows.reduce((s,r) => s + Math.max(0, r.totalPaid), 0);

    return (
      <div key={area} style={{ marginBottom: mobileOnly ? 12 : 20, marginRight: mobileOnly ? 0 : 8 }}>
        {/* Area header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, padding: mobileOnly ? "8px 12px" : "10px 24px", background: `${areaColor}12`, borderRadius: 12, borderLeft: `4px solid ${areaColor}` }}>
          <span style={{ color: areaColor, fontWeight: 700, fontSize: mobileOnly ? 14 : 16 }}>{area}</span>
          <span style={{ color: areaColor, fontWeight: 700, fontSize: mobileOnly ? 14 : 16, fontFamily: "'DM Mono',monospace" }}>{fmt(areaSubtotal)}</span>
        </div>

        {mobileOnly ? (
          /* ── MOBILE: cards ── */
          areaRows.map(r => {
            const adjustColor = r.autoAdjust > 0 ? "#047857" : r.autoAdjust < 0 ? "#b45309" : "var(--text3)";
            return (
              <div key={r.emp.id} style={{ ...S.card, marginBottom: 6, padding: "10px 12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div>
                    <div style={{ color: "var(--text)", fontWeight: 700, fontSize: 13 }}>{r.emp.name}</div>
                    <div style={{ color: "var(--text3)", fontSize: 10 }}>{r.role?.name ?? "—"} • {r.plannedDays} dias</div>
                  </div>
                  <div style={{ color: "var(--text)", fontWeight: 700, fontSize: 15, fontFamily: "'DM Mono',monospace" }}>{fmt(Math.max(0, r.totalPaid))}</div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                  <div>
                    <div style={{ color: "var(--text3)", fontSize: 9, marginBottom: 3 }}>VT Diário</div>
                    <input type="text" inputMode="decimal" placeholder="0,00" value={localRates[r.emp.id] ?? ""} onChange={e => moneyOnChange(e, setLocalRates, r.emp.id, "rate")} onBlur={e => moneyOnBlur(e, setLocalRates, r.emp.id, "rate")} style={{ ...inputStyle, width: "100%", padding: "5px 7px", fontSize: 12 }} />
                  </div>
                  <div>
                    <div style={{ color: "var(--text3)", fontSize: 9, marginBottom: 3 }}>Ajuste {r.suggestedAdjust !== 0 && <span style={{ color: adjustColor }}>({r.suggestedAdjust > 0 ? "+" : ""}{fmtBR(r.suggestedAdjust)})</span>}</div>
                    <input type="text" inputMode="decimal" placeholder="0,00" value={localOverrides[r.emp.id]?.adjustDisplay ?? toBR(r.autoAdjust)} onChange={e => moneyOnChange(e, setLocalOverrides, r.emp.id, "adjust")} onBlur={e => moneyOnBlur(e, setLocalOverrides, r.emp.id, "adjust")} style={{ ...inputStyle, width: "100%", padding: "5px 7px", fontSize: 12, color: adjustColor }} />
                  </div>
                  <div>
                    <div style={{ color: "var(--text3)", fontSize: 9, marginBottom: 3 }}>Ajuda de Custo</div>
                    <input type="text" inputMode="decimal" placeholder="0,00" value={localOverrides[r.emp.id]?.bonusDisplay ?? toBR(r.manualBonus)} onChange={e => moneyOnChange(e, setLocalOverrides, r.emp.id, "bonus")} onBlur={e => moneyOnBlur(e, setLocalOverrides, r.emp.id, "bonus")} style={{ ...inputStyle, width: "100%", padding: "5px 7px", fontSize: 12, color: (r.manualBonus ?? 0) > 0 ? "#047857" : "var(--text)" }} />
                  </div>
                  <div>
                    <div style={{ color: "var(--text3)", fontSize: 9, marginBottom: 3 }}>Desconto</div>
                    <input type="text" inputMode="decimal" placeholder="0,00" value={localOverrides[r.emp.id]?.discountDisplay ?? toBR(r.manualDiscount)} onChange={e => moneyOnChange(e, setLocalOverrides, r.emp.id, "discount")} onBlur={e => moneyOnBlur(e, setLocalOverrides, r.emp.id, "discount")} style={{ ...inputStyle, width: "100%", padding: "5px 7px", fontSize: 12 }} />
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          /* ── DESKTOP: table section ── */
          <div style={{ ...S.card, padding: "0 28px", overflow: "hidden", marginBottom: 4 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, tableLayout: "fixed" }}>
              <thead>
                <tr style={{ background: "var(--bg1)" }}>
                  <th style={{ padding: cellPad, textAlign: "left", color: "var(--text)", fontWeight: 700, borderBottom: "2px solid var(--border)" }}>Empregado</th>
                  <th style={{ padding: cellPad, textAlign: "left", color: "var(--text3)", fontWeight: 600, borderBottom: "2px solid var(--border)", width: "13%" }}>Cargo</th>
                  <th style={{ padding: cellPad, textAlign: "right", color: "var(--text3)", fontWeight: 600, borderBottom: "2px solid var(--border)", width: 86 }}>VT Diário</th>
                  <th style={{ padding: cellPad, textAlign: "center", color: "var(--text3)", fontWeight: 600, borderBottom: "2px solid var(--border)", width: 50 }}>Dias</th>
                  <th style={{ padding: cellPad, textAlign: "right", color: "var(--text3)", fontWeight: 600, borderBottom: "2px solid var(--border)", width: 85 }}>VT Bruto</th>
                  <th style={{ padding: cellPad, textAlign: "center", color: "var(--text3)", fontWeight: 600, borderBottom: "2px solid var(--border)", width: 60 }} title="Dias trabalhados no mês anterior (quando fechado)">Real ant.</th>
                  <th style={{ padding: cellPad, textAlign: "right", color: "var(--text3)", fontWeight: 600, borderBottom: "2px solid var(--border)", width: 100 }}>Ajuste</th>
                  <th style={{ padding: cellPad, textAlign: "right", color: "var(--text3)", fontWeight: 600, borderBottom: "2px solid var(--border)", width: 90 }} title="Pré-preenchido com o valor do mês anterior">Ajuda</th>
                  <th style={{ padding: cellPad, textAlign: "right", color: "var(--text3)", fontWeight: 600, borderBottom: "2px solid var(--border)", width: 86 }}>Desconto</th>
                  <th style={{ padding: cellPad, textAlign: "right", color: "var(--text)", fontWeight: 700, borderBottom: "2px solid var(--border)", width: 96 }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {areaRows.map((r, i) => {
                  const adjustColor = r.autoAdjust > 0 ? "#047857" : r.autoAdjust < 0 ? "#b45309" : "var(--text3)";
                  return (
                    <tr key={r.emp.id} style={{ borderBottom: i < areaRows.length - 1 ? "1px solid var(--border)" : "none" }}>
                      <td style={{ padding: cellPad, color: "var(--text)", fontWeight: 600 }}>{r.emp.name}</td>
                      <td style={{ padding: cellPad, color: "var(--text3)" }}>{r.role?.name ?? "—"}</td>
                      <td style={{ padding: cellPad, textAlign: "right" }}>
                        <input type="text" inputMode="decimal" placeholder="0,00" value={localRates[r.emp.id] ?? ""} onChange={e => moneyOnChange(e, setLocalRates, r.emp.id, "rate")} onBlur={e => moneyOnBlur(e, setLocalRates, r.emp.id, "rate")} style={inputStyle} />
                      </td>
                      <td style={{ padding: cellPad, textAlign: "center", color: "var(--text)", fontWeight: 600, fontFamily: "'DM Mono',monospace" }}>{r.plannedDays}</td>
                      <td style={{ padding: cellPad, textAlign: "right", color: "var(--text)", fontFamily: "'DM Mono',monospace" }}>{fmt(r.grossVT)}</td>
                      <td style={{ padding: cellPad, textAlign: "center", color: r.prevActualDays !== null ? "var(--text)" : "var(--text3)", fontFamily: "'DM Mono',monospace", fontSize: 13 }}>{r.prevActualDays !== null ? r.prevActualDays : "—"}</td>
                      <td style={{ padding: cellPad, textAlign: "right" }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                          <input type="text" inputMode="decimal" placeholder="0,00" value={localOverrides[r.emp.id]?.adjustDisplay ?? toBR(r.autoAdjust)} onChange={e => moneyOnChange(e, setLocalOverrides, r.emp.id, "adjust")} onBlur={e => moneyOnBlur(e, setLocalOverrides, r.emp.id, "adjust")} style={{ ...inputStyle, color: adjustColor }} />
                          {r.suggestedAdjust !== 0 && r.suggestedAdjust !== r.autoAdjust && (
                            <span style={{ fontSize: 9, color: "var(--text3)" }} title="Sugerido pelo sistema com base no mês anterior">sug: {r.suggestedAdjust > 0 ? "+" : ""}{fmtBR(r.suggestedAdjust)}</span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: cellPad, textAlign: "right" }}>
                        <input type="text" inputMode="decimal" placeholder="0,00" value={localOverrides[r.emp.id]?.bonusDisplay ?? toBR(r.manualBonus)} onChange={e => moneyOnChange(e, setLocalOverrides, r.emp.id, "bonus")} onBlur={e => moneyOnBlur(e, setLocalOverrides, r.emp.id, "bonus")} style={{ ...inputStyle, color: (r.manualBonus ?? 0) > 0 ? "#047857" : "var(--text)" }} title="Pré-preenchido com o valor do mês anterior — edite se necessário" />
                      </td>
                      <td style={{ padding: cellPad, textAlign: "right" }}>
                        <input type="text" inputMode="decimal" placeholder="0,00" value={localOverrides[r.emp.id]?.discountDisplay ?? toBR(r.manualDiscount)} onChange={e => moneyOnChange(e, setLocalOverrides, r.emp.id, "discount")} onBlur={e => moneyOnBlur(e, setLocalOverrides, r.emp.id, "discount")} style={inputStyle} />
                      </td>
                      <td style={{ padding: cellPad, textAlign: "right", fontWeight: 700, fontSize: 15, fontFamily: "'DM Mono',monospace", color: r.totalPaid < 0 ? "var(--red)" : "var(--text)" }}>{fmt(Math.max(0, r.totalPaid))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // v8.4 — gate de mês: força confirmar antes de ver os dados
  if (!vtMonthConfirmed) {
    return (
      <MonthPickerGate
        contextLabel="Vale Transporte"
        year={year} month={month}
        scheduleStatus={scheduleStatus}
        restaurantId={restaurantId}
        onPick={(y, m) => { setYear(y); setMonth(m); setVtMonthConfirmed(true); onUpdate("_toast", `🚌 VT: trabalhando em ${monthLabel(y, m)}`); }}
        onCancel={onExit}
      />
    );
  }

  return (
    <div>
      {/* v8.12.6: header com mês no título + Trocar mês — sem ◀ ▶ navegação inline */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <h3 style={{ color: "var(--text)", margin: 0, fontSize: mobileOnly ? 16 : 20, textTransform: "capitalize" }}>
            🚌 Vale Transporte — {monthLabel(year, month)}
          </h3>
          <SaveIndicator mode="auto" pending={dirty} savedAt={vtSavedAt} compact />
          <button onClick={()=>setVtMonthConfirmed(false)} style={{ ...S.btnSecondary, fontSize: 11, padding: "3px 10px" }} title="Voltar pra seleção de mês">↺ Trocar mês</button>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={exportCSV} style={{ ...S.btnSecondary, fontSize: 12, padding: "6px 14px" }}>📊 CSV</button>
          <button onClick={exportPDF} style={{ ...S.btnSecondary, fontSize: 12, padding: "6px 14px" }}>🖨️ PDF</button>
        </div>
      </div>

      {/* Payment status */}
      {currentPayment && (
        <div style={{ ...S.card, background: "#10b98118", borderColor: "#10b98133", marginBottom: 16, padding: "14px 20px" }}>
          <div style={{ color: "#047857", fontWeight: 700, fontSize: 14 }}>✅ Pago em {new Date(currentPayment.paidAt).toLocaleString("pt-BR")} por {currentPayment.paidBy}</div>
          <div style={{ color: "#047857", fontSize: 12, marginTop: 4 }}>Total pago: {fmt(currentPayment.grandTotal)}</div>
        </div>
      )}

      {prevPayment && (
        <div style={{ ...S.card, background: "#3b82f618", borderColor: "#3b82f633", marginBottom: 16, padding: "12px 20px" }}>
          <div style={{ color: "#2563eb", fontWeight: 600, fontSize: 13 }}>ℹ️ Ajustes calculados a partir do pagamento de {new Date(prevPayment.paidAt).toLocaleDateString("pt-BR")}</div>
        </div>
      )}
      {prevPayment && !(scheduleStatus?.[restaurantId]?.[prevMk]?.status === "closed") && (
        <div style={{background:"#f59e0b15",border:"1px solid #f59e0b44",borderRadius:10,padding:"10px 16px",marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:16}}>{"⚠️"}</span>
          <span style={{color:"#f59e0b",fontSize:12}}>
            {prevMk} ainda não fechado — o ajuste automático de {prevMk} será aplicado quando o mês for fechado.
          </span>
        </div>
      )}
      {!prevPayment && (
        <div style={{ ...S.card, background: "#f59e0b18", borderColor: "#f59e0b33", marginBottom: 16, padding: "12px 20px" }}>
          <div style={{ color: "#b45309", fontWeight: 600, fontSize: 13 }}>⚠️ Mês anterior sem pagamento registrado — ajustes automáticos indisponíveis</div>
        </div>
      )}

      {/* Area sections */}
      {activeAreas.map(area => renderAreaSection(area, byArea[area]))}

      {/* Grand total */}
      <div style={{ ...S.card, background: "var(--bg1)", padding: mobileOnly ? "14px 16px" : "16px 24px", marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ color: "var(--text)", fontWeight: 700, fontSize: mobileOnly ? 14 : 16 }}>TOTAL GERAL</span>
          <span style={{ color: ac, fontWeight: 700, fontSize: mobileOnly ? 20 : 24, fontFamily: "'DM Mono',monospace" }}>{fmt(grandTotal)}</span>
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
        <button onClick={openPayModal} style={{ ...S.btn, background: currentPayment ? "#10b981" : "#2563eb", color: "#fff", fontWeight: 700, padding: "12px 24px", fontSize: 14 }}>
          {currentPayment ? "🔄 Remarcar como Pago" : "💰 Marcar como Pago"}
        </button>
      </div>

      {restEmps.length === 0 && (
        <div style={{ textAlign: "center", padding: 40, color: "var(--text3)" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🚌</div>
          <div style={{ fontSize: 15 }}>Nenhum empregado cadastrado</div>
        </div>
      )}

      {/* ── Modal: selecionar data do pagamento ── */}
      {showPayDateModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => { setShowPayDateModal(false); setVersionWarning(null); }}>
          <div style={{ background: "var(--bg)", borderRadius: 16, padding: 28, maxWidth: 440, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }} onClick={e => e.stopPropagation()}>
            {!versionWarning ? (
              <>
                <h3 style={{ margin: "0 0 6px", color: "var(--text)", fontSize: 18 }}>💰 Marcar como Pago</h3>
                <p style={{ color: "var(--text2)", fontSize: 13, margin: "0 0 18px", lineHeight: 1.5 }}>Os valores serão congelados como referência para o cálculo de ajuste do próximo mês.</p>
                <label style={{ display: "block", color: "var(--text2)", fontSize: 13, marginBottom: 6 }}>Data do pagamento:</label>
                <input type="date" value={payDate} onChange={e => setPayDate(e.target.value)} style={{ ...S.input, fontSize: 15, padding: "10px 14px", marginBottom: 18, width: "100%", boxSizing: "border-box" }} />
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button onClick={() => { setShowPayDateModal(false); setVersionWarning(null); }} style={{ ...S.btn, padding: "10px 20px" }}>Cancelar</button>
                  <button onClick={checkVersionAndPay} style={{ ...S.btn, background: "#2563eb", color: "#fff", fontWeight: 700, padding: "10px 20px" }}>Confirmar</button>
                </div>
              </>
            ) : (
              <>
                <h3 style={{ margin: "0 0 6px", color: "#b45309", fontSize: 18 }}>⚠️ Edições encontradas após a data</h3>
                <p style={{ color: "var(--text2)", fontSize: 13, margin: "0 0 12px", lineHeight: 1.5 }}>
                  A escala foi editada <strong>{versionWarning.edits.length}×</strong> após {versionWarning.payDateStr.split("-").reverse().join("/")}. Qual versão dos valores deseja usar?
                </p>
                <div style={{ background: "var(--bg1)", borderRadius: 10, padding: 12, marginBottom: 16, maxHeight: 150, overflowY: "auto", fontSize: 12, color: "var(--text2)" }}>
                  {versionWarning.edits.map((e, i) => (
                    <div key={i} style={{ padding: "4px 0", borderBottom: i < versionWarning.edits.length - 1 ? "1px solid var(--border)" : "none" }}>
                      📝 {new Date(e.ts).toLocaleString("pt-BR")} — {e.author} ({e.reason})
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <button onClick={() => confirmPay(versionWarning.payDateStr, true)} style={{ ...S.btn, background: "#f59e0b", color: "#fff", fontWeight: 700, padding: "12px 16px", fontSize: 13, textAlign: "left" }}>
                    📋 Usar valores da data do pagamento ({versionWarning.payDateStr.split("-").reverse().join("/")})
                  </button>
                  <button onClick={() => confirmPay(versionWarning.payDateStr, false)} style={{ ...S.btn, background: "#2563eb", color: "#fff", fontWeight: 700, padding: "12px 16px", fontSize: 13, textAlign: "left" }}>
                    📊 Usar valores atuais (com as edições recentes)
                  </button>
                  <button onClick={() => { setVersionWarning(null); }} style={{ ...S.btn, padding: "10px 16px", fontSize: 13 }}>← Voltar</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TRILHA DO EMPREGADO — Components
// ═══════════════════════════════════════════════════════════════════

const INCIDENT_TYPES = [
  { id:"advertencia_verbal", label:"Advertência verbal", negative:true },
  { id:"advertencia_escrita", label:"Advertência escrita", negative:true },
  { id:"desentendimento", label:"Desentendimento", negative:true },
  { id:"dano_material", label:"Dano material", negative:true },
  { id:"indisciplina", label:"Indisciplina", negative:true },
  { id:"elogio_formal", label:"Elogio formal", negative:false },
  { id:"destaque_positivo", label:"Destaque positivo", negative:false },
  { id:"outro", label:"Outro", negative:true },
];
const SEVERITY_OPTIONS = [
  { id:"leve", label:"Leve", color:"#f59e0b" },
  { id:"media", label:"Média", color:"#f97316" },
  { id:"grave", label:"Grave", color:"#e74c3c" },
];

// EmpTimeline removed — replaced by interactive Jornada in trilha tab


function IncidentForm({ restaurantId, employees, onUpdate, incidents, currentUser, isOwner, preSelectedEmpId }) { // eslint-disable-line no-unused-vars
  const restEmps = employees.filter(e => e.restaurantId === restaurantId && !(e.inactive && e.inactiveFrom && e.inactiveFrom <= today()));
  const [selectedEmps, setSelectedEmps] = useState(preSelectedEmpId ? [preSelectedEmpId] : []);
  const [type, setType] = useState("");
  const [severity, setSeverity] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(today());

  const typeObj = INCIDENT_TYPES.find(t => t.id === type);
  const needsSeverity = typeObj?.negative ?? true;

  function handleSubmit() {
    if (selectedEmps.length === 0) { window.alert("Selecione ao menos um empregado."); return; }
    if (!type) { window.alert("Selecione o tipo de ocorrência."); return; }
    if (needsSeverity && !severity) { window.alert("Selecione a gravidade."); return; }
    if (!description.trim()) { window.alert("Descreva a ocorrência."); return; }
    const inc = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
      restaurantId,
      employeeIds: selectedEmps,
      type,
      severity: needsSeverity ? severity : null,
      description: description.trim(),
      date,
      createdAt: new Date().toISOString(),
      createdBy: isOwner ? "Master" : (currentUser?.name ?? "Gestor Adm."),
      createdById: currentUser?.id ?? null,
      visibility: "internal",
    };
    onUpdate("incidents", [...(incidents ?? []), inc]);
    setSelectedEmps([]); setType(""); setSeverity(""); setDescription(""); setDate(today());
  }

  const formBorderColor = typeObj ? (typeObj.negative ? "#e74c3c33" : "#10b98133") : "var(--border)";
  const formBgColor = typeObj ? (typeObj.negative ? "#e74c3c06" : "#10b98106") : undefined;
  const formHeaderColor = typeObj ? (typeObj.negative ? "var(--red)" : "var(--green)") : "var(--text)";
  const formTitle = typeObj ? (typeObj.negative ? "🚨 Registrar ocorrência negativa" : "🌟 Registrar ocorrência positiva") : "Registrar ocorrência";

  return (
    <div style={{...S.card, padding:"18px 20px", border:`1px solid ${formBorderColor}`, background:formBgColor, transition:"all 0.2s"}}>
      <h4 style={{color:formHeaderColor,margin:"0 0 14px",fontSize:15,fontWeight:700}}>{formTitle}</h4>
      <div style={{marginBottom:12}}>
        <label style={S.label}>Empregados envolvidos</label>
        <div style={{display:"flex",flexWrap:"wrap",gap:6,maxHeight:120,overflowY:"auto",padding:4}}>
          {restEmps.map(emp => (
            <label key={emp.id} style={{display:"flex",alignItems:"center",gap:4,fontSize:12,color:"var(--text2)",cursor:"pointer",padding:"4px 8px",borderRadius:6,border:`1px solid ${selectedEmps.includes(emp.id)?"var(--ac)":"var(--border)"}`,background:selectedEmps.includes(emp.id)?"var(--ac-bg,#d4a01711)":"transparent"}}>
              <input type="checkbox" checked={selectedEmps.includes(emp.id)} onChange={()=>setSelectedEmps(prev=>prev.includes(emp.id)?prev.filter(x=>x!==emp.id):[...prev,emp.id])} style={{accentColor:"var(--ac)"}}/>
              {emp.name}
            </label>
          ))}
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
        <div>
          <label style={S.label}>Tipo</label>
          <select value={type} onChange={e=>setType(e.target.value)} style={S.input}>
            <option value="">Selecionar...</option>
            <optgroup label="⚠️ Negativas">
              {INCIDENT_TYPES.filter(t=>t.negative).map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </optgroup>
            <optgroup label="🌟 Positivas">
              {INCIDENT_TYPES.filter(t=>!t.negative).map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </optgroup>
          </select>
        </div>
        <div>
          <label style={S.label}>Data</label>
          <input type="date" value={date} onChange={e=>setDate(e.target.value)} style={S.input}/>
        </div>
      </div>
      {needsSeverity && type && (
        <div style={{marginBottom:12}}>
          <label style={S.label}>Gravidade</label>
          <div style={{display:"flex",gap:8}}>
            {SEVERITY_OPTIONS.map(s => (
              <button key={s.id} onClick={()=>setSeverity(s.id)} style={{flex:1,padding:"8px",borderRadius:8,border:`1px solid ${severity===s.id?s.color:s.color+"44"}`,background:severity===s.id?s.color+"22":"transparent",color:s.color,cursor:"pointer",fontWeight:severity===s.id?700:500,fontSize:13,fontFamily:"'DM Sans',sans-serif"}}>{s.label}</button>
            ))}
          </div>
        </div>
      )}
      <div style={{marginBottom:14}}>
        <label style={S.label}>Descrição</label>
        <textarea value={description} onChange={e=>setDescription(e.target.value)} rows={3} placeholder="Descreva a ocorrência em detalhes..." style={{...S.input,resize:"vertical"}}/>
      </div>
      <button onClick={handleSubmit} style={{...S.btnPrimary,width:"auto",padding:"10px 24px",background:typeObj?(typeObj.negative?"var(--red)":"var(--green)"):undefined}}>
        {typeObj ? (typeObj.negative ? "🚨 Registrar ocorrência" : "🌟 Registrar elogio") : "Registrar ocorrência"}
      </button>
    </div>
  );
}

const RATING_LABELS = ["Abaixo do esperado","Em desenvolvimento","Atende","Supera","Excepcional"];
const RATING_COLORS = ["#ef4444","#f97316","#eab308","#3b82f6","#10b981"];

const GOAL_TYPES = [
  { value: "manter", label: "Manter no cargo", icon: "🏠", color: "#10b981" },
  { value: "subir",  label: "Subir de cargo",  icon: "⬆️", color: "#3b82f6" },
  { value: "conhecimento", label: "Aprofundar conhecimento", icon: "📚", color: "#8b5cf6" },
  { value: "personalizado", label: "Personalizado", icon: "✏️", color: "#f59e0b" },
];
const MATERIAL_TYPES = [
  { value: "livro", label: "Livro", icon: "📖" },
  { value: "video", label: "Vídeo", icon: "🎬" },
  { value: "curso", label: "Curso", icon: "🎓" },
  { value: "pratica", label: "Prática", icon: "🔧" },
  { value: "link", label: "Link", icon: "🔗" },
];

const AUTO_META_RULES = [
  { id: "zero_faltas_inj", label: "0 faltas injustificadas no mês", check: (empId, schedules, rid) => {
    const now = new Date();
    const mk = monthKey(now.getFullYear(), now.getMonth());
    const days = schedules?.[rid]?.[mk]?.[empId] ?? {};
    return Object.values(days).filter(s => s === DAY_FAULT_U).length === 0;
  }},
  { id: "zero_faltas_tot", label: "0 faltas (todas) no mês", check: (empId, schedules, rid) => {
    const now = new Date();
    const mk = monthKey(now.getFullYear(), now.getMonth());
    const days = schedules?.[rid]?.[mk]?.[empId] ?? {};
    return Object.values(days).filter(s => s === DAY_FAULT_U || s === DAY_FAULT_J).length === 0;
  }},
  { id: "feedback_excepcional", label: "Avaliação Excepcional no último feedback", check: (empId, _s, _r, feedbacks, rid) => {
    const myFbs = (feedbacks ?? []).filter(f => f.employeeId === empId && f.restaurantId === rid && !f.deletedAt).sort((a,b)=>(b.createdAt??"").localeCompare(a.createdAt??""));
    return myFbs[0]?.rating === 5;
  }},
  { id: "90_dias", label: "90 dias na empresa", check: (empId, _s, _r, _f, _rid, employees) => {
    const emp = (employees ?? []).find(e => e.id === empId);
    if (!emp?.admission) return false;
    return Math.floor((new Date() - new Date(emp.admission+"T12:00:00")) / 86400000) >= 90;
  }},
];

function GoalsManager({ empId, employeeGoals, roles, restaurantId, onUpdate, currentUser, isOwner, schedules, feedbacks, employees }) {
  const [showForm, setShowForm] = useState(false);
  const [goalType, setGoalType] = useState("manter");
  const [goalTitle, setGoalTitle] = useState("");
  const [goalTopic, setGoalTopic] = useState("");
  const [goalTargetRoleId, setGoalTargetRoleId] = useState("");
  const [showMaterialForm, setShowMaterialForm] = useState(null); // goalId
  const [matTitle, setMatTitle] = useState("");
  const [matLink, setMatLink] = useState("");
  const [matType, setMatType] = useState("link");
  const [showMetaForm, setShowMetaForm] = useState(null); // goalId
  const [metaTitle, setMetaTitle] = useState("");
  const [metaAutoRule, setMetaAutoRule] = useState("");

  const restRoles = (roles ?? []).filter(r => r.restaurantId === restaurantId && !r.inactive);
  const goals = (employeeGoals?.[empId] ?? []).filter(g => g.status !== "cancelled");
  const activeGoals = goals.filter(g => g.status === "active");
  const completedGoals = goals.filter(g => g.status === "completed");

  function saveGoals(newGoals) {
    const updated = { ...(employeeGoals ?? {}), [empId]: newGoals };
    onUpdate("employeeGoals", updated);
  }

  function addGoal() {
    let title = "";
    if (goalType === "manter") title = "Manter no cargo";
    else if (goalType === "subir") {
      const tRole = restRoles.find(r => r.id === goalTargetRoleId);
      title = tRole ? `Subir para ${tRole.name}` : "Subir de cargo";
    } else if (goalType === "conhecimento") title = goalTopic.trim() || "Aprofundar conhecimento";
    else title = goalTitle.trim() || "Objetivo personalizado";

    const newGoal = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
      type: goalType,
      title,
      targetRoleId: goalType === "subir" ? goalTargetRoleId || null : null,
      topic: goalType === "conhecimento" ? goalTopic.trim() : null,
      materials: [],
      metas: [],
      createdAt: new Date().toISOString(),
      createdBy: isOwner ? "Master" : (currentUser?.name ?? "Gestor Adm."),
      status: "active",
    };
    saveGoals([...(employeeGoals?.[empId] ?? []), newGoal]);
    setShowForm(false); setGoalTitle(""); setGoalTopic(""); setGoalTargetRoleId("");
  }

  function toggleGoalStatus(goalId) {
    const all = [...(employeeGoals?.[empId] ?? [])];
    const idx = all.findIndex(g => g.id === goalId);
    if (idx < 0) return;
    all[idx] = { ...all[idx], status: all[idx].status === "active" ? "completed" : "active" };
    saveGoals(all);
  }

  async function removeGoal(goalId) {
    if (!await appConfirm("Remover este objetivo?")) return;
    const all = [...(employeeGoals?.[empId] ?? [])];
    const idx = all.findIndex(g => g.id === goalId);
    if (idx < 0) return;
    all[idx] = { ...all[idx], status: "cancelled" };
    saveGoals(all);
  }

  function addMaterial(goalId) {
    if (!matTitle.trim()) return;
    const all = [...(employeeGoals?.[empId] ?? [])];
    const idx = all.findIndex(g => g.id === goalId);
    if (idx < 0) return;
    const mat = { id: `m${Date.now()}`, title: matTitle.trim(), link: matLink.trim() || null, type: matType };
    all[idx] = { ...all[idx], materials: [...(all[idx].materials ?? []), mat] };
    saveGoals(all);
    setMatTitle(""); setMatLink(""); setShowMaterialForm(null);
  }

  function removeMaterial(goalId, matId) {
    const all = [...(employeeGoals?.[empId] ?? [])];
    const idx = all.findIndex(g => g.id === goalId);
    if (idx < 0) return;
    all[idx] = { ...all[idx], materials: (all[idx].materials ?? []).filter(m => m.id !== matId) };
    saveGoals(all);
  }

  function addMeta(goalId) {
    if (metaAutoRule) {
      const rule = AUTO_META_RULES.find(r => r.id === metaAutoRule);
      if (!rule) return;
      const meta = { id: `t${Date.now()}`, title: rule.label, done: false, doneAt: null, doneBy: null, autoCheck: true, autoCheckRule: metaAutoRule };
      const all = [...(employeeGoals?.[empId] ?? [])];
      const idx = all.findIndex(g => g.id === goalId);
      if (idx < 0) return;
      all[idx] = { ...all[idx], metas: [...(all[idx].metas ?? []), meta] };
      saveGoals(all);
      setMetaAutoRule(""); setShowMetaForm(null);
      return;
    }
    if (!metaTitle.trim()) return;
    const all = [...(employeeGoals?.[empId] ?? [])];
    const idx = all.findIndex(g => g.id === goalId);
    if (idx < 0) return;
    const meta = { id: `t${Date.now()}`, title: metaTitle.trim(), done: false, doneAt: null, doneBy: null, autoCheck: false, autoCheckRule: null };
    all[idx] = { ...all[idx], metas: [...(all[idx].metas ?? []), meta] };
    saveGoals(all);
    setMetaTitle(""); setMetaAutoRule(""); setShowMetaForm(null);
  }

  function toggleMeta(goalId, metaId) {
    const all = [...(employeeGoals?.[empId] ?? [])];
    const idx = all.findIndex(g => g.id === goalId);
    if (idx < 0) return;
    all[idx] = { ...all[idx], metas: (all[idx].metas ?? []).map(m =>
      m.id === metaId ? { ...m, done: !m.done, doneAt: !m.done ? new Date().toISOString() : null, doneBy: !m.done ? (currentUser?.name ?? "Gestor Adm.") : null } : m
    )};
    saveGoals(all);
  }


  function removeMeta(goalId, metaId) {
    const all = [...(employeeGoals?.[empId] ?? [])];
    const idx = all.findIndex(g => g.id === goalId);
    if (idx < 0) return;
    all[idx] = { ...all[idx], metas: (all[idx].metas ?? []).filter(m => m.id !== metaId) };
    saveGoals(all);
  }

  function renderGoalCard(goal) {
    const typeInfo = GOAL_TYPES.find(t => t.value === goal.type) ?? GOAL_TYPES[3];
    const metas = goal.metas ?? [];
    const doneMetas = metas.filter(m => {
      if (m.autoCheck && m.autoCheckRule) {
        const rule = AUTO_META_RULES.find(r => r.id === m.autoCheckRule);
        return rule ? rule.check(empId, schedules, restaurantId, feedbacks, restaurantId, employees) : m.done;
      }
      return m.done;
    }).length;
    const progressPct = metas.length > 0 ? Math.round((doneMetas / metas.length) * 100) : 0;
    const isCompleted = goal.status === "completed";

    return (
      <div key={goal.id} style={{...S.card, marginBottom: 12, padding: "14px 16px", opacity: isCompleted ? 0.7 : 1, borderLeft: `4px solid ${typeInfo.color}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
              <span style={{fontSize:16}}>{typeInfo.icon}</span>
              <span style={{color:"var(--text)",fontWeight:700,fontSize:14}}>{goal.title}</span>
              {isCompleted && <span style={{fontSize:10,background:"#10b98122",color:"#10b981",padding:"2px 8px",borderRadius:8,fontWeight:600}}>Concluído</span>}
            </div>
            <div style={{color:"var(--text3)",fontSize:11}}>
              {typeInfo.label} · Criado em {new Date(goal.createdAt).toLocaleDateString("pt-BR")} por {goal.createdBy}
            </div>
          </div>
          <div style={{display:"flex",gap:4}}>
            <button onClick={()=>toggleGoalStatus(goal.id)} title={isCompleted?"Reabrir":"Concluir"} style={{background:"none",border:"none",cursor:"pointer",fontSize:16,padding:2}}>{isCompleted?"🔄":"✅"}</button>
            <button onClick={()=>removeGoal(goal.id)} title="Remover" style={{background:"none",border:"none",cursor:"pointer",fontSize:16,padding:2}}>🗑️</button>
          </div>
        </div>

        {/* Progress bar */}
        {metas.length > 0 && (
          <div style={{marginBottom:10}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
              <span style={{color:"var(--text3)",fontSize:11}}>Progresso</span>
              <span style={{color:typeInfo.color,fontSize:11,fontWeight:700}}>{doneMetas}/{metas.length} ({progressPct}%)</span>
            </div>
            <div style={{height:6,borderRadius:3,background:"var(--border)",overflow:"hidden"}}>
              <div style={{height:"100%",borderRadius:3,background:typeInfo.color,width:`${progressPct}%`,transition:"width 0.3s"}}/>
            </div>
          </div>
        )}

        {/* Metas checklist */}
        {metas.length > 0 && (
          <div style={{marginBottom:8}}>
            {metas.map(meta => {
              const isAuto = meta.autoCheck && meta.autoCheckRule;
              const autoRule = isAuto ? AUTO_META_RULES.find(r => r.id === meta.autoCheckRule) : null;
              const autoResult = autoRule ? autoRule.check(empId, schedules, restaurantId, feedbacks, restaurantId, []) : false;
              const isDone = isAuto ? autoResult : meta.done;
              return (
              <div key={meta.id} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 0",borderBottom:"1px solid var(--border)22",flexWrap:"wrap"}}>
                {isAuto ? (
                  <span style={{fontSize:14,padding:0,lineHeight:1}}>{isDone?"✅":"⏳"}</span>
                ) : (
                  <button onClick={()=>toggleMeta(goal.id, meta.id)} title="Confirmação do gestor" style={{background:"none",border:"none",cursor:"pointer",fontSize:14,padding:0,lineHeight:1}}>{isDone?"☑️":"⬜"}</button>
                )}
                <span style={{flex:1,color:isDone?"var(--text3)":"var(--text)",fontSize:12,textDecoration:isDone?"line-through":"none"}}>{meta.title}</span>
                {isAuto && <span style={{fontSize:9,color:"#8b5cf6",background:"#8b5cf611",padding:"1px 6px",borderRadius:4}}>auto</span>}
                {!isAuto && meta.employeeMarked && !isDone && <span style={{fontSize:9,color:"#f59e0b",background:"#f59e0b11",padding:"1px 6px",borderRadius:4}}>empregado concluiu em {meta.employeeDoneAt ? new Date(meta.employeeDoneAt).toLocaleDateString("pt-BR") : "—"}</span>}
                {!isAuto && isDone && meta.doneAt && <span style={{color:"var(--text3)",fontSize:9}}>✓ {new Date(meta.doneAt).toLocaleDateString("pt-BR")}</span>}
                <button onClick={()=>removeMeta(goal.id, meta.id)} style={{background:"none",border:"none",cursor:"pointer",fontSize:10,color:"var(--text3)",padding:0}}>✕</button>
              </div>
              );
            })}
          </div>
        )}
        {showMetaForm === goal.id ? (
          <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:8}}>
            <div style={{display:"flex",gap:6}}>
              <input value={metaTitle} onChange={e=>{setMetaTitle(e.target.value);setMetaAutoRule("");}} placeholder="Meta manual..." style={{...S.input,flex:1,fontSize:12,padding:"6px 10px"}} onKeyDown={e=>e.key==="Enter"&&addMeta(goal.id)}/>
              <button onClick={()=>addMeta(goal.id)} style={{...S.btn,fontSize:11,padding:"6px 12px",background:typeInfo.color,color:"#fff",fontWeight:600}}>+</button>
              <button onClick={()=>{setShowMetaForm(null);setMetaTitle("");setMetaAutoRule("");}} style={{...S.btn,fontSize:11,padding:"6px 10px"}}>✕</button>
            </div>
            <div style={{display:"flex",gap:6,alignItems:"center"}}>
              <span style={{fontSize:10,color:"#8b5cf6",fontWeight:600,whiteSpace:"nowrap"}}>ou automática:</span>
              <select value={metaAutoRule} onChange={e=>{setMetaAutoRule(e.target.value);if(e.target.value)setMetaTitle("");}} style={{...S.input,flex:1,fontSize:11,padding:"5px 8px"}}>
                <option value="">Selecionar regra...</option>
                {AUTO_META_RULES.map(r=><option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
              {metaAutoRule && <button onClick={()=>addMeta(goal.id)} style={{...S.btn,fontSize:11,padding:"5px 10px",background:"#8b5cf6",color:"#fff",fontWeight:600}}>+</button>}
            </div>
          </div>
        ) : (
          <button onClick={()=>{setShowMetaForm(goal.id);setMetaTitle("");setMetaAutoRule("");}} style={{background:"none",border:"none",color:typeInfo.color,cursor:"pointer",fontSize:11,fontWeight:600,padding:"2px 0",marginBottom:6}}>+ Adicionar meta</button>
        )}

        {/* Materials */}
        {(goal.materials ?? []).length > 0 && (
          <div style={{marginBottom:6}}>
            <div style={{color:"var(--text3)",fontSize:10,fontWeight:600,marginBottom:4}}>📎 Material de apoio</div>
            {goal.materials.map(mat => {
              const mtIcon = MATERIAL_TYPES.find(t=>t.value===mat.type)?.icon ?? "📋";
              return (
                <div key={mat.id} style={{display:"flex",alignItems:"center",gap:6,padding:"3px 0",fontSize:12}}>
                  <span>{mtIcon}</span>
                  {mat.link ? <a href={mat.link} target="_blank" rel="noreferrer" style={{color:"#3b82f6",flex:1}}>{mat.title}</a> : <span style={{color:"var(--text)",flex:1}}>{mat.title}</span>}
                  <button onClick={()=>removeMaterial(goal.id, mat.id)} style={{background:"none",border:"none",cursor:"pointer",fontSize:10,color:"var(--text3)",padding:0}}>✕</button>
                </div>
              );
            })}
          </div>
        )}
        {showMaterialForm === goal.id ? (
          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:4}}>
            <select value={matType} onChange={e=>setMatType(e.target.value)} style={{...S.input,width:90,fontSize:11,padding:"5px 8px"}}>
              {MATERIAL_TYPES.map(t=><option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
            </select>
            <input value={matTitle} onChange={e=>setMatTitle(e.target.value)} placeholder="Título" style={{...S.input,flex:1,fontSize:11,padding:"5px 8px",minWidth:100}}/>
            <input value={matLink} onChange={e=>setMatLink(e.target.value)} placeholder="Link (opcional)" style={{...S.input,flex:1,fontSize:11,padding:"5px 8px",minWidth:100}}/>
            <button onClick={()=>addMaterial(goal.id)} style={{...S.btn,fontSize:11,padding:"5px 10px",background:typeInfo.color,color:"#fff",fontWeight:600}}>+</button>
            <button onClick={()=>{setShowMaterialForm(null);setMatTitle("");setMatLink("");}} style={{...S.btn,fontSize:11,padding:"5px 10px"}}>✕</button>
          </div>
        ) : (
          <button onClick={()=>{setShowMaterialForm(goal.id);setMatTitle("");setMatLink("");}} style={{background:"none",border:"none",color:"var(--text3)",cursor:"pointer",fontSize:11,padding:"2px 0"}}>+ Material de apoio</button>
        )}
      </div>
    );
  }

  return (
    <div style={{marginBottom:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <h4 style={{color:"var(--text)",margin:0,fontSize:15,fontWeight:700}}>🎯 Objetivos</h4>
        <button onClick={()=>setShowForm(!showForm)} style={{...S.btn,fontSize:12,padding:"6px 14px",background:showForm?"var(--accent)11":"transparent",color:showForm?"var(--accent)":"var(--text3)",border:`1px solid ${showForm?"var(--accent)":"var(--border)"}`}}>
          {showForm ? "✕ Fechar" : "+ Novo objetivo"}
        </button>
      </div>

      {showForm && (
        <div style={{...S.card,padding:"16px",marginBottom:12}}>
          <label style={S.label}>Tipo de objetivo</label>
          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:12}}>
            {GOAL_TYPES.map(t => (
              <button key={t.value} onClick={()=>setGoalType(t.value)} style={{
                padding:"8px 14px",fontSize:12,fontWeight:goalType===t.value?700:500,
                border:`2px solid ${goalType===t.value?t.color:"var(--border)"}`,borderRadius:8,cursor:"pointer",
                background:goalType===t.value?t.color+"18":"var(--bg)",color:goalType===t.value?t.color:"var(--text2)",
              }}>{t.icon} {t.label}</button>
            ))}
          </div>

          {goalType === "subir" && (
            <div style={{marginBottom:12}}>
              <label style={S.label}>Cargo-alvo</label>
              <select value={goalTargetRoleId} onChange={e=>setGoalTargetRoleId(e.target.value)} style={S.input}>
                <option value="">Selecionar cargo...</option>
                {restRoles.map(r => <option key={r.id} value={r.id}>{r.name} ({r.area})</option>)}
              </select>
            </div>
          )}
          {goalType === "conhecimento" && (
            <div style={{marginBottom:12}}>
              <label style={S.label}>Tema</label>
              <input value={goalTopic} onChange={e=>setGoalTopic(e.target.value)} placeholder="Ex: Vinhos italianos, Atendimento VIP..." style={S.input}/>
            </div>
          )}
          {goalType === "personalizado" && (
            <div style={{marginBottom:12}}>
              <label style={S.label}>Título do objetivo</label>
              <input value={goalTitle} onChange={e=>setGoalTitle(e.target.value)} placeholder="Ex: Melhorar comunicação com equipe" style={S.input}/>
            </div>
          )}
          <button onClick={addGoal} style={{...S.btnPrimary,width:"auto",padding:"10px 20px"}}>Criar objetivo</button>
        </div>
      )}

      {activeGoals.length === 0 && !showForm && (
        <div style={{textAlign:"center",padding:24,color:"var(--text3)"}}>
          <div style={{fontSize:32,marginBottom:8}}>🎯</div>
          <div style={{fontSize:13}}>Nenhum objetivo ativo</div>
        </div>
      )}

      {activeGoals.map(g => renderGoalCard(g))}

      {completedGoals.length > 0 && (
        <details style={{marginTop:8}}>
          <summary style={{color:"var(--text3)",fontSize:12,cursor:"pointer",marginBottom:8}}>Objetivos concluídos ({completedGoals.length})</summary>
          {completedGoals.map(g => renderGoalCard(g))}
        </details>
      )}
    </div>
  );
}

// ── Meeting agenda templates ──
const AGENDA_TEMPLATES = [
  { id:"semanal", label:"Reunião Semanal de Lideranças", icon:"📋", items:[
    "Revisão de ações pendentes da semana anterior",
    "Ocorrências registradas",
    "Ideias do banco",
    "Indicadores da semana (faltas, atrasos)",
    "Novas ações e responsáveis",
  ]},
];

function MeetingPlannerSection({ restaurantId, employees, roles, areas, meetingPlans, allMeetingPlans, feedbacks, onUpdate, currentUser, isOwner, isDP, mobileOnly, isLider, managerAreas, data, forceSubTab }) {
  // Sub-tab state
  const [subTab, setSubTab] = useState(forceSubTab || "reunioes"); // reunioes | ideias | ocorrencias | pendencias | relatorio
  // v7: se forceSubTab muda externamente (top-level tab "ideias"/"ocorrencias"), seguir
  useEffect(() => { if (forceSubTab && forceSubTab !== subTab) setSubTab(forceSubTab); }, [forceSubTab]); // eslint-disable-line react-hooks/exhaustive-deps
  // Reuniões (existing) — showForm: false | "equipe" | "lideranca"
  const [showForm, setShowForm] = useState(false);
  const [planType, setPlanType] = useState("alinhamento");
  const [planDate, setPlanDate] = useState("");
  const [planNote, setPlanNote] = useState("");
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [selectedEmps, setSelectedEmps] = useState([]);
  // Avaliação individual checklist
  const [avalPontosFortes, setAvalPontosFortes] = useState([]);
  const [avalPontosMelhorar, setAvalPontosMelhorar] = useState([]);
  const [avalNotas, setAvalNotas] = useState([]);
  const [avalNewItem, setAvalNewItem] = useState("");
  const [avalSection, setAvalSection] = useState("fortes"); // fortes | melhorar | notas
  // Alinhamento topics checklist
  const [alinhTopics, setAlinhTopics] = useState([]);
  const [alinhNewTopic, setAlinhNewTopic] = useState("");
  // Area filter for Líder
  const [showAllAreas, setShowAllAreas] = useState(false);
  // Ideias
  const [showIdeaForm, setShowIdeaForm] = useState(false);
  const [ideaTitle, setIdeaTitle] = useState("");
  const [ideaDesc, setIdeaDesc] = useState("");
  const [ideaAreas, setIdeaAreas] = useState([]);
  // Ocorrências
  const [showOccForm, setShowOccForm] = useState(false);
  const [occTitle, setOccTitle] = useState("");
  const [occDesc, setOccDesc] = useState("");
  const [occType, setOccType] = useState("cliente");
  const [occAreas, setOccAreas] = useState([]);
  const [occEmployeeIds, setOccEmployeeIds] = useState([]);
  const [occSubtype, setOccSubtype] = useState("");
  // Occurrence resolution
  const [resolvingOccId, setResolvingOccId] = useState(null);
  const [resolveOccText, setResolveOccText] = useState("");
  // Liderança participants
  const [lidParticipants, setLidParticipants] = useState([]);
  // Execution mode (live meeting)
  const [livePautaId, setLivePautaId] = useState(null);
  const [livePlanId, setLivePlanId] = useState(null);
  const [livePlanNotes, setLivePlanNotes] = useState("");
  // Pautas
  const [pautaTitle, setPautaTitle] = useState("");
  const [pautaType, setPautaType] = useState("semanal");
  const [pautaDate, setPautaDate] = useState("");
  const [pautaItems, setPautaItems] = useState([]);
  const [pautaNewItem, setPautaNewItem] = useState("");
  const [pautaPickIdeas, setPautaPickIdeas] = useState([]);
  const [activePautaId, setActivePautaId] = useState(null);
  // v8.11.2: edição de plan/pauta — null quando criando, id quando editando
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [editingPautaId, setEditingPautaId] = useState(null);
  // v8.14.0: visibilidade da reunião sendo criada/editada
  const [planVisibilityMode, setPlanVisibilityMode] = useState("default"); // "default" | "shared" | "private"
  const [planSharedWith, setPlanSharedWith] = useState([]); // [pessoaId]
  const [pautaVisibilityMode, setPautaVisibilityMode] = useState("default");
  const [pautaSharedWith, setPautaSharedWith] = useState([]);
  // Ações pós-reunião
  // Relatório
  const [relFrom, setRelFrom] = useState("");
  const [relTo, setRelTo] = useState("");
  const [relFilterArea, setRelFilterArea] = useState("Todos");
  const [relFilterLider, setRelFilterLider] = useState("Todos");

  // Data keys
  const allIdeas = (data?.meetingIdeas ?? []).filter(i => i.restaurantId === restaurantId);
  const TRASH_DAYS = 30;
  const allPautasRaw = (data?.meetingAgendas ?? []).filter(p => p.restaurantId === restaurantId);
  // v8.14.0: aplica filtro de visibilidade nas pautas (helper canSeeMeeting)
  const _isAdminGeneralForPautas = isOwner || isDP || (!isLider);
  const allPautas = allPautasRaw.filter(p => !p.deletedAt && canSeeMeeting({ item: p, currentUserId: currentUser?.id, isOwner, isDP, isAdminGeneral: _isAdminGeneralForPautas }));
  const trashedPautas = allPautasRaw.filter(p => p.deletedAt && (Date.now() - new Date(p.deletedAt).getTime()) < TRASH_DAYS * 86400000 && canSeeMeeting({ item: p, currentUserId: currentUser?.id, isOwner, isDP, isAdminGeneral: _isAdminGeneralForPautas }));
  const allActions = (data?.meetingActions ?? []).filter(a => a.restaurantId === restaurantId);
  const allOccurrences = (data?.meetingOccurrences ?? []).filter(o => o.restaurantId === restaurantId);
  const allPendencias = (data?.meetingPendencias ?? []).filter(p => p.restaurantId === restaurantId);
  const OCC_TYPES = [["cliente","🧑‍🍳","Cliente"],["estrutura","🏗️","Estrutura"],["operacao","⚙️","Operação"],["pessoal","👤","Pessoal"],["outro","📝","Outro"]];
  const PESSOAL_SUBTYPES = [["advertencia_verbal","Advertência verbal"],["advertencia_escrita","Advertência escrita"],["desentendimento","Desentendimento"],["dano_material","Dano material"],["indisciplina","Indisciplina"],["elogio_formal","Elogio formal"],["destaque_positivo","Destaque positivo"],["outro_pessoal","Outro"]];

  // Wrapper for reading data
  const getData = (key) => data?.[key] ?? [];

  // Area filter logic for Líder Operacional
  const filteredAreas = (isLider && !showAllAreas && managerAreas?.length > 0)
    ? areas.filter(a => managerAreas.includes(a))
    : areas;
  const filteredEmployees = (isLider && !showAllAreas && managerAreas?.length > 0)
    ? employees.filter(e => { const r = roles.find(rl => rl.id === e.roleId); return r && managerAreas.includes(r.area); })
    : employees;
  const activePlans = meetingPlans.filter(p => !p.deletedAt);
  const trashedPlans = meetingPlans.filter(p => p.deletedAt && (Date.now() - new Date(p.deletedAt).getTime()) < TRASH_DAYS * 86400000);
  // v8.14.0: filtro por área (existente) + filtro por visibilidade (novo)
  const isAdminGeneralForVis = isOwner || isDP || (!isLider); // gestor admin geral (sem ser só líder de área)
  const visiblePlans = activePlans.filter(p => canSeeMeeting({ item: p, currentUserId: currentUser?.id, isOwner, isDP, isAdminGeneral: isAdminGeneralForVis }));
  const filteredPlans = (isLider && !showAllAreas && managerAreas?.length > 0)
    ? visiblePlans.filter(p => (p.employeeIds ?? []).some(eid => filteredEmployees.find(e => e.id === eid)))
    : visiblePlans;
  const filteredIdeas = (isLider && !showAllAreas && managerAreas?.length > 0)
    ? allIdeas.filter(i => { const ia = i.areas ?? (i.area ? [i.area] : []); return ia.length === 0 || ia.some(a => managerAreas.includes(a)); })
    : allIdeas;
  // v8.14.0: ocorrências privadas — só criador + Owner + DP
  const visibleOccurrences = allOccurrences.filter(o => canSeeOccurrence({ item: o, currentUserId: currentUser?.id, isOwner, isDP }));
  const filteredOccurrences = (isLider && !showAllAreas && managerAreas?.length > 0)
    ? visibleOccurrences.filter(o => { const oa = o.areas ?? []; return oa.length === 0 || oa.some(a => managerAreas.includes(a)); })
    : visibleOccurrences;

  // v8.14.0: lista de gestores/líderes do restaurante elegíveis pra compartilhar visibilidade.
  // Dedupe por CPF (canônico) + linkedManagerId — evita repetir quando pessoa migrada existe em managers e pessoas.
  const shareableManagers = (() => {
    const result = [];
    const seenIds = new Set();
    const seenCpfs = new Set();
    const cpfClean = s => (s || "").replace(/\D/g, "");
    const cpfCurrent = cpfClean(currentUser?.cpf);
    // Pessoa primeiro (fonte preferencial — tem o id "novo" que aparece em todas as nossas listas)
    (data?.pessoas ?? []).forEach(p => {
      if (!(p.restaurantIds ?? []).includes(restaurantId)) return;
      if (p.id === currentUser?.id) return;
      const pcpf = cpfClean(p.cpf);
      if (cpfCurrent && pcpf === cpfCurrent) return;
      const perms = p.permissions?.[restaurantId];
      if (!perms) return;
      const hasAdmin = Object.values(perms.admin ?? {}).some(v => v === true);
      const isLid = perms.special?.isLider === true || perms.special?.profile === "lider";
      const isDpP = perms.special?.isDP === true;
      if (!hasAdmin && !isLid && !isDpP) return;
      seenIds.add(p.id);
      if (p.linkedManagerId) seenIds.add(p.linkedManagerId);
      if (pcpf) seenCpfs.add(pcpf);
      const role = isLid ? "Líder de Área" : isDpP ? "DP" : "Gestor";
      result.push({ id: p.id, name: p.name, role });
    });
    // Managers que ainda não vieram de pessoas
    (data?.managers ?? []).forEach(m => {
      if (!(m.restaurantIds ?? []).includes(restaurantId)) return;
      if (m.id === currentUser?.id) return;
      if (seenIds.has(m.id)) return;
      const mcpf = cpfClean(m.cpf);
      if (cpfCurrent && mcpf === cpfCurrent) return;
      if (mcpf && seenCpfs.has(mcpf)) return;
      seenIds.add(m.id);
      if (mcpf) seenCpfs.add(mcpf);
      const role = m.profile === "lider" ? "Líder de Área" : (m.isDP ? "DP" : "Gestor");
      result.push({ id: m.id, name: m.name, role });
    });
    return result.sort((a,b) => a.name.localeCompare(b.name));
  })();

  const toggleArea = (area) => {
    const areaEmps = employees.filter(e => { const r = roles.find(rl => rl.id === e.roleId); return r?.area === area; }).map(e => e.id);
    if (selectedAreas.includes(area)) {
      setSelectedAreas(prev => prev.filter(a => a !== area));
      // For avaliação, just clear selection if the deselected area contained the selected emp
      if (planType === "avaliação") {
        setSelectedEmps(prev => prev.filter(id => !areaEmps.includes(id)));
      } else {
        setSelectedEmps(prev => prev.filter(id => !areaEmps.includes(id)));
      }
    } else {
      // v8.12.0: Não auto-seleciona empregados ao escolher área. Gestor pica quem participa manualmente.
      setSelectedAreas(prev => [...prev, area]);
    }
  };

  const toggleEmp = (empId) => {
    setSelectedEmps(prev => prev.includes(empId) ? prev.filter(id => id !== empId) : [...prev, empId]);
  };

  function handleCreatePlan() {
    if (!planDate) { window.alert("Informe a data prevista."); return; }
    if (planType === "avaliação" && selectedEmps.length !== 1) { window.alert("Selecione exatamente um empregado para avaliação individual."); return; }
    if (planType !== "avaliação" && selectedEmps.length === 0) { window.alert("Selecione ao menos um participante."); return; }
    // v8.11.2: edição preserva id/createdBy/createdAt/completedFeedbackIds; criação gera novos
    const isEdit = !!editingPlanId;
    const existing = isEdit ? (allMeetingPlans ?? []).find(p => p.id === editingPlanId) : null;
    const plan = {
      id: existing?.id || `${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
      restaurantId,
      type: planType,
      employeeIds: [...selectedEmps],
      plannedDate: planDate,
      note: planNote.trim(),
      createdBy: existing?.createdBy || (isOwner ? "Master" : (currentUser?.name ?? "Gestor Adm.")),
      createdById: existing?.createdById || currentUser?.id || null, // v8.14.0
      createdAt: existing?.createdAt || new Date().toISOString(),
      completedFeedbackIds: existing?.completedFeedbackIds || {},
      // v8.14.0: visibilidade
      visibility: {
        mode: planVisibilityMode || "default",
        sharedWith: planVisibilityMode === "shared" ? [...planSharedWith] : [],
      },
      ...(existing?.status ? { status: existing.status } : {}),
      ...(existing?.meetingNotes ? { meetingNotes: existing.meetingNotes } : {}),
      // Avaliação: preserva done de itens já marcados se forem os mesmos textos
      ...(planType === "avaliação" ? {
        pontosFortes: avalPontosFortes.map(t => {
          const prev = (existing?.pontosFortes || []).find(p => p.text === t);
          return prev || { text: t, done: false };
        }),
        pontosMelhorar: avalPontosMelhorar.map(t => {
          const prev = (existing?.pontosMelhorar || []).find(p => p.text === t);
          return prev || { text: t, done: false };
        }),
        notasInternas: avalNotas.map(t => {
          const prev = (existing?.notasInternas || []).find(p => p.text === t);
          return prev || { text: t, done: false };
        }),
      } : {}),
      // Alinhamento: preserva done de topics já marcados
      ...(planType === "alinhamento" ? {
        topics: alinhTopics.map(t => {
          const prev = (existing?.topics || []).find(p => p.text === t);
          return prev || { text: t, done: false };
        }),
      } : {}),
      ...(isEdit ? { editedBy: currentUser?.name ?? "Gestor", editedAt: new Date().toISOString() } : {}),
    };
    if (isEdit) {
      const all = (allMeetingPlans ?? []).map(p => p.id === editingPlanId ? plan : p);
      onUpdate("meetingPlans", all);
    } else {
      onUpdate("meetingPlans", [...(allMeetingPlans ?? []), plan]);
      // Etapa 6a: Notificar participantes sobre reunião agendada (só na criação)
      if (plan.employeeIds?.length > 0 && plan.plannedDate) {
        const createdByInfo = { id: currentUser?.id, nome: plan.createdBy, role: isOwner ? "owner" : "manager" };
        const participants = plan.employeeIds.map(eid => ({ id: eid }));
        const meetingType = plan.type === "alinhamento" ? "lideranca" : "equipe";
        const newNotifs = createMeetingNotifications({ meetingType, meetingDate: plan.plannedDate, restaurantId, participants, createdBy: createdByInfo });
        if (newNotifs.length > 0) {
          onUpdate("inbox", [...(data?.inbox ?? []), ...newNotifs]);
        }
      }
    }
    setPlanDate(""); setPlanNote(""); setSelectedAreas([]); setSelectedEmps([]); setShowForm(false);
    setAvalPontosFortes([]); setAvalPontosMelhorar([]); setAvalNotas([]); setAvalNewItem(""); setAvalSection("fortes");
    setAlinhTopics([]); setAlinhNewTopic("");
    setEditingPlanId(null);
    setPlanVisibilityMode("default"); setPlanSharedWith([]); // v8.14.0
  }

  // v8.11.2: abre form de edição populado com dados do plan
  function handleStartEditPlan(plan) {
    setPlanType(plan.type || "alinhamento");
    setPlanDate(plan.plannedDate || "");
    setPlanNote(plan.note || "");
    setSelectedEmps([...(plan.employeeIds || [])]);
    // Áreas derivadas dos empregados selecionados
    const areasFromEmps = new Set();
    (plan.employeeIds || []).forEach(eid => {
      const emp = employees.find(e => e.id === eid);
      const r = emp ? roles.find(rl => rl.id === emp.roleId) : null;
      if (r?.area) areasFromEmps.add(r.area);
    });
    setSelectedAreas([...areasFromEmps]);
    setAvalPontosFortes((plan.pontosFortes || []).map(p => p.text));
    setAvalPontosMelhorar((plan.pontosMelhorar || []).map(p => p.text));
    setAvalNotas((plan.notasInternas || []).map(p => p.text));
    setAlinhTopics((plan.topics || []).map(p => p.text));
    // v8.14.0: visibilidade — popula do existente
    setPlanVisibilityMode(plan.visibility?.mode || "default");
    setPlanSharedWith(Array.isArray(plan.visibility?.sharedWith) ? [...plan.visibility.sharedWith] : []);
    setEditingPlanId(plan.id);
    setShowForm("equipe");
  }

  async function handleDeletePlan(planId) {
    if (!await appConfirm("Excluir esta reunião? Ficará na lixeira por 30 dias.")) return;
    const all = [...(allMeetingPlans ?? [])];
    const idx = all.findIndex(p => p.id === planId);
    if (idx >= 0) { all[idx] = { ...all[idx], deletedAt: new Date().toISOString(), deletedBy: currentUser?.name ?? "Gestor" }; onUpdate("meetingPlans", all); }
  }
  function handleRestorePlan(planId) {
    const all = [...(allMeetingPlans ?? [])];
    const idx = all.findIndex(p => p.id === planId);
    if (idx >= 0) { const { deletedAt, deletedBy, ...rest } = all[idx]; all[idx] = rest; onUpdate("meetingPlans", all); }
  }

  function getEmpStatus(plan, empId) {
    if (plan.completedFeedbackIds?.[empId]) return "realizada";
    const fb = (feedbacks ?? []).find(f => {
      if (f.employeeId !== empId || f.deletedAt) return false;
      const fbType = f.meetingType || (f.rating > 0 ? "avaliação" : "alinhamento");
      if (fbType !== plan.type) return false;
      const fbDate = f.meetingDate || f.createdAt?.slice(0,10);
      if (!fbDate) return false;
      const diff = Math.abs(new Date(fbDate+"T12:00:00") - new Date(plan.plannedDate+"T12:00:00"));
      return diff < 7 * 86400000;
    });
    if (fb) return "realizada";
    if (plan.plannedDate < today()) return "atrasada";
    return "pendente";
  }

  // ── Ideias ──
  function handleCreateIdea() {
    if (!ideaTitle.trim()) { window.alert("Informe o título da ideia."); return; }
    const idea = {
      id: `idea-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
      restaurantId,
      title: ideaTitle.trim(),
      description: ideaDesc.trim(),
      areas: ideaAreas.length > 0 ? [...ideaAreas] : null,
      createdBy: currentUser?.name ?? (isOwner ? "Master" : "Gestor Adm."),
      createdAt: new Date().toISOString(),
      priority: false,
      status: "nova", // nova | na_pauta | descartada
    };
    onUpdate("meetingIdeas", [...getData("meetingIdeas"), idea]);
    setIdeaTitle(""); setIdeaDesc(""); setIdeaAreas([]); setShowIdeaForm(false);
  }

  function toggleIdeaPriority(ideaId) {
    const all = [...getData("meetingIdeas")];
    const idx = all.findIndex(i => i.id === ideaId);
    if (idx >= 0) { all[idx] = { ...all[idx], priority: !all[idx].priority }; onUpdate("meetingIdeas", all); }
  }

  function handleUpdateIdeaStatus(ideaId, status) {
    const all = [...getData("meetingIdeas")];
    const idx = all.findIndex(i => i.id === ideaId);
    if (idx >= 0) { all[idx] = { ...all[idx], status }; onUpdate("meetingIdeas", all); }
  }

  async function deleteIdea(ideaId) {
    if (!await appConfirm("Excluir esta ideia?")) return;
    onUpdate("meetingIdeas", getData("meetingIdeas").filter(i => i.id !== ideaId));
  }

  // ── Pautas ──
  function handleApplyTemplate(templateId) {
    const tpl = AGENDA_TEMPLATES.find(t => t.id === templateId);
    if (!tpl) return;
    setPautaTitle(tpl.label);
    setPautaItems(tpl.items.map((text, i) => ({ id: `tpl-${i}`, text, fromIdea: null, status: "pendente", decision: "" })));
  }

  function handleAddPautaItem() {
    if (!pautaNewItem.trim()) return;
    setPautaItems(prev => [...prev, { id: `item-${Date.now()}`, text: pautaNewItem.trim(), fromIdea: null, status: "pendente", decision: "" }]);
    setPautaNewItem("");
  }

  function handleAddIdeaToPauta(idea) {
    if (pautaPickIdeas.includes(idea.id)) return;
    setPautaPickIdeas(prev => [...prev, idea.id]);
    setPautaItems(prev => [...prev, { id: `idea-${idea.id}`, text: idea.title + (idea.description ? ` — ${idea.description}` : ""), fromIdea: idea.id, status: "pendente", decision: "" }]);
  }

  function handleRemovePautaItem(itemId) {
    setPautaItems(prev => prev.filter(i => i.id !== itemId));
    const item = pautaItems.find(i => i.id === itemId);
    if (item?.fromIdea) setPautaPickIdeas(prev => prev.filter(id => id !== item.fromIdea));
    if (item?.fromOccurrence) setPautaPickIdeas(prev => prev.filter(id => id !== item.fromOccurrence));
  }

  function handleMovePautaItem(idx, dir) {
    const newItems = [...pautaItems];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= newItems.length) return;
    [newItems[idx], newItems[newIdx]] = [newItems[newIdx], newItems[idx]];
    setPautaItems(newItems);
  }

  function handleSavePauta() {
    if (!pautaTitle.trim()) { window.alert("Informe o título da pauta."); return; }
    if (pautaItems.length === 0) { window.alert("Adicione ao menos um item à pauta."); return; }
    // v8.11.2: edição preserva id/createdBy/createdAt/status; criação gera novos
    const isEdit = !!editingPautaId;
    const existing = isEdit ? (data?.meetingAgendas ?? []).find(p => p.id === editingPautaId) : null;
    const pauta = {
      id: existing?.id || `pauta-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
      restaurantId,
      title: pautaTitle.trim(),
      templateType: pautaType,
      date: pautaDate || today(),
      // Preserva itens existentes (notes, decision, status) quando o texto bate
      items: pautaItems.map(it => {
        const prev = (existing?.items || []).find(p => p.id === it.id || p.text === it.text);
        return prev ? { ...prev, ...it, id: prev.id, status: prev.status, notes: prev.notes, decision: prev.decision, reason: prev.reason } : { ...it };
      }),
      participants: lidParticipants.length > 0 ? [...lidParticipants] : [],
      createdBy: existing?.createdBy || (currentUser?.name ?? (isOwner ? "Master" : "Gestor Adm.")),
      createdById: existing?.createdById || currentUser?.id || null, // v8.14.0
      createdAt: existing?.createdAt || new Date().toISOString(),
      status: existing?.status || "aberta",
      actions: existing?.actions || [],
      // v8.14.0: visibilidade
      visibility: {
        mode: pautaVisibilityMode || "default",
        sharedWith: pautaVisibilityMode === "shared" ? [...pautaSharedWith] : [],
      },
      ...(isEdit ? { editedBy: currentUser?.name ?? "Gestor", editedAt: new Date().toISOString() } : {}),
    };
    // Mark ideas as included (skip for edit — items já marcados antes)
    if (!isEdit) {
      const ideaIds = pautaItems.filter(i => i.fromIdea).map(i => i.fromIdea);
      if (ideaIds.length > 0) {
        const allIdeasData = [...getData("meetingIdeas")];
        ideaIds.forEach(iid => { const idx = allIdeasData.findIndex(i => i.id === iid); if (idx >= 0) allIdeasData[idx] = { ...allIdeasData[idx], status: "na_pauta" }; });
        onUpdate("meetingIdeas", allIdeasData);
      }
      const occIds = pautaItems.filter(i => i.fromOccurrence).map(i => i.fromOccurrence);
      if (occIds.length > 0) {
        const allOccData = [...getData("meetingOccurrences")];
        occIds.forEach(oid => { const idx = allOccData.findIndex(o => o.id === oid); if (idx >= 0) allOccData[idx] = { ...allOccData[idx], status: "na_pauta" }; });
        onUpdate("meetingOccurrences", allOccData);
      }
      const pendIds = pautaItems.filter(i => i.fromPendencia).map(i => i.fromPendencia);
      if (pendIds.length > 0) {
        const allPendData = [...getData("meetingPendencias")];
        pendIds.forEach(pid => { const idx = allPendData.findIndex(p => p.id === pid); if (idx >= 0) allPendData[idx] = { ...allPendData[idx], status: "na_pauta" }; });
        onUpdate("meetingPendencias", allPendData);
      }
    }
    if (isEdit) {
      const all = (data?.meetingAgendas ?? []).map(p => p.id === editingPautaId ? pauta : p);
      onUpdate("meetingAgendas", all);
    } else {
      onUpdate("meetingAgendas", [...getData("meetingAgendas"), pauta]);
    }
    setPautaTitle(""); setPautaType("semanal"); setPautaDate(""); setPautaItems([]); setPautaNewItem(""); setPautaPickIdeas([]); setLidParticipants([]); setShowForm(false);
    setEditingPautaId(null);
    setPautaVisibilityMode("default"); setPautaSharedWith([]); // v8.14.0
  }

  // v8.11.2: abre form de edição populado com dados da pauta
  function handleStartEditPauta(pauta) {
    setPautaTitle(pauta.title || "");
    setPautaType(pauta.templateType || "semanal");
    setPautaDate(pauta.date || "");
    setPautaItems((pauta.items || []).map(it => ({ ...it })));
    setLidParticipants([...(pauta.participants || [])]);
    setPautaPickIdeas((pauta.items || []).filter(i => i.fromIdea).map(i => i.fromIdea));
    // v8.14.0: visibilidade
    setPautaVisibilityMode(pauta.visibility?.mode || "default");
    setPautaSharedWith(Array.isArray(pauta.visibility?.sharedWith) ? [...pauta.visibility.sharedWith] : []);
    setEditingPautaId(pauta.id);
    setShowForm("lideranca");
  }

  // Checklist ao vivo
  function handleUpdatePautaItem(pautaId, itemId, updates) {
    const all = [...getData("meetingAgendas")];
    const idx = all.findIndex(p => p.id === pautaId);
    if (idx < 0) return;
    all[idx] = { ...all[idx], items: all[idx].items.map(it => it.id === itemId ? { ...it, ...updates } : it) };
    onUpdate("meetingAgendas", all);
  }

  function handleUpdatePautaStatus(pautaId, status) {
    const all = [...getData("meetingAgendas")];
    const idx = all.findIndex(p => p.id === pautaId);
    if (idx < 0) return;
    all[idx] = { ...all[idx], status };
    onUpdate("meetingAgendas", all);
  }

  // Ações pós-reunião
  function handleAddAction(pautaId, actionText, responsible, deadline) {
    if (!actionText.trim()) return;
    const action = {
      id: `act-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
      restaurantId,
      pautaId,
      text: actionText.trim(),
      responsible: responsible || "",
      deadline: deadline || "",
      done: false,
      createdAt: new Date().toISOString(),
    };
    onUpdate("meetingActions", [...getData("meetingActions"), action]);
  }

  function handleToggleAction(actionId) {
    const all = [...getData("meetingActions")];
    const idx = all.findIndex(a => a.id === actionId);
    if (idx >= 0) { all[idx] = { ...all[idx], done: !all[idx].done }; onUpdate("meetingActions", all); }
  }

  async function handleDeletePauta(pautaId) {
    if (!await appConfirm("Excluir esta pauta? Ficará na lixeira por 30 dias.")) return;
    const all = [...getData("meetingAgendas")];
    const idx = all.findIndex(p => p.id === pautaId);
    if (idx >= 0) { all[idx] = { ...all[idx], deletedAt: new Date().toISOString(), deletedBy: currentUser?.name ?? "Gestor" }; onUpdate("meetingAgendas", all); }
  }
  function handleRestorePauta(pautaId) {
    const all = [...getData("meetingAgendas")];
    const idx = all.findIndex(p => p.id === pautaId);
    if (idx >= 0) { const { deletedAt, deletedBy, ...rest } = all[idx]; all[idx] = rest; onUpdate("meetingAgendas", all); }
  }

  // ── Ocorrências ──
  function handleCreateOccurrence() {
    if (!occTitle.trim()) { window.alert("Informe o título da ocorrência."); return; }
    if (occType === "pessoal" && occEmployeeIds.length === 0) { window.alert("Selecione ao menos um empregado para ocorrência pessoal."); return; }
    const occ = {
      id: `occ-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
      restaurantId,
      title: occTitle.trim(),
      description: occDesc.trim(),
      type: occType,
      subtype: occType === "pessoal" ? occSubtype : "",
      areas: occAreas.length > 0 ? [...occAreas] : [],
      employeeIds: occEmployeeIds.length > 0 ? [...occEmployeeIds] : [],
      createdBy: currentUser?.name ?? (isOwner ? "Master" : "Gestor Adm."),
      createdById: currentUser?.id ?? null, // v8.14.0: pra filtro de visibilidade
      createdAt: new Date().toISOString(),
      status: "nova", // nova | na_pauta | resolvida | descartada
    };
    onUpdate("meetingOccurrences", [...getData("meetingOccurrences"), occ]);
    setOccTitle(""); setOccDesc(""); setOccType("cliente"); setOccAreas([]); setOccEmployeeIds([]); setOccSubtype(""); setShowOccForm(false);
  }

  function handleUpdateOccStatus(occId, status, resolvedText) {
    const all = [...getData("meetingOccurrences")];
    const idx = all.findIndex(o => o.id === occId);
    if (idx >= 0) {
      all[idx] = { ...all[idx], status };
      if (status === "resolvida") {
        all[idx].resolvedText = resolvedText || "";
        all[idx].resolvedAt = new Date().toISOString();
        all[idx].resolvedBy = currentUser?.name ?? (isOwner ? "Master" : "Gestor Adm.");
      }
      onUpdate("meetingOccurrences", all);
    }
    setResolvingOccId(null);
    setResolveOccText("");
  }

  function toggleOccPriority(occId) {
    const all = [...getData("meetingOccurrences")];
    const idx = all.findIndex(o => o.id === occId);
    if (idx >= 0) { all[idx] = { ...all[idx], priority: !all[idx].priority }; onUpdate("meetingOccurrences", all); }
  }

  async function handleDeleteOccurrence(occId) {
    if (!await appConfirm("Excluir esta ocorrência?")) return;
    onUpdate("meetingOccurrences", getData("meetingOccurrences").filter(o => o.id !== occId));
  }

  function handleAddOccToPauta(occ) {
    if (pautaPickIdeas.includes(occ.id)) return;
    setPautaPickIdeas(prev => [...prev, occ.id]);
    const occTypeLabel = OCC_TYPES.find(([t])=>t===occ.type)?.[2] ?? occ.type;
    setPautaItems(prev => [...prev, { id: `occ-${occ.id}`, text: `[${occTypeLabel}] ${occ.title}${occ.description ? ` — ${occ.description}` : ""}`, fromOccurrence: occ.id, fromIdea: null, status: "pendente", decision: "", reason: "" }]);
  }

  function handleAddIncidentToPauta(inc) {
    if (pautaPickIdeas.includes(inc.id)) return;
    setPautaPickIdeas(prev => [...prev, inc.id]);
    const empNames = (inc.employeeIds ?? []).map(eid => { const e = employees.find(emp => emp.id === eid); return shortName(e?.name); }).filter(Boolean).join(", ");
    setPautaItems(prev => [...prev, { id: `inc-${inc.id}`, text: `[Empregado${empNames ? `: ${empNames}` : ""}] ${inc.type ?? "Ocorrência"} — ${inc.description ?? ""}`, fromIncident: inc.id, fromIdea: null, fromOccurrence: null, status: "pendente", decision: "", reason: "" }]);
  }

  // ── Live meeting (execution mode) ──
  function handleStartLiveMeeting(pautaId) {
    handleUpdatePautaStatus(pautaId, "em_andamento");
    setLivePautaId(pautaId);
  }

  function handleEndLiveMeeting(pautaId) {
    handleUpdatePautaStatus(pautaId, "encerrada");
    const pauta = allPautas.find(p => p.id === pautaId);
    if (pauta) {
      const resolvedItems = (pauta.items ?? []).filter(it => it.status === "resolvido" || it.status === "acao_definida");
      // Mark resolved occurrence items
      const occIds = resolvedItems.filter(it => it.fromOccurrence).map(it => it.fromOccurrence);
      if (occIds.length > 0) {
        const allOcc = [...getData("meetingOccurrences")];
        occIds.forEach(oid => { const idx = allOcc.findIndex(o => o.id === oid); if (idx >= 0 && (allOcc[idx].status === "na_pauta" || allOcc[idx].status === "nova")) allOcc[idx] = { ...allOcc[idx], status: "resolvida", resolvedAt: new Date().toISOString(), resolvedBy: currentUser?.name ?? (isOwner ? "Master" : "Gestor Adm.") }; });
        onUpdate("meetingOccurrences", allOcc);
      }
      // Mark resolved idea items
      const ideaIds = resolvedItems.filter(it => it.fromIdea).map(it => it.fromIdea);
      if (ideaIds.length > 0) {
        const allIdeasData = [...getData("meetingIdeas")];
        ideaIds.forEach(iid => { const idx = allIdeasData.findIndex(i => i.id === iid); if (idx >= 0 && (allIdeasData[idx].status === "na_pauta" || allIdeasData[idx].status === "nova")) allIdeasData[idx] = { ...allIdeasData[idx], status: "resolvida" }; });
        onUpdate("meetingIdeas", allIdeasData);
      }
      // Create pendências for items marked pendente or sem_resolucao
      const pendingItems = (pauta.items ?? []).filter(it => it.status === "pendente" || it.status === "sem_resolucao");
      if (pendingItems.length > 0) {
        const allPend = [...getData("meetingPendencias")];
        pendingItems.forEach(item => {
          allPend.push({
            id: `pend-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
            restaurantId,
            text: item.text,
            reason: item.reason ?? "",
            type: item.status, // "pendente" or "sem_resolucao"
            fromPautaId: pautaId,
            fromItemId: item.id,
            fromIdea: item.fromIdea ?? null,
            fromOccurrence: item.fromOccurrence ?? null,
            fromIncident: item.fromIncident ?? null,
            createdAt: new Date().toISOString(),
            status: "ativa", // ativa | na_pauta | resolvida
          });
        });
        onUpdate("meetingPendencias", allPend);
      }
    }
    setLivePautaId(null);
  }


  // Update pauta item notes (for live meeting)
  function handleUpdatePautaItemNotes(pautaId, itemId, notes) {
    const all = [...getData("meetingAgendas")];
    const idx = all.findIndex(p => p.id === pautaId);
    if (idx < 0) return;
    all[idx] = { ...all[idx], items: all[idx].items.map(it => it.id === itemId ? { ...it, notes } : it) };
    onUpdate("meetingAgendas", all);
  }

  // ── Live team meeting (plans - avaliação/alinhamento) ──
  function handleStartLivePlan(planId) {
    const all = [...(allMeetingPlans ?? [])];
    const idx = all.findIndex(p => p.id === planId);
    if (idx >= 0) { all[idx] = { ...all[idx], status: "em_andamento", startedAt: all[idx].startedAt ?? new Date().toISOString() }; onUpdate("meetingPlans", all); }
    setLivePlanId(planId);
    setLivePlanNotes("");
  }
  function handleTogglePlanItem(planId, listKey, itemIdx) {
    const all = [...(allMeetingPlans ?? [])];
    const idx = all.findIndex(p => p.id === planId);
    if (idx < 0) return;
    const items = [...(all[idx][listKey] ?? [])];
    if (items[itemIdx]) items[itemIdx] = { ...items[itemIdx], done: !items[itemIdx].done };
    all[idx] = { ...all[idx], [listKey]: items };
    onUpdate("meetingPlans", all);
  }
  function handleUpdatePlanItemNotes(planId, listKey, itemIdx, notes) {
    const all = [...(allMeetingPlans ?? [])];
    const idx = all.findIndex(p => p.id === planId);
    if (idx < 0) return;
    const items = [...(all[idx][listKey] ?? [])];
    if (items[itemIdx]) items[itemIdx] = { ...items[itemIdx], notes };
    all[idx] = { ...all[idx], [listKey]: items };
    onUpdate("meetingPlans", all);
  }
  function handleEndLivePlan(planId) {
    const all = [...(allMeetingPlans ?? [])];
    const idx = all.findIndex(p => p.id === planId);
    if (idx >= 0) {
      all[idx] = { ...all[idx], status: "encerrada", endedAt: new Date().toISOString(), meetingNotes: livePlanNotes || all[idx].meetingNotes || "" };
      onUpdate("meetingPlans", all);
      // Etapa 6b: Enviar ações pós-reunião para cada participante responsável
      const plan = all[idx];
      const planActions = getData("meetingActions").filter(a => a.restaurantId === restaurantId && !a.done);
      if (planActions.length > 0) {
        // Encontrar responsáveis por nome → mapear para IDs de employees
        const actionsWithIds = planActions.map(a => {
          const emp = employees.find(e => e.name === a.responsible);
          return { ...a, responsibleId: emp?.id || null };
        });
        const createdByInfo = { id: currentUser?.id, nome: currentUser?.name || "Gestor", role: isOwner ? "owner" : "manager" };
        const actionNotifs = createActionNotifications({ actions: actionsWithIds, restaurantId, meetingDate: plan.plannedDate || today(), createdBy: createdByInfo });
        if (actionNotifs.length > 0) {
          onUpdate("inbox", [...(data?.inbox ?? []), ...actionNotifs]);
        }
      }
    }
    setLivePlanId(null);
    setLivePlanNotes("");
  }
  function handleReopenPlan(planId) {
    const all = [...(allMeetingPlans ?? [])];
    const idx = all.findIndex(p => p.id === planId);
    if (idx >= 0) { const { status, endedAt, ...rest } = all[idx]; all[idx] = { ...rest, status: "aberta" }; onUpdate("meetingPlans", all); }
  }

  const typeColor = planType === "avaliação" ? "#8b5cf6" : "#3b82f6";

  // Group plans by month
  const grouped = {};
  filteredPlans.forEach(p => {
    const mk = p.plannedDate?.slice(0,7) ?? "sem-data";
    if (!grouped[mk]) grouped[mk] = [];
    grouped[mk].push(p);
  });

  // Pending actions count
  const pendingActions = allActions.filter(a => !a.done);

  // Sub-tabs config
  const activePendCount = allPendencias.filter(p=>p.status==="ativa").length;
  // v7: Ideias e Ocorrências viraram tabs top-level (em Planejamento/Operação).
  // Aqui dentro de Reuniões só mostramos Reuniões, Pendências e Relatório.
  const subTabs = [
    ["reunioes",`📅 Reuniões${pendingActions.length>0?` (${pendingActions.length})`:""}`],
    ["pendencias",`📌 Pendências${activePendCount>0?` (${activePendCount})`:""}`],
    ...(!isLider ? [["relatorio","📄 Relatório"]] : []),
  ];

  return (
    <div>
      {/* Area filter for Líder */}
      {isLider && managerAreas?.length > 0 && (
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12,flexWrap:"wrap"}}>
          <span style={{fontSize:11,color:"var(--text3)"}}>Áreas:</span>
          {managerAreas.map(a => {
            const col = AREA_COLORS[a] ?? "#888";
            return <span key={a} style={{fontSize:11,padding:"3px 10px",borderRadius:12,background:col+"18",color:col,fontWeight:600}}>{a}</span>;
          })}
          <button onClick={()=>setShowAllAreas(!showAllAreas)} style={{fontSize:10,padding:"3px 10px",borderRadius:12,border:"1px solid var(--border)",background:showAllAreas?"var(--ac)11":"transparent",color:showAllAreas?"var(--ac)":"var(--text3)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
            {showAllAreas ? "✓ Todas as áreas" : "Ver todas as áreas"}
          </button>
        </div>
      )}

      {/* Sub-tabs — ocultas quando v7 força um subTab via prop (página dedicada Ideias/Ocorrências) */}
      {!forceSubTab && (
        <div style={{display:"flex",gap:4,marginBottom:16,overflowX:"auto",scrollbarWidth:"none",WebkitOverflowScrolling:"touch"}}>
          {subTabs.map(([id,lbl]) => (
            <button key={id} onClick={()=>setSubTab(id)} style={{
              padding:mobileOnly?"8px 12px":"9px 16px",borderRadius:10,border:`1px solid ${subTab===id?"var(--ac)":"var(--border)"}`,
              background:subTab===id?"var(--ac)11":"transparent",color:subTab===id?"var(--ac)":"var(--text3)",
              cursor:"pointer",fontSize:mobileOnly?11:12,fontFamily:"'DM Sans',sans-serif",fontWeight:subTab===id?700:500,whiteSpace:"nowrap",flexShrink:0
            }}>{lbl}</button>
          ))}
        </div>
      )}

      {/* Pending actions reminder */}
      {pendingActions.length > 0 && subTab !== "reunioes" && (
        <div onClick={()=>setSubTab("reunioes")} style={{padding:"10px 14px",borderRadius:10,border:"1px solid #f59e0b33",background:"#f59e0b08",marginBottom:12,cursor:"pointer",display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:14}}>⚠️</span>
          <span style={{fontSize:12,color:"#f59e0b",fontWeight:600}}>{pendingActions.length} ação{pendingActions.length!==1?"ões":""} pendente{pendingActions.length!==1?"s":""}</span>
          <span style={{fontSize:10,color:"var(--text3)"}}>— clique para ver</span>
        </div>
      )}

      {/* ═══ SUB-TAB: REUNIÕES (equipe + liderança merged) ═══ */}
      {subTab === "reunioes" && (
        <div>
          {/* Action buttons */}
          {!showForm && (
            <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
              <button onClick={()=>setShowForm("equipe")} style={{...S.btnPrimary,fontSize:13,padding:"10px 20px",background:"#3b82f6",borderColor:"#3b82f6"}}>
                + Reunião com equipe
              </button>
              <button onClick={()=>{setShowForm("lideranca");const autoP=allPendencias.filter(p=>p.status==="ativa");const autoItems=autoP.map(p=>({id:`item-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,text:p.text,fromPendencia:p.id}));setPautaItems(autoItems);setPautaPickIdeas(autoP.map(p=>p.id));setPautaTitle("");setPautaDate("");}} style={{...S.btnPrimary,fontSize:13,padding:"10px 20px",background:"#10b981",borderColor:"#10b981"}}>
                + Reunião de liderança
              </button>
            </div>
          )}
          {showForm && (
            <div style={{marginBottom:4,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
              <button onClick={()=>{
                setShowForm(false);
                // v8.11.2: limpa estado de edição ao cancelar
                setEditingPlanId(null);
                setEditingPautaId(null);
                // v8.14.0: reset visibilidade
                setPlanVisibilityMode("default"); setPlanSharedWith([]);
                setPautaVisibilityMode("default"); setPautaSharedWith([]);
              }} style={{...S.btnSecondary,fontSize:12,padding:"6px 14px",color:"var(--red)",borderColor:"var(--red)44",marginBottom:12}}>✕ Cancelar</button>
              {(editingPlanId || editingPautaId) && (
                <span style={{fontSize:11,color:"var(--ac)",fontWeight:600,marginBottom:12}}>✏️ Editando — alterações substituem a reunião existente</span>
              )}
            </div>
          )}

          {/* ── Form: Reunião com equipe (alinhamento / avaliação) ── */}
          {showForm === "equipe" && (
            <div style={{...S.card,padding:"18px 20px",marginBottom:20,border:`1px solid ${typeColor}22`}}>
              <h4 style={{color:"var(--text)",margin:"0 0 14px",fontSize:15,fontWeight:700}}>👥 Reunião com equipe</h4>
              <div style={{display:"flex",gap:6,marginBottom:14}}>
                {[["alinhamento","💬 Alinhamento","#3b82f6"],["avaliação","📋 Avaliação","#8b5cf6"]].map(([val,lbl,col]) => (
                  <button key={val} onClick={()=>{setPlanType(val);setSelectedEmps([]);setSelectedAreas([]);}} style={{
                    flex:1,padding:"10px",borderRadius:10,border:`2px solid ${planType===val?col:"var(--border)"}`,
                    background:planType===val?col+"14":"transparent",color:planType===val?col:"var(--text3)",
                    cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12,fontWeight:planType===val?700:400
                  }}>{lbl}</button>
                ))}
              </div>

              {planType === "avaliação" && (
                <div style={{padding:"8px 12px",borderRadius:8,background:"#8b5cf608",border:"1px solid #8b5cf622",marginBottom:14,fontSize:11,color:"#8b5cf6"}}>
                  📋 Avaliação individual — selecione 1 empregado e monte a pauta com pontos fortes, a melhorar e notas.
                </div>
              )}

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
                <div>
                  <label style={S.label}>Data prevista</label>
                  <input type="date" value={planDate} onChange={e=>setPlanDate(e.target.value)} style={S.input}/>
                </div>
                <div>
                  <label style={S.label}>Observação <span style={{fontWeight:400,color:"var(--text3)"}}>(opcional)</span></label>
                  <input value={planNote} onChange={e=>setPlanNote(e.target.value)} placeholder={planType==="avaliação"?"Ex: Avaliação trimestral Q1":"Ex: Fechamento trimestral"} style={S.input}/>
                </div>
              </div>

              {/* Participant selection */}
              <label style={S.label}>{planType === "avaliação" ? "Empregado" : "Participantes"}</label>
              <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap"}}>
                {filteredAreas.map(area => {
                  const active = selectedAreas.includes(area);
                  const col = AREA_COLORS[area] ?? "#888";
                  return (
                    <button key={area} onClick={()=>toggleArea(area)} style={{
                      padding:"6px 14px",borderRadius:20,border:`1px solid ${active?col:"var(--border)"}`,
                      background:active?col+"22":"transparent",color:active?col:"var(--text3)",
                      cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12,fontWeight:active?700:400
                    }}>{area}</button>
                  );
                })}
              </div>
              {selectedAreas.length > 0 && (
                <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:14,maxHeight:160,overflowY:"auto",padding:4}}>
                  {filteredEmployees.filter(e => {
                    const r = roles.find(rl => rl.id === e.roleId);
                    return selectedAreas.includes(r?.area);
                  }).map(emp => {
                    const active = selectedEmps.includes(emp.id);
                    return (
                      <button key={emp.id} onClick={()=>{
                        if (planType === "avaliação") { setSelectedEmps(active ? [] : [emp.id]); }
                        else { toggleEmp(emp.id); }
                      }} style={{
                        padding:"4px 10px",borderRadius:8,border:`1px solid ${active?"var(--ac)":"var(--border)"}`,
                        background:active?"var(--ac-bg,#d4a01711)":"transparent",color:active?"var(--ac)":"var(--text3)",
                        cursor:"pointer",fontSize:11,fontFamily:"'DM Mono',monospace",fontWeight:active?600:400
                      }}>{active ? "✓ " : ""}{emp.name}</button>
                    );
                  })}
                </div>
              )}
              {selectedEmps.length > 0 && <div style={{fontSize:11,color:"var(--text3)",marginBottom:10}}>{selectedEmps.length} participante{selectedEmps.length!==1?"s":""} selecionado{selectedEmps.length!==1?"s":""}</div>}

              {/* ── Avaliação: 3 seções checklist ── */}
              {planType === "avaliação" && selectedEmps.length === 1 && (
                <div style={{marginBottom:14,padding:14,borderRadius:10,border:"1px solid #8b5cf622",background:"#8b5cf604"}}>
                  <div style={{fontSize:12,fontWeight:700,color:"var(--text)",marginBottom:10}}>Pauta da avaliação</div>
                  <div style={{display:"flex",gap:4,marginBottom:10}}>
                    {[["fortes","💪 Fortes","#10b981"],["melhorar","📈 Melhorar","#f59e0b"],["notas","📝 Notas","#6366f1"]].map(([sec,lbl,col]) => (
                      <button key={sec} onClick={()=>setAvalSection(sec)} style={{
                        flex:1,padding:"6px 6px",borderRadius:8,border:`1px solid ${avalSection===sec?col:"var(--border)"}`,
                        background:avalSection===sec?col+"14":"transparent",color:avalSection===sec?col:"var(--text3)",
                        cursor:"pointer",fontSize:11,fontFamily:"'DM Sans',sans-serif",fontWeight:avalSection===sec?700:400,minWidth:0
                      }}>{lbl}</button>
                    ))}
                  </div>
                  {/* Items for current section */}
                  {(() => {
                    const items = avalSection === "fortes" ? avalPontosFortes : avalSection === "melhorar" ? avalPontosMelhorar : avalNotas;
                    const setItems = avalSection === "fortes" ? setAvalPontosFortes : avalSection === "melhorar" ? setAvalPontosMelhorar : setAvalNotas;
                    const secColor = avalSection === "fortes" ? "#10b981" : avalSection === "melhorar" ? "#f59e0b" : "#6366f1";
                    return (
                      <div>
                        {items.map((item, idx) => (
                          <div key={idx} style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                            <span style={{fontSize:10,color:secColor}}>•</span>
                            <span style={{flex:1,fontSize:12,color:"var(--text)"}}>{item}</span>
                            <button onClick={()=>setItems(prev=>prev.filter((_,i)=>i!==idx))} style={{padding:"2px 6px",borderRadius:4,border:"1px solid var(--border)",background:"transparent",color:"var(--text3)",cursor:"pointer",fontSize:9}}>✕</button>
                          </div>
                        ))}
                        <div style={{display:"flex",gap:6,marginTop:6}}>
                          <input value={avalNewItem} onChange={e=>setAvalNewItem(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&avalNewItem.trim()){setItems(prev=>[...prev,avalNewItem.trim()]);setAvalNewItem("");}}} placeholder={avalSection==="fortes"?"Ex: Pontualidade, liderança...":avalSection==="melhorar"?"Ex: Comunicação, organização...":"Ex: Observação interna..."} style={{...S.input,flex:1,fontSize:12,padding:"8px 10px",minWidth:0}}/>
                          <button onClick={()=>{if(avalNewItem.trim()){setItems(prev=>[...prev,avalNewItem.trim()]);setAvalNewItem("");}}} style={{background:secColor,color:"#fff",border:"none",borderRadius:8,cursor:"pointer",fontSize:14,fontWeight:700,width:36,minWidth:36,maxWidth:36,height:36,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>+</button>
                        </div>
                      </div>
                    );
                  })()}
                  {/* Summary */}
                  <div style={{display:"flex",gap:10,marginTop:10,fontSize:10,color:"var(--text3)"}}>
                    <span style={{color:"#10b981"}}>💪 {avalPontosFortes.length}</span>
                    <span style={{color:"#f59e0b"}}>📈 {avalPontosMelhorar.length}</span>
                    <span style={{color:"#6366f1"}}>📝 {avalNotas.length}</span>
                  </div>
                </div>
              )}

              {/* ── Alinhamento: topics checklist ── */}
              {planType === "alinhamento" && (
                <div style={{marginBottom:14,padding:14,borderRadius:10,border:"1px solid #3b82f622",background:"#3b82f604"}}>
                  <div style={{fontSize:12,fontWeight:700,color:"var(--text)",marginBottom:10}}>Temas a tratar</div>
                  {alinhTopics.map((topic, idx) => (
                    <div key={idx} style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                      <span style={{fontSize:10,color:"#3b82f6",fontWeight:700,minWidth:18}}>{idx+1}.</span>
                      <span style={{flex:1,fontSize:12,color:"var(--text)"}}>{topic}</span>
                      <button onClick={()=>setAlinhTopics(prev=>prev.filter((_,i)=>i!==idx))} style={{padding:"2px 6px",borderRadius:4,border:"1px solid var(--border)",background:"transparent",color:"var(--text3)",cursor:"pointer",fontSize:9}}>✕</button>
                    </div>
                  ))}
                  <div style={{display:"flex",gap:4,marginTop:6,alignItems:"center"}}>
                    <input value={alinhNewTopic} onChange={e=>setAlinhNewTopic(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&alinhNewTopic.trim()){setAlinhTopics(prev=>[...prev,alinhNewTopic.trim()]);setAlinhNewTopic("");}}} placeholder="Ex: Escala do próximo mês, treinamento..." style={{...S.input,flex:1,fontSize:12,padding:"8px 10px",minWidth:0}}/>
                    <button onClick={()=>{if(alinhNewTopic.trim()){setAlinhTopics(prev=>[...prev,alinhNewTopic.trim()]);setAlinhNewTopic("");}}} style={{background:ac,color:"#fff",border:"none",borderRadius:8,cursor:"pointer",fontSize:14,fontWeight:700,width:36,minWidth:36,maxWidth:36,height:36,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>+</button>
                  </div>
                </div>
              )}

              {/* v8.14.0: Bloco de visibilidade — quem pode ver esta reunião */}
              <div style={{padding:"12px 14px",background:"var(--bg2)",border:"1px solid var(--border)",borderRadius:10,marginBottom:14,marginTop:12}}>
                <div style={{fontSize:11,fontWeight:700,color:"var(--text2)",textTransform:"uppercase",letterSpacing:0.4,marginBottom:8}}>🔒 Quem pode ver esta reunião</div>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {[
                    {value:"default", label:"🌐 Padrão", desc:"Gestores admin + líderes da área dos participantes"},
                    {value:"shared",  label:"👥 Compartilhada", desc:"Eu + Owner + DP + pessoas que eu escolher"},
                    {value:"private", label:"🔒 Privada", desc:"Só eu + Owner + DP — outros gestores e líderes não veem"},
                  ].map(opt => (
                    <label key={opt.value} style={{display:"flex",alignItems:"flex-start",gap:8,padding:"8px 10px",borderRadius:8,cursor:"pointer",border:`1px solid ${planVisibilityMode===opt.value?"var(--ac)":"var(--border)"}`,background:planVisibilityMode===opt.value?"var(--ac)11":"transparent"}}>
                      <input type="radio" checked={planVisibilityMode===opt.value} onChange={()=>setPlanVisibilityMode(opt.value)} style={{marginTop:2,accentColor:"var(--ac)"}}/>
                      <div style={{flex:1}}>
                        <div style={{fontSize:12,fontWeight:600,color:"var(--text)"}}>{opt.label}</div>
                        <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>{opt.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
                {planVisibilityMode === "shared" && (
                  <div style={{marginTop:10,padding:"10px 12px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:8}}>
                    <div style={{fontSize:11,color:"var(--text2)",fontWeight:600,marginBottom:8}}>Quem mais pode ver (além de mim, Owner e DP):</div>
                    {shareableManagers.length === 0 ? (
                      <div style={{fontSize:11,color:"var(--text3)",fontStyle:"italic"}}>Nenhum outro gestor disponível.</div>
                    ) : (
                      <div style={{display:"flex",flexDirection:"column",gap:4,maxHeight:180,overflow:"auto"}}>
                        {shareableManagers.map(m => {
                          const checked = planSharedWith.includes(m.id);
                          return (
                            <label key={m.id} style={{display:"flex",alignItems:"center",gap:8,padding:"4px 6px",borderRadius:6,cursor:"pointer",fontSize:12}}>
                              <input type="checkbox" checked={checked} onChange={()=>{
                                setPlanSharedWith(prev => checked ? prev.filter(x=>x!==m.id) : [...prev, m.id]);
                              }} style={{accentColor:"var(--ac)"}}/>
                              <span style={{color:"var(--text)"}}>{m.name}</span>
                              <span style={{color:"var(--text3)",fontSize:10,marginLeft:"auto"}}>{m.role}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                <div style={{fontSize:10,color:"var(--text3)",marginTop:8,fontStyle:"italic",lineHeight:1.4}}>
                  ℹ️ Em qualquer modo, o empregado da reunião <strong>nunca</strong> vê pontos fortes, melhorias ou notas internas — só sabe que tem reunião naquela data.
                </div>
              </div>

              <button onClick={handleCreatePlan} style={{...S.btnPrimary,width:"auto",padding:"10px 24px",background:typeColor,borderColor:typeColor}}>{editingPlanId ? "Salvar alterações" : "Criar reunião"}</button>
            </div>
          )}

          {/* ── Form: Reunião de liderança (pauta com template) ── */}
          {showForm === "lideranca" && (
            <div style={{...S.card,padding:"18px 20px",marginBottom:20,border:"1px solid #10b98122"}}>
              <h4 style={{color:"var(--text)",margin:"0 0 14px",fontSize:15,fontWeight:700}}>🤝 Reunião de liderança</h4>

              {/* Apply template button */}
              {pautaItems.length === 0 && (
                <button onClick={()=>{setPautaType("semanal");handleApplyTemplate("semanal");}} style={{...S.btnSecondary,fontSize:12,padding:"8px 14px",marginBottom:14,borderColor:"#10b98144",color:"#10b981"}}>
                  📋 Usar template da reunião semanal
                </button>
              )}

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
                <div>
                  <label style={S.label}>Título da pauta</label>
                  <input value={pautaTitle} onChange={e=>setPautaTitle(e.target.value)} placeholder="Ex: Reunião semanal 14/04" style={S.input}/>
                </div>
                <div>
                  <label style={S.label}>Data</label>
                  <input type="date" value={pautaDate} onChange={e=>setPautaDate(e.target.value)} style={S.input}/>
                </div>
              </div>

              {/* Participants */}
              <label style={S.label}>Participantes</label>
              <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:10}}>
                {(data?.managers ?? []).filter(mg => mg.restaurants?.includes(restaurantId)).map(mg => {
                  const active = lidParticipants.includes(mg.id);
                  return (
                    <button key={mg.id} onClick={()=>setLidParticipants(prev=>prev.includes(mg.id)?prev.filter(x=>x!==mg.id):[...prev,mg.id])} style={{
                      padding:"5px 12px",borderRadius:8,border:`1px solid ${active?"#10b981":"var(--border)"}`,
                      background:active?"#10b98118":"transparent",color:active?"#10b981":"var(--text3)",
                      cursor:"pointer",fontSize:11,fontFamily:"'DM Mono',monospace",fontWeight:active?600:400
                    }}>{active?"✓ ":""}{mg.name}{mg.areas?.length>0?` (${mg.areas.join(", ")})`:""}</button>
                  );
                })}
              </div>
              {lidParticipants.length > 0 && <div style={{fontSize:11,color:"var(--text3)",marginBottom:12}}>{lidParticipants.length} participante{lidParticipants.length!==1?"s":""}</div>}

              {/* Items list */}
              <label style={S.label}>Itens da pauta</label>
              <div style={{marginBottom:12}}>
                {pautaItems.map((item, idx) => (
                  <div key={item.id} style={{display:"flex",alignItems:"center",gap:6,marginBottom:4,padding:"6px 10px",borderRadius:8,background:"var(--bg2)",border:"1px solid var(--border)"}}>
                    <span style={{color:"var(--text3)",fontSize:10,fontWeight:700,minWidth:20}}>{idx+1}.</span>
                    <span style={{flex:1,fontSize:12,color:"var(--text)"}}>{item.text}</span>
                    {item.fromIdea && <span style={{fontSize:9,padding:"1px 5px",borderRadius:4,background:"#f59e0b18",color:"#f59e0b"}}>💡</span>}
                    {item.fromOccurrence && <span style={{fontSize:9,padding:"1px 5px",borderRadius:4,background:"#ef444418",color:"#ef4444"}}>🚨</span>}
                    {item.fromIncident && <span style={{fontSize:9,padding:"1px 5px",borderRadius:4,background:"#8b5cf618",color:"#8b5cf6"}}>👤</span>}
                    {item.fromPendencia && <span style={{fontSize:9,padding:"1px 5px",borderRadius:4,background:"#6366f118",color:"#6366f1"}}>📌</span>}
                    <button onClick={()=>handleMovePautaItem(idx,-1)} disabled={idx===0} style={{padding:"2px 5px",border:"none",background:"transparent",color:idx===0?"var(--border)":"var(--text3)",cursor:idx===0?"default":"pointer",fontSize:10}}>▲</button>
                    <button onClick={()=>handleMovePautaItem(idx,1)} disabled={idx===pautaItems.length-1} style={{padding:"2px 5px",border:"none",background:"transparent",color:idx===pautaItems.length-1?"var(--border)":"var(--text3)",cursor:idx===pautaItems.length-1?"default":"pointer",fontSize:10}}>▼</button>
                    <button onClick={()=>handleRemovePautaItem(item.id)} style={{padding:"2px 5px",border:"none",background:"transparent",color:"var(--red)",cursor:"pointer",fontSize:10}}>✕</button>
                  </div>
                ))}
              </div>

              {/* Add custom item */}
              <div style={{display:"flex",gap:6,marginBottom:12}}>
                <input value={pautaNewItem} onChange={e=>setPautaNewItem(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")handleAddPautaItem();}} placeholder="Adicionar item..." style={{...S.input,flex:1}}/>
                <button onClick={handleAddPautaItem} style={{...S.btnSecondary,fontSize:12,padding:"8px 14px",whiteSpace:"nowrap"}}>+ Adicionar</button>
              </div>

              {/* Pull ideas */}
              {filteredIdeas.filter(i=>i.status==="nova" && !pautaPickIdeas.includes(i.id)).length > 0 && (
                <div style={{marginBottom:14}}>
                  <label style={{...S.label,display:"flex",alignItems:"center",gap:4}}>💡 Puxar ideias do banco</label>
                  <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                    {filteredIdeas.filter(i=>i.status==="nova" && !pautaPickIdeas.includes(i.id)).map(idea => (
                      <button key={idea.id} onClick={()=>handleAddIdeaToPauta(idea)} style={{
                        padding:"5px 10px",borderRadius:8,border:"1px solid #f59e0b44",background:"#f59e0b08",color:"var(--text2)",
                        cursor:"pointer",fontSize:11,fontFamily:"'DM Sans',sans-serif"
                      }}>💡 {idea.title}</button>
                    ))}
                  </div>
                </div>
              )}

              {/* Pull occurrences */}
              {filteredOccurrences.filter(o=>o.status==="nova" && !pautaPickIdeas.includes(o.id)).length > 0 && (
                <div style={{marginBottom:14}}>
                  <label style={{...S.label,display:"flex",alignItems:"center",gap:4}}>🚨 Puxar ocorrências</label>
                  <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                    {filteredOccurrences.filter(o=>o.status==="nova" && !pautaPickIdeas.includes(o.id)).map(occ => {
                      const occT = OCC_TYPES.find(([t])=>t===occ.type);
                      return (
                        <button key={occ.id} onClick={()=>handleAddOccToPauta(occ)} style={{
                          padding:"5px 10px",borderRadius:8,border:"1px solid #ef444444",background:"#ef444408",color:"var(--text2)",
                          cursor:"pointer",fontSize:11,fontFamily:"'DM Sans',sans-serif"
                        }}>{occT?.[1]??""} {occ.title}</button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Pull employee incidents */}
              {(() => {
                const recentIncidents = (data?.incidents ?? []).filter(i => i.restaurantId === restaurantId && !i.deletedAt && !pautaPickIdeas.includes(i.id)).sort((a,b)=>(b.createdAt??"").localeCompare(a.createdAt??"")).slice(0, 20);
                return recentIncidents.length > 0 ? (
                  <div style={{marginBottom:14}}>
                    <label style={{...S.label,display:"flex",alignItems:"center",gap:4}}>👤 Puxar ocorrências de empregados</label>
                    <div style={{display:"flex",gap:4,flexWrap:"wrap",maxHeight:120,overflowY:"auto"}}>
                      {recentIncidents.map(inc => {
                        const empNames = (inc.employeeIds ?? []).map(eid => { const e = employees.find(emp => emp.id === eid); return shortName(e?.name); }).filter(Boolean).join(", ");
                        return (
                          <button key={inc.id} onClick={()=>handleAddIncidentToPauta(inc)} style={{
                            padding:"5px 10px",borderRadius:8,border:"1px solid #8b5cf644",background:"#8b5cf608",color:"var(--text2)",
                            cursor:"pointer",fontSize:11,fontFamily:"'DM Sans',sans-serif"
                          }}>👤 {empNames ? `${empNames}: ` : ""}{(inc.description ?? "").slice(0,40)}{(inc.description??"").length>40?"...":""}</button>
                        );
                      })}
                    </div>
                  </div>
                ) : null;
              })()}

              {/* Pull pendências */}
              {allPendencias.filter(p=>p.status==="ativa" && !pautaPickIdeas.includes(p.id)).length > 0 && (
                <div style={{marginBottom:14}}>
                  <label style={{...S.label,display:"flex",alignItems:"center",gap:4}}>📌 Puxar pendências anteriores</label>
                  <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                    {allPendencias.filter(p=>p.status==="ativa" && !pautaPickIdeas.includes(p.id)).map(pend => {
                      const typeIcon = pend.type === "pendente" ? "⏸" : "💭";
                      return (
                        <button key={pend.id} onClick={()=>{setPautaPickIdeas(prev=>[...prev,pend.id]);setPautaItems(prev=>[...prev,{id:`item-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,text:pend.text,fromPendencia:pend.id}]);}} style={{padding:"4px 10px",borderRadius:6,border:"1px solid #8b5cf622",background:"#8b5cf608",color:"#8b5cf6",cursor:"pointer",fontSize:11,fontFamily:"'DM Sans',sans-serif"}}>
                          {typeIcon} {pend.text.length > 40 ? pend.text.slice(0,40)+"…" : pend.text}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* v8.14.0: Bloco de visibilidade da pauta */}
              <div style={{padding:"12px 14px",background:"var(--bg2)",border:"1px solid var(--border)",borderRadius:10,marginBottom:14,marginTop:12}}>
                <div style={{fontSize:11,fontWeight:700,color:"var(--text2)",textTransform:"uppercase",letterSpacing:0.4,marginBottom:8}}>🔒 Quem pode ver esta pauta</div>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {[
                    {value:"default", label:"🌐 Padrão", desc:"Todos os gestores admin e líderes"},
                    {value:"shared",  label:"👥 Compartilhada", desc:"Eu + Owner + DP + pessoas que eu escolher"},
                    {value:"private", label:"🔒 Privada", desc:"Só eu + Owner + DP"},
                  ].map(opt => (
                    <label key={opt.value} style={{display:"flex",alignItems:"flex-start",gap:8,padding:"8px 10px",borderRadius:8,cursor:"pointer",border:`1px solid ${pautaVisibilityMode===opt.value?"#10b981":"var(--border)"}`,background:pautaVisibilityMode===opt.value?"#10b98111":"transparent"}}>
                      <input type="radio" checked={pautaVisibilityMode===opt.value} onChange={()=>setPautaVisibilityMode(opt.value)} style={{marginTop:2,accentColor:"#10b981"}}/>
                      <div style={{flex:1}}>
                        <div style={{fontSize:12,fontWeight:600,color:"var(--text)"}}>{opt.label}</div>
                        <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>{opt.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
                {pautaVisibilityMode === "shared" && (
                  <div style={{marginTop:10,padding:"10px 12px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:8}}>
                    <div style={{fontSize:11,color:"var(--text2)",fontWeight:600,marginBottom:8}}>Quem mais pode ver (além de mim, Owner e DP):</div>
                    {shareableManagers.length === 0 ? (
                      <div style={{fontSize:11,color:"var(--text3)",fontStyle:"italic"}}>Nenhum outro gestor disponível.</div>
                    ) : (
                      <div style={{display:"flex",flexDirection:"column",gap:4,maxHeight:180,overflow:"auto"}}>
                        {shareableManagers.map(m => {
                          const checked = pautaSharedWith.includes(m.id);
                          return (
                            <label key={m.id} style={{display:"flex",alignItems:"center",gap:8,padding:"4px 6px",borderRadius:6,cursor:"pointer",fontSize:12}}>
                              <input type="checkbox" checked={checked} onChange={()=>{
                                setPautaSharedWith(prev => checked ? prev.filter(x=>x!==m.id) : [...prev, m.id]);
                              }} style={{accentColor:"#10b981"}}/>
                              <span style={{color:"var(--text)"}}>{m.name}</span>
                              <span style={{color:"var(--text3)",fontSize:10,marginLeft:"auto"}}>{m.role}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <button onClick={handleSavePauta} style={{...S.btnPrimary,width:"auto",padding:"10px 24px",background:"#10b981",borderColor:"#10b981"}}>{editingPautaId ? "Salvar alterações" : "Salvar pauta"}</button>
            </div>
          )}

          {/* ── Reuniões de equipe (plans) ── */}
          {Object.keys(grouped).length > 0 && !livePlanId && (
            <div style={{marginBottom:20}}>
              <h4 style={{color:"var(--text)",fontSize:14,fontWeight:700,margin:"0 0 12px"}}>👥 Reuniões com equipe</h4>
              {Object.entries(grouped).sort(([a],[b])=>b.localeCompare(a)).map(([mk, plans]) => {
                const [y,m] = mk.split("-");
                const monthName = new Date(parseInt(y), parseInt(m)-1, 15).toLocaleDateString("pt-BR", {month:"long", year:"numeric"});
                return (
                  <div key={mk} style={{marginBottom:16}}>
                    <div style={{fontSize:12,fontWeight:600,color:"var(--text3)",marginBottom:6,textTransform:"capitalize"}}>{monthName}</div>
                    {plans.map(plan => {
                      const pColor = plan.type === "avaliação" ? "#8b5cf6" : "#3b82f6";
                      const pIcon = plan.type === "avaliação" ? "📋" : "💬";
                      const totalEmps = (plan.employeeIds ?? []).length;
                      const doneCount = (plan.employeeIds ?? []).filter(eid => getEmpStatus(plan, eid) === "realizada").length;
                      const lateCount = (plan.employeeIds ?? []).filter(eid => getEmpStatus(plan, eid) === "atrasada").length;
                      const dateStr = plan.plannedDate ? new Date(plan.plannedDate+"T12:00:00").toLocaleDateString("pt-BR") : "—";
                      const isPast = plan.plannedDate < today();
                      return (
                        <div key={plan.id} style={{...S.card,padding:"14px 16px",marginBottom:8,border:`1px solid ${isPast && doneCount < totalEmps ? "#f59e0b33" : pColor+"22"}`}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                            <div style={{display:"flex",alignItems:"center",gap:8}}>
                              <span style={{fontSize:16}}>{pIcon}</span>
                              <span style={{color:pColor,fontWeight:700,fontSize:13}}>Conversa de {plan.type}</span>
                              <span style={{color:"var(--text3)",fontSize:11}}>— {dateStr}</span>
                            </div>
                            <div style={{display:"flex",alignItems:"center",gap:6}}>
                              <span style={{fontSize:10,color:doneCount===totalEmps?"#10b981":lateCount>0?"#f59e0b":"var(--text3)",fontWeight:700}}>
                                {doneCount}/{totalEmps} {doneCount===totalEmps?"✓":""}
                              </span>
                              <button onClick={()=>handleDeletePlan(plan.id)} style={{padding:"2px 6px",borderRadius:6,border:"1px solid var(--red)33",background:"transparent",color:"var(--red)",cursor:"pointer",fontSize:9,fontFamily:"'DM Mono',monospace"}}>✕</button>
                            </div>
                          </div>
                          {plan.note && <div style={{fontSize:11,color:"var(--text3)",marginBottom:8,fontStyle:"italic"}}>{plan.note}</div>}
                          <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                            {(plan.employeeIds ?? []).map(eid => {
                              const emp = employees.find(e => e.id === eid);
                              const status = getEmpStatus(plan, eid);
                              const sColor = status === "realizada" ? "#10b981" : status === "atrasada" ? "#f59e0b" : "var(--text3)";
                              const sIcon = status === "realizada" ? "✓" : status === "atrasada" ? "⏰" : "○";
                              return (
                                <span key={eid} style={{fontSize:10,padding:"3px 8px",borderRadius:6,border:`1px solid ${sColor}33`,background:sColor+"08",color:sColor,fontWeight:status==="realizada"?700:400}}>
                                  {sIcon} {shortName(emp?.name) || eid}
                                </span>
                              );
                            })}
                          </div>
                          {/* Avaliação checklist summary */}
                          {plan.type === "avaliação" && (plan.pontosFortes?.length > 0 || plan.pontosMelhorar?.length > 0 || plan.notasInternas?.length > 0) && (
                            <div style={{marginTop:10,padding:"8px 10px",borderRadius:8,background:"#8b5cf606",border:"1px solid #8b5cf611"}}>
                              {(plan.pontosFortes ?? []).length > 0 && (
                                <div style={{marginBottom:4}}>
                                  <span style={{fontSize:10,color:"#10b981",fontWeight:600}}>💪 Pontos fortes:</span>
                                  {plan.pontosFortes.map((p,i) => <span key={i} style={{fontSize:10,color:"var(--text3)",marginLeft:4}}>{p.done?"✓ ":"○ "}{p.text}{i<plan.pontosFortes.length-1?", ":""}</span>)}
                                </div>
                              )}
                              {(plan.pontosMelhorar ?? []).length > 0 && (
                                <div style={{marginBottom:4}}>
                                  <span style={{fontSize:10,color:"#f59e0b",fontWeight:600}}>📈 A melhorar:</span>
                                  {plan.pontosMelhorar.map((p,i) => <span key={i} style={{fontSize:10,color:"var(--text3)",marginLeft:4}}>{p.done?"✓ ":"○ "}{p.text}{i<plan.pontosMelhorar.length-1?", ":""}</span>)}
                                </div>
                              )}
                              {(plan.notasInternas ?? []).length > 0 && (
                                <div>
                                  <span style={{fontSize:10,color:"#6366f1",fontWeight:600}}>📝 Notas:</span>
                                  {plan.notasInternas.map((p,i) => <span key={i} style={{fontSize:10,color:"var(--text3)",marginLeft:4}}>{p.done?"✓ ":"○ "}{p.text}{i<plan.notasInternas.length-1?", ":""}</span>)}
                                </div>
                              )}
                            </div>
                          )}
                          {/* Alinhamento topics summary */}
                          {plan.type === "alinhamento" && (plan.topics ?? []).length > 0 && (
                            <div style={{marginTop:10,padding:"8px 10px",borderRadius:8,background:"#3b82f606",border:"1px solid #3b82f611"}}>
                              <span style={{fontSize:10,color:"#3b82f6",fontWeight:600}}>Temas: </span>
                              {plan.topics.map((t,i) => <span key={i} style={{fontSize:10,color:"var(--text3)"}}>{t.done?"✓ ":"○ "}{t.text}{i<plan.topics.length-1?" · ":""}</span>)}
                            </div>
                          )}
                          {/* Action buttons */}
                          <div style={{display:"flex",gap:6,marginTop:10,flexWrap:"wrap"}}>
                            {(!plan.status || plan.status === "aberta") && (
                              <button onClick={()=>handleStartLivePlan(plan.id)} style={{...S.btnPrimary,fontSize:11,padding:"6px 14px",background:pColor,borderColor:pColor}}>
                                ▶ Iniciar conversa ao vivo
                              </button>
                            )}
                            {/* v8.11.2: editar reunião antes de iniciar */}
                            {(!plan.status || plan.status === "aberta") && (
                              <button onClick={()=>handleStartEditPlan(plan)} style={{...S.btnSecondary,fontSize:11,padding:"6px 12px",color:pColor,borderColor:pColor+"44"}} title="Editar antes de iniciar">
                                ✏️ Editar
                              </button>
                            )}
                            {plan.status === "em_andamento" && (
                              <button onClick={()=>{setLivePlanId(plan.id);setLivePlanNotes(plan.meetingNotes??"");}} style={{...S.btnPrimary,fontSize:11,padding:"6px 14px",background:"#f59e0b",borderColor:"#f59e0b"}}>
                                ▶ Continuar conversa
                              </button>
                            )}
                            {plan.status === "encerrada" && (
                              <button onClick={()=>handleReopenPlan(plan.id)} style={{...S.btnSecondary,fontSize:10,padding:"5px 10px",color:pColor,borderColor:pColor+"44"}}>
                                Reabrir
                              </button>
                            )}
                            {plan.status === "encerrada" && (
                              <span style={{fontSize:10,color:"#10b981",fontWeight:600,display:"flex",alignItems:"center",gap:4}}>✓ Encerrada{plan.endedAt ? ` em ${new Date(plan.endedAt).toLocaleDateString("pt-BR")}` : ""}</span>
                            )}
                            {plan.editedAt && (
                              <span style={{fontSize:9,color:"var(--text3)",fontStyle:"italic",alignSelf:"center"}} title={`Editada por ${plan.editedBy} em ${new Date(plan.editedAt).toLocaleString("pt-BR")}`}>✏️ editada</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Live team meeting execution (avaliação/alinhamento) ── */}
          {livePlanId && (() => {
            const livePlan = activePlans.find(p => p.id === livePlanId);
            if (!livePlan) return null;
            const lColor = livePlan.type === "avaliação" ? "#8b5cf6" : "#3b82f6";
            const lIcon = livePlan.type === "avaliação" ? "📋" : "💬";
            const empNames = (livePlan.employeeIds ?? []).map(eid => { const e = employees.find(emp => emp.id === eid); return e?.name ?? eid; });

            // Collect all checklist sections
            const sections = livePlan.type === "avaliação" ? [
              { key: "pontosFortes", label: "💪 Pontos fortes", color: "#10b981", items: livePlan.pontosFortes ?? [] },
              { key: "pontosMelhorar", label: "📈 A melhorar", color: "#f59e0b", items: livePlan.pontosMelhorar ?? [] },
              { key: "notasInternas", label: "📝 Notas internas", color: "#6366f1", items: livePlan.notasInternas ?? [] },
            ] : [
              { key: "topics", label: "📋 Temas a tratar", color: "#3b82f6", items: livePlan.topics ?? [] },
            ];
            const totalItems = sections.reduce((s,sec) => s + sec.items.length, 0);
            const doneItems = sections.reduce((s,sec) => s + sec.items.filter(it => it.done).length, 0);

            return (
              <div style={{...S.card,padding:0,marginBottom:20,border:`2px solid ${lColor}44`,overflow:"hidden"}}>
                <div style={{padding:"16px 18px",background:lColor+"08",borderBottom:`1px solid ${lColor}22`}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:18}}>{lIcon}</span>
                      <span style={{color:"var(--text)",fontWeight:700,fontSize:15}}>Conversa de {livePlan.type}</span>
                      <span style={{fontSize:10,padding:"2px 8px",borderRadius:6,background:lColor+"18",color:lColor,fontWeight:600}}>AO VIVO</span>
                    </div>
                    <button onClick={()=>handleEndLivePlan(livePlanId)} style={{...S.btnPrimary,fontSize:12,padding:"8px 16px",background:"#10b981",borderColor:"#10b981"}}>✓ Encerrar</button>
                  </div>
                  <div style={{fontSize:11,color:"var(--text3)"}}>
                    {empNames.join(", ")} — {livePlan.plannedDate ? new Date(livePlan.plannedDate+"T12:00:00").toLocaleDateString("pt-BR") : ""}
                    {totalItems > 0 && <span style={{marginLeft:8,fontWeight:600}}>{doneItems}/{totalItems} itens</span>}
                  </div>
                </div>
                <div style={{padding:"16px 18px"}}>
                  {sections.map(sec => (
                    <div key={sec.key} style={{marginBottom:16}}>
                      <div style={{fontSize:12,fontWeight:700,color:sec.color,marginBottom:8}}>{sec.label}</div>
                      {sec.items.length === 0 && <div style={{fontSize:11,color:"var(--text3)",fontStyle:"italic"}}>Nenhum item cadastrado</div>}
                      {sec.items.map((item, idx) => (
                        <div key={idx} style={{display:"flex",gap:8,alignItems:"flex-start",marginBottom:8,padding:"8px 10px",borderRadius:8,border:`1px solid ${item.done?sec.color+"33":"var(--border)"}`,background:item.done?sec.color+"06":"transparent"}}>
                          <button onClick={()=>handleTogglePlanItem(livePlanId, sec.key, idx)} style={{marginTop:2,width:20,height:20,minWidth:20,borderRadius:6,border:`2px solid ${item.done?sec.color:"var(--border)"}`,background:item.done?sec.color:"transparent",color:"#fff",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,flexShrink:0}}>
                            {item.done?"✓":""}
                          </button>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:12,color:item.done?"var(--text3)":"var(--text)",textDecoration:item.done?"line-through":"none"}}>{item.text}</div>
                            <textarea value={item.notes??""} onChange={e=>handleUpdatePlanItemNotes(livePlanId, sec.key, idx, e.target.value)} rows={1} placeholder="Anotações..." style={{...S.input,fontSize:11,padding:"4px 8px",resize:"vertical",width:"100%",boxSizing:"border-box",marginTop:4}}/>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                  {/* General notes */}
                  <div style={{marginTop:8,borderTop:"1px solid var(--border)",paddingTop:12}}>
                    <div style={{fontSize:12,fontWeight:700,color:"var(--text)",marginBottom:6}}>Anotações gerais</div>
                    <textarea value={livePlanNotes} onChange={e=>setLivePlanNotes(e.target.value)} rows={3} placeholder="Observações da conversa..." style={{...S.input,fontSize:12,padding:"8px 10px",resize:"vertical",width:"100%",boxSizing:"border-box"}}/>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── Live meeting execution ── */}
          {livePautaId && (() => {
            const livePauta = allPautas.find(p => p.id === livePautaId);
            if (!livePauta) return null;
            const liveActions = allActions.filter(a => a.pautaId === livePautaId);
            const liveDone = (livePauta.items ?? []).filter(it => it.status === "resolvido" || it.status === "acao_definida" || it.status === "pendente" || it.status === "sem_resolucao").length;
            const liveTotal = (livePauta.items ?? []).length;
            const managers = data?.managers ?? [];
            const pParts = (livePauta.participants ?? []).map(pid => managers.find(m => m.id === pid)).filter(Boolean);
            return (
              <div style={{...S.card,padding:0,marginBottom:20,border:"2px solid #f59e0b44",overflow:"hidden"}}>
                <div style={{background:"#f59e0b08",padding:"14px 16px",borderBottom:"1px solid #f59e0b22"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                        <span style={{fontSize:10,padding:"3px 8px",borderRadius:6,background:"#f59e0b22",color:"#f59e0b",fontWeight:700,letterSpacing:0.5,textTransform:"uppercase"}}>Ao vivo</span>
                        <span style={{color:"var(--text)",fontWeight:700,fontSize:15}}>{livePauta.title}</span>
                      </div>
                      <div style={{fontSize:11,color:"var(--text3)"}}>{liveDone}/{liveTotal} itens concluídos</div>
                      {pParts.length > 0 && <div style={{fontSize:10,color:"var(--text3)",marginTop:2}}>👥 {pParts.map(p=>p.name).join(", ")}</div>}
                    </div>
                    <button onClick={()=>handleEndLiveMeeting(livePautaId)} style={{...S.btnPrimary,fontSize:12,padding:"8px 16px",background:"#10b981",borderColor:"#10b981"}}>✓ Encerrar reunião</button>
                  </div>
                </div>
                <div style={{padding:"14px 16px"}}>
                  {(livePauta.items ?? []).map((item, idx) => {
                    const newStatusOpts = [["pendente","⏸","var(--text3)","Pendente"],["sem_resolucao","💭","#8b5cf6","Sem resolução"],["resolvido","✓","#10b981","Resolvido"],["acao_definida","⚡","#f59e0b","Ação definida"]];
                    const curSt = newStatusOpts.find(([s])=>s===item.status) ?? newStatusOpts[0];
                    return (
                      <div key={item.id} style={{padding:"12px 14px",marginBottom:8,borderRadius:10,background:item.status==="resolvido"?"#10b98108":item.status==="acao_definida"?"#f59e0b08":item.status==="sem_resolucao"?"#8b5cf608":"var(--bg2)",border:`1px solid ${curSt[2]}33`}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                          <span style={{fontSize:11,color:"var(--text3)",fontWeight:700,minWidth:22}}>{idx+1}.</span>
                          <span style={{flex:1,fontSize:13,color:"var(--text)",fontWeight:600,textDecoration:item.status==="resolvido"?"line-through":"none"}}>{item.text}</span>
                          {item.fromIdea && <span style={{fontSize:9,padding:"1px 5px",borderRadius:4,background:"#f59e0b18",color:"#f59e0b"}}>💡</span>}
                          {item.fromOccurrence && <span style={{fontSize:9,padding:"1px 5px",borderRadius:4,background:"#ef444418",color:"#ef4444"}}>🚨</span>}
                          {item.fromIncident && <span style={{fontSize:9,padding:"1px 5px",borderRadius:4,background:"#8b5cf618",color:"#8b5cf6"}}>👤</span>}
                    {item.fromPendencia && <span style={{fontSize:9,padding:"1px 5px",borderRadius:4,background:"#6366f118",color:"#6366f1"}}>📌</span>}
                        </div>
                        <div style={{display:"flex",gap:4,marginBottom:6,marginLeft:30,flexWrap:"wrap"}}>
                          {newStatusOpts.map(([s,icon,col,lbl]) => (
                            <button key={s} onClick={()=>handleUpdatePautaItem(livePautaId,item.id,{status:s})} style={{
                              padding:"4px 10px",borderRadius:6,border:`1px solid ${item.status===s?col:"var(--border)"}`,
                              background:item.status===s?col+"18":"transparent",color:item.status===s?col:"var(--text3)",
                              cursor:"pointer",fontSize:11,fontFamily:"'DM Sans',sans-serif",fontWeight:item.status===s?700:400
                            }}>{icon} {lbl}</button>
                          ))}
                        </div>
                        {/* Inline resolve for ideas/occurrences */}
                        {(item.fromIdea || item.fromOccurrence) && item.status === "resolvido" && (
                          <div style={{display:"flex",gap:4,marginLeft:30,marginBottom:6,flexWrap:"wrap"}}>
                            {item.fromIdea && (() => { const srcIdea = allIdeas.find(i=>i.id===item.fromIdea); return srcIdea && srcIdea.status !== "resolvida" ? (
                              <button onClick={()=>handleUpdateIdeaStatus(item.fromIdea,"resolvida")} style={{padding:"3px 8px",borderRadius:6,border:"1px solid #10b98144",background:"#10b98108",color:"#10b981",cursor:"pointer",fontSize:10}}>💡 Marcar ideia resolvida</button>
                            ) : srcIdea?.status === "resolvida" ? <span style={{fontSize:10,color:"#10b981"}}>💡 ✓ Resolvida</span> : null; })()}
                            {item.fromOccurrence && (() => { const srcOcc = allOccurrences.find(o=>o.id===item.fromOccurrence); return srcOcc && srcOcc.status !== "resolvida" ? (
                              <button onClick={()=>handleUpdateOccStatus(item.fromOccurrence,"resolvida")} style={{padding:"3px 8px",borderRadius:6,border:"1px solid #10b98144",background:"#10b98108",color:"#10b981",cursor:"pointer",fontSize:10}}>🚨 Marcar ocorrência resolvida</button>
                            ) : srcOcc?.status === "resolvida" ? <span style={{fontSize:10,color:"#10b981"}}>🚨 ✓ Resolvida</span> : null; })()}
                          </div>
                        )}
                        {/* Notes */}
                        <div style={{marginLeft:30}}>
                          <textarea value={item.notes??""} onChange={e=>handleUpdatePautaItemNotes(livePautaId,item.id,e.target.value)} rows={1} placeholder="Anotações..." style={{...S.input,fontSize:11,padding:"6px 8px",resize:"vertical",width:"100%",boxSizing:"border-box"}}/>
                        </div>
                        {/* Reason required for pendente and sem_resolucao */}
                        {(item.status === "pendente" || item.status === "sem_resolucao") && (
                          <div style={{marginTop:6,marginLeft:30}}>
                            <input value={item.reason??""} onChange={e=>handleUpdatePautaItem(livePautaId,item.id,{reason:e.target.value})} placeholder={item.status==="pendente"?"Por que ficou pendente?":"Por que não foi resolvido?"} style={{...S.input,fontSize:11,padding:"5px 8px",borderColor:!(item.reason??"").trim()?"#f59e0b":"var(--border)"}}/>
                            {!(item.reason??"").trim() && <div style={{fontSize:10,color:"#f59e0b",marginTop:2}}>⚠️ Motivo obrigatório</div>}
                          </div>
                        )}
                        {/* Decision/actions required for acao_definida */}
                        {item.status === "acao_definida" && (
                          <div style={{marginTop:6,marginLeft:30}}>
                            <input value={item.decision??""} onChange={e=>handleUpdatePautaItem(livePautaId,item.id,{decision:e.target.value})} placeholder="Quais ações e responsáveis?" style={{...S.input,fontSize:11,padding:"5px 8px",borderColor:!(item.decision??"").trim()?"#f59e0b":"var(--border)"}}/>
                            {!(item.decision??"").trim() && <div style={{fontSize:10,color:"#f59e0b",marginTop:2}}>⚠️ Defina as ações e responsáveis</div>}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  <div style={{marginTop:16,borderTop:"1px solid var(--border)",paddingTop:14}}>
                    <h4 style={{color:"var(--text)",fontSize:13,fontWeight:700,margin:"0 0 10px"}}>📌 Ações definidas</h4>
                    {liveActions.map(action => (
                      <div key={action.id} style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,padding:"8px 10px",borderRadius:8,background:action.done?"#10b98106":"var(--bg2)",border:`1px solid ${action.done?"#10b98122":"var(--border)"}`}}>
                        <button onClick={()=>handleToggleAction(action.id)} style={{padding:"2px 6px",borderRadius:4,border:`1px solid ${action.done?"#10b981":"var(--border)"}`,background:action.done?"#10b98122":"transparent",color:action.done?"#10b981":"var(--text3)",cursor:"pointer",fontSize:11}}>{action.done?"✓":"○"}</button>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:12,color:action.done?"var(--text3)":"var(--text)",textDecoration:action.done?"line-through":"none"}}>{action.text}</div>
                          <div style={{fontSize:10,color:"var(--text3)"}}>
                            {action.responsible && `👤 ${action.responsible}`}
                            {action.responsible && action.deadline && " · "}
                            {action.deadline && `📅 ${new Date(action.deadline+"T12:00:00").toLocaleDateString("pt-BR")}`}
                          </div>
                        </div>
                      </div>
                    ))}
                    <AddActionInline pautaId={livePautaId} onAdd={handleAddAction}/>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── Reuniões de liderança (pautas) ── */}
          {allPautas.length > 0 && !livePautaId && !livePlanId && (
            <div style={{marginBottom:20}}>
              <h4 style={{color:"var(--text)",fontSize:14,fontWeight:700,margin:"0 0 12px"}}>🤝 Reuniões de liderança</h4>
              {allPautas.sort((a,b)=>(b.createdAt??"").localeCompare(a.createdAt??"")).map(pauta => {
                const pautaActions = allActions.filter(a => a.pautaId === pauta.id);
                const isActive = activePautaId === pauta.id;
                const statusColors = { aberta:"#3b82f6", em_andamento:"#f59e0b", encerrada:"#10b981" };
                const statusLabels = { aberta:"Agendada", em_andamento:"Em andamento", encerrada:"Encerrada" };
                const doneItems = pauta.items?.filter(it => it.status === "resolvido" || it.status === "acao_definida" || it.status === "pendente" || it.status === "sem_resolucao").length ?? 0;
                const totalItems = pauta.items?.length ?? 0;
                const managers = data?.managers ?? [];
                const pParts = (pauta.participants ?? []).map(pid => managers.find(m => m.id === pid)).filter(Boolean);

                return (
                  <div key={pauta.id} style={{...S.card,padding:0,marginBottom:12,border:`1px solid ${statusColors[pauta.status]??"var(--border)"}22`,overflow:"hidden"}}>
                    <div onClick={()=>setActivePautaId(isActive?null:pauta.id)} style={{padding:"14px 16px",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center",background:isActive?"var(--bg2)":"transparent"}}>
                      <div>
                        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                          <span style={{fontSize:14}}>📋</span>
                          <span style={{color:"var(--text)",fontWeight:700,fontSize:14}}>{pauta.title}</span>
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:8,fontSize:10,color:"var(--text3)",flexWrap:"wrap"}}>
                          <span style={{padding:"2px 6px",borderRadius:4,background:(statusColors[pauta.status]??"#888")+"18",color:statusColors[pauta.status]??"#888",fontWeight:600}}>{statusLabels[pauta.status]??"—"}</span>
                          <span>{new Date(pauta.date+"T12:00:00").toLocaleDateString("pt-BR")}</span>
                          <span>{doneItems}/{totalItems} itens</span>
                          {pParts.length > 0 && <span>👥 {pParts.length}</span>}
                          {pautaActions.filter(a=>!a.done).length > 0 && <span style={{color:"#f59e0b",fontWeight:600}}>{pautaActions.filter(a=>!a.done).length} ação pendente</span>}
                        </div>
                      </div>
                      <span style={{color:"var(--text3)",fontSize:12}}>{isActive?"▲":"▼"}</span>
                    </div>

                    {isActive && (
                      <div style={{borderTop:"1px solid var(--border)",padding:"14px 16px"}}>
                        {/* Participants */}
                        {pParts.length > 0 && (
                          <div style={{marginBottom:12}}>
                            <div style={{fontSize:11,color:"var(--text3)",fontWeight:600,marginBottom:4}}>Participantes</div>
                            <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                              {pParts.map(p => (
                                <span key={p.id} style={{fontSize:10,padding:"3px 8px",borderRadius:6,background:"#10b98112",border:"1px solid #10b98122",color:"#10b981"}}>{p.name}{p.areas?.length>0?` (${p.areas.join(", ")})`:""}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Start / status buttons */}
                        <div style={{display:"flex",gap:6,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
                          {pauta.status === "aberta" && (
                            <button onClick={()=>handleStartLiveMeeting(pauta.id)} style={{...S.btnPrimary,fontSize:12,padding:"8px 16px",background:"#f59e0b",borderColor:"#f59e0b"}}>
                              ▶ Iniciar reunião ao vivo
                            </button>
                          )}
                          {/* v8.11.2: editar pauta antes de iniciar */}
                          {pauta.status === "aberta" && (
                            <button onClick={()=>handleStartEditPauta(pauta)} style={{...S.btnSecondary,fontSize:11,padding:"6px 12px",color:"#10b981",borderColor:"#10b98144"}} title="Editar antes de iniciar">
                              ✏️ Editar
                            </button>
                          )}
                          {pauta.status === "em_andamento" && (
                            <button onClick={()=>setLivePautaId(pauta.id)} style={{...S.btnPrimary,fontSize:12,padding:"8px 16px",background:"#f59e0b",borderColor:"#f59e0b"}}>
                              ▶ Continuar reunião
                            </button>
                          )}
                          {pauta.status !== "encerrada" && pauta.status !== "em_andamento" && (
                            <button onClick={()=>handleUpdatePautaStatus(pauta.id,"encerrada")} style={{...S.btnSecondary,fontSize:11,padding:"6px 12px",color:"#10b981",borderColor:"#10b98144"}}>
                              ✓ Marcar como encerrada
                            </button>
                          )}
                          {pauta.status === "encerrada" && (
                            <button onClick={()=>handleUpdatePautaStatus(pauta.id,"aberta")} style={{...S.btnSecondary,fontSize:11,padding:"6px 12px",color:"#3b82f6",borderColor:"#3b82f644"}}>
                              Reabrir
                            </button>
                          )}
                          {pauta.editedAt && (
                            <span style={{fontSize:10,color:"var(--text3)",fontStyle:"italic"}} title={`Editada por ${pauta.editedBy} em ${new Date(pauta.editedAt).toLocaleString("pt-BR")}`}>✏️ editada</span>
                          )}
                        </div>

                        {/* Items summary */}
                        {(pauta.items ?? []).map((item, idx) => {
                          const sIcon = item.status==="resolvido"?"✓":item.status==="acao_definida"?"⚡":item.status==="sem_resolucao"?"💭":item.status==="pendente"?"⏸":"○";
                          const sCol = item.status==="resolvido"?"#10b981":item.status==="acao_definida"?"#f59e0b":item.status==="sem_resolucao"?"#8b5cf6":item.status==="pendente"?"var(--text3)":"var(--text3)";
                          return (
                            <div key={item.id} style={{padding:"8px 10px",marginBottom:4,borderRadius:8,background:item.status==="resolvido"?"#10b98106":item.status==="acao_definida"?"#f59e0b06":item.status==="sem_resolucao"?"#8b5cf606":"var(--bg2)",border:`1px solid ${sCol}22`}}>
                              <div style={{display:"flex",alignItems:"center",gap:6}}>
                                <span style={{fontSize:10,color:"var(--text3)",fontWeight:700,minWidth:18}}>{idx+1}.</span>
                                <span style={{color:sCol,fontSize:11}}>{sIcon}</span>
                                <span style={{flex:1,fontSize:12,color:"var(--text)",textDecoration:item.status==="resolvido"?"line-through":"none"}}>{item.text}</span>
                                {item.fromIdea && <span style={{fontSize:9,padding:"1px 5px",borderRadius:4,background:"#f59e0b18",color:"#f59e0b"}}>💡</span>}
                                {item.fromOccurrence && <span style={{fontSize:9,padding:"1px 5px",borderRadius:4,background:"#ef444418",color:"#ef4444"}}>🚨</span>}
                    {item.fromIncident && <span style={{fontSize:9,padding:"1px 5px",borderRadius:4,background:"#8b5cf618",color:"#8b5cf6"}}>👤</span>}
                    {item.fromPendencia && <span style={{fontSize:9,padding:"1px 5px",borderRadius:4,background:"#6366f118",color:"#6366f1"}}>📌</span>}
                              </div>
                              {item.notes && <div style={{fontSize:10,color:"var(--text3)",marginTop:4,marginLeft:36,fontStyle:"italic"}}>{item.notes}</div>}
                              {item.decision && <div style={{fontSize:10,color:"#f59e0b",marginTop:2,marginLeft:36}}>⚡ {item.decision}</div>}
                              {item.reason && <div style={{fontSize:10,color:item.status==="sem_resolucao"?"#8b5cf6":"var(--text3)",marginTop:2,marginLeft:36}}>💬 {item.reason}</div>}
                            </div>
                          );
                        })}

                        {/* Actions */}
                        <div style={{marginTop:16,borderTop:"1px solid var(--border)",paddingTop:14}}>
                          <h4 style={{color:"var(--text)",fontSize:13,fontWeight:700,margin:"0 0 10px"}}>📌 Ações pós-reunião</h4>
                          {pautaActions.map(action => (
                            <div key={action.id} style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,padding:"8px 10px",borderRadius:8,background:action.done?"#10b98106":"var(--bg2)",border:`1px solid ${action.done?"#10b98122":"var(--border)"}`}}>
                              <button onClick={()=>handleToggleAction(action.id)} style={{padding:"2px 6px",borderRadius:4,border:`1px solid ${action.done?"#10b981":"var(--border)"}`,background:action.done?"#10b98122":"transparent",color:action.done?"#10b981":"var(--text3)",cursor:"pointer",fontSize:11}}>{action.done?"✓":"○"}</button>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontSize:12,color:action.done?"var(--text3)":"var(--text)",textDecoration:action.done?"line-through":"none"}}>{action.text}</div>
                                <div style={{fontSize:10,color:"var(--text3)"}}>
                                  {action.responsible && `👤 ${action.responsible}`}
                                  {action.responsible && action.deadline && " · "}
                                  {action.deadline && `📅 ${new Date(action.deadline+"T12:00:00").toLocaleDateString("pt-BR")}`}
                                </div>
                              </div>
                            </div>
                          ))}
                          <AddActionInline pautaId={pauta.id} onAdd={handleAddAction}/>
                        </div>

                        <div style={{marginTop:14,textAlign:"right"}}>
                          <button onClick={()=>handleDeletePauta(pauta.id)} style={{fontSize:11,padding:"5px 12px",borderRadius:6,border:"1px solid var(--red)33",background:"transparent",color:"var(--red)",cursor:"pointer"}}>🗑️ Excluir pauta</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Empty state when no plans and no pautas */}
          {Object.keys(grouped).length === 0 && allPautas.length === 0 && !showForm && !livePautaId && (
            <div style={{...S.card,textAlign:"center",padding:40}}>
              <div style={{fontSize:36,marginBottom:12}}>📅</div>
              <p style={{color:"var(--text3)",fontSize:14}}>Nenhuma reunião planejada ainda.</p>
              <p style={{color:"var(--text3)",fontSize:12}}>Planeje reuniões com a equipe ou de liderança.</p>
            </div>
          )}
          {/* ── Lixeira ── */}
          {(trashedPautas.length > 0 || trashedPlans.length > 0) && (
            <div style={{marginTop:24,padding:"14px 16px",borderRadius:10,border:"1px dashed var(--border)",background:"var(--bg2)"}}>
              <h4 style={{color:"var(--text3)",fontSize:12,fontWeight:600,margin:"0 0 10px"}}>🗑️ Lixeira ({trashedPautas.length + trashedPlans.length})</h4>
              {trashedPautas.map(p => {
                const daysLeft = Math.ceil(TRASH_DAYS - (Date.now() - new Date(p.deletedAt).getTime()) / 86400000);
                return (
                  <div key={p.id} style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,padding:"8px 10px",borderRadius:8,background:"var(--card-bg)",border:"1px solid var(--border)"}}>
                    <span style={{fontSize:10}}>🤝</span>
                    <span style={{flex:1,fontSize:11,color:"var(--text3)",textDecoration:"line-through"}}>{p.title}</span>
                    <span style={{fontSize:9,color:"var(--text3)"}}>{daysLeft}d</span>
                    <button onClick={()=>handleRestorePauta(p.id)} style={{padding:"2px 8px",borderRadius:4,border:"1px solid #3b82f633",background:"transparent",color:"#3b82f6",cursor:"pointer",fontSize:10}}>↩ Restaurar</button>
                    {isOwner && <button onClick={async ()=>{
                      if(!await appConfirm("Excluir definitivamente esta pauta? Esta ação não pode ser desfeita.")) return;
                      onUpdate("meetingAgendas", (data?.meetingAgendas??[]).filter(x => x.id !== p.id));
                    }} style={{padding:"2px 8px",borderRadius:4,border:"1px solid #ef444433",background:"transparent",color:"#ef4444",cursor:"pointer",fontSize:10}}>✕ Excluir</button>}
                  </div>
                );
              })}
              {trashedPlans.map(p => {
                const daysLeft = Math.ceil(TRASH_DAYS - (Date.now() - new Date(p.deletedAt).getTime()) / 86400000);
                const emp0 = (p.employeeIds??[]).length > 0 ? employees.find(e=>e.id===p.employeeIds[0]) : null;
                return (
                  <div key={p.id} style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,padding:"8px 10px",borderRadius:8,background:"var(--card-bg)",border:"1px solid var(--border)"}}>
                    <span style={{fontSize:10}}>{p.type==="avaliação"?"📋":"💬"}</span>
                    <span style={{flex:1,fontSize:11,color:"var(--text3)",textDecoration:"line-through"}}>{p.type} {emp0?`— ${shortName(emp0.name)}`:""} {p.plannedDate?new Date(p.plannedDate+"T12:00:00").toLocaleDateString("pt-BR"):""}</span>
                    <span style={{fontSize:9,color:"var(--text3)"}}>{daysLeft}d</span>
                    <button onClick={()=>handleRestorePlan(p.id)} style={{padding:"2px 8px",borderRadius:4,border:"1px solid #3b82f633",background:"transparent",color:"#3b82f6",cursor:"pointer",fontSize:10}}>↩ Restaurar</button>
                    {isOwner && <button onClick={async ()=>{
                      if(!await appConfirm("Excluir definitivamente esta reunião planejada? Esta ação não pode ser desfeita.")) return;
                      onUpdate("meetingPlans", (data?.meetingPlans??[]).filter(x => x.id !== p.id));
                    }} style={{padding:"2px 8px",borderRadius:4,border:"1px solid #ef444433",background:"transparent",color:"#ef4444",cursor:"pointer",fontSize:10}}>✕ Excluir</button>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ═══ SUB-TAB: IDEIAS ═══ */}
      {subTab === "ideias" && (
        <div>
          <button onClick={()=>setShowIdeaForm(!showIdeaForm)} style={{...S.btnPrimary,marginBottom:16,fontSize:13,padding:"10px 20px",background:showIdeaForm?"var(--red)":"#f59e0b",borderColor:showIdeaForm?"var(--red)":"#f59e0b"}}>
            {showIdeaForm ? "✕ Cancelar" : "+ Nova ideia"}
          </button>

          {showIdeaForm && (
            <div style={{...S.card,padding:"18px 20px",marginBottom:20,border:"1px solid #f59e0b22"}}>
              <h4 style={{color:"var(--text)",margin:"0 0 14px",fontSize:15,fontWeight:700}}>Nova ideia para reunião</h4>
              <div style={{marginBottom:12}}>
                <label style={S.label}>Título</label>
                <input value={ideaTitle} onChange={e=>setIdeaTitle(e.target.value)} placeholder="Ex: Revisar escala do feriado" style={S.input}/>
              </div>
              <div style={{marginBottom:12}}>
                <label style={S.label}>Descrição <span style={{fontWeight:400,color:"var(--text3)"}}>(opcional)</span></label>
                <textarea value={ideaDesc} onChange={e=>setIdeaDesc(e.target.value)} rows={2} placeholder="Detalhes..." style={{...S.input,resize:"vertical"}}/>
              </div>
              <div style={{marginBottom:14}}>
                <label style={S.label}>Áreas relacionadas <span style={{fontWeight:400,color:"var(--text3)"}}>(opcional, clique para selecionar várias)</span></label>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {filteredAreas.map(a => {
                    const col = AREA_COLORS[a] ?? "#888";
                    const active = ideaAreas.includes(a);
                    return <button key={a} onClick={()=>setIdeaAreas(prev=>prev.includes(a)?prev.filter(x=>x!==a):[...prev,a])} style={{padding:"5px 12px",borderRadius:8,border:`1px solid ${active?col:"var(--border)"}`,background:active?col+"18":"transparent",color:active?col:"var(--text3)",cursor:"pointer",fontSize:11,fontFamily:"'DM Sans',sans-serif",fontWeight:active?600:400}}>{active?"✓ ":""}{a}</button>;
                  })}
                  {ideaAreas.length > 0 && <button onClick={()=>setIdeaAreas([])} style={{padding:"5px 12px",borderRadius:8,border:"1px solid var(--border)",background:"transparent",color:"var(--text3)",cursor:"pointer",fontSize:11,fontFamily:"'DM Sans',sans-serif"}}>Limpar</button>}
                </div>
              </div>
              <button onClick={handleCreateIdea} style={{...S.btnPrimary,width:"auto",padding:"10px 24px",background:"#f59e0b",borderColor:"#f59e0b"}}>Salvar ideia</button>
            </div>
          )}

          {filteredIdeas.filter(i=>i.status==="nova").length === 0 && !showIdeaForm && (
            <div style={{...S.card,textAlign:"center",padding:40}}>
              <div style={{fontSize:36,marginBottom:12}}>💡</div>
              <p style={{color:"var(--text3)",fontSize:14}}>Nenhuma ideia cadastrada.</p>
              <p style={{color:"var(--text3)",fontSize:12}}>Anote temas para discutir nas próximas reuniões.</p>
            </div>
          )}

          {/* Priority ideas first */}
          {filteredIdeas.filter(i=>i.status==="nova").sort((a,b) => (b.priority?1:0) - (a.priority?1:0) || (b.createdAt??"").localeCompare(a.createdAt??"")).map(idea => {
            const ideaAreaList = idea.areas ?? (idea.area ? [idea.area] : []);
            return (
              <div key={idea.id} style={{...S.card,padding:"12px 16px",marginBottom:8,border:`1px solid ${idea.priority?"#f59e0b33":"var(--border)"}`,background:idea.priority?"#f59e0b05":"var(--card-bg)"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4,flexWrap:"wrap"}}>
                      {idea.priority && <span style={{fontSize:10,padding:"2px 6px",borderRadius:4,background:"#f59e0b22",color:"#f59e0b",fontWeight:700}}>⚡ Prioridade</span>}
                      {ideaAreaList.map(a => { const col = AREA_COLORS[a] ?? "#888"; return <span key={a} style={{fontSize:10,padding:"2px 6px",borderRadius:4,background:col+"18",color:col}}>{a}</span>; })}
                    </div>
                    <div style={{color:"var(--text)",fontWeight:600,fontSize:13}}>{idea.title}</div>
                    {idea.description && <div style={{color:"var(--text3)",fontSize:11,marginTop:2}}>{idea.description}</div>}
                    <div style={{color:"var(--text3)",fontSize:10,marginTop:4}}>{idea.createdBy} · {new Date(idea.createdAt).toLocaleDateString("pt-BR")}</div>
                  </div>
                  <div style={{display:"flex",gap:4,flexShrink:0}}>
                    <button onClick={()=>toggleIdeaPriority(idea.id)} title={idea.priority?"Remover prioridade":"Marcar como prioridade"} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #f59e0b44",background:idea.priority?"#f59e0b22":"transparent",color:"#f59e0b",cursor:"pointer",fontSize:12}}>⚡</button>
                    <button onClick={()=>handleUpdateIdeaStatus(idea.id,"resolvida")} title="Marcar como resolvida/implantada" style={{padding:"4px 8px",borderRadius:6,border:"1px solid #10b98144",background:"transparent",color:"#10b981",cursor:"pointer",fontSize:11}}>✓</button>
                    <button onClick={()=>deleteIdea(idea.id)} style={{padding:"4px 8px",borderRadius:6,border:"1px solid var(--red)33",background:"transparent",color:"var(--red)",cursor:"pointer",fontSize:10}}>✕</button>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Ideas already in pauta */}
          {filteredIdeas.filter(i=>i.status==="na_pauta").length > 0 && (
            <div style={{marginTop:16}}>
              <h4 style={{color:"var(--text3)",fontSize:12,fontWeight:600,margin:"0 0 8px"}}>Já incluídas em pauta</h4>
              {filteredIdeas.filter(i=>i.status==="na_pauta").map(idea => (
                <div key={idea.id} style={{padding:"8px 14px",marginBottom:4,borderRadius:8,background:"var(--bg2)",border:"1px solid var(--border)"}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{color:"#f59e0b",fontSize:10}}>📋</span>
                    <span style={{flex:1,color:"var(--text3)",fontSize:12}}>{idea.title}</span>
                    <button onClick={()=>handleUpdateIdeaStatus(idea.id,"resolvida")} title="Marcar como resolvida/implantada" style={{padding:"2px 8px",borderRadius:4,border:"1px solid #10b98144",background:"transparent",color:"#10b981",cursor:"pointer",fontSize:10}}>✓ Resolvida</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Resolved ideas */}
          {filteredIdeas.filter(i=>i.status==="resolvida").length > 0 && (
            <div style={{marginTop:16}}>
              <h4 style={{color:"var(--text3)",fontSize:12,fontWeight:600,margin:"0 0 8px"}}>Resolvidas / Implantadas</h4>
              {filteredIdeas.filter(i=>i.status==="resolvida").map(idea => (
                <div key={idea.id} style={{padding:"8px 14px",marginBottom:4,borderRadius:8,background:"#10b98106",border:"1px solid #10b98122"}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{color:"#10b981",fontSize:10}}>✓</span>
                    <span style={{flex:1,color:"var(--text3)",fontSize:12,textDecoration:"line-through"}}>{idea.title}</span>
                    <button onClick={()=>handleUpdateIdeaStatus(idea.id,"nova")} title="Reabrir" style={{padding:"2px 8px",borderRadius:4,border:"1px solid var(--border)",background:"transparent",color:"var(--text3)",cursor:"pointer",fontSize:10}}>↩ Reabrir</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══ SUB-TAB: OCORRÊNCIAS ═══ */}
      {subTab === "ocorrencias" && (
        <div>
          <button onClick={()=>setShowOccForm(!showOccForm)} style={{...S.btnPrimary,marginBottom:16,fontSize:13,padding:"10px 20px",background:showOccForm?"var(--red)":"#ef4444",borderColor:showOccForm?"var(--red)":"#ef4444"}}>
            {showOccForm ? "✕ Cancelar" : "+ Nova ocorrência"}
          </button>

          {showOccForm && (
            <div style={{...S.card,padding:"18px 20px",marginBottom:20,border:"1px solid #ef444422"}}>
              <h4 style={{color:"var(--text)",margin:"0 0 14px",fontSize:15,fontWeight:700}}>Nova ocorrência</h4>
              <div style={{marginBottom:12}}>
                <label style={S.label}>Tipo</label>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {OCC_TYPES.map(([val,icon,lbl]) => (
                    <button key={val} onClick={()=>{setOccType(val);if(val!=="pessoal"){setOccSubtype("");setOccEmployeeIds([]);}}} style={{
                      padding:"6px 14px",borderRadius:8,border:`2px solid ${occType===val?"#ef4444":"var(--border)"}`,
                      background:occType===val?"#ef444414":"transparent",color:occType===val?"#ef4444":"var(--text3)",
                      cursor:"pointer",fontSize:12,fontFamily:"'DM Sans',sans-serif",fontWeight:occType===val?700:400
                    }}>{icon} {lbl}</button>
                  ))}
                </div>
              </div>

              {/* Pessoal subtype selector */}
              {occType === "pessoal" && (
                <div style={{marginBottom:12}}>
                  <label style={S.label}>Subtipo</label>
                  <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                    {PESSOAL_SUBTYPES.map(([val,lbl]) => (
                      <button key={val} onClick={()=>setOccSubtype(val)} style={{
                        padding:"4px 10px",borderRadius:6,border:`1px solid ${occSubtype===val?"#8b5cf6":"var(--border)"}`,
                        background:occSubtype===val?"#8b5cf614":"transparent",color:occSubtype===val?"#8b5cf6":"var(--text3)",
                        cursor:"pointer",fontSize:11,fontFamily:"'DM Sans',sans-serif",fontWeight:occSubtype===val?600:400
                      }}>{lbl}</button>
                    ))}
                  </div>
                </div>
              )}

              {/* Employee selector (mandatory for pessoal, optional for others) */}
              <div style={{marginBottom:12}}>
                <label style={S.label}>{occType === "pessoal" ? "Empregados envolvidos" : "Empregados envolvidos"} <span style={{fontWeight:400,color:"var(--text3)"}}>{occType === "pessoal" ? "(obrigatório)" : "(opcional)"}</span></label>
                <div style={{display:"flex",gap:4,flexWrap:"wrap",maxHeight:120,overflowY:"auto"}}>
                  {(employees ?? []).filter(e => e.restaurantId === restaurantId && !e.inactive).map(emp => {
                    const active = occEmployeeIds.includes(emp.id);
                    return (
                      <button key={emp.id} onClick={()=>setOccEmployeeIds(prev=>prev.includes(emp.id)?prev.filter(x=>x!==emp.id):[...prev,emp.id])} style={{
                        padding:"3px 8px",borderRadius:6,border:`1px solid ${active?"#8b5cf6":"var(--border)"}`,
                        background:active?"#8b5cf614":"transparent",color:active?"#8b5cf6":"var(--text3)",
                        cursor:"pointer",fontSize:10,fontFamily:"'DM Sans',sans-serif",fontWeight:active?600:400
                      }}>{active?"✓ ":""}{shortName(emp.name)}</button>
                    );
                  })}
                </div>
              </div>

              <div style={{marginBottom:12}}>
                <label style={S.label}>Título</label>
                <input value={occTitle} onChange={e=>setOccTitle(e.target.value)} placeholder={occType==="pessoal"?"Ex: Advertência por atraso":"Ex: Reclamação de cliente sobre demora"} style={S.input}/>
              </div>
              <div style={{marginBottom:12}}>
                <label style={S.label}>Descrição <span style={{fontWeight:400,color:"var(--text3)"}}>(opcional)</span></label>
                <textarea value={occDesc} onChange={e=>setOccDesc(e.target.value)} rows={2} placeholder="Detalhes..." style={{...S.input,resize:"vertical"}}/>
              </div>
              <div style={{marginBottom:14}}>
                <label style={S.label}>Áreas envolvidas <span style={{fontWeight:400,color:"var(--text3)"}}>(opcional)</span></label>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {filteredAreas.map(a => {
                    const col = AREA_COLORS[a] ?? "#888";
                    const active = occAreas.includes(a);
                    return <button key={a} onClick={()=>setOccAreas(prev=>prev.includes(a)?prev.filter(x=>x!==a):[...prev,a])} style={{padding:"5px 12px",borderRadius:8,border:`1px solid ${active?col:"var(--border)"}`,background:active?col+"18":"transparent",color:active?col:"var(--text3)",cursor:"pointer",fontSize:11,fontFamily:"'DM Sans',sans-serif",fontWeight:active?600:400}}>{active?"✓ ":""}{a}</button>;
                  })}
                  {occAreas.length > 0 && <button onClick={()=>setOccAreas([])} style={{padding:"5px 12px",borderRadius:8,border:"1px solid var(--border)",background:"transparent",color:"var(--text3)",cursor:"pointer",fontSize:11,fontFamily:"'DM Sans',sans-serif"}}>Limpar</button>}
                </div>
              </div>
              <button onClick={handleCreateOccurrence} style={{...S.btnPrimary,width:"auto",padding:"10px 24px",background:"#ef4444",borderColor:"#ef4444"}}>Salvar ocorrência</button>
            </div>
          )}

          {filteredOccurrences.filter(o=>o.status==="nova").length === 0 && !showOccForm && (
            <div style={{...S.card,textAlign:"center",padding:40}}>
              <div style={{fontSize:36,marginBottom:12}}>🚨</div>
              <p style={{color:"var(--text3)",fontSize:14}}>Nenhuma ocorrência registrada.</p>
              <p style={{color:"var(--text3)",fontSize:12}}>Registre ocorrências gerais ou de pessoal para discutir nas reuniões.</p>
            </div>
          )}

          {filteredOccurrences.filter(o=>o.status==="nova").sort((a,b)=>(b.priority?1:0)-(a.priority?1:0)||(b.createdAt??"").localeCompare(a.createdAt??"")).map(occ => {
            const occT = OCC_TYPES.find(([t])=>t===occ.type);
            const occAreaList = occ.areas ?? [];
            return (
              <div key={occ.id} style={{...S.card,padding:"12px 16px",marginBottom:8,border:`1px solid ${occ.priority?"#f59e0b33":"#ef444422"}`,background:occ.priority?"#f59e0b05":"var(--card-bg)"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4,flexWrap:"wrap"}}>
                      {occ.priority && <span style={{fontSize:10,padding:"2px 6px",borderRadius:4,background:"#f59e0b22",color:"#f59e0b",fontWeight:700}}>⚡ Prioridade</span>}
                      <span style={{fontSize:10,padding:"2px 6px",borderRadius:4,background:"#ef444418",color:"#ef4444",fontWeight:600}}>{occT?.[1]} {occT?.[2]}</span>
                      {occ.subtype && <span style={{fontSize:10,padding:"2px 6px",borderRadius:4,background:"#8b5cf618",color:"#8b5cf6"}}>{PESSOAL_SUBTYPES.find(([s])=>s===occ.subtype)?.[1] ?? occ.subtype}</span>}
                      {occAreaList.map(a => { const col = AREA_COLORS[a] ?? "#888"; return <span key={a} style={{fontSize:10,padding:"2px 6px",borderRadius:4,background:col+"18",color:col}}>{a}</span>; })}
                    </div>
                    <div style={{color:"var(--text)",fontWeight:600,fontSize:13}}>{occ.title}</div>
                    {(occ.employeeIds ?? []).length > 0 && (
                      <div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:4}}>
                        {occ.employeeIds.map(eid => { const emp = employees.find(e=>e.id===eid); return emp ? <span key={eid} style={{fontSize:9,padding:"2px 6px",borderRadius:4,background:"#8b5cf612",color:"#8b5cf6",border:"1px solid #8b5cf622"}}>👤 {shortName(emp.name)}</span> : null; })}
                      </div>
                    )}
                    {occ.description && <div style={{color:"var(--text3)",fontSize:11,marginTop:2}}>{occ.description}</div>}
                    <div style={{color:"var(--text3)",fontSize:10,marginTop:4}}>{occ.createdBy} · {new Date(occ.createdAt).toLocaleDateString("pt-BR")}</div>
                  </div>
                  <div style={{display:"flex",gap:4,flexShrink:0}}>
                    <button onClick={()=>toggleOccPriority(occ.id)} title={occ.priority?"Remover prioridade":"Marcar como prioridade"} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #f59e0b44",background:occ.priority?"#f59e0b22":"transparent",color:"#f59e0b",cursor:"pointer",fontSize:12}}>⚡</button>
                    <button onClick={()=>{setResolvingOccId(resolvingOccId===occ.id?null:occ.id);setResolveOccText("");}} title="Marcar como resolvida" style={{padding:"4px 8px",borderRadius:6,border:"1px solid #10b98144",background:resolvingOccId===occ.id?"#10b98122":"transparent",color:"#10b981",cursor:"pointer",fontSize:11}}>✓</button>
                    <button onClick={()=>handleUpdateOccStatus(occ.id,"descartada")} title="Descartar" style={{padding:"4px 8px",borderRadius:6,border:"1px solid var(--border)",background:"transparent",color:"var(--text3)",cursor:"pointer",fontSize:10}}>✕</button>
                    <button onClick={()=>handleDeleteOccurrence(occ.id)} style={{padding:"4px 8px",borderRadius:6,border:"1px solid var(--red)33",background:"transparent",color:"var(--red)",cursor:"pointer",fontSize:10}}>🗑️</button>
                  </div>
                </div>
                {resolvingOccId===occ.id && (
                  <div style={{marginTop:8,padding:"10px 12px",borderRadius:8,background:"#10b98108",border:"1px solid #10b98133"}}>
                    <label style={{fontSize:11,color:"#10b981",fontWeight:700,display:"block",marginBottom:4}}>O que foi resolvido?</label>
                    <textarea value={resolveOccText} onChange={e=>setResolveOccText(e.target.value)} placeholder="Descreva a resolução..." rows={2} style={{...S.input,fontSize:12,resize:"vertical",marginBottom:6}}/>
                    <div style={{display:"flex",gap:6}}>
                      <button onClick={()=>handleUpdateOccStatus(occ.id,"resolvida",resolveOccText)} style={{padding:"6px 14px",borderRadius:6,border:"none",background:"#10b981",color:"#fff",cursor:"pointer",fontSize:11,fontWeight:700}}>Confirmar</button>
                      <button onClick={()=>{setResolvingOccId(null);setResolveOccText("");}} style={{padding:"6px 14px",borderRadius:6,border:"1px solid var(--border)",background:"transparent",color:"var(--text3)",cursor:"pointer",fontSize:11}}>Cancelar</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Occurrences in pauta */}
          {filteredOccurrences.filter(o=>o.status==="na_pauta").length > 0 && (
            <div style={{marginTop:16}}>
              <h4 style={{color:"var(--text3)",fontSize:12,fontWeight:600,margin:"0 0 8px"}}>Incluídas em pauta</h4>
              {filteredOccurrences.filter(o=>o.status==="na_pauta").map(occ => {
                const occT = OCC_TYPES.find(([t])=>t===occ.type);
                return (
                  <div key={occ.id} style={{padding:"8px 14px",marginBottom:4,borderRadius:8,background:"var(--bg2)",border:"1px solid var(--border)"}}>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <span style={{color:"#f59e0b",fontSize:10}}>📋</span>
                      <span style={{color:"var(--text3)",fontSize:12}}>{occT?.[1]} {occ.title}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Resolved occurrences */}
          {filteredOccurrences.filter(o=>o.status==="resolvida").length > 0 && (
            <div style={{marginTop:16}}>
              <h4 style={{color:"var(--text3)",fontSize:12,fontWeight:600,margin:"0 0 8px"}}>Resolvidas</h4>
              {filteredOccurrences.filter(o=>o.status==="resolvida").map(occ => {
                const occT = OCC_TYPES.find(([t])=>t===occ.type);
                return (
                  <div key={occ.id} style={{padding:"8px 14px",marginBottom:4,borderRadius:8,background:"#10b98106",border:"1px solid #10b98122"}}>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <span style={{color:"#10b981",fontSize:10}}>✓</span>
                      <span style={{flex:1,color:"var(--text3)",fontSize:12,textDecoration:"line-through"}}>{occT?.[1]} {occ.title}</span>
                      <button onClick={()=>handleUpdateOccStatus(occ.id,"nova")} title="Reabrir" style={{padding:"2px 8px",borderRadius:4,border:"1px solid var(--border)",background:"transparent",color:"var(--text3)",cursor:"pointer",fontSize:10}}>↩ Reabrir</button>
                    </div>
                    {occ.resolvedText && <div style={{color:"#10b981",fontSize:11,marginTop:4}}>Resolução: {occ.resolvedText}</div>}
                    {occ.resolvedAt && <div style={{color:"var(--text3)",fontSize:10,marginTop:2}}>{occ.resolvedBy ? `${occ.resolvedBy} · ` : ""}{new Date(occ.resolvedAt).toLocaleDateString("pt-BR")}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ═══ SUB-TAB: PENDÊNCIAS ═══ */}
      {subTab === "pendencias" && (
        <div>
          {allPendencias.filter(p=>p.status==="ativa").length === 0 && allPendencias.filter(p=>p.status==="na_pauta").length === 0 && allPendencias.filter(p=>p.status==="resolvida").length === 0 && (
            <div style={{...S.card,padding:"30px 20px",textAlign:"center"}}>
              <div style={{fontSize:32,marginBottom:8}}>📌</div>
              <div style={{color:"var(--text3)",fontSize:13}}>Nenhuma pendência registrada.</div>
              <div style={{color:"var(--text3)",fontSize:11,marginTop:4}}>Itens pendentes ou sem resolução das reuniões aparecerão aqui automaticamente.</div>
            </div>
          )}

          {/* Active pendências */}
          {allPendencias.filter(p=>p.status==="ativa").map(pend => {
            const fromPauta = allPautas.find(p=>p.id===pend.fromPautaId);
            const typeLabel = pend.type === "pendente" ? "Pendente" : "Sem resolução";
            const typeColor = pend.type === "pendente" ? "var(--text3)" : "#8b5cf6";
            const typeIcon = pend.type === "pendente" ? "⏸" : "💭";
            return (
              <div key={pend.id} style={{...S.card,padding:"14px 16px",marginBottom:8,border:`1px solid ${typeColor}33`}}>
                <div style={{display:"flex",alignItems:"flex-start",gap:8}}>
                  <span style={{fontSize:14}}>{typeIcon}</span>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",marginBottom:4}}>
                      <span style={{color:"var(--text)",fontSize:13,fontWeight:600}}>{pend.text}</span>
                      {pend.fromIdea && <span style={{fontSize:9,padding:"1px 5px",borderRadius:4,background:"#f59e0b18",color:"#f59e0b"}}>💡</span>}
                      {pend.fromOccurrence && <span style={{fontSize:9,padding:"1px 5px",borderRadius:4,background:"#ef444418",color:"#ef4444"}}>🚨</span>}
                      {pend.fromIncident && <span style={{fontSize:9,padding:"1px 5px",borderRadius:4,background:"#8b5cf618",color:"#8b5cf6"}}>👤</span>}
                    </div>
                    {pend.reason && <div style={{fontSize:11,color:typeColor,marginBottom:4}}>💬 {pend.reason}</div>}
                    <div style={{display:"flex",alignItems:"center",gap:8,fontSize:10,color:"var(--text3)"}}>
                      <span style={{padding:"2px 6px",borderRadius:4,background:typeColor+"18",color:typeColor,fontWeight:600}}>{typeLabel}</span>
                      {fromPauta && <span>de: {fromPauta.title} ({new Date(fromPauta.date+"T12:00:00").toLocaleDateString("pt-BR")})</span>}
                      <span>{new Date(pend.createdAt).toLocaleDateString("pt-BR")}</span>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:4}}>
                    <button onClick={()=>{const allP=[...getData("meetingPendencias")];const idx=allP.findIndex(p=>p.id===pend.id);if(idx>=0){allP[idx]={...allP[idx],status:"resolvida"};onUpdate("meetingPendencias",allP);}}} title="Marcar como resolvida" style={{padding:"4px 8px",borderRadius:6,border:"1px solid #10b98133",background:"#10b98108",color:"#10b981",cursor:"pointer",fontSize:11,fontFamily:"'DM Sans',sans-serif"}}>✓</button>
                  </div>
                </div>
              </div>
            );
          })}

          {/* In pauta pendências */}
          {allPendencias.filter(p=>p.status==="na_pauta").length > 0 && (
            <div style={{marginTop:16}}>
              <h4 style={{color:"var(--text3)",fontSize:12,fontWeight:600,margin:"0 0 8px"}}>Incluídas em pauta</h4>
              {allPendencias.filter(p=>p.status==="na_pauta").map(pend => (
                <div key={pend.id} style={{padding:"8px 14px",marginBottom:4,borderRadius:8,background:"var(--bg2)",border:"1px solid var(--border)"}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{color:"#f59e0b",fontSize:10}}>📋</span>
                    <span style={{color:"var(--text3)",fontSize:12}}>{pend.text}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Resolved pendências */}
          {allPendencias.filter(p=>p.status==="resolvida").length > 0 && (
            <div style={{marginTop:16}}>
              <h4 style={{color:"var(--text3)",fontSize:12,fontWeight:600,margin:"0 0 8px"}}>Resolvidas</h4>
              {allPendencias.filter(p=>p.status==="resolvida").map(pend => (
                <div key={pend.id} style={{padding:"8px 14px",marginBottom:4,borderRadius:8,background:"#10b98106",border:"1px solid #10b98122"}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{color:"#10b981",fontSize:10}}>✓</span>
                    <span style={{flex:1,color:"var(--text3)",fontSize:12,textDecoration:"line-through"}}>{pend.text}</span>
                    <button onClick={()=>{const allP=[...getData("meetingPendencias")];const idx=allP.findIndex(p=>p.id===pend.id);if(idx>=0){allP[idx]={...allP[idx],status:"ativa"};onUpdate("meetingPendencias",allP);}}} title="Reabrir" style={{padding:"2px 8px",borderRadius:4,border:"1px solid var(--border)",background:"transparent",color:"var(--text3)",cursor:"pointer",fontSize:10}}>↩ Reabrir</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══ SUB-TAB: RELATÓRIO ═══ */}
      {subTab === "relatorio" && (
        <div>
          <div style={{...S.card,padding:"18px 20px",marginBottom:16}}>
            <h4 style={{color:"var(--text)",margin:"0 0 14px",fontSize:15,fontWeight:700}}>📄 Relatório de Reuniões</h4>
            <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"1fr 1fr 1fr 1fr",gap:10,marginBottom:14}}>
              <div>
                <label style={S.label}>Data inicial</label>
                <input type="date" value={relFrom} onChange={e=>setRelFrom(e.target.value)} style={S.input}/>
              </div>
              <div>
                <label style={S.label}>Data final</label>
                <input type="date" value={relTo} onChange={e=>setRelTo(e.target.value)} style={S.input}/>
              </div>
              <div>
                <label style={S.label}>Área</label>
                <select value={relFilterArea} onChange={e=>setRelFilterArea(e.target.value)} style={S.input}>
                  <option value="Todos">Todas</option>
                  {areas.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>Líder</label>
                <select value={relFilterLider} onChange={e=>setRelFilterLider(e.target.value)} style={S.input}>
                  <option value="Todos">Todos</option>
                  {[...new Set((feedbacks??[]).filter(f=>f.restaurantId===restaurantId&&f.createdBy).map(f=>f.createdBy))].sort().map(name => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
            </div>
            <button onClick={()=>{
              if(!relFrom||!relTo){window.alert("Informe data inicial e final.");return;}
              // Build report data
              const fbs = (feedbacks??[]).filter(f=>{
                if(f.restaurantId!==restaurantId||f.deletedAt)return false;
                const d=f.meetingDate||f.createdAt?.slice(0,10);
                if(!d||d<relFrom||d>relTo)return false;
                if(relFilterLider!=="Todos"&&f.createdBy!==relFilterLider)return false;
                if(relFilterArea!=="Todos"){
                  const emp=employees.find(e=>e.id===f.employeeId);
                  const role=emp?roles.find(r=>r.id===emp.roleId):null;
                  if(role?.area!==relFilterArea)return false;
                }
                return true;
              }).sort((a,b)=>(a.meetingDate||(a.createdAt?.slice(0,10)??"")).localeCompare(b.meetingDate||(b.createdAt?.slice(0,10)??"")));

              if(fbs.length===0){window.alert("Nenhuma reunião encontrada no período.");return;}

              // Group by area
              const byArea={};
              fbs.forEach(f=>{
                const emp=employees.find(e=>e.id===f.employeeId);
                const role=emp?roles.find(r=>r.id===emp.roleId):null;
                const area=role?.area??"Sem área";
                if(!byArea[area])byArea[area]=[];
                byArea[area].push({...f,empName:emp?.name??"—",roleName:role?.name??"—"});
              });

              // Generate HTML for PDF
              const fromStr=new Date(relFrom+"T12:00:00").toLocaleDateString("pt-BR");
              const toStr=new Date(relTo+"T12:00:00").toLocaleDateString("pt-BR");
              let html=`<html><head><meta charset="utf-8"><style>
                *{margin:0;padding:0;box-sizing:border-box}
                body{font-family:'Segoe UI',Arial,sans-serif;padding:40px;color:#1a1a2e;font-size:12px;line-height:1.5}
                h1{font-size:20px;margin-bottom:4px;color:#1a1a2e}
                .sub{font-size:12px;color:#666;margin-bottom:24px}
                .area-header{font-size:16px;font-weight:700;color:#3b82f6;border-bottom:2px solid #3b82f6;padding-bottom:4px;margin:20px 0 12px}
                .meeting{padding:10px 14px;margin-bottom:8px;border-radius:8px;border:1px solid #e5e7eb;page-break-inside:avoid}
                .meeting-header{display:flex;justify-content:space-between;align-items:center;margin-bottom:4px}
                .emp-name{font-weight:700;font-size:13px}
                .meeting-meta{font-size:10px;color:#888}
                .meeting-type{font-size:10px;padding:2px 8px;border-radius:4px;font-weight:600}
                .type-alinhamento{background:#3b82f614;color:#3b82f6}
                .type-avaliacao{background:#8b5cf614;color:#8b5cf6}
                .notes{font-size:11px;color:#444;margin-top:4px}
                .rating{margin-top:4px;font-size:11px}
                .summary{background:#f8f9fa;border-radius:8px;padding:14px;margin-top:20px;page-break-inside:avoid}
                .summary h3{font-size:14px;margin-bottom:8px}
                .stat{display:inline-block;margin-right:16px;font-size:12px}
                @media print{body{padding:20px}}.page-break{page-break-before:always}
              </style></head><body>`;
              html+=`<h1>Relatório de Reuniões</h1>`;
              html+=`<div class="sub">${fromStr} a ${toStr}${relFilterArea!=="Todos"?` · Área: ${relFilterArea}`:""}${relFilterLider!=="Todos"?` · Líder: ${relFilterLider}`:""}</div>`;

              // Summary
              const totalMeetings=fbs.length;
              const totalAval=fbs.filter(f=>f.meetingType==="avaliação"||(!f.meetingType&&f.rating>0)).length;
              const totalAlinh=totalMeetings-totalAval;
              const uniqueEmps=new Set(fbs.map(f=>f.employeeId)).size;
              html+=`<div class="summary"><h3>Resumo</h3><div><span class="stat"><strong>${totalMeetings}</strong> reuniões</span><span class="stat"><strong>${totalAlinh}</strong> alinhamentos</span><span class="stat"><strong>${totalAval}</strong> avaliações</span><span class="stat"><strong>${uniqueEmps}</strong> pessoas atendidas</span></div></div>`;

              Object.entries(byArea).sort(([a],[b])=>a.localeCompare(b)).forEach(([area,meetings],aIdx)=>{
                if(aIdx>0)html+=`<div class="page-break"></div>`;
                html+=`<div class="area-header">${area} (${meetings.length} reunião${meetings.length!==1?"ões":""})</div>`;
                meetings.forEach(m=>{
                  const isAval=m.meetingType==="avaliação"||(!m.meetingType&&m.rating>0);
                  const dateStr=m.meetingDate?new Date(m.meetingDate+"T12:00:00").toLocaleDateString("pt-BR"):"—";
                  html+=`<div class="meeting">`;
                  html+=`<div class="meeting-header"><div><span class="emp-name">${m.empName}</span> <span class="meeting-meta">${m.roleName}</span></div><div><span class="meeting-type ${isAval?"type-avaliacao":"type-alinhamento"}">${isAval?"Avaliação":"Alinhamento"}</span> <span class="meeting-meta">${dateStr}</span></div></div>`;
                  if(m.notes)html+=`<div class="notes">${m.notes.replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/\n/g,"<br>")}</div>`;
                  if(isAval&&m.rating){
                    const rLabels=["","Insuficiente","Em desenvolvimento","Atende","Supera","Excepcional"];
                    html+=`<div class="rating">Avaliação: <strong>${rLabels[m.rating]??m.rating}</strong></div>`;
                  }
                  if(m.strengths)html+=`<div class="notes"><strong>Pontos positivos:</strong> ${m.strengths.replace(/</g,"&lt;").replace(/\n/g,"<br>")}</div>`;
                  if(m.improvements)html+=`<div class="notes"><strong>Pontos a melhorar:</strong> ${m.improvements.replace(/</g,"&lt;").replace(/\n/g,"<br>")}</div>`;
                  if(m.createdBy)html+=`<div class="meeting-meta" style="margin-top:4px">Conduzida por: ${m.createdBy}</div>`;
                  html+=`</div>`;
                });
              });
              html+=`</body></html>`;

              // Open print dialog
              const w=window.open("","_blank","width=800,height=600");
              if(w){w.document.write(html);w.document.close();setTimeout(()=>w.print(),500);}
              else{onUpdate("_toast","⚠️ Popup bloqueado — permita popups para gerar o PDF");}
            }} style={{...S.btnPrimary,width:"auto",padding:"10px 24px",background:"#3b82f6",borderColor:"#3b82f6"}}>
              📄 Gerar relatório PDF
            </button>
          </div>

          {/* Preview of what will be included */}
          {relFrom && relTo && (() => {
            const fbs = (feedbacks??[]).filter(f=>{
              if(f.restaurantId!==restaurantId||f.deletedAt)return false;
              const d=f.meetingDate||f.createdAt?.slice(0,10);
              if(!d||d<relFrom||d>relTo)return false;
              if(relFilterLider!=="Todos"&&f.createdBy!==relFilterLider)return false;
              if(relFilterArea!=="Todos"){
                const emp=employees.find(e=>e.id===f.employeeId);
                const role=emp?roles.find(r=>r.id===emp.roleId):null;
                if(role?.area!==relFilterArea)return false;
              }
              return true;
            });
            const byArea={};
            fbs.forEach(f=>{
              const emp=employees.find(e=>e.id===f.employeeId);
              const role=emp?roles.find(r=>r.id===emp.roleId):null;
              const area=role?.area??"Sem área";
              if(!byArea[area])byArea[area]=[];
              byArea[area].push(f);
            });
            return (
              <div style={{...S.card,padding:"14px 16px"}}>
                <div style={{fontSize:12,color:"var(--text3)",marginBottom:8}}>Prévia: {fbs.length} reunião{fbs.length!==1?"ões":""} no período</div>
                {Object.entries(byArea).sort(([a],[b])=>a.localeCompare(b)).map(([area,meetings]) => (
                  <div key={area} style={{marginBottom:8}}>
                    <div style={{fontSize:12,fontWeight:700,color:AREA_COLORS[area]??'var(--text)',marginBottom:2}}>{area} — {meetings.length} reunião{meetings.length!==1?"ões":""}</div>
                    <div style={{fontSize:10,color:"var(--text3)",paddingLeft:8}}>
                      {[...new Set(meetings.map(m=>m.employeeId))].map(eid=>{
                        const emp=employees.find(e=>e.id===eid);
                        const count=meetings.filter(m=>m.employeeId===eid).length;
                        return <span key={eid} style={{marginRight:10}}>{shortName(emp?.name)||"?"} ({count})</span>;
                      })}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// Inline component for adding actions to a pauta
function AddActionInline({ pautaId, onAdd }) {
  const [text, setText] = useState("");
  const [responsible, setResponsible] = useState("");
  const [deadline, setDeadline] = useState("");
  const [show, setShow] = useState(false);

  if (!show) return (
    <button onClick={()=>setShow(true)} style={{fontSize:11,padding:"6px 12px",borderRadius:8,border:"1px dashed var(--border)",background:"transparent",color:"var(--text3)",cursor:"pointer",width:"100%",textAlign:"left"}}>
      + Adicionar ação...
    </button>
  );

  return (
    <div style={{padding:"10px",borderRadius:8,border:"1px solid var(--border)",background:"var(--bg2)"}}>
      <input value={text} onChange={e=>setText(e.target.value)} placeholder="O que precisa ser feito..." style={{...S.input,marginBottom:6,fontSize:11}}/>
      <div style={{display:"flex",gap:6,marginBottom:8}}>
        <input value={responsible} onChange={e=>setResponsible(e.target.value)} placeholder="Responsável" style={{...S.input,flex:1,fontSize:11}}/>
        <input type="date" value={deadline} onChange={e=>setDeadline(e.target.value)} style={{...S.input,flex:1,fontSize:11}}/>
      </div>
      <div style={{display:"flex",gap:6}}>
        <button onClick={()=>{onAdd(pautaId,text,responsible,deadline);setText("");setResponsible("");setDeadline("");}} style={{...S.btnPrimary,fontSize:11,padding:"6px 14px",background:"#10b981",borderColor:"#10b981"}}>Salvar</button>
        <button onClick={()=>{setShow(false);setText("");}} style={{...S.btnSecondary,fontSize:11,padding:"6px 14px"}}>Cancelar</button>
      </div>
    </div>
  );
}

function FeedbackForm({ restaurantId, employees, roles, onUpdate, feedbacks, currentUser, isOwner, preSelectedEmpId, allMeetingPlans }) { // eslint-disable-line no-unused-vars
  const restEmps = employees.filter(e => e.restaurantId === restaurantId && !(e.inactive && e.inactiveFrom && e.inactiveFrom <= today()));
  const [empId, setEmpId] = useState(preSelectedEmpId ?? "");
  const [meetingType, setMeetingType] = useState("alinhamento");
  const [meetingDate, setMeetingDate] = useState(today());
  const [notes, setNotes] = useState("");
  const [rating, setRating] = useState(0);
  const [strengths, setStrengths] = useState("");
  const [improvements, setImprovements] = useState("");
  const [otherNotes, setOtherNotes] = useState("");
  const [showNextSuggestion, setShowNextSuggestion] = useState(false);
  const [suggestedNextDate, setSuggestedNextDate] = useState("");
  const [lastRegisteredEmpId, setLastRegisteredEmpId] = useState("");

  const isAvaliacao = meetingType === "avaliação";

  function handleSubmit() {
    if (!empId) { window.alert("Selecione um empregado."); return; }
    if (!meetingDate) { window.alert("Informe a data da reunião."); return; }
    if (isAvaliacao && rating < 1) { window.alert("Selecione a avaliação."); return; }
    const fb = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
      restaurantId,
      employeeId: empId,
      meetingType,
      meetingDate,
      notes: notes.trim(),
      // Campos de avaliação (só preenchidos quando é avaliação)
      rating: isAvaliacao ? rating : null,
      strengths: isAvaliacao ? strengths.trim() : "",
      improvements: isAvaliacao ? improvements.trim() : "",
      internalNotes: isAvaliacao ? otherNotes.trim() : "",
      // Compatibilidade — preencher quarter/year automaticamente
      quarter: Math.ceil((new Date(meetingDate+"T12:00:00").getMonth()+1)/3),
      year: new Date(meetingDate+"T12:00:00").getFullYear(),
      nextFeedbackDate: null,
      goal: "",
      targetRoleId: null,
      devChecklist: [],
      createdAt: new Date().toISOString(),
      createdBy: isOwner ? "Master" : (currentUser?.name ?? "Gestor Adm."),
    };
    onUpdate("feedbacks", [...(feedbacks ?? []), fb]);
    // Suggest next meeting
    const daysAhead = isAvaliacao ? 90 : 15;
    const nextD = new Date(meetingDate+"T12:00:00");
    nextD.setDate(nextD.getDate() + daysAhead);
    setSuggestedNextDate(nextD.toISOString().slice(0,10));
    setLastRegisteredEmpId(empId);
    setShowNextSuggestion(true);
    setEmpId(""); setRating(0); setStrengths(""); setImprovements(""); setOtherNotes(""); setNotes(""); setMeetingDate(today());
  }

  function handleCreateNextPlan() {
    if (!suggestedNextDate || !lastRegisteredEmpId) return;
    const plan = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
      restaurantId,
      type: meetingType || "alinhamento",
      employeeIds: [lastRegisteredEmpId],
      plannedDate: suggestedNextDate,
      note: "Sugestão automática",
      createdBy: isOwner ? "Master" : (currentUser?.name ?? "Gestor Adm."),
      createdAt: new Date().toISOString(),
      completedFeedbackIds: {},
    };
    onUpdate("meetingPlans", [...(allMeetingPlans ?? []), plan]);
    setShowNextSuggestion(false);
  }

  const typeColor = isAvaliacao ? "#8b5cf6" : "#3b82f6";

  return (
    <div style={{...S.card, padding:"18px 20px", border:`1px solid ${typeColor}22`}}>
      <h4 style={{color:"var(--text)",margin:"0 0 14px",fontSize:15,fontWeight:700}}>Registrar reunião</h4>
      {/* Tipo toggle */}
      <div style={{display:"flex",gap:6,marginBottom:14}}>
        {[["alinhamento","💬 Alinhamento","#3b82f6"],["avaliação","📋 Avaliação","#8b5cf6"]].map(([val,lbl,col]) => (
          <button key={val} onClick={()=>setMeetingType(val)} style={{
            flex:1,padding:"10px",borderRadius:10,border:`2px solid ${meetingType===val?col:"var(--border)"}`,
            background:meetingType===val?col+"14":"transparent",color:meetingType===val?col:"var(--text3)",
            cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12,fontWeight:meetingType===val?700:400,transition:"all 0.15s"
          }}>{lbl}</button>
        ))}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
        <div>
          <label style={S.label}>Empregado</label>
          <select value={empId} onChange={e=>setEmpId(e.target.value)} style={S.input}>
            <option value="">Selecionar...</option>
            {restEmps.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
          </select>
        </div>
        <div>
          <label style={S.label}>Data da reunião</label>
          <input type="date" value={meetingDate} onChange={e=>setMeetingDate(e.target.value)} style={S.input}/>
        </div>
      </div>
      <div style={{marginBottom:12}}>
        <label style={S.label}>Anotações / pauta</label>
        <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={2} placeholder="O que foi conversado..." style={{...S.input,resize:"vertical"}}/>
      </div>
      {isAvaliacao && <>
        <div style={{marginBottom:14}}>
          <label style={S.label}>Avaliação</label>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {RATING_LABELS.map((label, i) => {
              const val = i + 1;
              const active = rating === val;
              const color = RATING_COLORS[i];
              return (
                <button key={val} onClick={()=>setRating(val)} style={{
                  padding:"8px 14px",fontSize:12,fontWeight:active?700:500,border:`2px solid ${active?color:"var(--border)"}`,
                  borderRadius:8,cursor:"pointer",background:active?color+"18":"var(--bg)",color:active?color:"var(--text2)",
                  transition:"all 0.15s",lineHeight:1.2,
                }}>{label}</button>
              );
            })}
          </div>
        </div>
        <div style={{marginBottom:12}}>
          <label style={S.label}>Pontos positivos</label>
          <textarea value={strengths} onChange={e=>setStrengths(e.target.value)} rows={2} placeholder="O que o empregado faz bem..." style={{...S.input,resize:"vertical"}}/>
        </div>
        <div style={{marginBottom:12}}>
          <label style={S.label}>Pontos a melhorar</label>
          <textarea value={improvements} onChange={e=>setImprovements(e.target.value)} rows={2} placeholder="Onde pode evoluir..." style={{...S.input,resize:"vertical"}}/>
        </div>
        <div style={{marginBottom:12}}>
          <label style={S.label}>Notas internas</label>
          <textarea value={otherNotes} onChange={e=>setOtherNotes(e.target.value)} rows={2} placeholder="Observações adicionais..." style={{...S.input,resize:"vertical"}}/>
        </div>
      </>}
      <button onClick={handleSubmit} style={{...S.btnPrimary,width:"auto",padding:"10px 24px",background:typeColor,borderColor:typeColor}}>
        {isAvaliacao ? "Registrar avaliação" : "Registrar alinhamento"}
      </button>
      {/* Suggestion to plan next meeting */}
      {showNextSuggestion && (
        <div style={{marginTop:12,padding:"12px 14px",borderRadius:10,border:"1px solid #10b98133",background:"#10b98108"}}>
          <div style={{fontSize:12,color:"#10b981",fontWeight:700,marginBottom:6}}>✓ Reunião registrada!</div>
          <div style={{fontSize:11,color:"var(--text2)",marginBottom:8}}>
            Agendar próxima para <strong>{new Date(suggestedNextDate+"T12:00:00").toLocaleDateString("pt-BR")}</strong>?
          </div>
          <div style={{display:"flex",gap:6}}>
            <button onClick={handleCreateNextPlan} style={{padding:"6px 14px",borderRadius:8,border:"1px solid #10b98144",background:"#10b98118",color:"#10b981",cursor:"pointer",fontSize:11,fontFamily:"'DM Mono',monospace",fontWeight:700}}>Sim, agendar</button>
            <input type="date" value={suggestedNextDate} onChange={e=>setSuggestedNextDate(e.target.value)} style={{...S.input,fontSize:11,padding:"4px 8px",maxWidth:140}}/>
            <button onClick={()=>setShowNextSuggestion(false)} style={{padding:"6px 10px",borderRadius:8,border:"1px solid var(--border)",background:"transparent",color:"var(--text3)",cursor:"pointer",fontSize:11,fontFamily:"'DM Mono',monospace"}}>Não</button>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Gamified Employee Trail View ──
function EmpTrilhaView({ empId, employees, roles, schedules, incidents, feedbacks, devChecklists, restaurantId, onUpdate, employeeGoals, meetingPlans }) {
  const emp = employees.find(e => e.id === empId);
  const role = emp ? roles.find(r => r.id === emp.roleId) : null;
  const admDate = emp?.admission ? new Date(emp.admission+"T12:00:00") : null;
  const daysInCompany = admDate ? Math.floor((new Date() - admDate) / 86400000) : 0;

  // Latest feedback
  const myFeedbacks = (feedbacks ?? []).filter(f => f.restaurantId === restaurantId && f.employeeId === empId && !f.deletedAt).sort((a,b)=>(b.createdAt??"").localeCompare(a.createdAt??""));
  const latestFb = myFeedbacks[0];
  // targetRole removed — goals system replaces it

  // Incidents are fully internal — employee sees nothing
  // const positiveIncidents = []; // removed: ocorrências são internas

  // (badges calculados mais abaixo como badgesNew)

  // Schedule metrics — mês anterior para badge "Sem Faltas"
  const now = new Date();
  const prevMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const mk = monthKey(prevMonthDate.getFullYear(), prevMonthDate.getMonth());
  const ridSchedules = schedules?.[restaurantId] ?? {};
  const myDays = ridSchedules[mk]?.[empId] ?? {};
  const myFaults = Object.values(myDays).filter(s => s === DAY_FAULT_U).length;
  const hasPrevMonthData = Object.keys(myDays).length > 0;

  // Badges — tempo de casa NÃO cumulativo (só o maior)
  const badgesNew = [];
  if (daysInCompany >= 365) badgesNew.push({ icon:"🏆", label:"1 Ano", desc:"Completou 1 ano na empresa" });
  else if (daysInCompany >= 180) badgesNew.push({ icon:"⭐", label:"6 Meses", desc:"6 meses de dedicação" });
  else if (daysInCompany >= 90) badgesNew.push({ icon:"🌟", label:"3 Meses", desc:"Primeiros 90 dias concluídos" });
  // Mérito
  if ((emp?.roleHistory ?? []).length > 0) badgesNew.push({ icon:"⬆️", label:"Promovido", desc:"Já recebeu uma promoção" });
  if (latestFb?.rating === 5) badgesNew.push({ icon:"💎", label:"Excelência", desc:"Avaliação Excepcional" });
  const myGoalsAll = (employeeGoals ?? {})[empId] ?? [];
  const completedGoals = myGoalsAll.filter(g => g.status === "completed");
  if (completedGoals.length > 0) badgesNew.push({ icon:"🎯", label:"Objetivo Concluído", desc:`${completedGoals.length} objetivo${completedGoals.length>1?"s":""} finalizado${completedGoals.length>1?"s":""}` });
  if (hasPrevMonthData && myFaults === 0) badgesNew.push({ icon:"🔥", label:"Sem Faltas", desc:"0 faltas injustificadas no mês anterior" });
  const conhecGoals = completedGoals.filter(g => g.type === "conhecimento" && (g.metas??[]).length > 0 && (g.metas??[]).every(m => m.done));
  if (conhecGoals.length > 0) badgesNew.push({ icon:"📚", label:"Estudioso", desc:"Completou objetivo de conhecimento" });

  // Próximo feedback agendado
  const nextFbInfo = (() => {
    const empFbs = (feedbacks ?? []).filter(f => f.restaurantId === restaurantId && f.employeeId === empId && !f.deletedAt && f.nextFeedbackDate).sort((a,b) => (b.createdAt??"").localeCompare(a.createdAt??""));
    return empFbs[0]?.nextFeedbackDate ?? null;
  })();

  // Jornada timeline — eventos mês a mês desde admissão
  const jornadaEvents = (() => {
    if (!admDate) return [];
    const events = [];
    // Admissão
    events.push({ date: admDate, label: "Admissão", icon: "🚀" });
    // Promoções
    (emp.roleHistory ?? []).forEach(rh => {
      if (rh.date) {
        const newRole = roles.find(r => r.id === rh.newRoleId);
        events.push({ date: new Date(rh.date + "T12:00:00"), label: `Promovido a ${newRole?.name ?? "novo cargo"}`, icon: "⬆️" });
      }
    });
    // Marcos de tempo
    [[90,"🌟","3 Meses"],[180,"⭐","6 Meses"],[365,"🏆","1 Ano"]].forEach(([days,ic,lb]) => {
      if (daysInCompany >= days) {
        const d = new Date(admDate.getTime() + days * 86400000);
        events.push({ date: d, label: lb, icon: ic });
      }
    });
    // Reuniões — texto genérico, sem nota/avaliação
    (feedbacks ?? []).filter(f => f.restaurantId === restaurantId && f.employeeId === empId && !f.deletedAt && f.createdAt).forEach(f => {
      const isAval = f.meetingType === "avaliação" || (!f.meetingType && f.rating > 0);
      events.push({ date: new Date(f.meetingDate ? f.meetingDate+"T12:00:00" : f.createdAt), label: isAval ? "Conversa de avaliação realizada" : "Conversa de alinhamento realizada", icon: isAval ? "📋" : "💬" });
    });
    // Reuniões planejadas futuras
    (meetingPlans ?? []).filter(p => p.restaurantId === restaurantId && (p.employeeIds ?? []).includes(empId) && p.plannedDate >= today()).forEach(p => {
      const isAval = p.type === "avaliação";
      events.push({ date: new Date(p.plannedDate+"T12:00:00"), label: isAval ? "Conversa de avaliação prevista" : "Conversa de alinhamento prevista", icon: "📅", future: true });
    });
    // Objetivos concluídos
    myGoalsAll.filter(g => g.status === "completed").forEach(g => {
      events.push({ date: new Date(g.createdAt), label: `Objetivo: ${g.title}`, icon: "🎯" });
    });
    events.sort((a,b) => a.date - b.date);
    return events;
  })();

  // Empregado marca meta como concluída (self-assessment)
  function empToggleMeta(goalId, metaId) {
    const all = [...(employeeGoals?.[empId] ?? [])];
    const idx = all.findIndex(g => g.id === goalId);
    if (idx < 0) return;
    all[idx] = { ...all[idx], metas: (all[idx].metas ?? []).map(m =>
      m.id === metaId ? { ...m, employeeMarked: !m.employeeMarked, employeeDoneAt: !m.employeeMarked ? new Date().toISOString() : null } : m
    )};
    const updated = { ...(employeeGoals ?? {}), [empId]: all };
    onUpdate("employeeGoals", updated);
  }

  if (!emp) return <p style={{color:"var(--text3)",textAlign:"center"}}>Empregado não encontrado.</p>;

  const activeGoals = myGoalsAll.filter(g => g.status === "active");

  return (
    <div>
      {/* Card do empregado — sem barra de progresso */}
      <div style={{...S.card,marginBottom:16,padding:"20px"}}>
        <div style={{display:"flex",gap:16,alignItems:"center",flexWrap:"wrap"}}>
          <div style={{width:56,height:56,borderRadius:28,background:"linear-gradient(135deg,#d4a017,#f59e0b)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,fontWeight:700,color:"#fff",flexShrink:0}}>{(emp.name??"?").charAt(0)}</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{color:"var(--text)",fontWeight:700,fontSize:18}}>{emp.name}</div>
            <div style={{color:"var(--ac)",fontSize:13,fontWeight:600}}>{role?.name ?? "—"}</div>
            <div style={{color:"var(--text3)",fontSize:11}}>{role?.area ?? "—"} · {daysInCompany} dias na empresa</div>
          </div>
        </div>
      </div>

      {/* Seus Objetivos — em destaque */}
      {activeGoals.length > 0 && (
        <div style={{marginBottom:16}}>
          <h4 style={{color:"var(--text)",margin:"0 0 12px",fontSize:14}}>🎯 Seus Objetivos</h4>
          {activeGoals.map(goal => {
            const typeInfo = GOAL_TYPES.find(t => t.value === goal.type) ?? GOAL_TYPES[3];
            const metas = goal.metas ?? [];
            const doneMetas = metas.filter(m => {
              if (m.autoCheck && m.autoCheckRule) {
                const rule = AUTO_META_RULES.find(r => r.id === m.autoCheckRule);
                return rule ? rule.check(empId, schedules, restaurantId, feedbacks, restaurantId, employees) : m.done;
              }
              return m.done;
            }).length;
            const pct = metas.length > 0 ? Math.round((doneMetas / metas.length) * 100) : 0;
            const materials = goal.materials ?? [];

            return (
              <div key={goal.id} style={{...S.card,marginBottom:10,padding:"14px 16px",borderLeft:`4px solid ${typeInfo.color}`}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                  <span style={{fontSize:18}}>{typeInfo.icon}</span>
                  <div style={{flex:1}}>
                    <div style={{color:"var(--text)",fontWeight:700,fontSize:14}}>{goal.title}</div>
                    <div style={{color:"var(--text3)",fontSize:11}}>{typeInfo.label}</div>
                  </div>
                </div>

                {metas.length > 0 && (
                  <>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                      <span style={{color:"var(--text3)",fontSize:11}}>Progresso</span>
                      <span style={{color:typeInfo.color,fontSize:11,fontWeight:700}}>{pct}%</span>
                    </div>
                    <div style={{height:6,borderRadius:3,background:"var(--border)",overflow:"hidden",marginBottom:8}}>
                      <div style={{height:"100%",borderRadius:3,background:typeInfo.color,width:`${pct}%`,transition:"width 0.3s"}}/>
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:2,marginBottom:8}}>
                      {metas.map(meta => {
                        const isAuto = meta.autoCheck && meta.autoCheckRule;
                        const autoRule = isAuto ? AUTO_META_RULES.find(r => r.id === meta.autoCheckRule) : null;
                        const gestorDone = isAuto ? (autoRule ? autoRule.check(empId, schedules, restaurantId, feedbacks, restaurantId, employees) : meta.done) : meta.done;
                        const empMarked = meta.employeeMarked;
                        const fullyDone = gestorDone;
                        return (
                          <div key={meta.id} style={{padding:"6px 0",borderBottom:"1px solid var(--border)11"}}>
                            <div style={{display:"flex",alignItems:"center",gap:8}}>
                              {isAuto ? (
                                <span style={{fontSize:13}}>{gestorDone?"✅":"⏳"}</span>
                              ) : (
                                <button onClick={()=>!fullyDone && empToggleMeta(goal.id, meta.id)} style={{background:"none",border:"none",cursor:fullyDone?"default":"pointer",fontSize:13,padding:0,lineHeight:1,opacity:fullyDone?1:undefined}}>
                                  {fullyDone ? "✅" : empMarked ? "🟡" : "⬜"}
                                </button>
                              )}
                              <span style={{color:fullyDone?"var(--text3)":"var(--text)",fontSize:12,textDecoration:fullyDone?"line-through":"none",flex:1}}>{meta.title}</span>
                            </div>
                            {!isAuto && empMarked && !fullyDone && (
                              <div style={{marginLeft:21,marginTop:2,fontSize:10,color:"#f59e0b",fontStyle:"italic"}}>
                                Concluída por você em {meta.employeeDoneAt ? new Date(meta.employeeDoneAt).toLocaleDateString("pt-BR") : "—"} · aguardando confirmação do gestor
                              </div>
                            )}
                            {fullyDone && meta.doneAt && (
                              <div style={{marginLeft:21,marginTop:2,fontSize:10,color:"var(--green)"}}>
                                Confirmada em {new Date(meta.doneAt).toLocaleDateString("pt-BR")}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {materials.length > 0 && (
                  <div>
                    <div style={{color:"var(--text3)",fontSize:10,fontWeight:600,marginBottom:4}}>📎 Material de apoio</div>
                    {materials.map(mat => {
                      const mtIcon = MATERIAL_TYPES.find(t=>t.value===mat.type)?.icon ?? "📋";
                      return (
                        <div key={mat.id} style={{display:"flex",alignItems:"center",gap:6,padding:"3px 0",fontSize:12}}>
                          <span>{mtIcon}</span>
                          {mat.link ? <a href={mat.link} target="_blank" rel="noreferrer" style={{color:"#3b82f6"}}>{mat.title}</a> : <span style={{color:"var(--text)"}}>{mat.title}</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Próximas reuniões planejadas */}
      {(() => {
        const upcoming = (meetingPlans ?? []).filter(p => p.restaurantId === restaurantId && (p.employeeIds ?? []).includes(empId) && p.plannedDate >= today()).sort((a,b)=>a.plannedDate.localeCompare(b.plannedDate)).slice(0,3);
        if (upcoming.length === 0 && nextFbInfo) {
          // Fallback: old nextFbDate from legacy feedbacks
          const nfd = new Date(nextFbInfo + "T00:00:00");
          const todayD = new Date(); todayD.setHours(0,0,0,0);
          const diff = Math.round((nfd - todayD) / 86400000);
          return (
            <div style={{...S.card,marginBottom:16,padding:"14px 16px",display:"flex",alignItems:"center",gap:10,borderLeft:"4px solid #3b82f6"}}>
              <span style={{fontSize:20}}>📅</span>
              <div>
                <div style={{color:"var(--text)",fontWeight:600,fontSize:13}}>Próxima reunião</div>
                <div style={{color:"var(--text3)",fontSize:12}}>{diff <= 0 ? `Agendada para ${nfd.toLocaleDateString("pt-BR")}` : `Em ${diff} dia${diff!==1?"s":""} · ${nfd.toLocaleDateString("pt-BR")}`}</div>
              </div>
            </div>
          );
        }
        if (upcoming.length === 0) return null;
        return (
          <div style={{marginBottom:16}}>
            {upcoming.map(p => {
              const pDate = new Date(p.plannedDate+"T12:00:00");
              const todayD = new Date(); todayD.setHours(0,0,0,0);
              const diff = Math.round((pDate - todayD) / 86400000);
              const isAval = p.type === "avaliação";
              const pColor = isAval ? "#8b5cf6" : "#3b82f6";
              return (
                <div key={p.id} style={{...S.card,padding:"14px 16px",marginBottom:8,display:"flex",alignItems:"center",gap:10,borderLeft:`4px solid ${pColor}`}}>
                  <span style={{fontSize:20}}>📅</span>
                  <div>
                    <div style={{color:"var(--text)",fontWeight:600,fontSize:13}}>
                      {isAval ? "Conversa de avaliação prevista" : "Conversa de alinhamento prevista"}
                    </div>
                    <div style={{color:"var(--text3)",fontSize:12}}>
                      {diff === 0 ? "Hoje" : diff === 1 ? "Amanhã" : `Em ${diff} dia${diff!==1?"s":""}`} · {pDate.toLocaleDateString("pt-BR")}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Conquistas */}
      {badgesNew.length > 0 && (
        <div style={{marginBottom:16}}>
          <h4 style={{color:"var(--text)",margin:"0 0 10px",fontSize:14}}>🏅 Conquistas</h4>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            {badgesNew.map((b,i) => {
              const gradients = [
                "linear-gradient(135deg,#fef3c7,#fde68a)", // gold
                "linear-gradient(135deg,#dbeafe,#bfdbfe)", // blue
                "linear-gradient(135deg,#d1fae5,#a7f3d0)", // green
                "linear-gradient(135deg,#ede9fe,#ddd6fe)", // purple
                "linear-gradient(135deg,#fee2e2,#fecaca)", // red
                "linear-gradient(135deg,#ffedd5,#fed7aa)", // orange
                "linear-gradient(135deg,#f0fdf4,#dcfce7)", // lime
                "linear-gradient(135deg,#fdf4ff,#f5d0fe)", // pink
              ];
              return (
                <div key={i} style={{borderRadius:12,padding:"14px 12px",background:gradients[i % gradients.length],display:"flex",alignItems:"center",gap:10,boxShadow:"0 1px 3px rgba(0,0,0,0.06)"}}>
                  <div style={{width:40,height:40,borderRadius:20,background:"rgba(255,255,255,0.7)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>{b.icon}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{color:"#1f2937",fontWeight:700,fontSize:12,lineHeight:1.2}}>{b.label}</div>
                    <div style={{color:"#6b7280",fontSize:10,lineHeight:1.3,marginTop:1}}>{b.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Sua Jornada — timeline mês a mês */}
      {jornadaEvents.length > 0 && (
        <div style={{marginBottom:16}}>
          <h4 style={{color:"var(--text)",margin:"0 0 12px",fontSize:14}}>🗓️ Sua Jornada</h4>
          <div style={{position:"relative",paddingLeft:24}}>
            {/* Linha vertical */}
            <div style={{position:"absolute",left:7,top:4,bottom:4,width:2,background:"var(--border)",borderRadius:1}}/>
            {jornadaEvents.map((ev,i) => (
              <div key={i} style={{position:"relative",marginBottom:i<jornadaEvents.length-1?12:0,display:"flex",alignItems:"flex-start",gap:10}}>
                {/* Dot */}
                <div style={{position:"absolute",left:-20,top:3,width:12,height:12,borderRadius:6,background:i===jornadaEvents.length-1?"var(--ac)":"var(--border)",border:"2px solid var(--bg)",flexShrink:0,zIndex:1}}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{fontSize:13}}>{ev.icon}</span>
                    <span style={{color:"var(--text)",fontSize:12,fontWeight:600}}>{ev.label}</span>
                  </div>
                  <div style={{color:"var(--text3)",fontSize:10,marginTop:1}}>
                    {ev.date.toLocaleDateString("pt-BR",{month:"short",year:"numeric"})}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// v8.1 — Indicador padronizado de status de salvamento.
// Usado tanto em telas auto-save quanto em telas com botão Salvar manual.
// Props:
//   mode: "auto" (auto-save) ou "manual" (botão explícito)
//   pending: bool — há edições não persistidas?
//   savedAt: Date|number|null — timestamp do último save bem-sucedido (auto)
//   compact: bool — versão pequena pra encaixar em header
function SaveIndicator({ mode = "auto", pending = false, savedAt = null, compact = false }) {
  const fmtTime = (t) => {
    if (!t) return "";
    const d = t instanceof Date ? t : new Date(t);
    return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  };
  let bg, color, text;
  if (mode === "auto") {
    if (pending) { bg = "#f59e0b15"; color = "#b45309"; text = "💾 Salvando…"; }
    else if (savedAt) { bg = "#10b98115"; color = "var(--green)"; text = `✓ Salvo às ${fmtTime(savedAt)}`; }
    else { bg = "#3b82f615"; color = "#0284c7"; text = "✓ Salvamento automático ativo"; }
  } else {
    if (pending) { bg = "#f59e0b22"; color = "#b45309"; text = "📝 Edições não salvas"; }
    else { bg = "#10b98115"; color = "var(--green)"; text = "✓ Salvo"; }
  }
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: compact ? "3px 8px" : "5px 11px",
      borderRadius: 999, background: bg, color,
      fontSize: compact ? 10 : 11, fontWeight: 700, whiteSpace: "nowrap",
      fontFamily: "'DM Sans',sans-serif",
    }}>{text}</span>
  );
}

// v8.4 — Modal bloqueante de seleção de mês.
// Aparece na 1ª entrada de telas com mês contextual (Escala, VT).
// Força usuário a escolher antes de ver/editar dados — elimina edição em mês errado.
//   props: contextLabel ("Escala" | "Vale Transporte"), year, month, onPick(year,month), scheduleStatus
function MonthPickerGate({ contextLabel, year, month, onPick, onCancel, scheduleStatus, restaurantId }) {
  // Smart default
  const sd = smartDefaultMonth();
  const today = new Date();
  // v8.15.2: 3 botões — anterior, atual em destaque, seguinte. Setas navegam ±3 meses.
  const [offset, setOffset] = useState(0);
  // v8.15.3: ESC fecha o gate e volta pra tela anterior (se onCancel foi passado)
  useEffect(() => {
    if (!onCancel) return;
    const handler = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel]);
  const startDelta = -1 + offset;
  const options = [];
  for (let i = 0; i < 3; i++) {
    const delta = startDelta + i;
    const d = new Date(today.getFullYear(), today.getMonth() + delta, 1);
    const y = d.getFullYear(), m = d.getMonth();
    let context = "", bg = "var(--bg2)", color = "var(--text)", border = "var(--border)";
    if (delta < 0) { context = "Mês passado"; bg = "#64748b15"; color = "#64748b"; border = "#64748b44"; }
    else if (delta === 0) { context = "Mês atual"; bg = "#d4a01715"; color = "#d4a017"; border = "#d4a01744"; }
    else { context = "Mês futuro"; bg = "#16a34a15"; color = "#16a34a"; border = "#16a34a44"; }
    const isCurrent = delta === 0;
    const isSmart = (y === sd.year && m === sd.month);
    const mk = monthKey(y, m);
    const isClosed = !!scheduleStatus?.[restaurantId]?.[mk] && scheduleStatus[restaurantId][mk].status === "closed";
    if (isClosed) { context = "Mês fechado 🔒"; bg = "#dc262615"; color = "#dc2626"; border = "#dc262644"; }
    options.push({ y, m, label: monthLabel(y, m), context, bg, color, border, isSmart, isCurrent, mk, isClosed });
  }
  const firstM = options[0], lastM = options[options.length - 1];
  const rangeLabel = `${monthLabel(firstM.y, firstM.m).replace(" de ","/")} → ${monthLabel(lastM.y, lastM.m).replace(" de ","/")}`;
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 10000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      animation: "fadeIn .15s ease-out",
    }} onClick={onCancel ? () => onCancel() : undefined}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "var(--bg)", borderRadius: 16, padding: 28, maxWidth: 560, width: "100%",
        border: "1px solid var(--border)", boxShadow: "0 16px 48px rgba(0,0,0,0.25)",
        position: "relative",
      }}>
        {/* v8.15.3: Botão fechar — volta pra tela anterior */}
        {onCancel && (
          <button onClick={onCancel} title="Fechar (ESC)" style={{
            position: "absolute", top: 12, right: 12,
            width: 32, height: 32, borderRadius: 16, border: "1px solid var(--border)",
            background: "var(--bg2)", color: "var(--text2)",
            cursor: "pointer", fontSize: 16, fontWeight: 700,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "'DM Sans',sans-serif",
          }} onMouseEnter={e => { e.currentTarget.style.background = "var(--red)15"; e.currentTarget.style.color = "var(--red)"; e.currentTarget.style.borderColor = "var(--red)44"; }}
             onMouseLeave={e => { e.currentTarget.style.background = "var(--bg2)"; e.currentTarget.style.color = "var(--text2)"; e.currentTarget.style.borderColor = "var(--border)"; }}>×</button>
        )}
        <div style={{ textAlign: "center", marginBottom: 14 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>📅</div>
          <h2 style={{ color: "var(--text)", margin: "0 0 6px", fontSize: 20, fontWeight: 800 }}>
            Qual mês você vai trabalhar em <span style={{ color: "var(--ac)" }}>{contextLabel}</span>?
          </h2>
          <p style={{ color: "var(--text3)", fontSize: 13, lineHeight: 1.5, margin: 0 }}>
            Confirme o mês antes de visualizar ou editar — evita alterações em mês errado.
          </p>
        </div>

        {/* Navegação ±3 meses + voltar pra hoje */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,gap:8}}>
          <button onClick={()=>setOffset(o => o - 3)} style={{...S.btnSecondary, fontSize:12, padding:"6px 12px", whiteSpace:"nowrap"}} title="3 meses anteriores">◀ Anteriores</button>
          <div style={{flex:1, textAlign:"center"}}>
            <div style={{fontSize:11,color:"var(--text3)",fontFamily:"'DM Mono',monospace",textTransform:"capitalize"}}>{rangeLabel}</div>
            {offset !== 0 && (
              <button onClick={()=>setOffset(0)} style={{marginTop:4,fontSize:10,padding:"3px 10px",border:"1px solid var(--ac)44",background:"var(--ac)11",color:"var(--ac)",borderRadius:14,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontWeight:600}}>📍 Voltar pra hoje</button>
            )}
          </div>
          <button onClick={()=>setOffset(o => o + 3)} style={{...S.btnSecondary, fontSize:12, padding:"6px 12px", whiteSpace:"nowrap"}} title="3 meses seguintes">Próximos ▶</button>
        </div>

        {/* 3 botões em linha — mês atual em destaque (maior, sombra mais forte, badge) */}
        <div style={{display:"grid", gridTemplateColumns:"1fr 1.25fr 1fr", gridAutoRows:"1fr", gap:10, alignItems:"stretch"}}>
          {options.map(o => {
            const big = o.isCurrent;
            return (
              <button key={`${o.y}-${o.m}`} onClick={() => onPick(o.y, o.m)}
                style={{
                  background: o.bg, border: `${big ? 2 : 1}px solid ${big ? o.color : o.border}`,
                  borderRadius: big ? 14 : 12,
                  padding: big ? "20px 12px" : "14px 10px",
                  cursor: "pointer", fontFamily: "'DM Sans',sans-serif",
                  position: "relative", transition: "transform .12s, box-shadow .12s",
                  width: "100%", boxSizing: "border-box", minWidth: 0,
                  minHeight: big ? 110 : 88,
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4,
                  boxShadow: big ? `0 4px 16px ${o.color}33` : "none",
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 6px 18px ${o.color}55`; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = big ? `0 4px 16px ${o.color}33` : "none"; }}>
                {big && (
                  <span style={{ position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)", fontSize: 10, padding: "3px 12px", borderRadius: 999, background: o.color, color: "#fff", fontWeight: 800, letterSpacing: 0.5, whiteSpace:"nowrap" }}>★ ATUAL</span>
                )}
                {!big && o.isSmart && (
                  <span style={{ position: "absolute", top: 6, right: 6, fontSize: 9, padding: "2px 8px", borderRadius: 999, background: "var(--ac)", color: "#fff", fontWeight: 800, letterSpacing: 0.3 }}>SUGERIDO</span>
                )}
                <div style={{ fontSize: big ? 16 : 13, fontWeight: 800, color: "var(--text)", lineHeight: 1.2, textAlign: "center", textTransform: "capitalize" }}>{o.label}</div>
                <div style={{ fontSize: big ? 12 : 10, color: o.color, fontWeight: 600, textAlign: "center" }}>{o.context}</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function RestaurantPanel({ restaurant, restaurants, employees, roles, tips, splits, schedules, onUpdate, perms, isOwner, data, currentUser, privacyMask, mobileOnly, onEnterOperational, hideTabNav, forceTab, realIsOwner, onStartImpersonate, restrictToOperational }) {
  const now = new Date();
  // v8.0 — default inteligente: dia ≥ 20 → próximo mês (planejamento)
  const _smartDef = smartDefaultMonth();
  const [year, setYear] = useState(_smartDef.year);
  const [month, setMonth] = useState(_smartDef.month);
  // v8.4 — gate de seleção de mês: força usuário a confirmar antes de editar
  const [scheduleMonthConfirmed, setScheduleMonthConfirmed] = useState(false);
  const mk = monthKey(year, month);
  const rid = restaurant.id;

  // Privacy: mascarar dados quando ativo (admin vendo restaurante com privacyMode)
  const pFmt = privacyMask ? () => "R$ ••••,••" : fmt;
  const pText = privacyMask ? () => "••••••••••••••••" : (v => v);

  // ── Helpers de versionamento (Onda 2 cleanup) ──
  // Encapsulam o autor + snapshot + saveVersion num único call.
  // Snapshot é opcional: se omitido, captura o estado atual.
  function authorName() {
    return currentUser?.name || (isOwner ? "Master" : "Gestor Adm.");
  }
  function saveTipsSnapshot(reason, snapshot) {
    const snap = snapshot !== undefined ? snapshot : snapshotTipsMonth(tips, rid, mk);
    saveVersion("tips", rid, mk, data?.tipVersions, snap, authorName(), reason, onUpdate, true);
  }
  function saveSchedulesSnapshot(reason, snapshot) {
    const snap = snapshot !== undefined ? snapshot : snapshotSchedulesMonth(schedules, rid, mk);
    saveVersion("schedules", rid, mk, data?.scheduleVersions, snap, authorName(), reason, onUpdate, true);
  }
  // Commit das edições pendentes em schedLocalEdits → escala persistida.
  // Usado nos 3 caminhos onde o usuário sai com edições não salvas
  // (mudar de aba, voltar pro dashboard mobile, mudar mês).
  // O botão dedicado "Salvar nova versão" tem fluxo próprio (gera adjustments).
  function commitPendingScheduleEdits(reason = "Edição manual") {
    if (!schedLocalEdits) return;
    // Freeze prevista no primeiro ajuste (depois disso vira read-only)
    if (!data?.schedulePrevista?.[rid]?.[mk]) {
      const frozenPrevista = JSON.parse(JSON.stringify(schedules?.[rid]?.[mk] ?? {}));
      const newPrev = { ...(data?.schedulePrevista ?? {}) };
      if (!newPrev[rid]) newPrev[rid] = {};
      newPrev[rid][mk] = frozenPrevista;
      onUpdate("schedulePrevista", newPrev);
    }
    saveSchedulesSnapshot(reason);
    let newMonth = { ...(schedules?.[rid]?.[mk] ?? {}) };
    Object.entries(schedLocalEdits).forEach(([eid, dayEdits]) => {
      const empMap = { ...(newMonth[eid] ?? {}) };
      Object.entries(dayEdits).forEach(([dt, val]) => { if (val === null) delete empMap[dt]; else empMap[dt] = val; });
      newMonth[eid] = empMap;
    });
    onUpdate("schedules", { ...schedules, [rid]: { ...(schedules?.[rid]??{}), [mk]: newMonth } });
    setSchedLocalEdits(null);
  }

  // v7.9: usa getSplitForDate (suporta versionado novo + legacy). Pega regra ativa
  // no fim do mês visualizado (mk) — essa é a referência canônica pra resumo do mês.
  const _mkLastDay = (() => {
    const [yy, mm] = mk.split("-").map(Number);
    const last = new Date(yy, mm, 0).getDate(); // último dia do mês
    return `${mk}-${String(last).padStart(2, "0")}`;
  })();
  const _activeSplitMk = getSplitForDate(splits?.[rid], _mkLastDay);
  const _empsPerAreaMk = {};
  (employees || [])
    .filter(e => e.restaurantId === rid && !e.isFreela && !(e.inactive && e.inactiveFrom && e.inactiveFrom <= _mkLastDay))
    .forEach(e => {
      const r = (roles || []).find(x => x.id === e.roleId);
      if (r?.area) _empsPerAreaMk[r.area] = (_empsPerAreaMk[r.area] || 0) + 1;
    });
  const curSplit = _activeSplitMk?.percentages
    ? computeAreaPercentages(_activeSplitMk.percentages, _empsPerAreaMk)
    : DEFAULT_SPLIT;
  const monthTips = tips.filter(t => t.restaurantId === rid && t.monthKey === mk);
  const tipDates  = [...new Set(monthTips.map(t => t.date))].sort();
  const totalNet   = monthTips.reduce((a, t) => a + t.myNet, 0);
  const totalTax   = monthTips.reduce((a, t) => a + t.myTax, 0);

  const managerAreas = perms.managerAreas ?? [];
  const isLider = managerAreas.length > 0;
  const allRestEmps = employees.filter(e => e.restaurantId === rid && !(e.inactive && e.inactiveFrom && e.inactiveFrom <= today()));
  // For schedule: regra v7.4
  //   - Empregado demitido em MÊS ANTERIOR ao visualizado (mk) → fora, sempre
  //     (mesmo que inactive flag não tenha sido setado por algum bug histórico)
  //   - Empregado inativo por outro motivo (ex.: férias, afastamento longo) → fora
  //   - Empregado demitido NESTE mês visualizado → APARECE (pra ver últimos dias)
  //   - Empregado ativo → APARECE
  const allSchedEmps = employees.filter(e => {
    if (e.restaurantId !== rid) return false;
    // demitidoEm = primeiro dia FORA → mês relevante é o do último dia trabalhado (= demitidoEm - 1).
    const demMonth = e.demitidoEm ? addDaysISO(e.demitidoEm, -1).slice(0,7) : null;
    if (demMonth && demMonth < mk) return false; // último dia trabalhado num mês anterior — sempre fora
    const isInactiveNow = !!(e.inactive && e.inactiveFrom && e.inactiveFrom <= today());
    if (isInactiveNow && demMonth !== mk) return false; // inativo e não demitido neste mês — fora
    return true;
  });
  const restRoles = roles.filter(r => r.restaurantId === rid);
  const restEmps  = isLider
    ? allRestEmps.filter(e => { const role = restRoles.find(r => r.id === e.roleId); return role && managerAreas.includes(role.area); })
    : allRestEmps;
  // Líder na Escala: vê todos (apenas visualização), sem filtro por área.
  const schedEmps = allSchedEmps;

  // forms
  const [tipRows, setTipRows]   = useState([{date:today(),total:"",note:""}]);
  const [splitForm, setSplitForm]         = useState(null);
  const [schedArea, setSchedArea]           = useState("Todos");
  const [showVacForm, setShowVacForm]       = useState(false);
  const [showSchedHistory, setShowSchedHistory] = useState(false);
  // Ponto import — REMOVIDO em abril/2026 (parser PDF Sólides era frágil; voltar quando estável)
  // Delay (atraso) batch form
  const [showDelayForm, setShowDelayForm] = useState(false);
  const [delayEmpId, setDelayEmpId] = useState("");
  const [delayInputs, setDelayInputs] = useState({}); // { "day": minutes }
  // Schedule view mode: "vigente" shows effective (with local edits), "prevista" shows saved only
  const [schedViewMode, setSchedViewMode] = useState("vigente");
  // v8.12.0: removido sundaySuggestionPreview (escala deriva automático)
  // v8.11.0: modal de inversão informal de domingo (Feature 2) — empAId + date pré-selecionados
  const [swapModalContext, setSwapModalContext] = useState(null); // { empAId, date } | null
  // v8.12.0: confirmação do reset da escala
  const [showResetSchedConfirm, setShowResetSchedConfirm] = useState(false);
  // Inversão de dia inteiro (feriados): troca o status de todos os empregados entre dois dias do mês.
  // Empregados de férias num dos dois dias são preservados.
  const [invertDayForm, setInvertDayForm] = useState(null); // null | { dateA, dateB, reason }
  // Month close / reopen confirmation
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showReopenConfirm, setShowReopenConfirm] = useState(false);
  const [closeDelta, setCloseDelta] = useState(null);
  const [closeVtImpact, setCloseVtImpact] = useState(null);
  // pontoSummary REMOVIDO junto com Importar ponto (abril/2026)
  // Local schedule edits — accumulated before saving as new version
  const [schedLocalEdits, setSchedLocalEdits] = useState(null); // null = no pending edits, object = pending edits overlay
  const schedDirty = schedLocalEdits !== null;

  // v8.12.0: Escala derivada — folgas fixas do horário + ciclo de domingos.
  // Aplicada APENAS aos empregados que ainda não têm dados salvos no mês.
  // Empregados sem ciclo de domingo cadastrado: trabalham todos os domingos (sem alerta).
  const derivedScheduleByEmp = useMemo(() => {
    const out = {};
    const dim = new Date(year, month + 1, 0).getDate();
    allSchedEmps.forEach(emp => {
      const empWS = data?.workSchedules?.[rid]?.[emp.id] ?? [];
      if (empWS.length === 0) return;
      const empMap = {};
      for (let d = 1; d <= dim; d++) {
        const dateStr = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
        // Skip pre-vigência do restaurante
        if (restaurant.serviceStartDate && dateStr < restaurant.serviceStartDate) continue;
        // Skip após demissão
        if (emp.demitidoEm && dateStr >= emp.demitidoEm) continue;
        const ws = getActiveWorkSchedule(empWS, dateStr);
        if (!ws) continue;
        if (ws.validFrom && dateStr < ws.validFrom) continue;
        const days = getEffectiveDays(ws, dateStr);
        if (!days) continue;
        const dow = new Date(dateStr+"T12:00:00").getDay();
        const dayCfg = days[dow];
        // Folga fixa do horário
        if (!dayCfg || dayCfg.active === false) {
          empMap[dateStr] = DAY_OFF;
          continue;
        }
        // Domingo + ciclo cadastrado → usa ciclo. Sem ciclo → trabalha (default).
        if (dow === 0) {
          const cycle = getSundayCycleFromSchedule(ws, dateStr);
          if (cycle && cycle.refDate && isSundayOffByCycle(cycle, dateStr)) {
            empMap[dateStr] = DAY_OFF;
          }
        }
      }
      if (Object.keys(empMap).length > 0) out[emp.id] = empMap;
    });
    return out;
  }, [allSchedEmps, year, month, mk, rid, data?.workSchedules, restaurant.serviceStartDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Empregado é considerado "não editado" (=> aplica derivação) quando:
  //  • não tem nenhuma entrada em schedules[rid][mk][empId] (mapa vazio)
  // Se já tem qualquer entrada salva, mostra exatamente o salvo (sem derivar).
  function applyDerivationToEmp(empId) {
    const saved = schedules?.[rid]?.[mk]?.[empId];
    return !saved || Object.keys(saved).length === 0;
  }

  // Effective schedule month: derivado (se sem saved) → saved → local edits
  const effectiveMonth = (() => {
    const base = {};
    const saved = schedules?.[rid]?.[mk] ?? {};
    // Para cada empregado da escala: se tem saved, usa saved; senão usa derivado
    allSchedEmps.forEach(emp => {
      if (applyDerivationToEmp(emp.id)) {
        base[emp.id] = { ...(derivedScheduleByEmp[emp.id] ?? {}) };
      } else {
        base[emp.id] = { ...saved[emp.id] };
      }
    });
    // Outros empregados que estejam em saved mas não em allSchedEmps (ex.: já demitidos antes)
    Object.entries(saved).forEach(([eid, dayMap]) => {
      if (!base[eid]) base[eid] = { ...dayMap };
    });
    if (schedLocalEdits) {
      Object.entries(schedLocalEdits).forEach(([eid, dayMap]) => {
        base[eid] = { ...(base[eid] ?? {}), ...dayMap };
        Object.entries(base[eid]).forEach(([dt, val]) => { if (val === null) delete base[eid][dt]; });
      });
    }
    return base;
  })();
  // Prevista: frozen snapshot, com fallback pra effectiveMonth (que já inclui derivação)
  const previstaMonth = (() => {
    const frozen = data?.schedulePrevista?.[rid]?.[mk];
    if (frozen) return frozen;
    // Sem snapshot: usa effective (que já tem derivação aplicada onde cabe)
    return effectiveMonth;
  })();
  // Displayed schedule depends on view mode
  const displayedMonth = schedViewMode === "prevista" ? previstaMonth : effectiveMonth;
  // Month status from scheduleStatus
  const monthStatus = data?.scheduleStatus?.[rid]?.[mk]?.status ?? "open";
  const monthClosed = monthStatus === "closed";
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewFileName, setPreviewFileName] = useState("");
  const [showTipHistory, setShowTipHistory]     = useState(false);
  const [vacEmpId, setVacEmpId]             = useState("");
  const [vacFrom, setVacFrom]               = useState("");
  const [vacTo, setVacTo]                   = useState("");
  function calcWeekForToday(y, m) {
    const now = new Date();
    const isCurrentMonth = now.getFullYear() === y && now.getMonth() === m;
    if (!isCurrentMonth) return 0;
    const todayDay = now.getDate();
    const daysInM = new Date(y, m + 1, 0).getDate();
    let wk = 0, d = 1;
    while (d <= daysInM) {
      const wd0 = new Date(y, m, d).getDay();
      let weekEnd = d + (6 - wd0);
      weekEnd = Math.min(weekEnd, daysInM);
      if (todayDay >= d && todayDay <= weekEnd) return wk;
      d = weekEnd + 1; wk++;
    }
    return 0;
  }
  const [weekIdx, setWeekIdx] = useState(() => calcWeekForToday(now.getFullYear(), now.getMonth()));

  const [showExport, setShowExport]       = useState(false);

  // DP gestor management
  const [dpMgrModal, setDpMgrModal] = useState(false);
  const [dpMgrEdit, setDpMgrEdit]   = useState(null);
  const [dpMgrForm, setDpMgrForm]   = useState({name:"",cpf:"",pin:"",restaurantIds:[rid],perms:{tips:true,schedule:true},isDP:false,profile:"custom",areas:[]});

  // Config local state — unified deferred save for ALL config settings
  const [localConfig, setLocalConfig] = useState(null); // null = clean, object = pending changes
  const configDirty = localConfig !== null;
  // Modal pra escolher data retroativa quando muda taxRate
  const [taxRetroModal, setTaxRetroModal] = useState(null); // null | { oldRate, newRate, fromDate, applyRetro }

  function getLocalRest() {
    if (!localConfig) return { ...restaurant };
    return { ...restaurant, ...localConfig };
  }
  function patchConfig(patch) {
    setLocalConfig(prev => ({ ...(prev ?? {}), ...patch }));
  }

  const localRest = getLocalRest();
  function getTabsConfig(key) { return (localRest.tabsConfig ?? {})[key] !== false; }
  function getTabsGestor(key) { return (localRest.tabsGestor ?? {})[key] !== false; }

  function toggleAdminTab(key) {
    const cur = { ...(localRest.tabsConfig ?? {}) };
    const novoValor = !(cur[key] !== false);
    cur[key] = novoValor;
    const tabFaqMap = { dp:"dp", comunicados:"comunicados" }; // v8.9.1 sem __ — Firestore rejeita
    const faqId = tabFaqMap[key];
    const curGestor = { ...(localRest.tabsGestor ?? {}) };
    if (faqId) curGestor.faqAuto = { ...(curGestor.faqAuto ?? {}), [faqId]: novoValor };
    patchConfig({ tabsConfig: cur, tabsGestor: curGestor });
  }
  function toggleGestorTab(key) {
    const cur = { ...(localRest.tabsGestor ?? {}) };
    const novoValor = !(cur[key] !== false);
    cur[key] = novoValor;
    const tabFaqMap = { dp:"dp", comunicados:"comunicados" }; // v8.9.1 sem __
    const faqId = tabFaqMap[key];
    if (faqId) cur.faqAuto = { ...(cur.faqAuto ?? {}), [faqId]: novoValor };
    patchConfig({ tabsGestor: cur });
  }
  function saveConfig() {
    if (!localConfig) return;
    // Detecta mudança de taxRate — se mudou, pergunta a partir de qual data aplicar
    const oldTaxRate = restaurant.taxRate ?? TAX;
    const newTaxRate = localConfig.taxRate ?? oldTaxRate;
    const taxChanged = "taxRate" in localConfig && newTaxRate !== oldTaxRate;
    if (taxChanged) {
      setTaxRetroModal({ oldRate: oldTaxRate, newRate: newTaxRate, fromDate: today(), applyRetro: true });
      return; // Espera escolha do usuário no modal
    }
    doSaveConfig(null, null);
  }
  function doSaveConfig(retroFromDate, newTaxRateForRetro) {
    if (!localConfig) return;
    const updated = restaurants.map(r => r.id === rid ? { ...r, ...localConfig } : r);
    onUpdate("restaurants", updated);
    // Recalcula tips se foi pedido retroativo
    if (retroFromDate && newTaxRateForRetro != null) {
      let affectedCount = 0;
      const updatedTips = (tips || []).map(t => {
        if (t.restaurantId !== rid) return t;
        if (t.date < retroFromDate) return t;
        const myShare = t.myShare || 0;
        const newTax = round2(myShare * newTaxRateForRetro);
        affectedCount++;
        return { ...t, taxRate: newTaxRateForRetro, myTax: newTax, myNet: round2(myShare - newTax) };
      });
      onUpdate("tips", updatedTips);
      onUpdate("_toast", `✅ Configurações salvas + ${affectedCount} lançamento(s) recalculado(s) com ${(newTaxRateForRetro*100).toFixed(0)}%`);
    } else {
      onUpdate("_toast", "✅ Configurações salvas!");
    }
    setLocalConfig(null);
  }
  function discardConfig() { setLocalConfig(null); }


  // calcTipForDate agora aceita currentTips para evitar stale closure em batch
  function calcTipForDate(date, totalVal, noteVal, currentTips) {
    const allTips = currentTips ?? tips;
    const total = parseFloat(totalVal);
    if (!total || isNaN(total) || total <= 0) return { count: 0, updatedTips: allTips };
    if (restaurant.serviceStartDate && date < restaurant.serviceStartDate) { alert(`Não é possível lançar gorjeta antes da data de vigência (${new Date(restaurant.serviceStartDate+"T12:00:00").toLocaleDateString("pt-BR")})`); return { count: 0, updatedTips: allTips }; }
    const td = new Date(date + "T12:00:00");
    const tKey = monthKey(td.getFullYear(), td.getMonth());
    const taxRate = restaurant.taxRate ?? TAX;
    const totalTaxAmt = total * taxRate;
    const toDistribute = total - totalTaxAmt;
    const mode = restaurant.divisionMode ?? MODE_AREA_POINTS;
    const empDayStatus = (empId) => { const m = schedules?.[rid]?.[tKey]?.[empId] ?? {}; return m[date]; };
    const activeEmps = restEmps.filter(emp => {
      const r = restRoles.find(r => r.id === emp.roleId);
      if (!r) return false;
      if (emp.isFreela) return false;
      const admDate = emp.admission || DEFAULT_ADMISSION();
      if (admDate > date) return false;
      if (emp.inactive && emp.inactiveFrom && emp.inactiveFrom <= date) return false;
      if (emp.demitidoEm && emp.demitidoEm <= date) return false;
      const status = empDayStatus(emp.id);
      if (status === DAY_VACATION || status === DAY_FREELA) return false;
      if (emp.isProducao) return true;
      if (!status || status === DAY_COMP || status === DAY_COMP_TRAB) return true;
      if (status === DAY_FAULT_J || status === DAY_FAULT_U) return false;
      if (status === DAY_OFF) return false;
      return true;
    }).map(emp => { const r = restRoles.find(r => r.id === emp.roleId); return { ...emp, points: parseFloat(r?.points) || 0, area: r?.area ?? "Salão" }; });
    const newTips = [];
    try {
      if (mode === MODE_GLOBAL_POINTS) {
        const totalPoints = activeEmps.reduce((a,e)=>a+e.points,0);
        if (!totalPoints) return { count: 0, updatedTips: allTips };
        activeEmps.forEach(emp => { const g=round2(total*(emp.points/totalPoints)),tx=round2(totalTaxAmt*(emp.points/totalPoints)); newTips.push({id:`${Date.now()}-${emp.id}-${Math.random().toString(36).slice(2,6)}`,restaurantId:rid,employeeId:emp.id,date,monthKey:tKey,poolTotal:total,areaPool:toDistribute,area:emp.area??"—",myShare:g,myTax:tx,myNet:round2(g-tx),note:noteVal,taxRate}); });
      } else {
        // Resolve regra vigente NA DATA do lançamento (suporta versionado novo + legado)
        const splitVersion = getSplitForDate(splits?.[rid], date);
        const rawPercentages = (splitVersion && splitVersion.percentages) || splits?.[rid]?.[tKey] || DEFAULT_SPLIT;
        // Conta empregados ativos por área (pra áreas perEmployee)
        const employeesPerArea = {};
        AREAS.forEach(a => { employeesPerArea[a] = countActiveEmployeesInArea(employees, restRoles, a, date, rid); });
        const tSplit = computeAreaPercentages(rawPercentages, employeesPerArea);
        const byArea = {}; AREAS.forEach(a=>{byArea[a]=[];}); activeEmps.forEach(emp=>{if(emp.area)byArea[emp.area].push(emp);});
        AREAS.forEach(area => { const emps=byArea[area],tp=emps.reduce((a,e)=>a+e.points,0); if(!tp)return; const ap=round2(toDistribute*(tSplit[area]/100)); emps.forEach(emp=>{const g=round2(total*(tSplit[area]/100)*(emp.points/tp)),tx=round2(totalTaxAmt*(tSplit[area]/100)*(emp.points/tp));newTips.push({id:`${Date.now()}-${emp.id}-${Math.random().toString(36).slice(2,6)}`,restaurantId:rid,employeeId:emp.id,date,monthKey:tKey,poolTotal:total,areaPool:ap,area,myShare:g,myTax:tx,myNet:round2(g-tx),note:noteVal,taxRate});}); });
      }
    } catch (err) {
      onUpdate("_toast", "⚠️ Erro no cálculo de gorjeta: " + (err.message || "desconhecido"));
      return { count: 0, updatedTips: allTips };
    }
    // Remove existing tips for this date before adding new ones (supports re-launch)
    const tipsWithoutDate = allTips.filter(t => !(t.restaurantId === rid && t.date === date));
    const updatedTips = [...tipsWithoutDate, ...newTips];
    return { count: newTips.length, updatedTips };
  }


  function recalcTipDay(date, currentTips) {
    const allTips = currentTips ?? tips;
    // Find existing tips for this date
    const existing = allTips.filter(t => t.restaurantId === rid && t.date === date);
    if (!existing.length) return 0;
    const poolTotal = existing[0].poolTotal;
    const noteVal   = existing[0].note ?? "";
    const taxRate   = restaurant.taxRate ?? TAX;
    const td        = new Date(date + "T12:00:00");
    const tKey      = monthKey(td.getFullYear(), td.getMonth());
    const totalTaxAmt = poolTotal * taxRate;
    const toDistribute = poolTotal - totalTaxAmt;
    const mode = restaurant.divisionMode ?? MODE_AREA_POINTS;

    const daySchedule = schedules?.[rid]?.[tKey] ?? {};
    const empDayStatus = (empId) => {
      const empDayMap = daySchedule[empId] ?? {};
      return empDayMap[date];
    };

    const allRestEmps = employees.filter(e =>
      e.restaurantId === rid &&
      !(e.inactive && e.inactiveFrom && e.inactiveFrom <= date)
    );

    const activeEmps = allRestEmps.filter(emp => {
      const r = restRoles.find(r => r.id === emp.roleId);
      if (!r || r.noTip) return false;
      if (emp.isFreela) return false; // freela nunca entra na gorjeta
      if (emp.admission && emp.admission > date) return false;
      if (emp.demitidoEm && emp.demitidoEm <= date) return false; // demitido = não entra
      const status = empDayStatus(emp.id);
      if (status === DAY_VACATION) return false; // férias = ninguém entra, nem produção
      if (status === DAY_FREELA) return false; // freela no dia = não entra
      if (emp.isProducao) return true; // produção entra em todos os outros dias
      if (!status) return true; // trabalho = entra
      if (status === DAY_COMP || status === DAY_COMP_TRAB) return true; // compensação (folga ou trabalho) = entra
      if (status === DAY_FAULT_J || status === DAY_FAULT_U) return false; // faltas = nao entra
      return false; // demais: folga = nao entra
    }).map(emp => ({
      ...emp,
      points: parseFloat(restRoles.find(r => r.id === emp.roleId)?.points) || 0,
      area: restRoles.find(r => r.id === emp.roleId)?.area,
    }));

    const newTips = [];
    if (mode === MODE_GLOBAL_POINTS) {
      const totalPoints = activeEmps.reduce((a, e) => a + e.points, 0);
      if (!totalPoints) return 0;
      activeEmps.forEach(emp => {
        const myGross = poolTotal * (emp.points / totalPoints);
        const myTaxShare = totalTaxAmt * (emp.points / totalPoints);
        newTips.push({ id: `${Date.now()}-${emp.id}-${Math.random().toString(36).slice(2,6)}`, restaurantId: rid, employeeId: emp.id, date, monthKey: tKey, poolTotal, areaPool: toDistribute, area: emp.area ?? "—", myShare: myGross, myTax: myTaxShare, myNet: myGross - myTaxShare, note: noteVal, taxRate });
      });
    } else {
      // Resolve regra vigente NA DATA (suporta versionado novo + legado)
      const splitVersion = getSplitForDate(splits?.[rid], date);
      const rawPercentages = (splitVersion && splitVersion.percentages) || splits?.[rid]?.[tKey] || DEFAULT_SPLIT;
      const employeesPerArea = {};
      AREAS.forEach(a => { employeesPerArea[a] = countActiveEmployeesInArea(employees, restRoles, a, date, rid); });
      const tSplit = computeAreaPercentages(rawPercentages, employeesPerArea);
      const empsByArea = {};
      AREAS.forEach(a => { empsByArea[a] = []; });
      activeEmps.forEach(emp => { if (emp.area) empsByArea[emp.area].push(emp); });
      AREAS.forEach(area => {
        const emps = empsByArea[area];
        const totalPoints = emps.reduce((a, e) => a + e.points, 0);
        if (!totalPoints) return;
        const areaPool = toDistribute * (tSplit[area] / 100);
        emps.forEach(emp => {
          const myGross = poolTotal * (tSplit[area] / 100) * (emp.points / totalPoints);
          const myTaxShare = totalTaxAmt * (tSplit[area] / 100) * (emp.points / totalPoints);
          const newTip = { id: `${Date.now()}-${emp.id}-${Math.random().toString(36).slice(2,6)}`, restaurantId: rid, employeeId: emp.id, date, monthKey: tKey, poolTotal, areaPool, area, myShare: myGross, myTax: myTaxShare, myNet: myGross - myTaxShare, note: noteVal, taxRate };
          if (splitVersion?.id) newTip.splitVersionId = splitVersion.id;
          newTips.push(newTip);
        });
      });
    }
    // Remove old tips for this date and add new ones
    const remaining = allTips.filter(t => !(t.restaurantId === rid && t.date === date));
    const updatedTips = [...remaining, ...newTips];
    return { count: newTips.length, updatedTips };
  }

  async function saveSplit() {
    const total = AREAS.reduce((a, k) => a + parseFloat(splitForm[k] || 0), 0);
    if (Math.abs(total - 100) > 0.01) { alert("Os percentuais devem somar 100%."); return; }
    const newSplit = Object.fromEntries(AREAS.map(a => [a, parseFloat(splitForm[a])]));
    const updatedRestSplits = { ...(splits?.[rid] ?? {}), [mk]: newSplit };

    // Ask if they want to apply to future months too
    const applyFuture = await appConfirm("Deseja aplicar esses percentuais também para os próximos meses?");
    if (applyFuture) {
      // Apply to 12 future months from current
      const [cy, cm] = mk.split("-").map(Number);
      for (let i = 1; i <= 12; i++) {
        const fm = cm - 1 + i;
        const fy = cy + Math.floor(fm / 12);
        const fmIdx = fm % 12;
        const fmk = `${fy}-${String(fmIdx + 1).padStart(2, "0")}`;
        updatedRestSplits[fmk] = { ...newSplit };
      }
    }

    onUpdate("splits", { ...splits, [rid]: updatedRestSplits });
    setSplitForm(null);
  }

  const areaEmps = schedArea === "Todos"
    ? schedEmps.slice().sort((a,b) => {
        const aA = restRoles.find(r=>r.id===a.roleId)?.area ?? "z";
        const bA = restRoles.find(r=>r.id===b.roleId)?.area ?? "z";
        return aA.localeCompare(bA) || a.name.localeCompare(b.name);
      })
    : schedEmps.filter(e => restRoles.find(r => r.id === e.roleId)?.area === schedArea);
  const dim = new Date(year, month + 1, 0).getDate();

  const ac = "var(--ac)";
  const canTips  = perms.tips     || isOwner;
  const canSched = perms.schedule || isOwner;
  // Líder de área: por padrão só visualiza. Mas se a perm `liderEditaEscala` estiver
  // marcada (admin.liderEditaEscala em pessoas → manager.perms.liderEditaEscala), edita.
  const canSchedEdit = canSched && (!isLider || perms.liderEditaEscala === true);
  // v8.17.0: isDP é mantido como fonte legada; novos checks usam perms granulares com fallback no isDP.
  const isDP     = perms.isDP === true;
  const canDpCanal           = isDP || perms.dpCanal === true;
  const canAtravessarPrivado = isDP || perms.atravessarPrivado === true;
  const canReabrirEscala     = isDP || perms.reabrirEscala === true;
  const canFecharEscala      = isDP || perms.fecharEscala === true || canSchedEdit;
  // eslint-disable-next-line no-unused-vars
  const _permsV2 = { canDpCanal, canAtravessarPrivado, canReabrirEscala, canFecharEscala };
  const currentIsMasterDP = (currentUser?.masterRestaurantIds ?? []).includes(rid) || isOwner;

  // Abas opcionais — admin autoriza via tabsConfig, gestor escolhe via tabsGestor
  const adminAutoriza = (key) => restaurant.tabsConfig?.[key] !== false;
  const gestorAtivou  = (key) => restaurant.tabsGestor?.[key] !== false;
  const tabVisible    = (key) => adminAutoriza(key) && (isOwner || gestorAtivou(key));

  const userRoleForInbox = isOwner ? "owner" : isLider ? "lider" : "manager";
  const inboxUnread = ((data?.notifications??[]).filter(n=>n.restaurantId===rid&&!n.read&&!n.deleted&&n.targetRole!=="admin"&&n.type!=="upgrade_request").length + (data?.dpMessages??[]).filter(m=>m.restaurantId===rid&&!m.read&&!m.deleted).length + getInboxForUser({ inbox: data?.inbox, userId: currentUser?.id, userRole: userRoleForInbox, restaurantId: rid, includeRead: false }).length);

  // Líder Operacional: acesso restrito a Dashboard + Escala + Horários + Equipe
  // ── Grouped tabs ──
  const liderInboxCount = isLider ? getInboxForUser({ inbox: data?.inbox, userId: currentUser?.id, userRole: "lider", restaurantId: rid, includeRead: false }).length : 0;
  // Recursos: tab só aparece se o usuário tem ao menos um item visível (mesma regra de canSeeItem do RecursosTab).
  // Owner sempre vê (precisa pra criar o primeiro item).
  const hasVisibleRecurso = (() => {
    if (isOwner) return true;
    const items = (data?.recursos ?? []).filter(r => r.restaurantId === rid && !r.deletedAt);
    if (items.length === 0) return false;
    if (isDP) return true;
    return items.some(r => {
      const v = r.visibility || { mode: "default", sharedWith: [] };
      const isCreator = r.createdById && r.createdById === currentUser?.id;
      if (v.mode === "private") return isCreator;
      if (v.mode === "shared") return isCreator || (Array.isArray(v.sharedWith) && v.sharedWith.includes(currentUser?.id));
      return true; // default
    });
  })();
  // ──────────────────────────────────────────────────────────────────────
  // v7.0 IA — 5 seções: Início, Operação, Planejamento, Escritório, Ajustes
  // Líder Operacional: vê Início + Planejamento + Escritório (restrito)
  // ──────────────────────────────────────────────────────────────────────
  const TAB_GROUPS = isLider ? [
    { id:"inicio", label:"🏠 Início", icon:"🏠", tabs: [
      ["inicio",`Início${liderInboxCount>0?` (${liderInboxCount})`:""}`],
    ] },
    canSched && { id:"planejamento", label:"📅 Planejamento", icon:"📅", tabs: [
      canSched && ["schedule","Escalas"],
      ["reunioes","Reuniões"],
    ].filter(Boolean) },
    { id:"escritorio", label:"🏢 Escritório", icon:"🏢", tabs: [
      ["employees","Equipe"],
      ["freelas","🎒 Freelas"],
      hasVisibleRecurso && ["recursos","📚 Recursos"],
    ].filter(Boolean) },
  ].filter(Boolean) : [
    { id:"inicio", label:"🏠 Início", icon:"🏠", tabs: [
      ["inicio",`Início${inboxUnread>0?` (${inboxUnread})`:""}`],
    ] },
    { id:"operacao", label:"🔧 Operação", icon:"🔧", tabs: [
      (canTips || isOwner) && ["ocorrencias","🚨 Ocorrências"],
      (canTips || isOwner) && ["mise_checklists","Checklists"],
      (canTips || isOwner) && ["mise_contagens","Contagens"],
      (canTips || isOwner) && ["mise_temperaturas","Temperaturas"],
      (canTips || isOwner) && ["mise_fichas","Fichas Técnicas"],
    ].filter(Boolean) },
    { id:"planejamento", label:"📅 Planejamento", icon:"📅", tabs: [
      canSched && ["schedule","Escalas"],
      (isOwner || isDP || isLider) && ["freelas","🎒 Freelas"],
      (isOwner || canTips || perms.employees !== false) && ["reunioes","Reuniões"],
      (canTips || isOwner) && ["trilhas","Trilhas"],
      (canTips || isOwner) && ["ideias","💡 Ideias"],
    ].filter(Boolean) },
    { id:"escritorio", label:"🏢 Escritório", icon:"🏢", tabs: [
      (isOwner || canTips) && ["pessoas","Pessoas"],
      (isOwner || canTips) && ["permissoes","Permissões"],
      (isOwner || canTips || perms.employees !== false) && ["employees","Equipe"],
      (isOwner || perms.vt !== false) && ["vt","Vale Transporte"],
      (isOwner || perms.roles !== false) && ["roles","Cargos"],
      (canTips || isOwner) && ["mise_compras","🛒 Compras"],
      canTips && ["tips","Gorjetas"],
      canTips && ["dashboard","Dashboard"],
      canTips && ["regra_divisao","Regra"],
      // v8.15.0: Recursos — Owner sempre; demais (DP/Líder/admin) só se tiverem ao menos 1 item visível.
      (canTips || isOwner || isDP || isLider) && hasVisibleRecurso && ["recursos","📚 Recursos"],
      (isOwner || tabVisible("comunicados")) && ["comunicados","Comunicados"],
      (isOwner || tabVisible("faq")) && ["faq","FAQ"],
      (isOwner || tabVisible("dp")) && ["dp","Fale com DP"],
    ].filter(Boolean) },
    { id:"ajustes", label:"⚙️ Ajustes", icon:"⚙️", tabs: [
      (canTips || isOwner) && ["config","Configurações"],
    ].filter(Boolean) },
  ].filter(g => g.tabs.length > 0);

  // Mobile: ampla — perms já filtram por pessoa. Inclui IDs novos da v7.
  const MOBILE_ALLOWED = [
    "inicio","ocorrencias","mise_checklists","mise_contagens","mise_temperaturas","mise_fichas",
    "schedule","freelas","reunioes","trilhas","ideias",
    "pessoas","permissoes","employees","vt","roles","mise_compras","tips","dashboard","regra_divisao","recursos","comunicados","faq","dp",
    "config","horarios","notificacoes","dp_gestores",
  ];
  // v7: se restrictToOperational, oculta seções "escritorio" e "ajustes" (modo Operação)
  const OPERATIONAL_ALLOWED_GROUPS = ["inicio","operacao","planejamento"];
  const TAB_GROUPS_BASE = restrictToOperational
    ? TAB_GROUPS.filter(g => OPERATIONAL_ALLOWED_GROUPS.includes(g.id))
    : TAB_GROUPS;
  const TAB_GROUPS_FINAL = mobileOnly
    ? TAB_GROUPS_BASE.map(g => ({ ...g, tabs: g.tabs.filter(([id]) => MOBILE_ALLOWED.includes(id)) })).filter(g => g.tabs.length > 0)
    : TAB_GROUPS_BASE;

  // Flat TABS for backward compatibility
  const TABS = TAB_GROUPS_FINAL.flatMap(g => g.tabs.map(([id,lbl]) => [id,lbl]));

  const defaultGroup = "inicio";
  const [tabGroup, setTabGroup] = useState(defaultGroup);
  const [tab, setTab] = useState(forceTab || (isLider ? "employees" : "inicio"));
  // v8.20.0: trigger pra abrir uma pessoa específica em edição (vindo do resumo de permissões)
  const [editPessoaTrigger, setEditPessoaTrigger] = useState(null); // { id, ts } — ts pra forçar effect mesmo se for o mesmo id
  function openPessoaForEdit(pessoaId) {
    setTab("pessoas");
    setEditPessoaTrigger({ id: pessoaId, ts: Date.now() });
  }
  // Se o shell forçar um tab específico, sincroniza
  useEffect(() => {
    if (forceTab && forceTab !== tab) setTab(forceTab);
  }, [forceTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listener legado do CustomEvent "apptip:recalc-tips" mantido como no-op.
  // O useEffect unificado abaixo (Fase B v2) já catch tudo via dataSignature.
  // Mantemos o listener pra evitar warnings caso outro código dispare o evento.

  const [empResetSignal, setEmpResetSignal] = useState(0);
  // v7: state pra aba Trilhas (admin) — qual empregado está selecionado pra drilldown
  const [trilhasAdminSelEmp, setTrilhasAdminSelEmp] = useState(null);
  const activeGroup = TAB_GROUPS_FINAL.find(g => g.id === tabGroup) ?? TAB_GROUPS_FINAL[0];

  // Fase B v2: auto-recálculo unificado. Vigia mudanças em employees, roles,
  // schedules e splits do restaurante. Quando algo relevante muda, recalcula
  // TODOS os dias com tip lançado neste rid (todos os meses) — debounce 1.2s
  // pra agrupar várias edições rápidas num único recalc.
  const dataSignature = useMemo(() => {
    const empSig = (employees||[])
      .filter(e => e.restaurantId === rid)
      .map(e => `${e.id}|${e.roleId||""}|${e.isFreela?1:0}|${e.isProducao?1:0}|${e.admission||""}|${e.demitidoEm||""}|${e.inactive?1:0}|${e.inactiveFrom||""}`)
      .sort().join(";");
    const roleSig = (roles||[])
      .filter(r => r.restaurantId === rid)
      .map(r => `${r.id}|${r.area||""}|${r.points||0}|${r.noTip?1:0}`)
      .sort().join(";");
    const schedSig = JSON.stringify(schedules?.[rid] || {});
    const splitSig = JSON.stringify(splits?.[rid] || {});
    return `${empSig}#${roleSig}#${schedSig}#${splitSig}`;
  }, [employees, roles, schedules, splits, rid]);

  const lastSigRef = useRef(null);
  useEffect(() => {
    // Pula a primeira renderização — evita recalc desnecessário ao abrir a tela
    if (lastSigRef.current === null) {
      lastSigRef.current = dataSignature;
      return;
    }
    if (lastSigRef.current === dataSignature) return;
    lastSigRef.current = dataSignature;

    // Debounce: agrupa várias mudanças rápidas num só recalc
    const t = setTimeout(() => {
      const datesWithTips = [...new Set(
        (tips || []).filter(t => t.restaurantId === rid).map(t => t.date)
      )].sort();
      if (datesWithTips.length === 0) return;

      let cur = tips;
      let anyChange = false;
      datesWithTips.forEach(d => {
        const r = recalcTipDay(d, cur);
        if (r && r.updatedTips) {
          // Detecta se houve mudança real (compara antes/depois pelos ids/valores)
          const oldDay = cur.filter(t => t.restaurantId === rid && t.date === d);
          const newDay = r.updatedTips.filter(t => t.restaurantId === rid && t.date === d);
          const oldKey = oldDay.map(t => `${t.employeeId}:${(t.myShare||0).toFixed(4)}`).sort().join("|");
          const newKey = newDay.map(t => `${t.employeeId}:${(t.myShare||0).toFixed(4)}`).sort().join("|");
          if (oldKey !== newKey) {
            cur = r.updatedTips;
            anyChange = true;
          }
        }
      });
      if (anyChange) {
        onUpdate("tips", cur);
        onUpdate("_toast", `🔄 Gorjetas atualizadas automaticamente (${datesWithTips.length} dia(s) recalculados)`);
      }
    }, 1200);
    return () => clearTimeout(t);
  }, [dataSignature]); // eslint-disable-line react-hooks/exhaustive-deps

  // (helper antigo recalcAfterScheduleSave removido — substituído pelo useEffect unificado acima)

  function switchGroup(gid) {
    const g = TAB_GROUPS_FINAL.find(x => x.id === gid);
    if (!g || g.tabs.length === 0) return;
    setTabGroup(gid);
    // If current tab is already in this group, keep it
    if (g.tabs.some(([id]) => id === tab)) return;
    setTab(g.tabs[0][0]);
  }

  // Reset de aba — só Master (isOwner)
  async function resetTab(tabKey, tabLabel, getSnapshot) {
    if (!isOwner) return;
    if (!await appConfirm(`Resetar "${tabLabel}"?\n\nOs dados ficam na lixeira por 90 dias e podem ser restaurados.`)) return;
    const snapshot = getSnapshot();
    const entry = {
      id: Date.now().toString(),
      restaurantId: rid,
      restaurantName: restaurant.name,
      tabKey,
      tabLabel,
      snapshot,
      deletedAt: new Date().toISOString(),
    };
    const trash = data?.trash ?? { restaurants:[], managers:[], employees:[], tabData:[] };
    onUpdate("trash", { ...trash, tabData: [...(trash.tabData??[]), entry] });
    return true; // indica que pode prosseguir com o reset
  }

  // --- Gorjetas dirty check & save helper ---
  const tipsDirty = tipRows.some(r => {
    const v = parseBR(r.total);
    const dayTips = (tips ?? []).filter(t => t.restaurantId === rid && t.date === r.date);
    const isLaunched = dayTips.length > 0;
    const launchedPool = isLaunched ? dayTips[0].poolTotal : null;
    return (v > 0 && !isNaN(v) && !isLaunched) || (isLaunched && launchedPool != null && v > 0 && !isNaN(v) && v !== launchedPool);
  });

  function parseBR(v) { if (typeof v === "number") return v; return parseFloat(String(v).replace(/\./g,"").replace(",",".")); }

  function saveTipRows() {
    const dirtyRows = tipRows.filter(r => {
      const v = parseBR(r.total);
      if (!v || isNaN(v) || v <= 0) return false;
      const dayTips = (tips ?? []).filter(t => t.restaurantId === rid && t.date === r.date);
      const isLaunched = dayTips.length > 0;
      const launchedPool = isLaunched ? dayTips[0].poolTotal : null;
      return !isLaunched || (launchedPool != null && v !== launchedPool);
    });
    if (!dirtyRows.length) { onUpdate("_toast","⚠️ Nenhum dia com valor alterado para salvar."); return; }
    let count = 0, currentTips = tips;
    dirtyRows.forEach(row => {
      const result = calcTipForDate(row.date, parseBR(row.total), row.note ?? "", currentTips);
      count += result.count;
      currentTips = result.updatedTips;
    });
    if (count > 0) {
      // Snapshot pré-save das gorjetas do mês
      const preSnapT = snapshotTipsMonth(tips, rid, mk);
      saveTipsSnapshot(`Salvar gorjetas (${dirtyRows.length} dia${dirtyRows.length>1?"s":""})`, preSnapT);
      onUpdate("tips", currentTips);
      setTipRows([]);
      onUpdate("_toast", `✅ ${dirtyRows.length} dia${dirtyRows.length>1?"s":""} salvo${dirtyRows.length>1?"s":""}! (${count} empregados)`);
    } else {
      const sampleDate = dirtyRows[0]?.date ?? "?";
      const admCount = restEmps.filter(e => e.admission && e.admission > sampleDate).length;
      const noRoleCount = restEmps.filter(e => !restRoles.find(r => r.id === e.roleId)).length;
      const freelaCount = restEmps.filter(e => e.isFreela).length;
      let reason = `Total ${restEmps.length} empregados. `;
      if (admCount > 0) reason += `${admCount} com admissão após ${new Date(sampleDate+"T12:00:00").toLocaleDateString("pt-BR")}. `;
      if (noRoleCount > 0) reason += `${noRoleCount} sem cargo. `;
      if (freelaCount > 0) reason += `${freelaCount} freela. `;
      reason += "Corrija as datas de admissão dos empregados se necessário.";
      onUpdate("_toast","⚠️ " + reason);
    }
  }

  function discardTipRows() { setTipRows([]); }

  return (
    <div style={{ fontFamily:"'DM Sans',sans-serif" }}>
      {!hideTabNav && <>
      {/* Tab groups — row 1: group pills + shortcut owner */}
      <div style={{ display:"flex", gap:2, padding:"8px 8px 0", background:"var(--header-bg)", alignItems:"center" }}>
        {TAB_GROUPS_FINAL.map(g => (
          <button key={g.id} onClick={() => switchGroup(g.id)}
            style={{ padding:"6px 10px", background:tabGroup===g.id?ac:"transparent", border:"none", borderRadius:16, color:tabGroup===g.id?"#fff":"var(--text3)", cursor:"pointer", fontSize:12, fontFamily:"'DM Sans',sans-serif", fontWeight:tabGroup===g.id?700:500, whiteSpace:"nowrap", transition:"all .15s" }}>
            {g.label}
          </button>
        ))}
        {isOwner && onEnterOperational && (
          <>
            <div style={{flex:1}} />
            <button onClick={()=>onEnterOperational(rid)} title="Entrar no restaurante como master — mesma UI que qualquer usuário vê, com acesso total"
              style={{padding:"6px 12px",background:"#7c9e5e",border:`1px solid #7c9e5e`,borderRadius:16,color:"#fff",cursor:"pointer",fontSize:11,fontFamily:"'DM Sans',sans-serif",fontWeight:700,whiteSpace:"nowrap",marginRight:6}}>
              ▶ Entrar
            </button>
          </>
        )}
      </div>
      {/* Tab groups — row 2: sub-tab pills */}
      <div style={{ display:"flex", borderBottom:"1px solid var(--border)", background:"var(--header-bg)", overflowX:"auto", scrollbarWidth:"none", WebkitOverflowScrolling:"touch" }}>
        {(activeGroup?.tabs??[]).map(([id, lbl]) => (
          <button key={id} onClick={async () => {
            if (tab === "config" && id !== "config" && configDirty) {
              const action = await appConfirm("Você tem alterações não salvas nas configurações.\n\nDeseja salvar antes de sair?");
              if (action) { saveConfig(); }
              else { discardConfig(); }
            }
            if (tab === "tips" && id !== "tips" && tipsDirty) {
              const action = await appConfirm("Você tem gorjetas não salvas.\n\nDeseja salvar antes de sair?");
              if (action) { saveTipRows(); }
              else { discardTipRows(); }
            }
            if (tab === "schedule" && id !== "schedule" && schedDirty) {
              const action = await appConfirm("Você tem edições na escala não salvas.\n\nDeseja salvar como nova versão antes de sair?");
              if (action) commitPendingScheduleEdits();
              else setSchedLocalEdits(null);
            }
            if (id === tab && id === "employees") setEmpResetSignal(s => s + 1);
            setTab(id);
          }}
            style={{ padding:"11px 16px", background:"none", border:"none", borderBottom:`2px solid ${tab===id?ac:"transparent"}`, color:tab===id?ac:"var(--text3)", cursor:"pointer", fontSize:13, fontFamily:"'DM Sans',sans-serif", fontWeight:tab===id?700:500, whiteSpace:"nowrap", flexShrink:0 }}>
            {lbl}
          </button>
        ))}
      </div>
      </>}

      {/* Botão voltar ao Dashboard no mobile */}
      {mobileOnly && !hideTabNav && tab !== "dashboard" && (
        <button onClick={async ()=>{
          if (tab === "schedule" && schedDirty) {
            const action = await appConfirm("Você tem edições na escala não salvas.\n\nDeseja salvar como nova versão antes de sair?");
            if (action) commitPendingScheduleEdits();
            else setSchedLocalEdits(null);
          }
          setTab("dashboard");
        }} style={{display:"flex",alignItems:"center",gap:6,padding:"10px 16px",background:"none",border:"none",borderBottom:"1px solid var(--border)",color:"var(--ac-text,var(--ac))",cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:"'DM Sans',sans-serif",width:"100%"}}>
          ← Dashboard
        </button>
      )}

      <div style={{ padding:mobileOnly?"12px 10px":"20px 24px", maxWidth:1100, margin:"0 auto" }}>
        {["dashboard","tips","schedule"].includes(tab) && (
          <>
            {/* v8.12.6: Schedule usa o gate de seleção de mês — sem MonthNav inline. Trocar mês via botão no header. */}
            {tab !== "schedule" && (
              <div style={{ marginBottom: 12 }}><MonthNav year={year} month={month} onChange={async (y,m)=>{
                setYear(y);setMonth(m);setWeekIdx(calcWeekForToday(y,m));
                onUpdate("_toast", `📅 ${monthLabel(y, m)} selecionado`);
              }} /></div>
            )}
            {/* v8.0 — Banner contextual de mês: futuro/atual/passado/fechado, cor-coded */}
            {(() => {
              const today = new Date();
              const cYear = today.getFullYear(); const cMonth = today.getMonth();
              const isFuture = year > cYear || (year === cYear && month > cMonth);
              const isPast = year < cYear || (year === cYear && month < cMonth);
              const isClosed = !!data?.scheduleStatus?.[rid]?.[mk] && data.scheduleStatus[rid][mk].status === "closed";
              let bg, color, label;
              if (isClosed) { bg = "#dc262615"; color = "#dc2626"; label = "🔒 Mês fechado — somente leitura recomendada"; }
              else if (isPast) { bg = "#64748b15"; color = "#64748b"; label = "⚠️ Mês passado — edições retroativas"; }
              else if (isFuture) { bg = "#16a34a15"; color = "#16a34a"; label = "📆 Mês futuro — planejamento"; }
              else { bg = "#d4a01715"; color = "#d4a017"; label = "📍 Mês vigente"; }
              return (
                <div style={{ background:bg, border:`1px solid ${color}33`, borderLeft:`4px solid ${color}`, borderRadius:10, padding:mobileOnly?"10px 14px":"12px 18px", marginBottom: 16, display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <span style={{ fontSize:mobileOnly?15:18, fontWeight:800, color }}>Você está em {monthLabel(year, month)}</span>
                    <span style={{ fontSize:mobileOnly?11:12, color, fontWeight:600 }}>{label}</span>
                  </div>
                </div>
              );
            })()}
          </>
        )}

        {/* Banner de privacidade */}
        {privacyMask && (
          <div style={{background:"#f59e0b15",border:"1px solid #f59e0b44",borderRadius:12,padding:"12px 16px",marginBottom:16,display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:18}}>🔒</span>
            <div>
              <div style={{color:"#f59e0b",fontSize:13,fontWeight:700}}>Modo Privacidade ativo</div>
              <div style={{color:"var(--text3)",fontSize:12}}>Valores de gorjeta, CPFs e mensagens estão ocultos para o administrador.</div>
            </div>
          </div>
        )}

        {/* DASHBOARD */}
        {tab === "dashboard" && (() => {
          const dim = new Date(year, month+1, 0).getDate();
          const todayStr = today();
          const isCurrentMonth = todayStr.startsWith(`${year}-${String(month+1).padStart(2,"0")}`);

          // — Gorjetas —
          const tipPoolTotal = [...new Set(monthTips.map(t=>t.date))].reduce((s,d)=>{
            const dt = monthTips.filter(t=>t.date===d);
            return s + (dt[0]?.poolTotal ?? 0);
          }, 0);
          const diasLancados = [...new Set(monthTips.map(t=>t.date))].length;
          const restNoTipDays = (data?.noTipDays?.[rid] ?? []).filter(d=>d.startsWith(`${year}-${String(month+1).padStart(2,"0")}`));
          const diasResolvidos = diasLancados + restNoTipDays.length;
          const limitDay = isCurrentMonth ? parseInt(todayStr.slice(-2)) : dim;
          let diasUteisPassados = 0;
          for (let d=1; d<=limitDay; d++) {
            const wd = new Date(`${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}T12:00:00`).getDay();
            if (wd!==0 && wd!==6) diasUteisPassados++;
          }
          const diasSemLancamento = Math.max(0, diasUteisPassados - diasResolvidos);

          // — Pendências —
          const semCargo = restEmps.filter(e=>!restRoles.find(r=>r.id===e.roleId)).length;
          const semHorario = restEmps.filter(e=>!(data?.workSchedules?.[rid]?.[e.id]?.length)).length;
          const schedMonth = schedules?.[rid]?.[mk] ?? {};
          let totalFaltasU = 0;
          restEmps.forEach(emp => { Object.values(schedMonth[emp.id]??{}).forEach(v=>{ if(v===DAY_FAULT_U) totalFaltasU++; }); });
          const dpNaoLidas = (data?.dpMessages??[]).filter(m=>m.restaurantId===rid&&!m.read&&!m.deleted).length;
          const commsSemCiencia = (data?.communications??[]).filter(c=>
            c.restaurantId===rid && !c.autoSchedule && !c.deleted &&
            restEmps.some(e=>!(data?.commAcks??{})[`${c.id}_${e.id}`])
          ).length;

          const alerts = [];
          if (!isLider && diasSemLancamento > 0)
            alerts.push({ icon:"💸", color:"#f59e0b", msg:`${diasSemLancamento} dia${diasSemLancamento>1?"s":""} útil${diasSemLancamento>1?"eis":""} sem gorjeta lançada`, tab:"tips" });
          if (semCargo > 0)
            alerts.push({ icon:"👤", color:"var(--red)", msg:`${semCargo} empregado${semCargo>1?"s":""} sem cargo definido`, tab:"employees" });
          if (semHorario > 0)
            alerts.push({ icon:"🕐", color:"#8b5cf6", msg:`${semHorario} empregado${semHorario>1?"s":""} sem horário cadastrado`, tab:"horarios" });
          if (totalFaltasU > 0)
            alerts.push({ icon:"⚠️", color:"var(--red)", msg:`${totalFaltasU} falta${totalFaltasU>1?"s":""} injustificada${totalFaltasU>1?"s":""} este mês`, tab:"schedule" });
          if (dpNaoLidas > 0)
            alerts.push({ icon:"💬", color:"#3b82f6", msg:`${dpNaoLidas} mensagem${dpNaoLidas>1?"ns":""} não lida${dpNaoLidas>1?"s":""} no Fale com DP`, tab:"dp" });
          if (commsSemCiencia > 0)
            alerts.push({ icon:"📢", color:"#f59e0b", msg:`${commsSemCiencia} comunicado${commsSemCiencia>1?"s":""} aguardando ciência`, tab:"comunicados" });

          // — Mensagens / Notificações recentes —
          const recentDp = (data?.dpMessages??[])
            .filter(m=>m.restaurantId===rid&&!m.deleted)
            .sort((a,b)=>b.date.localeCompare(a.date))
            .slice(0,4);
          const recentNotifs = (data?.notifications??[])
            .filter(n=>n.restaurantId===rid&&!n.deleted)
            .sort((a,b)=>b.date.localeCompare(a.date))
            .slice(0,3);
          const CATS = { sugestao:"💡", elogio:"👏", reclamacao:"⚠️", denuncia:"🚨" };

          // — Resumo do dia —
          const todayTips = monthTips.filter(t => t.date === todayStr);
          const gorjetaHoje = todayTips.length > 0 ? todayTips[0].poolTotal : null;
          const escalaHoje = schedules?.[rid]?.[mk] ?? {};
          const teamToday = { work:[], off:[], vacation:[], comp:[], comptrab:[], faultJ:[], faultU:[], freela:[] };
          restEmps.forEach(e => {
            const status = (escalaHoje[e.id] ?? {})[todayStr];
            const role = restRoles.find(r => r.id === e.roleId);
            const sched = data?.workSchedules?.[rid]?.[e.id];
            const currentSched = sched?.[sched.length-1];
            const dayIdx = new Date(todayStr+"T12:00:00").getDay();
            const dayData = currentSched?.days?.[dayIdx];
            const parts = (e.name??"").split(" ").filter(Boolean);
            const shortName = parts.length > 1 ? `${parts[0]} ${parts[parts.length-1]}` : parts[0] ?? "";
            const entry = { name: shortName, role: role?.name, area: role?.area, in: dayData?.in, out: dayData?.out, break: dayData?.break, hasSchedule: !!currentSched && dayData?.active !== false, isProducao: !!e.isProducao, isFreela: !!e.isFreela };
            if (status === DAY_OFF) teamToday.off.push(entry);
            else if (status === DAY_FREELA) teamToday.freela.push(entry);
            else if (status === DAY_VACATION) teamToday.vacation.push(entry);
            else if (status === DAY_COMP) teamToday.comp.push(entry);
            else if (status === DAY_COMP_TRAB) teamToday.comptrab.push(entry);
            else if (status === DAY_FAULT_J) teamToday.faultJ.push(entry);
            else if (status === DAY_FAULT_U) teamToday.faultU.push(entry);
            else teamToday.work.push(entry);
          });
          const trabalhando = teamToday.work.length + teamToday.comptrab.length;

          return (
            <div>
              {/* Resumo do dia — só no mês atual */}
              {isCurrentMonth && (
                <div style={{...S.card, marginBottom:14, background:"var(--ac-bg)", border:"1px solid var(--ac)22"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                    <span style={{color:"var(--ac-text)",fontWeight:700,fontSize:mobileOnly?14:16}}>📅 Hoje — {new Date().toLocaleDateString("pt-BR",{weekday:"long",day:"numeric",month:"long"})}</span>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:isLider?"1fr":"repeat(3,1fr)",gap:8}}>
                    <div style={{background:"var(--card-bg)",borderRadius:10,padding:"10px 6px",textAlign:"center"}}>
                      <div style={{fontSize:18,marginBottom:2}}>👥</div>
                      <div style={{color:"var(--text)",fontWeight:700,fontSize:16}}>{trabalhando}</div>
                      <div style={{color:"var(--text3)",fontSize:9,marginTop:2}}>trabalhando</div>
                    </div>
                    {!isLider && (
                    <div style={{background:"var(--card-bg)",borderRadius:10,padding:"10px 6px",textAlign:"center"}}>
                      <div style={{fontSize:18,marginBottom:2}}>💸</div>
                      <div style={{color: gorjetaHoje!==null?"var(--green)":"var(--text3)", fontWeight:700,fontSize:gorjetaHoje!==null?13:12}}>
                        {gorjetaHoje !== null ? pFmt(gorjetaHoje) : "—"}
                      </div>
                      <div style={{color:"var(--text3)",fontSize:9,marginTop:2}}>gorjeta</div>
                    </div>
                    )}
                    {!isLider && (
                    <div style={{background:"var(--card-bg)",borderRadius:10,padding:"10px 6px",textAlign:"center"}}>
                      <div style={{fontSize:18,marginBottom:2}}>{diasSemLancamento>0?"⚠️":"✅"}</div>
                      <div style={{color:diasSemLancamento>0?"#f59e0b":"var(--green)",fontWeight:700,fontSize:14}}>
                        {diasSemLancamento>0 ? `${diasSemLancamento}d` : "Ok"}
                      </div>
                      <div style={{color:"var(--text3)",fontSize:9,marginTop:2}}>pendência</div>
                    </div>
                    )}
                  </div>
                  {!isLider && gorjetaHoje === null && isCurrentMonth && !mobileOnly && (
                    <button onClick={()=>setTab("tips")}
                      style={{...S.btnPrimary, width:"100%", marginTop:10, padding:"10px", fontSize:13}}>
                      💸 Lançar gorjeta de hoje
                    </button>
                  )}
                </div>
              )}

              {/* Equipe hoje — detalhamento por área */}
              {isCurrentMonth && teamToday.work.length + teamToday.comptrab.length + teamToday.off.length + teamToday.vacation.length + teamToday.freela.length > 0 && (() => {
                // Separate workers by area, production, and off-duty
                const byArea = {};
                AREAS.forEach(a => { byArea[a] = []; });
                const prodSection = [];
                teamToday.work.forEach(e => {
                  if (e.isProducao) prodSection.push(e);
                  else if (e.area && byArea[e.area]) byArea[e.area].push(e);
                  else if (e.area) { byArea[e.area] = byArea[e.area] || []; byArea[e.area].push(e); }
                });
                // Folga por comp goes into off section
                teamToday.comp.forEach(e => {
                  if (e.isProducao) prodSection.push({...e, isComp:true});
                  else if (e.area && byArea[e.area]) byArea[e.area].push({...e, isComp:true});
                });
                // Trabalho por comp goes into working section
                teamToday.comptrab.forEach(e => {
                  if (e.isProducao) prodSection.push({...e, isCompTrab:true});
                  else if (e.area && byArea[e.area]) byArea[e.area].push({...e, isCompTrab:true});
                });
                // Freela-day employees go to their area with badge
                teamToday.freela.forEach(e => {
                  if (e.isProducao) prodSection.push({...e, isFreelaDia:true});
                  else if (e.area && byArea[e.area]) byArea[e.area].push({...e, isFreelaDia:true});
                });
                const offSection = [...teamToday.off.map(e=>({...e,isFolga:true})), ...teamToday.vacation.map(e=>({...e,isVac:true})), ...teamToday.faultJ.map(e=>({...e,isFJ:true})), ...teamToday.faultU.map(e=>({...e,isFU:true}))];

                const EmpLine = ({e,i,color}) => (
                  <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"4px 0",borderBottom:"1px solid var(--border)",fontSize:12}}>
                    <div style={{minWidth:0}}>
                      <span style={{color:"var(--text2)",fontWeight:500}}>{e.name}</span>
                      {e.isProducao && <span style={{fontSize:10,marginLeft:3}} title="Produção">🏭</span>}
                      {e.isFreela && <span style={{fontSize:10,marginLeft:3}} title="Freela">🎯</span>}
                      {e.isComp && <span style={{fontSize:9,marginLeft:4,color:"#3b82f6"}}>folga comp.</span>}
                      {e.isCompTrab && <span style={{fontSize:9,marginLeft:4,color:"#0ea5e9"}}>trab. comp.</span>}
                      {e.isFreelaDia && <span style={{fontSize:9,marginLeft:4,color:"#06b6d4"}}>freela</span>}
                      {e.isVac && <span style={{fontSize:9,marginLeft:4,color:"#8b5cf6"}}>férias</span>}
                      {e.isFJ && <span style={{fontSize:9,marginLeft:4,color:"#f59e0b"}}>falta just.</span>}
                      {e.isFU && <span style={{fontSize:9,marginLeft:4,color:"var(--red)"}}>falta inj.</span>}
                      {e.role && <span style={{color:"var(--text3)",fontSize:10,marginLeft:4}}>{e.role}</span>}
                    </div>
                    {e.hasSchedule && e.in && e.out
                      ? <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                          <span style={{color:color||"var(--green)",fontSize:10,fontFamily:"'DM Mono',monospace",fontWeight:600}}>{e.in}–{e.out}</span>
                          {e.break > 0 && <span style={{color:"var(--text3)",fontSize:9,background:"var(--bg2)",padding:"1px 5px",borderRadius:6}}>{e.break}min</span>}
                        </div>
                      : <span style={{color:"var(--text3)",fontSize:9,fontStyle:"italic"}}>sem horário</span>
                    }
                  </div>
                );

                return (
                  <div style={{...S.card,marginBottom:14}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                      <span style={{color:"var(--text)",fontWeight:700,fontSize:mobileOnly?14:16}}>👥 Equipe hoje <span style={{color:"var(--text3)",fontWeight:400,fontSize:mobileOnly?11:12}}>({new Date().toLocaleDateString("pt-BR")})</span></span>
                      <button onClick={()=>setTab("schedule")} style={{...S.btnSecondary,fontSize:12,padding:"6px 14px"}}>Escala →</button>
                    </div>
                    {/* Áreas */}
                    {AREAS.map(area => byArea[area].length > 0 && (
                      <div key={area} style={{marginBottom:10}}>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:mobileOnly?"6px 10px":"8px 14px",background:`${AREA_COLORS[area]??"var(--green)"}12`,borderRadius:10,borderLeft:`4px solid ${AREA_COLORS[area]??"var(--green)"}`,marginBottom:6}}>
                          <span style={{color:AREA_COLORS[area]??"var(--green)",fontSize:mobileOnly?12:13,fontWeight:700}}>{area}</span>
                          <span style={{color:AREA_COLORS[area]??"var(--green)",fontSize:mobileOnly?11:12,fontWeight:600}}>{byArea[area].length}</span>
                        </div>
                        {byArea[area].map((e,i) => <EmpLine key={i} e={e} i={i} color={AREA_COLORS[area]} />)}
                      </div>
                    ))}
                    {/* Produção */}
                    {prodSection.length > 0 && (
                      <div style={{marginBottom:10}}>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:mobileOnly?"6px 10px":"8px 14px",background:"#ec489912",borderRadius:10,borderLeft:"4px solid #ec4899",marginBottom:6}}>
                          <span style={{color:"#ec4899",fontSize:mobileOnly?12:13,fontWeight:700}}>🏭 Produção</span>
                          <span style={{color:"#ec4899",fontSize:mobileOnly?11:12,fontWeight:600}}>{prodSection.length}</span>
                        </div>
                        {prodSection.map((e,i) => <EmpLine key={i} e={e} i={i} color="#ec4899" />)}
                      </div>
                    )}
                    {/* Folga / Férias / Faltas */}
                    {offSection.length > 0 && (
                      <div style={{marginBottom:4}}>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:mobileOnly?"6px 10px":"8px 14px",background:"var(--bg1)",borderRadius:10,borderLeft:"4px solid var(--text3)",marginBottom:6}}>
                          <span style={{color:"var(--text3)",fontSize:mobileOnly?12:13,fontWeight:700}}>Folga / Ausentes</span>
                          <span style={{color:"var(--text3)",fontSize:mobileOnly?11:12,fontWeight:600}}>{offSection.length}</span>
                        </div>
                        {offSection.map((e,i) => (
                          <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"4px 0",borderBottom:"1px solid var(--border)",fontSize:12}}>
                            <div style={{minWidth:0}}>
                              <span style={{color:"var(--text3)",fontWeight:500}}>{e.name}</span>
                              {e.role && <span style={{color:"var(--text3)",fontSize:10,marginLeft:4,opacity:0.7}}>{e.role}</span>}
                              {e.isVac && <span style={{fontSize:9,marginLeft:4,color:"#8b5cf6",background:"#8b5cf622",padding:"1px 5px",borderRadius:4}}>férias</span>}
                              {e.isFJ && <span style={{fontSize:9,marginLeft:4,color:"#f59e0b",background:"#f59e0b22",padding:"1px 5px",borderRadius:4}}>falta just.</span>}
                              {e.isFU && <span style={{fontSize:9,marginLeft:4,color:"var(--red)",background:"#ef444422",padding:"1px 5px",borderRadius:4}}>falta inj.</span>}
                              {e.isFolga && <span style={{fontSize:9,marginLeft:4,color:"var(--red)",background:"#ef444422",padding:"1px 5px",borderRadius:4}}>folga</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {!isLider && (
              <div style={{...S.card, marginBottom:14}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                  <span style={{color:"var(--text)",fontWeight:700,fontSize:mobileOnly?14:16}}>💸 Gorjetas — {monthLabel(year,month)}</span>
                  {!mobileOnly && <button onClick={()=>setTab("tips")} style={{...S.btnSecondary,fontSize:12,padding:"6px 14px"}}>Ver tudo →</button>}
                </div>
                <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr 1fr":"1fr 1fr 1fr 1fr",gap:mobileOnly?6:8}}>
                  {[
                    ["Pool total",    pFmt(tipPoolTotal || 0), "var(--text)"],
                    ["Retenção",      pFmt(totalTax || 0),     "var(--red)"],
                    ["Distribuído",   pFmt(totalNet || 0),     ac],
                    ["Dias preenchidos", `${diasLancados}/${dim}`, diasLancados===dim?"var(--green)":diasLancados>=diasUteisPassados?"var(--green)":"#f59e0b"],
                  ].map(([lbl,val,col])=>(
                    <div key={lbl} style={{background:"var(--bg1)",borderRadius:10,padding:mobileOnly?"8px 6px":"10px 8px",textAlign:"center"}}>
                      <div style={{color:"var(--text3)",fontSize:mobileOnly?8:9,marginBottom:3,lineHeight:1.2}}>{lbl}</div>
                      <div style={{color:col,fontWeight:700,fontSize:mobileOnly?12:13}}>{val}</div>
                    </div>
                  ))}
                </div>
                {(restaurant.divisionMode ?? MODE_AREA_POINTS) === MODE_AREA_POINTS && totalNet > 0 && (
                  <div style={{marginTop:12,display:"flex",flexDirection:"column",gap:5}}>
                    {AREAS.map(a=>{
                      const aNet = monthTips.filter(t=>t.area===a).reduce((s,t)=>s+t.myNet,0);
                      if(!aNet) return null;
                      return (
                        <div key={a} style={{display:"flex",alignItems:"center",gap:10,padding:"6px 10px",borderLeft:`3px solid ${AREA_COLORS[a]}`,background:`${AREA_COLORS[a]}08`,borderRadius:"0 8px 8px 0"}}>
                          <span style={{color:AREA_COLORS[a],fontSize:12,minWidth:70,fontWeight:700}}>{a}</span>
                          <div style={{flex:1,background:"var(--bg2)",borderRadius:4,height:6,overflow:"hidden"}}>
                            <div style={{width:`${(aNet/totalNet)*100}%`,height:"100%",background:AREA_COLORS[a],borderRadius:4}}/>
                          </div>
                          <span style={{color:"var(--text2)",fontSize:12,minWidth:80,textAlign:"right",fontFamily:"'DM Mono',monospace",fontWeight:600}}>{pFmt(aNet)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              )}

              {/* Pendências */}
              {alerts.length > 0 ? (
                <div style={{...S.card,marginBottom:14,border:"1px solid var(--red)33",background:"#fef2f2"}}>
                  <span style={{color:"var(--red)",fontWeight:700,fontSize:mobileOnly?14:16,display:"block",marginBottom:10}}>⚡ Pendências</span>
                  {alerts.map((a,i)=>(
                    <div key={i} onClick={()=>setTab(a.tab)} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderRadius:8,cursor:"pointer",marginBottom:4,background:"#fff",border:`1px solid ${a.color}33`}}>
                      <span style={{fontSize:15}}>{a.icon}</span>
                      <span style={{color:"var(--text2)",fontSize:13,flex:1}}>{a.msg}</span>
                      <span style={{color:a.color,fontSize:12,fontWeight:700}}>→</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{...S.card,marginBottom:14,border:"1px solid var(--green)33",background:"#f0fdf4",textAlign:"center",padding:"14px"}}>
                  <span style={{fontSize:22}}>✅</span>
                  <p style={{color:"var(--green)",fontSize:13,margin:"4px 0 0",fontWeight:600}}>Tudo em dia!</p>
                  <p style={{color:"var(--text3)",fontSize:11,margin:"2px 0 0"}}>Nenhuma pendência para {monthLabel(year,month)}</p>
                </div>
              )}

              {/* Mensagens e Notificações recentes */}
              {(recentDp.length > 0 || recentNotifs.length > 0) && (
                <div style={{...S.card,marginBottom:14}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                    <span style={{color:"var(--text)",fontWeight:700,fontSize:mobileOnly?14:16}}>📬 Recentes</span>
                    <button onClick={()=>setTab("notificacoes")} style={{...S.btnSecondary,fontSize:12,padding:"6px 14px"}}>Ver tudo →</button>
                  </div>
                  {recentDp.map(m=>(
                    <div key={m.id} style={{display:"flex",gap:10,padding:"8px 0",borderBottom:"1px solid var(--border)",alignItems:"flex-start"}}>
                      <span style={{fontSize:14,flexShrink:0}}>{CATS[m.category]??"💬"}</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:"flex",justifyContent:"space-between",gap:6}}>
                          <span style={{color:m.read?"var(--text3)":"var(--text)",fontSize:12,fontWeight:m.read?400:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.empName}</span>
                          <span style={{color:"var(--text3)",fontSize:10,flexShrink:0}}>{new Date(m.date).toLocaleDateString("pt-BR")}</span>
                        </div>
                        <p style={{color:"var(--text3)",fontSize:11,margin:"2px 0 0",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{pText(m.body)}</p>
                      </div>
                      {!m.read && <span style={{background:"#3b82f6",borderRadius:4,padding:"1px 5px",fontSize:9,color:"var(--text)",fontWeight:700,flexShrink:0}}>Novo</span>}
                    </div>
                  ))}
                  {recentNotifs.map(n=>(
                    <div key={n.id} style={{display:"flex",gap:10,padding:"8px 0",borderBottom:"1px solid var(--border)",alignItems:"flex-start"}}>
                      <span style={{fontSize:14,flexShrink:0}}>📋</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:"flex",justifyContent:"space-between",gap:6}}>
                          <span style={{color:n.read?"var(--text3)":"var(--text)",fontSize:12,fontWeight:n.read?400:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{pText(n.body?.split("\n")[0]?.replace("📋 ",""))}</span>
                          <span style={{color:"var(--text3)",fontSize:10,flexShrink:0}}>{new Date(n.date).toLocaleDateString("pt-BR")}</span>
                        </div>
                      </div>
                      {!n.read && <span style={{background:"#f59e0b",borderRadius:4,padding:"1px 5px",fontSize:9,color:"var(--text)",fontWeight:700,flexShrink:0}}>Novo</span>}
                    </div>
                  ))}
                </div>
              )}

              {/* Ações rápidas — só no desktop */}
              {!mobileOnly && <div style={{...S.card}}>
                <span style={{color:"var(--text)",fontWeight:700,fontSize:mobileOnly?14:16,display:"block",marginBottom:10}}>⚡ Ações rápidas</span>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  {[
                    ["💸 Lançar gorjeta", "tips"],
                    ["📅 Ver escala",     "schedule"],
                    ["👥 Equipe",         "employees"],
                    ["📢 Comunicados",    "comunicados"],
                  ].filter(([,t])=>TABS.some(tb=>tb[0]===t)).map(([lbl,t])=>(
                    <button key={t} onClick={()=>setTab(t)} style={{padding:"10px",borderRadius:10,border:"1px solid var(--border)",background:"var(--bg1)",color:"var(--text2)",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12,textAlign:"left",fontWeight:500}}>
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>}
            </div>
          );
        })()}

        {/* GORJETAS */}
        {tab === "tips" && (
          <div>
            {/* Header */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:16}}>
              <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?16:20}}>💸 Gorjetas</h3>
              {canTips && <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                {(isOwner || isDP) && (() => {
                  const vCount = (data?.tipVersions?.[rid]?.[mk] ?? []).length;
                  return (
                    <button onClick={()=>setShowTipHistory(true)}
                      title="Ver e restaurar versões anteriores das gorjetas deste mês"
                      style={{...S.btnSecondary,fontSize:12,color:"var(--ac-text)",borderColor:"var(--ac)44"}}>
                      🕐 Histórico{vCount>0?` (${vCount})`:""}
                    </button>
                  );
                })()}
                {(isOwner || isDP) && <button onClick={async ()=>{
                  const monthDates = [...new Set(monthTips.map(t => t.date))].sort();
                  if (monthDates.length === 0) { onUpdate("_toast","Nada para recalcular neste mês."); return; }
                  if (!await appConfirm(`Recalcular ${monthDates.length} dia(s) com gorjeta lançada de ${monthLabel(year, month)}?\n\nUm backup será salvo no Histórico — você pode restaurar se algo sair errado.`)) return;
                  const preSnap = snapshotTipsMonth(tips, rid, mk);
                  let cur = tips;
                  let totalChanged = 0;
                  monthDates.forEach(d => {
                    const r = recalcTipDay(d, cur);
                    if (r && r.updatedTips) { cur = r.updatedTips; totalChanged += r.count; }
                  });
                  if (totalChanged > 0) {
                    saveTipsSnapshot(`Recalcular mês inteiro (${monthDates.length} dias)`, preSnap);
                    onUpdate("tips", cur);
                    onUpdate("_toast", `🔄 ${monthDates.length} dia(s) recalculados — ${totalChanged} lançamentos atualizados`);
                  } else {
                    onUpdate("_toast", "Nada mudou — gorjetas já estavam consistentes");
                  }
                }} title="Re-roda o cálculo em todos os dias com gorjeta lançada deste mês usando a escala e regra atuais"
                  style={{...S.btnSecondary,fontSize:12,color:"#f59e0b",borderColor:"#f59e0b44"}}>🔄 Recalcular mês</button>}
                {isOwner && <button onClick={()=>{
                  const ok = resetTab("tips","Gorjetas",()=>({tips:tips.filter(t=>t.restaurantId===rid), splits:splits?.[rid]}));
                  if(ok){ onUpdate("tips",tips.filter(t=>t.restaurantId!==rid)); onUpdate("_toast","🗑️ Gorjetas enviadas para a lixeira"); }
                }} style={{...S.btnSecondary,fontSize:12,color:"var(--red)",borderColor:"var(--red)44"}}>🗑️ Resetar gorjetas</button>}
                <button onClick={() => setShowExport(true)} style={{ ...S.btnSecondary, fontSize: 12, color: ac, borderColor: ac }}>📤 Exportar</button>
              </div>}
            </div>

            {/* Modal de histórico das gorjetas */}
            {showTipHistory && (
              <VersionHistoryModal
                title={`🕐 Histórico de Gorjetas — ${monthLabel(year, month)}`}
                versions={data?.tipVersions?.[rid]?.[mk]}
                onClose={()=>setShowTipHistory(false)}
                onRestore={(v)=>{
                  // Salva estado atual como nova versão
                  const curSnap = snapshotTipsMonth(tips, rid, mk);
                  saveTipsSnapshot(`Antes de restaurar "${v.reason}"`, curSnap);
                  // Aplica versão restaurada: remove tips atuais desse mês e adiciona os do snapshot
                  const otherTips = (tips ?? []).filter(t => !(t.restaurantId === rid && t.monthKey === mk));
                  const restoredTips = [...otherTips, ...((v.snapshot ?? []).map(t => ({...t})))];
                  onUpdate("tips", restoredTips);
                  setShowTipHistory(false);
                  onUpdate("_toast", `♻️ Gorjetas restauradas para "${v.reason}" (${fmtRelTime(v.ts)})`);
                }}
              />
            )}
            {/* Botão salvar gorjetas — sticky no topo quando há alterações */}
            {tipsDirty && (
              <div style={{position:"sticky",top:0,zIndex:50,marginBottom:16}}>
                <div style={{background:"var(--card-bg)",border:"2px solid var(--ac)",borderRadius:14,padding:"14px 18px",boxShadow:"0 4px 20px rgba(0,0,0,0.15)"}}>
                  <p style={{color:ac,fontSize:13,fontWeight:700,margin:"0 0 10px"}}>⚠️ Gorjetas não salvas</p>
                  <div style={{display:"flex",gap:10}}>
                    <button onClick={saveTipRows} style={{...S.btnPrimary,flex:1,padding:"12px",fontSize:14,fontWeight:700}}>Salvar Gorjetas</button>
                    <button onClick={discardTipRows} style={{...S.btnSecondary,padding:"12px 16px",fontSize:13}}>Descartar</button>
                  </div>
                </div>
              </div>
            )}

            {/* Lançamento Diário — coluna única (Fase D) */}
            <div style={{ ...S.card, marginBottom: 16 }}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8,marginBottom:12}}>
                <p style={{color:ac,fontSize:14,fontWeight:700,margin:0}}>💸 Lançamento Diário</p>
                {!mobileOnly && (
                  <div style={{display:"flex",gap:10,fontSize:10,color:"var(--text3)",flexWrap:"wrap"}}>
                    <span><span style={{color:"var(--text3)"}}>○</span> Vazio</span>
                    <span><span style={{color:"#f59e0b"}}>●</span> Rascunho</span>
                    <span><span style={{color:ac}}>🔄</span> Pendente</span>
                    <span><span style={{color:"var(--green)"}}>✓</span> Publicado</span>
                    <span><span style={{color:"#f59e0b"}}>⚠</span> Recalcular</span>
                  </div>
                )}
              </div>
              {(() => {
                const daysInMonth = new Date(year, month+1, 0).getDate();
                const noTipDays = data?.noTipDays?.[rid] ?? [];
                const ridApprovals = data?.tipApprovals?.[rid] ?? {};
                const canPublish = isDP || isOwner;

                const allDays = Array.from({length: daysInMonth}, (_, i) => {
                  const d = i+1;
                  return `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
                });

                const setNoTip = (date, checked) => {
                  const updated = { ...(data?.noTipDays??{}), [rid]: checked ? [...noTipDays.filter(d=>d!==date), date] : noTipDays.filter(d=>d!==date) };
                  onUpdate("noTipDays", updated);
                  if (checked) setTipRows(prev => prev.filter(r => r.date !== date));
                };

                // Helper local — delega ao isTipPublished centralizado
                const tipPublished = (tip) => isTipPublished(tip, ridApprovals);

                // Layout grid: desktop 7 col, mobile 4 col + segunda linha
                const gridDesktop = "44px 1fr 44px 38px 110px 90px";
                const gridMobile  = "40px 1fr 38px 32px";

                return (
                  <div>
                    {/* Cabeçalho — só desktop */}
                    {!mobileOnly && (
                      <div style={{display:"grid",gridTemplateColumns:gridDesktop,gap:6,padding:"0 8px 6px",marginBottom:4,borderBottom:"1px solid var(--border)"}}>
                        {[
                          ["Data","left"],
                          ["Valor (R$)","left"],
                          ["S/g","center"],
                          ["St","center"],
                          ["Publicação","center"],
                          ["Ações","center"],
                        ].map(([h,a],i)=>(
                          <div key={i} style={{color:"var(--text3)",fontSize:10,fontWeight:700,textAlign:a,textTransform:"uppercase",letterSpacing:0.4}}>{h}</div>
                        ))}
                      </div>
                    )}

                    {allDays.map(date => {
                      const isNoTip    = noTipDays.includes(date);
                      const dayTips    = monthTips.filter(t => t.date === date);
                      const isLaunched = dayTips.length > 0;
                      const launchedPool = isLaunched ? dayTips[0].poolTotal : null;

                      const localRow = tipRows.find(r => r.date === date);
                      const displayVal = localRow ? localRow.total : (launchedPool != null ? String(launchedPool).replace(".",",") : "");
                      const val = parseBR(displayVal);
                      const hasVal = val > 0 && !isNaN(val);
                      const isDirty = localRow && ((isLaunched && launchedPool != null && hasVal && val !== launchedPool) || (!isLaunched && hasVal));

                      const weekday  = new Date(date+"T12:00:00").toLocaleDateString("pt-BR",{weekday:"short"});
                      const isWeekend = [0,6].includes(new Date(date+"T12:00:00").getDay());
                      const isBeforeVigencia = restaurant.serviceStartDate && date < restaurant.serviceStartDate;

                      // Detecta stale (regra de divisão mudou após o lançamento)
                      const splitVersion = getSplitForDate(splits?.[rid], date);
                      const activeVersionId = splitVersion?.id;
                      const tipVersionId = dayTips[0]?.splitVersionId;
                      const isStale = isLaunched && activeVersionId && tipVersionId && activeVersionId !== tipVersionId;

                      // Estado de publicação do dia
                      const publishedTips  = isLaunched ? dayTips.filter(tipPublished) : [];
                      const isPublished    = isLaunched && publishedTips.length === dayTips.length;
                      const partialPublish = isLaunched && publishedTips.length > 0 && publishedTips.length < dayTips.length;

                      // Status icon & cor
                      let statusIcon, statusColor, statusTitle;
                      if (isNoTip) { statusIcon = "—"; statusColor = "#6366f1"; statusTitle = "Sem gorjeta"; }
                      else if (isStale) { statusIcon = "⚠"; statusColor = "#f59e0b"; statusTitle = "Regra de divisão mudou — clique em Recalcular"; }
                      else if (isDirty) { statusIcon = "●"; statusColor = "#f59e0b"; statusTitle = "Rascunho não salvo"; }
                      else if (isPublished) { statusIcon = "✓"; statusColor = "var(--green)"; statusTitle = "Publicado para empregados"; }
                      else if (partialPublish) { statusIcon = "◐"; statusColor = "#f59e0b"; statusTitle = "Publicação parcial — re-publique"; }
                      else if (isLaunched) { statusIcon = "🔄"; statusColor = ac; statusTitle = "Lançado, aguardando publicação"; }
                      else { statusIcon = "○"; statusColor = "var(--text3)"; statusTitle = "Sem dados"; }

                      let bg = "var(--card-bg)", border = "var(--border)";
                      if      (isBeforeVigencia)            { bg = "var(--bg3)"; border = "var(--border)"; }
                      else if (isNoTip)                     { bg = "#f5f0ff"; border = "#6366f133"; }
                      else if (isDirty)                     { bg = "#fffbeb"; border = "#f59e0b44"; }
                      else if (isStale)                     { bg = "#fffbeb"; border = "#f59e0b66"; }
                      else if (isPublished)                 { bg = "#f0fdf4"; border = "#10b98144"; }
                      else if (isLaunched)                  { bg = "#eff6ff"; border = "var(--ac)33"; }

                      if (isBeforeVigencia) return (
                        <div key={date} style={{display:"grid",gridTemplateColumns:"40px 1fr",gap:4,padding:"5px 8px",marginBottom:3,borderRadius:10,background:bg,border:`1px solid ${border}`,alignItems:"center",opacity:0.4}}>
                          <div style={{textAlign:"center"}}>
                            <div style={{color:"var(--text3)",fontSize:13,fontWeight:700}}>{parseInt(date.slice(-2))}</div>
                            <div style={{color:"var(--text3)",fontSize:9}}>{weekday}</div>
                          </div>
                          <div style={{color:"var(--text3)",fontSize:11}}>🔒 Antes da vigência</div>
                        </div>
                      );

                      // Ações: publicar/despublicar
                      const publishDay = () => {
                        if (!isLaunched) return;
                        const now = new Date().toISOString();
                        const author = currentUser?.name || (isOwner ? "Master" : "Gestor Adm.");
                        const authorId = currentUser?.id || "admin";
                        const updatedTips = tips.map(t => {
                          if (t.restaurantId === rid && t.date === date) {
                            return { ...t, publishedAt: now, publishedBy: authorId, publishedByName: author };
                          }
                          return t;
                        });
                        onUpdate("tips", updatedTips);
                        onUpdate("_toast", `📢 ${fmtDate(date)} publicado para empregados`);
                      };
                      const unpublishDay = async () => {
                        if (!isLaunched) return;
                        if (!await appConfirm(`Despublicar ${fmtDate(date)}?\n\nOs valores deste dia serão ocultados do extrato do empregado.`)) return;
                        const updatedTips = tips.map(t => {
                          if (t.restaurantId === rid && t.date === date) {
                            return { ...t, publishedAt: null, unpublishedAt: new Date().toISOString() };
                          }
                          return t;
                        });
                        onUpdate("tips", updatedTips);
                        onUpdate("_toast", `🔒 ${fmtDate(date)} despublicado`);
                      };
                      const removeDay = async () => {
                        if(!await appConfirm(`Zerar gorjeta de ${fmtDate(date)}?\n\n⚠️ Um backup será salvo no Histórico — você pode restaurar depois.`)) return;
                        const preSnap = snapshotTipsMonth(tips, rid, mk);
                        saveTipsSnapshot(`Remover gorjeta de ${fmtDate(date)}`, preSnap);
                        onUpdate("tips",tips.filter(t=>!(t.restaurantId===rid&&t.date===date)));
                        setTipRows(prev=>prev.filter(r=>r.date!==date));
                        onUpdate("_toast",`🗑️ ${fmtDate(date)}: removido`);
                      };

                      // Renders compactos reutilizáveis
                      const dataCol = (
                        <div style={{textAlign:"center"}}>
                          <div style={{color:isWeekend?"#f59e0b":isNoTip?"#818cf8":isPublished?"var(--green)":isLaunched&&!isDirty?ac:isDirty?"#f59e0b":"var(--text3)",fontSize:13,fontWeight:700}}>{parseInt(date.slice(-2))}</div>
                          <div style={{color:"var(--text3)",fontSize:9}}>{weekday}</div>
                        </div>
                      );
                      const valorCol = (
                        privacyMask && isLaunched && !isDirty ? (
                          <div style={{...S.input, fontSize:14, padding:"8px 10px", background:"#e8faf0", color:"var(--green)", borderColor:"#10b98133", display:"flex", alignItems:"center"}}>
                            ••••,••
                          </div>
                        ) : (
                          <input
                            type="text" inputMode="decimal"
                            value={isNoTip ? "" : displayVal}
                            disabled={isNoTip}
                            onChange={e=>{ const raw = e.target.value.replace(/[^0-9.,]/g,""); setTipRows(prev => { const without = prev.filter(r => r.date !== date); return [...without, { date, total: raw, note: "" }]; }); }}
                            placeholder="0,00"
                            style={{...S.input, fontSize:14, padding:"8px 10px",
                              background:  isNoTip?"#f5f0ff" : isDirty?"#fef9e7" : isStale?"#fef9e7" : isPublished?"#e8faf0" : isLaunched?"#eff6ff" : "var(--bg2)",
                              color:       isNoTip?"#6366f1" : isDirty?"#f59e0b" : isStale?"#f59e0b" : isPublished?"var(--green)" : isLaunched?ac : "var(--text)",
                              borderColor: isNoTip?"transparent" : isDirty?"#f59e0b44" : isStale?"#f59e0b66" : isPublished?"#10b98133" : isLaunched?"var(--ac)22" : "var(--border)",
                              cursor:      isNoTip?"not-allowed" : "text",
                            }}
                          />
                        )
                      );
                      const noTipCol = (
                        <label style={{display:"flex",justifyContent:"center",alignItems:"center",cursor:isLaunched?"default":"pointer",userSelect:"none",opacity:isLaunched?0.3:1}}>
                          <input type="checkbox" checked={isNoTip} disabled={isLaunched}
                            onChange={e=>setNoTip(date, e.target.checked)}
                            style={{width:18,height:18,cursor:isLaunched?"default":"pointer",accentColor:"#6366f1"}} />
                        </label>
                      );
                      const statusCol = (
                        <div title={statusTitle} style={{display:"flex",justifyContent:"center",alignItems:"center",fontSize:14,fontWeight:700,color:statusColor,cursor:"help"}}>
                          {statusIcon}
                        </div>
                      );
                      const pubCol = (
                        <div style={{display:"flex",justifyContent:"center",alignItems:"center"}}>
                          {!isLaunched && <span style={{color:"var(--text3)",fontSize:10}}>—</span>}
                          {isLaunched && canPublish && !isPublished && (
                            <button onClick={publishDay} title="Publicar este dia para os empregados" style={{padding:"4px 10px",borderRadius:8,border:"none",background:ac,color:"#fff",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:"'DM Mono',monospace",whiteSpace:"nowrap"}}>
                              {partialPublish ? "Re-pub." : "Publicar"}
                            </button>
                          )}
                          {isLaunched && canPublish && isPublished && (
                            <button onClick={unpublishDay} title="Despublicar — esconder do empregado" style={{padding:"4px 10px",borderRadius:8,border:"1px solid var(--border)",background:"var(--bg2)",color:"var(--text2)",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:"'DM Mono',monospace",whiteSpace:"nowrap"}}>
                              Despublicar
                            </button>
                          )}
                          {isLaunched && !canPublish && (
                            <span style={{color:isPublished?"var(--green)":"var(--text3)",fontSize:10}}>{isPublished?"✓ pub.":"pendente"}</span>
                          )}
                        </div>
                      );
                      const acoesCol = (
                        <div style={{display:"flex",gap:4,justifyContent:"center",alignItems:"center"}}>
                          {isLaunched && (
                            <button onClick={removeDay} title="Remover lançamento" style={{padding:"3px 6px",borderRadius:6,border:"none",background:"transparent",color:"var(--red)",cursor:"pointer",fontSize:11}}>
                              ✕
                            </button>
                          )}
                        </div>
                      );

                      // Mobile: empilha em 2 linhas (compactez)
                      if (mobileOnly) {
                        return (
                          <div key={date} style={{padding:"6px 8px",marginBottom:3,borderRadius:10,background:bg,border:`1px solid ${border}`}}>
                            <div style={{display:"grid",gridTemplateColumns:gridMobile,gap:4,alignItems:"center"}}>
                              {dataCol}{valorCol}{noTipCol}{statusCol}
                            </div>
                            {(isLaunched || (canPublish && isLaunched)) && (
                              <div style={{display:"flex",justifyContent:"flex-end",gap:6,marginTop:6,paddingTop:6,borderTop:"1px dashed var(--border)"}}>
                                {pubCol}
                                {acoesCol}
                              </div>
                            )}
                          </div>
                        );
                      }

                      // Desktop: tudo numa linha
                      return (
                        <div key={date} style={{display:"grid",gridTemplateColumns:gridDesktop,gap:6,padding:"5px 8px",marginBottom:3,borderRadius:10,background:bg,border:`1px solid ${border}`,alignItems:"center"}}>
                          {dataCol}
                          {valorCol}
                          {noTipCol}
                          {statusCol}
                          {pubCol}
                          {acoesCol}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* Resumo Semanal — informativo (Fase D) */}
            {(() => {
              const weeks = getWeeksInMonth(year, month);
              const ridApprovals = data?.tipApprovals?.[rid] ?? {};
              const tipPublished = (tip) => isTipPublished(tip, ridApprovals);
              const fmtDay = (ds) => { const [,mm,dd] = ds.split("-"); return `${dd}/${mm}`; };
              return (
                <div style={{marginBottom:24}}>
                  <p style={{color:"var(--text3)",fontSize:11,fontWeight:700,marginBottom:8,textTransform:"uppercase",letterSpacing:0.5}}>📊 Resumo Semanal</p>
                  <div style={{display:"grid",gridTemplateColumns:`repeat(auto-fill, minmax(${mobileOnly?"100%":"200px"}, 1fr))`,gap:8}}>
                    {weeks.map(w => {
                      const weekTips = monthTips.filter(t => { const day = parseInt(t.date.split("-")[2]); return w.daysInMonth.includes(day); });
                      const weekTotal = weekTips.reduce((s,t) => s + (t.myNet ?? 0), 0);
                      const weekEmps = new Set(weekTips.map(t => t.employeeId)).size;
                      const weekDates = [...new Set(weekTips.map(t => t.date))];
                      const pubDates = [...new Set(weekTips.filter(tipPublished).map(t => t.date))];
                      const allPub  = weekDates.length > 0 && pubDates.length === weekDates.length;
                      const partial = pubDates.length > 0 && !allPub;
                      return (
                        <div key={w.monday} style={{padding:"8px 10px",borderRadius:8,background:allPub?"#f0fdf4":"var(--bg2)",border:`1px solid ${allPub?"#10b98133":partial?"#f59e0b33":"var(--border)"}`}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:2}}>
                            <div style={{fontWeight:700,fontSize:12,color:"var(--text)"}}>{fmtDay(w.monday)} — {fmtDay(w.sunday)}</div>
                            {allPub && <span style={{fontSize:10,color:"var(--green)"}}>✓</span>}
                            {partial && <span style={{fontSize:10,color:"#f59e0b"}}>{pubDates.length}/{weekDates.length}</span>}
                          </div>
                          <div style={{fontSize:10,color:"var(--text3)"}}>{weekTips.length} lanç. · {weekEmps} emp.</div>
                          <div style={{fontSize:11,color:ac,fontWeight:700,marginTop:2}}>
                            {privacyMask ? "R$ ••••,••" : `R$ ${weekTotal.toFixed(2)}`}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Divisão por dia — largura total */}
            {tipDates.length === 0 && <p style={{ color: "var(--text3)", textAlign: "center" }}>Nenhum lançamento neste mês.</p>}
            {tipDates.map(d => {
              const dT = monthTips.filter(t => t.date === d);
              return (
                <div key={d} style={{ ...S.card, marginBottom: 10 }}>
                  <div style={{ display:"flex",justifyContent:"space-between",marginBottom:10 }}>
                    <span style={{color:"var(--text2)"}}>{fmtDate(d)}</span>
                    <div style={{textAlign:"right"}}><div style={{color:"var(--text)",fontSize:12}}>Pool: {pFmt(dT[0]?.poolTotal)}</div><div style={{color:ac,fontSize:12}}>Dist: {pFmt(dT.reduce((a,t)=>a+t.myNet,0))}</div></div>
                  </div>
                  {AREAS.map(a => {
                    const aT = dT.filter(t => t.area === a);
                    if (!aT.length) return null;
                    return (
                      <div key={a} style={{borderTop:"1px solid var(--border)",paddingTop:8,marginTop:8}}>
                        <div style={{marginBottom:4}}><AreaBadge area={a} /></div>
                        {aT.map(t => {
                          // Busca em employees (todos), não em restEmps (que filtra inativos/demitidos)
                          // — isso garante que demitido apareça com nome em gorjetas anteriores à demissão.
                          const emp = employees.find(e => e.id === t.employeeId);
                          const isDemit = emp?.demitidoEm && emp.demitidoEm <= t.date;
                          const isInact = emp?.inactive && emp.inactiveFrom && emp.inactiveFrom <= t.date;
                          const showInactive = (emp?.demitidoEm || emp?.inactive) && !isDemit && !isInact;
                          return (
                            <div key={t.id} style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",fontSize:12,padding:"4px 0",flexWrap:"wrap",gap:"2px 12px"}}>
                              <span style={{color:"var(--text2)",minWidth:80,flex:"1 1 auto"}}>
                                {emp?.name ?? "—"}
                                {showInactive && <span style={{fontSize:10,color:"var(--text3)",marginLeft:6,fontStyle:"italic"}}>(ex-funcionário)</span>}
                              </span>
                              <div style={{display:"flex",gap:8,flexShrink:0,fontSize:11}}>
                                <span style={{color:"var(--text)"}}>{pFmt(t.myShare)}</span>
                                <span style={{color:"var(--red)"}}>-{pFmt(t.myTax)}</span>
                                <span style={{color:ac,fontWeight:700}}>{pFmt(t.myNet)}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                  <button onClick={()=>{
                    const ids=new Set(dT.map(t=>t.id));
                    const preSnap = snapshotTipsMonth(tips, rid, mk);
                    saveTipsSnapshot(`Remover lançamento (${fmtDate(dT[0]?.date)})`, preSnap);
                    onUpdate("tips",tips.filter(t=>!ids.has(t.id)));
                    onUpdate("_toast","Lançamento removido.");
                  }} style={{marginTop:10,background:"none",border:"1px solid #e74c3c33",borderRadius:8,color:"var(--red)",cursor:"pointer",fontSize:12,padding:"4px 12px",fontFamily:"'DM Mono',monospace"}}>Remover lançamento</button>
                </div>
              );
            })}
          </div>
        )}

        {/* EQUIPE */}
        {tab === "employees" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:16}}>
              <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?16:20}}>👥 Equipe</h3>
              {isOwner && <button onClick={()=>{
                const emps = employees.filter(e=>e.restaurantId===rid);
                const ok = resetTab("employees","Equipe",()=>({employees:emps}));
                if(ok){ onUpdate("employees",employees.filter(e=>e.restaurantId!==rid)); onUpdate("_toast","🗑️ Equipe enviada para a lixeira"); }
              }} style={{...S.btnSecondary,fontSize:12,color:"var(--red)",borderColor:"var(--red)44"}}>🗑️ Resetar</button>}
            </div>
            <EmployeeSpreadsheet
              restEmps={employees.filter(e => e.restaurantId === rid)}
              restRoles={restRoles} rid={rid}
              employees={employees} pessoas={data?.pessoas ?? []} onUpdate={onUpdate} restCode={restaurant.shortCode}
              isOwner={isOwner} restaurant={restaurant}
              workSchedules={data?.workSchedules ?? {}} managers={data?.managers ?? []} communications={data?.communications ?? []}
              resetSignal={empResetSignal}
              notifications={data?.notifications??[]}
              privacyMask={privacyMask}
              incidents={data?.incidents??[]} feedbacks={data?.feedbacks??[]}
              devChecklists={data?.devChecklists??{}} schedules={data?.schedules??{}}
              currentUser={currentUser} isLider={isLider} mobileOnly={mobileOnly} roles={roles}
              vtPayments={data?.vtPayments??{}} vtConfig={data?.vtConfig??{}} scheduleStatus={data?.scheduleStatus??{}}
              employeeGoals={data?.employeeGoals??{}}
              delays={data?.delays??{}}
              meetingPlans={data?.meetingPlans??[]}
              meetingOccurrences={data?.meetingOccurrences??[]}
              onGenerateDismissalReport={async (emp) => {
                try {
                  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
                  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js");
                  const { jsPDF } = window.jspdf;
                  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
                  const role = restRoles.find(r => r.id === emp.roleId);
                  const demDate = emp.demitidoEm;
                  const admDate = emp.admission || "—";
                  const W = doc.internal.pageSize.getWidth();
                  const H = doc.internal.pageSize.getHeight();
                  const mx = 12; // margin x
                  const cw = W - mx * 2; // content width
                  const f2 = v => { const n = parseFloat(v) || 0; return `R$ ${n.toFixed(2).replace(".",",").replace(/\B(?=(\d{3})+(?!\d))/g,".")}`; };
                  const fmtDate = d => d && d !== "—" ? new Date(d+"T12:00:00").toLocaleDateString("pt-BR") : "—";
                  let y = 0;

                  // ── Accent bar at top ──
                  doc.setFillColor(37, 99, 235);
                  doc.rect(0, 0, W, 4, "F");

                  // ── Header ──
                  y = 12;
                  doc.setFontSize(15);
                  doc.setTextColor(37, 99, 235);
                  doc.setFont(undefined, "bold");
                  doc.text("RELATÓRIO DE DESLIGAMENTO", mx, y);
                  doc.setFontSize(9);
                  doc.setTextColor(120, 120, 120);
                  doc.setFont(undefined, "normal");
                  doc.text(`${restaurant.name}  ·  ${new Date().toLocaleDateString("pt-BR")}`, mx, y + 5);

                  // Logo text right-aligned
                  doc.setFontSize(10);
                  doc.setTextColor(37, 99, 235);
                  doc.setFont(undefined, "bold");
                  doc.text("Sistema", W - mx, y, { align: "right" });
                  doc.setFont(undefined, "normal");
                  y += 10;

                  // Thin separator line
                  doc.setDrawColor(220, 220, 220);
                  doc.setLineWidth(0.3);
                  doc.line(mx, y, W - mx, y);
                  y += 5;

                  // ── Section 1: Employee data (2-column compact layout) ──
                  doc.setFontSize(9);
                  doc.setTextColor(37, 99, 235);
                  doc.setFont(undefined, "bold");
                  doc.text("DADOS DO EMPREGADO", mx, y);
                  doc.setFont(undefined, "normal");
                  y += 1;

                  const empFields = [
                    ["Nome", emp.name], ["CPF", emp.cpf || "—"],
                    ["Código", emp.empCode || "—"], ["Cargo", role?.name || "—"],
                    ["Área", role?.area || "—"], ["Admissão", fmtDate(admDate)],
                    ["Demissão", fmtDate(demDate)], ["Demitido por", emp.demitidoPor || "—"],
                  ];
                  doc.autoTable({
                    startY: y,
                    head: [],
                    body: [
                      [empFields[0][0], empFields[0][1], empFields[1][0], empFields[1][1]],
                      [empFields[2][0], empFields[2][1], empFields[3][0], empFields[3][1]],
                      [empFields[4][0], empFields[4][1], empFields[5][0], empFields[5][1]],
                      [empFields[6][0], empFields[6][1], empFields[7][0], empFields[7][1]],
                    ],
                    theme: "plain",
                    styles: { fontSize: 8, cellPadding: {top:1.5,bottom:1.5,left:2,right:2}, textColor: [50,50,50] },
                    columnStyles: {
                      0: { fontStyle: "bold", cellWidth: 24, textColor: [100,100,100] },
                      1: { cellWidth: cw/2 - 24 },
                      2: { fontStyle: "bold", cellWidth: 24, textColor: [100,100,100] },
                      3: { cellWidth: cw/2 - 24 },
                    },
                    margin: { left: mx, right: mx },
                    tableLineColor: [230,230,230],
                    tableLineWidth: 0.2,
                  });
                  y = doc.lastAutoTable.finalY + 5;

                  // ── Section 2: Gorjetas summary ──
                  doc.setFontSize(9);
                  doc.setTextColor(37, 99, 235);
                  doc.setFont(undefined, "bold");
                  doc.text("GORJETAS — RESUMO MENSAL", mx, y);
                  doc.setFont(undefined, "normal");
                  y += 1;

                  const empTips = tips.filter(t => t.restaurantId === rid && t.employeeId === emp.id);
                  const tipsByMonth = {};
                  empTips.forEach(t => {
                    if (!tipsByMonth[t.monthKey]) tipsByMonth[t.monthKey] = [];
                    tipsByMonth[t.monthKey].push(t);
                  });
                  const sortedMonths = Object.keys(tipsByMonth).sort();

                  let grandBruto = 0, grandDed = 0, grandLiq = 0;
                  const tipSummaryRows = [];
                  sortedMonths.forEach(smk => {
                    const monthTips = tipsByMonth[smk].sort((a,b) => a.date.localeCompare(b.date));
                    const mBruto = monthTips.reduce((s,t) => s + (t.myShare ?? 0), 0);
                    const mDed = monthTips.reduce((s,t) => s + (t.myTax ?? 0), 0);
                    const mLiq = monthTips.reduce((s,t) => s + (t.myNet ?? 0), 0);
                    grandBruto += mBruto; grandDed += mDed; grandLiq += mLiq;
                    tipSummaryRows.push([smk, `${monthTips.length}d`, f2(mBruto), f2(mDed), f2(mLiq)]);
                  });

                  const valColW = (cw - 22 - 14) / 3; // equal width for Bruto/Dedução/Líquido
                  doc.autoTable({
                    startY: y,
                    head: [["Mês", "Dias", "Bruto", "Dedução", "Líquido"]],
                    body: tipSummaryRows,
                    theme: "striped",
                    styles: { fontSize: 7.5, cellPadding: { top: 1.5, bottom: 1.5, left: 3, right: 3 } },
                    headStyles: { fillColor: [37,99,235], fontSize: 7.5, cellPadding: { top: 2, bottom: 2, left: 3, right: 3 } },
                    columnStyles: {
                      0: { cellWidth: 22 },
                      1: { cellWidth: 14, halign: "center" },
                      2: { cellWidth: valColW, halign: "right" },
                      3: { cellWidth: valColW, halign: "right" },
                      4: { cellWidth: valColW, halign: "right" },
                    },
                    alternateRowStyles: { fillColor: [245, 247, 250] },
                    margin: { left: mx, right: mx },
                  });
                  y = doc.lastAutoTable.finalY + 5;

                  // ── Section 3: Day-by-day detail (last month, compact 2-col if > 16 days) ──
                  if (sortedMonths.length > 0) {
                    const lastMk = sortedMonths[sortedMonths.length - 1];
                    const lastTips = tipsByMonth[lastMk].sort((a,b) => a.date.localeCompare(b.date));
                    doc.setFontSize(9);
                    doc.setTextColor(37, 99, 235);
                    doc.setFont(undefined, "bold");
                    doc.text(`DETALHAMENTO DIA A DIA — ${lastMk}`, mx, y);
                    doc.setFont(undefined, "normal");
                    y += 1;

                    if (lastTips.length <= 16) {
                      // Single compact table
                      const ddColW = 22;
                      const ddValW = (cw - ddColW) / 4;
                      doc.autoTable({
                        startY: y,
                        head: [["Data", "Bruto", "Dedução", "Penalidade", "Líquido"]],
                        body: lastTips.map(t => [
                          fmtDate(t.date),
                          f2(t.myShare), f2(t.myTax),
                          t.penalty ? `-${f2(t.penalty)}` : "—", f2(t.myNet),
                        ]),
                        theme: "striped",
                        styles: { fontSize: 6.5, cellPadding: { top: 1, bottom: 1, left: 2, right: 2 } },
                        headStyles: { fillColor: [100,116,139], fontSize: 6.5, cellPadding: { top: 1.5, bottom: 1.5, left: 2, right: 2 } },
                        columnStyles: {
                          0: { cellWidth: ddColW },
                          1: { cellWidth: ddValW, halign: "right" },
                          2: { cellWidth: ddValW, halign: "right" },
                          3: { cellWidth: ddValW, halign: "right" },
                          4: { cellWidth: ddValW, halign: "right" },
                        },
                        alternateRowStyles: { fillColor: [248,249,250] },
                        margin: { left: mx, right: mx },
                      });
                      y = doc.lastAutoTable.finalY + 5;
                    } else {
                      // Split into 2 side-by-side tables for compactness
                      const half = Math.ceil(lastTips.length / 2);
                      const col1 = lastTips.slice(0, half);
                      const col2 = lastTips.slice(half);
                      const colW = (cw - 4) / 2;
                      const dcW = 20;
                      const dvW = (colW - dcW) / 2;
                      const colStyles = { fontSize: 6.5, cellPadding: { top: 0.8, bottom: 0.8, left: 1.5, right: 1.5 } };
                      const colHead = { fillColor: [100,116,139], fontSize: 6.5, cellPadding: { top: 1.2, bottom: 1.2, left: 1.5, right: 1.5 } };
                      const mkBody = arr => arr.map(t => [fmtDate(t.date), f2(t.myShare), f2(t.myNet)]);
                      const dColStyles = { 0: { cellWidth: dcW }, 1: { cellWidth: dvW, halign: "right" }, 2: { cellWidth: dvW, halign: "right" } };

                      doc.autoTable({
                        startY: y,
                        head: [["Data", "Bruto", "Líquido"]],
                        body: mkBody(col1),
                        theme: "striped",
                        styles: colStyles,
                        headStyles: colHead,
                        columnStyles: dColStyles,
                        alternateRowStyles: { fillColor: [248,249,250] },
                        margin: { left: mx, right: W - mx - colW },
                        tableWidth: colW,
                      });
                      const y1 = doc.lastAutoTable.finalY;

                      doc.autoTable({
                        startY: y,
                        head: [["Data", "Bruto", "Líquido"]],
                        body: mkBody(col2),
                        theme: "striped",
                        styles: colStyles,
                        headStyles: colHead,
                        columnStyles: dColStyles,
                        alternateRowStyles: { fillColor: [248,249,250] },
                        margin: { left: mx + colW + 4, right: mx },
                        tableWidth: colW,
                      });
                      y = Math.max(y1, doc.lastAutoTable.finalY) + 5;
                    }
                  }

                  // ── Section 4: VT Restitution ──
                  doc.setFontSize(9);
                  doc.setTextColor(37, 99, 235);
                  doc.setFont(undefined, "bold");
                  doc.text("RESTITUIÇÃO DE VALE TRANSPORTE", mx, y);
                  doc.setFont(undefined, "normal");
                  y += 1;

                  const demMk = demDate.slice(0,7);
                  const [demY, demM] = demMk.split("-").map(Number);
                  const demDay = parseInt(demDate.slice(8,10));
                  const daysInDemMonth = new Date(demY, demM, 0).getDate();

                  const vtSnap = data?.vtPayments?.[rid]?.[demMk]?.snapshot?.find(s => s.empId === emp.id);
                  const vtConf = data?.vtConfig?.[rid]?.[emp.id];
                  const dailyRate = vtSnap?.dailyRate ?? vtConf?.dailyRate ?? 0;
                  const schedDayMap = schedules?.[rid]?.[demMk]?.[emp.id] ?? {};

                  let plannedFullMonth = vtSnap?.plannedDays ?? 0;
                  let workedUntilDismissal = 0;
                  if (!vtSnap) {
                    for (let d = 1; d <= daysInDemMonth; d++) {
                      const dateStr = `${demY}-${String(demM).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
                      const escalaSt = schedDayMap[dateStr];
                      if (!escalaSt || escalaSt === "comptrab") plannedFullMonth++;
                    }
                  }
                  for (let d = 1; d < demDay && d <= daysInDemMonth; d++) {
                    const dateStr = `${demY}-${String(demM).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
                    const escalaSt = schedDayMap[dateStr];
                    if (!escalaSt || escalaSt === "comptrab") workedUntilDismissal++;
                  }

                  const vtPaidMonth = dailyRate * plannedFullMonth;
                  const vtOwed = dailyRate * workedUntilDismissal;
                  const vtToReturn = Math.max(0, vtPaidMonth - vtOwed);

                  const prevDemDate = new Date(demY, demM - 2, 1);
                  const prevDemMk = monthKey(prevDemDate.getFullYear(), prevDemDate.getMonth());
                  const prevDemPayment = data?.vtPayments?.[rid]?.[prevDemMk];
                  let pendingAdjust = 0;
                  if (prevDemPayment?.snapshot) {
                    const prevSnap = prevDemPayment.snapshot.find(s => s.empId === emp.id);
                    if (prevSnap) {
                      const prevLastDay = new Date(prevDemDate.getFullYear(), prevDemDate.getMonth()+1, 0).getDate();
                      const prevSchedMap = schedules?.[rid]?.[prevDemMk]?.[emp.id] ?? {};
                      let prevActual = 0;
                      for (let d = 1; d <= prevLastDay; d++) {
                        const dateStr = `${prevDemDate.getFullYear()}-${String(prevDemDate.getMonth()+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
                        const st = prevSchedMap[dateStr];
                        if (!st || st === "comptrab") prevActual++;
                      }
                      pendingAdjust = round2((prevActual - prevSnap.plannedDays) * prevSnap.dailyRate);
                    }
                  }

                  const vtFinalReturn = Math.max(0, vtToReturn - pendingAdjust);
                  const labelW = 30;
                  const vtValW = (cw - labelW * 3) / 3;
                  const vtRows = [
                    ["VT diário", f2(dailyRate), "Dias planejados", String(plannedFullMonth), "Dias até demissão", String(workedUntilDismissal)],
                    ["VT pago (mês)", f2(vtPaidMonth), "VT devido", f2(vtOwed), pendingAdjust !== 0 ? "Ajuste ant." : "", pendingAdjust !== 0 ? f2(pendingAdjust) : ""],
                  ];

                  doc.autoTable({
                    startY: y,
                    head: [],
                    body: vtRows,
                    theme: "plain",
                    styles: { fontSize: 7.5, cellPadding: { top: 1.5, bottom: 1.5, left: 2, right: 2 }, textColor: [60,60,60] },
                    columnStyles: {
                      0: { fontStyle: "bold", textColor: [100,100,100], cellWidth: labelW },
                      1: { cellWidth: vtValW, halign: "right" },
                      2: { fontStyle: "bold", textColor: [100,100,100], cellWidth: labelW },
                      3: { cellWidth: vtValW, halign: "right" },
                      4: { fontStyle: "bold", textColor: [100,100,100], cellWidth: labelW },
                      5: { cellWidth: vtValW, halign: "right" },
                    },
                    margin: { left: mx, right: mx },
                  });
                  y = doc.lastAutoTable.finalY + 5;

                  // ── Section 5: Summary highlight boxes ──
                  doc.setDrawColor(220, 220, 220);
                  doc.setLineWidth(0.3);
                  doc.line(mx, y, W - mx, y);
                  y += 4;
                  doc.setFontSize(9);
                  doc.setTextColor(37, 99, 235);
                  doc.setFont(undefined, "bold");
                  doc.text("RESUMO FINANCEIRO", mx, y);
                  doc.setFont(undefined, "normal");
                  y += 4;

                  const boxH = 9;
                  const boxGap = 3;
                  const boxW = (cw - boxGap) / 2;

                  // Row 1: Gorjeta Bruta + Dedução
                  doc.setFillColor(219, 234, 254); // light blue
                  doc.roundedRect(mx, y, boxW, boxH, 1.5, 1.5, "F");
                  doc.setFontSize(7);
                  doc.setTextColor(37, 99, 235);
                  doc.setFont(undefined, "bold");
                  doc.text("GORJETA BRUTA TOTAL", mx + 3, y + 3.5);
                  doc.setFontSize(9);
                  doc.text(f2(grandBruto), mx + boxW - 3, y + 3.5, { align: "right" });
                  doc.setFont(undefined, "normal");

                  const bx12 = mx + boxW + boxGap;
                  doc.setFillColor(254, 226, 226); // light red
                  doc.roundedRect(bx12, y, boxW, boxH, 1.5, 1.5, "F");
                  doc.setFontSize(7);
                  doc.setTextColor(185, 28, 28);
                  doc.setFont(undefined, "bold");
                  const taxPct = (restaurant.taxRate ?? TAX) * 100;
                  doc.text(`DEDUÇÃO TOTAL GORJETA (${taxPct.toFixed(0)}%)`, bx12 + 3, y + 3.5);
                  doc.setFontSize(9);
                  doc.text(f2(grandDed), bx12 + boxW - 3, y + 3.5, { align: "right" });
                  doc.setFont(undefined, "normal");
                  y += boxH + boxGap;

                  // Row 2: Gorjeta Líquida + VT a Restituir
                  doc.setFillColor(209, 250, 229); // light green
                  doc.roundedRect(mx, y, boxW, boxH, 1.5, 1.5, "F");
                  doc.setFontSize(7);
                  doc.setTextColor(5, 150, 105);
                  doc.setFont(undefined, "bold");
                  doc.text("GORJETA LÍQUIDA TOTAL", mx + 3, y + 3.5);
                  doc.setFontSize(9);
                  doc.text(f2(grandLiq), mx + boxW - 3, y + 3.5, { align: "right" });
                  doc.setFont(undefined, "normal");

                  const bx22 = mx + boxW + boxGap;
                  doc.setFillColor(254, 243, 199); // warm yellow
                  doc.roundedRect(bx22, y, boxW, boxH, 1.5, 1.5, "F");
                  doc.setFontSize(7);
                  doc.setTextColor(180, 83, 9);
                  doc.setFont(undefined, "bold");
                  doc.text("VT A RESTITUIR", bx22 + 3, y + 3.5);
                  doc.setFontSize(9);
                  doc.setTextColor(220, 50, 50);
                  doc.text(f2(vtFinalReturn), bx22 + boxW - 3, y + 3.5, { align: "right" });
                  doc.setFont(undefined, "normal");
                  y += boxH + 6;

                  // ── Footer: accent bar + text ──
                  doc.setFillColor(37, 99, 235);
                  doc.rect(0, H - 3, W, 3, "F");
                  doc.setFontSize(7);
                  doc.setTextColor(150,150,150);
                  doc.text(`  Relatório de Desligamento  ·  ${emp.name}  ·  ${fmtDate(demDate)}`, W/2, H - 6, { align: "center" });

                  const safeName = emp.name.replace(/[^a-zA-Z0-9]/g,"_").toLowerCase();
                  setPreviewDoc(doc); setPreviewFileName(`relatorio_desligamento_${safeName}_${demDate}.pdf`);
                  onUpdate("_toast", `📄 Relatório de ${emp.name} exportado!`);
                } catch (err) {
                  console.error("Erro ao gerar relatório:", err);
                  onUpdate("_toast", "⚠️ Erro ao gerar relatório: " + (err.message || "desconhecido"));
                }
              }}
            />
          </div>
        )}

        {/* REUNIÕES — Planejamento de reuniões */}
        {tab === "reunioes" && (() => {
          const restEmps = employees.filter(e => e.restaurantId === rid && !e.inactive);
          const areas = [...new Set(restRoles.map(r => r.area).filter(Boolean))];
          const plans = (data?.meetingPlans ?? []).filter(p => p.restaurantId === rid).sort((a,b) => (b.plannedDate??"").localeCompare(a.plannedDate??""));
          // Form state via refs is not possible in this inline pattern, so we use a sub-component approach
          // We'll render a self-contained section
          return (
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:16}}>
                <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?16:20}}>📅 Reuniões</h3>
              </div>
              <MeetingPlannerSection
                restaurantId={rid}
                employees={restEmps}
                roles={restRoles}
                areas={areas}
                meetingPlans={plans}
                allMeetingPlans={data?.meetingPlans ?? []}
                feedbacks={data?.feedbacks ?? []}
                onUpdate={onUpdate}
                currentUser={currentUser}
                isOwner={isOwner}
                isDP={isDP}
                mobileOnly={mobileOnly}
                isLider={isLider}
                managerAreas={managerAreas}
                data={data}
              />
            </div>
          );
        })()}

        {/* CARGOS (super only) */}
        {tab === "roles" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:16}}>
              <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?16:20}}>🏷️ Cargos</h3>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <button
                  onClick={()=>{
                    // Export pro Planejamento.app — CSV compatível com importador do gestaocentral
                    const escapeCsv = (v) => {
                      if (v == null) return "";
                      const s = String(v);
                      return (s.includes(",") || s.includes('"') || s.includes("\n"))
                        ? '"' + s.replace(/"/g, '""') + '"'
                        : s;
                    };
                    const header = "nome,area,tipo_vinculo,pontos,sem_gorjeta,recebe_producao,ativo";
                    const lines = restRoles.map(r => [
                      escapeCsv(r.name),
                      escapeCsv(r.area || "Salão"),
                      "registrado",
                      String(r.points ?? 0),
                      String(!!r.noTip),
                      "false",                       // edite a planilha pra marcar produção (ex: Cozinha)
                      String(!r.inactive),
                    ].join(","));
                    const csv = [header, ...lines].join("\n");
                    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `cargos_${(restaurant?.shortCode || "rest").toLowerCase()}_${new Date().toISOString().slice(0,10)}.csv`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                    onUpdate("_toast", `📤 ${restRoles.length} cargo(s) exportado(s)`);
                  }}
                  style={{...S.btnSecondary, fontSize:12}}
                  title="Baixa um CSV pra importar no planejamento.app"
                >📤 Exportar CSV</button>
                {isOwner && <button onClick={()=>{
                  const ok = resetTab("roles","Cargos",()=>({roles:roles.filter(r=>r.restaurantId===rid)}));
                  if(ok){ onUpdate("roles",roles.filter(r=>r.restaurantId!==rid)); onUpdate("_toast","🗑️ Cargos enviados para a lixeira"); }
                }} style={{...S.btnSecondary,fontSize:12,color:"var(--red)",borderColor:"var(--red)44"}}>🗑️ Resetar</button>}
              </div>
            </div>
            <RoleSpreadsheet
              restRoles={restRoles} rid={rid}
              roles={roles} employees={employees} onUpdate={onUpdate}
            />
          </div>
        )}

        {/* PESSOAS — CRUD unificado */}
        {tab === "pessoas" && (
          <PessoasAdmin
            restaurantId={rid}
            pessoas={data?.pessoas ?? []}
            roles={roles}
            owners={data?.owners ?? []}
            employees={employees}
            managers={data?.managers ?? []}
            freelaShifts={data?.freelaShifts ?? []}
            restCode={restaurant.shortCode}
            onUpdate={onUpdate}
            mobileOnly={mobileOnly}
            realIsOwner={realIsOwner || isOwner}
            onStartImpersonate={onStartImpersonate}
            editTrigger={editPessoaTrigger}
          />
        )}

        {/* INÍCIO — saudação + Caixa de entrada (v7) + widgets opcionais (futuro) */}
        {tab === "inicio" && (
          <div>
            <InicioModule
              currentUser={currentUser}
              restaurant={restaurant}
              isOwner={isOwner}
              isDP={isDP}
              isLider={isLider}
              mobileOnly={mobileOnly}
              setTab={setTab}
              perms={perms}
            />
            {/* Inbox direto na página inicial — antes ficava em "notificacoes" (separado) */}
            {!privacyMask && (
              <div style={{maxWidth:760,margin:"0 auto",padding:mobileOnly?"8px 4px":"16px 8px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:12,marginTop:8}}>
                  <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?15:18}}>📬 Caixa de entrada</h3>
                </div>
                <NotificacoesTab
                  restaurantId={rid}
                  dpMessages={[]}
                  notifications={data?.notifications??[]}
                  onUpdate={onUpdate}
                  isOwner={isOwner}
                  inbox={data?.inbox??[]}
                  managerId={currentUser?.id}
                  managerRole={userRoleForInbox}
                  inboxFolders={data?.inboxFolders??{}}
                />
              </div>
            )}
          </div>
        )}

        {/* OCORRÊNCIAS — top-level v7 (reaproveita MeetingPlannerSection com forceSubTab) */}
        {tab === "ocorrencias" && (() => {
          const restEmps = employees.filter(e => e.restaurantId === rid && !e.inactive);
          const areas = [...new Set(restRoles.map(r => r.area).filter(Boolean))];
          const plans = (data?.meetingPlans ?? []).filter(p => p.restaurantId === rid).sort((a,b) => (b.plannedDate??"").localeCompare(a.plannedDate??""));
          return (
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:16}}>
                <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?16:20}}>🚨 Ocorrências</h3>
              </div>
              <MeetingPlannerSection
                restaurantId={rid}
                employees={restEmps}
                roles={restRoles}
                areas={areas}
                meetingPlans={plans}
                allMeetingPlans={data?.meetingPlans ?? []}
                feedbacks={data?.feedbacks ?? []}
                onUpdate={onUpdate}
                currentUser={currentUser}
                isOwner={isOwner}
                isDP={isDP}
                mobileOnly={mobileOnly}
                isLider={isLider}
                managerAreas={managerAreas}
                data={data}
                forceSubTab="ocorrencias"
              />
            </div>
          );
        })()}

        {/* IDEIAS — top-level v7 (reaproveita MeetingPlannerSection com forceSubTab) */}
        {tab === "ideias" && (() => {
          const restEmps = employees.filter(e => e.restaurantId === rid && !e.inactive);
          const areas = [...new Set(restRoles.map(r => r.area).filter(Boolean))];
          const plans = (data?.meetingPlans ?? []).filter(p => p.restaurantId === rid).sort((a,b) => (b.plannedDate??"").localeCompare(a.plannedDate??""));
          return (
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:16}}>
                <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?16:20}}>💡 Ideias</h3>
              </div>
              <MeetingPlannerSection
                restaurantId={rid}
                employees={restEmps}
                roles={restRoles}
                areas={areas}
                meetingPlans={plans}
                allMeetingPlans={data?.meetingPlans ?? []}
                feedbacks={data?.feedbacks ?? []}
                onUpdate={onUpdate}
                currentUser={currentUser}
                isOwner={isOwner}
                isDP={isDP}
                mobileOnly={mobileOnly}
                isLider={isLider}
                managerAreas={managerAreas}
                data={data}
                forceSubTab="ideias"
              />
            </div>
          );
        })()}

        {/* COMPRAS — admin v7 (reaproveita OperationalCompras com employee virtual) */}
        {tab === "mise_compras" && (() => {
          const virtEmp = {
            id: currentUser?.id ? `admin_${currentUser.id}` : `admin_compras_${rid}`,
            restaurantId: rid,
            name: currentUser?.name || (isOwner ? "Master" : "Gestor"),
            cpf: currentUser?.cpf || "",
            operationalAreas: { compras: true, contagens: true, checklists: true, fichasTecnicas: true, temperaturas: true },
          };
          return (
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:16}}>
                <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?16:20}}>🛒 Compras</h3>
              </div>
              <OperationalCompras employee={virtEmp} data={data} onUpdate={onUpdate} />
            </div>
          );
        })()}
        {/* TRILHAS — admin v7 (lista de equipe + drilldown EmpTrilhaView) */}
        {tab === "trilhas" && (() => {
          const teamEmps = restEmps.filter(e => !e.inactive).sort((a,b)=>(a.name||"").localeCompare(b.name||""));
          if (trilhasAdminSelEmp) {
            const emp = teamEmps.find(e => e.id === trilhasAdminSelEmp);
            if (!emp) { setTrilhasAdminSelEmp(null); return null; }
            return (
              <div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:16}}>
                  <button onClick={()=>setTrilhasAdminSelEmp(null)} style={{...S.btnSecondary,fontSize:12}}>← Voltar à lista</button>
                  <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?14:18}}>🎯 Trilha · {emp.name}</h3>
                </div>
                <EmpTrilhaView
                  empId={emp.id}
                  employees={employees}
                  roles={roles}
                  schedules={schedules}
                  incidents={data?.incidents??[]}
                  feedbacks={data?.feedbacks??[]}
                  devChecklists={data?.devChecklists??{}}
                  restaurantId={rid}
                  onUpdate={onUpdate}
                  employeeGoals={data?.employeeGoals??{}}
                  meetingPlans={data?.meetingPlans??[]}
                />
              </div>
            );
          }
          return (
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:16}}>
                <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?16:20}}>🎯 Trilhas</h3>
                <span style={{fontSize:12,color:"var(--text3)"}}>{teamEmps.length} {teamEmps.length===1?"pessoa":"pessoas"}</span>
              </div>
              {teamEmps.length === 0 ? (
                <div style={{padding:32,textAlign:"center",color:"var(--text3)",fontSize:13}}>Nenhum funcionário cadastrado ainda.</div>
              ) : (
                <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"repeat(auto-fill,minmax(220px,1fr))",gap:10}}>
                  {teamEmps.map(emp => {
                    const role = restRoles.find(r => r.id === emp.roleId);
                    return (
                      <button key={emp.id} onClick={()=>setTrilhasAdminSelEmp(emp.id)}
                        style={{textAlign:"left",padding:"14px 16px",border:"1px solid var(--border)",borderRadius:12,background:"var(--card-bg)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",transition:"all .15s"}}
                        onMouseEnter={e=>{e.currentTarget.style.borderColor="var(--ac)";e.currentTarget.style.background="var(--ac)08";}}
                        onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--border)";e.currentTarget.style.background="var(--card-bg)";}}>
                        <div style={{fontWeight:700,color:"var(--text)",fontSize:14,marginBottom:4}}>{emp.name}</div>
                        <div style={{fontSize:11,color:"var(--text3)"}}>{role?.name || "—"}{role?.area ? ` · ${role.area}` : ""}</div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {/* FREELAS — Lançamento, Fechamento, Histórico */}
        {tab === "freelas" && (
          <FreelasModule
            restaurantId={rid}
            pessoas={data?.pessoas ?? []}
            freelaShifts={data?.freelaShifts ?? []}
            freelaPagamentos={data?.freelaPagamentos ?? []}
            employees={employees}
            roles={roles}
            schedules={schedules}
            restaurants={restaurants}
            currentUser={currentUser}
            isDP={isDP}
            isLider={isLider}
            isOwner={isOwner}
            perms={perms}
            onUpdate={onUpdate}
            mobileOnly={mobileOnly}
          />
        )}

        {/* PERMISSÕES — v8.20.0: nova view por nível (substituiu a matriz sanfona) */}
        {tab === "permissoes" && (
          <PermissoesResumo
            restaurantId={rid}
            pessoas={data?.pessoas ?? []}
            owners={data?.owners ?? []}
            onOpenPessoa={openPessoaForEdit}
            mobileOnly={mobileOnly}
          />
        )}

        {/* MISE — CONTAGENS (admin) */}
        {tab === "mise_contagens" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:8}}>
              <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?16:20}}>📦 Contagens <span style={{fontSize:11,color:"#7c9e5e",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,marginLeft:6}}>Mise</span></h3>
            </div>
            <MiseContagensAdmin
              restaurantId={rid}
              restaurantName={restaurants.find(r => r.id === rid)?.name || ""}
              employees={employees}
              pessoas={data?.pessoas ?? []}
              miseCategories={data?.miseCategories ?? []}
              miseStocks={data?.miseStocks ?? []}
              miseAssignments={data?.miseAssignments ?? []}
              miseItems={data?.miseItems ?? []}
              miseCounts={data?.miseCounts ?? []}
              miseSuppliers={data?.miseSuppliers ?? []}
              miseProductSuppliers={data?.miseProductSuppliers ?? []}
              miseCycles={data?.miseCycles ?? []}
              miseSupplierOrders={data?.miseSupplierOrders ?? []}
              isOwner={isOwner}
              onUpdate={onUpdate}
              mobileOnly={mobileOnly}
            />
          </div>
        )}

        {/* MISE — CHECKLISTS (admin) */}
        {tab === "mise_checklists" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:8}}>
              <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?16:20}}>✅ Checklists <span style={{fontSize:11,color:"#7c9e5e",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,marginLeft:6}}>Mise</span></h3>
            </div>
            <MiseChecklistsAdmin
              restaurantId={rid}
              miseChecklistTemplates={data?.miseChecklistTemplates ?? []}
              miseChecklistRuns={data?.miseChecklistRuns ?? []}
              employees={employees}
              pessoas={data?.pessoas ?? []}
              roles={restRoles}
              onUpdate={onUpdate}
              mobileOnly={mobileOnly}
            />
          </div>
        )}

        {/* MISE — FICHAS TÉCNICAS (admin) */}
        {tab === "mise_fichas" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:8}}>
              <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?16:20}}>📋 Fichas Técnicas <span style={{fontSize:11,color:"#7c9e5e",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,marginLeft:6}}>Mise</span></h3>
            </div>
            <MiseFichasTecnicasAdmin
              restaurantId={rid}
              miseFtInsumos={data?.miseFtInsumos ?? []}
              miseFtEquipamentos={data?.miseFtEquipamentos ?? {}}
              miseFtDishes={data?.miseFtDishes ?? []}
              onUpdate={onUpdate}
              mobileOnly={mobileOnly}
            />
          </div>
        )}

        {/* MISE — TEMPERATURAS (admin) */}
        {tab === "mise_temperaturas" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:8}}>
              <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?16:20}}>🌡️ Temperaturas <span style={{fontSize:11,color:"#7c9e5e",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,marginLeft:6}}>Mise</span></h3>
              <span style={{fontSize:10,padding:"2px 8px",borderRadius:6,background:"#f59e0b22",color:"#f59e0b",fontWeight:700,letterSpacing:0.3}}>PILOTO</span>
            </div>
            <MiseTemperaturasAdmin
              restaurantId={rid}
              tuyaLink={data?.tuyaLinks?.[rid]}
              tempSensors={(data?.tempSensors ?? []).filter(s => s.restaurantId === rid)}
              tempReadings={(data?.tempReadings ?? []).filter(r => r.restaurantId === rid)}
              miseFtEquipamentos={data?.miseFtEquipamentos ?? {}}
              pessoas={(data?.pessoas ?? []).filter(p => (p.restaurantIds || []).includes(rid))}
              currentUser={currentUser}
              onUpdate={onUpdate}
              mobileOnly={mobileOnly}
            />
          </div>
        )}

        {/* ESCALA */}
        {tab === "schedule" && !scheduleMonthConfirmed && (
          <MonthPickerGate
            contextLabel="Escala"
            year={year} month={month}
            scheduleStatus={data?.scheduleStatus}
            restaurantId={rid}
            onPick={(y, m) => { setYear(y); setMonth(m); setScheduleMonthConfirmed(true); onUpdate("_toast", `📅 Trabalhando em ${monthLabel(y, m)}`); }}
            onCancel={() => setTab("inicio")}
          />
        )}
        {tab === "schedule" && scheduleMonthConfirmed && (
          <div>
            {/* Header */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:schedDirty?8:16}}>
              <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?16:20}}>📅 Escala — {monthLabel(year,month)}</h3>
                <SaveIndicator mode="manual" pending={schedDirty} compact />
                <button onClick={()=>setScheduleMonthConfirmed(false)} style={{...S.btnSecondary,fontSize:11,padding:"3px 10px"}} title="Voltar pra seleção de mês">↺ Trocar mês</button>
                {monthClosed && <span style={{fontSize:11,padding:"3px 10px",borderRadius:6,background:"var(--red)22",color:"var(--red)",fontWeight:700}}>🔒 Mês fechado</span>}
                {/* v8.13.0/v8.17.0: Reabrir destacado no header — quem tem perm reabrirEscala (Owner sempre) */}
                {monthClosed && (isOwner || canReabrirEscala) && (
                  <button onClick={()=>setShowReopenConfirm(true)} style={{fontSize:11,padding:"3px 10px",border:"1px solid #f59e0b",background:"#f59e0b15",color:"#f59e0b",fontWeight:700,borderRadius:6,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}} title="Reabrir mês para edição">
                    🔓 Reabrir
                  </button>
                )}
                {(() => { const vtPaidInfo = data?.vtPayments?.[rid]?.[mk]; return vtPaidInfo ? <span style={{fontSize:11,padding:"3px 10px",borderRadius:6,background:"#10b98122",color:"var(--green)",fontWeight:700}}>VT pago em {new Date(vtPaidInfo.paidAt).toLocaleDateString("pt-BR")}</span> : null; })()}
                {/* v8.13.0: Toggle — Vigente vira Fechada quando mês está fechado */}
                <div style={{display:"flex",borderRadius:6,overflow:"hidden",border:"1px solid var(--border)"}}>
                  {["vigente","prevista"].map(mode => {
                    const label = mode === "vigente" ? (monthClosed ? "🔒 Fechada" : "Vigente") : "Prevista";
                    return (
                      <button key={mode} onClick={()=>setSchedViewMode(mode)}
                        style={{padding:"4px 12px",fontSize:11,fontWeight:schedViewMode===mode?700:500,border:"none",background:schedViewMode===mode?(monthClosed&&mode==="vigente"?"#dc262622":"var(--ac)22"):"transparent",color:schedViewMode===mode?(monthClosed&&mode==="vigente"?"#dc2626":"var(--ac)"):"var(--text3)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}
                        title={mode === "vigente" ? (monthClosed ? "Estado da escala quando foi fechada (read-only)" : "Estado atual da escala (editável)") : "Snapshot da escala original (antes da primeira edição ou pagamento de VT)"}>
                        {label}
                      </button>
                    );
                  })}
                </div>
                {/* v8.11.0: Registrar inversão informal */}
                {(canSchedEdit || isOwner) && !monthClosed && (
                  <button onClick={()=>setSwapModalContext({ empAId: "", empBId: "", date1: "", date2: "" })} style={{...S.btnSecondary,fontSize:11,padding:"3px 10px"}} title="Registrar inversão recíproca de turno entre dois empregados (não muda a escala — só registra)">↔️ Registrar inversão</button>
                )}
                {/* v8.12.0: "Sugerir folgas de domingo" removido — escala deriva auto do horário+ciclo. */}
              </div>
              {/* Botão "Apagar todas as escalas" movido pra "Mais ações" no rodapé (Configurações avançadas) */}
            </div>
            {/* v8.12.2: Wizard de inversão em 4 passos + recíproca — domingo1, empB folgando, empA trab, domingo2 (folga recíproca de A) */}
            {swapModalContext && (() => {
              const ctx = swapModalContext;
              const dim2 = new Date(year, month + 1, 0).getDate();
              const sundays = [];
              for (let d = 1; d <= dim2; d++) {
                const dt = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
                if (new Date(dt+"T12:00:00").getDay() === 0) sundays.push({ date: dt, day: d });
              }
              // Step calculation: 1=date1, 2=empB, 3=empA, 4=date2, 5=confirm
              const step = !ctx.date1 ? 1 : (!ctx.empBId ? 2 : (!ctx.empAId ? 3 : (!ctx.date2 ? 4 : 5)));
              const empsFolgando = (() => {
                if (!ctx.date1) return [];
                return [...schedEmps].filter(e => {
                  const status = displayedMonth?.[e.id]?.[ctx.date1];
                  if (e.demitidoEm && ctx.date1 >= e.demitidoEm) return false;
                  return status === DAY_OFF || status === DAY_COMP;
                }).sort((a,b) => a.name.localeCompare(b.name));
              })();
              const empsTrabalhando = (() => {
                if (!ctx.date1) return [];
                return [...schedEmps].filter(e => {
                  if (e.id === ctx.empBId) return false;
                  if (e.demitidoEm && ctx.date1 >= e.demitidoEm) return false;
                  const status = displayedMonth?.[e.id]?.[ctx.date1];
                  return !status || status === DAY_COMP_TRAB;
                }).sort((a,b) => a.name.localeCompare(b.name));
              })();
              // Step 4: v8.12.3 — escaneia 3 meses (anterior, atual, próximo) procurando folgas de domingo de empA
              const empAObj = ctx.empAId ? schedEmps.find(e => e.id === ctx.empAId) : null;
              const empBObj = ctx.empBId ? schedEmps.find(e => e.id === ctx.empBId) : null;
              // Coleta domingos de 3 meses, marca o status de empA (saved + derived) e empB pra cada um
              const reciprocCandidates = (() => {
                if (!ctx.empAId) return [];
                const months = [
                  { y: month === 0 ? year - 1 : year, m: month === 0 ? 11 : month - 1, label: "Mês anterior" },
                  { y: year, m: month, label: "Este mês" },
                  { y: month === 11 ? year + 1 : year, m: month === 11 ? 0 : month + 1, label: "Próximo mês" },
                ];
                const result = [];
                const empAWS = data?.workSchedules?.[rid]?.[ctx.empAId] ?? [];
                months.forEach(({y, m, label}) => {
                  const dim = new Date(y, m + 1, 0).getDate();
                  const monthMk = monthKey(y, m);
                  for (let d = 1; d <= dim; d++) {
                    const dt = `${y}-${String(m+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
                    if (new Date(dt+"T12:00:00").getDay() !== 0) continue;
                    if (dt === ctx.date1) continue;
                    // Saved status (override) > derived from cycle
                    const savedA = data?.schedules?.[rid]?.[monthMk]?.[ctx.empAId]?.[dt];
                    const savedB = ctx.empBId ? data?.schedules?.[rid]?.[monthMk]?.[ctx.empBId]?.[dt] : null;
                    let aIsOff = savedA === DAY_OFF || savedA === DAY_COMP;
                    if (!savedA) {
                      // Derive from cycle
                      const ws = getActiveWorkSchedule(empAWS, dt);
                      const cycle = ws ? getSundayCycleFromSchedule(ws, dt) : null;
                      if (cycle && cycle.refDate && isSundayOffByCycle(cycle, dt)) aIsOff = true;
                      // Folga fixa? domingo é dia desligado no horário?
                      if (ws) {
                        const days = getEffectiveDays(ws, dt);
                        const dow0 = days?.[0];
                        if (!dow0 || dow0.active === false) aIsOff = true;
                      }
                    }
                    if (!aIsOff) continue;
                    // Status de empB no mesmo dia (pra info)
                    let bIsWork = !savedB || savedB === DAY_COMP_TRAB;
                    if (ctx.empBId && !savedB) {
                      // empB working by default unless cycle says folga
                      const empBWS = data?.workSchedules?.[rid]?.[ctx.empBId] ?? [];
                      const wsB = getActiveWorkSchedule(empBWS, dt);
                      if (wsB) {
                        const daysB = getEffectiveDays(wsB, dt);
                        const dow0B = daysB?.[0];
                        if (!dow0B || dow0B.active === false) bIsWork = false;
                        const cycleB = getSundayCycleFromSchedule(wsB, dt);
                        if (cycleB && cycleB.refDate && isSundayOffByCycle(cycleB, dt)) bIsWork = false;
                      }
                    }
                    result.push({ date: dt, monthLabel: label, perfect: bIsWork });
                  }
                });
                return result.sort((a,b) => a.date.localeCompare(b.date));
              })();
              const ac = "var(--ac)";
              const date1Label = ctx.date1 ? new Date(ctx.date1+"T12:00:00").toLocaleDateString("pt-BR") : "";
              const date2Label = ctx.date2 ? new Date(ctx.date2+"T12:00:00").toLocaleDateString("pt-BR") : "";
              return (
                <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={()=>setSwapModalContext(null)}>
                  <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:14,maxWidth:560,width:"100%",maxHeight:"88vh",overflow:"auto",padding:24,boxShadow:"0 20px 50px rgba(0,0,0,0.3)"}}>
                    <h3 style={{margin:"0 0 4px",color:"var(--text)",fontSize:16,fontWeight:700}}>↔️ Registrar inversão de domingo</h3>
                    <p style={{color:"var(--text3)",fontSize:12,margin:"0 0 14px",lineHeight:1.5}}>
                      Inversão recíproca: registra os dois domingos da troca. Escala, gorjeta e VT continuam idênticos.
                    </p>
                    {/* Breadcrumb */}
                    <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:14,fontSize:11,fontFamily:"'DM Mono',monospace",color:"var(--text3)",flexWrap:"wrap"}}>
                      <span style={{color:step>=1?ac:"var(--text3)",fontWeight:step===1?700:500}}>1·Domingo</span>
                      {ctx.date1 && <span style={{color:"var(--text2)"}}>{date1Label}</span>}
                      <span>›</span>
                      <span style={{color:step>=2?ac:"var(--text3)",fontWeight:step===2?700:500}}>2·Folga</span>
                      {empBObj && <span style={{color:"var(--text2)"}}>{shortName(empBObj.name)}</span>}
                      <span>›</span>
                      <span style={{color:step>=3?ac:"var(--text3)",fontWeight:step===3?700:500}}>3·Trab</span>
                      {empAObj && <span style={{color:"var(--text2)"}}>{shortName(empAObj.name)}</span>}
                      <span>›</span>
                      <span style={{color:step>=4?ac:"var(--text3)",fontWeight:step===4?700:500}}>4·Recíproca</span>
                      {ctx.date2 && <span style={{color:"var(--text2)"}}>{date2Label}</span>}
                    </div>

                    {/* STEP 1: pick Sunday (date1) */}
                    {step === 1 && (
                      <div>
                        <p style={{color:"var(--text2)",fontSize:13,margin:"0 0 12px"}}>Escolha o domingo da troca:</p>
                        {sundays.length === 0 ? (
                          <div style={{padding:"20px",textAlign:"center",color:"var(--text3)",fontSize:12}}>Sem domingos neste mês.</div>
                        ) : (
                          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))",gap:8}}>
                            {sundays.map(s => (
                              <button key={s.date} onClick={()=>setSwapModalContext({...ctx,date1:s.date})}
                                style={{padding:"12px 8px",borderRadius:10,border:`1px solid ${ac}66`,background:"var(--bg2)",color:"var(--text)",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:13,fontWeight:600,textAlign:"center",transition:"all 0.15s"}}
                                onMouseEnter={e=>{e.currentTarget.style.background=ac+"22";e.currentTarget.style.borderColor=ac;}}
                                onMouseLeave={e=>{e.currentTarget.style.background="var(--bg2)";e.currentTarget.style.borderColor=ac+"66";}}>
                                <div style={{fontSize:18,fontWeight:800,color:ac}}>{String(s.day).padStart(2,"0")}</div>
                                <div style={{fontSize:10,color:"var(--text3)",marginTop:2}}>{monthLabel(year,month).split(" ")[0]}</div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* STEP 2: empB folga */}
                    {step === 2 && (
                      <div>
                        <p style={{color:"var(--text2)",fontSize:13,margin:"0 0 12px"}}>Quem está de <strong>folga</strong> em {date1Label} e fez a troca:</p>
                        {empsFolgando.length === 0 ? (
                          <div style={{padding:"20px",textAlign:"center",color:"var(--text3)",fontSize:12,background:"var(--bg2)",borderRadius:10}}>
                            Ninguém marcado como folga neste domingo.
                          </div>
                        ) : (
                          <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:320,overflow:"auto"}}>
                            {empsFolgando.map(e => {
                              const role = restRoles.find(r => r.id === e.roleId);
                              const status = displayedMonth?.[e.id]?.[ctx.date1];
                              return (
                                <button key={e.id} onClick={()=>setSwapModalContext({...ctx,empBId:e.id})}
                                  style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",borderRadius:10,border:"1px solid var(--border)",background:"var(--bg2)",color:"var(--text)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,textAlign:"left",transition:"all 0.15s"}}
                                  onMouseEnter={ev=>{ev.currentTarget.style.background=ac+"15";ev.currentTarget.style.borderColor=ac;}}
                                  onMouseLeave={ev=>{ev.currentTarget.style.background="var(--bg2)";ev.currentTarget.style.borderColor="var(--border)";}}>
                                  <div>
                                    <div style={{fontWeight:600}}>{e.name}</div>
                                    <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>{role?.name ?? "—"} · {role?.area ?? ""}</div>
                                  </div>
                                  <span style={{fontSize:10,padding:"3px 8px",borderRadius:6,background:STATUS_COLORS[status]+"22",color:STATUS_COLORS[status],fontWeight:700,fontFamily:"'DM Mono',monospace"}}>{status === DAY_OFF ? "FOLGA" : "COMP"}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                        <button onClick={()=>setSwapModalContext({...ctx,date1:""})} style={{...S.btnSecondary,fontSize:11,padding:"6px 12px",marginTop:12}}>← Trocar domingo</button>
                      </div>
                    )}

                    {/* STEP 3: empA trabalhando */}
                    {step === 3 && (
                      <div>
                        <p style={{color:"var(--text2)",fontSize:13,margin:"0 0 12px"}}>
                          <strong>{empBObj?.name}</strong> trocou com quem? (lista de quem está <strong>trabalhando</strong> em {date1Label})
                        </p>
                        {empsTrabalhando.length === 0 ? (
                          <div style={{padding:"20px",textAlign:"center",color:"var(--text3)",fontSize:12,background:"var(--bg2)",borderRadius:10}}>
                            Ninguém trabalhando neste domingo.
                          </div>
                        ) : (
                          <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:320,overflow:"auto"}}>
                            {empsTrabalhando.map(e => {
                              const role = restRoles.find(r => r.id === e.roleId);
                              return (
                                <button key={e.id} onClick={()=>setSwapModalContext({...ctx,empAId:e.id})}
                                  style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",borderRadius:10,border:"1px solid var(--border)",background:"var(--bg2)",color:"var(--text)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,textAlign:"left",transition:"all 0.15s"}}
                                  onMouseEnter={ev=>{ev.currentTarget.style.background=ac+"15";ev.currentTarget.style.borderColor=ac;}}
                                  onMouseLeave={ev=>{ev.currentTarget.style.background="var(--bg2)";ev.currentTarget.style.borderColor="var(--border)";}}>
                                  <div>
                                    <div style={{fontWeight:600}}>{e.name}</div>
                                    <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>{role?.name ?? "—"} · {role?.area ?? ""}</div>
                                  </div>
                                  <span style={{fontSize:10,padding:"3px 8px",borderRadius:6,background:"#10b98122",color:"var(--green)",fontWeight:700,fontFamily:"'DM Mono',monospace"}}>TRAB</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                        <button onClick={()=>setSwapModalContext({...ctx,empBId:""})} style={{...S.btnSecondary,fontSize:11,padding:"6px 12px",marginTop:12}}>← Trocar empregado de folga</button>
                      </div>
                    )}

                    {/* STEP 4: date2 (folga recíproca de empA) — v8.12.3: 3 meses */}
                    {step === 4 && (
                      <div>
                        <p style={{color:"var(--text2)",fontSize:13,margin:"0 0 6px"}}>
                          Em qual domingo <strong>{empAObj?.name}</strong> folgaria — e <strong>{empBObj?.name}</strong> vai cobrir?
                        </p>
                        <p style={{color:"var(--text3)",fontSize:11,margin:"0 0 12px"}}>Folgas de domingo do {shortName(empAObj?.name)} (mês anterior, atual e próximo).</p>
                        {reciprocCandidates.length > 0 ? (
                          (() => {
                            // Agrupa por mês pra ficar mais claro
                            const grouped = {};
                            reciprocCandidates.forEach(c => {
                              if (!grouped[c.monthLabel]) grouped[c.monthLabel] = [];
                              grouped[c.monthLabel].push(c);
                            });
                            return (
                              <div style={{display:"flex",flexDirection:"column",gap:14,maxHeight:380,overflow:"auto"}}>
                                {["Mês anterior","Este mês","Próximo mês"].map(label => {
                                  const items = grouped[label];
                                  if (!items || items.length === 0) return null;
                                  return (
                                    <div key={label}>
                                      <div style={{fontSize:10,fontWeight:700,color:"var(--text3)",letterSpacing:0.6,textTransform:"uppercase",marginBottom:6}}>{label}</div>
                                      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:8}}>
                                        {items.map(c => {
                                          const d = new Date(c.date+"T12:00:00");
                                          const day = d.getDate();
                                          const monShort = d.toLocaleDateString("pt-BR",{month:"short"}).replace(".","");
                                          return (
                                            <button key={c.date} onClick={()=>setSwapModalContext({...ctx,date2:c.date})}
                                              style={{padding:"12px 8px",borderRadius:10,border:`1px solid ${c.perfect?ac+"66":"var(--border)"}`,background:c.perfect?ac+"08":"var(--bg2)",color:"var(--text)",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:13,fontWeight:600,textAlign:"center",transition:"all 0.15s"}}
                                              onMouseEnter={e=>{e.currentTarget.style.background=ac+"22";e.currentTarget.style.borderColor=ac;}}
                                              onMouseLeave={e=>{e.currentTarget.style.background=c.perfect?ac+"08":"var(--bg2)";e.currentTarget.style.borderColor=c.perfect?ac+"66":"var(--border)";}}>
                                              <div style={{fontSize:18,fontWeight:800,color:c.perfect?ac:"var(--text2)"}}>{String(day).padStart(2,"0")}/{monShort}</div>
                                              <div style={{fontSize:9,color:"var(--text3)",marginTop:2}}>
                                                {c.perfect ? `A folga · B trab ✓` : "A folga"}
                                              </div>
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()
                        ) : (
                          <div style={{padding:"20px",textAlign:"center",color:"var(--text3)",fontSize:12,background:"var(--bg2)",borderRadius:10,marginBottom:12}}>
                            {empAObj?.name} não tem folga marcada em nenhum domingo nos 3 meses (anterior, atual, próximo).
                            <br/><span style={{fontSize:11,marginTop:8,display:"block"}}>Marque a folga na escala ou cadastre o ciclo de domingo no horário e volte aqui.</span>
                          </div>
                        )}
                        <button onClick={()=>setSwapModalContext({...ctx,empAId:""})} style={{...S.btnSecondary,fontSize:11,padding:"6px 12px",marginTop:12}}>← Voltar</button>
                      </div>
                    )}

                    {/* STEP 5: confirmação */}
                    {step === 5 && (
                      <div>
                        <div style={{padding:"14px 16px",background:ac+"15",border:`1px solid ${ac}55`,borderRadius:10,marginBottom:12}}>
                          <div style={{fontSize:13,color:"var(--text)",lineHeight:1.7,marginBottom:8}}>
                            <div><strong>Domingo {date1Label}:</strong> {empAObj?.name} aparece trabalhando, {empBObj?.name} aparece de folga — mas {empBObj?.name} cobriu.</div>
                            <div style={{marginTop:6}}><strong>Domingo {date2Label}:</strong> {empBObj?.name} aparece trabalhando, {empAObj?.name} aparece de folga — mas {empAObj?.name} cobriu.</div>
                          </div>
                          <div style={{fontSize:11,color:"var(--text3)",marginTop:6,lineHeight:1.5,paddingTop:8,borderTop:`1px dashed ${ac}55`}}>
                            Escala, gorjeta e VT seguem o original. Só fica o registro nos 4 domingos (badge ↔).
                          </div>
                        </div>
                        <div style={{marginBottom:12}}>
                          <label style={{...S.label,fontSize:11}}>Observação (opcional)</label>
                          <input value={ctx.note || ""} onChange={e=>setSwapModalContext({...ctx,note:e.target.value})} placeholder="ex: aniversário do amigo" style={S.input} />
                        </div>
                        <div style={{display:"flex",justifyContent:"space-between",gap:10}}>
                          <button onClick={()=>setSwapModalContext({...ctx,date2:""})} style={{...S.btnSecondary,fontSize:12,padding:"8px 14px"}}>← Voltar</button>
                          <button onClick={()=>{
                            const newSwap = {
                              id: `swap-${Date.now()}-${Math.random().toString(36).slice(2,5)}`,
                              restaurantId: rid,
                              date1: ctx.date1,
                              date2: ctx.date2,
                              empAId: ctx.empAId,
                              empBId: ctx.empBId,
                              note: ctx.note?.trim() || "",
                              registeredAt: new Date().toISOString(),
                              registeredBy: currentUser?.name || "—",
                            };
                            onUpdate("scheduleSwaps", [...(data?.scheduleSwaps ?? []), newSwap]);
                            setSwapModalContext(null);
                            onUpdate("_toast", `↔️ Inversão registrada: ${empAObj?.name} ↔ ${empBObj?.name} (${date1Label} ↔ ${date2Label})`);
                          }} style={{...S.btnPrimary,fontSize:13,padding:"8px 16px",fontWeight:700}}>✓ Registrar inversão</button>
                        </div>
                      </div>
                    )}

                    <div style={{marginTop:14,paddingTop:12,borderTop:"1px solid var(--border)",display:"flex",justifyContent:"flex-end"}}>
                      <button onClick={()=>setSwapModalContext(null)} style={{...S.btnSecondary,fontSize:11,padding:"5px 12px",color:"var(--text3)"}}>Fechar</button>
                    </div>
                  </div>
                </div>
              );
            })()}
            {/* Pending edits banner */}
            {schedDirty && (
              <div style={{background:"#f59e0b15",border:"1px solid #f59e0b44",borderRadius:10,padding:mobileOnly?"10px 12px":"12px 16px",marginBottom:12,display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:16}}>✏️</span>
                  <span style={{color:"#f59e0b",fontSize:mobileOnly?12:13,fontWeight:600}}>Edições pendentes</span>
                  <span style={{color:"var(--text3)",fontSize:mobileOnly?10:11}}>— salve para criar nova versão</span>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>{
                    // Freeze prevista on first adjustment
                    if (!data?.schedulePrevista?.[rid]?.[mk]) {
                      const frozenPrevista = JSON.parse(JSON.stringify(schedules?.[rid]?.[mk] ?? {}));
                      const newPrev = { ...(data?.schedulePrevista ?? {}) };
                      if (!newPrev[rid]) newPrev[rid] = {};
                      newPrev[rid][mk] = frozenPrevista;
                      onUpdate("schedulePrevista", newPrev);
                    }
                    // Save as new version
                    const preSnap = snapshotSchedulesMonth(schedules, rid, mk);
                    saveSchedulesSnapshot("Edição manual", preSnap);
                    // Record adjustments for Phase 2
                    const adjAuthor = currentUser?.name || (isOwner?"Master":"Gestor Adm.");
                    const adjTimestamp = new Date().toISOString();
                    const newAdjs = [];
                    Object.entries(schedLocalEdits).forEach(([eid, dayEdits]) => {
                      Object.entries(dayEdits).forEach(([dt, val]) => {
                        const fromVal = schedules?.[rid]?.[mk]?.[eid]?.[dt] ?? "";
                        newAdjs.push({ id: `${Date.now()}-${Math.random().toString(36).slice(2,6)}`, empId: eid, date: dt, from: fromVal, to: val ?? "", author: adjAuthor, timestamp: adjTimestamp });
                      });
                    });
                    if (newAdjs.length > 0) {
                      const curAdjs = { ...(data?.scheduleAdjustments ?? {}) };
                      if (!curAdjs[rid]) curAdjs[rid] = {};
                      curAdjs[rid][mk] = [...(curAdjs[rid][mk] ?? []), ...newAdjs];
                      onUpdate("scheduleAdjustments", curAdjs);
                    }
                    // Apply local edits to schedules
                    let newMonth = { ...(schedules?.[rid]?.[mk] ?? {}) };
                    Object.entries(schedLocalEdits).forEach(([eid, dayEdits]) => {
                      const empMap = { ...(newMonth[eid] ?? {}) };
                      Object.entries(dayEdits).forEach(([dt, val]) => {
                        if (val === null) delete empMap[dt]; else empMap[dt] = val;
                      });
                      newMonth[eid] = empMap;
                    });
                    onUpdate("schedules", {
                      ...schedules,
                      [rid]: { ...(schedules?.[rid]??{}), [mk]: newMonth }
                    });
                        setSchedLocalEdits(null);
                    onUpdate("_toast", "✅ Escala salva como nova versão");
                  }} style={{...S.btnPrimary,fontSize:mobileOnly?11:12,padding:mobileOnly?"8px 14px":"8px 18px",fontWeight:700}}>
                    💾 {mobileOnly?"Salvar":"Salvar nova versão"}
                  </button>
                  <button onClick={async ()=>{
                    if (await appConfirm("Descartar todas as edições pendentes?")) setSchedLocalEdits(null);
                  }} style={{...S.btnSecondary,fontSize:mobileOnly?11:12,padding:mobileOnly?"8px 10px":"8px 14px"}}>
                    Descartar
                  </button>
                </div>
              </div>
            )}
            {/* Area filter */}
            <div style={{marginBottom:12}}>
              <PillBar options={["Todos", ...AREAS]} value={schedArea} onChange={setSchedArea}/>
            </div>
            <div style={{display:"flex",gap:mobileOnly?4:8,flexWrap:"wrap",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
              <details open={!mobileOnly} style={{flex:mobileOnly?"1 1 100%":"0 1 auto",minWidth:0}}>
                <summary style={{
                  display: mobileOnly ? "flex" : "none",
                  alignItems:"center", gap:8, padding:"10px 14px",
                  background:"var(--bg2)", borderRadius:10,
                  fontSize:13, color:"var(--text)", fontWeight:600,
                  minHeight:44, border:"1px solid var(--border)",
                  cursor:"pointer", listStyle:"none", marginBottom:8,
                }}>
                  <span>⚙️ Ações da escala</span>
                  <span style={{marginLeft:"auto",fontSize:10,color:"var(--text3)"}}>tocar pra expandir</span>
                </summary>
                <div style={{display:"flex",gap:mobileOnly?4:8,flexWrap:"wrap"}}>

                {/* v8.12.0: "📅 Folgas do contrato" REMOVIDO — escala deriva auto. */}
                {/* v8.12.0: Resetar escala — substitui dados salvos pela derivação (folgas do contrato + ciclo de domingos) */}
                {(canSchedEdit || isOwner) && !monthClosed && (
                  <button onClick={()=>setShowResetSchedConfirm(true)} style={{...S.btnSecondary,fontSize:mobileOnly?11:12,color:"var(--red)",borderColor:"var(--red)44",whiteSpace:"nowrap"}} title="Substitui a escala atual pela escala derivada do horário cadastrado + ciclo de domingos">
                    {mobileOnly?"🔄 Reset":"🔄 Resetar escala"}
                  </button>
                )}

                {/* Marcar férias */}
                {(canSchedEdit || isOwner) && (
                  <button onClick={()=>{setShowVacForm(!showVacForm);setVacEmpId("");setVacFrom("");setVacTo("");}}
                    style={{...S.btnSecondary,fontSize:mobileOnly?11:12,border:`1px solid ${showVacForm?"#8b5cf6":"#8b5cf644"}`,background:showVacForm?"#8b5cf622":"transparent",color:"#8b5cf6",whiteSpace:"nowrap"}}>
                    {mobileOnly?"🏖️ Férias":"🏖️ Marcar férias"}
                  </button>
                )}

                {/* Botão "📄 Importar ponto" REMOVIDO em abril/2026 */}

                {/* Registrar atrasos */}
                {(canSchedEdit || isOwner) && (
                  <button onClick={()=>{setShowDelayForm(!showDelayForm);setDelayEmpId("");setDelayInputs({});}}
                    style={{...S.btnSecondary,fontSize:mobileOnly?11:12,border:`1px solid ${showDelayForm?"#f59e0b":"#f59e0b44"}`,background:showDelayForm?"#f59e0b22":"transparent",color:"#f59e0b",whiteSpace:"nowrap"}}>
                    {mobileOnly?"⏰ Atrasos":"⏰ Registrar atrasos"}
                  </button>
                )}

                {/* "Histórico" movido pra "Mais ações" no rodapé */}

                {/* Fechar / Reabrir mês — v8.17.0: quem tem perm fecharEscala (Owner sempre) */}
                {(isOwner || canFecharEscala) && !monthClosed && (
                  <button onClick={async ()=>{
                    const hasPonto = !!(data?.scheduleStatus?.[rid]?.[mk]?.lastPontoImport);
                    if (!hasPonto && !await appConfirm("Nenhum ponto importado para este mes. Deseja fechar mesmo assim?")) return;
                    // Phase 4: compute delta and show confirm modal
                    const curSched = effectiveMonth;
                    const prevSched = schedules?.[rid]?.[mk] ?? {};
                    const daysInM = new Date(year, month + 1, 0).getDate();
                    const delta = {};
                    schedEmps.forEach(emp => {
                      let prevWork = 0, curWork = 0;
                      for (let d = 1; d <= daysInM; d++) {
                        const dt = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
                        const isDem = emp.demitidoEm && dt >= emp.demitidoEm;
                        if (isDem) continue;
                        const ps = prevSched[emp.id]?.[dt];
                        const cs = curSched[emp.id]?.[dt];
                        if (!ps || ps === "comptrab") prevWork++;
                        if (!cs || cs === "comptrab") curWork++;
                      }
                      if (prevWork !== curWork) {
                        delta[emp.id] = { name: emp.name, prevWork, curWork, diff: curWork - prevWork };
                      }
                    });
                    // Compute VT impact
                    const vtPayment = data?.vtPayments?.[rid]?.[mk];
                    const vtImpact = {};
                    if (vtPayment?.snapshot) {
                      const daysInM2 = new Date(year, month + 1, 0).getDate();
                      vtPayment.snapshot.forEach(snap => {
                        let actualDays = 0;
                        for (let d2 = 1; d2 <= daysInM2; d2++) {
                          const dt2 = `${year}-${String(month+1).padStart(2,"0")}-${String(d2).padStart(2,"0")}`;
                          const emp2 = schedEmps.find(e => e.id === snap.empId);
                          if (emp2?.demitidoEm && dt2 >= emp2.demitidoEm) continue;
                          const cs2 = effectiveMonth[snap.empId]?.[dt2];
                          if (!cs2 || cs2 === "comptrab") actualDays++;
                        }
                        if (actualDays !== snap.plannedDays) {
                          vtImpact[snap.empId] = { name: snap.name, plannedDays: snap.plannedDays, actualDays, diff: actualDays - snap.plannedDays, dailyRate: snap.dailyRate, adjustValue: parseFloat(((actualDays - snap.plannedDays) * snap.dailyRate).toFixed(2)) };
                        }
                      });
                    }
                    setCloseVtImpact(Object.keys(vtImpact).length > 0 ? vtImpact : null);
                    if (Object.keys(delta).length > 0 || Object.keys(vtImpact).length > 0) {
                      setCloseDelta(Object.keys(delta).length > 0 ? delta : null);
                      setShowCloseConfirm(true);
                    } else {
                      // No delta, close directly
                      const newStatus = { ...(data?.scheduleStatus ?? {}) };
                      if (!newStatus[rid]) newStatus[rid] = {};
                      newStatus[rid][mk] = { ...(newStatus[rid][mk] ?? {}), status: "closed", closedAt: new Date().toISOString(), closedBy: currentUser?.name || "Master" };
                      onUpdate("scheduleStatus", newStatus);
                      onUpdate("_toast", "Mes fechado com sucesso");
                    }
                  }}
                    style={{...S.btnSecondary,fontSize:mobileOnly?11:12,color:"var(--green)",borderColor:"var(--green)44",whiteSpace:"nowrap"}}>
                    {mobileOnly ? "Fechar" : "Fechar Mes"}
                  </button>
                )}
                {monthClosed && (isOwner || canReabrirEscala) && (
                  <button onClick={()=>setShowReopenConfirm(true)}
                    style={{...S.btnSecondary,fontSize:mobileOnly?11:12,color:"#f59e0b",borderColor:"#f59e0b44",whiteSpace:"nowrap"}}>
                    Reabrir
                  </button>
                )}
              </div>
              </details>

              {/* v8.12.0: Modal de confirmação do reset da escala */}
              {showResetSchedConfirm && (() => {
                // Pré-cálculo: quantos empregados serão afetados e o que vai mudar
                let totalEmps = 0;
                let totalFolgasNova = 0;
                let totalFolgasAntiga = 0;
                schedEmps.forEach(emp => {
                  const saved = schedules?.[rid]?.[mk]?.[emp.id] ?? {};
                  const derived = derivedScheduleByEmp[emp.id] ?? {};
                  const savedKeys = Object.keys(saved).length;
                  const derivedKeys = Object.keys(derived).length;
                  if (savedKeys > 0 || derivedKeys > 0) {
                    totalEmps++;
                    totalFolgasAntiga += Object.values(saved).filter(s => s === DAY_OFF).length;
                    totalFolgasNova += Object.values(derived).filter(s => s === DAY_OFF).length;
                  }
                });
                return (
                  <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={()=>setShowResetSchedConfirm(false)}>
                    <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:14,maxWidth:520,width:"100%",padding:24,boxShadow:"0 20px 50px rgba(0,0,0,0.3)"}}>
                      <h3 style={{margin:"0 0 8px",color:"var(--red)",fontSize:16,fontWeight:700}}>🔄 Resetar escala de {monthLabel(year, month)}?</h3>
                      <p style={{color:"var(--text2)",fontSize:13,margin:"0 0 12px",lineHeight:1.6}}>
                        Vai <strong>apagar todas as marcações atuais</strong> deste mês (folgas, freelas, compensações, faltas, férias) e substituir pela escala derivada do <strong>horário cadastrado</strong> de cada empregado + <strong>ciclo de domingos</strong>.
                      </p>
                      <div style={{padding:"10px 14px",background:"var(--bg2)",borderRadius:10,fontSize:12,color:"var(--text2)",lineHeight:1.6,marginBottom:14}}>
                        Empregados na grade: <strong>{totalEmps}</strong><br/>
                        Folgas na escala atual: <strong>{totalFolgasAntiga}</strong><br/>
                        Folgas após o reset (derivadas): <strong>{totalFolgasNova}</strong>
                      </div>
                      <p style={{color:"var(--text3)",fontSize:11,margin:"0 0 14px",fontStyle:"italic"}}>
                        ⚠️ A versão anterior fica no histórico. Empregados sem ciclo de domingo cadastrado vão trabalhar todos os domingos.
                      </p>
                      <div style={{display:"flex",justifyContent:"flex-end",gap:10}}>
                        <button onClick={()=>setShowResetSchedConfirm(false)} style={{...S.btnSecondary,padding:"8px 14px",fontSize:13}}>Cancelar</button>
                        <button onClick={()=>{
                          // Snapshot antes
                          const preSnap = snapshotSchedulesMonth(schedules, rid, mk);
                          saveSchedulesSnapshot("Reset pra contrato + ciclo", preSnap);
                          // Substitui pela derivação
                          const newMonth = {};
                          schedEmps.forEach(emp => {
                            const derived = derivedScheduleByEmp[emp.id];
                            if (derived && Object.keys(derived).length > 0) {
                              newMonth[emp.id] = { ...derived };
                            }
                          });
                          onUpdate("schedules", { ...schedules, [rid]: { ...(schedules?.[rid]??{}), [mk]: newMonth } });
                          setSchedLocalEdits(null);
                          setShowResetSchedConfirm(false);
                          onUpdate("_toast", "🔄 Escala resetada para o contrato + ciclo");
                        }} style={{...S.btnPrimary,padding:"8px 14px",fontSize:13,fontWeight:700,background:"var(--red)",borderColor:"var(--red)"}}>
                          Resetar escala
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Modal: Inverter dia inteiro (feriados) */}
              {invertDayForm && (() => {
                const { dateA, dateB, reason } = invertDayForm;
                const sameMonth = (d) => d && d.slice(0,7) === mk;
                const valid = dateA && dateB && dateA !== dateB && sameMonth(dateA) && sameMonth(dateB);
                // Preview: empregados afetados (preserva férias)
                let affected = 0; let preserved = 0;
                if (valid) {
                  schedEmps.forEach(emp => {
                    const cur = effectiveMonth[emp.id] ?? {};
                    const sa = cur[dateA] ?? null;
                    const sb = cur[dateB] ?? null;
                    if (sa === DAY_VACATION || sb === DAY_VACATION) preserved++;
                    else affected++;
                  });
                }
                return (
                  <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={()=>setInvertDayForm(null)}>
                    <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:14,maxWidth:520,width:"100%",padding:24,boxShadow:"0 20px 50px rgba(0,0,0,0.3)"}}>
                      <h3 style={{margin:"0 0 8px",color:"#7c3aed",fontSize:16,fontWeight:700}}>↔️ Inverter dia (feriados)</h3>
                      <p style={{color:"var(--text2)",fontSize:12,margin:"0 0 14px",lineHeight:1.5}}>
                        Troca o status (trabalho/folga/etc.) entre dois dias do mês para todos os empregados. Útil quando um feriado muda o dia de fechamento da casa.
                        <br/><span style={{color:"var(--text3)"}}>Empregados de férias num dos dois dias são preservados.</span>
                      </p>
                      {(() => {
                        const monthMin = `${year}-${String(month+1).padStart(2,"0")}-01`;
                        const monthMax = `${year}-${String(month+1).padStart(2,"0")}-${String(new Date(year, month+1, 0).getDate()).padStart(2,"0")}`;
                        return (
                          <div style={{display:"flex",gap:10,marginBottom:12}}>
                            <div style={{flex:1}}>
                              <label style={{...S.label,fontSize:11}}>Dia A</label>
                              <input type="date" min={monthMin} max={monthMax} value={dateA} onChange={e=>setInvertDayForm({...invertDayForm, dateA:e.target.value})} style={S.input}/>
                            </div>
                            <div style={{flex:0,alignSelf:"flex-end",padding:"10px 4px",fontSize:18,color:"#7c3aed"}}>↔</div>
                            <div style={{flex:1}}>
                              <label style={{...S.label,fontSize:11}}>Dia B</label>
                              <input type="date" min={monthMin} max={monthMax} value={dateB} onChange={e=>setInvertDayForm({...invertDayForm, dateB:e.target.value})} style={S.input}/>
                            </div>
                          </div>
                        );
                      })()}
                      <div style={{marginBottom:12}}>
                        <label style={{...S.label,fontSize:11}}>Motivo (opcional)</label>
                        <input value={reason} onChange={e=>setInvertDayForm({...invertDayForm, reason:e.target.value})} placeholder="ex: Feriado 7 de Setembro — fechar 8/9, abrir 7" style={S.input}/>
                      </div>
                      {dateA && !sameMonth(dateA) && <div style={{padding:"8px 10px",background:"#ef444411",border:"1px solid #ef444433",borderRadius:8,fontSize:11,color:"#ef4444",marginBottom:8}}>Dia A precisa estar em {monthLabel(year,month)}.</div>}
                      {dateB && !sameMonth(dateB) && <div style={{padding:"8px 10px",background:"#ef444411",border:"1px solid #ef444433",borderRadius:8,fontSize:11,color:"#ef4444",marginBottom:8}}>Dia B precisa estar em {monthLabel(year,month)}.</div>}
                      {dateA && dateB && dateA === dateB && <div style={{padding:"8px 10px",background:"#ef444411",border:"1px solid #ef444433",borderRadius:8,fontSize:11,color:"#ef4444",marginBottom:8}}>Os dois dias precisam ser diferentes.</div>}
                      {valid && (
                        <div style={{padding:"10px 12px",background:"var(--bg2)",borderRadius:8,fontSize:12,color:"var(--text2)",lineHeight:1.6,marginBottom:14}}>
                          <strong>{affected}</strong> empregado(s) terão o status invertido entre {new Date(dateA+"T12:00:00").toLocaleDateString("pt-BR")} e {new Date(dateB+"T12:00:00").toLocaleDateString("pt-BR")}.
                          {preserved > 0 && <><br/><span style={{color:"var(--text3)"}}><strong>{preserved}</strong> de férias serão preservados.</span></>}
                        </div>
                      )}
                      <div style={{display:"flex",justifyContent:"flex-end",gap:10}}>
                        <button onClick={()=>setInvertDayForm(null)} style={{...S.btnSecondary,padding:"8px 14px",fontSize:13}}>Cancelar</button>
                        <button onClick={()=>{
                          if (!valid) return;
                          // Snapshot pra histórico
                          const preSnap = snapshotSchedulesMonth(schedules, rid, mk);
                          saveSchedulesSnapshot(`Inversão ${new Date(dateA+"T12:00:00").toLocaleDateString("pt-BR")} ↔ ${new Date(dateB+"T12:00:00").toLocaleDateString("pt-BR")}${reason?` — ${reason}`:""}`, preSnap);
                          // Aplica swap usando effectiveMonth como fonte (saved + derivado + edits locais).
                          // IMPORTANTE: materializa o effectiveMonth INTEIRO de cada empregado afetado.
                          // Sem isso, escala derivada (sem dados salvos) viraria saved só com 2 dias e
                          // applyDerivationToEmp deixaria de derivar — perdendo folgas dos outros dias.
                          const newMonth = JSON.parse(JSON.stringify(schedules?.[rid]?.[mk] ?? {}));
                          // Audit trail
                          const adjAuthor = currentUser?.name || (isOwner?"Master":"Gestor Adm.");
                          const adjTimestamp = new Date().toISOString();
                          const newAdjs = [];
                          schedEmps.forEach(emp => {
                            const cur = effectiveMonth[emp.id] ?? {};
                            const sa = cur[dateA] ?? null;
                            const sb = cur[dateB] ?? null;
                            if (sa === DAY_VACATION || sb === DAY_VACATION) return;
                            // Materializa o effective do empregado (sem ainda aplicar swap)
                            newMonth[emp.id] = { ...cur };
                            // dateA recebe o status que estava em dateB
                            if (sb === null) delete newMonth[emp.id][dateA];
                            else newMonth[emp.id][dateA] = sb;
                            // dateB recebe o status que estava em dateA
                            if (sa === null) delete newMonth[emp.id][dateB];
                            else newMonth[emp.id][dateB] = sa;
                            // Audit
                            newAdjs.push({ id: `${Date.now()}-${Math.random().toString(36).slice(2,6)}-a`, empId: emp.id, date: dateA, from: sa ?? "", to: sb ?? "", author: adjAuthor, timestamp: adjTimestamp, reason: `Inversão${reason?`: ${reason}`:""}` });
                            newAdjs.push({ id: `${Date.now()}-${Math.random().toString(36).slice(2,6)}-b`, empId: emp.id, date: dateB, from: sb ?? "", to: sa ?? "", author: adjAuthor, timestamp: adjTimestamp, reason: `Inversão${reason?`: ${reason}`:""}` });
                          });
                          if (newAdjs.length > 0) {
                            const curAdjs = { ...(data?.scheduleAdjustments ?? {}) };
                            if (!curAdjs[rid]) curAdjs[rid] = {};
                            curAdjs[rid][mk] = [...(curAdjs[rid][mk] ?? []), ...newAdjs];
                            onUpdate("scheduleAdjustments", curAdjs);
                          }
                          // Registra a inversão pra exibir badge nas células e tooltip do motivo
                          const inversionRecord = {
                            id: `inv-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
                            restaurantId: rid,
                            dateA, dateB,
                            reason: reason || "",
                            createdAt: adjTimestamp,
                            createdBy: adjAuthor,
                          };
                          onUpdate("dayInversions", [...(data?.dayInversions ?? []), inversionRecord]);
                          onUpdate("schedules", { ...schedules, [rid]: { ...(schedules?.[rid]??{}), [mk]: newMonth } });
                          setSchedLocalEdits(null);
                          setInvertDayForm(null);
                          onUpdate("_toast", `↔️ ${affected} empregado(s) tiveram a escala invertida${preserved>0?` (${preserved} de férias preservados)`:""}`);
                        }} disabled={!valid}
                          style={{...S.btnPrimary,padding:"8px 14px",fontSize:13,fontWeight:700,background:"#7c3aed",borderColor:"#7c3aed",opacity:valid?1:0.5}}>
                          Inverter
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Month close confirmation modal — Phase 4 */}
              {showCloseConfirm && (
                <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={()=>setShowCloseConfirm(false)}>
                  <div onClick={e=>e.stopPropagation()} style={{background:"var(--bg2)",borderRadius:16,padding:24,maxWidth:520,width:"100%",maxHeight:"80vh",overflowY:"auto",border:"1px solid var(--border)"}}>
                    <h3 style={{color:"var(--text)",margin:"0 0 12px",fontSize:16}}>Confirmar fechamento do mes</h3>
                    <p style={{color:"var(--text2)",fontSize:13,margin:"0 0 12px"}}>Foram detectadas diferencas entre a escala prevista e a vigente:</p>
                    <div style={{maxHeight:300,overflowY:"auto",marginBottom:16}}>
                      {closeDelta && Object.entries(closeDelta).map(([empId, d]) => (
                        <div key={empId} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 12px",borderRadius:8,background:"var(--bg1)",marginBottom:4,border:"1px solid var(--border)"}}>
                          <span style={{color:"var(--text)",fontSize:13,fontWeight:600}}>{d.name}</span>
                          <div style={{display:"flex",gap:12,alignItems:"center"}}>
                            <span style={{color:"var(--text3)",fontSize:12}}>Antes: {d.prevWork}d</span>
                            <span style={{color:"var(--text3)",fontSize:10}}>-&gt;</span>
                            <span style={{color:d.diff>0?"var(--green)":"var(--red)",fontSize:12,fontWeight:700}}>Depois: {d.curWork}d ({d.diff>0?"+":""}{d.diff})</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p style={{color:"#f59e0b",fontSize:12,margin:"0 0 16px"}}>As gorjetas serao recalculadas com base na escala vigente.</p>
                    {closeVtImpact && Object.keys(closeVtImpact).length > 0 && (
                      <div style={{marginTop:12}}>
                        <p style={{color:"var(--text)",fontSize:13,fontWeight:700,marginBottom:8}}>Impacto no VT</p>
                        {Object.entries(closeVtImpact).map(([empId, v]) => (
                          <div key={empId} style={{display:"flex",justifyContent:"space-between",padding:"6px 12px",borderRadius:8,background:v.adjustValue>0?"#10b98109":"#e74c3c09",border:`1px solid ${v.adjustValue>0?"#10b98133":"#e74c3c33"}`,marginBottom:4}}>
                            <span style={{color:"var(--text)",fontSize:12}}>{v.name}</span>
                            <span style={{fontSize:12,color:v.adjustValue>0?"var(--green)":"var(--red)",fontWeight:700}}>
                              {v.plannedDays}d pago {"\u2192"} {v.actualDays}d real = {v.adjustValue>0?"+":""}R$ {Math.abs(v.adjustValue).toFixed(2).replace(".",",")}
                            </span>
                          </div>
                        ))}
                        <p style={{color:"var(--text3)",fontSize:11,marginTop:6}}>Esses ajustes serão aplicados no próximo ciclo de VT.</p>
                      </div>
                    )}
                    <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                      <button onClick={()=>setShowCloseConfirm(false)} style={S.btnSecondary}>Cancelar</button>
                      <button onClick={()=>{
                        // Close the month and persist
                        const newStatus = { ...(data?.scheduleStatus ?? {}) };
                        if (!newStatus[rid]) newStatus[rid] = {};
                        newStatus[rid][mk] = { ...(newStatus[rid][mk] ?? {}), status: "closed", closedAt: new Date().toISOString(), closedBy: currentUser?.name || "Master" };
                        onUpdate("scheduleStatus", newStatus);
                        // Save effective schedule as the final schedule
                        const finalMonth = { ...effectiveMonth };
                        onUpdate("schedules", { ...schedules, [rid]: { ...(schedules?.[rid] ?? {}), [mk]: finalMonth } });
                        setSchedLocalEdits(null);
                        setShowCloseConfirm(false);
                        setCloseDelta(null);
                        setCloseVtImpact(null);
                        onUpdate("_toast", "Mes fechado e gorjetas ajustadas");
                      }} style={{...S.btnPrimary,fontSize:13}}>
                        Confirmar e fechar
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Reopen confirmation modal */}
              {showReopenConfirm && (
                <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={()=>setShowReopenConfirm(false)}>
                  <div onClick={e=>e.stopPropagation()} style={{background:"var(--bg2)",borderRadius:16,padding:24,maxWidth:480,width:"100%",border:"1px solid #f59e0b44"}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
                      <span style={{fontSize:24}}>⚠️</span>
                      <h3 style={{color:"#f59e0b",margin:0,fontSize:16}}>Reabrir escala fechada</h3>
                    </div>
                    <p style={{color:"var(--text2)",fontSize:13,margin:"0 0 14px",lineHeight:1.6}}>
                      Ao reabrir a escala de <strong>{monthLabel(year, month)}</strong>, as edições voltarão a ser permitidas. Considere as seguintes consequências:
                    </p>
                    <div style={{background:"#f59e0b08",border:"1px solid #f59e0b22",borderRadius:10,padding:14,marginBottom:16}}>
                      <div style={{fontSize:12,color:"var(--text2)",lineHeight:1.8}}>
                        <div style={{marginBottom:8,display:"flex",gap:8}}><span>📊</span><span><strong>Gorjetas não serão recalculadas automaticamente.</strong> Se a escala for alterada, será necessário recalcular manualmente na aba Gorjeta.</span></div>
                        <div style={{marginBottom:8,display:"flex",gap:8}}><span>🚌</span><span><strong>Ajustes de VT já foram processados.</strong> Alterações na escala não desfazem ajustes de vale-transporte já aplicados ao fechar.</span></div>
                        <div style={{display:"flex",gap:8}}><span>👁️</span><span><strong>Semanas já confirmadas continuam visíveis</strong> para empregados que têm acesso à gorjeta.</span></div>
                      </div>
                    </div>
                    <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                      <button onClick={()=>setShowReopenConfirm(false)} style={S.btnSecondary}>Cancelar</button>
                      <button onClick={()=>{
                        const newStatus = { ...(data?.scheduleStatus ?? {}) };
                        if (!newStatus[rid]) newStatus[rid] = {};
                        newStatus[rid][mk] = { ...(newStatus[rid][mk] ?? {}), status: "open", closedAt: null, closedBy: null };
                        onUpdate("scheduleStatus", newStatus);
                        setShowReopenConfirm(false);
                        onUpdate("_toast", "Mês reaberto com sucesso");
                      }} style={{...S.btnPrimary,fontSize:13,background:"#f59e0b",borderColor:"#f59e0b"}}>
                        Reabrir mês
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Modal de histórico da escala */}
              {showSchedHistory && (
                <VersionHistoryModal
                  title={`🕐 Histórico da Escala — ${monthLabel(year, month)}`}
                  versions={data?.scheduleVersions?.[rid]?.[mk]}
                  onClose={()=>setShowSchedHistory(false)}
                  onRestore={(v)=>{
                    // Salva o estado atual como nova versão (pra poder desfazer o restore)
                    const curSnap = snapshotSchedulesMonth(schedules, rid, mk);
                    saveSchedulesSnapshot(`Antes de restaurar "${v.reason}"`, curSnap);
                    // Aplica a versão restaurada
                    const newSched = JSON.parse(JSON.stringify(schedules ?? {}));
                    if (!newSched[rid]) newSched[rid] = {};
                    newSched[rid][mk] = JSON.parse(JSON.stringify(v.snapshot ?? {}));
                    onUpdate("schedules", newSched);
                    setSchedLocalEdits(null);
                    setShowSchedHistory(false);
                    onUpdate("_toast", `♻️ Escala restaurada para "${v.reason}" (${fmtRelTime(v.ts)})`);
                  }}
                />
              )}

              {/* PDF export — à direita (desktop only) */}
              {!mobileOnly && <button onClick={async () => {
                  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
                  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js");
                  const { jsPDF } = window.jspdf;
                  const doc = new jsPDF({ orientation:"landscape", unit:"mm", format:"a4" });
                  const daysInMonth = new Date(year, month+1, 0).getDate();
                  const PDF_STATUS_COLORS = {
                    work: [39,174,96],
                    off:  [231,76,60],
                    freela:[6,182,212],
                    comp: [59,130,246],
                    comptrab:[14,165,233],
                    vac:  [139,92,246],
                    faultj:[245,158,11],
                    faultu:[180,30,30],
                  };

                  doc.setFontSize(11);
                  doc.setTextColor(30,30,30);
                  doc.text(`Escala — ${schedArea} — ${monthLabel(year,month)} — ${restaurant.name}`, 14, 12);

                  const legend = [
                    ["T  Trabalho", PDF_STATUS_COLORS.work],
                    ["F  Folga", PDF_STATUS_COLORS.off],
                    ["FL  Freela", PDF_STATUS_COLORS.freela],
                    ["FC  Folga Comp.", PDF_STATUS_COLORS.comp],
                    ["TC  Trab. Comp.", PDF_STATUS_COLORS.comptrab],
                    ["FÉR  Férias", PDF_STATUS_COLORS.vac],
                    ["FJ  Falta Just.", PDF_STATUS_COLORS.faultj],
                    ["FI  Falta Injust.", PDF_STATUS_COLORS.faultu],
                  ];
                  let lx = 14;
                  legend.forEach(([lbl, col]) => {
                    doc.setFillColor(...col);
                    doc.rect(lx, 15, 3, 3, "F");
                    doc.setFontSize(6);
                    doc.setTextColor(30,30,30);
                    doc.text(lbl, lx+4, 17.5);
                    lx += 38;
                  });

                  const head = [["Empregado", ...Array.from({length:daysInMonth},(_,i)=>String(i+1)), "T"]];
                  const areasToExport = schedArea === "Todos" ? AREAS.filter(a => areaEmps.some(e => { const r = restRoles.find(x=>x.id===e.roleId); return r?.area === a; })) : [schedArea];

                  function buildAreaBody(empsForArea) {
                    return empsForArea.map(emp => {
                      const role = restRoles.find(r=>r.id===emp.roleId);
                      const dayMap = effectiveMonth[emp.id] ?? {};
                      let workDays = 0;
                      const dayCells = Array.from({length:daysInMonth},(_,i)=>{
                        const k = `${year}-${String(month+1).padStart(2,"0")}-${String(i+1).padStart(2,"0")}`;
                        const s = dayMap[k];
                        if(!s) { workDays++; return "T"; }
                        return STATUS_SHORT[s] ?? "";
                      });
                      return [`${emp.name}\n${role?.name??""}`, ...dayCells, String(workDays)];
                    });
                  }

                  const drawTable = (body, startY, empsForArea) => {
                    doc.autoTable({
                      head, body,
                      startY,
                      styles: { fontSize: 6, cellPadding: 1.2, halign:"center", textColor:[255,255,255], lineColor:[180,180,180], lineWidth:0.1 },
                      headStyles: { fillColor:[40,40,40], textColor:[220,220,220], fontStyle:"bold", fontSize:6 },
                      columnStyles: { 0: { halign:"left", cellWidth:30, fontSize:6.5, textColor:[30,30,30] } },
                      didDrawCell: (data) => {
                        if(data.section==="body" && data.column.index > 0 && data.column.index <= daysInMonth) {
                          const dayIdx = data.column.index - 1;
                          const emp = empsForArea[data.row.index];
                          if(!emp) return;
                          const k = `${year}-${String(month+1).padStart(2,"0")}-${String(dayIdx+1).padStart(2,"0")}`;
                          const s = (effectiveMonth[emp.id] ?? {})[k];
                          const {x,y,width,height} = data.cell;
                          const color = !s ? PDF_STATUS_COLORS.work : PDF_STATUS_COLORS[s];
                          const label = !s ? "T" : (STATUS_SHORT[s] ?? "");
                          if(color) {
                            doc.setFillColor(...color);
                            doc.rect(x,y,width,height,"F");
                            doc.setTextColor(255,255,255);
                            doc.setFontSize(5);
                            doc.text(label, x+width/2, y+height/2+1.2, {align:"center"});
                          }
                        }
                        if(data.section==="body" && data.column.index === daysInMonth+1) {
                          const {x,y,width,height} = data.cell;
                          doc.setFillColor(40,40,40);
                          doc.rect(x,y,width,height,"F");
                          doc.setTextColor(245,200,66);
                          doc.setFontSize(6);
                          doc.text(data.cell.text[0]??"", x+width/2, y+height/2+1.2, {align:"center"});
                        }
                      },
                      theme: "grid",
                    });
                    return doc.lastAutoTable.finalY;
                  };

                  let curY = 21;
                  areasToExport.forEach((area, aIdx) => {
                    const empsForArea = schedArea === "Todos"
                      ? areaEmps.filter(e => { const r = restRoles.find(x=>x.id===e.roleId); return r?.area === area; })
                      : areaEmps;
                    if (empsForArea.length === 0) return;
                    if (schedArea === "Todos") {
                      if (aIdx > 0 && curY > 170) { doc.addPage(); curY = 12; }
                      doc.setFontSize(9);
                      doc.setTextColor(60,60,60);
                      doc.text(`▸ ${area.toUpperCase()} (${empsForArea.length})`, 14, curY + 2);
                      curY += 5;
                    }
                    const body = buildAreaBody(empsForArea);
                    curY = drawTable(body, curY, empsForArea) + 4;
                  });

                  // v8.12.3: Lista de inversões do mês ao final do PDF
                  const monthSwapsForPDF = (data?.scheduleSwaps ?? []).filter(s => {
                    if (s.restaurantId !== rid) return false;
                    const dates = [s.date1, s.date2, s.date].filter(Boolean);
                    return dates.some(d => d.slice(0,7) === mk);
                  });
                  if (monthSwapsForPDF.length > 0) {
                    if (curY > 170) { doc.addPage(); curY = 12; }
                    doc.setFontSize(9);
                    doc.setTextColor(124,58,237);
                    doc.text(`↔ INVERSÕES INFORMAIS REGISTRADAS (${monthSwapsForPDF.length})`, 14, curY + 4);
                    curY += 7;
                    const empNamePDF = {};
                    schedEmps.forEach(e => { empNamePDF[e.id] = e.name; });
                    const swapBody = monthSwapsForPDF
                      .sort((a,b) => (a.date1||a.date||"").localeCompare(b.date1||b.date||""))
                      .map(sw => {
                        const d1 = sw.date1 || sw.date || "";
                        const d2 = sw.date2 || "";
                        const fmtD = (d) => d ? new Date(d+"T12:00:00").toLocaleDateString("pt-BR",{day:"2-digit",month:"2-digit"}) : "—";
                        const datesStr = d2 ? `${fmtD(d1)} ↔ ${fmtD(d2)}` : fmtD(d1);
                        return [datesStr, empNamePDF[sw.empAId] || "—", empNamePDF[sw.empBId] || "—", sw.note || "", sw.registeredBy || ""];
                      });
                    doc.autoTable({
                      head: [["Domingos", "Empregado A (trab. na escala)", "Empregado B (folga na escala)", "Obs.", "Por"]],
                      body: swapBody,
                      startY: curY,
                      styles: { fontSize: 7, cellPadding: 1.4, textColor:[40,40,40], lineColor:[200,200,200], lineWidth:0.1 },
                      headStyles: { fillColor:[124,58,237], textColor:[255,255,255], fontStyle:"bold", fontSize:7 },
                      columnStyles: { 0: { cellWidth: 36, fontStyle:"bold", textColor:[124,58,237] }, 1: { cellWidth: 50 }, 2: { cellWidth: 50 }, 3: { cellWidth: 60 } },
                      theme: "grid",
                    });
                  }

                  setPreviewDoc(doc); setPreviewFileName(`escala_${schedArea}_${year}_${String(month+1).padStart(2,"0")}.pdf`);
                }} style={{padding:"8px 12px",borderRadius:10,border:"1px solid var(--border)",background:"transparent",color:"var(--text2)",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12,whiteSpace:"nowrap"}}>
                📄 Exportar PDF
              </button>}
            </div>

            {/* Legend — no mobile vira expandable com labels; no desktop exibe inline */}
            {mobileOnly ? (
              <details style={{marginBottom:12}}>
                <summary style={{cursor:"pointer",listStyle:"none",display:"flex",alignItems:"center",gap:8,padding:"6px 10px",background:"var(--bg2)",borderRadius:8,fontSize:11,color:"var(--text2)",fontWeight:600}}>
                  <span>ℹ️ Legenda dos status</span>
                  <span style={{marginLeft:"auto",fontSize:10,color:"var(--text3)"}}>tocar pra ver</span>
                </summary>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginTop:8,padding:"0 4px"}}>
                  {[["var(--green)","T","Trabalho"],["var(--red)","F","Folga"],["#06b6d4","FL","Freela"],["#3b82f6","FC","Folga Comp."],["#0ea5e9","TC","Trab. Comp."],["#8b5cf6","Fér","Férias"],["#f59e0b","FJ","F.Just."],["var(--red)","FI","F.Inj."]].map(([c,s,l])=>(
                    <div key={s} style={{display:"flex",alignItems:"center",gap:6}}>
                      <div style={{width:18,height:16,borderRadius:3,background:c+"33",border:`1px solid ${c}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        <span style={{color:c,fontSize:8,fontWeight:700}}>{s}</span>
                      </div>
                      <span style={{color:"var(--text2)",fontSize:11,fontFamily:"'DM Mono',monospace"}}>{l}</span>
                    </div>
                  ))}
                </div>
              </details>
            ) : (
              <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:12,alignItems:"center"}}>
                {[["var(--green)","T","Trabalho"],["var(--red)","F","Folga"],["#06b6d4","FL","Freela"],["#3b82f6","FC","Folga Comp."],["#0ea5e9","TC","Trab. Comp."],["#8b5cf6","Fér","Férias"],["#f59e0b","FJ","F.Just."],["var(--red)","FI","F.Inj."]].map(([c,s,l])=>(
                  <div key={s} style={{display:"flex",alignItems:"center",gap:3,flexShrink:0}}>
                    <div style={{width:20,height:16,borderRadius:3,background:c+"33",border:`1px solid ${c}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                      <span style={{color:c,fontSize:9,fontWeight:700}}>{s}</span>
                    </div>
                    <span style={{color:"var(--text3)",fontSize:10,fontFamily:"'DM Mono',monospace"}}>{l}</span>
                  </div>
                ))}
                {/* v8.12.3: badge ↔ na legenda */}
                <div style={{display:"flex",alignItems:"center",gap:3,flexShrink:0}}>
                  <div style={{width:20,height:16,borderRadius:3,background:"#7c3aed22",border:"1px solid #7c3aed",display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <span style={{color:"#7c3aed",fontSize:9,fontWeight:700}}>↔</span>
                  </div>
                  <span style={{color:"var(--text3)",fontSize:10,fontFamily:"'DM Mono',monospace"}}>Inversão</span>
                </div>
              </div>
            )}
            {/* v8.12.3: Lista compacta de inversões do mês — fica logo abaixo da legenda */}
            {(() => {
              const monthSwapsAll = (data?.scheduleSwaps ?? []).filter(s => {
                if (s.restaurantId !== rid) return false;
                const dates = [s.date1, s.date2, s.date].filter(Boolean);
                return dates.some(d => d.slice(0,7) === mk);
              });
              if (monthSwapsAll.length === 0) return null;
              const empNameMap2 = {};
              schedEmps.forEach(e => { empNameMap2[e.id] = e.name; });
              const fmtSwapPair = (sw) => {
                const fmt1 = sw.date1 ? new Date(sw.date1+"T12:00:00").toLocaleDateString("pt-BR",{day:"2-digit",month:"2-digit"}) : (sw.date ? new Date(sw.date+"T12:00:00").toLocaleDateString("pt-BR",{day:"2-digit",month:"2-digit"}) : "—");
                const fmt2 = sw.date2 ? new Date(sw.date2+"T12:00:00").toLocaleDateString("pt-BR",{day:"2-digit",month:"2-digit"}) : null;
                return fmt2 ? `${fmt1} ↔ ${fmt2}` : fmt1;
              };
              return (
                <details style={{marginBottom:12}} open={!mobileOnly && monthSwapsAll.length <= 4}>
                  <summary style={{cursor:"pointer",listStyle:"none",display:"flex",alignItems:"center",gap:8,padding:"6px 10px",background:"#7c3aed11",border:"1px solid #7c3aed44",borderRadius:8,fontSize:11,fontWeight:600,color:"#7c3aed"}}>
                    <span>↔️ Inversões registradas neste mês ({monthSwapsAll.length})</span>
                    <span style={{marginLeft:"auto",fontSize:10,opacity:0.6}}>{mobileOnly?"toque":"clique"} pra ver</span>
                  </summary>
                  <div style={{display:"flex",flexDirection:"column",gap:4,marginTop:8,padding:"0 4px"}}>
                    {monthSwapsAll.sort((a,b) => (a.date1||a.date||"").localeCompare(b.date1||b.date||"")).map(sw => (
                      <div key={sw.id} style={{fontSize:11,padding:"4px 10px",borderLeft:"3px solid #7c3aed",background:"var(--bg2)",borderRadius:4,fontFamily:"'DM Sans',sans-serif",color:"var(--text2)"}}>
                        <strong style={{color:"#7c3aed",fontFamily:"'DM Mono',monospace"}}>{fmtSwapPair(sw)}</strong>
                        {" · "}{empNameMap2[sw.empAId] || "—"} ↔ {empNameMap2[sw.empBId] || "—"}
                        {sw.note && <span style={{color:"var(--text3)",marginLeft:6,fontStyle:"italic"}}>· {sw.note}</span>}
                      </div>
                    ))}
                  </div>
                </details>
              );
            })()}

            {/* Formulário de atrasos em lote */}
            {showDelayForm && (
              <div style={{...S.card,marginBottom:14,border:"1px solid #f59e0b44"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                  <span style={{fontSize:16}}>⏰</span>
                  <span style={{fontWeight:700,color:"var(--text)",fontSize:14}}>Registrar atrasos — {monthLabel(year, month)}</span>
                </div>
                <div style={{marginBottom:12}}>
                  <label style={S.label}>Empregado</label>
                  <select value={delayEmpId} onChange={e=>{
                    setDelayEmpId(e.target.value);
                    // Pre-fill with existing delays for this employee
                    const existing = data?.delays?.[rid]?.[mk]?.[e.target.value] ?? {};
                    setDelayInputs({...existing});
                  }} style={{...S.input,fontSize:13}}>
                    <option value="">Selecionar...</option>
                    {schedEmps.filter(e=>!e.inactive).sort((a,b)=>a.name.localeCompare(b.name)).map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </div>
                {delayEmpId && (() => {
                  const dim = new Date(year, month + 1, 0).getDate();
                  const empSched = schedules?.[rid]?.[mk]?.[delayEmpId] ?? {};
                  const workDays = [];
                  for (let d = 1; d <= dim; d++) {
                    const dk = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
                    const st = empSched[dk];
                    // Only show days that are work days (not off, vacation, etc.)
                    if (!st || st === "T" || st === DAY_COMP_TRAB) workDays.push({ day: d, dk });
                  }
                  const totalMin = Object.values(delayInputs).reduce((s,v) => s + (parseInt(v)||0), 0);
                  const totalCount = Object.values(delayInputs).filter(v => (parseInt(v)||0) > 0).length;
                  return (
                    <div>
                      <div style={{color:"var(--text3)",fontSize:11,marginBottom:8}}>Informe os minutos de atraso em cada dia trabalhado. Dias sem valor serão ignorados.</div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(120px, 1fr))",gap:6,marginBottom:12}}>
                        {workDays.map(({day, dk}) => {
                          const val = delayInputs[String(day)] ?? "";
                          const dayName = new Date(year, month, day).toLocaleDateString("pt-BR",{weekday:"short"}).replace(".","");
                          return (
                            <div key={day} style={{display:"flex",alignItems:"center",gap:6,padding:"4px 8px",borderRadius:8,background:val&&parseInt(val)>0?"#f59e0b11":"var(--bg1)"}}>
                              <span style={{color:"var(--text2)",fontSize:11,fontWeight:600,minWidth:50}}>{String(day).padStart(2,"0")} {dayName}</span>
                              <input type="number" min="0" max="480" value={val} placeholder="min"
                                onChange={e => setDelayInputs(prev => ({...prev, [String(day)]: e.target.value}))}
                                style={{...S.input,width:55,fontSize:12,padding:"4px 6px",textAlign:"center"}}/>
                            </div>
                          );
                        })}
                      </div>
                      {totalCount > 0 && (
                        <div style={{marginBottom:10,padding:"8px 12px",borderRadius:8,background:"#f59e0b11",color:"#f59e0b",fontSize:12,fontWeight:600}}>
                          {totalCount} dia{totalCount!==1?"s":""} com atraso · {totalMin} min total ({(totalMin/60).toFixed(1)}h)
                        </div>
                      )}
                      <button onClick={() => {
                        // Clean inputs — remove zeros and empty
                        const cleaned = {};
                        Object.entries(delayInputs).forEach(([d, v]) => {
                          const mins = parseInt(v) || 0;
                          if (mins > 0) cleaned[d] = mins;
                        });
                        const allDelays = { ...(data?.delays ?? {}) };
                        if (!allDelays[rid]) allDelays[rid] = {};
                        if (!allDelays[rid][mk]) allDelays[rid][mk] = {};
                        allDelays[rid][mk][delayEmpId] = cleaned;
                        onUpdate("delays", allDelays);
                        setShowDelayForm(false);
                        setDelayEmpId("");
                        setDelayInputs({});
                      }} style={{...S.btnPrimary,background:"#f59e0b",fontSize:12,padding:"8px 20px"}}>
                        Salvar atrasos
                      </button>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Formulário de férias */}
            {showVacForm && (
              <div style={{...S.card,marginBottom:14,border:"1px solid #8b5cf644"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                  <span style={{fontSize:14}}>🏖️</span>
                  <span style={{color:"#8b5cf6",fontWeight:700,fontSize:14}}>Marcar Férias</span>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  <div>
                    <label style={S.label}>Empregado</label>
                    <select value={vacEmpId} onChange={e=>setVacEmpId(e.target.value)} style={{...S.input,cursor:"pointer"}}>
                      <option value="">Selecione...</option>
                      {areaEmps.sort((a,b)=>a.name.localeCompare(b.name)).map(e => (
                        <option key={e.id} value={e.id}>{e.name}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                    <div>
                      <label style={S.label}>Data início</label>
                      <input type="date" value={vacFrom} onChange={e=>setVacFrom(e.target.value)} style={S.input}/>
                    </div>
                    <div>
                      <label style={S.label}>Data fim</label>
                      <input type="date" value={vacTo} onChange={e=>setVacTo(e.target.value)} style={S.input}/>
                    </div>
                  </div>
                  {vacEmpId && vacFrom && vacTo && vacFrom <= vacTo && (()=>{
                    const d1 = new Date(vacFrom+"T12:00:00"), d2 = new Date(vacTo+"T12:00:00");
                    const dias = Math.round((d2 - d1) / (1000*60*60*24)) + 1;
                    return <p style={{color:"#8b5cf6",fontSize:12,margin:0}}>→ {dias} dia(s) de férias para {areaEmps.find(e=>e.id===vacEmpId)?.name}</p>;
                  })()}
                  <div style={{display:"flex",gap:8}}>
                    <button onClick={()=>{
                      if (!vacEmpId || !vacFrom || !vacTo || vacFrom > vacTo) return;
                      const emp = areaEmps.find(e => e.id === vacEmpId);
                      if (!emp) return;
                      // Accumulate vacation days in local edits
                      const vacEdits = {};
                      let count = 0;
                      const cur = new Date(vacFrom+"T12:00:00");
                      const end = new Date(vacTo+"T12:00:00");
                      while (cur <= end) {
                        const dateStr = cur.toISOString().slice(0,10);
                        if (dateStr.slice(0,7) === `${year}-${String(month+1).padStart(2,"0")}`) {
                          vacEdits[dateStr] = DAY_VACATION;
                          count++;
                        }
                        cur.setDate(cur.getDate() + 1);
                      }
                      setSchedLocalEdits(prev => {
                        const edits = prev ? { ...prev } : {};
                        edits[vacEmpId] = { ...(edits[vacEmpId] ?? {}), ...vacEdits };
                        return edits;
                      });
                      setShowVacForm(false);
                      onUpdate("_toast", `🏖️ ${count} dia(s) de férias marcados para ${emp.name} — salve para confirmar`);
                    }} disabled={!vacEmpId||!vacFrom||!vacTo||vacFrom>vacTo}
                      style={{...S.btnPrimary,background:"#8b5cf6",flex:1,opacity:(!vacEmpId||!vacFrom||!vacTo||vacFrom>vacTo)?0.5:1}}>
                      Confirmar férias
                    </button>
                    <button onClick={()=>setShowVacForm(false)} style={S.btnSecondary}>Cancelar</button>
                  </div>
                </div>
              </div>
            )}

            {areaEmps.length === 0 && <p style={{color:"var(--text3)",textAlign:"center"}}>Nenhum empregado {schedArea === "Todos" ? "cadastrado" : "nesta área"}.</p>}

            {areaEmps.length > 0 && (() => {
              const daysInMonth = dim;
              // v8.12.2: índice de inversões — agora suporta date1+date2 (recíproca). Cada swap marca 4 cells (empA@date1, empA@date2, empB@date1, empB@date2).
              // Compat: entradas legadas com `date` (sem date2) — só marcam 2 cells.
              const swapsByEmpDate = {};
              const empNameById = {};
              schedEmps.forEach(e => { empNameById[e.id] = e.name; });
              const restSwaps = (data?.scheduleSwaps ?? []).filter(s => {
                if (s.restaurantId !== rid) return false;
                const dates = [s.date1, s.date2, s.date].filter(Boolean);
                return dates.some(d => d.slice(0,7) === mk);
              });
              const addEntry = (empId, dateStr, sw, partnerName, otherDateStr) => {
                if (!empId || !dateStr) return;
                if (dateStr.slice(0,7) !== mk) return;
                if (!swapsByEmpDate[empId]) swapsByEmpDate[empId] = {};
                swapsByEmpDate[empId][dateStr] = { ...sw, partnerName, otherDate: otherDateStr };
              };
              restSwaps.forEach(sw => {
                const aName = empNameById[sw.empAId] || "—";
                const bName = empNameById[sw.empBId] || "—";
                if (sw.date1 || sw.date2) {
                  // Modelo novo recíproco
                  if (sw.date1) {
                    addEntry(sw.empAId, sw.date1, sw, bName, sw.date2);
                    addEntry(sw.empBId, sw.date1, sw, aName, sw.date2);
                  }
                  if (sw.date2) {
                    addEntry(sw.empAId, sw.date2, sw, bName, sw.date1);
                    addEntry(sw.empBId, sw.date2, sw, aName, sw.date1);
                  }
                } else if (sw.date) {
                  // Legado: 1 data só
                  addEntry(sw.empAId, sw.date, sw, bName, null);
                  addEntry(sw.empBId, sw.date, sw, aName, null);
                }
              });
              // Inversões de dia inteiro (feriados): map por data → { otherDate, reason, createdBy, createdAt }
              const dayInversionsByDate = {};
              (data?.dayInversions ?? []).forEach(inv => {
                if (inv.restaurantId !== rid) return;
                if (inv.dateA?.slice(0,7) !== mk && inv.dateB?.slice(0,7) !== mk) return;
                if (inv.dateA?.slice(0,7) === mk) dayInversionsByDate[inv.dateA] = { otherDate: inv.dateB, reason: inv.reason, createdBy: inv.createdBy, createdAt: inv.createdAt };
                if (inv.dateB?.slice(0,7) === mk) dayInversionsByDate[inv.dateB] = { otherDate: inv.dateA, reason: inv.reason, createdBy: inv.createdBy, createdAt: inv.createdAt };
              });
              const dayInversionTooltip = (dateStr) => {
                const inv = dayInversionsByDate[dateStr];
                if (!inv) return null;
                const otherBR = new Date(inv.otherDate+"T12:00:00").toLocaleDateString("pt-BR");
                return `↔️ Dia invertido com ${otherBR}${inv.reason?` · Motivo: ${inv.reason}`:""}${inv.createdBy?` · por ${inv.createdBy}`:""}`;
              };
              const swapTooltip = (empId, dateStr) => {
                const sw = swapsByEmpDate[empId]?.[dateStr];
                if (!sw) return null;
                const otherBR = sw.otherDate ? ` · folga recíproca em ${new Date(sw.otherDate+"T12:00:00").toLocaleDateString("pt-BR")}` : "";
                return `↔️ Invertido com ${sw.partnerName}${otherBR}${sw.note?` · ${sw.note}`:""} · registrado por ${sw.registeredBy}`;
              };

              function cycleStatus(empId, dateStr) {
                if (!canSchedEdit) return; // Líder e quem não tem permissão: somente visualização
                if (monthClosed) return; // Month is closed, no editing allowed
                if (restaurant.serviceStartDate && dateStr < restaurant.serviceStartDate) return;
                const empDayMap = effectiveMonth[empId] ?? {};
                const cur = empDayMap[dateStr];
                const idx = DAY_CYCLE.indexOf(cur);
                const next = idx === DAY_CYCLE.length - 1 ? null : DAY_CYCLE[idx + 1];
                // Accumulate in local edits (not saved to Firestore yet)
                setSchedLocalEdits(prev => {
                  const edits = prev ? { ...prev } : {};
                  if (!edits[empId]) edits[empId] = {};
                  if (next === null) edits[empId][dateStr] = null; // null = mark for deletion
                  else edits[empId][dateStr] = next;
                  return edits;
                });
              }

              /* ——— MOBILE: Visão Semanal ——— */
              if (mobileOnly) {
                // Gera semanas do mês
                const weeks = [];
                let d = 1;
                while (d <= daysInMonth) {
                  const weekDays = [];
                  for (let i = 0; i < 7 && d <= daysInMonth; i++) {
                    const date = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
                    const wd = new Date(date+"T12:00:00").getDay();
                    // Primeira semana: preenche dias anteriores com null
                    if (weeks.length === 0 && weekDays.length === 0 && wd > 0) {
                      for (let p = 0; p < wd; p++) weekDays.push(null);
                    }
                    weekDays.push({ day: d, date, wd });
                    d++;
                    // Se caiu no sábado, quebra semana
                    if (wd === 6) break;
                  }
                  // Preenche final da semana com null
                  while (weekDays.length < 7) weekDays.push(null);
                  weeks.push(weekDays);
                }
                const safeWeek = Math.min(weekIdx, weeks.length - 1);
                const curWeek = weeks[safeWeek] || [];
                const validDays = curWeek.filter(Boolean);
                const rangeLabel = validDays.length ? `${validDays[0].day} — ${validDays[validDays.length-1].day}` : "";

                return (
                  <div>
                    {/* Navegação de semana */}
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,padding:"0 4px"}}>
                      <button onClick={()=>setWeekIdx(Math.max(0, safeWeek-1))} disabled={safeWeek===0}
                        aria-label="Semana anterior"
                        style={{background:"none",border:"1px solid var(--border)",borderRadius:8,padding:"6px 12px",color:safeWeek===0?"var(--text3)":"var(--text)",cursor:safeWeek===0?"default":"pointer",fontSize:14}}>
                        ◀
                      </button>
                      <div style={{textAlign:"center"}}>
                        <div style={{color:"var(--text)",fontWeight:700,fontSize:13,fontFamily:"'DM Sans',sans-serif"}}>Semana {safeWeek+1} de {weeks.length}</div>
                        <div style={{color:"var(--text3)",fontSize:11}}>Dias {rangeLabel}</div>
                      </div>
                      <button onClick={()=>setWeekIdx(Math.min(weeks.length-1, safeWeek+1))} disabled={safeWeek>=weeks.length-1}
                        aria-label="Próxima semana"
                        style={{background:"none",border:"1px solid var(--border)",borderRadius:8,padding:"6px 12px",color:safeWeek>=weeks.length-1?"var(--text3)":"var(--text)",cursor:safeWeek>=weeks.length-1?"default":"pointer",fontSize:14}}>
                        ▶
                      </button>
                    </div>

                    {/* Header dos dias da semana */}
                    <div style={{display:"grid",gridTemplateColumns:"80px repeat(7,1fr)",gap:2,marginBottom:4,padding:"0 2px"}}>
                      <div></div>
                      {["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"].map((dl,i)=>(
                        <div key={dl} style={{textAlign:"center",color:i===0||i===6?"#f59e0b":"var(--text3)",fontSize:9,fontWeight:700,fontFamily:"'DM Mono',monospace",padding:"2px 0"}}>
                          <div>{dl}</div>
                          <div style={{fontSize:10,color:"var(--text2)"}}>{curWeek[i]?.day ?? ""}</div>
                        </div>
                      ))}
                    </div>

                    {/* Grid de empregados */}
                    {areaEmps.map((emp, ei) => {
                      const role = restRoles.find(r=>r.id===emp.roleId);
                      const dayMap = displayedMonth[emp.id] ?? {};
                      const empDelays = data?.delays?.[rid]?.[mk]?.[emp.id] ?? {};
                      const curArea = role?.area;
                      const prevEmp = areaEmps[ei-1];
                      const prevArea = prevEmp ? restRoles.find(r=>r.id===prevEmp.roleId)?.area : null;
                      const showAreaHeader = schedArea === "Todos" && curArea !== prevArea;

                      return (
                        <div key={emp.id}>
                          {showAreaHeader && (
                            <div style={{padding:"8px 4px 4px",marginTop:ei>0?8:0}}>
                              <span style={{color:AREA_COLORS[curArea]??"#888",fontSize:10,fontWeight:700,letterSpacing:1}}>{(curArea??"").toUpperCase()}</span>
                            </div>
                          )}
                          <div style={{display:"grid",gridTemplateColumns:"80px repeat(7,1fr)",gap:2,padding:"4px 2px",background:ei%2===0?"var(--bg1)":"var(--bg2)",borderRadius:6,marginBottom:2}}>
                            {/* Nome do empregado */}
                            <div style={{display:"flex",flexDirection:"column",justifyContent:"center",padding:"2px 4px",minWidth:0}}>
                              <div style={{color:"var(--text)",fontSize:10,fontWeight:600,lineHeight:1.2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{emp.name.split(" ")[0]}</div>
                              <div style={{color:"var(--text3)",fontSize:8,lineHeight:1.1}}>{role?.name}</div>
                            </div>
                            {/* 7 dias da semana */}
                            {curWeek.map((slot, di) => {
                              if (!slot) return <div key={di} style={{minHeight:36}}></div>;
                              const isDem = emp.demitidoEm && slot.date >= emp.demitidoEm;
                              const status = isDem ? null : dayMap[slot.date];
                              const color = isDem ? "#6b7280" : (STATUS_COLORS[status] ?? "var(--green)");
                              const label = isDem ? "DEM" : (STATUS_SHORT[status] ?? "T");
                              const locked = isDem || monthClosed || !canSchedEdit || (restaurant.serviceStartDate && slot.date < restaurant.serviceStartDate);
                              const dayNum = slot.date ? String(parseInt(slot.date.slice(-2))) : null;
                              const delayMin = dayNum ? (empDelays[dayNum] || 0) : 0;
                              const swapInfo = swapsByEmpDate[emp.id]?.[slot.date];
                              const swapTip = swapInfo ? swapTooltip(emp.id, slot.date) : null;
                              const dayInvInfo = dayInversionsByDate[slot.date];
                              const dayInvTip = dayInvInfo ? dayInversionTooltip(slot.date) : null;
                              return (
                                <div key={di} onClick={()=>!locked && cycleStatus(emp.id, slot.date)}
                                  title={`${label === "T" ? "Trabalho" : (label === "DEM" ? "Demitido" : label)}${delayMin > 0 ? ` · Atraso ${delayMin} min` : ""}${swapTip ? `\n${swapTip}` : ""}${dayInvTip ? `\n${dayInvTip}` : ""}`}
                                  style={{
                                    display:"flex", alignItems:"center", justifyContent:"center",
                                    minHeight:36, borderRadius:6,
                                    cursor: locked ? "not-allowed" : "pointer",
                                    // v8.3: bg sólido em exceções, neutro em work
                                    background: isDem ? "#6b7280" : (status ? color : "transparent"),
                                    border: `1px solid ${isDem ? "#6b728088" : (delayMin>0 ? "#f59e0b" : (status ? color : "var(--border)"))}`,
                                    opacity: locked ? 0.55 : 1,
                                    position: "relative"
                                  }}>
                                  <span style={{
                                    color: status || isDem ? "#fff" : (locked ? "var(--text3)" : color),
                                    fontSize: isDem ? 9 : 11,
                                    fontWeight: 700,
                                    letterSpacing: 0.3,
                                  }}>{restaurant.serviceStartDate && !isDem && slot.date < restaurant.serviceStartDate?"🔒":label}</span>
                                  {delayMin > 0 && <span style={{position:"absolute",top:0,right:1,fontSize:6,color:status||isDem?"#fff":"#f59e0b",fontWeight:800}}>⏰</span>}
                                  {swapInfo && <span style={{position:"absolute",top:-3,right:-3,fontSize:11,lineHeight:1,background:"#7c3aed",color:"#fff",borderRadius:"50%",width:16,height:16,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,boxShadow:"0 2px 4px rgba(0,0,0,0.3)",border:"2px solid var(--card-bg)"}}>↔</span>}
                                  {dayInvInfo && !swapInfo && <span style={{position:"absolute",top:-3,left:-3,fontSize:9,lineHeight:1,background:"#f59e0b",color:"#fff",borderRadius:"50%",width:14,height:14,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,boxShadow:"0 2px 4px rgba(0,0,0,0.3)",border:"2px solid var(--card-bg)"}}>↔</span>}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              }

              /* ——— DESKTOP: Tabela completa ——— */
              // O div é o scroll-container (x + y). Sticky no <th> pina relativo a este div.
              // max-height garante que o div scrolla em vez da página — assim sticky funciona.
              return (
                <div style={{overflowX:"auto",overflowY:"auto",maxHeight:"calc(100vh - 260px)",WebkitOverflowScrolling:"touch",border:"1px solid var(--border)",borderRadius:8}}>
                  <table style={{borderCollapse:"collapse",fontFamily:"'DM Mono',monospace",fontSize:11,width:"100%",tableLayout:"fixed"}}>
                    <colgroup>
                      <col style={{width:110,minWidth:110}}/>
                      {Array.from({length:daysInMonth},(_,i)=><col key={i} style={{width:26,minWidth:22}}/>)}
                      <col style={{width:30,minWidth:28}}/>
                      <col style={{width:30,minWidth:28}}/>
                    </colgroup>
                    <thead>
                      <tr>
                        {/* "Empregado" — sticky top+left. z-index 12 fica acima do breadcrumb (z:10) do AppShell */}
                        <th style={{position:"sticky",left:0,top:0,background:"var(--card-bg)",zIndex:12,padding:"6px 6px",textAlign:"left",color:"var(--text3)",fontSize:10,borderBottom:"1px solid var(--border)",whiteSpace:"nowrap",boxShadow:"0 1px 0 var(--border)"}}>
                          Empregado
                        </th>
                        {Array.from({length:daysInMonth},(_,i)=>{
                          const d = i+1;
                          const date = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
                          const wd = new Date(date+"T12:00:00").getDay();
                          const isWe = wd===0||wd===6;
                          return (
                            <th key={d} style={{position:"sticky",top:0,background:"var(--card-bg)",zIndex:11,padding:"2px 0",textAlign:"center",color:isWe?"#f59e0b":"#555",fontSize:8,borderBottom:"1px solid var(--border)",boxShadow:"0 1px 0 var(--border)"}}>
                              <div>{d}</div>
                              <div style={{fontSize:7}}>{["D","S","T","Q","Q","S","S"][wd]}</div>
                            </th>
                          );
                        })}
                        {/* "T" e "F" — sticky top+right */}
                        <th style={{position:"sticky",top:0,right:30,background:"var(--card-bg)",zIndex:12,padding:"4px 2px",textAlign:"center",color:"var(--green)",fontSize:9,borderBottom:"1px solid var(--border)",borderLeft:"1px solid var(--border)",boxShadow:"0 1px 0 var(--border)"}}>T</th>
                        <th style={{position:"sticky",top:0,right:0,background:"var(--card-bg)",zIndex:12,padding:"4px 2px",textAlign:"center",color:"var(--red)",fontSize:9,borderBottom:"1px solid var(--border)",borderLeft:"1px solid var(--border)",boxShadow:"0 1px 0 var(--border)"}}>F</th>
                      </tr>
                    </thead>
                    <tbody>
                      {areaEmps.map((emp,ei) => {
                        const role = restRoles.find(r=>r.id===emp.roleId);
                        const dayMap = displayedMonth[emp.id] ?? {};
                        const empDelaysD = data?.delays?.[rid]?.[mk]?.[emp.id] ?? {};
                        // Contagem alinhada com o visual: sem status = "•" = trabalho
                        let workC=0, offC=0;
                        for (let dd = 1; dd <= daysInMonth; dd++) {
                          const dateStr2 = `${year}-${String(month+1).padStart(2,"0")}-${String(dd).padStart(2,"0")}`;
                          const isDem2 = emp.demitidoEm && dateStr2 >= emp.demitidoEm;
                          if (isDem2) continue;
                          const st = dayMap[dateStr2];
                          if (!st || st === DAY_COMP_TRAB) { workC++; }
                          else { offC++; }
                        }

                        const prevEmp = areaEmps[ei-1];
                        const prevArea = prevEmp ? restRoles.find(r=>r.id===prevEmp.roleId)?.area : null;
                        const curArea = role?.area;
                        const showAreaHeader = schedArea === "Todos" && curArea !== prevArea;

                        const rows = [];
                        if (showAreaHeader) {
                          rows.push(
                            <tr key={`area-${curArea}`}>
                              <td colSpan={daysInMonth + 3} style={{padding:"8px 10px 4px",background:"var(--bg5)",borderTop:"1px solid var(--border)",borderBottom:"1px solid var(--border)"}}>
                                <span style={{color:AREA_COLORS[curArea]??"#888",fontSize:10,fontWeight:700,letterSpacing:1}}>{(curArea??"").toUpperCase()}</span>
                              </td>
                            </tr>
                          );
                        }
                        rows.push(
                          <tr key={emp.id} style={{background:ei%2===0?"var(--bg1)":"var(--bg2)"}}>
                            <td style={{position:"sticky",left:0,background:ei%2===0?"var(--bg1)":"var(--bg2)",zIndex:1,padding:"4px 6px",borderRight:"1px solid var(--border)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                              <div style={{color:"var(--text)",fontSize:10,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{emp.name}</div>
                              <div style={{color:"var(--text3)",fontSize:8,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{role?.name}</div>
                            </td>
                            {Array.from({length:daysInMonth},(_,i)=>{
                              const d = i+1;
                              const date = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
                              const isDem = emp.demitidoEm && date >= emp.demitidoEm;
                              const status = isDem ? null : dayMap[date];
                              const color = isDem ? "#6b7280" : (STATUS_COLORS[status] ?? "var(--green)");
                              const label = isDem ? "D" : (STATUS_SHORT[status] ?? "•");
                              const wd = new Date(date+"T12:00:00").getDay();
                              const isWe = wd===0||wd===6;
                              const locked = isDem || monthClosed || !canSchedEdit || (restaurant.serviceStartDate && date < restaurant.serviceStartDate);
                              const dDelayMin = empDelaysD[String(d)] || 0;
                              const swapInfo = swapsByEmpDate[emp.id]?.[date];
                              const swapTip = swapInfo ? swapTooltip(emp.id, date) : null;
                              const dayInvInfo = dayInversionsByDate[date];
                              const dayInvTip = dayInvInfo ? dayInversionTooltip(date) : null;
                              return (
                                <td key={d} onClick={()=>!isDem && cycleStatus(emp.id, date)}
                                  title={`${label === "•" ? "Trabalho" : (label === "D" ? "Demitido" : label)}${dDelayMin > 0 ? ` · Atraso: ${dDelayMin} min` : ""}${swapTip ? `\n${swapTip}` : ""}${dayInvTip ? `\n${dayInvTip}` : ""}`}
                                  style={{
                                    textAlign:"center", padding:"2px 0",
                                    cursor: locked ? "not-allowed" : "pointer",
                                    // v8.3: bg sólido em exceções (status definido); work day fica neutro
                                    background: isDem ? "#6b7280" : (status ? color : (locked ? "var(--bg3)" : (isWe ? "var(--bg3)" : "transparent"))),
                                    borderRight: `1px solid ${dDelayMin>0?"#f59e0b":"var(--border)"}`,
                                    opacity: locked ? 0.5 : 1,
                                    position: "relative"
                                  }}>
                                  <span style={{
                                    color: status || isDem ? "#fff" : (locked ? "var(--text3)" : color),
                                    fontSize: isDem ? 8 : (status ? 9 : 10),
                                    fontWeight: 700,
                                    letterSpacing: 0.2,
                                  }}>{!isDem&&restaurant.serviceStartDate&&date<restaurant.serviceStartDate?"🔒":label}</span>
                                  {dDelayMin > 0 && <span style={{position:"absolute",bottom:0,right:0,fontSize:5,color:status||isDem?"#fff":"#f59e0b",lineHeight:1}}>⏰</span>}
                                  {swapInfo && <span style={{position:"absolute",top:-2,right:-2,fontSize:9,lineHeight:1,background:"#7c3aed",color:"#fff",borderRadius:"50%",width:13,height:13,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,boxShadow:"0 1px 3px rgba(0,0,0,0.4)",border:"1.5px solid var(--card-bg)",zIndex:2}}>↔</span>}
                                  {dayInvInfo && !swapInfo && <span style={{position:"absolute",top:-2,left:-2,fontSize:8,lineHeight:1,background:"#f59e0b",color:"#fff",borderRadius:"50%",width:11,height:11,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,boxShadow:"0 1px 3px rgba(0,0,0,0.4)",border:"1.5px solid var(--card-bg)",zIndex:2}}>↔</span>}
                                </td>
                              );
                            })}
                            <td style={{position:"sticky",right:30,background:ei%2===0?"var(--bg1)":"var(--bg2)",zIndex:1,textAlign:"center",color:"var(--green)",fontSize:10,fontWeight:700,padding:"3px 2px",borderLeft:"1px solid var(--border)"}}>{workC}</td>
                            <td style={{position:"sticky",right:0,background:ei%2===0?"var(--bg1)":"var(--bg2)",zIndex:1,textAlign:"center",color:"var(--red)",fontSize:10,padding:"3px 2px",borderLeft:"1px solid var(--border)"}}>{offC}</td>
                          </tr>
                        );
                        return rows;
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}

            {/* ═══ MAIS AÇÕES — rodapé com itens menos comuns ═══ */}
            <details style={{marginTop:18,padding:"12px 14px",background:"var(--bg2)",borderRadius:10,border:"1px solid var(--border)"}}>
              <summary style={{cursor:"pointer",fontSize:12,fontWeight:600,color:"var(--text2)",padding:"4px 0"}}>⋯ Mais ações</summary>
              <div style={{marginTop:12,display:"flex",flexDirection:"column",gap:10}}>
                {/* Inverter dia inteiro (feriados) — Owner only, não fechado */}
                {isOwner && !monthClosed && (
                  <button onClick={()=>{
                    // Default: dia 1 do mês exibido (assim os pickers abrem no mês certo)
                    const firstDay = `${year}-${String(month+1).padStart(2,"0")}-01`;
                    setInvertDayForm({ dateA: firstDay, dateB: firstDay, reason: "" });
                  }}
                    style={{...S.btnSecondary,fontSize:12,padding:"8px 12px",alignSelf:"flex-start",color:"#7c3aed",borderColor:"#7c3aed44",background:"#7c3aed10"}}
                    title="Inverter o status (trabalho ↔ folga ↔ etc.) entre dois dias do mês — útil pra feriados que mudam o dia de fechamento da casa">
                    ↔️ Inverter dia (feriados)
                  </button>
                )}
                {/* v8.12.1 (one-shot): Inferir refDate de abril/2026 a partir dos schedules existentes — Owner only */}
                {isOwner && (() => {
                  const targetMk = "2026-04";
                  // Pré-cálculo
                  const candidatos = [];
                  schedEmps.forEach(emp => {
                    const empWS = data?.workSchedules?.[rid]?.[emp.id] ?? [];
                    const cur = empWS[empWS.length - 1];
                    if (!cur) return;
                    // Domingo precisa estar ativo no horário
                    const sundayActive = cur.type === "alternating"
                      ? (cur.weeks?.A?.days?.[0]?.in || cur.weeks?.B?.days?.[0]?.in)
                      : (cur.days?.[0]?.in || cur.days?.[0]?.active === true);
                    if (!sundayActive) return;
                    // Já tem ciclo/refDate? skip
                    if (cur.sundayCycle?.refDate) return;
                    // Procura 1º domingo de folga em abril/2026
                    const sched = data?.schedules?.[rid]?.[targetMk]?.[emp.id] ?? {};
                    const folgaSundays = Object.entries(sched)
                      .filter(([dt, st]) => st === DAY_OFF && new Date(dt+"T12:00:00").getDay() === 0)
                      .map(([dt]) => dt)
                      .sort();
                    if (folgaSundays.length === 0) return;
                    candidatos.push({ emp, refDate: folgaSundays[0] });
                  });
                  if (candidatos.length === 0) return null;
                  return (
                    <div style={{padding:"10px 12px",background:"var(--card-bg)",border:"1px solid #f59e0b44",borderRadius:8}}>
                      <div style={{fontSize:12,fontWeight:700,color:"#f59e0b",marginBottom:6}}>🎯 Inferir refDate de domingo a partir de abril/2026</div>
                      <div style={{fontSize:11,color:"var(--text3)",marginBottom:10,lineHeight:1.5}}>
                        Pra cada empregado com domingo trabalhado mas sem ciclo cadastrado: pega o 1º domingo marcado como folga em abril/2026 da escala atual e usa como referência (ciclo padrão 3:1). <strong>{candidatos.length} empregado(s) elegível(eis).</strong>
                      </div>
                      <button onClick={async ()=>{
                        const linhas = ["🎯 INFERIR refDate DE DOMINGO\n", `Vai aplicar em ${candidatos.length} empregado(s) deste restaurante:\n`];
                        candidatos.slice(0, 12).forEach(c => {
                          linhas.push(`  • ${c.emp.name} → 1º folga: ${new Date(c.refDate+"T12:00:00").toLocaleDateString("pt-BR")}`);
                        });
                        if (candidatos.length > 12) linhas.push(`  ... e mais ${candidatos.length - 12}`);
                        linhas.push("\nCiclo padrão: trabalha 3 domingos, folga 1.");
                        linhas.push("Atualiza a versão vigente do horário in-place (não cria nova versão).");
                        if (!await appConfirm(linhas.join("\n"))) return;
                        // Aplica
                        const wsAll = { ...(data?.workSchedules ?? {}) };
                        const restWs = { ...(wsAll[rid] ?? {}) };
                        candidatos.forEach(({emp, refDate}) => {
                          const empArr = [...(restWs[emp.id] ?? [])];
                          if (empArr.length === 0) return;
                          const lastIdx = empArr.length - 1;
                          empArr[lastIdx] = {
                            ...empArr[lastIdx],
                            sundayCycle: { workCount: 3, offCount: 1, refDate },
                          };
                          restWs[emp.id] = empArr;
                        });
                        wsAll[rid] = restWs;
                        onUpdate("workSchedules", wsAll);
                        onUpdate("_toast", `🎯 ${candidatos.length} ciclo(s) inferido(s) — empregados receberam refDate baseado em abril/2026`);
                      }} style={{...S.btnPrimary,fontSize:12,padding:"8px 16px",background:"#f59e0b",borderColor:"#f59e0b"}}>
                        Aplicar pra {candidatos.length} empregado(s)
                      </button>
                    </div>
                  );
                })()}
                {/* v8.12.2: Inversões registradas — agora mostra date1 ↔ date2 */}
                {(() => {
                  const monthSwaps = (data?.scheduleSwaps ?? []).filter(s => {
                    if (s.restaurantId !== rid) return false;
                    const dates = [s.date1, s.date2, s.date].filter(Boolean);
                    return dates.some(d => d.slice(0,7) === mk);
                  });
                  if (monthSwaps.length === 0) return null;
                  const empNameMap = {};
                  schedEmps.forEach(e => { empNameMap[e.id] = e.name; });
                  const fmtSwapDates = (sw) => {
                    if (sw.date1 && sw.date2) {
                      const d1 = new Date(sw.date1+"T12:00:00").toLocaleDateString("pt-BR");
                      const d2 = new Date(sw.date2+"T12:00:00").toLocaleDateString("pt-BR");
                      return `${d1} ↔ ${d2}`;
                    }
                    if (sw.date) return new Date(sw.date+"T12:00:00").toLocaleDateString("pt-BR");
                    if (sw.date1) return new Date(sw.date1+"T12:00:00").toLocaleDateString("pt-BR");
                    return "—";
                  };
                  const swSortKey = (sw) => sw.date1 || sw.date || "";
                  return (
                    <div style={{padding:"10px 12px",background:"var(--card-bg)",border:"1px solid #7c3aed44",borderRadius:8}}>
                      <div style={{fontSize:12,fontWeight:700,color:"#7c3aed",marginBottom:8}}>↔️ Inversões registradas neste mês ({monthSwaps.length})</div>
                      <div style={{display:"flex",flexDirection:"column",gap:6}}>
                        {monthSwaps.sort((a,b) => swSortKey(a).localeCompare(swSortKey(b))).map(sw => (
                          <div key={sw.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,padding:"6px 10px",background:"var(--bg2)",borderRadius:6,fontSize:11}}>
                            <span style={{color:"var(--text2)"}}>
                              <strong>{fmtSwapDates(sw)}</strong>
                              {" "}· {empNameMap[sw.empAId] || "—"} ↔ {empNameMap[sw.empBId] || "—"}
                              {sw.note && <span style={{color:"var(--text3)",marginLeft:6}}>· {sw.note}</span>}
                              <span style={{color:"var(--text3)",marginLeft:6,fontSize:10}}>· por {sw.registeredBy}</span>
                            </span>
                            {!monthClosed && (canSchedEdit || isOwner) && (
                              <button onClick={async ()=>{
                                if (!await appConfirm("Remover este registro de inversão?\n\nA escala/gorjeta/VT não são afetados — só apaga o registro.")) return;
                                onUpdate("scheduleSwaps", (data?.scheduleSwaps ?? []).filter(s => s.id !== sw.id));
                                onUpdate("_toast", "↔️ Inversão removida");
                              }} style={{...S.btnSecondary,fontSize:10,padding:"3px 8px",color:"var(--red)",borderColor:"var(--red)44",whiteSpace:"nowrap"}}>Remover</button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
                {/* Inversões de dia inteiro (feriados) — Owner only, lista + desfazer */}
                {isOwner && (() => {
                  const monthInvs = (data?.dayInversions ?? []).filter(inv => {
                    if (inv.restaurantId !== rid) return false;
                    return inv.dateA?.slice(0,7) === mk || inv.dateB?.slice(0,7) === mk;
                  });
                  if (monthInvs.length === 0) return null;
                  return (
                    <div style={{padding:"10px 12px",background:"var(--card-bg)",border:"1px solid #f59e0b44",borderRadius:8}}>
                      <div style={{fontSize:12,fontWeight:700,color:"#f59e0b",marginBottom:8}}>↔️ Dias invertidos neste mês ({monthInvs.length})</div>
                      <div style={{display:"flex",flexDirection:"column",gap:6}}>
                        {monthInvs.sort((a,b) => (a.dateA||"").localeCompare(b.dateA||"")).map(inv => {
                          const dA = new Date(inv.dateA+"T12:00:00").toLocaleDateString("pt-BR");
                          const dB = new Date(inv.dateB+"T12:00:00").toLocaleDateString("pt-BR");
                          return (
                            <div key={inv.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,padding:"6px 10px",background:"var(--bg2)",borderRadius:6,fontSize:11}}>
                              <span style={{color:"var(--text2)",flex:1,minWidth:0}}>
                                <strong>{dA} ↔ {dB}</strong>
                                {inv.reason && <span style={{color:"var(--text3)",marginLeft:6}}>· {inv.reason}</span>}
                                <span style={{color:"var(--text3)",marginLeft:6,fontSize:10}}>· por {inv.createdBy}</span>
                              </span>
                              {!monthClosed && (
                                <button onClick={async ()=>{
                                  if (!await appConfirm(`Desfazer a inversão entre ${dA} e ${dB}?\n\nA escala desses dois dias volta ao estado de antes da inversão (empregados de férias preservados, como na inversão original).`)) return;
                                  // Re-aplica swap (A↔B↔A = identity), preservando férias.
                                  const preSnap = snapshotSchedulesMonth(schedules, rid, mk);
                                  saveSchedulesSnapshot(`Desfazer inversão ${dA} ↔ ${dB}`, preSnap);
                                  const newMonth = JSON.parse(JSON.stringify(schedules?.[rid]?.[mk] ?? {}));
                                  let undone = 0; let preserved = 0;
                                  schedEmps.forEach(emp => {
                                    const cur = effectiveMonth[emp.id] ?? {};
                                    const sa = cur[inv.dateA] ?? null;
                                    const sb = cur[inv.dateB] ?? null;
                                    if (sa === DAY_VACATION || sb === DAY_VACATION) { preserved++; return; }
                                    newMonth[emp.id] = { ...cur };
                                    if (sb === null) delete newMonth[emp.id][inv.dateA]; else newMonth[emp.id][inv.dateA] = sb;
                                    if (sa === null) delete newMonth[emp.id][inv.dateB]; else newMonth[emp.id][inv.dateB] = sa;
                                    undone++;
                                  });
                                  onUpdate("schedules", { ...schedules, [rid]: { ...(schedules?.[rid]??{}), [mk]: newMonth } });
                                  onUpdate("dayInversions", (data?.dayInversions ?? []).filter(x => x.id !== inv.id));
                                  setSchedLocalEdits(null);
                                  onUpdate("_toast", `↩️ Inversão desfeita — ${undone} empregado(s)${preserved>0?`, ${preserved} de férias preservados`:""}`);
                                }} style={{...S.btnSecondary,fontSize:10,padding:"3px 8px",color:"#f59e0b",borderColor:"#f59e0b88",whiteSpace:"nowrap"}}>↩️ Desfazer</button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
                {(isOwner || isDP) && (() => {
                  const vCount = (data?.scheduleVersions?.[rid]?.[mk] ?? []).length;
                  return (
                    <button onClick={()=>setShowSchedHistory(true)}
                      title="Ver e restaurar versões anteriores desta escala"
                      style={{...S.btnSecondary,fontSize:12,color:"var(--ac-text)",borderColor:"var(--ac)44",alignSelf:"flex-start"}}>
                      🕐 Histórico{vCount>0?` (${vCount} versões)`:""}
                    </button>
                  );
                })()}
                {(canSchedEdit || isOwner) && <button onClick={async ()=>{
                  const mesNome = monthLabel(year, month);
                  const n = areaEmps.length;
                  if (!n) return;
                  const typed = window.prompt(
                    `🔄 LIMPAR ESTE MÊS\n\n`
                    + `Vai apagar TODOS os status (folgas, freelas, férias, faltas, compensações) `
                    + `de ${mesNome} pra ${n} empregado(s)${schedArea==="Todos"?"":` da área ${schedArea}`}.\n\n`
                    + `Os dias voltam pro status "Trabalho". Salvar a escala depois confirma a alteração no banco.\n\n`
                    + `Pra confirmar, digite o nome do mês:\n${mesNome}`
                  );
                  if (typed == null) return;
                  if ((typed || "").trim().toLowerCase() !== mesNome.toLowerCase()) {
                    onUpdate("_toast", "Nome do mês não bateu — ação cancelada por segurança"); return;
                  }
                  const resetEdits = {};
                  areaEmps.forEach(emp => {
                    const empDayMap = effectiveMonth[emp.id] ?? {};
                    const empNulls = {};
                    Object.keys(empDayMap).forEach(date => { empNulls[date] = null; });
                    if (Object.keys(empNulls).length) resetEdits[emp.id] = empNulls;
                  });
                  if (Object.keys(resetEdits).length) {
                    setSchedLocalEdits(prev => {
                      const edits = prev ? { ...prev } : {};
                      Object.entries(resetEdits).forEach(([eid, dayMap]) => {
                        edits[eid] = { ...(edits[eid] ?? {}), ...dayMap };
                      });
                      return edits;
                    });
                  }
                  onUpdate("_toast", `🔄 ${mesNome} limpo pra ${n} empregado(s) — salve para confirmar`);
                }} style={{...S.btnSecondary,fontSize:12,color:"var(--red)",borderColor:"var(--red)44",alignSelf:"flex-start"}}>
                  🔄 Limpar este mês
                </button>}

                {/* Configurações avançadas — Apagar todas as escalas */}
                {isOwner && (
                  <details style={{marginTop:6,padding:"10px 12px",background:"var(--bg1)",borderRadius:8,border:"1px dashed var(--red)44"}}>
                    <summary style={{cursor:"pointer",fontSize:11,fontWeight:600,color:"var(--red)"}}>⚙️ Configurações avançadas (perigoso)</summary>
                    <div style={{marginTop:10}}>
                      <button onClick={async ()=>{
                        if (schedDirty && !await appConfirm("Você tem edições não salvas. Deseja descartar antes de continuar?")) return;
                        const expected = (restaurant?.name || "").trim();
                        const typed = window.prompt(
                          `🗑️ APAGAR TODAS AS ESCALAS\n\n`
                          + `Esta ação envia pra LIXEIRA todas as escalas de TODOS os meses e TODOS os empregados deste restaurante.\n`
                          + `Os dados ficam na lixeira por 90 dias (pode ser restaurado).\n\n`
                          + `Pra confirmar, digite o nome do restaurante:\n${expected}`
                        );
                        if (typed == null) return;
                        if ((typed || "").trim().toLowerCase() !== expected.toLowerCase()) {
                          onUpdate("_toast", "Nome não bateu — ação cancelada por segurança"); return;
                        }
                        const ok = resetTab("schedule","Escala",()=>({schedules:schedules?.[rid]}));
                        if(ok){ const s={...schedules}; delete s[rid]; onUpdate("schedules",s); setSchedLocalEdits(null); onUpdate("_toast","🗑️ Todas as escalas foram movidas pra lixeira"); }
                      }} style={{...S.btnSecondary,fontSize:12,color:"var(--red)",borderColor:"var(--red)44"}}>
                        🗑️ Apagar todas as escalas (lixeira)
                      </button>
                      <p style={{marginTop:6,fontSize:10,color:"var(--text3)",fontStyle:"italic"}}>
                        Apaga TODAS as escalas de TODOS os meses deste restaurante. Vai pra lixeira (90 dias) — pode ser restaurado.
                      </p>
                    </div>
                  </details>
                )}
              </div>
            </details>

            {/* v8.28.0 — Freelas do mês (planned/aberto/fechamento/pago/no_show) */}
            <FreelasMesNaEscala
              restaurantId={rid}
              year={year}
              month={month}
              freelaShifts={data?.freelaShifts ?? []}
              pessoas={data?.pessoas ?? []}
              mobileOnly={mobileOnly}
            />
          </div>
        )}

        {/* COMUNICADOS */}
        {tab === "comunicados" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:16}}>
              <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?16:20}}>📢 Comunicados</h3>
              {isOwner && <button onClick={()=>{
                const comms = (data?.communications??[]).filter(c=>c.restaurantId===rid);
                const ok = resetTab("comunicados","Comunicados",()=>({communications:comms}));
                if(ok){ onUpdate("communications",(data?.communications??[]).filter(c=>c.restaurantId!==rid)); onUpdate("_toast","🗑️ Comunicados enviados para a lixeira"); }
              }} style={{...S.btnSecondary,fontSize:12,color:"var(--red)",borderColor:"var(--red)44"}}>🗑️ Resetar</button>}
            </div>
            {privacyMask ? (
              <div style={{...S.card,textAlign:"center",padding:40}}>
                <div style={{fontSize:36,marginBottom:12}}>🔒</div>
                <p style={{color:"var(--text3)",fontSize:14}}>Conteúdo dos comunicados oculto pelo modo privacidade.</p>
              </div>
            ) : (
              <ComunicadosManagerTab
                restaurantId={rid} communications={data?.communications ?? []}
                commAcks={data?.commAcks ?? {}} employees={employees}
                onUpdate={onUpdate} currentManagerName={currentUser?.name ?? "Gestor Adm."}
                isOwner={isOwner} trash={data?.trash}
              />
            )}
          </div>
        )}

        {/* FAQ */}
        {tab === "faq" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:16}}>
              <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?16:20}}>❓ FAQ</h3>
              {isOwner && <button onClick={()=>{
                const faqRest = data?.faq?.[rid];
                const ok = resetTab("faq","FAQ",()=>({faq:faqRest}));
                if(ok){ const f={...data?.faq}; delete f[rid]; onUpdate("faq",f); onUpdate("_toast","🗑️ FAQ enviado para a lixeira"); }
              }} style={{...S.btnSecondary,fontSize:12,color:"var(--red)",borderColor:"var(--red)44"}}>🗑️ Resetar</button>}
            </div>

            {/* FAQs automáticas — expansíveis e com toggle */}
            {(()=>{
              // eslint-disable-next-line no-unused-vars
              const ac = "var(--ac)";
              const splitType = (localRest.divisionMode ?? MODE_AREA_POINTS) === MODE_AREA_POINTS ? "area" : "points";
              const taxRate = localRest.taxRate ?? TAX;
              const taxLabel = taxRate===0.20?"20% (Simples Nacional)":"33% (Lucro Real/Presumido)";
              const restRolesCom = (data?.roles??[]).filter(r=>r.restaurantId===rid&&!r.inactive&&!r.noTip);
              const restRolesSem = (data?.roles??[]).filter(r=>r.restaurantId===rid&&!r.inactive&&r.noTip);
              const totalPts = restRolesCom.reduce((s,r)=>s+(parseFloat(r.points)||0),0);
              const now2 = new Date();
              // v7.9.1: getSplitForDate (suporta versionado novo + legacy)
              const _todayStr2 = now2.toISOString().slice(0, 10);
              const _activeSplit2 = getSplitForDate(splits?.[rid], _todayStr2);
              const _empsPerArea2 = {};
              (data?.employees || [])
                .filter(e => e.restaurantId === rid && !e.isFreela && !(e.inactive && e.inactiveFrom && e.inactiveFrom <= _todayStr2))
                .forEach(e => {
                  const r = (data?.roles || []).find(x => x.id === e.roleId);
                  if (r?.area) _empsPerArea2[r.area] = (_empsPerArea2[r.area] || 0) + 1;
                });
              const curSplit2 = _activeSplit2?.percentages
                ? computeAreaPercentages(_activeSplit2.percentages, _empsPerArea2)
                : DEFAULT_SPLIT;
              const EX = 1000;
              const fmtR2 = n=>n.toLocaleString("pt-BR",{minimumFractionDigits:2,maximumFractionDigits:2});

              const FAQS_AUTO = [
                {
                  id:"__gorjeta__",
                  tabKey: null,
                  q:"💸 Como é calculada a gorjeta?",
                  a: splitType==="area"
                    ? `Modo atual: Área + Pontos\nA gorjeta total do dia é primeiro dividida pelo percentual de cada área. Depois, dentro de cada área, é dividida pelos pontos dos cargos presentes.\n\nDedução aplicada: ${taxLabel}\n\nPasso a passo:\n1. Gestor lança o valor total do dia\n2. Sistema separa o valor por área conforme os percentuais configurados (${AREAS.map(a=>`${a} ${curSplit2[a]??0}%`).join(", ")})\n3. Dentro de cada área, soma os pontos de quem trabalhou\n4. Divide o valor da área pelos pontos → valor por ponto da área\n5. Multiplica pelos pontos do cargo → bruto\n6. Deduz ${(taxRate*100).toFixed(0)}% → líquido\n\nExemplo (gorjeta R$${fmtR2(EX)}):\n• Salão recebe ${curSplit2["Salão"]??0}% → R$${fmtR2(EX*(curSplit2["Salão"]??0)/100)}\n• Cargo com 6pt de ${totalPts>0?totalPts:"X"}pt na área → bruto proporcional\n• Após ${(taxRate*100).toFixed(0)}% → líquido`
                    : `Modo atual: Pontos Global\nA gorjeta total do dia é somada e dividida diretamente pelos pontos de todos os empregados do restaurante que trabalharam, sem separação por área.\n\nDedução aplicada: ${taxLabel}\n\nPasso a passo:\n1. Gestor lança o valor total do dia\n2. Sistema soma os pontos de todos que trabalharam (total: ${totalPts}pt)\n3. Divide o total pelos pontos → valor por ponto\n4. Multiplica pelos pontos do cargo → bruto\n5. Deduz ${(taxRate*100).toFixed(0)}% → líquido\n\nExemplo (gorjeta R$${fmtR2(EX)}, ${totalPts}pt totais):\n• Valor por ponto: R$${totalPts>0?fmtR2(EX/totalPts):"—"}\n• Cargo com 6pt → R$${totalPts>0?fmtR2(EX/totalPts*6):"—"} bruto → R$${totalPts>0?fmtR2(EX/totalPts*6*(1-taxRate)):"—"} líquido`,
                },
                {
                  id:"__sistema__",
                  tabKey: null,
                  q: splitType==="area"?"🏢 Como funciona a divisão por área e pontos?":"📊 Como funciona a tabela de pontos?",
                  a: splitType==="area" ? (()=>{
                    const AREAS2=["Bar","Cozinha","Salão","Limpeza"];
                    const ativas=AREAS2.filter(a=>(curSplit2[a]??0)>0);
                    const linhas=ativas.map(a=>{
                      const pct=curSplit2[a]??0;
                      const cargos=restRolesCom.filter(r=>r.area===a);
                      const pts=cargos.reduce((s,r)=>s+(parseFloat(r.points)||0),0);
                      return `${a} — ${pct}%\n${cargos.map(r=>`   • ${r.name}: ${r.points}pt`).join("\n")}\n   Total: ${pts}pt`;
                    }).join("\n\n");
                    return `Sistema atual: Área + Pontos\n\nO valor total da gorjeta do dia é primeiro dividido pelo percentual configurado de cada área. Depois, dentro de cada área, o valor é dividido pelos pontos dos cargos dos empregados presentes.\n\nDistribuição por área:\n${linhas}${restRolesSem.length>0?"\n\nCargos sem gorjeta: "+restRolesSem.map(r=>r.name).join(", "):""}`;
                  })() : (()=>{
                    const linhas=restRolesCom.sort((a,b)=>(parseFloat(b.points)||0)-(parseFloat(a.points)||0)).map(r=>{
                      const pct=totalPts>0?((parseFloat(r.points)||0)/totalPts*100).toFixed(1):"0";
                      return `• ${r.name}: ${r.points}pt (${pct}%)`;
                    }).join("\n");
                    return `Sistema atual: Pontos Global\n\nTodos os pontos de todos os empregados do restaurante que trabalharam no dia são somados. O valor total da gorjeta é dividido por essa soma, e cada empregado recebe proporcionalmente aos pontos do seu cargo. Não há separação por área.\n\nTabela de cargos (${totalPts}pt total):\n${linhas}${restRolesSem.length>0?"\n\nCargos sem gorjeta: "+restRolesSem.map(r=>r.name).join(", "):""}`;
                  })(),
                },
                { id:"__escala__", tabKey:null, q:"📅 Como funciona a escala e por que ela importa?", a:"A escala registra a presença em cada dia e define quem recebe gorjeta.\n\nRecebe gorjeta: trabalhando normalmente ou compensação (C).\nNÃO recebe: folga, freela (FL), falta injustificada (F) + penalidade, falta justificada (FJ), atestado (A), férias (V).\n\nExceções:\n• Produção (🏭): recebe gorjeta todos os dias, exceto férias. Penalidade com percentuais distintos para cada tipo de falta.\n• Freela (🎯): nunca participa do rateio de gorjeta.\n• Status Freela no dia: empregado presente mas sem gorjeta naquele dia.\n\nNenhum empregado recebe gorjeta durante férias, incluindo os de produção." },
                { id:"__producao__", tabKey:null, q:`🏭 O que é empregado de produção?`, a:`Empregados marcados como "Produção" têm regras especiais de gorjeta:\n\n1. Recebem gorjeta TODOS os dias (trabalhando, folga, compensação, etc.)\n2. NÃO recebem gorjeta durante férias — assim como qualquer outro empregado\n3. A distribuição segue os pontos do cargo normalmente\n4. Penalidade por falta injustificada: ${localRest.producaoPenaltyU??6.66}% do pool mensal por dia\n5. Penalidade por falta justificada: ${localRest.producaoPenaltyJ??3.33}% do pool mensal por dia\n\nExemplo: pool mensal de R$10.000, 2 faltas injustificadas (${localRest.producaoPenaltyU??6.66}%) + 1 justificada (${localRest.producaoPenaltyJ??3.33}%) → desconto de ${((localRest.producaoPenaltyU??6.66)*2+(localRest.producaoPenaltyJ??3.33))}% = R$${fmtR2(10000*((localRest.producaoPenaltyU??6.66)*2+(localRest.producaoPenaltyJ??3.33))/100)}\n\nO status de produção é definido pelo gestor na aba Equipe, usando o ícone 🏭 ao lado do cargo. Pode ser atribuído a empregados de qualquer área.` },
                { id:"__freela__", tabKey:null, q:"🎯 O que é empregado freela?", a:"Empregados marcados como 'Freela' são colaboradores esporádicos que cobrem a equipe:\n\n1. Aparecem normalmente na escala\n2. Nunca participam do rateio de gorjeta\n3. Cargo e área são atribuídos normalmente\n\nAlém do flag no cadastro, existe o status 'Freela' (FL) na escala: marca um empregado regular como presente sem gorjeta naquele dia (ex: veio cobrir na folga).\n\nDefina o flag na aba Equipe usando o ícone 🎯 ao lado do cargo." },
                { id:"__dp__", tabKey:"dp", q:"💬 Para que serve o Fale com DP?", a:"Canal direto com o DP. Use para: dúvidas trabalhistas, atestados, documentos, sugestões, elogios e denúncias anônimas. O gestor do DP responde pelo app." },
                { id:"__comunicados__", tabKey:"comunicados", q:"📢 Como funcionam os comunicados?", a:"Avisos enviados pelo gestor para a equipe. O empregado recebe notificação, lê e confirma com \"Li e entendi\". O gestor acompanha quem confirmou." },
                { id:"__pin__", tabKey:null, q:"🔐 O que é o PIN e como trocar?", a:"O PIN é a senha de 4 dígitos numéricos. Para login: ID de empregado (ex: LBZ0005) ou CPF + PIN. Para trocar o PIN, solicite ao gestor que faça o reset." },
              ];

              // Estado local simulado via dataset (sem useState fora de componente)
              return (
                <div style={{padding:"16px 16px 0"}}>
                  <div style={{padding:"14px",borderRadius:12,background:"var(--ac-bg)",border:"1px solid var(--ac)33",marginBottom:16}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                      <span style={{fontSize:14}}>📐</span>
                      <span style={{color:"var(--ac-text)",fontWeight:700,fontSize:13}}>FAQs automáticas do sistema</span>
                    </div>
                    <p style={{color:"var(--text2)",fontSize:12,margin:"0 0 12px",lineHeight:1.5}}>
                      Geradas automaticamente com as regras do restaurante. Aparecem na seção "Regras do sistema" do FAQ do empregado.
                      <br/>
                      <span style={{color:"var(--text3)",fontSize:11}}>💡 Para ocultar perguntas individualmente, vá em <strong>Configurações → 👁️ O que o empregado vê</strong>.</span>
                    </p>
                    <div style={{display:"flex",flexDirection:"column",gap:6}}>
                      {FAQS_AUTO.map((item) => {
                        // v8.10: aqui só pré-visualização do conteúdo — toggle vive em Configurações.
                        const faqAutoOk = restaurant?.tabsGestor?.faqAuto?.[safeFaqKey(item.id)] !== false;
                        const visivel = faqAutoOk;
                        return (
                          <details key={item.id} style={{borderRadius:10,background:"var(--card-bg)",border:`1px solid ${visivel?"var(--ac)22":"var(--border)"}`,overflow:"hidden",opacity:visivel?1:0.55}}>
                            <summary style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 12px",cursor:"pointer",listStyle:"none",gap:8}}>
                              <span style={{fontSize:13,fontWeight:600,color:visivel?"var(--text)":"var(--text3)",flex:1}}>{item.q}</span>
                              <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                                <span style={{fontSize:10,fontWeight:700,padding:"3px 9px",borderRadius:20,background:visivel?"#10b98122":"var(--bg2)",color:visivel?"var(--green)":"var(--text3)",border:`1px solid ${visivel?"#10b98144":"var(--border)"}`,fontFamily:"'DM Sans',sans-serif",whiteSpace:"nowrap"}}>
                                  {visivel?"👁 Exibindo":"🚫 Oculto"}
                                </span>
                                <span style={{color:"var(--text3)",fontSize:12}}>▾</span>
                              </div>
                            </summary>
                            <div style={{padding:"10px 12px 12px",color:"var(--text2)",fontSize:12,lineHeight:1.7,borderTop:"1px solid var(--ac)22",whiteSpace:"pre-wrap",fontFamily:"'DM Mono',monospace"}}>
                              {item.a}
                            </div>
                          </details>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })()}

            <FaqManagerTab restaurantId={rid} faq={data?.faq ?? {}} onUpdate={onUpdate} restaurant={restaurant} />
          </div>
        )}

        {/* FALE COM DP */}
        {tab === "dp" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:16}}>
              <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?16:20}}>💬 Fale com DP</h3>
              {isOwner && <button onClick={()=>{
                const msgs = (data?.dpMessages??[]).filter(m=>m.restaurantId===rid);
                const ok = resetTab("dp","Fale com DP",()=>({dpMessages:msgs}));
                if(ok){ onUpdate("dpMessages",(data?.dpMessages??[]).filter(m=>m.restaurantId!==rid)); onUpdate("_toast","🗑️ Mensagens DP enviadas para a lixeira"); }
              }} style={{...S.btnSecondary,fontSize:12,color:"var(--red)",borderColor:"var(--red)44"}}>🗑️ Resetar</button>}
            </div>
            {privacyMask ? (
              <div style={{...S.card,textAlign:"center",padding:40}}>
                <div style={{fontSize:36,marginBottom:12}}>🔒</div>
                <p style={{color:"var(--text3)",fontSize:14}}>Mensagens do DP ocultas pelo modo privacidade.</p>
              </div>
            ) : (
              <DpManagerTab restaurantId={rid} dpMessages={data?.dpMessages ?? []} onUpdate={onUpdate} isOwner={isOwner} />
            )}
          </div>
        )}

        {/* HORARIOS */}
        {tab === "horarios" && (
          <div>
            <WorkScheduleManagerTab restaurantId={rid} employees={employees} roles={roles} workSchedules={data?.workSchedules??{}} notifications={data?.notifications??[]} managers={data?.managers??[]} currentManagerName={currentUser?.name ?? (isOwner?"Master":"Gestor Adm.")} onUpdate={onUpdate} communications={data?.communications??[]} isOwner={isOwner} mobileOnly={mobileOnly} />
          </div>
        )}

        {/* VALE TRANSPORTE */}
        {tab === "vt" && (
          <ValeTransporteTab restaurantId={rid} employees={employees} roles={roles} workSchedules={data?.workSchedules??{}} schedules={data?.schedules??{}} vtConfig={data?.vtConfig??{}} vtMonthly={data?.vtMonthly??{}} vtPayments={data?.vtPayments??{}} onUpdate={onUpdate} currentUser={currentUser} isOwner={isOwner} mobileOnly={mobileOnly} schedulePrevista={data?.schedulePrevista??{}} scheduleStatus={data?.scheduleStatus??{}} scheduleVersions={data?.scheduleVersions??{}} onExit={()=>setTab("inicio")} />
        )}

        {/* REGRA DE DIVISÃO — versionada com ata + ativação por data */}
        {tab === "regra_divisao" && (
          <RegraDivisaoAdmin
            restaurantId={rid}
            restaurant={restaurant}
            splits={splits}
            pessoas={data?.pessoas ?? []}
            roles={restRoles}
            employees={employees.filter(e => e.restaurantId === rid)}
            tips={tips}
            currentUser={currentUser}
            onUpdate={onUpdate}
            mobileOnly={mobileOnly}
            isOwner={isOwner}
          />
        )}

        {/* v8.15.0: RECURSOS — hub de pastas + itens */}
        {tab === "recursos" && (
          <RecursosTab
            restaurantId={rid}
            recursoFolders={data?.recursoFolders ?? []}
            recursos={data?.recursos ?? []}
            recursosInitialized={data?.recursosInitialized ?? {}}
            employees={employees}
            pessoas={data?.pessoas ?? []}
            managers={data?.managers ?? []}
            currentUser={currentUser}
            isOwner={isOwner}
            isDP={isDP}
            isLider={isLider}
            mobileOnly={mobileOnly}
            onUpdate={onUpdate}
          />
        )}

        {/* RESERVAS — módulo de reservas de mesa */}
        {(tab === "reservas_hoje" || tab === "reservas_calendario" || tab === "reservas_clientes" || tab === "reservas_config") && (
          <ReservasTab
            tab={tab}
            restaurantId={rid}
            restaurant={restaurant}
            reservas={data?.reservas ?? []}
            reservasConfig={data?.reservasConfig ?? {}}
            reservasSalas={data?.reservasSalas ?? []}
            reservasTiposMesa={data?.reservasTiposMesa ?? []}
            reservasClientes={data?.reservasClientes ?? []}
            currentUser={currentUser}
            perms={perms}
            isOwner={isOwner}
            mobileOnly={mobileOnly}
            onUpdate={onUpdate}
          />
        )}

        {/* TRILHA — integrada na aba Equipe */}

        {/* NOTIFICAÇÕES */}
        {tab === "notificacoes" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:16}}>
              <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?16:20}}>📬 Notificações</h3>
            </div>
          {privacyMask ? (
            <div style={{...S.card,textAlign:"center",padding:40,margin:20}}>
              <div style={{fontSize:36,marginBottom:12}}>🔒</div>
              <p style={{color:"var(--text3)",fontSize:14}}>Notificações ocultas pelo modo privacidade.</p>
            </div>
          ) : (
            // DP tem canal próprio (aba Fale com DP) — passa array vazio pra caixa não misturar
            <NotificacoesTab restaurantId={rid} dpMessages={[]} notifications={data?.notifications??[]} onUpdate={onUpdate} isOwner={isOwner} inbox={data?.inbox??[]} managerId={currentUser?.id} managerRole={userRoleForInbox} inboxFolders={data?.inboxFolders??{}} />
          )}
          </div>
        )}


        {/* GESTORES (DP) */}
        {tab === "dp_gestores" && (isDP || isOwner) && (() => {
          const managers = data?.managers ?? [];
          const myId = currentUser?.id;
          // eslint-disable-next-line no-unused-vars
          const myRestIds = currentUser?.restaurantIds ?? [];
          const currentIsMasterDP = (currentUser?.masterRestaurantIds ?? []).includes(rid) || isOwner;
          const restMgrs = managers.filter(m => (m.restaurantIds??[]).includes(rid) && m.id !== myId);
          const canEdit = (m) => currentIsMasterDP;
          const mastersInRest = managers.filter(m => (m.restaurantIds??[]).includes(rid) && m.profile === "dp" && (m.masterRestaurantIds??[]).includes(rid));

          return (
            <div style={{padding:"24px",maxWidth:700,margin:"0 auto"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
                <div>
                  <h3 style={{color:"var(--text)",fontSize:16,fontWeight:700,margin:"0 0 4px"}}>Gestores do restaurante</h3>
                  <p style={{color:"var(--text3)",fontSize:12,margin:0}}>{currentIsMasterDP ? "Crie e gerencie gestores para este restaurante" : "Visualize os gestores deste restaurante"}</p>
                </div>
                {currentIsMasterDP && <button onClick={()=>{
                  setDpMgrEdit(null);
                  setDpMgrForm({name:"",cpf:"",pin:"",restaurantIds:[rid],perms:{tips:true,schedule:true},isDP:false,profile:"custom",areas:[],masterRestaurantIds:[]});
                  setDpMgrModal(true);
                }} style={{...S.btnPrimary,width:"auto",padding:"10px 20px"}}>+ Novo Gestor</button>}
              </div>

              {restMgrs.length === 0 && (
                <div style={{...S.card,textAlign:"center",padding:40}}>
                  <div style={{fontSize:36,marginBottom:12}}>👔</div>
                  <p style={{color:"var(--text3)",fontSize:14}}>Nenhum outro gestor neste restaurante.</p>
                </div>
              )}

              {restMgrs.map(m => {
                const profileLabel = m.profile==="dp"?"📬 Gestor Adm.":m.profile==="lider"?"👔 Líder Op.":"⚙️ Custom";
                const mine = canEdit(m);
                const isLastMaster = (m.masterRestaurantIds??[]).includes(rid) && mastersInRest.length <= 1;
                return (
                  <div key={m.id} style={{...S.card,marginBottom:10,display:"flex",justifyContent:"space-between",alignItems:"center",gap:12}}>
                    <div>
                      <div style={{display:"flex",alignItems:"center",gap:6}}>
                        <span style={{color:"var(--text)",fontWeight:700,fontSize:14}}>{m.name}</span>
                        {(m.masterRestaurantIds??[]).includes(rid) && <span style={{background:"#d4a01722",color:"#d4a017",borderRadius:6,padding:"2px 8px",fontSize:10,fontWeight:700}}>🔑 Master</span>}
                      </div>
                      <div style={{color:"var(--text3)",fontSize:11}}>
                        {profileLabel}
                        {m.profile==="lider" && (m.areas??[]).length>0 && ` · ${m.areas.join(", ")}`}
                        {m.cpf && ` · ${privacyMask ? "•••.•••.•••-••" : m.cpf}`}
                      </div>
                      {!mine && <div style={{color:"var(--text3)",fontSize:10,marginTop:2,fontStyle:"italic"}}>Criado pelo admin</div>}
                    </div>
                    {mine && (
                      <div style={{display:"flex",gap:6,flexShrink:0,flexWrap:"wrap"}}>
                        <button onClick={async ()=>{
                          const cpfDigits = (m.cpf??"").replace(/\D/g,"");
                          if (cpfDigits.length < 4) { onUpdate("_toast","⚠️ CPF inválido — não foi possível gerar PIN automático"); return; }
                          const newPin = cpfDigits.slice(0,4);
                          if(!await appConfirm(`Resetar PIN de "${m.name}"?\n\nO PIN voltará para ${newPin} (4 primeiros dígitos do CPF) e ele será obrigado a trocar no próximo acesso.`)) return;
                          // v7.7: sincroniza reset nos 3 records (manager + pessoa + employee linked) com mustChangePin=true
                          const allPessoas = data?.pessoas || [];
                          const allEmployees = data?.employees || [];
                          onUpdate("managers", managers.map(x=>{
                            if (x.id === m.id) return { ...x, pin:newPin, mustChangePin:true };
                            if ((x.cpf||"").replace(/\D/g,"") === cpfDigits) return { ...x, pin:newPin, mustChangePin:true };
                            return x;
                          }));
                          if (allPessoas.length > 0) onUpdate("pessoas", allPessoas.map(p=>{
                            if (p.linkedManagerId === m.id) return { ...p, pin:newPin, mustChangePin:true };
                            if ((p.cpf||"").replace(/\D/g,"") === cpfDigits) return { ...p, pin:newPin, mustChangePin:true };
                            return p;
                          }));
                          if (allEmployees.length > 0) onUpdate("employees", allEmployees.map(e=>{
                            if ((e.cpf||"").replace(/\D/g,"") === cpfDigits) return { ...e, pin:newPin, mustChangePin:true };
                            return e;
                          }));
                          onUpdate("_toast",`🔐 PIN de ${m.name} resetado para ${newPin}`);
                        }} title="Resetar PIN para os 4 primeiros dígitos do CPF" style={{...S.btnSecondary,fontSize:11,padding:"5px 10px"}}>🔐 Resetar PIN</button>
                        <button onClick={()=>{
                          setDpMgrEdit(m.id);
                          setDpMgrForm({name:m.name,cpf:m.cpf??"",pin:m.pin??"",restaurantIds:m.restaurantIds??[],perms:m.perms??{tips:true,schedule:true},isDP:m.isDP??false,profile:m.profile??"custom",areas:m.areas??[],masterRestaurantIds:m.masterRestaurantIds??[]});
                          setDpMgrModal(true);
                        }} style={{...S.btnSecondary,fontSize:11,padding:"5px 12px"}}>Editar</button>
                        <button onClick={async ()=>{
                          if (isLastMaster) { onUpdate("_toast","⚠️ Este é o último Master do restaurante. Nomeie outro Master antes de excluir."); return; }
                          if(!await appConfirm(`Excluir gestor "${m.name}"?`)) return;
                          onUpdate("managers",managers.filter(x=>x.id!==m.id));
                          onUpdate("_toast",`🗑️ ${m.name} excluído`);
                        }} style={{...S.btnSecondary,fontSize:11,padding:"5px 10px",color:isLastMaster?"var(--text3)":"var(--red)",borderColor:isLastMaster?"var(--border)":"var(--red)44"}}>✕</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Modal novo gestor (DP) */}
        {dpMgrModal && (isDP || isOwner) && (
          <Modal title={dpMgrEdit?"Editar Gestor":"Novo Gestor"} onClose={()=>setDpMgrModal(false)} wide>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              {/* Puxar da equipe */}
              {!dpMgrEdit && (() => {
                const restEmpsList = restEmps.filter(e => !e.inactive);
                if (restEmpsList.length === 0) return null;
                return (
                  <div style={{background:"var(--bg2)",border:"1px solid var(--border)",borderRadius:10,padding:"10px 14px"}}>
                    <label style={{...S.label,margin:0}}>👥 Puxar dados de um empregado da equipe</label>
                    <p style={{color:"var(--text3)",fontSize:11,margin:"4px 0 8px"}}>Selecione para pré-preencher nome e CPF. O empregado continua ativo na equipe.</p>
                    <select onChange={e => { const emp = restEmpsList.find(x => x.id === e.target.value); if (!emp) return; setDpMgrForm(f => ({...f, name: emp.name, cpf: emp.cpf ?? ""})); }} style={{...S.input,cursor:"pointer"}} defaultValue="">
                      <option value="" disabled>Selecionar empregado...</option>
                      {restEmpsList.sort((a,b)=>a.name.localeCompare(b.name)).map(e => { const role = restRoles.find(r => r.id === e.roleId); return <option key={e.id} value={e.id}>{e.name}{role ? ` — ${role.name}` : ""}{e.cpf ? ` (${e.cpf})` : ""}</option>; })}
                    </select>
                  </div>
                );
              })()}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <div><label style={S.label}>Nome completo</label><input value={dpMgrForm.name} onChange={e=>setDpMgrForm({...dpMgrForm,name:e.target.value})} style={S.input}/></div>
                <div><label style={S.label}>CPF *</label><input value={dpMgrForm.cpf} onChange={e=>setDpMgrForm({...dpMgrForm,cpf:maskCpf(e.target.value)})} placeholder="000.000.000-00" style={S.input} inputMode="numeric"/></div>
              </div>
              {dpMgrEdit
                ? <div><label style={S.label}>PIN (4 dígitos)</label><input type="password" value={dpMgrForm.pin} onChange={e=>setDpMgrForm({...dpMgrForm,pin:e.target.value})} maxLength={4} inputMode="numeric" style={S.input}/></div>
                : <div style={{padding:"8px 12px",borderRadius:8,background:"var(--bg2)",border:"1px solid var(--border)",fontSize:12,color:"var(--text3)"}}> PIN inicial = 4 primeiros dígitos do CPF. No primeiro acesso o gestor será solicitado a trocar.</div>
              }

              {/* Perfil */}
              <div>
                <label style={S.label}>Perfil do gestor</label>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {[
                    {id:"dp",icon:"📬",title:"Gestor Administrativo",desc:"Acesso completo: gorjetas, escala, cargos, equipe, comunicados, FAQ, DP e notificações."},
                    {id:"lider",icon:"👔",title:"Líder Operacional",desc:"Acesso apenas à escala e horários da(s) sua(s) área(s)."},
                    {id:"custom",icon:"⚙️",title:"Personalizado",desc:"Escolha as permissões manualmente."},
                  ].map(p=>{
                    const on = dpMgrForm.profile === p.id;
                    return (
                      <button key={p.id} onClick={()=>{
                        const presets = {
                          dp: {perms:{tips:true,schedule:true,roles:true,employees:true,comunicados:true,faq:true,dp:true,horarios:true},isDP:true,areas:[]},
                          lider: {perms:{tips:false,schedule:true,roles:false,employees:false,comunicados:false,faq:false,dp:false,horarios:true,vt:false},isDP:false},
                          custom: {isDP:dpMgrForm.isDP},
                        };
                        setDpMgrForm(f=>({...f,...presets[p.id],profile:p.id,areas:p.id==="lider"?f.areas:[]}));
                      }}
                        style={{width:"100%",padding:"12px 14px",borderRadius:10,border:`1px solid ${on?ac:"var(--border)"}`,background:on?"var(--ac-bg)":"transparent",color:on?"var(--ac-text)":"var(--text3)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,textAlign:"left",display:"flex",alignItems:"center",gap:10}}>
                        <span style={{fontSize:18}}>{p.icon}</span>
                        <div>
                          <div style={{fontWeight:700}}>{on?"✓ ":""}{p.title}</div>
                          <div style={{fontSize:11,opacity:0.7,marginTop:2}}>{p.desc}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Master toggle — só para Gestor Adm. e visível para Master */}
              {dpMgrForm.profile === "dp" && currentIsMasterDP && (
                <div style={{borderTop:"1px solid var(--border)",paddingTop:12}}>
                  <button onClick={()=>{const ids=dpMgrForm.masterRestaurantIds??[];setDpMgrForm({...dpMgrForm,masterRestaurantIds:ids.includes(rid)?ids.filter(x=>x!==rid):[...ids,rid]});}}
                    style={{width:"100%",padding:"12px 14px",borderRadius:10,border:`1px solid ${(dpMgrForm.masterRestaurantIds??[]).includes(rid)?"#d4a017":"var(--border)"}`,background:(dpMgrForm.masterRestaurantIds??[]).includes(rid)?"#d4a01715":"transparent",color:(dpMgrForm.masterRestaurantIds??[]).includes(rid)?"#d4a017":"var(--text3)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,textAlign:"left",display:"flex",alignItems:"center",gap:10}}>
                    <span style={{fontSize:18}}>🔑</span>
                    <div>
                      <div style={{fontWeight:700}}>{(dpMgrForm.masterRestaurantIds??[]).includes(rid)?"✓ Master do Restaurante":"○ Master do Restaurante"}</div>
                      <div style={{fontSize:11,opacity:0.7,marginTop:2}}>Pode criar, editar e excluir outros gestores deste restaurante</div>
                    </div>
                  </button>
                </div>
              )}

              {/* Áreas Líder */}
              {dpMgrForm.profile === "lider" && (
                <div>
                  <label style={S.label}>Áreas do líder</label>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {AREAS.map(a=>{
                      const on = (dpMgrForm.areas??[]).includes(a);
                      return <button key={a} onClick={()=>setDpMgrForm(f=>({...f,areas:on?f.areas.filter(x=>x!==a):[...(f.areas??[]),a]}))}
                        style={{padding:"8px 16px",borderRadius:20,border:`1px solid ${on?AREA_COLORS[a]??"#555":"var(--border)"}`,background:on?(AREA_COLORS[a]??"#555")+"22":"transparent",color:on?AREA_COLORS[a]??"var(--text)":"var(--text3)",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12,fontWeight:on?700:400}}>
                        {on?"✓ ":""}{a}
                      </button>;
                    })}
                  </div>
                  {(dpMgrForm.areas??[]).length===0 && <p style={{color:"var(--red)",fontSize:11,marginTop:6}}>Selecione pelo menos uma área.</p>}
                </div>
              )}

              {/* Custom perms */}
              {dpMgrForm.profile === "custom" && (
                <div>
                  <label style={S.label}>Permissões</label>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                    {[["tips","💸 Gorjetas"],["schedule","📅 Escala"],["roles","🏷️ Cargos"],["employees","👥 Equipe"],["comunicados","📢 Comunicados"],["faq","❓ FAQ"],["dp","💬 Fale c/ DP"],["horarios","🕐 Horários"],["vt","🚌 Vale Transporte"]].map(([k,lbl])=>{
                      const on = dpMgrForm.perms?.[k] !== false;
                      return (
                        <button key={k} onClick={()=>setDpMgrForm({...dpMgrForm,perms:{...dpMgrForm.perms,[k]:!on}})}
                          style={{padding:"10px",borderRadius:10,border:`1px solid ${on?"var(--green)":"var(--border)"}`,background:on?"var(--green-bg)":"transparent",color:on?"var(--green)":"var(--text3)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:12,textAlign:"left"}}>
                          {on?"✓":"○"} {lbl}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {dpMgrForm.profile === "custom" && (
                <div style={{borderTop:"1px solid var(--border)",paddingTop:12}}>
                  <button onClick={()=>setDpMgrForm({...dpMgrForm,isDP:!dpMgrForm.isDP})}
                    style={{width:"100%",padding:"12px 14px",borderRadius:10,border:`1px solid ${dpMgrForm.isDP?"var(--blue)":"var(--border)"}`,background:dpMgrForm.isDP?"var(--blue-bg)":"transparent",color:dpMgrForm.isDP?"var(--blue)":"var(--text3)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,textAlign:"left",display:"flex",alignItems:"center",gap:10}}>
                    <span style={{fontSize:18}}>📬</span>
                    <div>
                      <div style={{fontWeight:700}}>{dpMgrForm.isDP?"✓ Recebe mensagens do Fale com DP":"○ Não recebe mensagens do Fale com DP"}</div>
                      <div style={{fontSize:11,opacity:0.7,marginTop:2}}>Recebe notificações de horários e mensagens do Fale com DP</div>
                    </div>
                  </button>
                </div>
              )}

              {/* Restaurantes */}
              {(currentUser?.restaurantIds??[]).length > 1 && (
                <div>
                  <label style={S.label}>Restaurantes com acesso</label>
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {restaurants.filter(r=>(currentUser?.restaurantIds??[]).includes(r.id)).map(r=>{
                      const sel = (dpMgrForm.restaurantIds??[]).includes(r.id);
                      return (
                        <button key={r.id} onClick={()=>setDpMgrForm(f=>({...f,restaurantIds:sel?f.restaurantIds.filter(x=>x!==r.id):[...(f.restaurantIds??[]),r.id]}))}
                          style={{padding:"10px 14px",borderRadius:10,border:`1px solid ${sel?ac:"var(--border)"}`,background:sel?"var(--ac-bg)":"transparent",color:sel?"var(--ac-text)":"var(--text3)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,textAlign:"left"}}>
                          {sel?"✓":"○"} {r.name} {r.id===rid?"(atual)":""}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <button onClick={()=>{
                if(!dpMgrForm.name.trim()) { onUpdate("_toast","⚠️ Nome é obrigatório"); return; }
                const cpfDigits = (dpMgrForm.cpf??"").replace(/\D/g,"");
                if(cpfDigits.length<11) { onUpdate("_toast","⚠️ CPF é obrigatório (11 dígitos)"); return; }
                if(dpMgrForm.profile==="lider"&&(dpMgrForm.areas??[]).length===0) { onUpdate("_toast","⚠️ Selecione pelo menos uma área"); return; }
                const managers = data?.managers ?? [];
                const isNew = !dpMgrEdit;
                const pin = isNew ? cpfDigits.slice(0,4) : (dpMgrForm.pin || cpfDigits.slice(0,4));
                // Auto-master: primeiro gestor adm. do restaurante vira master
                const existingDPs = managers.filter(x => x.profile === "dp" && (x.restaurantIds??[]).includes(rid));
                const autoMaster = isNew && dpMgrForm.profile === "dp" && existingDPs.length === 0;
                const masterIds = autoMaster ? [...new Set([...(dpMgrForm.masterRestaurantIds??[]),rid])] : (dpMgrForm.masterRestaurantIds??[]);
                const m = { ...dpMgrForm, pin, id: dpMgrEdit ?? Date.now().toString(), createdBy: currentUser?.id, masterRestaurantIds: masterIds, ...(isNew ? {mustChangePin:true} : {}) };
                onUpdate("managers", dpMgrEdit ? managers.map(x=>x.id===dpMgrEdit?{...x,...m}:x) : [...managers,m]);
                setDpMgrModal(false);
                onUpdate("_toast", dpMgrEdit?"✅ Gestor atualizado": autoMaster?"✅ Gestor criado como Master (primeiro do restaurante)":"✅ Gestor criado");
              }} style={S.btnPrimary}>{dpMgrEdit?"Salvar":"Criar Gestor"}</button>
            </div>
          </Modal>
        )}

        {/* CONFIG */}
        {tab === "config" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:16}}>
              <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?16:20}}>⚙️ Configurações</h3>
            </div>
            {/* Salvar / Descartar configurações — topo fixo */}
            {configDirty && (
              <div style={{position:"sticky",top:0,zIndex:50,marginBottom:16}}>
                <div style={{background:"var(--card-bg)",border:"2px solid var(--ac)",borderRadius:14,padding:"14px 18px",boxShadow:"0 4px 20px rgba(0,0,0,0.15)"}}>
                  <p style={{color:"var(--ac)",fontSize:13,fontWeight:700,margin:"0 0 10px"}}>⚠️ Alterações não salvas</p>
                  <div style={{display:"flex",gap:10}}>
                    <button onClick={saveConfig} style={{...S.btnPrimary,flex:1,padding:"12px",fontSize:14,fontWeight:700}}>Salvar Configurações</button>
                    <button onClick={discardConfig} style={{...S.btnSecondary,padding:"12px 16px",fontSize:13}}>Descartar</button>
                  </div>
                </div>
              </div>
            )}
            {/* v8.10.1: card "Abas do painel admin" removido — Cargos/Equipe agora vão pelo
                 controle por pessoa em Pessoas → Permissões (perms.roles / perms.employees). */}

            {/* v8.10: Portal do empregado — Abas + FAQ unificados num só lugar */}
            <div style={{...S.card,marginBottom:20}}>
              <p style={{color:ac,fontSize:14,fontWeight:700,margin:"0 0 4px"}}>👁️ O que o empregado vê</p>
              <p style={{color:"var(--text3)",fontSize:12,marginBottom:14}}>Tudo que aparece no portal do empregado deste restaurante — abas do app e perguntas do FAQ.</p>

              {/* Bloco A: Abas do app */}
              <div style={{marginBottom:18}}>
                <p style={{color:"var(--text2)",fontSize:11,fontWeight:700,margin:"0 0 8px",textTransform:"uppercase",letterSpacing:0.6,fontFamily:"'DM Mono',monospace"}}>📱 Abas do app · 8 abas</p>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {[
                    ["comunicados","📢 Avisos"],
                    ["escala",     "📅 Escala"],
                    ["gorjeta",    "💸 Gorjeta"],
                    ["trilha",     "📈 Trilha"],
                    ["horarios",   "🕐 Horários"],
                    ["faq",        "❓ FAQ"],
                    ["dp",         "💬 Fale com DP"],
                    ["empInbox",   "📬 Caixa"],
                  ].map(([key, label]) => {
                    // Gorjeta: fonte da verdade é o flag legado showTipsToEmployee (default off / opt-in)
                    if (key === "gorjeta") {
                      const isOn = localRest.showTipsToEmployee === true;
                      return (
                        <div key={key} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",background:"var(--bg1)",borderRadius:10,border:`1px solid ${isOn?"#10b98133":"var(--border)"}`}}>
                          <div style={{display:"flex",flexDirection:"column",gap:2}}>
                            <span style={{color:isOn?"var(--text)":"var(--text3)",fontSize:13,fontWeight:isOn?600:400}}>{label}</span>
                            <span style={{color:"var(--text3)",fontSize:10}}>Opt-in. Ative apenas após confirmação semanal pelo DP estar funcionando.</span>
                          </div>
                          <button onClick={()=>patchConfig({ showTipsToEmployee: !localRest.showTipsToEmployee })} style={{padding:"5px 14px",borderRadius:20,border:"none",background:isOn?"var(--green)":"var(--border)",color:isOn?"#fff":"#555",fontWeight:700,cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12}}>
                            {isOn?"Visível":"Oculta"}
                          </button>
                        </div>
                      );
                    }
                    // Default: dual-tier (admin authorize + gestor escolhe)
                    const adminOk  = getTabsConfig(key);
                    const gestorOk = getTabsGestor(key);
                    const isOn = adminOk && gestorOk;
                    const adminBlocked = !isOwner && !adminOk;
                    const onClick = () => {
                      if (adminBlocked) return;
                      if (isOwner) toggleAdminTab(key);
                      else toggleGestorTab(key);
                    };
                    return (
                      <div key={key} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",background:"var(--bg1)",borderRadius:10,border:`1px solid ${isOn?"#10b98133":"var(--border)"}`,opacity:adminBlocked?0.55:1}}>
                        <div style={{display:"flex",flexDirection:"column",gap:2}}>
                          <span style={{color:isOn?"var(--text)":"var(--text3)",fontSize:13,fontWeight:isOn?600:400}}>{label}</span>
                          {adminBlocked && <span style={{fontSize:10,color:"var(--text3)"}}>🔒 Bloqueado pelo admin</span>}
                        </div>
                        <button disabled={adminBlocked} onClick={onClick} style={{padding:"5px 14px",borderRadius:20,border:"none",background:isOn?"var(--green)":"var(--border)",color:isOn?"#fff":"#555",fontWeight:700,cursor:adminBlocked?"not-allowed":"pointer",fontFamily:"'DM Mono',monospace",fontSize:12}}>
                          {isOn?"Visível":"Oculta"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Bloco B: Perguntas do FAQ */}
              <div>
                <p style={{color:"var(--text2)",fontSize:11,fontWeight:700,margin:"0 0 4px",textTransform:"uppercase",letterSpacing:0.6,fontFamily:"'DM Mono',monospace"}}>❓ Perguntas do FAQ · 8 perguntas</p>
                <p style={{color:"var(--text3)",fontSize:11,margin:"0 0 8px"}}>Aparecem na seção "Regras do sistema" do FAQ do empregado. Independente das abas — pode esconder o FAQ-aba e manter perguntas, ou vice-versa.</p>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {[
                    {id:"__gorjeta__",    label:"💸 Como é calculada a gorjeta?"},
                    {id:"__sistema__",    label:"🏢 Como funciona a divisão (área / pontos)?"},
                    {id:"__escala__",     label:"📅 Como funciona a escala?"},
                    {id:"__producao__",   label:"🏭 O que é empregado de produção?"},
                    {id:"__freela__",     label:"🎯 O que é empregado freela?"},
                    {id:"__dp__",         label:"💬 Para que serve o Fale com DP?"},
                    {id:"__comunicados__",label:"📢 Como funcionam os comunicados?"},
                    {id:"__pin__",        label:"🔐 O que é o PIN e como trocar?"},
                  ].map(({id,label}) => {
                    const visivel = restaurant?.tabsGestor?.faqAuto?.[safeFaqKey(id)] !== false;
                    return (
                      <div key={id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 12px",background:"var(--bg1)",borderRadius:10,border:`1px solid ${visivel?"#10b98133":"var(--border)"}`,gap:8}}>
                        <span style={{color:visivel?"var(--text)":"var(--text3)",fontSize:12,fontWeight:visivel?500:400,flex:1}}>{label}</span>
                        <button onClick={()=>{
                          const cur = restaurant?.tabsGestor?.faqAuto ?? {};
                          const newVal = !visivel;
                          const updated = restaurants.map(r=>r.id===rid?{...r,tabsGestor:{...(r.tabsGestor??{}),faqAuto:{...cur,[safeFaqKey(id)]:newVal}}}:r);
                          onUpdate("restaurants",updated);
                        }} style={{padding:"4px 10px",borderRadius:20,border:"none",background:visivel?"var(--green)":"var(--border)",color:visivel?"#fff":"#555",fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:11,whiteSpace:"nowrap"}}>
                          {visivel?"👁 Exibindo":"🚫 Oculto"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div style={{...S.card,marginBottom:20}}>
              <p style={{color:ac,fontSize:14,fontWeight:700,margin:"0 0 4px"}}>Retenção Fiscal sobre Gorjeta</p>
              <p style={{color:"var(--text3)",fontSize:12,marginBottom:12}}>Conforme a Lei 13.419/2017, a gorjeta é rendimento do trabalhador sujeito a encargos. A alíquota depende do regime tributário do estabelecimento.</p>

              <div style={{display:"flex",gap:10,marginBottom:14}}>
                {[[0.33,"33%"],[0.20,"20%"]].map(([rate,lbl])=>{
                  const sel = (localRest.taxRate ?? TAX) === rate;
                  return (
                    <button key={rate} onClick={()=>{
                      patchConfig({ taxRate: rate });
                    }} style={{flex:1,padding:"12px",borderRadius:12,border:`2px solid ${sel?ac:"var(--border)"}`,background:sel?ac+"11":"transparent",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:16,fontWeight:700,color:sel?ac:"#555"}}>
                      {lbl}
                    </button>
                  );
                })}
              </div>

              {/* Explicação das alíquotas */}
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                <div style={{padding:"12px 14px",borderRadius:10,background:(localRest.taxRate??TAX)===0.33?"var(--ac-bg)":"var(--bg2)",border:`1px solid ${(localRest.taxRate??TAX)===0.33?"var(--ac)33":"var(--border)"}`}}>
                  <div style={{fontWeight:700,fontSize:13,color:"var(--text)",marginBottom:4}}>33% — Lucro Real ou Lucro Presumido</div>
                  <div style={{fontSize:12,color:"var(--text3)",lineHeight:1.6}}>
                    Aplica-se a empresas tributadas pelo Lucro Real ou Presumido. Incide INSS patronal (20%) + FGTS (8%) + RAT/terceiros (~5%). Base: art. 457 CLT e Lei 13.419/2017.
                  </div>
                </div>
                <div style={{padding:"12px 14px",borderRadius:10,background:(localRest.taxRate??TAX)===0.20?"var(--ac-bg)":"var(--bg2)",border:`1px solid ${(localRest.taxRate??TAX)===0.20?"var(--ac)33":"var(--border)"}`}}>
                  <div style={{fontWeight:700,fontSize:13,color:"var(--text)",marginBottom:4}}>20% — Simples Nacional</div>
                  <div style={{fontSize:12,color:"var(--text3)",lineHeight:1.6}}>
                    Aplica-se a MEI, ME e EPP optantes pelo Simples Nacional. A gorjeta integra a folha de pagamento com encargos simplificados. Base: LC 123/2006 e Resolução CGSN 140/2018.
                  </div>
                </div>
              </div>
              <p style={{color:"var(--text3)",fontSize:11,marginTop:10,marginBottom:0,fontStyle:"italic"}}>⚠️ Esta configuração não substitui orientação contábil. Consulte seu contador para confirmar a alíquota correta.</p>
            </div>
            {/* Fault Penalty */}
            <div style={{...S.card,marginBottom:20}}>
              <p style={{color:ac,fontSize:14,fontWeight:700,margin:"0 0 6px"}}>Penalidade por Falta Injustificada</p>
              <p style={{color:"var(--text3)",fontSize:12,marginBottom:14}}>Percentual do pool mensal de gorjetas descontado por cada dia de falta injustificada. O desconto é cumulativo: se um empregado tiver 3 faltas e a penalidade for 2%, será descontado 6% do pool mensal.</p>
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <p style={{color:"var(--text3)",fontSize:11,margin:0,fontWeight:600}}>Por área (empregados regulares):</p>
                {AREAS.map(area => {
                  const current = localRest.faultPenalty?.[area] ?? 0;
                  return (
                    <div key={area} style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{minWidth:80}}><AreaBadge area={area}/></div>
                      <input type="number" min="0" max="20" step="0.01" value={current}
                        onChange={e => {
                          const val = parseFloat(e.target.value) || 0;
                          patchConfig({ faultPenalty: { ...(localRest.faultPenalty??{}), [area]: Math.round(val*100)/100 } });
                        }}
                        style={{...S.input, width:70, textAlign:"center"}}
                      />
                      <span style={{color:"var(--text3)",fontSize:13}}>% por falta</span>
                    </div>
                  );
                })}
                <div style={{borderTop:"1px solid var(--border)",paddingTop:10,marginTop:4}}>
                  <p style={{color:"#ec4899",fontSize:11,margin:"0 0 8px",fontWeight:600}}>🏭 Empregados de Produção:</p>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                    <div style={{minWidth:130}}>
                      <span style={{background:"#dc262611",color:"var(--red)",padding:"3px 10px",borderRadius:8,fontSize:11,fontWeight:700,fontFamily:"'DM Mono',monospace"}}>Falta injustificada</span>
                    </div>
                    <input type="number" min="0" max="20" step="0.01" value={localRest.producaoPenaltyU ?? 6.66}
                      onChange={e => {
                        const val = parseFloat(e.target.value) || 0;
                        patchConfig({ producaoPenaltyU: Math.round(val*100)/100 });
                      }}
                      style={{...S.input, width:70, textAlign:"center"}}
                    />
                    <span style={{color:"var(--text3)",fontSize:13}}>% por falta</span>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <div style={{minWidth:130}}>
                      <span style={{background:"#f59e0b11",color:"#f59e0b",padding:"3px 10px",borderRadius:8,fontSize:11,fontWeight:700,fontFamily:"'DM Mono',monospace"}}>Falta justificada</span>
                    </div>
                    <input type="number" min="0" max="20" step="0.01" value={localRest.producaoPenaltyJ ?? 3.33}
                      onChange={e => {
                        const val = parseFloat(e.target.value) || 0;
                        patchConfig({ producaoPenaltyJ: Math.round(val*100)/100 });
                      }}
                      style={{...S.input, width:70, textAlign:"center"}}
                    />
                    <span style={{color:"var(--text3)",fontSize:13}}>% por falta</span>
                  </div>
                  <div style={{background:"#ec489911",border:"1px solid #ec489922",borderRadius:10,padding:"10px 14px",marginTop:10}}>
                    <p style={{color:"var(--text2)",fontSize:12,margin:0,lineHeight:1.6}}>
                      <strong style={{color:"#ec4899"}}>Como funciona a produção:</strong> Empregados marcados como "Produção" (🏭) recebem gorjeta <strong>todos os dias</strong>, exceto férias. Sofrem penalidade tanto por falta injustificada quanto justificada, cada uma com seu percentual. O desconto é sobre o pool mensal total, por dia de falta. Pode ser atribuído a empregados de qualquer área.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div style={{...S.card,marginBottom:20}}>
              <p style={{color:ac,fontSize:14,fontWeight:700,margin:"0 0 8px"}}>Modalidade de Divisão</p>
              <p style={{color:"var(--text3)",fontSize:12,marginBottom:14}}>Define como a gorjeta é dividida entre os empregados.</p>
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {[[MODE_AREA_POINTS,"🏷️ Áreas + Pontos","Divide por área (%) e depois por pontos dentro de cada área"],[MODE_GLOBAL_POINTS,"⚡ Pontos Global","Divide diretamente pelos pontos de todos os empregados, sem separação por área"]].map(([mode,label,desc])=>{
                  const selected = (localRest.divisionMode ?? MODE_AREA_POINTS) === mode;
                  return (
                    <button key={mode} onClick={()=>{
                      patchConfig({ divisionMode: mode });
                    }} style={{padding:"14px 16px",borderRadius:12,border:`2px solid ${selected?ac:"var(--border)"}`,background:selected?ac+"11":"transparent",cursor:"pointer",textAlign:"left",fontFamily:"'DM Mono',monospace"}}>
                      <div style={{color:selected?ac:"var(--text)",fontWeight:700,fontSize:14}}>{label}</div>
                      <div style={{color:"var(--text3)",fontSize:12,marginTop:4}}>{desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            {/* v8.10: showTipsToEmployee migrado para o card "O que o empregado vê" — bloco Abas → Gorjeta */}
            {/* Privacidade */}
            <div style={{...S.card,marginBottom:20}}>
              <p style={{color:ac,fontSize:14,fontWeight:700,margin:"0 0 8px"}}>🔒 Privacidade</p>
              <p style={{color:"var(--text3)",fontSize:12,marginBottom:14}}>Quando ativo, oculta dados sensíveis (valores de gorjeta, CPFs, mensagens DP e comunicados) da visão do master.</p>
              <label style={{display:"flex",alignItems:"center",gap:12,cursor:"pointer"}}>
                <div onClick={()=>patchConfig({ privacyMode: !localRest.privacyMode })} style={{width:48,height:26,borderRadius:13,background:localRest.privacyMode?ac:"var(--border)",position:"relative",cursor:"pointer",transition:"background 0.2s"}}>
                  <div style={{width:22,height:22,borderRadius:11,background:"#fff",position:"absolute",top:2,left:localRest.privacyMode?24:2,transition:"left 0.2s",boxShadow:"0 1px 3px rgba(0,0,0,0.2)"}}/>
                </div>
                <span style={{color:"var(--text)",fontSize:14,fontWeight:600}}>{localRest.privacyMode ? "Ativo — dados ocultos para o admin" : "Desativado"}</span>
              </label>
            </div>
            {/* Backup do restaurante — admin only */}
            {isOwner && (
              <div style={{...S.card,marginBottom:20}}>
                <p style={{color:ac,fontSize:14,fontWeight:700,margin:"0 0 4px"}}>💾 Backup do Restaurante</p>
                <p style={{color:"var(--text3)",fontSize:12,marginBottom:14}}>Exporta todos os dados deste restaurante: empregados, cargos, gorjetas, escalas, gestores vinculados e configurações financeiras.</p>
                <button onClick={()=>{
                  const restEmps = employees.filter(e=>e.restaurantId===restaurant.id);
                  const restRoles = roles.filter(ro=>ro.restaurantId===restaurant.id);
                  const restTips = tips.filter(t=>t.restaurantId===restaurant.id);
                  const restSchedules = schedules.filter(s=>s.restaurantId===restaurant.id);
                  const restMgrs = (data?.managers??[]).filter(m=>m.restaurantIds?.includes(restaurant.id)).map(m=>({...m,pin:"***"}));
                  const exportData = {
                    exportedAt: new Date().toISOString(),
                    restaurante: restaurant,
                    empregados: restEmps,
                    cargos: restRoles,
                    gorjetas: restTips,
                    escalas: restSchedules,
                    gestores: restMgrs,
                  };
                  const safeName = restaurant.name.replace(/[^a-zA-Z0-9]/g,"_").toLowerCase();
                  const blob = new Blob([JSON.stringify(exportData,null,2)],{type:"application/json"});
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href=url; a.download=`apptip_backup_${safeName}_${today()}.json`; a.click();
                  URL.revokeObjectURL(url);
                  onUpdate("_toast",`✅ Backup de ${restaurant.name} exportado!`);
                }} style={{...S.btnSecondary,color:"var(--green)",borderColor:"var(--green)",fontSize:13,width:"100%",textAlign:"center",padding:"12px"}}>
                  💾 Exportar backup de {restaurant.name}
                </button>
              </div>
            )}
            {/* Zona de perigo — excluir restaurante (admin only) */}
            {isOwner && (
              <div style={{...S.card,marginBottom:20,border:"1px solid var(--red)33",background:"#fef2f2"}}>
                <p style={{color:"var(--red)",fontSize:14,fontWeight:700,margin:"0 0 4px"}}>⚠️ Zona de Perigo</p>
                <p style={{color:"var(--text3)",fontSize:12,marginBottom:14}}>Mover este restaurante para a lixeira. Você poderá restaurá-lo posteriormente pela aba Lixeira.</p>
                <button onClick={async ()=>{
                  if(!await appConfirm(`Tem certeza que deseja mover "${restaurant.name}" para a lixeira?\n\nTodos os dados do restaurante serão preservados na lixeira.`)) return;
                  if(await appConfirm(`Deseja exportar um backup de "${restaurant.name}" antes de excluir?`)) {
                    const restEmps = employees.filter(e=>e.restaurantId===restaurant.id);
                    const restRoles = roles.filter(ro=>ro.restaurantId===restaurant.id);
                    const restTips = tips.filter(t=>t.restaurantId===restaurant.id);
                    const restSchedules = schedules.filter(s=>s.restaurantId===restaurant.id);
                    const restMgrs = (data?.managers??[]).filter(m=>m.restaurantIds?.includes(restaurant.id)).map(m=>({...m,pin:"***"}));
                    const exportData = {
                      exportedAt: new Date().toISOString(),
                      restaurante: restaurant,
                      empregados: restEmps,
                      cargos: restRoles,
                      gorjetas: restTips,
                      escalas: restSchedules,
                      gestores: restMgrs,
                    };
                    const safeName = restaurant.name.replace(/[^a-zA-Z0-9]/g,"_").toLowerCase();
                    const blob = new Blob([JSON.stringify(exportData,null,2)],{type:"application/json"});
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href=url; a.download=`apptip_backup_${safeName}_${today()}.json`; a.click();
                    URL.revokeObjectURL(url);
                  }
                  const trash = data?.trash ?? { restaurants:[], managers:[], employees:[] };
                  const entry = { ...restaurant, deletedAt: new Date().toISOString(), deletedBy: currentUser?.name ?? "Admin" };
                  const newTrash = { ...trash, restaurants: [...(trash.restaurants??[]), entry] };
                  onUpdate("trash", newTrash);
                  onUpdate("restaurants", restaurants.filter(x=>x.id!==restaurant.id));
                  onUpdate("_toast", `🗑️ ${restaurant.name} movido para a lixeira.`);
                }} style={{...S.btnSecondary,color:"var(--red)",borderColor:"var(--red)",fontSize:13,width:"100%",textAlign:"center",padding:"12px"}}>
                  🗑️ Excluir {restaurant.name}
                </button>
              </div>
            )}
            {(localRest.divisionMode ?? MODE_AREA_POINTS) === MODE_AREA_POINTS && (
              <div style={{...S.card,marginBottom:20}}>
                <p style={{color:ac,fontSize:14,fontWeight:700,margin:"0 0 4px"}}>Distribuição por Área</p>
                <p style={{color:"var(--text3)",fontSize:11,margin:"0 0 14px"}}>Percentuais de cada área no pool total.</p>
                {Array.isArray(splits?.[rid]) ? (
                  <div style={{padding:"12px 14px",background:"var(--ac)10",border:"1px solid var(--ac)44",borderRadius:8,fontSize:12,color:"var(--text2)",lineHeight:1.5}}>
                    💡 A regra de divisão deste restaurante agora é gerida pela aba <b>Gorjetas → Regra</b>, com versionamento, ata em PDF e ativação por data. O editor antigo aqui foi descontinuado pra evitar conflitos.
                  </div>
                ) : (
                  <>
                    <div style={{marginBottom:14}}><MonthNav year={year} month={month} onChange={(y,m)=>{setYear(y);setMonth(m);setSplitForm(null);}}/></div>
                    {splitForm ? (
                      <div>
                        {AREAS.map(a=><div key={a} style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}><div style={{minWidth:70}}><AreaBadge area={a}/></div><input type="number" min="0" max="100" step="0.5" value={splitForm[a]} onChange={e=>setSplitForm({...splitForm,[a]:e.target.value})} style={{...S.input,width:80,textAlign:"center"}}/><span style={{color:"var(--text3)",fontSize:13}}>%</span></div>)}
                        <div style={{color:Math.abs(AREAS.reduce((a,k)=>a+parseFloat(splitForm[k]||0),0)-100)<0.01?"var(--green)":"var(--red)",fontSize:13,marginBottom:10}}>Total: {AREAS.reduce((a,k)=>a+parseFloat(splitForm[k]||0),0).toFixed(1)}%</div>
                        <div style={{display:"flex",gap:8}}><button onClick={saveSplit} style={{...S.btnPrimary,flex:1}}>Salvar</button><button onClick={()=>setSplitForm(null)} style={S.btnSecondary}>Cancelar</button></div>
                      </div>
                    ) : (
                      <div>
                        {AREAS.map(a=><div key={a} style={{display:"flex",justifyContent:"space-between",marginBottom:8,alignItems:"center"}}><AreaBadge area={a}/><span style={{color:"var(--text2)",fontSize:14}}>{curSplit[a]}%</span></div>)}
                        <button onClick={()=>setSplitForm({...curSplit})} style={{...S.btnSecondary,marginTop:12,width:"100%",textAlign:"center"}}>Editar percentuais</button>
                        <div style={{marginTop:10,fontSize:11,color:"var(--text3)",lineHeight:1.5}}>
                          💡 Regra de divisão evoluiu — passe a usar a aba <b>Gorjetas → Regra</b> pra ter versionamento, ata em PDF e ativação por data.
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

          </div>
        )}
      </div>

      {/* Modal: Aplicar nova alíquota retroativamente — top-level pra renderizar mesmo se tab mudar */}
      {taxRetroModal && (() => {
        const { oldRate, newRate, fromDate, applyRetro } = taxRetroModal;
        const monthStart = `${year}-${String(month+1).padStart(2,"0")}-01`;
        const affected = (tips || []).filter(t => t.restaurantId === rid && t.date >= fromDate).length;
        const empAffected = new Set((tips || []).filter(t => t.restaurantId === rid && t.date >= fromDate).map(t => t.employeeId)).size;
        const ac2 = "var(--ac)";
        return (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={()=>setTaxRetroModal(null)}>
            <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:14,maxWidth:520,width:"100%",padding:24,boxShadow:"0 20px 50px rgba(0,0,0,0.3)"}}>
              <h3 style={{margin:"0 0 8px",color:ac2,fontSize:16,fontWeight:700}}>💸 Mudança de alíquota: {(oldRate*100).toFixed(0)}% → {(newRate*100).toFixed(0)}%</h3>
              <p style={{color:"var(--text2)",fontSize:13,margin:"0 0 14px",lineHeight:1.5}}>
                A nova alíquota vale a partir de quando? Lançamentos antes da data ficam intactos.
              </p>
              <div style={{marginBottom:12}}>
                <label style={{display:"flex",alignItems:"flex-start",gap:8,padding:"10px 12px",borderRadius:8,border:`2px solid ${applyRetro?ac2:"var(--border)"}`,background:applyRetro?ac2+"08":"transparent",cursor:"pointer",marginBottom:6}}>
                  <input type="radio" checked={applyRetro} onChange={()=>setTaxRetroModal({...taxRetroModal, applyRetro: true})} style={{accentColor:ac2,marginTop:3}}/>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>Aplicar retroativamente a partir de:</div>
                    <input type="date" value={fromDate} onChange={e=>setTaxRetroModal({...taxRetroModal, fromDate: e.target.value, applyRetro: true})} style={{...S.input,fontSize:12,marginTop:6,padding:"6px 8px"}}/>
                  </div>
                </label>
                <label style={{display:"flex",alignItems:"flex-start",gap:8,padding:"10px 12px",borderRadius:8,border:`2px solid ${!applyRetro?ac2:"var(--border)"}`,background:!applyRetro?ac2+"08":"transparent",cursor:"pointer"}}>
                  <input type="radio" checked={!applyRetro} onChange={()=>setTaxRetroModal({...taxRetroModal, applyRetro: false})} style={{accentColor:ac2,marginTop:3}}/>
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>Só novos lançamentos</div>
                    <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>Os existentes ficam com {(oldRate*100).toFixed(0)}%.</div>
                  </div>
                </label>
              </div>
              {applyRetro && fromDate && (
                <div style={{padding:"10px 12px",background:"var(--bg2)",borderRadius:8,marginBottom:14,fontSize:12,color:"var(--text2)",lineHeight:1.6}}>
                  <strong>{affected}</strong> lançamento(s) de <strong>{empAffected}</strong> empregado(s) terão imposto e líquido recalculados.
                  <br/><span style={{color:"var(--text3)",fontSize:11}}>Bruto não muda. Só imposto e líquido são ajustados.</span>
                  <div style={{marginTop:6,fontSize:11,color:"var(--text3)"}}>
                    Atalhos: <button onClick={()=>setTaxRetroModal({...taxRetroModal, fromDate: monthStart, applyRetro: true})} style={{background:"none",border:"none",color:ac2,cursor:"pointer",padding:0,fontSize:11,textDecoration:"underline"}}>início deste mês</button>
                    {" · "}
                    <button onClick={()=>setTaxRetroModal({...taxRetroModal, fromDate: `${year}-01-01`, applyRetro: true})} style={{background:"none",border:"none",color:ac2,cursor:"pointer",padding:0,fontSize:11,textDecoration:"underline"}}>início deste ano</button>
                  </div>
                </div>
              )}
              <div style={{display:"flex",justifyContent:"flex-end",gap:10}}>
                <button onClick={()=>setTaxRetroModal(null)} style={{...S.btnSecondary,padding:"8px 14px",fontSize:13}}>Cancelar</button>
                <button onClick={()=>{
                  const retroDate = applyRetro ? fromDate : null;
                  setTaxRetroModal(null);
                  doSaveConfig(retroDate, newRate);
                }} disabled={applyRetro && !fromDate}
                  style={{...S.btnPrimary,padding:"8px 14px",fontSize:13,fontWeight:700,opacity:(applyRetro&&!fromDate)?0.5:1}}>
                  Salvar
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {showExport && <ExportModal onClose={()=>setShowExport(false)} employees={employees} roles={roles} tips={tips} restaurant={restaurant}/>}
      {previewDoc && <PDFPreviewModal pdfDoc={previewDoc} fileName={previewFileName} title="Pré-visualização do PDF" onClose={()=>setPreviewDoc(null)} />}
    </div>
  );
}

//
// ARCHITECTURE MAP TAB
//
function ArchitectureTab({ isMobile }) {
  const ac = "var(--ac)";
  const [archSec, setArchSec] = useState("overview");
  const [openNodes, setOpenNodes] = useState({});
  const toggleN = (id) => setOpenNodes(p => ({...p, [id]: !p[id]}));
  const isOpen = (id) => !!openNodes[id];
  const sty = {
    nav: { display:"flex", gap:6, flexWrap:"wrap", marginBottom:16 },
    navBtn: (active) => ({ background: active ? ac : "var(--card-bg)", color: active ? "#fff" : "var(--text2)", border: active ? "none" : "1px solid var(--border)", padding:"7px 14px", borderRadius:8, cursor:"pointer", fontSize:12, fontWeight:600 }),
    card: { ...S.card, marginBottom:10, padding:isMobile?12:16 },
    cardTitle: { fontSize:14, fontWeight:700, color:"var(--text)", marginBottom:6, display:"flex", alignItems:"center", gap:8 },
    cardDesc: { fontSize:12, color:"var(--text2)", lineHeight:1.6 },
    tag: { display:"inline-block", background:`${ac}22`, color:ac, padding:"2px 8px", borderRadius:6, fontSize:10, fontWeight:700, marginTop:6 },
    grid: { display:"grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(280px, 1fr))", gap:10 },
    treeNode: (color) => ({ display:"flex", alignItems:"center", gap:8, padding:"8px 12px", borderRadius:8, cursor:"pointer", border:`1px solid ${color}33`, background:`${color}08`, marginBottom:4 }),
    treeChild: { marginLeft:24, paddingLeft:14, borderLeft:"2px solid var(--border)" },
    leaf: { padding:"5px 12px", marginBottom:3, borderRadius:6, fontSize:12, display:"flex", alignItems:"flex-start", gap:8, background:"var(--card-bg)", border:"1px solid var(--border)" },
    tbl: { width:"100%", borderCollapse:"collapse", fontSize:12 },
    th: { background:"var(--card-bg)", padding:"8px 10px", textAlign:"left", fontWeight:600, color:"var(--text3)", fontSize:11, borderBottom:"1px solid var(--border)" },
    td: { padding:"6px 10px", borderBottom:"1px solid var(--border)", color:"var(--text2)" },
    code: { background:"var(--card-bg)", padding:"1px 5px", borderRadius:3, fontSize:11, fontFamily:"'DM Mono',monospace", color:ac },
    flow: { display:"flex", alignItems:"center", gap:6, flexWrap:"wrap", marginBottom:10, padding:12, background:"var(--card-bg)", borderRadius:8, border:"1px solid var(--border)" },
    flowBox: (hl) => ({ background: hl ? ac : "var(--bg)", color: hl ? "#fff" : "var(--text2)", padding:"6px 12px", borderRadius:6, fontSize:12, fontWeight:500, whiteSpace:"nowrap" }),
    flowArrow: { color:ac, fontSize:16 },
    secTitle: { fontSize:15, fontWeight:700, color:"var(--text)", margin:"18px 0 8px", display:"flex", alignItems:"center", gap:8 },
  };
  const SECTIONS = [["overview","🏠 Visão Geral"],["portals","🚪 Portais"],["data","🗄️ Dados"],["tabs","📑 Abas"],["access","🔐 Permissões"],["flows","🔄 Fluxos"],["planning","🗺️ Planejamento"]];
  const TreeNode = ({id, icon, label, desc, color, children}) => (<div><div style={sty.treeNode(color||"var(--border)")} onClick={()=>toggleN(id)}><span style={{fontSize:16}}>{icon}</span><span style={{fontWeight:600,fontSize:13,color:"var(--text)"}}>{label}</span><span style={{fontSize:11,color:"var(--text3)",marginLeft:"auto"}}>{desc}</span><span style={{fontSize:10,color:"var(--text3)",transition:"transform 0.2s",transform:isOpen(id)?"rotate(90deg)":"rotate(0)"}}>{children?"▶":""}</span></div>{isOpen(id) && children && <div style={sty.treeChild}>{children}</div>}</div>);
  const Leaf = ({icon, label, desc}) => (<div style={sty.leaf}><span>{icon}</span><div><span style={{fontWeight:500,color:"var(--text)"}}>{label}</span><br/><span style={{color:"var(--text3)",fontSize:11}}>{desc}</span></div></div>);
  const Flow = ({steps}) => (<div style={sty.flow}>{steps.map((s,i) => (<React.Fragment key={i}>{i>0 && <span style={sty.flowArrow}>→</span>}<div style={sty.flowBox(i===steps.length-1)}>{s}</div></React.Fragment>))}</div>);

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}><h3 style={{color:ac,fontSize:16,fontWeight:700,margin:0}}>🗺️ Mapa da Arquitetura</h3><span style={{color:"var(--text3)",fontSize:11}}>v{APP_VERSION}</span></div>
      <div style={sty.nav}>{SECTIONS.map(([id,label])=>(<button key={id} style={sty.navBtn(archSec===id)} onClick={()=>setArchSec(id)}>{label}</button>))}</div>

      {archSec === "overview" && (<div>
        <p style={{fontSize:13,color:"var(--text2)",marginBottom:14,lineHeight:1.6}}>Esta é uma aplicação React de página única (~17.000 linhas) com Firebase Firestore. Todos os dados são carregados ao iniciar e gerenciados pela função central <span style={sty.code}>handleUpdate(campo, valor)</span>.</p>
        <Flow steps={["👤 Usuário","🔐 Login (CPF/Código)","⚛️ React App","🔥 Firebase Firestore"]} />
        <div style={sty.secTitle}>🛠️ Stack Tecnológica</div>
        <div style={sty.grid}>
          <div style={sty.card}><div style={sty.cardTitle}>📱 Frontend</div><div style={sty.cardDesc}>React 18 com hooks. Arquivo único App.js. Tema claro/escuro.</div><span style={sty.tag}>React + Hooks</span></div>
          <div style={sty.card}><div style={sty.cardTitle}>🔥 Banco de Dados</div><div style={sty.cardDesc}>Firebase Firestore, coleção appdata. Cache IndexedDB + localStorage.</div><span style={sty.tag}>Firestore</span></div>
          <div style={sty.card}><div style={sty.cardTitle}>🤖 IA</div><div style={sty.cardDesc}>API Groq (Llama 3.3-70B) para FAQ, horários e ponto.</div><span style={sty.tag}>Groq / Llama</span></div>
          <div style={sty.card}><div style={sty.cardTitle}>🔒 Segurança</div><div style={sty.cardDesc}>App Check (reCAPTCHA v3). CPF + PIN. Rate limiting.</div><span style={sty.tag}>App Check</span></div>
          <div style={sty.card}><div style={sty.cardTitle}>💾 Salvamento</div><div style={sty.cardDesc}>handleUpdate() → estado + Firebase. Bloqueio offline (master).</div><span style={sty.tag}>handleUpdate</span></div>
          <div style={sty.card}><div style={sty.cardTitle}>📊 Versionamento</div><div style={sty.cardDesc}>Gorjetas e escalas (30 versões). Snapshots debounce 30s.</div><span style={sty.tag}>30 versões</span></div>
        </div>
        <div style={sty.secTitle}>👥 Perfis de Usuário</div>
        <div style={sty.grid}>
          <div style={{...sty.card,borderColor:"#a855f7"}}><div style={sty.cardTitle}>⭐ Master</div><div style={sty.cardDesc}>Admin SaaS. Acesso total. isMaster para lixeira, offline.</div><span style={sty.tag}>view = "super"</span></div>
          <div style={{...sty.card,borderColor:"#3b82f6"}}><div style={sty.cardTitle}>👔 Gestor Adm.</div><div style={sty.cardDesc}>Gerente do restaurante. isDP para mensagens.</div><span style={sty.tag}>view = "manager"</span></div>
          <div style={{...sty.card,borderColor:"#22c55e"}}><div style={sty.cardTitle}>🎯 Líder Op.</div><div style={sty.cardDesc}>Dashboard, Escala, Horários, Equipe, Reuniões.</div><span style={sty.tag}>profile = "lider"</span></div>
          <div style={{...sty.card,borderColor:"#f59e0b"}}><div style={sty.cardTitle}>👷 Empregado</div><div style={sty.cardDesc}>Escala, gorjetas, trilha, comunicados, FAQ, DP.</div><span style={sty.tag}>view = "employee"</span></div>
        </div>
      </div>)}

      {archSec === "portals" && (<div>
        <p style={{fontSize:13,color:"var(--text2)",marginBottom:14,lineHeight:1.6}}>Cada tipo de usuário entra num portal diferente. Owner e Manager delegam ao RestaurantPanel.</p>
        <TreeNode id="app" icon="🏠" label="App()" desc="Raiz" color="#78716c"><TreeNode id="owner" icon="⭐" label="OwnerPortal" desc="Multi-restaurante" color="#a855f7"><Leaf icon="💰" label="Financeiro Geral" desc="Todos os restaurantes e cobranças" /><Leaf icon="👔" label="Gestores" desc="CRUD de gestores" /><Leaf icon="⭐" label="Admins" desc="Somente master" /><Leaf icon="📬" label="Caixa" desc="Notificações" /><Leaf icon="🗑️" label="Lixeira" desc="Exclusão definitiva" /><Leaf icon="📋" label="Versões" desc="Changelog" /><Leaf icon="🗺️" label="Arquitetura" desc="Este mapa!" /><TreeNode id="owner-rest" icon="🍽️" label="Restaurante → RestaurantPanel" desc="isOwner=true" color="#22c55e"><Leaf icon="📊" label="Operacional" desc="Abas do restaurante" /><Leaf icon="👔" label="Gestores" desc="Associados" /><Leaf icon="💰" label="Financeiro" desc="Cobrança/plano" /></TreeNode></TreeNode><TreeNode id="mgr" icon="👔" label="ManagerPortal" desc="Gestor" color="#3b82f6"><Leaf icon="🍽️" label="Seletor" desc="Multi-restaurante" /><Leaf icon="📊" label="RestaurantPanel" desc="isOwner=false" /></TreeNode><TreeNode id="emp" icon="👷" label="EmployeePortal" desc="Barra inferior" color="#f59e0b"><Leaf icon="📢" label="Avisos" desc="Obrigatório ler" /><Leaf icon="📅" label="Escala" desc="Calendário" /><Leaf icon="💸" label="Gorjeta" desc="Se habilitado" /><Leaf icon="📈" label="Trilha" desc="Jornada, metas" /><Leaf icon="🕐" label="Horários" desc="Se habilitado" /><Leaf icon="❓" label="FAQ" desc="Se habilitado" /><Leaf icon="💬" label="Fale DP" desc="Se habilitado" /></TreeNode><TreeNode id="pub" icon="📄" label="Páginas públicas" desc="Sem login" color="#78716c"><Leaf icon="🏠" label="Home" desc="Landing" /><Leaf icon="🧾" label="Fatura" desc="/fatura/:id" /><Leaf icon="📖" label="Guia" desc="/guia-gestor" /></TreeNode></TreeNode>
      </div>)}

      {archSec === "data" && (<div>
        <p style={{fontSize:13,color:"var(--text2)",marginBottom:14,lineHeight:1.6}}>Coleção <span style={sty.code}>appdata</span> do Firestore. Chave <span style={sty.code}>v4:nome</span>, valor <span style={sty.code}>{"{ value: ... }"}</span>.</p>
        {[{title:"📋 Principais",rows:[["owners","Masters"],["managers","Gestores"],["restaurants","Restaurantes"],["employees","Empregados"],["roles","Cargos"]]},{title:"💰 Gorjetas",rows:[["tips","Por dia"],["splits","% por área"],["tipVersions","30 snapshots"],["tipApprovals","Aprovação semanal"],["noTipDays","Sem gorjeta"]]},{title:"📅 Escala",rows:[["schedules","Mensais"],["workSchedules","Horário contratado"],["schedTemplates","Templates"],["scheduleVersions","30 snapshots"],["scheduleStatus","Status mês"],["schedulePrevista","Snapshot"],["delays","Atrasos"]]},{title:"🚌 VT",rows:[["vtConfig","Taxa diária"],["vtMonthly","Ajustes"],["vtPayments","Pagamentos"]]},{title:"👥 Pessoas",rows:[["incidents","Ocorrências"],["feedbacks","Avaliações"],["employeeGoals","Objetivos"],["devChecklists","Checklists"]]},{title:"🤝 Reuniões",rows:[["meetingPlans","Planejamento"],["meetingAgendas","Pautas"],["meetingIdeas","Ideias"],["meetingActions","Ações"],["meetingOccurrences","Ocorrências"],["meetingPendencias","Pendências"]]},{title:"📢 Comunicação",rows:[["communications","Comunicados"],["commAcks","Ciências"],["faq","FAQ"],["dpMessages","Mensagens DP"],["notifications","Notificações"]]},{title:"🗑️ Sistema",rows:[["trash","Lixeira (30 dias)"]]}
        ].map((g,gi) => (<div key={gi}><div style={sty.secTitle}>{g.title}</div><table style={sty.tbl}><thead><tr><th style={sty.th}>Campo</th><th style={sty.th}>Descrição</th></tr></thead><tbody>{g.rows.map(([f,d],ri) => (<tr key={ri}><td style={sty.td}><span style={sty.code}>{f}</span></td><td style={sty.td}>{d}</td></tr>))}</tbody></table></div>))}
      </div>)}

      {archSec === "tabs" && (<div>
        <p style={{fontSize:13,color:"var(--text2)",marginBottom:14,lineHeight:1.6}}>RestaurantPanel — 4 grupos de abas.</p>
        <TreeNode id="t-op" icon="💰" label="Operação" desc="Financeiro" color="#f59e0b"><Leaf icon="📊" label="Dashboard" desc="Resumo mensal" /><Leaf icon="💰" label="Gorjetas" desc="Grade diária" /><Leaf icon="🚌" label="VT" desc="Cálculo e pagamento" /></TreeNode>
        <TreeNode id="t-pp" icon="👥" label="Pessoas" desc="Equipe, escala, reuniões" color="#22c55e"><TreeNode id="t-eq" icon="👥" label="Equipe" desc="Maior componente" color="#22c55e"><Leaf icon="📋" label="Lista" desc="Cards, filtro" /><Leaf icon="📝" label="Cadastro" desc="Dados, cargo" /><Leaf icon="⚡" label="Ações" desc="Ocorrências, feedbacks" /><Leaf icon="📈" label="Trilha" desc="Timeline, badges" /></TreeNode><Leaf icon="🏷️" label="Cargos" desc="Área e pontos" /><Leaf icon="🕐" label="Horários" desc="Contratado + IA" /><Leaf icon="📅" label="Escala" desc="Grade, ponto, fechamento" /><TreeNode id="t-re" icon="🤝" label="Reuniões" desc="Completo" color="#22c55e"><Leaf icon="📝" label="Planejamento" desc="Tipos" /><Leaf icon="📋" label="Pautas" desc="Templates" /><Leaf icon="💡" label="Ideias" desc="Banco" /><Leaf icon="🔔" label="Ocorrências" desc="Operacionais" /><Leaf icon="⚡" label="Ações" desc="Pós-reunião" /><Leaf icon="⏳" label="Pendências" desc="Anteriores" /><Leaf icon="🎯" label="Ao vivo" desc="Execução" /><Leaf icon="📄" label="Relatório" desc="PDF" /></TreeNode></TreeNode>
        <TreeNode id="t-co" icon="📢" label="Comunicação" desc="Avisos, FAQ, DP" color="#3b82f6"><Leaf icon="📢" label="Comunicados" desc="Criar, segmentar" /><Leaf icon="❓" label="FAQ" desc="Com IA" /><Leaf icon="💬" label="Fale DP" desc="Diretas/anônimas" /><Leaf icon="📬" label="Caixa" desc="Somente DP" /></TreeNode>
        <TreeNode id="t-cf" icon="⚙️" label="Config" desc="Settings" color="#78716c"><Leaf icon="⚙️" label="Configurações" desc="Taxa, divisão" /><Leaf icon="👔" label="Gestores" desc="Sub-gestores" /></TreeNode>
      </div>)}

      {archSec === "access" && (<div>
        <p style={{fontSize:13,color:"var(--text2)",marginBottom:14,lineHeight:1.6}}>Quem pode ver e editar.</p>
        <div style={{display:"flex",gap:16,marginBottom:12,flexWrap:"wrap",fontSize:11,color:"var(--text3)"}}><span>🟢 Total</span><span>🟡 Condicional</span><span>🔵 Leitura</span><span>⚫ Sem acesso</span></div>
        <div style={{overflowX:"auto"}}><table style={sty.tbl}><thead><tr><th style={sty.th}>Func.</th><th style={sty.th}>⭐ Master</th><th style={sty.th}>👔 Gest.</th><th style={sty.th}>🎯 Líder</th><th style={sty.th}>👷 Emp.</th></tr></thead><tbody>{[["Gorjetas","🟢","🟡","⚫","🔵"],["Escala","🟢","🟡","🟡","🔵"],["Equipe","🟢","🟢","🔵","⚫"],["VT","🟢","🟡","⚫","⚫"],["Reuniões","🟢","🟢","🟡","⚫"],["Comunicados","🟢","🟢","⚫","🔵"],["Fale DP","🟢","🟢","⚫","🟡"],["Ocorrências","🟢","🟢","⚫","🔵"],["Financeiro","🟢","⚫","⚫","⚫"],["Lixeira","🟡","⚫","⚫","⚫"],["Offline","🟡","⚫","⚫","⚫"]].map(([f,...c],ri) => (<tr key={ri}><td style={{...sty.td,fontWeight:500}}>{f}</td>{c.map((v,ci)=>(<td key={ci} style={{...sty.td,textAlign:"center"}}>{v}</td>))}</tr>))}</tbody></table></div>
        <div style={sty.secTitle}>🔑 Flags</div>
        <div style={sty.grid}><div style={sty.card}><div style={sty.cardTitle}>isMaster</div><div style={sty.cardDesc}>Lixeira, admins, offline.</div></div><div style={sty.card}><div style={sty.cardTitle}>isDP</div><div style={sty.cardDesc}>Mensagens, sub-gestores.</div></div><div style={sty.card}><div style={sty.cardTitle}>isOwner</div><div style={sty.cardDesc}>true=Owner, false=Manager.</div></div><div style={sty.card}><div style={sty.cardTitle}>privacyMode</div><div style={sty.cardDesc}>Mascara dados do Owner.</div></div><div style={sty.card}><div style={sty.cardTitle}>profile: "lider"</div><div style={sty.cardDesc}>Restringe a áreas.</div></div><div style={sty.card}><div style={sty.cardTitle}>tabsConfig/tabsGestor</div><div style={sty.cardDesc}>Duas camadas de visibilidade.</div></div></div>
      </div>)}

      {archSec === "flows" && (<div>
        <div style={sty.secTitle}>🔐 Login</div><Flow steps={["CPF/Código","Busca owners→managers→employees","PIN","Portal"]} />
        <div style={sty.secTitle}>💰 Gorjeta</div><Flow steps={["Pool total","Divide por área","Divide por pontos","Imposto 33%","Líquida"]} />
        <div style={sty.secTitle}>📅 Escala</div><Flow steps={["Mês aberto","Preenche","Importa ponto","Compara","Fecha","Snapshot"]} />
        <div style={sty.secTitle}>🚌 VT</div><Flow steps={["Taxa diária","Dias × taxa","Ajustes","Pago","Snapshot"]} />
        <div style={sty.secTitle}>🤝 Reunião</div><Flow steps={["Planeja","Pauta","Ao vivo","Ações","PDF"]} />
        <div style={sty.secTitle}>👷 Empregado</div><Flow steps={["Admissão","Escala+gorjetas","Feedbacks","Promoção","Demissão→PDF"]} />
        <div style={sty.secTitle}>💾 Dados</div><Flow steps={["Botão","onUpdate","Offline?","setState","Firestore","Toast"]} />
      </div>)}

      {archSec === "planning" && (<div>
        <div style={sty.secTitle}>📐 Como adicionar</div>
        <div style={sty.grid}><div style={{...sty.card,borderColor:"#22c55e"}}><div style={sty.cardTitle}>1️⃣ Campo de dados</div><div style={sty.cardDesc}>K, useState, load(), data, setters.</div><span style={sty.tag}>~6 edições</span></div><div style={{...sty.card,borderColor:"#3b82f6"}}><div style={sty.cardTitle}>2️⃣ Nova aba</div><div style={sty.cardDesc}>Array de abas, componente.</div><span style={sty.tag}>~3 edições</span></div><div style={{...sty.card,borderColor:"#f59e0b"}}><div style={sty.cardTitle}>3️⃣ Sub-aba Equipe</div><div style={sty.cardDesc}>Pill + bloco condicional.</div><span style={sty.tag}>~2 edições</span></div><div style={{...sty.card,borderColor:"#a855f7"}}><div style={sty.cardTitle}>4️⃣ Aba empregado</div><div style={sty.cardDesc}>Portal, componente, tabs.</div><span style={sty.tag}>~4 edições</span></div><div style={{...sty.card,borderColor:"#ec4899"}}><div style={sty.cardTitle}>5️⃣ Tipo reunião</div><div style={sty.cardDesc}>MEETING_TYPES + template.</div><span style={sty.tag}>~2 edições</span></div><div style={{...sty.card,borderColor:"#14b8a6"}}><div style={sty.cardTitle}>6️⃣ Relatório PDF</div><div style={sty.cardDesc}>Função + botão.</div><span style={sty.tag}>Novo</span></div></div>
        <div style={sty.secTitle}>⚠️ Atenção</div>
        <div style={sty.grid}><div style={{...sty.card,borderColor:"#ef4444"}}><div style={sty.cardTitle}>🔴 Arquivo único</div><div style={sty.cardDesc}>~17.000 linhas. CI=true detecta erros.</div></div><div style={{...sty.card,borderColor:"#ef4444"}}><div style={sty.cardTitle}>🔴 Dados globais</div><div style={sty.cardDesc}>Tudo carrega no startup.</div></div><div style={{...sty.card,borderColor:"#f59e0b"}}><div style={sty.cardTitle}>🟡 Permissões</div><div style={sty.cardDesc}>Cada componente verifica flags.</div></div><div style={{...sty.card,borderColor:"#f59e0b"}}><div style={sty.cardTitle}>🟡 Mobile</div><div style={sty.cardDesc}>Algumas abas bloqueiam.</div></div></div>
        <div style={sty.secTitle}>💡 Expansão</div>
        <div style={sty.grid}><div style={sty.card}><div style={sty.cardTitle}>📊 Analytics</div><div style={sty.cardDesc}>Gráficos, KPIs.</div></div><div style={sty.card}><div style={sty.cardTitle}>📱 PWA</div><div style={sty.cardDesc}>Push notifications.</div></div><div style={sty.card}><div style={sty.cardTitle}>🔔 Tempo real</div><div style={sty.cardDesc}>onSnapshot.</div></div><div style={sty.card}><div style={sty.cardTitle}>📋 Relatórios</div><div style={sty.cardDesc}>Gorjetas, atrasos.</div></div><div style={sty.card}><div style={sty.cardTitle}>🤖 Mais IA</div><div style={sty.cardDesc}>Padrões, sugestões.</div></div><div style={sty.card}><div style={sty.cardTitle}>🔗 Integrações</div><div style={sty.cardDesc}>WhatsApp, calendário.</div></div></div>
      </div>)}
    </div>
  );
}

// SUPER MANAGER PORTAL
//
function OwnerPortal({ data, onUpdate, onBack, currentUser, toggleTheme, theme, onEnterOperational }) {
  // eslint-disable-next-line no-unused-vars
  const { owners, managers, restaurants, employees, roles, tips, splits, schedules, noTipDays } = data;
  const [tab, setTab] = useState("financeiro_geral");
  const isMobile = useMobile();
  const [selRestaurant, setSelRestaurantState] = useState(() => {
    const saved = localStorage.getItem("apptip_selrest");
    if (saved && restaurants.find(r => r.id === saved)) return saved;
    return null;
  });

  function setSelRestaurant(id) {
    setSelRestaurantState(id);
    if (id) localStorage.setItem("apptip_selrest", id);
    else localStorage.removeItem("apptip_selrest");
  }

  // forms
  // Modal states — grouped to reduce useState count
  const [modals, setModals] = useState({ rest:false, mgr:false, owner:false });
  const [editIds, setEditIds] = useState({ rest:null, mgr:null, owner:null });
  const showRestModal = modals.rest, showMgrModal = modals.mgr, showOwnerModal = modals.owner;
  const editRestId = editIds.rest, editMgrId = editIds.mgr, editOwnerId = editIds.owner;
  const setShowRestModal = v => setModals(p=>({...p,rest:v}));
  const setShowMgrModal = v => setModals(p=>({...p,mgr:v}));
  const setShowOwnerModal = v => setModals(p=>({...p,owner:v}));
  const setEditRestId = v => setEditIds(p=>({...p,rest:v}));
  const setEditMgrId = v => setEditIds(p=>({...p,mgr:v}));
  const setEditOwnerId = v => setEditIds(p=>({...p,owner:v}));
  const [restForm, setRestForm]             = useState({ name:"",shortCode:"",cnpj:"",razaoSocial:"",codigoContabil:"",address:"",whatsappFin:"",whatsappOp:"",serviceStartDate:"" });
  const [mgrForm, setMgrForm]               = useState({ name:"",cpf:"",pin:"",restaurantIds:[],perms:{tips:true,schedule:true},isDP:false,profile:"custom",areas:[],masterRestaurantIds:[] });
  // Inbox state
  const [inboxFilter, setInboxFilter] = useState("all");
  const [inboxFolder, setInboxFolder] = useState(null); // null=Entrada, "__trash"=Excluídas, string=folderId
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeData, setComposeData] = useState({ restaurantId: "", assunto: "", corpo: "" });
  const [folderModal, setFolderModal] = useState(null); // null|"new"|{id,nome,cor} for edit
  const [folderForm, setFolderForm] = useState({ nome: "", cor: "#3498db" });
  const [moveModal, setMoveModal] = useState(null); // null | msgId
  const [ownerForm, setOwnerForm]           = useState({ name:"",cpf:"",pin:"" });
  const [viewOnly, setViewOnly]             = useState(false);
  // View-only guard — shadow onUpdate to block writes when locked
  const _realUpdate = onUpdate;
  onUpdate = (field, value) => { // eslint-disable-line
    if (viewOnly && field !== "_toast") { _realUpdate("_toast", "🔒 Modo somente leitura — desbloqueie para editar"); return; }
    _realUpdate(field, value);
  };

  function saveRest() {
    if (!restForm.name.trim()) return;
    const code = restForm.shortCode.trim().toUpperCase().replace(/[^A-Z]/g,"").slice(0,3);
    if (code.length !== 3) { alert("O ID deve ter exatamente 3 letras (ex: QUI)."); return; }
    // Check uniqueness
    const conflict = restaurants.find(r => r.shortCode === code && r.id !== editRestId);
    if (conflict) { alert(`ID "${code}" já está em uso por "${conflict.name}".`); return; }
    const r = { ...restForm, shortCode: code, id: editRestId ?? Date.now().toString() };
    onUpdate("restaurants", editRestId ? restaurants.map(x=>x.id===editRestId?r:x) : [...restaurants,r]);
    setShowRestModal(false);
  }
  function saveMgr() {
    if (!mgrForm.name.trim()) return;
    // CPF required for managers
    const cpfDigits = (mgrForm.cpf??"").replace(/\D/g,"");
    if (cpfDigits.length < 11) { onUpdate("_toast","⚠️ CPF é obrigatório para gestores (11 dígitos)"); return; }
    // Líder must have at least one area
    if (mgrForm.profile==="lider" && (mgrForm.areas??[]).length===0) { onUpdate("_toast","⚠️ Selecione pelo menos uma área para o Líder Operacional"); return; }
    // PIN = primeiros 4 dígitos do CPF no cadastro inicial
    const isNew = !editMgrId;
    const pin = isNew ? cpfDigits.slice(0,4) : (mgrForm.pin || cpfDigits.slice(0,4));
    // Auto-master: primeiro gestor adm. de cada restaurante selecionado vira master
    const restIds = mgrForm.restaurantIds ?? [];
    let masterIds = [...(mgrForm.masterRestaurantIds ?? [])];
    let autoMaster = false;
    if (isNew && mgrForm.profile === "dp") {
      restIds.forEach(rId => {
        if (!managers.some(x => x.profile === "dp" && (x.restaurantIds??[]).includes(rId)) && !masterIds.includes(rId)) {
          masterIds.push(rId);
          autoMaster = true;
        }
      });
    }
    const m = { ...mgrForm, pin, id: editMgrId ?? Date.now().toString(), masterRestaurantIds: masterIds, ...(isNew ? {mustChangePin:true} : {}) };
    onUpdate("managers", editMgrId ? managers.map(x=>x.id===editMgrId?m:x) : [...managers,m]);
    setShowMgrModal(false);
    if (autoMaster) onUpdate("_toast","✅ Gestor criado como Master (primeiro do restaurante)");
  }
  function saveOwner() {
    if (!ownerForm.name.trim()||!ownerForm.pin.trim()) return;
    // Primeiro admin cadastrado vira master automaticamente
    const isMaster = ownerForm.isMaster ?? (owners.length === 0 && !editOwnerId);
    const s = { ...ownerForm, isMaster, id: editOwnerId ?? Date.now().toString() };
    // Não pode remover isMaster de um master via edição
    if (editOwnerId) {
      const existing = owners.find(x=>x.id===editOwnerId);
      if (existing?.isMaster) s.isMaster = true; // preserva
    }
    onUpdate("owners", editOwnerId ? owners.map(x=>x.id===editOwnerId?s:x) : [...owners,s]);
    setShowOwnerModal(false);
  }

  const PLANOS = [
    { id:"pIsento", label:"Isento",       empMax:999, mensal:0,     anual:0,      multi:true, isento:true },
    { id:"p10",  label:"Starter",     empMax:10,  mensal:97,    anual:87.30,  multi:false },
    { id:"p20",  label:"Básico",      empMax:20,  mensal:187,   anual:168.30, multi:true  },
    { id:"p50",  label:"Profissional",empMax:50,  mensal:397,   anual:357.30, multi:true  },
    { id:"p999", label:"Enterprise",  empMax:100, mensal:null,  anual:null,   multi:true  },
    { id:"pOrc", label:"On Demand",    empMax:999, mensal:null,  anual:null,   multi:true  },
  ];
  function getPlano(r) { return PLANOS.find(p=>p.id===(r.planoId??"p10")) ?? PLANOS[0]; }

  // Privacy helpers — mascarar dados sensíveis quando restaurante tem privacyMode
  function isPrivate(restId) {
    const r = restaurants.find(x => x.id === restId);
    return r?.privacyMode === true;
  }
  function maskCpfPriv(cpf, restId) {
    if (!isPrivate(restId)) return cpf || "—";
    return cpf ? "•••.•••.•••-••" : "—";
  }
  // Check if ANY selected restaurant is private
  function anyPrivate() {
    if (selRestaurant) return isPrivate(selRestaurant);
    return restaurants.some(r => r.privacyMode === true);
  }

  const notifications = data?.notifications ?? [];
  const inboxMsgs = data?.inbox ?? [];
  const ownerInbox = getInboxForUser({ inbox: inboxMsgs, userId: currentUser?.id, userRole: "owner", includeRead: false });
  const unreadNotifs = notifications.filter(n => !n.read && n.targetRole === "admin").length + ownerInbox.length;
  const isMaster = currentUser?.isMaster === true;
  const trash = data?.trash ?? { restaurants:[], managers:[], employees:[], tabData:[] };
  const trashCount = (trash.restaurants?.length??0) + (trash.managers?.length??0) + (trash.employees?.length??0) + (trash.tabData?.length??0);
  const [restTab, setRestTab] = useState("operacional");
  const [filtroFinanceiro, setFiltroFinanceiro] = useState("todos");
  const PIX_PADRAO = data?.pixChave  || "11985499821";
  const PIX_NOME   = data?.pixNome   || "Gustavo Rodrigues da Silva";
  const [cob, setCob] = useState({ forma:"pix", chave:PIX_PADRAO, link:"", valor:"", periodo:"", venc:"" });
  const cobForma = cob.forma, cobChave = cob.chave, cobLink = cob.link, cobValor = cob.valor, cobPeriodo = cob.periodo, cobVenc = cob.venc;
  const setCobForma = v => setCob(p=>({...p,forma:v}));
  const setCobChave = v => setCob(p=>({...p,chave:v}));
  const setCobLink  = v => setCob(p=>({...p,link:v}));
  const setCobValor = v => setCob(p=>({...p,valor:v}));
  const setCobPeriodo = v => setCob(p=>({...p,periodo:v}));
  const setCobVenc  = v => setCob(p=>({...p,venc:v}));

  // Soft delete helpers
  function softDelete(type, item) {
    const entry = { ...item, deletedAt: new Date().toISOString(), deletedBy: currentUser?.name ?? "Admin" };
    const newTrash = { ...trash, [type]: [...(trash[type]??[]), entry] };
    onUpdate("trash", newTrash);
  }
  function restore(type, item) {
    const newTrash = { ...trash, [type]: (trash[type]??[]).filter(x=>x.id!==item.id) };
    onUpdate("trash", newTrash);
    const clean = {...item};
    delete clean.deletedAt; delete clean.deletedBy; delete clean._type; delete clean._icon;
    if (type === "restaurants") {
      const exists = restaurants.find(r=>r.id===item.id);
      if (!exists) onUpdate("restaurants", [...restaurants, clean]);
    }
    if (type === "managers") {
      const exists = managers.find(m=>m.id===item.id);
      if (!exists) onUpdate("managers", [...managers, clean]);
    }
    if (type === "employees") {
      const exists = (data?.employees??[]).find(e=>e.id===item.id);
      if (!exists) onUpdate("employees", [...(data?.employees??[]), clean]);
    }
    onUpdate("_toast", `✅ ${item.name} restaurado!`);
  }
  async function hardDelete(type, item) {
    if (!await appConfirm(`Excluir permanentemente "${item.name}"? Esta ação não pode ser desfeita.`)) return;
    const newTrash = { ...trash, [type]: (trash[type]??[]).filter(x=>x.id!==item.id) };
    onUpdate("trash", newTrash);
    onUpdate("_toast", `🗑️ ${item.name} excluído permanentemente.`);
  }

  const ac = "var(--ac)";
  const TABS = [
    ["financeiro_geral", "💰 Financeiro"],
    ["owners","👔 Masters"],
    ["inbox", `📬 Caixa${unreadNotifs > 0 ? ` (${unreadNotifs})` : ""}`],
    ...(isMaster ? [["trash", `🗑️ Lixeira${trashCount > 0 ? ` (${trashCount})` : ""}`]] : []),
    ["changelog", "📋 Versões"],
    ["arquitetura", "🗺️ Arquitetura"],
  ];

  if (selRestaurant) {
    const rest = restaurants.find(r => r.id === selRestaurant);
    const restMgrs = managers.filter(m => m.restaurantIds?.includes(selRestaurant));

    return (
      <div style={{ minHeight:"100vh", background:"var(--bg)", fontFamily:"'DM Sans',sans-serif" }}>
        {/* Header */}
        <div style={{ background:"var(--header-bg)", borderBottom:"1px solid var(--border)", padding:isMobile?"10px 12px":"12px 20px", display:"flex", justifyContent:"space-between", alignItems:"center", boxShadow:"0 1px 4px rgba(0,0,0,0.04)", gap:8 }}>
          <div style={{ display:"flex", alignItems:"center", gap:isMobile?6:8, minWidth:0, flex:1 }}>
            <button onClick={()=>setSelRestaurant(null)} style={{ ...S.btnSecondary, fontSize:isMobile?11:12, padding:isMobile?"5px 8px":"6px 12px",flexShrink:0 }}>← {isMobile?"":"Voltar"}</button>
            <span style={{ color:"var(--text)", fontWeight:700, fontSize:isMobile?13:15, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{rest?.name}</span>
            {!isMobile && <span style={{ background:"var(--ac-bg)", color:"var(--ac-text)", borderRadius:6, padding:"2px 8px", fontSize:11, fontWeight:700 }}>{getPlano(rest).label}</span>}
            {!isMobile && rest?.earlyAdopter && <span style={{background:"#d4a01715",color:"#d4a017",borderRadius:6,padding:"2px 8px",fontSize:11,fontWeight:700}}>🚀 Early Adopter</span>}
          </div>
          <button onClick={onBack} style={{ ...S.btnSecondary, fontSize:12 }}>Sair</button>
        </div>

        {/* Sub-tabs */}
        <div style={{ display:"flex", borderBottom:"1px solid var(--border)", background:"var(--header-bg)", overflowX:"auto" }}>
          {[["operacional",isMobile?"🏠 Restaurante":"🏠 Restaurante"],["financeiro","💳 Financeiro"]].map(([id,lbl])=>(
            <button key={id} onClick={()=>setRestTab(id)}
              style={{ padding:isMobile?"10px 14px":"10px 20px", background:"none", border:"none", borderBottom:`2px solid ${restTab===id?ac:"transparent"}`, color:restTab===id?ac:"var(--text3)", cursor:"pointer", fontSize:isMobile?12:13, fontFamily:"'DM Sans',sans-serif", fontWeight:restTab===id?700:500, whiteSpace:"nowrap", flex:isMobile?1:undefined, textAlign:"center" }}>
              {lbl}
            </button>
          ))}
        </div>

        {/* Operacional */}
        {restTab === "operacional" && (
          <RestaurantPanel restaurant={rest} restaurants={restaurants} employees={employees} roles={roles} tips={tips} splits={splits} schedules={schedules} onUpdate={onUpdate} perms={{ tips:true, schedule:true }} isOwner data={data} currentUser={currentUser} privacyMask={rest?.privacyMode === true} mobileOnly={isMobile} onEnterOperational={onEnterOperational} />
        )}

        {/* Gestores deste restaurante */}
        {restTab === "gestores" && (
          <div style={{ padding:isMobile?"12px 10px":"24px", maxWidth:800, margin:"0 auto" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:isMobile?14:20, gap:8 }}>
              <div style={{minWidth:0}}>
                <h3 style={{ color:"var(--text)", fontSize:isMobile?14:16, fontWeight:700, margin:"0 0 4px" }}>Gestores de {rest?.name}</h3>
                <p style={{ color:"var(--text3)", fontSize:isMobile?11:13, margin:0 }}>{restMgrs.length} gestor{restMgrs.length!==1?"es":""} com acesso</p>
              </div>
              <button onClick={()=>{
                setEditMgrId(null);
                setMgrForm({name:"",cpf:"",pin:"",restaurantIds:[selRestaurant],perms:{tips:true,schedule:true},isDP:false,profile:"custom",areas:[],masterRestaurantIds:[]});
                setShowMgrModal(true);
              }} style={{...S.btnPrimary,width:"auto",padding:isMobile?"8px 14px":"10px 20px",fontSize:isMobile?12:14,whiteSpace:"nowrap"}}>+ Novo Gestor</button>
            </div>

            {restMgrs.length === 0 && (
              <div style={{...S.card, textAlign:"center", padding:40}}>
                <div style={{fontSize:36,marginBottom:12}}>👔</div>
                <p style={{color:"var(--text3)",fontSize:14,marginBottom:16}}>Nenhum gestor atribuído a este restaurante.</p>
                <button onClick={()=>{
                  setEditMgrId(null);
                  setMgrForm({name:"",cpf:"",pin:"",restaurantIds:[selRestaurant],perms:{tips:true,schedule:true},isDP:false,profile:"custom",areas:[],masterRestaurantIds:[]});
                  setShowMgrModal(true);
                }} style={{...S.btnPrimary,width:"auto",padding:"10px 24px"}}>Adicionar primeiro gestor</button>
              </div>
            )}

            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {restMgrs.map(m => (
                <div key={m.id} style={{...S.card}}>
                  <div style={{display:"flex",flexDirection:isMobile?"column":"row",justifyContent:"space-between",alignItems:isMobile?"stretch":"flex-start",gap:isMobile?10:0}}>
                    <div style={{minWidth:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2,flexWrap:"wrap"}}>
                        <span style={{color:"var(--text)",fontWeight:700,fontSize:isMobile?14:15}}>{m.name}</span>
                        {m.profile==="dp" && <span style={{background:"var(--blue-bg)",color:"var(--blue)",borderRadius:6,padding:"2px 8px",fontSize:10,fontWeight:700}}>📬 Gestor Adm.</span>}
                        {(m.masterRestaurantIds??[]).includes(selRestaurant) && <span style={{background:"#d4a01722",color:"#d4a017",borderRadius:6,padding:"2px 8px",fontSize:10,fontWeight:700}}>🔑 Master</span>}
                        {m.profile==="lider" && <span style={{background:"#f59e0b22",color:"#f59e0b",borderRadius:6,padding:"2px 8px",fontSize:10,fontWeight:700}}>👔 Líder Op.</span>}
                      </div>
                      <div style={{color:"var(--text3)",fontSize:isMobile?11:12,marginBottom:4}}>CPF: {isPrivate(selRestaurant) ? "•••.•••.•••-••" : (m.cpf||"—")}</div>
                      {m.profile==="lider" && (m.areas??[]).length>0 && (
                        <div style={{color:"var(--text3)",fontSize:11,marginBottom:6}}>Áreas: {m.areas.join(", ")}</div>
                      )}
                      <div style={{display:"flex",gap:isMobile?4:6,flexWrap:"wrap",marginBottom:6}}>
                        {[["tips","💸 Gorjetas"],["schedule","📅 Escala"],["roles","🏷️ Cargos"],["employees","👥 Equipe"],["comunicados","📢 Comuns."],["faq","❓ FAQ"],["dp","💬 DP"],["horarios","🕐 Horários"],["vt","🚌 VT"]].map(([k,lbl])=>
                          m.perms?.[k]!==false ? <span key={k} style={{background:"var(--green-bg)",color:"var(--green)",borderRadius:6,padding:"2px 6px",fontSize:isMobile?10:11,fontWeight:600}}>{lbl}</span> : null
                        )}
                        {m.isDP && !m.profile && <span style={{background:"var(--blue-bg)",color:"var(--blue)",borderRadius:6,padding:"2px 6px",fontSize:isMobile?10:11,fontWeight:600}}>📬 Gestor Adm.</span>}
                      </div>
                      {(m.restaurantIds??[]).filter(rid=>rid!==selRestaurant).length > 0 && (
                        <div style={{color:"var(--text3)",fontSize:11}}>
                          Também acessa: {(m.restaurantIds??[]).filter(rid=>rid!==selRestaurant).map(rid=>restaurants.find(r=>r.id===rid)?.name).filter(Boolean).join(", ")}
                        </div>
                      )}
                    </div>
                    <div style={{display:"flex",gap:8,flexShrink:0,flexWrap:"nowrap"}}>
                      <button onClick={async ()=>{
                        const cpfDigits = (m.cpf??"").replace(/\D/g,"");
                        if (cpfDigits.length < 4) { onUpdate("_toast","⚠️ CPF inválido — não foi possível gerar PIN automático"); return; }
                        const newPin = cpfDigits.slice(0,4);
                        if(!await appConfirm(`Resetar PIN de "${m.name}"?\n\nO PIN voltará para ${newPin} (4 primeiros dígitos do CPF) e ele será obrigado a trocar no próximo acesso.`)) return;
                        // v7.7: sincroniza reset nos 3 records (manager + pessoa + employee) com mustChangePin=true
                        const allPessoas = data?.pessoas || [];
                        const allEmployees = data?.employees || [];
                        onUpdate("managers", managers.map(x=>{
                          if (x.id === m.id) return { ...x, pin:newPin, mustChangePin:true };
                          if ((x.cpf||"").replace(/\D/g,"") === cpfDigits) return { ...x, pin:newPin, mustChangePin:true };
                          return x;
                        }));
                        if (allPessoas.length > 0) onUpdate("pessoas", allPessoas.map(p=>{
                          if (p.linkedManagerId === m.id) return { ...p, pin:newPin, mustChangePin:true };
                          if ((p.cpf||"").replace(/\D/g,"") === cpfDigits) return { ...p, pin:newPin, mustChangePin:true };
                          return p;
                        }));
                        if (allEmployees.length > 0) onUpdate("employees", allEmployees.map(e=>{
                          if ((e.cpf||"").replace(/\D/g,"") === cpfDigits) return { ...e, pin:newPin, mustChangePin:true };
                          return e;
                        }));
                        onUpdate("_toast", `🔐 PIN de ${m.name} resetado para ${newPin}`);
                      }} title="Resetar PIN para os 4 primeiros dígitos do CPF" style={{...S.btnSecondary,fontSize:isMobile?11:12,flex:isMobile?1:undefined,textAlign:"center",padding:isMobile?"6px 8px":undefined}}>{isMobile?"🔐 PIN":"🔐 Resetar PIN"}</button>
                      <button onClick={()=>{setEditMgrId(m.id);setMgrForm({name:m.name,cpf:m.cpf??"",pin:m.pin??"",restaurantIds:m.restaurantIds??[],perms:m.perms??{tips:true,schedule:true},isDP:m.isDP??false,profile:m.profile??"custom",areas:m.areas??[],masterRestaurantIds:m.masterRestaurantIds??[]});setShowMgrModal(true);}} style={{...S.btnSecondary,fontSize:isMobile?11:12,flex:isMobile?1:undefined,textAlign:"center",padding:isMobile?"6px 8px":undefined}}>Editar</button>
                      <button onClick={async ()=>{
                        // Proteger último master do restaurante
                        if ((m.masterRestaurantIds??[]).includes(selRestaurant)) {
                          const mastersHere = managers.filter(x => x.profile === "dp" && (x.masterRestaurantIds??[]).includes(selRestaurant) && x.id !== m.id);
                          if (mastersHere.length === 0) { onUpdate("_toast","⚠️ Este é o último Master do restaurante. Nomeie outro Master antes de remover."); return; }
                        }
                        if(!await appConfirm(`Remover ${m.name} deste restaurante?`)) return;
                        const newIds = (m.restaurantIds??[]).filter(rid=>rid!==selRestaurant);
                        if(newIds.length === 0) {
                          softDelete("managers", m);
                          onUpdate("managers", managers.filter(x=>x.id!==m.id));
                          onUpdate("_toast", `🗑️ ${m.name} movido para a lixeira.`);
                        } else {
                          onUpdate("managers", managers.map(x=>x.id===m.id?{...x,restaurantIds:newIds}:x));
                          onUpdate("_toast", `✅ ${m.name} removido deste restaurante.`);
                        }
                      }} style={{background:"none",border:"1px solid var(--red)33",borderRadius:8,color:"var(--red)",cursor:"pointer",fontSize:isMobile?11:12,padding:isMobile?"6px 8px":"6px 12px",fontFamily:"'DM Sans',sans-serif",flex:isMobile?1:undefined,textAlign:"center"}}>Remover</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Adicionar gestor existente */}
            {managers.filter(m=>!m.restaurantIds?.includes(selRestaurant)).length > 0 && (
              <div style={{...S.card,marginTop:16,background:"var(--bg2)"}}>
                <p style={{color:"var(--text3)",fontSize:13,marginBottom:12,fontWeight:600}}>Adicionar gestor existente a este restaurante:</p>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {managers.filter(m=>!m.restaurantIds?.includes(selRestaurant)).map(m=>(
                    <div key={m.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",borderRadius:10,background:"var(--card-bg)",border:"1px solid var(--border)"}}>
                      <div>
                        <span style={{color:"var(--text)",fontWeight:600,fontSize:14}}>{m.name}</span>
                        <span style={{color:"var(--text3)",fontSize:12,marginLeft:8}}>já acessa: {(m.restaurantIds??[]).map(rid=>restaurants.find(r=>r.id===rid)?.name).filter(Boolean).join(", ")||"nenhum"}</span>
                      </div>
                      <button onClick={()=>{
                        onUpdate("managers", managers.map(x=>x.id===m.id?{...x,restaurantIds:[...(x.restaurantIds??[]),selRestaurant]}:x));
                        onUpdate("_toast",`✅ ${m.name} adicionado a ${rest?.name}`);
                      }} style={{padding:"6px 14px",borderRadius:8,border:`1px solid ${ac}`,background:"transparent",color:"var(--ac-text)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:12,fontWeight:600}}>
                        + Adicionar
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Financeiro */}
        {restTab === "financeiro" && (() => {
          const fin = rest?.financeiro ?? {};

          // ─── Helpers ────────────────────────────────────────────────
          const fmt = (d) => d ? new Date(d+"T12:00:00").toLocaleDateString("pt-BR") : "—";
          const addDays = (d, n) => { const r = new Date(d+"T12:00:00"); r.setDate(r.getDate()+n); return r.toISOString().slice(0,10); };
          const addYear = (d) => { const r = new Date(d+"T12:00:00"); r.setFullYear(r.getFullYear()+1); return r.toISOString().slice(0,10); };

          // ─── Plano e valor ──────────────────────────────────────────
          const plano        = getPlano(rest);
          const tipo         = rest?.tipoCobranca ?? "mensal";
          const isIsento     = rest?.planoId === "pIsento";
          const isEnt        = rest?.planoId === "p999";
          const isOrc        = rest?.planoId === "pOrc";
          const empMax       = isIsento ? 999 : isEnt ? (rest?.empMaxCustom ?? 51) : isOrc ? (rest?.empMaxCustom ?? 101) : plano.empMax;
          const empAtivos    = employees.filter(e=>e.restaurantId===selRestaurant&&!e.inactive).length;

          // Early Adopter — 30% desconto permanente
          const isEarlyAdopter = rest?.earlyAdopter === true;
          const EA_DESC = 0.7; // fator multiplicador (1 - 0.30)

          // Valor da cobrança — mensal = valor do mês, anual = total do ano
          const valorBruto = (() => {
            if (isIsento) return 0;
            if (isOrc) return null;
            if (isEnt) {
              const porEmp = empMax * 7.99;
              return tipo === "anual" ? porEmp * 12 * 0.9 : porEmp;
            }
            if (tipo === "anual") return (plano.anual ?? 0) * 12;
            return plano.mensal;
          })();
          const valorMensal = valorBruto != null && isEarlyAdopter ? Math.round(valorBruto * EA_DESC * 100) / 100 : valorBruto;

          // ─── Ciclo ─────────────────────────────────────────────────
          // fonte única de verdade: fin.cicloInicio e fin.cicloFim
          const cicloIni  = fin.cicloInicio ?? null;
          const cicloFim  = fin.cicloFim ?? null;
          const trialIni  = fin.trialInicio ?? null;
          const trialFim  = fin.trialFim ?? null;
          const hoje      = today();

          const emTrial       = trialIni && !cicloIni && trialFim >= hoje;
          const trialVencido  = trialIni && !cicloIni && trialFim < hoje;
          const cicloAtivo    = cicloIni && cicloFim && cicloFim >= hoje;
          const cicloVencido  = cicloIni && cicloFim && cicloFim < hoje;

          const diasCiclo  = cicloFim ? Math.ceil((new Date(cicloFim+"T12:00:00")-new Date())/(1000*60*60*24)) : null;
          const diasTrial  = trialFim ? Math.ceil((new Date(trialFim+"T12:00:00")-new Date())/(1000*60*60*24)) : null;
          const alertaVenc = cicloAtivo && diasCiclo !== null && diasCiclo <= 7;

          const inadimplente = fin.status === "inadimplente";

          // ─── Próximo ciclo a cobrar ─────────────────────────────────
          // começa no dia seguinte ao fim do ciclo atual (ou trial)
          const proxIni = (() => {
            if (cicloFim) return addDays(cicloFim, 1);
            if (trialFim) return addDays(trialFim, 1);
            return hoje;
          })();
          const proxFim = tipo === "anual" ? addDays(addYear(proxIni), -1) : addDays(proxIni, 29);
          // Vencimento = dia anterior ao início do próximo ciclo = último dia do ciclo atual
          const proxVenc = addDays(proxIni, -1);

          // ─── Helpers de persistência ────────────────────────────────
          function saveFin(update) {
            onUpdate("restaurants", restaurants.map(r => r.id===selRestaurant ? {...r, financeiro:{...fin,...update}} : r));
          }

          function confirmar(cob) {
            const ini  = hoje;
            const fim  = tipo === "anual" ? addDays(addYear(ini), -1) : addDays(ini, 29);
            const nIni = addDays(fim, 1);
            const nFim = tipo === "anual" ? addDays(addYear(nIni), -1) : addDays(nIni, 29);
            const updCobs = (fin.cobrancas??[]).map(x => x.id===cob.id ? {...x, status:"pago", pagoEm:new Date().toISOString()} : x);
            const novoPag = { id:Date.now().toString(), data:ini, valor:cob.valor, forma:cob.forma, periodoLabel:`${fmt(ini)} a ${fmt(fim)}`, registradoEm:new Date().toISOString() };
            const proxCob = { id:(Date.now()+1).toString(), periodoLabel:`${fmt(nIni)} a ${fmt(nFim)}`, periodoInicio:nIni, periodoFim:nFim, venc:nFim, valor:valorMensal??cob.valor, forma:cob.forma??"PIX", chave:cob.chave??PIX_PADRAO, criadaEm:new Date().toISOString(), status:"pendente", autoGerada:true };
            saveFin({ cobrancas:[...updCobs, proxCob], pagamentos:[novoPag,...(fin.pagamentos??[])], status:"ativo", cicloInicio:ini, cicloFim:fim });
            onUpdate("_toast", `✅ Pago! Ciclo ${fmt(ini)} a ${fmt(fim)}`);
          }

          const pagamentos = fin.pagamentos ?? [];
          const cobrancasAbertas = (fin.cobrancas??[]).filter(c=>c.status==="pendente"||c.status==="aguardando_confirmacao");

          return (
            <div style={{padding:"24px",maxWidth:700,margin:"0 auto"}}>

              {/* ── STATUS ISENTO ── */}
              {isIsento && (
                <div style={{...S.card,marginBottom:20,border:"1px solid #10b98133",background:"linear-gradient(135deg,#10b98108,#10b98118)"}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                    <span style={{fontSize:28}}>🤝</span>
                    <div>
                      <div style={{color:"#10b981",fontWeight:700,fontSize:16}}>Plano Isento</div>
                      <div style={{color:"var(--text3)",fontSize:12}}>Sem cobrança — ativo por tempo indeterminado</div>
                    </div>
                  </div>
                  <div style={{color:"var(--text2)",fontSize:13,marginTop:4}}>
                    Empregados ativos: <strong>{empAtivos}</strong> · Sem limite
                  </div>
                </div>
              )}

              {/* ── 1. STATUS DO CICLO ── */}
              {!isIsento && <div style={{...S.card, marginBottom:20, border:`1px solid ${
                inadimplente?"var(--red)44":
                trialVencido||cicloVencido?"var(--red)44":
                emTrial||alertaVenc?"#f59e0b44":"var(--green)44"
              }`, background:
                inadimplente?"var(--red-bg)":
                trialVencido||cicloVencido?"var(--red-bg)":
                emTrial||alertaVenc?"#fffbeb":"var(--green-bg)"
              }}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12}}>
                  <div>
                    {inadimplente && <div style={{color:"var(--red)",fontWeight:700,fontSize:16,marginBottom:4}}>🔴 Inadimplente — acesso bloqueado</div>}
                    {!inadimplente && emTrial && <>
                      <div style={{color:"#92400e",fontWeight:700,fontSize:16,marginBottom:4}}>🎯 Período de teste</div>
                      <div style={{color:"#92400e",fontSize:13}}>{diasTrial} dia{diasTrial!==1?"s":""} restante{diasTrial!==1?"s":""} — até {fmt(trialFim)}</div>
                    </>}                    {!inadimplente && trialVencido && <>
                      <div style={{color:"var(--red)",fontWeight:700,fontSize:16,marginBottom:4}}>⏰ Trial encerrado</div>
                      <div style={{color:"var(--red)",fontSize:13}}>Gere a 1ª cobrança para ativar o acesso pago.</div>
                    </>}
                    {!inadimplente && cicloAtivo && <>
                      <div style={{color:alertaVenc?"#92400e":"var(--green)",fontWeight:700,fontSize:16,marginBottom:4}}>
                        {alertaVenc?`⚡ Vence em ${diasCiclo} dia${diasCiclo!==1?"s":""}!`:"✅ Ciclo ativo"}
                      </div>
                      <div style={{color:"var(--text2)",fontSize:13}}>
                        {fmt(cicloIni)} a {fmt(cicloFim)}
                        {alertaVenc && <span style={{color:"#a16207",marginLeft:8,fontSize:12}}>— envie a cobrança logo!</span>}
                      </div>
                    </>}
                    {!inadimplente && cicloVencido && <>
                      <div style={{color:"var(--red)",fontWeight:700,fontSize:16,marginBottom:4}}>🔴 Ciclo vencido</div>
                      <div style={{color:"var(--red)",fontSize:13}}>Venceu em {fmt(cicloFim)} — {Math.abs(diasCiclo)} dias sem renovação</div>
                    </>}
                    {!cicloIni && !trialIni && <div style={{color:"var(--text3)",fontWeight:600,fontSize:14}}>⚙️ Sem ciclo iniciado</div>}
                  </div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    {inadimplente && <button onClick={()=>saveFin({status:"ativo"})} style={{padding:"8px 16px",borderRadius:8,border:"1px solid var(--green)44",background:"var(--green-bg)",color:"var(--green)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:700}}>✅ Liberar acesso</button>}

                    {/* Trial ativo — estender ou suspender */}
                    {!inadimplente && emTrial && <>
                      <button onClick={()=>{
                        const maxDias = 30 - Math.ceil((new Date(trialFim+"T12:00:00")-new Date(trialIni+"T12:00:00"))/(1000*60*60*24));
                        const diasExtra = parseInt(window.prompt(`Quantos dias a mais de trial? (máximo ${maxDias} dias adicionais, total máximo 30 dias)`));
                        if (!diasExtra || isNaN(diasExtra) || diasExtra <= 0) return;
                        const novoFim = addDays(trialFim, Math.min(diasExtra, maxDias));
                        saveFin({ trialFim: novoFim });
                        onUpdate("_toast", `✅ Trial estendido até ${fmt(novoFim)}`);
                      }} style={{padding:"8px 16px",borderRadius:8,border:"1px solid #f59e0b44",background:"#fffbeb",color:"#92400e",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:600}}>
                        ⏰ Estender trial
                      </button>
                      <button onClick={async ()=>{
                        if(!await appConfirm("Suspender o período de teste e bloquear o acesso?")) return;
                        saveFin({ trialFim: addDays(hoje, -1), status:"inadimplente" });
                        onUpdate("_toast","🔴 Trial suspenso. Acesso bloqueado.");
                      }} style={{padding:"8px 16px",borderRadius:8,border:"1px solid var(--red)33",background:"transparent",color:"var(--red)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:600}}>
                        🚫 Suspender trial
                      </button>
                    </>}

                    {!inadimplente && cicloIni && <button onClick={async ()=>{if(!await appConfirm("Marcar como inadimplente e bloquear acesso?"))return; saveFin({status:"inadimplente"});}} style={{padding:"8px 16px",borderRadius:8,border:"1px solid var(--red)33",background:"transparent",color:"var(--red)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:600}}>🔴 Inadimplente</button>}

                    {/* Trial vencido — bloquear ou reativar */}
                    {!inadimplente && trialVencido && <>
                      <button onClick={()=>{
                        const novoFim = addDays(hoje, 7);
                        saveFin({ trialFim: novoFim, status:"ativo" });
                        onUpdate("_toast",`🎯 Trial reativado até ${fmt(novoFim)}`);
                      }} style={{padding:"8px 16px",borderRadius:8,border:"1px solid #f59e0b44",background:"#fffbeb",color:"#92400e",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:600}}>
                        🔄 Reativar trial
                      </button>
                      <button onClick={()=>saveFin({status:"inadimplente"})} style={{padding:"8px 16px",borderRadius:8,border:"1px solid var(--red)33",background:"transparent",color:"var(--red)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:600}}>
                        🔴 Bloquear acesso
                      </button>
                    </>}
                  </div>
                </div>
              </div>}

              {/* Iniciar trial */}
              {!isIsento && !trialIni && !cicloIni && (
                <div style={{...S.card,marginBottom:20,textAlign:"center",padding:28}}>
                  <div style={{fontSize:32,marginBottom:12}}>🎯</div>
                  <h4 style={{color:"var(--text)",fontWeight:700,fontSize:15,margin:"0 0 8px"}}>Iniciar período de teste</h4>
                  <p style={{color:"var(--text3)",fontSize:13,margin:"0 0 16px"}}>7 dias gratuitos — a 1ª cobrança vence no último dia do trial</p>
                  <button onClick={()=>{
                    const fim7 = addDays(hoje, 6);
                    const cob = { id:Date.now().toString(), periodoLabel:`Trial — ${fmt(hoje)} a ${fmt(fim7)}`, periodoInicio:hoje, periodoFim:fim7, venc:fim7, valor:valorMensal??0, forma:"PIX", chave:PIX_PADRAO, criadaEm:new Date().toISOString(), status:"pendente", isTrial:true };
                    saveFin({ trialInicio:hoje, trialFim:fim7, status:"ativo", cobrancas:[...(fin.cobrancas??[]), cob] });
                    onUpdate("_toast","🎯 Trial iniciado até "+fmt(fim7));
                  }} style={{...S.btnPrimary,width:"auto",padding:"10px 28px"}}>Iniciar trial</button>
                </div>
              )}

              {/* ── 2. PLANO ── */}
              <div style={{...S.card,marginBottom:20}}>
                <h4 style={{color:"var(--text)",fontWeight:700,fontSize:14,margin:"0 0 14px"}}>📦 Plano contratado</h4>
                <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:14}}>
                  {PLANOS.map(p=>{
                    const sel = (rest?.planoId??"p10")===p.id;
                    const precos = {
                      pIsento: "Sem cobrança",
                      p10:  "R$97/mês · R$1.047,60/ano (−10%)",
                      p20:  "R$187/mês · R$2.019,60/ano (−10%)",
                      p50:  "R$397/mês · R$4.287,60/ano (−10%)",
                      p999: "R$7,99/emp./mês · 10% desc. no anual",
                      pOrc: "Sob orçamento"
                    };
                    const empLabel = p.id==="pIsento"?"(ilimitado)":p.id==="p10"?"(até 10)":p.id==="p20"?"(até 20)":p.id==="p50"?"(até 50)":p.id==="p999"?"(51–100)":"(+100)";
                    const isentoSel = p.id==="pIsento" && sel;
                    return (
                      <button key={p.id} onClick={()=>onUpdate("restaurants",restaurants.map(r=>r.id===selRestaurant?{...r,planoId:p.id}:r))}
                        style={{padding:"10px 14px",borderRadius:10,border:`1px solid ${isentoSel?"#10b981":sel?ac:"var(--border)"}`,background:isentoSel?"#10b98112":sel?"var(--ac-bg)":"transparent",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",textAlign:"left",display:"flex",flexDirection:"column",gap:4}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",width:"100%"}}>
                          <span style={{color:isentoSel?"#10b981":sel?"var(--ac-text)":"var(--text2)",fontWeight:sel?700:400}}>{sel?"✓":"○"} {p.id==="pIsento"?"🤝 ":""}{p.label} {empLabel}</span>
                          <span style={{color:p.id==="pIsento"?"#10b981":"var(--text3)",fontSize:11,fontWeight:p.id==="pIsento"?600:400}}>{precos[p.id]}</span>
                        </div>
                        <span style={{fontSize:10,color:p.multi?"var(--green)":"var(--text3)",fontWeight:500}}>
                          {p.id==="pIsento"?"🤝 Parceiro / uso interno — sem cobrança":p.multi?"🏢 Múltiplas unidades":"🏢 Máx. 1 unidade"}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {isIsento ? (
                  <div style={{padding:"14px 16px",borderRadius:10,border:"1px solid #10b98133",background:"linear-gradient(135deg,#10b98108,#10b98115)",marginBottom:12}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                      <span style={{fontSize:18}}>🤝</span>
                      <span style={{color:"#10b981",fontWeight:700,fontSize:14}}>Plano Isento</span>
                    </div>
                    <div style={{color:"var(--text3)",fontSize:12}}>Restaurante parceiro ou de uso interno. Sem cobrança, sem trial, sem vencimento. Ativo até que o plano seja alterado.</div>
                  </div>
                ) : (<>
                <div style={{display:"flex",gap:8,marginBottom:12}}>
                  {[["mensal","Mensal"],["anual","Anual (−10%)"]].map(([v,l])=>{
                    const sel=(rest?.tipoCobranca??"mensal")===v;
                    return <button key={v} onClick={()=>onUpdate("restaurants",restaurants.map(r=>r.id===selRestaurant?{...r,tipoCobranca:v}:r))}
                      style={{flex:1,padding:"10px",borderRadius:10,border:`1px solid ${sel?"var(--green)":"var(--border)"}`,background:sel?"var(--green-bg)":"transparent",color:sel?"var(--green)":"var(--text3)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:sel?700:400}}>
                      {sel?"✓":""} {l}
                    </button>;
                  })}
                </div>

                {/* Early Adopter toggle */}
                <label style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:10,border:`1px solid ${isEarlyAdopter?"#d4a01766":"var(--border)"}`,background:isEarlyAdopter?"linear-gradient(135deg,#1c120811,#d4a01711)":"transparent",cursor:"pointer",marginBottom:12,userSelect:"none"}}>
                  <input type="checkbox" checked={isEarlyAdopter}
                    onChange={e=>onUpdate("restaurants",restaurants.map(r=>r.id===selRestaurant?{...r,earlyAdopter:e.target.checked}:r))}
                    style={{width:18,height:18,accentColor:"#d4a017",cursor:"pointer",flexShrink:0}}/>
                  <div>
                    <div style={{color:isEarlyAdopter?"#d4a017":"var(--text2)",fontWeight:700,fontSize:13}}>🚀 Early Adopter — 30% off permanente</div>
                    <div style={{color:"var(--text3)",fontSize:11,marginTop:2}}>Primeiros 30 clientes. Desconto válido enquanto a assinatura estiver ativa.</div>
                  </div>
                </label>
                </>)}

                {isEnt && <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                  <label style={{...S.label,marginBottom:0}}>Empregados contratados (51–100):</label>
                  <input type="number" min="51" max="100" defaultValue={rest?.empMaxCustom??51}
                    onBlur={e=>{const v=Math.min(100,Math.max(51,parseInt(e.target.value)||51));onUpdate("restaurants",restaurants.map(r=>r.id===selRestaurant?{...r,empMaxCustom:v}:r));}}
                    style={{...S.input,width:80,textAlign:"center",fontFamily:"'DM Mono',monospace"}}/>
                </div>}
                {isOrc && <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                  <label style={{...S.label,marginBottom:0}}>Empregados (+100):</label>
                  <input type="number" min="101" defaultValue={rest?.empMaxCustom??101}
                    onBlur={e=>{const v=Math.max(101,parseInt(e.target.value)||101);onUpdate("restaurants",restaurants.map(r=>r.id===selRestaurant?{...r,empMaxCustom:v}:r));}}
                    style={{...S.input,width:90,textAlign:"center",fontFamily:"'DM Mono',monospace"}}/>
                </div>}

                {/* Valor calculado */}
                <div style={{padding:"14px",borderRadius:12,background:isEarlyAdopter?"linear-gradient(135deg,#1c120811,#d4a01718)":"var(--ac-bg)",border:`1px solid ${isEarlyAdopter?"#d4a01744":"var(--ac)33"}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <div style={{color:"var(--text3)",fontSize:11,fontWeight:600,marginBottom:4}}>
                      VALOR A COBRAR {isEarlyAdopter && <span style={{color:"#d4a017",fontWeight:700}}>· 🚀 −30% EARLY ADOPTER</span>}
                    </div>
                    {isOrc
                      ? <div style={{color:"var(--ac-text)",fontWeight:800,fontSize:18}}>Sob orçamento</div>
                      : <div>
                          {isEarlyAdopter && valorBruto != null && <div style={{color:"var(--text3)",fontSize:13,textDecoration:"line-through",marginBottom:2,fontFamily:"'DM Mono',monospace"}}>
                            R$ {valorBruto.toLocaleString("pt-BR",{minimumFractionDigits:2})}
                          </div>}
                          <div style={{color:isEarlyAdopter?"#d4a017":"var(--ac-text)",fontWeight:800,fontSize:22,fontFamily:"'DM Mono',monospace"}}>
                            R$ {valorMensal?.toLocaleString("pt-BR",{minimumFractionDigits:2})}
                            <span style={{color:"var(--text3)",fontSize:13,fontWeight:400}}>
                              {tipo==="anual"?"/ano (12x)":"/mês"}
                            </span>
                          </div>
                        </div>
                    }
                    {isEnt && <div style={{color:"var(--text3)",fontSize:11,marginTop:2}}>
                      {tipo==="anual"
                        ? `${empMax} emp. × R$7,99 × 12 meses × 0,9 (−10%)${isEarlyAdopter?" × 0,7 (−30%)":""}`
                        : `${empMax} emp. × R$7,99/mês${isEarlyAdopter?" × 0,7 (−30%)":""}`}
                    </div>}
                  </div>
                  <div style={{color:"var(--text3)",fontSize:13}}>{empAtivos}/{isOrc?"∞":empMax} ativos</div>
                </div>
              </div>

              {/* ── 3. GERAR COBRANÇA ── */}
              {!isIsento && <div style={{...S.card,marginBottom:20}}>
                <h4 style={{color:"var(--text)",fontWeight:700,fontSize:14,margin:"0 0 4px"}}>📲 Gerar cobrança</h4>
                <p style={{color:"var(--text3)",fontSize:12,margin:"0 0 14px"}}>Envia a fatura via WhatsApp para o contato financeiro</p>

                {!rest?.whatsappFin && <div style={{padding:"10px 14px",borderRadius:10,background:"var(--red-bg)",border:"1px solid var(--red)33",marginBottom:12}}>
                  <p style={{color:"var(--red)",fontSize:12,margin:0}}>⚠️ WhatsApp financeiro não cadastrado. Edite o restaurante.</p>
                </div>}

                {/* Próximo ciclo pré-calculado */}
                <div style={{background:"var(--bg2)",borderRadius:12,padding:"16px",marginBottom:14,border:"1px solid var(--border)"}}>
                  <div style={{color:"var(--text3)",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5,marginBottom:10}}>Próximo ciclo</div>

                  <div style={{marginBottom:12}}>
                    <div style={{color:"var(--text)",fontWeight:700,fontSize:15,marginBottom:2}}>{fmt(cobPeriodo||proxIni)} a {fmt(cobVenc||proxFim)}</div>
                    <div style={{color:"var(--text3)",fontSize:12,marginBottom:10}}>{tipo==="anual"?"Ciclo anual":"Ciclo 30 dias"} · Venc. {fmt(cobVenc||proxVenc)}</div>
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <span style={{color:"var(--text3)",fontSize:12,width:80,flexShrink:0}}>Início:</span>
                        <input key={`ini-${proxIni}`} type="date" defaultValue={cobPeriodo||proxIni}
                          onChange={e=>setCobPeriodo(e.target.value)}
                          style={{...S.input,fontSize:13,flex:1,boxSizing:"border-box"}}/>
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <span style={{color:"var(--text3)",fontSize:12,width:80,flexShrink:0}}>Vencimento:</span>
                        <input key={`fim-${proxVenc}`} type="date" defaultValue={cobVenc||proxVenc}
                          onChange={e=>setCobVenc(e.target.value)}
                          style={{...S.input,fontSize:13,flex:1,boxSizing:"border-box"}}/>
                      </div>
                    </div>
                  </div>

                  {/* Valor */}
                  <div style={{marginBottom:12}}>
                    <div style={{color:"var(--text3)",fontSize:11,marginBottom:4}}>Valor (R$)</div>
                    <input type="number" value={cobValor||(valorMensal?.toFixed(2)??"0")} onChange={e=>setCobValor(e.target.value)}
                      style={{...S.input,fontSize:20,fontWeight:700,color:"var(--ac-text)",fontFamily:"'DM Mono',monospace",width:"100%",boxSizing:"border-box"}}/>
                    {valorMensal && <div style={{color:"var(--text3)",fontSize:11,marginTop:3}}>Plano {plano.label} — R${valorMensal.toFixed(2)}{tipo==="anual"?"/ano":"/mês"}</div>}
                  </div>
                  <div style={{color:"var(--text3)",fontSize:11,marginBottom:8}}>Forma de pagamento</div>
                  <div style={{display:"flex",gap:8,marginBottom:10}}>
                    {[["pix","PIX"],["link","Link"]].map(([v,l])=>(
                      <button key={v} onClick={()=>{setCobForma(v);if(v==="pix")setCobChave(PIX_PADRAO);else setCobChave("");}}
                        style={{flex:1,padding:"9px",borderRadius:10,border:`2px solid ${cobForma===v?ac:"var(--border)"}`,background:cobForma===v?"var(--ac-bg)":"transparent",color:cobForma===v?"var(--ac-text)":"var(--text3)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:cobForma===v?700:400}}>
                        {l}
                      </button>
                    ))}
                  </div>
                  {cobForma==="pix"
                    ? <div><input value={cobChave} onChange={e=>setCobChave(e.target.value)} placeholder="Chave PIX" style={S.input}/>
                        <p style={{color:"var(--text3)",fontSize:11,marginTop:4,marginBottom:0}}>Padrão: <strong>{PIX_PADRAO}</strong> — {PIX_NOME}
                          {cobChave!==PIX_PADRAO&&<button onClick={()=>setCobChave(PIX_PADRAO)} style={{background:"none",border:"none",color:ac,cursor:"pointer",fontSize:11,marginLeft:8,padding:0,textDecoration:"underline"}}>Restaurar</button>}
                        </p></div>
                    : <input value={cobLink} onChange={e=>setCobLink(e.target.value)} placeholder="Cole o link de pagamento" style={S.input}/>
                  }
                </div>

                {/* Preview */}
                <div style={{background:"#f0fdf4",borderRadius:10,padding:"10px 14px",marginBottom:12,border:"1px solid #86efac"}}>
                  <div style={{color:"#166534",fontSize:11,fontWeight:700,marginBottom:4}}>📱 Mensagem que será enviada</div>
                  <div style={{color:"#166534",fontSize:13,lineHeight:1.6}}>
                    Ola, <strong>{rest?.name}</strong>! Segue o link da sua fatura referente ao período <strong>{fmt(cobPeriodo||proxIni)} a {fmt(cobVenc||proxFim)}</strong>: apptip.app/fatura/(link)
                  </div>
                </div>

                <button onClick={()=>{
                  if(!rest?.whatsappFin){alert("Cadastre o WhatsApp financeiro primeiro.");return;}
                  const valor=parseFloat(cobValor)||valorMensal;
                  if(!valor){alert("Verifique o valor.");return;}
                  if(cobForma==="link"&&!cobLink.trim()){alert("Cole o link de pagamento.");return;}
                  const ini=cobPeriodo||proxIni;
                  const fim=cobVenc||proxFim;
                  const venc=cobVenc||proxVenc;
                  const pLabel=`${fmt(ini)} a ${fmt(fim)}`;
                  const chave=cobForma==="pix"?(cobChave||PIX_PADRAO):cobLink;
                  const cob={id:Date.now().toString(),periodoLabel:pLabel,periodoInicio:ini,periodoFim:fim,venc,valor,forma:cobForma==="pix"?"PIX":"Link",chave,criadaEm:new Date().toISOString(),status:"pendente"};
                  saveFin({cobrancas:[...(fin.cobrancas??[]).filter(c=>!(c.autoGerada&&c.status==="pendente")),cob]});
                  const faturaUrl=`https://apptip.app/fatura/${cob.id}`;
                  const msg=`Ola, *${rest?.name}*!\n\nSegue o link da sua fatura *Sistema* referente ao periodo *${pLabel}*:\n\n${faturaUrl}\n\nQualquer duvida estamos a disposicao!\n*Equipe de gestão*`;
                  const numero=rest.whatsappFin.replace(/\D/g,"");
                  setTimeout(()=>{window.location.href=`https://wa.me/55${numero}?text=${encodeURIComponent(msg)}`;},300);
                  setCobValor("");setCobVenc("");setCobLink("");setCobPeriodo("");
                  onUpdate("_toast","📲 Cobrança enviada!");
                }} disabled={!rest?.whatsappFin}
                  style={{...S.btnPrimary,opacity:rest?.whatsappFin?1:0.5,cursor:rest?.whatsappFin?"pointer":"not-allowed"}}>
                  📲 Enviar cobrança via WhatsApp
                </button>
              </div>}

              {/* ── 4. COBRANÇAS EM ABERTO ── */}
              {!isIsento && cobrancasAbertas.length > 0 && (
                <div style={{...S.card,marginBottom:20,border:"1px solid #f59e0b33",background:"#fffbeb"}}>
                  <h4 style={{color:"#92400e",fontWeight:700,fontSize:14,margin:"0 0 12px"}}>⏳ Cobranças em aberto</h4>
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {cobrancasAbertas.map(c=>(
                      <div key={c.id} style={{padding:"12px 14px",borderRadius:10,background:"#fff",border:`1px solid ${c.status==="aguardando_confirmacao"?"#86efac":"#fde68a"}`}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                          <div>
                            <div style={{color:"var(--text)",fontWeight:700,fontSize:14,marginBottom:2}}>
                              R$ {c.valor?.toLocaleString("pt-BR",{minimumFractionDigits:2})}
                            </div>
                            <div style={{color:"var(--text3)",fontSize:12,marginBottom:4}}>
                              {c.periodoLabel} · {c.forma} · Venc. {fmt(c.venc)}
                            </div>
                            {c.status==="aguardando_confirmacao" && (
                              <div style={{color:"#166534",fontSize:12,fontWeight:600,background:"#f0fdf4",padding:"4px 10px",borderRadius:6,display:"inline-block"}}>
                                ✅ Cliente informou pagamento em {fmt(c.clienteConfirmouEm?.slice(0,10))} — aguarda sua confirmação
                              </div>
                            )}
                          </div>
                          <div style={{display:"flex",gap:6,flexShrink:0}}>
                            {c.status==="aguardando_confirmacao" && <>
                              <button onClick={()=>confirmar(c)} style={{padding:"7px 14px",borderRadius:8,border:"1px solid var(--green)44",background:"var(--green-bg)",color:"var(--green)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:700}}>✅ Confirmar</button>
                              <button onClick={async ()=>{if(!await appConfirm("Negar e marcar inadimplente?"))return;saveFin({cobrancas:(fin.cobrancas??[]).map(x=>x.id===c.id?{...x,status:"pendente",clienteConfirmou:false}:x),status:"inadimplente"});onUpdate("_toast","🔴 Inadimplente.");}} style={{padding:"7px 10px",borderRadius:8,border:"1px solid var(--red)33",background:"transparent",color:"var(--red)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:12}}>✕ Negar</button>
                            </>}
                            {c.status==="pendente" && <>
                              <button onClick={async ()=>{if(!await appConfirm("Confirmar recebimento deste pagamento?"))return;confirmar(c);}} style={{padding:"7px 14px",borderRadius:8,border:"1px solid var(--green)44",background:"var(--green-bg)",color:"var(--green)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:700}}>✅ Confirmar pago</button>
                              <button onClick={async ()=>{if(!await appConfirm("Cancelar esta cobrança?"))return;saveFin({cobrancas:(fin.cobrancas??[]).map(x=>x.id===c.id?{...x,status:"cancelada"}:x)});}} style={{padding:"7px 10px",borderRadius:8,border:"1px solid var(--border)",background:"transparent",color:"var(--text3)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:12}}>✕ Cancelar</button>
                            </>}                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── 5. HISTÓRICO ── */}
              <div style={{...S.card}}>
                <h4 style={{color:"var(--text)",fontWeight:700,fontSize:14,margin:"0 0 14px"}}>📋 Histórico de pagamentos</h4>
                {pagamentos.length === 0 && <p style={{color:"var(--text3)",fontSize:13,textAlign:"center",padding:"20px 0"}}>Nenhum pagamento confirmado ainda.</p>}
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {pagamentos.map(p=>(
                    <div key={p.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 14px",borderRadius:10,background:"var(--bg2)"}}>
                      <div>
                        <div style={{color:"var(--text)",fontWeight:700,fontSize:14,fontFamily:"'DM Mono',monospace",marginBottom:2}}>
                          R$ {p.valor?.toLocaleString("pt-BR",{minimumFractionDigits:2})}
                        </div>
                        <div style={{color:"var(--text3)",fontSize:12}}>
                          {fmt(p.data)} · {p.forma?.toUpperCase()} · {p.periodoLabel || p.obs || ""}
                        </div>
                        <div style={{color:"var(--green)",fontSize:11,marginTop:2,fontWeight:600}}>✅ Confirmado pelo Admin</div>
                      </div>
                      <button onClick={async ()=>{if(!await appConfirm("Cancelar este pagamento? O ciclo será revertido."))return;saveFin({pagamentos:pagamentos.filter(x=>x.id!==p.id),cicloInicio:null,cicloFim:null,status:"ativo"});onUpdate("_toast","↩ Pagamento cancelado.");}}
                        style={{background:"none",border:"1px solid var(--red)33",borderRadius:8,color:"var(--red)",cursor:"pointer",fontSize:12,padding:"5px 10px",fontFamily:"'DM Sans',sans-serif"}}>
                        Cancelar
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Modais herdados */}
        {showMgrModal && (
          <Modal title={editMgrId?"Editar Gestor":"Novo Gestor"} onClose={()=>setShowMgrModal(false)} wide>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              {/* Puxar da equipe — só no modo criação */}
              {!editMgrId && (() => {
                const restEmps = employees.filter(e => e.restaurantId === selRestaurant && !e.inactive);
                if (restEmps.length === 0) return null;
                return (
                  <div style={{background:"var(--bg2)",border:"1px solid var(--border)",borderRadius:10,padding:"10px 14px"}}>
                    <label style={{...S.label,margin:0}}>👥 Puxar dados de um empregado da equipe</label>
                    <p style={{color:"var(--text3)",fontSize:11,margin:"4px 0 8px"}}>Selecione para pré-preencher nome, CPF e PIN. O empregado continua ativo na equipe.</p>
                    <select
                      onChange={e => {
                        const emp = restEmps.find(x => x.id === e.target.value);
                        if (!emp) return;
                        setMgrForm(f => ({...f, name: emp.name, cpf: emp.cpf ?? ""}));
                      }}
                      style={{...S.input,cursor:"pointer"}}
                      defaultValue=""
                    >
                      <option value="" disabled>Selecionar empregado...</option>
                      {restEmps.sort((a,b)=>a.name.localeCompare(b.name)).map(e => {
                        const role = roles.find(r => r.id === e.roleId);
                        return <option key={e.id} value={e.id}>{e.name}{role ? ` — ${role.name}` : ""}{e.cpf ? ` (${isPrivate(selRestaurant) ? "•••.•••.•••-••" : e.cpf})` : ""}</option>;
                      })}
                    </select>
                  </div>
                );
              })()}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <div><label style={S.label}>Nome completo</label><input value={mgrForm.name} onChange={e=>setMgrForm({...mgrForm,name:e.target.value})} style={S.input}/></div>
                <div><label style={S.label}>CPF *</label><input value={mgrForm.cpf} onChange={e=>setMgrForm({...mgrForm,cpf:maskCpf(e.target.value)})} placeholder="000.000.000-00" style={S.input} inputMode="numeric"/></div>
              </div>
              {editMgrId
                ? <div><label style={S.label}>PIN (4 dígitos)</label><input type="password" value={mgrForm.pin} onChange={e=>setMgrForm({...mgrForm,pin:e.target.value})} maxLength={4} inputMode="numeric" style={S.input}/></div>
                : <div style={{padding:"8px 12px",borderRadius:8,background:"var(--bg2)",border:"1px solid var(--border)",fontSize:12,color:"var(--text3)"}}> PIN inicial = 4 primeiros dígitos do CPF. No primeiro acesso o gestor será solicitado a trocar.</div>
              }
              {/* Perfil do gestor */}
              <div>
                <label style={S.label}>Perfil do gestor</label>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {[
                    {id:"dp",icon:"📬",title:"Gestor Administrativo",desc:"Acesso completo: gorjetas, escala, cargos, equipe, comunicados, FAQ, DP e notificações. Recebe mensagens do Fale com DP."},
                    {id:"lider",icon:"👔",title:"Líder Operacional",desc:"Acesso apenas à escala e horários da(s) sua(s) área(s)."},
                    {id:"custom",icon:"⚙️",title:"Personalizado",desc:"Escolha as permissões manualmente."},
                  ].map(p=>{
                    const on = mgrForm.profile === p.id;
                    return (
                      <button key={p.id} onClick={()=>{
                        const presets = {
                          dp: {perms:{tips:true,schedule:true,roles:true,employees:true,comunicados:true,faq:true,dp:true,horarios:true},isDP:true,areas:[]},
                          lider: {perms:{tips:false,schedule:true,roles:false,employees:false,comunicados:false,faq:false,dp:false,horarios:true,vt:false},isDP:false},
                          custom: {isDP:mgrForm.isDP},
                        };
                        const preset = presets[p.id];
                        setMgrForm(f=>({...f,...preset,profile:p.id,areas:p.id==="lider"?f.areas:[]}));
                      }}
                        style={{width:"100%",padding:"12px 14px",borderRadius:10,border:`1px solid ${on?ac:"var(--border)"}`,background:on?"var(--ac-bg)":"transparent",color:on?"var(--ac-text)":"var(--text3)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,textAlign:"left",display:"flex",alignItems:"center",gap:10}}>
                        <span style={{fontSize:18}}>{p.icon}</span>
                        <div>
                          <div style={{fontWeight:700}}>{on?"✓ ":""}{p.title}</div>
                          <div style={{fontSize:11,opacity:0.7,marginTop:2}}>{p.desc}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Master toggle — só para Gestor Adm. (Master sempre pode) */}
              {mgrForm.profile === "dp" && (
                <div style={{borderTop:"1px solid var(--border)",paddingTop:12}}>
                  <button onClick={()=>{const ids=mgrForm.masterRestaurantIds??[];setMgrForm({...mgrForm,masterRestaurantIds:ids.includes(selRestaurant)?ids.filter(x=>x!==selRestaurant):[...ids,selRestaurant]});}}
                    style={{width:"100%",padding:"12px 14px",borderRadius:10,border:`1px solid ${(mgrForm.masterRestaurantIds??[]).includes(selRestaurant)?"#d4a017":"var(--border)"}`,background:(mgrForm.masterRestaurantIds??[]).includes(selRestaurant)?"#d4a01715":"transparent",color:(mgrForm.masterRestaurantIds??[]).includes(selRestaurant)?"#d4a017":"var(--text3)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,textAlign:"left",display:"flex",alignItems:"center",gap:10}}>
                    <span style={{fontSize:18}}>🔑</span>
                    <div>
                      <div style={{fontWeight:700}}>{(mgrForm.masterRestaurantIds??[]).includes(selRestaurant)?"✓ Master deste Restaurante":"○ Master deste Restaurante"}</div>
                      <div style={{fontSize:11,opacity:0.7,marginTop:2}}>Pode criar, editar e excluir outros gestores deste restaurante</div>
                    </div>
                  </button>
                </div>
              )}

              {/* Area restriction for Líder */}
              {mgrForm.profile === "lider" && (
                <div>
                  <label style={S.label}>Áreas do líder</label>
                  <p style={{color:"var(--text3)",fontSize:11,margin:"0 0 8px"}}>O líder só verá empregados e escalas das áreas selecionadas.</p>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {AREAS.map(a=>{
                      const on = (mgrForm.areas??[]).includes(a);
                      return <button key={a} onClick={()=>setMgrForm(f=>({...f,areas:on?f.areas.filter(x=>x!==a):[...(f.areas??[]),a]}))}
                        style={{padding:"8px 16px",borderRadius:20,border:`1px solid ${on?AREA_COLORS[a]??"#555":"var(--border)"}`,background:on?(AREA_COLORS[a]??"#555")+"22":"transparent",color:on?AREA_COLORS[a]??"var(--text)":"var(--text3)",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12,fontWeight:on?700:400}}>
                        {on?"✓ ":""}{a}
                      </button>;
                    })}
                  </div>
                  {(mgrForm.areas??[]).length===0 && <p style={{color:"var(--red)",fontSize:11,marginTop:6}}>Selecione pelo menos uma área.</p>}
                </div>
              )}

              {/* Custom permissions */}
              {mgrForm.profile === "custom" && (
                <div>
                  <label style={S.label}>Permissões</label>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                    {[["tips","💸 Gorjetas"],["schedule","📅 Escala"],["roles","🏷️ Cargos"],["employees","👥 Equipe"],["comunicados","📢 Comunicados"],["faq","❓ FAQ"],["dp","💬 Fale c/ DP"],["horarios","🕐 Horários"],["vt","🚌 Vale Transporte"]].map(([k,lbl])=>{
                      const on = mgrForm.perms?.[k] !== false;
                      return (
                        <button key={k} onClick={()=>setMgrForm({...mgrForm,perms:{...mgrForm.perms,[k]:!on}})}
                          style={{padding:"10px",borderRadius:10,border:`1px solid ${on?"var(--green)":"var(--border)"}`,background:on?"var(--green-bg)":"transparent",color:on?"var(--green)":"var(--text3)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:12,textAlign:"left"}}>
                          {on?"✓":"○"} {lbl}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {mgrForm.profile === "custom" && (
                <div style={{borderTop:"1px solid var(--border)",paddingTop:12}}>
                  <button onClick={()=>setMgrForm({...mgrForm,isDP:!mgrForm.isDP})}
                    style={{width:"100%",padding:"12px 14px",borderRadius:10,border:`1px solid ${mgrForm.isDP?"var(--blue)":"var(--border)"}`,background:mgrForm.isDP?"var(--blue-bg)":"transparent",color:mgrForm.isDP?"var(--blue)":"var(--text3)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,textAlign:"left",display:"flex",alignItems:"center",gap:10}}>
                    <span style={{fontSize:18}}>📬</span>
                    <div>
                      <div style={{fontWeight:700}}>{mgrForm.isDP?"✓ Recebe mensagens do Fale com DP":"○ Não recebe mensagens do Fale com DP"}</div>
                      <div style={{fontSize:11,opacity:0.7,marginTop:2}}>Recebe notificações de horários e mensagens do Fale com DP</div>
                    </div>
                  </button>
                </div>
              )}

              <div>
                <label style={S.label}>Outros restaurantes com acesso</label>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {restaurants.filter(r=>r.id!==selRestaurant).map(r=>{
                    const sel = mgrForm.restaurantIds?.includes(r.id);
                    return (
                      <button key={r.id} onClick={()=>setMgrForm({...mgrForm,restaurantIds:sel?mgrForm.restaurantIds.filter(x=>x!==r.id):[...(mgrForm.restaurantIds??[]),r.id]})}
                        style={{padding:"10px 14px",borderRadius:10,border:`1px solid ${sel?ac:"var(--border)"}`,background:sel?"var(--ac-bg)":"transparent",color:sel?"var(--ac-text)":"var(--text3)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,textAlign:"left"}}>
                        {sel?"✓":"○"} {r.name}
                      </button>
                    );
                  })}
                  {restaurants.filter(r=>r.id!==selRestaurant).length===0 && <p style={{color:"var(--text3)",fontSize:12}}>Nenhum outro restaurante cadastrado.</p>}
                </div>
              </div>
              <button onClick={saveMgr} style={S.btnPrimary}>{editMgrId?"Salvar":"Criar Gestor"}</button>
            </div>
          </Modal>
        )}
      </div>
    );
  }

  return (
    <div style={{ minHeight:"100vh", background:"var(--bg)", fontFamily:"'DM Sans',sans-serif" }}>
      <div style={{ background:"var(--header-bg)", borderBottom:"1px solid var(--border)", padding:isMobile?"10px 12px":"14px 20px", display:"flex", justifyContent:"space-between", alignItems:"center", boxShadow:"0 1px 4px rgba(0,0,0,0.04)", gap:8 }}>
        <div style={{ display:"flex", alignItems:"center", gap:isMobile?6:8, minWidth:0 }}>
          <span style={{ fontSize:isMobile?14:18 }}>⭐</span>
          <span style={{ color:"var(--text)", fontWeight:800, fontSize:isMobile?13:16 }}>{isMobile?"Gestor":"Master"}</span>
          {!isMobile && <span style={{ color:"var(--text3)", fontSize:12 }}>· {currentUser?.name}</span>}
        </div>
        <div style={{display:"flex",gap:isMobile?4:6,alignItems:"center",flexShrink:0}}>
          <button onClick={()=>setViewOnly(!viewOnly)} title={viewOnly?"Modo somente leitura ativo — clique para desbloquear":"Clique para ativar modo somente leitura"}
            style={{background:viewOnly?"var(--ac)22":"none",border:`1px solid ${viewOnly?"var(--ac)":"var(--border)"}`,borderRadius:20,padding:isMobile?"5px 8px":"6px 10px",cursor:"pointer",fontSize:isMobile?12:14,color:viewOnly?"var(--ac)":"var(--text3)"}}>
            {viewOnly?"🔒":"🔓"}
          </button>
          <button onClick={toggleTheme} style={{background:"none",border:"1px solid var(--border)",borderRadius:20,padding:isMobile?"5px 8px":"6px 10px",cursor:"pointer",fontSize:isMobile?12:14,color:"var(--text2)"}}>{theme==="dark"?"☀️":"🌙"}</button>
          <button onClick={onBack} style={{ ...S.btnSecondary, fontSize:isMobile?11:12, padding:isMobile?"5px 10px":undefined }}>Sair</button>
        </div>
      </div>
      {viewOnly && <div style={{background:"var(--ac)11",borderBottom:"1px solid var(--ac)33",padding:isMobile?"5px 12px":"6px 20px",textAlign:"center",fontSize:isMobile?11:12,color:"var(--ac)",fontWeight:600}}>🔒 Modo somente leitura — edições bloqueadas</div>}
      <div style={{ display:"flex", borderBottom:"1px solid var(--border)", background:"var(--header-bg)", overflowX:"auto", WebkitOverflowScrolling:"touch" }}>
        {TABS.map(([id,lbl])=>(
          <button key={id} onClick={()=>setTab(id)} style={{ padding:isMobile?"10px 12px":"12px 20px", background:"none", border:"none", borderBottom:`2px solid ${tab===id?ac:"transparent"}`, color:tab===id?ac:"var(--text3)", cursor:"pointer", fontSize:isMobile?11:14, fontFamily:"'DM Sans',sans-serif", fontWeight:tab===id?700:500, whiteSpace:"nowrap" }}>{lbl}</button>
        ))}
      </div>

      <div style={{ padding:isMobile?"12px 10px":"20px 24px", maxWidth:1100, margin:"0 auto" }}>

        {/* DASHBOARD (removido — info consolidada no Financeiro) */}
        {false && (() => {
          const totalRests = restaurants.length;
          const totalEmps = employees.filter(e=>!e.inactive).length;
          const totalMgrs = managers.length;
          const receitaMensal = restaurants.reduce((sum, r) => {
            const p = getPlano(r);
            const ea = r.earlyAdopter ? 0.7 : 1;
            if (r.planoId === "pIsento") return sum;
            if (r.planoId === "p999") return sum + ((r.empMaxCustom ?? 51) * 7.99 * ea);
            if (r.planoId === "pOrc") return sum;
            return sum + ((p.mensal ?? 0) * ea);
          }, 0);

          const today_ = today();
          const thisMonth = today_.slice(0,7);

          return (
            <div>
              {/* Métricas */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:16,marginBottom:28}}>
                {[
                  { label:"Restaurantes", value:totalRests, icon:"🏢", color:"var(--blue)" },
                  { label:"Empregados ativos", value:totalEmps, icon:"👥", color:"var(--green)" },
                  { label:"Gestores", value:totalMgrs, icon:"👔", color:"#8b5cf6" },
                  { label:"Receita mensal est.", value:`R$${receitaMensal.toLocaleString("pt-BR")}`, icon:"💰", color:"var(--ac)" },
                ].map(m=>(
                  <div key={m.label} style={{...S.card,display:"flex",alignItems:"center",gap:14}}>
                    <div style={{fontSize:28,width:48,height:48,borderRadius:12,background:m.color+"15",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{m.icon}</div>
                    <div>
                      <div style={{color:"var(--text3)",fontSize:11,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5,marginBottom:2}}>{m.label}</div>
                      <div style={{color:"var(--text)",fontSize:22,fontWeight:800,fontFamily:"'DM Mono',monospace"}}>{m.value}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Status dos restaurantes */}
              <div style={{...S.card,marginBottom:20}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                  <h3 style={{color:"var(--text)",fontSize:15,fontWeight:700,margin:0}}>Status dos clientes</h3>
                  <span style={{color:"var(--text3)",fontSize:12}}>{thisMonth}</span>
                </div>
                {restaurants.length === 0 && <p style={{color:"var(--text3)",fontSize:13,textAlign:"center"}}>Nenhum restaurante cadastrado.</p>}
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {restaurants.map(r => {
                    const plano = getPlano(r);
                    const empAtivos = employees.filter(e=>e.restaurantId===r.id&&!e.inactive).length;
                    const pct = Math.min(100, Math.round((empAtivos/plano.empMax)*100));
                    const temGorjetaMes = tips.some(t=>t.restaurantId===r.id&&t.monthKey===thisMonth);
                    const ultimaGorjeta = tips.filter(t=>t.restaurantId===r.id).sort((a,b)=>b.date?.localeCompare(a.date??"")??"").at(0);
                    const diasSemGorjeta = ultimaGorjeta?.date
                      ? Math.floor((new Date()-new Date(ultimaGorjeta.date+"T12:00:00"))/(1000*60*60*24))
                      : 999;

                    // Semáforo
                    let semaforo = "verde";
                    let semaforoMsg = "Ativo";
                    const finStatus = r.financeiro?.status ?? "ativo";
                    const cicloFimR = r.financeiro?.cicloFim;
                    const trialFimR = r.financeiro?.trialFim;
                    const cicloInicioR = r.financeiro?.cicloInicio;
                    const diasCiclo = cicloFimR ? Math.ceil((new Date(cicloFimR+"T12:00:00")-new Date())/(1000*60*60*24)) : null;
                    const diasTrial = (!cicloInicioR && trialFimR) ? Math.ceil((new Date(trialFimR+"T12:00:00")-new Date())/(1000*60*60*24)) : null;
                    if (finStatus === "inadimplente")              { semaforo = "vermelho"; semaforoMsg = "🔴 Inadimplente"; }
                    else if (!cicloInicioR && !trialFimR)          { semaforo = "amarelo";  semaforoMsg = "⚙️ Sem ciclo iniciado"; }
                    else if (!cicloInicioR && diasTrial !== null && diasTrial <= 0) { semaforo = "vermelho"; semaforoMsg = "⏰ Trial encerrado"; }
                    else if (!cicloInicioR && diasTrial !== null)  { semaforo = "amarelo";  semaforoMsg = `🎯 Trial — ${diasTrial}d restantes`; }
                    else if (diasCiclo !== null && diasCiclo < 0)  { semaforo = "vermelho"; semaforoMsg = `⏰ Vencido há ${Math.abs(diasCiclo)}d`; }
                    else if (diasCiclo !== null && diasCiclo <= 7) { semaforo = "amarelo";  semaforoMsg = `⚡ Vence em ${diasCiclo}d`; }
                    else if (pct >= 100)                           { semaforo = "vermelho"; semaforoMsg = "Limite atingido"; }
                    else if (!temGorjetaMes)                       { semaforo = "amarelo";  semaforoMsg = "Sem gorjeta este mês"; }
                    else if (pct >= 80)                            { semaforo = "amarelo";  semaforoMsg = "Próximo do limite"; }

                    const semColor = semaforo === "verde" ? "var(--green)" : semaforo === "amarelo" ? "#f59e0b" : "var(--red)";

                    return (
                      <div key={r.id} style={{display:"flex",alignItems:"center",gap:14,padding:"12px 16px",borderRadius:12,background:"var(--bg2)",border:"1px solid var(--border)",cursor:"pointer"}}
                        onClick={()=>setSelRestaurant(r.id)}>
                        {/* Semáforo */}
                        <div style={{width:10,height:10,borderRadius:"50%",background:semColor,flexShrink:0,boxShadow:`0 0 6px ${semColor}`}}/>
                        {/* Info */}
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                            <span style={{color:"var(--text)",fontWeight:700,fontSize:14}}>{r.name}</span>
                            <span style={{background:plano.isento?"#10b98115":"var(--ac-bg)",color:plano.isento?"#10b981":"var(--ac-text)",borderRadius:6,padding:"1px 8px",fontSize:11,fontWeight:700}}>{plano.isento?"🤝 ":""}{plano.label}</span>
                          </div>
                          <div style={{display:"flex",gap:12,fontSize:12,color:"var(--text3)"}}>
                            <span>{empAtivos}/{plano.empMax} emp.</span>
                            <span style={{color:semColor,fontWeight:600}}>{semaforoMsg}</span>
                            {ultimaGorjeta && <span>Última gorjeta: {diasSemGorjeta}d atrás</span>}
                          </div>
                        </div>
                        {/* Barra de uso */}
                        <div style={{width:80,flexShrink:0}}>
                          <div style={{background:"var(--border)",borderRadius:4,height:6,overflow:"hidden"}}>
                            <div style={{width:`${pct}%`,height:"100%",background:semColor,borderRadius:4,transition:"width 0.3s"}}/>
                          </div>
                          <div style={{textAlign:"right",fontSize:10,color:"var(--text3)",marginTop:2}}>{pct}%</div>
                        </div>
                        <span style={{color:"var(--text3)",fontSize:16}}>›</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Alertas de pagamentos aguardando confirmação */}
              {(() => {
                const aguardando = restaurants.flatMap(r =>
                  (r.financeiro?.cobrancas??[])
                    .filter(c => c.status === "aguardando_confirmacao")
                    .map(c => ({ ...c, restName: r.name, restId: r.id }))
                );
                if (aguardando.length === 0) return null;
                return (
                  <div style={{...S.card,marginBottom:16,border:"1px solid #f59e0b44",background:"#fffbeb"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                      <span style={{color:"#92400e",fontWeight:700,fontSize:14}}>💬 {aguardando.length} pagamento{aguardando.length>1?"s":""} aguardando confirmação</span>
                    </div>
                    {aguardando.map(c=>(
                      <div key={c.id} style={{display:"flex",flexDirection:isMobile?"column":"row",justifyContent:"space-between",alignItems:isMobile?"stretch":"center",gap:isMobile?8:0,padding:isMobile?"10px":"10px 12px",borderRadius:10,background:"#fff",border:"1px solid #fde68a",marginBottom:8}}>
                        <div>
                          <div style={{color:"var(--text)",fontWeight:700,fontSize:13}}>{c.restName}</div>
                          <div style={{color:"var(--text3)",fontSize:12}}>
                            {c.periodoLabel} · R$ {c.valor?.toLocaleString("pt-BR",{minimumFractionDigits:2})} · Cliente confirmou em {c.clienteConfirmouEm ? new Date(c.clienteConfirmouEm).toLocaleDateString("pt-BR") : "—"}
                          </div>
                        </div>
                        <div style={{display:"flex",gap:6,flexShrink:0}}>
                          <button onClick={()=>{
                            // Confirma — chama confirmarPagamento indiretamente via update
                            const r = restaurants.find(x=>x.id===c.restId);
                            if (!r) return;
                            const dataPag = c.clienteConfirmouEm?.slice(0,10) ?? today();
                            const cicloEnd = (() => {
                              const d = new Date(dataPag+"T12:00:00");
                              if ((r.tipoCobranca??"mensal") === "anual") d.setFullYear(d.getFullYear()+1);
                              else d.setDate(d.getDate()+30);
                              return d.toISOString().slice(0,10);
                            })();
                            const [ano,mes] = cicloEnd.split("-");
                            const mesesNome = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
                            const proxLabel = `${mesesNome[parseInt(mes)-1]}/${ano}`;
                            const proxCob = { id:(Date.now()).toString(), periodo:cicloEnd.slice(0,7), periodoLabel:proxLabel, venc:cicloEnd, valor:c.valor, forma:c.forma, chave:c.chave, criadaEm:new Date().toISOString(), status:"pendente", autoGerada:true };
                            const updatedCobs = (r.financeiro?.cobrancas??[]).map(x=>x.id===c.id?{...x,status:"pago",pagoEm:new Date().toISOString()}:x);
                            const novoPag = { id:(Date.now()+1).toString(), data:dataPag, valor:c.valor, forma:c.forma, obs:`Ref. ${c.periodoLabel}`, registradoEm:new Date().toISOString() };
                            const updated = restaurants.map(x=>x.id===c.restId?{...x,financeiro:{...x.financeiro,cobrancas:[...updatedCobs,proxCob],pagamentos:[novoPag,...(x.financeiro?.pagamentos??[])],status:"ativo",cicloInicio:dataPag,cicloFim:cicloEnd,proximoVencimento:cicloEnd}}:x);
                            onUpdate("restaurants",updated);
                            onUpdate("_toast",`✅ Pagamento de ${c.restName} confirmado!`);
                          }} style={{padding:"6px 12px",borderRadius:8,border:"1px solid var(--green)44",background:"var(--green-bg)",color:"var(--green)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:12,fontWeight:700}}>
                            ✅ Confirmar
                          </button>
                          <button onClick={()=>{
                            const updated = restaurants.map(r=>r.id===c.restId?{...r,financeiro:{...r.financeiro,cobrancas:(r.financeiro?.cobrancas??[]).map(x=>x.id===c.id?{...x,status:"pendente",clienteConfirmou:false}:x),status:"inadimplente"}}:r);
                            onUpdate("restaurants",updated);
                            onUpdate("_toast",`🔴 ${c.restName} marcado como inadimplente.`);
                          }} style={{padding:"6px 12px",borderRadius:8,border:"1px solid var(--red)33",background:"transparent",color:"var(--red)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:12,fontWeight:600}}>
                            ✕ Negar
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Notificações recentes */}
              {unreadNotifs > 0 && (
                <div style={{...S.card,border:"1px solid var(--ac)33",background:"var(--ac-bg)"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                    <span style={{color:"var(--ac-text)",fontWeight:700,fontSize:14}}>📬 {unreadNotifs} mensagem{unreadNotifs>1?"ns":""} não lida{unreadNotifs>1?"s":""}</span>
                    <button onClick={()=>setTab("inbox")} style={{...S.btnSecondary,fontSize:12,padding:"4px 12px"}}>Ver caixa →</button>
                  </div>
                  {notifications.filter(n=>!n.read&&n.targetRole==="admin").slice(0,3).map(n=>(
                    <div key={n.id} style={{padding:"8px 0",borderBottom:"1px solid var(--border)",fontSize:13,color:"var(--text2)"}}>
                      {n.body}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* CAIXA — INBOX UNIFICADO */}
        {tab === "inbox" && (() => {
          const uid = currentUser?.id;
          const myFolders = (data?.inboxFolders ?? {})[uid] ?? [];
          const FOLDER_COLORS = ["#3498db","#e74c3c","#2ecc71","#f39c12","#9b59b6","#1abc9c","#e67e22","#34495e"];

          // Legado: notificações do sistema antigo
          const adminNotifs = [...notifications].filter(n => n.targetRole === "admin" || n.type === "upgrade_request")
            .sort((a,b) => b.date?.localeCompare(a.date??""))
            .map(n => ({ ...n, _source: "legacy", _sortDate: n.date || n.criadoEm || "" }));
          // Novo inbox filtrado por pasta
          const myInbox = getInboxForUser({ inbox: data?.inbox, userId: uid, userRole: "owner", folder: inboxFolder })
            .map(m => ({ ...m, _source: "inbox", _sortDate: m.criadoEm || "" }));
          // Merge (legado só aparece na Entrada)
          const showLegacy = inboxFolder === null;
          const allMessages = showLegacy ? [...adminNotifs, ...myInbox].sort((a,b) => b._sortDate.localeCompare(a._sortDate)) : myInbox;
          // Filtro de tipo
          const filtered = inboxFilter === "all" ? allMessages
            : inboxFilter === "unread" ? allMessages.filter(m => m._source === "legacy" ? !m.read : !m.lido)
            : allMessages.filter(m => m.tipo === inboxFilter || (inboxFilter === "sistema" && m._source === "legacy"));
          // Contadores
          const entradaUnread = getInboxForUser({ inbox: data?.inbox, userId: uid, userRole: "owner", folder: null, includeRead: false }).length
            + notifications.filter(n => (n.targetRole==="admin"||n.type==="upgrade_request") && !n.read).length;
          const trashCount = getInboxForUser({ inbox: data?.inbox, userId: uid, userRole: "owner", folder: "__trash" }).length;
          const folderUnread = (fid) => getInboxForUser({ inbox: data?.inbox, userId: uid, userRole: "owner", folder: fid, includeRead: false }).length;
          const currentUnread = allMessages.filter(m => m._source === "legacy" ? !m.read : !m.lido).length;

          function markAllRead() {
            if (showLegacy) {
              onUpdate("notifications", notifications.map(n => n.targetRole==="admin"||n.type==="upgrade_request" ? {...n,read:true} : n));
            }
            const updInbox = (data?.inbox ?? []).map(m => {
              const isForMe = myInbox.some(x => x.id === m.id);
              return isForMe ? { ...m, lido: true } : m;
            });
            onUpdate("inbox", updInbox);
          }
          function markRead(msg) {
            if (msg._source === "legacy") {
              onUpdate("notifications", notifications.map(x => x.id===msg.id ? {...x,read:true} : x));
            } else {
              onUpdate("inbox", (data?.inbox ?? []).map(x => x.id===msg.id ? {...x, lido:true} : x));
            }
          }
          function softDelete(msg) {
            if (msg._source === "legacy") {
              onUpdate("notifications", notifications.filter(x => x.id!==msg.id));
            } else {
              onUpdate("inbox", (data?.inbox ?? []).map(x => x.id===msg.id ? {...x, deletedAt: new Date().toISOString(), folder: null} : x));
            }
            onUpdate("_toast","🗑️ Movido para Excluídas");
          }
          function restoreMsg(msg) {
            onUpdate("inbox", (data?.inbox ?? []).map(x => x.id===msg.id ? {...x, deletedAt: null} : x));
            onUpdate("_toast","📥 Restaurado para Entrada");
          }
          async function permDelete(msg) {
            if (!await appConfirm("Excluir definitivamente? Esta ação não pode ser desfeita.")) return;
            onUpdate("inbox", (data?.inbox ?? []).filter(x => x.id!==msg.id));
            onUpdate("_toast","🗑️ Excluído definitivamente");
          }
          function moveToFolder(msgId, folderId) {
            onUpdate("inbox", (data?.inbox ?? []).map(x => x.id===msgId ? {...x, folder: folderId, deletedAt: null} : x));
            setMoveModal(null);
            onUpdate("_toast", folderId ? `📁 Movido para ${myFolders.find(f=>f.id===folderId)?.nome || "pasta"}` : "📥 Movido para Entrada");
          }
          function sendMessage() {
            if (!composeData.assunto.trim() || !composeData.corpo.trim()) return;
            const msg = createInboxMessage({
              tipo: "comunicado",
              de: { id: uid, nome: currentUser?.name ?? "Master", role: "owner" },
              para: composeData.restaurantId ? { role: "manager", restaurantId: composeData.restaurantId } : { role: "manager" },
              restaurantId: composeData.restaurantId || null,
              assunto: composeData.assunto.trim(),
              corpo: composeData.corpo.trim(),
            });
            onUpdate("inbox", [...(data?.inbox ?? []), msg]);
            setComposeOpen(false);
            setComposeData({ restaurantId: "", assunto: "", corpo: "" });
            onUpdate("_toast", "📨 Mensagem enviada!");
          }
          // CRUD pastas
          function saveFolder() {
            if (!folderForm.nome.trim()) return;
            const allFolders = { ...(data?.inboxFolders ?? {}) };
            const mine = [...(allFolders[uid] ?? [])];
            if (folderModal === "new") {
              mine.push({ id: Date.now().toString(36), nome: folderForm.nome.trim(), cor: folderForm.cor, ordem: mine.length });
            } else if (folderModal?.id) {
              const idx = mine.findIndex(f => f.id === folderModal.id);
              if (idx >= 0) mine[idx] = { ...mine[idx], nome: folderForm.nome.trim(), cor: folderForm.cor };
            }
            allFolders[uid] = mine;
            onUpdate("inboxFolders", allFolders);
            setFolderModal(null);
            onUpdate("_toast", "📁 Pasta salva");
          }
          async function deleteFolder(fid) {
            if (!await appConfirm("Excluir esta pasta? As mensagens serão movidas para Entrada.")) return;
            // Move messages back to Entrada
            onUpdate("inbox", (data?.inbox ?? []).map(x => x.folder === fid ? {...x, folder: null} : x));
            const allFolders = { ...(data?.inboxFolders ?? {}) };
            allFolders[uid] = (allFolders[uid] ?? []).filter(f => f.id !== fid);
            onUpdate("inboxFolders", allFolders);
            if (inboxFolder === fid) setInboxFolder(null);
            onUpdate("_toast", "📁 Pasta excluída");
          }

          const isTrash = inboxFolder === "__trash";
          const FILTER_OPTS = [
            ["all", "Todas"],
            ["unread", `Não lidas${currentUnread>0?` (${currentUnread})`:""}`],
            ["reuniao", "📅 Reuniões"],
            ["acao_reuniao", "📋 Ações"],
            ["solicitacao", "📩 Solicitações"],
            ["sistema", "⚙️ Sistema"],
          ];
          const TIPO_ICON = { reuniao: "📅", acao_reuniao: "📋", solicitacao: "📩", comunicado: "📨", sistema: "⚙️" };
          const TIPO_LABEL = { reuniao: "Reunião", acao_reuniao: "Ação de reunião", solicitacao: "Solicitação", comunicado: "Comunicado", sistema: "Sistema" };
          const folderTabStyle = (active) => ({padding:"6px 14px",borderRadius:20,border:`1px solid ${active?ac+"66":"var(--border)"}`,background:active?ac+"15":"transparent",color:active?"var(--ac-text)":"var(--text3)",cursor:"pointer",fontSize:12,fontFamily:"'DM Sans',sans-serif",fontWeight:active?700:500,display:"flex",alignItems:"center",gap:4});

          return (
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:8}}>
                <h3 style={{color:"var(--text)",fontSize:16,fontWeight:700,margin:0}}>📬 Caixa de entrada</h3>
                <div style={{display:"flex",gap:8}}>
                  {currentUnread > 0 && <button onClick={markAllRead} style={{...S.btnSecondary,fontSize:12,padding:"6px 14px"}}>Marcar todas como lidas</button>}
                  <button onClick={()=>setComposeOpen(true)} style={{...S.btnPrimary,fontSize:12,padding:"6px 14px"}}>+ Nova mensagem</button>
                </div>
              </div>

              {/* Abas de pastas */}
              <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap",alignItems:"center"}}>
                <button onClick={()=>{setInboxFolder(null);setInboxFilter("all");}} style={folderTabStyle(inboxFolder===null)}>
                  📥 Entrada{entradaUnread>0?` (${entradaUnread})`:""}
                </button>
                {myFolders.map(f => {
                  const fu = folderUnread(f.id);
                  return (
                    <button key={f.id} onClick={()=>{setInboxFolder(f.id);setInboxFilter("all");}} style={{...folderTabStyle(inboxFolder===f.id),borderColor:inboxFolder===f.id?(f.cor||ac)+"66":"var(--border)",background:inboxFolder===f.id?(f.cor||ac)+"15":"transparent",color:inboxFolder===f.id?f.cor||"var(--ac-text)":"var(--text3)"}}>
                      <span style={{width:8,height:8,borderRadius:"50%",background:f.cor||ac,display:"inline-block"}}></span>
                      {f.nome}{fu>0?` (${fu})`:""}
                    </button>
                  );
                })}
                <button onClick={()=>{setInboxFolder("__trash");setInboxFilter("all");}} style={folderTabStyle(isTrash)}>
                  🗑️ Excluídas{trashCount>0?` (${trashCount})`:""}
                </button>
                <button onClick={()=>{setFolderModal("new");setFolderForm({nome:"",cor:"#3498db"});}} style={{background:"none",border:"1px dashed var(--border)",borderRadius:20,padding:"6px 12px",color:"var(--text3)",cursor:"pointer",fontSize:12,fontFamily:"'DM Sans',sans-serif"}} title="Nova pasta">+ Pasta</button>
              </div>

              {/* Filtros de tipo (não aparece na lixeira) */}
              {!isTrash && (
                <div style={{display:"flex",gap:6,marginBottom:16,flexWrap:"wrap"}}>
                  {FILTER_OPTS.map(([id,lbl])=>(
                    <button key={id} onClick={()=>setInboxFilter(id)} style={{padding:"4px 10px",borderRadius:16,border:`1px solid ${inboxFilter===id?ac+"44":"var(--border)"}`,background:inboxFilter===id?ac+"10":"transparent",color:inboxFilter===id?"var(--ac-text)":"var(--text3)",cursor:"pointer",fontSize:11,fontFamily:"'DM Sans',sans-serif",fontWeight:inboxFilter===id?600:400}}>
                      {lbl}
                    </button>
                  ))}
                </div>
              )}

              {/* Botão editar/excluir pasta custom atual */}
              {inboxFolder && inboxFolder !== "__trash" && (() => {
                const cf = myFolders.find(f=>f.id===inboxFolder);
                return cf ? (
                  <div style={{display:"flex",gap:8,marginBottom:12}}>
                    <button onClick={()=>{setFolderModal(cf);setFolderForm({nome:cf.nome,cor:cf.cor||"#3498db"});}} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px"}}>Renomear pasta</button>
                    <button onClick={()=>deleteFolder(cf.id)} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px",color:"#e74c3c",borderColor:"#e74c3c44"}}>Excluir pasta</button>
                  </div>
                ) : null;
              })()}

              {filtered.length === 0 && (
                <div style={{...S.card,textAlign:"center",padding:40}}>
                  <div style={{fontSize:36,marginBottom:12}}>{isTrash?"🗑️":"📭"}</div>
                  <p style={{color:"var(--text3)",fontSize:14}}>{isTrash?"Nenhuma mensagem excluída.":"Nenhuma mensagem"+(inboxFilter!=="all"?" neste filtro":"")+"."}</p>
                  {isTrash && <p style={{color:"var(--text3)",fontSize:12,marginTop:4}}>Mensagens excluídas são removidas automaticamente após 90 dias.</p>}
                </div>
              )}

              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {filtered.map(msg => {
                  const isLegacy = msg._source === "legacy";
                  const isRead = isLegacy ? msg.read : msg.lido;
                  const isUpgrade = isLegacy && msg.type === "upgrade_request";
                  const rest = restaurants.find(r=>r.id===msg.restaurantId);
                  const icon = isLegacy ? (isUpgrade ? "📦" : "💬") : (TIPO_ICON[msg.tipo] ?? "💬");
                  const label = isLegacy ? (isUpgrade ? "Solicitação de upgrade" : "Notificação do sistema") : (TIPO_LABEL[msg.tipo] ?? "Mensagem");
                  const body = isLegacy ? msg.body : msg.corpo;
                  const subject = isLegacy ? null : msg.assunto;
                  const dateStr = isLegacy ? msg.date : msg.criadoEm;
                  const from = isLegacy ? null : msg.de;
                  const msgFolder = !isLegacy && msg.folder ? myFolders.find(f=>f.id===msg.folder) : null;

                  return (
                    <div key={msg.id} style={{...S.card,border:`1px solid ${isRead?"var(--border)":isUpgrade?"var(--ac)44":"var(--blue)33"}`,background:isRead?"var(--card-bg)":isUpgrade?"var(--ac-bg)":"var(--blue-bg)"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
                        <div style={{flex:1}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                            <span style={{fontSize:16}}>{icon}</span>
                            <span style={{color:"var(--text)",fontWeight:700,fontSize:14}}>{subject || label}</span>
                            {!isRead && <span style={{background:isUpgrade?ac:"var(--blue)",color:"#fff",borderRadius:20,padding:"1px 8px",fontSize:10,fontWeight:700}}>Novo</span>}
                            {rest && <span style={{color:"var(--text3)",fontSize:12}}>· {rest.name}</span>}
                            {msgFolder && <span style={{color:msgFolder.cor,fontSize:10,fontWeight:600,border:`1px solid ${msgFolder.cor}44`,borderRadius:10,padding:"1px 6px"}}>📁 {msgFolder.nome}</span>}
                          </div>
                          {from && <div style={{color:"var(--text3)",fontSize:11,marginBottom:4}}>De: {from.nome || from.role}</div>}
                          <p style={{color:"var(--text2)",fontSize:13,margin:"0 0 8px",lineHeight:1.5,whiteSpace:"pre-line"}}>{body}</p>
                          <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                            <span style={{color:"var(--text3)",fontSize:11}}>{dateStr ? fmtRelTime(dateStr) : ""}</span>
                            {isTrash && msg.deletedAt && <span style={{color:"var(--text3)",fontSize:10}}>Excluído {fmtRelTime(msg.deletedAt)}</span>}
                            {isUpgrade && <button onClick={()=>setSelRestaurant(msg.restaurantId)} style={{padding:"4px 12px",borderRadius:8,border:`1px solid ${ac}`,background:"transparent",color:"var(--ac-text)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:12,fontWeight:600}}>Abrir restaurante →</button>}
                          </div>
                        </div>
                        <div style={{display:"flex",flexDirection:"column",gap:4,flexShrink:0,alignItems:"flex-end"}}>
                          {!isRead && !isTrash && <button onClick={()=>markRead(msg)} style={{background:"none",border:"none",color:"var(--text3)",cursor:"pointer",fontSize:18,padding:4}} title="Marcar como lida">✓</button>}
                          {!isLegacy && !isTrash && <button onClick={()=>setMoveModal(msg.id)} style={{background:"none",border:"1px solid var(--border)",borderRadius:6,color:"var(--text3)",cursor:"pointer",fontSize:11,padding:"3px 8px",fontFamily:"'DM Mono',monospace"}} title="Mover para pasta">📁</button>}
                          {isTrash ? (
                            <>
                              <button onClick={()=>restoreMsg(msg)} style={{background:"none",border:"1px solid var(--border)",borderRadius:6,color:"var(--text3)",cursor:"pointer",fontSize:11,padding:"3px 8px",fontFamily:"'DM Mono',monospace"}} title="Restaurar">📥</button>
                              <button onClick={()=>permDelete(msg)} style={{background:"none",border:"1px solid #e74c3c22",borderRadius:6,color:"#e74c3c",cursor:"pointer",fontSize:11,padding:"3px 8px",fontFamily:"'DM Mono',monospace"}} title="Excluir definitivamente">❌</button>
                            </>
                          ) : (
                            <button onClick={()=>softDelete(msg)} style={{background:"none",border:"1px solid #e74c3c22",borderRadius:6,color:"var(--text3)",cursor:"pointer",fontSize:11,padding:"3px 8px",fontFamily:"'DM Mono',monospace"}} title="Excluir">🗑️</button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Modal mover para pasta */}
              {moveModal && (
                <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999,padding:16}} onClick={()=>setMoveModal(null)}>
                  <div style={{background:"var(--card-bg)",borderRadius:16,padding:24,maxWidth:360,width:"100%",border:"1px solid var(--border)"}} onClick={e=>e.stopPropagation()}>
                    <h3 style={{color:"var(--text)",fontSize:15,fontWeight:700,margin:"0 0 16px"}}>📁 Mover para pasta</h3>
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      <button onClick={()=>moveToFolder(moveModal, null)} style={{...S.btnSecondary,fontSize:13,textAlign:"left"}}>📥 Entrada</button>
                      {myFolders.map(f => (
                        <button key={f.id} onClick={()=>moveToFolder(moveModal, f.id)} style={{...S.btnSecondary,fontSize:13,textAlign:"left",display:"flex",alignItems:"center",gap:8}}>
                          <span style={{width:10,height:10,borderRadius:"50%",background:f.cor||ac}}></span> {f.nome}
                        </button>
                      ))}
                    </div>
                    <div style={{marginTop:12,textAlign:"right"}}>
                      <button onClick={()=>setMoveModal(null)} style={{...S.btnSecondary,fontSize:12}}>Cancelar</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Modal criar/editar pasta */}
              {folderModal && (
                <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999,padding:16}} onClick={()=>setFolderModal(null)}>
                  <div style={{background:"var(--card-bg)",borderRadius:16,padding:24,maxWidth:400,width:"100%",border:"1px solid var(--border)"}} onClick={e=>e.stopPropagation()}>
                    <h3 style={{color:"var(--text)",fontSize:15,fontWeight:700,margin:"0 0 16px"}}>{folderModal==="new"?"📁 Nova pasta":"📁 Editar pasta"}</h3>
                    <div style={{marginBottom:12}}>
                      <label style={{color:"var(--text3)",fontSize:12,fontWeight:600,display:"block",marginBottom:4}}>Nome</label>
                      <input value={folderForm.nome} onChange={e=>setFolderForm(p=>({...p,nome:e.target.value}))} style={{...S.input,width:"100%"}} placeholder="Nome da pasta" maxLength={30}/>
                    </div>
                    <div style={{marginBottom:16}}>
                      <label style={{color:"var(--text3)",fontSize:12,fontWeight:600,display:"block",marginBottom:4}}>Cor</label>
                      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                        {FOLDER_COLORS.map(c => (
                          <button key={c} onClick={()=>setFolderForm(p=>({...p,cor:c}))} style={{width:28,height:28,borderRadius:"50%",background:c,border:folderForm.cor===c?"3px solid var(--text)":"2px solid transparent",cursor:"pointer"}}/>
                        ))}
                      </div>
                    </div>
                    <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
                      <button onClick={()=>setFolderModal(null)} style={{...S.btnSecondary,fontSize:13}}>Cancelar</button>
                      <button onClick={saveFolder} disabled={!folderForm.nome.trim()} style={{...S.btnPrimary,fontSize:13,opacity:!folderForm.nome.trim()?0.5:1}}>Salvar</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Modal compor mensagem */}
              {composeOpen && (
                <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999,padding:16}} onClick={()=>setComposeOpen(false)}>
                  <div style={{background:"var(--card-bg)",borderRadius:16,padding:24,maxWidth:500,width:"100%",border:"1px solid var(--border)"}} onClick={e=>e.stopPropagation()}>
                    <h3 style={{color:"var(--text)",fontSize:16,fontWeight:700,margin:"0 0 16px"}}>📨 Nova mensagem</h3>
                    <div style={{marginBottom:12}}>
                      <label style={{color:"var(--text3)",fontSize:12,fontWeight:600,display:"block",marginBottom:4}}>Destinatário</label>
                      <select value={composeData.restaurantId} onChange={e=>setComposeData(p=>({...p,restaurantId:e.target.value}))} style={{...S.input,width:"100%"}}>
                        <option value="">Todos os restaurantes</option>
                        {restaurants.filter(r=>!r.deleted).map(r=>(
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{marginBottom:12}}>
                      <label style={{color:"var(--text3)",fontSize:12,fontWeight:600,display:"block",marginBottom:4}}>Assunto</label>
                      <input value={composeData.assunto} onChange={e=>setComposeData(p=>({...p,assunto:e.target.value}))} style={{...S.input,width:"100%"}} placeholder="Assunto da mensagem"/>
                    </div>
                    <div style={{marginBottom:16}}>
                      <label style={{color:"var(--text3)",fontSize:12,fontWeight:600,display:"block",marginBottom:4}}>Mensagem</label>
                      <textarea value={composeData.corpo} onChange={e=>setComposeData(p=>({...p,corpo:e.target.value}))} style={{...S.input,width:"100%",minHeight:100,resize:"vertical"}} placeholder="Escreva sua mensagem..."/>
                    </div>
                    <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
                      <button onClick={()=>setComposeOpen(false)} style={{...S.btnSecondary,fontSize:13}}>Cancelar</button>
                      <button onClick={sendMessage} disabled={!composeData.assunto.trim()||!composeData.corpo.trim()} style={{...S.btnPrimary,fontSize:13,opacity:(!composeData.assunto.trim()||!composeData.corpo.trim())?0.5:1}}>Enviar</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* FINANCEIRO GERAL */}
        {tab === "financeiro_geral" && (() => {
          const totalEmps = employees.filter(e=>!e.inactive).length;
          const totalMgrs = managers.length;
          const thisMonth = today().slice(0,7);

          const rows = restaurants.map(r => {
            const plano = getPlano(r);
            const fin = r.financeiro ?? {};
            const tipoCobranca = r.tipoCobranca ?? "mensal";
            const isIsento_ = r.planoId === "pIsento";
            const isEnt = r.planoId === "p999";
            const isOrc = r.planoId === "pOrc";
            const empMax = isIsento_ ? 999 : isEnt ? (r.empMaxCustom ?? 51) : (isOrc ? (r.empMaxCustom ?? 101) : plano.empMax);
            const ea = r.earlyAdopter ? 0.7 : 1;
            const valorMensalCalc = (() => {
              if (isIsento_) return 0;
              if (isOrc) return null;
              if (isEnt) {
                const porEmp = empMax * 7.99;
                return (tipoCobranca === "anual" ? porEmp * 0.9 : porEmp) * ea;
              }
              return (tipoCobranca === "anual" ? (plano.anual ?? 0) : (plano.mensal ?? 0)) * ea;
            })();
            const valorTotal = valorMensalCalc;
            const status = fin.status ?? "ativo";
            const venc = fin.proximoVencimento;
            const diasParaVencer = venc ? Math.ceil((new Date(venc+"T12:00:00") - new Date()) / (1000*60*60*24)) : null;
            const ultimoPag = fin.pagamentos?.[0];
            const empAtivos = employees.filter(e=>e.restaurantId===r.id&&!e.inactive).length;
            const gorjetasMes = tips.filter(t=>t.restaurantId===r.id&&t.monthKey===thisMonth);
            const diasGorjeta = [...new Set(gorjetasMes.map(t=>t.date))].length;
            return { r, plano, fin, tipoCobranca, valorTotal, status, venc, diasParaVencer, ultimoPag, empAtivos, empMax, diasGorjeta };
          });

          const receitaTotal = rows.reduce((s, x) => s + (x.valorTotal ?? 0), 0);
          const inadimplentes = rows.filter(x => x.status === "inadimplente").length;
          const vencendo = rows.filter(x => x.status !== "inadimplente" && x.diasParaVencer !== null && x.diasParaVencer <= 7 && x.diasParaVencer >= 0).length;
          const vencidos = rows.filter(x => x.status !== "inadimplente" && x.diasParaVencer !== null && x.diasParaVencer < 0).length;
          const emDia = rows.length - inadimplentes - vencidos - vencendo;

          const [filtro, setFiltro] = [filtroFinanceiro, setFiltroFinanceiro];
          const rowsFiltrados = rows.filter(x => {
            if (filtro === "inadimplente") return x.status === "inadimplente";
            if (filtro === "vencido") return x.status !== "inadimplente" && x.diasParaVencer !== null && x.diasParaVencer < 0;
            if (filtro === "vencendo") return x.status !== "inadimplente" && x.diasParaVencer !== null && x.diasParaVencer >= 0 && x.diasParaVencer <= 7;
            if (filtro === "emdia") return x.status === "ativo" && (x.diasParaVencer === null || x.diasParaVencer > 7);
            return true;
          });

          // Pagamentos aguardando confirmação
          const aguardando = restaurants.flatMap(r =>
            (r.financeiro?.cobrancas??[])
              .filter(c => c.status === "aguardando_confirmacao")
              .map(c => ({ ...c, restName: r.name, restId: r.id }))
          );

          return (
            <div>
              {/* ── Ações rápidas ── */}
              <div style={{display:"flex",gap:8,marginBottom:isMobile?12:16,flexWrap:"wrap"}}>
                <button onClick={()=>{setEditRestId(null);setRestForm({name:"",shortCode:"",cnpj:"",razaoSocial:"",codigoContabil:"",address:"",whatsappFin:"",whatsappOp:"",serviceStartDate:""});setShowRestModal(true);}} style={{...S.btnPrimary,fontSize:isMobile?12:14}}>+ Novo Restaurante</button>
                {restaurants.length > 0 && (
                  <button onClick={()=>{
                    const exportData = {
                      exportedAt: new Date().toISOString(),
                      restaurantes: restaurants,
                      empregados: employees,
                      cargos: roles,
                      gorjetas: tips,
                      escalas: schedules,
                      gestores: managers.map(m=>({...m,pin:"***"})),
                    };
                    const blob = new Blob([JSON.stringify(exportData,null,2)],{type:"application/json"});
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href=url; a.download=`apptip_backup_${today()}.json`; a.click();
                    URL.revokeObjectURL(url);
                    onUpdate("_toast","✅ Backup exportado com sucesso!");
                  }} style={{...S.btnSecondary,fontSize:isMobile?11:12,color:"var(--green)",borderColor:"var(--green)"}}>
                    💾 Backup completo
                  </button>
                )}
              </div>

              {/* ── Overview rápido ── */}
              <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(4,1fr)",gap:isMobile?8:14,marginBottom:isMobile?16:24}}>
                {[
                  { label:"Restaurantes", value:restaurants.length, icon:"🏢", color:"var(--blue)" },
                  { label:"Empregados", value:totalEmps, icon:"👥", color:"var(--green)" },
                  { label:"Gestores", value:totalMgrs, icon:"👔", color:"#8b5cf6" },
                  { label:"Receita/mês", value:`R$${receitaTotal.toLocaleString("pt-BR",{minimumFractionDigits:0,maximumFractionDigits:0})}`, icon:"💰", color:"var(--ac)" },
                ].map(m=>(
                  <div key={m.label} style={{...S.card,display:"flex",alignItems:"center",gap:isMobile?10:14,padding:isMobile?"10px 12px":undefined}}>
                    <div style={{fontSize:isMobile?20:28,width:isMobile?36:48,height:isMobile?36:48,borderRadius:12,background:m.color+"15",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{m.icon}</div>
                    <div style={{minWidth:0}}>
                      <div style={{color:"var(--text3)",fontSize:isMobile?9:11,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5,marginBottom:1}}>{m.label}</div>
                      <div style={{color:"var(--text)",fontSize:isMobile?16:22,fontWeight:800,fontFamily:"'DM Mono',monospace"}}>{m.value}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* ── Semáforo financeiro ── */}
              <div style={{display:"flex",gap:isMobile?6:12,marginBottom:isMobile?16:24}}>
                {[
                  { label:"Em dia", value:emDia, color:"var(--green)", bg:"#10b98112" },
                  { label:"Vencendo", value:vencendo, color:"#f59e0b", bg:"#f59e0b12" },
                  { label:"Vencidos", value:vencidos, color:"var(--red)", bg:"#ef444412" },
                  { label:"Inadimpl.", value:inadimplentes, color:"var(--red)", bg:"#ef444412" },
                ].map(s=>(
                  <div key={s.label} style={{flex:1,background:s.bg,borderRadius:12,padding:isMobile?"10px 8px":"14px 16px",textAlign:"center",border:`1px solid ${s.color}22`}}>
                    <div style={{color:s.color,fontSize:isMobile?20:28,fontWeight:800,fontFamily:"'DM Mono',monospace"}}>{s.value}</div>
                    <div style={{color:s.color,fontSize:isMobile?9:11,fontWeight:600,marginTop:2}}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* ── Alertas de pagamento aguardando ── */}
              {aguardando.length > 0 && (
                <div style={{...S.card,marginBottom:isMobile?16:24,border:"1px solid #f59e0b44",background:"#fffbeb",padding:isMobile?"12px":"16px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                    <span style={{color:"#92400e",fontWeight:700,fontSize:isMobile?12:14}}>💬 {aguardando.length} pagamento{aguardando.length>1?"s":""} aguardando confirmação</span>
                  </div>
                  {aguardando.map(c=>(
                    <div key={c.id} style={{display:"flex",flexDirection:isMobile?"column":"row",justifyContent:"space-between",alignItems:isMobile?"stretch":"center",gap:isMobile?8:0,padding:isMobile?"10px":"10px 12px",borderRadius:10,background:"#fff",border:"1px solid #fde68a",marginBottom:8}}>
                      <div style={{minWidth:0}}>
                        <div style={{color:"var(--text)",fontWeight:700,fontSize:13}}>{c.restName}</div>
                        <div style={{color:"var(--text3)",fontSize:isMobile?11:12}}>
                          {c.periodoLabel} · R$ {c.valor?.toLocaleString("pt-BR",{minimumFractionDigits:2})} · {c.clienteConfirmouEm ? new Date(c.clienteConfirmouEm).toLocaleDateString("pt-BR") : "—"}
                        </div>
                      </div>
                      <div style={{display:"flex",gap:6,flexShrink:0}}>
                        <button onClick={()=>{
                          const r = restaurants.find(x=>x.id===c.restId);
                          if (!r) return;
                          const dataPag = c.clienteConfirmouEm?.slice(0,10) ?? today();
                          const cicloEnd = (() => {
                            const d = new Date(dataPag+"T12:00:00");
                            if ((r.tipoCobranca??"mensal") === "anual") d.setFullYear(d.getFullYear()+1);
                            else d.setDate(d.getDate()+30);
                            return d.toISOString().slice(0,10);
                          })();
                          const [ano,mes] = cicloEnd.split("-");
                          const mesesNome = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
                          const proxLabel = `${mesesNome[parseInt(mes)-1]}/${ano}`;
                          const proxCob = { id:(Date.now()).toString(), periodo:cicloEnd.slice(0,7), periodoLabel:proxLabel, venc:cicloEnd, valor:c.valor, forma:c.forma, chave:c.chave, criadaEm:new Date().toISOString(), status:"pendente", autoGerada:true };
                          const updatedCobs = (r.financeiro?.cobrancas??[]).map(x=>x.id===c.id?{...x,status:"pago",pagoEm:new Date().toISOString()}:x);
                          const novoPag = { id:(Date.now()+1).toString(), data:dataPag, valor:c.valor, forma:c.forma, obs:`Ref. ${c.periodoLabel}`, registradoEm:new Date().toISOString() };
                          const updated = restaurants.map(x=>x.id===c.restId?{...x,financeiro:{...x.financeiro,cobrancas:[...updatedCobs,proxCob],pagamentos:[novoPag,...(x.financeiro?.pagamentos??[])],status:"ativo",cicloInicio:dataPag,cicloFim:cicloEnd,proximoVencimento:cicloEnd}}:x);
                          onUpdate("restaurants",updated);
                          onUpdate("_toast",`✅ Pagamento de ${c.restName} confirmado!`);
                        }} style={{padding:"6px 12px",borderRadius:8,border:"1px solid var(--green)44",background:"var(--green-bg)",color:"var(--green)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:12,fontWeight:700,flex:isMobile?1:undefined,textAlign:"center"}}>
                          ✅ Confirmar
                        </button>
                        <button onClick={()=>{
                          const updated = restaurants.map(r=>r.id===c.restId?{...r,financeiro:{...r.financeiro,cobrancas:(r.financeiro?.cobrancas??[]).map(x=>x.id===c.id?{...x,status:"pendente",clienteConfirmou:false}:x),status:"inadimplente"}}:r);
                          onUpdate("restaurants",updated);
                          onUpdate("_toast",`🔴 ${c.restName} marcado como inadimplente.`);
                        }} style={{padding:"6px 12px",borderRadius:8,border:"1px solid var(--red)33",background:"transparent",color:"var(--red)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:12,fontWeight:600,flex:isMobile?1:undefined,textAlign:"center"}}>
                          ✕ Negar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Notificações não lidas ── */}
              {unreadNotifs > 0 && (
                <div style={{...S.card,marginBottom:isMobile?16:24,border:"1px solid var(--ac)33",background:"var(--ac-bg)",padding:isMobile?"12px":"16px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                    <span style={{color:"var(--ac-text)",fontWeight:700,fontSize:isMobile?12:14}}>📬 {unreadNotifs} mensagem{unreadNotifs>1?"ns":""} não lida{unreadNotifs>1?"s":""}</span>
                    <button onClick={()=>setTab("inbox")} style={{...S.btnSecondary,fontSize:11,padding:"4px 12px"}}>Ver caixa →</button>
                  </div>
                  {notifications.filter(n=>!n.read&&n.targetRole==="admin").slice(0,3).map(n=>(
                    <div key={n.id} style={{padding:"6px 0",borderBottom:"1px solid var(--border)",fontSize:isMobile?12:13,color:"var(--text2)"}}>
                      {n.body}
                    </div>
                  ))}
                </div>
              )}

              {/* ── Filtros ── */}
              <div style={{display:"flex",gap:isMobile?4:8,marginBottom:isMobile?12:16,flexWrap:"wrap"}}>
                {[
                  ["todos","Todos"],
                  ["emdia","✅ Em dia"],
                  ["vencendo","⚡ Vencendo"],
                  ["vencido","⏰ Vencidos"],
                  ["inadimplente","🔴 Inadimpl."],
                ].map(([v,l])=>(
                  <button key={v} onClick={()=>setFiltro(v)}
                    style={{padding:isMobile?"5px 10px":"6px 16px",borderRadius:20,border:`1px solid ${filtro===v?ac:"var(--border)"}`,background:filtro===v?"var(--ac-bg)":"transparent",color:filtro===v?"var(--ac-text)":"var(--text3)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:isMobile?11:13,fontWeight:filtro===v?700:400}}>
                    {l}
                  </button>
                ))}
              </div>

              {/* ── Tabela / Cards de restaurantes ── */}
              {isMobile ? (
                /* Mobile: cards empilhados */
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {rowsFiltrados.length === 0 && (
                    <div style={{...S.card,textAlign:"center",padding:32,color:"var(--text3)"}}>Nenhum restaurante neste filtro.</div>
                  )}
                  {rowsFiltrados.map(({r, plano, valorTotal, status, venc, diasParaVencer, ultimoPag, empAtivos, empMax, diasGorjeta})=>{
                    const isInad = status === "inadimplente";
                    const cicloFimRow = r.financeiro?.cicloFim;
                    const cicloInicioRow = r.financeiro?.cicloInicio;
                    const trialFimRow = r.financeiro?.trialFim;
                    const emTrialRow = !cicloInicioRow && trialFimRow;
                    const diasRow = cicloFimRow ? Math.ceil((new Date(cicloFimRow+"T12:00:00")-new Date())/(1000*60*60*24)) : null;
                    const diasTrialRow = emTrialRow ? Math.ceil((new Date(trialFimRow+"T12:00:00")-new Date())/(1000*60*60*24)) : null;
                    const isVencido = !isInad && cicloFimRow && diasRow < 0;
                    const isVencendo = !isInad && diasRow !== null && diasRow >= 0 && diasRow <= 7;
                    const semColor = isInad||isVencido ? "var(--red)" : isVencendo ? "#f59e0b" : emTrialRow ? "#92400e" : "var(--green)";
                    const statusLabel = isInad ? "🔴 Inadimplente" : isVencido ? `⏰ Vencido ${Math.abs(diasRow)}d` : isVencendo ? `⚡ ${diasRow}d` : emTrialRow ? `🎯 Trial ${diasTrialRow}d` : !cicloInicioRow ? "⚙️ Não iniciado" : "✅ Em dia";

                    return (
                      <div key={r.id} style={{...S.card,padding:"12px",borderLeft:`3px solid ${semColor}`}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}} onClick={()=>{ setSelRestaurant(r.id); setRestTab("financeiro"); }}>
                          <div style={{display:"flex",alignItems:"center",gap:6,minWidth:0}}>
                            <span style={{color:"var(--text)",fontWeight:700,fontSize:14}}>{r.name}</span>
                            {r.earlyAdopter && <span style={{fontSize:9,color:"#d4a017",fontWeight:700,background:"#d4a01715",padding:"1px 4px",borderRadius:6}}>EA</span>}
                          </div>
                          <span style={{color:semColor,fontWeight:700,fontSize:11,flexShrink:0}}>{statusLabel}</span>
                        </div>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4,fontSize:11,color:"var(--text3)",marginBottom:8}} onClick={()=>{ setSelRestaurant(r.id); setRestTab("financeiro"); }}>
                          <div><span style={{fontWeight:600}}>Plano:</span> {plano.label}</div>
                          <div><span style={{fontWeight:600}}>Valor:</span> {valorTotal ? `R$${valorTotal.toLocaleString("pt-BR",{minimumFractionDigits:2})}` : "—"}</div>
                          <div><span style={{fontWeight:600}}>Emp.:</span> {empAtivos}/{empMax}</div>
                          <div><span style={{fontWeight:600}}>Gorjetas:</span> {diasGorjeta}d no mês</div>
                          <div><span style={{fontWeight:600}}>Últ. pag.:</span> {ultimoPag ? new Date(ultimoPag.data+"T12:00:00").toLocaleDateString("pt-BR") : "—"}</div>
                          <div><span style={{fontWeight:600}}>Venc.:</span> {venc ? new Date(venc+"T12:00:00").toLocaleDateString("pt-BR") : "—"}</div>
                        </div>
                        <div style={{display:"flex",gap:6,borderTop:"1px solid var(--border)",paddingTop:8}}>
                          <button onClick={()=>onEnterOperational && onEnterOperational(r.id)} style={{background:"#7c9e5e",border:"none",borderRadius:8,color:"#fff",cursor:"pointer",fontSize:11,padding:"6px 10px",fontFamily:"'DM Sans',sans-serif",fontWeight:700,flex:1}}>▶ Entrar</button>
                          <button onClick={()=>{setSelRestaurant(r.id);setRestTab("financeiro");}} style={{...S.btnSecondary,fontSize:11,flex:"0 0 auto",textAlign:"center",color:"var(--green)",borderColor:"var(--green)",padding:"6px 10px"}} title="Financeiro">💳</button>
                          <button onClick={()=>{setEditRestId(r.id);setRestForm({name:r.name,shortCode:r.shortCode??"",cnpj:r.cnpj??"",razaoSocial:r.razaoSocial??"",codigoContabil:r.codigoContabil??"",address:r.address??"",whatsappFin:r.whatsappFin??"",whatsappOp:r.whatsappOp??"",serviceStartDate:r.serviceStartDate??""});setShowRestModal(true);}} style={{...S.btnSecondary,fontSize:11,flex:"0 0 auto",textAlign:"center",padding:"6px 10px"}} title="Editar cadastro">✏️</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* Desktop: tabela */
                <div style={{...S.card,padding:0,overflow:"hidden"}}>
                  <div style={{display:"grid",gridTemplateColumns:"2fr 0.8fr 0.7fr 1fr 0.8fr 0.8fr 1fr 0.6fr",gap:0,padding:"10px 16px",background:"var(--bg2)",borderBottom:"1px solid var(--border)"}}>
                    {["Restaurante","Plano","Emp.","Valor/mês","Últ. pag.","Vencimento","Status","Ações"].map(h=>(
                      <div key={h} style={{color:"var(--text3)",fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5}}>{h}</div>
                    ))}
                  </div>

                  {rowsFiltrados.length === 0 && (
                    <div style={{padding:"32px",textAlign:"center",color:"var(--text3)"}}>Nenhum restaurante neste filtro.</div>
                  )}

                  {rowsFiltrados.map(({r, plano, valorTotal, status, venc, diasParaVencer, ultimoPag, empAtivos, empMax, diasGorjeta})=>{
                    const isInad = status === "inadimplente";
                    const cicloFimRow = r.financeiro?.cicloFim;
                    const cicloInicioRow = r.financeiro?.cicloInicio;
                    const trialFimRow = r.financeiro?.trialFim;
                    const emTrialRow = !cicloInicioRow && trialFimRow;
                    const diasRow = cicloFimRow ? Math.ceil((new Date(cicloFimRow+"T12:00:00")-new Date())/(1000*60*60*24)) : null;
                    const diasTrialRow = emTrialRow ? Math.ceil((new Date(trialFimRow+"T12:00:00")-new Date())/(1000*60*60*24)) : null;
                    const isVencido = !isInad && cicloFimRow && diasRow < 0;
                    const isVencendo = !isInad && diasRow !== null && diasRow >= 0 && diasRow <= 7;

                    const isIsentoRow = r.planoId === "pIsento";
                    const rowBg = isInad ? "var(--red-bg)" : isVencido ? "#fff8f0" : "transparent";
                    const statusEl = isIsentoRow
                      ? <span style={{color:"#10b981",fontWeight:600,fontSize:11}}>🤝 Isento</span>
                      : isInad
                      ? <span style={{color:"var(--red)",fontWeight:700,fontSize:11}}>🔴 Inadimplente</span>
                      : isVencido
                      ? <span style={{color:"var(--red)",fontWeight:600,fontSize:11}}>⏰ Vencido {Math.abs(diasRow)}d</span>
                      : isVencendo
                      ? <span style={{color:"#f59e0b",fontWeight:600,fontSize:11}}>⚡ {diasRow}d</span>
                      : emTrialRow
                      ? <span style={{color:"#92400e",fontWeight:600,fontSize:11}}>🎯 Trial {diasTrialRow}d</span>
                      : !cicloInicioRow
                      ? <span style={{color:"var(--text3)",fontSize:11}}>⚙️ Não iniciado</span>
                      : <span style={{color:"var(--green)",fontWeight:600,fontSize:11}}>✅ Em dia</span>;

                    return (
                      <div key={r.id} style={{display:"grid",gridTemplateColumns:"2fr 0.8fr 0.7fr 1fr 0.8fr 0.8fr 1fr 0.6fr",gap:0,padding:"10px 16px",borderBottom:"1px solid var(--border)",background:rowBg,alignItems:"center"}}>
                        <div style={{cursor:"pointer"}} onClick={()=>{ setSelRestaurant(r.id); setRestTab("financeiro"); }}>
                          <div style={{color:"var(--text)",fontWeight:700,fontSize:13}}>
                            {r.name}
                            {r.earlyAdopter && <span style={{marginLeft:6,fontSize:9,color:"#d4a017",fontWeight:700,background:"#d4a01715",padding:"1px 5px",borderRadius:6}}>🚀 EA</span>}
                          </div>
                          <div style={{color:"var(--text3)",fontSize:10}}>{r.planoId==="pIsento"?"Isento":r.tipoCobranca==="anual"?"Anual":"Mensal"} {diasGorjeta>0?`· ${diasGorjeta}d gorjeta`:""}</div>
                        </div>
                        <div style={{color:"var(--text2)",fontSize:12,cursor:"pointer"}} onClick={()=>{ setSelRestaurant(r.id); setRestTab("financeiro"); }}>{plano.label}</div>
                        <div style={{color:"var(--text2)",fontSize:12,fontFamily:"'DM Mono',monospace"}}>{empAtivos}/{empMax}</div>
                        <div style={{color:isIsentoRow?"#10b981":"var(--text)",fontWeight:700,fontSize:12,fontFamily:"'DM Mono',monospace"}}>
                          {isIsentoRow ? "Isento" : valorTotal ? `R$${valorTotal.toLocaleString("pt-BR",{minimumFractionDigits:2})}` : "—"}
                        </div>
                        <div style={{color:"var(--text3)",fontSize:11}}>
                          {ultimoPag ? new Date(ultimoPag.data+"T12:00:00").toLocaleDateString("pt-BR") : "—"}
                        </div>
                        <div style={{color:isVencido||isInad?"var(--red)":isVencendo?"#f59e0b":"var(--text3)",fontSize:11,fontWeight:isVencido||isVencendo||isInad?700:400}}>
                          {venc ? new Date(venc+"T12:00:00").toLocaleDateString("pt-BR") : "—"}
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          {statusEl}
                        </div>
                        <div style={{display:"flex",gap:4,alignItems:"center"}} onClick={e=>e.stopPropagation()}>
                          <button onClick={()=>onEnterOperational && onEnterOperational(r.id)} title="Entrar no restaurante como master" style={{background:"#7c9e5e",border:"none",borderRadius:6,color:"#fff",cursor:"pointer",fontSize:11,padding:"4px 10px",fontFamily:"'DM Sans',sans-serif",fontWeight:700}}>▶ Entrar</button>
                          <button onClick={()=>setSelRestaurant(r.id)} title="Ver detalhes / financeiro" style={{background:"none",border:`1px solid ${ac}33`,borderRadius:6,color:ac,cursor:"pointer",fontSize:11,padding:"4px 8px",fontFamily:"'DM Sans',sans-serif"}}>Detalhes</button>
                          <button onClick={()=>{setEditRestId(r.id);setRestForm({name:r.name,shortCode:r.shortCode??"",cnpj:r.cnpj??"",razaoSocial:r.razaoSocial??"",codigoContabil:r.codigoContabil??"",address:r.address??"",whatsappFin:r.whatsappFin??"",whatsappOp:r.whatsappOp??"",serviceStartDate:r.serviceStartDate??""});setShowRestModal(true);}} title="Editar cadastro" style={{background:"none",border:"1px solid var(--border)",borderRadius:6,color:"var(--text3)",cursor:"pointer",fontSize:11,padding:"4px 8px",fontFamily:"'DM Sans',sans-serif"}}>✏️</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Resumo */}
              <div style={{marginTop:12,color:"var(--text3)",fontSize:isMobile?11:12,textAlign:"right"}}>
                {rowsFiltrados.length} de {rows.length} restaurantes · Receita filtrada: R${rowsFiltrados.reduce((s,x)=>s+(x.valorTotal??0),0).toLocaleString("pt-BR",{minimumFractionDigits:2})}
              </div>
            </div>
          );
        })()}


        {/* GESTORES */}
        {tab === "managers" && (
          <div>
            <div style={{...S.card,background:"var(--bg2)",marginBottom:20,display:"flex",alignItems:"center",gap:12}}>
              <span style={{fontSize:20}}>ℹ️</span>
              <p style={{color:"var(--text3)",fontSize:13,margin:0}}>Visão global de todos os gestores. Clique em <strong>Editar</strong> para modificar ou em um restaurante para abri-lo.</p>
            </div>
            {managers.length === 0 && <p style={{color:"var(--text3)",textAlign:"center"}}>Nenhum gestor cadastrado. Crie gestores dentro de cada restaurante.</p>}
            {managers.map(m=>(
              <div key={m.id} style={{...S.card,marginBottom:10}}>
                <div style={{display:"flex",flexDirection:isMobile?"column":"row",justifyContent:"space-between",alignItems:isMobile?"stretch":"center",gap:isMobile?10:12}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2,flexWrap:"wrap"}}>
                      <span style={{color:"var(--text)",fontWeight:700,fontSize:isMobile?13:15}}>{m.name}</span>
                      {m.isDP && <span style={{background:"var(--blue-bg)",color:"var(--blue)",borderRadius:6,padding:"2px 8px",fontSize:isMobile?10:11,fontWeight:700}}>📬 Gestor Adm.</span>}
                      {m.profile && m.profile !== "custom" && <span style={{background:"var(--bg2)",color:"var(--text3)",borderRadius:6,padding:"2px 8px",fontSize:isMobile?10:11,fontWeight:600}}>{m.profile === "lider" ? "👑 Líder Op." : m.profile === "padrao" ? "📋 Padrão" : m.profile}</span>}
                    </div>
                    <div style={{color:"var(--text3)",fontSize:isMobile?11:12,marginBottom:6}}>CPF: {(m.restaurantIds??[]).some(rid=>isPrivate(rid)) ? maskCpfPriv(m.cpf, (m.restaurantIds??[])[0]) : (m.cpf||"—")}</div>
                    <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                      {(m.restaurantIds??[]).map(rid=>{
                        const r=restaurants.find(x=>x.id===rid);
                        return r ? (
                          <button key={rid} onClick={()=>setSelRestaurant(rid)}
                            style={{background:"var(--ac-bg)",color:"var(--ac-text)",borderRadius:6,padding:"3px 10px",fontSize:isMobile?11:12,fontWeight:600,border:"none",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                            {r.name} →
                          </button>
                        ) : null;
                      })}
                      {(!m.restaurantIds||m.restaurantIds.length===0)&&<span style={{color:"var(--text3)",fontSize:isMobile?11:12}}>Sem restaurantes atribuídos</span>}
                    </div>
                  </div>
                  <div style={{display:"flex",gap:6,flexShrink:0,justifyContent:isMobile?"stretch":"flex-end"}}>
                    <button onClick={()=>{setEditMgrId(m.id);setMgrForm({name:m.name,cpf:m.cpf??"",pin:m.pin??"",restaurantIds:m.restaurantIds??[],perms:m.perms??{tips:true,schedule:true},isDP:m.isDP??false,profile:m.profile??"custom",areas:m.areas??[]});setShowMgrModal(true);}} style={{...S.btnSecondary,fontSize:isMobile?11:12,flex:isMobile?1:undefined,textAlign:"center"}}>✏️ Editar</button>
                    <button onClick={async ()=>{
                      if(!await appConfirm(`Resetar o PIN de ${m.name}? O novo PIN temporário será 0000.`)) return;
                      // v7.7: sincroniza reset nos 3 records (manager + pessoa + employee linked) com mustChangePin=true
                      const cpfDigits = (m.cpf||"").replace(/\D/g,"");
                      const allPessoas = data?.pessoas || [];
                      const allEmployees = data?.employees || [];
                      onUpdate("managers", managers.map(x=>{
                        if (x.id === m.id) return { ...x, pin:"0000", mustChangePin:true };
                        if (cpfDigits && (x.cpf||"").replace(/\D/g,"") === cpfDigits) return { ...x, pin:"0000", mustChangePin:true };
                        return x;
                      }));
                      if (allPessoas.length > 0) onUpdate("pessoas", allPessoas.map(p=>{
                        if (p.linkedManagerId === m.id) return { ...p, pin:"0000", mustChangePin:true };
                        if (cpfDigits && (p.cpf||"").replace(/\D/g,"") === cpfDigits) return { ...p, pin:"0000", mustChangePin:true };
                        return p;
                      }));
                      if (allEmployees.length > 0 && cpfDigits) onUpdate("employees", allEmployees.map(e=>{
                        if ((e.cpf||"").replace(/\D/g,"") === cpfDigits) return { ...e, pin:"0000", mustChangePin:true };
                        return e;
                      }));
                      onUpdate("_toast",`🔐 PIN de ${m.name} resetado para 0000`);
                    }} style={{...S.btnSecondary,fontSize:isMobile?11:12,flex:isMobile?1:undefined,textAlign:"center"}}>{isMobile?"🔐 PIN":"🔐 Resetar PIN"}</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* SUPER GESTORES */}
        {tab === "owners" && (
          <div>
            <button onClick={()=>{setEditOwnerId(null);setOwnerForm({name:"",cpf:"",pin:""});setShowOwnerModal(true);}} style={{...S.btnPrimary,marginBottom:20,fontSize:isMobile?12:14}}>+ Novo Master</button>
            {owners.map(s=>(
              <div key={s.id} style={{...S.card,marginBottom:10,display:"flex",flexDirection:isMobile?"column":"row",justifyContent:"space-between",alignItems:isMobile?"stretch":"center",gap:isMobile?10:0,border:s.isMaster?"1px solid var(--ac)44":"1px solid var(--border)"}}>
                <div style={{minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                    <span style={{color:"var(--text)",fontWeight:700,fontSize:isMobile?14:15}}>{s.name}</span>
                    {s.isMaster && <span style={{background:"var(--ac-bg)",color:"var(--ac-text)",borderRadius:6,padding:"2px 8px",fontSize:isMobile?10:11,fontWeight:700}}>👑 Master</span>}
                    {s.id===currentUser?.id&&<span style={{color:"var(--text3)",fontSize:isMobile?10:11}}>← você</span>}
                  </div>
                  <div style={{color:"var(--text3)",fontSize:isMobile?11:12}}>CPF: {anyPrivate() ? "•••.•••.•••-••" : (s.cpf||"—")}</div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>{setEditOwnerId(s.id);setOwnerForm({name:s.name,cpf:s.cpf??"",pin:s.pin??"",isMaster:s.isMaster??false});setShowOwnerModal(true);}} style={{...S.btnSecondary,fontSize:isMobile?11:12,flex:isMobile?1:undefined,textAlign:"center"}}>Editar</button>
                  {!s.isMaster && owners.length>1 && isMaster && (
                    <button onClick={async ()=>{
                      if(!await appConfirm(`Excluir admin "${s.name}"?`)) return;
                      onUpdate("owners",owners.filter(x=>x.id!==s.id));
                    }} style={{background:"none",border:"1px solid var(--red)33",borderRadius:8,color:"var(--red)",cursor:"pointer",fontSize:isMobile?11:12,padding:"6px 12px",fontFamily:"'DM Sans',sans-serif",flex:isMobile?1:undefined,textAlign:"center"}}>✕ Excluir</button>
                  )}
                  {s.isMaster && <span style={{color:"var(--text3)",fontSize:isMobile?10:11,padding:"6px 8px"}}>🔒 Protegido</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* LIXEIRA — só master */}
        {tab === "trash" && isMaster && (() => {
          const cutoff7  = new Date(Date.now() - 7  * 24*60*60*1000).toISOString();

          const allItems = [
            ...(trash.restaurants??[]).map(x=>({...x,_type:"restaurants",_icon:"🏢",_days:30})),
            ...(trash.managers??[]).map(x=>({...x,_type:"managers",_icon:"👔",_days:30})),
            ...(trash.employees??[]).map(x=>({...x,_type:"employees",_icon:"👤",_days:30})),
          ].sort((a,b)=>(b.deletedAt??"").localeCompare(a.deletedAt??""));

          const tabItems = (trash.tabData??[])
            .filter(x=>x.deletedAt > cutoff7)
            .sort((a,b)=>(b.deletedAt??"").localeCompare(a.deletedAt??""));

          const tabIcons = { tips:"💸", schedule:"📅", roles:"🏷️", employees:"👥", comunicados:"📢", faq:"❓", dp:"💬", horarios:"🕐" };

          function restoreTab(entry) {
            const { tabKey, snapshot, restaurantId } = entry;
            if (tabKey === "tips") onUpdate("tips", [...(data?.tips??[]).filter(t=>t.restaurantId!==restaurantId), ...(snapshot.tips??[])]);
            if (tabKey === "schedule") onUpdate("schedules", {...(data?.schedules??{}), [restaurantId]: snapshot.schedules});
            if (tabKey === "roles") onUpdate("roles", [...(data?.roles??[]).filter(r=>r.restaurantId!==restaurantId), ...(snapshot.roles??[])]);
            if (tabKey === "employees") onUpdate("employees", [...(data?.employees??[]).filter(e=>e.restaurantId!==restaurantId), ...(snapshot.employees??[])]);
            if (tabKey === "comunicados") onUpdate("communications", [...(data?.communications??[]).filter(c=>c.restaurantId!==restaurantId), ...(snapshot.communications??[])]);
            if (tabKey === "faq") onUpdate("faq", {...(data?.faq??{}), [restaurantId]: snapshot.faq});
            if (tabKey === "dp") onUpdate("dpMessages", [...(data?.dpMessages??[]).filter(m=>m.restaurantId!==restaurantId), ...(snapshot.dpMessages??[])]);
            if (tabKey === "horarios") onUpdate("workSchedules", prev => ({...(prev??{}), [restaurantId]: snapshot.workSchedules}));
            onUpdate("trash", prev => ({...prev, tabData:(prev.tabData??[]).filter(x=>x.id!==entry.id)}));
            onUpdate("_toast", `↩ ${entry.tabLabel} restaurado!`);
          }

          async function hardDeleteTab(entry) {
            if(!await appConfirm(`Excluir permanentemente "${entry.tabLabel}" de ${entry.restaurantName}? Não tem volta.`)) return;
            onUpdate("trash", prev => ({...prev, tabData:(prev.tabData??[]).filter(x=>x.id!==entry.id)}));
            onUpdate("_toast","🗑️ Excluído permanentemente.");
          }

          const totalCount = allItems.length + tabItems.length;

          return (
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                <div>
                  <h3 style={{color:"var(--text)",fontSize:16,fontWeight:700,margin:"0 0 4px"}}>🗑️ Lixeira</h3>
                  <p style={{color:"var(--text3)",fontSize:13,margin:0}}>Restaurantes/gestores/empregados: 30 dias · Dados de abas: 7 dias</p>
                </div>
                {totalCount > 0 && (
                  <button onClick={async ()=>{
                    if(!await appConfirm("Esvaziar lixeira permanentemente? Esta ação não pode ser desfeita.")) return;
                    onUpdate("trash", {restaurants:[],managers:[],employees:[],tabData:[]});
                    onUpdate("_toast","🗑️ Lixeira esvaziada.");
                  }} style={{padding:"8px 16px",borderRadius:10,border:"1px solid var(--red)33",background:"transparent",color:"var(--red)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:600}}>
                    Esvaziar tudo
                  </button>
                )}
              </div>

              {totalCount === 0 && (
                <div style={{...S.card,textAlign:"center",padding:48}}>
                  <div style={{fontSize:36,marginBottom:12}}>✨</div>
                  <p style={{color:"var(--text3)",fontSize:14}}>Lixeira vazia.</p>
                </div>
              )}

              {/* Dados de abas — 7 dias */}
              {tabItems.length > 0 && (
                <div style={{marginBottom:20}}>
                  <h4 style={{color:"var(--text2)",fontSize:13,fontWeight:700,margin:"0 0 10px",textTransform:"uppercase",letterSpacing:0.5}}>📂 Dados de abas — 7 dias</h4>
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {tabItems.map(item=>{
                      const daysAgo = Math.floor((new Date()-new Date(item.deletedAt))/(1000*60*60*24));
                      const daysLeft = 7 - daysAgo;
                      return (
                        <div key={item.id} style={{...S.card,opacity:0.9,border:`1px solid var(--border)`}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                            <div>
                              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                                <span style={{fontSize:16}}>{tabIcons[item.tabKey]??""}</span>
                                <span style={{color:"var(--text)",fontWeight:700,fontSize:14}}>{item.tabLabel}</span>
                                <span style={{color:"var(--text3)",fontSize:12}}>— {item.restaurantName}</span>
                              </div>
                              <div style={{color:"var(--text3)",fontSize:12}}>
                                Resetado em {new Date(item.deletedAt).toLocaleDateString("pt-BR")}
                                <span style={{color:daysLeft<=2?"var(--red)":"var(--text3)",marginLeft:8}}>· {daysLeft}d restante{daysLeft!==1?"s":""}</span>
                              </div>
                            </div>
                            <div style={{display:"flex",gap:8}}>
                              <button onClick={()=>restoreTab(item)}
                                style={{padding:"7px 16px",borderRadius:8,border:"1px solid var(--green)44",background:"var(--green-bg)",color:"var(--green)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:700}}>
                                ↩ Restaurar
                              </button>
                              <button onClick={()=>hardDeleteTab(item)}
                                style={{padding:"7px 12px",borderRadius:8,border:"1px solid var(--red)33",background:"transparent",color:"var(--red)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:12}}>
                                🗑️
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Restaurantes, gestores, empregados — 30 dias */}
              {allItems.length > 0 && (
                <div>
                  <h4 style={{color:"var(--text2)",fontSize:13,fontWeight:700,margin:"0 0 10px",textTransform:"uppercase",letterSpacing:0.5}}>🏢 Restaurantes · Gestores · Empregados — 30 dias</h4>
                  <div style={{display:"flex",flexDirection:"column",gap:10}}>
                    {allItems.map(item=>{
                      const deletedDaysAgo = item.deletedAt ? Math.floor((new Date()-new Date(item.deletedAt))/(1000*60*60*24)) : 0;
                      const daysLeft = 30 - deletedDaysAgo;
                      return (
                        <div key={item.id} style={{...S.card,opacity:0.85}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                            <div>
                              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                                <span style={{fontSize:18}}>{item._icon}</span>
                                <span style={{color:"var(--text)",fontWeight:700,fontSize:14,textDecoration:"line-through",opacity:0.7}}>{item.name}</span>
                              </div>
                              <div style={{color:"var(--text3)",fontSize:12}}>
                                Excluído em {item.deletedAt ? new Date(item.deletedAt).toLocaleDateString("pt-BR") : "—"}
                                <span style={{color:daysLeft<7?"var(--red)":"var(--text3)",marginLeft:8}}>· {daysLeft}d até exclusão permanente</span>
                              </div>
                            </div>
                            <div style={{display:"flex",gap:8}}>
                              <button onClick={()=>restore(item._type, item)}
                                style={{padding:"7px 16px",borderRadius:8,border:"1px solid var(--green)44",background:"var(--green-bg)",color:"var(--green)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:700}}>
                                ↩ Restaurar
                              </button>
                              <button onClick={()=>hardDelete(item._type, item)}
                                style={{padding:"7px 12px",borderRadius:8,border:"1px solid var(--red)33",background:"transparent",color:"var(--red)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:12}}>
                                🗑️
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {tab === "changelog" && (() => {
          const CHANGELOG = [
            { version:"5.48.0", date:"2026-04-19", items:[
              "Novo: pastas customizáveis na Caixa de entrada — crie, renomeie e exclua pastas com cores para organizar mensagens",
              "Novo: aba Excluídas com lixeira de 90 dias e purge automático",
              "Novo: mover mensagens entre pastas e Entrada com modal dedicado",
              "Novo: exclusão definitiva restrita ao master (owner)",
              "Novo: pastas disponíveis para todos os perfis — Master, Gestor ADM, Líder e Funcionário",
              "Melhoria: aba Caixa sempre visível no portal do funcionário (não apenas com mensagens não lidas)",
              "Melhoria: contadores de não-lidas por pasta em todas as visualizações",
            ]},
            { version:"5.47.0", date:"2026-04-19", items:[
              "Novo: sistema unificado de Caixa de Entrada (inbox) — mensagens por papel (Master, ADM, Líder, Funcionário)",
              "Novo: notificações automáticas ao agendar reuniões — cada participante recebe aviso na Caixa",
              "Novo: notificações de ações pós-reunião — responsável recebe resumo com responsabilidades diretas e envolvidas",
              "Novo: composição de comunicados pelo Master para todos ou restaurante específico",
              "Novo: aba Caixa no portal do funcionário — visualização e marcação de lidas",
              "Novo: grupo Comunicação com aba Caixa para líderes operacionais",
              "Melhoria: filtros por tipo (reuniões, ações, solicitações, sistema) na Caixa do owner",
              "Melhoria: contador de não-lidas no badge da aba Caixa em todos os portais",
            ]},
            { version:"5.46.0", date:"2026-04-19", items:[
              "Novo: aba Arquitetura no portal do master — mapa interativo do sistema com 7 seções navegáveis",
              "Novo: visão geral da stack, portais (árvore expandível), modelo de dados, abas, permissões, fluxos e guia de planejamento",
            ]},
            { version:"5.45.0", date:"2026-04-19", items:[
              "Novo: persistência offline com cache local (IndexedDB) para master master",
              "Novo: indicador de conexão online/offline no rodapé (somente master)",
              "Novo: banner vermelho no topo quando offline — modo somente leitura",
              "Novo: bloqueio de escrita quando offline impede sobrescrever dados de gestores online",
            ]},
            { version:"5.44.0", date:"2026-04-19", items:[
              "Novo: gestor administrativo pode excluir definitivamente itens de todas as lixeiras do sistema",
              "Novo: jornada do empregado exibe reuniões realizadas e ocorrências unificadas com detalhes clicáveis",
              "Melhoria: reuniões passadas/encerradas e em andamento integradas à timeline cronológica",
            ]},
            { version:"5.19.0", date:"2026-04-18", items:[
              "Refatoração: removidas ~400 linhas de código morto (componentes e funções não utilizadas)",
              "Refatoração: hook useAiGenerate unifica chamadas de IA — reduz duplicação de lógica de loading/erro",
              "Refatoração: hook useMobile substitui detecções duplicadas de viewport",
              "Refatoração: modal de privacidade agora usa state React em vez de manipulação DOM direta",
              "Refatoração: estados de cobrança e modais agrupados — reduz número de useState no OwnerPortal",
              "Melhoria: paleta de cores C e estilos S.btnSmall padronizados, AREA_COLORS consistente",
              "Melhoria: botões secundários com minHeight:44px para touch targets adequados em mobile",
            ]},
            { version:"5.18.0", date:"2026-04-17", items:[
              "Novo: importação de folha de ponto via PDF — análise automática com IA identifica atrasos, faltas e horas extras, atualiza escala e cria ocorrências",
              "Novo: exportação da trilha do empregado em PDF — relatório completo com ocorrências, feedbacks, histórico de cargos e resumo do período selecionado",
            ]},
            { version:"5.17.1", date:"2026-04-17", items:[
              "Segurança: ocorrências (positivas e negativas) agora são 100% internas — empregado não vê nenhuma",
              "Melhoria: métricas do empregado mostram mês anterior (fechado) em vez do mês corrente",
              "Melhoria: feedback do empregado simplificado — exibe apenas meta e cargo-alvo, sem estrelas ou detalhes internos",
              "Melhoria: formulário de ocorrência do gestor diferencia visualmente positivas (verde) e negativas (vermelho)",
              "Melhoria: timeline do gestor com cores e badges distintos por tipo de ocorrência",
              "Removido: badge 'Destaque' e seção 'Elogios Recebidos' da visão do empregado",
            ]},
            { version:"5.17.0", date:"2026-04-17", items:[
              "Redesign: aba Equipe totalmente refeita — listagem com cards compactos (nome, cargo, área, status) em vez de planilha",
              "Novo: detalhe do empregado com 3 abas internas: Cadastro (dados editáveis), Ações (promover, demitir, PIN, etc.) e Trilha (timeline + ocorrências + feedback)",
              "Novo: botão '+ Novo' no topo abre formulário dedicado para adicionar empregado",
              "Melhoria: card de cada empregado mostra avatar com inicial, cargo, badges de status (Ativo/Inativo/Demitido/Promoção), flags (Produção/Freela)",
              "Melhoria: aba Ações com botões descritivos — cada ação com título e explicação clara",
            ]},
            { version:"5.16.1", date:"2026-04-17", items:[
              "Unificação: abas Equipe e Trilha integradas em uma única aba Equipe",
              "Novo: visão detalhada do empregado com timeline, ocorrências e feedback integrados",
              "Melhoria: formulários de ocorrência e feedback pré-selecionados para o empregado em contexto",
            ]},
            { version:"5.16.0", date:"2026-04-17", items:[
              "Novo: Aba 📈 Trilha do Empregado — linha do tempo completa com eventos automáticos (escala, promoções, demissão) e manuais (ocorrências, feedbacks)",
              "Novo: Registro de ocorrências/incidentes — seletor múltiplo de envolvidos, 8 tipos (advertências, desentendimento, elogio, destaque), 3 gravidades",
              "Novo: Registro de feedback trimestral — avaliação 1-5 estrelas, pontos fortes/melhorar, observações internas, meta e cargo-alvo",
              "Novo: Botão ⬆️ Promover na aba Equipe — mudança de cargo com data efetiva, imediata ou agendada, com histórico (roleHistory)",
              "Novo: Promoções agendadas auto-aplicadas no carregamento do app quando a data chega",
              "Novo: Visão gamificada 'Minha Trilha' no portal do empregado — nível/progresso, badges, métricas vs área, metas, checklist de desenvolvimento",
              "Novo: Badges automáticos (tempo de casa, promoção, avaliação 5★, elogios recebidos)",
              "Novo: Métricas anônimas comparando faltas do empregado com média da área",
              "Novo: Checklist de desenvolvimento por cargo com links para materiais de estudo",
              "Segurança: ocorrências negativas, gravidade, nomes de envolvidos e observações internas NÃO visíveis ao empregado",
              "Acesso: Master, Gestor Adm. e Líder Operacional (líder só vê empregados da sua área)",
            ]},
            { version:"5.15.4", date:"2026-04-17", items:[
              "Correção: tela de primeiro acesso do empregado (cadastro de PIN) agora tem botão '← Voltar ao login'",
            ]},
            { version:"5.15.3", date:"2026-04-17", items:[
              "Correção: demissão agora marca empregado como inativo imediatamente (antes só no mês seguinte)",
              "Correção: empregado demitido sai do dashboard (equipe de hoje) e do cálculo de gorjeta na hora",
              "Correção: na escala do mês da demissão, demitido continua aparecendo como 'DEM'; no mês seguinte não aparece mais",
            ]},
            { version:"5.15.2", date:"2026-04-17", items:[
              "Melhoria: ações bulk da escala (Folgas do contrato, Reiniciar escala, Marcar férias) agora acumulam edições locais",
              "Melhoria: todas as edições exigem clique em 'Salvar nova versão' para confirmar — gera snapshot automático no histórico",
            ]},
            { version:"5.15.1", date:"2026-04-16", items:[
              "Melhoria: design unificado em todas as abas do painel do gestor — mesma linguagem visual em cabeçalhos, botões, cards e espaçamentos",
              "Melhoria: todas as abas agora têm título H3 consistente (fontSize 16/20) com botões de ação à direita",
              "Melhoria: Dashboard — seções de área agora usam barras com borda lateral colorida (mesmo padrão do VT)",
              "Melhoria: Dashboard — barras de distribuição de gorjeta por área com borda lateral e DM Mono nos valores",
              "Melhoria: Escala — botões de ação unificados com S.btnSecondary (padding e fontSize consistentes)",
              "Melhoria: Gorjetas — cabeçalho h3 + botões de exportar/histórico alinhados à direita",
              "Melhoria: Cargos, Equipe, FAQ, Comunicados, DP, Notificações, Configurações — todos com layout de cabeçalho padrão",
            ]},
            { version:"5.15.0", date:"2026-04-16", items:[
              "Novo: Aba Vale Transporte (🚌 VT) — cálculo mensal de VT por empregado com base na escala de trabalho",
              "Novo: VT Diário editável por empregado, persistido entre meses (altera valor dali pra frente)",
              "Novo: Dias previstos calculados automaticamente a partir do horário contratual + escala do mês",
              "Novo: Ajuste automático do mês anterior — compara dias pagos vs dias reais (faltas descontam, dias extras somam)",
              "Novo: Campo de ajuste editável — sistema sugere valor mas gestor pode alterar livremente",
              "Novo: Campo de desconto manual por empregado (livre para qualquer desconto adicional)",
              "Novo: Botão 'Marcar como Pago' — congela snapshot dos valores e usa como referência para ajuste do mês seguinte",
              "Novo: Exportação de VT para CSV e PDF (impressão)",
              "Novo: Layout responsivo — tabela completa no desktop, cards compactos no mobile",
              "Regras: Férias, folgas, compensações e faltas (justificadas e injustificadas) não pagam VT. Freelancers ficam fora",
            ]},
            { version:"5.14.1", date:"2026-04-12", items:[
              "Removido: botão 'Salvar Dias' da aba Horários — fluxo unificado em Validar → Salvar Horário",
              "Removido: botão 'Resetar horários' da página geral de Horários — ação preservada apenas na tela de edição do empregado (evita duplicação)",
              "Melhoria: botão de Validar/Salvar Horário ocupa toda a largura disponível (mais visível)",
            ]},
            { version:"5.14.0", date:"2026-04-12", items:[
              "Novo: Histórico de versões da Escala — botão '🕐 Histórico' no topo da aba (Admin/DP). Últimas 30 versões, restore do mês inteiro, cada alteração vira ponto no histórico",
              "Novo: Histórico de versões das Gorjetas — mesmo formato da escala, botão '🕐 Histórico' na aba Gorjetas",
              "Novo: ações bulk (Reiniciar escala, Folgas do contrato, Marcar férias, Recalcular, Remover gorjeta) geram snapshot automático antes de aplicar",
              "Novo: edições individuais na escala geram snapshot debounced de 30s (agrupa cliques em sequência)",
              "Novo: restaurar versão também cria um ponto — permite desfazer o desfazer",
              "Novo: Horários — botão '♻️ Reativar' em cada versão do histórico copia a versão antiga como nova vigência a partir de hoje",
              "Melhoria: fluxo de Horários separado em 2 passos — '✓ Validar Horários' confirma as regras CLT e só depois libera '💾 Salvar Horário' (previne salvamento acidental sem validar)",
              "Melhoria: card verde 'Horário validado' aparece entre validação e salvamento como confirmação visual",
            ]},
            { version:"5.13.0", date:"2026-04-12", items:[
              "Novo: botão '🔐 Resetar PIN' nos cards de gestores (Master e DP Gestores) — PIN volta para 4 primeiros dígitos do CPF e força troca no próximo acesso",
              "Melhoria: fluxo de reset de PIN dos gestores agora consistente com o dos empregados",
              "Correção: IA (Comunicados, FAQ, Cargos, Empregados, Horários) agora exibe mensagem real do erro em vez de 'Não foi possível gerar'",
              "Correção: groqGenerate detecta chave ausente, modelo descontinuado, rate limit (429) e auth inválido (401) com mensagens específicas",
              "Correção: erros da IA agora aparecem no console.error com detalhes completos (status, código, payload)",
            ]},
            { version:"5.12.3", date:"2026-04-12", items:[
              "Melhoria: cards 'Copiar horário' e 'Assistente IA' em Horários com padding 18px 22px, ícones maiores, subtítulo descritivo e divisor ao expandir",
              "Melhoria: no desktop, input + botão da IA ficam lado a lado (antes empilhados)",
              "Melhoria: resultado da IA com padding 16px 18px e fontes maiores",
            ]},
            { version:"5.12.2", date:"2026-04-12", items:[
              "Melhoria: barra de Total Semanal em Horários redesenhada — padding generoso, ícone ✅/⚠️, total em 28px mono, descrição do status CLT",
            ]},
            { version:"5.12.1", date:"2026-04-12", items:[
              "Melhoria: botões de ação em Horários (Salvar Dias / Validar e Salvar) com padding 14px 24px no desktop",
              "Melhoria: botão 'Confirmar e Salvar' com padding generoso e peso 700",
              "Melhoria: lista de empregados em Horários (desktop) redesenhada — avatar colorido por área, grid 48px + 1fr + status + seta, padding 16px 20px, hover border",
              "Melhoria: status do empregado agora exibido como badge colorido (verde=completo, âmbar=pendente, cinza=sem horário)",
            ]},
            { version:"5.12.0", date:"2026-04-12", items:[
              "Novo: IA de horários entende listas estruturadas por dia (ex: 'Qua e Qui: das 15h às 24h com 1h de intervalo')",
              "Novo: IA reconhece separadores como linha nova, •, ·, ; e dias não mencionados viram folga automaticamente",
              "Novo: IA aceita 'das X às Y' como entrada+saída na mesma expressão (inclui 24h → meia-noite)",
              "Novo: botão 'Resetar horário' do empregado — apaga todas as versões do horário atual",
              "Melhoria: layout desktop dos horários refeito — linhas espaçosas em grid (dia | campos | toggle), intervalo com 110px, labels uppercase",
              "Melhoria: dias de folga no desktop mostram texto discreto ao invés de card vazio",
            ]},
            { version:"5.11.0", date:"2026-04-12", items:[
              "Novo: Horários — copiar horário de outro empregado ao cadastrar/editar (seletor por área com carga semanal)",
              "Melhoria: Horários mobile redesenhado — 7 dias em um único card compacto com divisores (em vez de 7 cards separados)",
              "Melhoria: Horários mobile — inputs inline sem labels por linha, placeholder como label",
            ]},
            { version:"5.10.1", date:"2026-04-12", items:[
              "Melhoria: layout de Horários otimizado para mobile — cards, inputs, labels e botões compactos",
              "Melhoria: container geral com padding reduzido no celular",
              "Melhoria: assistente IA com texto de ajuda compacto no mobile",
              "Melhoria: lista de empregados mais densa no celular (tags e subtítulos abreviados)",
              "Melhoria: cálculos de horas simplificados no mobile (Trab + Contr em vez de Real + Diurna + Contratual)",
            ]},
            { version:"5.10.0", date:"2026-04-12", items:[
              "Novo: aba Horários disponível no mobile do gestor — edição completa + assistente IA",
              "Atualizado: aviso de funcionalidades desktop removeu Horários da lista (agora disponível no celular)",
            ]},
            { version:"5.9.0", date:"2026-04-12", items:[
              "Melhoria: IA de horários — parser reescrito para entender linguagem natural flexível em português",
              "Melhoria: IA aceita conjugações verbais (folgue, trabalhe, dividindo, entrando), typos (semanis, entrandoa s) e frases compostas",
              "Melhoria: IA reconhece 'trabalhe nos outros dias' como padrão para preencher dias não mencionados",
              "Melhoria: campo de texto da IA de horários em layout empilhado (input + botão 100% largura)",
              "Removido: aba Gorjetas do mobile (lançamentos apenas pelo PC)",
              "Removido: 'Ações rápidas' do dashboard mobile",
              "Novo: aba Caixa de entrada na visão mobile do gestor",
              "Correção: mobile tabs agora Dashboard + Escala + Caixa de entrada",
            ]},
            { version:"5.8.0", date:"2026-04-12", items:[
              "Novo: Escala mobile — visão semanal com navegação ◀/▶, grid 7 colunas, toque para alterar status",
              "Melhoria: botões de ação da escala compactos no mobile (Folgas, Reiniciar, Férias)",
              "Melhoria: legenda da escala ajustada para telas menores",
              "Removido: botão PDF export e Reset ocultos no mobile",
            ]},
            { version:"5.7.0", date:"2026-04-12", items:[
              "Melhoria: dashboard gorjetas — label 'Dias preenchidos' + valores zerados exibem R$ 0,00",
              "Melhoria: aba Escala agora disponível no mobile do gestor (Dashboard + Gorjetas + Escala)",
              "Melhoria: arredondamento financeiro (2 casas) em todos os cálculos de gorjeta e penalidade",
              "Melhoria: data de admissão padrão agora é dinâmica (ano corrente) em vez de fixa",
              "Melhoria: PIX e nome agora lidos da config (data.pixChave / data.pixNome) com fallback",
              "Correção: cor do 'Pool total' trocada de branco fixo para var(--text) — visível em ambos os temas",
              "Correção: versão centralizada no rodapé em todas as telas",
              "Limpeza: console.logs de debug removidos, variáveis mortas eliminadas, eslint granular",
              "Acessibilidade: Toast com role=status, Modal com role=dialog + Escape, aria-labels em botões",
              "Refatoração: STATUS_SHORT e STATUS_COLORS centralizados como constantes globais",
              "Segurança: try-catch em calcTipForDate e applyFaultPenalty com feedback via toast",
            ]},
            { version:"5.6.0", date:"2026-04-12", items:[
              "Novo: Admissão default — empregados sem data de admissão assumem 01/01/2026",
              "Novo: Privacidade de dados na landing page e no guia do gestor",
              "Novo: Botões da escala reorganizados — Folgas, Reiniciar, Férias lado a lado + Exportar PDF à direita",
              "Novo: Mobile gestor — apenas Dashboard e Gorjetas, com aviso para usar demais funções no PC",
              "Novo: Assistente de Horários com IA — descreva o que deseja e o sistema sugere preenchimento automático",
              "Correção: input de gorjeta aceita vírgula como separador decimal (padrão brasileiro)",
            ]},
            { version:"5.5.0", date:"2026-04-12", items:[
              "Tabela de gorjetas simplificada — digita valores e salva tudo de uma vez (padrão deferred save)",
              "Removido: modo simples vs tabela, botões salvar por dia, 'Lançar Todos Preenchidos'",
              "Botão sticky 'Salvar Gorjetas' + 'Descartar' aparece quando há alterações pendentes",
              "Aviso ao sair da aba Gorjetas sem salvar — pergunta se deseja salvar antes",
            ]},
            { version:"5.4.2", date:"2026-04-12", items:[
              "Correção: gorjeta lançada não sumia mais — refatoração de calcTipForDate e recalcTipDay para evitar stale closure",
              "Correção: 'Lançar Todos Preenchidos' agora acumula tips corretamente em batch",
              "Correção: 'Recalcular Todos os Dias' agora preserva todas as gorjetas ao recalcular em batch",
            ]},
            { version:"5.4.1", date:"2026-04-12", items:[
              "Privacidade: tabela diária de gorjetas — dias lançados mostram ••••,•• para o admin, dias vazios ficam normais",
            ]},
            { version:"5.4.0", date:"2026-04-12", items:[
              "Novo: Modo Privacidade — gestor pode ocultar dados sensíveis da visão do admin",
              "Valores de gorjeta, CPFs, mensagens DP e comunicados ficam mascarados (•••) para o admin",
              "Toggle na aba Config do gestor: 🔒 Privacidade",
              "Banner visual no topo quando admin acessa restaurante com privacidade ativa",
            ]},
            { version:"5.3.3", date:"2026-04-12", items:[
              "Login por ID (ex: LBZ0001): agora mostra tela de escolha Gestor/Empregado quando o CPF do empregado também é de um gestor",
            ]},
            { version:"5.3.2", date:"2026-04-12", items:[
              "FAQ gorjeta: texto agora explica claramente o cálculo para cada modo (Área+Pontos vs Pontos Global)",
              "FAQ sistema: descrição detalhada de como funciona cada modalidade de divisão",
              "Botão Salvar Config movido para o topo da aba (sticky) — visível imediatamente",
            ]},
            { version:"5.3.1", date:"2026-04-12", items:[
              "Aba Config unificada: todas as configurações agora salvam com botão explícito (retenção, penalidades, modalidade, abas)",
              "Botão Salvar Config fixo no rodapé (sticky) — sempre visível quando há alterações pendentes",
              "Aviso ao sair da aba Config sem salvar: pergunta se deseja salvar antes",
              "FAQ automático do gestor agora reflete alterações de config em tempo real (antes de salvar)",
              "Correção: FAQ de gorjeta atualiza corretamente ao alternar entre Área+Pontos e Pontos Global",
            ]},
            { version:"5.3.0", date:"2026-04-12", items:[
              "Aba Horários: empregados agrupados por área",
              "Alternância empregado/gestor: botão direto no header para usuários com perfil duplo",
              "Tela do gestor agora inicia sempre pelo Dashboard",
              "Correção: comunicados automáticos de horário não bloqueiam mais a tela do empregado",
              "Percentuais de área: ao salvar, pergunta se deseja aplicar aos próximos meses",
              "Novos percentuais padrão: Bar 12%, Cozinha 40%, Salão 40%, Limpeza 8%",
              "Campo PIN substituído por botão 🔑 Resetar na aba Equipe",
            ]},
            { version:"5.2.0", date:"2026-04-12", items:[
              "Novo status de escala: FREELA — empregado presente mas sem gorjeta",
              "Novo flag de empregado: Freela (🎯) — nunca participa do rateio de gorjeta",
              "Dashboard de equipe separado por áreas (Bar, Cozinha, Salão, Limpeza), Produção e Fora",
              "Log de versões (esta aba) exclusivo para administradores",
              "Penalidade de produção: percentuais separados para falta justificada e injustificada",
              "Férias exclui todos os empregados da gorjeta, incluindo produção",
            ]},
            { version:"5.1.0", date:"2026-04-10", items:[
              "Produção deixa de ser área e passa a ser flag no empregado (🏭)",
              "Penalidade de produção com percentual configurável (padrão 6,66% / 3,33%)",
              "PIN de 4 dígitos para gestores e empregados com login unificado",
              "Forçar troca de PIN no primeiro acesso do gestor",
              "IA de cadastro de empregados com preview, edição e inativação",
              "Matching fuzzy de cargos na IA de cadastro",
              "FAQ de produção para empregados e gestores",
              "Guia do Gestor atualizado com produção e penalidades",
            ]},
            { version:"5.0.0", date:"2026-04-06", items:[
              "Rewrite completo: React 18 single-file architecture",
              "Sistema de gorjetas com modo Área+Pontos e Global Pontos",
              "Escala mensal com status: trabalho, folga, compensação, férias, faltas",
              "Gestão de cargos com pontos e áreas",
              "FAQ inteligente para empregados",
              "Guia do Gestor interativo",
              "Fale com DP (canal direto empregado-gestor)",
              "Comunicados com confirmação de leitura",
              "Horários contratuais com geração automática de escala",
              "Penalidade por falta injustificada por área",
              "Export PDF/CSV de gorjetas e escala",
              "Painel administrativo com dashboard financeiro",
              "Sistema de notificações e caixa de entrada",
              "Tema claro/escuro",
            ]},
          ];
          return (
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                <h3 style={{color:ac,fontSize:16,fontWeight:700,margin:0}}>📋 Log de Versões</h3>
                <span style={{color:"var(--text3)",fontSize:12}}>Versão atual: <strong style={{color:ac}}>{APP_VERSION}</strong></span>
              </div>
              {CHANGELOG.map((v,vi) => (
                <div key={v.version} style={{...S.card,marginBottom:12,border:vi===0?`1px solid ${ac}33`:"1px solid var(--border)"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{background:vi===0?ac:"var(--text3)",color:vi===0?"#fff":"var(--bg)",padding:"2px 10px",borderRadius:20,fontSize:12,fontWeight:700,fontFamily:"'DM Mono',monospace"}}>v{v.version}</span>
                      {vi===0 && <span style={{background:"#10b98133",color:"var(--green)",padding:"2px 8px",borderRadius:10,fontSize:10,fontWeight:700}}>ATUAL</span>}
                    </div>
                    <span style={{color:"var(--text3)",fontSize:11}}>{new Date(v.date+"T12:00:00").toLocaleDateString("pt-BR",{day:"numeric",month:"long",year:"numeric"})}</span>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:4}}>
                    {v.items.map((item,ii) => (
                      <div key={ii} style={{display:"flex",gap:6,alignItems:"flex-start",fontSize:12,color:"var(--text2)"}}>
                        <span style={{color:vi===0?ac:"var(--text3)",fontSize:10,marginTop:2}}>•</span>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        {tab === "arquitetura" && <ArchitectureTab isMobile={isMobile} />}
      </div>

      {/* Modals */}
      {showRestModal && (
        <Modal title={editRestId?"Editar Restaurante":"Novo Restaurante"} onClose={()=>setShowRestModal(false)}>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <div><label style={S.label}>Nome do restaurante</label><input value={restForm.name} onChange={e=>setRestForm({...restForm,name:e.target.value})} style={S.input}/></div>
            <div>
              <label style={S.label}>ID do restaurante (3 letras únicas, ex: QUI)</label>
              <input value={restForm.shortCode} onChange={e=>setRestForm({...restForm,shortCode:e.target.value.toUpperCase().replace(/[^A-Z]/g,"").slice(0,3)})}
                placeholder="QUI" maxLength={3} style={{...S.input, textTransform:"uppercase", letterSpacing:6, fontSize:18, textAlign:"center"}}
                disabled={!!editRestId}/>
              {editRestId && <p style={{color:"var(--text3)",fontSize:11,marginTop:4}}>O ID não pode ser alterado após criação.</p>}
            </div>
            <div><label style={S.label}>CNPJ (opcional)</label><input value={restForm.cnpj} onChange={e=>setRestForm({...restForm,cnpj:e.target.value})} placeholder="00.000.000/0000-00" style={S.input}/></div>
            <div><label style={S.label}>Razão Social (opcional)</label><input value={restForm.razaoSocial??""} onChange={e=>setRestForm({...restForm,razaoSocial:e.target.value})} placeholder="ex: SOROROCA BAR LTDA" style={S.input}/><p style={{color:"var(--text3)",fontSize:11,marginTop:3}}>Aparece no cabeçalho da planilha exportada pra contabilidade.</p></div>
            <div><label style={S.label}>Código contábil (opcional)</label><input value={restForm.codigoContabil??""} onChange={e=>setRestForm({...restForm,codigoContabil:e.target.value.replace(/\D/g,"")})} placeholder="ex: 2992" inputMode="numeric" style={S.input}/><p style={{color:"var(--text3)",fontSize:11,marginTop:3}}>Código da empresa junto à contabilidade. Aparece no cabeçalho da planilha como "{restForm.codigoContabil||"XXXX"} - {restForm.razaoSocial||restForm.name||"NOME"}".</p></div>
            <div><label style={S.label}>Endereço (opcional)</label><input value={restForm.address} onChange={e=>setRestForm({...restForm,address:e.target.value})} style={S.input}/></div>
            <div>
              <label style={S.label}>📅 Data de início da vigência do serviço</label>
              <input type="date" value={restForm.serviceStartDate??""} max={new Date().toISOString().split("T")[0]}
                onChange={e=>setRestForm({...restForm,serviceStartDate:e.target.value})} style={S.input}/>
              <p style={{color:"var(--text3)",fontSize:11,marginTop:4}}>Escala e gorjetas só podem ser lançadas a partir desta data. Admissão e horários não são afetados. Máximo: data de hoje.</p>
            </div>
            <div style={{borderTop:"1px solid var(--border)",paddingTop:10}}>
              <p style={{color:"var(--text3)",fontSize:12,margin:"0 0 8px",fontWeight:600}}>📱 Contatos WhatsApp</p>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                <div>
                  <label style={S.label}>WhatsApp Financeiro <span style={{color:"var(--red)"}}>*</span></label>
                  <input value={restForm.whatsappFin??""} onChange={e=>setRestForm({...restForm,whatsappFin:e.target.value})} placeholder="11987654321" inputMode="numeric" style={S.input}/>
                  <p style={{color:"var(--text3)",fontSize:11,marginTop:3}}>Recebe cobranças e avisos de pagamento</p>
                </div>
                <div>
                  <label style={S.label}>WhatsApp Operacional</label>
                  <input value={restForm.whatsappOp??""} onChange={e=>setRestForm({...restForm,whatsappOp:e.target.value})} placeholder="11987654321" inputMode="numeric" style={S.input}/>
                  <p style={{color:"var(--text3)",fontSize:11,marginTop:3}}>Recebe comunicados técnicos e suporte</p>
                </div>
              </div>
            </div>
            {!editRestId && (
              <div style={{padding:"10px 14px",borderRadius:10,background:"var(--bg2)",border:"1px solid var(--border)"}}>
                <p style={{color:"var(--text3)",fontSize:12,margin:0}}>💡 Plano e cobrança podem ser definidos na aba <strong>💳 Financeiro</strong> após o cadastro.</p>
              </div>
            )}
            <button onClick={saveRest} style={S.btnPrimary}>{editRestId?"Salvar":"Cadastrar"}</button>
          </div>
        </Modal>
      )}

      {showMgrModal && (
        <Modal title={editMgrId?"Editar Gestor":"Novo Gestor"} onClose={()=>setShowMgrModal(false)} wide>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            {/* Puxar da equipe */}
            {!editMgrId && (() => {
              const allActiveEmps = employees.filter(e => !e.inactive);
              if (allActiveEmps.length === 0) return null;
              return (
                <div style={{background:"var(--bg2)",border:"1px solid var(--border)",borderRadius:10,padding:"10px 14px"}}>
                  <label style={{...S.label,margin:0}}>👥 Puxar dados de um empregado da equipe</label>
                  <p style={{color:"var(--text3)",fontSize:11,margin:"4px 0 8px"}}>Selecione para pré-preencher nome e CPF. O empregado continua ativo na equipe.</p>
                  <select onChange={e => { const emp = allActiveEmps.find(x => x.id === e.target.value); if (!emp) return; setMgrForm(f => ({...f, name: emp.name, cpf: emp.cpf ?? "", restaurantIds: [...new Set([...(f.restaurantIds??[]), emp.restaurantId])]})); }} style={{...S.input,cursor:"pointer"}} defaultValue="">
                    <option value="" disabled>Selecionar empregado...</option>
                    {allActiveEmps.sort((a,b)=>a.name.localeCompare(b.name)).map(e => { const rest = restaurants.find(r => r.id === e.restaurantId); const role = roles.find(r => r.id === e.roleId); return <option key={e.id} value={e.id}>{e.name}{role ? ` — ${role.name}` : ""} ({rest?.name ?? "?"}){e.cpf ? ` (${e.cpf})` : ""}</option>; })}
                  </select>
                </div>
              );
            })()}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div><label style={S.label}>Nome completo</label><input value={mgrForm.name} onChange={e=>setMgrForm({...mgrForm,name:e.target.value})} style={S.input}/></div>
              <div><label style={S.label}>CPF *</label><input value={mgrForm.cpf} onChange={e=>setMgrForm({...mgrForm,cpf:maskCpf(e.target.value)})} placeholder="000.000.000-00" style={S.input} inputMode="numeric"/></div>
            </div>
            {editMgrId ? (
              <div><label style={S.label}>PIN (4 dígitos)</label><input type="password" value={mgrForm.pin} onChange={e=>setMgrForm({...mgrForm,pin:e.target.value.replace(/\D/g,"").slice(0,4)})} maxLength={4} style={S.input} inputMode="numeric"/></div>
            ) : (
              <div style={{background:"var(--ac-bg)",border:"1px solid var(--ac)33",borderRadius:10,padding:"10px 14px"}}>
                <span style={{color:"var(--ac-text)",fontSize:13}}>🔐 PIN inicial = 4 primeiros dígitos do CPF. No primeiro acesso o gestor será solicitado a trocar.</span>
              </div>
            )}

            <div>
              <label style={S.label}>Permissões de acesso às abas</label>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                {[["tips","💸 Gorjetas"],["schedule","📅 Escala"],["roles","🏷️ Cargos"],["employees","👥 Equipe"],["comunicados","📢 Comunicados"],["faq","❓ FAQ"],["dp","💬 Fale c/ DP"],["horarios","🕐 Horários"],["vt","🚌 Vale Transporte"]].map(([k,lbl])=>{
                  const on = mgrForm.perms?.[k] !== false;
                  return (
                    <button key={k} onClick={()=>setMgrForm({...mgrForm,perms:{...mgrForm.perms,[k]:!on}})}
                      style={{padding:"10px",borderRadius:10,border:`1px solid ${on?"var(--green)":"var(--border)"}`,background:on?"#10b98122":"transparent",color:on?"var(--green)":"#555",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12,textAlign:"left"}}>
                      {on?"✓":"✗"} {lbl}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label style={S.label}>Restaurantes com acesso</label>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {restaurants.length===0&&<p style={{color:"var(--text3)",fontSize:13}}>Nenhum restaurante cadastrado ainda.</p>}
                {restaurants.map(r=>{
                  const sel = mgrForm.restaurantIds?.includes(r.id);
                  return (
                    <button key={r.id} onClick={()=>setMgrForm({...mgrForm,restaurantIds:sel?mgrForm.restaurantIds.filter(x=>x!==r.id):[...(mgrForm.restaurantIds??[]),r.id]})}
                      style={{padding:"10px 14px",borderRadius:10,border:`1px solid ${sel?"var(--ac)":"var(--border)"}`,background:sel?"var(--ac)22":"transparent",color:sel?"var(--ac)":"#555",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:13,textAlign:"left"}}>
                      {sel?"✓":"○"} {r.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{borderTop:"1px solid var(--border)",paddingTop:12}}>
              <label style={S.label}>Fale com DP</label>
              <button onClick={()=>setMgrForm({...mgrForm,isDP:!mgrForm.isDP})}
                style={{width:"100%",padding:"12px 14px",borderRadius:10,border:`1px solid ${mgrForm.isDP?"#3b82f6":"var(--border)"}`,background:mgrForm.isDP?"#3b82f622":"transparent",color:mgrForm.isDP?"#3b82f6":"#555",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:13,textAlign:"left",display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontSize:18}}>📬</span>
                <div>
                  <div style={{fontWeight:700}}>{mgrForm.isDP?"✓ Recebe mensagens do Fale com DP":"○ Não recebe mensagens do Fale com DP"}</div>
                  <div style={{fontSize:11,opacity:0.7,marginTop:2}}>Recebe notificações de horários, mensagens do Fale com DP e avisos internos</div>
                </div>
              </button>
            </div>

            {/* Master toggle — por restaurante */}
            {mgrForm.profile === "dp" && (mgrForm.restaurantIds??[]).length > 0 && (
              <div style={{borderTop:"1px solid var(--border)",paddingTop:12}}>
                <label style={{display:"block",color:"var(--text)",fontWeight:700,fontSize:13,marginBottom:8}}>🔑 Master por restaurante</label>
                <p style={{color:"var(--text3)",fontSize:11,margin:"0 0 8px"}}>Pode criar, editar e excluir outros gestores nos restaurantes selecionados.</p>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {(mgrForm.restaurantIds??[]).map(rId => {
                    const rest = restaurants.find(r=>r.id===rId);
                    const on = (mgrForm.masterRestaurantIds??[]).includes(rId);
                    return <button key={rId} onClick={()=>{const ids=mgrForm.masterRestaurantIds??[];setMgrForm({...mgrForm,masterRestaurantIds:on?ids.filter(x=>x!==rId):[...ids,rId]});}}
                      style={{padding:"10px 14px",borderRadius:10,border:`1px solid ${on?"#d4a017":"var(--border)"}`,background:on?"#d4a01715":"transparent",color:on?"#d4a017":"var(--text3)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,textAlign:"left"}}>
                      {on?"🔑 ":"○ "}{rest?.name ?? rId}
                    </button>;
                  })}
                </div>
              </div>
            )}

            <button onClick={saveMgr} style={S.btnPrimary}>{editMgrId?"Salvar":"Criar Gestor"}</button>
          </div>
        </Modal>
      )}

      {showOwnerModal && (
        <Modal title={editOwnerId?"Editar Master":"Novo Master"} onClose={()=>setShowOwnerModal(false)}>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div><label style={S.label}>Nome completo</label><input value={ownerForm.name} onChange={e=>setOwnerForm({...ownerForm,name:e.target.value})} style={S.input}/></div>
            <div><label style={S.label}>CPF</label><input value={ownerForm.cpf} onChange={e=>setOwnerForm({...ownerForm,cpf:maskCpf(e.target.value)})} placeholder="000.000.000-00" style={S.input} inputMode="numeric"/></div>
            <div><label style={S.label}>PIN (4 dígitos)</label><input type="password" value={ownerForm.pin} onChange={e=>setOwnerForm({...ownerForm,pin:e.target.value})} maxLength={4} style={S.input}/></div>
            {/* Só o master pode designar outro master */}
            {isMaster && !owners.find(o=>o.id===editOwnerId)?.isMaster && (
              <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",padding:"12px 14px",borderRadius:10,border:`1px solid ${ownerForm.isMaster?"var(--ac)":"var(--border)"}`,background:ownerForm.isMaster?"var(--ac-bg)":"transparent"}}>
                <input type="checkbox" checked={!!ownerForm.isMaster} onChange={e=>setOwnerForm({...ownerForm,isMaster:e.target.checked})} style={{width:16,height:16,accentColor:ac}}/>
                <div>
                  <div style={{color:"var(--text)",fontWeight:600,fontSize:14}}>👑 Gestor Master</div>
                  <div style={{color:"var(--text3)",fontSize:12}}>Pode ver lixeira e não pode ser excluído</div>
                </div>
              </label>
            )}
            {owners.find(o=>o.id===editOwnerId)?.isMaster && (
              <div style={{padding:"10px 14px",borderRadius:10,background:"var(--ac-bg)",border:"1px solid var(--ac)33"}}>
                <span style={{color:"var(--ac-text)",fontSize:13,fontWeight:600}}>👑 Este admin é Master — proteção não pode ser removida</span>
              </div>
            )}
            <button onClick={saveOwner} style={S.btnPrimary}>{editOwnerId?"Salvar":"Criar"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

//
// MANAGER PORTAL (regular manager, single or multi restaurant)
//
function ManagerPortal({ manager, data, onUpdate, onBack, toggleTheme, theme, onSwitchToEmployee }) {
  const { restaurants, employees, roles, tips, splits, schedules } = data;
  const myRestaurants = restaurants.filter(r => manager.restaurantIds?.includes(r.id));
  const [selId, setSelId] = useState(() => {
    const saved = localStorage.getItem("apptip_selrest");
    if (myRestaurants.length === 1) return myRestaurants[0].id;
    if (saved && myRestaurants.find(r => r.id === saved)) return saved;
    return null;
  });
  const isMobile = useMobile();

  useEffect(() => {
    if (selId) localStorage.setItem("apptip_selrest", selId);
    else localStorage.removeItem("apptip_selrest");
  }, [selId]);

  const selRest = myRestaurants.find(r => r.id === selId);

  // Métricas rápidas por restaurante para o card de seleção
  function getRestMetrics(r) {
    const rid = r.id;
    const empAtivos = employees.filter(e => e.restaurantId === rid && !e.inactive).length;
    const now = new Date();
    const mk = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`;
    const monthTips = tips.filter(t => t.restaurantId === rid && t.monthKey === mk);
    const diasComGorjeta = [...new Set(monthTips.map(t => t.date))].length;
    const dpNaoLidas = (data?.dpMessages??[]).filter(m => m.restaurantId === rid && !m.read && !m.deleted).length;
    const commsPendentes = (data?.communications??[]).filter(c =>
      c.restaurantId === rid && !c.deleted &&
      employees.filter(e => e.restaurantId === rid).some(e => !(data?.commAcks??{})[`${c.id}_${e.id}`])
    ).length;
    return { empAtivos, diasComGorjeta, dpNaoLidas, commsPendentes };
  }

  return (
    <div style={{ minHeight:"100vh", background:"var(--bg)", fontFamily:"'DM Sans',sans-serif" }}>

      {/* Header */}
      <div style={{ background:"var(--header-bg)", borderBottom:"1px solid var(--border)", padding:"10px 16px", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:"6px 12px", boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, minWidth:0, flex:"1 1 auto" }}>
          {selId && myRestaurants.length > 1 && (
            <button onClick={()=>setSelId(null)}
              style={{ background:"none", border:"1px solid var(--border)", borderRadius:8, padding:"4px 8px", cursor:"pointer", color:"var(--text3)", fontSize:11, flexShrink:0 }}>
              ‹
            </button>
          )}
          <div style={{minWidth:0}}>
            <div style={{ display:"flex", alignItems:"center", gap:5 }}>
              <span style={{fontSize:14}}>📊</span>
              <span style={{color:"var(--text)",fontWeight:800,fontSize:14,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                {selRest ? selRest.name : "Gestor Adm."}
              </span>
            </div>
            <div style={{color:"var(--text3)",fontSize:10,marginTop:1}}>
              {manager.name}
              {selRest?.cnpj && <span style={{marginLeft:6,color:"var(--text3)",fontSize:10}}>{selRest.cnpj}</span>}
            </div>
          </div>
        </div>
        <div style={{display:"flex",gap:5,alignItems:"center",flexShrink:0}}>
          <button onClick={toggleTheme} style={{background:"none",border:"1px solid var(--border)",borderRadius:20,padding:"5px 8px",cursor:"pointer",fontSize:13,color:"var(--text2)"}}>
            {theme==="dark"?"☀️":"🌙"}
          </button>
          <a href="/guia-gestor" target="_blank" rel="noreferrer"
            style={{...S.btnSecondary,fontSize:11,textDecoration:"none",display:"flex",alignItems:"center",gap:3,padding:"5px 10px"}}>
            ❓ Ajuda
          </a>
          {onSwitchToEmployee && <button onClick={onSwitchToEmployee} style={{...S.btnSecondary,fontSize:11,padding:"5px 10px",color:"var(--ac)",borderColor:"var(--ac)"}}>👤 Empregado</button>}
          <button onClick={onBack} style={{...S.btnSecondary,fontSize:11,padding:"5px 10px"}}>Sair</button>
        </div>
      </div>

      {/* Seleção de restaurante */}
      {!selId && (
        <div style={{padding:"32px 20px",maxWidth:520,margin:"0 auto"}}>
          {myRestaurants.length === 0 ? (
            <div style={{textAlign:"center",padding:"60px 24px"}}>
              <div style={{fontSize:48,marginBottom:16}}>🏢</div>
              <h3 style={{color:"var(--text)",fontSize:18,fontWeight:700,margin:"0 0 8px"}}>Nenhum restaurante atribuído</h3>
              <p style={{color:"var(--text3)",fontSize:14,lineHeight:1.6}}>Seu acesso ainda não foi configurado.<br/>Entre em contato com o Master.</p>
            </div>
          ) : (
            <>
              <h2 style={{color:"var(--text)",fontSize:18,fontWeight:800,margin:"0 0 6px"}}>Seus restaurantes</h2>
              <p style={{color:"var(--text3)",fontSize:13,margin:"0 0 20px"}}>Selecione para entrar</p>
              {myRestaurants.map(r => {
                const inad = r.financeiro?.status === "inadimplente";
                const m = getRestMetrics(r);
                return (
                  <button key={r.id} onClick={()=>setSelId(r.id)}
                    style={{width:"100%",cursor:"pointer",textAlign:"left",display:"block",marginBottom:12,
                      background: inad ? "var(--red-bg)" : "var(--card-bg)",
                      border: `1px solid ${inad?"var(--red)44":"var(--border)"}`,
                      borderRadius:14, padding:"16px 18px",
                      boxShadow:"0 2px 8px rgba(0,0,0,0.04)", transition:"box-shadow 0.15s"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                      <div>
                        <div style={{color:"var(--text)",fontWeight:700,fontSize:16}}>{r.name}</div>
                        {r.address && <div style={{color:"var(--text3)",fontSize:12,marginTop:2}}>{r.address}</div>}
                      </div>
                      {inad
                        ? <span style={{color:"var(--red)",fontSize:12,fontWeight:700,background:"var(--red-bg)",padding:"3px 10px",borderRadius:20,border:"1px solid var(--red)33"}}>🔒 Suspenso</span>
                        : <span style={{color:"var(--text3)",fontSize:18}}>›</span>
                      }
                    </div>
                    {!inad && (
                      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                        <span style={{fontSize:12,color:"var(--text3)",background:"var(--bg2)",padding:"3px 10px",borderRadius:20}}>👥 {m.empAtivos} ativos</span>
                        <span style={{fontSize:12,color:"var(--text3)",background:"var(--bg2)",padding:"3px 10px",borderRadius:20}}>💸 {m.diasComGorjeta}d lançados</span>
                        {m.dpNaoLidas > 0 && <span style={{fontSize:12,color:"#3b82f6",background:"#eff6ff",padding:"3px 10px",borderRadius:20}}>💬 {m.dpNaoLidas} nova{m.dpNaoLidas>1?"s":""}</span>}
                        {m.commsPendentes > 0 && <span style={{fontSize:12,color:"#f59e0b",background:"#fffbeb",padding:"3px 10px",borderRadius:20}}>📢 {m.commsPendentes} pendente{m.commsPendentes>1?"s":""}</span>}
                      </div>
                    )}
                  </button>
                );
              })}
            </>
          )}
        </div>
      )}

      {selId && selRest && (
        <div>
          {/* Bloqueia restaurante inadimplente */}
          {selRest.financeiro?.status === "inadimplente" ? (
            <div style={{minHeight:"60vh",display:"flex",alignItems:"center",justifyContent:"center",padding:32}}>
              <div style={{maxWidth:400,textAlign:"center"}}>
                <div style={{fontSize:56,marginBottom:20}}>🔒</div>
                <h2 style={{color:"var(--text)",fontSize:22,fontWeight:800,margin:"0 0 12px"}}>Acesso suspenso</h2>
                <p style={{color:"var(--text3)",fontSize:15,lineHeight:1.6,margin:"0 0 20px"}}>
                  O acesso ao <strong>{selRest.name}</strong> está temporariamente suspenso por pendência financeira.
                </p>
                <div style={{marginTop:24,padding:"14px 18px",borderRadius:12,background:"var(--bg2)",border:"1px solid var(--border)",fontSize:13,color:"var(--text3)"}}>
                  📱 WhatsApp: <strong style={{color:"var(--text)"}}>+55 11 98549-9821</strong>
                </div>
                {myRestaurants.filter(r=>r.financeiro?.status!=="inadimplente").length > 0 && (
                  <button onClick={()=>setSelId(null)} style={{...S.btnSecondary,marginTop:16,width:"100%",textAlign:"center"}}>
                    ← Ver outros restaurantes
                  </button>
                )}
              </div>
            </div>
          ) : isMobile ? (
            <div>
              <div style={{margin:"16px 16px 0",padding:"14px 16px",borderRadius:12,background:"#fffbeb",border:"1px solid #f59e0b44",display:"flex",alignItems:"center",gap:12}}>
                <span style={{fontSize:22,flexShrink:0}}>🖥️</span>
                <div>
                  <p style={{color:"#92400e",fontSize:12,fontWeight:700,margin:0}}>Demais funcionalidades no computador</p>
                  <p style={{color:"#92400e99",fontSize:11,margin:"2px 0 0",lineHeight:1.4}}>Gorjetas, Cargos, VT, Comunicados, FAQ, DP e Config.</p>
                </div>
              </div>
              <RestaurantPanel
                restaurant={selRest} restaurants={restaurants} employees={employees}
                roles={roles} tips={tips} splits={splits} schedules={schedules}
                onUpdate={onUpdate}
                perms={{...(manager.perms ?? {tips:true,schedule:true}), isDP: manager.isDP ?? false, managerAreas: manager.profile==="lider"?(manager.areas??[]):[] }}
                isOwner={false} data={data} currentUser={manager} mobileOnly
              />
            </div>
          ) : (
            <RestaurantPanel
              restaurant={selRest} restaurants={restaurants} employees={employees}
              roles={roles} tips={tips} splits={splits} schedules={schedules}
              onUpdate={onUpdate}
              perms={{...(manager.perms ?? {tips:true,schedule:true}), isDP: manager.isDP ?? false, managerAreas: manager.profile==="lider"?(manager.areas??[]):[] }}
              isOwner={false} data={data} currentUser={manager}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ──  OPERATIONAL PORTAL (Gestor Operacional)                   ──
// ═══════════════════════════════════════════════════════════════
//
// Um empregado com employee.operationalAreas não-vazio pode alternar
// para o Portal do Gestor Operacional, onde vê abas apenas das áreas
// em que operationalAreas[area] === true.
//
// Áreas: escalas, gorjetas, trilhas, reunioes 
//        contagens, checklists, fichasTecnicas (Operacional)
//
const OPERATIONAL_AREA_DEFS = [
  { key: "escalas",        label: "Escalas",         icon: "📅", module: "tip"  },
  { key: "gorjetas",       label: "Gorjetas",        icon: "💰", module: "tip"  },
  { key: "trilhas",        label: "Trilhas",         icon: "🎯", module: "tip"  },
  { key: "reunioes",       label: "Reuniões",        icon: "🗣️", module: "tip"  },
  { key: "contagens",      label: "Contagens",       icon: "📦", module: "mise" },
  { key: "compras",        label: "Compras",         icon: "🛒", module: "mise" },
  { key: "checklists",     label: "Checklists",      icon: "✅", module: "mise" },
  { key: "fichasTecnicas", label: "Fichas Técnicas", icon: "📋", module: "mise" },
  { key: "temperaturas",   label: "Temperaturas",    icon: "🌡️", module: "mise" },
];

function hasAnyOperationalArea(emp) {
  const a = emp?.operationalAreas;
  if (!a) return false;
  return OPERATIONAL_AREA_DEFS.some(def => a[def.key] === true);
}

function OperationalPortal({ employee, data, onUpdate, onBack, toggleTheme, theme, onSwitchToEmployee, onReturnToOwner }) {
  const { restaurants } = data;
  const restaurant = restaurants.find(r => r.id === employee.restaurantId);
  const areas = employee.operationalAreas ?? {};
  const activeAreas = OPERATIONAL_AREA_DEFS.filter(a => areas[a.key] === true);
  const [tab, setTab] = useState(() => activeAreas[0]?.key ?? null);
  // v7: opt-in para preview do shell unificado. Não persistido — só nesta sessão.
  // OBS: as tabs Mise no shell unificado renderizam as versões ADMIN (CRUD completo)
  // e não as lean operacionais — full migration fica pra v7.1.
  const [unifiedV7Mode, setUnifiedV7Mode] = useState(false);

  // Se a permissão da aba atual foi revogada ao vivo, realinha
  useEffect(() => {
    if (tab && !activeAreas.find(a => a.key === tab)) {
      setTab(activeAreas[0]?.key ?? null);
    }
  }, [tab, activeAreas.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // v7: render do modo unificado (após todos os hooks pra não violar rules-of-hooks)
  if (unifiedV7Mode && restaurant) {
    const synthCurrentUser = { id: employee.id, name: employee.name, cpf: employee.cpf || "" };
    return (
      <div>
        <div style={{background:"#7c9e5e15",borderBottom:"1px solid #7c9e5e44",padding:"8px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:12,color:"#7c9e5e",fontFamily:"'DM Sans',sans-serif"}}>
          <span><strong>🧪 Modo unificado v7 (preview)</strong> — Operação + Planejamento. Escritório e Ajustes ocultos.</span>
          <button onClick={()=>setUnifiedV7Mode(false)} style={{background:"none",border:"1px solid #7c9e5e88",borderRadius:8,padding:"3px 10px",cursor:"pointer",color:"#7c9e5e",fontSize:11,fontFamily:"inherit"}}>← Voltar ao modo Operacional</button>
        </div>
        <RestaurantPanel
          restaurant={restaurant}
          restaurants={restaurants}
          employees={data?.employees ?? []}
          roles={data?.roles ?? []}
          tips={data?.tips ?? {}}
          splits={data?.splits ?? {}}
          schedules={data?.schedules ?? {}}
          onUpdate={onUpdate}
          perms={{ tips:false, schedule:false, vt:false }}
          isOwner={false}
          data={data}
          currentUser={synthCurrentUser}
          privacyMask={false}
          mobileOnly={false}
          restrictToOperational
        />
      </div>
    );
  }

  if (activeAreas.length === 0) {
    return (
      <div style={{minHeight:"100vh",background:"var(--bg)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Sans',sans-serif",padding:24}}>
        <div style={{maxWidth:420,textAlign:"center"}}>
          <div style={{fontSize:48,marginBottom:16}}>🔒</div>
          <h2 style={{color:"var(--text)",fontSize:20,fontWeight:800,margin:"0 0 8px"}}>Sem áreas concedidas</h2>
          <p style={{color:"var(--text3)",fontSize:14,lineHeight:1.6,margin:"0 0 20px"}}>
            O gestor administrativo ainda não concedeu nenhuma área para você no Portal do Gestor Operacional.
          </p>
          <button onClick={onSwitchToEmployee ?? onBack} style={S.btnPrimary}>Voltar ao Portal</button>
        </div>
      </div>
    );
  }

  const activeArea = OPERATIONAL_AREA_DEFS.find(a => a.key === tab);
  const miseColor = "#7c9e5e";

  return (
    <div style={{minHeight:"100vh",background:"var(--bg)",fontFamily:"'DM Sans',sans-serif"}}>
      {/* Header */}
      <div style={{background:"var(--header-bg)",borderBottom:"1px solid var(--border)",padding:"10px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:"6px 12px",boxShadow:"0 1px 4px rgba(0,0,0,0.04)"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0,flex:"1 1 auto"}}>
          <div style={{minWidth:0}}>
            <div style={{display:"flex",alignItems:"center",gap:5}}>
              <span style={{fontSize:14}}>⚙️</span>
              <span style={{color:"var(--text)",fontWeight:800,fontSize:14,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                {restaurant?.name ?? "Gestor Operacional"}
              </span>
            </div>
            <div style={{color:"var(--text3)",fontSize:10,marginTop:1}}>
              {employee.name} • Gestor Operacional
            </div>
          </div>
        </div>
        <div style={{display:"flex",gap:5,alignItems:"center",flexShrink:0}}>
          <button onClick={toggleTheme} style={{background:"none",border:"1px solid var(--border)",borderRadius:20,padding:"5px 8px",cursor:"pointer",fontSize:13,color:"var(--text2)"}}>
            {theme==="dark"?"☀️":"🌙"}
          </button>
          {employee?._isOwnerVirtual && onReturnToOwner && (
            <button onClick={onReturnToOwner} title="Voltar ao Portal Super (Master)"
              style={{...S.btnSecondary,fontSize:11,padding:"5px 10px",color:"#a855f7",borderColor:"#a855f7"}}>
              ← Super
            </button>
          )}
          <button onClick={()=>setUnifiedV7Mode(true)} title="Preview do shell unificado v7 (Operação + Planejamento)"
            style={{...S.btnSecondary,fontSize:11,padding:"5px 10px",color:"#7c9e5e",borderColor:"#7c9e5e"}}>
            🧪 v7
          </button>
          {onSwitchToEmployee && !employee?._isOwnerVirtual && <button onClick={onSwitchToEmployee} style={{...S.btnSecondary,fontSize:11,padding:"5px 10px",color:"var(--ac)",borderColor:"var(--ac)"}}>👤 Empregado</button>}
          <button onClick={onBack} style={{...S.btnSecondary,fontSize:11,padding:"5px 10px"}}>Sair</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{background:"var(--card-bg)",borderBottom:"1px solid var(--border)",padding:"0 16px",display:"flex",gap:4,overflowX:"auto"}}>
        {activeAreas.map(a => {
          const isActive = tab === a.key;
          const accent = a.module === "mise" ? miseColor : "var(--ac)";
          return (
            <button key={a.key} onClick={()=>setTab(a.key)}
              style={{
                background:"none",
                border:"none",
                borderBottom: isActive ? `2px solid ${accent}` : "2px solid transparent",
                padding:"10px 14px",
                cursor:"pointer",
                fontSize:13,
                fontWeight: isActive ? 700 : 500,
                color: isActive ? "var(--text)" : "var(--text3)",
                whiteSpace:"nowrap",
                flexShrink:0,
              }}>
              {a.icon} {a.label}
            </button>
          );
        })}
      </div>

      {/* Body */}
      <div style={{padding:"24px 20px",maxWidth:1040,margin:"0 auto"}}>
        {tab === "contagens" ? (
          <OperationalContagens
            employee={employee}
            pessoas={data?.pessoas ?? []}
            isOwner={(() => {
              const empCpf = (employee?.cpf || "").replace(/\D/g, "");
              if (!empCpf) return false;
              return ((data?.owners) || []).some(o => (o.cpf || "").replace(/\D/g, "") === empCpf);
            })()}
            miseCategories={data?.miseCategories ?? []}
            miseStocks={data?.miseStocks ?? []}
            miseAssignments={data?.miseAssignments ?? []}
            miseItems={data?.miseItems ?? []}
            miseCounts={data?.miseCounts ?? []}
            onUpdate={onUpdate}
          />
        ) : tab === "fichasTecnicas" ? (
          <OperationalFichasTecnicas
            employee={employee}
            miseFtInsumos={data?.miseFtInsumos ?? []}
            miseFtDishes={data?.miseFtDishes ?? []}
          />
        ) : tab === "compras" ? (
          <OperationalCompras
            employee={employee}
            data={data}
            onUpdate={onUpdate}
          />
        ) : tab === "checklists" ? (
          <OperationalChecklists
            employee={employee}
            pessoas={data?.pessoas ?? []}
            isOwner={(() => {
              const empCpf = (employee?.cpf || "").replace(/\D/g, "");
              if (!empCpf) return false;
              return ((data?.owners) || []).some(o => (o.cpf || "").replace(/\D/g, "") === empCpf);
            })()}
            miseChecklistTemplates={data?.miseChecklistTemplates ?? []}
            miseChecklistRuns={data?.miseChecklistRuns ?? []}
            onUpdate={onUpdate}
          />
        ) : tab === "gorjetas" ? (
          <OperationalGorjetas
            employee={employee}
            data={data}
            onUpdate={onUpdate}
            isOwner={false}
          />
        ) : activeArea?.module === "tip" ? (
          <div style={{textAlign:"center",padding:"50px 24px",color:"var(--text3)"}}>
            <div style={{fontSize:48,marginBottom:16}}>{activeArea?.icon}</div>
            <h3 style={{color:"var(--text)",fontSize:18,fontWeight:700,margin:"0 0 8px"}}>{activeArea?.label}</h3>
            <p style={{fontSize:14,lineHeight:1.6,maxWidth:460,margin:"0 auto 20px"}}>
              Esta área do sistema é acessível hoje pelo <b>Portal do Gestor Administrativo</b>. Use o botão <span style={{fontFamily:"monospace",background:"var(--bg2)",padding:"2px 6px",borderRadius:4,color:"var(--ac)"}}>📊</span> no cabeçalho acima para alternar — se você tiver também esse perfil.
            </p>
            <div style={{fontSize:12,color:"var(--text3)",background:"#fffbeb",border:"1px solid #f59e0b44",borderRadius:10,padding:"10px 16px",maxWidth:460,margin:"0 auto"}}>
              💡 <b>Em evolução:</b> uma visão operacional dedicada (diferente do Gestor Adm) chega em iteração futura. Por enquanto, quem tem a permissão <b>{activeArea?.label}</b> no Gestor Operacional vê este aviso.
            </div>
          </div>
        ) : (
          <div style={{textAlign:"center",padding:"60px 24px",color:"var(--text3)"}}>
            <div style={{fontSize:48,marginBottom:16}}>{activeArea?.icon}</div>
            <h3 style={{color:"var(--text)",fontSize:18,fontWeight:700,margin:"0 0 8px"}}>{activeArea?.label}</h3>
            <p style={{fontSize:14,lineHeight:1.6,maxWidth:420,margin:"0 auto"}}>
              Em construção. Esta área do {activeArea?.module === "mise" ? "Operacional" : "Sistema"} será habilitada nos próximos passos da implantação.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ──  MISE — Helpers de importação/exportação XLSX (Produtos)   ──
// ═══════════════════════════════════════════════════════════════
// Modelo de planilha: 5 abas (Produtos, Fornecedores, Estoques, Categorias, Instruções).
// Importação faz merge por nome (case-insensitive) com normalização ftNrm.
// Índice dia da semana: 0=Dom, 1=Seg, 2=Ter, 3=Qua, 4=Qui, 5=Sex, 6=Sáb (compatível com new Date().getDay()).

const MISE_DAYS = [
  { idx: 1, label: "Seg" },
  { idx: 2, label: "Ter" },
  { idx: 3, label: "Qua" },
  { idx: 4, label: "Qui" },
  { idx: 5, label: "Sex" },
  { idx: 6, label: "Sáb" },
  { idx: 0, label: "Dom" },
];

function miseBuildModeloXLSX({ items = [], suppliers = [], stocks = [], categories = [], productSuppliers = [] }) {
  const XLSX = window.XLSX;
  if (!XLSX) throw new Error("Biblioteca XLSX ainda não foi carregada.");
  const wb = XLSX.utils.book_new();

  const catById   = Object.fromEntries(categories.map(c => [c.id, c.name]));
  const stockById = Object.fromEntries(stocks.map(s => [s.id, s.name]));
  const supById   = Object.fromEntries(suppliers.map(s => [s.id, s.name]));

  // ── Aba 1: Produtos ──
  const headerProd = [
    "Produto", "Categoria", "Estoques físicos",
    "Fornec. preferencial", "Fornec. alternativos",
    "Unidade", "Pacote",
    ...MISE_DAYS.map(d => `Ideal ${d.label}`),
  ];
  const prodRows = [headerProd];
  items.forEach(it => {
    const catName = it.categoryId ? (catById[it.categoryId] || "") : "";
    const stockNames = (it.stockIds || []).map(sid => stockById[sid]).filter(Boolean).join("; ");
    let prefName = "", altsName = "";
    if (Array.isArray(it.suppliers) && it.suppliers.length) {
      const pref = it.suppliers.find(s => s.preferred);
      const alts = it.suppliers.filter(s => !s.preferred);
      prefName = pref ? (supById[pref.supplierId] || "") : "";
      altsName = alts.map(s => supById[s.supplierId]).filter(Boolean).join("; ");
    } else {
      const ps = productSuppliers.filter(p => p.productId === it.id);
      const pref = ps.find(p => p.preferred);
      const alts = ps.filter(p => !p.preferred);
      prefName = pref ? (supById[pref.supplierId] || "") : "";
      altsName = alts.map(p => supById[p.supplierId]).filter(Boolean).join("; ");
    }
    const ideal = it.idealQtys || {};
    prodRows.push([
      it.name || "",
      catName,
      stockNames,
      prefName,
      altsName,
      it.unit || "un",
      (it.pkgSize ?? "") === null ? "" : (it.pkgSize ?? ""),
      ...MISE_DAYS.map(d => (ideal[d.idx] ?? "")),
    ]);
  });
  if (items.length === 0) {
    // Linha exemplo só pra ilustrar
    prodRows.push([
      "Cerveja Heineken 600ml", "Bebidas", "Bar; Adega",
      "Distribuidora ABC", "Distribuidora XYZ",
      "un", 12,
      24, 24, 24, 36, 48, 60, 36,
    ]);
  }
  const wsProd = XLSX.utils.aoa_to_sheet(prodRows);
  wsProd['!cols'] = [
    { wch: 32 }, { wch: 18 }, { wch: 22 },
    { wch: 24 }, { wch: 30 },
    { wch: 10 }, { wch: 8 },
    { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 },
  ];
  XLSX.utils.book_append_sheet(wb, wsProd, "Produtos");

  // ── Aba 2: Fornecedores ──
  const supRows = [["Nome", "Contato", "WhatsApp", "Observações"]];
  suppliers.forEach(s => supRows.push([
    s.name || "",
    s.nomeContato || "",
    s.whatsapp || "",
    s.notes || s.observacoes || "",
  ]));
  if (suppliers.length === 0) {
    supRows.push(["Distribuidora ABC", "João da Silva", "11912345678", "Entrega às terças e sextas"]);
  }
  const wsSup = XLSX.utils.aoa_to_sheet(supRows);
  wsSup['!cols'] = [{ wch: 28 }, { wch: 22 }, { wch: 18 }, { wch: 38 }];
  XLSX.utils.book_append_sheet(wb, wsSup, "Fornecedores");

  // ── Aba 3: Estoques ──
  const stockRows = [["Nome"]];
  stocks.forEach(s => stockRows.push([s.name || ""]));
  if (stocks.length === 0) stockRows.push(["Bar"], ["Cozinha"], ["Adega"]);
  const wsStock = XLSX.utils.aoa_to_sheet(stockRows);
  wsStock['!cols'] = [{ wch: 24 }];
  XLSX.utils.book_append_sheet(wb, wsStock, "Estoques");

  // ── Aba 4: Categorias ──
  const catRows = [["Nome"]];
  categories.forEach(c => catRows.push([c.name || ""]));
  if (categories.length === 0) catRows.push(["Bebidas"], ["Cozinha"], ["Limpeza"]);
  const wsCat = XLSX.utils.aoa_to_sheet(catRows);
  wsCat['!cols'] = [{ wch: 24 }];
  XLSX.utils.book_append_sheet(wb, wsCat, "Categorias");

  // ── Aba 5: Instruções ──
  const instRows = [
    ["Modelo de Importação de Produtos"],
    [""],
    ["Como usar:"],
    ["1. Preencha a aba Fornecedores PRIMEIRO (nome obrigatório). Os fornecedores precisam existir antes dos produtos referenciá-los."],
    ["2. Preencha a aba Estoques (nomes dos estoques físicos: Bar, Cozinha, Adega, etc.)."],
    ["3. Preencha a aba Categorias (Bebidas, Cozinha, Limpeza...). Categoria é opcional na aba Produtos — pode categorizar depois em massa."],
    ["4. Preencha a aba Produtos."],
    [""],
    ["Coluna a coluna (Produtos):"],
    ["• Produto: nome único do item (ex: Cerveja Heineken 600ml)"],
    ["• Categoria: nome de uma categoria já criada na aba Categorias. Pode deixar em branco e categorizar depois."],
    ["• Estoques físicos: nome(s) dos estoques onde o produto pode estar. Use ; (ponto-e-vírgula) pra múltiplos. Ex: Bar; Adega"],
    ["• Fornec. preferencial: nome de um fornecedor da aba Fornecedores. É de quem você compra normalmente."],
    ["• Fornec. alternativos: até 3 nomes separados por ; (caso o preferencial fique sem)."],
    ["• Unidade: un, kg, l, cx, fardo... como você conta o produto."],
    ["• Pacote: tamanho do pacote/caixa fechado(a) (ex: 12 se a caixa tem 12 unidades). O sistema arredonda o pedido pra cima em múltiplos do pacote. Deixe em branco se compra por unidade avulsa."],
    ["• Ideal Seg-Dom: quantidade que você quer ter em estoque ANTES daquele dia. Se na noite de domingo a contagem é 5 e o ideal de segunda é 12, o sistema sugere comprar 7 (arredondado pelo pacote)."],
    [""],
    ["Coluna a coluna (Fornecedores):"],
    ["• Nome: nome da empresa/distribuidora."],
    ["• Contato: nome da pessoa que atende. Usado na mensagem WhatsApp (\"Olá [contato]...\")."],
    ["• WhatsApp: só números, com DDD. Ex: 11912345678."],
    ["• Observações: texto livre (dias de entrega, mínimo do pedido, etc.)."],
    [""],
    ["Importante:"],
    ["• Importação faz merge: itens com o mesmo nome são ATUALIZADOS, novos são criados."],
    ["• Não apague abas. Não renomeie cabeçalhos."],
    ["• Você pode importar a planilha quantas vezes quiser — sempre faz merge inteligente."],
  ];
  const wsInst = XLSX.utils.aoa_to_sheet(instRows);
  wsInst['!cols'] = [{ wch: 100 }];
  XLSX.utils.book_append_sheet(wb, wsInst, "Instruções");

  return wb;
}

function miseDownloadModeloXLSX(ctx) {
  const XLSX = window.XLSX;
  if (!XLSX) { alert("Biblioteca XLSX ainda não foi carregada. Aguarde ou recarregue a página."); return; }
  const wb = miseBuildModeloXLSX(ctx);
  const safeName = ftSlugify(ctx.restaurantName || "restaurante");
  const today = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `apptip-produtos-${safeName}-${today}.xlsx`);
}

// Lê o XLSX e devolve o conteúdo bruto (linhas). Não faz commit ainda — o caller mostra preview.
// Retorna Promise<{ produtos:[], fornecedores:[], estoques:[], categorias:[], errors:[] }>.
function miseParsePlanilhaXLSX(file) {
  return new Promise((resolve, reject) => {
    const XLSX = window.XLSX;
    if (!XLSX) { reject(new Error("Biblioteca XLSX ainda não foi carregada.")); return; }
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const wb = XLSX.read(e.target.result, { type: "binary" });
        const errors = [];
        // Helpers
        const readSheet = (name) => {
          const ws = wb.Sheets[name];
          if (!ws) return null;
          const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
          return rows;
        };
        const norm = s => (s == null ? "" : String(s)).trim();
        const lower = s => norm(s).toLowerCase();
        const splitMulti = s => norm(s).split(/[;|]/).map(x => x.trim()).filter(Boolean);

        // ── Fornecedores ──
        const fornecedores = [];
        const supRows = readSheet("Fornecedores");
        if (supRows && supRows.length >= 2) {
          const header = (supRows[0] || []).map(lower);
          const idxName = header.findIndex(h => h.includes("nome"));
          const idxContato = header.findIndex(h => h.includes("contato"));
          const idxWhats = header.findIndex(h => h.includes("whats") || h.includes("telefone"));
          const idxObs = header.findIndex(h => h.includes("obs") || h.includes("nota"));
          if (idxName < 0) errors.push("Aba Fornecedores: coluna 'Nome' não encontrada.");
          else {
            for (let i = 1; i < supRows.length; i++) {
              const r = supRows[i] || [];
              const name = norm(r[idxName]);
              if (!name) continue;
              fornecedores.push({
                name,
                nomeContato: idxContato >= 0 ? norm(r[idxContato]) : "",
                whatsapp: idxWhats >= 0 ? norm(r[idxWhats]).replace(/\D/g, "") : "",
                notes: idxObs >= 0 ? norm(r[idxObs]) : "",
              });
            }
          }
        }

        // ── Estoques ──
        const estoques = [];
        const stockRows = readSheet("Estoques");
        if (stockRows && stockRows.length >= 2) {
          const header = (stockRows[0] || []).map(lower);
          const idxName = header.findIndex(h => h.includes("nome")) >= 0
            ? header.findIndex(h => h.includes("nome"))
            : 0;
          for (let i = 1; i < stockRows.length; i++) {
            const r = stockRows[i] || [];
            const name = norm(r[idxName]);
            if (name) estoques.push({ name });
          }
        }

        // ── Categorias ──
        const categorias = [];
        const catRows = readSheet("Categorias");
        if (catRows && catRows.length >= 2) {
          const header = (catRows[0] || []).map(lower);
          const idxName = header.findIndex(h => h.includes("nome")) >= 0
            ? header.findIndex(h => h.includes("nome"))
            : 0;
          for (let i = 1; i < catRows.length; i++) {
            const r = catRows[i] || [];
            const name = norm(r[idxName]);
            if (name) categorias.push({ name });
          }
        }

        // ── Produtos ──
        const produtos = [];
        const prodRows = readSheet("Produtos");
        if (prodRows && prodRows.length >= 2) {
          const header = (prodRows[0] || []).map(lower);
          const idx = (...keys) => header.findIndex(h => keys.some(k => h.includes(k)));
          const idxNome = idx("produto", "nome");
          const idxCat = idx("categoria");
          const idxStock = idx("estoque");
          const idxPref = idx("prefer");
          const idxAlt = idx("altern");
          const idxUnit = idx("unidade", "unid");
          const idxPkg = idx("pacote", "pkg");
          // Ideais — ordem MISE_DAYS (Seg..Sáb,Dom)
          const dayIdxMap = {};
          MISE_DAYS.forEach(d => {
            const lbl = d.label.toLowerCase();
            const found = header.findIndex(h =>
              h.startsWith("ideal") && (h.includes(lbl) || h.includes(lbl.replace("á","a")))
            );
            if (found >= 0) dayIdxMap[d.idx] = found;
          });
          if (idxNome < 0) errors.push("Aba Produtos: coluna 'Produto' não encontrada.");
          else {
            for (let i = 1; i < prodRows.length; i++) {
              const r = prodRows[i] || [];
              const name = norm(r[idxNome]);
              if (!name) continue;
              const idealQtys = {};
              for (const [d, ci] of Object.entries(dayIdxMap)) {
                const v = r[ci];
                if (v === "" || v == null) continue;
                const n = typeof v === "number" ? v : parseFloat(String(v).replace(",","."));
                if (!isNaN(n)) idealQtys[d] = n;
              }
              const pkgRaw = idxPkg >= 0 ? r[idxPkg] : "";
              const pkgNum = pkgRaw === "" || pkgRaw == null ? null
                : (typeof pkgRaw === "number" ? pkgRaw : parseFloat(String(pkgRaw).replace(",",".")));
              produtos.push({
                name,
                categoria: idxCat >= 0 ? norm(r[idxCat]) : "",
                estoques: idxStock >= 0 ? splitMulti(r[idxStock]) : [],
                fornecedorPreferencial: idxPref >= 0 ? norm(r[idxPref]) : "",
                fornecedoresAlternativos: idxAlt >= 0 ? splitMulti(r[idxAlt]) : [],
                unit: idxUnit >= 0 ? (norm(r[idxUnit]) || "un") : "un",
                pkgSize: pkgNum != null && !isNaN(pkgNum) ? pkgNum : null,
                idealQtys,
              });
            }
          }
        }

        resolve({ produtos, fornecedores, estoques, categorias, errors });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error("Falha ao ler o arquivo."));
    reader.readAsBinaryString(file);
  });
}

// Aplica os dados parseados: merge por nome (ftNrm). Retorna estatísticas pra exibir no preview.
// Não chama onUpdate — só monta os next-states e retorna pro caller.
function miseBuildImportPlan(parsed, ctx) {
  const { restaurantId, miseSuppliers, miseStocks, miseCategories, miseItems, miseProductSuppliers } = ctx;
  const restSup = (miseSuppliers || []).filter(s => s.restaurantId === restaurantId);
  const restStock = (miseStocks || []).filter(s => s.restaurantId === restaurantId);
  const restCat = (miseCategories || []).filter(c => c.restaurantId === restaurantId);
  const restItems = (miseItems || []).filter(i => i.restaurantId === restaurantId);

  // 1. Suppliers — merge por nome
  const suppliersNext = [...miseSuppliers];
  let supAdded = 0, supUpdated = 0;
  const supByName = {}; // nrm(name) → supplier in next
  restSup.forEach(s => { supByName[ftNrm(s.name)] = s; });
  parsed.fornecedores.forEach(p => {
    const key = ftNrm(p.name);
    const existing = supByName[key];
    if (existing) {
      const idx = suppliersNext.indexOf(existing);
      const merged = {
        ...existing,
        nomeContato: p.nomeContato || existing.nomeContato || "",
        whatsapp: p.whatsapp || existing.whatsapp || "",
        notes: p.notes || existing.notes || "",
      };
      suppliersNext[idx] = merged;
      supByName[key] = merged;
      // Conta como atualizado só se mudou alguma coisa
      if (
        merged.nomeContato !== existing.nomeContato ||
        merged.whatsapp !== existing.whatsapp ||
        merged.notes !== existing.notes
      ) supUpdated++;
    } else {
      const ns = {
        id: ftUid(),
        restaurantId,
        name: p.name,
        nomeContato: p.nomeContato || "",
        whatsapp: p.whatsapp || "",
        notes: p.notes || "",
      };
      suppliersNext.push(ns);
      supByName[key] = ns;
      supAdded++;
    }
  });

  // 2. Stocks — merge por nome
  const stocksNext = [...miseStocks];
  let stockAdded = 0;
  const stockByName = {};
  restStock.forEach(s => { stockByName[ftNrm(s.name)] = s; });
  parsed.estoques.forEach(p => {
    const key = ftNrm(p.name);
    if (!stockByName[key]) {
      const ns = { id: ftUid(), restaurantId, name: p.name, location: null };
      stocksNext.push(ns);
      stockByName[key] = ns;
      stockAdded++;
    }
  });

  // 3. Categorias — merge por nome
  const categoriesNext = [...miseCategories];
  let catAdded = 0;
  const catByName = {};
  restCat.forEach(c => { catByName[ftNrm(c.name)] = c; });
  parsed.categorias.forEach(p => {
    const key = ftNrm(p.name);
    if (!catByName[key]) {
      const nc = { id: ftUid(), restaurantId, name: p.name };
      categoriesNext.push(nc);
      catByName[key] = nc;
      catAdded++;
    }
  });
  // Auto-criar categorias mencionadas em Produtos mas ausentes na aba Categorias
  parsed.produtos.forEach(p => {
    if (!p.categoria) return;
    const key = ftNrm(p.categoria);
    if (!catByName[key]) {
      const nc = { id: ftUid(), restaurantId, name: p.categoria };
      categoriesNext.push(nc);
      catByName[key] = nc;
      catAdded++;
    }
  });
  // Auto-criar estoques mencionados em Produtos mas ausentes na aba Estoques
  parsed.produtos.forEach(p => {
    (p.estoques || []).forEach(stName => {
      const key = ftNrm(stName);
      if (!stockByName[key]) {
        const ns = { id: ftUid(), restaurantId, name: stName, location: null };
        stocksNext.push(ns);
        stockByName[key] = ns;
        stockAdded++;
      }
    });
  });

  // 4. Produtos — merge por nome (case-insensitive)
  const itemsNext = [...miseItems];
  let itemAdded = 0, itemUpdated = 0;
  const itemByName = {}; // só do restaurante atual
  restItems.forEach(i => { itemByName[ftNrm(i.name)] = i; });
  const warnings = [];
  parsed.produtos.forEach(p => {
    const key = ftNrm(p.name);
    const existing = itemByName[key];
    const catId = p.categoria ? (catByName[ftNrm(p.categoria)]?.id || null) : null;
    const stockIds = (p.estoques || [])
      .map(stName => stockByName[ftNrm(stName)]?.id)
      .filter(Boolean);
    // Suppliers do produto: preferencial + alternativos
    const supRefs = [];
    if (p.fornecedorPreferencial) {
      const sid = supByName[ftNrm(p.fornecedorPreferencial)]?.id;
      if (sid) supRefs.push({ supplierId: sid, preferred: true });
      else warnings.push(`Produto "${p.name}": fornecedor preferencial "${p.fornecedorPreferencial}" não encontrado.`);
    }
    (p.fornecedoresAlternativos || []).slice(0, 3).forEach(altName => {
      const sid = supByName[ftNrm(altName)]?.id;
      if (sid && !supRefs.some(r => r.supplierId === sid)) {
        supRefs.push({ supplierId: sid, preferred: false });
      } else if (!sid) {
        warnings.push(`Produto "${p.name}": fornecedor alternativo "${altName}" não encontrado.`);
      }
    });

    if (existing) {
      const idx = itemsNext.indexOf(existing);
      const merged = {
        ...existing,
        unit: p.unit || existing.unit || "un",
      };
      if (catId) merged.categoryId = catId;
      if (stockIds.length) merged.stockIds = Array.from(new Set([...(existing.stockIds || []), ...stockIds]));
      if (Object.keys(p.idealQtys).length) merged.idealQtys = { ...(existing.idealQtys || {}), ...p.idealQtys };
      if (p.pkgSize != null) merged.pkgSize = p.pkgSize;
      if (supRefs.length) merged.suppliers = supRefs;
      itemsNext[idx] = merged;
      itemByName[key] = merged;
      itemUpdated++;
    } else {
      const ni = {
        id: ftUid(),
        restaurantId,
        name: p.name,
        unit: p.unit || "un",
        categoryId: catId,
        stockIds,
        idealQtys: p.idealQtys,
        suppliers: supRefs,
      };
      if (p.pkgSize != null) ni.pkgSize = p.pkgSize;
      itemsNext.push(ni);
      itemByName[key] = ni;
      itemAdded++;
    }
  });

  // 5. v8.9 — miseProductSuppliers (tabela N:N que a UI lê)
  // Reconstrói baseado nos suppliers que cada produto referencia, mergendo com existing
  // (preserva entries de outros produtos não importados; substitui as do produto importado).
  const productSuppliersNext = [...(miseProductSuppliers || [])];
  let psAdded = 0, psUpdated = 0;
  parsed.produtos.forEach(p => {
    const productId = itemByName[ftNrm(p.name)]?.id;
    if (!productId) return;
    // Lista de supplierIds com preferred flag, derivada do parsed
    const desiredLinks = [];
    if (p.fornecedorPreferencial) {
      const sid = supByName[ftNrm(p.fornecedorPreferencial)]?.id;
      if (sid) desiredLinks.push({ supplierId: sid, preferred: true });
    }
    (p.fornecedoresAlternativos || []).slice(0, 3).forEach(altName => {
      const sid = supByName[ftNrm(altName)]?.id;
      if (sid && !desiredLinks.some(l => l.supplierId === sid)) {
        desiredLinks.push({ supplierId: sid, preferred: false });
      }
    });
    if (desiredLinks.length === 0) return;
    desiredLinks.forEach(link => {
      const existingIdx = productSuppliersNext.findIndex(ps =>
        ps.restaurantId === restaurantId &&
        ps.productId === productId &&
        ps.supplierId === link.supplierId
      );
      if (existingIdx >= 0) {
        // Atualiza preferred se mudou
        const ex = productSuppliersNext[existingIdx];
        if (ex.preferred !== link.preferred) {
          productSuppliersNext[existingIdx] = { ...ex, preferred: link.preferred };
          psUpdated++;
        }
      } else {
        productSuppliersNext.push({
          id: ftUid(),
          restaurantId,
          productId,
          supplierId: link.supplierId,
          conversionFactor: 1,
          price: null,
          preferred: link.preferred,
        });
        psAdded++;
      }
    });
  });

  return {
    suppliersNext, stocksNext, categoriesNext, itemsNext,
    productSuppliersNext,
    stats: {
      supAdded, supUpdated,
      stockAdded,
      catAdded,
      itemAdded, itemUpdated,
      psAdded, psUpdated,
      totalProdutos: parsed.produtos.length,
      totalFornecedores: parsed.fornecedores.length,
    },
    warnings,
  };
}

// ═══════════════════════════════════════════════════════════════
// ──  MISE CONTAGENS — ADMIN (Gestor Administrativo)            ──
// ═══════════════════════════════════════════════════════════════
function MiseContagensAdmin({ restaurantId, restaurantName = "", employees, pessoas = [], miseCategories, miseStocks, miseAssignments, miseItems, miseCounts, miseSuppliers, miseProductSuppliers, miseCycles = [], miseSupplierOrders = [], isOwner = false, onUpdate, mobileOnly }) {
  const [subTab, setSubTab] = useState("atribuicoes");
  const restCategories = miseCategories.filter(c => c.restaurantId === restaurantId);
  const restStocks = miseStocks.filter(s => s.restaurantId === restaurantId);
  const restAssignments = miseAssignments.filter(a => a.restaurantId === restaurantId);
  const restItems = miseItems.filter(i => i.restaurantId === restaurantId);
  const restSuppliers = (miseSuppliers || []).filter(s => s.restaurantId === restaurantId);
  const restProductSuppliers = (miseProductSuppliers || []).filter(ps => ps.restaurantId === restaurantId);
  // v8.18.1: elegíveis pra contar = quem tem permissão "Fazer contagens" (operational.contagensExec)
  // Inclui também empregados com área legada `contagens` marcada (compat). Não restrito à equipe.
  const _today = today();
  const restPessoasMise = (pessoas || []).filter(p => (p.restaurantIds || []).includes(restaurantId));
  const eligibleEmps = (() => {
    const out = [];
    const seen = new Set();
    restPessoasMise.forEach(p => {
      const perms = p.permissions?.[restaurantId];
      const linkedEmp = (employees || []).find(e => e.id === p.linkedEmployeeId);
      const inactive = linkedEmp && linkedEmp.inactive && linkedEmp.inactiveFrom && linkedEmp.inactiveFrom <= _today;
      if (inactive) return;
      const hasNew = perms?.operational?.contagensExec === true;
      const hasLegacy = linkedEmp?.operationalAreas?.contagens === true;
      if (!hasNew && !hasLegacy) return;
      const id = p.linkedEmployeeId || `pes_as_emp_${p.id}`;
      if (seen.has(id)) return;
      seen.add(id);
      out.push({ id, name: p.name });
    });
    // Fallback: empregados sem pessoa mas com área legada
    (employees || []).forEach(e => {
      if (e.restaurantId !== restaurantId) return;
      if (e.inactive && e.inactiveFrom && e.inactiveFrom <= _today) return;
      if (e.operationalAreas?.contagens !== true) return;
      if (seen.has(e.id)) return;
      seen.add(e.id);
      out.push({ id: e.id, name: e.name });
    });
    return out.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  })();

  const [newCatName, setNewCatName] = useState("");
  const [newCatType, setNewCatType] = useState("ambos");
  const [editingCatId, setEditingCatId] = useState(null);
  const [editingCatName, setEditingCatName] = useState("");
  const [newStockName, setNewStockName] = useState("");
  const [newStockLoc, setNewStockLoc] = useState("");
  const [editingStockId, setEditingStockId] = useState(null);
  const [editingStockName, setEditingStockName] = useState("");
  const [editingStockLoc, setEditingStockLoc] = useState("");
  const [newItemCat, setNewItemCat] = useState("");
  const [newItemName, setNewItemName] = useState("");
  const [newItemUnit, setNewItemUnit] = useState("un");
  const [itemFilter, setItemFilter] = useState("");
  const [assignMatrixStock, setAssignMatrixStock] = useState(null); // estoque selecionado na matriz (modelo novo: estoque × categoria × pessoa)
  // Fornecedores
  const [newSupName, setNewSupName] = useState("");
  const [newSupContato, setNewSupContato] = useState("");
  const [newSupWhats, setNewSupWhats] = useState("");
  const [newSupNotes, setNewSupNotes] = useState("");
  const [editingSupId, setEditingSupId] = useState(null);
  const [editingSupForm, setEditingSupForm] = useState({ name: "", nomeContato: "", whatsapp: "", notes: "" });
  // Modal de vínculos Produto → Fornecedores
  const [linkModalProductId, setLinkModalProductId] = useState(null);
  // Modal de vínculos em lote: um fornecedor × muitos produtos
  const [bulkLinkSupplierId, setBulkLinkSupplierId] = useState(null);
  const [bulkFilter, setBulkFilter] = useState({ categoryId: "", search: "" });
  const [bulkSelected, setBulkSelected] = useState({}); // productId → true
  const [bulkDefaults, setBulkDefaults] = useState({ factor: "1", price: "", markPreferred: false });
  // Modal de vínculo categoria ↔ estoques
  const [catStocksModalId, setCatStocksModalId] = useState(null);
  // Import XLSX — preview antes do commit
  const [importPreview, setImportPreview] = useState(null); // { plan, fileName } ou null
  const [importing, setImporting] = useState(false);
  // Cadastro produto novo: campos extras
  const [newItemPkg, setNewItemPkg] = useState("");
  const [newItemStockIds, setNewItemStockIds] = useState([]);
  const [newItemSupplierPref, setNewItemSupplierPref] = useState("");
  // Modal "Editar produto" — form completo
  const [editItemId, setEditItemId] = useState(null);
  const [editItemForm, setEditItemForm] = useState(null);
  // Filtros + bulk-assign na tabela de itens (Fase 1.7)
  const [itemBulkFilter, setItemBulkFilter] = useState({ categoryId: "__all", stockId: "__all", supplierId: "__all", missing: "__all" });
  const [itemBulkSelected, setItemBulkSelected] = useState({}); // {itemId: true}
  const [itemBulkAction, setItemBulkAction] = useState({ open: false, type: "", value: "" });

  const mkId = () => Date.now().toString() + "_" + Math.random().toString(36).slice(2, 8);
  const miseAc = "#7c9e5e";

  // Actions — Categorias
  function addCategory() {
    const nm = newCatName.trim();
    if (!nm) return;
    if (restCategories.some(c => c.name.toLowerCase() === nm.toLowerCase())) { alert("Já existe categoria com esse nome."); return; }
    onUpdate("miseCategories", [...miseCategories, { id: mkId(), restaurantId, name: nm, type: newCatType || "ambos" }]);
    setNewCatName("");
    setNewCatType("ambos");
  }
  function saveCatEdit() {
    const nm = editingCatName.trim();
    if (!nm || !editingCatId) return;
    onUpdate("miseCategories", miseCategories.map(c => c.id === editingCatId ? { ...c, name: nm } : c));
    setEditingCatId(null); setEditingCatName("");
  }
  function updateCatType(catId, type) {
    onUpdate("miseCategories", miseCategories.map(c => c.id === catId ? { ...c, type } : c));
  }
  function toggleCatStock(catId, stockId) {
    const cat = restCategories.find(c => c.id === catId);
    const wasLinked = cat?.stockIds?.includes(stockId) === true;
    // 1) Atualiza category.stockIds
    onUpdate("miseCategories", miseCategories.map(c => {
      if (c.id !== catId) return c;
      const current = c.stockIds || [];
      const next = current.includes(stockId)
        ? current.filter(s => s !== stockId)
        : [...current, stockId];
      return { ...c, stockIds: next };
    }));
    // 2) v8.10.6: propaga pros itens — Atribuições deriva de items.stockIds, então sem isso
    //    a categoria não aparecia no estoque.
    onUpdate("miseItems", miseItems.map(i => {
      if (i.categoryId !== catId || i.restaurantId !== restaurantId) return i;
      const cur = i.stockIds || [];
      if (wasLinked) {
        // Removendo o vínculo: tira o estoque dos itens também
        if (!cur.includes(stockId)) return i;
        return { ...i, stockIds: cur.filter(s => s !== stockId) };
      } else {
        // Adicionando o vínculo: garante que o estoque está nos itens
        if (cur.includes(stockId)) return i;
        return { ...i, stockIds: [...cur, stockId] };
      }
    }));
    // 3) Se desvinculou, limpa as atribuições daquele par (categoria, estoque)
    if (wasLinked) {
      onUpdate("miseAssignments", miseAssignments.filter(a => !(a.categoryId === catId && a.stockId === stockId)));
    }
  }
  async function delCategory(id) {
    const cat = restCategories.find(c => c.id === id);
    const linkedItems = restItems.filter(i => i.categoryId === id).length;
    const linkedAssigns = restAssignments.filter(a => a.categoryId === id).length;
    if (!await appConfirm(`Remover categoria "${cat?.name}"?\n${linkedItems} item(ns) e ${linkedAssigns} atribuição(ões) vinculadas serão apagadas.`)) return;
    onUpdate("miseCategories", miseCategories.filter(c => c.id !== id));
    onUpdate("miseItems", miseItems.filter(i => i.categoryId !== id));
    onUpdate("miseAssignments", miseAssignments.filter(a => a.categoryId !== id));
  }

  // Actions — Estoques
  function addStock() {
    const nm = newStockName.trim();
    if (!nm) return;
    if (restStocks.some(s => s.name.toLowerCase() === nm.toLowerCase())) { alert("Já existe estoque com esse nome."); return; }
    onUpdate("miseStocks", [...miseStocks, { id: mkId(), restaurantId, name: nm, location: newStockLoc.trim() || null }]);
    setNewStockName(""); setNewStockLoc("");
  }
  function saveStockEdit() {
    const nm = editingStockName.trim();
    if (!nm || !editingStockId) return;
    onUpdate("miseStocks", miseStocks.map(s => s.id === editingStockId ? { ...s, name: nm, location: editingStockLoc.trim() || null } : s));
    setEditingStockId(null); setEditingStockName(""); setEditingStockLoc("");
  }
  async function delStock(id) {
    const st = restStocks.find(s => s.id === id);
    const linkedAssigns = restAssignments.filter(a => a.stockId === id).length;
    if (!await appConfirm(`Remover estoque "${st?.name}"?\n${linkedAssigns} atribuição(ões) vinculadas serão apagadas.`)) return;
    onUpdate("miseStocks", miseStocks.filter(s => s.id !== id));
    onUpdate("miseAssignments", miseAssignments.filter(a => a.stockId !== id));
  }

  // Actions — Itens
  function addItem() {
    const nm = newItemName.trim();
    if (!nm) return;
    if (restItems.some(i => ftNrm(i.name) === ftNrm(nm))) {
      alert("Já existe item com esse nome.");
      return;
    }
    const pkgNum = newItemPkg === "" ? null : parseFloat(String(newItemPkg).replace(",","."));
    const next = {
      id: mkId(),
      restaurantId,
      categoryId: newItemCat || null,
      name: nm,
      unit: newItemUnit.trim() || "un",
      stockIds: [...newItemStockIds],
      idealQtys: {},
      suppliers: newItemSupplierPref ? [{ supplierId: newItemSupplierPref, preferred: true }] : [],
    };
    if (pkgNum != null && !isNaN(pkgNum) && pkgNum > 0) next.pkgSize = pkgNum;
    onUpdate("miseItems", [...miseItems, next]);
    setNewItemName(""); setNewItemPkg(""); setNewItemStockIds([]); setNewItemSupplierPref("");
  }
  async function delItem(id) {
    if (!await appConfirm("Remover este item? Vínculos com fornecedores também serão removidos.")) return;
    onUpdate("miseItems", miseItems.filter(i => i.id !== id));
    onUpdate("miseProductSuppliers", (miseProductSuppliers || []).filter(ps => ps.productId !== id));
  }
  // Abre o modal de edição completa
  function openEditItem(item) {
    setEditItemId(item.id);
    setEditItemForm({
      name: item.name || "",
      categoryId: item.categoryId || "",
      unit: item.unit || "un",
      pkgSize: item.pkgSize ?? "",
      stockIds: [...(item.stockIds || [])],
      idealQtys: { ...(item.idealQtys || {}) },
      suppliers: [...(item.suppliers || [])], // [{supplierId, preferred}]
    });
  }
  function saveEditItem() {
    if (!editItemId || !editItemForm) return;
    const nm = editItemForm.name.trim();
    if (!nm) { alert("Nome é obrigatório."); return; }
    const dup = restItems.find(i => i.id !== editItemId && ftNrm(i.name) === ftNrm(nm));
    if (dup) { alert("Já existe outro item com esse nome."); return; }
    const pkgNum = editItemForm.pkgSize === "" ? null
      : (typeof editItemForm.pkgSize === "number" ? editItemForm.pkgSize : parseFloat(String(editItemForm.pkgSize).replace(",",".")));
    const cleanIdeal = {};
    Object.entries(editItemForm.idealQtys || {}).forEach(([d, v]) => {
      if (v === "" || v == null) return;
      const n = typeof v === "number" ? v : parseFloat(String(v).replace(",","."));
      if (!isNaN(n) && n >= 0) cleanIdeal[d] = n;
    });
    const next = miseItems.map(i => {
      if (i.id !== editItemId) return i;
      const merged = {
        ...i,
        name: nm,
        categoryId: editItemForm.categoryId || null,
        unit: editItemForm.unit.trim() || "un",
        stockIds: [...editItemForm.stockIds],
        idealQtys: cleanIdeal,
        suppliers: editItemForm.suppliers,
      };
      if (pkgNum != null && !isNaN(pkgNum) && pkgNum > 0) merged.pkgSize = pkgNum;
      else delete merged.pkgSize;
      return merged;
    });
    onUpdate("miseItems", next);
    setEditItemId(null); setEditItemForm(null);
  }
  function cancelEditItem() {
    setEditItemId(null); setEditItemForm(null);
  }
  // Edição inline — form (helpers do modal)
  function toggleEditFormStock(stockId) {
    setEditItemForm(f => {
      const has = f.stockIds.includes(stockId);
      return { ...f, stockIds: has ? f.stockIds.filter(x => x !== stockId) : [...f.stockIds, stockId] };
    });
  }
  function setEditFormSupplierPreferred(supplierId) {
    setEditItemForm(f => {
      const others = f.suppliers.filter(s => s.supplierId !== supplierId);
      return { ...f, suppliers: [{ supplierId, preferred: true }, ...others.map(s => ({ ...s, preferred: false }))] };
    });
  }
  function toggleEditFormSupplierAlt(supplierId) {
    setEditItemForm(f => {
      const has = f.suppliers.find(s => s.supplierId === supplierId);
      if (has) {
        // Não pode tirar o preferencial assim — só desmarcando alternativos
        if (has.preferred) return f;
        return { ...f, suppliers: f.suppliers.filter(s => s.supplierId !== supplierId) };
      }
      // Limite de 3 alternativos
      const alts = f.suppliers.filter(s => !s.preferred);
      if (alts.length >= 3) {
        alert("Máximo 3 fornecedores alternativos.");
        return f;
      }
      return { ...f, suppliers: [...f.suppliers, { supplierId, preferred: false }] };
    });
  }
  function clearEditFormSupplierPreferred() {
    setEditItemForm(f => ({ ...f, suppliers: f.suppliers.filter(s => !s.preferred) }));
  }
  function setEditFormIdealQty(dayIdx, value) {
    setEditItemForm(f => ({ ...f, idealQtys: { ...f.idealQtys, [dayIdx]: value } }));
  }

  // Bulk-assign (Fase 1.7) — aplica em lote nos items selecionados
  function applyBulkAction() {
    const ids = Object.keys(itemBulkSelected).filter(id => itemBulkSelected[id]);
    if (!ids.length) { alert("Nenhum item selecionado."); return; }
    const { type, value } = itemBulkAction;
    if (!type) { alert("Selecione uma ação."); return; }
    const next = miseItems.map(i => {
      if (!ids.includes(i.id)) return i;
      if (type === "category") return { ...i, categoryId: value || null };
      if (type === "unit") return { ...i, unit: value || "un" };
      if (type === "addStock") {
        const cur = i.stockIds || [];
        return { ...i, stockIds: cur.includes(value) ? cur : [...cur, value] };
      }
      if (type === "removeStock") {
        return { ...i, stockIds: (i.stockIds || []).filter(s => s !== value) };
      }
      if (type === "preferredSupplier") {
        const others = (i.suppliers || []).filter(s => s.supplierId !== value).map(s => ({ ...s, preferred: false }));
        return { ...i, suppliers: [{ supplierId: value, preferred: true }, ...others] };
      }
      if (type === "pkgSize") {
        const n = parseFloat(String(value).replace(",","."));
        if (isNaN(n) || n <= 0) return i;
        return { ...i, pkgSize: n };
      }
      return i;
    });
    onUpdate("miseItems", next);
    setItemBulkAction({ open: false, type: "", value: "" });
    setItemBulkSelected({});
  }
  function toggleItemSelect(id) {
    setItemBulkSelected(s => ({ ...s, [id]: !s[id] }));
  }
  function selectAllVisibleItems(visibleIds) {
    const next = {};
    visibleIds.forEach(id => { next[id] = true; });
    setItemBulkSelected(next);
  }
  function clearItemSelection() {
    setItemBulkSelected({});
  }

  // Actions — Fornecedores
  function addSupplier() {
    const nm = newSupName.trim();
    if (!nm) return;
    if (restSuppliers.some(s => ftNrm(s.name) === ftNrm(nm))) { alert("Já existe fornecedor com esse nome."); return; }
    onUpdate("miseSuppliers", [...(miseSuppliers || []), {
      id: mkId(), restaurantId,
      name: nm,
      nomeContato: newSupContato.trim() || "",
      whatsapp: newSupWhats.trim().replace(/\D/g, "") || "",
      notes: newSupNotes.trim() || "",
    }]);
    setNewSupName(""); setNewSupContato(""); setNewSupWhats(""); setNewSupNotes("");
  }
  function startEditSup(s) {
    setEditingSupId(s.id);
    setEditingSupForm({
      name: s.name,
      nomeContato: s.nomeContato ?? "",
      whatsapp: s.whatsapp ?? "",
      notes: s.notes ?? "",
    });
  }
  function saveSupEdit() {
    const nm = editingSupForm.name.trim();
    if (!nm || !editingSupId) return;
    onUpdate("miseSuppliers", (miseSuppliers || []).map(s => s.id === editingSupId ? {
      ...s,
      name: nm,
      nomeContato: editingSupForm.nomeContato.trim() || "",
      whatsapp: editingSupForm.whatsapp.trim().replace(/\D/g, "") || "",
      notes: editingSupForm.notes.trim() || "",
    } : s));
    setEditingSupId(null);
  }
  async function delSupplier(id) {
    const s = restSuppliers.find(x => x.id === id);
    const linked = restProductSuppliers.filter(ps => ps.supplierId === id).length;
    if (!await appConfirm(`Remover fornecedor "${s?.name}"?\n${linked} vínculo(s) com produtos serão removidos.`)) return;
    onUpdate("miseSuppliers", (miseSuppliers || []).filter(x => x.id !== id));
    onUpdate("miseProductSuppliers", (miseProductSuppliers || []).filter(ps => ps.supplierId !== id));
  }

  // Actions — Atribuições (modelo novo: estoque × categoria × pessoa)
  // A mesma pessoa pode contar diferentes categorias dentro do mesmo estoque, ou
  // dividir uma categoria entre estoques diferentes.
  function isAssigned(stockId, categoryId, userId) {
    return restAssignments.some(a => a.stockId === stockId && a.categoryId === categoryId && a.userId === userId);
  }
  function toggleAssignment(stockId, categoryId, userId) {
    const existing = restAssignments.find(a => a.stockId === stockId && a.categoryId === categoryId && a.userId === userId);
    if (existing) {
      onUpdate("miseAssignments", miseAssignments.filter(a => a.id !== existing.id));
    } else {
      onUpdate("miseAssignments", [...miseAssignments, { id: mkId(), restaurantId, stockId, categoryId, userId }]);
    }
  }
  // Categorias presentes em um estoque — deduzidas dos produtos cujo stockIds inclui o estoque
  function categoriesInStock(stockId) {
    const catIds = new Set();
    restItems.forEach(i => {
      if ((i.stockIds || []).includes(stockId) && i.categoryId) catIds.add(i.categoryId);
    });
    return [...restCategories].filter(c => catIds.has(c.id)).sort((a,b)=>a.name.localeCompare(b.name));
  }
  // Conta de produtos numa (estoque, categoria) — só pra UI mostrar volume
  function countItemsInStockCategory(stockId, categoryId) {
    return restItems.filter(i => (i.stockIds || []).includes(stockId) && i.categoryId === categoryId).length;
  }
  // Detecta atribuições antigas (modelo categoria × pessoa, sem stockId) — pra exibir banner
  const legacyAssignments = restAssignments.filter(a => !a.stockId && a.categoryId);
  function migrateLegacyAssignments() {
    if (legacyAssignments.length === 0) return;
    const expanded = [];
    legacyAssignments.forEach(a => {
      const cat = restCategories.find(c => c.id === a.categoryId);
      if (!cat) return;
      // Pega estoques onde a categoria aparece (via produtos OU via cat.stockIds antigo)
      const fromProducts = new Set();
      restItems.forEach(i => { if (i.categoryId === a.categoryId) (i.stockIds || []).forEach(sid => fromProducts.add(sid)); });
      const fromCat = new Set(cat.stockIds || []);
      const allStocks = new Set([...fromProducts, ...fromCat]);
      allStocks.forEach(sid => {
        // Evita duplicar se já existe atribuição com stockId igual
        const dup = restAssignments.some(b => b.stockId === sid && b.categoryId === a.categoryId && b.userId === a.userId);
        if (!dup && !expanded.some(b => b.stockId === sid && b.categoryId === a.categoryId && b.userId === a.userId)) {
          expanded.push({ id: mkId(), restaurantId, stockId: sid, categoryId: a.categoryId, userId: a.userId });
        }
      });
    });
    const legacyIds = new Set(legacyAssignments.map(a => a.id));
    const next = miseAssignments.filter(a => !legacyIds.has(a.id)).concat(expanded);
    onUpdate("miseAssignments", next);
    alert(`Migração concluída:\n\n  • ${legacyAssignments.length} atribuição(ões) antiga(s) convertida(s)\n  • ${expanded.length} atribuição(ões) (estoque × categoria × pessoa) criada(s)`);
  }
  // Resumo: conta atribuições por estoque (pra mostrar badges no dropdown)
  function assignmentCountByStock(stockId) {
    return restAssignments.filter(a => a.stockId === stockId).length;
  }

  // Actions — Vínculos Produto-Fornecedor
  function toggleProductSupplierLink(productId, supplierId) {
    const existing = restProductSuppliers.find(ps => ps.productId === productId && ps.supplierId === supplierId);
    if (existing) {
      onUpdate("miseProductSuppliers", (miseProductSuppliers || []).filter(ps => ps.id !== existing.id));
    } else {
      const linksForProduct = restProductSuppliers.filter(ps => ps.productId === productId);
      const isFirst = linksForProduct.length === 0;
      onUpdate("miseProductSuppliers", [...(miseProductSuppliers || []), { id: mkId(), restaurantId, productId, supplierId, conversionFactor: 1, price: null, preferred: isFirst }]);
    }
  }
  function updateProductSupplier(linkId, patch) {
    onUpdate("miseProductSuppliers", (miseProductSuppliers || []).map(ps => ps.id === linkId ? { ...ps, ...patch } : ps));
  }
  function setPreferred(productId, linkId) {
    // Desmarca outros vínculos do mesmo produto, marca só esse
    onUpdate("miseProductSuppliers", (miseProductSuppliers || []).map(ps => {
      if (ps.productId !== productId) return ps;
      return { ...ps, preferred: ps.id === linkId };
    }));
  }

  // Abre modal de vínculo em lote para um fornecedor
  function openBulkLink(supplierId) {
    setBulkLinkSupplierId(supplierId);
    // Pré-seleciona produtos já vinculados a esse fornecedor
    const preSelected = {};
    restProductSuppliers.filter(ps => ps.supplierId === supplierId).forEach(ps => { preSelected[ps.productId] = true; });
    setBulkSelected(preSelected);
    setBulkFilter({ categoryId: "", search: "" });
    setBulkDefaults({ factor: "1", price: "", markPreferred: false });
  }

  // Aplica os vínculos em lote: para cada produto selecionado, cria vínculo se não existe; se existe, atualiza com defaults
  function applyBulkLink() {
    if (!bulkLinkSupplierId) return;
    const factor = parseFloat(String(bulkDefaults.factor).replace(",",".")) || 1;
    const price = bulkDefaults.price === "" ? null : (parseFloat(String(bulkDefaults.price).replace(",",".")) || 0);
    const markPreferred = !!bulkDefaults.markPreferred;
    const selectedProductIds = Object.keys(bulkSelected).filter(pid => bulkSelected[pid]);
    if (selectedProductIds.length === 0) { alert("Nenhum produto selecionado."); return; }

    let next = [...(miseProductSuppliers || [])];
    let added = 0, updated = 0;
    selectedProductIds.forEach(productId => {
      const existing = next.find(ps => ps.productId === productId && ps.supplierId === bulkLinkSupplierId && ps.restaurantId === restaurantId);
      if (existing) {
        next = next.map(ps => ps.id === existing.id ? { ...ps, conversionFactor: factor, price, preferred: markPreferred ? true : ps.preferred } : ps);
        updated++;
      } else {
        const hasPreferred = next.some(ps => ps.productId === productId && ps.restaurantId === restaurantId && ps.preferred);
        next.push({
          id: mkId(),
          restaurantId,
          productId,
          supplierId: bulkLinkSupplierId,
          conversionFactor: factor,
          price,
          preferred: markPreferred || !hasPreferred, // se for o primeiro vínculo do produto, marca preferencial
        });
        added++;
      }
    });

    // Se markPreferred, desmarca preferred dos outros fornecedores nesses produtos
    if (markPreferred) {
      next = next.map(ps => {
        if (selectedProductIds.includes(ps.productId) && ps.supplierId !== bulkLinkSupplierId && ps.preferred) {
          return { ...ps, preferred: false };
        }
        return ps;
      });
    }

    // Remove vínculos de produtos que foram DES-selecionados (estavam marcados na abertura, mas foram desmarcados agora)
    const originalLinked = new Set(restProductSuppliers.filter(ps => ps.supplierId === bulkLinkSupplierId).map(ps => ps.productId));
    const toUnlink = [...originalLinked].filter(pid => !bulkSelected[pid]);
    if (toUnlink.length > 0) {
      next = next.filter(ps => !(ps.supplierId === bulkLinkSupplierId && toUnlink.includes(ps.productId)));
    }

    onUpdate("miseProductSuppliers", next);
    setBulkLinkSupplierId(null);
    onUpdate("_toast", `✓ Vínculos atualizados: ${added} novo(s), ${updated} atualizado(s)${toUnlink.length?`, ${toUnlink.length} removido(s)`:""}.`);
  }

  // Vincula categoria inteira: seleciona todos os produtos da categoria no modal
  function selectCategory(categoryId) {
    const productsInCat = restItems.filter(i => i.categoryId === categoryId);
    const next = { ...bulkSelected };
    productsInCat.forEach(p => { next[p.id] = true; });
    setBulkSelected(next);
  }
  function deselectCategory(categoryId) {
    const productsInCat = restItems.filter(i => i.categoryId === categoryId);
    const next = { ...bulkSelected };
    productsInCat.forEach(p => { delete next[p.id]; });
    setBulkSelected(next);
  }

  // (Funções de ciclos removidas — modelo sem ciclos.)

  const SUB_TABS = [
    { id: "atribuicoes",  label: "Atribuições" },
    { id: "categorias",   label: "Categorias"  },
    { id: "estoques",     label: "Estoques"    },
    { id: "itens",        label: "Itens"       },
    { id: "fornecedores", label: "Fornecedores"},
    // Aba "Ciclos" removida — modelo sem ciclos. Cada contagem é solta e Compras
    // consome diretamente as não-arquivadas. Histórico fica disponível em Compras.
  ];

  // (Matriz de atribuições agora é por estoque — ver assignMatrixStock)

  // ── Import/Export XLSX (modelo de produtos) ──────────────────────────────
  // Fluxo: user baixa modelo (com dados atuais pré-preenchidos), edita no Excel,
  // re-importa. Importação faz merge por nome com normalização ftNrm.

  function handleDownloadModelo() {
    try {
      miseDownloadModeloXLSX({
        restaurantName,
        items: restItems,
        suppliers: restSuppliers,
        stocks: restStocks,
        categories: restCategories,
        productSuppliers: restProductSuppliers,
      });
    } catch (err) {
      alert("Erro ao gerar modelo: " + (err.message || err));
    }
  }

  async function handleImportXLSX(e) {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const parsed = await miseParsePlanilhaXLSX(file);
      if (parsed.errors && parsed.errors.length) {
        alert("Erros no arquivo:\n\n" + parsed.errors.join("\n"));
        setImporting(false);
        return;
      }
      const total = parsed.produtos.length + parsed.fornecedores.length + parsed.estoques.length + parsed.categorias.length;
      if (total === 0) {
        alert("Planilha vazia ou sem dados reconhecíveis. Verifique se as abas Produtos / Fornecedores / Estoques / Categorias têm conteúdo.");
        setImporting(false);
        return;
      }
      const plan = miseBuildImportPlan(parsed, {
        restaurantId,
        miseSuppliers, miseStocks, miseCategories, miseItems, miseProductSuppliers,
      });
      setImportPreview({ plan, fileName: file.name });
    } catch (err) {
      alert("Erro ao ler planilha: " + (err.message || err));
    } finally {
      setImporting(false);
    }
  }

  function commitImport() {
    if (!importPreview) return;
    const { plan } = importPreview;
    onUpdate("miseSuppliers", plan.suppliersNext);
    onUpdate("miseStocks", plan.stocksNext);
    onUpdate("miseCategories", plan.categoriesNext);
    onUpdate("miseItems", plan.itemsNext);
    // v8.9: também atualiza miseProductSuppliers (tabela N:N que a UI lê pra Compras/Fornecedores)
    if (plan.productSuppliersNext) onUpdate("miseProductSuppliers", plan.productSuppliersNext);
    setImportPreview(null);
    const s = plan.stats;
    alert(
      `Importação concluída:\n\n` +
      `  • Fornecedores: +${s.supAdded} novos · ${s.supUpdated} atualizado(s)\n` +
      `  • Estoques: +${s.stockAdded} novo(s)\n` +
      `  • Categorias: +${s.catAdded} nova(s)\n` +
      `  • Produtos: +${s.itemAdded} novo(s) · ${s.itemUpdated} atualizado(s)\n` +
      (s.psAdded || s.psUpdated ? `  • Vínculos produto-fornecedor: +${s.psAdded || 0} novo(s) · ${s.psUpdated || 0} atualizado(s)\n` : "") +
      `\n` +
      (plan.warnings.length ? `⚠ ${plan.warnings.length} aviso(s):\n${plan.warnings.slice(0,5).join("\n")}` : `Tudo certo.`)
    );
  }

  function cancelImport() {
    setImportPreview(null);
  }

  return (
    <div>
      {/* Sub-tabs + import */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8,marginBottom:12}}>
        <div style={{display:"flex",gap:4,borderBottom:"1px solid var(--border)",overflowX:"auto"}}>
          {SUB_TABS.map(t => (
            <button key={t.id} onClick={()=>setSubTab(t.id)}
              style={{background:"none",border:"none",borderBottom: subTab===t.id ? `2px solid ${miseAc}` : "2px solid transparent",padding:"8px 14px",cursor:"pointer",fontSize:13,fontWeight: subTab===t.id ? 700 : 500,color: subTab===t.id ? "var(--text)" : "var(--text3)",whiteSpace:"nowrap",flexShrink:0}}>
              {t.label}
            </button>
          ))}
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <button
            type="button"
            onClick={handleDownloadModelo}
            title="Baixa um arquivo Excel com os produtos atuais. Edite no Excel e re-importe."
            style={{...S.btnSecondary,fontSize:11,padding:"6px 12px",cursor:"pointer",display:"inline-flex",alignItems:"center",color:miseAc,borderColor:miseAc+"66"}}
          >
            📥 Baixar modelo
          </button>
          <label style={{...S.btnSecondary,fontSize:11,padding:"6px 12px",cursor:importing?"wait":"pointer",display:"inline-flex",alignItems:"center",color:miseAc,borderColor:miseAc+"66",opacity:importing?0.6:1}}>
            {importing ? "⏳ Lendo..." : "📤 Importar planilha"}
            <input type="file" accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={handleImportXLSX} style={{display:"none"}} disabled={importing} />
          </label>
        </div>
      </div>

      {/* Preview de importação */}
      {importPreview && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={cancelImport}>
          <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:14,maxWidth:560,width:"100%",maxHeight:"86vh",overflow:"auto",padding:24,boxShadow:"0 20px 50px rgba(0,0,0,0.3)"}}>
            <h3 style={{margin:"0 0 4px",color:"var(--text)",fontSize:18,fontWeight:700}}>Confirmar importação</h3>
            <div style={{color:"var(--text3)",fontSize:12,marginBottom:14}}>{importPreview.fileName}</div>
            {(() => {
              const s = importPreview.plan.stats;
              const blocks = [
                { label: "Fornecedores", add: s.supAdded, upd: s.supUpdated },
                { label: "Estoques",     add: s.stockAdded, upd: 0 },
                { label: "Categorias",   add: s.catAdded, upd: 0 },
                { label: "Produtos",     add: s.itemAdded, upd: s.itemUpdated },
              ];
              return (
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
                  {blocks.map(b => (
                    <div key={b.label} style={{padding:"10px 12px",background:"var(--bg2,#f8f8f6)",border:"1px solid var(--border)",borderRadius:10}}>
                      <div style={{fontSize:12,color:"var(--text3)",marginBottom:4}}>{b.label}</div>
                      <div style={{display:"flex",alignItems:"baseline",gap:8}}>
                        <span style={{fontSize:18,fontWeight:700,color:b.add>0?miseAc:"var(--text2)"}}>+{b.add}</span>
                        <span style={{fontSize:11,color:"var(--text3)"}}>novo(s)</span>
                        {b.upd > 0 && (
                          <>
                            <span style={{fontSize:18,fontWeight:700,color:"#f59e0b",marginLeft:4}}>~{b.upd}</span>
                            <span style={{fontSize:11,color:"var(--text3)"}}>atualizad{b.upd===1?"o":"os"}</span>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
            {importPreview.plan.warnings.length > 0 && (
              <div style={{padding:"10px 12px",background:"#fffbeb",border:"1px solid #f59e0b44",borderRadius:10,marginBottom:14,fontSize:12,color:"#92400e",lineHeight:1.5,maxHeight:140,overflow:"auto"}}>
                <div style={{fontWeight:700,marginBottom:4}}>⚠ {importPreview.plan.warnings.length} aviso(s):</div>
                {importPreview.plan.warnings.slice(0, 8).map((w, i) => <div key={i}>• {w}</div>)}
                {importPreview.plan.warnings.length > 8 && <div>... e mais {importPreview.plan.warnings.length - 8}</div>}
              </div>
            )}
            <div style={{padding:"10px 12px",background:"var(--bg2,#f8f8f6)",border:"1px solid var(--border)",borderRadius:10,fontSize:12,color:"var(--text3)",lineHeight:1.5,marginBottom:16}}>
              📌 Confirme pra aplicar no banco. Itens com nome igual aos existentes serão atualizados (não duplicados).
            </div>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button type="button" onClick={cancelImport} style={{...S.btnSecondary,padding:"8px 14px",fontSize:13}}>Cancelar</button>
              <button type="button" onClick={commitImport} style={{...S.btn,padding:"8px 14px",fontSize:13,background:miseAc,color:"#fff",border:"none"}}>Aplicar importação</button>
            </div>
          </div>
        </div>
      )}

      {/* ATRIBUIÇÕES */}
      {subTab === "atribuicoes" && (
        <div>
          <p style={{color:"var(--text3)",fontSize:13,lineHeight:1.5,margin:"0 0 16px",maxWidth:760}}>
            Defina quem conta cada categoria em cada estoque. A mesma pessoa pode contar várias categorias dentro de um estoque, ou dividir uma categoria entre estoques diferentes (ex: João conta Comidas + Bebidas no Estoque Seco; Maria conta Limpeza no mesmo Estoque Seco).
          </p>

          {/* Banner de migração legado */}
          {legacyAssignments.length > 0 && (
            <div style={{padding:"12px 16px",background:"#eff6ff",border:"1px solid #93c5fd",borderRadius:10,marginBottom:16,display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
              <div style={{flex:1,minWidth:240}}>
                <div style={{fontSize:13,fontWeight:700,color:"#1e40af",marginBottom:2}}>📥 Atribuições antigas detectadas</div>
                <div style={{fontSize:12,color:"#1e3a8a",lineHeight:1.5}}>
                  Encontramos <b>{legacyAssignments.length}</b> atribuição(ões) no modelo antigo (categoria × pessoa). Convertemos automaticamente pro modelo novo (estoque × categoria × pessoa) — clique pra aplicar a conversão.
                </div>
              </div>
              <button onClick={migrateLegacyAssignments} style={{...S.btnPrimary,fontSize:12,padding:"7px 14px",background:"#2563eb",border:"none",color:"#fff"}}>Converter agora</button>
            </div>
          )}

          {eligibleEmps.length === 0 && (
            <div style={{padding:"12px 16px",background:"#fffbeb",border:"1px solid #f59e0b44",borderRadius:10,marginBottom:16,fontSize:13,color:"#92400e",lineHeight:1.5}}>
              ⚠️ Ninguém tem a permissão <b>Fazer contagens</b>. Primeiro conceda a permissão em <b>Pessoas → Permissões</b> (grupo Execução Operacional) para quem vai contar.
            </div>
          )}

          {restStocks.length === 0 ? (
            <div style={{padding:"32px 16px",textAlign:"center",color:"var(--text3)",fontSize:14,background:"var(--card-bg)",borderRadius:12,border:"1px solid var(--border)"}}>
              Cadastre ao menos um estoque na aba Estoques.
            </div>
          ) : restItems.length === 0 ? (
            <div style={{padding:"32px 16px",textAlign:"center",color:"var(--text3)",fontSize:14,background:"var(--card-bg)",borderRadius:12,border:"1px solid var(--border)"}}>
              Cadastre produtos (e atribua estoque + categoria a eles) antes de definir quem conta cada parte.
            </div>
          ) : (
            <>
              {/* Seletor de estoque */}
              <div style={{marginBottom:12,display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                <label style={{fontSize:13,color:"var(--text2)",fontWeight:600}}>Estoque:</label>
                <select value={assignMatrixStock ?? ""} onChange={e=>setAssignMatrixStock(e.target.value || null)} style={{...S.input,maxWidth:280,width:"auto"}}>
                  <option value="">— Selecione um estoque —</option>
                  {[...restStocks].sort((a,b)=>a.name.localeCompare(b.name)).map(s => {
                    const cnt = assignmentCountByStock(s.id);
                    return <option key={s.id} value={s.id}>{s.name}{cnt>0?` (${cnt})`:""}</option>;
                  })}
                </select>
              </div>

              {!assignMatrixStock ? (
                <div style={{padding:"32px 16px",textAlign:"center",color:"var(--text3)",fontSize:14,background:"var(--card-bg)",borderRadius:12,border:"1px solid var(--border)"}}>
                  Selecione um estoque para definir quem conta cada categoria dentro dele.
                </div>
              ) : (() => {
                const stock = restStocks.find(s => s.id === assignMatrixStock);
                if (!stock) return null;
                const cats = categoriesInStock(stock.id);
                if (cats.length === 0) {
                  return (
                    <div style={{padding:"32px 16px",textAlign:"center",color:"var(--text3)",fontSize:14,background:"var(--card-bg)",borderRadius:12,border:"1px solid var(--border)"}}>
                      <div style={{fontSize:32,marginBottom:10}}>📂</div>
                      <div style={{color:"var(--text)",fontWeight:600,fontSize:14,marginBottom:6}}>Nenhuma categoria nesse estoque</div>
                      <div style={{lineHeight:1.5,maxWidth:460,margin:"0 auto"}}>
                        Atribua produtos a <b>{stock.name}</b> (na aba <b>Itens</b>) com uma categoria definida. As categorias disponíveis aqui aparecem automaticamente.
                      </div>
                    </div>
                  );
                }
                return (
                  <div style={{border:"1px solid var(--border)",borderRadius:12,background:"var(--card-bg)",overflow:"hidden"}}>
                    <div style={{padding:"10px 14px",background:"var(--bg2)",borderBottom:"1px solid var(--border)",fontSize:12,color:"var(--text3)",lineHeight:1.5}}>
                      Estoque <b>{stock.name}</b> tem <b>{cats.length} categoria(s)</b>. Marque quem conta cada uma. Quem não estiver marcado em nenhuma categoria desse estoque <b>não vê</b> esse estoque na contagem.
                    </div>
                    {/* Matriz: linhas = categorias; pra cada categoria, lista pessoas com checkboxes */}
                    {cats.map((cat, idx) => {
                      const itemCount = countItemsInStockCategory(stock.id, cat.id);
                      const assignedHere = restAssignments.filter(a => a.stockId === stock.id && a.categoryId === cat.id);
                      return (
                        <div key={cat.id} style={{borderTop: idx>0 ? "1px solid var(--border)" : "none"}}>
                          <div style={{padding:"10px 14px",background:miseAc+"08",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                            <span style={{fontSize:13,fontWeight:700,color:"var(--text)"}}>{cat.name}</span>
                            <span style={{fontSize:11,color:"var(--text3)"}}>{itemCount} produto(s)</span>
                            <span style={{flex:1}} />
                            <span style={{fontSize:11,color:assignedHere.length>0?"#15803d":"#dc2626",fontWeight:600}}>
                              {assignedHere.length === 0 ? "⚠ ninguém atribuído" : `${assignedHere.length} pessoa(s)`}
                            </span>
                          </div>
                          {eligibleEmps.length === 0 ? (
                            <div style={{padding:"16px",textAlign:"center",color:"var(--text3)",fontSize:12}}>—</div>
                          ) : (
                            <div style={{display:"flex",flexWrap:"wrap",gap:6,padding:"10px 14px"}}>
                              {[...eligibleEmps].sort((a,b)=>a.name.localeCompare(b.name)).map(emp => {
                                const checked = isAssigned(stock.id, cat.id, emp.id);
                                return (
                                  <button key={emp.id} type="button"
                                    onClick={()=>toggleAssignment(stock.id, cat.id, emp.id)}
                                    style={{
                                      padding:"6px 12px",borderRadius:20,fontSize:12,fontWeight:checked?700:500,
                                      background:checked?miseAc:"transparent",color:checked?"#fff":"var(--text2)",
                                      border:`1px solid ${checked?miseAc:"var(--border)"}`,cursor:"pointer",
                                    }}>
                                    {checked ? "✓ " : ""}{shortName(emp.name)}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}

      {/* CATEGORIAS */}
      {subTab === "categorias" && (
        <div>
          <h3 style={{color:"var(--text)",margin:"0 0 12px",fontSize:mobileOnly?16:18}}>📂 Categorias</h3>
          <div style={{padding:"12px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,marginBottom:16}}>
            <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"2fr 1.5fr auto",gap:10,alignItems:"end"}}>
              <div>
                <label style={{...S.label,fontSize:11}}>Nome da categoria</label>
                <input value={newCatName} onChange={e=>setNewCatName(e.target.value)} placeholder="ex: Vinhos, Bar, Cozinha" style={S.input} onKeyDown={e=>e.key==="Enter"&&addCategory()} />
              </div>
              <div>
                <label style={{...S.label,fontSize:11}}>Tipo</label>
                <div style={{display:"flex",gap:4,background:"var(--bg2)",padding:3,borderRadius:8,border:"1px solid var(--border)"}}>
                  {[["contagem","📦 Contagem"],["pedido","🛒 Pedido"],["ambos","📦🛒 Ambos"]].map(([v,l]) => (
                    <button key={v} type="button" onClick={()=>setNewCatType(v)}
                      style={{flex:1,background:newCatType===v?miseAc:"transparent",color:newCatType===v?"#fff":"var(--text2)",border:"none",borderRadius:6,padding:"6px 10px",fontSize:11,fontWeight:newCatType===v?700:500,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <button onClick={addCategory} style={{...S.btnPrimary,fontSize:13,padding:"8px 16px"}}>+ Adicionar</button>
            </div>
            <div style={{marginTop:8,fontSize:11,color:"var(--text3)"}}>
              💡 <b>Contagem</b>: usuário conta estoque físico. <b>Pedido</b>: usuário informa direto o que pedir. <b>Ambos</b>: faz as duas.
            </div>
          </div>
          {restCategories.length === 0 ? (
            <div style={{padding:"32px 16px",textAlign:"center",color:"var(--text3)",fontSize:14,background:"var(--card-bg)",borderRadius:12,border:"1px solid var(--border)"}}>Nenhuma categoria cadastrada</div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {[...restCategories].sort((a,b)=>a.name.localeCompare(b.name)).map(c => {
                const nItems = restItems.filter(i => i.categoryId === c.id).length;
                const isEditing = editingCatId === c.id;
                const cType = c.type ?? "ambos";
                return (
                  <div key={c.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,flexWrap:"wrap"}}>
                    {isEditing ? (
                      <>
                        <input value={editingCatName} onChange={e=>setEditingCatName(e.target.value)} style={{...S.input,flex:1,minWidth:160}} onKeyDown={e=>e.key==="Enter"&&saveCatEdit()} autoFocus />
                        <button onClick={saveCatEdit} style={{...S.btnPrimary,fontSize:12,padding:"6px 12px"}}>Salvar</button>
                        <button onClick={()=>{setEditingCatId(null);setEditingCatName("");}} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px"}}>Cancelar</button>
                      </>
                    ) : (
                      <>
                        <div style={{flex:1,minWidth:140,color:"var(--text)",fontWeight:600}}>{c.name}</div>
                        <select value={cType} onChange={e=>updateCatType(c.id, e.target.value)} style={{...S.input,width:"auto",fontSize:11,padding:"4px 8px"}} title="Tipo da categoria">
                          <option value="contagem">📦 Contagem</option>
                          <option value="pedido">🛒 Pedido</option>
                          <option value="ambos">📦🛒 Ambos</option>
                        </select>
                        <div style={{fontSize:12,color:"var(--text3)"}}>{nItems} {nItems===1?"item":"itens"}</div>
                        {cType !== "pedido" && (
                          <button onClick={()=>setCatStocksModalId(c.id)} title="Em quais estoques essa categoria existe"
                            style={{...S.btnSecondary,fontSize:12,padding:"6px 12px",color:(c.stockIds||[]).length>0?miseAc:"var(--text3)",borderColor:(c.stockIds||[]).length>0?miseAc+"66":"var(--border)"}}>
                            📍 {(c.stockIds||[]).length > 0 ? `${(c.stockIds||[]).length} estoque(s)` : "estoques"}
                          </button>
                        )}
                        <button onClick={()=>{setEditingCatId(c.id);setEditingCatName(c.name);}} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px"}}>Editar</button>
                        <button onClick={()=>delCategory(c.id)} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px",color:"var(--red)",borderColor:"var(--red)44"}}>Remover</button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ESTOQUES */}
      {subTab === "estoques" && (
        <div>
          <h3 style={{color:"var(--text)",margin:"0 0 12px",fontSize:mobileOnly?16:18}}>🏬 Estoques</h3>
          <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
            <input value={newStockName} onChange={e=>setNewStockName(e.target.value)} placeholder="Nome do estoque (ex: Adega)" style={{...S.input,maxWidth:240,flex:"1 1 180px"}} onKeyDown={e=>e.key==="Enter"&&addStock()} />
            <input value={newStockLoc} onChange={e=>setNewStockLoc(e.target.value)} placeholder="Localização (opcional)" style={{...S.input,maxWidth:260,flex:"1 1 180px"}} onKeyDown={e=>e.key==="Enter"&&addStock()} />
            <button onClick={addStock} style={{...S.btnPrimary,fontSize:13,padding:"8px 16px"}}>+ Adicionar</button>
          </div>
          {restStocks.length === 0 ? (
            <div style={{padding:"32px 16px",textAlign:"center",color:"var(--text3)",fontSize:14,background:"var(--card-bg)",borderRadius:12,border:"1px solid var(--border)"}}>Nenhum estoque cadastrado</div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {[...restStocks].sort((a,b)=>a.name.localeCompare(b.name)).map(s => {
                const isEditing = editingStockId === s.id;
                return (
                  <div key={s.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,flexWrap:"wrap"}}>
                    {isEditing ? (
                      <>
                        <input value={editingStockName} onChange={e=>setEditingStockName(e.target.value)} style={{...S.input,flex:"1 1 160px"}} autoFocus />
                        <input value={editingStockLoc} onChange={e=>setEditingStockLoc(e.target.value)} placeholder="Localização" style={{...S.input,flex:"1 1 160px"}} />
                        <button onClick={saveStockEdit} style={{...S.btnPrimary,fontSize:12,padding:"6px 12px"}}>Salvar</button>
                        <button onClick={()=>{setEditingStockId(null);setEditingStockName("");setEditingStockLoc("");}} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px"}}>Cancelar</button>
                      </>
                    ) : (
                      <>
                        <div style={{flex:1,minWidth:160}}>
                          <div style={{color:"var(--text)",fontWeight:600}}>{s.name}</div>
                          {s.location && <div style={{fontSize:12,color:"var(--text3)",marginTop:2}}>📍 {s.location}</div>}
                        </div>
                        <button onClick={()=>{setEditingStockId(s.id);setEditingStockName(s.name);setEditingStockLoc(s.location??"");}} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px"}}>Editar</button>
                        <button onClick={()=>delStock(s.id)} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px",color:"var(--red)",borderColor:"var(--red)44"}}>Remover</button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ITENS */}
      {subTab === "itens" && (() => {
        // ── Filtros
        const filtered = restItems.filter(i => {
          if (itemBulkFilter.categoryId === "__none" && i.categoryId) return false;
          if (itemBulkFilter.categoryId !== "__all" && itemBulkFilter.categoryId !== "__none" && i.categoryId !== itemBulkFilter.categoryId) return false;
          if (itemBulkFilter.stockId === "__none" && (i.stockIds || []).length > 0) return false;
          if (itemBulkFilter.stockId !== "__all" && itemBulkFilter.stockId !== "__none" && !(i.stockIds || []).includes(itemBulkFilter.stockId)) return false;
          if (itemBulkFilter.supplierId === "__none" && (i.suppliers || []).length > 0) return false;
          if (itemBulkFilter.supplierId !== "__all" && itemBulkFilter.supplierId !== "__none" && !(i.suppliers || []).some(s => s.supplierId === itemBulkFilter.supplierId)) return false;
          if (itemBulkFilter.missing === "noPkg" && i.pkgSize) return false;
          if (itemBulkFilter.missing === "noIdeal" && Object.keys(i.idealQtys || {}).length > 0) return false;
          if (itemFilter && !i.name.toLowerCase().includes(itemFilter.toLowerCase())) return false;
          return true;
        });
        const sorted = [...filtered].sort((a,b) => {
          const ca = restCategories.find(c => c.id === a.categoryId)?.name ?? "";
          const cb = restCategories.find(c => c.id === b.categoryId)?.name ?? "";
          return ca.localeCompare(cb) || a.name.localeCompare(b.name);
        });
        const visibleIds = sorted.map(i => i.id);
        const selectedCount = visibleIds.filter(id => itemBulkSelected[id]).length;
        const allSelected = visibleIds.length > 0 && selectedCount === visibleIds.length;

        return (
        <div>
          <h3 style={{color:"var(--text)",margin:"0 0 12px",fontSize:mobileOnly?16:18}}>🍷 Itens <span style={{fontSize:12,color:"var(--text3)",fontWeight:400,marginLeft:8}}>({restItems.length})</span></h3>

          {/* Form de cadastro rápido */}
          <div style={{padding:"12px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,marginBottom:16}}>
            <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"2.4fr 1.6fr 1fr 1fr 1.6fr 1.8fr auto",gap:8,alignItems:"end"}}>
              <div>
                <label style={{...S.label,fontSize:11}}>Nome do produto</label>
                <input value={newItemName} onChange={e=>setNewItemName(e.target.value)} placeholder="ex: Vinho Malbec 750ml" style={{...S.input}} onKeyDown={e=>e.key==="Enter"&&addItem()} />
              </div>
              <div>
                <label style={{...S.label,fontSize:11}}>Categoria <span style={{color:"var(--text3)",fontWeight:400,fontSize:10}}>(opc.)</span></label>
                <select value={newItemCat} onChange={e=>setNewItemCat(e.target.value)} style={{...S.input}}>
                  <option value="">— sem categoria —</option>
                  {[...restCategories].sort((a,b)=>a.name.localeCompare(b.name)).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{...S.label,fontSize:11}}>Unidade</label>
                <input value={newItemUnit} onChange={e=>setNewItemUnit(e.target.value)} placeholder="un, kg, L" style={{...S.input}} />
              </div>
              <div>
                <label style={{...S.label,fontSize:11}} title="Tamanho do pacote — pedido arredonda pra cima">Pacote</label>
                <input type="number" step="0.01" value={newItemPkg} onChange={e=>setNewItemPkg(e.target.value)} placeholder="opc." style={{...S.input,fontFamily:"'DM Mono',monospace"}} />
              </div>
              <div>
                <label style={{...S.label,fontSize:11}}>Estoques</label>
                <select multiple value={newItemStockIds} onChange={e=>setNewItemStockIds(Array.from(e.target.selectedOptions, o => o.value))}
                  style={{...S.input,minHeight:34,height:34,padding:"4px 8px"}} title="Ctrl/Cmd+click pra múltiplos">
                  {[...restStocks].sort((a,b)=>a.name.localeCompare(b.name)).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{...S.label,fontSize:11}}>Fornecedor pref.</label>
                <select value={newItemSupplierPref} onChange={e=>setNewItemSupplierPref(e.target.value)} style={{...S.input}}>
                  <option value="">— sem —</option>
                  {[...restSuppliers].sort((a,b)=>a.name.localeCompare(b.name)).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <button onClick={addItem} disabled={!newItemName.trim()} style={{...S.btnPrimary,fontSize:13,padding:"8px 16px",opacity:!newItemName.trim()?0.5:1,cursor:!newItemName.trim()?"not-allowed":"pointer"}}>+ Adicionar</button>
            </div>
            <div style={{marginTop:6,fontSize:11,color:"var(--text3)",lineHeight:1.5}}>
              💡 Categoria, estoques e fornecedor são opcionais aqui — você pode atribuir depois em massa. Use <b>Editar</b> pra definir o estoque ideal por dia da semana e fornecedores alternativos.
            </div>
          </div>

          {/* Filtros + bulk-assign */}
          <div style={{padding:"10px 12px",background:"var(--bg2,#f8f8f6)",border:"1px solid var(--border)",borderRadius:10,marginBottom:10,display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
            <input value={itemFilter} onChange={e=>setItemFilter(e.target.value)} placeholder="🔍 Buscar..." style={{...S.input,flex:"1 1 200px",maxWidth:240,fontSize:12,padding:"6px 10px"}} />
            <select value={itemBulkFilter.categoryId} onChange={e=>setItemBulkFilter({...itemBulkFilter,categoryId:e.target.value})} style={{...S.input,fontSize:12,padding:"6px 10px",maxWidth:200}}>
              <option value="__all">Todas categorias</option>
              <option value="__none">Sem categoria</option>
              {[...restCategories].sort((a,b)=>a.name.localeCompare(b.name)).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select value={itemBulkFilter.stockId} onChange={e=>setItemBulkFilter({...itemBulkFilter,stockId:e.target.value})} style={{...S.input,fontSize:12,padding:"6px 10px",maxWidth:180}}>
              <option value="__all">Todos estoques</option>
              <option value="__none">Sem estoque</option>
              {[...restStocks].sort((a,b)=>a.name.localeCompare(b.name)).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select value={itemBulkFilter.supplierId} onChange={e=>setItemBulkFilter({...itemBulkFilter,supplierId:e.target.value})} style={{...S.input,fontSize:12,padding:"6px 10px",maxWidth:200}}>
              <option value="__all">Todos fornecedores</option>
              <option value="__none">Sem fornecedor</option>
              {[...restSuppliers].sort((a,b)=>a.name.localeCompare(b.name)).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select value={itemBulkFilter.missing} onChange={e=>setItemBulkFilter({...itemBulkFilter,missing:e.target.value})} style={{...S.input,fontSize:12,padding:"6px 10px",maxWidth:170}}>
              <option value="__all">Pacote/ideal</option>
              <option value="noPkg">Sem pacote</option>
              <option value="noIdeal">Sem ideal definido</option>
            </select>
            <span style={{flex:1}} />
            <span style={{fontSize:12,color:"var(--text3)"}}>{filtered.length} de {restItems.length}</span>
          </div>

          {/* Barra de bulk-assign — aparece quando algum item está selecionado */}
          {selectedCount > 0 && (
            <div style={{padding:"10px 12px",background:miseAc+"15",border:`1px solid ${miseAc}66`,borderRadius:10,marginBottom:10,display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
              <span style={{fontSize:13,fontWeight:600,color:miseAc}}>{selectedCount} selecionado(s)</span>
              <button onClick={()=>setItemBulkAction({open:true,type:"category",value:""})} style={{...S.btnSecondary,fontSize:11,padding:"5px 10px"}}>Atribuir categoria</button>
              <button onClick={()=>setItemBulkAction({open:true,type:"addStock",value:""})} style={{...S.btnSecondary,fontSize:11,padding:"5px 10px"}}>+ Estoque</button>
              <button onClick={()=>setItemBulkAction({open:true,type:"removeStock",value:""})} style={{...S.btnSecondary,fontSize:11,padding:"5px 10px"}}>– Estoque</button>
              <button onClick={()=>setItemBulkAction({open:true,type:"preferredSupplier",value:""})} style={{...S.btnSecondary,fontSize:11,padding:"5px 10px"}}>Atribuir fornecedor pref.</button>
              <button onClick={()=>setItemBulkAction({open:true,type:"unit",value:""})} style={{...S.btnSecondary,fontSize:11,padding:"5px 10px"}}>Atribuir unidade</button>
              <button onClick={()=>setItemBulkAction({open:true,type:"pkgSize",value:""})} style={{...S.btnSecondary,fontSize:11,padding:"5px 10px"}}>Atribuir pacote</button>
              <span style={{flex:1}} />
              <button onClick={clearItemSelection} style={{...S.btnSecondary,fontSize:11,padding:"5px 10px"}}>Limpar seleção</button>
            </div>
          )}

          {/* Tabela */}
          {restItems.length === 0 ? (
            <div style={{padding:"32px 16px",textAlign:"center",color:"var(--text3)",fontSize:14,background:"var(--card-bg)",borderRadius:12,border:"1px solid var(--border)"}}>Nenhum item cadastrado</div>
          ) : sorted.length === 0 ? (
            <div style={{padding:"32px 16px",textAlign:"center",color:"var(--text3)",fontSize:14,background:"var(--card-bg)",borderRadius:12,border:"1px solid var(--border)"}}>Nenhum item bate com os filtros.</div>
          ) : (
            <div style={{overflowX:"auto",border:"1px solid var(--border)",borderRadius:12,background:"var(--card-bg)"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                <thead>
                  <tr style={{background:"var(--bg2)",borderBottom:"1px solid var(--border)"}}>
                    <th style={{padding:"10px 8px",textAlign:"center",width:36}}>
                      <input type="checkbox" checked={allSelected} onChange={()=> allSelected ? clearItemSelection() : selectAllVisibleItems(visibleIds)} style={{cursor:"pointer",width:16,height:16,accentColor:miseAc}} />
                    </th>
                    <th style={{padding:"10px 12px",textAlign:"left",color:"var(--text)",fontWeight:700}}>Produto</th>
                    <th style={{padding:"10px 12px",textAlign:"left",color:"var(--text)",fontWeight:700}}>Categoria</th>
                    <th style={{padding:"10px 12px",textAlign:"left",color:"var(--text)",fontWeight:700}}>Estoques</th>
                    <th style={{padding:"10px 12px",textAlign:"left",color:"var(--text)",fontWeight:700}}>Fornecedor pref.</th>
                    <th style={{padding:"10px 12px",textAlign:"center",color:"var(--text)",fontWeight:700}}>Unid.</th>
                    <th style={{padding:"10px 12px",textAlign:"right",color:"var(--text)",fontWeight:700}} title="Tamanho do pacote">Pkg</th>
                    <th style={{padding:"10px 12px",textAlign:"center",color:"var(--text)",fontWeight:700}} title="Estoque ideal por dia da semana">Ideal</th>
                    <th style={{padding:"10px 12px",textAlign:"right",color:"var(--text)",fontWeight:700}}></th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map(i => {
                    const cat = restCategories.find(c => c.id === i.categoryId);
                    const stockNames = (i.stockIds || []).map(sid => restStocks.find(s => s.id === sid)?.name).filter(Boolean);
                    const prefSupplier = (i.suppliers || []).find(s => s.preferred);
                    const prefSupplierName = prefSupplier ? restSuppliers.find(s => s.id === prefSupplier.supplierId)?.name : null;
                    const altCount = (i.suppliers || []).filter(s => !s.preferred).length;
                    const idealCount = Object.keys(i.idealQtys || {}).length;
                    const isSelected = !!itemBulkSelected[i.id];
                    return (
                      <tr key={i.id} style={{borderTop:"1px solid var(--border)",background:isSelected?miseAc+"12":"transparent"}}>
                        <td style={{padding:"6px 8px",textAlign:"center"}}>
                          <input type="checkbox" checked={isSelected} onChange={()=>toggleItemSelect(i.id)} style={{cursor:"pointer",width:16,height:16,accentColor:miseAc}} />
                        </td>
                        <td style={{padding:"8px 12px",color:"var(--text)",fontWeight:500}}>{i.name}</td>
                        <td style={{padding:"8px 12px",fontSize:12}}>
                          {cat ? <span style={{color:"var(--text2)"}}>{cat.name}</span> : <span style={{color:"#f59e0b"}}>— sem —</span>}
                        </td>
                        <td style={{padding:"8px 12px",fontSize:12,color:"var(--text2)"}}>
                          {stockNames.length === 0 ? <span style={{color:"#f59e0b"}}>— sem —</span> : stockNames.join(", ")}
                        </td>
                        <td style={{padding:"8px 12px",fontSize:12}}>
                          {prefSupplierName ? (
                            <span style={{color:"var(--text2)"}}>
                              {prefSupplierName}
                              {altCount > 0 && <span style={{color:"var(--text3)",marginLeft:4}}>+{altCount}</span>}
                            </span>
                          ) : <span style={{color:"#f59e0b"}}>— sem —</span>}
                        </td>
                        <td style={{padding:"8px 12px",textAlign:"center",color:"var(--text2)",fontSize:12}}>{i.unit ?? "un"}</td>
                        <td style={{padding:"8px 12px",textAlign:"right",fontFamily:"'DM Mono',monospace",fontSize:12,color:"var(--text2)"}}>{i.pkgSize ?? "—"}</td>
                        <td style={{padding:"8px 12px",textAlign:"center",fontSize:11,color:idealCount>0?miseAc:"var(--text3)",fontWeight:idealCount>0?700:400}} title="dias da semana com estoque ideal definido">
                          {idealCount > 0 ? `${idealCount}/7` : "—"}
                        </td>
                        <td style={{padding:"8px 12px",textAlign:"right",whiteSpace:"nowrap"}}>
                          <button onClick={()=>openEditItem(i)} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px",marginRight:4}}>Editar</button>
                          <button onClick={()=>delItem(i.id)} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px",color:"var(--red)",borderColor:"var(--red)44"}}>×</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Modal de bulk-assign */}
          {itemBulkAction.open && (() => {
            const labels = {
              category: "Atribuir categoria a " + selectedCount + " item(ns)",
              addStock: "Adicionar estoque a " + selectedCount + " item(ns)",
              removeStock: "Remover estoque de " + selectedCount + " item(ns)",
              preferredSupplier: "Definir fornecedor preferencial de " + selectedCount + " item(ns)",
              unit: "Atribuir unidade a " + selectedCount + " item(ns)",
              pkgSize: "Atribuir tamanho de pacote a " + selectedCount + " item(ns)",
            };
            return (
              <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={()=>setItemBulkAction({open:false,type:"",value:""})}>
                <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:14,maxWidth:440,width:"100%",padding:24,boxShadow:"0 20px 50px rgba(0,0,0,0.3)"}}>
                  <h3 style={{margin:"0 0 12px",color:"var(--text)",fontSize:16,fontWeight:700}}>{labels[itemBulkAction.type]}</h3>
                  {itemBulkAction.type === "category" && (
                    <select value={itemBulkAction.value} onChange={e=>setItemBulkAction({...itemBulkAction,value:e.target.value})} style={{...S.input,width:"100%",marginBottom:14}}>
                      <option value="">— sem categoria —</option>
                      {[...restCategories].sort((a,b)=>a.name.localeCompare(b.name)).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  )}
                  {(itemBulkAction.type === "addStock" || itemBulkAction.type === "removeStock") && (
                    <select value={itemBulkAction.value} onChange={e=>setItemBulkAction({...itemBulkAction,value:e.target.value})} style={{...S.input,width:"100%",marginBottom:14}}>
                      <option value="">— escolha um —</option>
                      {[...restStocks].sort((a,b)=>a.name.localeCompare(b.name)).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  )}
                  {itemBulkAction.type === "preferredSupplier" && (
                    <select value={itemBulkAction.value} onChange={e=>setItemBulkAction({...itemBulkAction,value:e.target.value})} style={{...S.input,width:"100%",marginBottom:14}}>
                      <option value="">— escolha um —</option>
                      {[...restSuppliers].sort((a,b)=>a.name.localeCompare(b.name)).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  )}
                  {itemBulkAction.type === "unit" && (
                    <input value={itemBulkAction.value} onChange={e=>setItemBulkAction({...itemBulkAction,value:e.target.value})} placeholder="un, kg, L, cx..." style={{...S.input,width:"100%",marginBottom:14}} />
                  )}
                  {itemBulkAction.type === "pkgSize" && (
                    <input type="number" step="0.01" value={itemBulkAction.value} onChange={e=>setItemBulkAction({...itemBulkAction,value:e.target.value})} placeholder="ex: 12" style={{...S.input,width:"100%",marginBottom:14,fontFamily:"'DM Mono',monospace"}} />
                  )}
                  <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                    <button onClick={()=>setItemBulkAction({open:false,type:"",value:""})} style={{...S.btnSecondary,padding:"8px 14px",fontSize:13}}>Cancelar</button>
                    <button onClick={applyBulkAction} disabled={!itemBulkAction.value && itemBulkAction.type !== "category"} style={{...S.btnPrimary,padding:"8px 14px",fontSize:13,opacity:(!itemBulkAction.value && itemBulkAction.type !== "category")?0.5:1}}>Aplicar</button>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
        );
      })()}

      {/* Modal: Editar produto (form completo) */}
      {editItemId && editItemForm && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1001,display:"flex",alignItems:"flex-start",justifyContent:"center",padding:20,overflowY:"auto"}} onClick={cancelEditItem}>
          <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",borderRadius:14,maxWidth:760,width:"100%",margin:"40px 0",boxShadow:"0 20px 50px rgba(0,0,0,0.3)"}}>
            <div style={{padding:"16px 20px",borderBottom:"1px solid var(--border)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontSize:11,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Editar produto</div>
                <div style={{fontSize:16,color:"var(--text)",fontWeight:700,marginTop:2}}>{editItemForm.name || "(novo)"}</div>
              </div>
              <button onClick={cancelEditItem} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px"}}>Fechar</button>
            </div>
            <div style={{padding:"16px 20px",display:"flex",flexDirection:"column",gap:16}}>
              {/* Linha 1: Nome | Categoria | Unidade | Pacote */}
              <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"2fr 1.5fr 1fr 1fr",gap:10}}>
                <div>
                  <label style={{...S.label,fontSize:11}}>Nome</label>
                  <input value={editItemForm.name} onChange={e=>setEditItemForm({...editItemForm,name:e.target.value})} style={S.input} />
                </div>
                <div>
                  <label style={{...S.label,fontSize:11}}>Categoria</label>
                  <select value={editItemForm.categoryId || ""} onChange={e=>setEditItemForm({...editItemForm,categoryId:e.target.value})} style={S.input}>
                    <option value="">— sem categoria —</option>
                    {[...restCategories].sort((a,b)=>a.name.localeCompare(b.name)).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{...S.label,fontSize:11}}>Unidade</label>
                  <input value={editItemForm.unit} onChange={e=>setEditItemForm({...editItemForm,unit:e.target.value})} placeholder="un, kg, L" style={S.input} />
                </div>
                <div>
                  <label style={{...S.label,fontSize:11}} title="Tamanho do pacote — pedido arredonda pra cima">Pacote</label>
                  <input type="number" step="0.01" value={editItemForm.pkgSize} onChange={e=>setEditItemForm({...editItemForm,pkgSize:e.target.value})} placeholder="opc." style={{...S.input,fontFamily:"'DM Mono',monospace"}} />
                </div>
              </div>

              {/* Linha 2: Estoques (chips) */}
              <div>
                <label style={{...S.label,fontSize:11}}>Estoques físicos onde o produto pode estar</label>
                <div style={{display:"flex",flexWrap:"wrap",gap:6,padding:"8px 10px",background:"var(--bg2,#f8f8f6)",border:"1px solid var(--border)",borderRadius:10,minHeight:40}}>
                  {restStocks.length === 0 && <span style={{fontSize:12,color:"var(--text3)"}}>Cadastre estoques na aba Estoques.</span>}
                  {[...restStocks].sort((a,b)=>a.name.localeCompare(b.name)).map(s => {
                    const active = editItemForm.stockIds.includes(s.id);
                    return (
                      <button key={s.id} type="button" onClick={()=>toggleEditFormStock(s.id)}
                        style={{
                          padding:"5px 12px",borderRadius:20,fontSize:12,fontWeight:active?700:500,
                          background:active?miseAc:"transparent",color:active?"#fff":"var(--text2)",
                          border:`1px solid ${active?miseAc:"var(--border)"}`,cursor:"pointer",
                        }}>
                        {active ? "✓ " : ""}{s.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Linha 3: Fornecedores */}
              <div>
                <label style={{...S.label,fontSize:11}}>Fornecedor preferencial</label>
                <div style={{display:"flex",flexWrap:"wrap",gap:6,padding:"8px 10px",background:"var(--bg2,#f8f8f6)",border:"1px solid var(--border)",borderRadius:10,minHeight:40}}>
                  {restSuppliers.length === 0 && <span style={{fontSize:12,color:"var(--text3)"}}>Cadastre fornecedores na aba Fornecedores.</span>}
                  {(() => {
                    const pref = editItemForm.suppliers.find(s => s.preferred);
                    return [...restSuppliers].sort((a,b)=>a.name.localeCompare(b.name)).map(s => {
                      const isPref = pref?.supplierId === s.id;
                      return (
                        <button key={s.id} type="button"
                          onClick={()=> isPref ? clearEditFormSupplierPreferred() : setEditFormSupplierPreferred(s.id)}
                          style={{
                            padding:"5px 12px",borderRadius:20,fontSize:12,fontWeight:isPref?700:500,
                            background:isPref?miseAc:"transparent",color:isPref?"#fff":"var(--text2)",
                            border:`1px solid ${isPref?miseAc:"var(--border)"}`,cursor:"pointer",
                          }}>
                          {isPref ? "★ " : ""}{s.name}
                        </button>
                      );
                    });
                  })()}
                </div>
              </div>
              <div>
                <label style={{...S.label,fontSize:11}}>Fornecedores alternativos <span style={{color:"var(--text3)",fontSize:10,fontWeight:400}}>(até 3, caso o preferencial fique sem)</span></label>
                <div style={{display:"flex",flexWrap:"wrap",gap:6,padding:"8px 10px",background:"var(--bg2,#f8f8f6)",border:"1px solid var(--border)",borderRadius:10,minHeight:40}}>
                  {restSuppliers.length === 0 && <span style={{fontSize:12,color:"var(--text3)"}}>—</span>}
                  {(() => {
                    const pref = editItemForm.suppliers.find(s => s.preferred);
                    return [...restSuppliers].sort((a,b)=>a.name.localeCompare(b.name)).filter(s => s.id !== pref?.supplierId).map(s => {
                      const isAlt = editItemForm.suppliers.some(x => x.supplierId === s.id && !x.preferred);
                      return (
                        <button key={s.id} type="button" onClick={()=>toggleEditFormSupplierAlt(s.id)}
                          style={{
                            padding:"5px 12px",borderRadius:20,fontSize:12,fontWeight:isAlt?700:500,
                            background:isAlt?"#94a3b8":"transparent",color:isAlt?"#fff":"var(--text2)",
                            border:`1px solid ${isAlt?"#94a3b8":"var(--border)"}`,cursor:"pointer",
                          }}>
                          {isAlt ? "✓ " : ""}{s.name}
                        </button>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* Linha 4: idealQtys por dia da semana */}
              <div>
                <label style={{...S.label,fontSize:11}}>Estoque ideal por dia da semana <span style={{color:"var(--text3)",fontSize:10,fontWeight:400}}>(quanto você quer ter ANTES daquele dia)</span></label>
                <div style={{display:"grid",gridTemplateColumns:"repeat(7, 1fr)",gap:6}}>
                  {MISE_DAYS.map(d => (
                    <div key={d.idx}>
                      <div style={{fontSize:10,color:"var(--text3)",textAlign:"center",marginBottom:2,fontWeight:700}}>{d.label}</div>
                      <input type="number" step="0.01" value={editItemForm.idealQtys[d.idx] ?? ""}
                        onChange={e=>setEditFormIdealQty(d.idx, e.target.value)}
                        placeholder="—"
                        style={{...S.input,width:"100%",textAlign:"center",fontFamily:"'DM Mono',monospace",fontSize:13,padding:"6px 4px"}} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div style={{padding:"14px 20px",borderTop:"1px solid var(--border)",display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button onClick={cancelEditItem} style={{...S.btnSecondary,padding:"8px 14px",fontSize:13}}>Cancelar</button>
              <button onClick={saveEditItem} style={{...S.btnPrimary,padding:"8px 14px",fontSize:13}}>Salvar</button>
            </div>
          </div>
        </div>
      )}

      {/* FORNECEDORES */}
      {subTab === "fornecedores" && (
        <div>
          <h3 style={{color:"var(--text)",margin:"0 0 12px",fontSize:mobileOnly?16:18}}>🚚 Fornecedores</h3>
          <div style={{padding:"12px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,marginBottom:16}}>
            <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"2fr 1.5fr 1.5fr 2fr auto",gap:8,alignItems:"end"}}>
              <div>
                <label style={{...S.label,fontSize:11}}>Empresa</label>
                <input value={newSupName} onChange={e=>setNewSupName(e.target.value)} placeholder="ex: Distribuidora ABC" style={S.input} onKeyDown={e=>e.key==="Enter"&&addSupplier()} />
              </div>
              <div>
                <label style={{...S.label,fontSize:11}}>Contato <span style={{color:"var(--text3)",fontWeight:400,fontSize:10}}>(nome da pessoa)</span></label>
                <input value={newSupContato} onChange={e=>setNewSupContato(e.target.value)} placeholder="ex: João da Silva" style={S.input} onKeyDown={e=>e.key==="Enter"&&addSupplier()} />
              </div>
              <div>
                <label style={{...S.label,fontSize:11}}>WhatsApp <span style={{color:"var(--text3)",fontWeight:400,fontSize:10}}>(com DDD+55)</span></label>
                <input value={newSupWhats} onChange={e=>setNewSupWhats(e.target.value)} placeholder="5511987654321" style={{...S.input,fontFamily:"'DM Mono',monospace"}} />
              </div>
              <div>
                <label style={{...S.label,fontSize:11}}>Observações</label>
                <input value={newSupNotes} onChange={e=>setNewSupNotes(e.target.value)} placeholder="opcional" style={S.input} />
              </div>
              <button onClick={addSupplier} disabled={!newSupName.trim()} style={{...S.btnPrimary,fontSize:13,padding:"8px 16px",opacity:!newSupName.trim()?0.5:1}}>+ Adicionar</button>
            </div>
            <div style={{marginTop:6,fontSize:11,color:"var(--text3)"}}>
              💡 <b>Contato</b> é o nome usado na mensagem WhatsApp ("Olá [contato], tudo bem? Segue o pedido..."). <b>WhatsApp</b> abre o link <code style={{background:"var(--bg2)",padding:"1px 4px",borderRadius:4,fontFamily:"'DM Mono',monospace"}}>wa.me/NÚMERO</code> ao enviar o pedido. Inclua o código do país (ex: 55 para Brasil).
            </div>
          </div>
          {restSuppliers.length === 0 ? (
            <div style={{padding:"32px 16px",textAlign:"center",color:"var(--text3)",fontSize:14,background:"var(--card-bg)",borderRadius:12,border:"1px solid var(--border)"}}>Nenhum fornecedor cadastrado</div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {[...restSuppliers].sort((a,b)=>a.name.localeCompare(b.name)).map(s => {
                const isEditing = editingSupId === s.id;
                const linkedProductsCount = restProductSuppliers.filter(ps => ps.supplierId === s.id).length;
                return (
                  <div key={s.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,flexWrap:"wrap"}}>
                    {isEditing ? (
                      <>
                        <input value={editingSupForm.name} onChange={e=>setEditingSupForm({...editingSupForm,name:e.target.value})} placeholder="Empresa" style={{...S.input,flex:"1 1 140px"}} autoFocus />
                        <input value={editingSupForm.nomeContato} onChange={e=>setEditingSupForm({...editingSupForm,nomeContato:e.target.value})} placeholder="Contato" style={{...S.input,flex:"1 1 140px"}} />
                        <input value={editingSupForm.whatsapp} onChange={e=>setEditingSupForm({...editingSupForm,whatsapp:e.target.value})} placeholder="WhatsApp" style={{...S.input,flex:"1 1 140px",fontFamily:"'DM Mono',monospace"}} />
                        <input value={editingSupForm.notes} onChange={e=>setEditingSupForm({...editingSupForm,notes:e.target.value})} placeholder="Obs." style={{...S.input,flex:"1 1 200px"}} />
                        <button onClick={saveSupEdit} style={{...S.btnPrimary,fontSize:12,padding:"6px 12px"}}>Salvar</button>
                        <button onClick={()=>setEditingSupId(null)} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px"}}>Cancelar</button>
                      </>
                    ) : (
                      <>
                        <div style={{flex:1,minWidth:160}}>
                          <div style={{color:"var(--text)",fontWeight:600}}>{s.name}</div>
                          <div style={{fontSize:11,color:"var(--text3)",marginTop:2,display:"flex",gap:10,flexWrap:"wrap"}}>
                            {s.nomeContato && <span>👤 {s.nomeContato}</span>}
                            {s.whatsapp && <span style={{fontFamily:"'DM Mono',monospace"}}>📱 {s.whatsapp}</span>}
                            {s.notes && <span>📝 {s.notes}</span>}
                            <span>{linkedProductsCount} produto(s) vinculado(s)</span>
                          </div>
                        </div>
                        <button onClick={()=>openBulkLink(s.id)} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px",color:miseAc,borderColor:miseAc+"66"}}>🔗 Produtos</button>
                        <button onClick={()=>startEditSup(s)} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px"}}>Editar</button>
                        <button onClick={()=>delSupplier(s.id)} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px",color:"var(--red)",borderColor:"var(--red)44"}}>Remover</button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* (Aba de ciclos removida — modelo sem ciclos) */}

      {/* MODAL: Vínculos Produto → Fornecedores */}
      {linkModalProductId && (() => {
        const product = restItems.find(i => i.id === linkModalProductId);
        if (!product) { setTimeout(()=>setLinkModalProductId(null),0); return null; }
        const links = restProductSuppliers.filter(ps => ps.productId === product.id);
        return (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
            <div style={{background:"var(--card-bg)",borderRadius:14,maxWidth:720,width:"100%",maxHeight:"85vh",overflow:"auto",boxShadow:"0 10px 40px rgba(0,0,0,0.3)"}}>
              <div style={{padding:"14px 18px",borderBottom:"1px solid var(--border)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontSize:11,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Vínculos</div>
                  <div style={{fontSize:15,color:"var(--text)",fontWeight:700}}>{product.name}</div>
                </div>
                <button onClick={()=>setLinkModalProductId(null)} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px"}}>Fechar</button>
              </div>
              <div style={{padding:"14px 18px"}}>
                {restSuppliers.length === 0 ? (
                  <div style={{padding:"24px",textAlign:"center",color:"var(--text3)",fontSize:13}}>
                    Cadastre ao menos um fornecedor na aba <b>Fornecedores</b> antes de vincular.
                  </div>
                ) : (
                  <>
                    <div style={{fontSize:12,color:"var(--text3)",marginBottom:10,lineHeight:1.5}}>
                      Marque os fornecedores que vendem este produto. Para cada vinculado, defina o <b>fator de conversão</b> (ex: cerveja vendida em fardo de 12 = fator 12), o <b>preço</b> (opcional, permite comparar) e marque <b>um</b> deles como preferencial (usado na sugestão automática de compra).
                    </div>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                      <thead>
                        <tr style={{background:"var(--bg2)",borderBottom:"1px solid var(--border)"}}>
                          <th style={{padding:"8px 10px",textAlign:"left",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,width:36}}></th>
                          <th style={{padding:"8px 10px",textAlign:"left",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Fornecedor</th>
                          <th style={{padding:"8px 10px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,width:90}} title="Quantas unidades de contagem cabem em 1 unidade de compra">Fator</th>
                          <th style={{padding:"8px 10px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,width:100}}>Preço (R$)</th>
                          <th style={{padding:"8px 10px",textAlign:"center",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,width:90}}>Preferencial</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...restSuppliers].sort((a,b)=>a.name.localeCompare(b.name)).map(s => {
                          const link = links.find(ps => ps.supplierId === s.id);
                          const isLinked = !!link;
                          return (
                            <tr key={s.id} style={{borderTop:"1px solid var(--border)"}}>
                              <td style={{padding:"6px 10px",textAlign:"center"}}>
                                <input type="checkbox" checked={isLinked} onChange={()=>toggleProductSupplierLink(product.id, s.id)} style={{cursor:"pointer",width:18,height:18,accentColor:miseAc}} />
                              </td>
                              <td style={{padding:"6px 10px",color:"var(--text)",fontWeight:500}}>{s.name}</td>
                              <td style={{padding:"4px 10px"}}>
                                <input type="number" step="0.01" disabled={!isLinked} value={link?.conversionFactor ?? 1}
                                  onChange={e=>updateProductSupplier(link.id, { conversionFactor: parseFloat(e.target.value.replace(",",".")) || 1 })}
                                  style={{...S.input,width:"100%",textAlign:"right",fontFamily:"'DM Mono',monospace",fontSize:12,opacity:isLinked?1:0.4}} />
                              </td>
                              <td style={{padding:"4px 10px"}}>
                                <input type="number" step="0.01" disabled={!isLinked} value={link?.price ?? ""}
                                  onChange={e=>updateProductSupplier(link.id, { price: e.target.value === "" ? null : parseFloat(e.target.value.replace(",",".")) || 0 })}
                                  placeholder={isLinked ? "opc." : "—"}
                                  style={{...S.input,width:"100%",textAlign:"right",fontFamily:"'DM Mono',monospace",fontSize:12,opacity:isLinked?1:0.4}} />
                              </td>
                              <td style={{padding:"4px 10px",textAlign:"center"}}>
                                <input type="radio" name={`pref-${product.id}`} disabled={!isLinked} checked={!!link?.preferred}
                                  onChange={()=>setPreferred(product.id, link.id)}
                                  style={{cursor:isLinked?"pointer":"not-allowed",width:18,height:18,accentColor:miseAc,opacity:isLinked?1:0.3}} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {links.length > 0 && !links.some(l => l.preferred) && (
                      <div style={{marginTop:10,padding:"8px 12px",background:"#fffbeb",border:"1px solid #f59e0b44",borderRadius:8,fontSize:12,color:"#92400e"}}>
                        ⚠ Nenhum fornecedor marcado como preferencial. Marque um para que a sugestão automática de compra funcione.
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* MODAL: Categoria ↔ Estoques (vínculo estrutural) */}
      {catStocksModalId && (() => {
        const cat = restCategories.find(c => c.id === catStocksModalId);
        if (!cat) { setTimeout(()=>setCatStocksModalId(null),0); return null; }
        const currentIds = cat.stockIds || [];
        return (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
            <div style={{background:"var(--card-bg)",borderRadius:14,maxWidth:520,width:"100%",maxHeight:"85vh",overflow:"hidden",boxShadow:"0 10px 40px rgba(0,0,0,0.3)",display:"flex",flexDirection:"column"}}>
              <div style={{padding:"14px 18px",borderBottom:"1px solid var(--border)"}}>
                <div style={{fontSize:11,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Estoques da categoria</div>
                <div style={{fontSize:15,color:"var(--text)",fontWeight:700,marginTop:2}}>📂 {cat.name}</div>
              </div>
              <div style={{padding:"14px 18px",overflowY:"auto",flex:1}}>
                <p style={{color:"var(--text3)",fontSize:12,lineHeight:1.5,margin:"0 0 14px"}}>
                  Marque os estoques onde essa categoria existe fisicamente. Ex: Vinhos pode existir em Adega + Estoque, mas não no Bar. A matriz de atribuições só vai mostrar os estoques marcados aqui.
                </p>
                {restStocks.length === 0 ? (
                  <div style={{padding:"24px 16px",textAlign:"center",color:"var(--text3)",fontSize:13}}>
                    Nenhum estoque cadastrado. Cadastre na aba <b>Estoques</b>.
                  </div>
                ) : (
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {[...restStocks].sort((a,b)=>a.name.localeCompare(b.name)).map(s => {
                      const checked = currentIds.includes(s.id);
                      return (
                        <label key={s.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:checked?miseAc+"11":"var(--bg2)",border:`1px solid ${checked?miseAc+"66":"var(--border)"}`,borderRadius:10,cursor:"pointer"}}>
                          <input type="checkbox" checked={checked} onChange={()=>toggleCatStock(cat.id, s.id)}
                            style={{cursor:"pointer",width:18,height:18,accentColor:miseAc}} />
                          <div style={{flex:1}}>
                            <div style={{fontSize:14,color:"var(--text)",fontWeight:600}}>{s.name}</div>
                            {s.location && <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>📍 {s.location}</div>}
                          </div>
                          {checked && <span style={{fontSize:11,color:"#15803d",fontWeight:700}}>✓ vinculado</span>}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              <div style={{padding:"10px 18px",borderTop:"1px solid var(--border)",display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
                <div style={{fontSize:11,color:"var(--text3)"}}>{currentIds.length} de {restStocks.length} estoque(s)</div>
                <button onClick={()=>setCatStocksModalId(null)} style={{...S.btnPrimary,fontSize:13,padding:"8px 18px"}}>Fechar</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* MODAL: Vínculo em lote (Fornecedor × Muitos Produtos) */}
      {bulkLinkSupplierId && (() => {
        const sup = restSuppliers.find(s => s.id === bulkLinkSupplierId);
        if (!sup) { setTimeout(()=>setBulkLinkSupplierId(null),0); return null; }
        // Filtros
        const q = bulkFilter.search.trim().toLowerCase();
        const filteredProducts = restItems.filter(i => {
          if (bulkFilter.categoryId && i.categoryId !== bulkFilter.categoryId) return false;
          if (q && !i.name.toLowerCase().includes(q)) return false;
          return true;
        });
        const totalSelected = Object.values(bulkSelected).filter(Boolean).length;
        // Agrupa por categoria para exibição
        const byCategory = {};
        filteredProducts.forEach(p => {
          const cid = p.categoryId;
          if (!byCategory[cid]) byCategory[cid] = [];
          byCategory[cid].push(p);
        });
        const sortedCatIds = Object.keys(byCategory).sort((a,b) => {
          const nameA = restCategories.find(c => c.id === a)?.name || "";
          const nameB = restCategories.find(c => c.id === b)?.name || "";
          return nameA.localeCompare(nameB);
        });
        return (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
            <div style={{background:"var(--card-bg)",borderRadius:14,maxWidth:900,width:"100%",maxHeight:"90vh",overflow:"hidden",boxShadow:"0 10px 40px rgba(0,0,0,0.3)",display:"flex",flexDirection:"column"}}>
              <div style={{padding:"14px 18px",borderBottom:"1px solid var(--border)",display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
                <div>
                  <div style={{fontSize:11,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Vincular produtos em lote</div>
                  <div style={{fontSize:15,color:"var(--text)",fontWeight:700}}>🚚 {sup.name}</div>
                </div>
                <button onClick={()=>setBulkLinkSupplierId(null)} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px"}}>Cancelar</button>
              </div>
              {/* Filtros e defaults */}
              <div style={{padding:"12px 18px",borderBottom:"1px solid var(--border)",background:"var(--bg2)",flexShrink:0}}>
                <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"2fr 2fr 1fr 1fr 1fr",gap:10,alignItems:"end"}}>
                  <div>
                    <label style={{...S.label,fontSize:11}}>Filtrar por categoria</label>
                    <select value={bulkFilter.categoryId} onChange={e=>setBulkFilter({...bulkFilter,categoryId:e.target.value})} style={S.input}>
                      <option value="">— Todas —</option>
                      {[...restCategories].sort((a,b)=>a.name.localeCompare(b.name)).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{...S.label,fontSize:11}}>Buscar produto</label>
                    <input value={bulkFilter.search} onChange={e=>setBulkFilter({...bulkFilter,search:e.target.value})} placeholder="nome..." style={S.input} />
                  </div>
                  <div>
                    <label style={{...S.label,fontSize:11}} title="Fator de conversão contagem → compra (ex: fardo de 12)">Fator padrão</label>
                    <input type="number" step="0.1" value={bulkDefaults.factor} onChange={e=>setBulkDefaults({...bulkDefaults,factor:e.target.value})} style={{...S.input,fontFamily:"'DM Mono',monospace"}} />
                  </div>
                  <div>
                    <label style={{...S.label,fontSize:11}}>Preço (R$)</label>
                    <input type="number" step="0.01" value={bulkDefaults.price} onChange={e=>setBulkDefaults({...bulkDefaults,price:e.target.value})} placeholder="opc." style={{...S.input,fontFamily:"'DM Mono',monospace"}} />
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:6,paddingBottom:8}}>
                    <input type="checkbox" id="bulk-pref" checked={bulkDefaults.markPreferred} onChange={e=>setBulkDefaults({...bulkDefaults,markPreferred:e.target.checked})} style={{cursor:"pointer",accentColor:miseAc}} />
                    <label htmlFor="bulk-pref" style={{fontSize:11,color:"var(--text2)",cursor:"pointer"}}>Preferencial</label>
                  </div>
                </div>
                {bulkFilter.categoryId && (
                  <div style={{display:"flex",gap:6,marginTop:10,flexWrap:"wrap"}}>
                    <button onClick={()=>selectCategory(bulkFilter.categoryId)} style={{...S.btnSecondary,fontSize:11,padding:"4px 12px"}}>✓ Selecionar toda categoria</button>
                    <button onClick={()=>deselectCategory(bulkFilter.categoryId)} style={{...S.btnSecondary,fontSize:11,padding:"4px 12px"}}>✕ Desmarcar categoria</button>
                  </div>
                )}
              </div>
              {/* Lista de produtos */}
              <div style={{flex:1,overflowY:"auto",padding:"4px 0"}}>
                {sortedCatIds.length === 0 ? (
                  <div style={{padding:"32px 16px",textAlign:"center",color:"var(--text3)",fontSize:13}}>
                    {restItems.length === 0 ? "Nenhum produto cadastrado." : "Nenhum produto no filtro atual."}
                  </div>
                ) : sortedCatIds.map(catId => {
                  const cat = restCategories.find(c => c.id === catId);
                  const prods = byCategory[catId].sort((a,b)=>a.name.localeCompare(b.name));
                  const allSelected = prods.every(p => !!bulkSelected[p.id]);
                  const someSelected = prods.some(p => !!bulkSelected[p.id]);
                  return (
                    <div key={catId}>
                      <div style={{padding:"8px 18px",background:"var(--bg2)",borderTop:"1px solid var(--border)",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",gap:10,position:"sticky",top:0}}>
                        <input type="checkbox" checked={allSelected} ref={el => { if (el) el.indeterminate = !allSelected && someSelected; }}
                          onChange={e => { if (e.target.checked) selectCategory(catId); else deselectCategory(catId); }}
                          style={{cursor:"pointer",width:16,height:16,accentColor:miseAc}} />
                        <span style={{fontSize:12,color:"var(--text)",fontWeight:700,flex:1}}>{cat?.name || "—"}</span>
                        <span style={{fontSize:11,color:"var(--text3)"}}>{prods.filter(p => bulkSelected[p.id]).length}/{prods.length}</span>
                      </div>
                      {prods.map(p => {
                        const existingLink = restProductSuppliers.find(ps => ps.productId === p.id && ps.supplierId === bulkLinkSupplierId);
                        const hasOtherSuppliers = restProductSuppliers.some(ps => ps.productId === p.id && ps.supplierId !== bulkLinkSupplierId);
                        return (
                          <label key={p.id} style={{display:"flex",alignItems:"center",gap:10,padding:"6px 18px 6px 42px",fontSize:13,cursor:"pointer",borderBottom:"1px solid var(--border)"}}>
                            <input type="checkbox" checked={!!bulkSelected[p.id]}
                              onChange={e => setBulkSelected(prev => {
                                const next = { ...prev };
                                if (e.target.checked) next[p.id] = true; else delete next[p.id];
                                return next;
                              })}
                              style={{cursor:"pointer",width:16,height:16,accentColor:miseAc}} />
                            <span style={{flex:1,color:"var(--text)"}}>{p.name}</span>
                            <span style={{fontSize:10,color:"var(--text3)"}}>{p.unit}</span>
                            {existingLink && <span style={{fontSize:10,padding:"1px 6px",borderRadius:6,background:miseAc+"22",color:"#15803d",fontWeight:700}}>JÁ VINCULADO</span>}
                            {hasOtherSuppliers && <span style={{fontSize:10,color:"var(--text3)"}}>(+outros)</span>}
                          </label>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
              {/* Rodapé */}
              <div style={{padding:"12px 18px",borderTop:"1px solid var(--border)",background:"var(--bg2)",display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0,flexWrap:"wrap",gap:10}}>
                <div style={{fontSize:12,color:"var(--text2)"}}>
                  <b>{totalSelected}</b> produto(s) selecionado(s)
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setBulkLinkSupplierId(null)} style={{...S.btnSecondary,fontSize:12,padding:"8px 14px"}}>Cancelar</button>
                  <button onClick={applyBulkLink} disabled={totalSelected===0}
                    style={{background:totalSelected>0?miseAc:"var(--border)",color:totalSelected>0?"#fff":"var(--text3)",border:"none",borderRadius:8,padding:"8px 18px",fontSize:13,fontWeight:700,cursor:totalSelected>0?"pointer":"not-allowed",fontFamily:"'DM Sans',sans-serif"}}>
                    ✓ Aplicar vínculos
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* v8.8 — Owner-only: zona de perigo (apagar tudo de Contagens) */}
      {isOwner && (
        <details style={{ marginTop: 32, padding: "12px 16px", background: "#fee2e2", border: "1px solid #ef4444aa", borderRadius: 10 }}>
          <summary style={{ cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#991b1b", padding: "4px 0" }}>
            ⚠️ Zona de perigo (Owner) — apagar tudo
          </summary>
          <div style={{ marginTop: 12, fontSize: 12, color: "var(--text2)", lineHeight: 1.6 }}>
            <p style={{ margin: "0 0 12px" }}>
              Esta ação <strong style={{ color: "#991b1b" }}>apaga TODAS as configurações de Contagens deste restaurante</strong>:
            </p>
            <ul style={{ margin: "0 0 12px", paddingLeft: 20, color: "var(--text2)", fontSize: 12 }}>
              <li>{restCategories.length} categoria(s)</li>
              <li>{restStocks.length} estoque(s)</li>
              <li>{restItems.length} produto(s)</li>
              <li>{restAssignments.length} atribuição(ões) de pessoa</li>
              <li>{restSuppliers.length} fornecedor(es)</li>
              <li>{restProductSuppliers.length} vínculo(s) produto-fornecedor</li>
              <li>{(miseCounts || []).filter(c => c.restaurantId === restaurantId).length} contagem(ns) registrada(s)</li>
              <li>{(miseCycles || []).filter(c => c.restaurantId === restaurantId).length} ciclo(s) de contagem</li>
              <li>{(miseSupplierOrders || []).filter(o => o.restaurantId === restaurantId).length} pedido(s) de compra</li>
            </ul>
            <p style={{ margin: "0 0 12px", color: "#991b1b", fontWeight: 600 }}>
              ⚠️ Esta ação é IRREVERSÍVEL. Use apenas pra resetar configuração ou em ambiente de teste.
            </p>
            <button onClick={async () => {
              const expected = `APAGAR ${restaurantName || "TUDO"}`;
              const typed = window.prompt(`Pra confirmar, digite EXATAMENTE:\n\n${expected}\n\nSem aspas. Maiúsculas e espaços importam.`);
              if (typed !== expected) {
                if (typed != null) alert("Texto não confere. Operação cancelada.");
                return;
              }
              if (!await appConfirm(`ÚLTIMA CONFIRMAÇÃO\n\nApagar TODAS as configurações de Contagens de "${restaurantName}"?\n\nNão dá pra desfazer.`)) return;
              // Apaga tudo deste restaurante
              onUpdate("miseCategories",       (miseCategories || []).filter(x => x.restaurantId !== restaurantId));
              onUpdate("miseStocks",           (miseStocks || []).filter(x => x.restaurantId !== restaurantId));
              onUpdate("miseAssignments",      (miseAssignments || []).filter(x => x.restaurantId !== restaurantId));
              onUpdate("miseItems",            (miseItems || []).filter(x => x.restaurantId !== restaurantId));
              onUpdate("miseCounts",           (miseCounts || []).filter(x => x.restaurantId !== restaurantId));
              onUpdate("miseSuppliers",        (miseSuppliers || []).filter(x => x.restaurantId !== restaurantId));
              onUpdate("miseProductSuppliers", (miseProductSuppliers || []).filter(x => x.restaurantId !== restaurantId));
              onUpdate("miseCycles",           (miseCycles || []).filter(x => x.restaurantId !== restaurantId));
              onUpdate("miseSupplierOrders",   (miseSupplierOrders || []).filter(x => x.restaurantId !== restaurantId));
              onUpdate("_toast", `🗑️ Configuração de Contagens de ${restaurantName} apagada`);
            }}
              style={{ background: "#dc2626", color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              🗑️ Apagar TUDO de Contagens deste restaurante
            </button>
          </div>
        </details>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ──  MISE CONTAGENS — USER (Gestor Operacional)                ──
// ═══════════════════════════════════════════════════════════════
function OperationalContagens({ employee, pessoas = [], isOwner = false, miseCategories, miseStocks, miseAssignments, miseItems, miseCounts, onUpdate }) {
  // Modelo novo (Fase 1.9): contagem POR ESTOQUE.
  // 1. Pessoa vê estoques em que tem alguma atribuição
  // 2. Seleciona estoque → vê categorias daquele estoque com checkboxes pré-marcados nas atribuídas
  // 3. Pode marcar mais (cobrir colega) ou desmarcar (vai contar depois)
  // 4. Conta os itens das categorias selecionadas (agrupados por categoria como header visual)
  // 5. Tela de fechamento: avisa o que ficou sem contar com opções [Adicionar agora] / [Encerrar]
  const restaurantId = employee.restaurantId;
  const restAssignments = miseAssignments.filter(a => a.restaurantId === restaurantId);
  const myAssignments = restAssignments.filter(a => a.userId === employee.id);
  const restItems = miseItems.filter(i => i.restaurantId === restaurantId);
  const restCategories = miseCategories.filter(c => c.restaurantId === restaurantId);
  const restStocks = miseStocks.filter(s => s.restaurantId === restaurantId);

  // v8.23.5: Owner / quem configura contagens vê todos os estoques (igual ao checklists)
  const _myPessoaC = (() => {
    const pid = employee._pessoaId || (() => { const m = String(employee.id).match(/^pes_as_emp_(.+)$/); return m ? m[1] : null; })();
    if (pid) return (pessoas || []).find(p => p.id === pid);
    return (pessoas || []).find(p => p.linkedEmployeeId === employee.id);
  })();
  const seesAllStocks = isOwner || (_myPessoaC?.permissions?.[restaurantId]?.operational?.contagens === true);

  const [view, setView] = useState("list"); // list | categorias | contagem | fechamento
  const [selectedStockId, setSelectedStockId] = useState(null);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [drafts, setDrafts] = useState({}); // { itemId: qtyString }
  const [feedback, setFeedback] = useState(null);

  // Estoques onde a pessoa tem atribuição (modelo novo a.stockId)
  // v8.23.5: Owner / configurador vê todos os estoques do restaurante
  const myStockIds = seesAllStocks
    ? restStocks.map(s => s.id)
    : [...new Set(myAssignments.filter(a => a.stockId).map(a => a.stockId))];
  const myStocks = restStocks.filter(s => myStockIds.includes(s.id)).sort((a,b)=>a.name.localeCompare(b.name));

  // Helper: categorias presentes em um estoque (via produtos)
  function categoriesInStock(stockId) {
    const catIds = new Set();
    restItems.forEach(i => {
      if ((i.stockIds || []).includes(stockId) && i.categoryId) catIds.add(i.categoryId);
    });
    return restCategories.filter(c => catIds.has(c.id)).sort((a,b)=>a.name.localeCompare(b.name));
  }
  function isMyCategoryInStock(stockId, categoryId) {
    if (seesAllStocks) return true; // v8.23.5: Owner/configurador conta tudo
    return myAssignments.some(a => a.stockId === stockId && a.categoryId === categoryId);
  }
  function othersInStockCategory(stockId, categoryId) {
    return restAssignments.filter(a => a.stockId === stockId && a.categoryId === categoryId && a.userId !== employee.id);
  }
  function pessoaName(userId) {
    const p = pessoas.find(x => x.linkedEmployeeId === userId || x.id === userId);
    return p?.name || "outro colega";
  }
  // Status de contagem do estoque hoje (visão geral pra mostrar na lista de estoques)
  const todayISO = new Date().toISOString().slice(0,10);
  function stockStatus(stockId) {
    const cats = categoriesInStock(stockId);
    if (cats.length === 0) return { total: 0, done: 0 };
    const countedToday = new Set();
    miseCounts.forEach(c => {
      if (c.restaurantId !== restaurantId) return;
      if (c.stockId !== stockId) return;
      if (!c.countedAt || !c.countedAt.startsWith(todayISO)) return;
      const item = restItems.find(i => i.id === c.itemId);
      if (item?.categoryId) countedToday.add(item.categoryId);
    });
    return { total: cats.length, done: cats.filter(c => countedToday.has(c.id)).length };
  }

  // Última contagem (do usuário) pra (item, stock)
  function lastCountFor(itemId, stockId) {
    const matches = miseCounts.filter(c =>
      c.restaurantId === restaurantId &&
      c.itemId === itemId &&
      c.stockId === stockId &&
      c.userId === employee.id &&
      !c.archivedAt
    );
    if (matches.length === 0) return null;
    return matches.sort((a,b) => (b.countedAt||"").localeCompare(a.countedAt||""))[0];
  }

  // Ações
  function selectStock(stockId) {
    const cats = categoriesInStock(stockId);
    const myCatIds = cats.filter(c => isMyCategoryInStock(stockId, c.id)).map(c => c.id);
    setSelectedStockId(stockId);
    setSelectedCategoryIds(myCatIds);
    setDrafts({});
    setView("categorias");
  }
  function toggleCategorySelection(catId) {
    setSelectedCategoryIds(prev => prev.includes(catId) ? prev.filter(c => c !== catId) : [...prev, catId]);
  }
  function startCounting() {
    if (selectedCategoryIds.length === 0) {
      alert("Selecione ao menos uma categoria pra contar.");
      return;
    }
    setView("contagem");
  }
  function saveCounts() {
    const items = restItems.filter(i =>
      (i.stockIds || []).includes(selectedStockId) &&
      i.categoryId &&
      selectedCategoryIds.includes(i.categoryId)
    );
    const now = new Date().toISOString();
    const newCounts = [...miseCounts];
    let savedItems = 0;
    items.forEach((it, idx) => {
      const d = drafts[it.id];
      if (d === undefined || d === "") return;
      const qty = parseFloat(String(d).replace(",","."));
      if (isNaN(qty)) return;
      newCounts.push({
        id: Date.now().toString() + "_" + Math.random().toString(36).slice(2,8) + "_" + idx,
        restaurantId, cycleId: null,
        itemId: it.id, stockId: selectedStockId,
        userId: employee.id, qty, countedAt: now,
      });
      savedItems++;
    });
    if (savedItems === 0) {
      alert("Nenhum item foi preenchido. Insira ao menos uma quantidade.");
      return;
    }
    onUpdate("miseCounts", newCounts);
    // Tela de fechamento
    const allCats = categoriesInStock(selectedStockId);
    const counted = selectedCategoryIds;
    const notCounted = allCats.filter(c => !counted.includes(c.id));
    setFeedback({
      stockName: restStocks.find(s => s.id === selectedStockId)?.name || "?",
      savedCount: savedItems,
      savedCategories: allCats.filter(c => counted.includes(c.id)),
      notCountedCategories: notCounted.map(c => ({
        ...c,
        otherUsers: othersInStockCategory(selectedStockId, c.id),
      })),
    });
    setView("fechamento");
    setDrafts({});
  }
  function backToList() {
    setView("list");
    setSelectedStockId(null);
    setSelectedCategoryIds([]);
    setDrafts({});
    setFeedback(null);
  }
  function addCategoryAndCount(catId) {
    setSelectedCategoryIds(prev => prev.includes(catId) ? prev : [...prev, catId]);
    setView("contagem");
    setFeedback(null);
  }

  // ─────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────

  if (myStocks.length === 0) {
    return (
      <div style={{padding:"60px 24px",textAlign:"center",color:"var(--text3)"}}>
        <div style={{fontSize:48,marginBottom:16}}>📦</div>
        <h3 style={{color:"var(--text)",fontSize:18,fontWeight:700,margin:"0 0 8px"}}>Sem atribuições</h3>
        <p style={{fontSize:14,lineHeight:1.6,maxWidth:420,margin:"0 auto"}}>
          O gestor ainda não atribuiu nenhum estoque pra você contar. Peça pra ele te atribuir em <b>Contagens → Atribuições</b>.
        </p>
      </div>
    );
  }

  // VIEW: lista de estoques
  if (view === "list") {
    return (
      <div>
        <h3 style={{color:"var(--text)",fontSize:16,fontWeight:700,margin:"0 0 12px"}}>📦 Selecione o estoque pra contar</h3>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(240px, 1fr))",gap:12}}>
          {myStocks.map(s => {
            const myCatCount = myAssignments.filter(a => a.stockId === s.id).length;
            const status = stockStatus(s.id);
            const allDone = status.total > 0 && status.done === status.total;
            const partial = status.done > 0 && status.done < status.total;
            return (
              <button key={s.id} type="button" onClick={()=>selectStock(s.id)}
                style={{textAlign:"left",background:"var(--card-bg)",border:`1px solid ${allDone?"#15803d44":partial?"#f59e0b44":"var(--border)"}`,borderRadius:12,padding:"14px 16px",cursor:"pointer",transition:"all 0.15s"}}>
                <div style={{fontSize:16,fontWeight:700,color:"var(--text)",marginBottom:4}}>📦 {s.name}</div>
                {s.location && <div style={{fontSize:11,color:"var(--text3)",marginBottom:8}}>📍 {s.location}</div>}
                <div style={{fontSize:12,color:"var(--text2)",marginBottom:8}}>{myCatCount} categoria(s) pra você</div>
                <div style={{display:"flex",alignItems:"center",gap:6,fontSize:12}}>
                  <span style={{color:allDone?"#15803d":partial?"#f59e0b":"var(--text3)",fontWeight:700}}>
                    {allDone ? "🟢" : partial ? "🟡" : "⚪"}
                  </span>
                  <span style={{color:"var(--text3)"}}>
                    {status.done}/{status.total} categoria(s) contada(s) hoje
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // VIEW: seleção de categorias dentro do estoque
  if (view === "categorias") {
    const stock = restStocks.find(s => s.id === selectedStockId);
    if (!stock) { backToList(); return null; }
    const cats = categoriesInStock(selectedStockId);
    return (
      <div>
        <button type="button" onClick={backToList} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px",marginBottom:12}}>← Voltar</button>
        <h3 style={{color:"var(--text)",fontSize:18,fontWeight:700,margin:"0 0 4px"}}>📦 {stock.name}</h3>
        <p style={{color:"var(--text3)",fontSize:13,margin:"0 0 16px",lineHeight:1.5}}>
          Marque as categorias que vai contar agora. As que estão atribuídas a você já vêm marcadas — desmarque se for contar depois, marque outras pra cobrir um colega.
        </p>
        {cats.length === 0 ? (
          <div style={{padding:"32px 16px",textAlign:"center",color:"var(--text3)",fontSize:14,background:"var(--card-bg)",borderRadius:12,border:"1px solid var(--border)"}}>
            Sem produtos cadastrados nesse estoque ainda.
          </div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
            {cats.map(cat => {
              const isMine = isMyCategoryInStock(selectedStockId, cat.id);
              const others = othersInStockCategory(selectedStockId, cat.id);
              const checked = selectedCategoryIds.includes(cat.id);
              const itemCount = restItems.filter(i => i.categoryId === cat.id && (i.stockIds || []).includes(selectedStockId)).length;
              return (
                <label key={cat.id} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:"var(--card-bg)",border:`1px solid ${checked?"#7c9e5e66":"var(--border)"}`,borderRadius:10,cursor:"pointer",opacity:isMine?1:0.85}}>
                  <input type="checkbox" checked={checked} onChange={()=>toggleCategorySelection(cat.id)}
                    style={{cursor:"pointer",width:20,height:20,accentColor:"#7c9e5e"}} />
                  <div style={{flex:1}}>
                    <div style={{fontSize:14,fontWeight:600,color:"var(--text)"}}>{cat.name}</div>
                    <div style={{fontSize:11,color:"var(--text3)",marginTop:2,display:"flex",gap:8,flexWrap:"wrap"}}>
                      <span>{itemCount} produto(s)</span>
                      {isMine && <span style={{color:"#15803d",fontWeight:600}}>· atribuído a você</span>}
                      {!isMine && others.length > 0 && (
                        <span style={{color:"#94a3b8"}}>· {others.map(a => pessoaName(a.userId)).join(", ")} {others.length===1?"conta":"contam"} normalmente</span>
                      )}
                      {!isMine && others.length === 0 && (
                        <span style={{color:"#dc2626"}}>· sem responsável atribuído</span>
                      )}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}
        {cats.length > 0 && (
          <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
            <button type="button" onClick={backToList} style={{...S.btnSecondary,padding:"8px 16px",fontSize:13}}>Cancelar</button>
            <button type="button" onClick={startCounting} disabled={selectedCategoryIds.length === 0}
              style={{background:selectedCategoryIds.length>0?"#7c9e5e":"var(--border)",color:selectedCategoryIds.length>0?"#fff":"var(--text3)",border:"none",borderRadius:8,padding:"8px 18px",fontSize:13,fontWeight:700,cursor:selectedCategoryIds.length>0?"pointer":"not-allowed"}}>
              ▶ Iniciar contagem ({selectedCategoryIds.length})
            </button>
          </div>
        )}
      </div>
    );
  }

  // VIEW: contagem dos itens
  if (view === "contagem") {
    const stock = restStocks.find(s => s.id === selectedStockId);
    if (!stock) { backToList(); return null; }
    const cats = categoriesInStock(selectedStockId).filter(c => selectedCategoryIds.includes(c.id));
    const filledCount = Object.values(drafts).filter(v => v !== "" && v != null).length;
    return (
      <div>
        <button type="button" onClick={()=>setView("categorias")} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px",marginBottom:12}}>← Mudar categorias</button>
        <h3 style={{color:"var(--text)",fontSize:18,fontWeight:700,margin:"0 0 4px"}}>📦 {stock.name}</h3>
        <p style={{color:"var(--text3)",fontSize:13,margin:"0 0 16px"}}>
          Contando: <b>{cats.map(c=>c.name).join(", ")}</b>
        </p>
        <div style={{display:"flex",flexDirection:"column",gap:16,marginBottom:80}}>
          {cats.map(cat => {
            const items = restItems.filter(i => i.categoryId === cat.id && (i.stockIds || []).includes(selectedStockId)).sort((a,b)=>a.name.localeCompare(b.name));
            return (
              <div key={cat.id} style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:12,overflow:"hidden"}}>
                <div style={{padding:"10px 14px",background:"#7c9e5e15",borderBottom:"1px solid var(--border)",fontSize:13,fontWeight:700,color:"var(--text)",textTransform:"uppercase",letterSpacing:0.4}}>
                  {cat.name} <span style={{color:"var(--text3)",fontWeight:400,textTransform:"none",letterSpacing:0,marginLeft:4}}>· {items.length} {items.length===1?"item":"itens"}</span>
                </div>
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                    <thead>
                      <tr style={{borderBottom:"1px solid var(--border)"}}>
                        <th style={{padding:"6px 14px",textAlign:"left",color:"var(--text3)",fontWeight:600,fontSize:10,textTransform:"uppercase",letterSpacing:0.4}}>Item</th>
                        <th style={{padding:"6px 10px",textAlign:"center",color:"var(--text3)",fontWeight:600,fontSize:10,textTransform:"uppercase",letterSpacing:0.4}}>Unid.</th>
                        <th style={{padding:"6px 10px",textAlign:"right",color:"var(--text3)",fontWeight:600,fontSize:10,textTransform:"uppercase",letterSpacing:0.4}}>Última sua</th>
                        <th style={{padding:"6px 10px",textAlign:"right",color:"var(--text3)",fontWeight:600,fontSize:10,textTransform:"uppercase",letterSpacing:0.4,minWidth:120}}>Contagem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(it => {
                        const last = lastCountFor(it.id, selectedStockId);
                        return (
                          <tr key={it.id} style={{borderTop:"1px solid var(--border)"}}>
                            <td style={{padding:"8px 14px",color:"var(--text)",fontWeight:500}}>{it.name}</td>
                            <td style={{padding:"8px 10px",textAlign:"center",color:"var(--text2)"}}>{it.unit ?? "un"}</td>
                            <td style={{padding:"8px 10px",textAlign:"right",color:last?"var(--text2)":"var(--text3)",fontFamily:"'DM Mono',monospace",fontSize:12}}>
                              {last ? last.qty : "—"}
                            </td>
                            <td style={{padding:"4px 10px",textAlign:"right"}}>
                              <input type="number" step="0.01" inputMode="decimal"
                                value={drafts[it.id] ?? ""}
                                onChange={e=>setDrafts(prev=>({...prev, [it.id]: e.target.value}))}
                                placeholder={last?String(last.qty):"—"}
                                style={{...S.input,width:100,textAlign:"right",fontFamily:"'DM Mono',monospace"}} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
        {/* Barra de salvar fixa no rodapé */}
        <div style={{position:"fixed",bottom:0,left:0,right:0,background:"var(--card-bg)",borderTop:"1px solid var(--border)",padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,zIndex:10,boxShadow:"0 -4px 12px rgba(0,0,0,0.05)"}}>
          <span style={{fontSize:12,color:"var(--text3)"}}>{filledCount} item(ns) preenchido(s)</span>
          <button type="button" onClick={saveCounts} disabled={filledCount === 0}
            style={{background:filledCount>0?"#7c9e5e":"var(--border)",color:filledCount>0?"#fff":"var(--text3)",border:"none",borderRadius:8,padding:"10px 22px",fontSize:14,fontWeight:700,cursor:filledCount>0?"pointer":"not-allowed"}}>
            💾 Salvar contagem
          </button>
        </div>
      </div>
    );
  }

  // VIEW: fechamento (resumo + avisos)
  if (view === "fechamento" && feedback) {
    return (
      <div>
        <div style={{textAlign:"center",padding:"24px 16px",background:"#15803d11",border:"1px solid #15803d44",borderRadius:14,marginBottom:20}}>
          <div style={{fontSize:48,marginBottom:8}}>✅</div>
          <h3 style={{color:"#15803d",fontSize:18,fontWeight:700,margin:"0 0 4px"}}>Contagem salva!</h3>
          <p style={{color:"var(--text2)",fontSize:14,margin:0,lineHeight:1.5}}>
            Você contou <b>{feedback.savedCount} {feedback.savedCount===1?"item":"itens"}</b> em <b>{feedback.savedCategories.length} categoria(s)</b> no estoque <b>{feedback.stockName}</b>.
          </p>
          <div style={{fontSize:11,color:"var(--text3)",marginTop:6}}>
            {feedback.savedCategories.map(c => c.name).join(", ")}
          </div>
        </div>
        {feedback.notCountedCategories.length > 0 && (
          <div style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:12,padding:"14px 16px",marginBottom:20}}>
            <div style={{fontSize:14,fontWeight:700,color:"var(--text)",marginBottom:10}}>
              ⏳ Categorias que não foram contadas hoje
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {feedback.notCountedCategories.map(cat => {
                const itemCount = restItems.filter(i => i.categoryId === cat.id && (i.stockIds || []).includes(selectedStockId)).length;
                const others = cat.otherUsers || [];
                return (
                  <div key={cat.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:"var(--bg2,#f8f8f6)",borderRadius:10,flexWrap:"wrap"}}>
                    <div style={{flex:1,minWidth:160}}>
                      <div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>{cat.name}</div>
                      <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>
                        {itemCount} produto(s) ·{" "}
                        {others.length > 0 ? (
                          <span style={{color:"#94a3b8"}}>{others.map(a => pessoaName(a.userId)).join(", ")} {others.length===1?"conta":"contam"} normalmente</span>
                        ) : (
                          <span style={{color:"#dc2626",fontWeight:600}}>sem responsável atribuído</span>
                        )}
                      </div>
                    </div>
                    <button type="button" onClick={()=>addCategoryAndCount(cat.id)}
                      style={{...S.btnSecondary,fontSize:12,padding:"6px 12px",color:"#7c9e5e",borderColor:"#7c9e5e66"}}>
                      Contar agora
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div style={{display:"flex",justifyContent:"center",gap:8}}>
          <button type="button" onClick={backToList} style={{background:"#7c9e5e",color:"#fff",border:"none",borderRadius:8,padding:"10px 22px",fontSize:14,fontWeight:700,cursor:"pointer"}}>
            ✓ Encerrar
          </button>
        </div>
      </div>
    );
  }

  return null;
}

// ═══════════════════════════════════════════════════════════════
// ──  PESSOAS — MIGRAÇÃO + ACESSORS                              ──
// ═══════════════════════════════════════════════════════════════
// Gera pessoas.json a partir dos employees + managers existentes.
// Idempotente: passando os mesmos inputs, produz o mesmo output (IDs determinísticos).
function pessoasMigrate(employees, managers) {
  const pessoas = [];
  const byId = {}; // pessoaId → pessoa (pra merge rápido)

  // 1. Cada employee vira pessoa (isTeam=true no restaurante dele)
  (employees || []).forEach(emp => {
    const rid = emp.restaurantId;
    const pinFromCpf = (emp.cpf || "").replace(/\D/g, "").slice(0, 4).padEnd(4, "0");
    const p = {
      id: `pes_emp_${emp.id}`,
      restaurantIds: rid ? [rid] : [],
      name: emp.name || "",
      cpf: emp.cpf || "",
      pin: pinFromCpf,
      mustChangePin: true,
      email: emp.email || null,
      whatsapp: emp.whatsapp || null,
      isTeam: rid ? { [rid]: true } : {},
      teamData: rid ? {
        [rid]: {
          empCode: emp.empCode ?? null,
          roleId: emp.roleId ?? null,
          admission: emp.admission ?? null,
          inactive: !!emp.inactive,
          inactiveFrom: emp.inactiveFrom ?? null,
          demitidoEm: emp.demitidoEm ?? null,
          isFreela: !!emp.isFreela,
        }
      } : {},
      linkedEmployeeId: emp.id,
      permissions: rid ? {
        [rid]: {
          operational: emp.operationalAreas || {},
          admin: {},
          special: {},
        }
      } : {},
      createdAt: emp.createdAt || new Date().toISOString(),
    };
    pessoas.push(p);
    byId[p.id] = p;
  });

  // 2. Cada manager: merge se tem linkedEmpId/CPF coincidente, senão cria pessoa nova
  (managers || []).forEach(mgr => {
    let target = null;
    if (mgr.linkedEmpId) target = pessoas.find(p => p.linkedEmployeeId === mgr.linkedEmpId);
    if (!target && mgr.cpf) {
      const cpfDigits = mgr.cpf.replace(/\D/g, "");
      if (cpfDigits.length > 0) {
        target = pessoas.find(p => (p.cpf || "").replace(/\D/g, "") === cpfDigits);
      }
    }

    if (!target) {
      const pinFromCpf = (mgr.cpf || "").replace(/\D/g, "").slice(0, 4).padEnd(4, "0");
      target = {
        id: `pes_mgr_${mgr.id}`,
        restaurantIds: [],
        name: mgr.name || "",
        cpf: mgr.cpf || "",
        pin: pinFromCpf,
        mustChangePin: true,
        email: mgr.email || null,
        whatsapp: mgr.whatsapp || null,
        isTeam: {},
        teamData: {},
        linkedManagerId: mgr.id,
        permissions: {},
        createdAt: mgr.createdAt || new Date().toISOString(),
      };
      pessoas.push(target);
      byId[target.id] = target;
    } else {
      target.linkedManagerId = mgr.id;
    }

    // Union de restaurantIds
    const mgrRids = mgr.restaurantIds ?? (mgr.restaurantId ? [mgr.restaurantId] : []);
    mgrRids.forEach(rid => {
      if (!target.restaurantIds.includes(rid)) target.restaurantIds.push(rid);
      if (!target.permissions[rid]) target.permissions[rid] = { operational: {}, admin: {}, special: {} };
      const perms = target.permissions[rid];
      // Admin permissions derivadas de manager.perms
      if (mgr.perms?.tips) perms.admin.tips = true;
      if (mgr.perms?.schedule) perms.admin.schedule = true;
      if (mgr.perms?.vt !== false) perms.admin.vt = true;
      if (mgr.perms?.roles !== false) perms.admin.roles = true;
      if (mgr.perms?.employees !== false) perms.admin.employees = true;
      if (mgr.perms?.comunicados !== false) perms.admin.comunicados = true;
      if (mgr.perms?.faq !== false) perms.admin.faq = true;
      if (mgr.perms?.config !== false) perms.admin.config = true;
      // Especiais
      if (mgr.isDP) perms.special.isDP = true;
      if (mgr.profile === "lider") {
        perms.special.profile = "lider";
        perms.special.areas = mgr.areas || [];
      }
      if (mgr.isMaster) perms.special.isMaster = true;
      // Preserva PIN do manager se existir (mais "oficial" que o derivado do CPF do empregado)
      if (mgr.pin && typeof mgr.pin === "string" && mgr.pin.length === 4) {
        target.pin = mgr.pin;
        target.mustChangePin = !!mgr.mustChangePin;
      }
    });
  });

  return pessoas;
}

// Helpers acessores
/* eslint-disable no-unused-vars */
function pessoaIsTeamFor(pessoa, restaurantId) {
  return !!(pessoa?.isTeam?.[restaurantId]);
}
function pessoaPermsFor(pessoa, restaurantId) {
  return pessoa?.permissions?.[restaurantId] || { operational: {}, admin: {}, special: {} };
}
function pessoaHasAnyAdminArea(pessoa, restaurantId) {
  const a = pessoaPermsFor(pessoa, restaurantId).admin || {};
  return Object.values(a).some(v => v === true);
}
function pessoaHasAnyOperationalArea(pessoa, restaurantId) {
  const a = pessoaPermsFor(pessoa, restaurantId).operational || {};
  return Object.values(a).some(v => v === true);
}
/* eslint-enable no-unused-vars */

// ═══════════════════════════════════════════════════════════════
// ──  APPSHELL — sidebar unificada + header + content            ──
// ═══════════════════════════════════════════════════════════════
// Helper: pessoa tem ao menos 1 perm admin, especial ou é owner?
// Mantido por possível uso futuro — Inbox v7.1 vive embarcado no Início.
// eslint-disable-next-line no-unused-vars
function hasAnyAdminRole(ad, sp, isOwner) {
  if (isOwner) return true;
  if (Object.values(ad || {}).some(v => v === true)) return true;
  if (Object.values(sp || {}).some(v => v === true)) return true;
  return false;
}

// Define estrutura da sidebar derivada de permissões da pessoa
// v7.3: aceita employees pra check defensivo de isTeam
function buildShellSections({ pessoa, restaurantId, isOwner, employees, restaurants, recursos, currentUser }) {
  // Owner tem acesso implícito total — simula permissões completas
  let perms, isTeam;
  if (isOwner) {
    perms = {
      operational: { escalas:true, gorjetas:true, trilhas:true, reunioes:true, contagens:true, compras:true, checklists:true, fichasTecnicas:true, temperaturas:true },
      admin: { tips:true, schedule:true, employees:true, roles:true, vt:true, comunicados:true, faq:true, config:true, pessoas:true },
      special: { isDP:true },
    };
    isTeam = false; // Owner não é equipe do restaurante — "Meu Dia" não aplica
  } else {
    perms = pessoa?.permissions?.[restaurantId] || { operational: {}, admin: {}, special: {} };
    // v7.3 defensivo: isTeam=true se a flag existe OU se há linkedEmployeeId apontando pra
    // employee ATIVO desse restaurante. Cobre casos onde a flag isTeam[rid] não foi setada
    // mas a pessoa de fato tem registro de empregada (caso comum em migrations parciais).
    const flag = !!pessoa?.isTeam?.[restaurantId];
    let linked = false;
    if (!flag && pessoa?.linkedEmployeeId && Array.isArray(employees)) {
      const emp = employees.find(e => e.id === pessoa.linkedEmployeeId);
      if (emp && emp.restaurantId === restaurantId) {
        const isInactive = emp.inactive && emp.inactiveFrom && emp.inactiveFrom <= new Date().toISOString().slice(0,10);
        linked = !isInactive;
      }
    }
    isTeam = flag || linked;
  }
  const op = perms.operational || {};
  const ad = perms.admin || {};
  const sp = perms.special || {};

  // helper: emite subtab se condição for truthy
  const st = (...arr) => arr.filter(Boolean);

  const sections = [];

  // ──────────────────────────────────────────────────────────────────────
  // v7.1 IA — 5 seções: Início, Operação, Planejamento, Escritório, Ajustes
  // ──────────────────────────────────────────────────────────────────────

  // 0) INÍCIO — saudação + Caixa de entrada (Inbox embarcado) + widgets opcionais
  // É a primeira entrada do menu. Sempre visível.
  sections.push({
    group: "Início",
    color: "#7c3aed",
    items: [
      { id: "mod_inicio", label: "Caixa de entrada", icon: "🏠", kind: "manager", tab: "inicio" },
    ],
  });

  // 1) MINHA JORNADA — área PESSOAL do membro da equipe (v7.5)
  // Renomeado de "Meu Dia". Conteúdo limpo: só itens pessoais (extrato, escala, trilhas).
  // Comunicados/FAQ/DP saíram pra seção "Comunicação" (todos).
  // v7.8: tab IDs ajustados pra bater com os do EmployeePortal (extrato, escala) — não com os do admin (gorjeta, schedule).
  if (isTeam) {
    // v8.9.2: itens da Minha Jornada respeitam tabsConfig/tabsGestor do restaurante (admin pode ocultar)
    const restaurantObj = (restaurants || []).find(r => r.id === restaurantId);
    const empTabVisivel = (key) =>
      restaurantObj?.tabsConfig?.[key] !== false && restaurantObj?.tabsGestor?.[key] !== false;
    // v8.10.1: extrato gateado por showTipsToEmployee; escala/trilha/horarios via tabs config.
    const jornadaItems = [
      (restaurantObj?.showTipsToEmployee === true) && { id: "me_extrato", label: "Meu extrato",    icon: "💰", kind: "employee", tab: "extrato" },
      empTabVisivel("escala")   && { id: "me_escala",  label: "Minha escala",   icon: "📅", kind: "employee", tab: "escala"  },
      empTabVisivel("trilha")   && { id: "me_trilha",   label: "Minhas trilhas",  icon: "🎯", kind: "employee", tab: "trilha"   },
      empTabVisivel("horarios") && { id: "me_horarios", label: "Meus horários",   icon: "🕐", kind: "employee", tab: "horarios" },
    ].filter(Boolean);
    sections.push({
      group: "Minha Jornada",
      color: "#d4a017",
      items: jornadaItems,
    });
  }

  // 2) OPERAÇÃO — Ocorrências, CheckLists, Contagens, Temperaturas, Fichas Técnicas
  const opItems = [];
  // Ocorrências (extraído de Reuniões na v7.0)
  if (op.reunioes || ad.employees || isOwner) {
    opItems.push({ id: "mod_ocorrencias", label: "Ocorrências", icon: "🚨", kind: "manager", tab: "ocorrencias" });
  }
  // v8.23.3: aceita as perms novas exec/review além das antigas (configurar)
  const canChecklistOp   = op.checklists || op.checklistsExec || op.checklistsReview;
  const canContagensOp   = op.contagens || op.contagensExec;
  const canTemperaturasOp = op.temperaturas || op.temperaturasExec;
  // Checklists, Contagens, Temperaturas — operacional executa, admin (ad.tips) configura templates
  if (canChecklistOp || ad.tips) {
    opItems.push({
      id: "mod_mise_checklists", label: "Checklists", icon: "✅",
      subtabs: st(
        canChecklistOp && { id: "executar",  label: "Executar",  kind: "operational", tab: "checklists" },
        ad.tips        && { id: "templates", label: "Templates", kind: "manager",     tab: "mise_checklists" },
      ),
    });
  }
  if (canContagensOp || ad.tips) {
    opItems.push({
      id: "mod_mise_contagens", label: "Contagens", icon: "📦",
      subtabs: st(
        canContagensOp && { id: "contar", label: "Contar",    kind: "operational", tab: "contagens" },
        ad.tips        && { id: "config", label: "Configurar", kind: "manager",    tab: "mise_contagens" },
      ),
    });
  }
  if (canTemperaturasOp || ad.tips) {
    opItems.push({
      id: "mod_mise_temperaturas", label: "Temperaturas", icon: "🌡️",
      subtabs: st(
        canTemperaturasOp && { id: "monitor", label: "Monitorar",  kind: "operational", tab: "temperaturas" },
        ad.tips           && { id: "config",  label: "Configurar", kind: "manager",     tab: "mise_temperaturas" },
      ),
    });
  }
  // Fichas Técnicas movida de "Produção" pra Operação (spec v7)
  if (op.fichasTecnicas || ad.tips) {
    opItems.push({
      id: "mod_mise_fichas", label: "Fichas Técnicas", icon: "📋",
      subtabs: st(
        op.fichasTecnicas && { id: "consultar", label: "Consultar", kind: "operational", tab: "fichasTecnicas" },
        ad.tips           && { id: "editar",    label: "Editar",    kind: "manager",     tab: "mise_fichas" },
      ),
    });
  }
  // Reservas — visível pra qualquer perfil que tenha pelo menos a perm operar
  const canReservasOp     = op.reservasOperar || op.reservasOperarForce;
  const canReservasGer    = op.reservasGerenciar;
  const canReservasClient = ad.reservasClientes;
  const canReservasConfig = ad.reservasConfig;
  if (canReservasOp || canReservasGer || canReservasClient || canReservasConfig || isOwner) {
    opItems.push({
      id: "mod_reservas", label: "Reservas", icon: "📅",
      subtabs: st(
        (canReservasOp || canReservasGer || isOwner) && { id: "hoje",      label: "Hoje",          kind: "manager", tab: "reservas_hoje" },
        (canReservasOp || canReservasGer || isOwner) && { id: "calendario", label: "Calendário",   kind: "manager", tab: "reservas_calendario" },
        (canReservasClient || isOwner)               && { id: "clientes",   label: "Clientes",     kind: "manager", tab: "reservas_clientes" },
        (canReservasConfig || isOwner)               && { id: "config",     label: "Configurações", kind: "manager", tab: "reservas_config" },
      ),
    });
  }
  if (opItems.length > 0) {
    sections.push({ group: "Operação", color: "#d4a017", items: opItems });
  }

  // 3) PLANEJAMENTO — Escalas, Freelas, Reuniões, Trilhas, Ideias
  const planItems = [];
  if (op.escalas || ad.schedule) {
    planItems.push({ id: "mod_escalas", label: "Escalas", icon: "📅", kind: "manager", tab: "schedule" });
  }
  // v8.10.5: aceita tanto sp.isLider (matriz nova) quanto sp.profile=="lider" (legacy migração)
  const _isLiderShell = sp.isLider === true || sp.profile === "lider";
  // v8.24.0: aba Freelas aparece se tem qualquer das perms novas (exec/fechar) OU legado (isOwner/isDP/Lider/admin.freelas)
  // v8.30.0: freelasFechar agora vive em operational; ad.freelasFechar mantido como fallback de transição.
  const _canSeeFreelas = isOwner || sp.isDP || _isLiderShell
    || op.freelasExec === true
    || op.freelasFechar === true
    || ad.freelasFechar === true // legacy v8.24-v8.29
    || ad.freelas === true;
  if (_canSeeFreelas) {
    planItems.push({ id: "mod_freelas", label: "Freelas", icon: "🎒", kind: "manager", tab: "freelas" });
  }
  if (op.reunioes) {
    planItems.push({ id: "mod_reunioes", label: "Reuniões", icon: "🗣️", kind: "manager", tab: "reunioes" });
  }
  if (op.trilhas || ad.tips) {
    // v7.0 admin Trilhas: lista de equipe + drilldown no EmpTrilhaView
    planItems.push({ id: "mod_trilhas", label: "Trilhas", icon: "🎯", kind: "manager", tab: "trilhas" });
  }
  // Ideias (extraída de Reuniões na v7.0)
  if (op.reunioes || ad.employees || isOwner) {
    planItems.push({ id: "mod_ideias", label: "Ideias", icon: "💡", kind: "manager", tab: "ideias" });
  }
  if (planItems.length > 0) {
    sections.push({ group: "Planejamento", color: "var(--ac)", items: planItems });
  }

  // 4) ESCRITÓRIO — Pessoas, Equipe, VT, Cargos, Compras, Gorjetas, Comunicados, FAQ, Fale com DP
  const officeItems = [];
  if (ad.pessoas || isOwner) {
    officeItems.push({
      id: "mod_pessoas", label: "Pessoas", icon: "👤",
      subtabs: [
        { id: "cadastro",   label: "Cadastro",   kind: "manager", tab: "pessoas" },
        { id: "permissoes", label: "Permissões", kind: "manager", tab: "permissoes" },
      ],
    });
  }
  // Equipe agrupa Membros + VT + Cargos como sub-tabs (mantém UX existente)
  const equipeSubs = st(
    (ad.employees || isOwner) && { id: "membros", label: "Membros",      kind: "manager", tab: "employees" },
    (ad.vt        || isOwner) && { id: "vt",      label: "Vale Transp.", kind: "manager", tab: "vt" },
    (ad.roles     || isOwner) && { id: "cargos",  label: "Cargos",       kind: "manager", tab: "roles" },
  );
  if (equipeSubs.length > 0) {
    officeItems.push({ id: "mod_equipe", label: "Equipe", icon: "👥", subtabs: equipeSubs });
  }
  // Compras movida de Operação pra Escritório (spec v7)
  if (op.compras || ad.tips) {
    officeItems.push({ id: "mod_mise_compras", label: "Compras", icon: "🛒", kind: "operational", tab: "compras" });
  }
  if (op.gorjetas || ad.tips) {
    officeItems.push({
      id: "mod_gorjetas", label: "Gorjetas", icon: "💰",
      subtabs: st(
        op.gorjetas && { id: "dashboard", label: "Dashboard",   kind: "operational", tab: "gorjetas" },
        ad.tips     && { id: "lancar",    label: "Lançamentos", kind: "manager",     tab: "tips" },
        ad.tips     && { id: "regra",     label: "Regra",       kind: "manager",     tab: "regra_divisao" },
      ),
    });
  }
  // v8.15.0: Recursos — Owner sempre; demais (DP/Líder/admin) só se tiverem ao menos 1 item visível.
  const _isAnyMgr = isOwner || ad.tips || sp.isDP || sp.isLider || sp.profile === "lider" || ad.employees || ad.schedule;
  const _hasVisibleRecursoShell = (() => {
    if (isOwner) return true;
    const items = (recursos ?? []).filter(r => r.restaurantId === restaurantId && !r.deletedAt);
    if (items.length === 0) return false;
    if (sp.isDP === true) return true;
    return items.some(r => {
      const v = r.visibility || { mode: "default", sharedWith: [] };
      const isCreator = r.createdById && r.createdById === currentUser?.id;
      if (v.mode === "private") return isCreator;
      if (v.mode === "shared") return isCreator || (Array.isArray(v.sharedWith) && v.sharedWith.includes(currentUser?.id));
      return true; // default
    });
  })();
  if (_isAnyMgr && _hasVisibleRecursoShell) {
    officeItems.push({ id: "mod_recursos", label: "Recursos", icon: "📚", kind: "manager", tab: "recursos" });
  }
  if (officeItems.length > 0) {
    sections.push({ group: "Escritório", color: "#0284c7", items: officeItems });
  }

  // 4.5) COMUNICAÇÃO — Comunicados, FAQ, Fale com DP (TODOS, v7.5)
  // kind dinâmico: admin renderiza UI de gestão (criar/editar); empregado renderiza UI de leitura.
  // v8.10.1: empregados respeitam tabsConfig/tabsGestor; admins/owner sempre veem (gestão).
  const commItems = [];
  const _restObj = (restaurants || []).find(r => r.id === restaurantId);
  const _empVis = (key) => _restObj?.tabsConfig?.[key] !== false && _restObj?.tabsGestor?.[key] !== false;
  const isAdminCom = ad.comunicados || isOwner;
  if (isAdminCom || (isTeam && _empVis("comunicados"))) {
    commItems.push({ id: "mod_comunicados", label: "Comunicados", icon: "📢", kind: isAdminCom ? "manager" : "employee", tab: "comunicados" });
  }
  if (ad.faq || isOwner) {
    commItems.push({ id: "mod_faq", label: "FAQ", icon: "❓", kind: "manager", tab: "faq" });
  } else if (isTeam && _empVis("faq")) {
    commItems.push({ id: "mod_faq", label: "FAQ", icon: "❓", kind: "employee", tab: "faq" });
  }
  // Fale com DP: empregado envia / admin DP responde
  const isAdminDp = sp.isDP || isOwner;
  if (isAdminDp || (isTeam && _empVis("dp"))) {
    commItems.push({ id: "mod_dp", label: "Fale com DP", icon: "💬", kind: isAdminDp ? "manager" : "employee", tab: "dp" });
  }
  if (commItems.length > 0) {
    sections.push({ group: "Comunicação", color: "#8b5cf6", items: commItems });
  }

  // 5) AJUSTES — Configurações do restaurante (separado de Escritório por escolha do produto)
  if (ad.config || isOwner) {
    sections.push({
      group: "Ajustes",
      color: "#64748b",
      items: [
        { id: "mod_config", label: "Configurações", icon: "⚙️", kind: "manager", tab: "config" },
      ],
    });
  }

  // Nota: seções "Master" (Restaurantes, Financeiro, Admins, Caixa)
  // ficam no OwnerPortal (nível meta), não aqui dentro do restaurante.
  // Owner usa o botão "← Painel" no header do shell pra voltar ao OwnerPortal.

  return sections;
}

function AppShell({ pessoa, data, activeRestaurantId, setActiveRestaurantId, userRole, currentUser,
                    onSwitchToEmployee, onSwitchToManager, onSwitchToOperational, onLogout, onUpdate, onExitShell, onReturnToOwnerHome,
                    impersonatedPessoa, realIsOwner, onStartImpersonate, onStopImpersonate,
                    toggleTheme, theme }) {
  const restaurants = data?.restaurants || [];
  // isOwner real (do user logado). Quando impersonando, NÃO conta como owner pra fins de permissão —
  // queremos ver exatamente o que o impersonado vê, sem bypass.
  const isRealOwnerLocal = userRole === "super" || currentUser?.isMaster === true;
  const isOwner = isRealOwnerLocal && !impersonatedPessoa;
  const accessibleRestaurants = isRealOwnerLocal ? restaurants : (pessoa?.restaurantIds || []).map(rid => restaurants.find(r => r.id === rid)).filter(Boolean);
  const activeRest = restaurants.find(r => r.id === activeRestaurantId);
  const sections = buildShellSections({ pessoa, restaurantId: activeRestaurantId, isOwner, employees: data?.employees ?? [], restaurants: data?.restaurants ?? [], recursos: data?.recursos ?? [], currentUser });
  const allItems = sections.flatMap(s => s.items);

  // Seção ativa do shell (persistida em localStorage)
  const [activeSectionId, setActiveSectionId] = useState(() => {
    return localStorage.getItem("apptip_shell_section") || null;
  });
  useEffect(() => {
    if (activeSectionId) localStorage.setItem("apptip_shell_section", activeSectionId);
  }, [activeSectionId]);

  // Re-alinha quando allItems mudar (ex: pessoa/data carregou depois de refresh, ou perdeu permissão)
  useEffect(() => {
    if (allItems.length === 0) return; // aguarda dados carregarem
    const hasCurrent = activeSectionId && allItems.find(it => it.id === activeSectionId);
    if (!hasCurrent) {
      // Tenta restaurar do localStorage antes de cair no primeiro item
      const saved = localStorage.getItem("apptip_shell_section");
      if (saved && allItems.find(it => it.id === saved)) {
        setActiveSectionId(saved);
      } else {
        setActiveSectionId(allItems[0].id);
      }
    }
  }, [activeSectionId, allItems.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeItem = allItems.find(it => it.id === activeSectionId);

  // Subtab ativa por item (persistida como objeto {itemId: subtabId})
  const [activeSubtabByItem, setActiveSubtabByItem] = useState(() => {
    try { return JSON.parse(localStorage.getItem("apptip_shell_subtabs") || "{}"); }
    catch { return {}; }
  });
  useEffect(() => {
    localStorage.setItem("apptip_shell_subtabs", JSON.stringify(activeSubtabByItem));
  }, [activeSubtabByItem]);

  // Derivação: pra o item ativo, qual subtab tá selecionada?
  const hasSubtabs = !!(activeItem?.subtabs && activeItem.subtabs.length > 0);
  const activeSubtab = hasSubtabs
    ? (activeItem.subtabs.find(s => s.id === activeSubtabByItem[activeItem.id]) || activeItem.subtabs[0])
    : null;
  const effectiveKind = activeSubtab?.kind || activeItem?.kind;
  const effectiveTab  = activeSubtab?.tab  || activeItem?.tab;

  // Counter pra "voltar pro topo" — incrementa quando user clica num item/subtab que já está ativo,
  // forçando o RestaurantPanel/EmployeePortal a re-montar (zera detailEmp, scroll, etc).
  const [resetCounter, setResetCounter] = useState(0);

  function handleSubtabClick(subtabId) {
    if (!activeItem) return;
    // Se a subtab clicada já está ativa, dispara reset (volta pra lista, fecha detalhes)
    if (activeSubtab?.id === subtabId) {
      setResetCounter(c => c + 1);
      return;
    }
    setActiveSubtabByItem(prev => ({ ...prev, [activeItem.id]: subtabId }));
  }

  const ac = "var(--ac)";
  const isMobile = useMobile();

  // Badges de notificação por seção (agora usa IDs novos mod_mise_*)
  const badges = {};
  // Compras: pedidos ativos (aprovado/enviado/aguardando-correção)
  const activeOrders = (data?.miseSupplierOrders || []).filter(o =>
    o.restaurantId === activeRestaurantId &&
    !MISE_ORDER_STATUS[o.status]?.terminal
  );
  if (activeOrders.length > 0) badges.mod_mise_compras = activeOrders.length;
  // Temperaturas: alertas ativos (abertos e não reconhecidos)
  const openTempAlerts = (data?.tempAlerts || []).filter(a =>
    a.restaurantId === activeRestaurantId && !a.closedAt && !a.acknowledgedAt
  );
  if (openTempAlerts.length > 0) badges.mod_mise_temperaturas = `!${openTempAlerts.length}`;

  // Fale com DP: mensagens não lidas do restaurante
  const unreadDp = (data?.dpMessages || []).filter(m =>
    m.restaurantId === activeRestaurantId && !m.read && !m.deleted
  ).length;
  if (unreadDp > 0) badges.mod_dp = unreadDp;

  // Caixa de entrada: só notificações do sistema + inbox (DP tem canal próprio, fica fora)
  if (pessoa?.id) {
    const userRoleForInbox = pessoa?.linkedManagerId ? "manager" : "employee";
    const notifsUnread = (data?.notifications || []).filter(n =>
      n.restaurantId === activeRestaurantId && !n.read && !n.deleted && n.targetRole !== "admin" && n.type !== "upgrade_request"
    ).length;
    let inboxMsgsUnread = 0;
    try {
      inboxMsgsUnread = getInboxForUser({
        inbox: data?.inbox,
        userId: pessoa.linkedManagerId || pessoa.id,
        userRole: userRoleForInbox,
        restaurantId: activeRestaurantId,
        includeRead: false,
      }).length;
    } catch (e) { /* ignore */ }
    const totalInbox = notifsUnread + inboxMsgsUnread;
    if (totalInbox > 0) badges.mod_inbox = totalInbox;
  }
  // Sugestões de compra — calculadas a partir de TODAS as contagens não arquivadas (sem ciclo)
  if (pessoa) {
    try {
      const counts = data?.miseCounts || [];
      const looseCountsForBadge = counts.filter(c => c.restaurantId === activeRestaurantId && !c.archivedAt);
      if (looseCountsForBadge.length > 0) {
        const sugg = miseComputeSuggestedOrders({
          countsToUse: looseCountsForBadge,
          restaurantId: activeRestaurantId,
          items: data?.miseItems || [],
          counts,
          categories: data?.miseCategories || [],
          productSuppliers: data?.miseProductSuppliers || [],
          suppliers: (data?.miseSuppliers || []).filter(s => s.restaurantId === activeRestaurantId),
          overrides: {},
        });
        const suggestedItemsCount = sugg.reduce((s, g) => s + g.items.length, 0);
        if (suggestedItemsCount > 0 && badges.mod_mise_compras) {
          badges.mod_mise_compras = `${badges.mod_mise_compras}+${suggestedItemsCount}`;
        } else if (suggestedItemsCount > 0) {
          badges.mod_mise_compras = `+${suggestedItemsCount}`;
        }
      }
    } catch (e) { /* ignore */ }
  }
  const [sidebarOpen, setSidebarOpen] = useState(() => !isMobile);
  // Sincroniza estado inicial quando isMobile muda (ex: rotação do device)
  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  // Decide o que renderizar no content area
  function renderContent() {
    if (!activeItem) {
      return (
        <div style={{padding:"60px 24px",textAlign:"center",color:"var(--text3)"}}>
          <div style={{fontSize:48,marginBottom:16}}>🔒</div>
          <h3 style={{color:"var(--text)",fontSize:18,fontWeight:700,margin:"0 0 8px"}}>Sem áreas disponíveis</h3>
          <p style={{fontSize:14,lineHeight:1.6,maxWidth:420,margin:"0 auto"}}>
            Você ainda não tem permissões para nenhuma área neste restaurante.
          </p>
        </div>
      );
    }

    // Com subtabs, owner escolhe explicitamente op vs admin — não há mais redirect implícito.
    // Features já standalone: render inline diretamente
    if (effectiveTab === "contagens" && effectiveKind === "operational") {
      return (
        <OperationalContagens
          employee={buildVirtualEmpForPessoa(pessoa, activeRestaurantId)}
          pessoas={data?.pessoas ?? []}
          isOwner={isOwner}
          miseCategories={data?.miseCategories ?? []}
          miseStocks={data?.miseStocks ?? []}
          miseAssignments={data?.miseAssignments ?? []}
          miseItems={data?.miseItems ?? []}
          miseCounts={data?.miseCounts ?? []}
          onUpdate={onUpdate}
        />
      );
    }
    if (effectiveTab === "compras" && effectiveKind === "operational") {
      return (
        <OperationalCompras
          employee={buildVirtualEmpForPessoa(pessoa, activeRestaurantId)}
          data={data}
          onUpdate={onUpdate}
        />
      );
    }
    if (effectiveTab === "checklists" && effectiveKind === "operational") {
      return (
        <OperationalChecklists
          employee={buildVirtualEmpForPessoa(pessoa, activeRestaurantId)}
          pessoas={data?.pessoas ?? []}
          isOwner={isOwner}
          miseChecklistTemplates={data?.miseChecklistTemplates ?? []}
          miseChecklistRuns={data?.miseChecklistRuns ?? []}
          onUpdate={onUpdate}
        />
      );
    }
    if (effectiveTab === "fichasTecnicas" && effectiveKind === "operational") {
      return (
        <OperationalFichasTecnicas
          employee={buildVirtualEmpForPessoa(pessoa, activeRestaurantId)}
          miseFtInsumos={data?.miseFtInsumos ?? []}
          miseFtDishes={data?.miseFtDishes ?? []}
        />
      );
    }
    if (effectiveTab === "temperaturas" && effectiveKind === "operational") {
      return (
        <OperationalTemperaturas
          restaurantId={activeRestaurantId}
          restaurantName={activeRest?.name || ""}
          tempSensors={(data?.tempSensors ?? []).filter(s => s.restaurantId === activeRestaurantId)}
          tempReadings={(data?.tempReadings ?? []).filter(r => r.restaurantId === activeRestaurantId)}
          tempAlerts={(data?.tempAlerts ?? []).filter(a => a.restaurantId === activeRestaurantId)}
          tempBackfillState={data?.tempBackfillState ?? {}}
          tuyaLink={data?.tuyaLinks?.[activeRestaurantId]}
          onUpdate={onUpdate}
          pessoa={pessoa}
        />
      );
    }
    if (effectiveTab === "gorjetas" && effectiveKind === "operational") {
      return (
        <OperationalGorjetas
          employee={buildVirtualEmpForPessoa(pessoa, activeRestaurantId)}
          data={data}
          onUpdate={onUpdate}
          isOwner={isRealOwnerLocal}
        />
      );
    }

    // Embed dos monólitos no shell (Sprint 2)
    // Para kind="manager": renderiza RestaurantPanel com hideTabNav + forceTab
    if (effectiveKind === "manager" && activeRest) {
      const virtualMgr = pessoa?.linkedManagerId
        ? (data?.managers || []).find(m => m.id === pessoa.linkedManagerId)
        : (isOwner ? { id: "owner_virt", name: currentUser?.name || "Owner", cpf: currentUser?.cpf, restaurantIds: [activeRestaurantId], perms: {tips:true,schedule:true}, isMaster: true } : null);
      // v8.10.2: perms reais da pessoa (não mais hardcoded). Owner mantém tudo true; demais herdam do pessoa.permissions[rid].
      const _pessoaPerms = pessoa?.permissions?.[activeRestaurantId] || { admin: {}, special: {} };
      const _ad = _pessoaPerms.admin || {};
      const _sp = _pessoaPerms.special || {};
      const realPerms = isOwner
        ? { tips:true, schedule:true, vt:true, roles:true, employees:true, comunicados:true, faq:true, config:true, isDP:true, managerAreas:[] }
        : {
            tips:        _ad.tips        === true,
            schedule:    _ad.schedule    === true,
            vt:          _ad.vt          === true,
            roles:       _ad.roles       === true,
            employees:   _ad.employees   === true,
            comunicados: _ad.comunicados === true,
            faq:         _ad.faq         === true,
            config:      _ad.config      === true,
            isDP:        _sp.isDP        === true,
            // v8.10.5: aceita tanto profile=="lider" (legacy) quanto special.isLider (matriz nova).
            managerAreas: (_sp.profile === "lider" || _sp.isLider === true) ? (_sp.areas || []) : [],
          };
      return (
        <RestaurantPanel
          key={`rp-${activeRestaurantId}-${effectiveTab}-${resetCounter}`}
          restaurant={activeRest}
          restaurants={data?.restaurants || []}
          employees={data?.employees || []}
          roles={data?.roles || []}
          tips={data?.tips || []}
          splits={data?.splits || {}}
          schedules={data?.schedules || {}}
          onUpdate={onUpdate}
          perms={realPerms}
          isOwner={isOwner}
          data={data}
          currentUser={virtualMgr || currentUser}
          mobileOnly={isMobile}
          hideTabNav={true}
          forceTab={effectiveTab}
          realIsOwner={realIsOwner}
          onStartImpersonate={onStartImpersonate}
        />
      );
    }

    // Para kind="employee": renderiza EmployeePortal com hideHeader + hideTabNav + forceTab
    if (effectiveKind === "employee" && activeRest) {
      // 1) Tenta o linkedEmployeeId direto. 2) Fallback: busca por CPF no restaurante atual.
      // (Pessoas criadas via PessoasAdmin não têm linkedEmployeeId; só as migradas têm.)
      let linkedEmpId = pessoa?.linkedEmployeeId;
      if (!linkedEmpId && pessoa?.cpf) {
        const cpfClean = String(pessoa.cpf).replace(/\D/g, "");
        const matchByCpf = (data?.employees || []).find(e =>
          e.restaurantId === activeRestaurantId && String(e.cpf || "").replace(/\D/g, "") === cpfClean
        );
        if (matchByCpf) linkedEmpId = matchByCpf.id;
      }
      if (!linkedEmpId) {
        return (
          <div style={{padding:"60px 24px",textAlign:"center",color:"var(--text3)"}}>
            <div style={{fontSize:48,marginBottom:16}}>{activeItem.icon}</div>
            <h3 style={{color:"var(--text)",fontSize:18,fontWeight:700,margin:"0 0 8px"}}>{activeItem.label}</h3>
            <p style={{fontSize:14,lineHeight:1.6,maxWidth:460,margin:"0 auto"}}>
              Esta área precisa de um registro de funcionário vinculado a esta pessoa.
              Peça ao gestor para vincular {pessoa?.name ? <b>{pessoa.name}</b> : "a pessoa"} à equipe (aba Pessoas → Editar → marque "É equipe") ou cadastre como empregado em <b>Equipe → Membros</b>.
            </p>
          </div>
        );
      }
      const _empData = filterDataForRestaurant(data, activeRestaurantId);
      return (
        <EmployeePortal
          key={`ep-${activeRestaurantId}-${effectiveTab}-${resetCounter}`}
          employees={_empData?.employees || []}
          roles={_empData?.roles || []}
          tips={_empData?.tips || []}
          schedules={data?.schedules || {}}
          splits={data?.splits || {}}
          restaurants={_empData?.restaurants || []}
          communications={_empData?.communications || []}
          commAcks={data?.commAcks || {}}
          faq={data?.faq || {}}
          dpMessages={_empData?.dpMessages || []}
          workSchedules={data?.workSchedules || {}}
          incidents={_empData?.incidents || []}
          feedbacks={_empData?.feedbacks || []}
          devChecklists={data?.devChecklists || {}}
          employeeGoals={data?.employeeGoals || {}}
          tipApprovals={data?.tipApprovals || {}}
          delays={data?.delays || {}}
          meetingPlans={_empData?.meetingPlans || []}
          inbox={_empData?.inbox || []}
          inboxFolders={data?.inboxFolders || {}}
          pessoas={_empData?.pessoas || []}
          managers={_empData?.managers || []}
          dayInversions={_empData?.dayInversions || []}
          onBack={()=>{}}
          onUpdateEmployee={(emp)=>{
            const next = (data?.employees || []).map(e => e.id === emp.id ? emp : e);
            onUpdate("employees", next);
          }}
          onUpdate={onUpdate}
          toggleTheme={toggleTheme}
          theme={theme}
          hideHeader={true}
          hideTabNav={true}
          forceTab={effectiveTab}
          forceEmpId={linkedEmpId}
        />
      );
    }

    // Outras áreas: delegação para portais antigos via botão
    const kindToAction = {
      employee:    () => onSwitchToEmployee && onSwitchToEmployee(effectiveTab),
      manager:     () => onSwitchToManager && onSwitchToManager(effectiveTab),
      operational: () => onSwitchToOperational && onSwitchToOperational(effectiveTab),
      super:       () => onSwitchToManager && onSwitchToManager(effectiveTab), // super → OwnerPortal handler
    };
    const launch = kindToAction[effectiveKind];
    const portalLabel = {
      employee: "Portal do Empregado",
      manager: "Portal do Gestor Administrativo",
      operational: "Portal do Gestor Operacional",
      super: "Portal Super",
    }[effectiveKind] || "Portal";
    return (
      <div style={{padding:"60px 24px",textAlign:"center",color:"var(--text3)"}}>
        <div style={{fontSize:48,marginBottom:16}}>{activeItem.icon}</div>
        <h3 style={{color:"var(--text)",fontSize:20,fontWeight:700,margin:"0 0 10px"}}>{activeItem.label}</h3>
        <p style={{fontSize:14,lineHeight:1.6,maxWidth:460,margin:"0 auto 20px"}}>
          Essa área ainda vive no <b>{portalLabel}</b> (o refactor pro shell está em andamento). Abre lá num clique:
        </p>
        <button onClick={launch}
          style={{background:ac,color:"#fff",border:"none",borderRadius:10,padding:"10px 22px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
          🚀 Abrir em {portalLabel}
        </button>
      </div>
    );
  }

  function handleSectionClick(id) {
    // Se o item clicado já é o ativo, dispara reset (volta pra lista, fecha detalhes)
    if (id === activeSectionId) {
      setResetCounter(c => c + 1);
      if (isMobile) setSidebarOpen(false);
      return;
    }
    setActiveSectionId(id);
    // Reset subtab pra default (primeira) ao clicar na sidebar
    // UX: cada clique na sidebar = "fresh start", abre sempre na visão principal (Executar/Contar/Dashboard/etc)
    // Navegação entre subtabs dentro do item continua persistindo em sessão
    const newItem = allItems.find(it => it.id === id);
    if (newItem?.subtabs?.length > 0) {
      setActiveSubtabByItem(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
    if (isMobile) setSidebarOpen(false);
  }

  return (
    <div style={{height:"100vh",maxHeight:"100vh",background:"var(--bg)",fontFamily:"'DM Sans',sans-serif",display:"flex",flexDirection:"column",overflow:"hidden"}}>
      {/* HEADER */}
      <header style={{background:"var(--header-bg)",borderBottom:"1px solid var(--border)",padding:isMobile?"8px 10px":"10px 16px",display:"flex",alignItems:"center",gap:isMobile?6:12,flexWrap:"nowrap",overflowX:"auto"}}>
        <button onClick={()=>setSidebarOpen(o=>!o)} title={sidebarOpen?"Fechar menu":"Abrir menu"} style={{background:sidebarOpen?"var(--bg2)":"none",border:"none",cursor:"pointer",fontSize:20,color:"var(--text2)",padding:"4px 10px",borderRadius:8,flexShrink:0}}>☰</button>
        {!isMobile && <div style={{fontSize:14,fontWeight:800,color:"var(--text)",flexShrink:0}}>Sistema</div>}
        {accessibleRestaurants.length > 0 && (
          <>
            <button
              onClick={()=>setActiveSectionId("mod_inicio")}
              title="Início — saudação e visão geral"
              style={{
                background: activeSectionId === "mod_inicio" ? "#7c3aed22" : "var(--bg1)",
                border: `1px solid ${activeSectionId === "mod_inicio" ? "#7c3aed" : "var(--border)"}`,
                borderRadius: 8,
                padding: isMobile ? "6px 9px" : "4px 9px",
                fontSize: isMobile ? 14 : 15,
                cursor: "pointer",
                lineHeight: 1,
                flexShrink: 0,
              }}>
              🏠
            </button>
            <select value={activeRestaurantId ?? ""} onChange={e=>setActiveRestaurantId(e.target.value)}
              style={{background:"var(--bg1)",border:"1px solid var(--border)",borderRadius:8,padding:isMobile?"6px 8px":"4px 10px",fontSize:isMobile?12:13,color:"var(--text)",fontFamily:"'DM Sans',sans-serif",cursor:"pointer",maxWidth:isMobile?180:"none",minWidth:isMobile?100:"auto",flexShrink:0,textOverflow:"ellipsis"}}>
              {accessibleRestaurants.length > 1 && <option value="">— Selecione —</option>}
              {accessibleRestaurants.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </>
        )}
        <div style={{flex:1,minWidth:0}}/>
        {!isMobile && (
          <div style={{fontSize:13,color:"var(--text2)",fontWeight:500,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
            👤 {pessoa?.name || currentUser?.name || "—"}
            {isOwner && <span style={{marginLeft:6,fontSize:10,padding:"1px 6px",borderRadius:6,background:"#a855f722",color:"#a855f7",fontWeight:700}}>MASTER</span>}
          </div>
        )}
        {isOwner && onReturnToOwnerHome && (
          <button onClick={onReturnToOwnerHome} title="Voltar ao Painel"
            style={{background:"#a855f722",border:"1px solid #a855f744",borderRadius:8,padding:isMobile?"4px 8px":"5px 10px",cursor:"pointer",fontSize:isMobile?10:11,color:"#a855f7",fontWeight:700,fontFamily:"'DM Sans',sans-serif",flexShrink:0}}>
            ← Painel
          </button>
        )}
        <button onClick={toggleTheme} style={{background:"none",border:"1px solid var(--border)",borderRadius:20,padding:isMobile?"4px 8px":"5px 10px",cursor:"pointer",fontSize:isMobile?13:14,color:"var(--text2)",flexShrink:0}}>{theme==="dark"?"☀️":"🌙"}</button>
        <button onClick={onLogout} style={{...S.btnSecondary,fontSize:isMobile?10:11,padding:isMobile?"4px 8px":"5px 12px",flexShrink:0}}>Sair</button>
      </header>

      {/* Banner de impersonate (owner vendo como outra pessoa) */}
      {impersonatedPessoa && onStopImpersonate && (
        <div style={{background:"#a855f7",color:"#fff",padding:"6px 14px",fontSize:12,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
          <div>🎭 Vendo como <b>{impersonatedPessoa.name}</b> — você só vê o que ela veria.</div>
          <button onClick={onStopImpersonate} style={{background:"#fff",color:"#a855f7",border:"none",borderRadius:6,padding:"3px 12px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:"'DM Sans',sans-serif"}}>Sair do modo</button>
        </div>
      )}

      {/* BODY: sidebar + content */}
      <div style={{flex:1,display:"flex",overflow:"hidden",position:"relative"}}>
        {/* SIDEBAR */}
        {sidebarOpen && (
          <>
            {isMobile && (
              <div onClick={()=>setSidebarOpen(false)}
                style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.5)",zIndex:100}} />
            )}
            <aside style={{
              width:isMobile?"80%":240,
              maxWidth:isMobile?320:240,
              flexShrink:0,
              background:"var(--bg1)",
              borderRight:"1px solid var(--border)",
              overflowY:"auto",
              padding:"12px 0",
              ...(isMobile ? {position:"fixed",top:0,left:0,bottom:0,zIndex:101,paddingTop:60,boxShadow:"2px 0 16px rgba(0,0,0,0.2)"} : {}),
            }}>
              {isMobile && (
                <div style={{padding:"6px 16px 10px",borderBottom:"1px solid var(--border)",marginBottom:10,fontSize:12,color:"var(--text2)",fontWeight:600}}>
                  👤 {pessoa?.name || currentUser?.name || "—"}
                  {isOwner && <span style={{marginLeft:6,fontSize:10,padding:"1px 6px",borderRadius:6,background:"#a855f722",color:"#a855f7",fontWeight:700}}>SUPER</span>}
                </div>
              )}
              {!activeRest && accessibleRestaurants.length > 0 ? (
                <div style={{padding:"16px",fontSize:12,color:"var(--text3)",textAlign:"center"}}>Selecione um restaurante no topo pra ver as áreas disponíveis.</div>
              ) : sections.length === 0 ? (
                <div style={{padding:"16px",fontSize:12,color:"var(--text3)",textAlign:"center"}}>Nenhuma área disponível.</div>
              ) : sections.filter(s => !s.hidden).map((sec, secIdx) => (
                <div key={sec.group || `__sec_${secIdx}`} style={{marginBottom:14}}>
                  {sec.group && <div style={{padding:"4px 16px",fontSize:10,color:sec.color,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5}}>{sec.group}</div>}
                  {sec.items.map(it => {
                    const active = it.id === activeSectionId;
                    const badge = badges[it.id];
                    return (
                      <button key={it.id} onClick={()=>handleSectionClick(it.id)}
                        style={{
                          display:"flex",alignItems:"center",gap:10,width:"100%",background:active?sec.color+"22":"transparent",
                          border:"none",borderLeft:`3px solid ${active?sec.color:"transparent"}`,
                          padding:isMobile?"13px 16px 13px 13px":"10px 16px 10px 13px",
                          minHeight:isMobile?44:"auto",
                          cursor:"pointer",fontSize:isMobile?14:13,
                          color:active?"var(--text)":"var(--text2)",fontWeight:active?700:500,
                          fontFamily:"'DM Sans',sans-serif",textAlign:"left",
                        }}>
                        <span style={{fontSize:15}}>{it.icon}</span>
                        <span style={{flex:1}}>{it.label}</span>
                        {badge != null && (
                          <span style={{
                            background: sec.color, color: "#fff",
                            padding: badge === "•" ? "1px 5px" : "1px 7px",
                            borderRadius: 10, fontSize: 10, fontWeight: 700,
                            minWidth: 18, textAlign: "center", fontFamily: "'DM Sans',sans-serif",
                          }}>{badge}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </aside>
          </>
        )}

        {/* CONTENT WRAPPER — flex column: header fixo no topo + main scrollável */}
        <div style={{flex:1,display:"flex",flexDirection:"column",minWidth:0,minHeight:0}}>
          {/* Header fixo (fora do scroll do main) — sempre visível por estrutura, não depende de sticky */}
          {(activeRest || (hasSubtabs && activeItem.subtabs.length > 1)) && (
            <div style={{
              flex:"0 0 auto",
              background:"var(--bg)",
              padding: isMobile ? "10px 14px 0" : "20px 24px 0",
              borderBottom: isMobile && !(hasSubtabs && activeItem.subtabs.length > 1) ? "1px solid var(--border)" : "none",
            }}>
              {activeRest && (
                <div style={{marginBottom:isMobile?8:12,padding:isMobile?"6px 12px":"8px 14px",background:"var(--bg2)",borderRadius:10,fontSize:12,color:"var(--text3)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  <b style={{color:"var(--text)"}}>{activeRest.name}</b> · {activeItem?.label || "—"}
                  {!isMobile && hasSubtabs && activeItem.subtabs.length > 1 && activeSubtab && (
                    <span> · <span style={{color:"var(--text2)"}}>{activeSubtab.label}</span></span>
                  )}
                </div>
              )}
              {hasSubtabs && activeItem.subtabs.length > 1 && (
                <div style={{
                  display:"flex", gap:2, borderBottom:"1px solid var(--border)",
                  overflowX:"auto", flexWrap:"nowrap",
                  WebkitOverflowScrolling:"touch",
                }}>
                  {activeItem.subtabs.map(sub => {
                    const active = sub.id === activeSubtab.id;
                    return (
                      <button key={sub.id} onClick={()=>handleSubtabClick(sub.id)}
                        style={{
                          background:"none", border:"none",
                          borderBottom:`2px solid ${active ? "var(--ac)" : "transparent"}`,
                          padding:isMobile?"11px 16px":"9px 16px",
                          fontSize:13, cursor:"pointer",
                          color: active ? "var(--text)" : "var(--text3)",
                          fontWeight: active ? 700 : 500,
                          fontFamily:"'DM Sans',sans-serif",
                          whiteSpace:"nowrap",
                          marginBottom:-1,
                          minHeight:isMobile?44:"auto",
                        }}>
                        {sub.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {/* Main scrollável — dentro dele os componentes podem ter scrolls internos próprios */}
          <main style={{flex:1,overflowY:"auto",padding:isMobile?"12px 14px":"20px 24px",paddingTop:isMobile?14:18,minWidth:0,minHeight:0,position:"relative"}}>
            {renderContent()}
          </main>
        </div>
      </div>
    </div>
  );
}

// Helper: constrói um empregado virtual a partir da pessoa + restaurantId
// Usado quando features operacionais (Contagens, Compras, etc.) esperam um employee object
function buildVirtualEmpForPessoa(pessoa, restaurantId) {
  if (!pessoa) return { id: "virt_empty", restaurantId, name: "—", operationalAreas: {} };
  const perms = pessoa?.permissions?.[restaurantId] || { operational: {} };
  const op = perms.operational || {};
  // v8.23.2: normaliza operationalAreas pra que perms novas (xxxExec / configurar / Review)
  // acendam a aba correspondente no OperationalPortal.
  // Antes: só `op.checklists` ligava a aba "checklists" — agora qualquer das 3 perms (config/exec/review) liga.
  const normalizedAreas = {
    ...op,
    contagens:      op.contagens === true || op.contagensExec === true,
    checklists:     op.checklists === true || op.checklistsExec === true || op.checklistsReview === true,
    temperaturas:   op.temperaturas === true || op.temperaturasExec === true,
    fichasTecnicas: op.fichasTecnicas === true,
    compras:        op.compras === true,
    escalas:        op.escalas === true,
    gorjetas:       op.gorjetas === true,
    trilhas:        op.trilhas === true,
    reunioes:       op.reunioes === true,
  };
  return {
    id: pessoa.linkedEmployeeId || `pes_as_emp_${pessoa.id}`,
    restaurantId,
    name: pessoa.name,
    cpf: pessoa.cpf || "",
    operationalAreas: normalizedAreas,
    _pessoaId: pessoa.id,
  };
}

// ═══════════════════════════════════════════════════════════════
// ──  PESSOAS — CRUD                                             ──
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// ──  FREELAS — módulo dedicado (Fase 1: fundação + 3 sub-tabs)  ──
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// ──  INÍCIO — saudação amigável (V0, dashboard chega depois)    ──
// ═══════════════════════════════════════════════════════════════
function InicioModule({ currentUser, restaurant, isOwner, isDP, isLider, mobileOnly, setTab, perms }) {
  const ac = "#7c3aed";
  const now = new Date();
  const hora = now.getHours();
  const saudacao = hora < 5 ? "Boa madrugada" : hora < 12 ? "Bom dia" : hora < 18 ? "Boa tarde" : "Boa noite";
  const emoji = hora < 5 ? "🌙" : hora < 12 ? "☀️" : hora < 18 ? "🌤️" : "🌆";
  const primeiroNome = (currentUser?.name || "").split(" ")[0] || "";
  const diaSemana = now.toLocaleDateString("pt-BR", { weekday: "long" });
  const dataExt = now.toLocaleDateString("pt-BR", { day: "numeric", month: "long", year: "numeric" });
  // Capitaliza primeira letra (pt-BR vem minúsculo)
  const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
  // v8.10.3: empregado puro (sem nenhuma permissão admin/líder/DP) só vê saudação + caixa de entrada.
  const hasAnyAdmin = isOwner || isDP || isLider
    || perms?.tips || perms?.schedule || perms?.config
    || perms?.employees || perms?.roles || perms?.vt
    || perms?.comunicados || perms?.faq;
  const papel = isOwner ? "Master" : isDP ? "Departamento Pessoal" : isLider ? "Líder de Área" : hasAnyAdmin ? "Gestor" : "Equipe";

  return (
    <div style={{maxWidth:760,margin:"0 auto",padding:mobileOnly?"8px 4px":"16px 8px"}}>
      {/* Card principal de boas-vindas */}
      <div style={{
        background:`linear-gradient(135deg, ${ac}15, ${ac}05)`,
        border:`1px solid ${ac}33`,
        borderRadius:16,
        padding:mobileOnly?"22px 20px":"32px 30px",
        marginBottom:18,
      }}>
        <div style={{fontSize:mobileOnly?42:54,marginBottom:10,lineHeight:1}}>{emoji}</div>
        <div style={{fontSize:mobileOnly?22:28,fontWeight:800,color:"var(--text)",lineHeight:1.2,marginBottom:6}}>
          {saudacao}{primeiroNome ? `, ${primeiroNome}` : ""} 👋
        </div>
        <div style={{fontSize:mobileOnly?13:14,color:"var(--text2)",lineHeight:1.5}}>
          {cap(diaSemana)}, {dataExt}
        </div>
        {restaurant?.name && (
          <div style={{marginTop:14,paddingTop:14,borderTop:`1px dashed ${ac}33`,fontSize:13,color:"var(--text3)"}}>
            <span style={{color:"var(--text2)"}}>{restaurant.name}</span>
            <span style={{margin:"0 6px",opacity:0.5}}>·</span>
            <span style={{color:ac,fontWeight:600}}>{papel}</span>
          </div>
        )}
      </div>

      {/* v8.10.3: painel "chegando" só para gestores. Empregado vai direto pra Caixa de entrada. */}
      {hasAnyAdmin && (
        <div style={{
          background:"var(--card-bg)",
          border:"1px dashed var(--border)",
          borderRadius:12,
          padding:mobileOnly?"22px 18px":"28px 24px",
          textAlign:"center",
        }}>
          <div style={{fontSize:36,marginBottom:10,opacity:0.6}}>📊</div>
          <div style={{fontWeight:600,color:"var(--text)",fontSize:14,marginBottom:6}}>Seu painel está chegando</div>
          <p style={{fontSize:12,color:"var(--text3)",lineHeight:1.6,maxWidth:440,margin:"0 auto"}}>
            Em breve você vai ver aqui os números do dia, pendências, atalhos e um feed das últimas atividades do restaurante.
          </p>
          {!mobileOnly && (() => {
            // v8.4.5 — gate atalhos por permissão. Usuário sem acesso à feature não vê o atalho.
            // Owner sempre vê todos. Demais dependem de perms.
            const canFreelas  = isOwner || isDP || isLider;
            const canSchedule = isOwner || perms?.operational?.escalas === true || perms?.admin?.schedule === true;
            const canTips     = isOwner || perms?.operational?.gorjetas === true || perms?.admin?.tips === true;
            const shortcuts = [
              canFreelas  && { tab: "freelas",   label: "🎒 Ir pra Freelas",  highlight: true },
              canSchedule && { tab: "schedule",  label: "📅 Ver Escala",      highlight: false },
              canTips     && { tab: "dashboard", label: "💰 Ver Gorjetas",    highlight: false },
            ].filter(Boolean);
            if (shortcuts.length === 0) return null;
            return (
              <div style={{marginTop:16,display:"flex",gap:8,justifyContent:"center",flexWrap:"wrap"}}>
                {shortcuts.map(s => (
                  <button key={s.tab} onClick={()=>setTab && setTab(s.tab)}
                    style={{
                      background:"transparent",
                      color: s.highlight ? ac : "var(--text2)",
                      border: `1px solid ${s.highlight ? ac+"55" : "var(--border)"}`,
                      borderRadius:8, padding:"6px 14px", fontSize:12, fontWeight:600, cursor:"pointer",
                    }}>{s.label}</button>
                ))}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// v8.15.0 — Recursos: hub de pastas + itens (links / notas) por restaurante.
function RecursosTab({ restaurantId, recursoFolders, recursos, recursosInitialized, employees, pessoas, managers, currentUser, isOwner, isDP, isLider, mobileOnly, onUpdate }) {
  const ac = "#0284c7"; // azul Escritório
  // Pastas e itens deste restaurante
  const restFolders = (recursoFolders ?? []).filter(f => f.restaurantId === restaurantId && !f.deletedAt).sort((a,b) => (a.order ?? 999) - (b.order ?? 999) || a.name.localeCompare(b.name));
  const restItems = (recursos ?? []).filter(r => r.restaurantId === restaurantId && !r.deletedAt);
  // Auto-init pastas exemplo no primeiro acesso (Owner only)
  const initFlag = recursosInitialized?.[restaurantId];
  const DEFAULT_FOLDERS = [
    { name: "Compras",                          icon: "🛒" },
    { name: "Departamento Pessoal",             icon: "👥" },
    { name: "Financeiro",                       icon: "💰" },
    { name: "Documentos, Contratos e Seguros",  icon: "📄" },
    { name: "Comunicação e Marketing",          icon: "📣" },
    { name: "Outros",                           icon: "🔧" },
  ];
  useEffect(() => {
    if (!isOwner) return;
    if (initFlag) return;
    if (restFolders.length > 0) {
      // Já tem pastas — só marca o flag pra não rodar de novo
      onUpdate("recursosInitialized", { ...(recursosInitialized ?? {}), [restaurantId]: true });
      return;
    }
    // Cria as 6 pastas exemplo
    const now = new Date().toISOString();
    const newFolders = DEFAULT_FOLDERS.map((f, idx) => ({
      id: `folder-${Date.now()}-${idx}-${Math.random().toString(36).slice(2,5)}`,
      restaurantId, name: f.name, icon: f.icon, order: idx,
      createdAt: now, createdBy: currentUser?.name || "Owner", createdById: currentUser?.id,
    }));
    onUpdate("recursoFolders", [...(recursoFolders ?? []), ...newFolders]);
    onUpdate("recursosInitialized", { ...(recursosInitialized ?? {}), [restaurantId]: true });
  }, [restaurantId, isOwner, initFlag]); // eslint-disable-line react-hooks/exhaustive-deps

  // State da UI
  const [activeFolderId, setActiveFolderId] = useState(null);
  const [search, setSearch] = useState("");
  // Modal item
  const [itemForm, setItemForm] = useState(null); // null | {id?, folderId, name, type, url, content, tags, visibility:{mode,sharedWith}, editores:[]}
  // Modal pasta
  const [folderForm, setFolderForm] = useState(null);
  // Modal preview de link
  const [previewItem, setPreviewItem] = useState(null); // null | { name, url }
  const [previewBlocked, setPreviewBlocked] = useState(false);
  // Pasta selecionada (default = primeira). Para não-Owner, default é a primeira pasta com itens visíveis.
  useEffect(() => {
    const pool = isOwner ? restFolders : restFolders.filter(f => restItems.some(r => r.folderId === f.id && canSeeItem(r)));
    if (!activeFolderId && pool.length > 0) setActiveFolderId(pool[0].id);
    if (activeFolderId && !pool.find(f => f.id === activeFolderId)) setActiveFolderId(pool[0]?.id ?? null);
  }, [restFolders, restItems, activeFolderId, isOwner]); // eslint-disable-line react-hooks/exhaustive-deps

  // v8.15.1: Lista de gestores/líderes elegíveis pra delegação de edição
  const eligibleResponsaveis = (() => {
    const seenIds = new Set(), seenCpfs = new Set(), result = [];
    const cpfClean = s => (s || "").replace(/\D/g, "");
    (pessoas ?? []).forEach(p => {
      if (!(p.restaurantIds ?? []).includes(restaurantId)) return;
      const perms = p.permissions?.[restaurantId];
      if (!perms) return;
      const hasAdmin = Object.values(perms.admin ?? {}).some(v => v === true);
      const isLid = perms.special?.isLider === true || perms.special?.profile === "lider";
      const isDpP = perms.special?.isDP === true;
      if (!hasAdmin && !isLid && !isDpP) return;
      seenIds.add(p.id);
      if (p.linkedManagerId) seenIds.add(p.linkedManagerId);
      if (p.cpf) seenCpfs.add(cpfClean(p.cpf));
      result.push({ id: p.id, name: p.name });
    });
    (managers ?? []).forEach(m => {
      if (!(m.restaurantIds ?? []).includes(restaurantId)) return;
      if (seenIds.has(m.id)) return;
      const mcpf = cpfClean(m.cpf);
      if (mcpf && seenCpfs.has(mcpf)) return;
      seenIds.add(m.id); if (mcpf) seenCpfs.add(mcpf);
      result.push({ id: m.id, name: m.name });
    });
    return result.sort((a,b) => a.name.localeCompare(b.name));
  })();

  // Visibilidade
  const isCreatorOf = (item) => item.createdById && item.createdById === currentUser?.id;
  const canSeeItem = (item) => {
    if (isOwner) return true;
    if (isDP) return true;
    const v = item.visibility || { mode: "default", sharedWith: [] };
    if (v.mode === "private") return isCreatorOf(item);
    if (v.mode === "shared") return isCreatorOf(item) || (Array.isArray(v.sharedWith) && v.sharedWith.includes(currentUser?.id));
    return true; // default: todos os gestores que abrem essa aba
  };
  // v8.15.1: edição = Owner OU DP OU criador (legado) OU editor delegado pelo Owner
  const canEditItem = (item) => {
    if (isOwner) return true;
    if (isDP) return true;
    if (isCreatorOf(item)) return true;
    if (Array.isArray(item.editores) && item.editores.includes(currentUser?.id)) return true;
    return false;
  };

  // Itens da pasta ativa, filtrados por busca + visibilidade
  const visibleItems = restItems
    .filter(r => canSeeItem(r))
    .filter(r => activeFolderId ? r.folderId === activeFolderId : true)
    .filter(r => {
      if (!search.trim()) return true;
      const s = search.toLowerCase();
      return (r.name || "").toLowerCase().includes(s)
          || (r.url || "").toLowerCase().includes(s)
          || (r.content || "").toLowerCase().includes(s)
          || (r.tags || []).some(t => t.toLowerCase().includes(s));
    })
    .sort((a,b) => (b.updatedAt || b.createdAt || "").localeCompare(a.updatedAt || a.createdAt || ""));

  // Folder actions
  function startNewFolder() { setFolderForm({ name: "", icon: "🔧" }); }
  function startEditFolder(f) { setFolderForm({ id: f.id, name: f.name, icon: f.icon || "🔧" }); }
  function saveFolder() {
    if (!folderForm.name.trim()) return;
    const isEdit = !!folderForm.id;
    const now = new Date().toISOString();
    if (isEdit) {
      const next = (recursoFolders ?? []).map(f => f.id === folderForm.id ? { ...f, name: folderForm.name.trim(), icon: folderForm.icon || "🔧" } : f);
      onUpdate("recursoFolders", next);
    } else {
      const newF = {
        id: `folder-${Date.now()}-${Math.random().toString(36).slice(2,5)}`,
        restaurantId, name: folderForm.name.trim(), icon: folderForm.icon || "🔧",
        order: (restFolders.at(-1)?.order ?? 0) + 1,
        createdAt: now, createdBy: currentUser?.name || "—", createdById: currentUser?.id,
      };
      onUpdate("recursoFolders", [...(recursoFolders ?? []), newF]);
      setActiveFolderId(newF.id);
    }
    setFolderForm(null);
  }
  async function delFolder(f) {
    const itemsInside = restItems.filter(r => r.folderId === f.id).length;
    if (!await appConfirm(`Excluir a pasta "${f.name}"?\n${itemsInside > 0 ? `${itemsInside} item(ns) dentro também vão pra lixeira.` : ""}`)) return;
    const now = new Date().toISOString();
    onUpdate("recursoFolders", (recursoFolders ?? []).map(x => x.id === f.id ? { ...x, deletedAt: now } : x));
    if (itemsInside > 0) {
      onUpdate("recursos", (recursos ?? []).map(r => r.folderId === f.id ? { ...r, deletedAt: now } : r));
    }
  }

  // Item actions — v8.15.1: criação só pelo Owner; editores delegados podem editar item.
  function startNewItem(folderId) {
    setItemForm({
      folderId, name: "", type: "link", url: "", content: "",
      tags: [],
      visibility: { mode: "default", sharedWith: [] },
      editores: [],
    });
  }
  function startEditItem(r) {
    setItemForm({
      id: r.id, folderId: r.folderId, name: r.name || "", type: r.type || "link",
      url: r.url || "", content: r.content || "",
      tags: [...(r.tags || [])],
      visibility: { mode: r.visibility?.mode || "default", sharedWith: [...(r.visibility?.sharedWith || [])] },
      editores: Array.isArray(r.editores) ? [...r.editores] : [],
    });
  }
  function saveItem() {
    if (!itemForm.name.trim()) { window.alert("Informe o nome do recurso."); return; }
    if (itemForm.type === "link" && !itemForm.url.trim()) { window.alert("Informe a URL."); return; }
    const now = new Date().toISOString();
    const isEdit = !!itemForm.id;
    const existing = isEdit ? (recursos ?? []).find(r => r.id === itemForm.id) : null;
    const item = {
      id: existing?.id || `rec-${Date.now()}-${Math.random().toString(36).slice(2,5)}`,
      restaurantId, folderId: itemForm.folderId,
      name: itemForm.name.trim(),
      type: itemForm.type,
      ...(itemForm.type === "link" ? { url: itemForm.url.trim() } : { content: itemForm.content }),
      tags: (itemForm.tags || []).filter(Boolean),
      visibility: {
        mode: itemForm.visibility.mode || "default",
        sharedWith: itemForm.visibility.mode === "shared" ? [...(itemForm.visibility.sharedWith || [])] : [],
      },
      // v8.15.1: editores só são gravados pelo Owner. Outros mantêm o que estava.
      editores: isOwner ? [...(itemForm.editores || [])] : (existing?.editores || []),
      createdAt: existing?.createdAt || now,
      createdBy: existing?.createdBy || (currentUser?.name || "—"),
      createdById: existing?.createdById || currentUser?.id || null,
      ...(isEdit ? { updatedAt: now, updatedBy: currentUser?.name || "—", updatedById: currentUser?.id || null } : {}),
    };
    if (isEdit) {
      onUpdate("recursos", (recursos ?? []).map(r => r.id === itemForm.id ? item : r));
    } else {
      onUpdate("recursos", [...(recursos ?? []), item]);
    }
    setItemForm(null);
  }
  async function delItem(r) {
    if (!await appConfirm(`Excluir "${r.name}"? Vai pra lixeira (30 dias).`)) return;
    onUpdate("recursos", (recursos ?? []).map(x => x.id === r.id ? { ...x, deletedAt: new Date().toISOString() } : x));
  }

  // Lista pra "compartilhar com" no item
  const shareableManagers = eligibleResponsaveis.filter(p => p.id !== currentUser?.id);

  // Pastas visíveis: Owner vê todas (precisa pra organizar). Demais só veem pastas com ao menos 1 item visível.
  const visibleFolders = isOwner
    ? restFolders
    : restFolders.filter(f => restItems.some(r => r.folderId === f.id && canSeeItem(r)));
  const activeFolder = visibleFolders.find(f => f.id === activeFolderId) || null;

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:16}}>
        <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?16:20}}>📚 Recursos</h3>
        {isOwner && (
          <button onClick={startNewFolder} style={{...S.btnSecondary,fontSize:12,padding:"6px 14px",color:ac,borderColor:ac+"44"}}>+ Nova pasta</button>
        )}
      </div>

      {/* Pastas */}
      {visibleFolders.length === 0 ? (
        <div style={{...S.card,padding:24,textAlign:"center"}}>
          <div style={{fontSize:36,marginBottom:8}}>📚</div>
          <div style={{color:"var(--text)",fontWeight:700,fontSize:14,marginBottom:6}}>{isOwner ? "Sem pastas ainda" : "Sem recursos disponíveis"}</div>
          <p style={{color:"var(--text3)",fontSize:12,marginBottom:14}}>{isOwner ? "Crie pastas pra organizar seus recursos (ex: Documentos, Contratos, Manuais)." : "Você ainda não tem acesso a nenhum recurso. Peça pro Owner liberar."}</p>
          {isOwner && <button onClick={startNewFolder} style={{...S.btnPrimary,fontSize:13,padding:"8px 18px"}}>+ Criar primeira pasta</button>}
        </div>
      ) : (
        <>
          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14,padding:8,background:"var(--bg2)",borderRadius:10,border:"1px solid var(--border)"}}>
            {visibleFolders.map(f => {
              const count = restItems.filter(r => r.folderId === f.id && canSeeItem(r)).length;
              const isActive = f.id === activeFolderId;
              return (
                <button key={f.id} onClick={()=>setActiveFolderId(f.id)}
                  style={{padding:"8px 12px",borderRadius:8,border:`1px solid ${isActive?ac:"var(--border)"}`,background:isActive?ac+"15":"var(--card-bg)",color:isActive?ac:"var(--text2)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:12,fontWeight:isActive?700:500,display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:14}}>{f.icon}</span>
                  <span>{f.name}</span>
                  <span style={{fontSize:10,padding:"1px 7px",borderRadius:10,background:isActive?ac+"22":"var(--bg2)",color:isActive?ac:"var(--text3)",fontWeight:700}}>{count}</span>
                </button>
              );
            })}
          </div>

          {/* Header da pasta ativa + busca */}
          {activeFolder && (
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap",marginBottom:12}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:18}}>{activeFolder.icon}</span>
                <h4 style={{margin:0,color:"var(--text)",fontSize:15,fontWeight:700}}>{activeFolder.name}</h4>
                {isOwner && <button onClick={()=>startEditFolder(activeFolder)} style={{...S.btnSecondary,fontSize:10,padding:"3px 9px"}}>✏️</button>}
                {isOwner && <button onClick={()=>delFolder(activeFolder)} style={{...S.btnSecondary,fontSize:10,padding:"3px 9px",color:"var(--red)",borderColor:"var(--red)44"}}>🗑️</button>}
              </div>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Buscar..." style={{...S.input,fontSize:12,padding:"6px 10px",width:180}} />
                {isOwner && <button onClick={()=>startNewItem(activeFolder.id)} style={{...S.btnPrimary,fontSize:12,padding:"7px 14px",background:ac,borderColor:ac}}>+ Novo recurso</button>}
              </div>
            </div>
          )}

          {/* Lista de itens */}
          {visibleItems.length === 0 ? (
            <div style={{...S.card,padding:20,textAlign:"center",color:"var(--text3)",fontSize:13}}>
              {search.trim() ? "Nenhum recurso bate com a busca." : "Nenhum recurso nesta pasta. Clique em + Novo recurso pra começar."}
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {visibleItems.map(r => {
                const isLink = r.type === "link";
                const visMode = r.visibility?.mode || "default";
                const visBadge = visMode === "private" ? "🔒" : visMode === "shared" ? "👥" : null;
                const editoresCount = Array.isArray(r.editores) ? r.editores.length : 0;
                return (
                  <div key={r.id} style={{...S.card,padding:"12px 14px",display:"flex",alignItems:"flex-start",gap:12}}>
                    <span style={{fontSize:20,marginTop:1}}>{isLink ? "🔗" : "📝"}</span>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                        <span style={{color:"var(--text)",fontWeight:700,fontSize:14}}>{r.name}</span>
                        {visBadge && <span title={visMode === "private" ? "Privado" : "Compartilhado"} style={{fontSize:11}}>{visBadge}</span>}
                        {editoresCount > 0 && <span title={`${editoresCount} editor(es) delegado(s)`} style={{fontSize:10,padding:"1px 6px",borderRadius:10,background:ac+"22",color:ac,fontWeight:700}}>✏️ {editoresCount}</span>}
                      </div>
                      {isLink ? (
                        <div style={{display:"flex",flexDirection:"column",gap:6,marginTop:6}}>
                          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                            <button onClick={()=>{
                              // Drive/Docs/Forms: o iframe falha em pedir login da conta certa,
                              // então abre direto em nova aba (usa o login do Google que o usuário já tem).
                              const isGoogle = (() => { try { const h = new URL(r.url).hostname; return h === "drive.google.com" || h === "docs.google.com"; } catch { return false; } })();
                              if (isGoogle) { window.open(r.url, "_blank", "noopener,noreferrer"); return; }
                              setPreviewItem({name:r.name,url:r.url}); setPreviewBlocked(false);
                            }} style={{...S.btnSecondary,fontSize:11,padding:"5px 12px",color:ac,borderColor:ac+"55",background:ac+"10"}}>
                              👁️ Visualizar
                            </button>
                            <button onClick={async ()=>{
                              try {
                                if (navigator.clipboard?.writeText) {
                                  await navigator.clipboard.writeText(r.url);
                                } else {
                                  const ta = document.createElement("textarea"); ta.value = r.url; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
                                }
                                onUpdate("_toast", "📋 Link copiado");
                              } catch { onUpdate("_toast", "⚠️ Não consegui copiar — copie manualmente"); }
                            }} style={{...S.btnSecondary,fontSize:11,padding:"5px 12px"}}>
                              📋 Copiar link
                            </button>
                          </div>
                          {(() => { try { return <span style={{fontSize:10,color:"var(--text3)",fontFamily:"'DM Mono',monospace"}}>🌐 {new URL(r.url).hostname}</span>; } catch { return null; } })()}
                        </div>
                      ) : (
                        r.content && <div style={{color:"var(--text2)",fontSize:12,marginTop:4,whiteSpace:"pre-wrap",lineHeight:1.5}}>{r.content}</div>
                      )}
                      <div style={{display:"flex",gap:10,flexWrap:"wrap",marginTop:6,fontSize:10,color:"var(--text3)"}}>
                        {(r.tags || []).map(t => <span key={t} style={{padding:"1px 7px",borderRadius:10,background:"var(--bg2)",color:"var(--text2)"}}>#{t}</span>)}
                      </div>
                      {/* v8.15.1: Auditoria no rodapé — criado por X em DD/MM · editado por Y em DD/MM */}
                      <div style={{marginTop:6,paddingTop:6,borderTop:"1px solid var(--border)",fontSize:10,color:"var(--text3)",fontStyle:"italic"}}>
                        Criado por <strong>{r.createdBy || "—"}</strong> em {new Date(r.createdAt).toLocaleDateString("pt-BR")}
                        {r.updatedAt && r.updatedBy && (
                          <> · Editado por <strong>{r.updatedBy}</strong> em {new Date(r.updatedAt).toLocaleDateString("pt-BR")}</>
                        )}
                      </div>
                    </div>
                    {canEditItem(r) && (
                      <div style={{display:"flex",flexDirection:"column",gap:4}}>
                        <button onClick={()=>startEditItem(r)} style={{...S.btnSecondary,fontSize:10,padding:"3px 9px"}}>Editar</button>
                        <button onClick={()=>delItem(r)} style={{...S.btnSecondary,fontSize:10,padding:"3px 9px",color:"var(--red)",borderColor:"var(--red)44"}}>×</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Modal: Preview de link */}
      {previewItem && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:mobileOnly?8:24}} onClick={()=>setPreviewItem(null)}>
          <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:14,width:"100%",maxWidth:1100,height:"90vh",display:"flex",flexDirection:"column",overflow:"hidden"}}>
            <div style={{display:"flex",alignItems:"center",gap:10,padding:"12px 16px",borderBottom:"1px solid var(--border)",background:"var(--bg2)"}}>
              <span style={{fontSize:16}}>👁️</span>
              <div style={{flex:1,minWidth:0}}>
                <div style={{color:"var(--text)",fontWeight:700,fontSize:14,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{previewItem.name}</div>
                <div style={{color:"var(--text3)",fontSize:10,fontFamily:"'DM Mono',monospace",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{previewItem.url}</div>
              </div>
              <button onClick={()=>window.open(previewItem.url,"_blank","noopener,noreferrer")} style={{...S.btnSecondary,fontSize:11,padding:"5px 12px"}} title="Abrir em nova aba">↗ Nova aba</button>
              <button onClick={()=>setPreviewItem(null)} style={{...S.btnSecondary,fontSize:11,padding:"5px 12px"}}>✕ Fechar</button>
            </div>
            <div style={{flex:1,position:"relative",background:"var(--bg1)"}}>
              {previewBlocked ? (
                <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:14,padding:24,textAlign:"center"}}>
                  <div style={{fontSize:42}}>🚫</div>
                  <div style={{color:"var(--text)",fontWeight:700,fontSize:15}}>Esse site não permite preview embutido</div>
                  <p style={{color:"var(--text3)",fontSize:12,maxWidth:420,lineHeight:1.5,margin:0}}>Alguns sites (Google, bancos, sistemas internos) bloqueiam ser exibidos dentro de outras páginas por segurança. Abra em nova aba pra acessar.</p>
                  <button onClick={()=>window.open(previewItem.url,"_blank","noopener,noreferrer")} style={{...S.btnPrimary,fontSize:13,padding:"10px 22px",background:ac,borderColor:ac}}>↗ Abrir em nova aba</button>
                </div>
              ) : (
                <iframe
                  key={previewItem.url}
                  src={embeddableUrl(previewItem.url)}
                  title={previewItem.name}
                  onLoad={(e)=>{
                    try {
                      // Se carregou mas foi bloqueado por X-Frame-Options, contentDocument lança
                      // ou retorna um documento vazio. Heurística: se o iframe não tem URL definida ou está em about:blank após load, assume bloqueio.
                      const fr = e.target;
                      if (!fr.contentWindow || fr.contentWindow.location.href === "about:blank") setPreviewBlocked(true);
                    } catch {
                      // cross-origin: assume que carregou normalmente (é o caso comum)
                    }
                  }}
                  onError={()=>setPreviewBlocked(true)}
                  sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-popups-to-escape-sandbox"
                  style={{width:"100%",height:"100%",border:"none",background:"#fff"}}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Pasta */}
      {folderForm && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={()=>setFolderForm(null)}>
          <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:14,maxWidth:420,width:"100%",padding:24}}>
            <h3 style={{margin:"0 0 12px",color:"var(--text)",fontSize:16,fontWeight:700}}>{folderForm.id ? "Editar pasta" : "Nova pasta"}</h3>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <div>
                <label style={{...S.label,fontSize:11}}>Nome</label>
                <input value={folderForm.name} onChange={e=>setFolderForm({...folderForm,name:e.target.value})} placeholder="ex: Manuais Operacionais" style={S.input} autoFocus/>
              </div>
              <div>
                <label style={{...S.label,fontSize:11}}>Ícone (1 emoji)</label>
                <input value={folderForm.icon} onChange={e=>setFolderForm({...folderForm,icon:e.target.value.slice(0,4)})} placeholder="🔧" maxLength={4} style={{...S.input,width:80,textAlign:"center",fontSize:18}}/>
              </div>
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:18}}>
              <button onClick={()=>setFolderForm(null)} style={{...S.btnSecondary,padding:"8px 14px",fontSize:13}}>Cancelar</button>
              <button onClick={saveFolder} style={{...S.btnPrimary,padding:"8px 14px",fontSize:13,fontWeight:700}}>{folderForm.id ? "Salvar" : "Criar pasta"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Item (criar/editar) */}
      {itemForm && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16,overflow:"auto"}} onClick={()=>setItemForm(null)}>
          <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:14,maxWidth:560,width:"100%",maxHeight:"90vh",overflow:"auto",padding:24}}>
            <h3 style={{margin:"0 0 14px",color:"var(--text)",fontSize:16,fontWeight:700}}>{itemForm.id ? "Editar recurso" : "Novo recurso"}</h3>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div>
                <label style={{...S.label,fontSize:11}}>Nome</label>
                <input value={itemForm.name} onChange={e=>setItemForm({...itemForm,name:e.target.value})} placeholder="ex: Apólice de Seguros" style={S.input} autoFocus/>
              </div>
              <div>
                <label style={{...S.label,fontSize:11}}>Tipo</label>
                <div style={{display:"flex",gap:8}}>
                  {[["link","🔗 Link externo"],["nota","📝 Nota"]].map(([v,l]) => (
                    <button key={v} type="button" onClick={()=>setItemForm({...itemForm,type:v})}
                      style={{padding:"8px 14px",borderRadius:8,border:`1px solid ${itemForm.type===v?ac:"var(--border)"}`,background:itemForm.type===v?ac+"15":"transparent",color:itemForm.type===v?ac:"var(--text2)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:12,fontWeight:itemForm.type===v?700:500}}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              {itemForm.type === "link" ? (
                <div>
                  <label style={{...S.label,fontSize:11}}>URL</label>
                  <input value={itemForm.url} onChange={e=>setItemForm({...itemForm,url:e.target.value})} placeholder="https://..." style={{...S.input,fontFamily:"'DM Mono',monospace",fontSize:12}}/>
                </div>
              ) : (
                <div>
                  <label style={{...S.label,fontSize:11}}>Conteúdo (markdown simples)</label>
                  <textarea value={itemForm.content} onChange={e=>setItemForm({...itemForm,content:e.target.value})} rows={6} placeholder="Texto livre..." style={{...S.input,fontFamily:"'DM Mono',monospace",fontSize:12,resize:"vertical",minHeight:120}}/>
                </div>
              )}
              <div>
                <label style={{...S.label,fontSize:11}}>Pasta</label>
                <select value={itemForm.folderId} onChange={e=>setItemForm({...itemForm,folderId:e.target.value})} style={S.input}>
                  {restFolders.map(f => <option key={f.id} value={f.id}>{f.icon} {f.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{...S.label,fontSize:11}}>Tags (separadas por vírgula)</label>
                <input value={(itemForm.tags || []).join(", ")} onChange={e=>setItemForm({...itemForm,tags:e.target.value.split(",").map(s=>s.trim()).filter(Boolean)})} placeholder="ex: fechamento, fiscal" style={S.input}/>
              </div>

              {/* v8.15.1: Editores delegados — Owner only */}
              {isOwner && (
                <div style={{padding:"10px 12px",background:"var(--bg2)",border:"1px solid var(--border)",borderRadius:8}}>
                  <div style={{fontSize:11,fontWeight:700,color:"var(--text2)",textTransform:"uppercase",letterSpacing:0.4,marginBottom:6}}>✏️ Quem pode editar este recurso</div>
                  <div style={{fontSize:10,color:"var(--text3)",marginBottom:8,lineHeight:1.4}}>Por padrão, só você (Owner) edita. Marque outros gestores pra delegar permissão de edição.</div>
                  {eligibleResponsaveis.filter(p => p.id !== currentUser?.id).length === 0 ? (
                    <div style={{fontSize:11,color:"var(--text3)",fontStyle:"italic"}}>Nenhum outro gestor cadastrado.</div>
                  ) : (
                    <div style={{display:"flex",flexDirection:"column",gap:3,maxHeight:140,overflow:"auto"}}>
                      {eligibleResponsaveis.filter(p => p.id !== currentUser?.id).map(p => {
                        const checked = (itemForm.editores || []).includes(p.id);
                        return (
                          <label key={p.id} style={{display:"flex",alignItems:"center",gap:7,padding:"3px 4px",fontSize:12,cursor:"pointer"}}>
                            <input type="checkbox" checked={checked} onChange={()=>{
                              const cur = itemForm.editores || [];
                              setItemForm({...itemForm, editores: checked ? cur.filter(x=>x!==p.id) : [...cur, p.id]});
                            }} style={{accentColor:ac}}/>
                            <span style={{color:"var(--text)"}}>{p.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Visibilidade */}
              <div style={{padding:"10px 12px",background:"var(--bg2)",border:"1px solid var(--border)",borderRadius:8}}>
                <div style={{fontSize:11,fontWeight:700,color:"var(--text2)",textTransform:"uppercase",letterSpacing:0.4,marginBottom:6}}>🔒 Quem pode ver</div>
                <div style={{display:"flex",flexDirection:"column",gap:4}}>
                  {[
                    {value:"default", label:"🌐 Padrão", desc:"Todos os gestores que abrem Recursos"},
                    {value:"shared",  label:"👥 Compartilhada", desc:"Eu + Owner + DP + pessoas que escolher"},
                    {value:"private", label:"🔒 Privada", desc:"Só eu + Owner + DP"},
                  ].map(opt => (
                    <label key={opt.value} style={{display:"flex",alignItems:"flex-start",gap:8,padding:"6px 10px",borderRadius:8,cursor:"pointer",border:`1px solid ${itemForm.visibility.mode===opt.value?ac:"var(--border)"}`,background:itemForm.visibility.mode===opt.value?ac+"11":"transparent"}}>
                      <input type="radio" checked={itemForm.visibility.mode===opt.value} onChange={()=>setItemForm({...itemForm,visibility:{...itemForm.visibility,mode:opt.value}})} style={{marginTop:2,accentColor:ac}}/>
                      <div style={{flex:1}}>
                        <div style={{fontSize:12,fontWeight:600,color:"var(--text)"}}>{opt.label}</div>
                        <div style={{fontSize:10,color:"var(--text3)",marginTop:1}}>{opt.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
                {itemForm.visibility.mode === "shared" && shareableManagers.length > 0 && (
                  <div style={{marginTop:8,padding:"8px 10px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:6}}>
                    <div style={{fontSize:10,color:"var(--text3)",marginBottom:6}}>Selecione quem mais pode ver:</div>
                    <div style={{display:"flex",flexDirection:"column",gap:3,maxHeight:140,overflow:"auto"}}>
                      {shareableManagers.map(m => {
                        const checked = (itemForm.visibility.sharedWith || []).includes(m.id);
                        return (
                          <label key={m.id} style={{display:"flex",alignItems:"center",gap:7,padding:"3px 4px",fontSize:12,cursor:"pointer"}}>
                            <input type="checkbox" checked={checked} onChange={()=>{
                              const cur = itemForm.visibility.sharedWith || [];
                              setItemForm({...itemForm,visibility:{...itemForm.visibility,sharedWith: checked ? cur.filter(x=>x!==m.id) : [...cur, m.id]}});
                            }} style={{accentColor:ac}}/>
                            <span style={{color:"var(--text)"}}>{m.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:18}}>
              <button onClick={()=>setItemForm(null)} style={{...S.btnSecondary,padding:"8px 14px",fontSize:13}}>Cancelar</button>
              <button onClick={saveItem} style={{...S.btnPrimary,padding:"8px 14px",fontSize:13,fontWeight:700,background:ac,borderColor:ac}}>{itemForm.id ? "Salvar" : "Criar recurso"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FreelasModule({ restaurantId, pessoas, freelaShifts, freelaPagamentos, employees, roles, schedules, restaurants, currentUser, isDP, isLider, isOwner, perms, onUpdate, mobileOnly }) {
  const [subTab, setSubTab] = useState("lancamento");
  const ac = "#7c3aed"; // roxo Freelas — distingue dos outros módulos
  const restPessoas = (pessoas || []).filter(p => (p.restaurantIds || []).includes(restaurantId));
  const restFreelas = restPessoas.filter(p => p.isFreela === true);
  const restShifts = (freelaShifts || []).filter(s => s.restaurantId === restaurantId);

  // ── Helpers ──
  // Calcula horas trabalhadas (entrada/saída em "HH:MM", intervalo em minutos)
  function calcHoras(entrada, saida, intervalo) {
    if (!entrada || !saida) return 0;
    const [eh, em] = entrada.split(":").map(Number);
    const [sh, sm] = saida.split(":").map(Number);
    if (isNaN(eh) || isNaN(sh)) return 0;
    let mins = (sh * 60 + sm) - (eh * 60 + em);
    if (mins < 0) mins += 24 * 60; // virou madrugada
    mins -= (parseInt(intervalo) || 0);
    return Math.max(0, mins / 60);
  }

  function fmtHoras(h) {
    if (!h || h <= 0) return "—";
    const horas = Math.floor(h);
    const mins = Math.round((h - horas) * 60);
    return mins > 0 ? `${horas}h${String(mins).padStart(2,"0")}` : `${horas}h`;
  }

  function autoMarcaEscalaFreela(pessoaId, date) {
    // Marca o dia da escala daquela pessoa como DAY_FREELA (caso ela seja CLT)
    const pessoa = restPessoas.find(p => p.id === pessoaId);
    if (!pessoa?.isTeam?.[restaurantId]) return; // só pra CLT
    const empId = pessoa.linkedEmployeeId || (employees || []).find(e => e.linkedPessoaId === pessoaId && e.restaurantId === restaurantId)?.id;
    if (!empId) return;
    const td = new Date(date + "T12:00:00");
    const mk = monthKey(td.getFullYear(), td.getMonth());
    const cur = schedules?.[restaurantId]?.[mk] || {};
    const empMap = { ...(cur[empId] || {}), [date]: DAY_FREELA };
    const newMonth = { ...cur, [empId]: empMap };
    onUpdate("schedules", { ...(schedules || {}), [restaurantId]: { ...(schedules?.[restaurantId] || {}), [mk]: newMonth } });
  }

  // ── Adicionar shift ──
  async function addShift(shiftData) {
    const pessoa = restPessoas.find(p => p.id === shiftData.pessoaId);
    if (!pessoa) return;
    // Aviso só pra CLT PURO pegando freela ad-hoc (se já é freela cadastrado, não avisa)
    if (pessoa.isTeam?.[restaurantId] && !pessoa.isFreela) {
      if (!await appConfirm(`${pessoa.name} é da equipe (CLT). Lançar shift de freela vai marcar ${fmtDate(shiftData.date)} como folga-freela na escala dela e excluir esse dia da gorjeta. Confirmar?`)) return;
      autoMarcaEscalaFreela(shiftData.pessoaId, shiftData.date);
    }
    const horas = calcHoras(shiftData.entrada, shiftData.saida, shiftData.intervalo);
    const newShift = {
      id: `shf_${Date.now().toString(36)}${Math.random().toString(36).slice(2,5)}`,
      restaurantId,
      pessoaId: shiftData.pessoaId,
      date: shiftData.date,
      entrada: shiftData.entrada || "",
      saida: shiftData.saida || "",
      intervalo: parseInt(shiftData.intervalo) || 0,
      horas,
      area: shiftData.area || null,
      valorTipo: null, // preenchido pelo DP/admin depois
      valorUnit: null,
      totalCalc: null,
      observacao: shiftData.observacao || "",
      status: "aberto",
      lotePagamentoId: null,
      lancadoPor: currentUser?.name || "—",
      lancadoPorId: currentUser?.id || null,
      lancadoEm: new Date().toISOString(),
    };
    onUpdate("freelaShifts", [...(freelaShifts || []), newShift]);
    onUpdate("_toast", `✅ Shift de ${pessoa.name} em ${fmtDate(shiftData.date)} lançado`);
  }

  // v8.25.0: agendamento de shift futuro — sem horários reais, status "agendado"
  async function addPlannedShift(shiftData) {
    const pessoa = restPessoas.find(p => p.id === shiftData.pessoaId);
    if (!pessoa) return;
    if (!shiftData.scheduledDate) return;
    const newShift = {
      id: `shf_${Date.now().toString(36)}${Math.random().toString(36).slice(2,5)}`,
      restaurantId,
      pessoaId: shiftData.pessoaId,
      personType: pessoa.isTeam?.[restaurantId] ? "employee" : "freela", // v8.25.0
      date: shiftData.scheduledDate,            // mantém compat com schema antigo
      scheduledDate: shiftData.scheduledDate,   // novo — explicita que é planejado
      entrada: shiftData.entrada || "",
      saida: shiftData.saida || "",
      intervalo: 0,
      horas: 0,
      area: shiftData.area || null,
      valorTipo: shiftData.valorTipo || null,
      valorUnit: shiftData.valorUnit ?? null,
      totalCalc: null,
      observacao: shiftData.observacao || "",
      status: "agendado",                       // v8.25.0 — novo status
      lotePagamentoId: null,
      lancadoPor: currentUser?.name || "—",
      lancadoPorId: currentUser?.id || null,
      lancadoEm: new Date().toISOString(),
    };
    onUpdate("freelaShifts", (prev) => [...(prev || []), newShift]);
    onUpdate("_toast", `📅 Shift de ${pessoa.name} agendado pra ${fmtDate(shiftData.scheduledDate)}`);
  }

  // Cancela agendamento (só agendados podem ser cancelados; histórico fica vazio)
  async function cancelPlannedShift(shiftId) {
    const s = (freelaShifts || []).find(x => x.id === shiftId);
    if (!s || s.status !== "agendado") return;
    if (!await appConfirm(`Cancelar o shift agendado?`)) return;
    onUpdate("freelaShifts", (prev) => (prev || []).filter(x => x.id !== shiftId));
    onUpdate("_toast", `🗑️ Agendamento removido`);
  }

  // v8.28.0: encerra turno de freela em andamento — preenche saída + intervalo
  // (Geralmente outro líder no fim do turno; o que abriu marcou só entrada)
  async function closeShift(shiftId, payload) {
    const s = (freelaShifts || []).find(x => x.id === shiftId);
    if (!s) return;
    if (s.status !== "aberto") { onUpdate("_toast", "⚠️ Esse shift não está em andamento"); return; }
    const saida = (payload?.saida || "").trim();
    const intervalo = parseInt(payload?.intervalo) || 0;
    if (!saida) { alert("Hora de saída é obrigatória."); return; }
    const horas = calcHoras(s.entrada, saida, intervalo);
    onUpdate("freelaShifts", (prev) => (prev || []).map(x => x.id === shiftId ? {
      ...x,
      saida,
      intervalo,
      horas,
      observacao: (payload?.observacao || x.observacao || "").trim() || x.observacao,
      encerradoEm: new Date().toISOString(),
      encerradoPor: currentUser?.name || "—",
      encerradoPorId: currentUser?.id || null,
    } : x));
    onUpdate("_toast", `■ Turno encerrado — ${fmtHoras(horas)} trabalhadas`);
  }

  // v8.27.0: marca como "não compareceu" — fica no histórico, sem pagamento, conta na ficha do freela
  async function markNoShow(shiftId) {
    const s = (freelaShifts || []).find(x => x.id === shiftId);
    if (!s || s.status !== "agendado") return;
    const pessoa = restPessoas.find(p => p.id === s.pessoaId);
    if (!await appConfirm(`Marcar ${pessoa?.name || "—"} como "Não compareceu" no shift de ${fmtDate(s.scheduledDate || s.date)}?\n\n• Não vai gerar pagamento\n• Fica no histórico (auditoria)\n• Conta no histórico de no-shows da pessoa`)) return;
    onUpdate("freelaShifts", (prev) => (prev || []).map(x => x.id === shiftId ? {
      ...x,
      status: "nao_compareceu",
      noShowAt: new Date().toISOString(),
      noShowBy: currentUser?.name || "—",
      noShowById: currentUser?.id || null,
    } : x));
    onUpdate("_toast", `🚫 ${pessoa?.name || "—"} marcado como não compareceu`);
  }

  // v8.27.0: editar dados do agendamento (pessoa/data/horários/área/observação) antes da confirmação
  async function editPlannedShift(shiftId, patch) {
    const s = (freelaShifts || []).find(x => x.id === shiftId);
    if (!s || s.status !== "agendado") return;
    onUpdate("freelaShifts", (prev) => (prev || []).map(x => x.id === shiftId ? {
      ...x,
      pessoaId: patch.pessoaId ?? x.pessoaId,
      date: patch.scheduledDate ?? x.date,
      scheduledDate: patch.scheduledDate ?? x.scheduledDate,
      area: patch.area ?? x.area,
      entrada: patch.entrada ?? x.entrada,
      saida: patch.saida ?? x.saida,
      intervalo: patch.intervalo != null ? parseInt(patch.intervalo) : x.intervalo,
      observacao: patch.observacao ?? x.observacao,
      editedAt: new Date().toISOString(),
      editedBy: currentUser?.name || "—",
    } : x));
    onUpdate("_toast", `✏️ Agendamento atualizado`);
  }

  // v8.26.0: confirma que um shift agendado rolou — preenche horários reais e promove pra "aberto"
  // (entra no fluxo legado de fechamento/pagamento)
  async function confirmPlannedShift(shiftId, payload) {
    const s = (freelaShifts || []).find(x => x.id === shiftId);
    if (!s) return;
    if (s.status !== "agendado") { onUpdate("_toast", "⚠️ Shift não está mais agendado"); return; }
    const entrada = (payload?.entrada || "").trim();
    const saida   = (payload?.saida || "").trim();
    const intervalo = parseInt(payload?.intervalo) || 0;
    if (!entrada) { alert("Hora de entrada é obrigatória."); return; }
    const horas = calcHoras(entrada, saida, intervalo);
    // Sincroniza escala (mesma lógica do addShift normal pra CLT pegando freela)
    const pessoa = restPessoas.find(p => p.id === s.pessoaId);
    if (pessoa?.isTeam?.[restaurantId] && !pessoa.isFreela) {
      autoMarcaEscalaFreela(s.pessoaId, s.date);
    }
    onUpdate("freelaShifts", (prev) => (prev || []).map(x => x.id === shiftId ? {
      ...x,
      status: "aberto",
      entrada,
      saida,
      intervalo,
      horas,
      observacao: payload?.observacao || x.observacao || "",
      confirmadoEm: new Date().toISOString(),
      confirmadoPor: currentUser?.name || "—",
      confirmadoPorId: currentUser?.id || null,
    } : x));
    onUpdate("_toast", `✓ Shift de ${pessoa?.name || "—"} confirmado`);
  }

  function updateShift(shiftId, patch) {
    const updated = (freelaShifts || []).map(s => {
      if (s.id !== shiftId) return s;
      const next = { ...s, ...patch };
      // Recalcula horas se mudou entrada/saida/intervalo
      if (patch.entrada !== undefined || patch.saida !== undefined || patch.intervalo !== undefined) {
        next.horas = calcHoras(next.entrada, next.saida, next.intervalo);
      }
      // Recalcula total se mudou valor ou tipo
      if (patch.valorTipo !== undefined || patch.valorUnit !== undefined || next.horas !== s.horas) {
        if (next.valorTipo === "hora") next.totalCalc = (next.valorUnit || 0) * (next.horas || 0);
        else if (next.valorTipo === "diaria") next.totalCalc = next.valorUnit || 0;
        else next.totalCalc = null;
      }
      return next;
    });
    onUpdate("freelaShifts", updated);
  }

  async function deleteShift(shiftId) {
    const s = (freelaShifts || []).find(x => x.id === shiftId);
    if (!s) return;
    if (s.status !== "aberto") { alert("Shift travado em fechamento — corrija o lote primeiro."); return; }
    const pessoa = restPessoas.find(p => p.id === s.pessoaId);
    if (!await appConfirm(`Apagar o shift de ${pessoa?.name || "?"} em ${fmtDate(s.date)}?`)) return;
    onUpdate("freelaShifts", (freelaShifts || []).filter(x => x.id !== shiftId));
  }

  // ── Cadastrar freela rápido (líder/DP/owner cria sem ir pra aba Pessoas) ──
  async function addFreela(data) {
    const nm = (data.name || "").trim();
    if (!nm) { alert("Nome é obrigatório."); return false; }
    if (!(data.whatsapp || "").trim()) { alert("WhatsApp é obrigatório pra freela."); return false; }
    if (!(data.pix || "").trim()) { alert("Chave PIX é obrigatória pra freela."); return false; }
    const cpfRaw = (data.cpf || "").replace(/\D/g, "");
    // Se CPF preenchido, checa duplicata global
    if (cpfRaw) {
      const exists = (pessoas || []).find(p => (p.cpf || "").replace(/\D/g, "") === cpfRaw);
      if (exists) {
        if (!(exists.restaurantIds || []).includes(restaurantId)) {
          if (!await appConfirm(`Já existe ${exists.name} cadastrado em outro restaurante. Vincular esse restaurante e marcar como freela aqui?`)) return false;
          const next = (pessoas || []).map(p => p.id === exists.id ? {
            ...p,
            restaurantIds: [...(p.restaurantIds || []), restaurantId],
            isFreela: true,
            whatsapp: p.whatsapp || data.whatsapp.trim(),
            pix: p.pix || data.pix.trim(),
            permissions: { ...(p.permissions || {}), [restaurantId]: { operational: {}, admin: {}, special: {} } },
          } : p);
          onUpdate("pessoas", next);
          onUpdate("_toast", `✅ ${exists.name} vinculado e marcado como freela`);
          return exists.id;
        } else {
          if (!await appConfirm(`${exists.name} já está cadastrado neste restaurante. Marcar como freela?`)) return false;
          const next = (pessoas || []).map(p => p.id === exists.id ? {
            ...p, isFreela: true,
            whatsapp: p.whatsapp || data.whatsapp.trim(),
            pix: p.pix || data.pix.trim(),
          } : p);
          onUpdate("pessoas", next);
          onUpdate("_toast", `✅ ${exists.name} marcado como freela`);
          return exists.id;
        }
      }
    }
    const id = `pes_${Date.now().toString(36)}${Math.random().toString(36).slice(2,6)}`;
    const newP = {
      id,
      restaurantIds: [restaurantId],
      name: nm,
      cpf: cpfRaw || "",
      pin: cpfRaw ? cpfRaw.slice(-4) : "0000",
      mustChangePin: true,
      email: null,
      whatsapp: data.whatsapp.trim(),
      pix: data.pix.trim(),
      isFreela: true,
      isTeam: {},
      teamData: {},
      permissions: { [restaurantId]: { operational: {}, admin: {}, special: {} } },
      createdAt: new Date().toISOString(),
      cadastradoVia: "freela_rapido",
    };
    onUpdate("pessoas", [...(pessoas || []), newP]);
    onUpdate("_toast", `✅ ${nm} cadastrado como freela`);
    return id;
  }

  // v8.29.0: bulk-fill removido — DP edita valor shift a shift no Fechamento.

  // ── Lotes de pagamento (Fase 5) ──
  const restLotes = (freelaPagamentos || []).filter(l => l.restaurantId === restaurantId);

  // Util: valida shift pronto pra fechar (tem valor + pessoa tem PIX)
  function shiftPrntoPronto(s) {
    if (s.status !== "aberto") return { ok: false, motivo: "Não está aberto" };
    if (!s.valorTipo || s.totalCalc == null) return { ok: false, motivo: "Sem valor preenchido" };
    if (!s.entrada || !s.saida) return { ok: false, motivo: "Sem entrada/saída" };
    const p = restPessoas.find(x => x.id === s.pessoaId);
    if (!p) return { ok: false, motivo: "Pessoa removida" };
    if (!p.pix) return { ok: false, motivo: `${p.name} sem PIX` };
    return { ok: true };
  }

  async function criarLote({ shiftIds, numero, observacao }) {
    const sel = (freelaShifts || []).filter(s => shiftIds.includes(s.id) && s.restaurantId === restaurantId);
    if (sel.length === 0) { alert("Selecione pelo menos 1 shift."); return null; }
    // Valida cada um
    for (const s of sel) {
      const v = shiftPrntoPronto(s);
      if (!v.ok) { alert(`Shift de ${restPessoas.find(p=>p.id===s.pessoaId)?.name || "?"} em ${fmtDate(s.date)}: ${v.motivo}`); return null; }
    }
    // Resumo por pessoa
    const byPessoa = {};
    sel.forEach(s => {
      if (!byPessoa[s.pessoaId]) byPessoa[s.pessoaId] = { pessoaId: s.pessoaId, totalHoras: 0, totalValor: 0, qtdShifts: 0 };
      byPessoa[s.pessoaId].totalHoras += s.horas || 0;
      byPessoa[s.pessoaId].totalValor += s.totalCalc || 0;
      byPessoa[s.pessoaId].qtdShifts += 1;
    });
    const pessoasResumo = Object.values(byPessoa);
    const totalGeral = pessoasResumo.reduce((sum, p) => sum + p.totalValor, 0);
    const loteId = `lot_${Date.now().toString(36)}${Math.random().toString(36).slice(2,5)}`;
    const lote = {
      id: loteId,
      restaurantId,
      numero: numero || `Lote ${fmtDate(today())}`,
      observacao: observacao || "",
      shiftIds: sel.map(s => s.id),
      pessoasResumo,
      totalGeral,
      qtdShifts: sel.length,
      qtdPessoas: pessoasResumo.length,
      status: "pendente",
      criadoEm: new Date().toISOString(),
      criadoPor: currentUser?.name || "—",
      criadoPorId: currentUser?.id || null,
    };
    // Marca shifts como "fechamento" + lotePagamentoId
    const updatedShifts = (freelaShifts || []).map(s => {
      if (sel.find(x => x.id === s.id)) return { ...s, status: "fechamento", lotePagamentoId: loteId };
      return s;
    });
    onUpdate("freelaShifts", updatedShifts);
    onUpdate("freelaPagamentos", [...(freelaPagamentos || []), lote]);
    onUpdate("_toast", `📑 Lote criado com ${sel.length} shift(s) — R$ ${totalGeral.toFixed(2)}`);
    return loteId;
  }

  async function cancelarLote(loteId) {
    const lote = (freelaPagamentos || []).find(l => l.id === loteId);
    if (!lote) return;
    if (lote.status === "pago") { alert("Lote já foi pago — use 'Reabrir lote' antes de cancelar."); return; }
    if (!await appConfirm(`Cancelar o lote "${lote.numero}"? Os ${lote.qtdShifts} shifts voltam pro status "Aberto".`)) return;
    const updatedShifts = (freelaShifts || []).map(s => {
      if (lote.shiftIds.includes(s.id)) return { ...s, status: "aberto", lotePagamentoId: null };
      return s;
    });
    onUpdate("freelaShifts", updatedShifts);
    onUpdate("freelaPagamentos", (freelaPagamentos || []).filter(l => l.id !== loteId));
    onUpdate("_toast", `🗑️ Lote cancelado`);
  }

  // v8.5 — Owner reabre lote já pago (volta pra pendente)
  async function reabrirLotePago(loteId) {
    const lote = (freelaPagamentos || []).find(l => l.id === loteId);
    if (!lote) return;
    if (lote.status !== "pago") { alert("Esse lote não está pago."); return; }
    if (!await appConfirm(`Reabrir o lote "${lote.numero}"? Os shifts voltam para "Fechamento" e o lote volta a aguardar pagamento.`)) return;
    const updatedShifts = (freelaShifts || []).map(s => {
      if (lote.shiftIds.includes(s.id)) return { ...s, status: "fechamento", pagoEm: null };
      return s;
    });
    onUpdate("freelaShifts", updatedShifts);
    onUpdate("freelaPagamentos", (freelaPagamentos || []).map(l => l.id === loteId ? { ...l, status: "pendente", pagoEm: null, pagoPor: null, formaPagamento: null } : l));
    onUpdate("_toast", `↺ Lote ${lote.numero} reaberto`);
  }

  // v8.5 — Owner exclui shift do histórico (de qualquer status)
  async function excluirShiftHistorico(shiftId) {
    const s = (freelaShifts || []).find(x => x.id === shiftId);
    if (!s) return;
    const pessoa = restPessoas.find(p => p.id === s.pessoaId);
    if (!await appConfirm(`Excluir definitivamente o shift de ${pessoa?.name || "?"} em ${fmtDate(s.date)}?\n\nEssa ação é IRREVERSÍVEL.`)) return;
    // Se está em lote, também limpa do lote
    let updatedLotes = freelaPagamentos || [];
    if (s.lotePagamentoId) {
      updatedLotes = updatedLotes.map(l => {
        if (l.id !== s.lotePagamentoId) return l;
        return {
          ...l,
          shiftIds: (l.shiftIds || []).filter(id => id !== shiftId),
          qtdShifts: Math.max(0, (l.qtdShifts || 0) - 1),
          totalGeral: Math.max(0, (l.totalGeral || 0) - (s.totalCalc || 0)),
        };
      });
      onUpdate("freelaPagamentos", updatedLotes);
    }
    onUpdate("freelaShifts", (freelaShifts || []).filter(x => x.id !== shiftId));
    onUpdate("_toast", `🗑️ Shift de ${pessoa?.name} excluído`);
  }

  async function marcarLotePago(loteId, dataPagamento, formaPagamento) {
    const lote = (freelaPagamentos || []).find(l => l.id === loteId);
    if (!lote) return;
    if (lote.status === "pago") { alert("Já está pago."); return; }
    const updatedShifts = (freelaShifts || []).map(s => {
      if (lote.shiftIds.includes(s.id)) return { ...s, status: "pago", pagoEm: dataPagamento };
      return s;
    });
    const updatedLotes = (freelaPagamentos || []).map(l => {
      if (l.id !== loteId) return l;
      return { ...l, status: "pago", pagoEm: dataPagamento, formaPagamento: formaPagamento || "PIX", pagoPor: currentUser?.name || "—", pagoPorId: currentUser?.id || null };
    });
    onUpdate("freelaShifts", updatedShifts);
    onUpdate("freelaPagamentos", updatedLotes);
    onUpdate("_toast", `💸 Lote marcado como pago em ${fmtDate(dataPagamento)}`);
  }

  // ── Geração do PDF do lote (Fase 6) ──
  async function gerarPDFLote(loteId) {
    const lote = (freelaPagamentos || []).find(l => l.id === loteId);
    if (!lote) return;
    const jsPDFCtor = window.jspdf?.jsPDF || window.jsPDF;
    if (!jsPDFCtor) { alert("Biblioteca PDF não carregada — recarregue a página."); return; }
    const doc = new jsPDFCtor("p", "mm", "a4");
    const pageW = doc.internal.pageSize.getWidth();
    // Cabeçalho
    doc.setFont("helvetica", "bold"); doc.setFontSize(15);
    doc.text("Pagamento de Freelas", 14, 16);
    doc.setFontSize(10); doc.setFont("helvetica", "normal");
    doc.text(`Lote: ${lote.numero}`, 14, 23);
    doc.text(`Criado em: ${fmtDate(lote.criadoEm.slice(0,10))} por ${lote.criadoPor}`, 14, 28);
    doc.text(`Status: ${lote.status === "pago" ? "PAGO em " + fmtDate(lote.pagoEm) : "PENDENTE"}`, 14, 33);
    if (lote.observacao) doc.text(`Obs: ${lote.observacao}`, 14, 38);
    // Resumo por pessoa
    const headPessoas = [["Pessoa", "CPF", "PIX", "Shifts", "Horas", "Total R$"]];
    const bodyPessoas = lote.pessoasResumo.map(pr => {
      const p = restPessoas.find(x => x.id === pr.pessoaId) || {};
      const cpfFmt = (p.cpf || "").replace(/\D/g, "").replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      return [p.name || "(removido)", cpfFmt || "—", p.pix || "—", String(pr.qtdShifts), pr.totalHoras.toFixed(2) + "h", "R$ " + pr.totalValor.toFixed(2)];
    });
    bodyPessoas.push(["", "", "", String(lote.qtdShifts), "", "R$ " + lote.totalGeral.toFixed(2)]);
    doc.autoTable({
      head: headPessoas, body: bodyPessoas, startY: lote.observacao ? 44 : 39,
      theme: "grid", styles: { fontSize: 9 }, headStyles: { fillColor: [124, 58, 237], textColor: 255 },
      foot: [["", "", "TOTAL", String(lote.qtdShifts), "", "R$ " + lote.totalGeral.toFixed(2)]],
      footStyles: { fillColor: [240, 235, 250], textColor: 0, fontStyle: "bold" },
    });
    // Detalhe por shift
    let cursorY = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(11); doc.setFont("helvetica", "bold");
    doc.text("Detalhamento dos shifts", 14, cursorY); cursorY += 4;
    const headDet = [["Pessoa", "Data", "Área", "Ent.", "Saí.", "Int.", "Horas", "Valor", "Total"]];
    const shiftsLote = (freelaShifts || []).filter(s => lote.shiftIds.includes(s.id))
      .sort((a,b) => (a.pessoaId).localeCompare(b.pessoaId) || (a.date || "").localeCompare(b.date || ""));
    const bodyDet = shiftsLote.map(s => {
      const p = restPessoas.find(x => x.id === s.pessoaId) || {};
      return [
        (p.name || "?").split(" ").slice(0,2).join(" "),
        fmtDate(s.date), s.area || "—", s.entrada || "—", s.saida || "—",
        String(s.intervalo || 0) + "min", fmtHoras(s.horas),
        s.valorTipo === "hora" ? "R$" + (s.valorUnit||0).toFixed(2) + "/h" : "R$" + (s.valorUnit||0).toFixed(2),
        "R$ " + (s.totalCalc || 0).toFixed(2),
      ];
    });
    doc.autoTable({
      head: headDet, body: bodyDet, startY: cursorY,
      theme: "striped", styles: { fontSize: 8 }, headStyles: { fillColor: [124, 58, 237], textColor: 255 },
    });
    // Rodapé
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8); doc.setTextColor(120);
      doc.text(`Página ${i} de ${totalPages}`, pageW - 14, doc.internal.pageSize.getHeight() - 8, { align: "right" });
      doc.text(`Gerado em · ${new Date().toLocaleString("pt-BR")}`, 14, doc.internal.pageSize.getHeight() - 8);
    }
    doc.save(`Freelas_${lote.numero.replace(/[^a-zA-Z0-9]/g, "_")}.pdf`);
    // marca timestamp do PDF gerado
    const updatedLotes = (freelaPagamentos || []).map(l => l.id === loteId ? { ...l, pdfGeradoEm: new Date().toISOString() } : l);
    onUpdate("freelaPagamentos", updatedLotes);
  }

  // Quem pode ver cada sub-tab
  // v8.31.0: líder de área agenda/fecha automaticamente — não precisa mais de checkbox.
  // operational.freelasFechar mantido como fallback (pessoas que já tinham antes da v8.31).
  // ad.freelasFechar mantido como fallback de transição pré-v8.30.
  const _canFechar = isDP || isOwner || isLider
    || perms?.operational?.freelasFechar === true // legacy v8.30
    || perms?.admin?.freelasFechar === true;       // legacy v8.24-v8.29
  const canFechamento = _canFechar;
  const canAgendar    = _canFechar; // alias — quem fecha, agenda
  const canHistorico  = _canFechar;

  const SUB_TABS = [
    { id: "agendar",    label: "📅 Agendar",   visible: canAgendar },
    { id: "lancamento", label: "Lançamento",   visible: true },
    { id: "fechamento", label: "Fechamento",   visible: canFechamento },
    { id: "historico",  label: "Histórico",    visible: canHistorico },
  ].filter(t => t.visible);

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8,marginBottom:14}}>
        <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?16:18}}>🎒 Freelas</h3>
        <div style={{fontSize:11,color:"var(--text3)"}}>
          {restFreelas.length} freelancer{restFreelas.length===1?"":"s"} cadastrado{restFreelas.length===1?"":"s"} · {restShifts.filter(s => s.status === "aberto").length} shift(s) abertos
        </div>
      </div>

      {/* Sub-tabs internas */}
      <div style={{display:"flex",gap:4,marginBottom:16,borderBottom:"1px solid var(--border)",overflowX:"auto"}}>
        {SUB_TABS.map(t => (
          <button key={t.id} onClick={()=>setSubTab(t.id)}
            style={{background:"none",border:"none",borderBottom: subTab===t.id ? `2px solid ${ac}` : "2px solid transparent",padding:"8px 14px",cursor:"pointer",fontSize:13,fontWeight: subTab===t.id ? 700 : 500,color: subTab===t.id ? "var(--text)" : "var(--text3)",whiteSpace:"nowrap",flexShrink:0}}>
            {t.label}
          </button>
        ))}
      </div>

      {/* v8.25.0: Sub-tab Agendar — shifts futuros */}
      {subTab === "agendar" && (
        <FreelaAgendarTab
          restaurantId={restaurantId}
          restPessoas={restPessoas}
          allPessoas={pessoas}
          addFreela={addFreela}
          shifts={restShifts}
          addPlannedShift={addPlannedShift}
          cancelPlannedShift={cancelPlannedShift}
          editPlannedShift={editPlannedShift}
          mobileOnly={mobileOnly}
          ac={ac}
        />
      )}

      {/* Sub-tab: Lançamento — tabela de shifts em status Aberto */}
      {subTab === "lancamento" && (
        <FreelaLancamentoTab
          restaurantId={restaurantId}
          allPessoas={pessoas}
          restPessoas={restPessoas}
          restFreelas={restFreelas}
          shifts={restShifts}
          addShift={addShift}
          updateShift={updateShift}
          deleteShift={deleteShift}
          addFreela={addFreela}
          shiftPrntoPronto={shiftPrntoPronto}
          calcHoras={calcHoras}
          fmtHoras={fmtHoras}
          confirmPlannedShift={confirmPlannedShift}
          cancelPlannedShift={cancelPlannedShift}
          markNoShow={markNoShow}
          editPlannedShift={editPlannedShift}
          closeShift={closeShift}
          isDP={isDP}
          isOwner={isOwner}
          isLider={isLider}
          currentUser={currentUser}
          mobileOnly={mobileOnly}
          ac={ac}
        />
      )}

      {subTab === "fechamento" && (
        <FreelaFechamentoTab
          restaurantId={restaurantId}
          restPessoas={restPessoas}
          shifts={restShifts}
          lotes={restLotes}
          shiftPrntoPronto={shiftPrntoPronto}
          fmtHoras={fmtHoras}
          criarLote={criarLote}
          cancelarLote={cancelarLote}
          marcarLotePago={marcarLotePago}
          gerarPDFLote={gerarPDFLote}
          updateShift={updateShift}
          onUpdate={onUpdate}
          pessoas={pessoas}
          restaurants={restaurants}
          isOwner={isOwner}
          mobileOnly={mobileOnly}
          ac={ac}
        />
      )}

      {subTab === "historico" && (
        <FreelaHistoricoTab
          restaurantId={restaurantId}
          restPessoas={restPessoas}
          shifts={restShifts}
          lotes={restLotes}
          fmtHoras={fmtHoras}
          gerarPDFLote={gerarPDFLote}
          cancelarLote={cancelarLote}
          reabrirLotePago={reabrirLotePago}
          excluirShiftHistorico={excluirShiftHistorico}
          isOwner={isOwner}
          mobileOnly={mobileOnly}
          ac={ac}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ──  FREELAS — sub-componente Lançamento (tabela de shifts)     ──
// ═══════════════════════════════════════════════════════════════
// v8.28.0 — Seção "Freelas do mês" embaixo do grid da Escala
// Lista todos os shifts do mês selecionado (planned + aberto + fechamento + pago + nao_compareceu)
// agrupados por data, distinguindo freela vs empregado-turno-extra com badges.
function FreelasMesNaEscala({ restaurantId, year, month, freelaShifts, pessoas, mobileOnly }) {
  // Filtra shifts do mês: data formato YYYY-MM-DD, mês = month (0-indexed)
  const monthStr = `${year}-${String(month + 1).padStart(2, "0")}`;
  const monthShifts = (freelaShifts || []).filter(s => {
    if (s.restaurantId !== restaurantId) return false;
    const dt = s.scheduledDate || s.date || "";
    return dt.startsWith(monthStr);
  }).sort((a, b) => (a.scheduledDate || a.date || "").localeCompare(b.scheduledDate || b.date || ""));

  // Estatísticas
  const stats = {
    total: monthShifts.length,
    agendados: monthShifts.filter(s => s.status === "agendado").length,
    realizados: monthShifts.filter(s => ["aberto","fechamento","pago"].includes(s.status)).length,
    pagos: monthShifts.filter(s => s.status === "pago").length,
    noshows: monthShifts.filter(s => s.status === "nao_compareceu").length,
  };

  // Cores e labels por status
  function statusInfo(st) {
    if (st === "agendado")        return { label: "AGENDADO",      color: "#7c3aed", bg: "#7c3aed22" };
    if (st === "aberto")          return { label: "ABERTO",        color: "#15803d", bg: "#15803d22" };
    if (st === "fechamento")      return { label: "EM FECHAMENTO", color: "#f59e0b", bg: "#f59e0b22" };
    if (st === "pago")            return { label: "PAGO",          color: "#0ea5e9", bg: "#0ea5e922" };
    if (st === "nao_compareceu")  return { label: "NÃO COMPARECEU",color: "#dc2626", bg: "#dc262622" };
    return { label: st || "—", color: "var(--text3)", bg: "var(--bg2)" };
  }

  if (monthShifts.length === 0) {
    return (
      <details style={{marginTop:24,background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,padding:"10px 14px"}}>
        <summary style={{cursor:"pointer",fontSize:13,fontWeight:700,color:"var(--text2)"}}>🎒 Freelas do mês <span style={{color:"var(--text3)",fontWeight:400,fontSize:11}}>· nenhum shift neste mês</span></summary>
      </details>
    );
  }

  return (
    <details open style={{marginTop:24,background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10}}>
      <summary style={{cursor:"pointer",padding:"12px 14px",fontSize:14,fontWeight:700,color:"var(--text)",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
        <span>🎒 Freelas do mês</span>
        <span style={{fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:10,background:"#7c3aed22",color:"#7c3aed"}}>{stats.total} total</span>
        {stats.agendados > 0  && <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:"#7c3aed22",color:"#7c3aed"}}>{stats.agendados} agendado{stats.agendados>1?"s":""}</span>}
        {stats.realizados > 0 && <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:"#15803d22",color:"#15803d"}}>{stats.realizados} realizado{stats.realizados>1?"s":""}</span>}
        {stats.pagos > 0      && <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:"#0ea5e922",color:"#0ea5e9"}}>{stats.pagos} pago{stats.pagos>1?"s":""}</span>}
        {stats.noshows > 0    && <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:"#dc262622",color:"#dc2626"}}>🚫 {stats.noshows}</span>}
      </summary>
      <div style={{padding:"0 14px 14px"}}>
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {monthShifts.map(s => {
            const p = (pessoas || []).find(x => x.id === s.pessoaId);
            const isEmployeePlus = p?.isTeam?.[restaurantId] === true && !p?.isFreela;
            const dt = s.scheduledDate || s.date;
            const info = statusInfo(s.status);
            const horarios = (s.entrada || s.saida)
              ? `${s.entrada || "—"}${s.saida ? ` → ${s.saida}` : ""}${s.intervalo ? ` (${s.intervalo}min int.)` : ""}`
              : null;
            return (
              <div key={s.id} style={{padding:"8px 12px",background:"var(--bg2)",border:"1px solid var(--border)",borderRadius:8,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                <span style={{fontSize:11,fontFamily:"'DM Mono',monospace",color:"var(--text2)",minWidth:60}}>{fmtDate(dt)}</span>
                <span style={{fontSize:13,color:"var(--text)",fontWeight:600,flex:"0 0 auto"}}>{shortName(p?.name) || "—"}</span>
                {isEmployeePlus
                  ? <span style={{fontSize:9,padding:"1px 6px",borderRadius:6,background:"#10b98122",color:"#15803d",fontWeight:700,whiteSpace:"nowrap"}}>EMPREGADO · EXTRA</span>
                  : <span style={{fontSize:9,padding:"1px 6px",borderRadius:6,background:"#7c3aed22",color:"#7c3aed",fontWeight:700,whiteSpace:"nowrap"}}>FREELA</span>}
                <span style={{fontSize:9,padding:"1px 6px",borderRadius:6,background:info.bg,color:info.color,fontWeight:700,whiteSpace:"nowrap"}}>{info.label}</span>
                <div style={{flex:1,minWidth:120,display:"flex",gap:8,flexWrap:"wrap",fontSize:10,color:"var(--text3)"}}>
                  {s.area && <span>📍 {s.area}</span>}
                  {horarios && <span>🕐 {horarios}</span>}
                  {s.totalCalc != null && <span style={{fontFamily:"'DM Mono',monospace"}}>R$ {Number(s.totalCalc).toFixed(2)}</span>}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{marginTop:10,fontSize:10,color:"var(--text3)",fontStyle:"italic"}}>
          💡 Pra agendar/lançar/fechar shifts, use o módulo <b>🎒 Freelas</b> na sidebar.
        </div>
      </div>
    </details>
  );
}

// v8.28.0 — Modal pra encerrar turno em andamento (saída + intervalo)
// Geralmente quem encerra é DIFERENTE de quem iniciou — esse modal é o fechamento operacional.
function CloseShiftModal({ shift, pessoa, onSave, onClose, mobileOnly }) {
  const [saida, setSaida] = useState("");
  const [intervalo, setIntervalo] = useState("0");
  const [observacao, setObservacao] = useState(shift?.observacao || "");

  if (!shift) return null;

  function handleSave() {
    if (!saida) { alert("Hora de saída é obrigatória."); return; }
    onSave({ saida, intervalo, observacao });
  }

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",borderRadius:14,maxWidth:500,width:"100%",padding:24}}>
        <h3 style={{margin:"0 0 6px",color:"var(--text)",fontSize:16,fontWeight:700}}>■ Encerrar turno</h3>
        <p style={{margin:"0 0 14px",fontSize:12,color:"var(--text3)",lineHeight:1.5}}>
          <b>{shortName(pessoa?.name) || "—"}</b> · {fmtDate(shift.date)} · entrada às <b>{shift.entrada}</b>
          {shift.confirmadoPor && <> · iniciado por <i>{shortName(shift.confirmadoPor)}</i></>}
        </p>
        <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr 1fr":"1fr 1fr 2fr",gap:10,marginBottom:14}}>
          <div>
            <label style={{...S.label,fontSize:11}}>Saída efetiva *</label>
            <input type="time" value={saida} onChange={e=>setSaida(e.target.value)} style={S.input} autoFocus/>
          </div>
          <div>
            <label style={{...S.label,fontSize:11}}>Intervalo (min)</label>
            <input type="number" min="0" value={intervalo} onChange={e=>setIntervalo(e.target.value)} style={S.input}/>
          </div>
          <div>
            <label style={{...S.label,fontSize:11}}>Observação ao encerrar</label>
            <input value={observacao} onChange={e=>setObservacao(e.target.value)} placeholder="Ex: chegou atrasado, bom desempenho..." style={S.input}/>
          </div>
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button onClick={onClose} style={{...S.btnSecondary,padding:"8px 14px",fontSize:13}}>Cancelar</button>
          <button onClick={handleSave}
            style={{background:"#1e40af",color:"#fff",border:"none",borderRadius:8,padding:"8px 16px",fontSize:13,fontWeight:700,cursor:"pointer"}}>
            ■ Encerrar turno
          </button>
        </div>
      </div>
    </div>
  );
}

// v8.27.0 — Modal pra editar dados dum shift agendado (pessoa, data, horários, área, observação)
function PlannedShiftEditModal({ shift, restPessoas, restaurantId, onSave, onClose, mobileOnly }) {
  const [pessoaId, setPessoaId] = useState(shift.pessoaId || "");
  const [scheduledDate, setScheduledDate] = useState(shift.scheduledDate || shift.date || "");
  const [area, setArea] = useState(shift.area || "");
  const [entrada, setEntrada] = useState(shift.entrada || "");
  const [saida, setSaida] = useState(shift.saida || "");
  const [intervalo, setIntervalo] = useState(String(shift.intervalo || ""));
  const [observacao, setObservacao] = useState(shift.observacao || "");

  const freelasList = restPessoas.filter(p => p.isFreela === true && !p.inactive).sort((a,b) => (a.name||"").localeCompare(b.name||""));
  const empsList = restPessoas.filter(p => p.isTeam?.[restaurantId] === true && !p.isFreela && !p.inactive).sort((a,b) => (a.name||"").localeCompare(b.name||""));

  function handleSave() {
    if (!pessoaId) { alert("Selecione uma pessoa."); return; }
    if (!scheduledDate) { alert("Escolha a data."); return; }
    onSave({ pessoaId, scheduledDate, area: area || null, entrada, saida, intervalo, observacao });
  }

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",borderRadius:14,maxWidth:560,width:"100%",padding:24,maxHeight:"90vh",overflowY:"auto"}}>
        <h3 style={{margin:"0 0 14px",color:"var(--text)",fontSize:16,fontWeight:700}}>✏️ Editar agendamento</h3>
        <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"2fr 1fr",gap:10,marginBottom:10}}>
          <div>
            <label style={{...S.label,fontSize:11}}>Pessoa</label>
            <select value={pessoaId} onChange={e=>setPessoaId(e.target.value)} style={S.input}>
              <option value="">— Selecione —</option>
              {freelasList.length > 0 && (
                <optgroup label="🎒 Freelas">
                  {freelasList.map(p => <option key={p.id} value={p.id}>{shortName(p.name)}</option>)}
                </optgroup>
              )}
              {empsList.length > 0 && (
                <optgroup label="👥 Empregados">
                  {empsList.map(p => <option key={p.id} value={p.id}>{shortName(p.name)}</option>)}
                </optgroup>
              )}
            </select>
          </div>
          <div>
            <label style={{...S.label,fontSize:11}}>Data</label>
            <input type="date" value={scheduledDate} onChange={e=>setScheduledDate(e.target.value)} style={S.input}/>
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr 1fr":"1fr 1fr 1fr 2fr",gap:10,marginBottom:10}}>
          <div>
            <label style={{...S.label,fontSize:11}}>Entrada</label>
            <input type="time" value={entrada} onChange={e=>setEntrada(e.target.value)} style={S.input}/>
          </div>
          <div>
            <label style={{...S.label,fontSize:11}}>Saída</label>
            <input type="time" value={saida} onChange={e=>setSaida(e.target.value)} style={S.input}/>
          </div>
          <div>
            <label style={{...S.label,fontSize:11}}>Intervalo (min)</label>
            <input type="number" min="0" value={intervalo} onChange={e=>setIntervalo(e.target.value)} style={S.input}/>
          </div>
          <div>
            <label style={{...S.label,fontSize:11}}>Área</label>
            <select value={area} onChange={e=>setArea(e.target.value)} style={S.input}>
              <option value="">—</option>
              {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        </div>
        <div style={{marginBottom:14}}>
          <label style={{...S.label,fontSize:11}}>Observação</label>
          <input value={observacao} onChange={e=>setObservacao(e.target.value)} style={S.input}/>
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button onClick={onClose} style={{...S.btnSecondary,padding:"8px 14px",fontSize:13}}>Cancelar</button>
          <button onClick={handleSave} style={{...S.btnPrimary,padding:"8px 16px",fontSize:13,fontWeight:700}}>💾 Salvar</button>
        </div>
      </div>
    </div>
  );
}

// v8.26.0 — Card de confirmação dum shift agendado: inline com inputs de entrada/saída
function PlannedShiftConfirmCard({ shift, pessoa, restaurantId, today_, onConfirm, onCancel, ac, mobileOnly, onNoShow, onEdit }) {
  const [expanded, setExpanded] = useState(false);
  // v8.28.0: confirmação só pega ENTRADA. Saída/intervalo são marcados depois (CloseShiftModal).
  const [entrada, setEntrada] = useState(shift.entrada || "");
  const [observacao, setObservacao] = useState(shift.observacao || "");

  const dt = shift.scheduledDate || shift.date;
  const isToday = dt === today_;
  const isOverdue = dt < today_;
  const isEmployeePlus = pessoa?.isTeam?.[restaurantId] === true;

  return (
    <div style={{padding:"10px 12px",background:"var(--card-bg)",border:`1px solid ${isOverdue ? "#dc262666" : "var(--border)"}`,borderRadius:10}}>
      {/* Cabeçalho do card */}
      <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
        <div style={{flex:1,minWidth:200}}>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <span style={{fontSize:13,color:"var(--text)",fontWeight:700}}>{shortName(pessoa?.name) || "—"}</span>
            {isEmployeePlus
              ? <span style={{fontSize:10,padding:"1px 7px",borderRadius:8,background:"#10b98122",color:"#15803d",fontWeight:700}}>EMPREGADO · TURNO EXTRA</span>
              : <span style={{fontSize:10,padding:"1px 7px",borderRadius:8,background:ac+"22",color:ac,fontWeight:700}}>FREELA</span>}
            {isToday && <span style={{fontSize:10,padding:"1px 7px",borderRadius:8,background:"#f59e0b22",color:"#b45309",fontWeight:700}}>HOJE</span>}
            {isOverdue && <span style={{fontSize:10,padding:"1px 7px",borderRadius:8,background:"#dc262622",color:"#991b1b",fontWeight:700}}>ATRASADO</span>}
          </div>
          <div style={{fontSize:11,color:"var(--text3)",marginTop:3,display:"flex",gap:10,flexWrap:"wrap"}}>
            <span>📅 {fmtDate(dt)}</span>
            {shift.area && <span>📍 {shift.area}</span>}
            {shift.valorUnit && <span>💵 R$ {Number(shift.valorUnit).toFixed(2)} / {shift.valorTipo}</span>}
            {shift.observacao && <span style={{fontStyle:"italic"}}>· {shift.observacao}</span>}
          </div>
        </div>
        {!expanded ? (
          <>
            {onEdit && (
              <button onClick={onEdit} title="Editar dados do agendamento"
                style={{...S.btnSecondary,fontSize:11,padding:"6px 10px",whiteSpace:"nowrap"}}>
                ✏️ Editar
              </button>
            )}
            {onNoShow && (
              <button onClick={onNoShow} title="Não compareceu — fica no histórico, sem pagamento"
                style={{...S.btnSecondary,fontSize:11,padding:"6px 10px",color:"#dc2626",borderColor:"#dc262644",whiteSpace:"nowrap"}}>
                🚫 Não compareceu
              </button>
            )}
            <button onClick={onCancel} title="Cancelar agendamento (some sem registro — pra erros)"
              style={{...S.btnSecondary,fontSize:11,padding:"6px 10px",color:"var(--text3)",whiteSpace:"nowrap"}}>
              ✕ Cancelar
            </button>
            <button onClick={()=>setExpanded(true)}
              style={{background:"#15803d",color:"#fff",border:"none",borderRadius:8,padding:"7px 14px",fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
              ▶ Iniciar turno
            </button>
          </>
        ) : (
          <button onClick={()=>setExpanded(false)} style={{...S.btnSecondary,fontSize:11,padding:"5px 10px"}}>Fechar</button>
        )}
      </div>

      {/* Form inline expandido — só registra ENTRADA. Saída + intervalo ficam pra outro líder no final. */}
      {expanded && (
        <div style={{marginTop:10,padding:"10px",background:"var(--bg2)",borderRadius:8,border:"1px solid var(--border)"}}>
          <div style={{fontSize:11,color:"var(--text3)",marginBottom:8,fontStyle:"italic"}}>
            ▶ Marque só a hora de entrada. Saída e intervalo serão registrados ao final do turno (pode ser por outro líder).
          </div>
          <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"1fr 2fr",gap:8,marginBottom:10}}>
            <div>
              <label style={{...S.label,fontSize:10}}>Entrada efetiva *</label>
              <input type="time" value={entrada} onChange={e=>setEntrada(e.target.value)} style={S.input}/>
            </div>
            <div>
              <label style={{...S.label,fontSize:10}}>Observação no início (opcional)</label>
              <input value={observacao} onChange={e=>setObservacao(e.target.value)} placeholder="Algo a registrar..." style={S.input}/>
            </div>
          </div>
          <div style={{display:"flex",justifyContent:"flex-end",gap:6}}>
            <button onClick={()=>setExpanded(false)} style={{...S.btnSecondary,fontSize:11,padding:"6px 12px"}}>Cancelar</button>
            <button onClick={()=>onConfirm({ entrada, saida: "", intervalo: "", observacao })}
              disabled={!entrada}
              style={{background:entrada?"#15803d":"var(--border)",color:"#fff",border:"none",borderRadius:8,padding:"7px 16px",fontSize:12,fontWeight:700,cursor:entrada?"pointer":"not-allowed"}}>
              ▶ Iniciar turno
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// v8.29.0 — Form de cadastro rápido de freela (CPF-first), reutilizado em Agendar e Lançamento
function CadastrarFreelaForm({ ac, mobileOnly, allPessoas, restaurantId, addFreela, onCadastrado, onClose }) {
  const [data, setData] = useState({ name: "", whatsapp: "", pix: "", cpf: "" });
  const cpfRaw = (data.cpf || "").replace(/\D/g, "");
  const cpfValido = cpfRaw.length === 11;
  const existing = cpfValido ? (allPessoas || []).find(p => (p.cpf || "").replace(/\D/g, "") === cpfRaw) : null;
  const jaNesteRest = existing ? (existing.restaurantIds || []).includes(restaurantId) : false;
  const jaFreelaAqui = existing ? !!existing.isFreela : false;
  const effName = data.name || existing?.name || "";
  const effWA   = data.whatsapp || existing?.whatsapp || "";
  const effPIX  = data.pix || existing?.pix || "";
  const faltaWA   = !effWA.trim();
  const faltaPIX  = !effPIX.trim();
  const faltaNome = !effName.trim();

  let statusBox = null;
  if (cpfRaw.length > 0 && cpfRaw.length < 11) {
    statusBox = { kind: "info", icon: "⌨️", text: `Continue digitando — faltam ${11 - cpfRaw.length} dígito(s)` };
  } else if (cpfValido && existing) {
    if (jaFreelaAqui && jaNesteRest) {
      statusBox = { kind: "ok", icon: "✅", text: `${existing.name} já é freela aqui — usar este cadastro` };
    } else if (jaNesteRest) {
      statusBox = { kind: "warn", icon: "🔄", text: `${existing.name} já está aqui (equipe) — vamos marcar também como freela` };
    } else {
      statusBox = { kind: "info", icon: "🔗", text: `${existing.name} já cadastrado em outro restaurante — vamos vincular aqui como freela` };
    }
  } else if (cpfValido && !existing) {
    statusBox = { kind: "info", icon: "🆕", text: `CPF novo — preencha os dados abaixo` };
  }

  const colorByKind = { ok: "#15803d", warn: "#92400e", info: ac };
  const bgByKind    = { ok: "#10b98115", warn: "#fef3c7", info: `${ac}10` };

  async function handleSubmit() {
    if (jaFreelaAqui && jaNesteRest) {
      onCadastrado?.(existing.id);
      onClose?.();
      return;
    }
    const newId = await addFreela({ name: effName, whatsapp: effWA, pix: effPIX, cpf: cpfRaw });
    if (newId) {
      setData({ name: "", whatsapp: "", pix: "", cpf: "" });
      onCadastrado?.(newId);
      onClose?.();
    }
  }

  function handleCpfChange(e) {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 11);
    let formatted = digits;
    if (digits.length > 9)      formatted = digits.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2}).*/, "$1.$2.$3-$4");
    else if (digits.length > 6) formatted = digits.replace(/(\d{3})(\d{3})(\d{0,3}).*/, "$1.$2.$3");
    else if (digits.length > 3) formatted = digits.replace(/(\d{3})(\d{0,3}).*/, "$1.$2");
    setData({ ...data, cpf: formatted });
  }

  return (
    <div style={{background:`${ac}06`,border:`1px dashed ${ac}66`,borderRadius:10,padding:14,marginBottom:14}}>
      <div style={{fontSize:11,color:ac,fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,marginBottom:4}}>🎒 Cadastrar freela</div>
      <div style={{fontSize:11,color:"var(--text3)",marginBottom:10,lineHeight:1.4}}>
        Comece pelo <strong>CPF</strong> — se a pessoa já tiver cadastro em outro restaurante, puxamos os dados automaticamente.
      </div>

      <div style={{marginBottom:10}}>
        <label style={{...S.label,fontSize:11}}>CPF *</label>
        <input
          value={data.cpf}
          onChange={handleCpfChange}
          placeholder="000.000.000-00"
          inputMode="numeric"
          autoFocus
          style={{...S.input,fontFamily:"'DM Mono',monospace",fontSize:14,letterSpacing:0.5}}
        />
      </div>

      {statusBox && (
        <div style={{
          padding:"8px 12px",borderRadius:8,marginBottom:10,fontSize:12,
          background:bgByKind[statusBox.kind],color:colorByKind[statusBox.kind],
          border:`1px solid ${colorByKind[statusBox.kind]}33`,fontWeight:600,
        }}>
          {statusBox.icon} {statusBox.text}
        </div>
      )}

      {cpfValido && !(jaFreelaAqui && jaNesteRest) && (
        <>
          <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"2fr 1.3fr 1.5fr",gap:8,marginBottom:10}}>
            <div>
              <label style={{...S.label,fontSize:11}}>
                Nome {faltaNome && <span style={{color:"var(--red)"}}>*</span>}
                {existing?.name && data.name === "" && <span style={{color:colorByKind.ok,marginLeft:4,fontSize:10}}>(do cadastro)</span>}
              </label>
              <input
                value={data.name || existing?.name || ""}
                onChange={e=>setData({...data,name:e.target.value})}
                placeholder="Nome completo"
                disabled={!!existing?.name}
                style={{...S.input,opacity:existing?.name?0.7:1,cursor:existing?.name?"not-allowed":"text"}}
              />
            </div>
            <div>
              <label style={{...S.label,fontSize:11}}>
                WhatsApp {faltaWA && <span style={{color:"var(--red)"}}>*</span>}
                {existing?.whatsapp && data.whatsapp === "" && <span style={{color:colorByKind.ok,marginLeft:4,fontSize:10}}>(do cadastro)</span>}
              </label>
              <input
                value={data.whatsapp || existing?.whatsapp || ""}
                onChange={e=>setData({...data,whatsapp:e.target.value})}
                placeholder="(11) 9..."
                style={{...S.input,...(faltaWA?{borderColor:"var(--red)"}:null)}}
              />
            </div>
            <div>
              <label style={{...S.label,fontSize:11}}>
                PIX {faltaPIX && <span style={{color:"var(--red)"}}>*</span>}
                {existing?.pix && data.pix === "" && <span style={{color:colorByKind.ok,marginLeft:4,fontSize:10}}>(do cadastro)</span>}
              </label>
              <input
                value={data.pix || existing?.pix || ""}
                onChange={e=>setData({...data,pix:e.target.value})}
                placeholder="CPF, e-mail, tel ou aleatória"
                style={{...S.input,...(faltaPIX?{borderColor:"var(--red)"}:null)}}
              />
            </div>
          </div>

          {existing && (faltaWA || faltaPIX) && (
            <div style={{padding:"8px 12px",background:"#fef3c7",border:"1px solid #fbbf24",borderRadius:8,fontSize:12,color:"#92400e",marginBottom:10}}>
              ⚠️ <strong>{existing.name}</strong> já está cadastrado, mas falta:
              {faltaWA && " WhatsApp"}{faltaWA && faltaPIX && " e"}{faltaPIX && " PIX"}.
              Preencha aí em cima — vamos completar o cadastro existente.
            </div>
          )}
        </>
      )}

      <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:10}}>
        <button onClick={()=>{ setData({name:"",whatsapp:"",pix:"",cpf:""}); onClose?.(); }}
          style={{...S.btnSecondary,fontSize:12,padding:"7px 14px"}}>Cancelar</button>
        <button
          onClick={handleSubmit}
          disabled={!cpfValido || (cpfValido && !(jaFreelaAqui && jaNesteRest) && (faltaNome || faltaWA || faltaPIX))}
          style={{
            background:ac,color:"#fff",border:"none",borderRadius:8,padding:"7px 18px",fontSize:13,fontWeight:700,
            cursor:(!cpfValido || (faltaNome||faltaWA||faltaPIX)) && !(jaFreelaAqui&&jaNesteRest) ? "not-allowed":"pointer",
            opacity:(!cpfValido || (cpfValido && !(jaFreelaAqui && jaNesteRest) && (faltaNome || faltaWA || faltaPIX))) ? 0.5 : 1,
          }}>
          {jaFreelaAqui && jaNesteRest ? "✓ Já cadastrado — usar" :
           existing && jaNesteRest ? "🎒 Marcar como freela" :
           existing ? "🔗 Vincular e usar" :
           "🎒 Cadastrar e usar"}
        </button>
      </div>
    </div>
  );
}

// v8.25.0 — Sub-tab "Agendar": forma de criar shift futuro + lista dos próximos agendamentos
// v8.29.0 — Botão "Cadastrar freela" também aqui (antes só em Lançamento)
function FreelaAgendarTab({ restaurantId, restPessoas, allPessoas, addFreela, shifts, addPlannedShift, cancelPlannedShift, editPlannedShift, mobileOnly, ac }) {
  const [editingShiftId, setEditingShiftId] = useState(null);
  const editingShift = editingShiftId ? shifts.find(s => s.id === editingShiftId) : null;
  const today_ = today();
  const [pessoaId, setPessoaId] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [area, setArea] = useState("");
  // v8.26.2: horários previstos no agendamento (DP define valor depois)
  const [entrada, setEntrada] = useState("");
  const [saida, setSaida] = useState("");
  const [intervalo, setIntervalo] = useState("");
  const [observacao, setObservacao] = useState("");
  // v8.29.0: cadastro rápido de freela inline
  const [showFreelaForm, setShowFreelaForm] = useState(false);

  // Listas: freelas e empregados (separados pra select com optgroup)
  const freelasList = restPessoas.filter(p => p.isFreela === true && !p.inactive)
    .sort((a,b) => (a.name || "").localeCompare(b.name || ""));
  const empsList = restPessoas.filter(p => p.isTeam?.[restaurantId] === true && !p.isFreela && !p.inactive)
    .sort((a,b) => (a.name || "").localeCompare(b.name || ""));

  // Próximos agendamentos (status="agendado") — futuros + hoje
  const plannedShifts = shifts.filter(s =>
    s.restaurantId === restaurantId &&
    s.status === "agendado" &&
    (s.scheduledDate || s.date) >= today_
  ).sort((a,b) => (a.scheduledDate || a.date).localeCompare(b.scheduledDate || b.date));

  function clearForm() {
    setPessoaId(""); setScheduledDate(""); setArea(""); setEntrada(""); setSaida(""); setIntervalo(""); setObservacao("");
  }

  async function handleSubmit() {
    if (!pessoaId) { alert("Selecione uma pessoa."); return; }
    if (!scheduledDate) { alert("Escolha a data."); return; }
    if (scheduledDate < today_) { alert("Pra shift retroativo, use a aba Lançamento."); return; }
    await addPlannedShift({
      pessoaId,
      scheduledDate,
      area: area || null,
      entrada: entrada || "",
      saida: saida || "",
      intervalo: intervalo ? parseInt(intervalo) : 0,
      observacao,
    });
    clearForm();
  }

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      {/* Modal de edição */}
      {editingShift && (
        <PlannedShiftEditModal
          shift={editingShift}
          restPessoas={restPessoas}
          restaurantId={restaurantId}
          mobileOnly={mobileOnly}
          onClose={()=>setEditingShiftId(null)}
          onSave={(patch) => { editPlannedShift(editingShiftId, patch); setEditingShiftId(null); }}
        />
      )}

      {/* v8.29.0: Form de cadastro rápido de freela — pra quem agenda também conseguir cadastrar */}
      {showFreelaForm && (
        <CadastrarFreelaForm
          ac={ac}
          mobileOnly={mobileOnly}
          allPessoas={allPessoas}
          restaurantId={restaurantId}
          addFreela={addFreela}
          onClose={()=>setShowFreelaForm(false)}
          onCadastrado={(id)=>{ setPessoaId(id); }}
        />
      )}

      {/* Form de agendamento */}
      <div style={{padding:"14px 16px",background:"var(--card-bg)",border:`1px solid ${ac}44`,borderRadius:12}}>
        <div style={{fontSize:14,fontWeight:700,color:"var(--text)",marginBottom:10,display:"flex",alignItems:"center",justifyContent:"space-between",gap:6,flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <span>📅</span><span>Agendar shift de freela</span>
          </div>
          <button onClick={()=>setShowFreelaForm(!showFreelaForm)}
            title="Cadastrar um freela rapidinho — sem precisar ir na aba Pessoas"
            style={{background:showFreelaForm?"var(--bg2)":"transparent",color:ac,border:`1px solid ${ac}`,borderRadius:8,padding:"5px 12px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
            {showFreelaForm ? "✕ Fechar cadastro" : "🎒+ Cadastrar freela"}
          </button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"2fr 1fr 1fr",gap:10,marginBottom:10}}>
          <div>
            <label style={{...S.label,fontSize:11}}>Pessoa</label>
            <select value={pessoaId} onChange={e=>setPessoaId(e.target.value)} style={S.input}>
              <option value="">— Selecione —</option>
              {freelasList.length > 0 && (
                <optgroup label="🎒 Freelas cadastrados">
                  {freelasList.map(p => <option key={p.id} value={p.id}>{shortName(p.name)}</option>)}
                </optgroup>
              )}
              {empsList.length > 0 && (
                <optgroup label="👥 Empregados (turno extra)">
                  {empsList.map(p => <option key={p.id} value={p.id}>{shortName(p.name)}</option>)}
                </optgroup>
              )}
            </select>
          </div>
          <div>
            <label style={{...S.label,fontSize:11}}>Data</label>
            <input type="date" value={scheduledDate} min={today_} onChange={e=>setScheduledDate(e.target.value)} style={S.input}/>
          </div>
          <div>
            <label style={{...S.label,fontSize:11}}>Área (opcional)</label>
            <select value={area} onChange={e=>setArea(e.target.value)} style={S.input}>
              <option value="">—</option>
              {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr 1fr":"1fr 1fr 1fr 2fr",gap:10,marginBottom:10}}>
          <div>
            <label style={{...S.label,fontSize:11}}>Entrada prevista</label>
            <input type="time" value={entrada} onChange={e=>setEntrada(e.target.value)} style={S.input}/>
          </div>
          <div>
            <label style={{...S.label,fontSize:11}}>Saída prevista</label>
            <input type="time" value={saida} onChange={e=>setSaida(e.target.value)} style={S.input}/>
          </div>
          <div>
            <label style={{...S.label,fontSize:11}}>Intervalo (min)</label>
            <input type="number" min="0" value={intervalo} onChange={e=>setIntervalo(e.target.value)} placeholder="0" style={{...S.input,fontFamily:"'DM Mono',monospace"}}/>
          </div>
          <div>
            <label style={{...S.label,fontSize:11}}>Observação (opcional)</label>
            <input value={observacao} onChange={e=>setObservacao(e.target.value)} placeholder="Detalhes do shift..." style={S.input}/>
          </div>
        </div>
        <div style={{fontSize:10,color:"var(--text3)",marginBottom:10,fontStyle:"italic"}}>
          💡 O valor (R$) é definido pelo DP no fechamento — você só agenda o turno previsto.
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button onClick={clearForm} style={{...S.btnSecondary,fontSize:12,padding:"7px 14px"}}>Limpar</button>
          <button onClick={handleSubmit}
            style={{background:ac,color:"#fff",border:"none",borderRadius:8,padding:"8px 16px",fontSize:13,fontWeight:700,cursor:"pointer"}}>
            📅 Agendar shift
          </button>
        </div>
      </div>

      {/* Lista de agendamentos futuros */}
      <div>
        <div style={{fontSize:12,fontWeight:700,color:"var(--text2)",textTransform:"uppercase",letterSpacing:0.4,marginBottom:8}}>
          Próximos agendamentos ({plannedShifts.length})
        </div>
        {plannedShifts.length === 0 ? (
          <div style={{padding:"30px 20px",textAlign:"center",color:"var(--text3)",fontSize:13,background:"var(--card-bg)",borderRadius:10,border:"1px dashed var(--border)"}}>
            Sem shifts agendados. Use o formulário acima.
          </div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {plannedShifts.map(s => {
              const p = restPessoas.find(x => x.id === s.pessoaId);
              const isEmployeePlus = p?.isTeam?.[restaurantId] === true;
              const dt = s.scheduledDate || s.date;
              const isToday = dt === today_;
              return (
                <div key={s.id} style={{padding:"10px 14px",background:"var(--card-bg)",border:`1px solid ${isToday?ac+"66":"var(--border)"}`,borderRadius:10,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                  <div style={{flex:1,minWidth:200}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                      <span style={{fontSize:13,color:"var(--text)",fontWeight:700}}>{shortName(p?.name) || "—"}</span>
                      {isEmployeePlus
                        ? <span style={{fontSize:10,padding:"1px 7px",borderRadius:8,background:"#10b98122",color:"#15803d",fontWeight:700}}>EMPREGADO · TURNO EXTRA</span>
                        : <span style={{fontSize:10,padding:"1px 7px",borderRadius:8,background:ac+"22",color:ac,fontWeight:700}}>FREELA</span>}
                      {isToday && <span style={{fontSize:10,padding:"1px 7px",borderRadius:8,background:"#f59e0b22",color:"#b45309",fontWeight:700}}>HOJE</span>}
                    </div>
                    <div style={{fontSize:11,color:"var(--text3)",marginTop:3,display:"flex",gap:10,flexWrap:"wrap"}}>
                      <span>📅 {fmtDate(dt)}</span>
                      {s.area && <span>📍 {s.area}</span>}
                      {(s.entrada || s.saida) && (
                        <span>🕐 {s.entrada || "—"}{s.saida ? ` → ${s.saida}` : ""}{s.intervalo ? ` (${s.intervalo}min int.)` : ""}</span>
                      )}
                      {s.observacao && <span style={{fontStyle:"italic"}}>· {s.observacao}</span>}
                    </div>
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={()=>setEditingShiftId(s.id)} title="Editar agendamento"
                      style={{...S.btnSecondary,fontSize:11,padding:"5px 10px"}}>
                      ✏️ Editar
                    </button>
                    <button onClick={()=>cancelPlannedShift(s.id)} title="Cancelar agendamento"
                      style={{...S.btnSecondary,fontSize:11,padding:"5px 10px",color:"var(--red)",borderColor:"var(--red)44"}}>
                      🗑️
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function FreelaLancamentoTab({ restaurantId, allPessoas, restPessoas, restFreelas, shifts, addShift, updateShift, deleteShift, addFreela, shiftPrntoPronto, calcHoras, fmtHoras, confirmPlannedShift, cancelPlannedShift, markNoShow, editPlannedShift, closeShift, isDP, isOwner, isLider, currentUser, mobileOnly, ac }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newShift, setNewShift] = useState({ pessoaId: "", date: today(), entrada: "", saida: "", intervalo: 0, area: "", observacao: "" });
  const [showFreelaForm, setShowFreelaForm] = useState(false);
  // v8.29.0: form de cadastro extraído pra subcomponente CadastrarFreelaForm (state interno).
  // v8.29.0: bulk fill removido — DP edita valor shift a shift no Fechamento
  const [filterStatus, setFilterStatus] = useState("todos"); // todos | aberto | pendentes | fechamento | pago
  const [filterPeriod, setFilterPeriod] = useState({ from: "", to: "" });
  const [filterPessoa, setFilterPessoa] = useState("");

  // v8.5.1: qualquer líder/DP/owner pode editar shifts em aberto (líder B fecha turno aberto pelo líder A)
  // Travado se status != "aberto" (já em lote ou pago).
  const canEditAll = isDP || isOwner; // eslint-disable-line no-unused-vars
  const canEditShift = (s) => s.status === "aberto";

  // Filtros aplicados
  const filtered = shifts.filter(s => {
    // v8.26.1: agendados não aparecem na tabela principal — ficam na aba Agendar (futuros)
    // ou na seção amarela "Agendados pra hoje" (hoje/atrasados)
    if (s.status === "agendado") return false;
    if (filterStatus === "pendentes") {
      if (s.status !== "aberto" || s.valorTipo) return false;
    } else if (filterStatus !== "todos" && s.status !== filterStatus) return false;
    if (filterPessoa && s.pessoaId !== filterPessoa) return false;
    if (filterPeriod.from && s.date < filterPeriod.from) return false;
    if (filterPeriod.to && s.date > filterPeriod.to) return false;
    return true;
  }).sort((a,b) => (b.date || "").localeCompare(a.date || "") || (b.lancadoEm || "").localeCompare(a.lancadoEm || ""));

  function handleAddSubmit() {
    if (!newShift.pessoaId) { alert("Selecione a pessoa."); return; }
    if (!newShift.date) { alert("Selecione a data."); return; }
    // v8.5: só entrada é obrigatória na ABERTURA. Saída/intervalo/valor são preenchidos
    // pelo DP no Fechamento. Se o operador já souber o resto, pode preencher tudo de uma vez.
    if (!newShift.entrada) { alert("Preencha a hora de entrada."); return; }
    addShift(newShift);
    setNewShift({ pessoaId: "", date: today(), entrada: "", saida: "", intervalo: 0, area: "", observacao: "" });
    setShowAddForm(false);
  }

  // Pessoas disponíveis pra escolher: freelas deste rest + EQUIPE deste rest (líder pode pegar freela ad-hoc)
  // Importante: isTeam[restaurantId] (per-rest), não p.isTeam (objeto truthy mesmo vazio)
  const elegiveis = restPessoas.filter(p => p.isFreela || p.isTeam?.[restaurantId]).sort((a,b) => a.name.localeCompare(b.name));

  // v8.29.0: handleAddFreelaSubmit removido — lógica embutida no CadastrarFreelaForm + onCadastrado.

  // v8.26.0: shifts agendados pra hoje OU atrasados (já passou e ninguém confirmou)
  const today_ = today();
  const plannedToConfirm = shifts.filter(s =>
    s.restaurantId === restaurantId &&
    s.status === "agendado" &&
    (s.scheduledDate || s.date) <= today_
  ).sort((a,b) => (a.scheduledDate || a.date).localeCompare(b.scheduledDate || b.date));

  // v8.28.0: shifts EM ANDAMENTO — turno iniciado (entrada marcada) mas ainda sem saída
  const inProgressShifts = shifts.filter(s =>
    s.restaurantId === restaurantId &&
    s.status === "aberto" &&
    s.entrada && !s.saida
  ).sort((a,b) => (b.confirmadoEm || "").localeCompare(a.confirmadoEm || ""));

  // State pra encerrar turno
  const [closingShiftId, setClosingShiftId] = useState(null);

  // v8.27.0: state pra modal de edição
  const [editingShiftId, setEditingShiftId] = useState(null);
  const editingShift = editingShiftId ? shifts.find(s => s.id === editingShiftId) : null;

  return (
    <div style={{paddingBottom:48}}>
      {/* Modal de edição (compartilhado entre seção amarela e modais) */}
      {editingShift && (
        <PlannedShiftEditModal
          shift={editingShift}
          restPessoas={restPessoas}
          restaurantId={restaurantId}
          mobileOnly={mobileOnly}
          onClose={()=>setEditingShiftId(null)}
          onSave={(patch) => { editPlannedShift(editingShiftId, patch); setEditingShiftId(null); }}
        />
      )}

      {/* v8.28.0: Modal de encerrar turno (saída + intervalo) */}
      {closingShiftId && (
        <CloseShiftModal
          shift={shifts.find(s => s.id === closingShiftId)}
          pessoa={restPessoas.find(p => p.id === shifts.find(s => s.id === closingShiftId)?.pessoaId)}
          mobileOnly={mobileOnly}
          onClose={()=>setClosingShiftId(null)}
          onSave={(payload) => { closeShift(closingShiftId, payload); setClosingShiftId(null); }}
        />
      )}

      {/* v8.28.0: Card de Em andamento (entrada marcada, sem saída) */}
      {inProgressShifts.length > 0 && (
        <div style={{padding:"12px 14px",background:"#dbeafe",border:"1px solid #1e40af66",borderRadius:12,marginBottom:14}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,flexWrap:"wrap"}}>
            <span style={{fontSize:14,fontWeight:700,color:"#1e3a8a"}}>🔵 Em andamento</span>
            <span style={{fontSize:11,color:"#1e3a8a",background:"#fff",padding:"2px 8px",borderRadius:10,fontWeight:700}}>{inProgressShifts.length}</span>
            <span style={{fontSize:11,color:"#1e3a8a",fontStyle:"italic"}}>· encerre o turno marcando saída e intervalo (pode ser outro líder)</span>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {inProgressShifts.map(s => {
              const p = restPessoas.find(x => x.id === s.pessoaId);
              const isEmployeePlus = p?.isTeam?.[restaurantId] === true && !p?.isFreela;
              return (
                <div key={s.id} style={{padding:"10px 12px",background:"var(--card-bg)",border:"1px solid #1e40af33",borderRadius:10,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                  <div style={{flex:1,minWidth:200}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                      <span style={{fontSize:13,color:"var(--text)",fontWeight:700}}>{shortName(p?.name) || "—"}</span>
                      {isEmployeePlus
                        ? <span style={{fontSize:10,padding:"1px 7px",borderRadius:8,background:"#10b98122",color:"#15803d",fontWeight:700}}>EMPREGADO · TURNO EXTRA</span>
                        : <span style={{fontSize:10,padding:"1px 7px",borderRadius:8,background:ac+"22",color:ac,fontWeight:700}}>FREELA</span>}
                    </div>
                    <div style={{fontSize:11,color:"var(--text3)",marginTop:3,display:"flex",gap:10,flexWrap:"wrap"}}>
                      <span>📅 {fmtDate(s.date)}</span>
                      <span>▶ Entrada: <b>{s.entrada}</b></span>
                      {s.area && <span>📍 {s.area}</span>}
                      {s.confirmadoPor && <span style={{fontStyle:"italic"}}>· iniciado por {shortName(s.confirmadoPor)}</span>}
                    </div>
                  </div>
                  <button onClick={()=>setClosingShiftId(s.id)}
                    style={{background:"#1e40af",color:"#fff",border:"none",borderRadius:8,padding:"7px 14px",fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                    ■ Encerrar turno
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* v8.26.0: Card de Agendados pra hoje/atrasados — confirmação leva pro fluxo legado */}
      {plannedToConfirm.length > 0 && (
        <div style={{padding:"12px 14px",background:"#fef3c7",border:"1px solid #f59e0b66",borderRadius:12,marginBottom:14}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,flexWrap:"wrap"}}>
            <span style={{fontSize:14,fontWeight:700,color:"#92400e"}}>🟡 Agendados pra hoje</span>
            <span style={{fontSize:11,color:"#92400e",background:"#fff",padding:"2px 8px",borderRadius:10,fontWeight:700}}>{plannedToConfirm.length}</span>
            <span style={{fontSize:11,color:"#92400e",fontStyle:"italic"}}>· confirme, edite ou marque não compareceu</span>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {plannedToConfirm.map(s => (
              <PlannedShiftConfirmCard
                key={s.id}
                shift={s}
                pessoa={restPessoas.find(p => p.id === s.pessoaId)}
                restaurantId={restaurantId}
                today_={today_}
                onConfirm={(payload) => confirmPlannedShift(s.id, payload)}
                onCancel={() => cancelPlannedShift(s.id)}
                onNoShow={() => markNoShow(s.id)}
                onEdit={() => setEditingShiftId(s.id)}
                ac={ac}
                mobileOnly={mobileOnly}
              />
            ))}
          </div>
        </div>
      )}

      {/* Toolbar: botão + filtros */}
      <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap",alignItems:"center"}}>
        <button onClick={()=>setShowAddForm(!showAddForm)}
          style={{background:showAddForm?"var(--bg2)":ac,color:showAddForm?"var(--text)":"#fff",border:`1px solid ${ac}`,borderRadius:8,padding:"7px 16px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
          {showAddForm ? "✕ Fechar" : "+ Novo shift"}
        </button>
        <button onClick={()=>setShowFreelaForm(!showFreelaForm)}
          title="Cadastrar um freela rapidinho — sem precisar ir na aba Pessoas"
          style={{background:showFreelaForm?"var(--bg2)":"transparent",color:ac,border:`1px solid ${ac}`,borderRadius:8,padding:"7px 14px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
          {showFreelaForm ? "✕ Fechar" : "🎒+ Cadastrar freela"}
        </button>
        <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}
          style={{...S.input,maxWidth:180,fontSize:12,padding:"7px 10px",cursor:"pointer"}}>
          <option value="todos">📋 Todos status</option>
          <option value="aberto">🟢 Abertos</option>
          <option value="pendentes">⚠️ Pendentes (sem valor)</option>
          <option value="fechamento">🔒 Em fechamento</option>
          <option value="pago">💸 Pagos</option>
        </select>
        {/* v8.29.0: Bulk fill removido — DP edita valor shift a shift no Fechamento */}
        <select value={filterPessoa} onChange={e=>setFilterPessoa(e.target.value)}
          style={{...S.input,maxWidth:200,fontSize:12,padding:"7px 10px",cursor:"pointer"}}>
          <option value="">🎒 Todas as pessoas</option>
          {elegiveis.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <input type="date" value={filterPeriod.from} onChange={e=>setFilterPeriod({...filterPeriod,from:e.target.value})}
          title="De" style={{...S.input,maxWidth:140,fontSize:12,padding:"7px 10px"}}/>
        <input type="date" value={filterPeriod.to} onChange={e=>setFilterPeriod({...filterPeriod,to:e.target.value})}
          title="Até" style={{...S.input,maxWidth:140,fontSize:12,padding:"7px 10px"}}/>
      </div>

      {/* v8.29.0: Form de cadastro rápido extraído pra subcomponente reutilizável */}
      {showFreelaForm && (
        <CadastrarFreelaForm
          ac={ac}
          mobileOnly={mobileOnly}
          allPessoas={allPessoas}
          restaurantId={restaurantId}
          addFreela={addFreela}
          onClose={()=>setShowFreelaForm(false)}
          onCadastrado={(id)=>{
            setNewShift(s => ({ ...s, pessoaId: id }));
            // se o form de shift não estava aberto, abre — pra fluxo natural cadastro→lança
            if (!showAddForm) setShowAddForm(true);
          }}
        />
      )}

      {/* v8.29.0: Form de bulk-fill removido — DP edita valor shift a shift no Fechamento */}

      {/* Form de adicionar shift */}
      {showAddForm && (
        <div style={{background:`${ac}08`,border:`1px solid ${ac}44`,borderRadius:10,padding:14,marginBottom:14}}>
          <div style={{fontSize:11,color:ac,fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,marginBottom:4}}>Abrir shift</div>
          <div style={{fontSize:11,color:"var(--text3)",marginBottom:10}}>Saída, intervalo e valor são preenchidos pelo DP no <em>Fechamento</em>.</div>
          {/* v8.5.1: form simplificado — só pede o que o líder precisa pra abrir */}
          <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"2fr 1fr 1fr 1fr",gap:8,marginBottom:10}}>
            <div>
              <label style={{...S.label,fontSize:11}}>Pessoa</label>
              <select value={newShift.pessoaId} onChange={e=>setNewShift({...newShift,pessoaId:e.target.value})} style={{...S.input,cursor:"pointer"}}>
                <option value="">— Selecione —</option>
                {restFreelas.length > 0 && (
                  <optgroup label="🎒 Freelas">
                    {restFreelas.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </optgroup>
                )}
                {restPessoas.filter(p => p.isTeam?.[restaurantId] && !p.isFreela).length > 0 && (
                  <optgroup label="👥 Equipe (CLT pegando freela)">
                    {restPessoas.filter(p => p.isTeam?.[restaurantId] && !p.isFreela).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </optgroup>
                )}
              </select>
            </div>
            <div>
              <label style={{...S.label,fontSize:11}}>Data</label>
              <input type="date" value={newShift.date} onChange={e=>setNewShift({...newShift,date:e.target.value})} style={S.input}/>
            </div>
            <div>
              <label style={{...S.label,fontSize:11}}>Hora de entrada</label>
              <input type="time" value={newShift.entrada} onChange={e=>setNewShift({...newShift,entrada:e.target.value})} style={S.input}/>
            </div>
            <div>
              <label style={{...S.label,fontSize:11}}>Área</label>
              <select value={newShift.area} onChange={e=>setNewShift({...newShift,area:e.target.value})} style={{...S.input,cursor:"pointer"}}>
                <option value="">— Sem área —</option>
                {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          </div>
          <div style={{marginBottom:10}}>
            <label style={{...S.label,fontSize:11}}>Observação (opcional)</label>
            <input value={newShift.observacao} onChange={e=>setNewShift({...newShift,observacao:e.target.value})} placeholder="ex: chegou atrasado, cobriu evento, etc" style={S.input}/>
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <button onClick={()=>setShowAddForm(false)} style={{...S.btnSecondary,fontSize:12,padding:"7px 14px"}}>Cancelar</button>
            <button onClick={handleAddSubmit} style={{background:ac,color:"#fff",border:"none",borderRadius:8,padding:"7px 18px",fontSize:13,fontWeight:700,cursor:"pointer"}}>+ Abrir shift</button>
          </div>
        </div>
      )}

      {/* Tabela de shifts */}
      {filtered.length === 0 ? (
        <div style={{padding:"40px 24px",textAlign:"center",color:"var(--text3)",fontSize:13,background:"var(--card-bg)",border:"1px dashed var(--border)",borderRadius:12}}>
          <div style={{fontSize:36,marginBottom:10}}>🎒</div>
          <div style={{color:"var(--text)",fontWeight:600,fontSize:14,marginBottom:6}}>Nenhum shift {filterStatus!=="todos"||filterPessoa||filterPeriod.from||filterPeriod.to ? "com esses filtros" : "lançado ainda"}</div>
          {restFreelas.length === 0 && (
            <p style={{fontSize:12,marginTop:8}}>
              Use o botão <strong>🎒+ Cadastrar freela</strong> aí em cima pra começar.
            </p>
          )}
        </div>
      ) : (
        <div>
          {/* v8.5.1: cards coloridos por completude. Líder pode fechar turno (preencher saída/intervalo) inline */}
          <div style={{ display: "grid", gridTemplateColumns: mobileOnly ? "1fr" : "repeat(auto-fill, minmax(320px, 1fr))", gap: 10, marginBottom: 10 }}>
            {filtered.map(s => (
              <FreelaShiftCard
                key={s.id}
                shift={s}
                pessoa={restPessoas.find(p => p.id === s.pessoaId)}
                editable={canEditShift(s)}
                shiftPrntoPronto={shiftPrntoPronto}
                updateShift={updateShift}
                deleteShift={deleteShift}
                fmtHoras={fmtHoras}
                mobileOnly={mobileOnly}
                ac={ac}
              />
            ))}
          </div>

          {/* Resumo */}
          <div style={{ padding: "10px 14px", background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 10, fontSize: 11, color: "var(--text3)", display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "space-between" }}>
            <div>
              {filtered.length} shift{filtered.length===1?"":"s"} · {filtered.filter(s=>s.status==="aberto").length} aberto(s) · {filtered.filter(s=>s.status==="aberto"&&shiftPrntoPronto(s).ok).length} pronto(s)
            </div>
            <div style={{fontFamily:"'DM Mono',monospace",color:"var(--text)",fontWeight:700}}>
              Total fechado: R$ {filtered.reduce((sum, s) => sum + (s.totalCalc || 0), 0).toFixed(2)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ──  FREELAS — sub-componente Fechamento (criar lote, marcar pago) ─
// ═══════════════════════════════════════════════════════════════
// v8.5.1 — Card do Lançamento (Líder). Cor amarela se faltar saída/intervalo, verde se completo.
// Líder pode editar entrada/saída/intervalo enquanto status="aberto" (qualquer líder, não só quem abriu).
function FreelaShiftCard({ shift: s, pessoa, editable, shiftPrntoPronto, updateShift, deleteShift, fmtHoras, mobileOnly, ac }) {
  const [editing, setEditing] = useState(false);
  const [entrada, setEntrada] = useState(s.entrada || "");
  const [saida, setSaida] = useState(s.saida || "");
  const [intervalo, setIntervalo] = useState(s.intervalo || 0);

  // Sincroniza state com prop quando shift muda externamente
  useEffect(() => {
    setEntrada(s.entrada || "");
    setSaida(s.saida || "");
    setIntervalo(s.intervalo || 0);
  }, [s.entrada, s.saida, s.intervalo]);

  const turnoCompleto = !!(s.entrada && s.saida);
  const pronto = s.status === "aberto" ? shiftPrntoPronto(s) : { ok: false };

  // Cor do card por completude
  let bg, border, statusBadge;
  if (s.status === "pago") {
    bg = "#10b98108"; border = "#10b98144";
    statusBadge = { icon: "💸", color: "#15803d", bg: "#10b98122", label: "Pago" };
  } else if (s.status === "fechamento") {
    bg = "#7c3aed08"; border = "#7c3aed44";
    statusBadge = { icon: "🔒", color: "#6d28d9", bg: "#7c3aed22", label: "Em fechamento" };
  } else if (pronto.ok) {
    bg = "#10b98115"; border = "#10b98155";
    statusBadge = { icon: "✅", color: "#15803d", bg: "#10b98122", label: "Pronto pra lote" };
  } else if (turnoCompleto) {
    // Turno fechado mas falta valor (DP precifica)
    bg = "#dbeafe"; border = "#3b82f655";
    statusBadge = { icon: "💰", color: "#1e40af", bg: "#3b82f622", label: "Aguardando DP" };
  } else {
    // Turno em aberto (sem saída) — Líder precisa fechar
    bg = "#fef3c7"; border = "#f59e0b66";
    statusBadge = { icon: "⏳", color: "#92400e", bg: "#f59e0b22", label: "Turno em aberto" };
  }

  function handleSalvarTurno() {
    if (!entrada) { alert("Entrada é obrigatória."); return; }
    updateShift(s.id, { entrada, saida, intervalo: parseInt(intervalo) || 0 });
    setEditing(false);
  }

  return (
    <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 12, padding: 12, position: "relative" }}>
      {/* Header: nome + status */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: "var(--text)", fontSize: 14 }}>{pessoa?.name || "(removido)"}</div>
          <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>📅 {fmtDate(s.date)}{s.area ? ` · ${s.area}` : ""}</div>
        </div>
        <span style={{ padding: "3px 8px", background: statusBadge.bg, color: statusBadge.color, borderRadius: 6, fontSize: 10, fontWeight: 700, whiteSpace: "nowrap" }}>
          {statusBadge.icon} {statusBadge.label}
        </span>
      </div>

      {/* Modo editing: form inline pra Líder fechar turno */}
      {editing ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 8 }}>
          <div>
            <label style={{ ...S.label, fontSize: 9 }}>Entrada</label>
            <input type="time" value={entrada} onChange={e => setEntrada(e.target.value)} style={{ ...S.input, fontSize: 12, padding: "5px 7px" }} />
          </div>
          <div>
            <label style={{ ...S.label, fontSize: 9 }}>Saída</label>
            <input type="time" value={saida} onChange={e => setSaida(e.target.value)} style={{ ...S.input, fontSize: 12, padding: "5px 7px" }} />
          </div>
          <div>
            <label style={{ ...S.label, fontSize: 9 }}>Interv. (min)</label>
            <input type="number" min="0" value={intervalo} onChange={e => setIntervalo(e.target.value)} style={{ ...S.input, fontSize: 12, padding: "5px 7px" }} />
          </div>
        </div>
      ) : (
        // Modo visual: chips com horários
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
          <div style={{ background: "rgba(255,255,255,0.6)", padding: "5px 10px", borderRadius: 6, fontSize: 12 }}>
            <span style={{ color: "var(--text3)", fontSize: 10, marginRight: 4 }}>Entrada:</span>
            <strong style={{ fontFamily: "'DM Mono',monospace", color: "var(--text)" }}>{s.entrada || "—"}</strong>
          </div>
          <div style={{ background: "rgba(255,255,255,0.6)", padding: "5px 10px", borderRadius: 6, fontSize: 12 }}>
            <span style={{ color: "var(--text3)", fontSize: 10, marginRight: 4 }}>Saída:</span>
            <strong style={{ fontFamily: "'DM Mono',monospace", color: s.saida ? "var(--text)" : "#92400e" }}>{s.saida || "Aguardando"}</strong>
          </div>
          {(s.intervalo > 0 || s.saida) && (
            <div style={{ background: "rgba(255,255,255,0.6)", padding: "5px 10px", borderRadius: 6, fontSize: 12 }}>
              <span style={{ color: "var(--text3)", fontSize: 10, marginRight: 4 }}>Int.:</span>
              <strong style={{ fontFamily: "'DM Mono',monospace", color: "var(--text)" }}>{s.intervalo || 0}min</strong>
            </div>
          )}
          {s.horas > 0 && (
            <div style={{ background: "rgba(255,255,255,0.6)", padding: "5px 10px", borderRadius: 6, fontSize: 12 }}>
              <span style={{ color: "var(--text3)", fontSize: 10, marginRight: 4 }}>⏱️</span>
              <strong style={{ fontFamily: "'DM Mono',monospace", color: "var(--text)" }}>{fmtHoras(s.horas)}</strong>
            </div>
          )}
        </div>
      )}

      {/* Observação */}
      {s.observacao && !editing && (
        <div style={{ fontSize: 11, color: "var(--text3)", fontStyle: "italic", marginBottom: 8, padding: "4px 8px", background: "rgba(255,255,255,0.5)", borderRadius: 6 }}>
          💬 {s.observacao}
        </div>
      )}

      {/* Footer: total + ações */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, paddingTop: 8, borderTop: `1px solid ${border}` }}>
        <div style={{ fontSize: 13, fontWeight: 700, fontFamily: "'DM Mono',monospace", color: s.totalCalc != null ? ac : "var(--text3)" }}>
          {s.totalCalc != null ? `R$ ${s.totalCalc.toFixed(2)}` : "—"}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {editable && !editing && (
            <button onClick={()=>setEditing(true)} title="Editar entrada/saída/intervalo" style={{ background: "transparent", color: ac, border: `1px solid ${ac}55`, borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
              {turnoCompleto ? "✏️ Editar" : "🔚 Fechar turno"}
            </button>
          )}
          {editing && (
            <>
              <button onClick={()=>setEditing(false)} style={{ background: "transparent", color: "var(--text3)", border: "1px solid var(--border)", borderRadius: 6, padding: "3px 10px", fontSize: 11, cursor: "pointer" }}>Cancelar</button>
              <button onClick={handleSalvarTurno} style={{ background: ac, color: "#fff", border: "none", borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>✓ Salvar</button>
            </>
          )}
          {editable && !editing && (
            <button onClick={()=>deleteShift(s.id)} title="Apagar shift" style={{ background: "transparent", color: "var(--red)", border: "1px solid var(--red)55", borderRadius: 6, padding: "3px 10px", fontSize: 11, cursor: "pointer" }}>🗑️</button>
          )}
        </div>
      </div>
    </div>
  );
}

// v8.7.1 — Card do Fechamento por PESSOA (agrupa N turnos da mesma pessoa em 1 card).
// Pre-fill com última paga (valorTipo+valor) que essa pessoa recebeu em shifts anteriores.
// DP precifica todos os turnos da pessoa de uma vez com o mesmo valor.
function FechaPessoaCard({ pessoa, shifts: pessoaShifts, allShifts, updateShift, mobileOnly, ac, onUpdate, pessoas, fmtHoras, freelaRates, onOpenManageRates }) {
  // Encontra última paga (mais recente) dessa pessoa em qualquer shift histórico
  const lastRate = (() => {
    const sorted = (allShifts || [])
      .filter(s => s.pessoaId === pessoa?.id && s.valorTipo && (s.valorUnit || 0) > 0)
      .sort((a,b) => (b.lancadoEm || b.date || "").localeCompare(a.lancadoEm || a.date || ""));
    return sorted[0] ? { valorTipo: sorted[0].valorTipo, valorUnit: sorted[0].valorUnit } : null;
  })();

  const [selectedRateId, setSelectedRateId] = useState(""); // "" = Personalizado
  const [valorTipo, setValorTipo] = useState(lastRate?.valorTipo || "hora");
  const [valorUnit, setValorUnit] = useState(lastRate?.valorUnit != null ? String(lastRate.valorUnit).replace(".", ",") : "");
  const [pix, setPix] = useState(pessoa?.pix || "");
  const fromBR = (v) => parseFloat(String(v || "").replace(",", ".")) || 0;
  const missingPix = !pessoa?.pix;

  // Total estimado com base no valor escolhido
  const valorNum = fromBR(valorUnit);
  const totalEstimado = pessoaShifts.reduce((sum, s) => {
    if (valorTipo === "hora") return sum + (s.horas || 0) * valorNum;
    return sum + valorNum; // diária
  }, 0);

  function handleSelectRate(rateId) {
    setSelectedRateId(rateId);
    if (rateId === "__manage__") { onOpenManageRates && onOpenManageRates(); setSelectedRateId(""); return; }
    if (!rateId) return;
    const rate = (freelaRates || []).find(r => r.id === rateId);
    if (rate) {
      setValorTipo(rate.valorTipo);
      setValorUnit(String(rate.valor).replace(".", ","));
    }
  }

  function handleSalvarTodos() {
    const v = fromBR(valorUnit);
    if (v <= 0) { alert("Preencha o valor."); return; }
    pessoaShifts.forEach(s => updateShift(s.id, { valorTipo, valorUnit: v }));
    if (missingPix && pix.trim() && pessoa && onUpdate && pessoas) {
      const next = pessoas.map(p => p.id === pessoa.id ? { ...p, pix: pix.trim() } : p);
      onUpdate("pessoas", next);
    }
    onUpdate && onUpdate("_toast", `💰 ${pessoaShifts.length} turno(s) de ${pessoa?.name} precificado(s)`);
  }

  return (
    <div style={{ background: "#dbeafe", border: "1px solid #3b82f655", borderRadius: 12, padding: 12 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10, gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: "var(--text)", fontSize: 14 }}>{pessoa?.name || "(removido)"}</div>
          <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>
            {pessoaShifts.length} turno{pessoaShifts.length !== 1 ? "s" : ""} aguardando precificação
            {lastRate && <span style={{ color: "#1e40af", fontWeight: 600 }}> · 💡 sugerido: R$ {lastRate.valorUnit.toFixed(2)}{lastRate.valorTipo === "hora" ? "/h" : " diária"}</span>}
          </div>
        </div>
        <span style={{ padding: "3px 8px", background: "#3b82f622", color: "#1e40af", borderRadius: 6, fontSize: 10, fontWeight: 700, whiteSpace: "nowrap" }}>💰 Aguardando DP</span>
      </div>

      {/* Lista de turnos compacta */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10, padding: "6px 8px", background: "rgba(255,255,255,0.5)", borderRadius: 8 }}>
        {pessoaShifts.sort((a,b) => (a.date || "").localeCompare(b.date || "")).map(s => (
          <div key={s.id} style={{ display: "flex", flexDirection: "column", gap: 1, fontSize: 11, padding: "4px 0", borderBottom: "1px dashed rgba(0,0,0,0.06)" }}>
            <div style={{ color: "var(--text2)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>📅 {fmtDate(s.date)}{s.area ? ` · ${s.area}` : ""}</div>
            <div style={{ color: "var(--text3)", fontFamily: "'DM Mono',monospace", fontSize: 10, whiteSpace: "nowrap" }}>
              {s.entrada}→{s.saida}{s.intervalo > 0 ? ` (${s.intervalo}m)` : ""} = {fmtHoras ? fmtHoras(s.horas) : s.horas + "h"}
            </div>
          </div>
        ))}
      </div>

      {/* Dropdown de nível pré-cadastrado */}
      <div style={{ marginBottom: 8 }}>
        <label style={{ ...S.label, fontSize: 10 }}>Nível / Valor</label>
        <select value={selectedRateId} onChange={e => handleSelectRate(e.target.value)} style={{ ...S.input, fontSize: 12, padding: "7px 9px", cursor: "pointer" }}>
          <option value="">— Personalizado (digite abaixo) —</option>
          {(freelaRates || []).length > 0 && (
            <optgroup label="Cadastrados">
              {freelaRates.map(r => (
                <option key={r.id} value={r.id}>
                  {r.label} — R$ {r.valor.toFixed(2)} {r.valorTipo === "hora" ? "/hora" : "/diária"}
                </option>
              ))}
            </optgroup>
          )}
          <option value="__manage__" style={{ fontWeight: 700 }}>⚙️ Gerenciar níveis...</option>
        </select>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: mobileOnly ? "1fr 1fr" : "1.2fr 1fr", gap: 8, marginBottom: missingPix ? 8 : 0 }}>
        <div>
          <label style={{ ...S.label, fontSize: 10 }}>Tipo de valor</label>
          <select value={valorTipo} onChange={e => { setValorTipo(e.target.value); setSelectedRateId(""); }} style={{ ...S.input, fontSize: 12, padding: "7px 9px" }}>
            <option value="hora">R$ por hora</option>
            <option value="diaria">Diária</option>
          </select>
        </div>
        <div>
          <label style={{ ...S.label, fontSize: 10 }}>Valor (R$)</label>
          <input type="text" inputMode="decimal" value={valorUnit} onChange={e => { setValorUnit(e.target.value); setSelectedRateId(""); }} placeholder="0,00" style={{ ...S.input, fontSize: 12, padding: "7px 9px" }} />
        </div>
      </div>
      {missingPix && (
        <div style={{ marginBottom: 4 }}>
          <label style={{ ...S.label, fontSize: 10, color: "#b45309" }}>⚠️ PIX da pessoa (não cadastrado)</label>
          <input type="text" value={pix} onChange={e => setPix(e.target.value)} placeholder="CPF, e-mail, celular ou chave aleatória" style={{ ...S.input, fontSize: 12, padding: "7px 9px" }} />
        </div>
      )}

      {/* Total estimado + ação */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, paddingTop: 8, borderTop: "1px solid #3b82f633" }}>
        <div>
          <div style={{ fontSize: 10, color: "var(--text3)" }}>Total estimado ({pessoaShifts.length} turno{pessoaShifts.length !== 1 ? "s" : ""}):</div>
          <div style={{ fontFamily: "'DM Mono',monospace", fontWeight: 800, fontSize: 16, color: valorNum > 0 ? "#1e40af" : "var(--text3)" }}>
            R$ {totalEstimado.toFixed(2)}
          </div>
        </div>
        <button onClick={handleSalvarTodos} style={{ background: ac, color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          💰 Lançar pros {pessoaShifts.length} turno{pessoaShifts.length !== 1 ? "s" : ""}
        </button>
      </div>
    </div>
  );
}

// v8.6 — Row do Fechamento (DP) com dropdown de níveis pré-cadastrados.
// DP pode escolher nível (Ajudante/Operacional/etc) ou "Personalizado" pra digitar livre.
// (Mantido por compat — substituído por FechaPessoaCard na v8.7.1)
// eslint-disable-next-line no-unused-vars
function FechaShiftRow({ shift: s, pessoa, updateShift, mobileOnly, ac, onUpdate, pessoas, fmtHoras, freelaRates, onOpenManageRates }) {
  const [selectedRateId, setSelectedRateId] = useState(""); // "" = Personalizado
  const [valorTipo, setValorTipo] = useState(s.valorTipo || "hora");
  const [valorUnit, setValorUnit] = useState(s.valorUnit != null ? String(s.valorUnit).replace(".", ",") : "");
  const [pix, setPix] = useState(pessoa?.pix || "");
  const fromBR = (v) => parseFloat(String(v || "").replace(",", ".")) || 0;
  const missingPix = !pessoa?.pix;

  function handleSelectRate(rateId) {
    setSelectedRateId(rateId);
    if (rateId === "__manage__") { onOpenManageRates && onOpenManageRates(); setSelectedRateId(""); return; }
    if (!rateId) return; // Personalizado — mantém valores atuais
    const rate = (freelaRates || []).find(r => r.id === rateId);
    if (rate) {
      setValorTipo(rate.valorTipo);
      setValorUnit(String(rate.valor).replace(".", ","));
    }
  }

  function handleSalvar() {
    if (!valorUnit || fromBR(valorUnit) <= 0) { alert("Preencha o valor."); return; }
    updateShift(s.id, { valorTipo, valorUnit: fromBR(valorUnit) });
    if (missingPix && pix.trim() && pessoa && onUpdate && pessoas) {
      const next = pessoas.map(p => p.id === pessoa.id ? { ...p, pix: pix.trim() } : p);
      onUpdate("pessoas", next);
    }
    onUpdate && onUpdate("_toast", `💰 Valor de ${pessoa?.name} lançado`);
  }
  return (
    <div style={{ background: "#dbeafe", border: "1px solid #3b82f655", borderRadius: 12, padding: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10, flexWrap: "wrap", gap: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: "var(--text)", fontSize: 14 }}>{pessoa?.name || "(removido)"}</div>
          <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>
            📅 {fmtDate(s.date)} · ⏱️ {s.entrada || "?"} → {s.saida || "?"}
            {s.intervalo > 0 ? ` (${s.intervalo}min int.)` : ""}
            {s.horas > 0 ? ` = ${fmtHoras ? fmtHoras(s.horas) : s.horas + "h"}` : ""}
            {s.area ? ` · ${s.area}` : ""}
          </div>
          {s.observacao && <div style={{ fontSize: 11, color: "var(--text3)", fontStyle: "italic", marginTop: 2 }}>💬 {s.observacao}</div>}
        </div>
        <span style={{ padding: "3px 8px", background: "#3b82f622", color: "#1e40af", borderRadius: 6, fontSize: 10, fontWeight: 700, whiteSpace: "nowrap" }}>💰 Aguardando DP</span>
      </div>
      {/* Dropdown de nível pré-cadastrado */}
      <div style={{ marginBottom: 8 }}>
        <label style={{ ...S.label, fontSize: 10 }}>Nível / Valor pré-cadastrado</label>
        <select value={selectedRateId} onChange={e => handleSelectRate(e.target.value)} style={{ ...S.input, fontSize: 12, padding: "7px 9px", cursor: "pointer" }}>
          <option value="">— Personalizado (digite abaixo) —</option>
          {(freelaRates || []).length > 0 && (
            <optgroup label="Cadastrados">
              {freelaRates.map(r => (
                <option key={r.id} value={r.id}>
                  {r.label} — R$ {r.valor.toFixed(2)} {r.valorTipo === "hora" ? "/hora" : "/diária"}
                </option>
              ))}
            </optgroup>
          )}
          <option value="__manage__" style={{ fontWeight: 700 }}>⚙️ Gerenciar níveis...</option>
        </select>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: mobileOnly ? "1fr 1fr" : "1.2fr 1fr", gap: 8, marginBottom: missingPix ? 8 : 0 }}>
        <div>
          <label style={{ ...S.label, fontSize: 10 }}>Tipo de valor</label>
          <select value={valorTipo} onChange={e => { setValorTipo(e.target.value); setSelectedRateId(""); }} style={{ ...S.input, fontSize: 12, padding: "7px 9px" }}>
            <option value="hora">R$ por hora</option>
            <option value="diaria">Diária</option>
          </select>
        </div>
        <div>
          <label style={{ ...S.label, fontSize: 10 }}>Valor (R$)</label>
          <input type="text" inputMode="decimal" value={valorUnit} onChange={e => { setValorUnit(e.target.value); setSelectedRateId(""); }} placeholder="0,00" style={{ ...S.input, fontSize: 12, padding: "7px 9px" }} />
        </div>
      </div>
      {missingPix && (
        <div style={{ marginBottom: 4 }}>
          <label style={{ ...S.label, fontSize: 10, color: "#b45309" }}>⚠️ PIX da pessoa (não cadastrado)</label>
          <input type="text" value={pix} onChange={e => setPix(e.target.value)} placeholder="CPF, e-mail, celular ou chave aleatória" style={{ ...S.input, fontSize: 12, padding: "7px 9px" }} />
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
        <button onClick={handleSalvar} style={{ background: ac, color: "#fff", border: "none", borderRadius: 8, padding: "7px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>💰 Lançar valor</button>
      </div>
    </div>
  );
}

// v8.6 — Modal de gerenciar níveis pré-cadastrados de freela.
// DP pode adicionar / editar / excluir níveis (label, tipo, valor).
function FreelaRatesManager({ rates, restaurantId, restaurants, onUpdate, onClose, ac }) {
  const [list, setList] = useState(() => (rates || []).map(r => ({ ...r })));
  const [newRate, setNewRate] = useState({ label: "", valorTipo: "hora", valor: "" });
  const fromBR = (v) => parseFloat(String(v || "").replace(",", ".")) || 0;

  function persist(nextList) {
    const rest = (restaurants || []).find(r => r.id === restaurantId);
    if (!rest) return;
    const next = restaurants.map(r => r.id === restaurantId ? { ...r, freelaRates: nextList } : r);
    onUpdate("restaurants", next);
  }

  function addRate() {
    if (!newRate.label.trim()) { alert("Informe o nome do nível."); return; }
    const v = fromBR(newRate.valor);
    if (v <= 0) { alert("Informe um valor válido."); return; }
    const next = [...list, { id: `rate_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`, label: newRate.label.trim(), valorTipo: newRate.valorTipo, valor: v }];
    setList(next); persist(next);
    setNewRate({ label: "", valorTipo: "hora", valor: "" });
  }
  function editRate(id, patch) {
    const next = list.map(r => r.id === id ? { ...r, ...patch } : r);
    setList(next); persist(next);
  }
  function delRate(id) {
    if (!window.confirm("Excluir este nível?")) return;
    const next = list.filter(r => r.id !== id);
    setList(next); persist(next);
  }

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 10010, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "var(--bg)", borderRadius: 14, padding: 22, maxWidth: 600, width: "100%", maxHeight: "85vh", overflowY: "auto", border: "1px solid var(--border)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div>
            <h3 style={{ color: "var(--text)", margin: 0, fontSize: 18, fontWeight: 800 }}>⚙️ Gerenciar níveis</h3>
            <p style={{ color: "var(--text3)", fontSize: 12, margin: "4px 0 0" }}>Valores pré-cadastrados que aparecem como atalho no Fechamento</p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "var(--text3)" }}>×</button>
        </div>

        {/* Lista de níveis existentes */}
        {list.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
            {list.map(r => (
              <div key={r.id} style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr auto", gap: 8, alignItems: "center", padding: "8px 10px", background: "var(--card-bg)", border: "1px solid var(--border)", borderRadius: 8 }}>
                <input value={r.label} onChange={e => editRate(r.id, { label: e.target.value })} style={{ ...S.input, fontSize: 12, padding: "6px 9px" }} placeholder="Nome (ex: Ajudante)" />
                <select value={r.valorTipo} onChange={e => editRate(r.id, { valorTipo: e.target.value })} style={{ ...S.input, fontSize: 12, padding: "6px 9px" }}>
                  <option value="hora">R$/hora</option>
                  <option value="diaria">Diária</option>
                </select>
                <input type="text" inputMode="decimal" value={String(r.valor).replace(".", ",")} onChange={e => editRate(r.id, { valor: fromBR(e.target.value) })} style={{ ...S.input, fontSize: 12, padding: "6px 9px", textAlign: "right" }} placeholder="0,00" />
                <button onClick={() => delRate(r.id)} title="Excluir" style={{ background: "transparent", color: "var(--red)", border: "1px solid var(--red)55", borderRadius: 6, padding: "5px 10px", fontSize: 11, cursor: "pointer" }}>🗑️</button>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ padding: 16, color: "var(--text3)", fontSize: 12, textAlign: "center", background: "var(--card-bg)", border: "1px dashed var(--border)", borderRadius: 8, marginBottom: 16 }}>
            Nenhum nível cadastrado ainda. Adicione abaixo.
          </div>
        )}

        {/* Form de novo nível */}
        <div style={{ background: `${ac}08`, border: `1px dashed ${ac}55`, borderRadius: 10, padding: 12 }}>
          <div style={{ fontSize: 11, color: ac, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>+ Novo nível</div>
          <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr auto", gap: 8, alignItems: "end" }}>
            <div>
              <label style={{ ...S.label, fontSize: 10 }}>Nome</label>
              <input value={newRate.label} onChange={e => setNewRate({ ...newRate, label: e.target.value })} placeholder="Ajudante, Operacional, Cozinheiro..." style={{ ...S.input, fontSize: 12, padding: "6px 9px" }} />
            </div>
            <div>
              <label style={{ ...S.label, fontSize: 10 }}>Tipo</label>
              <select value={newRate.valorTipo} onChange={e => setNewRate({ ...newRate, valorTipo: e.target.value })} style={{ ...S.input, fontSize: 12, padding: "6px 9px" }}>
                <option value="hora">R$/hora</option>
                <option value="diaria">Diária</option>
              </select>
            </div>
            <div>
              <label style={{ ...S.label, fontSize: 10 }}>Valor (R$)</label>
              <input type="text" inputMode="decimal" value={newRate.valor} onChange={e => setNewRate({ ...newRate, valor: e.target.value })} placeholder="0,00" style={{ ...S.input, fontSize: 12, padding: "6px 9px", textAlign: "right" }} />
            </div>
            <button onClick={addRate} style={{ background: ac, color: "#fff", border: "none", borderRadius: 8, padding: "7px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>+ Adicionar</button>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <button onClick={onClose} style={{ ...S.btnSecondary, fontSize: 12, padding: "7px 18px" }}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

function FreelaFechamentoTab({ restaurantId, restPessoas, shifts, lotes, shiftPrntoPronto, fmtHoras, criarLote, cancelarLote, marcarLotePago, gerarPDFLote, updateShift, onUpdate, pessoas, isOwner, mobileOnly, ac, restaurants }) {
  const restaurant = (restaurants || []).find(r => r.id === restaurantId);
  const freelaRates = restaurant?.freelaRates || [];
  const [showRatesManager, setShowRatesManager] = useState(false);
  const [selected, setSelected] = useState({}); // { shiftId: true }
  const [showLoteForm, setShowLoteForm] = useState(false);
  const [loteData, setLoteData] = useState({ numero: "", observacao: "" });
  const [payingLoteId, setPayingLoteId] = useState(null);
  const [payData, setPayData] = useState({ data: today(), forma: "PIX" });

  const abertos = shifts.filter(s => s.status === "aberto");
  // v8.5.1: separa em "aguardando Líder" (sem saída ainda) vs "aguardando DP" (saída ok mas sem valor)
  const aguardandoLider = abertos.filter(s => !s.saida);
  const incompletos = abertos.filter(s => s.saida && !shiftPrntoPronto(s).ok); // aguardando DP precificar
  const prontos = abertos.filter(s => shiftPrntoPronto(s).ok);
  const pendentesLotes = lotes.filter(l => l.status === "pendente").sort((a,b) => (b.criadoEm||"").localeCompare(a.criadoEm||""));

  const selIds = Object.keys(selected).filter(id => selected[id]);
  const selShifts = prontos.filter(s => selected[s.id]);
  const selTotal = selShifts.reduce((sum, s) => sum + (s.totalCalc || 0), 0);
  const selPessoas = new Set(selShifts.map(s => s.pessoaId));

  function toggleAllProntos() {
    if (selIds.length === prontos.length && prontos.length > 0) setSelected({});
    else {
      const all = {}; prontos.forEach(s => { all[s.id] = true; });
      setSelected(all);
    }
  }

  async function handleCriarLote() {
    if (selShifts.length === 0) { alert("Selecione pelo menos 1 shift."); return; }
    const newId = await criarLote({ shiftIds: selIds, numero: loteData.numero, observacao: loteData.observacao });
    if (newId) {
      setSelected({}); setLoteData({ numero: "", observacao: "" }); setShowLoteForm(false);
    }
  }

  // agrupa prontos por pessoa pra UX melhor
  const prontosByPessoa = {};
  prontos.forEach(s => {
    if (!prontosByPessoa[s.pessoaId]) prontosByPessoa[s.pessoaId] = [];
    prontosByPessoa[s.pessoaId].push(s);
  });

  return (
    <div style={{paddingBottom:48}}>
      {/* Resumo + ações */}
      <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:14,alignItems:"center"}}>
        <div style={{padding:"8px 12px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:8,fontSize:12}}>
          <span style={{color:"var(--text3)"}}>Prontos pra fechar:</span>{" "}
          <strong style={{color:ac,fontFamily:"'DM Mono',monospace"}}>{prontos.length}</strong>
        </div>
        {incompletos.length > 0 && (
          <div style={{padding:"8px 12px",background:"#dbeafe",border:"1px solid #3b82f6",borderRadius:8,fontSize:12,color:"#1e40af"}}>
            💰 <strong>{incompletos.length}</strong> aguardando precificação (DP)
          </div>
        )}
        {aguardandoLider.length > 0 && (
          <div style={{padding:"8px 12px",background:"#fef3c7",border:"1px solid #fbbf24",borderRadius:8,fontSize:12,color:"#92400e"}}>
            ⏳ <strong>{aguardandoLider.length}</strong> aguardando o Líder fechar o turno (preencher saída/intervalo no Lançamento)
          </div>
        )}
        {selShifts.length > 0 && (
          <button onClick={()=>setShowLoteForm(true)}
            style={{background:ac,color:"#fff",border:"none",borderRadius:8,padding:"9px 18px",fontSize:13,fontWeight:700,cursor:"pointer",marginLeft:"auto"}}>
            📑 Gerar lote ({selShifts.length} · R$ {selTotal.toFixed(2)})
          </button>
        )}
        <button onClick={() => setShowRatesManager(true)} title="Cadastrar/editar níveis pré-cadastrados de valor"
          style={{ ...S.btnSecondary, fontSize: 11, padding: "7px 12px", marginLeft: selShifts.length > 0 ? 0 : "auto" }}>
          ⚙️ Níveis ({freelaRates.length})
        </button>
      </div>

      {/* Form de criar lote */}
      {showLoteForm && (
        <div style={{background:`${ac}08`,border:`1px solid ${ac}44`,borderRadius:10,padding:14,marginBottom:14}}>
          <div style={{fontSize:11,color:ac,fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,marginBottom:10}}>Revisar lote</div>
          <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"1fr 2fr",gap:8,marginBottom:10}}>
            <div>
              <label style={{...S.label,fontSize:11}}>Nome do lote</label>
              <input value={loteData.numero} onChange={e=>setLoteData({...loteData,numero:e.target.value})} placeholder={`Lote ${fmtDate(today())}`} style={S.input}/>
            </div>
            <div>
              <label style={{...S.label,fontSize:11}}>Observação (opcional)</label>
              <input value={loteData.observacao} onChange={e=>setLoteData({...loteData,observacao:e.target.value})} placeholder="ex: pagamento da semana 2" style={S.input}/>
            </div>
          </div>
          <div style={{padding:"10px 12px",background:"var(--bg2)",borderRadius:8,marginBottom:10,fontSize:12}}>
            📊 <strong>{selShifts.length}</strong> shift(s) · <strong>{selPessoas.size}</strong> pessoa(s) · Total: <strong style={{color:ac,fontFamily:"'DM Mono',monospace"}}>R$ {selTotal.toFixed(2)}</strong>
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <button onClick={()=>setShowLoteForm(false)} style={{...S.btnSecondary,fontSize:12,padding:"7px 14px"}}>Cancelar</button>
            <button onClick={handleCriarLote} style={{background:ac,color:"#fff",border:"none",borderRadius:8,padding:"7px 18px",fontSize:13,fontWeight:700,cursor:"pointer"}}>📑 Criar lote</button>
          </div>
        </div>
      )}

      {/* v8.7.1 — Aguardando precificação agrupado POR PESSOA (1 card por pessoa, N turnos dentro) */}
      {incompletos.length > 0 && (() => {
        // Agrupa incompletos por pessoaId
        const byPessoa = {};
        incompletos.forEach(s => {
          if (!byPessoa[s.pessoaId]) byPessoa[s.pessoaId] = [];
          byPessoa[s.pessoaId].push(s);
        });
        const pessoaCount = Object.keys(byPessoa).length;
        return (
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, color: "var(--text3)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>
              💰 Aguardando precificação ({incompletos.length} turno{incompletos.length !== 1 ? "s" : ""} · {pessoaCount} pessoa{pessoaCount !== 1 ? "s" : ""})
            </div>
            <div style={{ display: "grid", gridTemplateColumns: mobileOnly ? "1fr" : "repeat(auto-fill, minmax(360px, 1fr))", gap: 10 }}>
              {Object.entries(byPessoa).map(([pessoaId, pessoaShifts]) => {
                const pessoa = restPessoas.find(p => p.id === pessoaId);
                return (
                  <FechaPessoaCard
                    key={pessoaId}
                    pessoa={pessoa}
                    shifts={pessoaShifts}
                    allShifts={shifts}
                    updateShift={updateShift}
                    onUpdate={onUpdate}
                    pessoas={pessoas}
                    fmtHoras={fmtHoras}
                    freelaRates={freelaRates}
                    onOpenManageRates={() => setShowRatesManager(true)}
                    mobileOnly={mobileOnly}
                    ac={ac}
                  />
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* v8.7 — Lotes pendentes em grid de cards roxos */}
      {pendentesLotes.length > 0 && (
        <div style={{marginBottom:18}}>
          <div style={{fontSize:11,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,marginBottom:8}}>📑 Lotes pendentes de pagamento ({pendentesLotes.length})</div>
          <div style={{ display: "grid", gridTemplateColumns: mobileOnly ? "1fr" : "repeat(auto-fill, minmax(320px, 1fr))", gap: 10 }}>
            {pendentesLotes.map(l => (
              <div key={l.id} style={{ background: `${ac}10`, border: `2px solid ${ac}66`, borderRadius: 12, padding: 12 }}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,marginBottom:8}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:14,color:"var(--text)"}}>{l.numero}</div>
                    <div style={{fontSize:11,color:"var(--text3)",marginTop:3}}>
                      {l.qtdShifts} shift(s) · {l.qtdPessoas} pessoa(s)
                    </div>
                    <div style={{fontSize:10,color:"var(--text3)",marginTop:2}}>
                      criado por {l.criadoPor} em {fmtDate(l.criadoEm.slice(0,10))}
                    </div>
                    {l.observacao && <div style={{fontSize:11,color:"var(--text2)",marginTop:6,fontStyle:"italic",padding:"4px 8px",background:"rgba(255,255,255,0.5)",borderRadius:6}}>“{l.observacao}”</div>}
                  </div>
                  <span style={{ padding: "3px 8px", background: `${ac}33`, color: ac, borderRadius: 6, fontSize: 10, fontWeight: 700, whiteSpace: "nowrap" }}>📑 Pendente</span>
                </div>
                <div style={{ paddingTop: 8, borderTop: `1px solid ${ac}33`, marginBottom: 10, textAlign: "right" }}>
                  <div style={{fontFamily:"'DM Mono',monospace",fontWeight:800,fontSize:20,color:ac}}>R$ {l.totalGeral.toFixed(2)}</div>
                </div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  <button onClick={()=>gerarPDFLote(l.id)} style={{background:"transparent",color:ac,border:`1px solid ${ac}`,borderRadius:6,padding:"5px 12px",fontSize:11,fontWeight:600,cursor:"pointer"}}>📄 PDF</button>
                  <button onClick={()=>{setPayingLoteId(l.id); setPayData({data:today(),forma:"PIX"});}}
                    style={{background:"#10b981",color:"#fff",border:"none",borderRadius:6,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer"}}>💸 Marcar pago</button>
                  <button onClick={()=>cancelarLote(l.id)} style={{background:"transparent",color:"var(--red)",border:"1px solid var(--red)",borderRadius:6,padding:"5px 12px",fontSize:11,fontWeight:600,cursor:"pointer"}}>✕ Cancelar</button>
                </div>

                {/* Modal inline pra marcar como pago */}
                {payingLoteId === l.id && (
                  <div style={{marginTop:10,padding:10,background:"#10b98115",border:"1px solid #10b98155",borderRadius:8}}>
                    <div style={{fontSize:11,color:"#15803d",fontWeight:700,marginBottom:8}}>Marcar lote como pago</div>
                    <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"1fr 1fr auto auto",gap:6,alignItems:"end"}}>
                      <div>
                        <label style={{...S.label,fontSize:10}}>Data do pagamento</label>
                        <input type="date" value={payData.data} onChange={e=>setPayData({...payData,data:e.target.value})} style={{...S.input,fontSize:12,padding:"6px 8px"}}/>
                      </div>
                      <div>
                        <label style={{...S.label,fontSize:10}}>Forma</label>
                        <select value={payData.forma} onChange={e=>setPayData({...payData,forma:e.target.value})} style={{...S.input,fontSize:12,padding:"6px 8px",cursor:"pointer"}}>
                          <option>PIX</option><option>Dinheiro</option><option>Transferência</option><option>Outro</option>
                        </select>
                      </div>
                      <button onClick={()=>setPayingLoteId(null)} style={{...S.btnSecondary,fontSize:11,padding:"6px 12px"}}>Cancelar</button>
                      <button onClick={async()=>{await marcarLotePago(l.id, payData.data, payData.forma); setPayingLoteId(null);}}
                        style={{background:"#10b981",color:"#fff",border:"none",borderRadius:6,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"pointer"}}>💸 Confirmar</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lista de prontos pra fechar — agrupados por pessoa */}
      <div style={{fontSize:11,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span>Shifts prontos pra fechar</span>
        {prontos.length > 0 && (
          <button onClick={toggleAllProntos} style={{background:"none",border:`1px solid ${ac}`,color:ac,borderRadius:6,padding:"4px 10px",fontSize:11,fontWeight:600,cursor:"pointer"}}>
            {selIds.length === prontos.length ? "Desmarcar todos" : "Marcar todos"}
          </button>
        )}
      </div>
      {/* v8.7.2 — Prontos pra fechar agrupado POR PESSOA (1 card por pessoa, N turnos dentro) */}
      {prontos.length === 0 && incompletos.length === 0 && aguardandoLider.length === 0 && pendentesLotes.length === 0 ? (
        <div style={{padding:"30px 20px",textAlign:"center",color:"var(--text3)",fontSize:13,background:"var(--card-bg)",border:"1px dashed var(--border)",borderRadius:10}}>
          <div style={{fontSize:32,marginBottom:8}}>📭</div>
          Nenhum shift no momento. Os shifts abertos pelos Líderes em <strong>Lançamento</strong> aparecerão aqui pra precificar e fechar.
        </div>
      ) : prontos.length > 0 && (() => {
        const byPessoa = {};
        prontos.forEach(s => {
          if (!byPessoa[s.pessoaId]) byPessoa[s.pessoaId] = [];
          byPessoa[s.pessoaId].push(s);
        });
        const pessoaCount = Object.keys(byPessoa).length;
        return (
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, color: "var(--text3)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>
              ✅ Prontos pra entrar em lote ({prontos.length} turno{prontos.length !== 1 ? "s" : ""} · {pessoaCount} pessoa{pessoaCount !== 1 ? "s" : ""})
            </div>
            <div style={{ display: "grid", gridTemplateColumns: mobileOnly ? "1fr" : "repeat(auto-fill, minmax(340px, 1fr))", gap: 10 }}>
              {Object.entries(byPessoa).map(([pessoaId, sList]) => {
                const p = restPessoas.find(x => x.id === pessoaId) || {};
                const sortedShifts = sList.sort((a,b) => (a.date||"").localeCompare(b.date||""));
                const subTotal = sortedShifts.reduce((sum, s) => sum + (s.totalCalc || 0), 0);
                const allSel = sortedShifts.every(s => selected[s.id]);
                const someSel = sortedShifts.some(s => selected[s.id]);
                const headerSel = allSel; // borda forte só quando TUDO está marcado
                function toggleAll() {
                  const next = { ...selected };
                  sortedShifts.forEach(s => { next[s.id] = !allSel; });
                  setSelected(next);
                }
                return (
                  <div key={pessoaId} onClick={toggleAll}
                    style={{
                      background: headerSel ? "#10b98122" : "#10b98112",
                      border: `2px solid ${headerSel ? "#16a34a" : (someSel ? "#16a34a99" : "#10b98155")}`,
                      borderRadius: 12, padding: 12, cursor: "pointer",
                      transition: "all .12s",
                    }}>
                    {/* Header: pessoa + checkbox + status */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10, gap: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                        <input type="checkbox" checked={allSel} ref={el => { if (el) el.indeterminate = someSel && !allSel; }} onChange={toggleAll} onClick={e=>e.stopPropagation()} style={{ cursor: "pointer", width: 16, height: 16, accentColor: "#16a34a" }}/>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, color: "var(--text)", fontSize: 14 }}>{p.name || "(removido)"}</div>
                          <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>{sortedShifts.length} turno{sortedShifts.length !== 1 ? "s" : ""} pronto{sortedShifts.length !== 1 ? "s" : ""}</div>
                        </div>
                      </div>
                      <span style={{ padding: "3px 8px", background: "#16a34a22", color: "#15803d", borderRadius: 6, fontSize: 10, fontWeight: 700, whiteSpace: "nowrap" }}>✅ Pronto</span>
                    </div>

                    {/* Lista de turnos com checkbox individual */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10, padding: "6px 8px", background: "rgba(255,255,255,0.5)", borderRadius: 8 }}>
                      {sortedShifts.map(s => {
                        const isSel = !!selected[s.id];
                        return (
                          <div key={s.id}
                            onClick={(e) => { e.stopPropagation(); setSelected({ ...selected, [s.id]: !isSel }); }}
                            style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, padding: "4px 0", borderBottom: "1px dashed rgba(0,0,0,0.06)", cursor: "pointer" }}>
                            <input type="checkbox" checked={isSel} onChange={()=>setSelected({...selected,[s.id]:!isSel})} onClick={e=>e.stopPropagation()} style={{ cursor: "pointer", width: 13, height: 13, accentColor: "#16a34a", flexShrink: 0 }}/>
                            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1, overflow: "hidden" }}>
                              <div style={{ color: "var(--text2)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                📅 {fmtDate(s.date)}{s.area ? ` · ${s.area}` : ""}
                              </div>
                              <div style={{ color: "var(--text3)", fontFamily: "'DM Mono',monospace", fontSize: 10, whiteSpace: "nowrap" }}>
                                {s.entrada}→{s.saida}{s.intervalo > 0 ? ` (${s.intervalo}m)` : ""} · {fmtHoras(s.horas)}
                              </div>
                            </div>
                            <span style={{ color: "var(--text)", fontFamily: "'DM Mono',monospace", fontWeight: 700, minWidth: 70, textAlign: "right", flexShrink: 0 }}>
                              R$ {(s.totalCalc||0).toFixed(2)}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {/* Footer: valor unitário (uniforme se todos iguais) + total */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 8, borderTop: "1px solid #16a34a33" }}>
                      <div style={{ fontSize: 11, color: "var(--text3)" }}>
                        {(() => {
                          const allSameRate = sortedShifts.every(s => s.valorTipo === sortedShifts[0].valorTipo && s.valorUnit === sortedShifts[0].valorUnit);
                          const r = sortedShifts[0];
                          return allSameRate
                            ? <>R$ {(r.valorUnit||0).toFixed(2)} {r.valorTipo === "hora" ? "/hora" : "/diária"}</>
                            : <>valores variados</>;
                        })()}
                      </div>
                      <span style={{ fontFamily: "'DM Mono',monospace", fontWeight: 800, fontSize: 18, color: "#15803d" }}>R$ {subTotal.toFixed(2)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* v8.6 — Modal de gerenciar níveis pré-cadastrados */}
      {showRatesManager && (
        <FreelaRatesManager
          rates={freelaRates}
          restaurantId={restaurantId}
          restaurants={restaurants}
          onUpdate={onUpdate}
          onClose={() => setShowRatesManager(false)}
          ac={ac}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ──  FREELAS — sub-componente Histórico (lotes pagos + dashboard) ─
// ═══════════════════════════════════════════════════════════════
function FreelaHistoricoTab({ restaurantId, restPessoas, shifts, lotes, fmtHoras, gerarPDFLote, cancelarLote, reabrirLotePago, excluirShiftHistorico, isOwner, mobileOnly, ac }) {
  const [filterMes, setFilterMes] = useState(""); // "YYYY-MM" ou ""
  const [filterStatus, setFilterStatus] = useState("todos"); // todos | pago | pendente
  const [filterPessoa, setFilterPessoa] = useState("");

  const filtered = lotes.filter(l => {
    if (filterStatus !== "todos" && l.status !== filterStatus) return false;
    if (filterMes) {
      const lMes = (l.pagoEm || l.criadoEm || "").slice(0,7);
      if (lMes !== filterMes) return false;
    }
    if (filterPessoa) {
      if (!l.pessoasResumo.find(pr => pr.pessoaId === filterPessoa)) return false;
    }
    return true;
  }).sort((a,b) => (b.pagoEm || b.criadoEm || "").localeCompare(a.pagoEm || a.criadoEm || ""));

  // KPIs do mês selecionado (ou todos)
  const totalPago = filtered.filter(l=>l.status==="pago").reduce((sum,l)=>sum+(l.totalGeral||0),0);
  const totalPendente = filtered.filter(l=>l.status==="pendente").reduce((sum,l)=>sum+(l.totalGeral||0),0);
  const totalShifts = filtered.reduce((sum,l)=>sum+(l.qtdShifts||0),0);

  // Top 5 freelas (por valor)
  const byPessoa = {};
  filtered.forEach(l => {
    l.pessoasResumo.forEach(pr => {
      if (!byPessoa[pr.pessoaId]) byPessoa[pr.pessoaId] = { pessoaId: pr.pessoaId, total: 0, qtdShifts: 0 };
      byPessoa[pr.pessoaId].total += pr.totalValor;
      byPessoa[pr.pessoaId].qtdShifts += pr.qtdShifts;
    });
  });
  const topFreelas = Object.values(byPessoa).sort((a,b)=>b.total-a.total).slice(0,5);

  // Custo por área
  const byArea = {};
  filtered.forEach(l => {
    const lShifts = shifts.filter(s => l.shiftIds.includes(s.id));
    lShifts.forEach(s => {
      const a = s.area || "(sem área)";
      if (!byArea[a]) byArea[a] = { area: a, total: 0, qtdShifts: 0 };
      byArea[a].total += s.totalCalc || 0;
      byArea[a].qtdShifts += 1;
    });
  });
  const areasArr = Object.values(byArea).sort((a,b)=>b.total-a.total);

  // Meses disponíveis pra filtro
  const mesesSet = new Set();
  lotes.forEach(l => { mesesSet.add((l.pagoEm || l.criadoEm || "").slice(0,7)); });
  const meses = [...mesesSet].filter(Boolean).sort().reverse();

  function pessoaName(pessoaId) {
    const p = restPessoas.find(x => x.id === pessoaId);
    return p?.name || "(removido)";
  }

  return (
    <div style={{paddingBottom:48}}>
      {/* Filtros */}
      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:14}}>
        <select value={filterMes} onChange={e=>setFilterMes(e.target.value)}
          style={{...S.input,maxWidth:160,fontSize:12,padding:"7px 10px",cursor:"pointer"}}>
          <option value="">📅 Todos meses</option>
          {meses.map(m => {
            const [y,mo] = m.split("-");
            const nm = new Date(parseInt(y), parseInt(mo)-1, 1).toLocaleDateString("pt-BR", { month:"long", year:"numeric" });
            return <option key={m} value={m}>{nm}</option>;
          })}
        </select>
        <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}
          style={{...S.input,maxWidth:160,fontSize:12,padding:"7px 10px",cursor:"pointer"}}>
          <option value="todos">📋 Todos status</option>
          <option value="pago">💸 Pagos</option>
          <option value="pendente">⏳ Pendentes</option>
        </select>
        <select value={filterPessoa} onChange={e=>setFilterPessoa(e.target.value)}
          style={{...S.input,maxWidth:200,fontSize:12,padding:"7px 10px",cursor:"pointer"}}>
          <option value="">🎒 Todas pessoas</option>
          {restPessoas.filter(p=>p.isFreela).sort((a,b)=>a.name.localeCompare(b.name)).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {/* KPIs */}
      <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr 1fr":"repeat(4, 1fr)",gap:8,marginBottom:14}}>
        <div style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,padding:12}}>
          <div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:0.3,marginBottom:4}}>Pago</div>
          <div style={{fontFamily:"'DM Mono',monospace",fontWeight:800,fontSize:18,color:"#15803d"}}>R$ {totalPago.toFixed(2)}</div>
        </div>
        <div style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,padding:12}}>
          <div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:0.3,marginBottom:4}}>Pendente</div>
          <div style={{fontFamily:"'DM Mono',monospace",fontWeight:800,fontSize:18,color:"#92400e"}}>R$ {totalPendente.toFixed(2)}</div>
        </div>
        <div style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,padding:12}}>
          <div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:0.3,marginBottom:4}}>Lotes</div>
          <div style={{fontFamily:"'DM Mono',monospace",fontWeight:800,fontSize:18,color:"var(--text)"}}>{filtered.length}</div>
        </div>
        <div style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,padding:12}}>
          <div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:0.3,marginBottom:4}}>Shifts</div>
          <div style={{fontFamily:"'DM Mono',monospace",fontWeight:800,fontSize:18,color:"var(--text)"}}>{totalShifts}</div>
        </div>
      </div>

      {/* Top freelas e custo por área */}
      {filtered.length > 0 && (
        <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"1fr 1fr",gap:10,marginBottom:14}}>
          <div style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,padding:12}}>
            <div style={{fontSize:11,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.3,marginBottom:8}}>🏆 Top 5 freelas</div>
            {topFreelas.length === 0 ? <div style={{fontSize:12,color:"var(--text3)"}}>—</div> : (
              <div style={{display:"flex",flexDirection:"column",gap:5}}>
                {topFreelas.map((p,i) => (
                  <div key={p.pessoaId} style={{display:"flex",alignItems:"center",gap:6,fontSize:12}}>
                    <span style={{color:"var(--text3)",width:18}}>{i+1}.</span>
                    <span style={{flex:1,color:"var(--text)",fontWeight:600}}>{pessoaName(p.pessoaId)}</span>
                    <span style={{color:"var(--text3)",fontSize:10}}>{p.qtdShifts} shifts</span>
                    <span style={{fontFamily:"'DM Mono',monospace",fontWeight:700,color:ac,minWidth:80,textAlign:"right"}}>R$ {p.total.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,padding:12}}>
            <div style={{fontSize:11,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.3,marginBottom:8}}>🏷️ Custo por área</div>
            {areasArr.length === 0 ? <div style={{fontSize:12,color:"var(--text3)"}}>—</div> : (
              <div style={{display:"flex",flexDirection:"column",gap:5}}>
                {areasArr.map(a => (
                  <div key={a.area} style={{display:"flex",alignItems:"center",gap:6,fontSize:12}}>
                    <span style={{flex:1,color:"var(--text)",fontWeight:600}}>{a.area}</span>
                    <span style={{color:"var(--text3)",fontSize:10}}>{a.qtdShifts} shifts</span>
                    <span style={{fontFamily:"'DM Mono',monospace",fontWeight:700,color:ac,minWidth:80,textAlign:"right"}}>R$ {a.total.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Lista de lotes */}
      <div style={{fontSize:11,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.3,marginBottom:8}}>Lotes</div>
      {filtered.length === 0 ? (
        <div style={{padding:"30px 20px",textAlign:"center",color:"var(--text3)",fontSize:13,background:"var(--card-bg)",border:"1px dashed var(--border)",borderRadius:10}}>
          <div style={{fontSize:32,marginBottom:8}}>🗂️</div>
          Nenhum lote {filterMes||filterStatus!=="todos"||filterPessoa ? "com esses filtros" : "ainda"}
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {filtered.map(l => (
            <div key={l.id} style={{background:"var(--card-bg)",border:`1px solid ${l.status==="pago"?"#10b98155":l.status==="pendente"?ac+"55":"var(--border)"}`,borderRadius:10,padding:12}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,flexWrap:"wrap"}}>
                <div style={{flex:1,minWidth:200}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                    <span style={{fontWeight:700,fontSize:14,color:"var(--text)"}}>{l.numero}</span>
                    <span style={{padding:"2px 7px",borderRadius:6,fontSize:10,fontWeight:700,background:l.status==="pago"?"#10b98122":l.status==="pendente"?`${ac}22`:"var(--bg2)",color:l.status==="pago"?"#15803d":l.status==="pendente"?ac:"var(--text3)"}}>
                      {l.status==="pago"?"💸 PAGO":l.status==="pendente"?"⏳ PENDENTE":l.status.toUpperCase()}
                    </span>
                  </div>
                  <div style={{fontSize:11,color:"var(--text3)"}}>
                    {l.qtdShifts} shift(s) · {l.qtdPessoas} pessoa(s) · criado em {fmtDate((l.criadoEm||"").slice(0,10))}
                    {l.status==="pago" && ` · pago ${fmtDate(l.pagoEm)}${l.formaPagamento?` via ${l.formaPagamento}`:""}`}
                  </div>
                  {l.observacao && <div style={{fontSize:11,color:"var(--text2)",marginTop:3,fontStyle:"italic"}}>“{l.observacao}”</div>}
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontFamily:"'DM Mono',monospace",fontWeight:800,fontSize:18,color:l.status==="pago"?"#15803d":ac}}>R$ {l.totalGeral.toFixed(2)}</div>
                </div>
              </div>
              <div style={{display:"flex",gap:6,marginTop:10,flexWrap:"wrap"}}>
                <button onClick={()=>gerarPDFLote(l.id)} style={{background:"transparent",color:ac,border:`1px solid ${ac}`,borderRadius:6,padding:"5px 12px",fontSize:11,fontWeight:600,cursor:"pointer"}}>📄 PDF</button>
                {l.status === "pendente" && (
                  <button onClick={()=>cancelarLote(l.id)} style={{background:"transparent",color:"var(--red)",border:"1px solid var(--red)",borderRadius:6,padding:"5px 12px",fontSize:11,fontWeight:600,cursor:"pointer"}}>✕ Cancelar</button>
                )}
                {/* v8.5: Owner-only — reabrir lote pago */}
                {isOwner && l.status === "pago" && reabrirLotePago && (
                  <button onClick={()=>reabrirLotePago(l.id)} style={{background:"transparent",color:"#b45309",border:"1px solid #b45309",borderRadius:6,padding:"5px 12px",fontSize:11,fontWeight:600,cursor:"pointer"}} title="Reabre o lote: volta pra pendente, shifts pra Fechamento">↺ Reabrir lote</button>
                )}
                {/* Detalhe expandível por pessoa */}
                <details style={{marginLeft:"auto"}}>
                  <summary style={{cursor:"pointer",fontSize:11,color:"var(--text3)"}}>Ver detalhes{isOwner ? " (Owner pode excluir shifts)" : ""}</summary>
                  <div style={{marginTop:6,paddingTop:6,borderTop:"1px solid var(--border)"}}>
                    {l.pessoasResumo.map(pr => (
                      <div key={pr.pessoaId} style={{display:"flex",justifyContent:"space-between",fontSize:11,padding:"3px 0"}}>
                        <span style={{color:"var(--text2)"}}>{pessoaName(pr.pessoaId)}</span>
                        <span style={{color:"var(--text3)"}}>{pr.qtdShifts} shifts · {fmtHoras(pr.totalHoras)}</span>
                        <span style={{fontFamily:"'DM Mono',monospace",fontWeight:700,color:"var(--text)",minWidth:80,textAlign:"right"}}>R$ {pr.totalValor.toFixed(2)}</span>
                      </div>
                    ))}
                    {/* v8.5: Owner-only — lista de shifts individuais com botão excluir */}
                    {isOwner && excluirShiftHistorico && (
                      <div style={{marginTop:10,paddingTop:8,borderTop:"1px dashed var(--border)"}}>
                        <div style={{fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,marginBottom:6}}>Shifts individuais (excluir):</div>
                        {shifts.filter(s => l.shiftIds?.includes(s.id)).map(s => (
                          <div key={s.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",fontSize:11,padding:"3px 0",gap:6}}>
                            <span style={{color:"var(--text3)"}}>{pessoaName(s.pessoaId)} · {fmtDate(s.date)} · {s.entrada}-{s.saida || "?"} · R$ {(s.totalCalc||0).toFixed(2)}</span>
                            <button onClick={()=>excluirShiftHistorico(s.id)} style={{background:"transparent",color:"var(--red)",border:"1px solid var(--red)",borderRadius:4,padding:"1px 8px",fontSize:10,cursor:"pointer"}}>🗑️</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </details>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// v8.20.0: editor reusável de permissões da pessoa (usado no cadastro inline e no resumo).
// Recebe o objeto perms `{ operational, admin, special: { areas, ... } }` e devolve via onChange.
// Render: 1 acordeão por grupo de PERM_GROUPS (sem legacy) + bloco final de áreas (default vazio).
function PessoaPermsEditor({ perms, onChange, mobileOnly, compact = false }) {
  const safe = perms || { operational: {}, admin: {}, special: { areas: [] } };
  const sp = safe.special || {};
  const areas = Array.isArray(sp.areas) ? sp.areas : [];
  const [openGroups, setOpenGroups] = useState({});
  function isOn(key) {
    const [g, n] = key.split(".");
    return safe[g]?.[n] === true;
  }
  function setKey(key, val) {
    const [g, n] = key.split(".");
    const next = {
      ...safe,
      [g]: { ...(safe[g] || {}), [n]: val },
    };
    onChange(next);
  }
  function setGroupAll(group, val) {
    const next = { ...safe };
    group.perms.filter(p => !p.legacy).forEach(p => {
      const [g, n] = p.key.split(".");
      next[g] = { ...(next[g] || {}), [n]: val };
    });
    onChange(next);
  }
  function toggleArea(area) {
    const has = areas.includes(area);
    const newAreas = has ? areas.filter(a => a !== area) : [...areas, area];
    onChange({ ...safe, special: { ...sp, areas: newAreas } });
  }
  function setLider(val) {
    onChange({ ...safe, special: { ...sp, isLider: val, areas: val ? areas : [] } });
  }
  return (
    <div style={{display:"flex",flexDirection:"column",gap:8}}>
      {PERM_GROUPS.filter(g => !g.legacy).map(group => {
        const visiblePerms = group.perms.filter(p => !p.legacy);
        const onCount = visiblePerms.filter(p => isOn(p.key)).length;
        const total = visiblePerms.length;
        const allOn = onCount === total && total > 0;
        const open = openGroups[group.id] ?? false;
        return (
          <div key={group.id} style={{border:`1px solid ${onCount>0?group.color+"66":"var(--border)"}`,borderRadius:10,background:onCount>0?group.bg:"var(--card-bg)",overflow:"hidden"}}>
            <div onClick={()=>setOpenGroups(s=>({...s,[group.id]:!open}))}
              style={{padding:"10px 12px",display:"flex",alignItems:"center",gap:10,cursor:"pointer",borderBottom:open?`1px solid ${group.color}33`:"none"}}>
              <span style={{fontSize:13,fontWeight:700,color:"var(--text)",flex:1}}>{group.label}</span>
              <span style={{fontSize:11,fontWeight:700,color:onCount>0?group.color:"var(--text3)",fontFamily:"'DM Mono',monospace"}}>{onCount}/{total}</span>
              <button type="button" onClick={e=>{e.stopPropagation();setGroupAll(group, !allOn);}}
                style={{padding:"4px 10px",borderRadius:6,fontSize:10,fontWeight:700,letterSpacing:0.4,textTransform:"uppercase",cursor:"pointer",border:`1px solid ${group.color}66`,background:allOn?group.color:"transparent",color:allOn?"#fff":group.color}}>
                {allOn ? "Tudo ✓" : "Tudo"}
              </button>
              <span style={{color:"var(--text3)",fontSize:13}}>{open ? "▼" : "▶"}</span>
            </div>
            {open && (
              <div style={{padding:"6px 12px 10px",display:"grid",gridTemplateColumns:mobileOnly||compact?"1fr":"1fr 1fr",gap:4}}>
                {visiblePerms.map(p => {
                  const on = isOn(p.key);
                  return (
                    <label key={p.key} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 8px",cursor:"pointer",borderRadius:6,background:on?"#ffffff55":"transparent"}}>
                      <input type="checkbox" checked={on} onChange={e=>setKey(p.key, e.target.checked)} style={{accentColor:group.color,cursor:"pointer",width:16,height:16,flexShrink:0}}/>
                      <span style={{fontSize:11,marginRight:2}}>{p.icon}</span>
                      <span style={{fontSize:12,color:on?"var(--text)":"var(--text2)",fontWeight:on?600:400,lineHeight:1.3}}>{p.label}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      {/* Bloco Líder + Áreas — default vazio por segurança */}
      <div style={{border:`1px solid ${sp.isLider?"#f59e0b66":"var(--border)"}`,borderRadius:10,background:sp.isLider?"#fef3c7":"var(--card-bg)",padding:"10px 12px"}}>
        <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",marginBottom:sp.isLider?10:0}}>
          <input type="checkbox" checked={!!sp.isLider} onChange={e=>setLider(e.target.checked)} style={{accentColor:"#f59e0b",cursor:"pointer",width:18,height:18}}/>
          <span style={{fontSize:13,color:"var(--text)",fontWeight:700}}>⭐ Líder</span>
          <span style={{fontSize:11,color:"var(--text3)",fontWeight:400}}>(lidera uma ou mais áreas — pode ver dados da equipe da área)</span>
        </label>
        {sp.isLider && (
          <div>
            <div style={{fontSize:11,color:"var(--text3)",marginBottom:6}}>Áreas que lidera (default: nenhuma — selecione abaixo):</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {AREAS.map(a => {
                const on = areas.includes(a);
                return (
                  <button key={a} type="button" onClick={()=>toggleArea(a)}
                    style={{padding:"5px 12px",borderRadius:18,fontSize:12,fontWeight:on?700:500,cursor:"pointer",border:`1px solid ${on?"#f59e0b":"var(--border)"}`,background:on?"#f59e0b":"transparent",color:on?"#fff":"var(--text2)"}}>
                    {on ? "✓ " : ""}{a}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PessoasAdmin({ restaurantId, pessoas, roles, owners, employees, managers, freelaShifts = [], restCode, onUpdate, mobileOnly, realIsOwner, onStartImpersonate, editTrigger }) {
  // v8.27.0: contador de no-shows por pessoa neste restaurante
  const noShowsByPessoa = useMemo(() => {
    const map = {};
    (freelaShifts || []).forEach(s => {
      if (s.restaurantId !== restaurantId) return;
      if (s.status !== "nao_compareceu") return;
      map[s.pessoaId] = (map[s.pessoaId] || 0) + 1;
    });
    return map;
  }, [freelaShifts, restaurantId]);
  // Filtra owners do sistema — eles têm acesso implícito, não constam como pessoa do restaurante
  const ownerCpfSet = new Set((owners || []).map(o => (o.cpf || "").replace(/\D/g, "")).filter(Boolean));
  const restPessoas = (pessoas || []).filter(p => {
    if (!(p.restaurantIds || []).includes(restaurantId)) return false;
    const cpfD = (p.cpf || "").replace(/\D/g, "");
    if (cpfD && ownerCpfSet.has(cpfD)) return false; // esconde pessoas que são owners
    return true;
  });
  const [filter, setFilter] = useState("");
  const [vincFilter, setVincFilter] = useState("todos"); // todos | equipe | freela | sem
  // Form expandido: globais (nome/CPF/email/wa/emergência/pix) + por-rest (cargo/admissão se equipe; isFreela)
  const [form, setForm] = useState({
    name: "", cpf: "", email: "", whatsapp: "", pin: "",
    emergencyName: "", emergencyPhone: "", pix: "",
    isTeam: false, roleId: "", admission: "",
    isFreela: false,
  });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "", cpf: "", email: "", whatsapp: "", pin: "",
    emergencyName: "", emergencyPhone: "", pix: "",
    isTeam: false, roleId: "", admission: "",
    isFreela: false,
  });
  const [vincularOpen, setVincularOpen] = useState(false);
  const [vincularFilter, setVincularFilter] = useState("");
  // Bulk add via IA — só pra Master (realIsOwner)
  const [bulkAiOpen, setBulkAiOpen] = useState(false);
  const [bulkAiText, setBulkAiText] = useState("");
  const [bulkAiLoading, setBulkAiLoading] = useState(false);
  const [bulkAiError, setBulkAiError] = useState("");
  const [bulkAiResult, setBulkAiResult] = useState(null); // { pessoas: [{name, cpf, email, whatsapp, role, notes}] }
  const [bulkAiSelected, setBulkAiSelected] = useState({}); // { idx: true }
  const miseAc = "#7c9e5e";

  // Pessoas que existem em OUTROS restaurantes mas não neste — candidatas pra vincular
  const candidatasVincular = (pessoas || []).filter(p => {
    const ridList = p.restaurantIds || [];
    if (ridList.includes(restaurantId)) return false; // já neste
    if (ridList.length === 0) return false; // pessoa órfã, não considera
    const cpfD = (p.cpf || "").replace(/\D/g, "");
    if (cpfD && ownerCpfSet.has(cpfD)) return false; // owners têm acesso implícito
    return true;
  });

  function vincularPessoa(p) {
    const next = (pessoas || []).map(x => x.id === p.id ? {
      ...x,
      restaurantIds: [...(x.restaurantIds || []), restaurantId],
      permissions: { ...(x.permissions || {}), [restaurantId]: { operational: {}, admin: {}, special: {} } },
    } : x);
    onUpdate("pessoas", next);
    onUpdate("_toast", `✅ ${p.name} vinculada a este restaurante`);
    setVincularOpen(false);
    setVincularFilter("");
  }

  // v8.10.4: reset PIN da pessoa (sincroniza nos 3 records). Default = 4 primeiros do CPF.
  async function resetPessoaPin(pessoa) {
    const cpfDigits = (pessoa.cpf || "").replace(/\D/g, "");
    if (cpfDigits.length < 4) {
      onUpdate("_toast", "⚠️ CPF inválido — não foi possível gerar PIN automático");
      return;
    }
    const newPin = cpfDigits.slice(0, 4);
    if (!await appConfirm(`Resetar PIN de "${pessoa.name}"?\n\nO PIN voltará para ${newPin} (4 primeiros dígitos do CPF) e ${pessoa.name.split(" ")[0]} será obrigado(a) a trocar no próximo acesso.`)) return;
    onUpdate("pessoas", (pessoas || []).map(p =>
      p.id === pessoa.id || (cpfDigits && (p.cpf || "").replace(/\D/g, "") === cpfDigits)
        ? { ...p, pin: newPin, mustChangePin: true } : p
    ));
    if ((employees || []).length > 0) {
      onUpdate("employees", employees.map(e =>
        (pessoa.linkedEmployeeId && e.id === pessoa.linkedEmployeeId) ||
        (cpfDigits && (e.cpf || "").replace(/\D/g, "") === cpfDigits)
          ? { ...e, pin: newPin, mustChangePin: true } : e
      ));
    }
    if ((managers || []).length > 0) {
      onUpdate("managers", managers.map(m =>
        (pessoa.linkedManagerId && m.id === pessoa.linkedManagerId) ||
        (cpfDigits && (m.cpf || "").replace(/\D/g, "") === cpfDigits)
          ? { ...m, pin: newPin, mustChangePin: true } : m
      ));
    }
    onUpdate("_toast", `🔐 PIN de ${pessoa.name} resetado para ${newPin}`);
  }

  // ── Bulk add via IA ──
  // Cargos disponíveis no rest atual — passados pra IA pra ela tentar bater nome → cargo válido
  const restRolesActive = (roles || []).filter(r => r.restaurantId === restaurantId && !r.inactive);

  // Match nome de cargo (string da IA) → roleId (case-insensitive, fuzzy)
  function matchRoleByName(nameStr) {
    if (!nameStr) return null;
    const s = nameStr.toLowerCase().trim();
    return restRolesActive.find(r => r.name.toLowerCase() === s)
        || restRolesActive.find(r => r.name.toLowerCase().includes(s))
        || restRolesActive.find(r => s.includes(r.name.toLowerCase()))
        || null;
  }

  // Manda texto livre pro Groq, recebe lista estruturada de pessoas.
  async function bulkAiSubmit() {
    const text = bulkAiText.trim();
    if (!text) return;
    setBulkAiLoading(true);
    setBulkAiError("");
    setBulkAiResult(null);
    try {
      const cargosLista = restRolesActive.length > 0
        ? `Cargos VÁLIDOS deste restaurante (use exatamente esses nomes quando reconhecer):\n${restRolesActive.map(r => `- ${r.name} (${r.area})`).join("\n")}\n\n`
        : "";
      const systemPrompt = `Você extrai dados de pessoas de um texto livre em português e devolve JSON estruturado.

${cargosLista}Cada pessoa pode ter: nome completo (obrigatório), CPF (11 dígitos), telefone/WhatsApp, email, cargo, data de admissão, contato de emergência (nome + telefone). O texto pode usar separadores variados (vírgulas, traços, quebras de linha, parênteses).

Devolva APENAS um JSON neste formato exato:
{
  "pessoas": [
    {
      "name": "Nome Completo",
      "cpf": "12345678901",            // 11 dígitos só, sem pontuação. Vazio se não souber.
      "whatsapp": "5531999887766",     // só dígitos com DDI 55 quando possível. Vazio se não souber.
      "email": "fulano@dominio.com",   // vazio se não souber
      "role": "Cozinheiro",            // nome do cargo identificado (de preferência da lista acima). Vazio se não houver.
      "admission": "2024-03-15",       // data de admissão YYYY-MM-DD. Aceite formatos como "15/03/2024", "março/2024", "desde abril de 2023" — converta. Vazio se não souber.
      "emergencyName": "Maria Silva",  // contato de emergência: nome. Vazio se não souber.
      "emergencyPhone": "5531977665544"// contato de emergência: telefone (só dígitos). Vazio se não souber.
    }
  ]
}

Regras:
- Nunca invente CPF, telefone, email ou data — só use o que estiver explícito no texto.
- Capitalize nome corretamente (Janynna Silva, não JANYNNA SILVA ou janynna silva).
- Limpe CPF e telefone (só dígitos).
- Pessoas com cargo OU data de admissão são EMPREGADOS — extraia esses campos com prioridade.
- Se uma linha não parece pessoa (ex: header, texto solto), ignore.
- Se nada parecer pessoa, retorne { "pessoas": [] }.`;
      const result = await groqGenerate(systemPrompt, text);
      if (!result || !Array.isArray(result.pessoas)) {
        throw new Error("IA retornou formato inesperado.");
      }
      // Marca todas como selecionadas por default
      const sel = {};
      result.pessoas.forEach((_, i) => { sel[i] = true; });
      setBulkAiSelected(sel);
      setBulkAiResult(result);
    } catch (err) {
      setBulkAiError(err.message || "Erro ao chamar IA");
    } finally {
      setBulkAiLoading(false);
    }
  }

  function bulkAiClose() {
    setBulkAiOpen(false);
    setBulkAiText("");
    setBulkAiResult(null);
    setBulkAiSelected({});
    setBulkAiError("");
    setBulkAiLoading(false);
  }

  function updateBulkAiPessoa(idx, field, value) {
    setBulkAiResult(prev => {
      if (!prev) return prev;
      const next = { ...prev, pessoas: [...prev.pessoas] };
      next.pessoas[idx] = { ...next.pessoas[idx], [field]: value };
      return next;
    });
  }

  async function bulkAiAccept() {
    if (!bulkAiResult) return;
    const toAccept = bulkAiResult.pessoas
      .map((p, i) => ({ p, i }))
      .filter(({ i }) => bulkAiSelected[i]);
    if (toAccept.length === 0) { alert("Selecione pelo menos uma pessoa."); return; }

    let nextPessoas = [...(pessoas || [])];
    let nextEmployees = [...(employees || [])];
    let createdN = 0, linkedN = 0, skippedN = 0, asTeamN = 0;

    for (const { p } of toAccept) {
      const nm = (p.name || "").trim();
      const cpfRaw = (p.cpf || "").replace(/\D/g, "");
      if (!nm) { skippedN++; continue; }
      if (cpfRaw && cpfRaw.length !== 11) { skippedN++; continue; }

      // Resolve cargo via match (se a IA trouxe nome de cargo)
      const matchedRole = matchRoleByName(p.role);
      const admission = (p.admission || "").trim();
      // Marca como equipe automaticamente se IA identificou cargo OU admissão
      const shouldBeTeam = !!(matchedRole || admission);
      const teamDataEntry = shouldBeTeam ? {
        roleId: matchedRole?.id || null,
        admission: admission || today(),
      } : null;

      const emergencyName = (p.emergencyName || "").trim() || null;
      const emergencyPhone = (p.emergencyPhone || "").replace(/\D/g, "") || null;

      // Já existe globalmente? (mesmo CPF em qualquer restaurante)
      const globalExisting = cpfRaw ? nextPessoas.find(x => (x.cpf || "").replace(/\D/g, "") === cpfRaw) : null;

      if (globalExisting) {
        if ((globalExisting.restaurantIds || []).includes(restaurantId)) {
          // Já está neste rest — pula
          skippedN++;
          continue;
        }
        // Vincula ao rest atual + adiciona dados de equipe se aplicável
        const updatedPessoa = {
          ...globalExisting,
          restaurantIds: [...(globalExisting.restaurantIds || []), restaurantId],
          permissions: { ...(globalExisting.permissions || {}), [restaurantId]: { operational: {}, admin: {}, special: {} } },
          email: globalExisting.email || (p.email || "").trim() || null,
          whatsapp: globalExisting.whatsapp || (p.whatsapp || "").replace(/\D/g, "") || null,
          emergencyName: globalExisting.emergencyName || emergencyName,
          emergencyPhone: globalExisting.emergencyPhone || emergencyPhone,
          isTeam: { ...(globalExisting.isTeam || {}), ...(shouldBeTeam ? { [restaurantId]: true } : {}) },
          teamData: { ...(globalExisting.teamData || {}), ...(teamDataEntry ? { [restaurantId]: teamDataEntry } : {}) },
        };
        // Se virou equipe, cria employee record
        if (shouldBeTeam) {
          const sync = syncEmployeeForPessoa(updatedPessoa, nextEmployees, matchedRole?.id, admission);
          nextEmployees = sync.nextEmployees;
          updatedPessoa.linkedEmployeeId = sync.employeeId;
          asTeamN++;
        }
        nextPessoas = nextPessoas.map(x => x.id === globalExisting.id ? updatedPessoa : x);
        linkedN++;
      } else {
        // Cria nova
        const id = `pes_${Date.now().toString(36)}${Math.random().toString(36).slice(2,6)}_${createdN}`;
        const newPessoa = {
          id,
          restaurantIds: [restaurantId],
          name: nm,
          cpf: cpfRaw,
          pin: cpfRaw ? pinFromCpf(cpfRaw) : "0000",
          mustChangePin: true,
          email: (p.email || "").trim() || null,
          whatsapp: (p.whatsapp || "").replace(/\D/g, "") || null,
          emergencyName,
          emergencyPhone,
          isTeam: shouldBeTeam ? { [restaurantId]: true } : {},
          teamData: teamDataEntry ? { [restaurantId]: teamDataEntry } : {},
          permissions: { [restaurantId]: { operational: {}, admin: {}, special: {} } },
          createdAt: new Date().toISOString(),
          createdViaBulkAi: true,
        };
        // Se equipe, cria employee record antes de salvar pessoa
        if (shouldBeTeam) {
          const sync = syncEmployeeForPessoa(newPessoa, nextEmployees, matchedRole?.id, admission);
          nextEmployees = sync.nextEmployees;
          newPessoa.linkedEmployeeId = sync.employeeId;
          asTeamN++;
        }
        nextPessoas.push(newPessoa);
        createdN++;
      }
    }

    onUpdate("pessoas", nextPessoas);
    if (asTeamN > 0) onUpdate("employees", nextEmployees);
    const parts = [];
    if (createdN > 0) parts.push(`${createdN} criada${createdN===1?"":"s"}`);
    if (linkedN > 0) parts.push(`${linkedN} vinculada${linkedN===1?"":"s"}`);
    if (asTeamN > 0) parts.push(`${asTeamN} virou empregado${asTeamN===1?"":"s"}`);
    if (skippedN > 0) parts.push(`${skippedN} pulada${skippedN===1?"":"s"} (já no rest. ou sem dados)`);
    onUpdate("_toast", `✅ ${parts.join(" · ")}`);
    bulkAiClose();
  }

  function pinFromCpf(cpf) {
    const d = (cpf || "").replace(/\D/g, "").slice(0, 4);
    return d.padEnd(4, "0");
  }

  // ── Sincronização Pessoa ↔ Employee ──
  // Quando uma pessoa vira equipe (isTeam[rid]=true), garante que existe um employee
  // record correspondente em `employees` com nome/CPF/PIN/cargo/admissão sincronizados.
  // Retorna { nextEmployees, employeeId } pra encadear updates.
  function syncEmployeeForPessoa(pessoa, currentEmployees, teamRoleId, teamAdmission) {
    // Procura employee existente vinculado (por linkedPessoaId ou linkedEmployeeId direto)
    let emp = (currentEmployees || []).find(e =>
      e.restaurantId === restaurantId && (
        e.linkedPessoaId === pessoa.id ||
        (pessoa.linkedEmployeeId && e.id === pessoa.linkedEmployeeId)
      )
    );
    const now = new Date().toISOString();
    if (emp) {
      // Atualiza dados (nome, CPF, cargo, admissão) — mantém empCode/PIN
      const updated = {
        ...emp,
        name: pessoa.name,
        cpf: pessoa.cpf || emp.cpf,
        roleId: teamRoleId || emp.roleId || null,
        admission: teamAdmission || emp.admission || today(),
        phone: pessoa.whatsapp || emp.phone || null,
        email: pessoa.email || emp.email || null,
        emergencyName: pessoa.emergencyName || emp.emergencyName || null,
        emergencyPhone: pessoa.emergencyPhone || emp.emergencyPhone || null,
        // Reativa se estava inativo (vai voltar a ser equipe)
        inactive: false,
        inactiveFrom: null,
        linkedPessoaId: pessoa.id,
      };
      const nextEmployees = (currentEmployees || []).map(e => e.id === emp.id ? updated : e);
      return { nextEmployees, employeeId: emp.id };
    }
    // Cria novo employee
    const seq = nextEmpSeq(currentEmployees || [], restCode || "XXX");
    const empCode = makeEmpCode(restCode || "XXX", seq);
    const newEmpId = `emp_${Date.now().toString(36)}${Math.random().toString(36).slice(2,5)}`;
    const newEmp = {
      id: newEmpId,
      restaurantId,
      empCode,
      name: pessoa.name,
      cpf: pessoa.cpf || "",
      pin: pessoa.pin || pinFromCpf(pessoa.cpf || ""),
      mustChangePin: !!pessoa.mustChangePin,
      roleId: teamRoleId || null,
      admission: teamAdmission || today(),
      phone: pessoa.whatsapp || null,
      email: pessoa.email || null,
      emergencyName: pessoa.emergencyName || null,
      emergencyPhone: pessoa.emergencyPhone || null,
      linkedPessoaId: pessoa.id,
      createdAt: now,
      createdViaPessoa: true,
    };
    return { nextEmployees: [...(currentEmployees || []), newEmp], employeeId: newEmpId };
  }

  // Marca employee como inativo quando pessoa deixa de ser equipe
  function inactivateEmployeeForPessoa(pessoaId, currentEmployees) {
    return (currentEmployees || []).map(e => {
      if (e.restaurantId !== restaurantId || e.linkedPessoaId !== pessoaId || e.inactive) return e;
      return { ...e, inactive: true, inactiveFrom: today() };
    });
  }

  // ── Detecção de pessoas órfãs (isTeam=true mas sem employee record correspondente) ──
  // Pode acontecer com cadastros antigos feitos antes do auto-vínculo.
  const orphanPessoas = restPessoas.filter(p => {
    if (!p.isTeam?.[restaurantId]) return false;
    const hasEmp = (employees || []).some(e =>
      e.restaurantId === restaurantId && (e.linkedPessoaId === p.id || e.id === p.linkedEmployeeId)
    );
    return !hasEmp;
  });

  async function syncOrphanPessoas() {
    if (orphanPessoas.length === 0) return;
    if (!await appConfirm(`${orphanPessoas.length} pessoa${orphanPessoas.length===1?"":"s"} marcada${orphanPessoas.length===1?"":"s"} como equipe ainda não está${orphanPessoas.length===1?"":"o"} vinculada${orphanPessoas.length===1?"":"s"} ao registro de Empregados.\n\nCriar agora os registros e fazer aparecer em Equipe?`)) return;
    let nextEmployees = [...(employees || [])];
    let nextPessoas = [...(pessoas || [])];
    orphanPessoas.forEach(p => {
      const td = p.teamData?.[restaurantId] || {};
      const sync = syncEmployeeForPessoa(p, nextEmployees, td.roleId, td.admission);
      nextEmployees = sync.nextEmployees;
      nextPessoas = nextPessoas.map(x => x.id === p.id ? { ...x, linkedEmployeeId: sync.employeeId } : x);
    });
    onUpdate("employees", nextEmployees);
    onUpdate("pessoas", nextPessoas);
    onUpdate("_toast", `✅ ${orphanPessoas.length} pessoa${orphanPessoas.length===1?"":"s"} sincronizada${orphanPessoas.length===1?"":"s"} com Equipe`);
  }

  async function addPessoa() {
    const nm = form.name.trim();
    const cpfRaw = (form.cpf || "").replace(/\D/g, "");
    if (!nm) { alert("Nome é obrigatório."); return; }
    if (cpfRaw.length !== 11) { alert("CPF inválido — precisa ter 11 dígitos."); return; }
    const existing = restPessoas.find(p => (p.cpf || "").replace(/\D/g, "") === cpfRaw);
    if (existing) { alert(`Já existe uma pessoa com esse CPF neste restaurante: ${existing.name}.`); return; }
    // Verifica se existe em outro restaurante (pessoa com mesmo CPF)
    const globalExisting = (pessoas || []).find(p => (p.cpf || "").replace(/\D/g, "") === cpfRaw);
    if (globalExisting) {
      if (!await appConfirm(`Já existe uma pessoa com esse CPF cadastrada em outro restaurante: ${globalExisting.name}. Adicionar este restaurante à pessoa existente?`)) return;
      const next = (pessoas || []).map(p => p.id === globalExisting.id ? {
        ...p,
        restaurantIds: [...p.restaurantIds, restaurantId],
        permissions: { ...p.permissions, [restaurantId]: { operational: {}, admin: {}, special: {} } },
      } : p);
      onUpdate("pessoas", next);
      return;
    }
    // Validações extras para freela
    if (form.isFreela) {
      if (!form.whatsapp.trim()) { alert("Telefone/WhatsApp é obrigatório para freela."); return; }
      if (!form.pix.trim()) { alert("Chave PIX é obrigatória para freela."); return; }
    }
    const id = `pes_${Date.now().toString(36)}${Math.random().toString(36).slice(2,6)}`;
    const newP = {
      id,
      restaurantIds: [restaurantId],
      name: nm,
      cpf: cpfRaw,
      pin: form.pin.trim() || pinFromCpf(cpfRaw),
      mustChangePin: true,
      email: form.email.trim() || null,
      whatsapp: form.whatsapp.trim() || null,
      emergencyName: form.emergencyName.trim() || null,
      emergencyPhone: form.emergencyPhone.trim() || null,
      pix: form.pix.trim() || null,
      isFreela: !!form.isFreela,
      isTeam: form.isTeam ? { [restaurantId]: true } : {},
      teamData: form.isTeam ? {
        [restaurantId]: {
          roleId: form.roleId || null,
          admission: form.admission || null,
        }
      } : {},
      permissions: { [restaurantId]: { operational: {}, admin: {}, special: {} } },
      createdAt: new Date().toISOString(),
    };
    let pessoaToSave = newP;
    // Se equipe, cria employee record sincronizado e linka
    if (form.isTeam) {
      const { nextEmployees, employeeId } = syncEmployeeForPessoa(newP, employees, form.roleId, form.admission);
      pessoaToSave = { ...newP, linkedEmployeeId: employeeId };
      onUpdate("employees", nextEmployees);
    }
    onUpdate("pessoas", [...(pessoas || []), pessoaToSave]);
    setForm({ name: "", cpf: "", email: "", whatsapp: "", pin: "", emergencyName: "", emergencyPhone: "", pix: "", isTeam: false, roleId: "", admission: "", isFreela: false });
    const tags = [];
    if (form.isTeam) tags.push("equipe");
    if (form.isFreela) tags.push("freela");
    onUpdate("_toast", tags.length > 0
      ? `✅ ${nm} cadastrada (${tags.join(" + ")})`
      : `✅ ${nm} cadastrada`);
  }

  // v8.20.0: react ao editTrigger vindo do resumo de permissões — abre a pessoa em edição
  useEffect(() => {
    if (!editTrigger?.id) return;
    const p = (pessoas || []).find(x => x.id === editTrigger.id);
    if (!p) return;
    startEdit(p);
    // Scroll suave pro topo do form (depois do render)
    setTimeout(() => { try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (e) {} }, 50);
  }, [editTrigger?.id, editTrigger?.ts]); // eslint-disable-line react-hooks/exhaustive-deps

  function startEdit(p) {
    const td = p.teamData?.[restaurantId] || {};
    // v8.20.0: carrega perms da pessoa pra edição inline
    const ridPerms = p.permissions?.[restaurantId] || { operational: {}, admin: {}, special: {} };
    setEditingId(p.id);
    setEditForm({
      name: p.name ?? "",
      cpf: p.cpf ?? "",
      email: p.email ?? "",
      whatsapp: p.whatsapp ?? "",
      pin: p.pin ?? "",
      emergencyName: p.emergencyName ?? "",
      emergencyPhone: p.emergencyPhone ?? "",
      pix: p.pix ?? "",
      isTeam: !!p.isTeam?.[restaurantId],
      roleId: td.roleId ?? "",
      admission: td.admission ?? "",
      isFreela: !!p.isFreela,
      perms: {
        operational: { ...(ridPerms.operational || {}) },
        admin: { ...(ridPerms.admin || {}) },
        special: { ...(ridPerms.special || {}), areas: [...(ridPerms.special?.areas || [])] },
      },
    });
  }
  function saveEdit() {
    if (!editingId) return;
    const nm = editForm.name.trim();
    if (!nm) { alert("Nome é obrigatório."); return; }
    const cpfRaw = (editForm.cpf || "").replace(/\D/g, "");
    const original = (pessoas || []).find(p => p.id === editingId);
    const wasTeam = !!original?.isTeam?.[restaurantId];
    const willBeTeam = !!editForm.isTeam;

    let updatedPessoa = null;
    const next = (pessoas || []).map(p => {
      if (p.id !== editingId) return p;
      // v8.20.0: salva perms editadas inline no cadastro
      const newPerms = editForm.perms || { operational: {}, admin: {}, special: {} };
      updatedPessoa = {
        ...p,
        name: nm,
        cpf: cpfRaw,
        email: editForm.email.trim() || null,
        whatsapp: editForm.whatsapp.trim() || null,
        emergencyName: editForm.emergencyName.trim() || null,
        emergencyPhone: editForm.emergencyPhone.trim() || null,
        pix: editForm.pix.trim() || null,
        isFreela: !!editForm.isFreela,
        pin: editForm.pin.trim() || p.pin,
        isTeam: { ...(p.isTeam || {}), [restaurantId]: willBeTeam },
        teamData: {
          ...(p.teamData || {}),
          [restaurantId]: willBeTeam ? {
            ...(p.teamData?.[restaurantId] || {}),
            roleId: editForm.roleId || null,
            admission: editForm.admission || null,
          } : (p.teamData?.[restaurantId] || {}),
        },
        permissions: {
          ...(p.permissions || {}),
          [restaurantId]: newPerms,
        },
      };
      return updatedPessoa;
    });

    // Sincroniza employee record:
    // — virou equipe (ou continua) → cria/atualiza employee
    // — saiu da equipe → inativa employee
    if (willBeTeam) {
      const { nextEmployees, employeeId } = syncEmployeeForPessoa(updatedPessoa, employees, editForm.roleId, editForm.admission);
      onUpdate("employees", nextEmployees);
      // Atualiza linkedEmployeeId na pessoa também
      const finalPessoas = next.map(p => p.id === editingId ? { ...p, linkedEmployeeId: employeeId } : p);
      onUpdate("pessoas", finalPessoas);
    } else if (wasTeam && !willBeTeam) {
      onUpdate("employees", inactivateEmployeeForPessoa(editingId, employees));
      onUpdate("pessoas", next);
    } else {
      onUpdate("pessoas", next);
    }
    setEditingId(null);
  }
  async function delPessoa(id) {
    const p = restPessoas.find(x => x.id === id);
    if (!p) return;
    if (p.restaurantIds.length > 1) {
      if (!await appConfirm(`Remover ${p.name} apenas deste restaurante? A pessoa continua em outros ${p.restaurantIds.length - 1} restaurante(s).`)) return;
      const next = (pessoas || []).map(x => x.id === id ? {
        ...x,
        restaurantIds: x.restaurantIds.filter(r => r !== restaurantId),
        isTeam: Object.fromEntries(Object.entries(x.isTeam || {}).filter(([r]) => r !== restaurantId)),
        teamData: Object.fromEntries(Object.entries(x.teamData || {}).filter(([r]) => r !== restaurantId)),
        permissions: Object.fromEntries(Object.entries(x.permissions || {}).filter(([r]) => r !== restaurantId)),
      } : x);
      onUpdate("pessoas", next);
    } else {
      if (!await appConfirm(`Apagar ${p.name} definitivamente? Esta pessoa não está em nenhum outro restaurante.\n\nIsso vai apagar também o registro em Equipe (se existir).`)) return;
      onUpdate("pessoas", (pessoas || []).filter(x => x.id !== id));
      // Cascata: se tinha linkedEmployee, marca como inativo (não apaga histórico — gorjetas/escala dependem disso)
      if (p.linkedEmployeeId) {
        onUpdate("employees", (employees || []).map(e => e.id === p.linkedEmployeeId ? { ...e, inactive: true, inactiveFrom: today() } : e));
      }
    }
    setEditingId(null);
  }

  // v8.22.3: inativa/reativa pessoa neste restaurante (mantém histórico). Sincroniza com employee.
  async function toggleInactivePessoa(id) {
    const p = (pessoas || []).find(x => x.id === id);
    if (!p) return;
    const isCurrentlyInactive = p.inactive === true;
    const verb = isCurrentlyInactive ? "Reativar" : "Inativar";
    if (!await appConfirm(`${verb} ${p.name}?\n\n${isCurrentlyInactive
      ? "Vai voltar a aparecer nas listas operacionais."
      : "Vai sumir das listas operacionais (escala, contagens, atribuições) mas o histórico fica preservado."}`)) return;
    const today_ = today();
    onUpdate("pessoas", (pessoas || []).map(x => x.id === id ? {
      ...x,
      inactive: !isCurrentlyInactive,
      inactiveFrom: !isCurrentlyInactive ? today_ : null,
    } : x));
    if (p.linkedEmployeeId) {
      onUpdate("employees", (employees || []).map(e => e.id === p.linkedEmployeeId ? {
        ...e,
        inactive: !isCurrentlyInactive,
        inactiveFrom: !isCurrentlyInactive ? today_ : null,
      } : e));
    }
    onUpdate("_toast", `${isCurrentlyInactive ? "✅ Reativada" : "⏸ Inativada"}: ${p.name}`);
    setEditingId(null);
  }

  const q = filter.trim().toLowerCase();
  let filtered = q ? restPessoas.filter(p => p.name.toLowerCase().includes(q) || (p.cpf || "").includes(q)) : restPessoas;
  // v8.22.3: filtro de ativos (default esconde inativos, exceto se filtro "inativos" ou "todos com inativos")
  if (vincFilter !== "inativos" && vincFilter !== "todos_inc") {
    filtered = filtered.filter(p => !p.inactive);
  } else if (vincFilter === "inativos") {
    filtered = filtered.filter(p => p.inactive);
  }
  // Filtro por vínculo
  if (vincFilter !== "todos" && vincFilter !== "inativos" && vincFilter !== "todos_inc") {
    filtered = filtered.filter(p => {
      const isTeam = !!p.isTeam?.[restaurantId];
      const isFreela = !!p.isFreela;
      if (vincFilter === "equipe") return isTeam;
      if (vincFilter === "freela") return isFreela;
      if (vincFilter === "sem") return !isTeam && !isFreela;
      return true;
    });
  }
  const restRoles = (roles || []).filter(r => r.restaurantId === restaurantId);

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8,marginBottom:12}}>
        <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?16:18}}>👤 Pessoas do restaurante</h3>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {realIsOwner && (
            <button onClick={()=>setBulkAiOpen(true)} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px",color:"#8b5cf6",borderColor:"#8b5cf644"}}
              title="Cole uma lista de pessoas em texto livre — a IA estrutura e cria todas de uma vez">
              🤖 Adicionar em lote
            </button>
          )}
          {realIsOwner && candidatasVincular.length > 0 && (
            <button onClick={()=>setVincularOpen(true)} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px",color:"var(--ac)",borderColor:"var(--ac)44"}}
              title="Visível só pra Master — vincula pessoa que já existe em outro restaurante">
              🔗 Vincular existente <span style={{opacity:0.6,fontWeight:400}}>({candidatasVincular.length})</span>
            </button>
          )}
        </div>
      </div>

      {/* Banner de órfãs — pessoas marcadas como equipe sem employee record */}
      {orphanPessoas.length > 0 && (
        <div style={{padding:"12px 14px",background:"#fef3c7",border:"1px solid #f59e0b66",borderRadius:10,marginBottom:14,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10}}>
          <div style={{display:"flex",alignItems:"flex-start",gap:10,flex:"1 1 300px"}}>
            <span style={{fontSize:18,marginTop:1}}>⚠️</span>
            <div>
              <div style={{fontSize:13,color:"#92400e",fontWeight:700}}>
                {orphanPessoas.length} pessoa{orphanPessoas.length===1?"":"s"} marcada{orphanPessoas.length===1?"":"s"} como equipe ainda não aparece{orphanPessoas.length===1?"":"m"} em Equipe
              </div>
              <div style={{fontSize:11,color:"#92400e",marginTop:3,lineHeight:1.5}}>
                Cadastros antigos sem o vínculo automático. Click pra criar os registros faltantes.
              </div>
              <div style={{fontSize:10,color:"#78350f",marginTop:4,fontFamily:"'DM Mono',monospace",opacity:0.8}}>
                {orphanPessoas.slice(0,5).map(p => p.name).join(" · ")}{orphanPessoas.length > 5 ? ` · +${orphanPessoas.length-5}` : ""}
              </div>
            </div>
          </div>
          <button onClick={syncOrphanPessoas}
            style={{background:"#f59e0b",color:"#fff",border:"none",borderRadius:8,padding:"8px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",whiteSpace:"nowrap"}}>
            🔄 Sincronizar com Equipe
          </button>
        </div>
      )}
      {/* Form adicionar — todos os campos da pessoa, com toggle "É equipe" liberando cargo + admissão */}
      <div style={{padding:"14px 16px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,marginBottom:16}}>
        {/* SEÇÃO: Dados pessoais (globais) */}
        <div style={{fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,marginBottom:8}}>Dados pessoais</div>
        <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"2fr 1.4fr",gap:8,marginBottom:10}}>
          <div>
            <label style={{...S.label,fontSize:11}}>Nome completo *</label>
            <input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="ex: João Silva" style={S.input} />
          </div>
          <div>
            <label style={{...S.label,fontSize:11}}>CPF *</label>
            <input value={form.cpf} onChange={e=>setForm({...form,cpf:maskCpf(e.target.value),pin: form.pin || pinFromCpf(e.target.value)})} placeholder="000.000.000-00" style={{...S.input,fontFamily:"'DM Mono',monospace"}} />
          </div>
        </div>
        {/* v8.10.4: PIN não exposto na UI — auto-gerado pelos 4 primeiros dígitos do CPF.
             A pessoa troca no primeiro acesso. Reset é via botão na edição. */}

        {/* SEÇÃO: Contato */}
        <div style={{fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,marginBottom:8,marginTop:6}}>Contato</div>
        <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"1fr 1fr 1fr",gap:8,marginBottom:10}}>
          <div>
            <label style={{...S.label,fontSize:11}}>Email</label>
            <input value={form.email} onChange={e=>setForm({...form,email:e.target.value})} placeholder="opcional" style={S.input} />
          </div>
          <div>
            <label style={{...S.label,fontSize:11}}>WhatsApp {form.isFreela && <span style={{color:"#7c3aed"}}>*</span>}</label>
            <input value={form.whatsapp} onChange={e=>setForm({...form,whatsapp:e.target.value})} placeholder={form.isFreela?"obrigatório":"opcional"} style={{...S.input,borderColor:form.isFreela&&!form.whatsapp.trim()?"#7c3aed66":"var(--border)"}} />
          </div>
          <div>
            <label style={{...S.label,fontSize:11}}>PIX {form.isFreela && <span style={{color:"#7c3aed"}}>*</span>}</label>
            <input value={form.pix} onChange={e=>setForm({...form,pix:e.target.value})} placeholder={form.isFreela?"obrigatório (CPF/email/tel/chave)":"opcional"} style={{...S.input,borderColor:form.isFreela&&!form.pix.trim()?"#7c3aed66":"var(--border)"}} />
          </div>
        </div>

        {/* SEÇÃO: Contato de emergência */}
        <div style={{fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,marginBottom:8,marginTop:6}}>Contato de emergência</div>
        <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"1fr 1fr",gap:8,marginBottom:10}}>
          <div>
            <label style={{...S.label,fontSize:11}}>Nome</label>
            <input value={form.emergencyName} onChange={e=>setForm({...form,emergencyName:e.target.value})} placeholder="opcional" style={S.input} />
          </div>
          <div>
            <label style={{...S.label,fontSize:11}}>Telefone</label>
            <input value={form.emergencyPhone} onChange={e=>setForm({...form,emergencyPhone:e.target.value})} placeholder="opcional" style={S.input} />
          </div>
        </div>

        {/* SEÇÃO: Equipe deste restaurante (toggle libera cargo + admissão) */}
        <div style={{marginTop:6,padding:"10px 12px",background: form.isTeam?"var(--ac)11":"var(--bg2)",border:`1px solid ${form.isTeam?"var(--ac)44":"var(--border)"}`,borderRadius:10}}>
          <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",userSelect:"none"}}>
            <input type="checkbox" checked={form.isTeam} onChange={e=>setForm({...form,isTeam:e.target.checked})}
              style={{cursor:"pointer",width:18,height:18,accentColor:"var(--ac)"}}/>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:700,color:form.isTeam?"var(--ac-text,var(--ac))":"var(--text)"}}>É equipe deste restaurante</div>
              <div style={{fontSize:11,color:"var(--text3)",marginTop:2,lineHeight:1.45}}>
                Quando marcado, entra na <strong>escala</strong> e no <strong>cálculo de gorjetas</strong> de acordo com o cargo.
              </div>
            </div>
          </label>
          {form.isTeam && (
            <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"1.5fr 1fr",gap:8,marginTop:10,paddingTop:10,borderTop:"1px solid var(--ac)22"}}>
              <div>
                <label style={{...S.label,fontSize:11}}>Cargo</label>
                <select value={form.roleId} onChange={e=>setForm({...form,roleId:e.target.value})} style={{...S.input,cursor:"pointer"}}>
                  <option value="">— Selecione —</option>
                  {restRoles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{...S.label,fontSize:11}}>Data de admissão</label>
                <input type="date" value={form.admission} onChange={e=>setForm({...form,admission:e.target.value})} style={S.input}/>
              </div>
            </div>
          )}
        </div>

        {/* SEÇÃO: É freela neste restaurante (toggle independente — pode coexistir com equipe) */}
        <div style={{marginTop:8,padding:"10px 12px",background: form.isFreela?"#7c3aed11":"var(--bg2)",border:`1px solid ${form.isFreela?"#7c3aed44":"var(--border)"}`,borderRadius:10}}>
          <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",userSelect:"none"}}>
            <input type="checkbox" checked={form.isFreela} onChange={e=>setForm({...form,isFreela:e.target.checked})}
              style={{cursor:"pointer",width:18,height:18,accentColor:"#7c3aed"}}/>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:700,color:form.isFreela?"#7c3aed":"var(--text)"}}>🎒 É freela neste restaurante</div>
              <div style={{fontSize:11,color:"var(--text3)",marginTop:2,lineHeight:1.45}}>
                Pega shifts pontuais (não entra em gorjeta). PIX e WhatsApp viram <strong>obrigatórios</strong> — necessários pra pagamento.
              </div>
            </div>
          </label>
        </div>

        <div style={{display:"flex",justifyContent:"flex-end",marginTop:12}}>
          <button onClick={addPessoa} disabled={!form.name.trim()} style={{background:"var(--ac)",color:"#fff",border:"none",borderRadius:8,padding:"10px 24px",fontSize:13,fontWeight:700,cursor:!form.name.trim()?"not-allowed":"pointer",fontFamily:"'DM Sans',sans-serif",opacity:!form.name.trim()?0.5:1}}>
            + Adicionar pessoa
          </button>
        </div>

        <div style={{marginTop:10,fontSize:11,color:"var(--text3)",lineHeight:1.5,paddingTop:10,borderTop:"1px solid var(--border)"}}>
          💡 Toda pessoa que tem acesso ao sistema (membro da equipe, contador, consultor, fornecedor) é cadastrada aqui. Marcar "É equipe" cria o vínculo trabalhista neste restaurante. Permissões são definidas na aba ao lado.
        </div>
      </div>

      {/* Filter */}
      <div style={{marginBottom:10,display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
        <input value={filter} onChange={e=>setFilter(e.target.value)} placeholder="🔍 Buscar por nome ou CPF..." style={{...S.input,maxWidth:280,flex:"1 1 200px"}} />
        <select value={vincFilter} onChange={e=>setVincFilter(e.target.value)} style={{...S.input,maxWidth:200,fontSize:12,padding:"7px 10px",cursor:"pointer"}}>
          <option value="todos">📋 Ativos · Todos</option>
          <option value="equipe">👥 Ativos · Equipe</option>
          <option value="freela">🎒 Ativos · Freelas</option>
          <option value="sem">— Ativos · Sem vínculo</option>
          <option value="inativos">⏸ Apenas Inativos</option>
          <option value="todos_inc">📂 Todos (inc. inativos)</option>
        </select>
        <div style={{fontSize:11,color:"var(--text3)"}}>
          {restPessoas.filter(p=>!p.inactive).length} ativa(s) · {restPessoas.filter(p=>p.inactive).length} inativa(s) · {restPessoas.filter(p => p.isTeam?.[restaurantId]&&!p.inactive).length} equipe · {restPessoas.filter(p => p.isFreela&&!p.inactive).length} freela
        </div>
      </div>

      {/* Lista */}
      {filtered.length === 0 ? (
        <div style={{padding:"32px 16px",textAlign:"center",color:"var(--text3)",fontSize:14,background:"var(--card-bg)",borderRadius:12,border:"1px solid var(--border)"}}>
          {q ? "Nenhuma pessoa encontrada" : "Nenhuma pessoa cadastrada"}
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {[...filtered].sort((a,b)=>a.name.localeCompare(b.name)).map(p => {
            const isEditing = editingId === p.id;
            const isTeam = !!p.isTeam?.[restaurantId];
            const td = p.teamData?.[restaurantId] || {};
            const role = restRoles.find(r => r.id === td.roleId);
            if (isEditing) {
              return (
                <div key={p.id} style={{padding:"14px",background:"var(--bg2)",border:`1px solid ${miseAc}66`,borderRadius:10}}>
                  <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"2fr 1.5fr 1fr",gap:8,marginBottom:10}}>
                    <div>
                      <label style={{...S.label,fontSize:11}}>Nome</label>
                      <input value={editForm.name} onChange={e=>setEditForm({...editForm,name:e.target.value})} style={S.input} autoFocus />
                    </div>
                    <div>
                      <label style={{...S.label,fontSize:11}}>CPF</label>
                      <input value={maskCpf(editForm.cpf)} onChange={e=>setEditForm({...editForm,cpf:maskCpf(e.target.value)})} style={{...S.input,fontFamily:"'DM Mono',monospace"}} />
                    </div>
                    <div>
                      {/* v8.10.4: PIN não exibido — botão "Resetar" volta o PIN para os 4 primeiros do CPF e força troca. */}
                      <label style={{...S.label,fontSize:11}}>PIN</label>
                      <button type="button" onClick={()=>resetPessoaPin(p)} style={{width:"100%",padding:"8px 10px",borderRadius:8,border:"1px solid #f59e0b66",background:"#f59e0b11",color:"#f59e0b",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:11,fontWeight:700,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}} title="Resetar PIN para os 4 primeiros dígitos do CPF">🔑 Resetar PIN</button>
                    </div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"1fr 1fr 1fr",gap:8,marginBottom:10}}>
                    <div>
                      <label style={{...S.label,fontSize:11}}>Email</label>
                      <input value={editForm.email} onChange={e=>setEditForm({...editForm,email:e.target.value})} style={S.input} />
                    </div>
                    <div>
                      <label style={{...S.label,fontSize:11}}>WhatsApp {editForm.isFreela && <span style={{color:"#7c3aed"}}>*</span>}</label>
                      <input value={editForm.whatsapp} onChange={e=>setEditForm({...editForm,whatsapp:e.target.value})} style={{...S.input,borderColor:editForm.isFreela&&!editForm.whatsapp.trim()?"#7c3aed66":"var(--border)"}} />
                    </div>
                    <div>
                      <label style={{...S.label,fontSize:11}}>PIX {editForm.isFreela && <span style={{color:"#7c3aed"}}>*</span>}</label>
                      <input value={editForm.pix} onChange={e=>setEditForm({...editForm,pix:e.target.value})} placeholder="CPF/email/tel/chave" style={{...S.input,borderColor:editForm.isFreela&&!editForm.pix.trim()?"#7c3aed66":"var(--border)"}} />
                    </div>
                  </div>
                  {/* Campos de equipe */}
                  <div style={{padding:"10px 12px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:8,marginBottom:10}}>
                    <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",marginBottom:editForm.isTeam?10:0}}>
                      <input type="checkbox" checked={editForm.isTeam} onChange={e=>setEditForm({...editForm,isTeam:e.target.checked})} style={{cursor:"pointer",accentColor:miseAc,width:18,height:18}} />
                      <span style={{fontSize:13,color:"var(--text)",fontWeight:600}}>É membro da equipe deste restaurante</span>
                      <span style={{fontSize:11,color:"var(--text3)",fontWeight:400}}>(entra em escala, recebe gorjeta, etc.)</span>
                    </label>
                    {editForm.isTeam && (
                      <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"2fr 1fr",gap:8}}>
                        <div>
                          <label style={{...S.label,fontSize:11}}>Cargo</label>
                          <select value={editForm.roleId} onChange={e=>setEditForm({...editForm,roleId:e.target.value})} style={S.input}>
                            <option value="">— Sem cargo definido —</option>
                            {restRoles.map(r => <option key={r.id} value={r.id}>{r.name} ({r.area})</option>)}
                          </select>
                        </div>
                        <div>
                          <label style={{...S.label,fontSize:11}}>Data de admissão</label>
                          <input type="date" value={editForm.admission} onChange={e=>setEditForm({...editForm,admission:e.target.value})} style={S.input} />
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Toggle freela */}
                  <div style={{padding:"10px 12px",background:editForm.isFreela?"#7c3aed11":"var(--card-bg)",border:`1px solid ${editForm.isFreela?"#7c3aed44":"var(--border)"}`,borderRadius:8,marginBottom:10}}>
                    <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
                      <input type="checkbox" checked={editForm.isFreela} onChange={e=>setEditForm({...editForm,isFreela:e.target.checked})} style={{cursor:"pointer",accentColor:"#7c3aed",width:18,height:18}} />
                      <span style={{fontSize:13,color:editForm.isFreela?"#7c3aed":"var(--text)",fontWeight:600}}>🎒 É freela neste restaurante</span>
                      <span style={{fontSize:11,color:"var(--text3)",fontWeight:400}}>(pega shifts pontuais — PIX e WhatsApp obrigatórios)</span>
                    </label>
                  </div>
                  {/* v8.20.0: Permissões inline no cadastro */}
                  <div style={{padding:"10px 12px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:8,marginBottom:10}}>
                    <div style={{fontSize:13,color:"var(--text)",fontWeight:700,marginBottom:8}}>🔐 Permissões neste restaurante</div>
                    <div style={{fontSize:11,color:"var(--text3)",marginBottom:10,lineHeight:1.5}}>
                      Por padrão a pessoa não acessa nada — marque só o que precisar. Use "Tudo" pra liberar um grupo inteiro.
                    </div>
                    <PessoaPermsEditor
                      perms={editForm.perms}
                      onChange={(np)=>setEditForm(f=>({...f,perms:np}))}
                      mobileOnly={mobileOnly}
                    />
                  </div>
                  {/* v8.22.3: ações de inativar/excluir + salvar/cancelar */}
                  <div style={{display:"flex",gap:8,justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",marginTop:4}}>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                      <button onClick={()=>toggleInactivePessoa(p.id)}
                        style={{...S.btnSecondary,fontSize:12,padding:"6px 14px",color:p.inactive?"#15803d":"#b45309",borderColor:p.inactive?"#10b98144":"#f59e0b66"}}>
                        {p.inactive ? "✅ Reativar" : "⏸ Inativar"}
                      </button>
                      <button onClick={()=>delPessoa(p.id)}
                        style={{...S.btnSecondary,fontSize:12,padding:"6px 14px",color:"var(--red)",borderColor:"var(--red)44"}}>
                        🗑️ Excluir
                      </button>
                    </div>
                    <div style={{display:"flex",gap:8}}>
                      <button onClick={()=>setEditingId(null)} style={{...S.btnSecondary,fontSize:12,padding:"6px 14px"}}>Cancelar</button>
                      <button onClick={()=>{
                        if (editForm.isFreela) {
                          if (!editForm.whatsapp.trim()) { alert("Telefone/WhatsApp é obrigatório para freela."); return; }
                          if (!editForm.pix.trim()) { alert("Chave PIX é obrigatória para freela."); return; }
                        }
                        saveEdit();
                      }} style={{...S.btnPrimary,fontSize:12,padding:"6px 14px"}}>💾 Salvar</button>
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <div key={p.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,flexWrap:"wrap"}}>
                <div style={{flex:1,minWidth:180}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                    <span style={{color:"var(--text)",fontWeight:600}}>{p.name}</span>
                    {isTeam && <span style={{fontSize:10,padding:"1px 8px",borderRadius:10,background:miseAc+"22",color:"#15803d",fontWeight:700,letterSpacing:0.4}}>EQUIPE</span>}
                    {p.isFreela && <span style={{fontSize:10,padding:"1px 8px",borderRadius:10,background:"#7c3aed22",color:"#7c3aed",fontWeight:700,letterSpacing:0.4}}>FREELA</span>}
                    {p.linkedManagerId && <span style={{fontSize:10,padding:"1px 8px",borderRadius:10,background:"var(--ac)22",color:"var(--ac)",fontWeight:700,letterSpacing:0.4}}>GESTOR</span>}
                    {p.restaurantIds.length > 1 && <span style={{fontSize:10,padding:"1px 8px",borderRadius:10,background:"var(--bg2)",color:"var(--text3)",fontWeight:700}}>{p.restaurantIds.length}× rest.</span>}
                    {p.mustChangePin && <span style={{fontSize:10,padding:"1px 8px",borderRadius:10,background:"#fffbeb",color:"#d97706",fontWeight:700,letterSpacing:0.4}}>PIN INICIAL</span>}
                    {p.inactive && <span style={{fontSize:10,padding:"1px 8px",borderRadius:10,background:"#f3f4f6",color:"#6b7280",fontWeight:700,letterSpacing:0.4}}>INATIVO{p.inactiveFrom ? ` ${fmtDate(p.inactiveFrom)}` : ""}</span>}
                    {noShowsByPessoa[p.id] > 0 && (
                      <span title={`Não compareceu em ${noShowsByPessoa[p.id]} agendamento(s) neste restaurante`}
                        style={{fontSize:10,padding:"1px 8px",borderRadius:10,background:"#dc262622",color:"#991b1b",fontWeight:700,letterSpacing:0.4}}>
                        🚫 {noShowsByPessoa[p.id]} no-show{noShowsByPessoa[p.id]>1?"s":""}
                      </span>
                    )}
                  </div>
                  <div style={{fontSize:11,color:"var(--text3)",marginTop:2,display:"flex",gap:10,flexWrap:"wrap"}}>
                    {p.cpf && <span style={{fontFamily:"'DM Mono',monospace"}}>{maskCpf(p.cpf)}</span>}
                    {p.whatsapp && <span>📱 {p.whatsapp}</span>}
                    {p.email && <span>✉ {p.email}</span>}
                    {isTeam && role && <span>💼 {role.name}</span>}
                    {isTeam && td.admission && <span>📅 desde {fmtDate(td.admission)}</span>}
                  </div>
                </div>
                {realIsOwner && onStartImpersonate && (
                  <button onClick={()=>onStartImpersonate(p.id)} title="Ver o sistema como esta pessoa" style={{...S.btnSecondary,fontSize:12,padding:"6px 12px",color:"#a855f7",borderColor:"#a855f744"}}>👁️ Ver como</button>
                )}
                <button onClick={()=>startEdit(p)} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px"}}>Editar</button>
                <button onClick={()=>delPessoa(p.id)} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px",color:"var(--red)",borderColor:"var(--red)44"}}>Remover</button>
              </div>
            );
          })}
        </div>
      )}

      {/* MODAL: adicionar pessoas em lote via IA (somente Master) */}
      {bulkAiOpen && (
        <div onClick={bulkAiClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:9500,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",borderRadius:14,maxWidth:740,width:"100%",maxHeight:"90vh",display:"flex",flexDirection:"column",boxShadow:"0 10px 40px rgba(0,0,0,0.3)"}}>
            <div style={{padding:"16px 20px",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div>
                <div style={{fontSize:11,color:"#8b5cf6",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Pessoas · Assistente</div>
                <div style={{fontSize:16,color:"var(--text)",fontWeight:700,marginTop:2}}>🤖 Adicionar em lote via IA</div>
              </div>
              <button onClick={bulkAiClose} style={{background:"none",border:"none",fontSize:22,color:"var(--text3)",cursor:"pointer",padding:"0 4px"}}>×</button>
            </div>

            <div style={{padding:"14px 20px",overflowY:"auto",flex:1}}>
              {!bulkAiResult ? (
                <>
                  <p style={{color:"var(--text2)",fontSize:13,lineHeight:1.55,margin:"0 0 12px"}}>
                    Cole uma lista de pessoas em <strong>texto livre</strong> — pode vir de WhatsApp, email, planilha, qualquer formato. A IA extrai nome, CPF, contato e cargo. Depois você revisa e confirma.
                  </p>
                  <div style={{padding:"10px 12px",background:"#8b5cf608",border:"1px solid #8b5cf622",borderRadius:8,marginBottom:12,fontSize:11,color:"var(--text3)",lineHeight:1.6,fontFamily:"'DM Mono',monospace"}}>
                    <div style={{color:"#8b5cf6",fontWeight:700,marginBottom:4,fontFamily:"'DM Sans',sans-serif"}}>Exemplos que funcionam:</div>
                    Janynna Silva 11122233344 garçonete (31) 99988-7766<br/>
                    João Pedro - cozinheiro - cpf 222.333.444-55<br/>
                    Maria Souza, bartender, maria@bar.com, 31988776655
                  </div>
                  <textarea
                    value={bulkAiText}
                    onChange={e=>setBulkAiText(e.target.value)}
                    placeholder="Cole a lista aqui..."
                    style={{...S.input,width:"100%",minHeight:200,resize:"vertical",fontFamily:"'DM Mono',monospace",fontSize:13,lineHeight:1.5,boxSizing:"border-box"}}
                    autoFocus
                  />
                  {bulkAiError && (
                    <div style={{marginTop:10,padding:"10px 12px",background:"var(--red-bg)",border:"1px solid var(--red)33",borderRadius:8,color:"var(--red)",fontSize:12}}>
                      ⚠️ {bulkAiError}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10,flexWrap:"wrap",gap:8}}>
                    <div style={{fontSize:13,color:"var(--text2)"}}>
                      ✨ A IA encontrou <strong>{bulkAiResult.pessoas.length}</strong> pessoa{bulkAiResult.pessoas.length===1?"":"s"}.
                      Revise e desmarque o que não quiser criar.
                    </div>
                    <div style={{display:"flex",gap:6}}>
                      <button onClick={()=>{
                        const all = {}; bulkAiResult.pessoas.forEach((_, i) => { all[i] = true; });
                        setBulkAiSelected(all);
                      }} style={{...S.btnSecondary,fontSize:10,padding:"4px 10px"}}>✓ Todas</button>
                      <button onClick={()=>setBulkAiSelected({})} style={{...S.btnSecondary,fontSize:10,padding:"4px 10px"}}>Nenhuma</button>
                    </div>
                  </div>
                  {bulkAiResult.pessoas.length === 0 ? (
                    <div style={{padding:"30px 20px",textAlign:"center",color:"var(--text3)",fontSize:13,background:"var(--bg2)",borderRadius:10}}>
                      A IA não conseguiu identificar nenhuma pessoa no texto. Volte e ajuste.
                    </div>
                  ) : (
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      {bulkAiResult.pessoas.map((p, i) => {
                        const checked = !!bulkAiSelected[i];
                        const cpfRaw = (p.cpf || "").replace(/\D/g, "");
                        const cpfValid = cpfRaw.length === 11 || cpfRaw.length === 0;
                        const cpfDup = cpfRaw && (pessoas || []).some(x => (x.cpf || "").replace(/\D/g, "") === cpfRaw && (x.restaurantIds || []).includes(restaurantId));
                        const cpfElsewhere = cpfRaw && !cpfDup && (pessoas || []).some(x => (x.cpf || "").replace(/\D/g, "") === cpfRaw);
                        const matchedRole = matchRoleByName(p.role);
                        const willBeTeam = !!(matchedRole || (p.admission || "").trim());
                        return (
                          <div key={i} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"12px 14px",background: checked ? "#8b5cf608" : "var(--bg2)",borderRadius:10,border:`1px solid ${checked ? "#8b5cf644" : "var(--border)"}`,transition:"all 0.12s"}}>
                            <input type="checkbox" checked={checked}
                              onChange={()=>setBulkAiSelected(prev => ({ ...prev, [i]: !prev[i] }))}
                              style={{cursor:"pointer",marginTop:6,accentColor:"#8b5cf6",width:16,height:16,flexShrink:0}}/>
                            <div style={{flex:1,minWidth:0,display:"flex",flexDirection:"column",gap:6}}>
                              {/* Linha 1: nome + CPF */}
                              <div style={{display:"grid",gridTemplateColumns:"2fr 1.3fr",gap:6}}>
                                <input value={p.name||""} onChange={e=>updateBulkAiPessoa(i,"name",e.target.value)}
                                  placeholder="Nome completo"
                                  style={{...S.input,fontSize:12,padding:"6px 8px",fontWeight:600}}/>
                                <input value={p.cpf||""} onChange={e=>updateBulkAiPessoa(i,"cpf",e.target.value)}
                                  placeholder="CPF (11 dígitos)"
                                  style={{...S.input,fontSize:12,padding:"6px 8px",fontFamily:"'DM Mono',monospace",
                                    borderColor: cpfValid ? "var(--border)" : "var(--red)",
                                    background: cpfDup ? "#fef9e7" : (cpfElsewhere ? "#dbeafe" : undefined)}}
                                  title={cpfDup ? "⚠️ Já existe nesse restaurante — vai pular" : cpfElsewhere ? "ℹ️ Existe em outro rest. — vai vincular ao atual" : ""}/>
                              </div>
                              {/* Linha 2: WhatsApp + Email */}
                              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                                <input value={p.whatsapp||""} onChange={e=>updateBulkAiPessoa(i,"whatsapp",e.target.value)}
                                  placeholder="WhatsApp"
                                  style={{...S.input,fontSize:12,padding:"6px 8px",fontFamily:"'DM Mono',monospace"}}/>
                                <input value={p.email||""} onChange={e=>updateBulkAiPessoa(i,"email",e.target.value)}
                                  placeholder="Email"
                                  style={{...S.input,fontSize:12,padding:"6px 8px"}}/>
                              </div>
                              {/* Linha 3: Cargo (select) + Data de admissão */}
                              <div style={{display:"grid",gridTemplateColumns:"1.5fr 1fr",gap:6}}>
                                <select value={matchedRole?.id || ""}
                                  onChange={e=>{
                                    const r = restRolesActive.find(x => x.id === e.target.value);
                                    updateBulkAiPessoa(i, "role", r?.name || "");
                                  }}
                                  style={{...S.input,fontSize:12,padding:"6px 8px",cursor:"pointer",
                                    borderColor: p.role && !matchedRole ? "var(--red)" : "var(--border)"}}
                                  title={p.role && !matchedRole ? `Cargo "${p.role}" não bate com cargos do restaurante` : ""}>
                                  <option value="">— Cargo —</option>
                                  {AREAS.map(a => (
                                    <optgroup key={a} label={a}>
                                      {restRolesActive.filter(r => r.area === a).map(r => (
                                        <option key={r.id} value={r.id}>{r.name}{r.points?` (${r.points}pt)`:""}</option>
                                      ))}
                                    </optgroup>
                                  ))}
                                </select>
                                <input type="date" value={p.admission||""} onChange={e=>updateBulkAiPessoa(i,"admission",e.target.value)}
                                  style={{...S.input,fontSize:12,padding:"6px 8px"}}
                                  title="Data de admissão"/>
                              </div>
                              {/* Linha 4: Emergência */}
                              <div style={{display:"grid",gridTemplateColumns:"1.3fr 1fr",gap:6}}>
                                <input value={p.emergencyName||""} onChange={e=>updateBulkAiPessoa(i,"emergencyName",e.target.value)}
                                  placeholder="Contato de emergência (nome)"
                                  style={{...S.input,fontSize:12,padding:"6px 8px"}}/>
                                <input value={p.emergencyPhone||""} onChange={e=>updateBulkAiPessoa(i,"emergencyPhone",e.target.value)}
                                  placeholder="Tel. emergência"
                                  style={{...S.input,fontSize:12,padding:"6px 8px",fontFamily:"'DM Mono',monospace"}}/>
                              </div>
                              {/* Avisos */}
                              <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:2,fontSize:10}}>
                                {willBeTeam && (
                                  <span style={{color:"var(--green)",background:"#10b98115",padding:"2px 8px",borderRadius:6,fontWeight:600}}>
                                    ✓ Será marcada como equipe
                                  </span>
                                )}
                                {p.role && !matchedRole && (
                                  <span style={{color:"#92400e",background:"#fef3c7",padding:"2px 8px",borderRadius:6,fontWeight:600}}>
                                    ⚠️ Cargo "{p.role}" não bate — escolha um da lista
                                  </span>
                                )}
                                {cpfDup && (
                                  <span style={{color:"#92400e",background:"#fef9e7",padding:"2px 8px",borderRadius:6,fontWeight:600}}>
                                    ⚠️ CPF já no rest — será pulada
                                  </span>
                                )}
                                {cpfElsewhere && (
                                  <span style={{color:"#1e40af",background:"#dbeafe",padding:"2px 8px",borderRadius:6,fontWeight:600}}>
                                    ℹ️ CPF em outro rest — será vinculada
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>

            <div style={{padding:"12px 20px",borderTop:"1px solid var(--border)",display:"flex",gap:8,justifyContent:"space-between",background:"var(--bg2)",flexWrap:"wrap"}}>
              {!bulkAiResult ? (
                <>
                  <button onClick={bulkAiClose} style={{...S.btnSecondary,fontSize:12,padding:"8px 16px"}}>Cancelar</button>
                  <button onClick={bulkAiSubmit} disabled={bulkAiLoading || !bulkAiText.trim()}
                    style={{background:bulkAiLoading||!bulkAiText.trim()?"var(--border)":"#8b5cf6",color:"#fff",border:"none",borderRadius:8,padding:"8px 18px",fontSize:13,fontWeight:700,cursor:bulkAiLoading||!bulkAiText.trim()?"not-allowed":"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                    {bulkAiLoading ? "Processando..." : "✨ Estruturar com IA"}
                  </button>
                </>
              ) : (
                <>
                  <button onClick={()=>setBulkAiResult(null)} style={{...S.btnSecondary,fontSize:12,padding:"8px 16px"}}>← Voltar</button>
                  <button onClick={bulkAiAccept}
                    disabled={Object.values(bulkAiSelected).filter(Boolean).length === 0}
                    style={{background:"#8b5cf6",color:"#fff",border:"none",borderRadius:8,padding:"8px 18px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",opacity:Object.values(bulkAiSelected).filter(Boolean).length === 0 ? 0.5 : 1}}>
                    + Adicionar {Object.values(bulkAiSelected).filter(Boolean).length} pessoa{Object.values(bulkAiSelected).filter(Boolean).length===1?"":"s"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL: vincular pessoa existente (de outros restaurantes) */}
      {vincularOpen && (() => {
        const q = vincularFilter.trim().toLowerCase();
        const filteredCandidatas = q
          ? candidatasVincular.filter(p => p.name.toLowerCase().includes(q) || (p.cpf || "").includes(q))
          : candidatasVincular;
        const sortedCandidatas = [...filteredCandidatas].sort((a,b)=>a.name.localeCompare(b.name));
        return (
          <div onClick={()=>setVincularOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:9500,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
            <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",borderRadius:14,maxWidth:560,width:"100%",maxHeight:"85vh",display:"flex",flexDirection:"column",boxShadow:"0 10px 40px rgba(0,0,0,0.3)"}}>
              <div style={{padding:"16px 20px",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div>
                  <div style={{fontSize:11,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Pessoas</div>
                  <div style={{fontSize:16,color:"var(--text)",fontWeight:700,marginTop:2}}>🔗 Vincular pessoa existente</div>
                </div>
                <button onClick={()=>setVincularOpen(false)} style={{background:"none",border:"none",fontSize:22,color:"var(--text3)",cursor:"pointer",padding:"0 4px"}}>×</button>
              </div>
              <div style={{padding:"12px 20px",borderBottom:"1px solid var(--border)"}}>
                <p style={{color:"var(--text3)",fontSize:12,margin:"0 0 10px",lineHeight:1.5}}>
                  Pessoas que já existem em outros restaurantes . Vincular adiciona acesso a este restaurante sem criar cadastro novo — mantém o mesmo CPF, PIN e ID.
                </p>
                <input value={vincularFilter} onChange={e=>setVincularFilter(e.target.value)} placeholder="🔍 Buscar por nome ou CPF..." style={{...S.input}} autoFocus />
              </div>
              <div style={{padding:"10px 16px",overflowY:"auto",flex:1}}>
                {sortedCandidatas.length === 0 ? (
                  <div style={{padding:"24px 16px",textAlign:"center",color:"var(--text3)",fontSize:13}}>
                    {q ? "Nenhuma pessoa encontrada com esse termo." : "Nenhuma pessoa em outros restaurantes ainda."}
                  </div>
                ) : (
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {sortedCandidatas.map(p => {
                      const ridCount = (p.restaurantIds || []).length;
                      const cpfMasked = p.cpf ? p.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4") : "—";
                      const initial = (p.name || "?").trim().charAt(0).toUpperCase();
                      return (
                        <div key={p.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",background:"var(--card-bg)",borderRadius:10,border:"1px solid var(--border)",transition:"background 0.12s"}}
                          onMouseEnter={e=>e.currentTarget.style.background="var(--bg2)"}
                          onMouseLeave={e=>e.currentTarget.style.background="var(--card-bg)"}>
                          {/* Avatar com iniciais */}
                          <div style={{flexShrink:0,width:36,height:36,borderRadius:18,background:"var(--ac)22",color:"var(--ac)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:700}}>
                            {initial}
                          </div>
                          {/* Info da pessoa */}
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:13,fontWeight:600,color:"var(--text)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.name}</div>
                            <div style={{display:"flex",alignItems:"center",gap:8,marginTop:2,fontSize:11,color:"var(--text3)",flexWrap:"wrap"}}>
                              <span style={{fontFamily:"'DM Mono',monospace"}}>{cpfMasked}</span>
                              <span style={{opacity:0.4}}>·</span>
                              <span title={`Em ${ridCount} restaurante${ridCount===1?"":"s"}`}>📍 {ridCount}</span>
                              {p.email && (<>
                                <span style={{opacity:0.4}}>·</span>
                                <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:180}}>📧 {p.email}</span>
                              </>)}
                            </div>
                          </div>
                          {/* Botão (largura auto, não estica) */}
                          <button onClick={()=>vincularPessoa(p)}
                            style={{flexShrink:0,width:"auto",background:"var(--ac)",color:"#fff",border:"none",borderRadius:8,padding:"7px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",whiteSpace:"nowrap"}}>
                            Vincular →
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ──  PERMISSÕES — MATRIZ COM SANFONA                            ──
// ═══════════════════════════════════════════════════════════════
// v8.17.0: PERM_GROUPS reorganizado em grupos funcionais (Financeiro/DP/Escala/Comunicação/etc)
// que vão guiar a UI nova de permissões com toggle de grupo.
// `legacy: true` em uma perm = ela é mantida só pra compat e não aparece na matriz nova.
const PERM_GROUPS = [
  {
    id: "g_financeiro", label: "💰 Financeiro", color: "#16a34a", bg: "#16a34a22",
    perms: [
      { key: "admin.tips",          label: "Lançar gorjetas",            icon: "💰" },
      { key: "admin.vt",            label: "Gerir Vale Transporte",      icon: "🚌" },
      { key: "admin.regraDivisao",  label: "Mudar regra de divisão",     icon: "⚖️" },
      { key: "operational.gorjetas",label: "Ver dashboard de gorjetas",  icon: "📊" },
    ],
  },
  {
    id: "g_dp", label: "👥 DP / Departamento Pessoal", color: "#a855f7", bg: "#a855f722",
    perms: [
      { key: "admin.pessoas",            label: "Cadastrar/editar pessoas",      icon: "🛂" },
      { key: "admin.employees",          label: "Gerir aba Equipe",              icon: "👥" },
      { key: "admin.roles",              label: "Gerir cargos",                  icon: "🏷️" },
      { key: "admin.dpCanal",            label: "Acessar canal Fale com DP",     icon: "💬" },
      { key: "admin.atravessarPrivado",  label: "Atravessar privacidade",        icon: "🔓" },
      { key: "admin.reabrirEscala",      label: "Reabrir mês fechado",           icon: "🔓" },
    ],
  },
  {
    id: "g_escala", label: "📅 Escala e Freelas", color: "#3b82f6", bg: "#3b82f622",
    perms: [
      { key: "admin.schedule",              label: "Editar escala",                icon: "📅" },
      { key: "admin.liderEditaEscala",      label: "Líder pode editar escala (destrava bloqueio de líder)", icon: "✏️" },
      { key: "admin.fecharEscala",          label: "Fechar mês da escala",         icon: "🔒" },
      // v8.31.0: 'Agendar e fechar lote de freelas' deixou de ser checkbox — líder de área ganha automático.
      // Quem já tinha operational.freelasFechar marcado continua tendo (legacy fallback nas checagens).
      { key: "operational.reunioes",        label: "Gerir reuniões",               icon: "🗣️" },
      { key: "operational.trilhas",         label: "Gerir trilhas",                icon: "🎯" },
    ],
  },
  {
    id: "g_comunicacao", label: "📣 Comunicação", color: "#f59e0b", bg: "#f59e0b22",
    perms: [
      { key: "admin.comunicados",     label: "Criar comunicados",                icon: "📢" },
      { key: "admin.faq",             label: "Gerir FAQ",                        icon: "❓" },
      { key: "admin.recursosPastas",  label: "Criar/editar pastas em Recursos",  icon: "📁" },
      { key: "admin.recursosItens",   label: "Criar/editar itens em Recursos",   icon: "📄" },
    ],
  },
  {
    id: "g_config", label: "⚙️ Configurações", color: "#64748b", bg: "#64748b22",
    perms: [
      { key: "admin.config",      label: "Configurações do restaurante",                 icon: "⚙️" },
      { key: "admin.permissoes",  label: "Conceder permissões a outros (delegação)",     icon: "🔐" },
    ],
  },
  {
    id: "g_compras", label: "🛒 Compras / Operacional", color: "#7c9e5e", bg: "#7c9e5e22",
    perms: [
      { key: "admin.compras",            label: "Gerir compras (pedidos a fornecedores)", icon: "🛒" },
      { key: "admin.fornecedores",       label: "Gerir fornecedores",                     icon: "🏪" },
      { key: "admin.fichasTecnicas",     label: "Editar fichas técnicas",                 icon: "📋" },
      { key: "operational.contagens",    label: "Configurar contagens",                   icon: "📦" },
      { key: "operational.checklists",   label: "Configurar checklists",                  icon: "✅" },
      { key: "operational.temperaturas", label: "Configurar temperaturas",                icon: "🌡️" },
    ],
  },
  {
    id: "g_execucao", label: "✅ Execução Operacional", color: "#0ea5e9", bg: "#0ea5e922",
    perms: [
      { key: "operational.checklistsExec",   label: "Fazer checklists",        icon: "✅" },
      { key: "operational.checklistsReview", label: "Conferir checklists",     icon: "🔎" },
      { key: "operational.contagensExec",    label: "Fazer contagens",         icon: "📦" },
      { key: "operational.temperaturasExec", label: "Registrar temperaturas",  icon: "🌡️" },
      { key: "operational.freelasExec",      label: "Registrar turno de freela", icon: "🎒" },
      { key: "operational.fichasTecnicas",   label: "Consultar fichas técnicas", icon: "📖" },
    ],
  },
  {
    id: "g_reservas", label: "📅 Reservas de Mesa", color: "#dc2626", bg: "#dc262622",
    perms: [
      { key: "operational.reservasOperar",      label: "Operar reservas (criar respeitando lotação, confirmar, sentou, no-show)", icon: "📅" },
      { key: "operational.reservasOperarForce", label: "Forçar reserva mesmo lotado",      icon: "⚡" },
      { key: "operational.reservasGerenciar",   label: "Editar/cancelar reservas + ver histórico", icon: "✏️" },
      { key: "admin.reservasClientes",          label: "Gerir lista de clientes (CRM)",     icon: "👤" },
      { key: "admin.reservasConfig",            label: "Configurar salões, mesas, turnos, mensagens", icon: "⚙️" },
    ],
  },
  // Mantidos como legacy pra compat — não aparecem na matriz nova
  {
    id: "g_legacy", label: "(legacy)", color: "#999", bg: "#99922", legacy: true,
    perms: [
      { key: "special.isDP",    label: "(legacy) DP master",  icon: "🛂", legacy: true },
      { key: "special.isLider", label: "(legacy) Líder",      icon: "⭐", legacy: true },
      { key: "operational.escalas", label: "(legacy) Escalas operacional", icon: "📅", legacy: true },
    ],
  },
];

// v8.17.0: helper central pra checar permissão de uma pessoa.
// Faz fallback no flag `isDP` legado pra não quebrar comportamentos hoje:
// quem tem isDP=true continua tendo dpCanal/atravessarPrivado/reabrirEscala/fecharEscala automaticamente.
// eslint-disable-next-line no-unused-vars
function can(perms, key) {
  if (!perms) return false;
  const [group, name] = String(key).split(".");
  if (perms[group]?.[name] === true) return true;
  // Fallback legacy isDP → dpCanal/atravessarPrivado/reabrirEscala/fecharEscala
  if (perms.special?.isDP === true) {
    if (key === "admin.dpCanal" || key === "admin.atravessarPrivado"
     || key === "admin.reabrirEscala" || key === "admin.fecharEscala") return true;
  }
  return false;
}

function permGet(pessoa, restaurantId, key) {
  const [group, name] = key.split(".");
  return !!pessoa?.permissions?.[restaurantId]?.[group]?.[name];
}
function permSet(pessoa, restaurantId, key, value) {
  const [group, name] = key.split(".");
  const perms = { ...(pessoa.permissions || {}) };
  const byRest = { ...(perms[restaurantId] || {}) };
  const byGroup = { ...(byRest[group] || {}) };
  if (value) byGroup[name] = true; else delete byGroup[name];
  byRest[group] = byGroup;
  perms[restaurantId] = byRest;
  return { ...pessoa, permissions: perms };
}

// Profiles pré-prontos pra aplicar conjuntos de permissões com 1 clique.
// Cada profile define quais chaves devem virar `true` (todas as outras viram `false`).
// v7: perfis "operação" e "escritório" como defaults da nova IA.
const PERM_PROFILES = [
  { id: "escritorio_total", label: "Escritório", icon: "🏢", color: "#0284c7", desc: "Acesso total — Início + Operação + Planejamento + Escritório + Ajustes",
    keys: [
      "operational.escalas","operational.gorjetas","operational.trilhas","operational.reunioes",
      "operational.contagens","operational.compras","operational.checklists","operational.fichasTecnicas","operational.temperaturas",
      "admin.tips","admin.schedule","admin.employees","admin.roles","admin.vt","admin.comunicados","admin.faq","admin.config","admin.pessoas",
      "special.isDP",
    ],
  },
  { id: "operacao", label: "Operação", icon: "🔧", color: "#7c9e5e", desc: "Acessa Início + Operação + Planejamento (sem Escritório nem Ajustes)",
    keys: [
      "operational.escalas","operational.gorjetas","operational.trilhas","operational.reunioes",
      "operational.contagens","operational.checklists","operational.fichasTecnicas","operational.temperaturas",
    ],
  },
  { id: "gestor_pessoas", label: "Gestor de pessoas", icon: "🛂", color: "#0284c7", desc: "Gerencia equipe, cargos, comunicações (Escritório parcial)",
    keys: ["admin.employees","admin.roles","admin.pessoas","admin.comunicados","admin.faq"],
  },
  { id: "lider", label: "Líder de área", icon: "⭐", color: "#a855f7", desc: "Vê só funcionários das áreas que lidera",
    keys: ["special.isLider","operational.escalas","operational.reunioes"],
  },
  { id: "limpar", label: "Limpar tudo", icon: "🚫", color: "var(--text3)", desc: "Remove todas as permissões",
    keys: [],
  },
];

// Lista plana de TODAS as keys de permissões — usada pelos profiles e pelo master toggle
const ALL_PERM_KEYS = [
  "operational.escalas","operational.gorjetas","operational.trilhas","operational.reunioes",
  "operational.contagens","operational.compras","operational.checklists","operational.fichasTecnicas","operational.temperaturas",
  "operational.reservasOperar","operational.reservasOperarForce","operational.reservasGerenciar",
  "admin.tips","admin.schedule","admin.employees","admin.roles","admin.vt","admin.comunicados","admin.faq","admin.config","admin.pessoas",
  "admin.reservasClientes","admin.reservasConfig",
  "special.isDP","special.isLider",
];

// v8.21.0: helper pra checar se uma pessoa pode conferir checklists de um template específico.
// Regra: tem perm explícita "checklistsReview" OU é líder de uma área que cobre o template.
// Owner sempre pode (verificado externamente). Template "Geral" cai pra qualquer admin com checklists.
// eslint-disable-next-line no-unused-vars
function canReviewChecklist(pessoa, tpl, restaurantId) {
  if (!pessoa || !tpl) return false;
  const perms = pessoa.permissions?.[restaurantId];
  if (!perms) return false;
  // Perm explícita
  if (perms.operational?.checklistsReview === true) return true;
  // Líder com área compatível
  const isLider = perms.special?.isLider === true;
  const liderAreas = perms.special?.areas || [];
  if (isLider) {
    // Template "Geral" → qualquer líder confere
    if (!tpl.area || tpl.area === "Geral") return true;
    if (liderAreas.includes(tpl.area)) return true;
  }
  // Quem configura checklists também confere (admin do módulo)
  if (perms.operational?.checklists === true) return true;
  return false;
}

// Ícones e cores disponíveis pro editor de perfis customizados
const PROFILE_ICONS = ["👑","🛂","💰","⭐","🍳","🍷","📅","🎯","🌡️","📦","✅","📋","🚌","📢","❓","⚙️","👥","🏷️","🗂️","🔧","🚫","🛒","🔥","🧊","⚡","🎓","🎩"];
const PROFILE_COLORS = [
  { id: "amber",  label: "Dourado", color: "#d4a017" },
  { id: "blue",   label: "Azul",    color: "#0284c7" },
  { id: "green",  label: "Verde",   color: "#7c9e5e" },
  { id: "purple", label: "Roxo",    color: "#a855f7" },
  { id: "red",    label: "Vermelho",color: "#dc2626" },
  { id: "gray",   label: "Cinza",   color: "var(--text3)" },
];

// v8.20.0: nova view de permissões. Substitui PermissoesMatrix.
// Mostra pessoas agrupadas por nível (Owner / Admin / Líder / Equipe) com chips coloridos das perms ativas.
// "Editar permissões" → abre o cadastro da pessoa (via callback openPessoaForEdit).
function PermissoesResumo({ restaurantId, pessoas, owners, onOpenPessoa, mobileOnly }) {
  const [filter, setFilter] = useState("");
  const [collapsed, setCollapsed] = useState({}); // { nivel: bool }

  const ownerCpfSet = new Set((owners || []).map(o => (o.cpf || "").replace(/\D/g, "")).filter(Boolean));
  const restPessoas = (pessoas || []).filter(p => {
    if (!(p.restaurantIds || []).includes(restaurantId)) return false;
    const cpfD = (p.cpf || "").replace(/\D/g, "");
    if (cpfD && ownerCpfSet.has(cpfD)) return false;
    return true;
  });

  // Classifica cada pessoa em um nível
  function classify(p) {
    const perms = p.permissions?.[restaurantId] || { operational: {}, admin: {}, special: {} };
    const adminCount = Object.values(perms.admin || {}).filter(v => v === true).length;
    const isLider = perms.special?.isLider === true;
    const isTeam = !!p.isTeam?.[restaurantId];
    if (adminCount > 0 || perms.special?.isDP) return "admin";
    if (isLider) return "lider";
    if (isTeam || p.isFreela) return "empregado";
    return "empregado"; // default
  }

  // Filtro por nome
  const q = filter.trim().toLowerCase();
  const filtered = q ? restPessoas.filter(p => (p.name || "").toLowerCase().includes(q)) : restPessoas;

  const buckets = { admin: [], lider: [], empregado: [] };
  filtered.forEach(p => { buckets[classify(p)].push(p); });

  // Owners do sistema (acesso implícito a tudo) — só aparecem como info
  const ownersOfRest = (owners || []).filter(o => o.restaurantIds === undefined || (o.restaurantIds || []).includes(restaurantId) || o.isMaster);

  const NIVEIS = [
    { id: "owner",     label: "👑 Owners (acesso total implícito)", color: "#d4a017", bg: "#d4a01711", desc: "Acesso a tudo no sistema. Não editável aqui." },
    { id: "admin",     label: "🛂 Administradores", color: "#a855f7", bg: "#a855f711", desc: "Têm uma ou mais permissões administrativas." },
    { id: "lider",     label: "⭐ Líderes", color: "#f59e0b", bg: "#f59e0b11", desc: "Lideram uma ou mais áreas operacionais." },
    { id: "empregado", label: "👥 Equipe / Empregados", color: "#0ea5e9", bg: "#0ea5e911", desc: "Operacional sem permissões administrativas." },
  ];

  function renderPerssoaCard(p) {
    const perms = p.permissions?.[restaurantId] || { operational: {}, admin: {}, special: {} };
    const sp = perms.special || {};
    // Conta perms ativas por grupo (sem legacy)
    const activeByGroup = PERM_GROUPS.filter(g => !g.legacy).map(group => {
      const activeP = group.perms.filter(p2 => {
        if (p2.legacy) return false;
        const [g, n] = p2.key.split(".");
        return perms[g]?.[n] === true;
      });
      return { group, activeP };
    }).filter(x => x.activeP.length > 0);
    const isTeam = !!p.isTeam?.[restaurantId];
    return (
      <div key={p.id} style={{padding:"12px 14px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,display:"flex",flexDirection:"column",gap:8}}>
        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
          <span style={{color:"var(--text)",fontWeight:700,fontSize:14}}>{p.name}</span>
          {isTeam && <span style={{fontSize:10,padding:"1px 8px",borderRadius:10,background:"#7c9e5e22",color:"#15803d",fontWeight:700,letterSpacing:0.4}}>EQUIPE</span>}
          {p.isFreela && <span style={{fontSize:10,padding:"1px 8px",borderRadius:10,background:"#7c3aed22",color:"#7c3aed",fontWeight:700,letterSpacing:0.4}}>FREELA</span>}
          {p.linkedManagerId && <span style={{fontSize:10,padding:"1px 8px",borderRadius:10,background:"var(--ac)22",color:"var(--ac)",fontWeight:700,letterSpacing:0.4}}>GESTOR</span>}
          {sp.isLider && (sp.areas || []).length > 0 && (
            <span style={{fontSize:10,padding:"1px 8px",borderRadius:10,background:"#f59e0b22",color:"#b45309",fontWeight:700,letterSpacing:0.4}}>
              ⭐ Líder · {sp.areas.join(", ")}
            </span>
          )}
          <span style={{flex:1}}/>
          <button onClick={()=>onOpenPessoa(p.id)} style={{padding:"6px 12px",borderRadius:8,border:"1px solid var(--ac)44",background:"var(--ac)11",color:"var(--ac)",fontSize:12,fontWeight:700,cursor:"pointer"}}>
            ✏️ Editar permissões
          </button>
        </div>
        {activeByGroup.length === 0 ? (
          <div style={{fontSize:11,color:"var(--text3)",fontStyle:"italic"}}>
            Sem permissões ativas — só acessa o que é público (caixa de entrada, comunicados, etc.).
          </div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:4}}>
            {activeByGroup.map(({ group, activeP }) => (
              <div key={group.id} style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                <span style={{fontSize:10,fontWeight:700,color:group.color,minWidth:120,letterSpacing:0.3}}>{group.label}</span>
                <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                  {activeP.map(perm => (
                    <span key={perm.key} style={{fontSize:10,padding:"2px 8px",borderRadius:10,background:group.bg,color:group.color,fontWeight:600,border:`1px solid ${group.color}33`}}>
                      {perm.icon} {perm.label}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      {/* Cabeçalho com filtro */}
      <div style={{padding:"12px 14px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
        <div style={{flex:"1 1 280px",display:"flex",flexDirection:"column",gap:2}}>
          <span style={{fontSize:11,color:"var(--text3)",fontWeight:600}}>🔐 Permissões — visão por nível</span>
          <span style={{fontSize:11,color:"var(--text3)"}}>Pessoas agrupadas pelo seu nível mais alto. Clique em "Editar permissões" pra mudar individualmente.</span>
        </div>
        <input value={filter} onChange={e=>setFilter(e.target.value)} placeholder="🔎 Filtrar por nome..." style={{...S.input,maxWidth:260,fontSize:13}}/>
      </div>

      {/* Owners (info) */}
      {ownersOfRest.length > 0 && (
        <div style={{padding:"12px 14px",background:"#d4a01711",border:"1px solid #d4a01744",borderRadius:10}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
            <span style={{fontSize:13,color:"#92400e",fontWeight:700}}>👑 Owners (acesso total implícito)</span>
            <span style={{fontSize:11,color:"var(--text3)"}}>· {ownersOfRest.length} pessoa(s)</span>
          </div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {ownersOfRest.map(o => (
              <span key={o.id} style={{fontSize:12,padding:"4px 10px",borderRadius:14,background:"#fffbeb",border:"1px solid #d4a01744",color:"#92400e",fontWeight:600}}>
                {o.name}{o.isMaster ? " (Master)" : ""}
              </span>
            ))}
          </div>
        </div>
      )}

      {NIVEIS.filter(n => n.id !== "owner").map(nivel => {
        const list = buckets[nivel.id] || [];
        const isCollapsed = collapsed[nivel.id];
        return (
          <div key={nivel.id} style={{border:"1px solid var(--border)",borderRadius:12,background:nivel.bg,overflow:"hidden"}}>
            <div onClick={()=>setCollapsed(s=>({...s,[nivel.id]:!isCollapsed}))}
              style={{padding:"12px 14px",borderBottom:isCollapsed?"none":`1px solid ${nivel.color}22`,display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
              <span style={{fontSize:14,color:"var(--text)",fontWeight:700,flex:1}}>{nivel.label}</span>
              <span style={{fontSize:12,fontWeight:700,color:nivel.color,background:"#fff",padding:"2px 10px",borderRadius:12,border:`1px solid ${nivel.color}44`}}>{list.length}</span>
              <span style={{color:"var(--text3)",fontSize:13}}>{isCollapsed ? "▶" : "▼"}</span>
            </div>
            {!isCollapsed && (
              <div style={{padding:"10px 12px"}}>
                <div style={{fontSize:11,color:"var(--text3)",marginBottom:8,fontStyle:"italic"}}>{nivel.desc}</div>
                {list.length === 0 ? (
                  <div style={{padding:"20px",textAlign:"center",color:"var(--text3)",fontSize:13,background:"var(--card-bg)",borderRadius:10}}>
                    Nenhuma pessoa neste nível.
                  </div>
                ) : (
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {[...list].sort((a,b)=>(a.name||"").localeCompare(b.name||"")).map(renderPerssoaCard)}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// v8.20.0: PermissoesMatrix mantida no código mas não mais referenciada — substituída por PermissoesResumo.
// Será removida quando o resumo estabilizar em produção (Fase C+).
// eslint-disable-next-line no-unused-vars
function PermissoesMatrix({ restaurantId, pessoas, employees, managers, owners, permProfiles, onUpdate, mobileOnly }) {
  // permProfiles[restaurantId] aceita 2 formatos pra compat:
  //   LEGADO: Array<profile>            → custom apenas, sem hidden
  //   NOVO:   { custom: [], hiddenDefaults: [] }
  const ridProfilesRaw = permProfiles?.[restaurantId];
  const customProfiles = Array.isArray(ridProfilesRaw)
    ? ridProfilesRaw
    : (ridProfilesRaw?.custom || []);
  const hiddenDefaultIds = Array.isArray(ridProfilesRaw)
    ? []
    : (ridProfilesRaw?.hiddenDefaults || []);
  const visibleDefaults = PERM_PROFILES.filter(p => !hiddenDefaultIds.includes(p.id));
  const hiddenDefaults = PERM_PROFILES.filter(p => hiddenDefaultIds.includes(p.id));
  // Filtra owners do sistema — acesso implícito, não aparecem na matriz
  const ownerCpfSet = new Set((owners || []).map(o => (o.cpf || "").replace(/\D/g, "")).filter(Boolean));
  const restPessoas = (pessoas || []).filter(p => {
    if (!(p.restaurantIds || []).includes(restaurantId)) return false;
    const cpfD = (p.cpf || "").replace(/\D/g, "");
    if (cpfD && ownerCpfSet.has(cpfD)) return false;
    return true;
  });
  const [expanded, setExpanded] = useState({ secao_operacao: true, secao_planejamento: true, secao_escritorio: false, secao_ajustes: false, esp: false });
  const [filter, setFilter] = useState("");
  const [accessFilter, setAccessFilter] = useState("todos"); // todos | apptip_admin | apptip_op | mise | sem_perm
  const [sortMode, setSortMode] = useState("az"); // az | mais_perms | menos_perms
  const [liderAreasModal, setLiderAreasModal] = useState(null); // pessoaId
  const [profileMenuFor, setProfileMenuFor] = useState(null); // { pessoaId, top, right }
  const [profileManagerOpen, setProfileManagerOpen] = useState(false); // modal de gerenciar perfis
  const [editingProfile, setEditingProfile] = useState(null); // perfil sendo editado/criado

  // Helper que devolve o estado normalizado (formato novo) pra escrita
  function getRidProfilesObj() {
    const raw = permProfiles?.[restaurantId];
    if (Array.isArray(raw)) return { custom: raw, hiddenDefaults: [] };
    return { custom: raw?.custom || [], hiddenDefaults: raw?.hiddenDefaults || [] };
  }
  function persistRidProfiles(obj) {
    onUpdate("permProfiles", { ...(permProfiles || {}), [restaurantId]: obj });
  }

  // CRUD de perfis customizados
  function saveCustomProfile(profile, sourceDefaultId) {
    const ridObj = getRidProfilesObj();
    const exists = ridObj.custom.some(p => p.id === profile.id);
    const nextCustom = exists
      ? ridObj.custom.map(p => p.id === profile.id ? profile : p)
      : [...ridObj.custom, profile];
    // Se foi clonado de um default ("Editar" no default), oculta o default original do menu
    const nextHidden = sourceDefaultId && !ridObj.hiddenDefaults.includes(sourceDefaultId)
      ? [...ridObj.hiddenDefaults, sourceDefaultId]
      : ridObj.hiddenDefaults;
    persistRidProfiles({ custom: nextCustom, hiddenDefaults: nextHidden });
    setEditingProfile(null);
  }
  async function deleteCustomProfile(profileId) {
    if (!await appConfirm("Apagar este perfil personalizado?")) return;
    const ridObj = getRidProfilesObj();
    persistRidProfiles({ ...ridObj, custom: ridObj.custom.filter(p => p.id !== profileId) });
  }
  function duplicateProfile(source) {
    setEditingProfile({
      id: `prof_${Date.now().toString(36)}${Math.random().toString(36).slice(2,5)}`,
      label: source.label + " (cópia)",
      icon: source.icon,
      color: source.color,
      desc: source.desc,
      keys: [...source.keys],
    });
  }
  // "Editar" num default = clona pra custom + oculta o default ao salvar
  function editDefaultAsCustom(source) {
    setEditingProfile({
      id: `prof_${Date.now().toString(36)}${Math.random().toString(36).slice(2,5)}`,
      label: source.label,
      icon: source.icon,
      color: source.color,
      desc: source.desc,
      keys: [...source.keys],
      _sourceDefaultId: source.id, // marca pra ocultar o default ao salvar
    });
  }
  async function hideDefault(defaultId) {
    if (!await appConfirm("Ocultar este perfil padrão do menu? Você pode mostrar de volta a qualquer momento.")) return;
    const ridObj = getRidProfilesObj();
    if (ridObj.hiddenDefaults.includes(defaultId)) return;
    persistRidProfiles({ ...ridObj, hiddenDefaults: [...ridObj.hiddenDefaults, defaultId] });
  }
  function showDefault(defaultId) {
    const ridObj = getRidProfilesObj();
    persistRidProfiles({ ...ridObj, hiddenDefaults: ridObj.hiddenDefaults.filter(id => id !== defaultId) });
  }
  function newBlankProfile() {
    setEditingProfile({
      id: `prof_${Date.now().toString(36)}${Math.random().toString(36).slice(2,5)}`,
      label: "",
      icon: "🎯",
      color: "#0284c7",
      desc: "",
      keys: [],
    });
  }

  function saveLiderAreas(pessoaId, newAreas) {
    const updated = (pessoas || []).map(p => {
      if (p.id !== pessoaId) return p;
      const perms = { ...(p.permissions || {}) };
      const byRest = { ...(perms[restaurantId] || {}) };
      const special = { ...(byRest.special || {}) };
      special.areas = newAreas;
      byRest.special = special;
      perms[restaurantId] = byRest;
      return { ...p, permissions: perms };
    });
    onUpdate("pessoas", updated);
    // Sincroniza no manager legado
    const p = pessoas.find(x => x.id === pessoaId);
    if (p?.linkedManagerId) {
      onUpdate("managers", (managers || []).map(m => m.id === p.linkedManagerId ? { ...m, areas: newAreas } : m));
    }
  }

  function toggleGroup(gid) {
    setExpanded(prev => ({ ...prev, [gid]: !prev[gid] }));
  }
  function expandAll() { setExpanded({ secao_operacao: true, secao_planejamento: true, secao_escritorio: true, secao_ajustes: true, esp: true }); }
  function collapseAll() { setExpanded({ secao_operacao: false, secao_planejamento: false, secao_escritorio: false, secao_ajustes: false, esp: false }); }

  // Conta status agregado de um grupo de permissões para uma pessoa.
  // Retorna 0 (nenhuma), 1 (algumas), 2 (todas).
  function groupStateForPessoa(pessoa, group) {
    const total = group.perms.length;
    const checked = group.perms.filter(p => permGet(pessoa, restaurantId, p.key)).length;
    if (checked === 0) return 0;
    if (checked === total) return 2;
    return 1;
  }

  // Toggle todas as permissões de um grupo para uma pessoa.
  // Se já tem TODAS marcadas → desmarca tudo. Se tem zero ou parcial → marca tudo.
  function toggleGroupForPessoa(pessoa, group) {
    const state = groupStateForPessoa(pessoa, group);
    const targetValue = state !== 2; // se não está full, vira full; se está full, vira zero
    let updated = pessoa;
    group.perms.forEach(p => {
      updated = permSet(updated, restaurantId, p.key, targetValue);
    });
    persistPessoaWithLegacySync(pessoa, updated, group.perms.map(p => p.key), targetValue);
  }

  // Toggle TODAS as pessoas visíveis para um grupo.
  // Se TODAS as visíveis têm TODAS as permissões do grupo → desmarca tudo. Senão → marca tudo.
  function toggleGroupForAll(group, visibleList) {
    const allFullySet = visibleList.every(p => groupStateForPessoa(p, group) === 2);
    const targetValue = !allFullySet;
    let nextPessoas = pessoas;
    visibleList.forEach(pessoa => {
      let updated = pessoa;
      group.perms.forEach(p => {
        updated = permSet(updated, restaurantId, p.key, targetValue);
      });
      nextPessoas = nextPessoas.map(x => x.id === pessoa.id ? updated : x);
    });
    onUpdate("pessoas", nextPessoas);
    // Sincronia legado por simplicidade: dispara um togglePerm fictício (que faz a sync)
    // mas isso seria caro. Pra master toggle, só sincronizamos pessoas — telas legadas
    // se atualizam no próximo load.
  }

  // Aplica um profile pré-pronto a uma pessoa.
  function applyProfile(pessoa, profileKeys) {
    let updated = pessoa;
    ALL_PERM_KEYS.forEach(key => {
      const shouldBe = profileKeys.includes(key);
      updated = permSet(updated, restaurantId, key, shouldBe);
    });
    persistPessoaWithLegacySync(pessoa, updated, ALL_PERM_KEYS, null);
    setProfileMenuFor(null);
  }

  // Persiste uma pessoa atualizada e sincroniza nas estruturas legadas (employees + managers).
  function persistPessoaWithLegacySync(originalPessoa, updatedPessoa, _affectedKeys, _value) {
    onUpdate("pessoas", (pessoas || []).map(p => p.id === originalPessoa.id ? updatedPessoa : p));
    // Sincronia legada: re-deriva employees e managers da pessoa atualizada.
    const ridPerms = updatedPessoa.permissions?.[restaurantId] || {};
    const opPerms = ridPerms.operational || {};
    const adPerms = ridPerms.admin || {};
    const spPerms = ridPerms.special || {};
    if (originalPessoa.linkedEmployeeId) {
      const empId = originalPessoa.linkedEmployeeId;
      const newOpAreas = {};
      Object.keys(opPerms).forEach(k => { if (opPerms[k]) newOpAreas[k] = true; });
      onUpdate("employees", (employees || []).map(e => e.id === empId ? { ...e, operationalAreas: newOpAreas } : e));
    }
    if (originalPessoa.linkedManagerId) {
      const mgrId = originalPessoa.linkedManagerId;
      onUpdate("managers", (managers || []).map(m => m.id === mgrId ? {
        ...m,
        perms: { ...adPerms },
        isDP: !!spPerms.isDP,
        profile: spPerms.isLider ? "lider" : "custom",
      } : m));
    }
  }

  function togglePerm(pessoa, permKey) {
    const current = permGet(pessoa, restaurantId, permKey);
    const updated = permSet(pessoa, restaurantId, permKey, !current);
    const [group, name] = permKey.split(".");
    const value = !current;

    // Caso especial: marcar operational em pessoa sem linkedEmployeeId → auto-cria employee record
    // (assim a pessoa consegue entrar como Gestor Operacional, que exige um emp com operationalAreas)
    if (value === true && group === "operational" && !pessoa.linkedEmployeeId) {
      const newEmpId = `emp_auto_${pessoa.id}_${Date.now().toString(36).slice(-4)}`;
      const newEmployee = {
        id: newEmpId,
        restaurantId,
        name: pessoa.name,
        cpf: pessoa.cpf || "",
        pin: pessoa.pin || "0000",
        empCode: null,
        roleId: null,
        admission: today(),
        operationalAreas: { [name]: true },
        isFreela: false,
        inactive: false,
        linkedPessoaId: pessoa.id,
        createdAt: new Date().toISOString(),
      };
      onUpdate("employees", [...(employees || []), newEmployee]);
      const linkedPessoa = { ...updated, linkedEmployeeId: newEmpId, isTeam: { ...(pessoa.isTeam || {}), [restaurantId]: pessoa.isTeam?.[restaurantId] ?? false } };
      onUpdate("pessoas", (pessoas || []).map(p => p.id === pessoa.id ? linkedPessoa : p));
      return;
    }

    // Caso especial: marcar admin/special em pessoa sem linkedManagerId → auto-cria manager record
    // (assim as telas antigas de ManagerPortal conseguem renderizar quando a pessoa logar como admin)
    if (value === true && (group === "admin" || group === "special") && !pessoa.linkedManagerId) {
      const newMgrId = `mgr_auto_${pessoa.id}_${Date.now().toString(36).slice(-4)}`;
      const newManager = {
        id: newMgrId,
        name: pessoa.name,
        cpf: pessoa.cpf || "",
        pin: pessoa.pin || "0000",
        mustChangePin: !!pessoa.mustChangePin,
        restaurantIds: [restaurantId],
        perms: group === "admin" ? { [name]: true } : {},
        profile: (group === "special" && name === "isLider") ? "lider" : "custom",
        areas: [],
        isDP: !!(group === "special" && name === "isDP"),
        isMaster: false,
        linkedPessoaId: pessoa.id,
        createdAt: new Date().toISOString(),
      };
      onUpdate("managers", [...(managers || []), newManager]);
      const linkedPessoa = { ...updated, linkedManagerId: newMgrId };
      onUpdate("pessoas", (pessoas || []).map(p => p.id === pessoa.id ? linkedPessoa : p));
      return;
    }

    // Atualiza pessoa normalmente
    onUpdate("pessoas", (pessoas || []).map(p => p.id === pessoa.id ? updated : p));

    // Sincroniza no legado para compatibilidade com telas antigas
    if (group === "operational" && pessoa.linkedEmployeeId) {
      const empId = pessoa.linkedEmployeeId;
      const newEmps = (employees || []).map(e => {
        if (e.id !== empId) return e;
        // IMPORTANTE: Firestore rejeita undefined. Quando desmarca (value=false),
        // DELETAMOS a chave em vez de setar undefined.
        const newOpAreas = { ...(e.operationalAreas || {}) };
        if (value) newOpAreas[name] = true;
        else delete newOpAreas[name];
        return { ...e, operationalAreas: newOpAreas };
      });
      onUpdate("employees", newEmps);
    } else if (group === "admin" && pessoa.linkedManagerId) {
      const mgrId = pessoa.linkedManagerId;
      const newMgrs = (managers || []).map(m => m.id === mgrId ? {
        ...m,
        perms: { ...(m.perms || {}), [name]: value },
      } : m);
      onUpdate("managers", newMgrs);
    } else if (group === "special" && pessoa.linkedManagerId) {
      const mgrId = pessoa.linkedManagerId;
      const newMgrs = (managers || []).map(m => {
        if (m.id !== mgrId) return m;
        const next = { ...m };
        if (name === "isDP") next.isDP = value;
        if (name === "isLider") {
          next.profile = value ? "lider" : "custom";
        }
        return next;
      });
      onUpdate("managers", newMgrs);
    }
  }

  const q = filter.trim().toLowerCase();
  let filtered = q ? restPessoas.filter(p => p.name.toLowerCase().includes(q)) : restPessoas;
  // Filtro por área de acesso
  if (accessFilter !== "todos") {
    filtered = filtered.filter(p => {
      const ridPerms = p.permissions?.[restaurantId] || {};
      const op = ridPerms.operational || {};
      const ad = ridPerms.admin || {};
      const sp = ridPerms.special || {};
      const opCount = Object.values(op).filter(Boolean).length;
      const adCount = Object.values(ad).filter(Boolean).length;
      const spCount = Object.values(sp).filter(Boolean).length;
      const totalCount = opCount + adCount + spCount;
      if (accessFilter === "sem_perm") return totalCount === 0;
      if (accessFilter === "apptip_admin") return adCount > 0;
      if (accessFilter === "apptip_op") return op.escalas || op.gorjetas || op.trilhas || op.reunioes;
      if (accessFilter === "mise") return op.contagens || op.compras || op.checklists || op.fichasTecnicas || op.temperaturas;
      return true;
    });
  }
  // Ordenação
  const countAllPerms = (p) => {
    const ridPerms = p.permissions?.[restaurantId] || {};
    return Object.values(ridPerms.operational || {}).filter(Boolean).length
         + Object.values(ridPerms.admin || {}).filter(Boolean).length
         + Object.values(ridPerms.special || {}).filter(Boolean).length;
  };
  const sorted = [...filtered].sort((a,b) => {
    if (sortMode === "mais_perms") return countAllPerms(b) - countAllPerms(a) || a.name.localeCompare(b.name);
    if (sortMode === "menos_perms") return countAllPerms(a) - countAllPerms(b) || a.name.localeCompare(b.name);
    return a.name.localeCompare(b.name);
  });

  // Header sticky precisa de bg 100% opaco pra não ficar transparente.
  // --sticky-bg é definido em index.html como #ffffff (light) / #211c16 (dark) — totalmente opaco.
  // Os g.bg dos grupos tinham alpha (var(--ac)22) que deixava conteúdo passar — não usar no sticky.
  const HEADER_BG = "var(--sticky-bg)";
  const HEADER_SHADOW = "0 2px 4px rgba(0,0,0,0.08)";

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8,marginBottom:12}}>
        <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?16:18}}>🛂 Permissões</h3>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          <button onClick={()=>setProfileManagerOpen(true)} style={{...S.btnSecondary,fontSize:11,padding:"5px 10px",color:"var(--ac)",borderColor:"var(--ac)44"}}>✏️ Gerenciar perfis</button>
          <button onClick={expandAll} style={{...S.btnSecondary,fontSize:11,padding:"5px 10px"}}>Expandir todos</button>
          <button onClick={collapseAll} style={{...S.btnSecondary,fontSize:11,padding:"5px 10px"}}>Colapsar todos</button>
        </div>
      </div>

      {/* Linha de filtros — busca + área de acesso + ordenação */}
      <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap",alignItems:"center"}}>
        <input value={filter} onChange={e=>setFilter(e.target.value)} placeholder="🔍 Buscar pessoa..." style={{...S.input,maxWidth:280,flex:"1 1 200px"}} />
        <select value={accessFilter} onChange={e=>setAccessFilter(e.target.value)}
          style={{...S.input,maxWidth:200,fontSize:12,padding:"7px 10px",cursor:"pointer"}}>
          <option value="todos">📋 Todos</option>
          <option value="apptip_admin">🛂 Com Master</option>
          <option value="apptip_op">📅 Com Operacional</option>
          <option value="mise">📦 Com Operacional</option>
          <option value="sem_perm">🚫 Sem permissões</option>
        </select>
        <select value={sortMode} onChange={e=>setSortMode(e.target.value)}
          style={{...S.input,maxWidth:170,fontSize:12,padding:"7px 10px",cursor:"pointer"}}>
          <option value="az">↕️ A-Z</option>
          <option value="mais_perms">⬆ Mais permissões</option>
          <option value="menos_perms">⬇ Menos permissões</option>
        </select>
      </div>

      {sorted.length === 0 ? (
        <div style={{padding:"32px 16px",textAlign:"center",color:"var(--text3)",fontSize:14,background:"var(--card-bg)",borderRadius:12,border:"1px solid var(--border)"}}>
          {restPessoas.length === 0 ? "Nenhuma pessoa cadastrada. Vá na aba Pessoas para começar." : "Nenhuma pessoa encontrada com esses filtros."}
        </div>
      ) : (
        <div className="permissoes-scroll-area" style={{overflowX:"auto",overflowY:"auto",maxHeight:"calc(100vh - 280px)",WebkitOverflowScrolling:"touch",border:"1px solid var(--border)",borderRadius:12,background:"var(--card-bg)"}}>
          <table style={{width:"100%",borderCollapse:"separate",borderSpacing:0,fontSize:12}}>
            <thead>
              {/* Linha 1 — coluna Pessoa + ícones por permissão (ou nome do grupo se colapsado) */}
              <tr>
                <th style={{padding:"10px 12px",textAlign:"left",color:"var(--text)",fontWeight:700,position:"sticky",left:0,top:0,background:HEADER_BG,zIndex:13,minWidth:180,boxShadow:HEADER_SHADOW,borderBottom:"1px solid var(--border)"}}>
                  <div>Pessoa</div>
                  <div style={{fontSize:10,color:"var(--text3)",fontWeight:500,marginTop:2}}>{sorted.length} de {restPessoas.length}</div>
                </th>
                {PERM_GROUPS.map(g => (
                  expanded[g.id] ? (
                    g.perms.map(p => (
                      <th key={p.key} style={{padding:"8px 6px",textAlign:"center",color:"var(--text)",fontWeight:600,minWidth:62,background:HEADER_BG,borderLeft:"1px solid var(--border)",borderBottom:`3px solid ${g.color}`,position:"sticky",top:0,zIndex:11,boxShadow:HEADER_SHADOW}} title={`${g.label} · ${p.label}`}>
                        <div style={{fontSize:14,marginBottom:2}}>{p.icon}</div>
                        <div style={{fontSize:9,color:g.color,fontWeight:700,textTransform:"uppercase",letterSpacing:0.3,lineHeight:1.1}}>{p.label.length > 10 ? p.label.slice(0,10)+'…' : p.label}</div>
                      </th>
                    ))
                  ) : (
                    <th key={g.id} onClick={()=>toggleGroup(g.id)} style={{padding:"8px 10px",textAlign:"center",cursor:"pointer",color:g.color,fontWeight:700,fontSize:11,textTransform:"uppercase",letterSpacing:0.4,borderLeft:"1px solid var(--border)",borderBottom:`3px solid ${g.color}`,background:HEADER_BG,minWidth:100,position:"sticky",top:0,zIndex:11,boxShadow:HEADER_SHADOW}} title={`Clique para expandir ${g.label}`}>
                      ▶ {g.label}
                    </th>
                  )
                ))}
              </tr>
              {/* Linha 2 — botão de colapsar grupo + master toggle "marcar tudo do grupo pra todos" */}
              <tr>
                <th style={{padding:"6px 12px",textAlign:"left",color:"var(--text3)",fontWeight:600,fontSize:10,position:"sticky",left:0,top:50,background:HEADER_BG,zIndex:13,textTransform:"uppercase",letterSpacing:0.4,borderBottom:"1px solid var(--border)"}}>
                  Master
                </th>
                {PERM_GROUPS.map(g => {
                  if (!expanded[g.id]) {
                    return <th key={g.id+"_stub"} style={{borderLeft:"1px solid var(--border)",borderBottom:"1px solid var(--border)",background:HEADER_BG,position:"sticky",top:50,zIndex:11}}></th>;
                  }
                  // Calcula estado agregado: se TODAS visíveis têm TUDO marcado, mostra ☑; se ninguém tem nada, ☐; se misto, ⊟
                  const allFull = sorted.length > 0 && sorted.every(p => groupStateForPessoa(p, g) === 2);
                  const anyHas = sorted.some(p => groupStateForPessoa(p, g) > 0);
                  const masterIcon = allFull ? "☑" : anyHas ? "⊟" : "☐";
                  return (
                    <th key={g.id+"_master"} colSpan={g.perms.length} style={{padding:"4px 8px",borderLeft:"1px solid var(--border)",borderBottom:"1px solid var(--border)",background:HEADER_BG,textAlign:"center",position:"sticky",top:50,zIndex:11}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                        <button onClick={()=>toggleGroupForAll(g, sorted)}
                          title={`${allFull ? "Desmarcar" : "Marcar"} todas as permissões deste grupo para todos visíveis (${sorted.length} pessoa${sorted.length===1?"":"s"})`}
                          style={{background:allFull?g.color:"transparent",color:allFull?"#fff":g.color,border:`1px solid ${g.color}`,borderRadius:6,padding:"3px 10px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:"'DM Mono',monospace",lineHeight:1.2}}>
                          {masterIcon} marcar todos
                        </button>
                        <button onClick={()=>toggleGroup(g.id)} style={{background:"none",border:"none",cursor:"pointer",color:g.color,fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}} title="Colapsar grupo">
                          ▼ colapsar
                        </button>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sorted.map((p, idx) => {
                // Cor da linha (zebra). Com border-collapse:separate, o bg precisa ir em cada <td>
                // (bg em <tr> é ignorado), então definimos uma var local pra reusar.
                const rowBg = idx % 2 === 0 ? "var(--card-bg)" : "var(--bg2)";
                const rowBorder = "1px solid var(--border)";
                return (
                <tr key={p.id}>
                  <td style={{padding:"8px 12px",color:"var(--text)",fontWeight:500,position:"sticky",left:0,background:rowBg,zIndex:5,minWidth:180,borderTop:rowBorder,boxShadow:"2px 0 4px rgba(0,0,0,0.04)"}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.name}</div>
                        {(!p.linkedEmployeeId && !p.linkedManagerId) && <div style={{fontSize:10,color:"var(--text3)",marginTop:2}}>não vinculada ao legado</div>}
                      </div>
                      <button onClick={(e)=>{
                          if (profileMenuFor?.pessoaId === p.id) { setProfileMenuFor(null); return; }
                          // Calcula posição via getBoundingClientRect pra renderizar como `position: fixed`
                          // (sticky <td> cria stacking context que prende dropdown atrás).
                          // Se não couber abaixo do botão, flipa pra abrir acima.
                          const r = e.currentTarget.getBoundingClientRect();
                          const MENU_H_EST = 380; // altura aproximada do menu (7 opções + header)
                          const spaceBelow = window.innerHeight - r.bottom;
                          const openUp = spaceBelow < MENU_H_EST && r.top > MENU_H_EST;
                          setProfileMenuFor({
                            pessoaId: p.id,
                            top: openUp ? null : r.bottom + 4,
                            bottom: openUp ? window.innerHeight - r.top + 4 : null,
                            right: window.innerWidth - r.right,
                          });
                        }}
                        title="Aplicar perfil pré-pronto"
                        style={{background:"none",border:"1px solid var(--border)",borderRadius:6,padding:"3px 8px",cursor:"pointer",fontSize:10,color:"var(--text3)",fontWeight:600,whiteSpace:"nowrap"}}>
                        ⚡ Perfil
                      </button>
                    </div>
                  </td>
                  {PERM_GROUPS.map(g => {
                    if (!expanded[g.id]) {
                      const state = groupStateForPessoa(p, g);
                      const count = g.perms.filter(perm => permGet(p, restaurantId, perm.key)).length;
                      const cellLabel = state === 2 ? "todos" : state === 1 ? `${count}/${g.perms.length}` : "—";
                      return (
                        <td key={g.id+"_count"} onClick={()=>toggleGroupForPessoa(p, g)}
                            title={`${state === 2 ? "Desmarcar" : "Marcar"} todas as permissões de ${g.label} para ${p.name}\n(clique no nome do grupo no header pra expandir)`}
                            style={{padding:"8px",textAlign:"center",color:count>0?g.color:"var(--text3)",fontWeight:count>0?700:400,fontFamily:"'DM Mono',monospace",fontSize:12,cursor:"pointer",borderLeft:"1px solid var(--border)",borderTop:rowBorder,background:count>0?g.bg:rowBg}}>
                          {cellLabel}
                        </td>
                      );
                    }
                    return g.perms.map(perm => {
                      const checked = permGet(p, restaurantId, perm.key);
                      const isLiderKey = perm.key === "special.isLider";
                      const areas = (p.permissions?.[restaurantId]?.special?.areas) || [];
                      return (
                        <td key={perm.key} style={{padding:"6px",textAlign:"center",borderLeft:"1px solid var(--border)",borderTop:rowBorder,background:rowBg}}>
                          <input type="checkbox" checked={checked} onChange={()=>togglePerm(p, perm.key)} style={{cursor:"pointer",width:16,height:16,accentColor:g.color}} />
                          {isLiderKey && checked && (
                            <div style={{marginTop:4}}>
                              <button onClick={()=>setLiderAreasModal(p.id)}
                                style={{background:"none",border:`1px solid ${g.color}66`,borderRadius:6,padding:"2px 6px",cursor:"pointer",fontSize:10,color:g.color,fontWeight:600,fontFamily:"'DM Sans',sans-serif"}}
                                title="Editar áreas que esta pessoa lidera">
                                {areas.length > 0 ? `${areas.length} área${areas.length===1?"":"s"}` : "+ áreas"}
                              </button>
                            </div>
                          )}
                        </td>
                      );
                    });
                  })}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div style={{marginTop:10,fontSize:11,color:"var(--text3)",lineHeight:1.5}}>
        💡 <strong>Atalhos:</strong> ⚡ Perfil aplica conjunto pré-pronto. Header do grupo expandido tem "marcar todos" pra grupo inteiro. Célula colapsada (com "todos"/"3/5"/"—") clicável marca/desmarca o grupo só pra aquela pessoa.
      </div>

      {/* DROPDOWN de Profile — renderizado fora da tabela com position:fixed
          pra escapar dos stacking contexts criados pelas <td> sticky.        */}
      {profileMenuFor && (() => {
        const targetPessoa = restPessoas.find(x => x.id === profileMenuFor.pessoaId);
        if (!targetPessoa) { setTimeout(()=>setProfileMenuFor(null),0); return null; }
        return (
          <>
            {/* Backdrop pra fechar ao clicar fora */}
            <div onClick={()=>setProfileMenuFor(null)} style={{position:"fixed",inset:0,zIndex:9000,background:"transparent"}} />
            {/* Menu — top OU bottom, dependendo do espaço disponível na tela */}
            <div style={{position:"fixed",top:profileMenuFor.top??undefined,bottom:profileMenuFor.bottom??undefined,right:profileMenuFor.right,zIndex:9001,background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,boxShadow:"0 8px 28px rgba(0,0,0,0.22)",minWidth:240,maxHeight:"calc(100vh - 40px)",overflowY:"auto",padding:6,fontFamily:"'DM Sans',sans-serif"}}>
              <div style={{padding:"6px 10px 8px",borderBottom:"1px solid var(--border)",marginBottom:4}}>
                <div style={{fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Aplicar perfil em</div>
                <div style={{fontSize:13,color:"var(--text)",fontWeight:700,marginTop:1}}>{targetPessoa.name}</div>
              </div>
              {customProfiles.length > 0 && (
                <div style={{padding:"4px 10px 2px",fontSize:9,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Personalizados</div>
              )}
              {customProfiles.map(prof => (
                <button key={prof.id} onClick={()=>applyProfile(targetPessoa, prof.keys)}
                  style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",background:"none",border:"none",borderRadius:6,cursor:"pointer",fontSize:12,color:"var(--text)",width:"100%",textAlign:"left",fontFamily:"'DM Sans',sans-serif"}}
                  onMouseEnter={e=>e.currentTarget.style.background="var(--bg2)"}
                  onMouseLeave={e=>e.currentTarget.style.background="none"}>
                  <span style={{fontSize:14}}>{prof.icon}</span>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:600,color:prof.color,fontSize:11}}>{prof.label}</div>
                    <div style={{fontSize:10,color:"var(--text3)",marginTop:1,lineHeight:1.2}}>{prof.desc || `${prof.keys.length} permissões`}</div>
                  </div>
                </button>
              ))}
              {customProfiles.length > 0 && visibleDefaults.length > 0 && (
                <div style={{padding:"4px 10px 2px",marginTop:4,borderTop:"1px solid var(--border)",fontSize:9,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Padrões</div>
              )}
              {visibleDefaults.map(prof => (
                <button key={prof.id} onClick={()=>applyProfile(targetPessoa, prof.keys)}
                  style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",background:"none",border:"none",borderRadius:6,cursor:"pointer",fontSize:12,color:"var(--text)",width:"100%",textAlign:"left",fontFamily:"'DM Sans',sans-serif"}}
                  onMouseEnter={e=>e.currentTarget.style.background="var(--bg2)"}
                  onMouseLeave={e=>e.currentTarget.style.background="none"}>
                  <span style={{fontSize:14}}>{prof.icon}</span>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:600,color:prof.color,fontSize:11}}>{prof.label}</div>
                    <div style={{fontSize:10,color:"var(--text3)",marginTop:1,lineHeight:1.2}}>{prof.desc}</div>
                  </div>
                </button>
              ))}
            </div>
          </>
        );
      })()}

      {/* MODAL: gerenciar perfis (lista + criar/duplicar/editar/apagar) */}
      {profileManagerOpen && !editingProfile && (
        <div onClick={()=>setProfileManagerOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:9500,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",borderRadius:14,maxWidth:560,width:"100%",maxHeight:"85vh",display:"flex",flexDirection:"column",boxShadow:"0 10px 40px rgba(0,0,0,0.3)"}}>
            <div style={{padding:"16px 20px",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div>
                <div style={{fontSize:11,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Permissões</div>
                <div style={{fontSize:16,color:"var(--text)",fontWeight:700,marginTop:2}}>✏️ Gerenciar perfis</div>
              </div>
              <button onClick={()=>setProfileManagerOpen(false)} style={{background:"none",border:"none",fontSize:22,color:"var(--text3)",cursor:"pointer",padding:"0 4px"}}>×</button>
            </div>
            <div style={{padding:"14px 20px",overflowY:"auto",flex:1}}>
              {/* Personalizados */}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <div style={{fontSize:11,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Perfis personalizados ({customProfiles.length})</div>
                <button onClick={newBlankProfile} style={{...S.btnPrimary,fontSize:11,padding:"5px 12px"}}>+ Novo perfil</button>
              </div>
              {customProfiles.length === 0 ? (
                <div style={{padding:"16px",textAlign:"center",color:"var(--text3)",fontSize:12,background:"var(--bg2)",borderRadius:10,border:"1px dashed var(--border)",marginBottom:18}}>
                  Nenhum perfil personalizado ainda. Crie um do zero ou duplique um padrão abaixo.
                </div>
              ) : (
                <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:18}}>
                  {customProfiles.map(prof => (
                    <div key={prof.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:"var(--bg2)",borderRadius:10,border:"1px solid var(--border)"}}>
                      <span style={{fontSize:18}}>{prof.icon}</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:700,color:prof.color}}>{prof.label}</div>
                        <div style={{fontSize:10,color:"var(--text3)",marginTop:1}}>{prof.desc || `${prof.keys.length} permissões`}</div>
                      </div>
                      <button onClick={()=>setEditingProfile(prof)} style={{...S.btnSecondary,fontSize:10,padding:"4px 10px"}}>✏️ Editar</button>
                      <button onClick={()=>deleteCustomProfile(prof.id)} style={{...S.btnSecondary,fontSize:10,padding:"4px 10px",color:"var(--red)",borderColor:"var(--red)44"}}>🗑️</button>
                    </div>
                  ))}
                </div>
              )}

              {/* Padrões visíveis no menu */}
              <div style={{fontSize:11,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,marginBottom:10}}>Perfis padrão ({visibleDefaults.length} no menu)</div>
              <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:hiddenDefaults.length > 0 ? 18 : 0}}>
                {visibleDefaults.map(prof => (
                  <div key={prof.id} style={{display:"flex",alignItems:"center",gap:8,padding:"10px 12px",background:"var(--bg2)",borderRadius:10,border:"1px solid var(--border)",flexWrap:"wrap"}}>
                    <span style={{fontSize:18}}>{prof.icon}</span>
                    <div style={{flex:"1 1 200px",minWidth:120}}>
                      <div style={{fontSize:13,fontWeight:700,color:prof.color}}>{prof.label}</div>
                      <div style={{fontSize:10,color:"var(--text3)",marginTop:1}}>{prof.desc}</div>
                    </div>
                    <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                      <button onClick={()=>duplicateProfile(prof)} style={{...S.btnSecondary,fontSize:10,padding:"4px 10px"}} title="Cria um novo perfil custom como cópia, mantém o original">📋 Duplicar</button>
                      <button onClick={()=>editDefaultAsCustom(prof)} style={{...S.btnSecondary,fontSize:10,padding:"4px 10px"}} title="Abre o editor com este perfil — ao salvar, vira custom e o original some do menu">✏️ Editar</button>
                      <button onClick={()=>hideDefault(prof.id)} style={{...S.btnSecondary,fontSize:10,padding:"4px 10px",color:"var(--text3)"}} title="Esconde do menu sem perder — pode mostrar de volta">🚫 Ocultar</button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Padrões ocultos */}
              {hiddenDefaults.length > 0 && (
                <>
                  <div style={{fontSize:11,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,marginBottom:10}}>Padrões ocultos ({hiddenDefaults.length})</div>
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {hiddenDefaults.map(prof => (
                      <div key={prof.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:"var(--bg2)",borderRadius:10,border:"1px dashed var(--border)",opacity:0.6}}>
                        <span style={{fontSize:18,filter:"grayscale(0.5)"}}>{prof.icon}</span>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:13,fontWeight:700,color:prof.color,textDecoration:"line-through"}}>{prof.label}</div>
                          <div style={{fontSize:10,color:"var(--text3)",marginTop:1}}>{prof.desc}</div>
                        </div>
                        <button onClick={()=>showDefault(prof.id)} style={{...S.btnSecondary,fontSize:10,padding:"4px 10px",color:"var(--ac)",borderColor:"var(--ac)44"}}>👁️ Mostrar</button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL: editor de perfil (criar ou editar) */}
      {editingProfile && (
        <div onClick={()=>setEditingProfile(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:9600,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",borderRadius:14,maxWidth:540,width:"100%",maxHeight:"90vh",display:"flex",flexDirection:"column",boxShadow:"0 10px 40px rgba(0,0,0,0.3)"}}>
            <div style={{padding:"16px 20px",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div style={{fontSize:15,color:"var(--text)",fontWeight:700}}>
                <span style={{fontSize:18,marginRight:6}}>{editingProfile.icon}</span>
                {editingProfile.label || "Novo perfil"}
              </div>
              <button onClick={()=>setEditingProfile(null)} style={{background:"none",border:"none",fontSize:22,color:"var(--text3)",cursor:"pointer",padding:"0 4px"}}>×</button>
            </div>
            <div style={{padding:"14px 20px",overflowY:"auto",flex:1,display:"flex",flexDirection:"column",gap:14}}>
              <div>
                <label style={S.label}>Nome do perfil</label>
                <input value={editingProfile.label} onChange={e=>setEditingProfile({...editingProfile, label:e.target.value})} placeholder="ex: Gestor de Salão" style={S.input} />
              </div>
              <div>
                <label style={S.label}>Ícone</label>
                <div style={{display:"flex",flexWrap:"wrap",gap:4,padding:8,background:"var(--bg2)",borderRadius:10,border:"1px solid var(--border)"}}>
                  {PROFILE_ICONS.map(icon => (
                    <button key={icon} onClick={()=>setEditingProfile({...editingProfile, icon})}
                      style={{padding:"6px 8px",background:editingProfile.icon===icon?"var(--ac)22":"transparent",border:editingProfile.icon===icon?"1px solid var(--ac)":"1px solid transparent",borderRadius:6,cursor:"pointer",fontSize:18}}>
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={S.label}>Cor</label>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {PROFILE_COLORS.map(c => (
                    <button key={c.id} onClick={()=>setEditingProfile({...editingProfile, color:c.color})}
                      style={{display:"flex",alignItems:"center",gap:6,padding:"6px 10px",background:editingProfile.color===c.color?c.color+"22":"var(--bg2)",border:`1px solid ${editingProfile.color===c.color?c.color:"var(--border)"}`,borderRadius:8,cursor:"pointer",fontSize:11,fontWeight:600,color:c.color}}>
                      <span style={{display:"inline-block",width:10,height:10,borderRadius:5,background:c.color}}></span>
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={S.label}>Descrição (curta)</label>
                <input value={editingProfile.desc} onChange={e=>setEditingProfile({...editingProfile, desc:e.target.value})} placeholder="ex: Gerencia salão, escala e gorjetas" style={S.input} />
              </div>
              <div>
                <label style={S.label}>Permissões neste perfil ({editingProfile.keys.length} marcadas)</label>
                <div style={{padding:10,background:"var(--bg2)",borderRadius:10,border:"1px solid var(--border)",display:"flex",flexDirection:"column",gap:10}}>
                  {PERM_GROUPS.map(g => (
                    <div key={g.id}>
                      <div style={{fontSize:10,color:g.color,fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,marginBottom:6,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                        <span>{g.label}</span>
                        <button onClick={()=>{
                          const allKeys = g.perms.map(p => p.key);
                          const allChecked = allKeys.every(k => editingProfile.keys.includes(k));
                          const newKeys = allChecked
                            ? editingProfile.keys.filter(k => !allKeys.includes(k))
                            : [...new Set([...editingProfile.keys, ...allKeys])];
                          setEditingProfile({...editingProfile, keys:newKeys});
                        }} style={{background:"none",border:"none",color:g.color,fontSize:10,fontWeight:700,cursor:"pointer",textDecoration:"underline"}}>
                          {g.perms.every(p => editingProfile.keys.includes(p.key)) ? "desmarcar grupo" : "marcar grupo"}
                        </button>
                      </div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                        {g.perms.map(perm => {
                          const checked = editingProfile.keys.includes(perm.key);
                          return (
                            <label key={perm.key} style={{display:"flex",alignItems:"center",gap:5,padding:"5px 9px",background:checked?g.color+"22":"var(--card-bg)",border:`1px solid ${checked?g.color:"var(--border)"}`,borderRadius:8,cursor:"pointer",fontSize:11,color:checked?g.color:"var(--text2)",fontWeight:checked?600:400}}>
                              <input type="checkbox" checked={checked} style={{cursor:"pointer",accentColor:g.color}}
                                onChange={()=>{
                                  const newKeys = checked
                                    ? editingProfile.keys.filter(k => k !== perm.key)
                                    : [...editingProfile.keys, perm.key];
                                  setEditingProfile({...editingProfile, keys:newKeys});
                                }} />
                              {perm.icon} {perm.label}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div style={{padding:"12px 20px",borderTop:"1px solid var(--border)",display:"flex",gap:8,justifyContent:"flex-end",background:"var(--bg2)"}}>
              <button onClick={()=>setEditingProfile(null)} style={{...S.btnSecondary,fontSize:12,padding:"8px 16px"}}>Cancelar</button>
              <button onClick={()=>{
                if (!editingProfile.label.trim()) { alert("Nome é obrigatório"); return; }
                if (editingProfile.keys.length === 0) { alert("Selecione pelo menos 1 permissão"); return; }
                // Remove o marker antes de salvar (não vai pro Firestore)
                const { _sourceDefaultId, ...cleanProfile } = editingProfile;
                saveCustomProfile(cleanProfile, _sourceDefaultId);
              }} style={{...S.btnPrimary,fontSize:12,padding:"8px 18px"}}>💾 Salvar perfil</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: editor de áreas do Líder */}
      {liderAreasModal && (() => {
        const p = restPessoas.find(x => x.id === liderAreasModal);
        if (!p) { setTimeout(()=>setLiderAreasModal(null),0); return null; }
        const currentAreas = p.permissions?.[restaurantId]?.special?.areas ?? [];
        return (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
            <div style={{background:"var(--card-bg)",borderRadius:14,maxWidth:440,width:"100%",boxShadow:"0 10px 40px rgba(0,0,0,0.3)"}}>
              <div style={{padding:"14px 18px",borderBottom:"1px solid var(--border)"}}>
                <div style={{fontSize:11,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Áreas lideradas</div>
                <div style={{fontSize:15,color:"var(--text)",fontWeight:700,marginTop:2}}>⭐ {p.name}</div>
              </div>
              <div style={{padding:"14px 18px"}}>
                <p style={{color:"var(--text3)",fontSize:12,lineHeight:1.5,margin:"0 0 14px"}}>
                  Selecione as áreas que esta pessoa lidera. Como Líder, ela verá e atuará apenas em funcionários dessas áreas (filtro automático nas telas de Escala, Equipe, etc.).
                </p>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {AREAS.map(area => {
                    const checked = currentAreas.includes(area);
                    return (
                      <label key={area} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:checked?AREA_COLORS[area]+"22":"var(--bg2)",border:`1px solid ${checked?AREA_COLORS[area]:"var(--border)"}`,borderRadius:10,cursor:"pointer"}}>
                        <input type="checkbox" checked={checked}
                          onChange={e => {
                            const next = e.target.checked
                              ? [...currentAreas, area]
                              : currentAreas.filter(a => a !== area);
                            saveLiderAreas(p.id, next);
                          }}
                          style={{cursor:"pointer",width:18,height:18,accentColor:AREA_COLORS[area]}} />
                        <span style={{fontSize:14,color:"var(--text)",fontWeight:600,flex:1}}>{area}</span>
                        <span style={{fontSize:11,color:checked?AREA_COLORS[area]:"var(--text3)",fontWeight:700}}>
                          {checked ? "✓ Lidera" : "—"}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div style={{padding:"10px 18px",borderTop:"1px solid var(--border)",display:"flex",justifyContent:"flex-end"}}>
                <button onClick={()=>setLiderAreasModal(null)} style={{...S.btnPrimary,fontSize:13,padding:"8px 18px"}}>Fechar</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ──  MISE COMPRAS — STATUS E CÁLCULOS                           ──
// ═══════════════════════════════════════════════════════════════
const MISE_ORDER_STATUS = {
  draft:                   { label: "Rascunho",         color: "#7a6e5a", bg: "#7a6e5a22", terminal: false },
  approved:                { label: "Aprovado",         color: "#d97706", bg: "#fffbeb",    terminal: false },
  sent:                    { label: "Enviado",          color: "#2563eb", bg: "#eff6ff",    terminal: false },
  received_ok:             { label: "Recebido OK",      color: "#15803d", bg: "#f0fdf4",    terminal: true  },
  received_with_divergence:{ label: "Divergência aceita",color: "#15803d",bg: "#f0fdf4",    terminal: true  },
  awaiting_correction:     { label: "Aguardando correção",color: "#dc2626",bg:"#fef2f2",    terminal: false },
  rejected:                { label: "Recusado/Devolvido",color: "#6b7280",bg:"#f9fafb",     terminal: true  },
};

// Calcula pedidos sugeridos para um ciclo baseado nas contagens já lançadas.
// Retorna: [{ supplierId, supplier, items:[{productId,product,totalCounted,minStock,necessidade,linkId,link,conversionFactor,qtySuggested,estimatedCost}], totalCost }]
// `overrides` é um mapa { [productId]: linkId } que força um vínculo específico
// Calcula pedidos sugeridos a partir de um conjunto de contagens específico (sem conceito de ciclo).
// Signature antiga aceitava `cycle` e filtrava counts; nova aceita `countsToUse` direto (array já filtrado).
// Retrocompat: se vier `cycle` e não vier `countsToUse`, faz o filtro legado.
function miseComputeSuggestedOrders({ cycle, countsToUse, restaurantId, items, counts, categories, productSuppliers, suppliers, overrides = {} }) {
  const restItems = items.filter(i => i.restaurantId === restaurantId);
  const restCounts = countsToUse
    ? countsToUse.filter(c => c.restaurantId === restaurantId)
    : (cycle ? counts.filter(c => c.restaurantId === restaurantId && c.cycleId === cycle.id) : []);
  if (restCounts.length === 0) return [];
  const restLinks = productSuppliers.filter(ps => ps.restaurantId === restaurantId);
  const restCats = categories.filter(c => c.restaurantId === restaurantId);

  // Soma contagens por produto, separando contagem de estoque (stockId != null) de pedido direto (stockId === null)
  const countedPerProduct = {};
  const directPedidoPerProduct = {};
  restCounts.forEach(cnt => {
    if (cnt.stockId === null || cnt.stockId === undefined) {
      directPedidoPerProduct[cnt.itemId] = (directPedidoPerProduct[cnt.itemId] || 0) + (cnt.qty || 0);
    } else {
      countedPerProduct[cnt.itemId] = (countedPerProduct[cnt.itemId] || 0) + (cnt.qty || 0);
    }
  });

  // Agrupa por fornecedor escolhido (preferencial OU override)
  const bySupplier = {};
  restItems.forEach(prod => {
    const cat = restCats.find(c => c.id === prod.categoryId);
    const catType = cat?.type ?? "contagem";
    const total = countedPerProduct[prod.id] || 0;
    const directQty = directPedidoPerProduct[prod.id] || 0;
    const min = typeof prod.minStock === "number" ? prod.minStock : null;

    // Necessidade base via min/contagem (aplicável a contagem e ambos)
    let necessidadeBase = 0;
    let mustUseDirect = false;
    if (catType === "pedido") {
      // Só pedido direto conta
      mustUseDirect = true;
    } else {
      // contagem ou ambos: usa min vs contagem
      if (min != null && min > 0) {
        necessidadeBase = Math.max(0, min - total);
      }
    }
    const necessidade = necessidadeBase + (catType === "pedido" || catType === "ambos" ? directQty : 0);
    if (necessidade <= 0) return; // nada a pedir
    // Se é pedido puro e não tem direct, nada sugere
    if (mustUseDirect && directQty <= 0) return;
    const linksOfProd = restLinks.filter(ps => ps.productId === prod.id);
    if (linksOfProd.length === 0) return; // sem fornecedor → não sugere
    let link = null;
    const overrideLinkId = overrides[prod.id];
    if (overrideLinkId) link = linksOfProd.find(l => l.id === overrideLinkId) || null;
    if (!link) link = linksOfProd.find(l => l.preferred) || linksOfProd[0];
    const fator = typeof link.conversionFactor === "number" && link.conversionFactor > 0 ? link.conversionFactor : 1;
    const qtySuggested = Math.ceil(necessidade / fator) * fator;
    const estimatedCost = typeof link.price === "number" ? link.price * (qtySuggested / fator) : null;
    const row = { productId: prod.id, product: prod, totalCounted: total, directPedido: directQty, minStock: min, necessidade, linkId: link.id, link, conversionFactor: fator, qtySuggested, estimatedCost, source: mustUseDirect ? "pedido_direto" : (directQty > 0 ? "misto" : "contagem") };
    if (!bySupplier[link.supplierId]) bySupplier[link.supplierId] = { supplierId: link.supplierId, supplier: suppliers.find(s => s.id === link.supplierId), items: [], totalCost: 0 };
    bySupplier[link.supplierId].items.push(row);
    if (typeof estimatedCost === "number") bySupplier[link.supplierId].totalCost += estimatedCost;
  });

  return Object.values(bySupplier).filter(g => g.supplier).sort((a,b) => a.supplier.name.localeCompare(b.supplier.name));
}

// Gera mensagem de WhatsApp pre-formatada para um pedido ao fornecedor
function miseBuildWhatsMessage({ order, supplier, items, restaurantName, cycleDate }) {
  const lines = [];
  lines.push(`*Pedido — ${restaurantName || "Restaurante"}*`);
  if (cycleDate) lines.push(`Data: ${cycleDate}`);
  lines.push(`Fornecedor: ${supplier.name}`);
  lines.push("");
  lines.push("Itens:");
  items.forEach(it => {
    const qty = it.qtyApproved ?? it.qtySuggested ?? 0;
    const nome = it.productName || "—";
    const fator = it.conversionFactor && it.conversionFactor !== 1 ? ` (${it.conversionFactor}× / emb.)` : "";
    lines.push(`• ${nome}: ${qty}${fator}`);
  });
  lines.push("");
  lines.push("Obrigado!");
  return lines.join("\n");
}

function miseWhatsLink(whatsapp, message) {
  const digits = (whatsapp || "").replace(/\D/g, "");
  if (!digits) return null;
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

// ═══════════════════════════════════════════════════════════════
// ──  OPERATIONAL GORJETAS — dashboard read-only                 ──
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// ──  REGRA DE DIVISÃO DE GORJETAS — versionada + ata PDF        ──
// ═══════════════════════════════════════════════════════════════
function RegraDivisaoAdmin({ restaurantId, restaurant, splits, pessoas, roles, employees, tips, currentUser, onUpdate, mobileOnly, isOwner }) {
  const ac = "var(--ac)";

  // Lê splits[restaurantId] no formato novo (array) — se for legado (objeto), converte virtualmente
  // pra exibição. Conversão real só acontece quando o user salvar uma nova regra.
  const ridSplits = splits?.[restaurantId];
  const versions = useMemo(() => {
    if (!ridSplits) return [];
    if (Array.isArray(ridSplits)) return [...ridSplits].sort((a,b) => (b.effectiveFrom||"").localeCompare(a.effectiveFrom||""));
    // Legado: {monthKey: percentages} → cria 1 versão "virtual" pra cada mês
    const out = [];
    Object.entries(ridSplits).forEach(([mk, perc]) => {
      out.push({
        id: `legacy_${mk}`,
        effectiveFrom: `${mk}-01`,
        percentages: perc,
        mode: restaurant?.divisionMode || MODE_AREA_POINTS,
        status: "active",
        proposedBy: "(legado)",
        activatedBy: "(legado)",
        proposedAt: `${mk}-01T00:00:00`,
        activatedAt: `${mk}-01T00:00:00`,
        ata: null,
        note: "Regra importada do formato anterior (sem ata).",
        _legacy: true,
      });
    });
    return out.sort((a,b) => (b.effectiveFrom||"").localeCompare(a.effectiveFrom||""));
  }, [ridSplits, restaurant]);

  const today_ = today();
  const activeNow = versions.find(v => v.status === "active" && v.effectiveFrom <= today_);
  const futureActive = versions.filter(v => v.status === "active" && v.effectiveFrom > today_);
  const drafts = versions.filter(v => v.status === "draft");
  // "Histórico" inclui TODAS as regras com vigência iniciada (inclusive a vigente atual).
  // Assim a vigente fica editável a partir do histórico — corrigir ela atualiza a regra
  // (e como é a mesma regra, o card "Vigente agora" no topo reflete a correção).
  const past = versions.filter(v => v.status === "active" && v.effectiveFrom <= today_);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingDraftId, setEditingDraftId] = useState(null);

  async function deleteDraft(id) {
    if (!await appConfirm("Apagar este rascunho?")) return;
    onUpdate("splits", { ...splits, [restaurantId]: versions.filter(v => v.id !== id) });
  }

  async function cancelFutureRule(v) {
    if (!await appConfirm(`Cancelar a regra programada para ${fmtDate(v.effectiveFrom)}?\n\nEla ainda não entrou em vigor — nada será recalculado.`)) return;
    onUpdate("splits", { ...splits, [restaurantId]: versions.filter(x => x.id !== v.id) });
    onUpdate("_toast", "🗑️ Regra futura cancelada");
  }

  async function editPastRule(v) {
    if (v._legacy) {
      alert("Esta é uma regra importada do formato antigo (legado) — não pode ser editada diretamente. Crie uma nova regra com a data correta para sobrepô-la.");
      return;
    }
    const tipsAfter = (tips || []).filter(t => t.restaurantId === restaurantId && t.date >= v.effectiveFrom);
    const daysAfter = new Set(tipsAfter.map(t => t.date)).size;
    if (daysAfter > 0) {
      if (!await appConfirm(`Editar esta regra vigente desde ${fmtDate(v.effectiveFrom)}?\n\n⚠️ Já existem ${daysAfter} dia(s) de gorjeta lançados que serão recalculados quando você ativar a versão editada.`)) return;
    }
    setEditingDraftId(v.id);
    setWizardOpen(true);
  }

  if (!isOwner) {
    return (
      <div style={{padding:"40px 20px",textAlign:"center",color:"var(--text3)"}}>
        <div style={{fontSize:36,marginBottom:8}}>🔒</div>
        <div style={{color:"var(--text)",fontSize:14,fontWeight:600}}>Apenas o owner do restaurante pode mudar a regra de divisão</div>
      </div>
    );
  }

  return (
    <div>
      {/* Vigente */}
      <h3 style={{color:"var(--text)",margin:"0 0 12px",fontSize:mobileOnly?16:18}}>📜 Regra de divisão de gorjetas</h3>

      {activeNow ? (
        <RegraCard version={activeNow} restaurant={restaurant} employees={employees} roles={roles}
                   pessoas={pessoas} title="Vigente agora" titleColor="var(--green)" mobileOnly={mobileOnly} />
      ) : (
        <div style={{padding:"24px 20px",background:"var(--card-bg)",border:"1px dashed var(--border)",borderRadius:12,marginBottom:16,textAlign:"center",color:"var(--text3)"}}>
          Nenhuma regra ativa. Configure abaixo a primeira regra deste restaurante.
        </div>
      )}

      {/* Botão de propor nova */}
      <div style={{marginTop:16,marginBottom:24}}>
        <button onClick={()=>{ setEditingDraftId(null); setWizardOpen(true); }}
          style={{background:ac,color:"#fff",border:"none",borderRadius:10,padding:"10px 18px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
          + Propor nova regra
        </button>
      </div>

      {/* Rascunhos */}
      {drafts.length > 0 && (
        <div style={{marginBottom:24}}>
          <h4 style={{fontSize:13,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,margin:"0 0 8px"}}>📝 Rascunhos ({drafts.length})</h4>
          {drafts.map(v => (
            <RegraCard key={v.id} version={v} restaurant={restaurant} employees={employees} roles={roles}
                       pessoas={pessoas} title="Rascunho — aguarda assinaturas" titleColor="#f59e0b"
                       onEdit={()=>{ setEditingDraftId(v.id); setWizardOpen(true); }}
                       onDelete={()=>deleteDraft(v.id)} mobileOnly={mobileOnly} />
          ))}
        </div>
      )}

      {/* Futuras já ativadas */}
      {futureActive.length > 0 && (
        <div style={{marginBottom:24}}>
          <h4 style={{fontSize:13,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,margin:"0 0 8px"}}>⏳ Futuras (já ativas, esperando data)</h4>
          {futureActive.map(v => (
            <RegraCard key={v.id} version={v} restaurant={restaurant} employees={employees} roles={roles}
                       pessoas={pessoas} title={`Vigente desde ${fmtDate(v.effectiveFrom)}`} titleColor="#3b82f6"
                       onEdit={()=>{ setEditingDraftId(v.id); setWizardOpen(true); }}
                       editLabel="✏️ Editar"
                       onDelete={()=>cancelFutureRule(v)}
                       deleteLabel="❌ Cancelar"
                       mobileOnly={mobileOnly} />
          ))}
        </div>
      )}

      {/* Histórico de regras com vigência iniciada (inclui a vigente atual) */}
      {past.length > 0 && (
        <div>
          <h4 style={{fontSize:13,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,margin:"0 0 8px"}}>🕐 Histórico de regras ({past.length})</h4>
          <p style={{fontSize:11,color:"var(--text3)",margin:"0 0 10px"}}>Para corrigir um erro de cadastro, edite a regra correspondente. Os tips dos dias afetados serão recalculados automaticamente.</p>
          {past.map(v => {
            const isCurrentVigente = v.id === activeNow?.id;
            return (
              <RegraCard key={v.id} version={v} restaurant={restaurant} employees={employees} roles={roles}
                         pessoas={pessoas}
                         title={isCurrentVigente ? `Vigente desde ${fmtDate(v.effectiveFrom)} · em uso agora` : `Vigente desde ${fmtDate(v.effectiveFrom)}`}
                         titleColor={isCurrentVigente ? "var(--green)" : "var(--text3)"}
                         compact={!isCurrentVigente}
                         onEdit={v._legacy ? null : ()=>editPastRule(v)}
                         editLabel="✏️ Corrigir"
                         mobileOnly={mobileOnly} />
            );
          })}
        </div>
      )}

      {/* Wizard */}
      {wizardOpen && (
        <RegraWizard
          editingVersion={editingDraftId ? versions.find(v=>v.id===editingDraftId) : null}
          restaurant={restaurant}
          restaurantId={restaurantId}
          pessoas={pessoas}
          employees={employees}
          roles={roles}
          currentUser={currentUser}
          existingVersions={versions}
          tips={tips}
          onSave={(newOrUpdated) => {
            const others = versions.filter(v => v.id !== newOrUpdated.id && !v._legacy);
            const next = [...others, newOrUpdated];
            onUpdate("splits", { ...splits, [restaurantId]: next });
            setWizardOpen(false);
            setEditingDraftId(null);
            onUpdate("_toast", newOrUpdated.status === "active" ? "✓ Regra ativada" : "✓ Rascunho salvo");
          }}
          onClose={() => { setWizardOpen(false); setEditingDraftId(null); }}
          mobileOnly={mobileOnly}
        />
      )}
    </div>
  );
}

// Card que mostra uma regra (vigente / rascunho / passada)
function RegraCard({ version, restaurant, employees, roles, pessoas, title, titleColor, onEdit, onDelete, editLabel, deleteLabel, compact, mobileOnly }) {
  const [expanded, setExpanded] = useState(!compact);
  const today_ = today();
  // Calcula employeesPerArea pra hoje (mostrar % efetivo agora)
  const employeesPerArea = {};
  AREAS.forEach(a => { employeesPerArea[a] = countActiveEmployeesInArea(employees, roles, a, today_, restaurant?.id); });
  const finalPct = computeAreaPercentages(version.percentages, employeesPerArea);

  function downloadAta() {
    if (!window.jspdf) { alert("Lib PDF carregando, tente em 2s"); return; }
    generateAtaPDF({ version, restaurant, pessoas, roles, finalPct, employeesPerArea });
  }

  return (
    <div style={{background:"var(--card-bg)",border:`1px solid ${titleColor}66`,borderRadius:12,marginBottom:10,overflow:"hidden"}}>
      <div onClick={()=>setExpanded(!expanded)}
        style={{padding:"12px 16px",background:"var(--bg2)",borderBottom:expanded?`1px solid var(--border)`:"none",display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",flexWrap:"wrap",gap:8}}>
        <div>
          <div style={{fontSize:11,color:titleColor,fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>{title}</div>
          <div style={{fontSize:14,color:"var(--text)",fontWeight:700,marginTop:2}}>
            {version.mode === MODE_GLOBAL_POINTS ? "Pontos Globais" : "Por Área + Pontos"}
            {version.note && <span style={{fontSize:11,color:"var(--text3)",fontWeight:400,marginLeft:8}}>· {version.note}</span>}
          </div>
        </div>
        <span style={{fontSize:14,color:"var(--text3)"}}>{expanded ? "▼" : "▶"}</span>
      </div>
      {expanded && (
        <div style={{padding:"14px 16px"}}>
          <table style={{width:"100%",fontSize:13,borderCollapse:"collapse"}}>
            <thead>
              <tr style={{borderBottom:"1px solid var(--border)"}}>
                <th style={{textAlign:"left",padding:"6px 8px",color:"var(--text3)",fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Área</th>
                <th style={{textAlign:"left",padding:"6px 8px",color:"var(--text3)",fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Tipo</th>
                <th style={{textAlign:"right",padding:"6px 8px",color:"var(--text3)",fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>% Hoje</th>
              </tr>
            </thead>
            <tbody>
              {AREAS.map(area => {
                const cfg = version.percentages?.[area];
                let typeLabel = "—";
                let detailLabel = "";
                if (typeof cfg === "number") {
                  typeLabel = "Fixo";
                  detailLabel = `${Number(cfg).toFixed(2)}%`;
                } else if (cfg && cfg.type === "fixed") {
                  typeLabel = "Fixo";
                  detailLabel = `${Number(cfg.value).toFixed(2)}%`;
                } else if (cfg && cfg.type === "perEmployee") {
                  typeLabel = "Por empregado";
                  detailLabel = `${Number(cfg.valuePerEmp).toFixed(2)}% × ${employeesPerArea[area]} = ${(cfg.valuePerEmp * employeesPerArea[area]).toFixed(2)}%`;
                }
                const pct = finalPct[area];
                return (
                  <tr key={area} style={{borderTop:"1px solid var(--border)"}}>
                    <td style={{padding:"6px 8px",color:"var(--text)",fontWeight:600}}>{area}</td>
                    <td style={{padding:"6px 8px",color:"var(--text2)",fontSize:12}}>{typeLabel} <span style={{color:"var(--text3)",fontSize:11,marginLeft:4}}>({detailLabel})</span></td>
                    <td style={{padding:"6px 8px",textAlign:"right",color:"var(--text)",fontFamily:"'DM Mono',monospace",fontWeight:700}}>{pct != null ? `${pct.toFixed(2)}%` : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{marginTop:10,fontSize:11,color:"var(--text3)",lineHeight:1.5}}>
            Vigente desde <b>{fmtDate(version.effectiveFrom)}</b>
            {version.activatedBy && <> · ativada por {version.activatedBy}</>}
            {version.activatedAt && <> em {new Date(version.activatedAt).toLocaleString("pt-BR")}</>}
            {version.ata?.voters?.length > 0 && <> · {version.ata.voters.length} assinante(s) na ata</>}
          </div>
          <div style={{marginTop:12,display:"flex",gap:8,flexWrap:"wrap"}}>
            {version.ata && <button onClick={downloadAta} style={{...S.btnSecondary,fontSize:12,padding:"7px 14px"}}>📄 Baixar ata</button>}
            {onEdit && <button onClick={onEdit} style={{...S.btnSecondary,fontSize:12,padding:"7px 14px"}}>{editLabel || "✏️ Editar / Ativar"}</button>}
            {onDelete && <button onClick={onDelete} style={{...S.btnSecondary,fontSize:12,padding:"7px 14px",color:"var(--red)",borderColor:"var(--red)44"}}>{deleteLabel || "Apagar"}</button>}
          </div>
        </div>
      )}
    </div>
  );
}

// Wizard 3 etapas — Editar regra → Selecionar votantes → Gerar ata + Ativar
function RegraWizard({ editingVersion, restaurant, restaurantId, pessoas, employees, roles, currentUser, existingVersions, tips, onSave, onClose, mobileOnly }) {
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState(editingVersion?.mode || MODE_AREA_POINTS);
  // Normaliza percentages pra editar
  const initialAreas = {};
  AREAS.forEach(a => {
    const cfg = editingVersion?.percentages?.[a];
    if (typeof cfg === "number") {
      initialAreas[a] = { type: "fixed", value: cfg, valuePerEmp: 1.5 };
    } else if (cfg && cfg.type === "perEmployee") {
      initialAreas[a] = { type: "perEmployee", value: 0, valuePerEmp: cfg.valuePerEmp || 1.5 };
    } else if (cfg && cfg.type === "fixed") {
      initialAreas[a] = { type: "fixed", value: cfg.value || 0, valuePerEmp: 1.5 };
    } else {
      initialAreas[a] = { type: "fixed", value: 25, valuePerEmp: 1.5 };
    }
  });
  const [areasCfg, setAreasCfg] = useState(initialAreas);
  const [note, setNote] = useState(editingVersion?.note || "");
  // Votantes — pessoas isTeam do restaurante
  const restPessoas = (pessoas || []).filter(p => (p.restaurantIds||[]).includes(restaurantId) && p.isTeam?.[restaurantId]);
  const [voters, setVoters] = useState(() => {
    const initial = {};
    if (editingVersion?.ata?.voters) {
      editingVersion.ata.voters.forEach(v => { initial[v.pessoaId] = true; });
    } else {
      restPessoas.forEach(p => { initial[p.id] = true; });
    }
    return initial;
  });
  const [meetingDate, setMeetingDate] = useState(editingVersion?.ata?.meetingDate || today());
  const [effectiveFrom, setEffectiveFrom] = useState(editingVersion?.effectiveFrom || today());

  function setAreaType(area, type) {
    setAreasCfg(c => ({ ...c, [area]: { ...c[area], type } }));
  }
  function setAreaValue(area, val) {
    setAreasCfg(c => ({ ...c, [area]: { ...c[area], value: val } }));
  }
  function setAreaValuePerEmp(area, val) {
    setAreasCfg(c => ({ ...c, [area]: { ...c[area], valuePerEmp: val } }));
  }

  // Calcula soma e preview
  const today_ = today();
  const employeesPerArea = {};
  AREAS.forEach(a => { employeesPerArea[a] = countActiveEmployeesInArea(employees, roles, a, today_, restaurantId); });
  const percentages = {};
  AREAS.forEach(a => {
    const cfg = areasCfg[a];
    if (cfg.type === "perEmployee") {
      percentages[a] = { type: "perEmployee", valuePerEmp: parseFloat(cfg.valuePerEmp) || 0 };
    } else {
      percentages[a] = { type: "fixed", value: parseFloat(cfg.value) || 0 };
    }
  });
  const finalPct = computeAreaPercentages(percentages, employeesPerArea);
  const sumFinal = Object.values(finalPct).reduce((s, v) => s + (v || 0), 0);

  const selectedVoters = Object.entries(voters).filter(([_, v]) => v).map(([pid]) => pid);

  function buildVersion(status) {
    const id = editingVersion?.id || `spl_${Date.now().toString(36)}${Math.random().toString(36).slice(2,5)}`;
    const ata = selectedVoters.length > 0 ? {
      meetingDate,
      meetingLocation: restaurant?.name || "—",
      voters: selectedVoters.map(pid => {
        const p = restPessoas.find(x => x.id === pid);
        const td = p?.teamData?.[restaurantId];
        const r = td ? roles.find(rr => rr.id === td.roleId) : null;
        return {
          pessoaId: pid,
          name: p?.name || "—",
          cpf: p?.cpf || "",
          roleName: r?.name || "—",
        };
      }),
      generatedAt: new Date().toISOString(),
      generatedBy: currentUser?.name || "—",
    } : null;
    return {
      id,
      effectiveFrom,
      percentages,
      mode,
      status,
      proposedBy: editingVersion?.proposedBy || currentUser?.name || "—",
      proposedById: editingVersion?.proposedById || currentUser?.id || null,
      proposedAt: editingVersion?.proposedAt || new Date().toISOString(),
      activatedBy: status === "active" ? (currentUser?.name || "—") : (editingVersion?.activatedBy || null),
      activatedAt: status === "active" ? new Date().toISOString() : (editingVersion?.activatedAt || null),
      ata,
      note: note.trim() || null,
    };
  }

  function saveDraft() {
    if (selectedVoters.length === 0) { alert("Selecione pelo menos 1 votante."); return; }
    onSave(buildVersion("draft"));
  }
  async function activate() {
    if (selectedVoters.length === 0) { alert("Selecione pelo menos 1 votante."); return; }
    if (Math.abs(sumFinal - 100) > 0.5) {
      if (!await appConfirm(`A soma final dá ${sumFinal.toFixed(2)}% (esperado 100%). Ativar mesmo assim?`)) return;
    }
    // Conta dias afetados
    const affectedTips = (tips || []).filter(t => t.restaurantId === restaurantId && t.date >= effectiveFrom);
    const affectedDays = new Set(affectedTips.map(t => t.date)).size;
    if (affectedDays > 0) {
      if (!await appConfirm(`Esta regra vigente desde ${fmtDate(effectiveFrom)} vai recalcular ${affectedDays} dia(s) com gorjeta lançada. Continuar?`)) return;
    }
    const v = buildVersion("active");
    onSave(v);
    // Auto-recalc é feito pelo useEffect unificado em RestaurantPanel
    // (Fase B v2): a mudança no splits dispara recalc automático.
  }

  function downloadAtaPreview() {
    if (!window.jspdf) { alert("Lib PDF carregando, tente em 2s"); return; }
    const v = buildVersion(editingVersion?.status || "draft");
    generateAtaPDF({ version: v, restaurant, pessoas, roles, finalPct, employeesPerArea });
  }

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:mobileOnly?12:20}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"var(--bg1)",borderRadius:12,padding:mobileOnly?16:24,maxWidth:680,width:"100%",maxHeight:"92vh",overflowY:"auto",boxShadow:"0 10px 40px rgba(0,0,0,0.3)"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <h3 style={{margin:0,fontSize:mobileOnly?16:18,fontWeight:700,color:"var(--text)"}}>
            {editingVersion ? "Editar regra (rascunho)" : "Propor nova regra"} — etapa {step}/3
          </h3>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:18,color:"var(--text3)",cursor:"pointer",padding:"4px 8px"}}>×</button>
        </div>

        {/* ETAPA 1: Definir regra */}
        {step === 1 && (
          <div>
            <div style={{marginBottom:14}}>
              <label style={{fontSize:11,color:"var(--text3)",fontWeight:600,display:"block",marginBottom:4}}>Modo de divisão</label>
              <div style={{display:"flex",gap:6}}>
                {[[MODE_AREA_POINTS,"Por Área + Pontos"],[MODE_GLOBAL_POINTS,"Pontos Globais"]].map(([m,l]) => (
                  <button key={m} onClick={()=>setMode(m)}
                    style={{flex:1,padding:"10px",borderRadius:8,border:`1px solid ${mode===m?"var(--ac)":"var(--border)"}`,background:mode===m?"var(--ac)15":"transparent",color:mode===m?"var(--ac)":"var(--text2)",fontSize:12,fontWeight:mode===m?700:500,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                    {l}
                  </button>
                ))}
              </div>
            </div>

            {mode === MODE_AREA_POINTS && (
              <div style={{marginBottom:14}}>
                <label style={{fontSize:11,color:"var(--text3)",fontWeight:600,display:"block",marginBottom:6}}>Configuração por área</label>
                {AREAS.map(area => {
                  const cfg = areasCfg[area];
                  const isPerEmp = cfg.type === "perEmployee";
                  const empCount = employeesPerArea[area];
                  const effective = isPerEmp ? (parseFloat(cfg.valuePerEmp)||0) * empCount : (parseFloat(cfg.value)||0);
                  return (
                    <div key={area} style={{padding:"10px 12px",border:"1px solid var(--border)",borderRadius:8,marginBottom:6}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                        <span style={{fontSize:13,fontWeight:700,color:"var(--text)"}}>{area}</span>
                        <span style={{fontSize:10,color:"var(--text3)"}}>{empCount} empregado{empCount!==1?"s":""} hoje</span>
                      </div>
                      <div style={{display:"flex",gap:6,marginBottom:6}}>
                        <button onClick={()=>setAreaType(area,"fixed")}
                          style={{flex:1,padding:"6px 10px",borderRadius:6,border:`1px solid ${!isPerEmp?"var(--ac)":"var(--border)"}`,background:!isPerEmp?"var(--ac)15":"transparent",color:!isPerEmp?"var(--ac)":"var(--text3)",fontSize:11,fontWeight:!isPerEmp?700:500,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                          Fixo
                        </button>
                        <button onClick={()=>setAreaType(area,"perEmployee")}
                          style={{flex:1,padding:"6px 10px",borderRadius:6,border:`1px solid ${isPerEmp?"var(--ac)":"var(--border)"}`,background:isPerEmp?"var(--ac)15":"transparent",color:isPerEmp?"var(--ac)":"var(--text3)",fontSize:11,fontWeight:isPerEmp?700:500,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                          Por empregado
                        </button>
                      </div>
                      {!isPerEmp ? (
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <input type="number" step="0.01" value={cfg.value} onChange={e=>setAreaValue(area,e.target.value)} style={{...S.input,maxWidth:100}}/>
                          <span style={{fontSize:13,color:"var(--text2)"}}>%</span>
                        </div>
                      ) : (
                        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                          <input type="number" step="0.1" value={cfg.valuePerEmp} onChange={e=>setAreaValuePerEmp(area,e.target.value)} style={{...S.input,maxWidth:100}}/>
                          <span style={{fontSize:13,color:"var(--text2)"}}>% por empregado</span>
                          <span style={{fontSize:11,color:"var(--text3)"}}>= {effective.toFixed(2)}% hoje</span>
                        </div>
                      )}
                    </div>
                  );
                })}
                <div style={{padding:"10px 12px",background:"var(--bg2)",borderRadius:8,marginTop:8}}>
                  <div style={{fontSize:11,color:"var(--text3)",marginBottom:6}}>Resultado HOJE (com {Object.values(employeesPerArea).reduce((s,n)=>s+n,0)} empregados):</div>
                  {AREAS.map(area => (
                    <div key={area} style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"2px 0"}}>
                      <span style={{color:"var(--text2)"}}>{area}</span>
                      <span style={{color:"var(--text)",fontFamily:"'DM Mono',monospace",fontWeight:700}}>{(finalPct[area]||0).toFixed(2)}%</span>
                    </div>
                  ))}
                  <div style={{borderTop:"1px solid var(--border)",marginTop:6,paddingTop:6,display:"flex",justifyContent:"space-between",fontSize:13,fontWeight:700}}>
                    <span style={{color:"var(--text)"}}>Total</span>
                    <span style={{color:Math.abs(sumFinal-100)<0.5?"var(--green)":"var(--red)",fontFamily:"'DM Mono',monospace"}}>{sumFinal.toFixed(2)}%</span>
                  </div>
                </div>
              </div>
            )}

            <div style={{marginBottom:14}}>
              <label style={{fontSize:11,color:"var(--text3)",fontWeight:600,display:"block",marginBottom:4}}>Observação (opcional, fica no audit log)</label>
              <textarea value={note} onChange={e=>setNote(e.target.value)} rows={2} placeholder="Ex: aumento Bar negociado em reunião 24/04" style={{...S.input,resize:"vertical"}}/>
            </div>

            <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
              <button onClick={onClose} style={{...S.btnSecondary,fontSize:12,padding:"10px 16px"}}>Cancelar</button>
              <button onClick={()=>setStep(2)} style={{background:"var(--ac)",color:"#fff",border:"none",borderRadius:8,padding:"10px 18px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>Próximo →</button>
            </div>
          </div>
        )}

        {/* ETAPA 2: Selecionar votantes */}
        {step === 2 && (
          <div>
            <div style={{fontSize:13,color:"var(--text2)",marginBottom:12}}>
              Selecione os empregados que vão participar da votação. A ata será gerada com os nomes selecionados.
            </div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <div style={{fontSize:12,color:"var(--text3)"}}>{selectedVoters.length} de {restPessoas.length} selecionado(s)</div>
              <div style={{display:"flex",gap:6}}>
                <button onClick={()=>{ const all={}; restPessoas.forEach(p=>all[p.id]=true); setVoters(all); }} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px"}}>Marcar todos</button>
                <button onClick={()=>setVoters({})} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px"}}>Desmarcar</button>
              </div>
            </div>
            <div style={{maxHeight:300,overflowY:"auto",border:"1px solid var(--border)",borderRadius:8}}>
              {restPessoas.length === 0 ? (
                <div style={{padding:"30px 20px",textAlign:"center",color:"var(--text3)",fontSize:13}}>
                  Nenhum empregado da equipe encontrado. Cadastre pessoas em Pessoas → Cadastro com a flag "É equipe".
                </div>
              ) : restPessoas.map(p => {
                const td = p.teamData?.[restaurantId];
                const r = td ? roles.find(rr => rr.id === td.roleId) : null;
                return (
                  <label key={p.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderBottom:"1px solid var(--border)",cursor:"pointer",fontSize:13}}>
                    <input type="checkbox" checked={!!voters[p.id]} onChange={e=>setVoters(v=>({...v,[p.id]:e.target.checked}))}/>
                    <div style={{flex:1}}>
                      <div style={{color:"var(--text)",fontWeight:600}}>{p.name}</div>
                      <div style={{fontSize:11,color:"var(--text3)"}}>{p.cpf || "sem CPF"} · {r?.name || "sem cargo"}</div>
                    </div>
                  </label>
                );
              })}
            </div>
            <div style={{marginTop:14,display:"flex",justifyContent:"space-between",gap:8}}>
              <button onClick={()=>setStep(1)} style={{...S.btnSecondary,fontSize:12,padding:"10px 16px"}}>← Voltar</button>
              <button onClick={()=>setStep(3)} disabled={selectedVoters.length===0}
                style={{background:selectedVoters.length===0?"var(--bg3)":"var(--ac)",color:selectedVoters.length===0?"var(--text3)":"#fff",border:"none",borderRadius:8,padding:"10px 18px",fontSize:13,fontWeight:700,cursor:selectedVoters.length===0?"not-allowed":"pointer",fontFamily:"'DM Sans',sans-serif"}}>Próximo →</button>
            </div>
          </div>
        )}

        {/* ETAPA 3: Gerar ata + Salvar/Ativar */}
        {step === 3 && (
          <div>
            <div style={{padding:"12px 14px",background:"var(--bg2)",borderRadius:8,marginBottom:14,fontSize:12,color:"var(--text2)",lineHeight:1.5}}>
              <b>Resumo:</b><br/>
              Modo: <b>{mode === MODE_GLOBAL_POINTS ? "Pontos Globais" : "Por Área + Pontos"}</b><br/>
              Total HOJE: <b>{sumFinal.toFixed(2)}%</b><br/>
              Votantes: <b>{selectedVoters.length}</b> empregado(s)
            </div>

            <div style={{marginBottom:14}}>
              <label style={{fontSize:11,color:"var(--text3)",fontWeight:600,display:"block",marginBottom:4}}>Data da assembleia</label>
              <input type="date" value={meetingDate} onChange={e=>setMeetingDate(e.target.value)} style={S.input}/>
            </div>

            <div style={{marginBottom:14}}>
              <label style={{fontSize:11,color:"var(--text3)",fontWeight:600,display:"block",marginBottom:4}}>Vigente desde (data em que regra começa a valer)</label>
              <input type="date" value={effectiveFrom} onChange={e=>setEffectiveFrom(e.target.value)} style={S.input}/>
              <div style={{fontSize:10,color:"var(--text3)",marginTop:4,lineHeight:1.5}}>
                {effectiveFrom < today_ ? `⚠ Retroativo (${Math.ceil((new Date(today_)-new Date(effectiveFrom))/(1000*60*60*24))} dias) — vai recalcular gorjetas existentes nesse período.` : effectiveFrom > today_ ? "📅 Futuro — regra fica salva e aplica automaticamente quando a data chegar." : "Hoje — passa a valer imediatamente."}
              </div>
            </div>

            <div style={{padding:"10px 14px",background:"var(--ac)10",border:"1px solid var(--ac)33",borderRadius:8,fontSize:12,color:"var(--text2)",marginBottom:14,lineHeight:1.5}}>
              💡 <b>Fluxo recomendado:</b><br/>
              1. Baixe a ata em PDF<br/>
              2. Imprima e colete as assinaturas<br/>
              3. Salve como rascunho enquanto coleta assinaturas<br/>
              4. Quando todos assinarem, volte aqui e clique em <b>Ativar</b>
            </div>

            <div style={{display:"flex",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
              <button onClick={()=>setStep(2)} style={{...S.btnSecondary,fontSize:12,padding:"10px 16px"}}>← Voltar</button>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <button onClick={downloadAtaPreview} style={{...S.btnSecondary,fontSize:12,padding:"10px 14px"}}>📄 Baixar ata (preview)</button>
                <button onClick={saveDraft} style={{...S.btnSecondary,fontSize:12,padding:"10px 14px"}}>💾 Salvar rascunho</button>
                <button onClick={activate}
                  style={{background:"var(--ac)",color:"#fff",border:"none",borderRadius:8,padding:"10px 18px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>⚡ Ativar agora</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Gera ata em PDF formal com TODOS os detalhes da regra de divisão.
// Inclui: quem entra/sai, modo de divisão, áreas e pontuações, exemplos numéricos, assinaturas.
function generateAtaPDF({ version, restaurant, pessoas, roles, finalPct, employeesPerArea }) {
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const MARGIN = 20;
  const ACCENT = [124, 158, 94];
  const TEXT = [28, 23, 16];
  const TEXT2 = [74, 63, 48];
  const TEXT3 = [154, 141, 122];
  const BORDER = [232, 226, 216];

  let y = 15;

  // ─── helpers de layout com auto-pagination ───
  function ensureSpace(needed = 10) {
    if (y + needed > pageH - 18) {
      doc.addPage();
      y = 15;
    }
  }
  function writeText(text, opts = {}) {
    const fontSize = opts.fontSize || 10;
    const lineHeight = opts.lineHeight || (fontSize * 0.45);
    const x = opts.x || MARGIN;
    const maxWidth = opts.maxWidth || (pageW - 2 * MARGIN);
    const color = opts.color || TEXT;
    const fontStyle = opts.fontStyle || "normal";
    doc.setFont("helvetica", fontStyle);
    doc.setFontSize(fontSize);
    doc.setTextColor(...color);
    const lines = typeof text === "string" ? doc.splitTextToSize(text, maxWidth) : text;
    lines.forEach(line => {
      ensureSpace(lineHeight + 1);
      doc.text(line, x, y);
      y += lineHeight;
    });
    if (opts.gap) y += opts.gap;
  }
  function sectionTitle(title) {
    ensureSpace(14);
    y += 4;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...ACCENT);
    doc.text(title.toUpperCase(), MARGIN, y);
    doc.setDrawColor(...ACCENT);
    doc.setLineWidth(0.4);
    doc.line(MARGIN, y + 1.5, pageW - MARGIN, y + 1.5);
    y += 8;
  }
  function bulletList(items) {
    items.forEach(line => {
      writeText(`•  ${line}`, { fontSize: 10, lineHeight: 5, x: MARGIN + 2 });
    });
  }

  // ─── HEADER ───
  doc.setFillColor(...ACCENT);
  doc.rect(15, 15, 4, 18, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(...TEXT);
  doc.text(restaurant?.name || "Restaurante", 22, 22);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...TEXT3);
  const metaLine = [];
  if (restaurant?.cnpj) metaLine.push(`CNPJ: ${restaurant.cnpj}`);
  if (restaurant?.endereco || restaurant?.address) metaLine.push(restaurant.endereco || restaurant.address);
  if (metaLine.length > 0) doc.text(metaLine.join(" · "), 22, 28);

  // ─── TÍTULO ───
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(...TEXT);
  doc.text("ATA DE ASSEMBLEIA", pageW / 2, 45, { align: "center" });
  doc.setFontSize(11);
  doc.text("Definição da Regra de Divisão de Gorjetas", pageW / 2, 52, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...TEXT3);
  const meetDate = version.ata?.meetingDate || version.effectiveFrom;
  doc.text(`Data da assembleia: ${fmtDate(meetDate)}`, pageW / 2, 60, { align: "center" });
  doc.text(`Local: ${version.ata?.meetingLocation || restaurant?.name || "—"}`, pageW / 2, 65, { align: "center" });

  y = 76;

  // ─── 1) INTRODUÇÃO ───
  writeText(
    `Aos ${new Date(meetDate).toLocaleDateString("pt-BR", {day:"numeric", month:"long", year:"numeric"})}, reuniram-se na sede do estabelecimento ${restaurant?.name || "—"} os colaboradores listados ao final desta ata, em assembleia convocada pela direção, com o objetivo de deliberar, aprovar e formalizar a regra de divisão das gorjetas recebidas pelo restaurante. A regra abaixo entra em vigor a partir de ${fmtDate(version.effectiveFrom)} e substitui qualquer regra anterior aplicável a este estabelecimento.`,
    { fontSize: 10, lineHeight: 5, gap: 4 }
  );

  // ─── 2) MODO DE DIVISÃO ───
  sectionTitle("1. Modo de Divisão Adotado");
  const modeLabel = version.mode === MODE_GLOBAL_POINTS ? "Pontos Globais" : "Por Área + Pontos";
  writeText(`Modo: ${modeLabel}`, { fontStyle: "bold", fontSize: 11, lineHeight: 6 });
  if (version.mode === MODE_GLOBAL_POINTS) {
    writeText(
      "Todo o pool líquido de gorjetas é distribuído entre os empregados que efetivamente trabalharam no dia, proporcionalmente aos pontos do cargo de cada um. Não há separação por área.",
      { fontSize: 10, lineHeight: 5, gap: 3 }
    );
  } else {
    writeText(
      "O pool líquido de gorjetas é distribuído primeiro entre as áreas (Bar, Cozinha, Salão, Limpeza) conforme percentuais detalhados na seção seguinte. Dentro de cada área, o valor da área é distribuído entre os empregados ativos da área proporcionalmente aos pontos do cargo de cada um.",
      { fontSize: 10, lineHeight: 5, gap: 3 }
    );
  }

  // ─── 3) IMPOSTO ───
  sectionTitle("2. Imposto Retido");
  const taxRate = restaurant?.taxRate ?? TAX;
  writeText(
    `Sobre o valor bruto da gorjeta arrecadada em cada dia, é descontado o imposto de ${(taxRate * 100).toFixed(2)}% antes da divisão. O saldo líquido (${((1 - taxRate) * 100).toFixed(2)}% do bruto) é o que efetivamente vai pra divisão entre os colaboradores.`,
    { fontSize: 10, lineHeight: 5, gap: 3 }
  );

  // ─── 4) DETALHAMENTO DAS ÁREAS (só se modo área) ───
  if (version.mode !== MODE_GLOBAL_POINTS) {
    sectionTitle("3. Percentuais por Área");
    const tableRows = AREAS.map(area => {
      const cfg = version.percentages?.[area];
      let tipo = "Fixo", config = "—", efetivo = "—";
      if (typeof cfg === "number") {
        tipo = "Fixo";
        config = `${cfg}%`;
        efetivo = `${cfg}%`;
      } else if (cfg?.type === "fixed") {
        tipo = "Fixo";
        config = `${cfg.value}%`;
        efetivo = `${(finalPct[area] || 0).toFixed(2)}%`;
      } else if (cfg?.type === "perEmployee") {
        tipo = "Variável (por empregado)";
        config = `${cfg.valuePerEmp}% × N empregados`;
        const n = employeesPerArea?.[area] || 0;
        efetivo = `${(finalPct[area] || 0).toFixed(2)}% (com ${n} empregado${n!==1?"s":""} hoje)`;
      }
      return [area, tipo, config, efetivo];
    });
    doc.autoTable({
      startY: y,
      head: [["Área", "Tipo", "Configuração", "% Efetivo (data da ata)"]],
      body: tableRows,
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 2.5, textColor: TEXT, lineColor: BORDER, lineWidth: 0.2 },
      headStyles: { fillColor: ACCENT, textColor: [255,255,255], fontStyle: "bold", fontSize: 9 },
      columnStyles: {
        0: { cellWidth: 28, fontStyle: "bold" },
        1: { cellWidth: 42 },
        2: { cellWidth: 50 },
        3: { cellWidth: "auto" },
      },
      margin: { left: MARGIN, right: MARGIN },
    });
    y = doc.lastAutoTable.finalY + 6;

    // Explicação do tipo variável
    const hasVariable = AREAS.some(a => {
      const cfg = version.percentages?.[a];
      return cfg && typeof cfg === "object" && cfg.type === "perEmployee";
    });
    if (hasVariable) {
      writeText(
        "ÁREAS DE TIPO VARIÁVEL (POR EMPREGADO):",
        { fontStyle: "bold", fontSize: 10, lineHeight: 5 }
      );
      writeText(
        "Esses percentuais flutuam diariamente conforme o número de empregados registrados ativos na área naquela data. O cálculo é feito automaticamente em cada dia: percentual configurado × quantidade de empregados ativos = % da área. O saldo restante (100% menos a soma dos variáveis) é distribuído entre as áreas fixas, mantendo as proporções entre elas.",
        { fontSize: 9, lineHeight: 4.2, color: TEXT2, gap: 3 }
      );
      writeText("Exemplo prático para uma área variável:", { fontStyle: "bold", fontSize: 9, lineHeight: 4.5 });
      writeText(
        "• Empregado admitido no dia 1 do mês: contribui durante todos os 30 dias.\n• Empregado admitido no dia 10: contribui durante 21 dias (10 a 30).\n• Empregado demitido no dia 20: deixa de contar a partir do dia 20 (conta até o dia 19 inclusive).\n• Empregado em férias: NÃO conta no cálculo.",
        { fontSize: 9, lineHeight: 4.2, color: TEXT2, gap: 3 }
      );
    }
  }

  // ─── 5) PONTUAÇÃO POR CARGO ───
  sectionTitle((version.mode !== MODE_GLOBAL_POINTS ? "4." : "3.") + " Pontuação por Cargo");
  const restRoles = (roles || []).filter(r => r.restaurantId === restaurant?.id);
  if (restRoles.length === 0) {
    writeText("(Nenhum cargo cadastrado neste restaurante.)", { fontSize: 10, color: TEXT3, gap: 4 });
  } else {
    writeText(
      "Cada cargo tem uma pontuação que define o peso do empregado na divisão dentro da sua área (modo Por Área + Pontos) ou no pool global (modo Pontos Globais). Quanto maior a pontuação, maior a fração da gorjeta que o empregado recebe em comparação aos colegas.",
      { fontSize: 10, lineHeight: 5, gap: 3 }
    );
    // Só inclui cargos que recebem gorjeta (filtra noTip)
    const cargoRows = [...restRoles]
      .filter(r => !r.noTip)
      .sort((a, b) => (a.area || "z").localeCompare(b.area || "z") || (a.name || "").localeCompare(b.name || ""))
      .map(r => [
        r.name || "—",
        r.area || "—",
        r.points != null ? String(r.points) : "—",
      ]);
    if (cargoRows.length === 0) {
      writeText("(Nenhum cargo com participação na gorjeta foi cadastrado.)", { fontSize: 10, color: TEXT3, gap: 4 });
    } else {
      doc.autoTable({
        startY: y,
        head: [["Cargo", "Área", "Pontos"]],
        body: cargoRows,
        theme: "grid",
        styles: { fontSize: 9, cellPadding: 2.5, textColor: TEXT, lineColor: BORDER, lineWidth: 0.2 },
        headStyles: { fillColor: ACCENT, textColor: [255,255,255], fontStyle: "bold", fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 80, fontStyle: "bold" },
          1: { cellWidth: 50 },
          2: { cellWidth: "auto", halign: "right" },
        },
        margin: { left: MARGIN, right: MARGIN },
      });
      y = doc.lastAutoTable.finalY + 6;
    }
  }

  // ─── 6) QUEM PARTICIPA / NÃO PARTICIPA ───
  sectionTitle((version.mode !== MODE_GLOBAL_POINTS ? "5." : "4.") + " Quem Participa da Divisão");
  writeText("PARTICIPAM (recebem fração da gorjeta) os empregados que, no dia em questão:", { fontStyle: "bold", fontSize: 10, lineHeight: 5 });
  bulletList([
    "Estão registrados no quadro do restaurante e não estão demitidos antes da data;",
    "Foram admitidos em data anterior ou igual ao dia (admissão futura não conta);",
    "Têm cargo cadastrado e o cargo NÃO está marcado como 'sem participação na gorjeta';",
    "Estão escalados como TRABALHO (T), TRABALHO POR COMPENSAÇÃO (TC) ou COMPENSAÇÃO DE FOLGA (FC) — exceto produção, que entra todo dia;",
    "Empregados de PRODUÇÃO entram em todos os dias (independente do status de escala), exceto férias.",
  ]);
  y += 2;
  writeText("NÃO PARTICIPAM da divisão da gorjeta (não recebem fração) os empregados que, no dia em questão:", { fontStyle: "bold", fontSize: 10, lineHeight: 5 });
  bulletList([
    "Estão DEMITIDOS em data anterior ou igual ao dia;",
    "Não foram admitidos ainda (admissão futura);",
    "Estão em FÉRIAS (status FÉR);",
    "Estão em FOLGA programada (status F);",
    "Estão em FALTA JUSTIFICADA (FJ) ou FALTA INJUSTIFICADA (FI);",
    "Têm cargo marcado como 'sem participação na gorjeta'.",
  ]);
  y += 3;

  // ─── 7) COMO O CÁLCULO FUNCIONA ───
  sectionTitle((version.mode !== MODE_GLOBAL_POINTS ? "6." : "5.") + " Como o Cálculo Funciona Diariamente");
  if (version.mode === MODE_GLOBAL_POINTS) {
    writeText("Para cada dia em que houver gorjeta lançada, o sistema executa as seguintes etapas:", { fontSize: 10, lineHeight: 5, gap: 2 });
    bulletList([
      `Pega o valor BRUTO da gorjeta do dia (R$ X);`,
      `Desconta o imposto: R$ X × ${(taxRate*100).toFixed(2)}% = R$ Y. Saldo líquido = R$ X - R$ Y;`,
      `Lista todos os empregados ativos no dia (conforme regras da seção anterior);`,
      `Soma os pontos dos cargos de todos esses empregados (TOTAL);`,
      `Para cada empregado: parcela = (líquido) × (pontos do empregado / TOTAL);`,
      `Cada parcela é arredondada e creditada no dia.`,
    ]);
  } else {
    writeText("Para cada dia em que houver gorjeta lançada, o sistema executa as seguintes etapas:", { fontSize: 10, lineHeight: 5, gap: 2 });
    bulletList([
      `Pega o valor BRUTO da gorjeta do dia (R$ X);`,
      `Desconta o imposto: R$ X × ${(taxRate*100).toFixed(2)}% = R$ Y. Saldo líquido = R$ X - R$ Y;`,
      `Calcula o percentual EFETIVO de cada área conforme regras desta ata (variáveis primeiro, fixas depois);`,
      `Para cada ÁREA: pool da área = (líquido) × (% da área);`,
      `Para cada empregado da área: parcela = (pool da área) × (pontos do empregado / soma de pontos da área);`,
      `Empregados de produção entram em todas as áreas que tiverem alocação;`,
      `Cada parcela é arredondada e creditada no dia.`,
    ]);
  }
  y += 2;

  // ─── 8) ESCALA DE STATUS ───
  sectionTitle((version.mode !== MODE_GLOBAL_POINTS ? "7." : "6.") + " Tabela de Status da Escala");
  writeText("Os símbolos de status usados na escala diária definem se o empregado entra ou não na divisão da gorjeta:", { fontSize: 10, lineHeight: 5, gap: 2 });
  doc.autoTable({
    startY: y,
    head: [["Sigla", "Status", "Recebe gorjeta?"]],
    body: [
      ["T",   "Trabalho normal", "Sim"],
      ["F",   "Folga programada", "Não"],
      ["FL",  "Freela no dia (cobrindo plantão)", "Não — recebe por outro mecanismo"],
      ["FC",  "Folga compensada (banco de horas — folgando)", "Não"],
      ["TC",  "Trabalho compensado (banco de horas — devendo)", "Sim"],
      ["Fér", "Férias", "Não"],
      ["FJ",  "Falta justificada", "Não"],
      ["FI",  "Falta injustificada", "Não"],
    ],
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 2.5, textColor: TEXT, lineColor: BORDER, lineWidth: 0.2 },
    headStyles: { fillColor: ACCENT, textColor: [255,255,255], fontStyle: "bold", fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 18, halign: "center", fontStyle: "bold" },
      1: { cellWidth: 70 },
      2: { cellWidth: "auto" },
    },
    margin: { left: MARGIN, right: MARGIN },
  });
  y = doc.lastAutoTable.finalY + 6;

  // ─── 9) OBSERVAÇÃO DA REGRA ───
  if (version.note && version.note.trim()) {
    sectionTitle((version.mode !== MODE_GLOBAL_POINTS ? "8." : "7.") + " Observação Adicional");
    writeText(version.note.trim(), { fontSize: 10, lineHeight: 5, gap: 4 });
  }

  // ─── 10) VALIDADE E COMPROMISSO ───
  sectionTitle("Validade e Compromisso");
  writeText(
    `A regra acima passa a vigorar a partir de ${fmtDate(version.effectiveFrom)} e permanece em vigor até que outra ata, devidamente assinada, a substitua. Os colaboradores listados a seguir, ao assinar, declaram ciência e concordância com os termos aqui detalhados, comprometendo-se a respeitá-los enquanto vigentes. O histórico de regras anteriores e respectivas atas permanecerá registrado para fins de auditoria e consulta.`,
    { fontSize: 10, lineHeight: 5, gap: 5 }
  );

  // ─── 11) VOTAÇÃO + ASSINATURAS ───
  sectionTitle("Votação e Assinaturas");
  writeText(
    "Cada participante marca abaixo seu voto (FAVOR, CONTRA ou ABSTÉM) com um X no quadrado correspondente e assina ao lado para confirmar o voto registrado.",
    { fontSize: 9, lineHeight: 4.5, color: TEXT2, gap: 3 }
  );
  const voters = version.ata?.voters || [];
  if (voters.length === 0) {
    writeText("(Nenhum participante selecionado.)", { fontSize: 10, color: TEXT3, gap: 4 });
  } else {
    doc.autoTable({
      startY: y,
      head: [["Nome completo", "CPF", "Cargo", "FAVOR", "CONTRA", "ABSTÉM", "Assinatura"]],
      body: voters.map(v => [v.name, v.cpf || "—", v.roleName || "—", "", "", "", ""]),
      theme: "grid",
      styles: { fontSize: 8.5, cellPadding: 2.5, textColor: TEXT, lineColor: BORDER, lineWidth: 0.2, minCellHeight: 13, valign: "middle" },
      headStyles: { fillColor: ACCENT, textColor: [255,255,255], fontStyle: "bold", fontSize: 6.5, halign: "center", cellPadding: { top: 2, right: 0.5, bottom: 2, left: 0.5 }, overflow: "visible" },
      columnStyles: {
        0: { cellWidth: 44 },                             // Nome
        1: { cellWidth: 26 },                             // CPF
        2: { cellWidth: 20 },                             // Cargo
        3: { cellWidth: 15, halign: "center" },           // FAVOR
        4: { cellWidth: 16, halign: "center" },           // CONTRA
        5: { cellWidth: 16, halign: "center" },           // ABSTÉM
        6: { cellWidth: "auto" },                         // Assinatura (~33mm)
      },
      margin: { left: MARGIN, right: MARGIN },
      // Desenha quadradinho centralizado nas células de voto pra ficar claro onde marcar
      didDrawCell: function(data) {
        if (data.section === "body" && (data.column.index === 3 || data.column.index === 4 || data.column.index === 5)) {
          const boxSize = 4;
          const cx = data.cell.x + data.cell.width / 2 - boxSize / 2;
          const cy = data.cell.y + data.cell.height / 2 - boxSize / 2;
          doc.setDrawColor(...TEXT2);
          doc.setLineWidth(0.4);
          doc.rect(cx, cy, boxSize, boxSize, "S");
        }
      },
    });
    y = doc.lastAutoTable.finalY + 6;

    // Linha de resumo do resultado (a ser preenchido manualmente)
    writeText(
      "Resultado da votação:  Favoráveis: _____   Contrários: _____   Abstenções: _____   Total: " + voters.length,
      { fontSize: 10, fontStyle: "bold", lineHeight: 5, gap: 8 }
    );
  }

  // ─── 12) RESPONSÁVEL PELA ASSEMBLEIA ───
  // Nome / CPF / Assinatura ficam em branco para preenchimento manual no ato da assembleia.
  ensureSpace(40);
  writeText("Responsável pela Assembleia:", { fontStyle: "bold", fontSize: 10, lineHeight: 6 });
  writeText("Nome: _______________________________________", { fontSize: 10, lineHeight: 8 });
  writeText("CPF: _______________________________________", { fontSize: 10, lineHeight: 8 });
  writeText("Assinatura: _______________________________________", { fontSize: 10, lineHeight: 8 });
  y += 4;
  writeText(`Local: ${restaurant?.name || "—"} · Data: ___ / ___ / _____`, { fontSize: 10, lineHeight: 5 });

  // ─── RODAPÉ EM TODAS AS PÁGINAS ───
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...TEXT3);
    doc.text(`Documento gerado pelo sistema em ${new Date().toLocaleString("pt-BR")} · ID da regra: ${version.id}`, pageW / 2, pageH - 12, { align: "center" });
    doc.text(`Página ${i} de ${totalPages}`, pageW / 2, pageH - 8, { align: "center" });
  }

  doc.save(`ata_divisao_gorjetas_${(restaurant?.name||"rest").replace(/\s+/g,"_").toLowerCase()}_${version.effectiveFrom}.pdf`);
}

function OperationalGorjetas({ employee, data, onUpdate, isOwner }) {
  const restaurantId = employee.restaurantId;
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const mk = monthKey(year, month);
  const ac = "var(--ac)";
  const isMobile = useMobile();

  const tips = (data?.tips || []).filter(t => t.restaurantId === restaurantId && t.monthKey === mk);
  const employees = (data?.employees || []).filter(e => e.restaurantId === restaurantId);
  const roles = (data?.roles || []).filter(r => r.restaurantId === restaurantId);
  const restaurant = (data?.restaurants || []).find(r => r.id === restaurantId);

  function navMonth(delta) {
    const d = new Date(year, month + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
  }

  // Totais do mês
  const totalNet = tips.reduce((a, t) => a + (t.myNet ?? 0), 0);
  const totalTax = tips.reduce((a, t) => a + (t.myTax ?? 0), 0);
  const totalGross = totalNet + totalTax;

  // Por dia
  const byDate = {};
  tips.forEach(t => {
    if (!byDate[t.date]) byDate[t.date] = { date: t.date, total: 0, net: 0, tax: 0, empCount: new Set() };
    byDate[t.date].total += (t.myNet ?? 0) + (t.myTax ?? 0);
    byDate[t.date].net += (t.myNet ?? 0);
    byDate[t.date].tax += (t.myTax ?? 0);
    byDate[t.date].empCount.add(t.employeeId);
  });
  const days = Object.values(byDate).sort((a,b) => a.date.localeCompare(b.date));

  // Ranking por colaborador
  const byEmp = {};
  tips.forEach(t => {
    if (!byEmp[t.employeeId]) byEmp[t.employeeId] = { employeeId: t.employeeId, gross: 0, net: 0, tax: 0, daysCount: new Set(), tipsList: [] };
    byEmp[t.employeeId].gross += (t.myShare ?? 0);
    byEmp[t.employeeId].net += (t.myNet ?? 0);
    byEmp[t.employeeId].tax += (t.myTax ?? 0);
    byEmp[t.employeeId].daysCount.add(t.date);
    byEmp[t.employeeId].tipsList.push({ date: t.date, myNet: t.myNet ?? 0, myShare: t.myShare ?? 0, myTax: t.myTax ?? 0, poolTotal: t.poolTotal ?? 0 });
  });
  // Pool real do dia (soma de myShare de todos do dia) — pra calcular % de cada um
  const poolByDate = {};
  tips.forEach(t => {
    if (!poolByDate[t.date]) poolByDate[t.date] = 0;
    poolByDate[t.date] += (t.myShare ?? 0);
  });
  const ranking = Object.values(byEmp)
    .map(r => {
      const emp = employees.find(e => e.id === r.employeeId);
      const role = emp ? roles.find(rl => rl.id === emp.roleId) : null;
      const tipsList = r.tipsList
        .map(t => ({ ...t, dayPool: poolByDate[t.date] || 0, sharePct: (poolByDate[t.date] > 0 ? (t.myShare / poolByDate[t.date]) * 100 : 0) }))
        .sort((a,b) => a.date.localeCompare(b.date));
      return { ...r, emp, role, days: r.daysCount.size, tipsList };
    })
    .sort((a,b) => b.net - a.net);

  const [expandedEmpId, setExpandedEmpId] = useState(null);
  const [exportingContabil, setExportingContabil] = useState(false);
  const [importContabilForm, setImportContabilForm] = useState(null); // null | { rawText, parsed: [{empId, name, code}], unmatched: [...] }

  // Parser flexível pra "lista CPF/nome → código contábil"
  // Aceita CSV, TSV, "001 ALEZIO 062.720.413-90", "ALEZIO - 001", etc.
  function parseImportContabilLines(rawText) {
    if (!rawText || !employees) return { matched: [], unmatched: [], conflicts: [] };
    const cleanCpf = s => (s || "").replace(/\D/g, "");
    const norm = s => (s || "").trim().toUpperCase().replace(/\s+/g, " ");
    const lines = rawText.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    const matched = [];
    const unmatched = [];
    const conflicts = [];
    lines.forEach(line => {
      const parts = line.split(/[\t,;|]+| {2,}| - /).map(p => p.trim()).filter(Boolean);
      if (parts.length < 2) { unmatched.push(line); return; }
      let cpf = null, code = null, name = null;
      parts.forEach(p => {
        const digits = cleanCpf(p);
        if (digits.length === 11 && !cpf) cpf = digits;
        else if (digits.length >= 3 && digits.length <= 10 && /^\d+$/.test(digits) && !code) code = digits;
        else if (/[a-zA-ZÀ-ú]/.test(p) && !name) name = p;
      });
      if (!code) { unmatched.push(line); return; }
      let candidates = [];
      if (cpf) candidates = employees.filter(e => cleanCpf(e.cpf) === cpf);
      if (!candidates.length && name) {
        const target = norm(name);
        candidates = employees.filter(e => norm(e.name) === target);
        if (!candidates.length) {
          candidates = employees.filter(e => {
            const en = norm(e.name);
            return en.includes(target) || target.includes(en);
          });
        }
      }
      if (candidates.length === 0) { unmatched.push(line); return; }
      if (candidates.length > 1) { conflicts.push({ line, candidates: candidates.map(c => c.name) }); return; }
      matched.push({ empId: candidates[0].id, empName: candidates[0].name, code });
    });
    return { matched, unmatched, conflicts };
  }

  function applyImportContabil() {
    if (!importContabilForm || !onUpdate) return;
    const { matched } = importContabilForm.parsed || { matched: [] };
    if (matched.length === 0) return;
    const codeByEmp = {};
    matched.forEach(m => { codeByEmp[m.empId] = m.code; });
    const allEmps = data?.employees || [];
    const updated = allEmps.map(e => codeByEmp[e.id] ? { ...e, codigoContabil: codeByEmp[e.id] } : e);
    onUpdate("employees", updated);
    onUpdate("_toast", `📥 ${matched.length} código(s) atualizado(s)`);
    setImportContabilForm(null);
  }

  // Export Excel da Contabilidade (formato Planilha de Lançamentos)
  async function exportContabil(targetYear = year, targetMonth = month) {
    setExportingContabil(true);
    try {
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js");
      const XLSX = window.XLSX;
      const targetMk = monthKey(targetYear, targetMonth);
      const monthTips = (data?.tips || []).filter(t => t.restaurantId === restaurantId && t.monthKey === targetMk);
      const codigoContabil = restaurant?.codigoContabil || "";
      const layoutCode = codigoContabil || "0000";
      const companyName = (restaurant?.razaoSocial || restaurant?.name || "").toUpperCase();
      const monthLbl = `${String(targetMonth+1).padStart(2,"0")}/${targetYear}`;

      const monthStart = `${targetYear}-${String(targetMonth+1).padStart(2,"0")}-01`;
      const empRows = (employees || [])
        .filter(e => {
          if (e.demitidoEm && e.demitidoEm <= monthStart) return false;
          if (e.inactive && e.inactiveFrom && e.inactiveFrom < monthStart) {
            return monthTips.some(t => t.employeeId === e.id);
          }
          return true;
        })
        .map(e => {
          // Valor exportado é o BRUTO (myShare) — a contabilidade aplica os impostos depois.
          const empTipBruto = monthTips
            .filter(t => t.employeeId === e.id)
            .reduce((s, t) => s + (t.myShare ?? 0), 0);
          // Código da contabilidade: se não preenchido, fica vazio (não usa empCode interno)
          const codigo = e.codigoContabil || "";
          const cpfFormatted = (() => {
            const d = (e.cpf || "").replace(/\D/g, "");
            if (d.length !== 11) return e.cpf || "";
            return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9,11)}`;
          })();
          const classe = e.isProlaborista ? "3 - Prolaborista" : "1 - Assalariado";
          return [codigo, (e.name || "").toUpperCase(), cpfFormatted, classe, empTipBruto > 0 ? Number(empTipBruto.toFixed(2)) : "", ""];
        })
        .sort((a, b) => (a[1] || "").localeCompare(b[1] || ""));

      const wsData = [
        [`${codigoContabil ? `${codigoContabil} - ` : ""}${companyName}`, "", "", "", "", ""],
        [`Planilha de Lançamentos - ${monthLbl}`, "", "", "", "", ""],
        ["Código", "Nome", "CPF", "Classe", "Gorjeta SN - Lei 134", "Adic. noturno horas", "Qtd Colunas Auxiliares:#2", `Layout:#${layoutCode}`],
        ...empRows.map(r => [...r, "CodContInterm:#0", "Qtd Colunas Auxiliares:#2", `Layout:#${layoutCode}`]),
        [`TOTAL DE COLABORADORES: `, ` ${empRows.length}`],
      ];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws["!cols"] = [{ wch: 10 }, { wch: 30 }, { wch: 14 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 25 }, { wch: 14 }, { wch: 14 }];
      ws["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } },
        { s: { r: wsData.length - 1, c: 0 }, e: { r: wsData.length - 1, c: 1 } },
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Lançamentos");
      const fileName = `Gorjetas_${(restaurant?.name || "restaurante").replace(/\s+/g, "_")}_${targetYear}-${String(targetMonth+1).padStart(2,"0")}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (e) { console.error("export contabil error:", e); window.alert("Erro ao exportar planilha. Tente novamente."); }
    setExportingContabil(false);
  }

  // Semana corrente (últimos 7 dias do mês ou do mês atual)
  const today_ = today();
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth();
  const currentMonday = isCurrentMonth ? getWeekMonday(today_) : null;
  const weekTips = currentMonday ? tips.filter(t => getWeekMonday(t.date) === currentMonday) : [];
  const weekTotal = weekTips.reduce((a, t) => a + (t.myNet ?? 0) + (t.myTax ?? 0), 0);

  return (
    <div>
      {/* Header + navegação de mês */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12,marginBottom:16}}>
        <div>
          <div style={{fontSize:11,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Gorjetas — visão operacional</div>
          <div style={{fontSize:17,color:"var(--text)",fontWeight:700,marginTop:2}}>{restaurant?.name ?? "Restaurante"}</div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          {onUpdate && (
            <button onClick={()=>setImportContabilForm({ rawText: "", parsed: null })}
              title="Colar lista de empregados + código contábil pra preencher os cadastros em massa"
              style={{padding:"8px 14px",borderRadius:10,border:"1px solid #3b82f644",background:"#3b82f611",color:"#3b82f6",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:"'DM Sans',sans-serif"}}>
              📥 Importar códigos contábeis
            </button>
          )}
          <button onClick={()=>exportContabil()} disabled={exportingContabil || tips.length === 0}
            title={tips.length === 0 ? "Nenhuma gorjeta lançada neste mês" : "Baixar planilha do mês pra enviar pra contabilidade"}
            style={{padding:"8px 14px",borderRadius:10,border:"1px solid var(--green)44",background:tips.length===0?"transparent":"var(--green)11",color:"var(--green)",cursor:tips.length===0?"not-allowed":"pointer",fontSize:12,fontWeight:600,fontFamily:"'DM Sans',sans-serif",opacity:tips.length===0?0.5:1}}>
            {exportingContabil ? "⏳ Exportando..." : "📊 Exportar Excel da contabilidade"}
          </button>
          <div style={{display:"flex",gap:4,alignItems:"center",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,padding:4}}>
            <button onClick={()=>navMonth(-1)} style={{background:"none",border:"none",padding:"4px 10px",cursor:"pointer",fontSize:14,color:"var(--text2)"}}>‹</button>
            <span style={{fontSize:13,color:"var(--text)",fontWeight:700,minWidth:140,textAlign:"center",textTransform:"capitalize"}}>{monthLabel(year, month)}</span>
            <button onClick={()=>navMonth(1)} style={{background:"none",border:"none",padding:"4px 10px",cursor:"pointer",fontSize:14,color:"var(--text2)"}}>›</button>
          </div>
        </div>
      </div>

      {/* Cards de totais */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(160px, 1fr))",gap:12,marginBottom:16}}>
        <div style={{padding:"14px 16px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:12}}>
          <div style={{fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Bruto do mês</div>
          <div style={{fontSize:18,color:"var(--text)",fontWeight:700,fontFamily:"'DM Mono',monospace",marginTop:2}}>{fmt(totalGross)}</div>
          <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>{days.length} dia(s) com gorjeta</div>
        </div>
        <div style={{padding:"14px 16px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:12}}>
          <div style={{fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Líquido pago</div>
          <div style={{fontSize:18,color:"#15803d",fontWeight:700,fontFamily:"'DM Mono',monospace",marginTop:2}}>{fmt(totalNet)}</div>
          <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>aos colaboradores</div>
        </div>
        <div style={{padding:"14px 16px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:12}}>
          <div style={{fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Imposto retido</div>
          <div style={{fontSize:18,color:"var(--text2)",fontWeight:700,fontFamily:"'DM Mono',monospace",marginTop:2}}>{fmt(totalTax)}</div>
          <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>{totalGross > 0 ? `${((totalTax/totalGross)*100).toFixed(1)}%` : "—"}</div>
        </div>
        {isCurrentMonth && (
          <div style={{padding:"14px 16px",background:ac+"22",border:`1px solid ${ac}66`,borderRadius:12}}>
            <div style={{fontSize:10,color:ac,fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Semana atual</div>
            <div style={{fontSize:18,color:"var(--text)",fontWeight:700,fontFamily:"'DM Mono',monospace",marginTop:2}}>{fmt(weekTotal)}</div>
            <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>desde seg {currentMonday ? fmtDate(currentMonday) : "—"}</div>
          </div>
        )}
      </div>

      {/* Por dia */}
      <div style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:12,overflow:"hidden",marginBottom:16}}>
        <div style={{padding:"10px 16px",borderBottom:"1px solid var(--border)",background:"var(--bg2)"}}>
          <div style={{fontSize:13,color:"var(--text)",fontWeight:700}}>📅 Dias com gorjeta lançada</div>
        </div>
        {days.length === 0 ? (
          <div style={{padding:"32px 16px",textAlign:"center",color:"var(--text3)",fontSize:13}}>
            Nenhuma gorjeta lançada neste mês.
          </div>
        ) : (
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:isMobile?12:13}}>
              <thead>
                <tr style={{borderBottom:"1px solid var(--border)"}}>
                  <th style={{padding:isMobile?"8px 10px":"8px 14px",textAlign:"left",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Dia</th>
                  <th style={{padding:isMobile?"8px 6px":"8px 12px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>{isMobile?"Líq.":"Bruto"}</th>
                  {!isMobile && <th style={{padding:"8px 12px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Líquido</th>}
                  <th style={{padding:isMobile?"8px 10px":"8px 12px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Col.</th>
                </tr>
              </thead>
              <tbody>
                {days.map(d => (
                  <tr key={d.date} style={{borderTop:"1px solid var(--border)"}}>
                    <td style={{padding:isMobile?"8px 10px":"8px 14px",color:"var(--text)"}}>{fmtDate(d.date)} <span style={{color:"var(--text3)",fontSize:11,marginLeft:4}}>{WEEKDAYS[new Date(d.date + "T12:00:00").getDay()]}</span></td>
                    {!isMobile && <td style={{padding:"8px 12px",textAlign:"right",color:"var(--text2)",fontFamily:"'DM Mono',monospace"}}>{fmt(d.total)}</td>}
                    <td style={{padding:isMobile?"8px 6px":"8px 12px",textAlign:"right",color:"#15803d",fontFamily:"'DM Mono',monospace",fontWeight:600}}>{fmt(d.net)}</td>
                    <td style={{padding:isMobile?"8px 10px":"8px 12px",textAlign:"right",color:"var(--text3)"}}>{d.empCount.size}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Ranking */}
      <div style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:12,overflow:"hidden"}}>
        <div style={{padding:"10px 16px",borderBottom:"1px solid var(--border)",background:"var(--bg2)"}}>
          <div style={{fontSize:13,color:"var(--text)",fontWeight:700}}>🏆 Ranking por colaborador</div>
        </div>
        {ranking.length === 0 ? (
          <div style={{padding:"32px 16px",textAlign:"center",color:"var(--text3)",fontSize:13}}>
            Nenhum colaborador recebeu gorjeta neste mês.
          </div>
        ) : isMobile ? (
          /* Mobile: cards empilhados (tabela não cabe em 375px) */
          <div>
            {ranking.map((r, idx) => {
              const areaColor = r.role?.area ? AREA_COLORS[r.role.area] : null;
              const medal = idx===0?"🥇":idx===1?"🥈":idx===2?"🥉":null;
              const isExpanded = expandedEmpId === r.employeeId;
              return (
                <div key={r.employeeId} style={{borderTop:"1px solid var(--border)"}}>
                  <div onClick={()=>setExpandedEmpId(isExpanded?null:r.employeeId)} style={{padding:"12px 14px",display:"flex",alignItems:"center",gap:12,cursor:"pointer",background:isExpanded?"var(--bg2)":"transparent"}}>
                    <div style={{minWidth:32,textAlign:"center",fontSize:medal?20:15,fontFamily:"'DM Mono',monospace",color:idx<3?"var(--ac)":"var(--text3)",fontWeight:idx<3?700:400}}>
                      {medal || (idx+1)}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{color:"var(--text)",fontWeight:600,fontSize:14,lineHeight:1.2}}>
                        <span style={{color:"var(--text3)",marginRight:4,fontSize:9}}>{isExpanded?"▼":"▶"}</span>
                        {r.emp?.name ?? "—"}
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:6,marginTop:3,flexWrap:"wrap"}}>
                        {r.role && <span style={{fontSize:11,color:"var(--text2)"}}>
                          <span style={{display:"inline-block",width:6,height:6,borderRadius:3,background:areaColor ?? "var(--text3)",marginRight:5,verticalAlign:"middle"}}></span>
                          {r.role.name}
                        </span>}
                        <span style={{fontSize:11,color:"var(--text3)"}}>· {r.days} dia{r.days!==1?"s":""}</span>
                      </div>
                    </div>
                    <div style={{textAlign:"right",color:"#15803d",fontFamily:"'DM Mono',monospace",fontWeight:700,fontSize:14,whiteSpace:"nowrap"}}>
                      {fmt(r.net)}
                    </div>
                  </div>
                  {isExpanded && (
                    <div style={{padding:"4px 14px 14px",background:"var(--bg2)"}}>
                      <div style={{fontSize:10,color:"var(--text3)",marginBottom:6,fontWeight:600,textTransform:"uppercase"}}>📅 {r.tipsList.length} dia(s) — Pool · % · Líquido</div>
                      <div style={{display:"flex",flexDirection:"column",gap:4}}>
                        {r.tipsList.map((t,i) => (
                          <div key={i} style={{padding:"6px 10px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:8,display:"grid",gridTemplateColumns:"auto 1fr auto",gap:8,alignItems:"center",fontSize:11}}>
                            <span style={{color:"var(--text2)"}}>{fmtDate(t.date)} <span style={{color:"var(--text3)",fontSize:9}}>{WEEKDAYS[new Date(t.date+"T12:00:00").getDay()]}</span></span>
                            <span style={{color:"var(--text3)",fontFamily:"'DM Mono',monospace",textAlign:"right",fontSize:10}}>{fmt(t.dayPool)} · <strong style={{color:"var(--text2)"}}>{t.sharePct.toFixed(2)}%</strong></span>
                            <span style={{color:"#15803d",fontFamily:"'DM Mono',monospace",fontWeight:700}}>{fmt(t.myNet)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
              <thead>
                <tr style={{borderBottom:"1px solid var(--border)"}}>
                  <th style={{padding:"8px 14px",textAlign:"left",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,width:40}}>#</th>
                  <th style={{padding:"8px 14px",textAlign:"left",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Colaborador</th>
                  <th style={{padding:"8px 12px",textAlign:"left",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Área</th>
                  <th style={{padding:"8px 12px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Dias</th>
                  <th style={{padding:"8px 12px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Bruto</th>
                  <th style={{padding:"8px 12px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Imposto</th>
                  <th style={{padding:"8px 12px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Líquido</th>
                </tr>
              </thead>
              <tbody>
                {ranking.map((r, idx) => {
                  const areaColor = r.role?.area ? AREA_COLORS[r.role.area] : null;
                  const isExpanded = expandedEmpId === r.employeeId;
                  return (
                    <React.Fragment key={r.employeeId}>
                      <tr style={{borderTop:"1px solid var(--border)",cursor:"pointer",background:isExpanded?"var(--bg2)":"transparent"}}
                          onClick={()=>setExpandedEmpId(isExpanded ? null : r.employeeId)}
                          title="Clique para ver detalhamento dos dias">
                        <td style={{padding:"8px 14px",color:idx<3?"var(--ac)":"var(--text3)",fontWeight:idx<3?700:400,fontFamily:"'DM Mono',monospace"}}>{idx===0?"🥇":idx===1?"🥈":idx===2?"🥉":idx+1}</td>
                        <td style={{padding:"8px 14px",color:"var(--text)",fontWeight:600}}>
                          <span style={{color:"var(--text3)",marginRight:6,fontSize:10}}>{isExpanded?"▼":"▶"}</span>
                          {r.emp?.name ?? "—"}
                        </td>
                        <td style={{padding:"8px 12px",color:"var(--text2)",fontSize:12}}>
                          {r.role ? (
                            <span>
                              <span style={{display:"inline-block",width:8,height:8,borderRadius:4,background:areaColor ?? "var(--text3)",marginRight:6}}></span>
                              {r.role.name}
                            </span>
                          ) : "—"}
                        </td>
                        <td style={{padding:"8px 12px",textAlign:"right",color:"var(--text3)"}}>{r.days}</td>
                        <td style={{padding:"8px 12px",textAlign:"right",color:"var(--text2)",fontFamily:"'DM Mono',monospace"}}>{fmt(r.gross)}</td>
                        <td style={{padding:"8px 12px",textAlign:"right",color:"#dc2626",fontFamily:"'DM Mono',monospace"}}>{fmt(r.tax)}</td>
                        <td style={{padding:"8px 12px",textAlign:"right",color:"#15803d",fontFamily:"'DM Mono',monospace",fontWeight:700}}>{fmt(r.net)}</td>
                      </tr>
                      {isExpanded && (
                        <tr style={{background:"var(--bg2)"}}>
                          <td></td>
                          <td colSpan={6} style={{padding:"6px 14px 14px"}}>
                            <div style={{fontSize:11,color:"var(--text3)",marginBottom:6,fontWeight:600,textTransform:"uppercase",letterSpacing:0.4}}>📅 Detalhamento — {r.tipsList.length} dia(s)</div>
                            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:8,overflow:"hidden"}}>
                              <thead>
                                <tr style={{background:"var(--bg2)"}}>
                                  <th style={{padding:"6px 10px",textAlign:"left",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.3}}>Data</th>
                                  <th style={{padding:"6px 10px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.3}}>Pool (bruto)</th>
                                  <th style={{padding:"6px 10px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.3}}>% dele</th>
                                  <th style={{padding:"6px 10px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.3}}>Bruto</th>
                                  <th style={{padding:"6px 10px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.3}}>Líquido</th>
                                </tr>
                              </thead>
                              <tbody>
                                {r.tipsList.map((t,i) => (
                                  <tr key={i} style={{borderTop:"1px solid var(--border)"}}>
                                    <td style={{padding:"5px 10px",color:"var(--text2)"}}>{fmtDate(t.date)} <span style={{color:"var(--text3)",fontSize:10}}>{WEEKDAYS[new Date(t.date+"T12:00:00").getDay()]}</span></td>
                                    <td style={{padding:"5px 10px",textAlign:"right",color:"var(--text2)",fontFamily:"'DM Mono',monospace"}}>{fmt(t.dayPool)}</td>
                                    <td style={{padding:"5px 10px",textAlign:"right",color:"var(--text3)",fontFamily:"'DM Mono',monospace"}}>{t.sharePct.toFixed(2)}%</td>
                                    <td style={{padding:"5px 10px",textAlign:"right",color:"var(--text2)",fontFamily:"'DM Mono',monospace"}}>{fmt(t.myShare)}</td>
                                    <td style={{padding:"5px 10px",textAlign:"right",color:"#15803d",fontFamily:"'DM Mono',monospace",fontWeight:700}}>{fmt(t.myNet)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* HISTÓRICO — meses com escala fechada, com botão de exportar cada um */}
      {(() => {
        const allTipsRest = (data?.tips || []).filter(t => t.restaurantId === restaurantId);
        const allMksWithTips = [...new Set(allTipsRest.map(t => t.monthKey))];
        const closedMks = allMksWithTips
          .filter(mkk => data?.scheduleStatus?.[restaurantId]?.[mkk]?.status === "closed")
          .filter(mkk => mkk !== mk) // não mostra o mês atualmente exibido (já tem header e export)
          .sort((a, b) => b.localeCompare(a)); // mais recente primeiro
        if (closedMks.length === 0) return null;
        return (
          <div style={{marginTop:24,background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:12,overflow:"hidden"}}>
            <div style={{padding:"10px 16px",borderBottom:"1px solid var(--border)",background:"var(--bg2)"}}>
              <div style={{fontSize:13,color:"var(--text)",fontWeight:700}}>📚 Histórico — meses fechados ({closedMks.length})</div>
              <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>Exporte a planilha de qualquer mês com escala fechada pra enviar pra contabilidade.</div>
            </div>
            <div>
              {closedMks.map(mkk => {
                const [yy, mm] = mkk.split("-").map(Number);
                const mkkTips = allTipsRest.filter(t => t.monthKey === mkk);
                const monthGross = mkkTips.reduce((s, t) => s + (t.myShare ?? 0), 0);
                const monthNet = mkkTips.reduce((s, t) => s + (t.myNet ?? 0), 0);
                const empCount = new Set(mkkTips.map(t => t.employeeId)).size;
                const closedAt = data?.scheduleStatus?.[restaurantId]?.[mkk]?.closedAt;
                return (
                  <div key={mkk} style={{padding:"12px 16px",borderTop:"1px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:14,color:"var(--text)",fontWeight:700,textTransform:"capitalize"}}>{monthLabel(yy, mm-1)}</div>
                      <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>
                        {empCount} empregado(s) · Bruto {fmt(monthGross)} · Líquido {fmt(monthNet)}
                        {closedAt && <span> · fechado em {new Date(closedAt).toLocaleDateString("pt-BR")}</span>}
                      </div>
                    </div>
                    <div style={{display:"flex",gap:8}}>
                      <button onClick={()=>{ setYear(yy); setMonth(mm-1); }}
                        style={{...S.btnSecondary,fontSize:11,padding:"5px 10px"}}>
                        Ver detalhes
                      </button>
                      <button onClick={()=>exportContabil(yy, mm-1)} disabled={exportingContabil}
                        style={{padding:"5px 12px",borderRadius:8,border:"1px solid var(--green)44",background:"var(--green)11",color:"var(--green)",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:"'DM Sans',sans-serif"}}>
                        📊 Exportar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      <div style={{marginTop:14,fontSize:11,color:"var(--text3)",lineHeight:1.5}}>
        💡 Visão read-only dos dados já processados pelo Gestor Administrativo. Para lançar novas gorjetas, fechar semana ou editar a divisão, use o Portal Adm (botão 📊 no topo).
      </div>

      {/* Modal: Importar códigos contábeis em massa */}
      {importContabilForm && (() => {
        const parsed = importContabilForm.parsed;
        return (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={()=>setImportContabilForm(null)}>
            <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:14,maxWidth:680,width:"100%",maxHeight:"85vh",overflowY:"auto",padding:24}}>
              <h3 style={{margin:"0 0 8px",color:"#3b82f6",fontSize:16,fontWeight:700}}>📥 Importar códigos contábeis</h3>
              <p style={{color:"var(--text2)",fontSize:12,margin:"0 0 12px",lineHeight:1.5}}>
                Cole uma lista no formato de sua escolha. O sistema identifica CPF, nome e código automaticamente. Tenta casar primeiro pelo CPF, depois pelo nome.
              </p>
              <details style={{marginBottom:12,fontSize:11,color:"var(--text3)"}}>
                <summary style={{cursor:"pointer",color:"#3b82f6"}}>Ver exemplos de formato aceito</summary>
                <div style={{marginTop:8,padding:"8px 10px",background:"var(--bg2)",borderRadius:6,fontFamily:"'DM Mono',monospace",fontSize:10,whiteSpace:"pre-wrap"}}>{`# Tab/CSV (cole de Excel/Sheets):
000045	ALEZIO MATOS DA SILVA	062.720.413-90
000025	ANTONIA NAYARA SOARES DE SOUSA	383.998.798-94

# Lista simples:
ALEZIO MATOS DA SILVA, 000045
ANTONIA, 000025

# Com hífen:
DIEGO FERREIRA - 000004`}</div>
              </details>
              <textarea
                value={importContabilForm.rawText}
                onChange={e=>setImportContabilForm({...importContabilForm, rawText: e.target.value, parsed: null})}
                placeholder="Cole aqui as linhas..."
                rows={10}
                style={{...S.input,fontFamily:"'DM Mono',monospace",fontSize:12,width:"100%",boxSizing:"border-box",resize:"vertical"}}
              />
              <div style={{display:"flex",gap:8,marginTop:10,marginBottom:14}}>
                <button onClick={()=>{
                  const result = parseImportContabilLines(importContabilForm.rawText);
                  setImportContabilForm({...importContabilForm, parsed: result});
                }} disabled={!importContabilForm.rawText.trim()}
                  style={{...S.btnSecondary,fontSize:12,padding:"6px 12px",opacity:importContabilForm.rawText.trim()?1:0.5}}>
                  🔍 Analisar
                </button>
              </div>
              {parsed && (
                <div style={{padding:"10px 12px",background:"var(--bg2)",borderRadius:8,marginBottom:14,fontSize:12,color:"var(--text2)",lineHeight:1.6}}>
                  <div style={{color:"var(--green)"}}>✅ <strong>{parsed.matched.length}</strong> empregado(s) com código identificado</div>
                  {parsed.unmatched.length > 0 && <div style={{color:"#f59e0b",marginTop:4}}>⚠️ <strong>{parsed.unmatched.length}</strong> linha(s) sem match (não bateu CPF nem nome)</div>}
                  {parsed.conflicts.length > 0 && <div style={{color:"#ef4444",marginTop:4}}>❌ <strong>{parsed.conflicts.length}</strong> conflito(s) (mesmo nome bateu com vários empregados — adicione o CPF pra desambiguar)</div>}
                  {parsed.matched.length > 0 && (
                    <details style={{marginTop:8}}>
                      <summary style={{cursor:"pointer",fontSize:11,color:"var(--text3)"}}>Ver matches ({parsed.matched.length})</summary>
                      <div style={{marginTop:6,maxHeight:160,overflowY:"auto",fontSize:11,fontFamily:"'DM Mono',monospace"}}>
                        {parsed.matched.map((m,i) => (
                          <div key={i} style={{padding:"2px 0"}}>
                            <strong style={{color:"#3b82f6"}}>{m.code}</strong> → {m.empName}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                  {parsed.unmatched.length > 0 && (
                    <details style={{marginTop:6}}>
                      <summary style={{cursor:"pointer",fontSize:11,color:"#f59e0b"}}>Ver linhas sem match ({parsed.unmatched.length})</summary>
                      <div style={{marginTop:6,maxHeight:120,overflowY:"auto",fontSize:11,fontFamily:"'DM Mono',monospace",color:"var(--text3)"}}>
                        {parsed.unmatched.map((l,i) => <div key={i} style={{padding:"2px 0"}}>{l}</div>)}
                      </div>
                    </details>
                  )}
                  {parsed.conflicts.length > 0 && (
                    <details style={{marginTop:6}}>
                      <summary style={{cursor:"pointer",fontSize:11,color:"#ef4444"}}>Ver conflitos ({parsed.conflicts.length})</summary>
                      <div style={{marginTop:6,maxHeight:120,overflowY:"auto",fontSize:11,fontFamily:"'DM Mono',monospace",color:"var(--text3)"}}>
                        {parsed.conflicts.map((c,i) => <div key={i} style={{padding:"2px 0"}}>{c.line} → {c.candidates.join(" | ")}</div>)}
                      </div>
                    </details>
                  )}
                </div>
              )}
              <div style={{display:"flex",justifyContent:"flex-end",gap:10}}>
                <button onClick={()=>setImportContabilForm(null)} style={{...S.btnSecondary,padding:"8px 14px",fontSize:13}}>Cancelar</button>
                <button onClick={applyImportContabil}
                  disabled={!parsed || parsed.matched.length === 0}
                  style={{...S.btnPrimary,padding:"8px 14px",fontSize:13,fontWeight:700,background:"#3b82f6",borderColor:"#3b82f6",opacity:(parsed && parsed.matched.length > 0)?1:0.5}}>
                  Aplicar {parsed?.matched?.length > 0 ? `(${parsed.matched.length})` : ""}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ──  RESERVAS — MÓDULO DE RESERVAS DE MESA                      ──
// ═══════════════════════════════════════════════════════════════
// 4 sub-tabs (gated por permissão): Hoje, Calendário, Clientes, Configurações
// Permissões:
//   operational.reservasOperar       — Hoje + Calendário (futuras) + criar respeitando lotação + ações
//   operational.reservasOperarForce  — Permite forçar reserva mesmo lotado
//   operational.reservasGerenciar    — Editar/cancelar + ver histórico passado
//   admin.reservasClientes           — Ver/editar lista de clientes (CRM)
//   admin.reservasConfig             — Configurações (salões, tipos mesa, turnos, mensagens)

const RESERVA_STATUS_INFO = {
  feita:       { label: "Feita",       icon: "📋", color: "#3b82f6", bg: "#3b82f622" },
  confirmada:  { label: "Confirmada",  icon: "✅", color: "#10b981", bg: "#10b98122" },
  sentou:      { label: "Sentou",      icon: "🪑", color: "#8b5cf6", bg: "#8b5cf622" },
  finalizada:  { label: "Finalizada",  icon: "✔️", color: "#64748b", bg: "#64748b22" },
  "no-show":   { label: "No-show",     icon: "⛔", color: "#ef4444", bg: "#ef444422" },
  cancelada:   { label: "Cancelada",   icon: "❌", color: "#9ca3af", bg: "#9ca3af22" },
};

const DIAS_SEMANA = ["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"];

// Helpers
function reservaPlaceholders(reserva, restaurant, cliente) {
  const dt = new Date(reserva.dateTime);
  const dataStr = dt.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  const horaStr = dt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  return {
    "{nome}":         cliente?.nome || reserva.nomeCliente || "—",
    "{data}":         dataStr,
    "{hora}":         horaStr,
    "{pessoas}":      String(reserva.party || 0),
    "{restaurante}":  restaurant?.name || "—",
  };
}
function aplicarPlaceholders(template, vals) {
  let out = template || "";
  Object.entries(vals).forEach(([k, v]) => { out = out.split(k).join(v); });
  return out;
}
function whatsAppLink(telefone, mensagem) {
  const limpo = (telefone || "").replace(/\D/g, "");
  // Brasil: se 11 dígitos (DDD+9+8 numeros) ou 10 (sem 9), adiciona 55
  let e164 = limpo;
  if (limpo.length === 10 || limpo.length === 11) e164 = "55" + limpo;
  return `https://wa.me/${e164}?text=${encodeURIComponent(mensagem || "")}`;
}

const MENSAGEM_CONFIRMACAO_DEFAULT =
  "Olá {nome}! Confirmando sua reserva no {restaurante} para {data} às {hora} para {pessoas} pessoa(s). Confirma pra mim? Estamos te esperando!";

function ReservasTab({ tab, restaurantId, restaurant, reservas, reservasConfig, reservasSalas, reservasTiposMesa, reservasClientes, currentUser, perms, isOwner, mobileOnly, onUpdate }) {
  const op = perms?.operational || {};
  const ad = perms?.admin || {};
  const canOperar    = isOwner || op.reservasOperar || op.reservasOperarForce || op.reservasGerenciar;
  const canForce     = isOwner || op.reservasOperarForce;
  const canGerenciar = isOwner || op.reservasGerenciar;
  const canClientes  = isOwner || ad.reservasClientes;
  const canConfig    = isOwner || ad.reservasConfig;

  const config = reservasConfig?.[restaurantId] || {};
  const restReservas = (reservas || []).filter(r => r.restaurantId === restaurantId);
  const restSalas    = (reservasSalas || []).filter(s => s.restaurantId === restaurantId).sort((a,b) => (a.ordem ?? 999) - (b.ordem ?? 999));
  const restTipos    = (reservasTiposMesa || []).filter(t => t.restaurantId === restaurantId).sort((a,b) => (a.ordem ?? 999) - (b.ordem ?? 999));
  const restClientes = (reservasClientes || []).filter(c => c.restaurantId === restaurantId);

  // ─── HOJE ───
  if (tab === "reservas_hoje" && canOperar) {
    return <ReservasHoje
      restaurantId={restaurantId} restaurant={restaurant}
      reservas={restReservas} clientes={restClientes}
      config={config} salas={restSalas} tipos={restTipos}
      currentUser={currentUser} canForce={canForce} canGerenciar={canGerenciar}
      onUpdate={onUpdate} mobileOnly={mobileOnly}
      allReservas={reservas} allClientes={reservasClientes}
    />;
  }
  if (tab === "reservas_calendario" && canOperar) {
    return <ReservasCalendario
      restaurantId={restaurantId} restaurant={restaurant}
      reservas={restReservas} clientes={restClientes}
      config={config} salas={restSalas} tipos={restTipos}
      currentUser={currentUser} canForce={canForce} canGerenciar={canGerenciar}
      onUpdate={onUpdate} mobileOnly={mobileOnly}
      allReservas={reservas} allClientes={reservasClientes}
    />;
  }
  if (tab === "reservas_clientes" && canClientes) {
    return <ReservasClientes
      restaurantId={restaurantId} restaurant={restaurant}
      clientes={restClientes} reservas={restReservas}
      currentUser={currentUser} mobileOnly={mobileOnly} onUpdate={onUpdate}
      allClientes={reservasClientes}
    />;
  }
  if (tab === "reservas_config" && canConfig) {
    return <ReservasConfig
      restaurantId={restaurantId} restaurant={restaurant}
      config={config} salas={restSalas} tipos={restTipos}
      reservasConfig={reservasConfig} reservasSalas={reservasSalas} reservasTiposMesa={reservasTiposMesa}
      currentUser={currentUser} mobileOnly={mobileOnly} onUpdate={onUpdate}
    />;
  }

  return (
    <div style={{padding:24, textAlign:"center", color:"var(--text3)"}}>
      <div style={{fontSize:36,marginBottom:8}}>🔒</div>
      <div style={{color:"var(--text)",fontWeight:700,fontSize:14}}>Sem permissão</div>
      <p style={{fontSize:12,marginTop:4}}>Você não tem acesso a esta sub-aba de Reservas.</p>
    </div>
  );
}

// ─── HOJE ───
function ReservasHoje({ restaurantId, restaurant, reservas, clientes, config, salas, tipos, currentUser, canForce, canGerenciar, onUpdate, mobileOnly, allReservas, allClientes }) {
  const today_ = today();
  const [filterStatus, setFilterStatus] = useState("todos"); // todos | feita | confirmada | sentou | "no-show" | cancelada
  const [showNew, setShowNew] = useState(false);
  const reservasHoje = reservas
    .filter(r => r.dateTime?.slice(0,10) === today_)
    .sort((a,b) => (a.dateTime || "").localeCompare(b.dateTime || ""));
  const filtered = filterStatus === "todos" ? reservasHoje : reservasHoje.filter(r => r.status === filterStatus);
  const counts = {};
  reservasHoje.forEach(r => { counts[r.status] = (counts[r.status] || 0) + 1; });

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:16}}>
        <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?16:20}}>📅 Reservas — Hoje</h3>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <span style={{color:"var(--text3)",fontSize:12,alignSelf:"center"}}>{new Date(today_+"T12:00:00").toLocaleDateString("pt-BR",{weekday:"long",day:"2-digit",month:"long"})}</span>
          <button onClick={()=>setShowNew(true)} style={{...S.btnPrimary,fontSize:13,padding:"8px 16px"}}>+ Nova reserva</button>
        </div>
      </div>

      {/* Filtros por status */}
      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
        <button onClick={()=>setFilterStatus("todos")} style={{padding:"5px 12px",borderRadius:8,border:`1px solid ${filterStatus==="todos"?"var(--ac)":"var(--border)"}`,background:filterStatus==="todos"?"var(--ac)11":"transparent",color:filterStatus==="todos"?"var(--ac)":"var(--text2)",cursor:"pointer",fontSize:11,fontWeight:600}}>
          Todos ({reservasHoje.length})
        </button>
        {Object.entries(RESERVA_STATUS_INFO).map(([key, info]) => (
          <button key={key} onClick={()=>setFilterStatus(key)}
            style={{padding:"5px 12px",borderRadius:8,border:`1px solid ${filterStatus===key?info.color:"var(--border)"}`,background:filterStatus===key?info.bg:"transparent",color:filterStatus===key?info.color:"var(--text2)",cursor:"pointer",fontSize:11,fontWeight:600}}>
            {info.icon} {info.label} ({counts[key] || 0})
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div style={{...S.card,padding:30,textAlign:"center",color:"var(--text3)",fontSize:13}}>
          <div style={{fontSize:36,marginBottom:8}}>📭</div>
          {reservasHoje.length === 0 ? "Nenhuma reserva hoje." : `Nenhuma reserva com status "${RESERVA_STATUS_INFO[filterStatus]?.label || filterStatus}".`}
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {filtered.map(r => (
            <ReservaCard key={r.id} r={r}
              cliente={clientes.find(c => c.id === r.clienteId)}
              salas={salas} tipos={tipos}
              restaurant={restaurant}
              config={config}
              currentUser={currentUser}
              canGerenciar={canGerenciar}
              onUpdate={onUpdate}
              allReservas={allReservas}
              allClientes={allClientes}
              restaurantId={restaurantId}
              mobileOnly={mobileOnly}
            />
          ))}
        </div>
      )}

      {showNew && (
        <ReservaModal
          restaurantId={restaurantId}
          restaurant={restaurant}
          config={config}
          salas={salas} tipos={tipos}
          clientes={clientes}
          allReservas={allReservas}
          allClientes={allClientes}
          currentUser={currentUser}
          canForce={canForce}
          onUpdate={onUpdate}
          onClose={()=>setShowNew(false)}
        />
      )}
    </div>
  );
}

// ─── CARD DE RESERVA (lista) ───
function ReservaCard({ r, cliente, salas, tipos, restaurant, config, currentUser, canGerenciar, onUpdate, allReservas, allClientes, restaurantId, mobileOnly }) {
  const status = RESERVA_STATUS_INFO[r.status] || RESERVA_STATUS_INFO.feita;
  const sala = salas.find(s => s.id === r.salaId);
  const tipo = tipos.find(t => t.id === r.tipoMesaId);
  const dt = new Date(r.dateTime);
  const horaStr = dt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

  function setStatusReserva(novoStatus, extra = {}) {
    const now = new Date().toISOString();
    const author = currentUser?.name || "Gestor";
    const updates = { status: novoStatus, ...extra };
    if (novoStatus === "confirmada") { updates.confirmadaEm = now; updates.confirmadaPor = author; }
    if (novoStatus === "sentou")     { updates.sentouEm = now; updates.sentouPor = author; }
    if (novoStatus === "finalizada") { updates.finalizouEm = now; }
    if (novoStatus === "no-show")    { updates.noShowEm = now; }
    if (novoStatus === "cancelada")  { updates.canceladaEm = now; updates.canceladaPor = author; }
    const nextReservas = (allReservas || []).map(x => x.id === r.id ? { ...x, ...updates } : x);
    onUpdate("reservas", nextReservas);
    onUpdate("_toast", `${status.icon} ${status.label} → ${RESERVA_STATUS_INFO[novoStatus]?.label || novoStatus}`);
  }

  function abrirWhatsApp() {
    if (!r.telCliente) { window.alert("Sem telefone cadastrado."); return; }
    const tpl = config?.mensagemConfirmacao || MENSAGEM_CONFIRMACAO_DEFAULT;
    const msg = aplicarPlaceholders(tpl, reservaPlaceholders(r, restaurant, cliente));
    window.open(whatsAppLink(r.telCliente, msg), "_blank", "noopener,noreferrer");
  }

  return (
    <div style={{...S.card,padding:"12px 14px",border:`1px solid ${status.color}33`,background:`${status.bg}33`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
        <div style={{flex:1,minWidth:200}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
            <span style={{fontSize:18,fontWeight:700,color:"var(--text)",fontFamily:"'DM Mono',monospace"}}>{horaStr}</span>
            <span style={{padding:"2px 8px",borderRadius:6,background:status.bg,color:status.color,fontSize:10,fontWeight:700}}>{status.icon} {status.label}</span>
            {r.forcada && <span title="Reserva forçada (ultrapassou capacidade)" style={{padding:"2px 6px",borderRadius:6,background:"#f59e0b22",color:"#f59e0b",fontSize:10,fontWeight:700}}>⚡ Forçada</span>}
            <span style={{color:"var(--text3)",fontSize:11}}>· {r.party} pessoa{r.party!==1?"s":""}</span>
          </div>
          <div style={{color:"var(--text)",fontWeight:600,fontSize:14}}>{r.nomeCliente}{r.telCliente ? <span style={{color:"var(--text3)",fontWeight:400,fontSize:12}}> · {r.telCliente}</span> : null}</div>
          {(sala || tipo) && (
            <div style={{color:"var(--text3)",fontSize:11,marginTop:2}}>
              {sala?.nome}{sala && tipo ? " · " : ""}{tipo?.label}{tipo?.descricao ? ` (${tipo.descricao})` : ""}
            </div>
          )}
          {r.ocasiao && <div style={{color:"#a855f7",fontSize:11,marginTop:2}}>🎉 {r.ocasiao}</div>}
          {r.observacoesCliente && <div style={{color:"var(--text2)",fontSize:11,marginTop:2,fontStyle:"italic"}}>💬 {r.observacoesCliente}</div>}
          {r.observacoesInternas && <div style={{color:"var(--text3)",fontSize:11,marginTop:2,fontStyle:"italic"}}>📝 {r.observacoesInternas}</div>}
          <div style={{color:"var(--text3)",fontSize:10,marginTop:4}}>
            {r.fonte === "widget" ? "🌐 Cliente" : `👤 ${r.criadaPor || "—"}`}
            {r.confirmadaPor && ` · ✅ confirmada por ${r.confirmadaPor}`}
            {r.sentouPor && ` · 🪑 sentou (${r.sentouPor})`}
          </div>
        </div>
        <div style={{display:"flex",gap:6,flexDirection:mobileOnly?"row":"column",flexWrap:"wrap"}}>
          {(r.status === "feita") && (
            <button onClick={abrirWhatsApp} style={{padding:"6px 12px",borderRadius:8,border:"1px solid #25d36644",background:"#25d36611",color:"#15803d",cursor:"pointer",fontSize:11,fontWeight:600,whiteSpace:"nowrap"}}>
              💬 Confirmar via WhatsApp
            </button>
          )}
          {(r.status === "feita") && (
            <button onClick={()=>setStatusReserva("confirmada")} style={{padding:"6px 12px",borderRadius:8,border:"1px solid #10b98144",background:"#10b98111",color:"#15803d",cursor:"pointer",fontSize:11,fontWeight:600,whiteSpace:"nowrap"}}>
              ✅ Marcar confirmada
            </button>
          )}
          {(r.status === "feita" || r.status === "confirmada") && (
            <button onClick={()=>setStatusReserva("sentou")} style={{padding:"6px 12px",borderRadius:8,border:"1px solid #8b5cf644",background:"#8b5cf611",color:"#8b5cf6",cursor:"pointer",fontSize:11,fontWeight:600,whiteSpace:"nowrap"}}>
              🪑 Sentou
            </button>
          )}
          {(r.status === "sentou") && (
            <button onClick={()=>setStatusReserva("finalizada")} style={{padding:"6px 12px",borderRadius:8,border:"1px solid #64748b44",background:"#64748b11",color:"#64748b",cursor:"pointer",fontSize:11,fontWeight:600,whiteSpace:"nowrap"}}>
              ✔️ Finalizar
            </button>
          )}
          {(r.status === "feita" || r.status === "confirmada") && (
            <button onClick={async ()=>{
              if (!await appConfirm(`Marcar como no-show? ${r.nomeCliente} será penalizado(a) na contagem.`)) return;
              setStatusReserva("no-show");
              // Atualiza cliente se existe
              if (r.clienteId) {
                const nextClientes = (allClientes || []).map(c => c.id === r.clienteId ? { ...c, noShows: (c.noShows || 0) + 1 } : c);
                onUpdate("reservasClientes", nextClientes);
              }
            }} style={{padding:"6px 12px",borderRadius:8,border:"1px solid #ef444444",background:"#ef444411",color:"#ef4444",cursor:"pointer",fontSize:11,fontWeight:600,whiteSpace:"nowrap"}}>
              ⛔ No-show
            </button>
          )}
          {canGerenciar && r.status !== "cancelada" && r.status !== "finalizada" && (
            <button onClick={async ()=>{
              if (!await appConfirm("Cancelar esta reserva?")) return;
              setStatusReserva("cancelada");
            }} style={{padding:"6px 12px",borderRadius:8,border:"1px solid var(--border)",background:"transparent",color:"var(--text3)",cursor:"pointer",fontSize:11,whiteSpace:"nowrap"}}>
              ❌ Cancelar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── CALENDÁRIO ───
function ReservasCalendario({ restaurantId, restaurant, reservas, clientes, config, salas, tipos, currentUser, canForce, canGerenciar, onUpdate, mobileOnly, allReservas, allClientes }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [selectedDate, setSelectedDate] = useState(today());
  const [showNew, setShowNew] = useState(null); // null | "YYYY-MM-DD"

  const dim = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= dim; d++) cells.push(`${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`);

  const reservasByDate = {};
  reservas.forEach(r => {
    const d = r.dateTime?.slice(0,10);
    if (!d) return;
    if (!reservasByDate[d]) reservasByDate[d] = [];
    reservasByDate[d].push(r);
  });

  const reservasDoDia = (reservasByDate[selectedDate] || []).sort((a,b) => (a.dateTime || "").localeCompare(b.dateTime || ""));

  function navMonth(delta) {
    const d = new Date(year, month + delta, 1);
    setYear(d.getFullYear()); setMonth(d.getMonth());
  }

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:16}}>
        <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?16:20}}>📅 Reservas — Calendário</h3>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <button onClick={()=>navMonth(-1)} style={{...S.btnSecondary,padding:"6px 12px"}}>‹</button>
          <span style={{minWidth:140,textAlign:"center",fontSize:13,fontWeight:700,color:"var(--text)",textTransform:"capitalize"}}>{monthLabel(year, month)}</span>
          <button onClick={()=>navMonth(1)} style={{...S.btnSecondary,padding:"6px 12px"}}>›</button>
        </div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,marginBottom:4}}>
        {DIAS_SEMANA.map(d => <div key={d} style={{textAlign:"center",fontSize:11,color:"var(--text3)",fontWeight:700,padding:"4px 0"}}>{d}</div>)}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,marginBottom:18}}>
        {cells.map((d, idx) => {
          if (!d) return <div key={`e-${idx}`} />;
          const list = reservasByDate[d] || [];
          const total = list.length;
          const pessoas = list.reduce((s,r) => s + (r.party || 0), 0);
          const isSel = d === selectedDate;
          const isToday = d === today();
          return (
            <button key={d} onClick={()=>setSelectedDate(d)}
              style={{
                aspectRatio: "1", borderRadius: 8,
                border: `1px solid ${isSel?"var(--ac)":isToday?"var(--ac)44":"var(--border)"}`,
                background: isSel ? "var(--ac)22" : isToday ? "var(--ac)08" : "var(--card-bg)",
                cursor: "pointer", padding: "4px",
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between",
              }}>
              <span style={{fontSize:11,color:isSel?"var(--ac)":"var(--text2)",fontWeight:isToday?700:500}}>{parseInt(d.slice(-2))}</span>
              {total > 0 && (
                <div style={{fontSize:9,color:"var(--text3)",lineHeight:1.2,textAlign:"center"}}>
                  <div style={{color:"var(--ac)",fontWeight:700}}>{total}r</div>
                  <div>{pessoas}p</div>
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <div style={{fontSize:13,fontWeight:700,color:"var(--text)"}}>
          {new Date(selectedDate+"T12:00:00").toLocaleDateString("pt-BR",{weekday:"long",day:"2-digit",month:"long"})} — {reservasDoDia.length} reserva(s)
        </div>
        <button onClick={()=>setShowNew(selectedDate)} style={{...S.btnPrimary,fontSize:12,padding:"6px 14px"}}>+ Nova</button>
      </div>

      {reservasDoDia.length === 0 ? (
        <div style={{...S.card,padding:20,textAlign:"center",color:"var(--text3)",fontSize:13}}>Nenhuma reserva neste dia.</div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {reservasDoDia.map(r => (
            <ReservaCard key={r.id} r={r}
              cliente={clientes.find(c => c.id === r.clienteId)}
              salas={salas} tipos={tipos}
              restaurant={restaurant}
              config={config}
              currentUser={currentUser}
              canGerenciar={canGerenciar}
              onUpdate={onUpdate}
              allReservas={allReservas}
              allClientes={allClientes}
              restaurantId={restaurantId}
              mobileOnly={mobileOnly}
            />
          ))}
        </div>
      )}

      {showNew && (
        <ReservaModal
          restaurantId={restaurantId}
          restaurant={restaurant}
          config={config}
          salas={salas} tipos={tipos}
          clientes={clientes}
          allReservas={allReservas}
          allClientes={allClientes}
          currentUser={currentUser}
          canForce={canForce}
          onUpdate={onUpdate}
          dataInicial={showNew}
          onClose={()=>setShowNew(null)}
        />
      )}
    </div>
  );
}

// ─── CLIENTES ───
function ReservasClientes({ restaurantId, restaurant, clientes, reservas, currentUser, mobileOnly, onUpdate, allClientes }) {
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // null | {} | cliente
  const filtered = clientes.filter(c => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    const tel = (c.telefone || "").replace(/\D/g, "");
    const sLimpo = s.replace(/\D/g, "");
    return (c.nome || "").toLowerCase().includes(s)
        || (c.email || "").toLowerCase().includes(s)
        || (sLimpo && tel.includes(sLimpo));
  }).sort((a,b) => (a.nome || "").localeCompare(b.nome || ""));

  function saveCliente(data) {
    const isEdit = !!data.id;
    const now = new Date().toISOString();
    const author = currentUser?.name || "Gestor";
    if (isEdit) {
      const next = (allClientes || []).map(c => c.id === data.id ? { ...c, ...data } : c);
      onUpdate("reservasClientes", next);
    } else {
      const novo = {
        id: `cli-${Date.now()}-${Math.random().toString(36).slice(2,5)}`,
        restaurantId, ...data,
        visitas: 0, noShows: 0, blacklisted: false, tags: data.tags || [],
        createdAt: now, createdBy: author,
      };
      onUpdate("reservasClientes", [...(allClientes || []), novo]);
    }
    setEditing(null);
    onUpdate("_toast", isEdit ? "✏️ Cliente atualizado" : "+ Cliente cadastrado");
  }

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:16}}>
        <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?16:20}}>👤 Clientes ({filtered.length})</h3>
        <button onClick={()=>setEditing({ nome: "", telefone: "", email: "" })} style={{...S.btnPrimary,fontSize:13,padding:"8px 16px"}}>+ Novo cliente</button>
      </div>
      <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Buscar por nome, telefone ou email..." style={{...S.input,fontSize:13,marginBottom:14}}/>
      {filtered.length === 0 ? (
        <div style={{...S.card,padding:30,textAlign:"center",color:"var(--text3)",fontSize:13}}>
          {search.trim() ? "Nenhum cliente encontrado." : "Nenhum cliente cadastrado ainda."}
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {filtered.map(c => {
            const reservasCliente = reservas.filter(r => r.clienteId === c.id);
            const ultimaVisita = reservasCliente.filter(r => r.status === "finalizada" || r.status === "sentou").sort((a,b) => (b.dateTime || "").localeCompare(a.dateTime || ""))[0];
            return (
              <div key={c.id} onClick={()=>setEditing(c)} style={{...S.card,padding:"10px 14px",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center",gap:12}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{color:"var(--text)",fontWeight:700,fontSize:14}}>
                    {c.nome}
                    {c.blacklisted && <span style={{marginLeft:8,padding:"2px 6px",borderRadius:6,background:"#ef444422",color:"#ef4444",fontSize:10,fontWeight:700}}>🚫 Bloqueado</span>}
                    {c.tags?.includes("vip") && <span style={{marginLeft:8,padding:"2px 6px",borderRadius:6,background:"#f59e0b22",color:"#f59e0b",fontSize:10,fontWeight:700}}>⭐ VIP</span>}
                  </div>
                  <div style={{color:"var(--text3)",fontSize:11,marginTop:2}}>
                    {c.telefone}{c.email ? ` · ${c.email}` : ""}
                  </div>
                  <div style={{color:"var(--text3)",fontSize:10,marginTop:2}}>
                    {reservasCliente.length} reserva(s) total · {c.noShows || 0} no-show(s)
                    {ultimaVisita && ` · última visita ${new Date(ultimaVisita.dateTime).toLocaleDateString("pt-BR")}`}
                  </div>
                </div>
                <span style={{color:"var(--text3)",fontSize:11}}>›</span>
              </div>
            );
          })}
        </div>
      )}
      {editing && <ClienteModal cliente={editing} onSave={saveCliente} onClose={()=>setEditing(null)} mobileOnly={mobileOnly}/>}
    </div>
  );
}

function ClienteModal({ cliente, onSave, onClose, mobileOnly }) {
  const [form, setForm] = useState({
    id: cliente?.id,
    nome: cliente?.nome || "",
    telefone: cliente?.telefone || "",
    email: cliente?.email || "",
    aniversario: cliente?.aniversario || "",
    observacoes: cliente?.observacoes || "",
    tags: cliente?.tags || [],
    blacklisted: cliente?.blacklisted || false,
  });
  const isEdit = !!cliente?.id;
  const valid = form.nome.trim() && (form.telefone.replace(/\D/g,"").length >= 10);
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:14,maxWidth:480,width:"100%",padding:24,maxHeight:"85vh",overflowY:"auto"}}>
        <h3 style={{margin:"0 0 14px",color:"var(--text)",fontSize:16,fontWeight:700}}>{isEdit ? "Editar cliente" : "Novo cliente"}</h3>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <div><label style={{...S.label,fontSize:11}}>Nome *</label><input value={form.nome} onChange={e=>setForm({...form,nome:e.target.value})} style={S.input}/></div>
          <div><label style={{...S.label,fontSize:11}}>Telefone *</label><input value={form.telefone} onChange={e=>setForm({...form,telefone:e.target.value})} placeholder="(11) 99999-9999" style={S.input} inputMode="tel"/></div>
          <div><label style={{...S.label,fontSize:11}}>Email</label><input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} style={S.input}/></div>
          <div><label style={{...S.label,fontSize:11}}>Aniversário (opcional)</label><input type="date" value={form.aniversario} onChange={e=>setForm({...form,aniversario:e.target.value})} style={S.input}/></div>
          <div><label style={{...S.label,fontSize:11}}>Observações</label><textarea value={form.observacoes} onChange={e=>setForm({...form,observacoes:e.target.value})} rows={3} style={{...S.input,resize:"vertical"}}/></div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {["vip","alergico","vegetariano","fidelidade"].map(tag => {
              const on = form.tags.includes(tag);
              return (
                <button key={tag} onClick={()=>setForm({...form,tags:on?form.tags.filter(t=>t!==tag):[...form.tags,tag]})}
                  style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${on?"#f59e0b":"var(--border)"}`,background:on?"#f59e0b22":"transparent",color:on?"#f59e0b":"var(--text3)",cursor:"pointer",fontSize:11}}>
                  {on?"✓ ":""}{tag}
                </button>
              );
            })}
          </div>
          <label style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",border:"1px solid var(--border)",borderRadius:8,cursor:"pointer"}}>
            <input type="checkbox" checked={form.blacklisted} onChange={e=>setForm({...form,blacklisted:e.target.checked})}/>
            <span style={{fontSize:12,color:form.blacklisted?"#ef4444":"var(--text2)"}}>🚫 Bloquear este cliente (impede novas reservas)</span>
          </label>
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:18}}>
          <button onClick={onClose} style={S.btnSecondary}>Cancelar</button>
          <button onClick={()=>valid && onSave(form)} disabled={!valid} style={{...S.btnPrimary,opacity:valid?1:0.5}}>
            {isEdit ? "Salvar" : "Cadastrar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── CONFIGURAÇÕES ───
function ReservasConfig({ restaurantId, restaurant, config, salas, tipos, reservasConfig, reservasSalas, reservasTiposMesa, currentUser, mobileOnly, onUpdate }) {
  const [editingTurno, setEditingTurno] = useState(null);
  const [editingSala, setEditingSala] = useState(null);
  const [editingTipo, setEditingTipo] = useState(null);
  const [mensagem, setMensagem] = useState(config.mensagemConfirmacao ?? MENSAGEM_CONFIRMACAO_DEFAULT);
  const [duracaoMedia, setDuracaoMedia] = useState(config.duracaoMediaMin ?? 90);
  const [antecedenciaMin, setAntecedenciaMin] = useState(config.antecedenciaMinHoras ?? 1);
  const [antecedenciaMax, setAntecedenciaMax] = useState(config.antecedenciaMaxDias ?? 60);
  const turnos = config.turnos || [];

  function patchConfig(patch) {
    const next = { ...(reservasConfig || {}) };
    next[restaurantId] = { ...(next[restaurantId] || {}), ...patch };
    onUpdate("reservasConfig", next);
    onUpdate("_toast", "✅ Configuração salva");
  }
  function saveMensagem() {
    patchConfig({ mensagemConfirmacao: mensagem });
  }
  function saveDuracao() {
    patchConfig({
      duracaoMediaMin: parseInt(duracaoMedia) || 90,
      antecedenciaMinHoras: parseFloat(antecedenciaMin) || 1,
      antecedenciaMaxDias: parseInt(antecedenciaMax) || 60,
    });
  }
  function saveTurno(turno) {
    const novosTurnos = turno.id
      ? turnos.map(t => t.id === turno.id ? turno : t)
      : [...turnos, { ...turno, id: `turno-${Date.now()}-${Math.random().toString(36).slice(2,5)}` }];
    patchConfig({ turnos: novosTurnos });
    setEditingTurno(null);
  }
  async function delTurno(id) {
    if (!await appConfirm("Remover este turno?")) return;
    patchConfig({ turnos: turnos.filter(t => t.id !== id) });
  }
  function saveSala(sala) {
    const isEdit = !!sala.id;
    if (isEdit) {
      const next = (reservasSalas || []).map(s => s.id === sala.id ? sala : s);
      onUpdate("reservasSalas", next);
    } else {
      const nova = { ...sala, id: `sala-${Date.now()}-${Math.random().toString(36).slice(2,5)}`, restaurantId, ordem: salas.length };
      onUpdate("reservasSalas", [...(reservasSalas || []), nova]);
    }
    setEditingSala(null);
    onUpdate("_toast", "✅ Salão salvo");
  }
  async function delSala(id) {
    const tiposNaSala = tipos.filter(t => t.salaId === id);
    if (tiposNaSala.length > 0) {
      if (!await appConfirm(`Este salão tem ${tiposNaSala.length} tipo(s) de mesa cadastrados. Remover mesmo assim? Os tipos serão apagados.`)) return;
      onUpdate("reservasTiposMesa", (reservasTiposMesa || []).filter(t => t.salaId !== id));
    } else {
      if (!await appConfirm("Remover este salão?")) return;
    }
    onUpdate("reservasSalas", (reservasSalas || []).filter(s => s.id !== id));
  }
  function saveTipo(tipo) {
    const isEdit = !!tipo.id;
    if (isEdit) {
      const next = (reservasTiposMesa || []).map(t => t.id === tipo.id ? tipo : t);
      onUpdate("reservasTiposMesa", next);
    } else {
      const novo = { ...tipo, id: `tipo-${Date.now()}-${Math.random().toString(36).slice(2,5)}`, restaurantId, ordem: tipos.length };
      onUpdate("reservasTiposMesa", [...(reservasTiposMesa || []), novo]);
    }
    setEditingTipo(null);
    onUpdate("_toast", "✅ Tipo de mesa salvo");
  }
  async function delTipo(id) {
    if (!await appConfirm("Remover este tipo de mesa?")) return;
    onUpdate("reservasTiposMesa", (reservasTiposMesa || []).filter(t => t.id !== id));
  }

  return (
    <div>
      <h3 style={{color:"var(--text)",margin:"0 0 16px",fontSize:mobileOnly?16:20}}>⚙️ Configurações de Reservas</h3>

      {/* Turnos */}
      <div style={{...S.card,padding:16,marginBottom:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
          <div>
            <p style={{color:"var(--text)",fontSize:14,fontWeight:700,margin:0}}>🕐 Turnos</p>
            <p style={{color:"var(--text3)",fontSize:11,margin:"2px 0 0"}}>Períodos do dia em que aceitamos reservas (Almoço, Jantar...)</p>
          </div>
          <button onClick={()=>setEditingTurno({ nome: "", diasSemana: [1,2,3,4,5,6,0], horarios: [], capacidadePessoas: 50, slotMinutes: 30, ativo: true })} style={{...S.btnPrimary,fontSize:12,padding:"6px 14px"}}>+ Novo turno</button>
        </div>
        {turnos.length === 0 ? (
          <div style={{padding:14,textAlign:"center",color:"var(--text3)",fontSize:12,background:"var(--bg2)",borderRadius:8}}>Nenhum turno cadastrado. Crie pelo menos um pra aceitar reservas.</div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {turnos.map(t => (
              <div key={t.id} style={{padding:"10px 12px",borderRadius:8,border:"1px solid var(--border)",background:t.ativo?"var(--bg1)":"var(--bg2)",display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                <div style={{flex:1,minWidth:200}}>
                  <div style={{fontSize:13,fontWeight:700,color:"var(--text)"}}>{t.nome}{!t.ativo && <span style={{marginLeft:8,fontSize:10,color:"var(--text3)"}}>(inativo)</span>}</div>
                  <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>
                    Dias: {(t.diasSemana || []).map(d => DIAS_SEMANA[d]).join(", ") || "—"} · {(t.horarios || []).length} horário(s) · até {t.capacidadePessoas} pessoas
                  </div>
                  {(t.horarios || []).length > 0 && (
                    <div style={{fontSize:10,color:"var(--text3)",marginTop:2,fontFamily:"'DM Mono',monospace"}}>{(t.horarios || []).join(" · ")}</div>
                  )}
                </div>
                <div style={{display:"flex",gap:6}}>
                  <button onClick={()=>setEditingTurno(t)} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px"}}>✏️ Editar</button>
                  <button onClick={()=>delTurno(t.id)} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px",color:"var(--red)",borderColor:"var(--red)44"}}>🗑️</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Salões */}
      <div style={{...S.card,padding:16,marginBottom:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
          <div>
            <p style={{color:"var(--text)",fontSize:14,fontWeight:700,margin:0}}>🏛️ Salões</p>
            <p style={{color:"var(--text3)",fontSize:11,margin:"2px 0 0"}}>Áreas físicas do restaurante (Salão Principal, Varanda, Privativa...)</p>
          </div>
          <button onClick={()=>setEditingSala({ nome: "", descricao: "", ativa: true })} style={{...S.btnPrimary,fontSize:12,padding:"6px 14px"}}>+ Novo salão</button>
        </div>
        {salas.length === 0 ? (
          <div style={{padding:14,textAlign:"center",color:"var(--text3)",fontSize:12,background:"var(--bg2)",borderRadius:8}}>Nenhum salão cadastrado.</div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {salas.map(s => (
              <div key={s.id} style={{padding:"10px 12px",borderRadius:8,border:"1px solid var(--border)",display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:700,color:"var(--text)"}}>{s.nome}{!s.ativa && <span style={{marginLeft:8,fontSize:10,color:"var(--text3)"}}>(inativo)</span>}</div>
                  {s.descricao && <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>{s.descricao}</div>}
                </div>
                <div style={{display:"flex",gap:6}}>
                  <button onClick={()=>setEditingSala(s)} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px"}}>✏️</button>
                  <button onClick={()=>delSala(s.id)} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px",color:"var(--red)",borderColor:"var(--red)44"}}>🗑️</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tipos de Mesa */}
      <div style={{...S.card,padding:16,marginBottom:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
          <div>
            <p style={{color:"var(--text)",fontSize:14,fontWeight:700,margin:0}}>🪑 Tipos de Mesa</p>
            <p style={{color:"var(--text3)",fontSize:11,margin:"2px 0 0"}}>Cada tipo pertence a um salão. A descrição é o que o cliente vai ver.</p>
          </div>
          <button onClick={()=>setEditingTipo({ salaId: salas[0]?.id || "", label: "", capacidade: 4, quantidade: 1, descricao: "", ativa: true })} disabled={salas.length === 0} style={{...S.btnPrimary,fontSize:12,padding:"6px 14px",opacity:salas.length===0?0.5:1}}>+ Novo tipo</button>
        </div>
        {salas.length === 0 ? (
          <div style={{padding:14,textAlign:"center",color:"#f59e0b",fontSize:12,background:"#f59e0b11",border:"1px solid #f59e0b33",borderRadius:8}}>⚠️ Cadastre pelo menos um salão antes de cadastrar tipos de mesa.</div>
        ) : tipos.length === 0 ? (
          <div style={{padding:14,textAlign:"center",color:"var(--text3)",fontSize:12,background:"var(--bg2)",borderRadius:8}}>Nenhum tipo cadastrado.</div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {tipos.map(t => {
              const sala = salas.find(s => s.id === t.salaId);
              return (
                <div key={t.id} style={{padding:"10px 12px",borderRadius:8,border:"1px solid var(--border)",display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:700,color:"var(--text)"}}>{t.label}{!t.ativa && <span style={{marginLeft:8,fontSize:10,color:"var(--text3)"}}>(inativo)</span>}</div>
                    <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>
                      {sala?.nome || "(sala removida)"} · {t.capacidade} lugares · {t.quantidade} unidade(s)
                    </div>
                    {t.descricao && <div style={{fontSize:11,color:"var(--text2)",marginTop:2,fontStyle:"italic"}}>"{t.descricao}"</div>}
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={()=>setEditingTipo(t)} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px"}}>✏️</button>
                    <button onClick={()=>delTipo(t.id)} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px",color:"var(--red)",borderColor:"var(--red)44"}}>🗑️</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Política */}
      <div style={{...S.card,padding:16,marginBottom:14}}>
        <p style={{color:"var(--text)",fontSize:14,fontWeight:700,margin:"0 0 12px"}}>⏱️ Política de reserva</p>
        <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"1fr 1fr 1fr",gap:10,marginBottom:10}}>
          <div>
            <label style={{...S.label,fontSize:11}}>Duração média (min)</label>
            <input type="number" min="30" step="15" value={duracaoMedia} onChange={e=>setDuracaoMedia(e.target.value)} style={S.input}/>
          </div>
          <div>
            <label style={{...S.label,fontSize:11}}>Antecedência mín (horas)</label>
            <input type="number" min="0" step="0.5" value={antecedenciaMin} onChange={e=>setAntecedenciaMin(e.target.value)} style={S.input}/>
          </div>
          <div>
            <label style={{...S.label,fontSize:11}}>Antecedência máx (dias)</label>
            <input type="number" min="1" max="365" value={antecedenciaMax} onChange={e=>setAntecedenciaMax(e.target.value)} style={S.input}/>
          </div>
        </div>
        <button onClick={saveDuracao} style={{...S.btnPrimary,fontSize:12,padding:"6px 14px"}}>Salvar política</button>
      </div>

      {/* Mensagem de confirmação */}
      <div style={{...S.card,padding:16,marginBottom:14}}>
        <p style={{color:"var(--text)",fontSize:14,fontWeight:700,margin:"0 0 4px"}}>💬 Mensagem padrão de confirmação (WhatsApp)</p>
        <p style={{color:"var(--text3)",fontSize:11,margin:"0 0 10px"}}>Use os placeholders abaixo. Eles serão substituídos automaticamente.</p>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
          {["{nome}","{data}","{hora}","{pessoas}","{restaurante}"].map(p => (
            <span key={p} style={{padding:"3px 8px",borderRadius:6,background:"var(--bg2)",color:"var(--text3)",fontSize:10,fontFamily:"'DM Mono',monospace"}}>{p}</span>
          ))}
        </div>
        <textarea value={mensagem} onChange={e=>setMensagem(e.target.value)} rows={4} style={{...S.input,resize:"vertical",fontSize:13}}/>
        <div style={{display:"flex",gap:8,marginTop:8}}>
          <button onClick={saveMensagem} style={{...S.btnPrimary,fontSize:12,padding:"6px 14px"}}>Salvar mensagem</button>
          <button onClick={()=>setMensagem(MENSAGEM_CONFIRMACAO_DEFAULT)} style={{...S.btnSecondary,fontSize:12,padding:"6px 14px"}}>Restaurar padrão</button>
        </div>
      </div>

      {editingTurno && <TurnoModal turno={editingTurno} onSave={saveTurno} onClose={()=>setEditingTurno(null)} mobileOnly={mobileOnly}/>}
      {editingSala && <SalaModal sala={editingSala} onSave={saveSala} onClose={()=>setEditingSala(null)}/>}
      {editingTipo && <TipoMesaModal tipo={editingTipo} salas={salas} onSave={saveTipo} onClose={()=>setEditingTipo(null)}/>}
    </div>
  );
}

function TurnoModal({ turno, onSave, onClose, mobileOnly }) {
  const [form, setForm] = useState({
    id: turno?.id,
    nome: turno?.nome || "",
    diasSemana: turno?.diasSemana || [1,2,3,4,5,6,0],
    horarios: turno?.horarios || [],
    capacidadePessoas: turno?.capacidadePessoas || 50,
    slotMinutes: turno?.slotMinutes || 30,
    ativo: turno?.ativo !== false,
  });
  const [novoHorario, setNovoHorario] = useState("");
  function addHorario() {
    if (!/^\d{2}:\d{2}$/.test(novoHorario)) return;
    if (form.horarios.includes(novoHorario)) return;
    setForm({...form, horarios: [...form.horarios, novoHorario].sort()});
    setNovoHorario("");
  }
  function genHorarios(de, ate, step) {
    const [h1, m1] = de.split(":").map(Number);
    const [h2, m2] = ate.split(":").map(Number);
    let cur = h1*60+m1, end = h2*60+m2;
    const out = [];
    while (cur <= end) {
      out.push(`${String(Math.floor(cur/60)).padStart(2,"0")}:${String(cur%60).padStart(2,"0")}`);
      cur += step;
    }
    setForm({...form, horarios: out});
  }
  const [genDe, setGenDe] = useState("19:00");
  const [genAte, setGenAte] = useState("22:30");
  const valid = form.nome.trim() && form.diasSemana.length > 0 && form.horarios.length > 0 && form.capacidadePessoas > 0;
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:14,maxWidth:540,width:"100%",padding:24,maxHeight:"85vh",overflowY:"auto"}}>
        <h3 style={{margin:"0 0 14px",color:"var(--text)",fontSize:16,fontWeight:700}}>{turno?.id ? "Editar turno" : "Novo turno"}</h3>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <div><label style={{...S.label,fontSize:11}}>Nome do turno</label><input value={form.nome} onChange={e=>setForm({...form,nome:e.target.value})} placeholder="ex: Almoço, Jantar, Brunch" style={S.input}/></div>
          <div>
            <label style={{...S.label,fontSize:11}}>Dias da semana</label>
            <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
              {DIAS_SEMANA.map((d, i) => {
                const on = form.diasSemana.includes(i);
                return (
                  <button key={i} onClick={()=>setForm({...form,diasSemana:on?form.diasSemana.filter(x=>x!==i):[...form.diasSemana,i].sort()})}
                    style={{padding:"6px 12px",borderRadius:6,border:`1px solid ${on?"var(--ac)":"var(--border)"}`,background:on?"var(--ac)22":"transparent",color:on?"var(--ac)":"var(--text3)",cursor:"pointer",fontSize:11,fontWeight:600}}>
                    {d}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label style={{...S.label,fontSize:11}}>Horários disponíveis ({form.horarios.length})</label>
            <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr 1fr":"1fr 1fr auto auto",gap:6,marginBottom:8}}>
              <input type="time" value={genDe} onChange={e=>setGenDe(e.target.value)} style={{...S.input,fontSize:12}}/>
              <input type="time" value={genAte} onChange={e=>setGenAte(e.target.value)} style={{...S.input,fontSize:12}}/>
              <select value={form.slotMinutes} onChange={e=>setForm({...form,slotMinutes:parseInt(e.target.value)})} style={{...S.input,fontSize:12,cursor:"pointer"}}>
                <option value={15}>15min</option>
                <option value={30}>30min</option>
                <option value={60}>60min</option>
              </select>
              <button onClick={()=>genHorarios(genDe, genAte, form.slotMinutes)} style={{...S.btnSecondary,fontSize:11,padding:"6px 10px"}}>Gerar</button>
            </div>
            <div style={{display:"flex",gap:6,marginBottom:8}}>
              <input type="time" value={novoHorario} onChange={e=>setNovoHorario(e.target.value)} style={{...S.input,fontSize:12,flex:1}}/>
              <button onClick={addHorario} style={{...S.btnSecondary,fontSize:11,padding:"6px 10px"}}>+ Adicionar</button>
            </div>
            <div style={{display:"flex",gap:4,flexWrap:"wrap",maxHeight:120,overflowY:"auto"}}>
              {form.horarios.map(h => (
                <button key={h} onClick={()=>setForm({...form,horarios:form.horarios.filter(x=>x!==h)})} style={{padding:"3px 8px",borderRadius:6,background:"var(--bg2)",border:"1px solid var(--border)",color:"var(--text2)",fontSize:11,fontFamily:"'DM Mono',monospace",cursor:"pointer"}}>
                  {h} ✕
                </button>
              ))}
            </div>
          </div>
          <div><label style={{...S.label,fontSize:11}}>Capacidade total (pessoas)</label><input type="number" min="1" value={form.capacidadePessoas} onChange={e=>setForm({...form,capacidadePessoas:parseInt(e.target.value)||0})} style={S.input}/></div>
          <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
            <input type="checkbox" checked={form.ativo} onChange={e=>setForm({...form,ativo:e.target.checked})}/>
            <span style={{fontSize:12,color:"var(--text2)"}}>Turno ativo (aceita reservas)</span>
          </label>
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:18}}>
          <button onClick={onClose} style={S.btnSecondary}>Cancelar</button>
          <button onClick={()=>valid && onSave(form)} disabled={!valid} style={{...S.btnPrimary,opacity:valid?1:0.5}}>Salvar</button>
        </div>
      </div>
    </div>
  );
}

function SalaModal({ sala, onSave, onClose }) {
  const [form, setForm] = useState({
    id: sala?.id,
    nome: sala?.nome || "",
    descricao: sala?.descricao || "",
    ativa: sala?.ativa !== false,
  });
  const valid = form.nome.trim();
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:14,maxWidth:480,width:"100%",padding:24}}>
        <h3 style={{margin:"0 0 14px",color:"var(--text)",fontSize:16,fontWeight:700}}>{sala?.id ? "Editar salão" : "Novo salão"}</h3>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <div><label style={{...S.label,fontSize:11}}>Nome</label><input value={form.nome} onChange={e=>setForm({...form,nome:e.target.value})} placeholder="ex: Salão Principal, Varanda, Privativa" style={S.input}/></div>
          <div><label style={{...S.label,fontSize:11}}>Descrição (opcional)</label><textarea value={form.descricao} onChange={e=>setForm({...form,descricao:e.target.value})} rows={2} style={{...S.input,resize:"vertical"}}/></div>
          <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
            <input type="checkbox" checked={form.ativa} onChange={e=>setForm({...form,ativa:e.target.checked})}/>
            <span style={{fontSize:12,color:"var(--text2)"}}>Salão ativo</span>
          </label>
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:18}}>
          <button onClick={onClose} style={S.btnSecondary}>Cancelar</button>
          <button onClick={()=>valid && onSave(form)} disabled={!valid} style={{...S.btnPrimary,opacity:valid?1:0.5}}>Salvar</button>
        </div>
      </div>
    </div>
  );
}

function TipoMesaModal({ tipo, salas, onSave, onClose }) {
  const [form, setForm] = useState({
    id: tipo?.id,
    salaId: tipo?.salaId || salas[0]?.id || "",
    label: tipo?.label || "",
    capacidade: tipo?.capacidade || 4,
    quantidade: tipo?.quantidade || 1,
    descricao: tipo?.descricao || "",
    ativa: tipo?.ativa !== false,
  });
  const valid = form.label.trim() && form.salaId && form.capacidade > 0 && form.quantidade > 0;
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:14,maxWidth:520,width:"100%",padding:24,maxHeight:"85vh",overflowY:"auto"}}>
        <h3 style={{margin:"0 0 14px",color:"var(--text)",fontSize:16,fontWeight:700}}>{tipo?.id ? "Editar tipo de mesa" : "Novo tipo de mesa"}</h3>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <div>
            <label style={{...S.label,fontSize:11}}>Salão</label>
            <select value={form.salaId} onChange={e=>setForm({...form,salaId:e.target.value})} style={{...S.input,cursor:"pointer"}}>
              {salas.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          </div>
          <div><label style={{...S.label,fontSize:11}}>Nome do tipo</label><input value={form.label} onChange={e=>setForm({...form,label:e.target.value})} placeholder="ex: Mesa baixa 4p, Mesa do chef, Balcão" style={S.input}/></div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <div><label style={{...S.label,fontSize:11}}>Capacidade (lugares)</label><input type="number" min="1" value={form.capacidade} onChange={e=>setForm({...form,capacidade:parseInt(e.target.value)||0})} style={S.input}/></div>
            <div><label style={{...S.label,fontSize:11}}>Quantidade</label><input type="number" min="1" value={form.quantidade} onChange={e=>setForm({...form,quantidade:parseInt(e.target.value)||0})} style={S.input}/></div>
          </div>
          <div>
            <label style={{...S.label,fontSize:11}}>Descrição (importante — vai aparecer pro cliente confirmar)</label>
            <textarea value={form.descricao} onChange={e=>setForm({...form,descricao:e.target.value})} rows={3} placeholder="ex: Mesa convencional com cadeiras estofadas, perto da janela" style={{...S.input,resize:"vertical"}}/>
          </div>
          <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
            <input type="checkbox" checked={form.ativa} onChange={e=>setForm({...form,ativa:e.target.checked})}/>
            <span style={{fontSize:12,color:"var(--text2)"}}>Disponível pra reserva</span>
          </label>
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:18}}>
          <button onClick={onClose} style={S.btnSecondary}>Cancelar</button>
          <button onClick={()=>valid && onSave(form)} disabled={!valid} style={{...S.btnPrimary,opacity:valid?1:0.5}}>Salvar</button>
        </div>
      </div>
    </div>
  );
}

// ─── MODAL: NOVA RESERVA (backoffice) ───
function ReservaModal({ restaurantId, restaurant, config, salas, tipos, clientes, allReservas, allClientes, currentUser, canForce, onUpdate, dataInicial, onClose }) {
  const [step, setStep] = useState(1); // 1: data/horário, 2: cliente, 3: confirmação
  const [data, setData] = useState(dataInicial || today());
  const [hora, setHora] = useState("");
  const [turnoId, setTurnoId] = useState("");
  const [party, setParty] = useState(2);
  const [salaId, setSalaId] = useState("");
  const [tipoMesaId, setTipoMesaId] = useState("");
  const [ocasiao, setOcasiao] = useState("");
  const [obsCliente, setObsCliente] = useState("");
  const [obsInternas, setObsInternas] = useState("");

  // Cliente: pode buscar existente ou criar novo
  const [clienteId, setClienteId] = useState(null);
  const [clienteSearch, setClienteSearch] = useState("");
  const [novoNome, setNovoNome] = useState("");
  const [novoTel, setNovoTel] = useState("");
  const [novoEmail, setNovoEmail] = useState("");

  const [confirmTipoMesa, setConfirmTipoMesa] = useState(false);

  const turnos = (config.turnos || []).filter(t => t.ativo !== false);
  const dataDow = data ? new Date(data + "T12:00:00").getDay() : null;
  const turnosDoDia = dataDow !== null ? turnos.filter(t => (t.diasSemana || []).includes(dataDow)) : [];

  // Calcula capacidade já usada nesse data + turno
  function capacidadeUsada(d, turno) {
    if (!turno) return 0;
    return (allReservas || [])
      .filter(r => r.restaurantId === restaurantId)
      .filter(r => r.dateTime?.slice(0,10) === d)
      .filter(r => r.turnoId === turno.id)
      .filter(r => r.status !== "cancelada" && r.status !== "no-show")
      .reduce((s, r) => s + (r.party || 0), 0);
  }

  const turnoSelecionado = turnos.find(t => t.id === turnoId);
  const usadasNoTurno = turnoSelecionado ? capacidadeUsada(data, turnoSelecionado) : 0;
  const overbookingMsg = turnoSelecionado && (usadasNoTurno + parseInt(party || 0)) > turnoSelecionado.capacidadePessoas;

  function selectCliente(c) {
    setClienteId(c.id);
    setNovoNome(c.nome);
    setNovoTel(c.telefone);
    setNovoEmail(c.email || "");
  }
  function clearCliente() {
    setClienteId(null);
    setNovoNome("");
    setNovoTel("");
    setNovoEmail("");
  }

  const filteredClientes = clientes.filter(c => {
    if (!clienteSearch.trim()) return false;
    const s = clienteSearch.toLowerCase();
    const tel = (c.telefone || "").replace(/\D/g, "");
    const sLimpo = s.replace(/\D/g, "");
    return (c.nome || "").toLowerCase().includes(s) || (sLimpo && tel.includes(sLimpo));
  }).slice(0, 6);

  function podeAvancar1() { return data && hora && turnoId && party > 0; }
  function podeAvancar2() { return novoNome.trim() && novoTel.replace(/\D/g, "").length >= 10; }
  function podeFinalizar() { return confirmTipoMesa; }

  function finalizar() {
    if (!podeFinalizar()) return;
    if (overbookingMsg && !canForce) { window.alert("Capacidade do turno excedida. Sem permissão pra forçar."); return; }
    const now = new Date().toISOString();
    const author = currentUser?.name || "Gestor";
    const authorId = currentUser?.id || null;
    const dateTime = `${data}T${hora}:00`;

    let finalClienteId = clienteId;
    let nextClientes = allClientes || [];
    if (!finalClienteId) {
      finalClienteId = `cli-${Date.now()}-${Math.random().toString(36).slice(2,5)}`;
      nextClientes = [...nextClientes, {
        id: finalClienteId,
        restaurantId,
        nome: novoNome.trim(),
        telefone: novoTel.trim(),
        email: novoEmail.trim() || null,
        tags: [], visitas: 0, noShows: 0, blacklisted: false,
        createdAt: now, createdBy: author,
      }];
      onUpdate("reservasClientes", nextClientes);
    }

    const novaReserva = {
      id: `res-${Date.now()}-${Math.random().toString(36).slice(2,5)}`,
      restaurantId, dateTime, party: parseInt(party),
      turnoId, salaId: salaId || null, tipoMesaId: tipoMesaId || null,
      mesaIds: [],
      clienteId: finalClienteId,
      nomeCliente: novoNome.trim(),
      telCliente: novoTel.trim(),
      emailCliente: novoEmail.trim() || null,
      status: "feita",
      ocasiao: ocasiao || null,
      observacoesCliente: obsCliente || null,
      observacoesInternas: obsInternas || null,
      fonte: "backoffice",
      forcada: !!overbookingMsg,
      criadaPor: author,
      criadaPorId: authorId,
      createdAt: now,
    };
    onUpdate("reservas", [...(allReservas || []), novaReserva]);
    onUpdate("_toast", `📅 Reserva criada${overbookingMsg ? " (forçada)" : ""}`);
    onClose();
  }

  const tiposNoSalaoAtivos = tipos.filter(t => t.ativa !== false && (!salaId || t.salaId === salaId));
  const salaSel = salas.find(s => s.id === salaId);
  const tipoSel = tipos.find(t => t.id === tipoMesaId);

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:14,maxWidth:560,width:"100%",padding:24,maxHeight:"90vh",overflowY:"auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <h3 style={{margin:0,color:"var(--text)",fontSize:16,fontWeight:700}}>📅 Nova Reserva — Passo {step}/3</h3>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:20,color:"var(--text3)",cursor:"pointer"}}>×</button>
        </div>

        {/* PASSO 1: data + horário + pessoas */}
        {step === 1 && (
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div><label style={{...S.label,fontSize:11}}>Data</label><input type="date" value={data} onChange={e=>{setData(e.target.value);setHora("");setTurnoId("");}} style={S.input}/></div>
              <div><label style={{...S.label,fontSize:11}}>Pessoas</label><input type="number" min="1" max="50" value={party} onChange={e=>setParty(parseInt(e.target.value)||0)} style={S.input}/></div>
            </div>
            {turnosDoDia.length === 0 ? (
              <div style={{padding:14,textAlign:"center",color:"#f59e0b",fontSize:12,background:"#f59e0b11",border:"1px solid #f59e0b33",borderRadius:8}}>
                ⚠️ Nenhum turno disponível neste dia. Verifique as configurações.
              </div>
            ) : (
              <>
                <div>
                  <label style={{...S.label,fontSize:11}}>Turno</label>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {turnosDoDia.map(t => {
                      const usadas = capacidadeUsada(data, t);
                      const livre = t.capacidadePessoas - usadas;
                      return (
                        <button key={t.id} onClick={()=>{setTurnoId(t.id);setHora("");}}
                          style={{padding:"8px 14px",borderRadius:8,border:`1px solid ${turnoId===t.id?"var(--ac)":"var(--border)"}`,background:turnoId===t.id?"var(--ac)11":"transparent",color:"var(--text)",cursor:"pointer",fontSize:12,textAlign:"left"}}>
                          <div style={{fontWeight:700}}>{t.nome}</div>
                          <div style={{fontSize:10,color:livre<=0?"#ef4444":livre<5?"#f59e0b":"var(--text3)"}}>{livre} de {t.capacidadePessoas} lugares livres</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {turnoId && (
                  <div>
                    <label style={{...S.label,fontSize:11}}>Horário</label>
                    <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                      {(turnos.find(t => t.id === turnoId)?.horarios || []).map(h => (
                        <button key={h} onClick={()=>setHora(h)}
                          style={{padding:"6px 12px",borderRadius:6,border:`1px solid ${hora===h?"var(--ac)":"var(--border)"}`,background:hora===h?"var(--ac)22":"transparent",color:hora===h?"var(--ac)":"var(--text2)",cursor:"pointer",fontSize:12,fontFamily:"'DM Mono',monospace"}}>
                          {h}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {salas.filter(s => s.ativa !== false).length > 0 && (
                  <div>
                    <label style={{...S.label,fontSize:11}}>Salão (opcional)</label>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                      <button onClick={()=>{setSalaId("");setTipoMesaId("");}} style={{padding:"5px 10px",borderRadius:6,border:`1px solid ${!salaId?"var(--ac)":"var(--border)"}`,background:!salaId?"var(--ac)22":"transparent",color:!salaId?"var(--ac)":"var(--text2)",cursor:"pointer",fontSize:11}}>Sem preferência</button>
                      {salas.filter(s => s.ativa !== false).map(s => (
                        <button key={s.id} onClick={()=>{setSalaId(s.id);setTipoMesaId("");}} style={{padding:"5px 10px",borderRadius:6,border:`1px solid ${salaId===s.id?"var(--ac)":"var(--border)"}`,background:salaId===s.id?"var(--ac)22":"transparent",color:salaId===s.id?"var(--ac)":"var(--text2)",cursor:"pointer",fontSize:11}}>
                          {s.nome}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {tiposNoSalaoAtivos.length > 0 && (
                  <div>
                    <label style={{...S.label,fontSize:11}}>Tipo de mesa (opcional)</label>
                    <div style={{display:"flex",flexDirection:"column",gap:6}}>
                      <button onClick={()=>setTipoMesaId("")} style={{padding:"8px 10px",borderRadius:6,border:`1px solid ${!tipoMesaId?"var(--ac)":"var(--border)"}`,background:!tipoMesaId?"var(--ac)11":"transparent",color:!tipoMesaId?"var(--ac)":"var(--text2)",cursor:"pointer",fontSize:11,textAlign:"left"}}>Sem preferência</button>
                      {tiposNoSalaoAtivos.map(t => (
                        <button key={t.id} onClick={()=>setTipoMesaId(t.id)} style={{padding:"8px 10px",borderRadius:6,border:`1px solid ${tipoMesaId===t.id?"var(--ac)":"var(--border)"}`,background:tipoMesaId===t.id?"var(--ac)11":"transparent",color:"var(--text)",cursor:"pointer",fontSize:11,textAlign:"left"}}>
                          <div style={{fontWeight:700}}>{t.label}</div>
                          {t.descricao && <div style={{fontSize:10,color:"var(--text3)",marginTop:2}}>{t.descricao}</div>}
                          <div style={{fontSize:9,color:"var(--text3)",marginTop:2}}>{t.capacidade} lugar(es) · {t.quantidade} unidade(s)</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {turnoSelecionado && overbookingMsg && (
                  <div style={{padding:"10px 12px",background:"#ef444411",border:"1px solid #ef444466",borderRadius:8,fontSize:12,color:"#ef4444"}}>
                    ⚠️ <strong>Capacidade ultrapassada.</strong> Turno tem {turnoSelecionado.capacidadePessoas} lugares, já reservados {usadasNoTurno}, esta reserva pediria +{party} = {usadasNoTurno + parseInt(party || 0)}.
                    {canForce ? <div style={{marginTop:4}}>Você tem permissão pra forçar essa reserva.</div> : <div style={{marginTop:4}}>Você não tem permissão pra forçar.</div>}
                  </div>
                )}
              </>
            )}
            <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:6}}>
              <button onClick={onClose} style={S.btnSecondary}>Cancelar</button>
              <button onClick={()=>setStep(2)} disabled={!podeAvancar1() || (overbookingMsg && !canForce)} style={{...S.btnPrimary,opacity:(!podeAvancar1() || (overbookingMsg && !canForce))?0.5:1}}>Próximo →</button>
            </div>
          </div>
        )}

        {/* PASSO 2: cliente */}
        {step === 2 && (
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div>
              <label style={{...S.label,fontSize:11}}>Buscar cliente existente</label>
              <input value={clienteSearch} onChange={e=>setClienteSearch(e.target.value)} placeholder="Digite nome ou telefone..." style={S.input}/>
              {filteredClientes.length > 0 && (
                <div style={{maxHeight:140,overflowY:"auto",border:"1px solid var(--border)",borderRadius:6,marginTop:4}}>
                  {filteredClientes.map(c => (
                    <button key={c.id} onClick={()=>{selectCliente(c);setClienteSearch("");}} style={{width:"100%",padding:"8px 10px",border:"none",borderBottom:"1px solid var(--border)",background:"transparent",color:"var(--text)",cursor:"pointer",textAlign:"left",fontSize:12}}>
                      <strong>{c.nome}</strong>
                      <span style={{color:"var(--text3)",marginLeft:8}}>{c.telefone}</span>
                      {c.blacklisted && <span style={{marginLeft:8,color:"#ef4444",fontSize:10}}>🚫 Bloqueado</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div style={{textAlign:"center",fontSize:11,color:"var(--text3)"}}>— ou cadastre um novo —</div>
            {clienteId && (
              <div style={{padding:"8px 10px",background:"var(--ac)11",border:"1px solid var(--ac)44",borderRadius:6,fontSize:11,color:"var(--text2)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span>📋 Cliente vinculado</span>
                <button onClick={clearCliente} style={{background:"none",border:"none",color:"var(--ac)",cursor:"pointer",fontSize:11}}>Trocar</button>
              </div>
            )}
            <div><label style={{...S.label,fontSize:11}}>Nome *</label><input value={novoNome} onChange={e=>setNovoNome(e.target.value)} style={S.input}/></div>
            <div><label style={{...S.label,fontSize:11}}>Telefone *</label><input value={novoTel} onChange={e=>setNovoTel(e.target.value)} placeholder="(11) 99999-9999" style={S.input} inputMode="tel"/></div>
            <div><label style={{...S.label,fontSize:11}}>Email</label><input type="email" value={novoEmail} onChange={e=>setNovoEmail(e.target.value)} style={S.input}/></div>
            <div><label style={{...S.label,fontSize:11}}>Ocasião (opcional)</label>
              <select value={ocasiao} onChange={e=>setOcasiao(e.target.value)} style={{...S.input,cursor:"pointer"}}>
                <option value="">Nenhuma</option>
                <option value="Aniversário">Aniversário</option>
                <option value="Negócio">Negócio</option>
                <option value="Comemoração">Comemoração</option>
                <option value="Encontro">Encontro</option>
                <option value="Outro">Outro</option>
              </select>
            </div>
            <div><label style={{...S.label,fontSize:11}}>Observação do cliente</label><textarea value={obsCliente} onChange={e=>setObsCliente(e.target.value)} rows={2} style={{...S.input,resize:"vertical"}} placeholder="ex: alergia a frutos do mar"/></div>
            <div><label style={{...S.label,fontSize:11}}>Observação interna (não é compartilhada com o cliente)</label><textarea value={obsInternas} onChange={e=>setObsInternas(e.target.value)} rows={2} style={{...S.input,resize:"vertical"}}/></div>
            <div style={{display:"flex",justifyContent:"space-between",gap:10,marginTop:6}}>
              <button onClick={()=>setStep(1)} style={S.btnSecondary}>← Voltar</button>
              <button onClick={()=>setStep(3)} disabled={!podeAvancar2()} style={{...S.btnPrimary,opacity:!podeAvancar2()?0.5:1}}>Próximo →</button>
            </div>
          </div>
        )}

        {/* PASSO 3: confirmação */}
        {step === 3 && (
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div style={{padding:14,background:"var(--bg2)",borderRadius:10,fontSize:13,color:"var(--text2)",lineHeight:1.7}}>
              <div style={{fontWeight:700,color:"var(--text)",marginBottom:8,fontSize:14}}>📋 Revise e confirme</div>
              <div><strong>Cliente:</strong> {novoNome}</div>
              <div><strong>Telefone:</strong> {novoTel}</div>
              {novoEmail && <div><strong>Email:</strong> {novoEmail}</div>}
              <div><strong>Data:</strong> {new Date(data+"T12:00:00").toLocaleDateString("pt-BR",{weekday:"long",day:"2-digit",month:"long",year:"numeric"})}</div>
              <div><strong>Horário:</strong> {hora}</div>
              <div><strong>Pessoas:</strong> {party}</div>
              {ocasiao && <div><strong>Ocasião:</strong> {ocasiao}</div>}
            </div>
            {(salaSel || tipoSel) && (
              <div style={{padding:14,border:"2px solid var(--ac)",borderRadius:10,background:"var(--ac)08"}}>
                <div style={{fontSize:11,color:"var(--ac)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>📍 Local da Mesa</div>
                {salaSel && <div style={{fontSize:14,color:"var(--text)",fontWeight:700}}>{salaSel.nome}</div>}
                {tipoSel && (
                  <>
                    <div style={{fontSize:13,color:"var(--text)",marginTop:4}}>→ {tipoSel.label}</div>
                    {tipoSel.descricao && <div style={{fontSize:12,color:"var(--text2)",marginTop:6,fontStyle:"italic"}}>"{tipoSel.descricao}"</div>}
                  </>
                )}
                <label style={{display:"flex",alignItems:"flex-start",gap:8,marginTop:12,cursor:"pointer"}}>
                  <input type="checkbox" checked={confirmTipoMesa} onChange={e=>setConfirmTipoMesa(e.target.checked)} style={{accentColor:"var(--ac)",marginTop:3}}/>
                  <span style={{fontSize:12,color:"var(--text2)"}}>Confirmo que esse é o local que o cliente quer.</span>
                </label>
              </div>
            )}
            {!salaSel && !tipoSel && (
              <label style={{display:"flex",alignItems:"flex-start",gap:8,padding:"10px 12px",border:"1px solid var(--border)",borderRadius:8,cursor:"pointer"}}>
                <input type="checkbox" checked={confirmTipoMesa} onChange={e=>setConfirmTipoMesa(e.target.checked)} style={{marginTop:3}}/>
                <span style={{fontSize:12,color:"var(--text2)"}}>Sem preferência de salão/mesa — alocação pelo restaurante. Confirmo.</span>
              </label>
            )}
            {overbookingMsg && (
              <div style={{padding:"10px 12px",background:"#ef444411",border:"1px solid #ef444466",borderRadius:8,fontSize:12,color:"#ef4444"}}>
                ⚡ <strong>Reserva forçada</strong> — capacidade do turno foi ultrapassada. Ficará marcada como forçada no histórico.
              </div>
            )}
            <div style={{display:"flex",justifyContent:"space-between",gap:10,marginTop:6}}>
              <button onClick={()=>setStep(2)} style={S.btnSecondary}>← Voltar</button>
              <button onClick={finalizar} disabled={!podeFinalizar() || (overbookingMsg && !canForce)} style={{...S.btnPrimary,opacity:(!podeFinalizar() || (overbookingMsg && !canForce))?0.5:1}}>Finalizar Reserva</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ──  MISE CHECKLISTS — ADMIN + USER                             ──
// ═══════════════════════════════════════════════════════════════
function MiseChecklistsAdmin({ restaurantId, miseChecklistTemplates, miseChecklistRuns, employees, pessoas, roles, onUpdate, mobileOnly }) {
  const restTemplates = (miseChecklistTemplates || []).filter(t => t.restaurantId === restaurantId);
  const restRuns = (miseChecklistRuns || []).filter(r => r.restaurantId === restaurantId);
  const [editingTplId, setEditingTplId] = useState(null);
  const [view, setView] = useState("templates"); // templates | historico
  const [newTplName, setNewTplName] = useState("");
  const miseAc = "#7c9e5e";
  const mkId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

  // v8.18.1: lista de pessoas elegíveis = quem tem permissão "Fazer checklists" (operational.checklistsExec)
  // Inclui também empregados com a área legada `checklists` marcada (compat enquanto migration nova não roda).
  // Não é mais restrito à equipe — qualquer pessoa do restaurante (admin, líder, etc.) com a permissão aparece.
  const _today = today();
  const restPessoas = (pessoas || []).filter(p => (p.restaurantIds || []).includes(restaurantId));
  const restEmps = (() => {
    const out = [];
    const seen = new Set();
    restPessoas.forEach(p => {
      const perms = p.permissions?.[restaurantId];
      const linkedEmp = (employees || []).find(e => e.id === p.linkedEmployeeId);
      const inactive = linkedEmp && linkedEmp.inactive && linkedEmp.inactiveFrom && linkedEmp.inactiveFrom <= _today;
      if (inactive) return;
      const hasNew = perms?.operational?.checklistsExec === true;
      const hasLegacy = linkedEmp?.operationalAreas?.checklists === true;
      if (!hasNew && !hasLegacy) return;
      const id = p.linkedEmployeeId || `pes_as_emp_${p.id}`;
      if (seen.has(id)) return;
      seen.add(id);
      out.push({ id, name: p.name });
    });
    // Fallback: empregados com área legada mas sem pessoa vinculada
    (employees || []).forEach(e => {
      if (e.restaurantId !== restaurantId) return;
      if (e.inactive && e.inactiveFrom && e.inactiveFrom <= _today) return;
      if (e.operationalAreas?.checklists !== true) return;
      if (seen.has(e.id)) return;
      seen.add(e.id);
      out.push({ id: e.id, name: e.name });
    });
    return out.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  })();
  const restAreas = [...new Set((roles || []).map(r => r.area).filter(Boolean))];

  function addTemplate() {
    const nm = newTplName.trim();
    if (!nm) return;
    if (restTemplates.some(t => t.name.toLowerCase() === nm.toLowerCase())) { alert("Já existe template com esse nome."); return; }
    const t = { id: mkId(), restaurantId, name: nm, description: "", items: [], active: true, area: "Geral", responsaveis: [] };
    onUpdate("miseChecklistTemplates", [...(miseChecklistTemplates || []), t]);
    setNewTplName("");
    setEditingTplId(t.id);
  }
  async function delTemplate(id) {
    const t = restTemplates.find(x => x.id === id);
    const linkedRuns = restRuns.filter(r => r.templateId === id).length;
    if (!await appConfirm(`Apagar template "${t?.name}"?\n${linkedRuns} execução(ões) também serão apagadas.`)) return;
    onUpdate("miseChecklistTemplates", (miseChecklistTemplates || []).filter(x => x.id !== id));
    onUpdate("miseChecklistRuns", (miseChecklistRuns || []).filter(r => r.templateId !== id));
  }
  function updateTpl(id, patch) {
    onUpdate("miseChecklistTemplates", (miseChecklistTemplates || []).map(t => t.id === id ? { ...t, ...patch } : t));
    // v8.21.0: se mudou os responsáveis, auto-concede operational.checklistsExec aos novos pra que vejam o checklist no Operacional.
    if (patch.responsaveis !== undefined) {
      const oldTpl = (miseChecklistTemplates || []).find(t => t.id === id);
      const oldResp = (oldTpl?.responsaveis || []);
      const newResp = patch.responsaveis || [];
      const added = newResp.filter(eid => !oldResp.includes(eid));
      if (added.length > 0) {
        // Mapeia empId → pessoa (via linkedEmployeeId ou fallback id pes_as_emp_)
        const targetPessoaIds = added.map(empId => {
          const direct = (pessoas || []).find(p => p.linkedEmployeeId === empId);
          if (direct) return direct.id;
          // Caso o empId já seja pes_as_emp_<id>
          const m = String(empId).match(/^pes_as_emp_(.+)$/);
          if (m) return m[1];
          return null;
        }).filter(Boolean);
        if (targetPessoaIds.length > 0) {
          const nextPessoas = (pessoas || []).map(p => {
            if (!targetPessoaIds.includes(p.id)) return p;
            const cur = p.permissions?.[restaurantId]?.operational?.checklistsExec === true;
            if (cur) return p; // já tem
            return {
              ...p,
              permissions: {
                ...(p.permissions || {}),
                [restaurantId]: {
                  ...(p.permissions?.[restaurantId] || { operational: {}, admin: {}, special: {} }),
                  operational: {
                    ...(p.permissions?.[restaurantId]?.operational || {}),
                    checklistsExec: true,
                  },
                },
              },
            };
          });
          onUpdate("pessoas", nextPessoas);
          onUpdate("_toast", `🔓 Permissão "Fazer checklists" concedida automaticamente`);
        }
      }
    }
  }
  function toggleActive(id) {
    const t = restTemplates.find(x => x.id === id);
    updateTpl(id, { active: !t.active });
  }

  if (editingTplId) {
    const tpl = restTemplates.find(t => t.id === editingTplId);
    if (!tpl) { setTimeout(()=>setEditingTplId(null),0); return null; }
    return <MiseChecklistTemplateEditor
      tpl={tpl}
      updateTpl={(patch) => updateTpl(tpl.id, patch)}
      onBack={() => setEditingTplId(null)}
      mobileOnly={mobileOnly}
      mkId={mkId}
      restEmps={restEmps}
      restAreas={restAreas}
    />;
  }

  const VIEWS = [
    { id: "templates", label: "Templates" },
    { id: "historico", label: "Histórico" },
  ];

  return (
    <div>
      <div style={{display:"flex",gap:4,marginBottom:16,borderBottom:"1px solid var(--border)",overflowX:"auto"}}>
        {VIEWS.map(v => (
          <button key={v.id} onClick={()=>setView(v.id)}
            style={{background:"none",border:"none",borderBottom: view===v.id ? `2px solid ${miseAc}` : "2px solid transparent",padding:"8px 14px",cursor:"pointer",fontSize:13,fontWeight: view===v.id ? 700 : 500,color: view===v.id ? "var(--text)" : "var(--text3)",whiteSpace:"nowrap",flexShrink:0}}>
            {v.label}
          </button>
        ))}
      </div>

      {view === "templates" && (
        <div>
          <div style={{padding:"12px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,marginBottom:16}}>
            <div style={{display:"flex",gap:8,alignItems:"end",flexWrap:"wrap"}}>
              <div style={{flex:"1 1 220px"}}>
                <label style={{...S.label,fontSize:11}}>Nome do template</label>
                <input value={newTplName} onChange={e=>setNewTplName(e.target.value)} placeholder="ex: Abertura de Caixa, Limpeza Fim de Expediente" style={S.input} onKeyDown={e=>e.key==="Enter"&&addTemplate()} />
              </div>
              <button onClick={addTemplate} disabled={!newTplName.trim()} style={{...S.btnPrimary,fontSize:13,padding:"8px 16px",opacity:!newTplName.trim()?0.5:1}}>+ Novo</button>
            </div>
          </div>
          {restTemplates.length === 0 ? (
            <div style={{padding:"40px 24px",textAlign:"center",color:"var(--text3)",fontSize:14,background:"var(--card-bg)",borderRadius:12,border:"1px solid var(--border)"}}>
              <div style={{fontSize:40,marginBottom:12}}>✅</div>
              <div style={{color:"var(--text)",fontWeight:600,fontSize:16,marginBottom:6}}>Nenhum template criado</div>
              <div>Templates são listas reutilizáveis (ex: abertura, fechamento, limpeza).</div>
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {[...restTemplates].sort((a,b)=>a.name.localeCompare(b.name)).map(t => {
                const runsCount = restRuns.filter(r => r.templateId === t.id).length;
                const area = t.area || "Geral";
                const responsaveisCount = (t.responsaveis || []).length;
                const responsaveisNames = (t.responsaveis || []).map(eid => shortName(restEmps.find(e => e.id === eid)?.name)).filter(Boolean);
                return (
                  <div key={t.id} style={{display:"flex",alignItems:"center",gap:10,padding:"12px 14px",background:"var(--card-bg)",border:`1px solid ${t.active?"var(--border)":"#f59e0b44"}`,borderRadius:10,opacity:t.active?1:0.6,flexWrap:"wrap"}}>
                    <div style={{flex:1,minWidth:180,cursor:"pointer"}} onClick={()=>setEditingTplId(t.id)}>
                      <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                        <span style={{color:"var(--text)",fontWeight:700}}>{t.name}</span>
                        <span style={{fontSize:10,padding:"2px 8px",borderRadius:10,background:area==="Geral"?"var(--bg2)":(AREA_COLORS[area]||"#888")+"22",color:area==="Geral"?"var(--text3)":(AREA_COLORS[area]||"#888"),fontWeight:700}}>{area}</span>
                        {!t.active && <span style={{fontSize:10,padding:"1px 8px",borderRadius:10,background:"#fffbeb",color:"#92400e",fontWeight:700}}>INATIVO</span>}
                      </div>
                      <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>
                        {(t.items||[]).length} item(ns) · {runsCount} execução(ões)
                        {responsaveisCount > 0 && <> · 👤 {responsaveisNames.slice(0,3).join(", ")}{responsaveisCount > 3 ? ` +${responsaveisCount-3}` : ""}</>}
                      </div>
                    </div>
                    <button onClick={()=>setEditingTplId(t.id)} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px"}}>Editar</button>
                    <button onClick={()=>toggleActive(t.id)} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px"}}>{t.active?"Desativar":"Ativar"}</button>
                    <button onClick={()=>delTemplate(t.id)} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px",color:"var(--red)",borderColor:"var(--red)44"}}>Apagar</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {view === "historico" && (
        <div>
          {restRuns.length === 0 ? (
            <div style={{padding:"40px 24px",textAlign:"center",color:"var(--text3)",fontSize:14,background:"var(--card-bg)",borderRadius:12,border:"1px solid var(--border)"}}>
              Nenhuma execução registrada ainda.
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {[...restRuns].sort((a,b)=>(b.date||"").localeCompare(a.date||"")).slice(0, 100).map(r => {
                const tpl = restTemplates.find(t => t.id === r.templateId);
                const doneCount = (r.items || []).filter(i => i.done).length;
                const totalCount = (r.items || []).length;
                const pct = totalCount > 0 ? Math.round(doneCount/totalCount*100) : 0;
                return (
                  <div key={r.id} style={{padding:"10px 14px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                      <div style={{flex:1,minWidth:180}}>
                        <div style={{color:"var(--text)",fontWeight:600}}>{tpl?.name ?? "Template removido"}</div>
                        <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>
                          {r.userName || "—"} · {fmtDate(r.date)}
                          {r.completedAt && <span style={{marginLeft:6,color:"#15803d",fontWeight:600}}>✓ Finalizado</span>}
                        </div>
                      </div>
                      <div style={{textAlign:"right"}}>
                        <div style={{fontSize:13,fontWeight:700,color:pct===100?"#15803d":"var(--text)",fontFamily:"'DM Mono',monospace"}}>{doneCount}/{totalCount}</div>
                        <div style={{width:80,height:4,background:"var(--bg2)",borderRadius:2,marginTop:4,overflow:"hidden"}}>
                          <div style={{width:`${pct}%`,height:"100%",background:pct===100?"#15803d":miseAc}}/>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Editor de um template
function MiseChecklistTemplateEditor({ tpl, updateTpl, onBack, mobileOnly, mkId, restEmps, restAreas }) {
  const [local, setLocal] = useState(tpl);
  useEffect(() => { setLocal(tpl); }, [tpl.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = JSON.stringify(local) !== JSON.stringify(tpl);
  const miseAc = "#7c9e5e";
  // v8.18.0: modal de adicionar em lote
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");

  function save() { updateTpl(local); }
  async function handleBack() {
    if (dirty && !await appConfirm("Descartar alterações e voltar?")) return;
    onBack();
  }
  function addItem() {
    const text = window.prompt("Texto do item:");
    if (!text || !text.trim()) return;
    setLocal(l => ({ ...l, items: [...(l.items || []), { id: mkId(), text: text.trim(), order: (l.items || []).length }] }));
  }
  // v8.18.0: parse texto multi-linhas → vários itens
  function applyBulk() {
    const lines = bulkText.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
    if (lines.length === 0) { setBulkOpen(false); return; }
    setLocal(l => {
      const startOrder = (l.items || []).length;
      const newItems = lines.map((text, i) => ({ id: mkId(), text, order: startOrder + i }));
      return { ...l, items: [...(l.items || []), ...newItems] };
    });
    setBulkText("");
    setBulkOpen(false);
  }
  function updateItem(id, patch) {
    setLocal(l => ({ ...l, items: (l.items || []).map(i => i.id === id ? { ...i, ...patch } : i) }));
  }
  function delItem(id) {
    setLocal(l => ({ ...l, items: (l.items || []).filter(i => i.id !== id) }));
  }
  function moveItem(id, dir) {
    setLocal(l => {
      const arr = [...(l.items || [])];
      const idx = arr.findIndex(i => i.id === id);
      const ni = idx + dir;
      if (ni < 0 || ni >= arr.length) return l;
      [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
      return { ...l, items: arr.map((i, k) => ({ ...i, order: k })) };
    });
  }
  // v8.18.0: helpers responsáveis
  function toggleResponsavel(empId) {
    setLocal(l => {
      const cur = l.responsaveis || [];
      return { ...l, responsaveis: cur.includes(empId) ? cur.filter(x => x !== empId) : [...cur, empId] };
    });
  }
  const empsSorted = [...(restEmps || [])].sort((a,b) => a.name.localeCompare(b.name));
  const areasOpts = ["Geral", ...(restAreas || [])];

  return (
    <div>
      <div style={{display:"flex",gap:8,marginBottom:16,alignItems:"center",flexWrap:"wrap"}}>
        <button onClick={handleBack} style={{...S.btnSecondary,fontSize:13,padding:"8px 14px"}}>← Voltar</button>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:11,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Editando template</div>
          <div style={{fontSize:15,color:"var(--text)",fontWeight:700}}>{local.name}</div>
        </div>
        {dirty && <span style={{color:"#f59e0b",fontSize:11,fontWeight:700,padding:"3px 8px",background:"#fffbeb",border:"1px solid #f59e0b44",borderRadius:10}}>● Não salvo</span>}
        <button onClick={save} disabled={!dirty} style={{background:dirty?miseAc:"var(--border)",color:dirty?"#fff":"var(--text3)",border:"none",borderRadius:8,padding:"8px 18px",fontSize:13,fontWeight:700,cursor:dirty?"pointer":"not-allowed",fontFamily:"'DM Sans',sans-serif"}}>💾 Salvar</button>
      </div>

      <div style={{marginBottom:14}}>
        <label style={S.label}>Nome</label>
        <input value={local.name ?? ""} onChange={e=>setLocal({...local, name: e.target.value})} style={S.input} />
      </div>
      <div style={{marginBottom:14}}>
        <label style={S.label}>Descrição (opcional)</label>
        <textarea value={local.description ?? ""} onChange={e=>setLocal({...local, description: e.target.value})} placeholder="Quando usar, instruções gerais..." style={{...S.input,minHeight:60,fontFamily:"inherit",resize:"vertical"}} />
      </div>

      {/* v8.18.0: área + responsáveis */}
      <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"1fr 2fr",gap:14,marginBottom:14}}>
        <div>
          <label style={S.label}>Área</label>
          <select value={local.area || "Geral"} onChange={e=>setLocal({...local, area: e.target.value})} style={S.input}>
            {areasOpts.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <label style={S.label}>Responsáveis ({(local.responsaveis||[]).length})</label>
          <div style={{padding:"8px 10px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:8,maxHeight:120,overflow:"auto"}}>
            {empsSorted.length === 0 ? (
              <div style={{color:"var(--text3)",fontSize:12,fontStyle:"italic"}}>Nenhum empregado cadastrado.</div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:4}}>
                {empsSorted.map(e => {
                  const checked = (local.responsaveis || []).includes(e.id);
                  return (
                    <label key={e.id} style={{display:"flex",alignItems:"center",gap:7,fontSize:12,cursor:"pointer"}}>
                      <input type="checkbox" checked={checked} onChange={()=>toggleResponsavel(e.id)} style={{accentColor:miseAc}}/>
                      <span style={{color:"var(--text)"}}>{shortName(e.name)}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10,flexWrap:"wrap",gap:8}}>
        <h4 style={{color:"var(--text)",margin:0,fontSize:15,fontWeight:700}}>Itens ({(local.items || []).length})</h4>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          <button onClick={()=>setBulkOpen(true)} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px",color:miseAc,borderColor:miseAc+"55"}} title="Cole várias linhas — cada uma vira uma atividade">📋 Adicionar em lote</button>
          <button onClick={addItem} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px"}}>+ Adicionar item</button>
        </div>
      </div>

      {/* v8.18.0: Modal de adicionar em lote */}
      {bulkOpen && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={()=>setBulkOpen(false)}>
          <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:14,maxWidth:560,width:"100%",padding:24}}>
            <h3 style={{margin:"0 0 8px",color:"var(--text)",fontSize:16,fontWeight:700}}>📋 Adicionar atividades em lote</h3>
            <p style={{color:"var(--text3)",fontSize:12,margin:"0 0 12px",lineHeight:1.5}}>Cole abaixo, uma atividade por linha. Linhas em branco são ignoradas. As atividades vão pro fim da lista.</p>
            <textarea value={bulkText} onChange={e=>setBulkText(e.target.value)} rows={10} placeholder={"Conferir vidros do bar\nLavar pisos do salão\nLimpar mesas\nVerificar copos limpos\n..."} style={{...S.input,width:"100%",minHeight:200,fontFamily:"'DM Mono',monospace",fontSize:13,resize:"vertical"}} autoFocus/>
            <div style={{fontSize:11,color:"var(--text3)",marginTop:6}}>
              {bulkText.split(/\r?\n/).filter(s => s.trim()).length} atividade(s) detectada(s)
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:14}}>
              <button onClick={()=>{setBulkOpen(false);setBulkText("");}} style={{...S.btnSecondary,padding:"8px 14px",fontSize:13}}>Cancelar</button>
              <button onClick={applyBulk} disabled={bulkText.split(/\r?\n/).filter(s=>s.trim()).length===0} style={{...S.btnPrimary,padding:"8px 14px",fontSize:13,fontWeight:700,background:miseAc,borderColor:miseAc,opacity:bulkText.split(/\r?\n/).filter(s=>s.trim()).length===0?0.5:1}}>Adicionar todas</button>
            </div>
          </div>
        </div>
      )}

      {(local.items || []).length === 0 ? (
        <div style={{padding:"32px 16px",textAlign:"center",color:"var(--text3)",fontSize:14,background:"var(--card-bg)",borderRadius:12,border:"1px dashed var(--border)"}}>
          Sem itens. Clique em "+ Adicionar item" pra começar.
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {(local.items || []).map((it, idx) => (
            <div key={it.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10}}>
              <span style={{fontSize:11,color:"var(--text3)",fontFamily:"'DM Mono',monospace",minWidth:24}}>{idx+1}.</span>
              <input value={it.text} onChange={e=>updateItem(it.id, { text: e.target.value })} style={{...S.input,flex:1}} />
              <button onClick={()=>moveItem(it.id, -1)} disabled={idx===0} style={{...S.btnSecondary,fontSize:11,padding:"4px 8px",opacity:idx===0?0.3:1}}>↑</button>
              <button onClick={()=>moveItem(it.id, 1)} disabled={idx===(local.items||[]).length-1} style={{...S.btnSecondary,fontSize:11,padding:"4px 8px",opacity:idx===(local.items||[]).length-1?0.3:1}}>↓</button>
              <button onClick={()=>delItem(it.id)} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px",color:"var(--red)",borderColor:"var(--red)44"}}>×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── CHECKLISTS — USER (Gestor Operacional) ──
function OperationalChecklists({ employee, pessoas = [], isOwner = false, miseChecklistTemplates, miseChecklistRuns, onUpdate }) {
  const restaurantId = employee.restaurantId;
  const today_ = today();
  // v8.21.0: filtra templates onde:
  //   - responsaveis vazio → visível pra todos com perm exec (comportamento antigo)
  //   - responsaveis preenchido → só pra quem está na lista (delegação)
  // v8.22.1: Owner / quem configura (operational.checklists) / quem confere (operational.checklistsReview)
  //          enxerga TUDO mesmo se não estiver na lista de responsáveis.
  const _myPessoaForFilter = (() => {
    const pid = employee._pessoaId || (() => {
      const m = String(employee.id).match(/^pes_as_emp_(.+)$/);
      return m ? m[1] : null;
    })();
    if (pid) return (pessoas || []).find(p => p.id === pid);
    return (pessoas || []).find(p => p.linkedEmployeeId === employee.id);
  })();
  const _myPerms = _myPessoaForFilter?.permissions?.[restaurantId] || {};
  const seesAllChecklists = isOwner
    || _myPerms.operational?.checklists === true
    || _myPerms.operational?.checklistsReview === true;
  const templates = (miseChecklistTemplates || []).filter(t => {
    if (t.restaurantId !== restaurantId) return false;
    if (t.active === false) return false;
    if (seesAllChecklists) return true;
    const resp = t.responsaveis || [];
    if (resp.length === 0) return true;
    return resp.includes(employee.id);
  });
  const myRunsToday = (miseChecklistRuns || []).filter(r =>
    r.restaurantId === restaurantId && r.userId === employee.id && r.date === today_
  );
  // Runs DE HOJE de todo mundo no restaurante — pra mostrar "último envio" por template
  const restRunsToday = (miseChecklistRuns || []).filter(r =>
    r.restaurantId === restaurantId && r.date === today_ && r.completedAt
  );
  const [expandedTpl, setExpandedTpl] = useState(null);
  const miseAc = "#7c9e5e";

  // v8.19.0: rascunho agora persiste no Firestore (mesma coleção miseChecklistRuns, sem completedAt).
  // - drafts (state local) = espelho do que está sendo editado, pra UI responsiva
  // - persistDraft = grava o run sem completedAt (debounced) — sobrevive a fechar a aba
  // - submitChecklist = persistDraft com isSubmit=true → adiciona completedAt
  // - draftRunIds = ID estável por template no ciclo de vida da tela, evita duplicar runs
  // - savedTimestamps = mostra "💾 Salvo" depois de cada autosave
  const [drafts, setDrafts] = useState({});
  const [savedTimestamps, setSavedTimestamps] = useState({}); // { [tplId]: ISO }
  const draftRunIds = useRef({});
  const draftSaveTimers = useRef({});

  function getOrCreateRunId(tpl) {
    if (draftRunIds.current[tpl.id]) return draftRunIds.current[tpl.id];
    const existingRun = myRunsToday.find(r => r.templateId === tpl.id);
    const id = existingRun?.id || `clrun_${Date.now().toString(36)}${Math.random().toString(36).slice(2,6)}`;
    draftRunIds.current[tpl.id] = id;
    return id;
  }

  // Garante que um draft exista pra esse template, inicializado a partir do run salvo (rascunho ou enviado)
  function ensureDraft(tpl) {
    if (drafts[tpl.id]) return drafts[tpl.id];
    const existingRun = myRunsToday.find(r => r.templateId === tpl.id);
    const initial = {};
    (tpl.items || []).forEach(it => {
      const ri = (existingRun?.items || []).find(x => x.itemId === it.id);
      initial[it.id] = {
        done: !!ri?.done,
        note: ri?.note || "",
        showNote: !!ri?.note, // se já tinha nota, expande
      };
    });
    setDrafts(d => ({ ...d, [tpl.id]: initial }));
    return initial;
  }

  function getItemState(tpl, itemId) {
    const draft = drafts[tpl.id];
    if (draft && draft[itemId]) return draft[itemId];
    const existingRun = myRunsToday.find(r => r.templateId === tpl.id);
    const ri = existingRun?.items?.find(x => x.itemId === itemId);
    return { done: !!ri?.done, note: ri?.note || "", showNote: !!ri?.note };
  }

  // Constrói o objeto run a partir do draft. Se isSubmit=true, marca completedAt.
  // v8.21.0: bypass — se quem submete já pode conferir (líder da área), marca reviewedAt = completedAt.
  function buildRun(tpl, draftData, isSubmit) {
    const id = getOrCreateRunId(tpl);
    const now = new Date().toISOString();
    const existing = (miseChecklistRuns || []).find(r => r.id === id);
    const newItems = (tpl.items || []).map(it => {
      const d = draftData[it.id] || {};
      const row = { itemId: it.id, done: !!d.done };
      if (d.done) row.doneAt = (existing?.items?.find(x=>x.itemId===it.id)?.doneAt) || now;
      if (d.note && d.note.trim()) row.note = d.note.trim();
      return row;
    });
    const run = {
      id,
      restaurantId,
      templateId: tpl.id,
      userId: employee.id,
      userName: employee.name,
      date: today_,
      items: newItems,
      updatedAt: now,
    };
    // Preserva completedAt se já existia, ou seta agora se for submit
    if (isSubmit) run.completedAt = now;
    else if (existing?.completedAt) run.completedAt = existing.completedAt;
    // v8.21.0: bypass de conferência — quem confere e preenche, autoaprova
    if (isSubmit) {
      // Pessoa equivalente ao employee atual: tenta achar via _pessoaId (se foi um empVirtual) ou via linkedEmployeeId
      const myPessoaId = employee._pessoaId || (() => {
        const m = String(employee.id).match(/^pes_as_emp_(.+)$/);
        return m ? m[1] : null;
      })();
      const myPessoa = myPessoaId
        ? (pessoas || []).find(p => p.id === myPessoaId)
        : (pessoas || []).find(p => p.linkedEmployeeId === employee.id);
      if (myPessoa && canReviewChecklist(myPessoa, tpl, restaurantId)) {
        run.reviewedAt = now;
        run.reviewerId = employee.id;
        run.reviewerName = employee.name;
        run.autoBypass = true;
      }
    } else if (existing?.reviewedAt) {
      // Preserva review anterior (não deve acontecer em rascunho mas defensivo)
      run.reviewedAt = existing.reviewedAt;
      run.reviewerId = existing.reviewerId;
      run.reviewerName = existing.reviewerName;
    }
    if (existing?.reviewerNotes) run.reviewerNotes = existing.reviewerNotes;
    if (existing?.linkedOccurrenceIds) run.linkedOccurrenceIds = existing.linkedOccurrenceIds;
    return { run, existing };
  }

  // v8.22.2: helper — true se o draft tem ao menos 1 item marcado ou 1 nota não-vazia
  function draftHasContent(draftData) {
    return Object.values(draftData || {}).some(d => d?.done || (d?.note && d.note.trim()));
  }

  function persistDraft(tpl, draftData) {
    const id = getOrCreateRunId(tpl);
    // v8.22.2: se draft ficou vazio (ex: marcou e desmarcou tudo), descarta o rascunho
    // pra não poluir histórico/lista de pendentes com run sem conteúdo.
    if (!draftHasContent(draftData)) {
      onUpdate("miseChecklistRuns", (prev) => (prev || []).filter(r => !(r.id === id && !r.completedAt)));
      setDrafts(d => { const n = { ...d }; delete n[tpl.id]; return n; });
      setSavedTimestamps(s => { const n = { ...s }; delete n[tpl.id]; return n; });
      delete draftRunIds.current[tpl.id]; // próxima edição gera id novo
      return;
    }
    const { run } = buildRun(tpl, draftData, false);
    onUpdate("miseChecklistRuns", (prev) => {
      const arr = prev || [];
      if (arr.some(r => r.id === run.id)) return arr.map(r => r.id === run.id ? run : r);
      return [...arr, run];
    });
    setSavedTimestamps(s => ({ ...s, [tpl.id]: run.updatedAt }));
  }

  function updateItem(tpl, itemId, patch) {
    setDrafts(d => {
      const base = d[tpl.id] || ensureDraft(tpl);
      const newDraft = { ...base, [itemId]: { ...getItemState(tpl, itemId), ...base?.[itemId], ...patch } };
      // Debounce 800ms — segura ondas de cliques antes de gravar
      if (draftSaveTimers.current[tpl.id]) clearTimeout(draftSaveTimers.current[tpl.id]);
      draftSaveTimers.current[tpl.id] = setTimeout(() => persistDraft(tpl, newDraft), 800);
      return { ...d, [tpl.id]: newDraft };
    });
  }

  // v8.23.0: reabre o próprio envio pra editar/adicionar coisa (válido enquanto não foi conferido)
  function reopenSubmission(tpl) {
    const run = myRunsToday.find(r => r.templateId === tpl.id);
    if (!run || !run.completedAt) return;
    if (run.reviewedAt) {
      onUpdate("_toast", "⚠️ Este envio já foi conferido. Não é possível reabrir.");
      return;
    }
    // Mantém o mesmo id pra sobrescrever o run ao re-enviar
    draftRunIds.current[tpl.id] = run.id;
    // Popula drafts local com items atuais
    const draftFromRun = {};
    (tpl.items || []).forEach(it => {
      const ri = (run.items || []).find(x => x.itemId === it.id);
      draftFromRun[it.id] = {
        done: !!ri?.done,
        note: ri?.note || "",
        showNote: !!ri?.note,
      };
    });
    setDrafts(d => ({ ...d, [tpl.id]: draftFromRun }));
    // Remove completedAt + autoBypass do run no Firestore (vira rascunho de novo)
    onUpdate("miseChecklistRuns", (prev) => (prev || []).map(r => {
      if (r.id !== run.id) return r;
      const next = { ...r };
      delete next.completedAt;
      delete next.autoBypass;
      return next;
    }));
    setExpandedTpl(tpl.id);
    onUpdate("_toast", "✏️ Envio reaberto — edite e envie de novo");
  }

  async function submitChecklist(tpl) {
    const draft = drafts[tpl.id] || ensureDraft(tpl);
    // v8.22.2: bloqueia envio sem nada preenchido (pelo menos 1 item ou 1 nota)
    if (!draftHasContent(draft)) {
      onUpdate("_toast", "⚠️ Marque pelo menos 1 item antes de enviar");
      return;
    }
    // Cancela qualquer save em flight pra não sobrescrever depois do submit
    if (draftSaveTimers.current[tpl.id]) clearTimeout(draftSaveTimers.current[tpl.id]);
    const { run } = buildRun(tpl, draft, true);
    onUpdate("miseChecklistRuns", (prev) => {
      const arr = prev || [];
      if (arr.some(r => r.id === run.id)) return arr.map(r => r.id === run.id ? run : r);
      return [...arr, run];
    });
    // Limpa draft local — vai ler do run salvo agora (read-only)
    setDrafts(d => { const n = { ...d }; delete n[tpl.id]; return n; });
    setSavedTimestamps(s => { const n = { ...s }; delete n[tpl.id]; return n; });
    onUpdate("_toast", `✓ Checklist "${tpl.name}" enviado`);
  }

  // v8.21.0: detecta se a pessoa atual pode conferir algum template, pra exibir aba "Conferir"
  const myPessoaForReview = (() => {
    const pid = employee._pessoaId || (() => {
      const m = String(employee.id).match(/^pes_as_emp_(.+)$/);
      return m ? m[1] : null;
    })();
    if (pid) return (pessoas || []).find(p => p.id === pid);
    return (pessoas || []).find(p => p.linkedEmployeeId === employee.id);
  })();
  const allActiveTpls = (miseChecklistTemplates || []).filter(t => t.restaurantId === restaurantId && t.active !== false);
  const myReviewableTpls = myPessoaForReview
    ? allActiveTpls.filter(t => canReviewChecklist(myPessoaForReview, t, restaurantId))
    : [];
  const pendingReviews = (miseChecklistRuns || []).filter(r =>
    r.restaurantId === restaurantId &&
    r.completedAt && !r.reviewedAt &&
    myReviewableTpls.some(t => t.id === r.templateId) &&
    r.userId !== employee.id // não confere o próprio (já tem bypass)
  );
  const canReviewAny = myReviewableTpls.length > 0;
  const [view, setView] = useState("executar"); // executar | conferir
  // v8.22.0: agrupa runs do mesmo template+data — chave é `${tplId}__${date}`
  const [reviewingGroupKey, setReviewingGroupKey] = useState(null);

  if (templates.length === 0 && !canReviewAny) {
    return (
      <div style={{padding:"60px 24px",textAlign:"center",color:"var(--text3)"}}>
        <div style={{fontSize:48,marginBottom:16}}>✅</div>
        <h3 style={{color:"var(--text)",fontSize:18,fontWeight:700,margin:"0 0 8px"}}>Sem checklists disponíveis</h3>
        <p style={{fontSize:14,lineHeight:1.6,maxWidth:420,margin:"0 auto"}}>
          O gestor ainda não criou nenhum template de checklist para este restaurante.
        </p>
      </div>
    );
  }

  // Render do modo Conferir (sub-view ou modal de revisão)
  if (canReviewAny && view === "conferir") {
    return (
      <ChecklistsConferenciaView
        restaurantId={restaurantId}
        employee={employee}
        pessoas={pessoas}
        miseChecklistTemplates={miseChecklistTemplates}
        miseChecklistRuns={miseChecklistRuns}
        onUpdate={onUpdate}
        myReviewableTpls={myReviewableTpls}
        pendingReviews={pendingReviews}
        reviewingGroupKey={reviewingGroupKey}
        setReviewingGroupKey={setReviewingGroupKey}
        onSwitchToExecutar={() => setView("executar")}
        myPessoa={myPessoaForReview}
      />
    );
  }

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      {canReviewAny && (
        <div style={{display:"flex",gap:6,padding:4,background:"var(--bg2)",borderRadius:10,border:"1px solid var(--border)"}}>
          <button onClick={()=>setView("executar")}
            style={{flex:1,padding:"8px 12px",borderRadius:8,border:"none",background:"#15803d",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer"}}>
            ✅ Executar
          </button>
          <button onClick={()=>setView("conferir")}
            style={{flex:1,padding:"8px 12px",borderRadius:8,border:"none",background:"transparent",color:"var(--text2)",fontSize:13,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
            🔎 Conferir
            {pendingReviews.length > 0 && (
              <span style={{padding:"1px 8px",background:"#dc2626",color:"#fff",borderRadius:10,fontSize:11,fontWeight:700}}>{pendingReviews.length}</span>
            )}
          </button>
        </div>
      )}
      <div style={{padding:"12px 16px",background:"#f0fdf4",border:"1px solid #10b98144",borderRadius:10}}>
        <div style={{fontSize:11,color:"#15803d",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Checklists de hoje</div>
        <div style={{fontSize:13,color:"var(--text)",marginTop:2}}>{fmtDate(today_)} · {shortName(employee.name)}</div>
      </div>

      {templates.map(tpl => {
        const run = myRunsToday.find(r => r.templateId === tpl.id);
        const isCompleted = !!run?.completedAt;
        const isReviewed = !!run?.reviewedAt;
        // v8.19.0: rascunho = run existe mas sem completedAt, OU draft local com conteúdo de verdade
        // v8.23.4: só conta como rascunho se tem item marcado ou nota com texto (showNote=true sozinho não conta)
        const localDraft = drafts[tpl.id];
        const localHasRealContent = localDraft && Object.values(localDraft).some(d => d?.done || (d?.note && d.note.trim()));
        const isDraft = !isCompleted && (!!run || localHasRealContent);
        const items = tpl.items || [];
        const draftForTpl = drafts[tpl.id];
        const savedAt = savedTimestamps[tpl.id];
        // Contagem "ao vivo" baseada no draft (se aberto) ou no run salvo
        const doneCount = items.reduce((acc, it) => {
          if (draftForTpl && draftForTpl[it.id]) return acc + (draftForTpl[it.id].done ? 1 : 0);
          const ri = run?.items?.find(x => x.itemId === it.id);
          return acc + (ri?.done ? 1 : 0);
        }, 0);
        const pct = items.length > 0 ? Math.round(doneCount/items.length*100) : 0;
        // v8.19.1: cards sempre começam fechados — só expande o que o usuário clicou.
        // expandedTpl é singular (1 só aberto por vez) → clicar em outro card fecha o anterior.
        const isExpanded = expandedTpl === tpl.id;
        // v8.23.0: outros enviaram esse template hoje (excluindo eu)
        const othersRunsToday = restRunsToday.filter(r => r.templateId === tpl.id && r.userId !== employee.id);
        const otherContributors = othersRunsToday.map(r => ({ id: r.userId, name: r.userName })).filter((v,i,a)=>a.findIndex(x=>x.id===v.id)===i);
        // Itens marcados por TODOS (eu + outros) — usado pra mostrar progresso coletivo do dia
        const aggregateDoneSet = new Set();
        if (run) (run.items || []).forEach(it => { if (it.done) aggregateDoneSet.add(it.itemId); });
        othersRunsToday.forEach(r => (r.items || []).forEach(it => { if (it.done) aggregateDoneSet.add(it.itemId); }));
        const aggregateDone = aggregateDoneSet.size;
        // helper: pra um itemId, retorna lista [{userName, doneAt}] de OUTROS que já marcaram
        function getOtherMarksForItem(itemId) {
          const out = [];
          othersRunsToday.forEach(r => {
            const ri = (r.items || []).find(x => x.itemId === itemId);
            if (ri && ri.done) out.push({ userName: r.userName, doneAt: ri.doneAt || r.completedAt });
          });
          return out.sort((a,b) => (a.doneAt||"").localeCompare(b.doneAt||""));
        }
        // Último envio do template hoje (qualquer pessoa)
        const lastSubmission = restRunsToday
          .filter(r => r.templateId === tpl.id)
          .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))[0];
        return (
          <div key={tpl.id} style={{background:"var(--card-bg)",border:`1px solid ${isCompleted?"#10b98144":"var(--border)"}`,borderRadius:12,overflow:"hidden"}}>
            <div style={{padding:"12px 16px",background:isCompleted?"#f0fdf4":"var(--bg2)",borderBottom:"1px solid var(--border)",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8,cursor:"pointer"}}
              onClick={()=>setExpandedTpl(isExpanded ? null : tpl.id)}>
              <div style={{flex:1,minWidth:180}}>
                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                  <span style={{fontSize:15,color:"var(--text)",fontWeight:700}}>{tpl.name}</span>
                  {isCompleted && <span style={{fontSize:10,padding:"1px 8px",borderRadius:10,background:"#10b98122",color:"#15803d",fontWeight:700}}>✓ ENVIADO</span>}
                  {isReviewed && <span style={{fontSize:10,padding:"1px 8px",borderRadius:10,background:"#dbeafe",color:"#1e40af",fontWeight:700}}>🔒 CONFERIDO</span>}
                  {isDraft && <span style={{fontSize:10,padding:"1px 8px",borderRadius:10,background:"#f59e0b22",color:"#b45309",fontWeight:700}}>RASCUNHO</span>}
                </div>
                {tpl.description && <div style={{fontSize:12,color:"var(--text3)",marginTop:3}}>{tpl.description}</div>}
                {/* v8.23.4: aviso explícito quando EU já enviei esse checklist hoje */}
                {isCompleted && (
                  <div style={{fontSize:12,color:"#15803d",marginTop:4,fontWeight:600,display:"flex",alignItems:"center",gap:4,flexWrap:"wrap"}}>
                    <span>✅</span>
                    <span>Você já enviou esse checklist hoje às {new Date(run.completedAt).toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"})}</span>
                    {!isReviewed && <span style={{fontSize:10,color:"var(--text3)",fontWeight:500,fontStyle:"italic"}}>· libera amanhã</span>}
                  </div>
                )}
                {/* v8.23.0: outros contribuintes hoje + progresso coletivo */}
                {otherContributors.length > 0 && (
                  <div style={{fontSize:11,color:"#1e40af",marginTop:4,display:"flex",alignItems:"center",gap:4,flexWrap:"wrap"}}>
                    <span>👥</span>
                    <span>Já enviaram hoje: <b>{otherContributors.slice(0,3).map(c => shortName(c.name)).join(", ")}</b>{otherContributors.length > 3 ? ` +${otherContributors.length-3}` : ""} · <b>{aggregateDone}/{items.length}</b> itens marcados no total</span>
                  </div>
                )}
                {/* Último envio de hoje — só mostra se não tem o bloco "outros" acima (pra não duplicar) */}
                {otherContributors.length === 0 && lastSubmission && lastSubmission.userId !== employee.id && (
                  <div style={{fontSize:11,color:"#15803d",marginTop:3,display:"flex",alignItems:"center",gap:4}}>
                    <span>✓</span>
                    <span>Último envio: <b>{shortName(lastSubmission.userName) || "—"}</b> às {new Date(lastSubmission.completedAt).toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"})}</span>
                  </div>
                )}
                <div style={{display:"flex",alignItems:"center",gap:8,marginTop:6}}>
                  <div style={{width:120,height:5,background:"var(--bg2)",borderRadius:3,overflow:"hidden"}}>
                    <div style={{width:`${pct}%`,height:"100%",background:pct===100?"#15803d":miseAc,transition:"width 0.2s"}}/>
                  </div>
                  <span style={{fontSize:11,color:"var(--text3)",fontFamily:"'DM Mono',monospace"}}>{doneCount}/{items.length}</span>
                </div>
              </div>
              <span style={{color:"var(--text3)",fontSize:14}}>{isExpanded ? "▼" : "▶"}</span>
            </div>
            {isExpanded && (
              <div style={{padding:"12px 16px"}}>
                {items.length === 0 ? (
                  <div style={{padding:"16px",textAlign:"center",color:"var(--text3)",fontSize:13,background:"var(--bg2)",borderRadius:8}}>
                    Este template não tem itens. Peça ao gestor pra configurar.
                  </div>
                ) : (
                  <>
                    <div style={{display:"flex",flexDirection:"column",gap:6}}>
                      {items.map((it, idx) => {
                        const state = getItemState(tpl, it.id);
                        const done = state.done;
                        const otherMarks = getOtherMarksForItem(it.id); // v8.23.0
                        return (
                          <div key={it.id} style={{background:done?"#f0fdf4":(otherMarks.length>0?"#f0f9ff":"var(--bg2)"),border:`1px solid ${done?"#10b98144":(otherMarks.length>0?"#0ea5e944":"var(--border)")}`,borderRadius:8,opacity:isCompleted?0.7:1}}>
                            <div onClick={()=>!isCompleted && updateItem(tpl, it.id, { done: !done })}
                              style={{display:"flex",alignItems:"center",gap:10,padding:"12px 14px",cursor:isCompleted?"default":"pointer"}}>
                              <input type="checkbox" checked={done} disabled={isCompleted}
                                onChange={()=>{}} // controlado via onClick do div pra expandir área clicável
                                style={{cursor:isCompleted?"default":"pointer",width:20,height:20,accentColor:"#15803d",flexShrink:0}} />
                              <span style={{fontSize:11,color:"var(--text3)",fontFamily:"'DM Mono',monospace",minWidth:22}}>{idx+1}.</span>
                              <span style={{fontSize:14,color:done?"var(--text3)":"var(--text)",fontWeight:500,flex:1,textDecoration:done?"line-through":"none"}}>{it.text}</span>
                              {state.note && !state.showNote && <span title={state.note} style={{fontSize:11,padding:"2px 6px",borderRadius:4,background:"#f59e0b22",color:"#b45309",fontWeight:700}}>📝</span>}
                              {!isCompleted && (
                                <button onClick={e=>{ e.stopPropagation(); updateItem(tpl, it.id, { showNote: !state.showNote }); }}
                                  style={{background:state.showNote?"#f59e0b22":"transparent",border:`1px solid ${state.showNote?"#f59e0b66":"var(--border)"}`,borderRadius:6,padding:"4px 10px",fontSize:10,fontWeight:700,color:state.showNote?"#b45309":"var(--text3)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",flexShrink:0}}>
                                  OBS
                                </button>
                              )}
                            </div>
                            {/* v8.23.0: outros que já marcaram esse item hoje */}
                            {otherMarks.length > 0 && (
                              <div style={{padding:"0 14px 8px",marginTop:-4,display:"flex",gap:4,flexWrap:"wrap",alignItems:"center"}}>
                                <span style={{fontSize:10,color:"var(--text3)",marginRight:2}}>✓ Já marcado por:</span>
                                {otherMarks.map((m, i) => (
                                  <span key={i} style={{fontSize:10,padding:"1px 7px",background:"#0ea5e922",color:"#075985",borderRadius:8,fontWeight:600}}>
                                    {shortName(m.userName)} {m.doneAt ? `às ${new Date(m.doneAt).toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"})}` : ""}
                                  </span>
                                ))}
                              </div>
                            )}
                            {state.showNote && !isCompleted && (
                              <div style={{padding:"0 14px 12px",marginTop:-4}}>
                                <textarea
                                  value={state.note}
                                  onChange={e=>updateItem(tpl, it.id, { note: e.target.value })}
                                  placeholder="Observação sobre este item (opcional)"
                                  rows={2}
                                  style={{width:"100%",padding:"8px 10px",borderRadius:6,border:"1px solid var(--border)",background:"var(--card-bg)",fontSize:13,fontFamily:"'DM Sans',sans-serif",resize:"vertical",color:"var(--text)",boxSizing:"border-box"}}
                                />
                              </div>
                            )}
                            {isCompleted && state.note && (
                              <div style={{padding:"0 14px 10px",fontSize:12,color:"#b45309",fontStyle:"italic"}}>
                                📝 {state.note}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {!isCompleted && (
                      <div style={{marginTop:14,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                        <div style={{display:"flex",flexDirection:"column",gap:2}}>
                          <div style={{fontSize:11,color:"var(--text3)"}}>
                            {doneCount === 0 ? "Marque os itens conferidos e envie ao final." : `${doneCount} de ${items.length} marcado${doneCount>1?"s":""}`}
                          </div>
                          {savedAt ? (
                            <div style={{fontSize:10,color:"#15803d",fontWeight:600,display:"flex",alignItems:"center",gap:4}}>
                              <span>💾</span>
                              <span>Salvo às {new Date(savedAt).toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit",second:"2-digit"})}</span>
                            </div>
                          ) : run ? (
                            <div style={{fontSize:10,color:"var(--text3)",fontStyle:"italic"}}>
                              Rascunho preservado. Você pode fechar e voltar depois.
                            </div>
                          ) : null}
                        </div>
                        <button onClick={()=>submitChecklist(tpl)}
                          style={{background:"#15803d",color:"#fff",border:"none",borderRadius:8,padding:"10px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",minHeight:44}}>
                          📨 Enviar checklist
                        </button>
                      </div>
                    )}
                    {/* v8.23.0: card já enviado mas ainda não conferido — botão pra reabrir */}
                    {isCompleted && !isReviewed && (
                      <div style={{marginTop:14,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8,padding:"10px 12px",background:"#f0fdf4",border:"1px dashed #10b98144",borderRadius:8}}>
                        <div style={{fontSize:12,color:"#15803d"}}>
                          ✓ Enviado às {new Date(run.completedAt).toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"})}. Esqueceu algum item? Dá pra reabrir até a conferência do líder.
                        </div>
                        <button onClick={()=>reopenSubmission(tpl)}
                          style={{background:"transparent",color:"#15803d",border:"1px solid #10b98166",borderRadius:8,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",whiteSpace:"nowrap"}}>
                          ✏️ Reabrir envio
                        </button>
                      </div>
                    )}
                    {/* Já conferido — mensagem de bloqueio */}
                    {isCompleted && isReviewed && (
                      <div style={{marginTop:14,padding:"10px 12px",background:"#dbeafe",border:"1px solid #1e40af33",borderRadius:8,fontSize:12,color:"#1e40af"}}>
                        🔒 Conferido por <b>{shortName(run.reviewerName)}</b> em {new Date(run.reviewedAt).toLocaleString("pt-BR",{day:"2-digit",month:"2-digit",hour:"2-digit",minute:"2-digit"})}. Não é mais editável.
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ──  CHECKLISTS — CONFERÊNCIA (Líder/Conferente) — v8.21.0      ──
// ═══════════════════════════════════════════════════════════════
// Lista de runs pendentes de conferência + tela de revisão + modal de criar ocorrência.
// Critério de visibilidade: pessoa precisa passar em canReviewChecklist(pessoa, tpl, rid).
// Ao aprovar, seta reviewedAt no run. Ocorrências criadas viram incidents (visibility:"internal").
function ChecklistsConferenciaView({ restaurantId, employee, pessoas, miseChecklistTemplates, miseChecklistRuns, onUpdate, myReviewableTpls, pendingReviews, reviewingGroupKey, setReviewingGroupKey, onSwitchToExecutar, myPessoa }) {
  const miseAc = "#7c9e5e";
  // v8.22.0: helper de agrupamento por template+data
  function groupRunsByTplDate(runs) {
    const map = {};
    runs.forEach(r => {
      const k = `${r.templateId}__${r.date}`;
      if (!map[k]) map[k] = [];
      map[k].push(r);
    });
    return map;
  }
  // Pendentes agrupadas
  const pendingGroups = groupRunsByTplDate(pendingReviews);
  // Já conferidos por mim (histórico recente)
  const myReviewedAll = (miseChecklistRuns || []).filter(r =>
    r.restaurantId === restaurantId &&
    r.reviewedAt &&
    myReviewableTpls.some(t => t.id === r.templateId)
  );
  // Agrupa o histórico também
  const reviewedGroups = groupRunsByTplDate(myReviewedAll);
  // Lista as últimas 20 keys do histórico ordenadas pelo último reviewedAt
  const reviewedGroupKeys = Object.keys(reviewedGroups).sort((a,b) => {
    const lastA = Math.max(...reviewedGroups[a].map(r => new Date(r.reviewedAt || 0).getTime()));
    const lastB = Math.max(...reviewedGroups[b].map(r => new Date(r.reviewedAt || 0).getTime()));
    return lastB - lastA;
  }).slice(0, 20);

  if (reviewingGroupKey) {
    const groupRuns = (miseChecklistRuns || []).filter(r => `${r.templateId}__${r.date}` === reviewingGroupKey);
    if (groupRuns.length === 0) {
      setTimeout(() => setReviewingGroupKey(null), 0);
      return null;
    }
    const tpl = (miseChecklistTemplates || []).find(t => t.id === groupRuns[0].templateId);
    return (
      <ChecklistReviewAggregatedScreen
        runs={groupRuns}
        tpl={tpl}
        restaurantId={restaurantId}
        employee={employee}
        pessoas={pessoas}
        miseChecklistRuns={miseChecklistRuns}
        onUpdate={onUpdate}
        onBack={() => setReviewingGroupKey(null)}
        myPessoa={myPessoa}
      />
    );
  }

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{display:"flex",gap:6,padding:4,background:"var(--bg2)",borderRadius:10,border:"1px solid var(--border)"}}>
        <button onClick={onSwitchToExecutar}
          style={{flex:1,padding:"8px 12px",borderRadius:8,border:"none",background:"transparent",color:"var(--text2)",fontSize:13,fontWeight:600,cursor:"pointer"}}>
          ✅ Executar
        </button>
        <button
          style={{flex:1,padding:"8px 12px",borderRadius:8,border:"none",background:miseAc,color:"#fff",fontSize:13,fontWeight:700,cursor:"default",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
          🔎 Conferir
          {pendingReviews.length > 0 && (
            <span style={{padding:"1px 8px",background:"#fff",color:miseAc,borderRadius:10,fontSize:11,fontWeight:700}}>{pendingReviews.length}</span>
          )}
        </button>
      </div>

      <div style={{padding:"12px 16px",background:"#fef3c7",border:"1px solid #f59e0b44",borderRadius:10}}>
        <div style={{fontSize:11,color:"#92400e",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Conferência de checklists</div>
        <div style={{fontSize:13,color:"var(--text)",marginTop:2}}>
          {pendingReviews.length === 0
            ? "Nenhum checklist pendente. Tudo conferido!"
            : `${pendingReviews.length} pendente(s) — revise e aprove. Você pode marcar ocorrências relacionadas a empregados.`}
        </div>
      </div>

      {/* PENDENTES — agrupados por template+data */}
      {Object.keys(pendingGroups).length > 0 && (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          <div style={{fontSize:12,fontWeight:700,color:"var(--text2)",textTransform:"uppercase",letterSpacing:0.4}}>📋 Pendentes</div>
          {Object.keys(pendingGroups).sort((a,b) => {
            const maxA = Math.max(...pendingGroups[a].map(r => new Date(r.completedAt || 0).getTime()));
            const maxB = Math.max(...pendingGroups[b].map(r => new Date(r.completedAt || 0).getTime()));
            return maxB - maxA;
          }).map(key => {
            const groupRuns = pendingGroups[key];
            const tpl = (miseChecklistTemplates || []).find(t => t.id === groupRuns[0].templateId);
            const totalCount = (tpl?.items || []).length;
            // Agrega quantos itens DISTINTOS foram marcados (qualquer pessoa marcando conta)
            const itemsMarkedSet = new Set();
            groupRuns.forEach(r => (r.items || []).forEach(it => { if (it.done) itemsMarkedSet.add(it.itemId); }));
            const doneCount = itemsMarkedSet.size;
            const pct = totalCount > 0 ? Math.round(doneCount/totalCount*100) : 0;
            const hasNotes = groupRuns.some(r => (r.items || []).some(i => i.note && i.note.trim()));
            const area = tpl?.area || "Geral";
            const contributors = groupRuns.map(r => shortName(r.userName)).filter((v,i,a)=>a.indexOf(v)===i);
            const lastTime = new Date(Math.max(...groupRuns.map(r => new Date(r.completedAt || 0).getTime()))).toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"});
            return (
              <div key={key} onClick={() => setReviewingGroupKey(key)}
                style={{padding:"12px 14px",background:"var(--card-bg)",border:"1px solid #f59e0b66",borderRadius:10,cursor:"pointer",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                <div style={{flex:1,minWidth:180}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                    <span style={{fontSize:14,color:"var(--text)",fontWeight:700}}>{tpl?.name || "Template removido"}</span>
                    <span style={{fontSize:10,padding:"2px 8px",borderRadius:10,background:area==="Geral"?"var(--bg2)":(AREA_COLORS[area]||"#888")+"22",color:area==="Geral"?"var(--text3)":(AREA_COLORS[area]||"#888"),fontWeight:700}}>{area}</span>
                    {groupRuns.length > 1 && <span style={{fontSize:10,padding:"2px 6px",borderRadius:6,background:"#dbeafe",color:"#1e40af",fontWeight:700}}>👥 {groupRuns.length} pessoas</span>}
                    {hasNotes && <span style={{fontSize:10,padding:"2px 6px",borderRadius:6,background:"#fef3c7",color:"#b45309",fontWeight:700}}>📝 com observações</span>}
                  </div>
                  <div style={{fontSize:12,color:"var(--text3)",marginTop:3}}>
                    👤 <b>{contributors.slice(0,3).join(", ")}</b>{contributors.length > 3 ? ` +${contributors.length-3}` : ""} · {fmtDate(groupRuns[0].date)} · último envio às {lastTime}
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginTop:6}}>
                    <div style={{width:120,height:5,background:"var(--bg2)",borderRadius:3,overflow:"hidden"}}>
                      <div style={{width:`${pct}%`,height:"100%",background:pct===100?"#15803d":"#f59e0b"}}/>
                    </div>
                    <span style={{fontSize:11,color:"var(--text3)",fontFamily:"'DM Mono',monospace"}}>{doneCount}/{totalCount} item(s) marcado(s)</span>
                  </div>
                </div>
                <button style={{padding:"8px 16px",borderRadius:8,border:"none",background:"#f59e0b",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer"}}>
                  🔎 Conferir →
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* HISTÓRICO MEU — agrupado por template+data */}
      {reviewedGroupKeys.length > 0 && (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          <div style={{fontSize:12,fontWeight:700,color:"var(--text2)",textTransform:"uppercase",letterSpacing:0.4,marginTop:8}}>✅ Conferidos recentemente</div>
          {reviewedGroupKeys.map(key => {
            const groupRuns = reviewedGroups[key];
            const tpl = (miseChecklistTemplates || []).find(t => t.id === groupRuns[0].templateId);
            const totalCount = (tpl?.items || []).length;
            const itemsMarkedSet = new Set();
            groupRuns.forEach(r => (r.items || []).forEach(it => { if (it.done) itemsMarkedSet.add(it.itemId); }));
            const doneCount = itemsMarkedSet.size;
            const contributors = groupRuns.map(r => shortName(r.userName)).filter((v,i,a)=>a.indexOf(v)===i);
            const reviewerNames = groupRuns.map(r => shortName(r.reviewerName)).filter(Boolean).filter((v,i,a)=>a.indexOf(v)===i);
            const allBypass = groupRuns.every(r => r.autoBypass);
            return (
              <div key={key} onClick={() => setReviewingGroupKey(key)}
                style={{padding:"10px 14px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,cursor:"pointer",display:"flex",alignItems:"center",gap:10,opacity:0.85}}>
                <div style={{flex:1,minWidth:180}}>
                  <div style={{fontSize:13,color:"var(--text)",fontWeight:600,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                    {tpl?.name || "Template removido"}
                    {groupRuns.length > 1 && <span style={{fontSize:10,padding:"1px 6px",borderRadius:6,background:"#dbeafe",color:"#1e40af",fontWeight:700}}>{groupRuns.length}p</span>}
                    {allBypass && <span style={{fontSize:10,color:"var(--text3)",fontStyle:"italic"}}>(auto-aprovado)</span>}
                  </div>
                  <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>
                    👤 {contributors.slice(0,3).join(", ")}{contributors.length > 3 ? ` +${contributors.length-3}` : ""} · {fmtDate(groupRuns[0].date)}
                    {reviewerNames.length > 0 && <> · ✓ {reviewerNames.join(", ")}</>}
                  </div>
                </div>
                <div style={{fontSize:11,fontFamily:"'DM Mono',monospace",color:"var(--text3)"}}>{doneCount}/{totalCount}</div>
                <span style={{fontSize:18,color:"var(--text3)"}}>›</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// v8.22.0: Tela de revisão agregada — recebe lista de runs do mesmo template+data.
// Itens mostram TODOS os carimbos de quem marcou + horário, somando os runs.
// Approve aplica reviewedAt em TODOS os runs do grupo.
// Observação geral é compartilhada (gravada em todos os runs do grupo).
// Ocorrência criada vincula a todos os runs do grupo.
function ChecklistReviewAggregatedScreen({ runs, tpl, restaurantId, employee, pessoas, miseChecklistRuns, onUpdate, onBack, myPessoa }) {
  const items = (tpl?.items || []);
  // Já existe alguma observação salva em algum dos runs? — pega a primeira não-vazia
  const initialNotes = (runs.find(r => r.reviewerNotes && r.reviewerNotes.trim())?.reviewerNotes) || "";
  const [reviewerNotes, setReviewerNotes] = useState(initialNotes);
  const [occurrenceModalOpen, setOccurrenceModalOpen] = useState(false);
  // União das ocorrências vinculadas a qualquer run do grupo
  const linkedOccs = [...new Set(runs.flatMap(r => r.linkedOccurrenceIds || []))];
  const isAlreadyReviewed = runs.every(r => r.reviewedAt);

  // Pessoas atribuíveis na ocorrência: equipe do restaurante. Se o líder lidera áreas específicas, poderia restringir (TODO).
  const allTeamPessoas = (pessoas || []).filter(p => (p.restaurantIds || []).includes(restaurantId) && p.isTeam?.[restaurantId]);
  // v8.21.0 TODO: filtrar por área (liderAreas vs área do cargo da pessoa). Como roles não está em escopo, mostra todos por enquanto.
  // const liderAreas = myPessoa?.permissions?.[restaurantId]?.special?.areas || [];
  const candidatePessoas = allTeamPessoas;

  // Pra cada item do template, agrega todos os carimbos {user, doneAt, note} dos runs que marcaram done
  function getItemMarks(itemId) {
    const marks = [];
    runs.forEach(r => {
      const ri = (r.items || []).find(x => x.itemId === itemId);
      if (ri && ri.done) {
        marks.push({
          userId: r.userId,
          userName: r.userName,
          doneAt: ri.doneAt || r.completedAt,
          note: ri.note,
        });
      }
    });
    // Ordena por horário
    marks.sort((a,b) => (a.doneAt||"").localeCompare(b.doneAt||""));
    return marks;
  }

  function approve() {
    const now = new Date().toISOString();
    const groupIds = new Set(runs.map(r => r.id));
    const next = (miseChecklistRuns || []).map(r => {
      if (!groupIds.has(r.id)) return r;
      return {
        ...r,
        reviewedAt: now,
        reviewerId: employee.id,
        reviewerName: employee.name,
        reviewerNotes: reviewerNotes.trim() || null,
        updatedAt: now,
      };
    });
    onUpdate("miseChecklistRuns", next);
    onUpdate("_toast", runs.length > 1 ? `✅ ${runs.length} checklists conferidos` : `✅ Checklist conferido`);
    onBack();
  }

  // v8.23.1: líder reabre conferência — runs voltam pra fila de pendentes, usuários podem editar
  async function reopenReview() {
    if (!await appConfirm(`Reabrir conferência?\n\n${runs.length > 1 ? `Os ${runs.length} envios voltam` : "O envio volta"} pra fila de pendentes. Os empregados também poderão reabrir e editar os próprios envios.\n\n${(linkedOccs || []).length > 0 ? `⚠️ ${linkedOccs.length} ocorrência(s) já vinculada(s) ficam preservadas.` : ""}`)) return;
    const groupIds = new Set(runs.map(r => r.id));
    const now = new Date().toISOString();
    const next = (miseChecklistRuns || []).map(r => {
      if (!groupIds.has(r.id)) return r;
      const cleaned = { ...r, updatedAt: now };
      delete cleaned.reviewedAt;
      delete cleaned.reviewerId;
      delete cleaned.reviewerName;
      delete cleaned.reviewerNotes;
      return cleaned;
    });
    onUpdate("miseChecklistRuns", next);
    onUpdate("_toast", "🔓 Conferência reaberta — voltou pra fila de pendentes");
    onBack();
  }

  function addOccurrence(empPessoa, description, severity) {
    const now = new Date().toISOString();
    const incId = `inc_${Date.now().toString(36)}${Math.random().toString(36).slice(2,5)}`;
    const newInc = {
      id: incId,
      restaurantId,
      employeeIds: [empPessoa.linkedEmployeeId || empPessoa.id],
      type: "checklist_review",
      severity: severity || "media",
      description: description.trim(),
      date: today(),
      createdAt: now,
      createdBy: employee.name,
      createdById: employee.id,
      visibility: "internal",
      sourceChecklistRunIds: runs.map(r => r.id),
      sourceChecklistTemplateName: tpl?.name || "—",
    };
    onUpdate("incidents", (prev) => [...(prev || []), newInc]);
    // Vincula a TODOS os runs do grupo
    const groupIds = new Set(runs.map(r => r.id));
    onUpdate("miseChecklistRuns", (miseChecklistRuns || []).map(r => {
      if (!groupIds.has(r.id)) return r;
      return { ...r, linkedOccurrenceIds: [...(r.linkedOccurrenceIds || []), incId], updatedAt: now };
    }));
    onUpdate("_toast", `⚠️ Ocorrência registrada para ${shortName(empPessoa.name)}`);
    setOccurrenceModalOpen(false);
  }

  // Lista de contribuidores únicos pra mostrar no header
  const contributors = runs.map(r => ({ id: r.userId, name: r.userName }))
    .filter((v,i,a) => a.findIndex(x => x.id === v.id) === i);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
        <button onClick={onBack} style={{...S.btnSecondary,fontSize:13,padding:"8px 14px"}}>← Voltar</button>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:11,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>{isAlreadyReviewed ? "Visualizando (já conferido)" : "Conferindo"}</div>
          <div style={{fontSize:16,color:"var(--text)",fontWeight:700}}>{tpl?.name || "Template removido"}</div>
        </div>
        {isAlreadyReviewed ? (
          <button onClick={reopenReview} style={{background:"transparent",color:"#b45309",border:"1px solid #f59e0b66",borderRadius:8,padding:"10px 16px",fontSize:13,fontWeight:700,cursor:"pointer",minHeight:44,whiteSpace:"nowrap"}}>
            🔓 Reabrir conferência
          </button>
        ) : (
          <button onClick={approve} style={{background:"#15803d",color:"#fff",border:"none",borderRadius:8,padding:"10px 20px",fontSize:13,fontWeight:700,cursor:"pointer",minHeight:44}}>
            ✅ Aprovar{runs.length > 1 ? ` (${runs.length})` : ""}
          </button>
        )}
      </div>

      <div style={{padding:"10px 14px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,fontSize:13,color:"var(--text2)"}}>
        <div style={{marginBottom:contributors.length > 1 ? 6 : 0}}>
          {fmtDate(runs[0].date)}
          {runs.length > 1 && <span style={{marginLeft:6,fontSize:11,padding:"1px 6px",borderRadius:6,background:"#dbeafe",color:"#1e40af",fontWeight:700}}>👥 {runs.length} pessoas trabalharam neste checklist</span>}
        </div>
        {contributors.length === 1 ? (
          <span>Preenchido por <b>{shortName(contributors[0].name)}</b> às {new Date(runs[0].completedAt).toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"})}.</span>
        ) : (
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {contributors.map(c => {
              const r = runs.find(rr => rr.userId === c.id);
              return (
                <span key={c.id} style={{fontSize:11,padding:"3px 10px",background:"#dbeafe",color:"#1e40af",borderRadius:12,fontWeight:600}}>
                  👤 {shortName(c.name)} · {new Date(r.completedAt).toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"})}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Itens read-only com atribuição agregada */}
      <div style={{display:"flex",flexDirection:"column",gap:6}}>
        {items.map((it, idx) => {
          const marks = getItemMarks(it.id);
          const done = marks.length > 0;
          // Coleta notas únicas (pode ter várias se várias pessoas comentaram)
          const notes = marks.filter(m => m.note && m.note.trim()).map(m => ({ user: m.userName, note: m.note }));
          return (
            <div key={it.id} style={{padding:"10px 14px",background:done?"#f0fdf4":"#fef9e7",border:`1px solid ${done?"#10b98144":"#f59e0b44"}`,borderRadius:8}}>
              <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                <span style={{fontSize:18}}>{done ? "✅" : "⬜"}</span>
                <span style={{fontSize:11,color:"var(--text3)",fontFamily:"'DM Mono',monospace",minWidth:24}}>{idx+1}.</span>
                <span style={{fontSize:14,color:"var(--text)",fontWeight:500,flex:1,textDecoration:done?"line-through":"none"}}>{it.text}</span>
              </div>
              {marks.length > 0 && (
                <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:6,marginLeft:34}}>
                  {marks.map((m, i) => (
                    <span key={i} style={{fontSize:10,padding:"2px 8px",background:"#10b98122",color:"#15803d",borderRadius:10,fontWeight:600}}>
                      {shortName(m.userName)} {m.doneAt ? `às ${new Date(m.doneAt).toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"})}` : ""}
                    </span>
                  ))}
                </div>
              )}
              {notes.length > 0 && (
                <div style={{marginTop:6,marginLeft:34,display:"flex",flexDirection:"column",gap:4}}>
                  {notes.map((n, i) => (
                    <div key={i} style={{padding:"6px 10px",background:"#fef3c7",borderRadius:6,fontSize:12,color:"#92400e",fontStyle:"italic"}}>
                      📝 <b>{shortName(n.user)}:</b> {n.note}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Observação geral do conferente */}
      <div style={{padding:"12px 14px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10}}>
        <label style={{fontSize:12,fontWeight:700,color:"var(--text2)",display:"block",marginBottom:6}}>📋 Observação da conferência (opcional)</label>
        <textarea
          value={reviewerNotes}
          onChange={e=>setReviewerNotes(e.target.value)}
          placeholder="Comentário geral, contexto, observações que ficam no histórico..."
          rows={3}
          style={{width:"100%",padding:"8px 10px",borderRadius:6,border:"1px solid var(--border)",background:"var(--bg2)",fontSize:13,resize:"vertical",color:"var(--text)",boxSizing:"border-box"}}
        />
      </div>

      {/* Ocorrências relacionadas */}
      <div style={{padding:"12px 14px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10,gap:8,flexWrap:"wrap"}}>
          <div style={{fontSize:12,fontWeight:700,color:"var(--text2)"}}>⚠️ Ocorrências geradas ({linkedOccs.length})</div>
          <button onClick={()=>setOccurrenceModalOpen(true)}
            style={{background:"#dc2626",color:"#fff",border:"none",borderRadius:8,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
            + Nova ocorrência
          </button>
        </div>
        {linkedOccs.length === 0 ? (
          <div style={{fontSize:11,color:"var(--text3)",fontStyle:"italic"}}>
            Nenhuma ocorrência registrada. Use o botão acima se identificou problema com algum empregado.
          </div>
        ) : (
          <div style={{fontSize:11,color:"var(--text3)"}}>
            {linkedOccs.length} ocorrência(s) vinculada(s) — visível em Reuniões e na trilha interna do empregado.
          </div>
        )}
      </div>

      {occurrenceModalOpen && (
        <ChecklistOccurrenceModal
          candidatePessoas={candidatePessoas}
          onClose={()=>setOccurrenceModalOpen(false)}
          onConfirm={addOccurrence}
          tplName={tpl?.name || "—"}
        />
      )}
    </div>
  );
}

// Modal de criar ocorrência durante conferência
function ChecklistOccurrenceModal({ candidatePessoas, onClose, onConfirm, tplName }) {
  const [empId, setEmpId] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState("media");
  const sortedPessoas = [...candidatePessoas].sort((a,b) => (a.name||"").localeCompare(b.name||""));
  const selPessoa = sortedPessoas.find(p => p.id === empId);
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",borderRadius:14,maxWidth:520,width:"100%",padding:24,maxHeight:"90vh",overflowY:"auto"}}>
        <h3 style={{margin:"0 0 6px",color:"var(--text)",fontSize:18,fontWeight:700}}>⚠️ Nova ocorrência</h3>
        <p style={{margin:"0 0 14px",fontSize:12,color:"var(--text3)",lineHeight:1.5}}>
          Vinculada à conferência de <b>{tplName}</b>. Vai pra coleção de incidentes — visível em reuniões e na trilha interna do empregado.
        </p>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <div>
            <label style={{fontSize:12,fontWeight:600,color:"var(--text2)",display:"block",marginBottom:4}}>Empregado</label>
            <select value={empId} onChange={e=>setEmpId(e.target.value)} style={S.input}>
              <option value="">— Selecione —</option>
              {sortedPessoas.map(p => <option key={p.id} value={p.id}>{shortName(p.name)}</option>)}
            </select>
          </div>
          <div>
            <label style={{fontSize:12,fontWeight:600,color:"var(--text2)",display:"block",marginBottom:4}}>Severidade</label>
            <div style={{display:"flex",gap:6}}>
              {[
                {id:"baixa",label:"Baixa",color:"#0ea5e9"},
                {id:"media",label:"Média",color:"#f59e0b"},
                {id:"alta",label:"Alta",color:"#dc2626"},
              ].map(s => (
                <button key={s.id} onClick={()=>setSeverity(s.id)}
                  style={{flex:1,padding:"8px 10px",borderRadius:8,border:`1px solid ${severity===s.id?s.color:"var(--border)"}`,background:severity===s.id?s.color:"transparent",color:severity===s.id?"#fff":"var(--text2)",fontSize:13,fontWeight:severity===s.id?700:500,cursor:"pointer"}}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label style={{fontSize:12,fontWeight:600,color:"var(--text2)",display:"block",marginBottom:4}}>Descrição</label>
            <textarea value={description} onChange={e=>setDescription(e.target.value)}
              placeholder="Descreva o que foi observado durante a conferência..."
              rows={4}
              style={{width:"100%",padding:"8px 10px",borderRadius:6,border:"1px solid var(--border)",background:"var(--bg2)",fontSize:13,resize:"vertical",color:"var(--text)",boxSizing:"border-box"}}/>
          </div>
        </div>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16}}>
          <button onClick={onClose} style={{...S.btnSecondary,padding:"8px 14px",fontSize:13}}>Cancelar</button>
          <button onClick={()=>{
              if (!selPessoa) { alert("Escolha um empregado."); return; }
              if (!description.trim()) { alert("Descreva a ocorrência."); return; }
              onConfirm(selPessoa, description, severity);
            }}
            style={{background:"#dc2626",color:"#fff",border:"none",borderRadius:8,padding:"8px 16px",fontSize:13,fontWeight:700,cursor:"pointer"}}>
            Registrar ocorrência
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ──  MISE FICHAS TÉCNICAS — UTILITIES (portado do Operacional)      ──
// ═══════════════════════════════════════════════════════════════
/* eslint-disable no-unused-vars */
const FT_WEIGHT_G = ['g','gr','gram','grama','gramas'];
const FT_WEIGHT_MG = ['mg'];
const FT_WEIGHT_KG = ['kg','quilo','quilos','quilograma','quilogramas'];
const FT_VOL_ML = ['ml','millilitro','mililitro'];
const FT_VOL_L = ['l','lt','litro','litros'];
const FT_CANONICAL_UNITS = ['kg', 'l', 'und', 'folhas', 'fatias', 'colher de sopa'];

function ftFmtNum(v, d = 2) {
  return (typeof v === 'number' && isFinite(v))
    ? v.toLocaleString('pt-BR', { minimumFractionDigits: d, maximumFractionDigits: d })
    : '—';
}
function ftPickWeightDisplay(qty_kg) {
  if (qty_kg >= 1) return { qty: qty_kg, unit: 'kg', text: ftFmtNum(qty_kg, 3) };
  const g = qty_kg * 1000;
  const dec = g >= 10 ? 1 : (g >= 1 ? 2 : 3);
  return { qty: g, unit: 'g', text: ftFmtNum(g, dec) };
}
function ftPickVolumeDisplay(qty_l) {
  if (qty_l >= 1) return { qty: qty_l, unit: 'l', text: ftFmtNum(qty_l, 3) };
  const ml = qty_l * 1000;
  const dec = ml >= 10 ? 1 : (ml >= 1 ? 2 : 3);
  return { qty: ml, unit: 'ml', text: ftFmtNum(ml, dec) };
}
function ftNormUnitForDisplay(qty, unit) {
  if (qty == null) return { qty: null, unit: unit || '', text: '—' };
  const u = (unit || '').toLowerCase().trim().replace(/\s+/g, '');
  if (FT_WEIGHT_G.includes(u)) return ftPickWeightDisplay(qty / 1000);
  if (FT_WEIGHT_MG.includes(u)) return ftPickWeightDisplay(qty / 1e6);
  if (FT_WEIGHT_KG.includes(u)) return ftPickWeightDisplay(qty);
  if (FT_VOL_ML.includes(u)) return ftPickVolumeDisplay(qty / 1000);
  if (FT_VOL_L.includes(u)) return ftPickVolumeDisplay(qty);
  const dec = qty % 1 === 0 ? 0 : 2;
  return { qty, unit: unit || '', text: ftFmtNum(qty, dec) };
}
function ftFormatIngQty(ing, scaleFactor = 1) {
  if (ing?.is_qb) return { text: 'Q.B', unit: '' };
  if (ing?.qty == null) return { text: ing?.qty_raw || '—', unit: ing?.unit || '' };
  const scaled = ing.qty * (scaleFactor || 1);
  const n = ftNormUnitForDisplay(scaled, ing.unit);
  return { text: n.text, unit: n.unit };
}
function ftFormatRendimento(rendStr, scaleFactor = 1) {
  if (!rendStr) return '';
  const s = String(rendStr).trim();
  const m = s.match(/^(\d+(?:[.,]\d+)?)\s*([a-zA-Zµ/\u00c0-\u024f]*)(.*)$/);
  if (!m) return rendStr;
  const rawQty = parseFloat(m[1].replace(',', '.'));
  const originalUnit = m[2] || '';
  const qty = rawQty * (scaleFactor || 1);
  const n = ftNormUnitForDisplay(qty, originalUnit);
  const unitChanged = n.unit.toLowerCase() !== originalUnit.toLowerCase();
  const unitDisplay = unitChanged ? n.unit : originalUnit;
  const suffix = m[3].trim();
  return `${n.text} ${unitDisplay}${suffix ? ' ' + suffix : ''}`.trim();
}
function ftNrm(s) {
  if (!s) return '';
  return String(s).trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/\(.*?\)/g, '').replace(/\s+/g, ' ').trim().replace(/[,.;:\s]+$/, '');
}
function ftSlugify(s) {
  return (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'item';
}
function ftUid() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 7); }

function ftParseRendimentoQty(rendStr) {
  if (!rendStr) return { qty: 1, unit: '' };
  const s = String(rendStr).trim();
  const m = s.match(/^(\d+(?:[.,]\d+)?)\s*([a-zA-Zµ/\u00c0-\u024f]*)/);
  if (m) return { qty: parseFloat(m[1].replace(',', '.')), unit: (m[2] || '').toLowerCase() };
  return { qty: 1, unit: '' };
}
function ftGetSfRendimento(sf) {
  if (!sf) return { qty: 1, unit: '' };
  if (typeof sf.rendimento_qty === 'number' && sf.rendimento_qty > 0) {
    return { qty: sf.rendimento_qty, unit: (sf.rendimento_unit || '').toLowerCase().trim() };
  }
  return ftParseRendimentoQty(sf.rendimento);
}
function ftSfRendimentoText(sf, scale = 1) {
  const r = ftGetSfRendimento(sf);
  const q = r.qty * scale;
  const n = ftNormUnitForDisplay(q, r.unit);
  return `${n.text} ${n.unit}`.trim();
}
function ftNormalizeSubrefQty(qty, ingUnit, subUnit) {
  const iu = (ingUnit || '').toLowerCase().trim();
  const su = (subUnit || '').toLowerCase().trim();
  if (iu === su || !iu || !su) return [qty, qty];
  if (iu === 'g' && su === 'kg') return [qty / 1000, qty];
  if (iu === 'kg' && su === 'g') return [qty * 1000, qty];
  if (iu === 'ml' && (su === 'l' || su === 'lt' || su === 'litro')) return [qty / 1000, qty];
  if (iu === 'l' && su === 'ml') return [qty * 1000, qty];
  return [qty, qty];
}
function ftNormalizeQtyPrice(ing, insumo) {
  if (!insumo || ing.qty == null) return [null, null];
  const iu = (ing.unit || '').toLowerCase().trim();
  const pu = (insumo.unit || '').toLowerCase().trim();
  const q = ing.qty;
  const p = insumo.price || 0;
  if (iu === pu || !iu || !pu) return [q, p];
  if (iu === 'g' && pu === 'kg') return [q / 1000, p];
  if (iu === 'kg' && pu === 'g') return [q * 1000, p];
  if (iu === 'ml' && (pu === 'l' || pu === 'lt' || pu === 'litro')) return [q / 1000, p];
  if (iu === 'l' && pu === 'ml') return [q * 1000, p];
  return [q, p];
}
function ftParsePortions(rendStr) {
  if (!rendStr) return 1;
  const s = String(rendStr).toLowerCase();
  const m = s.match(/(\d+(?:[.,]\d+)?)\s*(?:und|unidades?|por[cç][oõ]es?|por[cç][aã]o)/);
  if (m) return parseFloat(m[1].replace(',', '.'));
  const m2 = s.match(/^(\d+(?:[.,]\d+)?)/);
  if (m2) return parseFloat(m2[1].replace(',', '.'));
  return 1;
}
function ftDetectSubref(dish, currentSfIdx, ingredientName) {
  const ingClean = ftNrm(ingredientName);
  if (ingClean.length < 4) return null;
  let best = null, bestScore = 0;
  for (let j = 0; j < currentSfIdx; j++) {
    const other = dish.sub_fichas[j];
    const otherN = ftNrm(other.name);
    if (!otherN) continue;
    if (otherN === ingClean) return other;
    if (otherN.includes(ingClean) || ingClean.includes(otherN)) {
      const ratio = Math.min(otherN.length, ingClean.length) / Math.max(otherN.length, ingClean.length);
      if (ratio > bestScore) { bestScore = ratio; best = other; }
    }
  }
  return bestScore > 0.55 ? best : null;
}
function ftFindSubproduto(dishes, name) {
  if (!name) return null;
  const target = ftNrm(name);
  if (target.length < 3) return null;
  for (const dish of dishes || []) {
    for (const sf of dish.sub_fichas || []) {
      if (!sf.subproduto || !sf.subproduto.name) continue;
      if (ftNrm(sf.subproduto.name) === target) {
        return { dish, sf, subproduto: sf.subproduto };
      }
    }
  }
  return null;
}
function ftSubfichaCost(sf, dish, insumos, dishes, cache, visited) {
  cache = cache || new Map();
  visited = visited || new Set();
  if (cache.has(sf.id)) return cache.get(sf.id);
  if (visited.has(sf.id)) return { rows: [], total: 0 };
  visited.add(sf.id);
  const sfIdx = dish.sub_fichas.findIndex(s => s.id === sf.id);
  let total = 0;
  const rows = (sf.ingredientes || []).map(ing => {
    let subSf = null;
    if (ing.subref_id) subSf = dish.sub_fichas.find(s => s.id === ing.subref_id);
    if (!subSf && sfIdx > 0) subSf = ftDetectSubref(dish, sfIdx, ing.insumo_name);
    if (subSf) {
      const subResult = ftSubfichaCost(subSf, dish, insumos, dishes, cache, new Set(visited));
      const subRend = ftGetSfRendimento(subSf);
      const ingQty = ing.qty;
      let cost = 0;
      if (ingQty != null && subRend.qty > 0) {
        const [qConverted] = ftNormalizeSubrefQty(ingQty, ing.unit, subRend.unit);
        cost = (qConverted / subRend.qty) * subResult.total;
      }
      total += cost;
      return { ing, insumo: null, cost, subSf, isSubref: true };
    }
    const subprodHit = ftFindSubproduto(dishes, ing.insumo_name);
    if (subprodHit && !(subprodHit.dish.id === dish.id && subprodHit.sf.id === sf.id)) {
      return { ing, insumo: null, cost: 0, isSubref: false, isSubproduto: true, subprodHit };
    }
    const insumo = insumos.find(i => i.id === ing.insumo_id);
    const isReutilizavel = !!(insumo && insumo.reutilizavel);
    const [qNorm, priceNorm] = ftNormalizeQtyPrice(ing, insumo);
    const fc = (typeof ing.fc === 'number' && ing.fc > 0) ? ing.fc : 1;
    const cost = isReutilizavel ? 0 : ((qNorm != null && priceNorm != null) ? qNorm * priceNorm * fc : 0);
    total += cost;
    return { ing, insumo, cost, isSubref: false, fc: isReutilizavel ? 1 : fc, isReutilizavel };
  });
  const result = { rows, total };
  cache.set(sf.id, result);
  return result;
}
function ftDishCost(dish, insumos, dishes) {
  const cache = new Map();
  const sfCosts = (dish.sub_fichas || []).map(sf => {
    const r = ftSubfichaCost(sf, dish, insumos, dishes, cache);
    return { ...r, sf };
  });
  const finalSf = (dish.sub_fichas || [])[dish.sub_fichas.length - 1];
  const finalResult = finalSf ? sfCosts.find(x => x.sf.id === finalSf.id) : null;
  const total = finalResult ? finalResult.total : 0;
  const portions = ftParsePortions(finalSf ? finalSf.rendimento : '');
  const costPerPortion = portions > 0 ? total / portions : 0;
  let suggestedPrice, cmv, markup;
  if (typeof dish.target_cmv === 'number' && dish.target_cmv > 0) {
    cmv = dish.target_cmv;
    suggestedPrice = costPerPortion / (cmv / 100);
    markup = costPerPortion > 0 ? ((suggestedPrice / costPerPortion) - 1) * 100 : 0;
  } else {
    markup = dish.markup || 300;
    suggestedPrice = costPerPortion * (1 + markup / 100);
    cmv = suggestedPrice > 0 ? (costPerPortion / suggestedPrice) * 100 : 0;
  }
  return { sfCosts, total, portions, costPerPortion, suggestedPrice, cmv, markup };
}

// Calcula o fator efetivo de produção de cada sub-ficha quando o usuário escala a ficha final.
// A sub-ficha final recebe `finalScaleFactor`. Sub-fichas intermediárias recebem
// (qty_consumida_convertida / rendimento) * fator_da_pai, somado entre todas as pais que a consomem.
// Retorna { [sfId]: fator }.
function ftComputeSfScaleFactors(dish, finalScaleFactor) {
  const factors = {};
  const sfs = (dish && dish.sub_fichas) || [];
  if (sfs.length === 0) return factors;
  sfs.forEach(sf => { factors[sf.id] = 0; });
  const finalSf = sfs[sfs.length - 1];
  factors[finalSf.id] = finalScaleFactor || 1;
  for (let i = sfs.length - 1; i >= 0; i--) {
    const sf = sfs[i];
    const myFactor = factors[sf.id] || 0;
    if (myFactor <= 0) continue;
    (sf.ingredientes || []).forEach(ing => {
      let subSf = null;
      if (ing.subref_id) subSf = sfs.find(s => s.id === ing.subref_id);
      if (!subSf && i > 0) subSf = ftDetectSubref(dish, i, ing.insumo_name);
      if (!subSf) return;
      const subRend = ftGetSfRendimento(subSf);
      if (!subRend || subRend.qty <= 0 || ing.qty == null) return;
      const [qConverted] = ftNormalizeSubrefQty(ing.qty, ing.unit, subRend.unit);
      const subFactor = (qConverted / subRend.qty) * myFactor;
      factors[subSf.id] = (factors[subSf.id] || 0) + subFactor;
    });
  }
  return factors;
}

// Export PDF de uma ficha (usa jsPDF + autoTable, carregados via CDN no index.html)
function ftExportDishPDF(dish, insumos, dishes, restaurantName) {
  const jsPDF = window.jspdf?.jsPDF;
  if (!jsPDF) { alert("Biblioteca PDF ainda não foi carregada. Aguarde ou recarregue a página."); return; }
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const costInfo = ftDishCost(dish, insumos, dishes);
  const margin = 15;
  let y = 18;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(18);
  pdf.text(dish.name || 'Ficha Técnica', margin, y);
  y += 7;
  if (restaurantName) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(120);
    pdf.text(restaurantName, margin, y);
    pdf.setTextColor(0);
    y += 5;
  }
  if (dish.description) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    const wrapped = pdf.splitTextToSize(dish.description, 180);
    pdf.text(wrapped, margin, y);
    y += wrapped.length * 5 + 2;
  }

  const lastSf = (dish.sub_fichas || [])[(dish.sub_fichas || []).length - 1];
  pdf.setFontSize(9);
  pdf.setTextColor(100);
  const meta = [];
  if (lastSf?.rendimento) meta.push(`Rend.: ${ftFormatRendimento(lastSf.rendimento)}`);
  if (dish.louca) meta.push(`Louça: ${dish.louca}`);
  if ((dish.equipamentos || []).length) meta.push(`Equip.: ${dish.equipamentos.join(', ')}`);
  if (meta.length) {
    pdf.text(meta.join(' · '), margin, y);
    y += 6;
  }
  pdf.setTextColor(60, 120, 80);
  const priceLine = `Custo: ${(costInfo.total).toLocaleString('pt-BR',{style:'currency',currency:'BRL'})} · Custo/porção: ${costInfo.costPerPortion.toLocaleString('pt-BR',{style:'currency',currency:'BRL'})} · Preço sug.: ${costInfo.suggestedPrice.toLocaleString('pt-BR',{style:'currency',currency:'BRL'})} · CMV: ${costInfo.cmv.toFixed(1)}%`;
  pdf.text(priceLine, margin, y);
  pdf.setTextColor(0);
  y += 9;

  (dish.sub_fichas || []).forEach((sf, sfIdx) => {
    if (y > 260) { pdf.addPage(); y = 18; }
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    const isFinal = sfIdx === dish.sub_fichas.length - 1;
    pdf.text(`${sfIdx+1}. ${sf.name}${isFinal ? ' — FINAL' : ''}`, margin, y);
    y += 6;
    if (sf.rendimento) {
      pdf.setFont('helvetica', 'italic');
      pdf.setFontSize(9);
      pdf.setTextColor(100);
      pdf.text(`Rend.: ${ftFormatRendimento(sf.rendimento)}`, margin, y);
      pdf.setTextColor(0);
      y += 5;
    }
    const body = (sf.ingredientes || []).map(ing => {
      const q = ftFormatIngQty(ing);
      return [
        (ing.subref_id ? '↳ ' : '') + (ing.insumo_name || ''),
        ing.is_qb ? 'Q.B' : `${q.text} ${q.unit}`.trim(),
      ];
    });
    if (body.length && pdf.autoTable) {
      pdf.autoTable({
        startY: y,
        head: [['Ingrediente', 'Quantidade']],
        body,
        margin: { left: margin, right: margin },
        theme: 'grid',
        headStyles: { fillColor: [232, 226, 216], textColor: 30, fontSize: 9 },
        bodyStyles: { fontSize: 9, textColor: 30 },
        columnStyles: { 1: { halign: 'right', cellWidth: 45 } },
      });
      y = pdf.lastAutoTable.finalY + 4;
    }
    if (sf.modo_preparo) {
      if (y > 260) { pdf.addPage(); y = 18; }
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.text('Modo de preparo:', margin, y);
      y += 5;
      pdf.setFont('helvetica', 'normal');
      const wrapped = pdf.splitTextToSize(sf.modo_preparo, 180);
      if (y + wrapped.length * 4 > 280) { pdf.addPage(); y = 18; }
      pdf.text(wrapped, margin, y);
      y += wrapped.length * 4 + 6;
    }
    y += 3;
  });

  pdf.save(`${ftSlugify(dish.name || 'ficha')}.pdf`);
}

// Export XLSX com lista de insumos
function ftExportInsumosXLSX(insumos, restaurantName) {
  const XLSX = window.XLSX;
  if (!XLSX) { alert("Biblioteca XLSX ainda não foi carregada. Aguarde ou recarregue a página."); return; }
  const data = [["Nome", "Unidade", "Preço (R$)", "Reutilizável"]];
  insumos.forEach(i => data.push([i.name, i.unit || "kg", i.price || 0, i.reutilizavel ? "sim" : ""]));
  const ws = XLSX.utils.aoa_to_sheet(data);
  ws['!cols'] = [{ wch: 30 }, { wch: 10 }, { wch: 12 }, { wch: 12 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Insumos");
  XLSX.writeFile(wb, `insumos-${ftSlugify(restaurantName || "restaurante")}.xlsx`);
}

// Download template XLSX vazio para importação
function ftDownloadInsumosTemplateXLSX() {
  const XLSX = window.XLSX;
  if (!XLSX) { alert("Biblioteca XLSX ainda não foi carregada."); return; }
  const data = [
    ["Nome", "Unidade", "Preço (R$)", "Reutilizável"],
    ["Farinha de trigo", "kg", 5.90, ""],
    ["Azeite de oliva", "l", 42.00, ""],
    ["Óleo de fritura", "l", 12.50, "sim"],
  ];
  const ws = XLSX.utils.aoa_to_sheet(data);
  ws['!cols'] = [{ wch: 30 }, { wch: 10 }, { wch: 12 }, { wch: 12 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Insumos");
  XLSX.writeFile(wb, "template-insumos.xlsx");
}

// Import XLSX de insumos (merge por nome; cria/atualiza)
function ftImportInsumosXLSX(file, existingInsumos, restaurantId, onResult) {
  const XLSX = window.XLSX;
  if (!XLSX) { alert("Biblioteca XLSX ainda não foi carregada."); return; }
  const reader = new FileReader();
  reader.onload = e => {
    try {
      const wb = XLSX.read(e.target.result, { type: "binary" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
      if (!rows || rows.length < 2) { alert("Arquivo vazio ou sem dados."); return; }
      const headers = (rows[0] || []).map(h => (h || "").toString().toLowerCase().trim());
      const idxName = headers.findIndex(h => h.includes("nome"));
      const idxUnit = headers.findIndex(h => h.includes("unid"));
      const idxPrice = headers.findIndex(h => h.includes("pre") || h.includes("prc"));
      const idxReut = headers.findIndex(h => h.includes("reut"));
      if (idxName < 0 || idxPrice < 0) {
        alert("Formato inválido. Esperado colunas: Nome, Unidade, Preço, Reutilizável.");
        return;
      }
      const parsed = [];
      for (let i = 1; i < rows.length; i++) {
        const r = rows[i] || [];
        const name = (r[idxName] || "").toString().trim();
        if (!name) continue;
        const unit = (((idxUnit >= 0 ? r[idxUnit] : "") || "kg").toString().trim().toLowerCase()) || "kg";
        const priceVal = r[idxPrice];
        const price = typeof priceVal === "number" ? priceVal : (parseFloat(String(priceVal || "").replace(",",".")) || 0);
        const reutRaw = idxReut >= 0 ? (r[idxReut] ?? "") : "";
        const reutilizavel = /sim|yes|true|reutiliz|^x$|^1$/i.test(String(reutRaw));
        parsed.push({ name, unit, price, reutilizavel });
      }
      let updated = 0, added = 0;
      const next = [...existingInsumos];
      parsed.forEach(p => {
        const existingIdx = next.findIndex(x => x.restaurantId === restaurantId && ftNrm(x.name) === ftNrm(p.name));
        if (existingIdx >= 0) {
          const cur = next[existingIdx];
          const nx = { ...cur, unit: p.unit || cur.unit, price: p.price };
          if (p.reutilizavel) nx.reutilizavel = true; else delete nx.reutilizavel;
          next[existingIdx] = nx;
          updated++;
        } else {
          const ni = { id: ftUid(), restaurantId, name: p.name, unit: p.unit || "kg", price: p.price };
          if (p.reutilizavel) ni.reutilizavel = true;
          next.push(ni);
          added++;
        }
      });
      onResult({ next, updated, added, total: parsed.length });
    } catch (err) {
      alert("Erro ao ler arquivo: " + err.message);
    }
  };
  reader.readAsBinaryString(file);
}
/* eslint-enable no-unused-vars */

// ═══════════════════════════════════════════════════════════════
// ──  MISE FICHAS TÉCNICAS — ADMIN SHELL + INSUMOS + EQUIPAMENTOS
// ═══════════════════════════════════════════════════════════════
function MiseFichasTecnicasAdmin({ restaurantId, miseFtInsumos, miseFtEquipamentos, miseFtDishes, onUpdate, mobileOnly }) {
  const [subTab, setSubTab] = useState("fichas");
  const miseAc = "#7c9e5e";
  const SUB_TABS = [
    { id: "fichas",       label: "Fichas" },
    { id: "insumos",      label: "Insumos" },
    { id: "equipamentos", label: "Equipamentos" },
  ];

  function handleImportJSON(e) {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async ev => {
      try {
        const data = JSON.parse(ev.target.result);
        const dishes = data.dishes || [];
        const insumos = data.insumos || [];
        const equipamentosList = data.equipamentos_disponiveis || [];
        if (!dishes.length && !insumos.length && !equipamentosList.length) {
          alert("Arquivo não tem dishes, insumos ou equipamentos. Verifique se é o data.json correto do Operacional.");
          return;
        }
        const existingDishes = miseFtDishes.filter(d => d.restaurantId === restaurantId).length;
        const existingInsumos = miseFtInsumos.filter(i => i.restaurantId === restaurantId).length;
        const confirmMsg =
          `Importar para este restaurante:\n\n` +
          `  • ${dishes.length} ficha(s)\n` +
          `  • ${insumos.length} insumo(s)\n` +
          `  • ${equipamentosList.length} equipamento(s)\n\n` +
          (existingDishes || existingInsumos
            ? `⚠ O restaurante já tem ${existingDishes} ficha(s) e ${existingInsumos} insumo(s). Itens com mesmo ID/nome serão sobrescritos; novos serão adicionados.\n\n`
            : ``) +
          `Continuar?`;
        if (!await appConfirm(confirmMsg)) return;

        // ── Insumos: merge por ID; se não tem mesmo ID mas tem mesmo nome em outra origem, mantém o do arquivo
        const insumosNext = [...miseFtInsumos];
        let insAdded = 0, insUpdated = 0;
        insumos.forEach(src => {
          const id = src.id || ftUid();
          const mapped = {
            id, restaurantId,
            name: src.name || id,
            unit: src.unit || "kg",
            price: typeof src.price === "number" ? src.price : (parseFloat(src.price) || 0),
          };
          if (src.reutilizavel) mapped.reutilizavel = true;
          const idx = insumosNext.findIndex(x => x.id === id && x.restaurantId === restaurantId);
          if (idx >= 0) { insumosNext[idx] = mapped; insUpdated++; }
          else { insumosNext.push(mapped); insAdded++; }
        });

        // ── Dishes: merge por ID
        const dishesNext = [...miseFtDishes];
        let dishAdded = 0, dishUpdated = 0;
        const photosDropped = dishes.reduce((s, d) => s + (d.photos || []).length, 0);
        dishes.forEach(src => {
          const id = src.id || ftUid();
          const mapped = {
            id, restaurantId,
            name: src.name || id,
            description: src.description || "",
            louca: src.louca || "",
            equipamentos: Array.isArray(src.equipamentos) ? src.equipamentos : [],
            markup: typeof src.markup === "number" ? src.markup : 300,
            sub_fichas: (src.sub_fichas || []).map(sf => ({
              id: sf.id || ftUid(),
              name: sf.name || "",
              rendimento: sf.rendimento || "",
              rendimento_qty: typeof sf.rendimento_qty === "number" ? sf.rendimento_qty : null,
              rendimento_unit: sf.rendimento_unit || "",
              modo_preparo: sf.modo_preparo || "",
              subproduto: sf.subproduto || undefined,
              ingredientes: (sf.ingredientes || []).map(ing => {
                const mi = {
                  qty: typeof ing.qty === "number" ? ing.qty : (ing.qty === null ? null : (parseFloat(ing.qty) || null)),
                  unit: ing.unit || "",
                };
                if (ing.insumo_id) mi.insumo_id = ing.insumo_id;
                if (ing.insumo_name) mi.insumo_name = ing.insumo_name;
                if (ing.subref_id) mi.subref_id = ing.subref_id;
                if (ing.qty_raw) mi.qty_raw = ing.qty_raw;
                if (ing.observacao) mi.observacao = ing.observacao;
                if (ing.is_qb) mi.is_qb = true;
                if (typeof ing.fc === "number" && ing.fc > 0) mi.fc = ing.fc;
                return mi;
              }),
            })),
            photos: [], // Fotos base64 descartadas — excedem limite Firestore (1MB/doc). Futuro: Firebase Storage
          };
          if (typeof src.target_cmv === "number" && src.target_cmv > 0) mapped.target_cmv = src.target_cmv;
          const idx = dishesNext.findIndex(x => x.id === id && x.restaurantId === restaurantId);
          if (idx >= 0) { dishesNext[idx] = mapped; dishUpdated++; }
          else { dishesNext.push(mapped); dishAdded++; }
        });

        // ── Equipamentos: merge com o set existente
        const currentEquip = (miseFtEquipamentos?.[restaurantId] ?? []);
        const merged = Array.from(new Set([...currentEquip, ...equipamentosList.filter(e => typeof e === "string")]));
        const equipNext = { ...(miseFtEquipamentos ?? {}), [restaurantId]: merged };

        // Commit
        if (insumos.length) onUpdate("miseFtInsumos", insumosNext);
        if (dishes.length) onUpdate("miseFtDishes", dishesNext);
        if (equipamentosList.length) onUpdate("miseFtEquipamentos", equipNext);

        alert(
          `Importação concluída:\n\n` +
          `  • Insumos: ${insAdded} novo(s), ${insUpdated} atualizado(s)\n` +
          `  • Fichas: ${dishAdded} nova(s), ${dishUpdated} atualizada(s)\n` +
          `  • Equipamentos: ${merged.length - currentEquip.length} novo(s) (total ${merged.length})` +
          (photosDropped > 0 ? `\n\n⚠ ${photosDropped} foto(s) descartada(s) — imagens base64 excederiam o limite de armazenamento. Upload de fotos será via Firebase Storage numa iteração futura.` : "")
        );
      } catch (err) {
        alert("Erro ao processar arquivo: " + err.message);
      }
    };
    reader.readAsText(file);
  }

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8,marginBottom:12}}>
        <div style={{display:"flex",gap:4,borderBottom:"1px solid var(--border)",overflowX:"auto"}}>
          {SUB_TABS.map(t => (
            <button key={t.id} onClick={()=>setSubTab(t.id)}
              style={{background:"none",border:"none",borderBottom: subTab===t.id ? `2px solid ${miseAc}` : "2px solid transparent",padding:"8px 14px",cursor:"pointer",fontSize:13,fontWeight: subTab===t.id ? 700 : 500,color: subTab===t.id ? "var(--text)" : "var(--text3)",whiteSpace:"nowrap",flexShrink:0}}>
              {t.label}
            </button>
          ))}
        </div>
        <label style={{...S.btnSecondary,fontSize:11,padding:"6px 12px",cursor:"pointer",display:"inline-flex",alignItems:"center",color:miseAc,borderColor:miseAc+"66"}}>
          ⬆ Importar do Operacional (JSON)
          <input type="file" accept=".json,application/json" onChange={handleImportJSON} style={{display:"none"}} />
        </label>
      </div>

      {subTab === "fichas" && (
        <MiseFtDishesAdmin
          restaurantId={restaurantId}
          miseFtInsumos={miseFtInsumos}
          miseFtEquipamentos={miseFtEquipamentos}
          miseFtDishes={miseFtDishes}
          onUpdate={onUpdate}
          mobileOnly={mobileOnly}
        />
      )}
      {subTab === "insumos" && (
        <MiseFtInsumos
          restaurantId={restaurantId}
          miseFtInsumos={miseFtInsumos}
          onUpdate={onUpdate}
          mobileOnly={mobileOnly}
        />
      )}
      {subTab === "equipamentos" && (
        <MiseFtEquipamentos
          restaurantId={restaurantId}
          miseFtEquipamentos={miseFtEquipamentos}
          onUpdate={onUpdate}
          mobileOnly={mobileOnly}
        />
      )}
    </div>
  );
}

// ── INSUMOS (CRUD) ──
function MiseFtInsumos({ restaurantId, miseFtInsumos, onUpdate, mobileOnly }) {
  const restInsumos = miseFtInsumos.filter(i => i.restaurantId === restaurantId);
  const [filter, setFilter] = useState("");
  const [form, setForm] = useState({ name: "", unit: "kg", price: "", reutilizavel: false });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", unit: "kg", price: "", reutilizavel: false });
  const miseAc = "#7c9e5e";

  function addInsumo() {
    const nm = form.name.trim();
    if (!nm) return;
    if (restInsumos.some(i => ftNrm(i.name) === ftNrm(nm))) { alert("Já existe insumo com esse nome."); return; }
    const price = parseFloat(String(form.price).replace(",","."));
    const newI = { id: ftUid(), restaurantId, name: nm, unit: form.unit || "kg", price: isNaN(price) ? 0 : price };
    if (form.reutilizavel) newI.reutilizavel = true;
    onUpdate("miseFtInsumos", [...miseFtInsumos, newI]);
    setForm({ name: "", unit: "kg", price: "", reutilizavel: false });
  }
  function startEdit(i) {
    setEditingId(i.id);
    setEditForm({ name: i.name, unit: i.unit ?? "kg", price: i.price ?? "", reutilizavel: !!i.reutilizavel });
  }
  function saveEdit() {
    if (!editingId) return;
    const nm = editForm.name.trim();
    if (!nm) return;
    const price = parseFloat(String(editForm.price).replace(",","."));
    onUpdate("miseFtInsumos", miseFtInsumos.map(x => {
      if (x.id !== editingId) return x;
      const next = { ...x, name: nm, unit: editForm.unit || "kg", price: isNaN(price) ? 0 : price };
      if (editForm.reutilizavel) next.reutilizavel = true; else delete next.reutilizavel;
      return next;
    }));
    setEditingId(null);
  }
  async function delInsumo(id) {
    const i = restInsumos.find(x => x.id === id);
    if (!await appConfirm(`Remover insumo "${i?.name}"?`)) return;
    onUpdate("miseFtInsumos", miseFtInsumos.filter(x => x.id !== id));
  }

  const q = filter.trim().toLowerCase();
  const filtered = q ? restInsumos.filter(i => i.name.toLowerCase().includes(q)) : restInsumos;

  function handleImport(e) {
    const file = e.target.files[0];
    if (!file) return;
    ftImportInsumosXLSX(file, miseFtInsumos, restaurantId, ({ next, updated, added, total }) => {
      onUpdate("miseFtInsumos", next);
      onUpdate("_toast", `✓ Importação concluída: ${added} novo(s), ${updated} atualizado(s), ${total} total.`);
    });
    e.target.value = "";
  }

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8,marginBottom:12}}>
        <h3 style={{color:"var(--text)",margin:0,fontSize:mobileOnly?16:18}}>🧂 Insumos</h3>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          <label style={{...S.btnSecondary,fontSize:11,padding:"6px 12px",cursor:"pointer",display:"inline-flex",alignItems:"center"}}>
            ⬆ Importar Excel
            <input type="file" accept=".xlsx,.xls" onChange={handleImport} style={{display:"none"}} />
          </label>
          <button onClick={()=>ftExportInsumosXLSX(restInsumos, "restaurante")} disabled={restInsumos.length===0} style={{...S.btnSecondary,fontSize:11,padding:"6px 12px",opacity:restInsumos.length===0?0.5:1}}>⬇ Exportar Excel</button>
          <button onClick={ftDownloadInsumosTemplateXLSX} style={{...S.btnSecondary,fontSize:11,padding:"6px 12px"}}>📋 Template</button>
        </div>
      </div>
      {/* Form */}
      <div style={{padding:"12px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,marginBottom:16}}>
        <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"2fr 1fr 1fr auto auto",gap:8,alignItems:"end"}}>
          <div>
            <label style={{...S.label,fontSize:11}}>Nome do insumo</label>
            <input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="ex: Farinha de trigo" style={{...S.input}} onKeyDown={e=>e.key==="Enter"&&addInsumo()} />
          </div>
          <div>
            <label style={{...S.label,fontSize:11}}>Unidade</label>
            <select value={form.unit} onChange={e=>setForm({...form,unit:e.target.value})} style={{...S.input}}>
              {FT_CANONICAL_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div>
            <label style={{...S.label,fontSize:11}}>Preço (R$)</label>
            <input type="number" step="0.01" value={form.price} onChange={e=>setForm({...form,price:e.target.value})} placeholder="0,00" style={{...S.input,fontFamily:"'DM Mono',monospace"}} />
          </div>
          <div style={{display:"flex",alignItems:"center",gap:6,paddingBottom:8}}>
            <input type="checkbox" id="new-reut" checked={form.reutilizavel} onChange={e=>setForm({...form,reutilizavel:e.target.checked})} style={{cursor:"pointer",accentColor:miseAc}} />
            <label htmlFor="new-reut" style={{fontSize:12,color:"var(--text2)",cursor:"pointer"}} title="Insumos reutilizáveis (óleo de fritura, etc) não entram no custo das fichas">Reut.</label>
          </div>
          <button onClick={addInsumo} disabled={!form.name.trim()} style={{...S.btnPrimary,fontSize:13,padding:"8px 16px",opacity:!form.name.trim()?0.5:1,cursor:!form.name.trim()?"not-allowed":"pointer"}}>+ Adicionar</button>
        </div>
      </div>
      {/* Filter */}
      <div style={{marginBottom:10}}>
        <input value={filter} onChange={e=>setFilter(e.target.value)} placeholder="🔍 Buscar..." style={{...S.input,maxWidth:360}} />
      </div>
      {/* List */}
      {filtered.length === 0 ? (
        <div style={{padding:"32px 16px",textAlign:"center",color:"var(--text3)",fontSize:14,background:"var(--card-bg)",borderRadius:12,border:"1px solid var(--border)"}}>
          {q ? "Nenhum insumo encontrado para esse filtro" : "Nenhum insumo cadastrado"}
        </div>
      ) : (
        <div style={{overflowX:"auto",border:"1px solid var(--border)",borderRadius:12,background:"var(--card-bg)"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
            <thead>
              <tr style={{background:"var(--bg2)",borderBottom:"1px solid var(--border)"}}>
                <th style={{padding:"10px 12px",textAlign:"left",color:"var(--text)",fontWeight:700}}>Nome</th>
                <th style={{padding:"10px 12px",textAlign:"center",color:"var(--text)",fontWeight:700}}>Unid.</th>
                <th style={{padding:"10px 12px",textAlign:"right",color:"var(--text)",fontWeight:700}}>Preço</th>
                <th style={{padding:"10px 12px",textAlign:"right",color:"var(--text)",fontWeight:700}}>Preço/un canônica</th>
                <th style={{padding:"10px 12px",textAlign:"center",color:"var(--text)",fontWeight:700}}>Reut.</th>
                <th style={{padding:"10px 12px",textAlign:"right",color:"var(--text)",fontWeight:700}}></th>
              </tr>
            </thead>
            <tbody>
              {[...filtered].sort((a,b)=>a.name.localeCompare(b.name)).map(i => {
                const isEditing = editingId === i.id;
                if (isEditing) {
                  return (
                    <tr key={i.id} style={{borderTop:"1px solid var(--border)",background:"var(--bg2)"}}>
                      <td style={{padding:"6px 12px"}}>
                        <input value={editForm.name} onChange={e=>setEditForm({...editForm,name:e.target.value})} style={{...S.input}} autoFocus />
                      </td>
                      <td style={{padding:"6px 12px"}}>
                        <select value={editForm.unit} onChange={e=>setEditForm({...editForm,unit:e.target.value})} style={{...S.input}}>
                          {FT_CANONICAL_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </td>
                      <td style={{padding:"6px 12px"}}>
                        <input type="number" step="0.01" value={editForm.price} onChange={e=>setEditForm({...editForm,price:e.target.value})} style={{...S.input,textAlign:"right",fontFamily:"'DM Mono',monospace"}} />
                      </td>
                      <td style={{padding:"6px 12px",textAlign:"right",color:"var(--text3)",fontFamily:"'DM Mono',monospace",fontSize:11}}>
                        {(() => { const n = FT_WEIGHT_G.concat(FT_WEIGHT_KG,FT_VOL_ML,FT_VOL_L).includes((editForm.unit||"").toLowerCase()); return n ? "calc." : "—"; })()}
                      </td>
                      <td style={{padding:"6px 12px",textAlign:"center"}}>
                        <input type="checkbox" checked={editForm.reutilizavel} onChange={e=>setEditForm({...editForm,reutilizavel:e.target.checked})} style={{cursor:"pointer",accentColor:miseAc}}/>
                      </td>
                      <td style={{padding:"6px 12px",textAlign:"right",whiteSpace:"nowrap"}}>
                        <button onClick={saveEdit} style={{...S.btnPrimary,fontSize:11,padding:"5px 10px",marginRight:4}}>Salvar</button>
                        <button onClick={()=>setEditingId(null)} style={{...S.btnSecondary,fontSize:11,padding:"5px 10px"}}>Cancelar</button>
                      </td>
                    </tr>
                  );
                }
                const norm = (() => {
                  const u = (i.unit || "").toLowerCase().trim();
                  const p = i.price || 0;
                  if (FT_WEIGHT_G.includes(u)) return { price: p * 1000, unit: 'kg' };
                  if (FT_WEIGHT_MG.includes(u)) return { price: p * 1e6, unit: 'kg' };
                  if (FT_WEIGHT_KG.includes(u)) return { price: p, unit: 'kg' };
                  if (FT_VOL_ML.includes(u)) return { price: p * 1000, unit: 'l' };
                  if (FT_VOL_L.includes(u)) return { price: p, unit: 'l' };
                  return null;
                })();
                return (
                  <tr key={i.id} style={{borderTop:"1px solid var(--border)"}}>
                    <td style={{padding:"8px 12px",color:"var(--text)",fontWeight:500}}>{i.name}</td>
                    <td style={{padding:"8px 12px",textAlign:"center",color:"var(--text2)"}}>{i.unit ?? "—"}</td>
                    <td style={{padding:"8px 12px",textAlign:"right",color:"var(--text2)",fontFamily:"'DM Mono',monospace"}}>{fmt(i.price ?? 0)}</td>
                    <td style={{padding:"8px 12px",textAlign:"right",color:"var(--text3)",fontFamily:"'DM Mono',monospace",fontSize:11}}>{norm ? `${fmt(norm.price)}/${norm.unit}` : "—"}</td>
                    <td style={{padding:"8px 12px",textAlign:"center"}}>{i.reutilizavel ? <span style={{color:"#a855f7",fontSize:14}} title="Insumo reutilizável — não entra no custo">♻️</span> : ""}</td>
                    <td style={{padding:"8px 12px",textAlign:"right",whiteSpace:"nowrap"}}>
                      <button onClick={()=>startEdit(i)} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px",marginRight:4}}>Editar</button>
                      <button onClick={()=>delInsumo(i.id)} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px",color:"var(--red)",borderColor:"var(--red)44"}}>Remover</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div style={{marginTop:10,fontSize:11,color:"var(--text3)"}}>{restInsumos.length} insumo(s) cadastrado(s)</div>
    </div>
  );
}

// ── EQUIPAMENTOS (CRUD) ──
function MiseFtEquipamentos({ restaurantId, miseFtEquipamentos, onUpdate, mobileOnly }) {
  const list = miseFtEquipamentos?.[restaurantId] ?? [];
  const [newName, setNewName] = useState("");
  const [editingIdx, setEditingIdx] = useState(null);
  const [editName, setEditName] = useState("");

  function save(newList) {
    onUpdate("miseFtEquipamentos", { ...(miseFtEquipamentos ?? {}), [restaurantId]: newList });
  }
  function add() {
    const nm = newName.trim();
    if (!nm) return;
    if (list.some(x => ftNrm(x) === ftNrm(nm))) { alert("Já existe equipamento com esse nome."); return; }
    save([...list, nm]);
    setNewName("");
  }
  function saveEdit() {
    const nm = editName.trim();
    if (!nm || editingIdx == null) return;
    const next = [...list];
    next[editingIdx] = nm;
    save(next);
    setEditingIdx(null);
    setEditName("");
  }
  async function remove(idx) {
    if (!await appConfirm(`Remover equipamento "${list[idx]}"?`)) return;
    save(list.filter((_, i) => i !== idx));
  }

  return (
    <div>
      <h3 style={{color:"var(--text)",margin:"0 0 12px",fontSize:mobileOnly?16:18}}>🔧 Equipamentos</h3>
      <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
        <input value={newName} onChange={e=>setNewName(e.target.value)} placeholder="ex: Forno combinado" style={{...S.input,maxWidth:360,flex:"1 1 220px"}} onKeyDown={e=>e.key==="Enter"&&add()} />
        <button onClick={add} style={{...S.btnPrimary,fontSize:13,padding:"8px 16px"}}>+ Adicionar</button>
      </div>
      {list.length === 0 ? (
        <div style={{padding:"32px 16px",textAlign:"center",color:"var(--text3)",fontSize:14,background:"var(--card-bg)",borderRadius:12,border:"1px solid var(--border)"}}>Nenhum equipamento cadastrado</div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {[...list].map((nm, idx) => {
            const isEditing = editingIdx === idx;
            return (
              <div key={idx} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10}}>
                {isEditing ? (
                  <>
                    <input value={editName} onChange={e=>setEditName(e.target.value)} style={{...S.input,flex:1}} autoFocus onKeyDown={e=>e.key==="Enter"&&saveEdit()} />
                    <button onClick={saveEdit} style={{...S.btnPrimary,fontSize:12,padding:"6px 12px"}}>Salvar</button>
                    <button onClick={()=>setEditingIdx(null)} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px"}}>Cancelar</button>
                  </>
                ) : (
                  <>
                    <div style={{flex:1,color:"var(--text)",fontWeight:500}}>{nm}</div>
                    <button onClick={()=>{setEditingIdx(idx);setEditName(nm);}} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px"}}>Editar</button>
                    <button onClick={()=>remove(idx)} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px",color:"var(--red)",borderColor:"var(--red)44"}}>Remover</button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div style={{marginTop:10,fontSize:11,color:"var(--text3)"}}>{list.length} equipamento(s) cadastrado(s)</div>
    </div>
  );
}

// ── DISHES LIST (Admin) ──
function MiseFtDishesAdmin({ restaurantId, miseFtInsumos, miseFtEquipamentos, miseFtDishes, onUpdate, mobileOnly }) {
  const [editingDishId, setEditingDishId] = useState(null);
  const [filter, setFilter] = useState("");
  const restDishes = miseFtDishes.filter(d => d.restaurantId === restaurantId);
  const restInsumos = miseFtInsumos.filter(i => i.restaurantId === restaurantId);
  const equipList = miseFtEquipamentos?.[restaurantId] ?? [];

  if (editingDishId) {
    const dish = restDishes.find(d => d.id === editingDishId);
    if (!dish) { setTimeout(() => setEditingDishId(null), 0); return null; }
    return (
      <MiseFtDishEditor
        dish={dish}
        restaurantId={restaurantId}
        restDishes={restDishes}
        insumos={restInsumos}
        equipamentos={equipList}
        miseFtInsumos={miseFtInsumos}
        miseFtDishes={miseFtDishes}
        onUpdate={onUpdate}
        onBack={()=>setEditingDishId(null)}
        mobileOnly={mobileOnly}
      />
    );
  }

  function newDish() {
    const name = window.prompt("Nome da nova ficha técnica:");
    if (!name || !name.trim()) return;
    const id = ftUid();
    const firstSfId = ftUid();
    const firstSf = { id: firstSfId, name: name.trim(), rendimento: "", rendimento_qty: null, rendimento_unit: "", ingredientes: [], modo_preparo: "" };
    const dish = { id, restaurantId, name: name.trim(), description: "", louca: "", equipamentos: [], markup: 300, sub_fichas: [firstSf], photos: [] };
    onUpdate("miseFtDishes", [...miseFtDishes, dish]);
    setEditingDishId(id);
  }
  async function delDish(id) {
    const d = restDishes.find(x => x.id === id);
    if (!await appConfirm(`Apagar ficha "${d?.name}"? Esta ação não pode ser desfeita.`)) return;
    onUpdate("miseFtDishes", miseFtDishes.filter(x => x.id !== id));
  }
  function duplicateDish(id) {
    const src = restDishes.find(x => x.id === id);
    if (!src) return;
    const nm = window.prompt("Nome da cópia:", `${src.name} (cópia)`);
    if (!nm || !nm.trim()) return;
    const sfIdMap = {};
    const newSubFichas = (src.sub_fichas || []).map(sf => {
      const newSfId = ftUid();
      sfIdMap[sf.id] = newSfId;
      return { ...sf, id: newSfId, ingredientes: (sf.ingredientes || []).map(ing => ({...ing})) };
    });
    // Re-map subref_ids to point to new sf ids
    newSubFichas.forEach(sf => {
      sf.ingredientes = sf.ingredientes.map(ing => ing.subref_id && sfIdMap[ing.subref_id] ? {...ing, subref_id: sfIdMap[ing.subref_id]} : ing);
    });
    const copy = { ...src, id: ftUid(), name: nm.trim(), sub_fichas: newSubFichas };
    onUpdate("miseFtDishes", [...miseFtDishes, copy]);
  }

  const q = filter.trim().toLowerCase();
  const filtered = q ? restDishes.filter(d => d.name.toLowerCase().includes(q)) : restDishes;

  return (
    <div>
      <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
        <button onClick={newDish} style={{...S.btnPrimary,fontSize:13,padding:"8px 16px"}}>+ Nova ficha</button>
        <input value={filter} onChange={e=>setFilter(e.target.value)} placeholder="🔍 Buscar ficha..." style={{...S.input,maxWidth:320,flex:"1 1 200px"}} />
        <div style={{flex:1}} />
        <div style={{fontSize:11,color:"var(--text3)"}}>{restInsumos.length} insumo(s) · {restDishes.length} ficha(s)</div>
      </div>

      {filtered.length === 0 ? (
        <div style={{padding:"48px 16px",textAlign:"center",color:"var(--text3)",fontSize:14,background:"var(--card-bg)",borderRadius:12,border:"1px solid var(--border)"}}>
          {q ? "Nenhuma ficha encontrada" : (
            <>
              <div style={{fontSize:40,marginBottom:12}}>📋</div>
              <div style={{color:"var(--text)",fontWeight:600,fontSize:16,marginBottom:6}}>Nenhuma ficha cadastrada neste restaurante</div>
              <div>Clique em "+ Nova ficha" para começar ou use "Importar do Operacional (JSON)" no topo.</div>
              {(() => {
                const totalDishes = (miseFtDishes || []).length;
                const otherRests = (miseFtDishes || []).reduce((s, d) => { s[d.restaurantId] = (s[d.restaurantId] || 0) + 1; return s; }, {});
                delete otherRests[restaurantId];
                const otherCount = Object.values(otherRests).reduce((a,b)=>a+b, 0);
                if (totalDishes > 0) {
                  return (
                    <div style={{marginTop:14,padding:"10px 14px",background:"#fffbeb",border:"1px solid #f59e0b44",borderRadius:8,fontSize:12,color:"#92400e",lineHeight:1.6,textAlign:"left",maxWidth:520,margin:"14px auto 0"}}>
                      ⚠️ Diagnóstico: há <b>{totalDishes} ficha(s)</b> no sistema, mas <b>0 neste restaurante</b> ({otherCount} em outros {Object.keys(otherRests).length > 0 ? `(${Object.keys(otherRests).length} restaurante${Object.keys(otherRests).length>1?"s":""})` : ""}).<br/>
                      Pode ter havido mismatch de restaurantId no momento da importação. ID atual: <code style={{fontFamily:"'DM Mono',monospace",background:"var(--bg2)",padding:"1px 4px",borderRadius:3}}>{restaurantId}</code>
                    </div>
                  );
                }
                return null;
              })()}
            </>
          )}
        </div>
      ) : (
        <div style={{overflowX:"auto",border:"1px solid var(--border)",borderRadius:12,background:"var(--card-bg)"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
            <thead>
              <tr style={{background:"var(--bg2)",borderBottom:"1px solid var(--border)"}}>
                <th style={{padding:"10px 12px",textAlign:"left",color:"var(--text)",fontWeight:700}}>Ficha</th>
                <th style={{padding:"10px 12px",textAlign:"center",color:"var(--text)",fontWeight:700}}>Sub-fichas</th>
                <th style={{padding:"10px 12px",textAlign:"right",color:"var(--text)",fontWeight:700}}>Rendimento</th>
                <th style={{padding:"10px 12px",textAlign:"right",color:"var(--text)",fontWeight:700}}>Custo/porção</th>
                <th style={{padding:"10px 12px",textAlign:"right",color:"var(--text)",fontWeight:700}}>Preço sug.</th>
                <th style={{padding:"10px 12px",textAlign:"right",color:"var(--text)",fontWeight:700}}>CMV</th>
                <th style={{padding:"10px 12px",textAlign:"right",color:"var(--text)",fontWeight:700}}></th>
              </tr>
            </thead>
            <tbody>
              {[...filtered].sort((a,b)=>a.name.localeCompare(b.name)).map(d => {
                const cost = ftDishCost(d, restInsumos, restDishes);
                const lastSf = (d.sub_fichas || [])[(d.sub_fichas || []).length - 1];
                const rendDisplay = lastSf?.rendimento ? ftFormatRendimento(lastSf.rendimento) : "—";
                return (
                  <tr key={d.id} style={{borderTop:"1px solid var(--border)"}}>
                    <td style={{padding:"10px 12px",color:"var(--text)",fontWeight:600,cursor:"pointer"}} onClick={()=>setEditingDishId(d.id)}>{d.name}</td>
                    <td style={{padding:"10px 12px",textAlign:"center",color:"var(--text3)"}}>{(d.sub_fichas||[]).length}</td>
                    <td style={{padding:"10px 12px",textAlign:"right",color:"var(--text2)",fontSize:12}}>{rendDisplay}</td>
                    <td style={{padding:"10px 12px",textAlign:"right",color:"var(--text2)",fontFamily:"'DM Mono',monospace"}}>{fmt(cost.costPerPortion)}</td>
                    <td style={{padding:"10px 12px",textAlign:"right",color:"#15803d",fontWeight:700,fontFamily:"'DM Mono',monospace"}}>{fmt(cost.suggestedPrice)}</td>
                    <td style={{padding:"10px 12px",textAlign:"right",color:"var(--text2)",fontFamily:"'DM Mono',monospace"}}>{cost.cmv ? cost.cmv.toFixed(1)+"%" : "—"}</td>
                    <td style={{padding:"10px 12px",textAlign:"right",whiteSpace:"nowrap"}}>
                      <button onClick={()=>setEditingDishId(d.id)} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px",marginRight:4}}>Editar</button>
                      <button onClick={()=>ftExportDishPDF(d, restInsumos, restDishes, "")} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px",marginRight:4}} title="Exportar PDF">📄</button>
                      <button onClick={()=>duplicateDish(d.id)} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px",marginRight:4}} title="Duplicar">⎘</button>
                      <button onClick={()=>delDish(d.id)} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px",color:"var(--red)",borderColor:"var(--red)44"}}>×</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── DISH EDITOR (Admin) ──
function MiseFtDishEditor({ dish, restaurantId, restDishes, insumos, equipamentos, miseFtInsumos, miseFtDishes, onUpdate, onBack, mobileOnly }) {
  const [local, setLocal] = useState(dish);
  // Reset local when dish prop changes (e.g. after save)
  useEffect(() => { setLocal(dish); }, [dish.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = JSON.stringify(local) !== JSON.stringify(dish);
  const miseAc = "#7c9e5e";

  function save() {
    onUpdate("miseFtDishes", miseFtDishes.map(d => d.id === local.id ? local : d));
  }
  async function handleBack() {
    if (dirty && !await appConfirm("Descartar alterações e voltar?")) return;
    onBack();
  }

  function updateSubFicha(sfId, patch) {
    setLocal(l => ({ ...l, sub_fichas: (l.sub_fichas || []).map(sf => sf.id === sfId ? { ...sf, ...patch } : sf) }));
  }
  function addSubFicha() {
    setLocal(l => {
      const n = (l.sub_fichas || []).length + 1;
      return { ...l, sub_fichas: [...(l.sub_fichas || []), { id: ftUid(), name: `Sub-ficha ${n}`, rendimento: "", rendimento_qty: null, rendimento_unit: "", ingredientes: [], modo_preparo: "" }] };
    });
  }
  async function delSubFicha(sfId) {
    if (!await appConfirm("Apagar essa sub-ficha?")) return;
    setLocal(l => ({ ...l, sub_fichas: (l.sub_fichas || []).filter(sf => sf.id !== sfId) }));
  }
  function moveSf(sfId, dir) {
    setLocal(l => {
      const arr = [...(l.sub_fichas || [])];
      const idx = arr.findIndex(s => s.id === sfId);
      const ni = idx + dir;
      if (idx < 0 || ni < 0 || ni >= arr.length) return l;
      [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
      return { ...l, sub_fichas: arr };
    });
  }

  function quickAddInsumo(name) {
    const nm = name.trim();
    if (!nm) return null;
    const existing = insumos.find(i => ftNrm(i.name) === ftNrm(nm));
    if (existing) return existing;
    const newI = { id: ftUid(), restaurantId, name: nm, unit: "kg", price: 0 };
    onUpdate("miseFtInsumos", [...miseFtInsumos, newI]);
    return newI;
  }

  const costInfo = ftDishCost(local, insumos, restDishes);
  const insumoListId = `ft-insumos-${local.id}`;

  return (
    <div>
      {/* Toolbar */}
      <div style={{display:"flex",gap:8,marginBottom:16,alignItems:"center",flexWrap:"wrap",position:"sticky",top:0,background:"var(--bg)",zIndex:10,paddingTop:4,paddingBottom:8,borderBottom:"1px solid var(--border)"}}>
        <button onClick={handleBack} style={{...S.btnSecondary,fontSize:13,padding:"8px 14px"}}>← Voltar</button>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:11,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Editando ficha</div>
          <div style={{fontSize:15,color:"var(--text)",fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{local.name}</div>
        </div>
        {dirty && <span style={{color:"#f59e0b",fontSize:11,fontWeight:700,padding:"3px 8px",background:"#fffbeb",border:"1px solid #f59e0b44",borderRadius:10,whiteSpace:"nowrap"}}>● Não salvo</span>}
        <button onClick={()=>ftExportDishPDF(local, insumos, restDishes, "")} style={{...S.btnSecondary,fontSize:12,padding:"7px 12px"}} title="Exportar PDF">📄 PDF</button>
        <button onClick={save} disabled={!dirty} style={{background: dirty ? miseAc : "var(--border)",color: dirty?"#fff":"var(--text3)",border:"none",borderRadius:8,padding:"8px 18px",fontSize:13,fontWeight:700,cursor:dirty?"pointer":"not-allowed",fontFamily:"'DM Sans',sans-serif"}}>💾 Salvar</button>
      </div>

      {/* Insumos datalist for autocomplete */}
      <datalist id={insumoListId}>
        {insumos.map(i => <option key={i.id} value={i.name} />)}
      </datalist>

      {/* Ficha main fields */}
      <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"2fr 1fr 1fr",gap:12,marginBottom:16}}>
        <div>
          <label style={S.label}>Nome da ficha</label>
          <input value={local.name ?? ""} onChange={e=>setLocal({...local, name: e.target.value})} style={S.input} />
        </div>
        <div>
          <label style={S.label}>Markup (%)</label>
          <input type="number" step="1" value={local.markup ?? 300} onChange={e=>setLocal({...local, markup: parseFloat(e.target.value)||0})} style={{...S.input,fontFamily:"'DM Mono',monospace"}} />
        </div>
        <div>
          <label style={S.label}>CMV alvo (%) <span style={{color:"var(--text3)",fontWeight:400,fontSize:10}}>opcional</span></label>
          <input type="number" step="1" value={local.target_cmv ?? ""} onChange={e=>{
            const v = e.target.value;
            const n = {...local};
            if (v === "") delete n.target_cmv;
            else n.target_cmv = parseFloat(v) || 0;
            setLocal(n);
          }} placeholder="—" style={{...S.input,fontFamily:"'DM Mono',monospace"}} />
        </div>
      </div>

      {/* Descrição */}
      <div style={{marginBottom:16}}>
        <label style={S.label}>Descrição</label>
        <textarea value={local.description ?? ""} onChange={e=>setLocal({...local, description: e.target.value})} placeholder="Breve descrição do prato..." style={{...S.input, minHeight:60, fontFamily:"inherit", resize:"vertical"}} />
      </div>

      {/* Louça + Equipamentos */}
      <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"1fr 2fr",gap:12,marginBottom:20}}>
        <div>
          <label style={S.label}>Louça</label>
          <input value={local.louca ?? ""} onChange={e=>setLocal({...local, louca: e.target.value})} placeholder="ex: Prato fundo 22cm" style={S.input} />
        </div>
        <div>
          <label style={S.label}>Equipamentos</label>
          {equipamentos.length === 0 ? (
            <div style={{padding:"8px 12px",fontSize:11,color:"var(--text3)",background:"var(--bg2)",border:"1px dashed var(--border)",borderRadius:8}}>
              Nenhum equipamento cadastrado. Cadastre na aba Equipamentos.
            </div>
          ) : (
            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
              {equipamentos.map(eq => {
                const checked = (local.equipamentos || []).includes(eq);
                return (
                  <label key={eq} style={{display:"inline-flex",alignItems:"center",gap:4,padding:"4px 10px",background: checked?miseAc+"22":"var(--bg2)",border:`1px solid ${checked?miseAc:"var(--border)"}`,borderRadius:20,fontSize:12,cursor:"pointer",color:checked?"var(--text)":"var(--text2)"}}>
                    <input type="checkbox" checked={checked} onChange={e=>{
                      const s = new Set(local.equipamentos || []);
                      if (e.target.checked) s.add(eq); else s.delete(eq);
                      setLocal({...local, equipamentos: [...s]});
                    }} style={{margin:0,cursor:"pointer",accentColor:miseAc}} />
                    {eq}
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Cost summary */}
      <div style={{padding:"14px 18px",background:miseAc+"11",border:`1px solid ${miseAc}44`,borderRadius:10,marginBottom:20,display:"grid",gridTemplateColumns:mobileOnly?"1fr 1fr":"repeat(4,1fr)",gap:16}}>
        <div>
          <div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:0.4,fontWeight:700}}>Custo total</div>
          <div style={{fontSize:16,color:"var(--text)",fontWeight:700,fontFamily:"'DM Mono',monospace",marginTop:2}}>{fmt(costInfo.total)}</div>
        </div>
        <div>
          <div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:0.4,fontWeight:700}}>Custo/porção</div>
          <div style={{fontSize:16,color:"var(--text)",fontWeight:700,fontFamily:"'DM Mono',monospace",marginTop:2}}>{fmt(costInfo.costPerPortion)}</div>
          <div style={{fontSize:10,color:"var(--text3)",marginTop:1}}>{costInfo.portions} porç.</div>
        </div>
        <div>
          <div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:0.4,fontWeight:700}}>Preço sugerido</div>
          <div style={{fontSize:16,color:"#15803d",fontWeight:700,fontFamily:"'DM Mono',monospace",marginTop:2}}>{fmt(costInfo.suggestedPrice)}</div>
        </div>
        <div>
          <div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:0.4,fontWeight:700}}>CMV · Markup</div>
          <div style={{fontSize:15,color:"var(--text)",fontWeight:700,fontFamily:"'DM Mono',monospace",marginTop:2}}>{costInfo.cmv ? costInfo.cmv.toFixed(1)+"%" : "—"} · {costInfo.markup?.toFixed(0) ?? 0}%</div>
        </div>
      </div>

      {/* Sub-fichas */}
      <div>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
          <h4 style={{color:"var(--text)",margin:0,fontSize:15,fontWeight:700}}>Sub-fichas <span style={{fontSize:11,color:"var(--text3)",fontWeight:400,marginLeft:6}}>(a última é a ficha final)</span></h4>
          <button onClick={addSubFicha} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px"}}>+ Nova sub-ficha</button>
        </div>

        {(local.sub_fichas || []).length === 0 ? (
          <div style={{padding:"32px 16px",textAlign:"center",color:"var(--text3)",fontSize:14,background:"var(--card-bg)",borderRadius:12,border:"1px dashed var(--border)"}}>
            A ficha precisa de pelo menos uma sub-ficha. Clique em "+ Nova sub-ficha".
          </div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            {local.sub_fichas.map((sf, sfIdx) => {
              const isFinal = sfIdx === local.sub_fichas.length - 1;
              const sfCost = costInfo.sfCosts.find(x => x.sf.id === sf.id);
              return (
                <MiseFtSubFichaEditor
                  key={sf.id}
                  sf={sf} sfIdx={sfIdx} total={local.sub_fichas.length}
                  isFinal={isFinal}
                  sfCost={sfCost}
                  insumos={insumos}
                  allSfs={local.sub_fichas}
                  insumoListId={insumoListId}
                  updateSubFicha={(patch)=>updateSubFicha(sf.id, patch)}
                  delSubFicha={()=>delSubFicha(sf.id)}
                  moveSf={(dir)=>moveSf(sf.id, dir)}
                  quickAddInsumo={quickAddInsumo}
                  mobileOnly={mobileOnly}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── SUB-FICHA EDITOR (inside dish editor) ──
function MiseFtSubFichaEditor({ sf, sfIdx, total, isFinal, sfCost, insumos, allSfs, insumoListId, updateSubFicha, delSubFicha, moveSf, quickAddInsumo, mobileOnly }) {
  const [expanded, setExpanded] = useState(true);
  const miseAc = "#7c9e5e";

  function updateIng(idx, patch) {
    const newIngs = (sf.ingredientes || []).map((ing, i) => i === idx ? { ...ing, ...patch } : ing);
    updateSubFicha({ ingredientes: newIngs });
  }
  function addIng() {
    updateSubFicha({ ingredientes: [...(sf.ingredientes || []), { insumo_id: null, insumo_name: "", qty: null, unit: "" }] });
  }
  function delIngAt(idx) {
    updateSubFicha({ ingredientes: (sf.ingredientes || []).filter((_, i) => i !== idx) });
  }
  function moveIng(idx, dir) {
    const arr = [...(sf.ingredientes || [])];
    const ni = idx + dir;
    if (ni < 0 || ni >= arr.length) return;
    [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
    updateSubFicha({ ingredientes: arr });
  }

  // Available sub-fichas for subref linking (exclude self + forward-only: subrefs must point earlier)
  const availableSubrefs = allSfs.filter((s, i) => s.id !== sf.id && i < sfIdx);

  return (
    <div style={{background:"var(--card-bg)",border:`1px solid ${isFinal ? miseAc+"66" : "var(--border)"}`,borderRadius:12,overflow:"hidden"}}>
      <div style={{padding:"10px 14px",background: isFinal ? miseAc+"11" : "var(--bg2)",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
        <button onClick={()=>setExpanded(!expanded)} style={{background:"none",border:"none",cursor:"pointer",fontSize:14,color:"var(--text3)",padding:"2px 6px"}}>{expanded?"▼":"▶"}</button>
        <input value={sf.name ?? ""} onChange={e=>updateSubFicha({name: e.target.value})} style={{...S.input,flex:"1 1 200px",minWidth:140,fontWeight:600}} />
        {isFinal && <span style={{fontSize:10,padding:"2px 8px",borderRadius:10,background:miseAc+"22",color:"#15803d",fontWeight:700,letterSpacing:0.4}}>FINAL</span>}
        <div style={{fontSize:11,color:"var(--text3)",fontFamily:"'DM Mono',monospace"}} title="Custo desta sub-ficha">
          {sfCost ? fmt(sfCost.total) : "—"} · {(sf.ingredientes||[]).length} ings
        </div>
        <button onClick={()=>moveSf(-1)} disabled={sfIdx===0} style={{...S.btnSecondary,fontSize:11,padding:"4px 8px",opacity:sfIdx===0?0.3:1}} title="Mover para cima">↑</button>
        <button onClick={()=>moveSf(1)} disabled={sfIdx===total-1} style={{...S.btnSecondary,fontSize:11,padding:"4px 8px",opacity:sfIdx===total-1?0.3:1}} title="Mover para baixo">↓</button>
        <button onClick={delSubFicha} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px",color:"var(--red)",borderColor:"var(--red)44"}} title="Apagar sub-ficha">×</button>
      </div>

      {expanded && (
        <div style={{padding:"14px"}}>
          {/* Rendimento + Subproduto */}
          <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"2fr 1fr 1fr 2fr",gap:8,marginBottom:14}}>
            <div>
              <label style={{...S.label,fontSize:11}}>Rendimento (texto)</label>
              <input value={sf.rendimento ?? ""} onChange={e=>updateSubFicha({rendimento: e.target.value})} placeholder="ex: 4 porções, 1 kg" style={S.input} />
            </div>
            <div>
              <label style={{...S.label,fontSize:11}}>Qtd base</label>
              <input type="number" step="0.001" value={sf.rendimento_qty ?? ""} onChange={e=>updateSubFicha({rendimento_qty: e.target.value ? parseFloat(e.target.value.replace(",",".")) : null})} placeholder="0" style={{...S.input,fontFamily:"'DM Mono',monospace"}} />
            </div>
            <div>
              <label style={{...S.label,fontSize:11}}>Unid. base</label>
              <select value={sf.rendimento_unit ?? ""} onChange={e=>updateSubFicha({rendimento_unit: e.target.value})} style={S.input}>
                <option value="">—</option>
                {FT_CANONICAL_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label style={{...S.label,fontSize:11}}>Subproduto gerado <span style={{color:"var(--text3)",fontWeight:400,fontSize:10}}>opcional</span></label>
              <input value={sf.subproduto?.name ?? ""} onChange={e=>{
                const v = e.target.value;
                updateSubFicha({subproduto: v.trim() ? { name: v } : undefined});
              }} placeholder="nome do produto pronto" style={S.input} />
            </div>
          </div>

          {/* Ingredientes */}
          <div style={{marginBottom:14}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
              <label style={{fontSize:11,fontWeight:700,color:"var(--text2)",textTransform:"uppercase",letterSpacing:0.4}}>Ingredientes</label>
              <div style={{display:"flex",gap:6}}>
                {availableSubrefs.length > 0 && (
                  <select onChange={e=>{
                    const subId = e.target.value;
                    if (!subId) return;
                    const subSf = availableSubrefs.find(s => s.id === subId);
                    if (!subSf) return;
                    updateSubFicha({ ingredientes: [...(sf.ingredientes || []), { subref_id: subSf.id, insumo_name: subSf.name, qty: null, unit: subSf.rendimento_unit || "" }] });
                    e.target.value = "";
                  }} style={{...S.input,fontSize:11,width:"auto",padding:"4px 8px"}}>
                    <option value="">+ Sub-ref</option>
                    {availableSubrefs.map(s => <option key={s.id} value={s.id}>↳ {s.name}</option>)}
                  </select>
                )}
                <button onClick={addIng} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px"}}>+ Ingrediente</button>
              </div>
            </div>
            {(sf.ingredientes || []).length === 0 ? (
              <div style={{padding:"16px",textAlign:"center",color:"var(--text3)",fontSize:12,background:"var(--bg2)",borderRadius:8,border:"1px dashed var(--border)"}}>
                Sem ingredientes. Clique em "+ Ingrediente" para adicionar.
              </div>
            ) : (
              <div style={{overflowX:"auto",border:"1px solid var(--border)",borderRadius:8}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                  <thead>
                    <tr style={{background:"var(--bg2)"}}>
                      <th style={{padding:"6px 8px",textAlign:"left",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Ingrediente</th>
                      <th style={{padding:"6px 8px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,width:90}}>Qtd</th>
                      <th style={{padding:"6px 8px",textAlign:"left",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,width:90}}>Unid.</th>
                      <th style={{padding:"6px 8px",textAlign:"center",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,width:50}} title="Quanto Baste (sem quantidade definida)">QB</th>
                      <th style={{padding:"6px 8px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,width:60}} title="Fator de Correção">FC</th>
                      <th style={{padding:"6px 8px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,width:90}}>Custo</th>
                      <th style={{padding:"6px 8px",width:80}}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(sf.ingredientes || []).map((ing, idx) => {
                      const costRow = sfCost?.rows?.[idx];
                      const isSubref = !!ing.subref_id;
                      return (
                        <tr key={idx} style={{borderTop:"1px solid var(--border)"}}>
                          <td style={{padding:"4px 8px"}}>
                            {isSubref ? (
                              <div style={{display:"flex",alignItems:"center",gap:4}}>
                                <span style={{fontSize:10,padding:"2px 6px",borderRadius:8,background:miseAc+"22",color:"#15803d",fontWeight:700,whiteSpace:"nowrap"}}>↳ SUB</span>
                                <span style={{color:"var(--text)",fontWeight:500,fontSize:12}}>{ing.insumo_name || "—"}</span>
                              </div>
                            ) : (
                              <input list={insumoListId} value={ing.insumo_name ?? ""}
                                onChange={e=>{
                                  const v = e.target.value;
                                  const match = insumos.find(i => ftNrm(i.name) === ftNrm(v));
                                  if (match) {
                                    updateIng(idx, { insumo_name: match.name, insumo_id: match.id, unit: ing.unit || match.unit || "" });
                                  } else {
                                    updateIng(idx, { insumo_name: v, insumo_id: null });
                                  }
                                }}
                                onBlur={async e => {
                                  const v = e.target.value.trim();
                                  if (!v || ing.insumo_id) return;
                                  if (insumos.some(i => ftNrm(i.name) === ftNrm(v))) return;
                                  if (await appConfirm(`Criar novo insumo "${v}" (preço 0, unidade kg)? Você pode editá-lo depois em Insumos.`)) {
                                    const ins = quickAddInsumo(v);
                                    if (ins) updateIng(idx, { insumo_id: ins.id, insumo_name: ins.name, unit: ing.unit || ins.unit });
                                  }
                                }}
                                placeholder="nome do insumo..." style={{...S.input,width:"100%",fontSize:12}} />
                            )}
                          </td>
                          <td style={{padding:"4px 8px"}}>
                            <input type="number" step="0.001" value={ing.qty ?? ""}
                              onChange={e=>{
                                const v = e.target.value;
                                updateIng(idx, { qty: v === "" ? null : parseFloat(v.replace(",",".")) });
                              }}
                              disabled={!!ing.is_qb}
                              style={{...S.input,width:"100%",textAlign:"right",fontFamily:"'DM Mono',monospace",fontSize:12,opacity:ing.is_qb?0.4:1}} />
                          </td>
                          <td style={{padding:"4px 8px"}}>
                            <select value={ing.unit ?? ""} onChange={e=>updateIng(idx, { unit: e.target.value })} style={{...S.input,width:"100%",fontSize:12}} disabled={isSubref}>
                              <option value="">—</option>
                              {FT_CANONICAL_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                              {ing.unit && !FT_CANONICAL_UNITS.includes((ing.unit||"").toLowerCase()) && <option value={ing.unit}>{ing.unit}</option>}
                            </select>
                          </td>
                          <td style={{padding:"4px 8px",textAlign:"center"}}>
                            <input type="checkbox" checked={!!ing.is_qb} onChange={e=>updateIng(idx, { is_qb: e.target.checked || undefined, qty: e.target.checked ? null : ing.qty })} style={{cursor:"pointer",accentColor:miseAc}} />
                          </td>
                          <td style={{padding:"4px 8px"}}>
                            <input type="number" step="0.1" min="0.1" value={ing.fc ?? ""} onChange={e=>{
                              const v = e.target.value;
                              updateIng(idx, { fc: v === "" ? undefined : parseFloat(v) });
                            }} placeholder="1.0" style={{...S.input,width:"100%",textAlign:"right",fontFamily:"'DM Mono',monospace",fontSize:12}} />
                          </td>
                          <td style={{padding:"4px 8px",textAlign:"right",color:costRow?.isReutilizavel?"#a855f7":(costRow?.isSubproduto?"var(--text3)":"var(--text2)"),fontFamily:"'DM Mono',monospace",fontSize:11}}>
                            {ing.is_qb ? "Q.B" : (costRow?.isReutilizavel ? "♻" : (costRow?.isSubproduto ? "0" : (costRow ? fmt(costRow.cost) : "—")))}
                          </td>
                          <td style={{padding:"4px 6px",textAlign:"center",whiteSpace:"nowrap"}}>
                            <button onClick={()=>moveIng(idx,-1)} disabled={idx===0} style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:"var(--text3)",opacity:idx===0?0.3:1,padding:"2px 4px"}}>↑</button>
                            <button onClick={()=>moveIng(idx,1)} disabled={idx===(sf.ingredientes||[]).length-1} style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:"var(--text3)",opacity:idx===(sf.ingredientes||[]).length-1?0.3:1,padding:"2px 4px"}}>↓</button>
                            <button onClick={()=>delIngAt(idx)} style={{background:"none",border:"none",cursor:"pointer",color:"var(--red)",fontSize:14,padding:"2px 4px"}}>×</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Modo de preparo */}
          <div>
            <label style={{...S.label,fontSize:11}}>Modo de preparo</label>
            <textarea value={sf.modo_preparo ?? ""} onChange={e=>updateSubFicha({modo_preparo: e.target.value})} placeholder="Passo a passo..." style={{...S.input,minHeight:70,fontFamily:"inherit",resize:"vertical"}} />
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ──  TEMPERATURAS — Sensores Tuya/SmartLife (PILOTO)             ──
// ═══════════════════════════════════════════════════════════════
// Status helper: compara temp atual com faixa config e retorna cor+label
function tempStatus(temp, minTemp, maxTemp) {
  if (temp == null) return { color: "var(--text3)", bg: "var(--bg2)", label: "Sem leitura", dot: "⚪" };
  if (temp < minTemp || temp > maxTemp) return { color: "var(--red)", bg: "var(--red-bg)", label: "Fora da faixa", dot: "🔴" };
  const margin = (maxTemp - minTemp) * 0.15;
  if (temp < minTemp + margin || temp > maxTemp - margin) return { color: "#f59e0b", bg: "#f59e0b18", label: "Atenção", dot: "🟡" };
  return { color: "var(--green)", bg: "var(--green-bg)", label: "OK", dot: "🟢" };
}

function fmtTemp(t) {
  if (t == null || isNaN(t)) return "—";
  return `${t.toFixed(1)}°C`;
}

function fmtRelMin(iso) {
  if (!iso) return "—";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (diff < 1) return "agora";
  if (diff < 60) return `há ${diff}min`;
  if (diff < 1440) return `há ${Math.floor(diff/60)}h`;
  return `há ${Math.floor(diff/1440)}d`;
}

// ─── HELPERS GLOBAIS DE TEMPERATURAS ──
// Retenção total: 6 meses (atende ANVISA com folga; SP/RJ pedem 6-12)
const TEMP_RETENTION_MS = 6 * 30 * 24 * 60 * 60 * 1000;
// Janela "alta resolução": últimas 24h (mantém 1 leitura por hora)
const TEMP_HIRES_WINDOW_MS = 24 * 60 * 60 * 1000;
// Âncoras de amostragem pra leituras antigas (4 por dia: 00h, 06h, 12h, 18h)
const TEMP_ANCHOR_HOURS = [0, 6, 12, 18];

// Compacta leituras pra caber em 1 doc Firestore (~1 MB):
//   • <24h: mantém 1 leitura por hora (24/dia/sensor)
//   • >24h e <6 meses: mantém 1 leitura próxima a cada âncora (4/dia/sensor)
//   • >6 meses: descarta
// Cálculo final (8 sensores): 24h×24/h×8 + 4/d×178d×8 = 192 + 5696 = ~5900 leituras (~880 KB)
function compactTempReadings(readings) {
  if (!readings || readings.length === 0) return [];
  const now = Date.now();
  const cutoffOld = now - TEMP_RETENTION_MS;
  const cutoffHires = now - TEMP_HIRES_WINDOW_MS;
  // Buckets: chave por (sensor, granularidade)
  const buckets = new Map();
  for (const r of readings) {
    const ts = new Date(r.timestamp).getTime();
    if (isNaN(ts) || ts < cutoffOld) continue; // descarta inválidas e velhas
    let bucketKey;
    if (ts >= cutoffHires) {
      // Alta resolução: 1 por hora cheia
      bucketKey = `${r.sensorId}|H|${Math.floor(ts / (60 * 60 * 1000))}`;
    } else {
      // Baixa resolução: âncora mais próxima do dia
      const d = new Date(ts);
      const dayKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      const hour = d.getHours();
      const anchor = TEMP_ANCHOR_HOURS.reduce((best, a) =>
        Math.abs(hour - a) < Math.abs(hour - best) ? a : best, TEMP_ANCHOR_HOURS[0]);
      bucketKey = `${r.sensorId}|A|${dayKey}|${anchor}`;
    }
    const existing = buckets.get(bucketKey);
    if (!existing) {
      buckets.set(bucketKey, r);
    } else {
      // Mantém a leitura mais próxima do alvo (hora cheia ou âncora)
      const tsExisting = new Date(existing.timestamp).getTime();
      // Pra alta-res: mantém a primeira da hora (já está). Pra baixa-res: mais próxima do anchor.
      if (ts < cutoffHires) {
        const d = new Date(ts);
        const hour = d.getHours();
        const anchor = parseInt(bucketKey.split('|').pop(), 10);
        const dExisting = new Date(tsExisting);
        const distNew = Math.abs(hour - anchor) * 60 + Math.abs(d.getMinutes());
        const distOld = Math.abs(dExisting.getHours() - anchor) * 60 + Math.abs(dExisting.getMinutes());
        if (distNew < distOld) buckets.set(bucketKey, r);
      }
    }
  }
  return [...buckets.values()].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
}

// Dashboard operacional: ver sensores + histórico + alertas (dados reais Tuya)
function OperationalTemperaturas({ restaurantId, restaurantName, tempSensors, tempReadings, tempAlerts, tempBackfillState, tuyaLink, onUpdate, pessoa }) {
  const isMobile = useMobile();
  const ac = "#7c9e5e";
  const activeSensors = (tempSensors || []).filter(s => s.active !== false);
  const openAlerts = (tempAlerts || []).filter(a => !a.closedAt && !a.acknowledgedAt);
  const [polling, setPolling] = useState(false);
  const [lastPollAt, setLastPollAt] = useState(null);
  const [backfilling, setBackfilling] = useState(false); // pull histórico do Tuya
  // eslint-disable-next-line no-unused-vars
  const [lastBackfillAt, setLastBackfillAt] = useState(null); // pra exibir status no futuro
  const [selectedSensorId, setSelectedSensorId] = useState(null); // modal de histórico 72h
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagData, setDiagData] = useState(null);
  const [diagError, setDiagError] = useState(null);

  async function runDiagnostico() {
    if (activeSensors.length === 0) { alert("Sem sensores ativos pra testar."); return; }
    setDiagOpen(true);
    setDiagLoading(true);
    setDiagError(null);
    setDiagData(null);
    try {
      const sensor = activeSensors[0];
      const res = await fetch(`/api/tuya/test-history?device=${encodeURIComponent(sensor.tuyaDeviceId)}&hours=72`);
      const text = await res.text();
      let json;
      try { json = JSON.parse(text); } catch { json = { _rawText: text, _status: res.status }; }
      setDiagData({ sensor, status: res.status, body: json });
    } catch (e) {
      setDiagError(e.message);
    }
    setDiagLoading(false);
  }
  const backfillRanRef = React.useRef(false); // flag pra rodar 1x por mount

  // Última leitura de cada sensor
  const lastReadingBySensor = {};
  (tempReadings || []).forEach(r => {
    const prev = lastReadingBySensor[r.sensorId];
    if (!prev || new Date(r.timestamp) > new Date(prev.timestamp)) {
      lastReadingBySensor[r.sensorId] = r;
    }
  });

  // Busca leitura real de cada sensor ativo via Vercel Function
  async function fetchReadings() {
    if (activeSensors.length === 0 || polling) return;
    setPolling(true);
    const newReadings = [];
    const errors = [];
    await Promise.all(activeSensors.map(async (s) => {
      try {
        const res = await fetch(`/api/tuya/device-status?device=${encodeURIComponent(s.tuyaDeviceId)}`);
        const data = await res.json();
        if (!data.success) {
          errors.push(`${s.name}: ${data.error || 'erro'}`);
          return;
        }
        newReadings.push({
          id: `rd_${Date.now().toString(36)}_${s.id.slice(-4)}_${Math.random().toString(36).slice(2,5)}`,
          sensorId: s.id,
          restaurantId,
          timestamp: data.timestamp,
          temp: data.temp,
          humidity: data.humidity,
          battery: data.battery,
          online: data.online,
        });
      } catch (e) {
        errors.push(`${s.name}: ${e.message}`);
      }
    }));

    if (newReadings.length > 0) {
      // Junta + compacta (alta-res 24h, baixa-res 4/dia até 6 meses)
      const merged = compactTempReadings([...(tempReadings || []), ...newReadings]);
      onUpdate("tempReadings", merged);

      // Detecta 4 tipos de alerta. Email só pra over_max, low_battery e offline.
      const BATTERY_THRESHOLD = 20; // %
      const OFFLINE_HOURS = 12;
      const offlineCutoff = Date.now() - OFFLINE_HOURS * 60 * 60 * 1000;

      const updatedAlerts = [...(tempAlerts || [])];
      const newAlerts = [];
      const alertsToEmail = [];

      activeSensors.forEach(s => {
        // Última leitura deste sensor (merge entre histórico já compactado + novas)
        const allRs = [...merged, ...newReadings].filter(r => r.sensorId === s.id);
        const lastR = allRs.sort((a,b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
        if (!lastR) return;

        // Função helper: abre um alerta se ainda não tem um aberto daquele tipo
        const openAlert = (type, peakValue, extraNote) => {
          const existingOpen = updatedAlerts.find(a => a.sensorId === s.id && a.type === type && !a.closedAt);
          if (existingOpen) {
            // Atualiza peakTemp se é over_max e pior que antes
            if (type === "over_max" && peakValue != null && peakValue > existingOpen.peakTemp) {
              existingOpen.peakTemp = peakValue;
            }
            return;
          }
          const al = {
            id: `al_${Date.now().toString(36)}_${s.id.slice(-4)}_${type.slice(0,3)}`,
            sensorId: s.id,
            restaurantId,
            type,
            openedAt: lastR.timestamp,
            firstTemp: lastR.temp ?? null,
            peakTemp: peakValue ?? lastR.temp ?? null,
            note: extraNote || "",
          };
          newAlerts.push(al);
          updatedAlerts.push(al);
          if (alertTypeSendsEmail(type)) alertsToEmail.push({ alert: al, sensor: s, reading: lastR });
        };

        // Função helper: fecha alertas abertos de um tipo
        const closeAlerts = (type) => {
          updatedAlerts.forEach(a => {
            if (a.sensorId === s.id && a.type === type && !a.closedAt) {
              a.closedAt = lastR.timestamp;
            }
          });
        };

        // 1) Offline > 12h
        if (lastR.online === false || new Date(lastR.timestamp).getTime() < offlineCutoff) {
          openAlert("offline");
        } else {
          closeAlerts("offline");
        }

        // 2) Bateria baixa
        if (lastR.battery != null && lastR.battery <= BATTERY_THRESHOLD) {
          openAlert("low_battery");
        } else if (lastR.battery != null && lastR.battery > BATTERY_THRESHOLD + 5) {
          // só fecha quando bateria subir além do threshold + histerese (troca de pilha)
          closeAlerts("low_battery");
        }

        // 3) Temperatura acima do máximo (crítico)
        if (lastR.temp != null && lastR.temp > s.maxTemp) {
          openAlert("over_max", lastR.temp);
        } else {
          closeAlerts("over_max");
        }

        // 4) Temperatura abaixo do mínimo (info — só registra, não manda email)
        if (lastR.temp != null && lastR.temp < s.minTemp) {
          openAlert("under_min", lastR.temp);
        } else {
          closeAlerts("under_min");
        }
      });

      if (newAlerts.length > 0 || updatedAlerts.some(a => a.closedAt && a.closedAt === newReadings[0]?.timestamp)) {
        onUpdate("tempAlerts", updatedAlerts);
      }

      // Dispara emails (se configurado) — fire-and-forget
      if (alertsToEmail.length > 0 && tuyaLink?.alertEmail) {
        alertsToEmail.forEach(({ alert, sensor, reading }) => {
          fetch("/api/alerts/send-email", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              to: tuyaLink.alertEmail,
              subject: `${ALERT_TYPES[alert.type]?.icon || "⚠️"} ${ALERT_TYPES[alert.type]?.label || "Alerta"} — ${sensor.name}`,
              html: buildAlertEmailHtml({
                restaurantName: restaurantName || "Restaurante",
                sensorName: sensor.name,
                location: sensor.location || "",
                currentTemp: reading.temp,
                minTemp: sensor.minTemp,
                maxTemp: sensor.maxTemp,
                when: new Date(reading.timestamp),
                alertType: alert.type,
                extraInfo: {
                  battery: reading.battery,
                  lastSeen: new Date(reading.timestamp).toLocaleString("pt-BR"),
                },
              }),
            }),
          }).catch(e => console.warn("Falha envio email alerta:", e));
        });
      }

      const criticalCount = newAlerts.filter(a => a.type === "over_max").length;
      const warningCount = newAlerts.filter(a => a.type === "low_battery" || a.type === "offline").length;
      if (criticalCount > 0) onUpdate("_toast", `🔴 ${criticalCount} alerta(s) crítico(s) novo(s)`);
      else if (warningCount > 0) onUpdate("_toast", `🟡 ${warningCount} alerta(s) de atenção novo(s)`);
    }

    if (errors.length > 0) {
      onUpdate("_toast", `⚠️ ${errors.length} sensor(es) com erro — ver console`);
      console.warn("Erros no fetchReadings:", errors);
    }
    setLastPollAt(new Date().toISOString());
    setPolling(false);
  }

  // ── BACKFILL DO HISTÓRICO TUYA ──
  // Puxa do servidor Tuya as leituras que aconteceram enquanto NINGUÉM estava com a tela aberta.
  // Roda 1x por mount, pra cada sensor ativo, do último timestamp salvo até agora (limitado a 7 dias).
  async function backfillHistory() {
    if (activeSensors.length === 0 || backfilling) return;
    setBackfilling(true);
    const now = Date.now();
    const MAX_RANGE = 7 * 24 * 60 * 60 * 1000; // Tuya limita a 7 dias por request
    const allNew = []; // leituras novas pra mesclar
    const errors = [];
    console.log("[backfill] 🚀 iniciando — sensores:", activeSensors.length);

    await Promise.all(activeSensors.map(async (s) => {
      try {
        // Última leitura salva desse sensor (pra saber de onde retomar)
        const sensorReadings = (tempReadings || []).filter(r => r.sensorId === s.id);
        const lastSaved = sensorReadings.reduce((max, r) => {
          const t = new Date(r.timestamp).getTime();
          return t > max ? t : max;
        }, 0);
        // Se nunca teve leitura ou a última foi > 7d atrás, puxa só os últimos 7d
        const fromMs = Math.max(lastSaved + 1000, now - MAX_RANGE);
        console.log(`[backfill] 📡 ${s.name} (device=${s.tuyaDeviceId}): from=${new Date(fromMs).toISOString()} to=${new Date(now).toISOString()} (${((now-fromMs)/3600000).toFixed(1)}h)`);
        // Se não há buraco pra preencher (tela acabou de ser atualizada), pula
        if (fromMs >= now - 60_000) {
          console.log(`[backfill]   ⏭️ pulando — sem buraco pra preencher`);
          return;
        }

        let lastRowKey = null;
        let pages = 0;
        // Paginação: até 5 páginas (500 leituras max por sensor por chamada)
        do {
          const params = new URLSearchParams({
            device: s.tuyaDeviceId,
            from: String(fromMs),
            to: String(now),
            size: "100",
          });
          if (lastRowKey) params.set("lastRowKey", lastRowKey);
          const res = await fetch(`/api/tuya/device-history?${params.toString()}`);
          const data = await res.json();
          console.log(`[backfill]   📥 ${s.name} pg${pages+1}: status=${res.status} success=${data.success} readings=${data.readings?.length || 0} hasMore=${data.hasMore}`);
          if (!data.success) {
            console.error(`[backfill]   ❌ erro:`, data.error, "tuyaCode:", data.tuyaCode);
            errors.push(`${s.name}: ${data.error || 'erro'}`);
            break;
          }
          if ((data.readings?.length || 0) > 0) {
            console.log(`[backfill]   📊 primeiras 3 leituras:`, data.readings.slice(0, 3));
          }
          // Agrupa logs por timestamp pra montar 1 leitura por momento (logs vêm separados por code)
          const byTs = new Map();
          (data.readings || []).forEach(log => {
            const ts = log.timestampMs;
            if (!byTs.has(ts)) byTs.set(ts, { temp: null, humidity: null, battery: null });
            const cur = byTs.get(ts);
            if (log.temp != null) cur.temp = log.temp;
            if (log.humidity != null) cur.humidity = log.humidity;
            if (log.battery != null) cur.battery = log.battery;
          });
          // Materializa as leituras
          [...byTs.entries()].forEach(([ts, vals]) => {
            // Só inclui se pelo menos a temp foi reportada
            if (vals.temp == null) return;
            allNew.push({
              id: `rd_${ts.toString(36)}_${s.id.slice(-4)}_h`,
              sensorId: s.id,
              restaurantId,
              timestamp: new Date(ts).toISOString(),
              temp: vals.temp,
              humidity: vals.humidity,
              battery: vals.battery,
              online: true,
              source: "tuya_history", // marca pra audit
            });
          });
          lastRowKey = data.hasMore ? data.lastRowKey : null;
          pages += 1;
        } while (lastRowKey && pages < 5);
      } catch (e) {
        errors.push(`${s.name}: ${e.message}`);
      }
    }));

    console.log(`[backfill] 🧮 total acumulado antes do dedupe:`, allNew.length, "leituras");
    if (allNew.length > 0) {
      // Dedupe contra leituras já salvas (mesmo sensor + mesmo timestamp ≈)
      const existingKeys = new Set(
        (tempReadings || []).map(r => `${r.sensorId}|${r.timestamp}`)
      );
      const toAdd = allNew.filter(r => !existingKeys.has(`${r.sensorId}|${r.timestamp}`));
      console.log(`[backfill] 🧹 após dedupe:`, toAdd.length, "novas (de", allNew.length, "totais)");
      if (toAdd.length > 0) {
        // Junta tudo e compacta (alta-res 24h, baixa-res 4/dia, retenção 6 meses)
        const merged = compactTempReadings([...(tempReadings || []), ...toAdd]);
        console.log(`[backfill] 💾 salvando — total final no banco (após compactar):`, merged.length);
        onUpdate("tempReadings", merged);
        onUpdate("_toast", `📥 ${toAdd.length} leitura(s) histórica(s) recuperada(s) do Tuya`);
      } else {
        console.log(`[backfill] ⏸️ nada novo após dedupe — todas as leituras já estavam salvas`);
      }
    } else {
      console.log(`[backfill] ⚠️ NENHUMA leitura veio do Tuya — verifique configuração`);
    }
    if (errors.length > 0) console.warn("[backfill] erros:", errors);
    console.log(`[backfill] ✅ fim`);
    setLastBackfillAt(new Date().toISOString());
    // Persiste timestamp do último backfill no banco — usado pra agendamento de 3 em 3 dias
    onUpdate("tempBackfillState", (prev) => ({
      ...(prev || {}),
      [restaurantId]: {
        lastBackfillAt: new Date().toISOString(),
        lastBackfillBy: pessoa?.name || null,
        readingsAdded: allNew.length,
      },
    }));
    setBackfilling(false);
  }

  // Auto-polling a cada 5 minutos enquanto a tela está montada + backfill agendado
  useEffect(() => {
    if (activeSensors.length === 0) return;

    // BACKFILL AGENDADO: roda 1x por mount SE faz mais de 3 dias desde o último.
    // Persiste timestamp em tempBackfillState pra qualquer user que abrir a tela
    // dispare o próximo só quando vencer (evita Tuya quota burning toda hora).
    if (!backfillRanRef.current) {
      backfillRanRef.current = true;
      const lastIso = tempBackfillState?.[restaurantId]?.lastBackfillAt;
      const lastMs = lastIso ? new Date(lastIso).getTime() : 0;
      const daysSince = (Date.now() - lastMs) / (24 * 60 * 60 * 1000);
      const SCHEDULE_INTERVAL_DAYS = 3;
      if (!lastIso || daysSince >= SCHEDULE_INTERVAL_DAYS) {
        console.log(`[backfill agendado] disparando — última: ${lastIso || "nunca"} (${daysSince.toFixed(1)}d atrás)`);
        backfillHistory();
      } else {
        console.log(`[backfill agendado] pulando — última há ${daysSince.toFixed(1)}d (próxima em ${(SCHEDULE_INTERVAL_DAYS - daysSince).toFixed(1)}d)`);
      }
    }

    // Primeira leitura "agora" (em paralelo com backfill): se não tem dos últimos 5min, busca
    const freshCutoff = Date.now() - 5 * 60 * 1000;
    const hasFresh = Object.values(lastReadingBySensor).some(r => new Date(r.timestamp).getTime() > freshCutoff);
    if (!hasFresh) fetchReadings();

    const interval = setInterval(fetchReadings, 5 * 60 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSensors.length]);

  const [showExport, setShowExport] = useState(false);

  function acknowledgeAlert(alertId) {
    const updated = (tempAlerts || []).map(a => a.id === alertId ? {
      ...a, acknowledgedAt: new Date().toISOString(), acknowledgedBy: pessoa?.name || "—",
    } : a);
    onUpdate("tempAlerts", updated);
  }

  // Empty states
  if (!tuyaLink) {
    return (
      <div style={{padding:isMobile?"30px 16px":"60px 24px",textAlign:"center",color:"var(--text3)"}}>
        <div style={{fontSize:48,marginBottom:12}}>🌡️</div>
        <h3 style={{color:"var(--text)",fontSize:isMobile?16:18,fontWeight:700,margin:"0 0 8px"}}>Nenhuma conta SmartLife vinculada</h3>
        <p style={{fontSize:13,lineHeight:1.6,maxWidth:420,margin:"0 auto"}}>
          Peça ao gestor pra ir em <b>Temperaturas → Configurar</b> e vincular a conta SmartLife do restaurante.
        </p>
      </div>
    );
  }
  if (activeSensors.length === 0) {
    return (
      <div style={{padding:isMobile?"30px 16px":"60px 24px",textAlign:"center",color:"var(--text3)"}}>
        <div style={{fontSize:48,marginBottom:12}}>📡</div>
        <h3 style={{color:"var(--text)",fontSize:isMobile?16:18,fontWeight:700,margin:"0 0 8px"}}>Nenhum sensor cadastrado</h3>
        <p style={{fontSize:13,lineHeight:1.6,maxWidth:420,margin:"0 auto"}}>
          A conta SmartLife está vinculada mas nenhum sensor foi cadastrado ainda. Peça ao gestor pra ir em <b>Temperaturas → Configurar</b>.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,marginBottom:14,flexWrap:"wrap"}}>
        <div>
          <div style={{fontSize:11,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Monitoramento — tempo real</div>
          <div style={{fontSize:isMobile?14:16,color:"var(--text)",fontWeight:700,marginTop:2}}>{activeSensors.length} sensor{activeSensors.length!==1?"es":""} · {openAlerts.length} alerta{openAlerts.length!==1?"s":""} ativo{openAlerts.length!==1?"s":""}</div>
        </div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          <button onClick={fetchReadings} disabled={polling}
            style={{background:polling?"var(--bg3)":ac+"22",border:`1px solid ${polling?"var(--border)":ac+"66"}`,borderRadius:8,padding:"8px 14px",fontSize:12,fontWeight:700,color:polling?"var(--text3)":ac,cursor:polling?"wait":"pointer",fontFamily:"'DM Sans',sans-serif",minHeight:isMobile?44:"auto"}}>
            {polling ? "🔄 Atualizando…" : "🔄 Atualizar"}
          </button>
          <button onClick={() => { backfillRanRef.current = true; backfillHistory(); }} disabled={backfilling}
            title="Recupera leituras históricas do servidor Tuya (preenche buracos enquanto sistema esteve fechado)"
            style={{background:backfilling?"var(--bg3)":"transparent",border:"1px solid #6366f166",borderRadius:8,padding:"8px 14px",fontSize:12,fontWeight:700,color:backfilling?"var(--text3)":"#6366f1",cursor:backfilling?"wait":"pointer",fontFamily:"'DM Sans',sans-serif",minHeight:isMobile?44:"auto"}}>
            {backfilling ? "📥 Buscando…" : "📥 Histórico Tuya"}
          </button>
          <button onClick={runDiagnostico}
            title="Testa o que o Tuya está retornando — útil pra debug quando histórico vem vazio"
            style={{background:"transparent",border:"1px dashed #f59e0b66",borderRadius:8,padding:"8px 14px",fontSize:12,fontWeight:700,color:"#f59e0b",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",minHeight:isMobile?44:"auto"}}>
            🔧 Diagnosticar Tuya
          </button>
          <button onClick={()=>setShowExport(true)}
            style={{background:"transparent",border:"1px solid var(--border)",borderRadius:8,padding:"8px 14px",fontSize:12,fontWeight:700,color:"var(--text2)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",minHeight:isMobile?44:"auto"}}>
            📤 Exportar
          </button>
        </div>
      </div>

      {/* Alertas ativos */}
      {openAlerts.length > 0 && (
        <div style={{background:"var(--red-bg)",border:"1px solid var(--red)44",borderRadius:12,padding:"12px 14px",marginBottom:14}}>
          <div style={{fontSize:12,color:"var(--red)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,marginBottom:8}}>⚠️ Alertas ativos</div>
          {openAlerts.map(a => {
            const s = activeSensors.find(x => x.id === a.sensorId);
            return (
              <div key={a.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderTop:"1px solid var(--red)22"}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,color:"var(--text)",fontWeight:600}}>{s?.name || "Sensor removido"}</div>
                  <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>Pico {fmtTemp(a.peakTemp)} · iniciou {fmtRelMin(a.openedAt)}</div>
                </div>
                <button onClick={()=>acknowledgeAlert(a.id)}
                  style={{background:"var(--red)",color:"#fff",border:"none",borderRadius:6,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                  Reconhecer
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Grid de sensores */}
      <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(auto-fit,minmax(240px,1fr))",gap:12}}>
        {activeSensors.map(s => {
          const reading = lastReadingBySensor[s.id];
          const status = tempStatus(reading?.temp, s.minTemp, s.maxTemp);
          const offline = reading && reading.online === false;
          return (
            <div key={s.id}
              onClick={()=>setSelectedSensorId(s.id)}
              title="Clique pra ver histórico das últimas 72h"
              style={{background:"var(--card-bg)",border:`1px solid ${status.color}33`,borderRadius:12,padding:"14px 16px",position:"relative",cursor:"pointer",transition:"transform 0.1s,box-shadow 0.1s"}}
              onMouseEnter={e=>{ e.currentTarget.style.transform="translateY(-1px)"; e.currentTarget.style.boxShadow="0 2px 8px rgba(0,0,0,0.06)"; }}
              onMouseLeave={e=>{ e.currentTarget.style.transform="none"; e.currentTarget.style.boxShadow="none"; }}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,color:"var(--text)",fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.name}</div>
                  {s.location && <div style={{fontSize:10,color:"var(--text3)",marginTop:1}}>{s.location}</div>}
                </div>
                <span style={{fontSize:14}} title={status.label}>{status.dot}</span>
              </div>
              <div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:6}}>
                <div style={{fontSize:32,fontWeight:700,color:status.color,fontFamily:"'DM Mono',monospace",lineHeight:1}}>{fmtTemp(reading?.temp)}</div>
                <div style={{fontSize:11,color:"var(--text3)"}}>faixa {s.minTemp}°–{s.maxTemp}°</div>
              </div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",fontSize:10,color:"var(--text3)"}}>
                <span>{reading ? `${fmtRelMin(reading.timestamp)}` : "aguardando leitura"}</span>
                {reading?.battery != null && <span>🔋 {reading.battery}%</span>}
                {offline && <span style={{color:"var(--red)",fontWeight:700}}>offline</span>}
              </div>
              <div style={{textAlign:"center",fontSize:9,color:"var(--text3)",marginTop:6,opacity:0.6}}>📈 ver histórico 72h →</div>
            </div>
          );
        })}
      </div>

      {/* Modal de DIAGNÓSTICO do Tuya — debug pra ver por que histórico vem vazio */}
      {diagOpen && (
        <div onClick={()=>setDiagOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:isMobile?12:24}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"var(--card-bg)",borderRadius:12,padding:isMobile?"16px":"20px 24px",maxWidth:820,width:"100%",maxHeight:"92vh",overflowY:"auto",border:"1px solid var(--border)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,gap:8}}>
              <div>
                <div style={{fontSize:16,fontWeight:700,color:"var(--text)"}}>🔧 Diagnóstico Tuya</div>
                <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>
                  Resposta crua do Tuya pro sensor "{diagData?.sensor?.name || activeSensors[0]?.name}" — últimas 72h
                </div>
              </div>
              <button onClick={()=>setDiagOpen(false)} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:"var(--text3)",padding:0}}>✕</button>
            </div>
            {diagLoading && <div style={{padding:"30px",textAlign:"center",color:"var(--text3)"}}>📡 Consultando Tuya…</div>}
            {diagError && <div style={{padding:14,background:"#ef444415",border:"1px solid #ef444444",borderRadius:8,color:"var(--red)",fontSize:13}}>❌ Erro: {diagError}</div>}
            {diagData && (
              <>
                <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap"}}>
                  <button onClick={async()=>{
                    try {
                      const s = JSON.stringify(diagData.body, null, 2);
                      await navigator.clipboard.writeText(s);
                      onUpdate("_toast", "📋 JSON copiado pra área de transferência");
                    } catch (e) {
                      onUpdate("_toast", "⚠️ Não foi possível copiar — selecione o texto manualmente");
                    }
                  }} style={{background:"var(--ac)",color:"#fff",border:"none",borderRadius:6,padding:"6px 12px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                    📋 Copiar JSON
                  </button>
                  <span style={{fontSize:11,color:"var(--text3)",alignSelf:"center"}}>
                    Status HTTP: <strong style={{color:diagData.status===200?"var(--green)":"var(--red)"}}>{diagData.status}</strong>
                  </span>
                </div>
                {/* Resumo legível */}
                {diagData.body?.statusCodes && (
                  <div style={{marginBottom:12,padding:10,background:"var(--bg1)",borderRadius:8,fontSize:11}}>
                    <div style={{fontWeight:700,marginBottom:4,color:"var(--text2)"}}>📊 Codes que o sensor reporta agora:</div>
                    <div style={{fontFamily:"'DM Mono',monospace",color:"var(--text)"}}>
                      {diagData.body.statusCodes.map(c => `${c.code} = ${JSON.stringify(c.value)}`).join(" · ")}
                    </div>
                  </div>
                )}
                {diagData.body?.historyAttempts && (
                  <div style={{marginBottom:12,padding:10,background:"var(--bg1)",borderRadius:8,fontSize:11}}>
                    <div style={{fontWeight:700,marginBottom:6,color:"var(--text2)"}}>📜 Tentativas de buscar histórico (4 listas de codes):</div>
                    {diagData.body.historyAttempts.map((a, i) => (
                      <div key={i} style={{padding:"4px 0",borderTop:i?"1px solid var(--border)":"none"}}>
                        <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--text3)",wordBreak:"break-all"}}>{a.codes_query}</div>
                        <div style={{marginTop:2}}>
                          {a.error ? (
                            <span style={{color:"var(--red)"}}>❌ {a.error}</span>
                          ) : a.total_logs === 0 ? (
                            <span style={{color:"#f59e0b"}}>⚠️ retornou 0 leituras</span>
                          ) : (
                            <span style={{color:"var(--green)"}}>✅ {a.total_logs} leituras · codes: {a.unique_codes_in_response?.join(", ")}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {/* JSON cru */}
                <details>
                  <summary style={{cursor:"pointer",fontSize:11,color:"var(--text3)",padding:"6px 0"}}>📄 Ver JSON completo</summary>
                  <pre style={{background:"var(--bg2)",padding:12,borderRadius:8,fontSize:10,overflow:"auto",maxHeight:400,lineHeight:1.4,fontFamily:"'DM Mono',monospace"}}>{JSON.stringify(diagData.body, null, 2)}</pre>
                </details>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal de histórico 72h por sensor */}
      {selectedSensorId && (
        <SensorHistoryModal
          sensor={activeSensors.find(s => s.id === selectedSensorId)}
          allReadings={tempReadings || []}
          onClose={()=>setSelectedSensorId(null)}
          onUpdate={onUpdate}
          restaurantId={restaurantId}
          isMobile={isMobile}
          ac={ac}
        />
      )}

      <div style={{marginTop:18,fontSize:11,color:"var(--text3)",lineHeight:1.5,textAlign:"center"}}>
        🟢 Leituras reais do Tuya Cloud. Atualização automática a cada 5 min enquanto esta tela está aberta.
        {lastPollAt && <> Última: {fmtRelMin(lastPollAt)}.</>}
      </div>

      {showExport && (
        <TemperaturasExportModal
          sensors={activeSensors}
          readings={tempReadings || []}
          alerts={tempAlerts || []}
          restaurantId={restaurantId}
          onClose={()=>setShowExport(false)}
          ac={ac}
          isMobile={isMobile}
        />
      )}
    </div>
  );
}

// ─── Modal de histórico de 72h por sensor (gráfico SVG) ──
// Ao abrir, dispara backfill de 72h pra preencher buracos.
function SensorHistoryModal({ sensor, allReadings, onClose, onUpdate, restaurantId, isMobile, ac }) {
  const [loading, setLoading] = useState(false);
  const [hoursToShow, setHoursToShow] = useState(72);
  const ranBackfill = React.useRef(false);

  // Filtra leituras do sensor + últimas 72h
  const cutoffMs = Date.now() - hoursToShow * 60 * 60 * 1000;
  const sensorReadings = (allReadings || [])
    .filter(r => r.sensorId === sensor?.id && new Date(r.timestamp).getTime() >= cutoffMs)
    .sort((a,b) => new Date(a.timestamp) - new Date(b.timestamp));

  // Backfill específico (72h) ao abrir o modal — preenche buracos pra esse sensor só
  useEffect(() => {
    if (!sensor || ranBackfill.current) return;
    ranBackfill.current = true;
    (async () => {
      setLoading(true);
      try {
        // Última leitura desse sensor pra saber de onde retomar
        const lastSaved = sensorReadings.reduce((max, r) => Math.max(max, new Date(r.timestamp).getTime()), 0);
        const fromMs = Math.max(lastSaved + 1000, Date.now() - 72 * 60 * 60 * 1000);
        if (fromMs >= Date.now() - 60_000) { setLoading(false); return; }

        const newOnes = [];
        let lastRowKey = null;
        let pages = 0;
        do {
          const params = new URLSearchParams({
            device: sensor.tuyaDeviceId,
            from: String(fromMs),
            to: String(Date.now()),
            size: "100",
          });
          if (lastRowKey) params.set("lastRowKey", lastRowKey);
          const res = await fetch(`/api/tuya/device-history?${params.toString()}`);
          const data = await res.json();
          if (!data.success) break;
          const byTs = new Map();
          (data.readings || []).forEach(log => {
            const ts = log.timestampMs;
            if (!byTs.has(ts)) byTs.set(ts, { temp: null, humidity: null, battery: null });
            const cur = byTs.get(ts);
            if (log.temp != null) cur.temp = log.temp;
            if (log.humidity != null) cur.humidity = log.humidity;
            if (log.battery != null) cur.battery = log.battery;
          });
          [...byTs.entries()].forEach(([ts, vals]) => {
            if (vals.temp == null) return;
            newOnes.push({
              id: `rd_${ts.toString(36)}_${sensor.id.slice(-4)}_h`,
              sensorId: sensor.id,
              restaurantId,
              timestamp: new Date(ts).toISOString(),
              temp: vals.temp,
              humidity: vals.humidity,
              battery: vals.battery,
              online: true,
              source: "tuya_history",
            });
          });
          lastRowKey = data.hasMore ? data.lastRowKey : null;
          pages += 1;
        } while (lastRowKey && pages < 5);

        if (newOnes.length > 0) {
          const existingKeys = new Set((allReadings || []).map(r => `${r.sensorId}|${r.timestamp}`));
          const toAdd = newOnes.filter(r => !existingKeys.has(`${r.sensorId}|${r.timestamp}`));
          if (toAdd.length > 0) {
            const merged = compactTempReadings([...(allReadings || []), ...toAdd]);
            onUpdate("tempReadings", merged);
          }
        }
      } catch (e) {
        console.warn("[SensorHistoryModal backfill]", e);
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sensor?.id]);

  if (!sensor) return null;

  // Estatísticas
  const temps = sensorReadings.map(r => r.temp).filter(t => t != null);
  const minT = temps.length ? Math.min(...temps) : null;
  const maxT = temps.length ? Math.max(...temps) : null;
  const avgT = temps.length ? (temps.reduce((s,t)=>s+t,0) / temps.length) : null;
  const outOfRange = sensorReadings.filter(r => r.temp != null && (r.temp < sensor.minTemp || r.temp > sensor.maxTemp)).length;
  const pctOutOfRange = sensorReadings.length ? ((outOfRange / sensorReadings.length) * 100).toFixed(1) : "0";

  // Gráfico SVG — últimas 72h
  const W = isMobile ? 320 : 720;
  const H = 220;
  const padL = 36, padR = 12, padT = 12, padB = 28;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const tNow = Date.now();
  const tFrom = tNow - hoursToShow * 60 * 60 * 1000;
  // Faixa de Y: usa min/max do sensor + margem 20%
  const dataMin = Math.min(sensor.minTemp, ...(temps.length ? temps : [sensor.minTemp]));
  const dataMax = Math.max(sensor.maxTemp, ...(temps.length ? temps : [sensor.maxTemp]));
  const margin = (dataMax - dataMin) * 0.2 || 2;
  const yMin = dataMin - margin;
  const yMax = dataMax + margin;
  const xOf = (ts) => padL + ((ts - tFrom) / (tNow - tFrom)) * plotW;
  const yOf = (t) => padT + plotH - ((t - yMin) / (yMax - yMin)) * plotH;

  const linePoints = sensorReadings.map(r => {
    const x = xOf(new Date(r.timestamp).getTime());
    const y = yOf(r.temp);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");

  // Faixa permitida (banda verde)
  const yMinSafe = yOf(sensor.maxTemp);
  const yMaxSafe = yOf(sensor.minTemp);

  // Ticks do eixo X (a cada 12h)
  const xTicks = [];
  for (let h = 0; h <= hoursToShow; h += 12) {
    const ts = tFrom + h * 60 * 60 * 1000;
    const d = new Date(ts);
    const lbl = `${String(d.getHours()).padStart(2,"0")}h`;
    xTicks.push({ x: xOf(ts), label: lbl, isToday: d.toDateString() === new Date().toDateString() });
  }

  return (
    <div onClick={onClose}
      style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:isMobile?12:24}}>
      <div onClick={e=>e.stopPropagation()}
        style={{background:"var(--card-bg)",borderRadius:12,padding:isMobile?"16px":"20px 24px",maxWidth:780,width:"100%",maxHeight:"92vh",overflowY:"auto",border:"1px solid var(--border)"}}>

        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12,gap:8}}>
          <div>
            <div style={{fontSize:16,fontWeight:700,color:"var(--text)"}}>📈 {sensor.name}</div>
            {sensor.location && <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>{sensor.location}</div>}
            <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>Faixa permitida: {sensor.minTemp}°C a {sensor.maxTemp}°C</div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:"var(--text3)",padding:0}}>✕</button>
        </div>

        {/* Botões de janela de tempo */}
        <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap"}}>
          {[24, 48, 72, 168].map(h => (
            <button key={h} onClick={()=>setHoursToShow(h)}
              style={{
                padding:"5px 12px",borderRadius:6,border:`1px solid ${hoursToShow===h?ac:"var(--border)"}`,
                background:hoursToShow===h?ac+"22":"transparent",color:hoursToShow===h?ac:"var(--text2)",
                cursor:"pointer",fontSize:11,fontWeight:600,
              }}>
              {h===168?"7 dias":`${h}h`}
            </button>
          ))}
          {loading && <span style={{fontSize:11,color:"var(--text3)",alignSelf:"center"}}>📥 Buscando histórico do Tuya…</span>}
        </div>

        {/* KPIs */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(4, 1fr)",gap:6,marginBottom:12}}>
          {[
            { lbl: "Mínima", val: minT != null ? `${minT.toFixed(1)}°C` : "—", color: "#3b82f6" },
            { lbl: "Média", val: avgT != null ? `${avgT.toFixed(1)}°C` : "—", color: "var(--text)" },
            { lbl: "Máxima", val: maxT != null ? `${maxT.toFixed(1)}°C` : "—", color: "var(--red)" },
            { lbl: "Fora faixa", val: `${pctOutOfRange}%`, color: outOfRange > 0 ? "var(--red)" : "var(--green)" },
          ].map((k, i) => (
            <div key={i} style={{background:"var(--bg1)",borderRadius:8,padding:"6px 8px",textAlign:"center"}}>
              <div style={{fontSize:9,color:"var(--text3)",textTransform:"uppercase",letterSpacing:0.3}}>{k.lbl}</div>
              <div style={{fontSize:13,fontWeight:700,color:k.color,fontFamily:"'DM Mono',monospace",marginTop:2}}>{k.val}</div>
            </div>
          ))}
        </div>

        {/* Gráfico SVG */}
        {sensorReadings.length === 0 ? (
          <div style={{padding:"40px 20px",textAlign:"center",color:"var(--text3)",background:"var(--bg1)",borderRadius:8,fontSize:13}}>
            {loading ? "📥 Carregando histórico…" : "📭 Sem leituras nas últimas " + hoursToShow + "h"}
          </div>
        ) : (
          <div style={{background:"var(--bg1)",borderRadius:8,padding:8,overflowX:"auto"}}>
            <svg width={W} height={H} style={{display:"block",margin:"0 auto"}}>
              {/* Banda da faixa permitida (verde) */}
              <rect x={padL} y={yMinSafe} width={plotW} height={yMaxSafe - yMinSafe}
                fill="#10b98115" stroke="#10b98144" strokeDasharray="3,3" />
              {/* Linhas de min/max */}
              <line x1={padL} y1={yOf(sensor.minTemp)} x2={padL+plotW} y2={yOf(sensor.minTemp)} stroke="#10b981" strokeDasharray="2,2" strokeWidth={1}/>
              <line x1={padL} y1={yOf(sensor.maxTemp)} x2={padL+plotW} y2={yOf(sensor.maxTemp)} stroke="#10b981" strokeDasharray="2,2" strokeWidth={1}/>

              {/* Eixo Y — ticks */}
              {[yMin, (yMin+yMax)/2, yMax].map((t, i) => (
                <g key={i}>
                  <line x1={padL} y1={yOf(t)} x2={padL+plotW} y2={yOf(t)} stroke="var(--border)" strokeWidth={0.5} opacity={0.4}/>
                  <text x={padL-4} y={yOf(t)+3} fontSize={9} fill="var(--text3)" textAnchor="end" fontFamily="'DM Mono',monospace">{t.toFixed(0)}°</text>
                </g>
              ))}

              {/* Eixo X — ticks */}
              {xTicks.map((tk, i) => (
                <g key={i}>
                  <line x1={tk.x} y1={padT+plotH} x2={tk.x} y2={padT+plotH+3} stroke="var(--text3)" strokeWidth={0.5}/>
                  <text x={tk.x} y={padT+plotH+14} fontSize={9} fill="var(--text3)" textAnchor="middle" fontFamily="'DM Mono',monospace">{tk.label}</text>
                </g>
              ))}

              {/* Linha de leituras */}
              {linePoints && (
                <polyline points={linePoints} fill="none" stroke={ac} strokeWidth={1.5} strokeLinejoin="round"/>
              )}

              {/* Pontos com cor por status */}
              {sensorReadings.map((r, i) => {
                const x = xOf(new Date(r.timestamp).getTime());
                const y = yOf(r.temp);
                const out = r.temp < sensor.minTemp || r.temp > sensor.maxTemp;
                return <circle key={i} cx={x} cy={y} r={1.8} fill={out ? "var(--red)" : ac} />;
              })}
            </svg>
          </div>
        )}

        <div style={{marginTop:10,fontSize:10,color:"var(--text3)",textAlign:"center"}}>
          {sensorReadings.length} leitura(s) nas últimas {hoursToShow}h. Banda verde = faixa permitida ({sensor.minTemp}°–{sensor.maxTemp}°). Pontos vermelhos = fora da faixa.
        </div>
      </div>
    </div>
  );
}

// Modal de exportação — gera XLSX em formato pivot (horário × sensor) em paisagem
function TemperaturasExportModal({ sensors, readings, alerts, restaurantId, onClose, ac, isMobile }) {
  // Default: últimos 7 dias
  const today = new Date();
  const weekAgo = new Date(today.getTime() - 7*24*60*60*1000);
  const toDate = (d) => d.toISOString().slice(0, 10);

  const [from, setFrom] = useState(toDate(weekAgo));
  const [to, setTo] = useState(toDate(today));
  const [selected, setSelected] = useState(() => {
    const s = {};
    sensors.forEach(sn => s[sn.id] = true);
    return s;
  });
  const [generating, setGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null); // blob URL quando em preview
  const [previewFname, setPreviewFname] = useState("");

  // Limpa blob URL ao fechar pra não vazar memória
  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  const selectedSensors = sensors.filter(s => selected[s.id]);

  function toggleSensor(id) {
    setSelected(s => ({ ...s, [id]: !s[id] }));
  }

  function toggleAll() {
    const allOn = sensors.every(s => selected[s.id]);
    const next = {};
    sensors.forEach(s => next[s.id] = !allOn);
    setSelected(next);
  }

  function doExport() {
    if (selectedSensors.length === 0) { alert("Selecione pelo menos 1 sensor"); return; }
    if (!window.jspdf || !window.jspdf.jsPDF) { alert("Biblioteca PDF ainda carregando, tenta de novo em 2s"); return; }
    setGenerating(true);
    try {
      const fromMs = new Date(from + "T00:00:00").getTime();
      const toMs   = new Date(to   + "T23:59:59").getTime();

      // Filtra leituras no período + sensores selecionados
      const relevant = readings.filter(r => {
        if (!selected[r.sensorId]) return false;
        const t = new Date(r.timestamp).getTime();
        return t >= fromMs && t <= toMs;
      });

      if (relevant.length === 0) {
        alert("Nenhuma leitura encontrada no período selecionado.");
        setGenerating(false);
        return;
      }

      // Agrupa leituras em buckets de 3h (sensorId, slotKey=YYYY-MM-DD HH) → [temps]
      // Slots: 00, 03, 06, 09, 12, 15, 18, 21. Cada leitura cai no slot do seu horário.
      const SLOT_HOURS = 3;
      const SLOTS_PER_DAY = 24 / SLOT_HOURS; // 8
      const buckets = {};
      relevant.forEach(r => {
        const dt = new Date(r.timestamp);
        const slotIdx = Math.floor(dt.getHours() / SLOT_HOURS); // 0..7
        const slotHH = String(slotIdx * SLOT_HOURS).padStart(2, "0");
        const slotKey = `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-${String(dt.getDate()).padStart(2,"0")} ${slotHH}`;
        const k = `${r.sensorId}|${slotKey}`;
        if (!buckets[k]) buckets[k] = [];
        if (r.temp != null) buckets[k].push(r.temp);
      });

      // Coleta dias únicos do período (com leituras)
      const daysSet = new Set();
      Object.keys(buckets).forEach(k => {
        const slotKey = k.split("|")[1];
        const dateStr = slotKey.split(" ")[0];
        daysSet.add(dateStr);
      });
      const days = [...daysSet].sort();

      // Resumo por sensor
      const summary = selectedSensors.map(s => {
        const sReads = relevant.filter(r => r.sensorId === s.id);
        const sAlerts = alerts.filter(a =>
          a.sensorId === s.id &&
          new Date(a.openedAt).getTime() >= fromMs &&
          new Date(a.openedAt).getTime() <= toMs
        );
        const temps = sReads.map(r => r.temp).filter(t => t != null);
        return {
          sensor: s,
          readings: sReads.length,
          tempAvg: temps.length ? temps.reduce((a,v)=>a+v,0)/temps.length : null,
          tempMin: temps.length ? Math.min(...temps) : null,
          tempMax: temps.length ? Math.max(...temps) : null,
          alerts: sAlerts.length,
          alertHours: sAlerts.reduce((acc, a) => {
            const end = a.closedAt ? new Date(a.closedAt).getTime() : Date.now();
            return acc + (end - new Date(a.openedAt).getTime()) / (1000*60*60);
          }, 0),
          hasOutOfRange: temps.some(t => t < s.minTemp || t > s.maxTemp),
        };
      });

      const { jsPDF } = window.jspdf;
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

      // Paleta
      const ACCENT_RGB = [124, 158, 94]; // #7c9e5e (Operacional verde)
      const TEXT_RGB = [28, 23, 16];
      const TEXT2_RGB = [74, 63, 48];
      const TEXT3_RGB = [154, 141, 122];
      const BORDER_RGB = [232, 226, 216];
      const RED_RGB = [192, 57, 43];
      const GREEN_RGB = [45, 143, 94];

      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();

      function fmtDate(iso) { // YYYY-MM-DD → DD/MM/YYYY
        const [y, m, d] = iso.split("-");
        return `${d}/${m}/${y}`;
      }

      function header(title, subtitle) {
        // Barra verde esquerda
        doc.setFillColor(...ACCENT_RGB);
        doc.rect(14, 12, 3, 16, 'F');
        // Título
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.setTextColor(...TEXT_RGB);
        doc.text(title, 22, 20);
        if (subtitle) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.setTextColor(...TEXT3_RGB);
          doc.text(subtitle, 22, 26);
        }
        // Linha separadora
        doc.setDrawColor(...BORDER_RGB);
        doc.setLineWidth(0.3);
        doc.line(14, 32, pageW - 14, 32);
      }

      function footer(pageNum, totalPages) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(...TEXT3_RGB);
        const footerText = `Relatório gerado em ${new Date().toLocaleString("pt-BR")} · Página ${pageNum}${totalPages?` de ${totalPages}`:""}`;
        doc.text(footerText, pageW / 2, pageH - 8, { align: "center" });
      }

      // ═══ PÁGINA 1: CAPA / RESUMO ═══
      doc.setFillColor(250, 248, 244); // bg claro
      doc.rect(0, 0, pageW, pageH, 'F');

      // Título grande
      doc.setFont("helvetica", "bold");
      doc.setFontSize(28);
      doc.setTextColor(...TEXT_RGB);
      doc.text("Relatório de Temperaturas", pageW / 2, 45, { align: "center" });

      // Subtítulo
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      doc.setTextColor(...TEXT2_RGB);
      doc.text(`Período: ${fmtDate(from)} a ${fmtDate(to)}`, pageW / 2, 55, { align: "center" });

      // Caixa de metadata
      const boxY = 70;
      doc.setDrawColor(...BORDER_RGB);
      doc.setLineWidth(0.5);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(30, boxY, pageW - 60, 60, 3, 3, 'FD');

      doc.setFontSize(10);
      doc.setTextColor(...TEXT3_RGB);
      doc.setFont("helvetica", "bold");
      const mdX1 = 40; const mdX2 = pageW / 2 + 10;
      doc.text("SENSORES MONITORADOS", mdX1, boxY + 10);
      doc.text("TOTAL DE LEITURAS", mdX2, boxY + 10);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.setTextColor(...TEXT_RGB);
      doc.text(String(selectedSensors.length), mdX1, boxY + 22);
      doc.text(String(relevant.length), mdX2, boxY + 22);

      const totalAlerts = summary.reduce((acc, s) => acc + s.alerts, 0);
      const totalAlertHours = summary.reduce((acc, s) => acc + s.alertHours, 0);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...TEXT3_RGB);
      doc.text("ALERTAS NO PERÍODO", mdX1, boxY + 38);
      doc.text("TEMPO EM ALERTA", mdX2, boxY + 38);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.setTextColor(...(totalAlerts > 0 ? RED_RGB : GREEN_RGB));
      doc.text(String(totalAlerts), mdX1, boxY + 50);
      doc.setTextColor(...(totalAlertHours > 0 ? RED_RGB : GREEN_RGB));
      doc.text(`${totalAlertHours.toFixed(1)}h`, mdX2, boxY + 50);

      // Info
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...TEXT3_RGB);
      doc.text(
        "Leituras capturadas pelo sistema a partir dos sensores Tuya/SmartLife. Cada célula de temperatura representa a média das leituras daquela hora. Faixas de referência configuradas por sensor são destacadas em cada seção.",
        pageW / 2,
        150,
        { align: "center", maxWidth: pageW - 60 }
      );

      footer(1);

      // ═══ PÁGINA 2: RESUMO POR SENSOR ═══
      doc.addPage();
      header("Resumo por sensor", `${fmtDate(from)} a ${fmtDate(to)}`);

      doc.autoTable({
        startY: 38,
        head: [["Sensor", "Localização", "Faixa (°C)", "Leituras", "Mín.", "Média", "Máx.", "Alertas", "Tempo alerta"]],
        body: summary.map(s => [
          s.sensor.name,
          s.sensor.location || "—",
          `${s.sensor.minTemp}° a ${s.sensor.maxTemp}°`,
          s.readings,
          s.tempMin != null ? `${s.tempMin.toFixed(1)}°` : "—",
          s.tempAvg != null ? `${s.tempAvg.toFixed(1)}°` : "—",
          s.tempMax != null ? `${s.tempMax.toFixed(1)}°` : "—",
          s.alerts,
          `${s.alertHours.toFixed(1)}h`,
        ]),
        theme: 'grid',
        styles: { fontSize: 9, cellPadding: 2.5, textColor: TEXT_RGB, lineColor: BORDER_RGB, lineWidth: 0.2 },
        headStyles: { fillColor: ACCENT_RGB, textColor: [255,255,255], fontStyle: 'bold', fontSize: 9 },
        alternateRowStyles: { fillColor: [250, 248, 244] },
        columnStyles: {
          0: { cellWidth: 45, fontStyle: 'bold' },
          1: { cellWidth: 40 },
          2: { cellWidth: 28, halign: 'center' },
          3: { cellWidth: 20, halign: 'right' },
          4: { cellWidth: 20, halign: 'right' },
          5: { cellWidth: 20, halign: 'right' },
          6: { cellWidth: 20, halign: 'right' },
          7: { cellWidth: 22, halign: 'right' },
          8: { cellWidth: 25, halign: 'right' },
        },
        didParseCell: function(data) {
          // Colore linha toda em amarelo se o sensor teve fora-da-faixa
          if (data.section === 'body') {
            const s = summary[data.row.index];
            if (s && s.hasOutOfRange) {
              data.cell.styles.fillColor = [255, 243, 224]; // amarelo claro
            }
            if (data.column.index === 7 && s && s.alerts > 0) {
              data.cell.styles.textColor = RED_RGB;
              data.cell.styles.fontStyle = 'bold';
            }
          }
        },
      });

      footer(2);

      // ═══ PÁGINAS 3+: 1 PÁGINA POR EQUIPAMENTO (até 30 dias × 8 slots de 3h por página) ═══
      // Layout: linhas = dias, colunas = 8 slots de 3h. Cabe em A4 paisagem com fonte 8pt.
      // Cabeçalho da página = nome do sensor + faixa + KPIs do período (mín/méd/máx/alertas).

      let pageCount = 2;
      const slotLabels = [];
      for (let slot = 0; slot < SLOTS_PER_DAY; slot++) {
        const startHH = String(slot * SLOT_HOURS).padStart(2, "0");
        const endHH = String(((slot + 1) * SLOT_HOURS) % 24).padStart(2, "0");
        slotLabels.push(`${startHH}—${endHH}h`);
      }
      // Quebra "days" em chunks de 30 (caso o período seja maior que 30 dias)
      function chunk(arr, size) {
        const out = [];
        for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
        return out;
      }
      const dayChunks = chunk(days, 30);

      selectedSensors.forEach(sensor => {
        const sensorSummary = summary.find(s => s.sensor.id === sensor.id);
        dayChunks.forEach((chunkDays, chunkIdx) => {
          doc.addPage();
          pageCount++;
          const subtitle = dayChunks.length > 1
            ? `${chunkIdx === 0 ? 'parte 1' : `parte ${chunkIdx+1}`} · ${chunkDays.length} dia${chunkDays.length>1?'s':''} · faixa ${sensor.minTemp}° a ${sensor.maxTemp}°`
            : `${fmtDate(chunkDays[0])} a ${fmtDate(chunkDays[chunkDays.length-1])} · faixa ${sensor.minTemp}° a ${sensor.maxTemp}°`;
          header(sensor.name + (sensor.location ? ` — ${sensor.location}` : ''), subtitle);

          // KPI box no topo da página (4 caixinhas)
          if (chunkIdx === 0 && sensorSummary) {
            const kpiY = 38;
            const kpis = [
              { lbl: "Leituras", val: String(sensorSummary.readings) },
              { lbl: "Mínima", val: sensorSummary.tempMin != null ? `${sensorSummary.tempMin.toFixed(1)}°` : "—" },
              { lbl: "Média", val: sensorSummary.tempAvg != null ? `${sensorSummary.tempAvg.toFixed(1)}°` : "—" },
              { lbl: "Máxima", val: sensorSummary.tempMax != null ? `${sensorSummary.tempMax.toFixed(1)}°` : "—" },
              { lbl: "Alertas", val: String(sensorSummary.alerts) },
              { lbl: "Tempo alerta", val: `${sensorSummary.alertHours.toFixed(1)}h` },
            ];
            const kpiW = (pageW - 40) / kpis.length;
            kpis.forEach((k, i) => {
              const x = 20 + i * kpiW;
              doc.setFillColor(250, 248, 244);
              doc.rect(x, kpiY, kpiW - 2, 12, "F");
              doc.setFontSize(7); doc.setTextColor(...TEXT3_RGB);
              doc.text(k.lbl, x + 3, kpiY + 4);
              doc.setFontSize(11); doc.setTextColor(...TEXT_RGB); doc.setFont(undefined, 'bold');
              doc.text(k.val, x + 3, kpiY + 10);
              doc.setFont(undefined, 'normal');
            });
          }
          const tableStartY = (chunkIdx === 0 && sensorSummary) ? 54 : 38;

          // Tabela: linhas = dias, colunas = slots de 3h.
          // Aplica CARRY-FORWARD: quando uma janela de 3h não tem leitura do sensor,
          // assume o último valor reportado (sensor T&H Tuya só envia quando há variação
          // além da sensibilidade — estabilidade = sem reporte). ANVISA aceita esse princípio.
          // Marcamos com asterisco (*) e cor cinza pra distinguir de leitura real.
          const head = [["Dia", ...slotLabels, "Méd."]];
          let lastKnownTemp = null; // valor mais recente conhecido (carry-forward através de dias/slots)
          let lastKnownTimeMs = null; // timestamp do slot da última leitura real
          const carryFlags = []; // mesma estrutura do body — true = carry-forward, false = leitura real
          // v8.23.6: não preenche carry-forward em janelas FUTURAS do dia atual
          // v8.23.7: limita carry-forward a MAX_CARRY_HOURS desde a última leitura real
          // (sensor T&H Tuya reporta ~1h em estabilidade; sem reporte por 6h+ é suspeita de offline)
          const MAX_CARRY_HOURS = 6;
          const todayStr = new Date().toISOString().slice(0, 10);
          const nowHour = new Date().getHours();
          const body = chunkDays.map(day => {
            const row = [fmtDate(day)];
            const flagRow = [false]; // primeira col (data) nunca é carry
            const dayTemps = [];
            for (let slot = 0; slot < SLOTS_PER_DAY; slot++) {
              const slotStartHour = slot * SLOT_HOURS;
              const startHH = String(slotStartHour).padStart(2, "0");
              const isFutureSlot = day === todayStr && slotStartHour > nowHour;
              const slotTimeMs = new Date(`${day}T${startHH}:00:00`).getTime();
              const temps = buckets[`${sensor.id}|${day} ${startHH}`];
              if (!temps || temps.length === 0) {
                const hoursSinceLast = lastKnownTimeMs != null ? (slotTimeMs - lastKnownTimeMs) / 3600000 : Infinity;
                const carryStillValid = lastKnownTemp != null && hoursSinceLast <= MAX_CARRY_HOURS;
                if (carryStillValid && !isFutureSlot) {
                  // Carry-forward dentro do limite (6h) — assume estabilidade
                  row.push(`${lastKnownTemp.toFixed(1)}*`);
                  flagRow.push(true);
                  dayTemps.push(lastKnownTemp);
                } else {
                  // Sem leitura + (carry expirou OU janela futura OU nunca houve leitura) — vazio
                  row.push("—");
                  flagRow.push(false);
                }
              } else {
                const avg = temps.reduce((a, v) => a + v, 0) / temps.length;
                row.push(`${avg.toFixed(1)}`);
                flagRow.push(false);
                dayTemps.push(avg);
                lastKnownTemp = avg; // atualiza valor mais recente
                lastKnownTimeMs = slotTimeMs;
              }
            }
            // Coluna "Méd." do dia (não marca como carry mesmo que componentes sejam)
            row.push(dayTemps.length ? `${(dayTemps.reduce((a,v)=>a+v,0)/dayTemps.length).toFixed(1)}` : "—");
            flagRow.push(false);
            carryFlags.push(flagRow);
            return row;
          });

          doc.autoTable({
            startY: tableStartY,
            head, body,
            theme: 'grid',
            styles: { fontSize: 8, cellPadding: 1.8, textColor: TEXT_RGB, lineColor: BORDER_RGB, lineWidth: 0.12, halign: 'center' },
            headStyles: { fillColor: ACCENT_RGB, textColor: [255,255,255], fontStyle: 'bold', fontSize: 8, halign: 'center' },
            alternateRowStyles: { fillColor: [250, 248, 244] },
            columnStyles: {
              0: { fontStyle: 'bold', halign: 'left', cellWidth: 26, fillColor: [242, 239, 232] },
              [SLOTS_PER_DAY + 1]: { fontStyle: 'bold', fillColor: [242, 239, 232] }, // Méd.
            },
            didParseCell: function(data) {
              // Colore células de temperatura fora da faixa (cols 1..SLOTS_PER_DAY são slots; última é média)
              if (data.section === 'body' && data.column.index >= 1 && data.column.index <= SLOTS_PER_DAY + 1) {
                const value = data.cell.raw;
                const isCarry = carryFlags[data.row.index]?.[data.column.index] === true;
                if (value !== "—") {
                  const t = parseFloat(value); // parseFloat ignora o * no fim
                  if (!isNaN(t)) {
                    if (t > sensor.maxTemp) {
                      data.cell.styles.fillColor = isCarry ? [252, 240, 240] : [252, 232, 230];
                      data.cell.styles.textColor = isCarry ? [200, 100, 100] : RED_RGB;
                      data.cell.styles.fontStyle = isCarry ? 'italic' : 'bold';
                    } else if (t < sensor.minTemp) {
                      data.cell.styles.fillColor = isCarry ? [240, 245, 252] : [230, 238, 252];
                      data.cell.styles.textColor = isCarry ? [100, 130, 175] : [30, 64, 175];
                      data.cell.styles.fontStyle = isCarry ? 'italic' : 'bold';
                    } else if (isCarry) {
                      // Dentro da faixa, mas é carry-forward: cinza claro pra distinguir
                      data.cell.styles.textColor = [150, 150, 150];
                      data.cell.styles.fontStyle = 'italic';
                    }
                  }
                }
              }
            },
          });

          // Nota de rodapé sobre carry-forward (se houver pelo menos 1 célula com *)
          const hasCarry = carryFlags.some(row => row.includes(true));
          if (hasCarry) {
            const yNote = doc.lastAutoTable.finalY + 4;
            doc.setFontSize(7); doc.setTextColor(...TEXT3_RGB); doc.setFont(undefined, 'italic');
            doc.text(
              "* Valor mantido por estabilidade — sensor não reportou variação na janela (sem registro novo de leitura, último valor conhecido foi assumido).",
              20, yNote, { maxWidth: pageW - 40 }
            );
            doc.setFont(undefined, 'normal');
          }

          footer(pageCount);
        });
      });

      // ═══ ÚLTIMAS PÁGINAS: ALERTAS DETALHADOS ═══
      const alertsInPeriod = alerts.filter(a =>
        selectedSensors.some(s => s.id === a.sensorId) &&
        new Date(a.openedAt).getTime() >= fromMs &&
        new Date(a.openedAt).getTime() <= toMs
      ).sort((a,b) => new Date(a.openedAt) - new Date(b.openedAt));

      if (alertsInPeriod.length > 0) {
        doc.addPage();
        pageCount++;
        header("Alertas detalhados", `${alertsInPeriod.length} alerta${alertsInPeriod.length>1?'s':''} no período`);

        doc.autoTable({
          startY: 38,
          head: [["Sensor", "Abertura", "Temp. pico", "Fechamento", "Duração", "Reconhecido por", "Observação"]],
          body: alertsInPeriod.map(a => {
            const sensor = selectedSensors.find(s => s.id === a.sensorId);
            const openDt = new Date(a.openedAt);
            const closeDt = a.closedAt ? new Date(a.closedAt) : null;
            const durMin = ((closeDt ? closeDt.getTime() : Date.now()) - openDt.getTime()) / 60000;
            return [
              sensor?.name || "(removido)",
              openDt.toLocaleString("pt-BR"),
              `${a.peakTemp?.toFixed(1) || '?'}°`,
              closeDt ? closeDt.toLocaleString("pt-BR") : "(aberto)",
              durMin >= 60 ? `${Math.floor(durMin/60)}h ${Math.round(durMin%60)}min` : `${Math.round(durMin)}min`,
              a.acknowledgedBy || "—",
              a.note || "",
            ];
          }),
          theme: 'grid',
          styles: { fontSize: 9, cellPadding: 2.5, textColor: TEXT_RGB, lineColor: BORDER_RGB, lineWidth: 0.2 },
          headStyles: { fillColor: RED_RGB, textColor: [255,255,255], fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [252, 232, 230] },
          columnStyles: {
            0: { cellWidth: 45, fontStyle: 'bold' },
            1: { cellWidth: 38 },
            2: { cellWidth: 22, halign: 'right' },
            3: { cellWidth: 38 },
            4: { cellWidth: 22, halign: 'right' },
            5: { cellWidth: 35 },
            6: { cellWidth: 'auto' },
          },
        });

        footer(pageCount);
      }

      // Re-render footers com total correto
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        // Sobrescreve footer anterior com retângulo branco
        doc.setFillColor(250, 248, 244);
        doc.rect(0, pageH - 12, pageW, 12, 'F');
        footer(i, totalPages);
      }

      // Gera blob e troca pro modo preview em vez de baixar direto
      const fname = `temperaturas_${from}_a_${to}.pdf`;
      const blob = doc.output("blob");
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewFname(fname);
      setGenerating(false);
    } catch (e) {
      console.error(e);
      alert("Erro ao gerar PDF: " + e.message);
      setGenerating(false);
    }
  }

  function downloadPreview() {
    if (!previewUrl || !previewFname) return;
    const a = document.createElement("a");
    a.href = previewUrl;
    a.download = previewFname;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  function backToConfig() {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      setPreviewFname("");
    }
  }

  // ═══ MODO PREVIEW (quando PDF já foi gerado) ═══
  if (previewUrl) {
    return (
      <div onClick={onClose}
        style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:200,display:"flex",flexDirection:"column",padding:isMobile?8:16}}>
        <div onClick={e=>e.stopPropagation()}
          style={{background:"var(--bg1)",borderRadius:12,width:"100%",flex:1,display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 10px 40px rgba(0,0,0,0.4)"}}>
          {/* Header do preview */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:isMobile?"10px 14px":"14px 20px",borderBottom:"1px solid var(--border)",background:"var(--bg2)",flexWrap:"wrap",gap:8}}>
            <div style={{flex:1,minWidth:200}}>
              <h3 style={{color:"var(--text)",margin:0,fontSize:isMobile?14:16,fontWeight:700}}>📄 Pré-visualização</h3>
              <div style={{fontSize:11,color:"var(--text3)",marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{previewFname}</div>
            </div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              <button onClick={backToConfig}
                style={{...S.btnSecondary,fontSize:12,padding:"8px 14px",minHeight:isMobile?40:"auto"}}>
                ← Voltar
              </button>
              <button onClick={downloadPreview}
                style={{background:ac,color:"#fff",border:"none",borderRadius:8,padding:"8px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",minHeight:isMobile?40:"auto"}}>
                📥 Baixar PDF
              </button>
              <button onClick={onClose} style={{background:"none",border:"none",fontSize:20,color:"var(--text3)",cursor:"pointer",padding:"4px 10px"}}>×</button>
            </div>
          </div>
          {/* Iframe do PDF */}
          <div style={{flex:1,background:"#525659",overflow:"hidden",position:"relative"}}>
            <iframe
              src={previewUrl + "#zoom=page-fit&view=Fit"}
              title="Pré-visualização do relatório"
              style={{width:"100%",height:"100%",border:"none",background:"#fff"}}
            />
          </div>
          {/* Footer mobile hint */}
          {isMobile && (
            <div style={{padding:"8px 14px",fontSize:10,color:"var(--text3)",textAlign:"center",borderTop:"1px solid var(--border)",background:"var(--bg2)"}}>
              Se não visualizar, toque em <b>📥 Baixar PDF</b> pra abrir no Files.
            </div>
          )}
        </div>
      </div>
    );
  }

  // ═══ MODO CONFIG (formulário pra gerar) ═══
  return (
    <div onClick={onClose}
      style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:isMobile?12:20}}>
      <div onClick={e=>e.stopPropagation()}
        style={{background:"var(--bg1)",borderRadius:12,padding:isMobile?"16px":"22px 26px",maxWidth:560,width:"100%",maxHeight:"90vh",overflowY:"auto",boxShadow:"0 10px 40px rgba(0,0,0,0.3)"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <h3 style={{color:"var(--text)",margin:0,fontSize:isMobile?16:18,fontWeight:700}}>📤 Exportar temperaturas</h3>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:18,color:"var(--text3)",cursor:"pointer",padding:"4px 8px"}}>×</button>
        </div>

        {/* Data range */}
        <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:8,marginBottom:14}}>
          <div>
            <label style={{fontSize:11,color:"var(--text3)",fontWeight:600}}>De</label>
            <input type="date" value={from} onChange={e=>setFrom(e.target.value)} style={S.input}/>
          </div>
          <div>
            <label style={{fontSize:11,color:"var(--text3)",fontWeight:600}}>Até</label>
            <input type="date" value={to} onChange={e=>setTo(e.target.value)} style={S.input}/>
          </div>
        </div>

        {/* Info sobre formato */}
        <div style={{marginBottom:14,padding:"10px 12px",background:ac+"10",borderRadius:8,border:`1px solid ${ac}33`}}>
          <div style={{fontSize:12,color:"var(--text)",fontWeight:600,marginBottom:4}}>📊 Formato: planilha sensores × slots de 3h</div>
          <div style={{fontSize:11,color:"var(--text2)",lineHeight:1.5}}>
            1 página por dia. Linhas = sensores. Colunas = 8 slots (00-03h, 03-06h…). Célula = média daquela janela.
            Células vermelhas = acima do max. Azuis = abaixo do min. Orientação paisagem, pronto pra auditoria ANVISA.
          </div>
        </div>

        {/* Sensores */}
        <div style={{marginBottom:14}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
            <label style={{fontSize:11,color:"var(--text3)",fontWeight:600}}>Sensores</label>
            <button onClick={toggleAll} style={{background:"none",border:"none",fontSize:11,color:ac,cursor:"pointer",fontWeight:600}}>
              {sensors.every(s=>selected[s.id]) ? "Desmarcar todos" : "Marcar todos"}
            </button>
          </div>
          <div style={{border:"1px solid var(--border)",borderRadius:8,maxHeight:200,overflowY:"auto"}}>
            {sensors.map(s => (
              <label key={s.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",borderBottom:"1px solid var(--border)",cursor:"pointer",fontSize:13,color:"var(--text)"}}>
                <input type="checkbox" checked={!!selected[s.id]} onChange={()=>toggleSensor(s.id)}/>
                <span style={{flex:1}}>{s.name}</span>
                <span style={{fontSize:10,color:"var(--text3)"}}>{s.minTemp}°–{s.maxTemp}°</span>
              </label>
            ))}
          </div>
        </div>

        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button onClick={onClose} style={{...S.btnSecondary,fontSize:12,padding:"10px 16px"}}>Cancelar</button>
          <button onClick={doExport} disabled={generating || selectedSensors.length === 0}
            style={{background:generating?"var(--bg3)":ac,color:generating?"var(--text3)":"#fff",border:"none",borderRadius:8,padding:"10px 18px",fontSize:13,fontWeight:700,cursor:generating?"wait":"pointer",fontFamily:"'DM Sans',sans-serif",minHeight:44}}>
            {generating ? "Gerando…" : "👁️ Pré-visualizar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Admin: vincular conta SmartLife + cadastrar sensores + thresholds
function MiseTemperaturasAdmin({ restaurantId, tuyaLink, tempSensors, tempReadings, miseFtEquipamentos, pessoas, currentUser, onUpdate, mobileOnly }) {
  const ac = "#7c9e5e";
  const [showLinkWizard, setShowLinkWizard] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);

  const equipamentos = miseFtEquipamentos?.[restaurantId] || [];
  const restSensors = (tempSensors || []).filter(s => s.restaurantId === restaurantId);
  const restPessoas = (pessoas || []).filter(p => p.whatsapp);

  // Último reading por sensor
  const lastRdBySensor = {};
  (tempReadings || []).forEach(r => {
    const prev = lastRdBySensor[r.sensorId];
    if (!prev || new Date(r.timestamp) > new Date(prev.timestamp)) lastRdBySensor[r.sensorId] = r;
  });

  function doLink({ uid, accountEmail }) {
    onUpdate("tuyaLinks", prev => ({ ...(prev || {}), [restaurantId]: {
      uid,
      accountEmail,
      linkedAt: new Date().toISOString(),
      linkedBy: currentUser?.name || "—",
    }}));
    setShowLinkWizard(false);
    onUpdate("_toast", `✓ Conta SmartLife "${accountEmail}" vinculada`);
  }
  async function doUnlink() {
    if (!await appConfirm("Desvincular a conta SmartLife deste restaurante?\n\nSensores cadastrados serão mantidos mas deixarão de receber leituras até religar.")) return;
    onUpdate("tuyaLinks", prev => {
      const next = { ...(prev || {}) };
      delete next[restaurantId];
      return next;
    });
    onUpdate("_toast", "Conta SmartLife desvinculada");
  }

  function startEdit(s) {
    setEditingId(s.id);
    setEditForm({ name:s.name, location:s.location||"", equipmentId:s.equipmentId||"", minTemp:s.minTemp, maxTemp:s.maxTemp, alertMinutes:s.alertMinutes, alertPessoaId:s.alertPessoaId||"", active:s.active!==false });
  }
  function saveEdit() {
    if (!editingId || !editForm) return;
    if (editForm.minTemp >= editForm.maxTemp) { alert("Min deve ser menor que max"); return; }
    const updated = (tempSensors || []).map(s => s.id === editingId ? {
      ...s,
      name: editForm.name.trim(),
      location: editForm.location.trim() || null,
      equipmentId: editForm.equipmentId || null,
      minTemp: +editForm.minTemp,
      maxTemp: +editForm.maxTemp,
      alertMinutes: +editForm.alertMinutes,
      alertPessoaId: editForm.alertPessoaId || null,
      active: !!editForm.active,
    } : s);
    onUpdate("tempSensors", updated);
    setEditingId(null);
    setEditForm(null);
  }
  async function delSensor(id) {
    const s = restSensors.find(x => x.id === id);
    if (!await appConfirm(`Remover sensor "${s?.name}"? Histórico de leituras também será descartado.`)) return;
    onUpdate("tempSensors", (tempSensors || []).filter(x => x.id !== id));
    onUpdate("tempReadings", prev => (prev || []).filter(r => r.sensorId !== id));
    onUpdate("_toast", "Sensor removido");
  }

  return (
    <div>
      {/* ═══ Card: Status da conta SmartLife ═══ */}
      {!tuyaLink ? (
        <div style={{background:"var(--card-bg)",border:`1px dashed ${ac}66`,borderRadius:12,padding:mobileOnly?"16px":"20px 24px",marginBottom:18}}>
          <div style={{fontSize:32,marginBottom:8}}>🔗</div>
          <h4 style={{color:"var(--text)",margin:"0 0 6px",fontSize:15,fontWeight:700}}>Conectar conta SmartLife</h4>
          <p style={{color:"var(--text2)",fontSize:12,lineHeight:1.5,marginBottom:14}}>
            Vincule a conta SmartLife deste restaurante pra começar a monitorar os sensores Tuya. Você só precisa fazer isso uma vez — novos sensores pareados no SmartLife aparecem automaticamente no sistema.
          </p>
          {!showLinkWizard ? (
            <button onClick={()=>setShowLinkWizard(true)}
              style={{background:ac,color:"#fff",border:"none",borderRadius:8,padding:"10px 18px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",minHeight:44}}>
              🔗 Conectar SmartLife
            </button>
          ) : (
            <LinkSmartLifeWizard onLink={doLink} onCancel={()=>setShowLinkWizard(false)} ac={ac} mobileOnly={mobileOnly}/>
          )}
        </div>
      ) : (
        <div style={{background:"var(--green-bg)",border:"1px solid var(--green)44",borderRadius:12,padding:"12px 16px",marginBottom:18,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
          <span style={{fontSize:20}}>✅</span>
          <div style={{flex:1,minWidth:200}}>
            <div style={{fontSize:13,color:"var(--text)",fontWeight:700}}>SmartLife conectada</div>
            <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>
              {tuyaLink.accountEmail || tuyaLink.uid} · vinculado em {new Date(tuyaLink.linkedAt).toLocaleDateString("pt-BR")} por {tuyaLink.linkedBy}
            </div>
          </div>
          <button onClick={doUnlink}
            style={{background:"transparent",color:"var(--red)",border:"1px solid var(--red)44",borderRadius:6,padding:"6px 14px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
            Desvincular
          </button>
        </div>
      )}

      {/* ═══ Email de alertas ═══ */}
      {tuyaLink && (
        <AlertEmailConfig
          restaurantId={restaurantId}
          currentEmail={tuyaLink.alertEmail || ""}
          onUpdate={onUpdate}
          ac={ac}
          mobileOnly={mobileOnly}
        />
      )}

      {/* ═══ Lista de sensores cadastrados ═══ */}
      {tuyaLink && (
        <>
          <div style={{marginBottom:14}}>
            <h4 style={{color:"var(--text)",margin:"0 0 10px",fontSize:14,fontWeight:700}}>Sensores deste restaurante ({restSensors.length})</h4>
            {restSensors.length === 0 ? (
              <div style={{padding:"20px 16px",textAlign:"center",background:"var(--bg2)",borderRadius:10,fontSize:12,color:"var(--text3)"}}>
                Nenhum sensor cadastrado. Use o formulário abaixo pra cadastrar o primeiro.
              </div>
            ) : (
              <div style={{display:"grid",gap:8}}>
                {restSensors.map(s => {
                  const rd = lastRdBySensor[s.id];
                  const status = tempStatus(rd?.temp, s.minTemp, s.maxTemp);
                  if (editingId === s.id && editForm) {
                    return (
                      <div key={s.id} style={{background:"var(--card-bg)",border:`2px solid ${ac}`,borderRadius:10,padding:"12px 14px"}}>
                        <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"2fr 1.5fr",gap:8,marginBottom:8}}>
                          <input value={editForm.name} onChange={e=>setEditForm(f=>({...f,name:e.target.value}))} placeholder="Nome" style={S.input}/>
                          <input value={editForm.location} onChange={e=>setEditForm(f=>({...f,location:e.target.value}))} placeholder="Localização" style={S.input}/>
                        </div>
                        <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr 1fr":"1fr 1fr 1fr 1fr",gap:8,marginBottom:8}}>
                          <div>
                            <label style={{fontSize:10,color:"var(--text3)"}}>Min (°C)</label>
                            <input type="number" step="0.1" value={editForm.minTemp} onChange={e=>setEditForm(f=>({...f,minTemp:e.target.value}))} style={S.input}/>
                          </div>
                          <div>
                            <label style={{fontSize:10,color:"var(--text3)"}}>Max (°C)</label>
                            <input type="number" step="0.1" value={editForm.maxTemp} onChange={e=>setEditForm(f=>({...f,maxTemp:e.target.value}))} style={S.input}/>
                          </div>
                          <div>
                            <label style={{fontSize:10,color:"var(--text3)"}}>Alerta (min)</label>
                            <input type="number" value={editForm.alertMinutes} onChange={e=>setEditForm(f=>({...f,alertMinutes:e.target.value}))} style={S.input}/>
                          </div>
                          <div>
                            <label style={{fontSize:10,color:"var(--text3)"}}>Equipamento</label>
                            <select value={editForm.equipmentId} onChange={e=>setEditForm(f=>({...f,equipmentId:e.target.value}))} style={S.input}>
                              <option value="">—</option>
                              {equipamentos.map(eq => <option key={eq} value={eq}>{eq}</option>)}
                            </select>
                          </div>
                        </div>
                        <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"1fr 1fr",gap:8,marginBottom:10}}>
                          <div>
                            <label style={{fontSize:10,color:"var(--text3)"}}>Responsável (WhatsApp de alerta)</label>
                            <select value={editForm.alertPessoaId} onChange={e=>setEditForm(f=>({...f,alertPessoaId:e.target.value}))} style={S.input}>
                              <option value="">—</option>
                              {restPessoas.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                          </div>
                          <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"var(--text2)",marginTop:mobileOnly?0:18}}>
                            <input type="checkbox" checked={editForm.active} onChange={e=>setEditForm(f=>({...f,active:e.target.checked}))}/>
                            Sensor ativo
                          </label>
                        </div>
                        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                          <button onClick={()=>delSensor(s.id)} style={{...S.btnSecondary,color:"var(--red)",borderColor:"var(--red)44",fontSize:11,padding:"6px 12px"}}>Remover</button>
                          <button onClick={()=>{setEditingId(null);setEditForm(null);}} style={{...S.btnSecondary,fontSize:11,padding:"6px 12px"}}>Cancelar</button>
                          <button onClick={saveEdit} style={{...S.btnPrimary,background:ac,borderColor:ac,fontSize:11,padding:"6px 14px"}}>Salvar</button>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={s.id} style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,padding:"12px 14px",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                      <span style={{fontSize:18}}>{status.dot}</span>
                      <div style={{flex:1,minWidth:180}}>
                        <div style={{fontSize:13,color:"var(--text)",fontWeight:700}}>{s.name} {!s.active && <span style={{fontSize:10,color:"var(--text3)",fontWeight:500}}>(inativo)</span>}</div>
                        <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>
                          {s.location && <>{s.location} · </>}
                          faixa {s.minTemp}°–{s.maxTemp}° · alerta em {s.alertMinutes}min
                          {s.equipmentId && <> · 🔧 {s.equipmentId}</>}
                        </div>
                      </div>
                      <div style={{fontSize:13,color:status.color,fontWeight:700,fontFamily:"'DM Mono',monospace",whiteSpace:"nowrap"}}>{fmtTemp(rd?.temp)}</div>
                      <button onClick={()=>startEdit(s)} style={{...S.btnSecondary,fontSize:11,padding:"6px 12px"}}>Editar</button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ═══ Cadastrar novo sensor — picker real de devices Tuya ═══ */}
          <AddSensorPanel
            restaurantId={restaurantId}
            tuyaUid={tuyaLink.uid}
            alreadyRegistered={restSensors.map(s => s.tuyaDeviceId)}
            equipamentos={equipamentos}
            restPessoas={restPessoas}
            onSave={(sensorData) => {
              onUpdate("tempSensors", [...(tempSensors || []), {
                ...sensorData,
                id: `snr_${Date.now().toString(36)}${Math.random().toString(36).slice(2,5)}`,
                restaurantId,
                createdAt: new Date().toISOString(),
                active: true,
              }]);
              onUpdate("_toast", `✓ Sensor "${sensorData.name}" cadastrado`);
            }}
            mobileOnly={mobileOnly}
            ac={ac}
          />
        </>
      )}

      <div style={{fontSize:11,lineHeight:1.5,marginTop:14,padding:"10px 14px",background:"#ecfccb",borderRadius:8,border:"1px solid #84cc1644",color:"#3f6212"}}>
        🟢 <b>Integração ativa:</b> leituras vêm do Tuya Cloud (atualização a cada 5 min quando o sistema está aberto). Para monitoramento 24/7 (alertas de madrugada), vamos precisar de uma camada adicional de backend — conversamos depois.
      </div>
    </div>
  );
}

// Painel de adicionar sensor — busca devices reais da conta SmartLife vinculada
function AddSensorPanel({ restaurantId, tuyaUid, alreadyRegistered, equipamentos, restPessoas, onSave, mobileOnly, ac }) {
  const [devices, setDevices] = useState(null);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState(null);
  const [form, setForm] = useState({ name:"", location:"", equipmentId:"", minTemp:-2, maxTemp:5, alertMinutes:10, alertPessoaId:"" });

  useEffect(() => {
    let cancelled = false;
    setError(null);
    (async () => {
      try {
        const res = await fetch(`/api/tuya/devices?uid=${encodeURIComponent(tuyaUid)}`);
        const data = await res.json();
        if (cancelled) return;
        if (!data.success) { setError(data.error || "Falha ao buscar devices"); return; }
        setDevices(data.devices || []);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [tuyaUid]);

  const available = (devices || []).filter(d => !alreadyRegistered.includes(d.id));
  const tempSensorsOnly = available.filter(d => d.is_temp_sensor);

  function pickDevice(d) {
    setPicked(d);
    setForm(f => ({ ...f, name: f.name || d.name || d.product_name || "Sensor" }));
  }

  function save() {
    if (!picked) { alert("Selecione um sensor"); return; }
    const nm = form.name.trim();
    if (!nm) { alert("Dê um nome pro sensor"); return; }
    if (+form.minTemp >= +form.maxTemp) { alert("Min deve ser menor que max"); return; }
    onSave({
      tuyaDeviceId: picked.id,
      name: nm,
      location: form.location.trim() || null,
      equipmentId: form.equipmentId || null,
      minTemp: +form.minTemp,
      maxTemp: +form.maxTemp,
      alertMinutes: +form.alertMinutes,
      alertPessoaId: form.alertPessoaId || null,
    });
    setPicked(null);
    setForm({ name:"", location:"", equipmentId:"", minTemp:-2, maxTemp:5, alertMinutes:10, alertPessoaId:"" });
  }

  return (
    <div style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,padding:"14px 16px",marginBottom:14}}>
      <h5 style={{color:"var(--text)",margin:"0 0 10px",fontSize:13,fontWeight:700}}>➕ Cadastrar novo sensor</h5>

      {devices === null && !error && (
        <div style={{padding:"10px 0",fontSize:12,color:"var(--text3)"}}>Buscando devices da conta SmartLife…</div>
      )}
      {error && (
        <div style={{padding:"10px 14px",background:"var(--red-bg)",border:"1px solid var(--red)44",borderRadius:8,fontSize:12,color:"var(--red)",marginBottom:10}}>
          ⚠️ {error}
        </div>
      )}
      {devices && available.length === 0 && (
        <div style={{padding:"10px 14px",background:"#f59e0b15",border:"1px solid #f59e0b44",borderRadius:8,fontSize:12,color:"var(--text2)",marginBottom:10}}>
          Todos os devices dessa conta já estão cadastrados neste restaurante. Pareie um sensor novo no SmartLife e volte aqui.
        </div>
      )}
      {devices && available.length > 0 && !picked && (
        <>
          <div style={{fontSize:11,color:"var(--text3)",marginBottom:8}}>
            {tempSensorsOnly.length > 0
              ? `Sensores de temperatura disponíveis na conta (${tempSensorsOnly.length})`
              : `Devices da conta (${available.length}) — nenhum é sensor de temperatura, mas você pode cadastrar outros tipos se quiser`}
          </div>
          <div style={{display:"grid",gap:6,marginBottom:10}}>
            {(tempSensorsOnly.length > 0 ? tempSensorsOnly : available).map(d => (
              <div key={d.id} onClick={()=>pickDevice(d)}
                style={{padding:"10px 12px",borderRadius:8,border:"1px solid var(--border)",background:"var(--bg2)",cursor:"pointer",display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontSize:16}}>{d.is_temp_sensor?"🌡️":"📡"}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{color:"var(--text)",fontSize:13,fontWeight:600}}>{d.name}</div>
                  <div style={{fontSize:10,color:"var(--text3)",marginTop:1}}>
                    {d.product_name} · {d.online?"🟢 online":"🔴 offline"} · {d.id}
                  </div>
                </div>
                <button style={{...S.btnSecondary,fontSize:11,padding:"5px 10px"}}>Selecionar</button>
              </div>
            ))}
          </div>
        </>
      )}

      {picked && (
        <>
          <div style={{padding:"10px 12px",borderRadius:8,background:ac+"15",border:`1px solid ${ac}`,marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:18}}>🌡️</span>
            <div style={{flex:1}}>
              <div style={{fontSize:12,color:"var(--text)",fontWeight:700}}>{picked.name}</div>
              <div style={{fontSize:10,color:"var(--text3)"}}>{picked.product_name} · {picked.id}</div>
            </div>
            <button onClick={()=>setPicked(null)} style={{...S.btnSecondary,fontSize:11,padding:"5px 10px"}}>Trocar</button>
          </div>

          <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"2fr 1.5fr",gap:8,marginBottom:8}}>
            <div>
              <label style={{fontSize:10,color:"var(--text3)"}}>Nome (apelido)</label>
              <input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Ex: Geladeira Bar" style={S.input}/>
            </div>
            <div>
              <label style={{fontSize:10,color:"var(--text3)"}}>Localização (opcional)</label>
              <input value={form.location} onChange={e=>setForm(f=>({...f,location:e.target.value}))} placeholder="Ex: Atrás do bar" style={S.input}/>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr 1fr":"1fr 1fr 1fr 1fr",gap:8,marginBottom:8}}>
            <div>
              <label style={{fontSize:10,color:"var(--text3)"}}>Min (°C)</label>
              <input type="number" step="0.1" value={form.minTemp} onChange={e=>setForm(f=>({...f,minTemp:e.target.value}))} style={S.input}/>
            </div>
            <div>
              <label style={{fontSize:10,color:"var(--text3)"}}>Max (°C)</label>
              <input type="number" step="0.1" value={form.maxTemp} onChange={e=>setForm(f=>({...f,maxTemp:e.target.value}))} style={S.input}/>
            </div>
            <div>
              <label style={{fontSize:10,color:"var(--text3)"}}>Alerta após (min)</label>
              <input type="number" value={form.alertMinutes} onChange={e=>setForm(f=>({...f,alertMinutes:e.target.value}))} style={S.input}/>
            </div>
            <div>
              <label style={{fontSize:10,color:"var(--text3)"}}>Equipamento</label>
              <select value={form.equipmentId} onChange={e=>setForm(f=>({...f,equipmentId:e.target.value}))} style={S.input}>
                <option value="">—</option>
                {equipamentos.map(eq => <option key={eq} value={eq}>{eq}</option>)}
              </select>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"1fr auto",gap:8,alignItems:"end"}}>
            <div>
              <label style={{fontSize:10,color:"var(--text3)"}}>Responsável por alertas (WhatsApp)</label>
              <select value={form.alertPessoaId} onChange={e=>setForm(f=>({...f,alertPessoaId:e.target.value}))} style={S.input}>
                <option value="">—</option>
                {restPessoas.map(p => <option key={p.id} value={p.id}>{p.name} · {p.whatsapp}</option>)}
              </select>
            </div>
            <button onClick={save}
              style={{background:ac,color:"#fff",border:"none",borderRadius:8,padding:"10px 18px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",minHeight:44}}>
              Cadastrar
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// Configurador do email de alertas do restaurante — salva em tuyaLinks[rid].alertEmail
function AlertEmailConfig({ restaurantId, currentEmail, onUpdate, ac, mobileOnly }) {
  const [email, setEmail] = useState(currentEmail);
  const [testing, setTesting] = useState(false);
  const isDirty = email !== currentEmail;

  function save() {
    const e = email.trim();
    if (e && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
      alert("Email inválido. Ex: gerente@restaurante.com");
      return;
    }
    onUpdate("tuyaLinks", prev => {
      const next = { ...(prev || {}) };
      const link = next[restaurantId] || {};
      next[restaurantId] = { ...link, alertEmail: e || null };
      return next;
    });
    onUpdate("_toast", e ? `✓ Email de alertas: ${e}` : "Email de alertas removido");
  }

  async function sendTest() {
    if (!currentEmail) { alert("Salve o email primeiro."); return; }
    setTesting(true);
    try {
      const res = await fetch("/api/alerts/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: currentEmail,
          subject: "🔔 Teste de alerta — Sistema",
          html: buildAlertEmailHtml({
            restaurantName: "Teste",
            sensorName: "Sensor de teste",
            location: "—",
            currentTemp: 8.5,
            minTemp: -2,
            maxTemp: 5,
            when: new Date(),
            isTest: true,
            alertType: "over_max",
          }),
        }),
      });
      const data = await res.json();
      if (data.success) {
        onUpdate("_toast", `✓ Email de teste enviado pra ${currentEmail}`);
      } else {
        alert("Falha no envio: " + (data.error || "erro desconhecido"));
      }
    } catch (e) {
      alert("Erro: " + e.message);
    }
    setTesting(false);
  }

  return (
    <div style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:10,padding:"14px 16px",marginBottom:18}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
        <span style={{fontSize:18}}>📧</span>
        <div style={{flex:1}}>
          <div style={{fontSize:13,color:"var(--text)",fontWeight:700}}>Email para alertas</div>
          <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>
            Quando um sensor sair da faixa, este endereço recebe um email imediatamente.
            Pode ser um email compartilhado (ex: <i>gerencia@restaurante.com</i>) pra múltiplas pessoas.
          </div>
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:mobileOnly?"1fr":"1fr auto auto",gap:8,alignItems:"end"}}>
        <input
          type="email"
          value={email}
          onChange={e=>setEmail(e.target.value)}
          placeholder="gerencia@restaurante.com"
          style={S.input}
        />
        <button
          onClick={save}
          disabled={!isDirty}
          style={{background:isDirty?ac:"var(--bg3)",color:isDirty?"#fff":"var(--text3)",border:"none",borderRadius:8,padding:"10px 16px",fontSize:12,fontWeight:700,cursor:isDirty?"pointer":"not-allowed",fontFamily:"'DM Sans',sans-serif",minHeight:44}}>
          {isDirty ? "Salvar" : "Salvo"}
        </button>
        <button
          onClick={sendTest}
          disabled={!currentEmail || testing || isDirty}
          title={!currentEmail ? "Salve o email antes" : isDirty ? "Salve primeiro" : "Envia um email de teste"}
          style={{...S.btnSecondary,fontSize:12,padding:"10px 14px",minHeight:44}}>
          {testing ? "Enviando…" : "Testar"}
        </button>
      </div>
    </div>
  );
}

// Tipos de alerta com severidade + rótulos + cores pro email
// - over_max: temperatura acima do max → vermelho (crítico)
// - low_battery: bateria baixa → amarelo (atenção)
// - offline: sem leitura há >12h → amarelo (atenção)
// - under_min: abaixo do min → só registro na UI, NÃO envia email
const ALERT_TYPES = {
  over_max:    { color: "#c0392b", label: "TEMPERATURA ACIMA DO MÁXIMO", icon: "🔴", severity: "critical" },
  low_battery: { color: "#f59e0b", label: "BATERIA BAIXA",               icon: "🟡", severity: "warning"  },
  offline:     { color: "#f59e0b", label: "SENSOR OFFLINE",              icon: "🟡", severity: "warning"  },
  under_min:   { color: "#3b82f6", label: "TEMPERATURA ABAIXO DO MÍNIMO",icon: "🔵", severity: "info"     },
};

// Decide se um tipo de alerta dispara email automático
function alertTypeSendsEmail(type) {
  return ["over_max", "low_battery", "offline"].includes(type);
}

// Gera HTML bonito do email de alerta (3 temas: teste azul, crítico vermelho, atenção amarelo)
function buildAlertEmailHtml({ restaurantName, sensorName, location, currentTemp, minTemp, maxTemp, when, isTest, alertType, extraInfo }) {
  const tempStr = currentTemp != null ? `${currentTemp.toFixed(1)}°C` : "—";
  const dateStr = when instanceof Date ? when.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" }) : when;

  let bannerText, bannerColor, reasonTitle, reasonBody, showTempRow = true;
  if (isTest) {
    bannerText = "TESTE — este email confirma que o canal está funcionando";
    bannerColor = "#2563eb";
    reasonTitle = "É só um teste";
    reasonBody = "Se você está recebendo este email, o canal de alertas está configurado corretamente. Alertas reais serão disparados automaticamente.";
  } else {
    const t = ALERT_TYPES[alertType] || ALERT_TYPES.over_max;
    bannerText = t.label;
    bannerColor = t.color;
    if (alertType === "over_max") {
      reasonTitle = "⚠️ Ação imediata recomendada";
      reasonBody = "A temperatura está acima do máximo configurado. Verifique o equipamento: porta aberta, falha de compressor, sobrecarga, queda de energia.";
    } else if (alertType === "low_battery") {
      reasonTitle = "🔋 Bateria do sensor baixa";
      reasonBody = `O sensor está com ${extraInfo?.battery ?? "?"}% de bateria. Troque as pilhas em breve pra evitar perder o monitoramento.`;
      showTempRow = false;
    } else if (alertType === "offline") {
      reasonTitle = "📡 Sensor sem comunicação";
      reasonBody = `Não recebemos leituras deste sensor há mais de 12 horas${extraInfo?.lastSeen ? ` (última leitura: ${extraInfo.lastSeen})` : ""}. Verifique se ele tá ligado, se o WiFi do restaurante tá funcionando, e se ele ainda aparece no app SmartLife.`;
      showTempRow = false;
    }
  }

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head><meta charset="utf-8"/><title>Alerta</title></head>
<body style="margin:0;padding:0;background:#faf8f4;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#1c1710;">
  <div style="max-width:560px;margin:24px auto;background:#fff;border-radius:12px;overflow:hidden;border:1px solid #e8e2d8;">
    <div style="background:${bannerColor};color:#fff;padding:16px 22px;">
      <div style="font-size:11px;font-weight:700;letter-spacing:1px;opacity:0.85;">${bannerText}</div>
      <div style="font-size:22px;font-weight:700;margin-top:4px;">${sensorName}</div>
    </div>
    <div style="padding:22px;">
      <table style="width:100%;border-collapse:collapse;font-size:14px;">
        <tr><td style="padding:8px 0;color:#9a8d7a;width:130px;">Restaurante</td><td style="padding:8px 0;font-weight:600;">${restaurantName}</td></tr>
        ${location ? `<tr><td style="padding:8px 0;color:#9a8d7a;">Localização</td><td style="padding:8px 0;">${location}</td></tr>` : ""}
        ${showTempRow ? `
        <tr>
          <td style="padding:8px 0;color:#9a8d7a;">Temperatura atual</td>
          <td style="padding:8px 0;font-family:'DM Mono',Courier,monospace;font-size:26px;font-weight:700;color:${bannerColor};">${tempStr}</td>
        </tr>
        <tr><td style="padding:8px 0;color:#9a8d7a;">Faixa configurada</td><td style="padding:8px 0;">${minTemp}°C a ${maxTemp}°C</td></tr>
        ` : ""}
        ${extraInfo?.battery != null && alertType !== "low_battery" ? `<tr><td style="padding:8px 0;color:#9a8d7a;">Bateria</td><td style="padding:8px 0;">${extraInfo.battery}%</td></tr>` : ""}
        <tr><td style="padding:8px 0;color:#9a8d7a;">Detectado em</td><td style="padding:8px 0;">${dateStr}</td></tr>
      </table>

      <div style="background:${bannerColor}12;border:1px solid ${bannerColor}44;border-radius:8px;padding:14px 16px;margin-top:18px;">
        <div style="font-weight:700;color:${bannerColor};margin-bottom:6px;">${reasonTitle}</div>
        <div style="font-size:13px;color:#1c1710;line-height:1.5;">${reasonBody}</div>
      </div>

      ${!isTest ? `
      <div style="margin-top:18px;text-align:center;">
        <a href="https://apptip.app" style="display:inline-block;background:#7c9e5e;color:#fff;text-decoration:none;padding:10px 22px;border-radius:8px;font-weight:700;font-size:14px;">Abrir Sistema pra reconhecer</a>
      </div>
      ` : ""}
    </div>
    <div style="padding:14px 22px;background:#f2efe8;font-size:11px;color:#9a8d7a;text-align:center;border-top:1px solid #e8e2d8;">
      Sistema — Monitoramento de temperaturas · integração Tuya/SmartLife
    </div>
  </div>
</body>
</html>`;
}

// Wizard de vínculo SmartLife — busca as contas já vinculadas no projeto Tuya
// (via QR code escaneado antes no painel iot.tuya.com) e deixa o admin escolher
// qual conta pertence a este restaurante.
function LinkSmartLifeWizard({ onLink, onCancel, ac, mobileOnly }) {
  const [accounts, setAccounts] = useState(null); // null = loading, [] = nenhuma
  const [error, setError] = useState(null);
  const [picking, setPicking] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/tuya/app-accounts");
        const data = await res.json();
        if (cancelled) return;
        if (!data.success) {
          setError(data.error || "Falha ao buscar contas");
          return;
        }
        setAccounts(data.users || []);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  function confirmPick() {
    if (!picking) return;
    onLink({
      uid: picking.uid,
      accountEmail: picking.email || picking.nick_name || `Conta ${picking.uid.slice(0, 8)}`,
    });
  }

  return (
    <div style={{background:"var(--bg2)",border:"1px solid var(--border)",borderRadius:10,padding:"12px 14px"}}>
      <div style={{fontSize:12,color:"var(--text2)",marginBottom:10,lineHeight:1.5}}>
        Contas SmartLife vinculadas ao projeto Tuya IoT. Escolha qual pertence a este restaurante.
        Se a conta não aparecer, vá em iot.tuya.com → projeto → Devices → <b>Link App Account</b>, escaneie o QR code com o SmartLife do restaurante, e volte aqui.
      </div>

      {accounts === null && !error && (
        <div style={{padding:"10px 0",fontSize:12,color:"var(--text3)"}}>Carregando contas…</div>
      )}
      {error && (
        <div style={{padding:"10px 14px",background:"var(--red-bg)",border:"1px solid var(--red)44",borderRadius:8,fontSize:12,color:"var(--red)",marginBottom:10}}>
          ⚠️ Erro: {error}
        </div>
      )}
      {accounts && accounts.length === 0 && (
        <div style={{padding:"10px 14px",background:"#f59e0b15",border:"1px solid #f59e0b44",borderRadius:8,fontSize:12,color:"var(--text2)",marginBottom:10}}>
          Nenhuma conta SmartLife encontrada no projeto. Vincule uma primeiro no painel Tuya IoT.
        </div>
      )}
      {accounts && accounts.length > 0 && (
        <div style={{marginBottom:12}}>
          {accounts.map(a => {
            const label = a.email || a.nick_name || `Conta ${a.uid.slice(0, 8)}…`;
            const selected = picking && picking.uid === a.uid;
            return (
              <div key={a.uid} onClick={()=>setPicking(a)}
                style={{padding:"10px 12px",marginBottom:6,borderRadius:8,border:`1px solid ${selected?ac:"var(--border)"}`,background:selected?ac+"15":"var(--card-bg)",cursor:"pointer",fontSize:13}}>
                <div style={{color:"var(--text)",fontWeight:selected?700:500}}>{label}</div>
                <div style={{fontSize:10,color:"var(--text3)",marginTop:2}}>
                  {a.device_count} device{a.device_count!==1?"s":""}
                  {a.sample_device_name && <> · ex: {a.sample_device_name}</>}
                  <br/>UID: {a.uid}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
        <button onClick={onCancel} style={{...S.btnSecondary,fontSize:12,padding:"8px 14px"}}>Cancelar</button>
        <button onClick={confirmPick} disabled={!picking}
          style={{background:picking?ac:"var(--bg3)",color:picking?"#fff":"var(--text3)",border:"none",borderRadius:8,padding:"8px 14px",fontSize:12,fontWeight:700,cursor:picking?"pointer":"not-allowed",fontFamily:"'DM Sans',sans-serif"}}>
          Vincular conta selecionada
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ──  MISE FICHAS TÉCNICAS — USER (Gestor Operacional)           ──
// ═══════════════════════════════════════════════════════════════
function OperationalFichasTecnicas({ employee, miseFtInsumos, miseFtDishes }) {
  const restaurantId = employee.restaurantId;
  const restDishes = miseFtDishes.filter(d => d.restaurantId === restaurantId);
  const restInsumos = miseFtInsumos.filter(i => i.restaurantId === restaurantId);
  const [openDishId, setOpenDishId] = useState(null);
  const [filter, setFilter] = useState("");
  const [scaleFactor, setScaleFactor] = useState(1);

  const openDish = openDishId ? restDishes.find(d => d.id === openDishId) : null;
  const sfFactors = useMemo(() => openDish ? ftComputeSfScaleFactors(openDish, scaleFactor) : {}, [openDish, scaleFactor]);

  if (openDish) {
    const costInfo = ftDishCost(openDish, restInsumos, restDishes);
    const lastSf = (openDish.sub_fichas || [])[(openDish.sub_fichas || []).length - 1];
    const finalPortions = costInfo.portions || 1;
    const targetPortions = Math.round(scaleFactor * finalPortions * 100) / 100;
    return (
      <div>
        {/* Toolbar */}
        <div style={{display:"flex",gap:8,marginBottom:16,alignItems:"center",flexWrap:"wrap"}}>
          <button onClick={()=>{setOpenDishId(null);setScaleFactor(1);}} style={{...S.btnSecondary,fontSize:13,padding:"8px 14px"}}>← Voltar</button>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:11,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Ficha técnica</div>
            <div style={{fontSize:17,color:"var(--text)",fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{openDish.name}</div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <div style={{display:"flex",alignItems:"center",gap:6,background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:8,padding:"4px 8px"}}>
              <span style={{fontSize:11,color:"var(--text3)",fontWeight:600}}>Escala:</span>
              {[0.5, 1, 2, 4].map(f => (
                <button key={f} onClick={()=>setScaleFactor(f)}
                  style={{background:Math.abs(scaleFactor-f)<0.0001?"#7c9e5e":"transparent",color:Math.abs(scaleFactor-f)<0.0001?"#fff":"var(--text2)",border:"none",borderRadius:6,padding:"4px 10px",fontSize:12,cursor:"pointer",fontWeight:Math.abs(scaleFactor-f)<0.0001?700:500}}>
                  {f}×
                </button>
              ))}
            </div>
            <div style={{display:"flex",alignItems:"center",gap:6,background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:8,padding:"4px 10px"}}>
              <span style={{fontSize:11,color:"var(--text3)",fontWeight:600}}>Fazer:</span>
              <input
                type="number"
                min="0"
                step="any"
                value={targetPortions || ""}
                onChange={e=>{
                  const n = parseFloat(e.target.value);
                  if (!isNaN(n) && n > 0 && finalPortions > 0) setScaleFactor(n / finalPortions);
                  else if (e.target.value === "") setScaleFactor(1);
                }}
                style={{width:70,border:"1px solid var(--border)",borderRadius:6,padding:"3px 6px",fontSize:12,fontFamily:"'DM Mono',monospace",background:"var(--bg2)",color:"var(--text)"}}
              />
              <span style={{fontSize:11,color:"var(--text3)",fontWeight:600}}>porções</span>
            </div>
          </div>
        </div>

        {openDish.description && <p style={{color:"var(--text3)",fontSize:14,lineHeight:1.6,margin:"0 0 20px",maxWidth:720}}>{openDish.description}</p>}

        {/* Metadata row */}
        <div style={{display:"flex",flexWrap:"wrap",gap:12,marginBottom:20}}>
          {openDish.louca && (
            <div style={{padding:"6px 12px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:8,fontSize:12}}>
              <span style={{color:"var(--text3)",fontWeight:600}}>Louça: </span>
              <span style={{color:"var(--text)"}}>{openDish.louca}</span>
            </div>
          )}
          {(openDish.equipamentos || []).length > 0 && (
            <div style={{padding:"6px 12px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:8,fontSize:12}}>
              <span style={{color:"var(--text3)",fontWeight:600}}>Equipamentos: </span>
              <span style={{color:"var(--text)"}}>{openDish.equipamentos.join(", ")}</span>
            </div>
          )}
          {lastSf?.rendimento && (
            <div style={{padding:"6px 12px",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:8,fontSize:12}}>
              <span style={{color:"var(--text3)",fontWeight:600}}>Rendimento: </span>
              <span style={{color:"var(--text)",fontFamily:"'DM Mono',monospace"}}>{ftFormatRendimento(lastSf.rendimento, scaleFactor)}</span>
            </div>
          )}
        </div>

        {/* Cost summary */}
        <div style={{padding:"14px 18px",background:"#7c9e5e11",border:"1px solid #7c9e5e44",borderRadius:10,marginBottom:20,display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(140px, 1fr))",gap:14}}>
          <div>
            <div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:0.4,fontWeight:700}}>Custo total</div>
            <div style={{fontSize:15,color:"var(--text)",fontWeight:700,fontFamily:"'DM Mono',monospace",marginTop:2}}>{fmt(costInfo.total * scaleFactor)}</div>
          </div>
          <div>
            <div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:0.4,fontWeight:700}}>Custo/porção</div>
            <div style={{fontSize:15,color:"var(--text)",fontWeight:700,fontFamily:"'DM Mono',monospace",marginTop:2}}>{fmt(costInfo.costPerPortion)}</div>
          </div>
          <div>
            <div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:0.4,fontWeight:700}}>Preço sugerido</div>
            <div style={{fontSize:15,color:"#15803d",fontWeight:700,fontFamily:"'DM Mono',monospace",marginTop:2}}>{fmt(costInfo.suggestedPrice)}</div>
          </div>
          <div>
            <div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:0.4,fontWeight:700}}>CMV</div>
            <div style={{fontSize:15,color:"var(--text)",fontWeight:700,fontFamily:"'DM Mono',monospace",marginTop:2}}>{costInfo.cmv ? costInfo.cmv.toFixed(1)+"%" : "—"}</div>
          </div>
        </div>

        {/* Sub-fichas */}
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          {(openDish.sub_fichas || []).map((sf, sfIdx) => {
            const isFinal = sfIdx === openDish.sub_fichas.length - 1;
            const sfCost = costInfo.sfCosts.find(x => x.sf.id === sf.id);
            const sfFactor = sfFactors[sf.id] > 0 ? sfFactors[sf.id] : scaleFactor;
            return (
              <div key={sf.id} style={{background:"var(--card-bg)",border:`1px solid ${isFinal?"#7c9e5e66":"var(--border)"}`,borderRadius:12,overflow:"hidden"}}>
                <div style={{padding:"12px 16px",background: isFinal ? "#7c9e5e11" : "var(--bg2)",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
                  <div>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:15,color:"var(--text)",fontWeight:700}}>{sfIdx+1}. {sf.name}</span>
                      {isFinal && <span style={{fontSize:10,padding:"2px 8px",borderRadius:10,background:"#7c9e5e22",color:"#15803d",fontWeight:700,letterSpacing:0.4}}>FINAL</span>}
                      {!isFinal && Math.abs(sfFactor - scaleFactor) > 0.0001 && <span title="Esta sub-ficha escala de forma diferente da ficha final por conta da proporção entre rendimento e consumo" style={{fontSize:10,padding:"2px 8px",borderRadius:10,background:"#f59e0b22",color:"#a16207",fontWeight:700,letterSpacing:0.4,fontFamily:"'DM Mono',monospace"}}>×{Math.round(sfFactor*100)/100}</span>}
                    </div>
                    {sf.rendimento && <div style={{fontSize:12,color:"var(--text3)",marginTop:2,fontFamily:"'DM Mono',monospace"}}>Rend.: {ftFormatRendimento(sf.rendimento, sfFactor)}</div>}
                  </div>
                  <div style={{fontSize:12,color:"var(--text3)",fontFamily:"'DM Mono',monospace"}}>{sfCost ? fmt(sfCost.total * sfFactor) : "—"}</div>
                </div>
                <div style={{padding:"14px 16px"}}>
                  {/* Ingredientes */}
                  {(sf.ingredientes || []).length > 0 ? (
                    <div style={{marginBottom: sf.modo_preparo ? 16 : 0}}>
                      <div style={{fontSize:11,fontWeight:700,color:"var(--text2)",textTransform:"uppercase",letterSpacing:0.4,marginBottom:8}}>Ingredientes</div>
                      <div style={{overflowX:"auto",border:"1px solid var(--border)",borderRadius:8}}>
                        <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                          <thead>
                            <tr style={{background:"var(--bg2)"}}>
                              <th style={{padding:"6px 10px",textAlign:"left",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Item</th>
                              <th style={{padding:"6px 10px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,width:100}}>Quantidade</th>
                              <th style={{padding:"6px 10px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,width:90}}>Custo</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(sf.ingredientes || []).map((ing, idx) => {
                              const costRow = sfCost?.rows?.[idx];
                              const isSubref = !!ing.subref_id;
                              const fmtQ = ftFormatIngQty(ing, sfFactor);
                              return (
                                <tr key={idx} style={{borderTop:"1px solid var(--border)"}}>
                                  <td style={{padding:"8px 10px",color:"var(--text)"}}>
                                    {isSubref && <span style={{fontSize:10,padding:"1px 6px",borderRadius:6,background:"#7c9e5e22",color:"#15803d",fontWeight:700,marginRight:6}}>↳ SUB</span>}
                                    {ing.insumo_name || "—"}
                                  </td>
                                  <td style={{padding:"8px 10px",textAlign:"right",color:"var(--text2)",fontFamily:"'DM Mono',monospace"}}>
                                    {fmtQ.text} {fmtQ.unit}
                                  </td>
                                  <td style={{padding:"8px 10px",textAlign:"right",color:costRow?.isReutilizavel?"#a855f7":(costRow?.isSubproduto?"var(--text3)":"var(--text2)"),fontFamily:"'DM Mono',monospace",fontSize:11}}>
                                    {ing.is_qb ? "Q.B" : (costRow?.isReutilizavel ? "♻" : (costRow?.isSubproduto ? "—" : (costRow ? fmt(costRow.cost * sfFactor) : "—")))}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div style={{padding:"12px",textAlign:"center",fontSize:12,color:"var(--text3)",background:"var(--bg2)",borderRadius:8,marginBottom:sf.modo_preparo?16:0}}>
                      Nenhum ingrediente listado.
                    </div>
                  )}
                  {/* Modo de preparo */}
                  {sf.modo_preparo && (
                    <div>
                      <div style={{fontSize:11,fontWeight:700,color:"var(--text2)",textTransform:"uppercase",letterSpacing:0.4,marginBottom:8}}>Modo de preparo</div>
                      <div style={{fontSize:13,color:"var(--text2)",lineHeight:1.7,whiteSpace:"pre-wrap",background:"var(--bg2)",padding:"12px 14px",borderRadius:8,border:"1px solid var(--border)"}}>{sf.modo_preparo}</div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // List view
  const q = filter.trim().toLowerCase();
  const filtered = q ? restDishes.filter(d => d.name.toLowerCase().includes(q)) : restDishes;

  if (restDishes.length === 0) {
    return (
      <div style={{padding:"60px 24px",textAlign:"center",color:"var(--text3)"}}>
        <div style={{fontSize:48,marginBottom:16}}>📋</div>
        <h3 style={{color:"var(--text)",fontSize:18,fontWeight:700,margin:"0 0 8px"}}>Sem fichas cadastradas</h3>
        <p style={{fontSize:14,lineHeight:1.6,maxWidth:420,margin:"0 auto"}}>
          O gestor ainda não cadastrou nenhuma ficha técnica para esse restaurante.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div style={{marginBottom:16}}>
        <input value={filter} onChange={e=>setFilter(e.target.value)} placeholder="🔍 Buscar ficha..." style={{...S.input,maxWidth:360}} />
      </div>
      {filtered.length === 0 ? (
        <div style={{padding:"32px 16px",textAlign:"center",color:"var(--text3)",fontSize:14,background:"var(--card-bg)",borderRadius:12,border:"1px solid var(--border)"}}>
          Nenhuma ficha encontrada para "{filter}"
        </div>
      ) : (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))",gap:12}}>
          {[...filtered].sort((a,b)=>a.name.localeCompare(b.name)).map(d => {
            const cost = ftDishCost(d, restInsumos, restDishes);
            const lastSf = (d.sub_fichas || [])[(d.sub_fichas || []).length - 1];
            const rendDisplay = lastSf?.rendimento ? ftFormatRendimento(lastSf.rendimento) : "";
            return (
              <button key={d.id} onClick={()=>setOpenDishId(d.id)}
                style={{textAlign:"left",background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:12,padding:"14px 16px",cursor:"pointer",fontFamily:"inherit",transition:"border-color 0.15s"}}
                onMouseEnter={e=>e.currentTarget.style.borderColor="#7c9e5e"}
                onMouseLeave={e=>e.currentTarget.style.borderColor="var(--border)"}>
                <div style={{fontSize:15,color:"var(--text)",fontWeight:700,marginBottom:6}}>{d.name}</div>
                {rendDisplay && <div style={{fontSize:11,color:"var(--text3)",marginBottom:8,fontFamily:"'DM Mono',monospace"}}>{rendDisplay}</div>}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginTop:12,paddingTop:12,borderTop:"1px solid var(--border)"}}>
                  <div>
                    <div style={{fontSize:10,color:"var(--text3)",fontWeight:600,textTransform:"uppercase",letterSpacing:0.4}}>Custo/porç.</div>
                    <div style={{fontSize:13,color:"var(--text)",fontWeight:700,fontFamily:"'DM Mono',monospace"}}>{fmt(cost.costPerPortion)}</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:10,color:"var(--text3)",fontWeight:600,textTransform:"uppercase",letterSpacing:0.4}}>Preço sug.</div>
                    <div style={{fontSize:13,color:"#15803d",fontWeight:700,fontFamily:"'DM Mono',monospace"}}>{fmt(cost.suggestedPrice)}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ──  MISE COMPRAS — USER (Gestor Operacional)                   ──
// ═══════════════════════════════════════════════════════════════
// (LooseCountsCard removido — modelo sem ciclos consome contagens diretamente)
function OperationalCompras({ employee, data, onUpdate }) {
  const restaurantId = employee.restaurantId;
  const [view, setView] = useState("sugestoes");
  const [overrides, setOverrides] = useState({}); // productId → linkId override para sugestão
  const [qtyEdits, setQtyEdits] = useState({}); // productId → qty manual override
  const [expandedOrder, setExpandedOrder] = useState(null); // orderId
  const [receiveFormOrder, setReceiveFormOrder] = useState(null); // orderId do pedido sendo recebido
  const [receiveDrafts, setReceiveDrafts] = useState({}); // itemIdx → qtyReceived
  const [receiveNote, setReceiveNote] = useState("");

  const miseAc = "#7c9e5e";

  // MODELO SEM CICLOS: sugestões vêm de TODAS as contagens não-arquivadas.
  // Aprovar pedido marca contagens dos produtos aprovados como `archivedAt` (saem da pool).
  const items = data.miseItems || [];
  const counts = data.miseCounts || [];
  const categories = data.miseCategories || [];
  const suppliers = (data.miseSuppliers || []).filter(s => s.restaurantId === restaurantId);
  const productSuppliers = data.miseProductSuppliers || [];
  const orders = (data.miseSupplierOrders || []).filter(o => o.restaurantId === restaurantId);
  const restaurantName = (data.restaurants || []).find(r => r.id === restaurantId)?.name || "Restaurante";

  // Contagens disponíveis (não-arquivadas) — alimentam a sugestão
  const looseCounts = counts.filter(c => c.restaurantId === restaurantId && !c.archivedAt);

  // Pedidos pendentes (não-terminais) e fechados (terminais) — flat, sem ciclo
  const activeOrders = orders.filter(o => !MISE_ORDER_STATUS[o.status]?.terminal);
  const terminalOrders = orders.filter(o => MISE_ORDER_STATUS[o.status]?.terminal);

  // Produtos que já estão em pedidos ATIVOS (pra não duplicar sugestão até receber)
  const productsAlreadyOrdered = new Set(
    activeOrders.flatMap(o => (o.items || []).map(it => it.productId))
  );

  const suggested = miseComputeSuggestedOrders({
    countsToUse: looseCounts, restaurantId, items, counts, categories,
    productSuppliers, suppliers, overrides
  }).map(g => ({ ...g, items: g.items.filter(it => !productsAlreadyOrdered.has(it.productId)) }))
    .filter(g => g.items.length > 0);

  // Ao aprovar pedido(s), marca as contagens dos produtos aprovados como `archivedAt`
  // (saem da pool de "contagens disponíveis")
  function archiveCountsForProducts(productIds) {
    if (!productIds.length) return;
    const productSet = new Set(productIds);
    const now = new Date().toISOString();
    const updated = (data.miseCounts || []).map(c => {
      if (c.restaurantId !== restaurantId || c.archivedAt || !productSet.has(c.itemId)) return c;
      return { ...c, archivedAt: now };
    });
    onUpdate("miseCounts", updated);
  }

  const totalSuggestedItems = suggested.reduce((s, g) => s + g.items.length, 0);

  // Opções de troca de fornecedor para um produto (só retorna quando tem >1 vínculo)
  function getSupplierOptions(productId) {
    return productSuppliers
      .filter(ps => ps.productId === productId && ps.restaurantId === restaurantId)
      .map(ps => ({ link: ps, supplier: suppliers.find(s => s.id === ps.supplierId) }))
      .filter(o => o.supplier);
  }

  async function approveGroup(group) {
    if (!await appConfirm(`Aprovar pedido com ${group.items.length} ite${group.items.length===1?"m":"ns"} para ${group.supplier.name}?`)) return;
    const order = {
      id: `ord_${Date.now()}_${Math.random().toString(36).slice(2,7)}`,
      restaurantId,
      cycleId: null, // sem ciclo
      supplierId: group.supplierId,
      status: "approved",
      items: group.items.map(it => ({
        productId: it.productId,
        productName: it.product.name,
        productUnit: it.product.unit,
        qtySuggested: it.qtySuggested,
        qtyApproved: qtyEdits[it.productId] != null ? qtyEdits[it.productId] : it.qtySuggested,
        conversionFactor: it.conversionFactor,
        unitPrice: it.link?.price ?? null,
        linkId: it.linkId,
      })),
      createdAt: new Date().toISOString(),
      approvedAt: new Date().toISOString(),
      approvedBy: employee.id,
      approvedByName: employee.name,
      history: [{ status: "approved", ts: new Date().toISOString(), by: employee.name }],
    };
    onUpdate("miseSupplierOrders", [...(data.miseSupplierOrders || []), order]);
    // Arquiva contagens dos produtos aprovados (saem da pool de sugestões)
    archiveCountsForProducts(group.items.map(it => it.productId));
    // Limpa overrides e qty edits dos produtos aprovados
    const clearedOverrides = { ...overrides };
    const clearedQtys = { ...qtyEdits };
    group.items.forEach(it => { delete clearedOverrides[it.productId]; delete clearedQtys[it.productId]; });
    setOverrides(clearedOverrides);
    setQtyEdits(clearedQtys);
  }

  async function approveAll() {
    if (suggested.length === 0) return;
    if (!await appConfirm(`Aprovar ${suggested.length} pedido(s) sugerido(s) (${totalSuggestedItems} ite${totalSuggestedItems===1?"m":"ns"} no total)?`)) return;
    const now = new Date().toISOString();
    const newOrders = suggested.map(group => ({
      id: `ord_${Date.now()}_${Math.random().toString(36).slice(2,7)}_${group.supplierId.slice(0,5)}`,
      restaurantId,
      cycleId: null, // sem ciclo
      supplierId: group.supplierId,
      status: "approved",
      items: group.items.map(it => ({
        productId: it.productId,
        productName: it.product.name,
        productUnit: it.product.unit,
        qtySuggested: it.qtySuggested,
        qtyApproved: qtyEdits[it.productId] != null ? qtyEdits[it.productId] : it.qtySuggested,
        conversionFactor: it.conversionFactor,
        unitPrice: it.link?.price ?? null,
        linkId: it.linkId,
      })),
      createdAt: now,
      approvedAt: now,
      approvedBy: employee.id,
      approvedByName: employee.name,
      history: [{ status: "approved", ts: now, by: employee.name }],
    }));
    onUpdate("miseSupplierOrders", [...(data.miseSupplierOrders || []), ...newOrders]);
    // Arquiva contagens de TODOS os produtos aprovados em batch
    const allProductIds = suggested.flatMap(g => g.items.map(it => it.productId));
    archiveCountsForProducts(allProductIds);
    setOverrides({});
    setQtyEdits({});
  }

  async function sendOrder(order) {
    const supplier = suppliers.find(s => s.id === order.supplierId);
    if (!supplier) { alert("Fornecedor não encontrado."); return; }
    if (!supplier.whatsapp) {
      if (!await appConfirm(`Fornecedor "${supplier.name}" não tem WhatsApp cadastrado. Apenas marcar como enviado?`)) return;
    } else {
      const msg = miseBuildWhatsMessage({
        order, supplier, items: order.items,
        restaurantName,
        cycleDate: today(),
      });
      const link = miseWhatsLink(supplier.whatsapp, msg);
      if (link) window.open(link, "_blank", "noopener");
    }
    const now = new Date().toISOString();
    onUpdate("miseSupplierOrders", (data.miseSupplierOrders || []).map(o => o.id === order.id ? {
      ...o,
      status: "sent",
      sentAt: now,
      sentBy: employee.id,
      sentByName: employee.name,
      history: [...(o.history || []), { status: "sent", ts: now, by: employee.name }],
    } : o));
  }

  function openReceiveForm(order) {
    setReceiveFormOrder(order.id);
    const drafts = {};
    (order.items || []).forEach((it, idx) => { drafts[idx] = it.qtyApproved; });
    setReceiveDrafts(drafts);
    setReceiveNote("");
  }

  function submitReceipt(order, terminalStatus) {
    const now = new Date().toISOString();
    const receivedItems = (order.items || []).map((it, idx) => ({
      ...it,
      qtyReceived: receiveDrafts[idx] != null ? parseFloat(String(receiveDrafts[idx]).replace(",",".")) : it.qtyApproved,
    }));
    const hasDivergence = receivedItems.some(it => (it.qtyReceived || 0) !== (it.qtyApproved || 0));
    const finalStatus = terminalStatus || (hasDivergence ? "received_with_divergence" : "received_ok");

    // Calcula próximo estado completo antes de persistir (sem setTimeout / sem race)
    const nextOrders = (data.miseSupplierOrders || []).map(o => o.id === order.id ? {
      ...o,
      status: finalStatus,
      items: receivedItems,
      receivedAt: now,
      receivedBy: employee.id,
      receivedByName: employee.name,
      receiveNote: receiveNote || null,
      history: [...(o.history || []), { status: finalStatus, ts: now, by: employee.name, note: receiveNote || null }],
    } : o);
    onUpdate("miseSupplierOrders", nextOrders);

    // (Encerramento automático de ciclo removido — modelo sem ciclos)

    setReceiveFormOrder(null);
    setReceiveDrafts({});
    setReceiveNote("");
  }

  const VIEWS = [
    { id: "sugestoes",    label: `Sugestões${suggested.length?` (${suggested.length})`:""}` },
    { id: "andamento",    label: `Em andamento${activeOrders.length?` (${activeOrders.length})`:""}` },
    { id: "historico",    label: `Histórico${terminalOrders.length?` (${terminalOrders.length})`:""}` },
  ];

  return (
    <div>
      {/* Banner informativo: contagens disponíveis */}
      <div style={{padding:"12px 16px",background:"var(--bg2)",border:"1px solid var(--border)",borderRadius:10,marginBottom:16,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
        <div>
          <div style={{fontSize:11,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>📦 Pool de contagens</div>
          <div style={{fontSize:14,color:"var(--text)",fontWeight:700,marginTop:2}}>{looseCounts.length} contagem{looseCounts.length===1?"":"ns"} disponíve{looseCounts.length===1?"l":"is"} pra gerar pedidos</div>
        </div>
        <div style={{fontSize:12,color:"var(--text3)"}}>
          {activeOrders.length} pedido{activeOrders.length===1?"":"s"} ativo{activeOrders.length===1?"":"s"} · {terminalOrders.length} no histórico
        </div>
      </div>

      {/* Sub-views */}
      <div style={{display:"flex",gap:4,marginBottom:16,borderBottom:"1px solid var(--border)",overflowX:"auto"}}>
        {VIEWS.map(v => (
          <button key={v.id} onClick={()=>setView(v.id)}
            style={{background:"none",border:"none",borderBottom: view===v.id ? `2px solid ${miseAc}` : "2px solid transparent",padding:"8px 14px",cursor:"pointer",fontSize:13,fontWeight: view===v.id ? 700 : 500,color: view===v.id ? "var(--text)" : "var(--text3)",whiteSpace:"nowrap",flexShrink:0}}>
            {v.label}
          </button>
        ))}
      </div>

      {/* SUGESTÕES */}
      {view === "sugestoes" && (
        <div>
          {suggested.length === 0 ? (
            <div style={{padding:"40px 24px",textAlign:"center",color:"var(--text3)",fontSize:14,background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:12}}>
              <div style={{fontSize:40,marginBottom:12}}>✅</div>
              <div style={{color:"var(--text)",fontWeight:600,fontSize:16,marginBottom:6}}>Nenhuma reposição sugerida</div>
              <div style={{lineHeight:1.6,maxWidth:420,margin:"0 auto"}}>
                Significa que o estoque atual está igual ou acima do mínimo para todos os produtos cadastrados. Se faltam produtos, confirme:
                <ul style={{textAlign:"left",display:"inline-block",margin:"8px auto 0",paddingLeft:20,fontSize:13}}>
                  <li>O mínimo está preenchido (aba Itens)</li>
                  <li>O produto tem fornecedor vinculado</li>
                  <li>Existe fornecedor preferencial</li>
                  <li>Há contagem lançada no ciclo atual</li>
                </ul>
              </div>
            </div>
          ) : (
            <>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:8}}>
                <div style={{fontSize:13,color:"var(--text2)"}}>
                  <b>{totalSuggestedItems}</b> ite{totalSuggestedItems===1?"m":"ns"} em <b>{suggested.length}</b> pedido(s) sugerido(s)
                </div>
                <button onClick={approveAll} style={{background:miseAc,color:"#fff",border:"none",borderRadius:8,padding:"8px 18px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                  ✓ Aprovar todos
                </button>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                {suggested.map(group => (
                  <div key={group.supplierId} style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:12,overflow:"hidden"}}>
                    <div style={{padding:"12px 16px",background:"var(--bg2)",borderBottom:"1px solid var(--border)",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                      <div>
                        <div style={{fontSize:14,color:"var(--text)",fontWeight:700}}>🚚 {group.supplier.name}</div>
                        <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>
                          {group.items.length} ite{group.items.length===1?"m":"ns"}
                          {group.totalCost > 0 && <span style={{marginLeft:8,fontFamily:"'DM Mono',monospace"}}>· est. {fmt(group.totalCost)}</span>}
                          {group.supplier.whatsapp && <span style={{marginLeft:8,fontFamily:"'DM Mono',monospace"}}>· 📱 {group.supplier.whatsapp}</span>}
                        </div>
                      </div>
                      <button onClick={()=>approveGroup(group)} style={{...S.btnSecondary,fontSize:12,padding:"8px 16px",color:miseAc,borderColor:miseAc+"66",fontWeight:700}}>✓ Aprovar</button>
                    </div>
                    <div style={{overflowX:"auto"}}>
                      <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                        <thead>
                          <tr style={{borderBottom:"1px solid var(--border)"}}>
                            <th style={{padding:"8px 12px",textAlign:"left",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Produto</th>
                            <th style={{padding:"8px 10px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Contado</th>
                            <th style={{padding:"8px 10px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Mín.</th>
                            <th style={{padding:"8px 10px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Qtd a pedir</th>
                            <th style={{padding:"8px 10px",textAlign:"left",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,width:160}}>Fornecedor</th>
                            <th style={{padding:"8px 10px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Custo est.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.items.map(it => {
                            const opts = getSupplierOptions(it.productId);
                            const multi = opts.length > 1;
                            const isDirect = it.source === "pedido_direto";
                            const isMisto = it.source === "misto";
                            return (
                              <tr key={it.productId} style={{borderTop:"1px solid var(--border)"}}>
                                <td style={{padding:"8px 12px",color:"var(--text)",fontWeight:500}}>
                                  {it.product.name}
                                  <span style={{color:"var(--text3)",fontWeight:400,marginLeft:6,fontSize:11}}>({it.product.unit})</span>
                                  {isDirect && <span style={{marginLeft:6,fontSize:10,padding:"1px 6px",borderRadius:6,background:"#fffbeb",color:"#d97706",fontWeight:700}}>🛒 direto</span>}
                                  {isMisto && <span style={{marginLeft:6,fontSize:10,padding:"1px 6px",borderRadius:6,background:"#fffbeb",color:"#d97706",fontWeight:700}}>+{ftFmtNum(it.directPedido,0)} direto</span>}
                                </td>
                                <td style={{padding:"8px 10px",textAlign:"right",color:"var(--text3)",fontFamily:"'DM Mono',monospace"}}>{isDirect ? "—" : ftFmtNum(it.totalCounted,0)}</td>
                                <td style={{padding:"8px 10px",textAlign:"right",color:"var(--text3)",fontFamily:"'DM Mono',monospace"}}>{isDirect ? "—" : (it.minStock != null ? ftFmtNum(it.minStock,0) : "—")}</td>
                                <td style={{padding:"4px 10px",textAlign:"right"}}>
                                  <input type="number" step="0.01" value={qtyEdits[it.productId] ?? it.qtySuggested}
                                    onChange={e=>setQtyEdits(prev=>({...prev, [it.productId]: parseFloat(e.target.value.replace(",",".")) }))}
                                    style={{...S.input,width:80,textAlign:"right",fontFamily:"'DM Mono',monospace",fontSize:12}} />
                                  {it.conversionFactor > 1 && <div style={{fontSize:10,color:"var(--text3)",marginTop:2}}>(fator {it.conversionFactor})</div>}
                                </td>
                                <td style={{padding:"4px 10px"}}>
                                  {multi ? (
                                    <select value={it.linkId} onChange={e=>setOverrides(prev=>({...prev, [it.productId]: e.target.value}))}
                                      style={{...S.input,fontSize:11,padding:"4px 6px",width:"100%"}}>
                                      {opts.map(o => <option key={o.link.id} value={o.link.id}>{o.supplier.name}{o.link.preferred?" ★":""}</option>)}
                                    </select>
                                  ) : (
                                    <span style={{fontSize:12,color:"var(--text3)"}}>{group.supplier.name}{it.link.preferred?" ★":""}</span>
                                  )}
                                </td>
                                <td style={{padding:"8px 10px",textAlign:"right",color:it.estimatedCost?"var(--text2)":"var(--text3)",fontFamily:"'DM Mono',monospace",fontSize:11}}>
                                  {it.estimatedCost ? fmt(it.estimatedCost) : "—"}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* EM ANDAMENTO */}
      {view === "andamento" && (
        <div>
          {activeOrders.length === 0 ? (
            <div style={{padding:"40px 24px",textAlign:"center",color:"var(--text3)",fontSize:14,background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:12}}>
              Nenhum pedido em andamento.
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              {activeOrders.map(order => renderOrderCard(order, { showActions: true }))}
            </div>
          )}
        </div>
      )}

      {/* HISTÓRICO */}
      {view === "historico" && (
        <div>
          {terminalOrders.length === 0 ? (
            <div style={{padding:"40px 24px",textAlign:"center",color:"var(--text3)",fontSize:14,background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:12}}>
              Nenhum pedido fechado neste ciclo.
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              {terminalOrders.map(order => renderOrderCard(order, { showActions: false }))}
            </div>
          )}
        </div>
      )}

      {/* MODAL de Recebimento */}
      {receiveFormOrder && (() => {
        const order = (data.miseSupplierOrders || []).find(o => o.id === receiveFormOrder);
        if (!order) { setTimeout(()=>setReceiveFormOrder(null),0); return null; }
        const supplier = suppliers.find(s => s.id === order.supplierId);
        return (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
            <div style={{background:"var(--card-bg)",borderRadius:14,maxWidth:760,width:"100%",maxHeight:"85vh",overflow:"auto",boxShadow:"0 10px 40px rgba(0,0,0,0.3)"}}>
              <div style={{padding:"14px 18px",borderBottom:"1px solid var(--border)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontSize:11,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Registrar recebimento</div>
                  <div style={{fontSize:15,color:"var(--text)",fontWeight:700}}>{supplier?.name}</div>
                </div>
                <button onClick={()=>setReceiveFormOrder(null)} style={{...S.btnSecondary,fontSize:12,padding:"6px 12px"}}>Cancelar</button>
              </div>
              <div style={{padding:"14px 18px"}}>
                <p style={{color:"var(--text3)",fontSize:12,lineHeight:1.5,margin:"0 0 14px"}}>
                  Informe o que foi <b>recebido de fato</b> para cada item. Se bater com o aprovado, status = Recebido OK. Se houver diferença, você escolhe o status final.
                </p>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:13,marginBottom:14}}>
                  <thead>
                    <tr style={{background:"var(--bg2)",borderBottom:"1px solid var(--border)"}}>
                      <th style={{padding:"8px 10px",textAlign:"left",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Produto</th>
                      <th style={{padding:"8px 10px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,width:80}}>Aprovado</th>
                      <th style={{padding:"8px 10px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,width:100}}>Recebido</th>
                      <th style={{padding:"8px 10px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,width:60}}>Diff</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(order.items || []).map((it, idx) => {
                      const received = receiveDrafts[idx] != null ? parseFloat(String(receiveDrafts[idx]).replace(",",".")) : it.qtyApproved;
                      const diff = (received || 0) - (it.qtyApproved || 0);
                      return (
                        <tr key={idx} style={{borderTop:"1px solid var(--border)"}}>
                          <td style={{padding:"8px 10px",color:"var(--text)",fontWeight:500}}>{it.productName}</td>
                          <td style={{padding:"8px 10px",textAlign:"right",color:"var(--text2)",fontFamily:"'DM Mono',monospace"}}>{it.qtyApproved}</td>
                          <td style={{padding:"4px 10px"}}>
                            <input type="number" step="0.01" value={receiveDrafts[idx] ?? it.qtyApproved}
                              onChange={e=>setReceiveDrafts(prev=>({...prev, [idx]: e.target.value}))}
                              style={{...S.input,width:"100%",textAlign:"right",fontFamily:"'DM Mono',monospace",fontSize:12}} />
                          </td>
                          <td style={{padding:"8px 10px",textAlign:"right",color:diff===0?"var(--text3)":(diff<0?"#dc2626":"#d97706"),fontFamily:"'DM Mono',monospace",fontWeight:diff===0?400:700,fontSize:11}}>
                            {diff === 0 ? "0" : (diff > 0 ? `+${diff}` : diff)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div style={{marginBottom:14}}>
                  <label style={{...S.label,fontSize:11}}>Observação (opcional)</label>
                  <textarea value={receiveNote} onChange={e=>setReceiveNote(e.target.value)} placeholder="Detalhes sobre a entrega, divergências, etc." style={{...S.input,minHeight:60,fontFamily:"inherit",resize:"vertical"}} />
                </div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"flex-end"}}>
                  <button onClick={()=>submitReceipt(order, "rejected")} style={{...S.btnSecondary,fontSize:12,padding:"8px 14px",color:"var(--red)",borderColor:"var(--red)44"}}>Recusar / Devolvido</button>
                  <button onClick={()=>submitReceipt(order, "awaiting_correction")} style={{...S.btnSecondary,fontSize:12,padding:"8px 14px",color:"#d97706",borderColor:"#d9770644"}}>Aguardando correção</button>
                  <button onClick={()=>submitReceipt(order)} style={{background:miseAc,color:"#fff",border:"none",borderRadius:8,padding:"8px 18px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>✓ Confirmar recebimento</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );

  function renderOrderCard(order, { showActions }) {
    const supplier = suppliers.find(s => s.id === order.supplierId);
    const st = MISE_ORDER_STATUS[order.status] || { label: order.status, color: "var(--text3)", bg: "var(--bg2)" };
    const isExpanded = expandedOrder === order.id;
    const totalApproved = (order.items || []).reduce((sum, it) => sum + (it.qtyApproved || 0), 0);
    const estCost = (order.items || []).reduce((sum, it) => sum + ((typeof it.unitPrice === "number" ? it.unitPrice : 0) * ((it.qtyApproved || 0) / (it.conversionFactor || 1))), 0);
    return (
      <div key={order.id} style={{background:"var(--card-bg)",border:"1px solid var(--border)",borderRadius:12,overflow:"hidden"}}>
        <div style={{padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8,cursor:"pointer"}} onClick={()=>setExpandedOrder(isExpanded ? null : order.id)}>
          <div style={{flex:1,minWidth:160}}>
            <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
              <span style={{fontSize:14,color:"var(--text)",fontWeight:700}}>🚚 {supplier?.name ?? "Fornecedor removido"}</span>
              <span style={{fontSize:10,padding:"2px 8px",borderRadius:10,background:st.bg,color:st.color,fontWeight:700,letterSpacing:0.4}}>{st.label.toUpperCase()}</span>
            </div>
            <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>
              {(order.items || []).length} ite{(order.items||[]).length===1?"m":"ns"} · {totalApproved} unidades
              {estCost > 0 && <span style={{marginLeft:6,fontFamily:"'DM Mono',monospace"}}>· est. {fmt(estCost)}</span>}
              {order.sentAt && <span style={{marginLeft:6}}>· enviado {fmtDate(order.sentAt.slice(0,10))}</span>}
              {order.receivedAt && <span style={{marginLeft:6}}>· recebido {fmtDate(order.receivedAt.slice(0,10))}</span>}
            </div>
          </div>
          {showActions && (
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}} onClick={e=>e.stopPropagation()}>
              {order.status === "approved" && (
                <button onClick={()=>sendOrder(order)} style={{...S.btnSecondary,fontSize:11,padding:"6px 12px",color:"#2563eb",borderColor:"#2563eb66"}}>📱 Enviar WhatsApp</button>
              )}
              {(order.status === "sent" || order.status === "awaiting_correction") && (
                <button onClick={()=>openReceiveForm(order)} style={{background:miseAc,color:"#fff",border:"none",borderRadius:8,padding:"6px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>📦 Registrar recebimento</button>
              )}
              {order.status === "approved" && (
                <button onClick={async ()=>{ if(await appConfirm("Descartar este pedido aprovado?")) onUpdate("miseSupplierOrders", (data.miseSupplierOrders || []).filter(o => o.id !== order.id)); }} style={{...S.btnSecondary,fontSize:11,padding:"6px 10px",color:"var(--red)",borderColor:"var(--red)44"}}>✕</button>
              )}
            </div>
          )}
          <span style={{color:"var(--text3)",fontSize:14}}>{isExpanded ? "▼" : "▶"}</span>
        </div>
        {isExpanded && (
          <div style={{borderTop:"1px solid var(--border)",padding:"10px 16px"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead>
                <tr style={{background:"var(--bg2)"}}>
                  <th style={{padding:"6px 10px",textAlign:"left",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Produto</th>
                  <th style={{padding:"6px 10px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Aprovado</th>
                  {order.items?.some(it => it.qtyReceived != null) && <th style={{padding:"6px 10px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Recebido</th>}
                  {order.items?.some(it => it.qtyReceived != null) && <th style={{padding:"6px 10px",textAlign:"right",fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Diff</th>}
                </tr>
              </thead>
              <tbody>
                {(order.items || []).map((it, idx) => {
                  const hasReceived = it.qtyReceived != null;
                  const diff = hasReceived ? (it.qtyReceived - it.qtyApproved) : null;
                  return (
                    <tr key={idx} style={{borderTop:"1px solid var(--border)"}}>
                      <td style={{padding:"6px 10px",color:"var(--text)"}}>{it.productName}</td>
                      <td style={{padding:"6px 10px",textAlign:"right",color:"var(--text2)",fontFamily:"'DM Mono',monospace"}}>{it.qtyApproved}</td>
                      {hasReceived && <td style={{padding:"6px 10px",textAlign:"right",color:"var(--text)",fontWeight:600,fontFamily:"'DM Mono',monospace"}}>{it.qtyReceived}</td>}
                      {hasReceived && <td style={{padding:"6px 10px",textAlign:"right",color:diff===0?"var(--text3)":(diff<0?"#dc2626":"#d97706"),fontFamily:"'DM Mono',monospace",fontWeight:diff===0?400:700}}>{diff===0?"0":(diff>0?`+${diff}`:diff)}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {order.receiveNote && <div style={{marginTop:10,padding:"8px 12px",background:"var(--bg2)",borderRadius:8,fontSize:12,color:"var(--text2)"}}>📝 {order.receiveNote}</div>}
            {order.history && order.history.length > 0 && (
              <div style={{marginTop:10}}>
                <div style={{fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,marginBottom:4}}>Histórico</div>
                <div style={{display:"flex",flexDirection:"column",gap:2}}>
                  {order.history.map((h, i) => (
                    <div key={i} style={{fontSize:11,color:"var(--text3)"}}>
                      <span style={{color:MISE_ORDER_STATUS[h.status]?.color || "var(--text3)",fontWeight:600}}>{MISE_ORDER_STATUS[h.status]?.label || h.status}</span>
                      {" — "}{new Date(h.ts).toLocaleString("pt-BR")}
                      {h.by && <span> por {h.by}</span>}
                      {h.note && <span style={{fontStyle:"italic"}}> · {h.note}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

//
// MANAGER PIN CHANGE (first access / reset)
//
function ManagerPinChange({ manager, onDone, onBack }) {
  const [pin1, setPin1] = useState("");
  const [pin2, setPin2] = useState("");
  const [err, setErr] = useState("");

  function submit() {
    if (pin1.length !== 4 || !/^\d{4}$/.test(pin1)) { setErr("PIN deve ter exatamente 4 dígitos numéricos."); return; }
    if (pin1 !== pin2) { setErr("PINs não coincidem."); return; }
    const cpfDigits = (manager.cpf??"").replace(/\D/g,"");
    if (pin1 === cpfDigits.slice(0,4)) { setErr("O novo PIN não pode ser igual aos 4 primeiros dígitos do CPF."); return; }
    onDone(pin1);
  }

  return (
    <div style={{minHeight:"100vh",background:"var(--bg)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Sans',sans-serif",padding:24}}>
      <div style={{...S.card,maxWidth:380,width:"100%",boxShadow:"0 8px 32px rgba(0,0,0,0.08)"}}>
        <div style={{textAlign:"center",marginBottom:24}}>
          <div style={{fontSize:36,marginBottom:12}}>🔑</div>
          <h2 style={{color:"var(--text)",margin:"0 0 8px",fontSize:22,fontWeight:800}}>Novo PIN</h2>
          <p style={{color:"var(--text3)",fontSize:14,lineHeight:1.5}}>
            Olá, {manager.name?.split(" ")[0]}! Defina um novo PIN de 4 dígitos para continuar.
          </p>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div>
            <label style={S.label}>Novo PIN (4 dígitos)</label>
            <input type="password" inputMode="numeric" maxLength={4} value={pin1} onChange={e=>setPin1(e.target.value.replace(/\D/g,"").slice(0,4))} placeholder="••••" style={{...S.input,letterSpacing:8,fontSize:22,textAlign:"center",fontFamily:"'DM Mono',monospace"}}/>
          </div>
          <div>
            <label style={S.label}>Confirmar PIN</label>
            <input type="password" inputMode="numeric" maxLength={4} value={pin2} onChange={e=>setPin2(e.target.value.replace(/\D/g,"").slice(0,4))} placeholder="••••" style={{...S.input,letterSpacing:8,fontSize:22,textAlign:"center",fontFamily:"'DM Mono',monospace"}} onKeyDown={e=>e.key==="Enter"&&submit()}/>
          </div>
          {err && <div style={{background:"var(--red-bg)",border:"1px solid var(--red)33",borderRadius:8,padding:"8px 12px",color:"var(--red)",fontSize:13}}>{err}</div>}
          <button onClick={submit} style={S.btnPrimary}>Salvar e Continuar →</button>
          <button onClick={onBack} style={{...S.btnSecondary,textAlign:"center"}}>← Sair</button>
        </div>
      </div>
    </div>
  );
}

//
// LOGIN
//
function UnifiedLogin({ owners, managers, employees, restaurants, pessoas, onLoginOwner, onLoginManager, onLoginEmployee, onLoginOperational, onLoginShell, onSetupFirst, onGoHome, onUpdatePessoas, onUpdate, toggleTheme, theme, dataLoaded }) {
  const [credential, setCredential] = useState("");
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [blockedUntil, setBlockedUntil] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(() => localStorage.getItem("apptip_terms") === "1");
  const [choices, setChoices] = useState(null); // { name, options: [{label, icon, action}] }
  // PIN change obrigatório para pessoa migrada
  const [pinChangePessoa, setPinChangePessoa] = useState(null); // pessoa
  const [newPin1, setNewPin1] = useState("");
  const [newPin2, setNewPin2] = useState("");
  const [pinChangeErr, setPinChangeErr] = useState("");

  const isBlocked = blockedUntil && new Date() < blockedUntil;
  const isEmpId = /^[A-Za-z]{2,4}\d+$/.test(credential.trim());
  const isCpf = credential.replace(/\D/g,"").length >= 11;

  useEffect(() => {
    if (!blockedUntil) return;
    const t = setInterval(() => {
      if (new Date() >= blockedUntil) { setBlockedUntil(null); setErr(""); clearInterval(t); }
      else setErr(`Muitas tentativas. Aguarde ${Math.ceil((blockedUntil-new Date())/1000)}s.`);
    }, 1000);
    return () => clearInterval(t);
  }, [blockedUntil]);

  // Monta opções de login a partir das permissões da pessoa em cada restaurante onde ela tem acesso
  function buildLoginChoicesFromPessoa(pessoa) {
    const options = [];
    const multiRestaurant = (pessoa.restaurantIds || []).length > 1;
    (pessoa.restaurantIds || []).forEach(rid => {
      const rest = restaurants.find(r => r.id === rid);
      const restName = rest?.name || "Restaurante";
      const inadimplente = rest?.financeiro?.status === "inadimplente";
      const perms = pessoa.permissions?.[rid] || { operational: {}, admin: {}, special: {} };
      const opKeys = Object.keys(perms.operational || {}).filter(k => perms.operational[k] === true);
      const admKeys = Object.keys(perms.admin || {}).filter(k => perms.admin[k] === true);
      const isTeam = !!pessoa.isTeam?.[rid];
      // Empregado
      if (isTeam && !inadimplente) {
        options.push({
          label: multiRestaurant ? `Empregado · ${restName}` : "Empregado",
          icon: "👤",
          action: () => {
            setChoices(null);
            // Usa linkedEmployeeId para localizar o employee no sistema legado
            const emp = employees.find(e => e.id === pessoa.linkedEmployeeId) ||
                         employees.find(e => e.restaurantId === rid && (e.cpf || "").replace(/\D/g,"") === (pessoa.cpf || "").replace(/\D/g,""));
            if (emp) {
              localStorage.setItem("apptip_empid", emp.id);
              localStorage.setItem("apptip_userid", emp.id);
              onLoginEmployee(emp);
            } else {
              alert("Não foi possível localizar seu registro de empregado. Avise o gestor.");
            }
          },
        });
      }
      // Gestor Operacional
      if (opKeys.length > 0 && !inadimplente) {
        options.push({
          label: multiRestaurant ? `Gestor Operacional · ${restName}` : "Gestor Operacional",
          icon: "⚙️",
          action: () => {
            setChoices(null);
            const emp = employees.find(e => e.id === pessoa.linkedEmployeeId) ||
                         employees.find(e => e.restaurantId === rid && (e.cpf || "").replace(/\D/g,"") === (pessoa.cpf || "").replace(/\D/g,""));
            if (emp && onLoginOperational) {
              onLoginOperational(emp);
            } else {
              alert("Para entrar como Gestor Operacional, você precisa estar cadastrado como membro da equipe. Avise o gestor.");
            }
          },
        });
      }
      // Gestor Administrativo
      if (admKeys.length > 0) {
        options.push({
          label: multiRestaurant ? `Gestor Adm. · ${restName}` : "Gestor Adm.",
          icon: "📊",
          action: () => {
            setChoices(null);
            const mgr = managers.find(m => m.id === pessoa.linkedManagerId) ||
                         managers.find(m => (m.restaurantIds || []).includes(rid) && (m.cpf || "").replace(/\D/g,"") === (pessoa.cpf || "").replace(/\D/g,""));
            if (mgr) {
              onLoginManager(mgr);
            } else {
              alert("Não foi possível localizar seu registro de gestor. Avise o administrador.");
            }
          },
        });
      }
    });
    // Se a pessoa tem mais de um perfil total, oferece o Portal Unificado como primeira opção
    if (options.length > 1 && onLoginShell) {
      options.unshift({
        label: "✨ Portal Unificado (novo)",
        icon: "🪟",
        action: () => { setChoices(null); onLoginShell(pessoa); },
        highlighted: true,
      });
    }
    return options;
  }

  function submitNewPin() {
    if (newPin1.length !== 4 || !/^\d{4}$/.test(newPin1)) { setPinChangeErr("PIN deve ter exatamente 4 dígitos numéricos."); return; }
    if (newPin1 !== newPin2) { setPinChangeErr("PINs não coincidem."); return; }
    const cpfDigits = (pinChangePessoa.cpf || "").replace(/\D/g, "");
    if (newPin1 === cpfDigits.slice(0, 4)) { setPinChangeErr("O novo PIN não pode ser igual aos 4 primeiros dígitos do seu CPF (ainda é o inicial)."); return; }

    // v7.6: sincroniza PIN nos 3 records (pessoa, employee, manager) usando o helper
    if (typeof onUpdate === "function") {
      syncPinAcrossLinkedRecords({
        newPin: newPin1,
        identityCpf: pinChangePessoa.cpf,
        pessoa: pinChangePessoa,
        pessoas: pessoas || [],
        employees: employees || [],
        managers: managers || [],
        onUpdate,
      });
    } else {
      // Fallback (deveria nunca acontecer): atualiza só pessoas via callback dedicado
      const updated = pessoas.map(p => p.id === pinChangePessoa.id ? { ...p, pin: newPin1, mustChangePin: false } : p);
      onUpdatePessoas(updated);
    }
    // Prossegue com o login
    const updatedPessoa = { ...pinChangePessoa, pin: newPin1, mustChangePin: false };
    setPinChangePessoa(null);
    const options = buildLoginChoicesFromPessoa(updatedPessoa);
    if (options.length === 1) options[0].action();
    else if (options.length > 1) setChoices({ name: updatedPessoa.name, options });
  }

  function tryLogin() {
    if (isBlocked || !termsAccepted) return;
    const clean = credential.trim();
    const cleanCpf = clean.replace(/\D/g,"");
    const cleanPin = pin.trim();

    // Detecta se os dados não carregaram (conexão falhou)
    const noData = owners.length === 0 && managers.length === 0 && employees.length === 0;
    if (noData && !dataLoaded) {
      setErr("⚠️ Erro de conexão — os dados não foram carregados. Verifique sua internet e recarregue a página.");
      return;
    }

    // ── FLUXO NOVO (pessoas) ── Tenta primeiro; fallback para legado
    if (!isEmpId && (pessoas || []).length > 0) {
      const pessoa = pessoas.find(p => (p.cpf || "").replace(/\D/g, "") === cleanCpf && String(p.pin) === cleanPin);
      if (pessoa) {
        // v8.22.3: bloqueio imediato pra pessoa inativada
        if (pessoa.inactive === true) {
          setErr("Acesso desativado. Fale com o departamento pessoal.");
          return;
        }
        if (pessoa.mustChangePin) {
          setErr(""); setAttempts(0);
          setPinChangePessoa(pessoa);
          setNewPin1(""); setNewPin2(""); setPinChangeErr("");
          return;
        }
        // Monta opções a partir das permissões em cada restaurante
        const options = buildLoginChoicesFromPessoa(pessoa);
        if (options.length === 0) {
          setErr("Você não tem acesso a nenhum perfil. Peça ao gestor para conceder permissões."); return;
        }
        // Regras:
        // - Se só 1 perfil e há onLoginShell: vai pro shell também (uniforme)
        // - Se >1 perfil: vai direto pro shell (sem seletor) — shell tem sidebar com tudo
        // - Fallback: se não tem onLoginShell, usa a 1a opção
        if (onLoginShell) {
          setErr(""); setAttempts(0); onLoginShell(pessoa); return;
        }
        if (options.length === 1) { setErr(""); setAttempts(0); options[0].action(); return; }
        setErr(""); setAttempts(0); setChoices({ name: pessoa.name, options }); return;
      }
    }

    if (!isEmpId) {
      // 1) Master — acesso direto, sem tela de escolha
      const superUser = owners.find(s => s.cpf?.replace(/\D/g,"") === cleanCpf && String(s.pin) === cleanPin);
      if (superUser) { setErr(""); setAttempts(0); onLoginOwner(superUser); return; }

      // 2) Buscar empregado e gestor pelo mesmo CPF (login unificado)
      const emp = employees.find(e => e.cpf?.replace(/\D/g,"") === cleanCpf);
      const mgr = managers.find(m => m.cpf?.replace(/\D/g,"") === cleanCpf);

      // PIN unificado: aceita PIN do empregado OU do gestor
      const pinMatch = (emp && String(emp.pin) === cleanPin) || (mgr && String(mgr.pin) === cleanPin);

      if (!pinMatch) {
        // Falhou
        const na = attempts + 1;
        setAttempts(na);
        if (na >= 5) {
          setBlockedUntil(new Date(Date.now() + 30000));
          setAttempts(0);
          setErr("Muitas tentativas. Aguarde 30 segundos.");
        } else {
          setErr(`Credenciais incorretas. ${5-na} tentativa${5-na!==1?"s":""} restante${5-na!==1?"s":""}.`);
        }
        return;
      }

      // PIN bateu — montar opções
      const found = [];

      // v8.22.3: bloqueio imediato — manager.inactive (consistência com pessoa inativa)
      if (mgr && !(mgr.inactive === true)) {
        found.push({ label:"Gestor Adm.", icon:"📊", action:()=>{ setChoices(null); onLoginManager(mgr); } });
      }

      // v8.22.3: empregado inativo (com ou sem inactiveFrom) → bloqueado
      if (emp && !(emp.inactive === true)) {
        const restDoEmp = restaurants.find(r=>r.id===emp.restaurantId);
        if (restDoEmp?.financeiro?.status === "inadimplente") {
          if (!mgr) { setErr("⚠️ O acesso ao sistema está suspenso. Entre em contato com o administrador do restaurante."); return; }
          // Se é gestor+empregado e restaurante inadimplente, permite só gestor
        } else {
          found.push({ label:"Empregado", icon:"👤", action:()=>{ setChoices(null); localStorage.setItem("apptip_empid", emp.id); localStorage.setItem("apptip_userid", emp.id); onLoginEmployee(emp); } });
        }
      }

      if (found.length === 1) { setErr(""); setAttempts(0); found[0].action(); return; }
      if (found.length > 1) {
        const name = mgr?.name ?? emp?.name ?? "Usuário";
        setErr(""); setAttempts(0); setChoices({ name, options: found }); return;
      }
      if (found.length === 0) {
        // CPF existe mas sem papel ativo
        const na = attempts + 1;
        setAttempts(na);
        if (na >= 5) { setBlockedUntil(new Date(Date.now() + 30000)); setAttempts(0); setErr("Muitas tentativas. Aguarde 30 segundos."); }
        else setErr(`Credenciais incorretas. ${5-na} tentativa${5-na!==1?"s":""} restante${5-na!==1?"s":""}.`);
        return;
      }
    } else {
      // Por ID — busca empregado, mas verifica se também é gestor
      const emp = employees.find(e => e.empCode?.toUpperCase() === clean.toUpperCase() && String(e.pin) === cleanPin);
      if (emp) {
        // v8.22.3: bloqueia inativo imediatamente, com ou sem inactiveFrom
        if (emp.inactive === true) {
          setErr("Acesso desativado. Fale com o departamento pessoal."); return;
        }
        // Verificar se o CPF desse empregado também é de um gestor
        const empCpf = emp.cpf?.replace(/\D/g,"");
        let mgr = empCpf ? managers.find(m => m.cpf?.replace(/\D/g,"") === empCpf) : null;
        // Fallback: linkedManagerId ou nome exato no mesmo restaurante
        if (!mgr && emp.linkedManagerId) mgr = managers.find(m => m.id === emp.linkedManagerId);
        if (!mgr && emp.name) mgr = managers.find(m => m.name === emp.name && (m.restaurantIds ?? []).includes(emp.restaurantId));
        if (mgr) {
          // Dual-role — mostrar tela de escolha
          const found = [];
          found.push({ label:"Gestor Adm.", icon:"📊", action:()=>{ setChoices(null); onLoginManager(mgr); } });
          const restDoEmp = restaurants.find(r=>r.id===emp.restaurantId);
          if (!(restDoEmp?.financeiro?.status === "inadimplente")) {
            found.push({ label:"Empregado", icon:"👤", action:()=>{ setChoices(null); localStorage.setItem("apptip_empid", emp.id); localStorage.setItem("apptip_userid", emp.id); onLoginEmployee(emp); } });
          }
          if (found.length === 1) { setErr(""); setAttempts(0); found[0].action(); return; }
          const name = mgr.name ?? emp.name ?? "Usuário";
          setErr(""); setAttempts(0); setChoices({ name, options: found }); return;
        }
        // Só empregado
        localStorage.setItem("apptip_empid", emp.id);
        localStorage.setItem("apptip_userid", emp.id);
        setErr(""); setAttempts(0); onLoginEmployee(emp); return;
      }
      // Falhou
      const na = attempts + 1;
      setAttempts(na);
      if (na >= 5) { setBlockedUntil(new Date(Date.now() + 30000)); setAttempts(0); setErr("Muitas tentativas. Aguarde 30 segundos."); }
      else setErr(`Credenciais incorretas. ${5-na} tentativa${5-na!==1?"s":""} restante${5-na!==1?"s":""}.`);
    }
  }

  // Tela de troca obrigatória de PIN (primeiro login)
  if (pinChangePessoa) return (
    <div style={{minHeight:"100vh",background:"var(--bg)",display:"flex",alignItems:"center",justifyContent:"center",padding:24,fontFamily:"'DM Sans',sans-serif"}}>
      <div style={{maxWidth:400,width:"100%",background:"var(--card-bg)",borderRadius:14,padding:28,boxShadow:"0 8px 32px rgba(0,0,0,0.08)"}}>
        <div style={{textAlign:"center",marginBottom:24}}>
          <div style={{fontSize:36,marginBottom:12}}>🔑</div>
          <h2 style={{color:"var(--text)",margin:"0 0 8px",fontSize:22,fontWeight:800}}>Defina seu novo PIN</h2>
          <p style={{color:"var(--text3)",fontSize:13,lineHeight:1.5}}>
            Olá, {pinChangePessoa.name?.split(" ")[0]}! Este é seu primeiro acesso. Por segurança, defina um novo PIN de 4 dígitos que só você conhece.
          </p>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div>
            <label style={S.label}>Novo PIN (4 dígitos)</label>
            <input type="password" inputMode="numeric" maxLength={4} value={newPin1} onChange={e=>setNewPin1(e.target.value.replace(/\D/g,"").slice(0,4))} placeholder="••••" style={{...S.input,letterSpacing:8,fontSize:22,textAlign:"center",fontFamily:"'DM Mono',monospace"}}/>
          </div>
          <div>
            <label style={S.label}>Confirmar PIN</label>
            <input type="password" inputMode="numeric" maxLength={4} value={newPin2} onChange={e=>setNewPin2(e.target.value.replace(/\D/g,"").slice(0,4))} placeholder="••••" style={{...S.input,letterSpacing:8,fontSize:22,textAlign:"center",fontFamily:"'DM Mono',monospace"}} onKeyDown={e=>e.key==="Enter"&&submitNewPin()}/>
          </div>
          {pinChangeErr && <div style={{background:"var(--red-bg)",border:"1px solid var(--red)33",borderRadius:8,padding:"8px 12px",color:"var(--red)",fontSize:13}}>{pinChangeErr}</div>}
          <button onClick={submitNewPin} style={S.btnPrimary}>Salvar e Continuar →</button>
          <button onClick={()=>{setPinChangePessoa(null);setPin("");setNewPin1("");setNewPin2("");}} style={{...S.btnSecondary,textAlign:"center"}}>← Cancelar</button>
        </div>
      </div>
    </div>
  );

  // Tela de escolha de papel
  if (choices) return (
    <div style={{minHeight:"100vh",background:"var(--bg)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24,fontFamily:"'DM Sans',sans-serif"}}>
      <div style={{width:"100%",maxWidth:380}}>
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{fontSize:40,marginBottom:12}}>🍽️</div>
          <h1 style={{fontSize:22,fontWeight:800,color:"var(--text)",margin:"0 0 6px"}}>Olá, {choices.name}!</h1>
          <p style={{color:"var(--text3)",fontSize:14}}>Como deseja entrar?</p>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {choices.options.map(opt=>(
            <button key={opt.label} onClick={opt.action}
              style={{display:"flex",alignItems:"center",gap:16,padding:"20px 24px",borderRadius:16,border:`1px solid var(--border)`,background:"var(--card-bg)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",textAlign:"left",boxShadow:"0 2px 8px rgba(0,0,0,0.05)",transition:"all 0.15s"}}
              onMouseEnter={e=>e.currentTarget.style.borderColor=ac}
              onMouseLeave={e=>e.currentTarget.style.borderColor="var(--border)"}>
              <span style={{fontSize:32}}>{opt.icon}</span>
              <div>
                <div style={{color:"var(--text)",fontWeight:700,fontSize:16}}>{opt.label}</div>
                <div style={{color:"var(--text3)",fontSize:13,marginTop:2}}>
                  {opt.label==="Master"&&"Gerenciar restaurantes e equipes"}
                  {opt.label==="Gestor Adm."&&"Gerenciar gorjetas, escala e equipe"}
                  {opt.label==="Empregado"&&"Ver meu extrato, escala e comunicados"}
                </div>
              </div>
              <span style={{marginLeft:"auto",color:"var(--text3)",fontSize:18}}>›</span>
            </button>
          ))}
          <button onClick={()=>{setChoices(null);setPin("");}}
            style={{...S.btnSecondary,width:"100%",textAlign:"center",marginTop:4}}>
            ← Voltar
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{minHeight:"100vh",background:"var(--bg)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24,fontFamily:"'DM Sans',sans-serif"}}>

      {/* Botão de tema */}
      <div style={{position:"fixed",top:16,right:16}}>
        <button onClick={toggleTheme} style={{background:"var(--bg1)",border:"1px solid var(--border)",borderRadius:20,padding:"8px 14px",cursor:"pointer",fontSize:16,color:"var(--text2)"}}>
          {theme==="dark"?"☀️":"🌙"}
        </button>
      </div>

      <div style={{width:"100%",maxWidth:380}}>
        {/* Logo */}
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{fontSize:40,marginBottom:12}}>🍽️</div>
          <h1 style={{fontSize:28,fontWeight:800,color:"var(--text)",margin:"0 0 6px",letterSpacing:-0.5}}>
            App<span style={{color:ac}}>Tip</span>
          </h1>
          <p style={{color:"var(--text3)",fontSize:14}}>Gestão de gorjetas para restaurantes</p>
        </div>

        {/* Card de login */}
        <div style={{background:"var(--card-bg)",borderRadius:20,padding:"28px 24px",border:"1px solid var(--border)",boxShadow:"0 4px 24px rgba(0,0,0,0.06)"}}>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>

            <div>
              <label style={S.label}>CPF ou ID do empregado</label>
              <input
                value={credential}
                onChange={e => {
                  const v = e.target.value;
                  if (/^\d/.test(v)) setCredential(maskCpf(v));
                  else setCredential(v.toUpperCase());
                }}
                placeholder="000.000.000-00 ou LBZ0001"
                style={{...S.input}}
                disabled={isBlocked}
                autoComplete="username"
              />
              {credential.length > 2 && (
                <div style={{fontSize:11,color:"var(--text3)",marginTop:4}}>
                  {isEmpId ? "👤 Acesso de empregado" : isCpf ? "🔐 Verificando perfis disponíveis" : ""}
                </div>
              )}
            </div>

            <div>
              <label style={S.label}>PIN</label>
              <input
                type="password" inputMode="numeric" maxLength={4}
                value={pin}
                onChange={e => setPin(e.target.value)}
                placeholder="••••"
                style={{...S.input, letterSpacing:8, fontSize:20, textAlign:"center", fontFamily:"'DM Mono',monospace"}}
                onKeyDown={e => e.key==="Enter" && tryLogin()}
                disabled={isBlocked}
                autoComplete="current-password"
              />
            </div>

            {!termsAccepted && (
              <label style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer"}}>
                <input type="checkbox" checked={termsAccepted}
                  onChange={e=>{setTermsAccepted(e.target.checked);if(e.target.checked)localStorage.setItem("apptip_terms","1");}}
                  style={{width:16,height:16,marginTop:2,accentColor:ac,flexShrink:0}}/>
                <span style={{color:"var(--text3)",fontSize:12,lineHeight:1.5}}>
                  Li e aceito a{" "}
                  <button onClick={e=>{e.preventDefault();window.__showPrivacy?.();}}
                    style={{background:"none",border:"none",color:ac,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:12,padding:0,textDecoration:"underline"}}>
                    Política de Privacidade
                  </button>
                </span>
              </label>
            )}

            {err && (
              <div style={{background:isBlocked?"#f59e0b12":err.includes("conexão")?"#3b82f612":"var(--red-bg)",border:`1px solid ${isBlocked?"#f59e0b33":err.includes("conexão")?"#3b82f633":"var(--red)33"}`,borderRadius:8,padding:"10px 12px",color:isBlocked?"#d97706":err.includes("conexão")?"#2563eb":"var(--red)",fontSize:13,fontWeight:500}}>
                {err}
                {err.includes("conexão") && (
                  <button onClick={()=>window.location.reload()}
                    style={{display:"block",marginTop:8,padding:"6px 16px",borderRadius:8,background:"#3b82f6",border:"none",color:"#fff",fontWeight:700,cursor:"pointer",fontSize:12,fontFamily:"'DM Sans',sans-serif"}}>
                    Recarregar página
                  </button>
                )}
              </div>
            )}

            <button onClick={tryLogin}
              disabled={isBlocked || !termsAccepted || !credential.trim() || !pin.trim()}
              style={{...S.btnPrimary, opacity:(isBlocked||!termsAccepted||!credential.trim()||!pin.trim())?0.5:1, cursor:(isBlocked||!termsAccepted||!credential.trim()||!pin.trim())?"not-allowed":"pointer", marginTop:4}}>
              Entrar →
            </button>


          </div>
        </div>

        <div style={{textAlign:"center",marginTop:20}}>
          {termsAccepted && (
            <button onClick={()=>window.__showPrivacy?.()}
              style={{background:"none",border:"none",color:"var(--text3)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:12,padding:0}}>
              Política de Privacidade
            </button>
          )}
        </div>

        {/* Link para landing page */}
        <div style={{textAlign:"center",marginTop:16,paddingTop:16,borderTop:"1px solid var(--border)"}}>
          <button onClick={onGoHome}
            style={{background:"none",border:"none",color:"var(--text3)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,padding:0}}>
            Ainda não usa o sistema? <span style={{color:"var(--ac-text)",fontWeight:700}}>Conheça como funciona →</span>
          </button>
        </div>
      </div>
    </div>
  );
}

//
// FIRST SETUP
//
function FirstSetup({ onDone }) {
  const INVITE = "apptip@2024"; // senha de convite — mude aqui se quiser
  const [step, setStep] = useState("invite"); // "invite" | "form"
  const [invite, setInvite] = useState("");
  const [inviteErr, setInviteErr] = useState("");
  const [form, setForm] = useState({ name:"",cpf:"",pin:"",pin2:"" });
  const [err, setErr] = useState("");

  function checkInvite() {
    if (invite.trim() !== INVITE) { setInviteErr("Senha de convite incorreta."); return; }
    setStep("form");
  }

  function submit() {
    if (!form.name.trim()) { setErr("Informe o nome."); return; }
    if (!form.cpf.trim()) { setErr("Informe o CPF."); return; }
    if (form.pin.length !== 4 || !/^\d{4}$/.test(form.pin)) { setErr("PIN deve ter exatamente 4 dígitos numéricos."); return; }
    if (form.pin !== form.pin2) { setErr("PINs não coincidem."); return; }
    onDone({ id: Date.now().toString(), name: form.name.trim(), cpf: form.cpf.trim(), pin: form.pin });
  }

  return (
    <div style={{minHeight:"100vh",background:"var(--bg)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Sans',sans-serif",padding:24}}>
      <div style={{...S.card,maxWidth:360,width:"100%",boxShadow:"0 4px 24px rgba(0,0,0,0.08)"}}>
        <div style={{textAlign:"center",marginBottom:24}}>
          <div style={{fontSize:40,marginBottom:8}}>🍽️</div>
          <h2 style={{color:"var(--text)",margin:"0 0 6px",fontSize:22,fontWeight:800}}>App<span style={{color:ac}}>Tip</span></h2>
          <p style={{color:"var(--text3)",fontSize:13}}>{step==="invite"?"Digite a senha de convite para continuar":"Cadastro do primeiro Master"}</p>
        </div>

        {step === "invite" ? (
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div>
              <label style={S.label}>Senha de convite</label>
              <input type="password" value={invite} onChange={e=>setInvite(e.target.value)} placeholder="••••••••" style={S.input} onKeyDown={e=>e.key==="Enter"&&checkInvite()}/>
            </div>
            {inviteErr && <div style={{background:"var(--red-bg)",border:"1px solid var(--red)33",borderRadius:8,padding:"8px 12px",color:"var(--red)",fontSize:13}}>{inviteErr}</div>}
            <button onClick={checkInvite} style={S.btnPrimary}>Continuar →</button>
          </div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div><label style={S.label}>Nome completo</label><input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} style={S.input}/></div>
            <div><label style={S.label}>CPF</label><input value={form.cpf} onChange={e=>setForm({...form,cpf:maskCpf(e.target.value)})} placeholder="000.000.000-00" style={S.input} inputMode="numeric"/></div>
            <div><label style={S.label}>PIN (4 dígitos)</label><input type="password" maxLength={4} value={form.pin} onChange={e=>setForm({...form,pin:e.target.value})} style={S.input}/></div>
            <div><label style={S.label}>Confirmar PIN</label><input type="password" maxLength={4} value={form.pin2} onChange={e=>setForm({...form,pin2:e.target.value})} style={S.input} onKeyDown={e=>e.key==="Enter"&&submit()}/></div>
            {err && <div style={{background:"var(--red-bg)",border:"1px solid var(--red)33",borderRadius:8,padding:"8px 12px",color:"var(--red)",fontSize:13}}>{err}</div>}
            <button onClick={submit} style={S.btnPrimary}>Criar e Entrar →</button>
          </div>
        )}
      </div>
    </div>
  );
}

//
// HOME
//
function Home({ onLogin }) {
  const [formData, setFormData] = useState({ nome:"", email:"", restaurante:"", empregados:"", mensagem:"" });
  const [formSent, setFormSent] = useState(false);
  const [formSending, setFormSending] = useState(false);

  async function sendForm() {
    if (!formData.nome.trim()) return;
    setFormSending(true);
    const msg = encodeURIComponent(
      `Olá! Tenho interesse no sistema.\n\nNome: ${formData.nome}\nRestaurante: ${formData.restaurante || "—"}\nNº empregados: ${formData.empregados || "—"}${formData.mensagem ? `\n\nMensagem: ${formData.mensagem}` : ""}`
    );
    window.open(`https://wa.me/5511985499821?text=${msg}`, "_blank");
    setTimeout(() => { setFormSent(true); setFormSending(false); }, 800);
  }

  const FEATURES = [
    { icon:"💸", title:"Gorjetas transparentes", desc:"Cálculo e distribuição automática por área e cargo. Cada empregado vê exatamente o que recebeu, bruto e líquido, sem dúvidas." },
    { icon:"📅", title:"Escala inteligente", desc:"Controle de folgas, faltas, férias e compensações integrado ao cálculo de gorjetas. Escala base gerada automaticamente pelo horário contratual." },
    { icon:"👥", title:"Gestão de equipe", desc:"Cadastro completo, cargos, pontos e acesso individual para cada empregado. Histórico preservado mesmo após inativação." },
    { icon:"🕐", title:"Horários com IA", desc:"Cadastre dias de trabalho e folga, preencha horários ou use o assistente de IA — descreva em linguagem natural e o sistema gera os horários automaticamente. Validação de carga semanal, interjornada e hora noturna." },
    { icon:"❓", title:"FAQ com assistente de IA", desc:"Base de perguntas e respostas para sua equipe. Perguntas automáticas sobre gorjetas e regras, mais IA para ajudar o gestor a redigir." },
    { icon:"📢", title:"Comunicados com IA", desc:"Envie avisos para toda a equipe ou áreas específicas. Assistente de IA ajuda a redigir. Acompanhe quem leu e confirmou." },
    { icon:"💬", title:"Canal com o DP", desc:"Canal direto, inclusive anônimo, para comunicação entre equipe e departamento pessoal. Sugestões, denúncias, atestados e dúvidas trabalhistas." },

    { icon:"👔", title:"Perfis de gestor", desc:"Gestor Administrativo com acesso completo ou Líder Operacional com visão restrita. Permissões granulares, criação de gestores e CPF obrigatório para segurança." },
    { icon:"🛡️", title:"Privacidade de dados", desc:"Gestores controlam a visibilidade dos dados. Com o modo privacidade, a equipe de administradores não acessa valores de gorjetas, CPFs, mensagens do DP ou comunicados — garantindo sigilo total da operação." },
    { icon:"🔒", title:"Segurança e controle", desc:"Acesso por PIN e CPF, permissões por perfil, modo somente leitura, lixeira com restauração e auditoria completa. Cada pessoa vê apenas o que deve." },
    { icon:"📱", title:"100% no celular", desc:"Sem app para instalar. Acessa pelo navegador em qualquer smartphone. Gestor gerencia escala, horários e caixa de entrada direto pelo celular. Empregado com portal próprio e seguro." },
  ];

  const PLANOS = [
    { nome:"Starter",      emp:"até 10",    precoAnual:"R$87,30",  precoMensal:"R$97",   anualSub:"por mês no plano anual", mensalSub:"ou R$97/mês no mensal",  destaque:false, cta:"Começar agora", multi:false },
    { nome:"Básico",       emp:"até 20",    precoAnual:"R$168,30", precoMensal:"R$187",  anualSub:"por mês no plano anual", mensalSub:"ou R$187/mês no mensal", destaque:true,  cta:"Começar agora", multi:true },
    { nome:"Profissional", emp:"até 50",    precoAnual:"R$357,30", precoMensal:"R$397",  anualSub:"por mês no plano anual", mensalSub:"ou R$397/mês no mensal", destaque:false, cta:"Começar agora", multi:true },
    { nome:"Enterprise",   emp:"51 a 100",  precoAnual:"R$7,99",   precoMensal:null,     anualSub:"por funcionário/mês",    mensalSub:"pagamento mensal",       destaque:false, cta:"Falar com a gente", multi:true },
    { nome:"On Demand",    emp:"+100",      precoAnual:null,       precoMensal:null,     anualSub:"",                       mensalSub:"Sob orçamento",          destaque:false, cta:"Falar com a gente", multi:true },
  ];

  const ac = "#d4a017";

  return (
    <div style={{fontFamily:"'DM Sans',sans-serif",background:"#faf8f4",color:"#2d2416",minHeight:"100vh"}}>

      {/* NAV */}
      <nav style={{position:"sticky",top:0,zIndex:100,background:"rgba(250,248,244,0.96)",backdropFilter:"blur(12px)",borderBottom:"1px solid #ede8df",padding:"0 24px",display:"flex",justifyContent:"space-between",alignItems:"center",height:64}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:22}}>🍽️</span>
          <span style={{fontWeight:800,fontSize:20,color:"#2d2416",letterSpacing:-0.5}}>App<span style={{color:ac}}>Tip</span></span>
        </div>
        <div style={{display:"flex",gap:16,alignItems:"center"}}>
          <a href="#funcionalidades" style={{color:"#8c7a5e",fontSize:14,textDecoration:"none",fontWeight:500}}>Funcionalidades</a>
          <a href="#precos" style={{color:"#8c7a5e",fontSize:14,textDecoration:"none",fontWeight:500}}>Preços</a>
          <a href="#contato" style={{color:"#8c7a5e",fontSize:14,textDecoration:"none",fontWeight:500}}>Contato</a>
          <button onClick={onLogin} style={{padding:"9px 22px",borderRadius:20,border:"none",background:ac,color:"#fff",fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:14,letterSpacing:-0.3}}>Entrar →</button>
        </div>
      </nav>

      {/* HERO */}
      <section style={{padding:"90px 24px 80px",textAlign:"center",background:"linear-gradient(180deg,#faf8f4 0%,#f5f0e8 100%)"}}>
        <div style={{maxWidth:680,margin:"0 auto"}}>
          <div style={{display:"inline-flex",alignItems:"center",gap:8,background:"#d4a01722",border:"1px solid #d4a01744",borderRadius:20,padding:"6px 16px",marginBottom:28}}>
            <span style={{color:ac,fontSize:12,fontWeight:700,letterSpacing:0.3}}>✦ Gestão completa para restaurantes</span>
          </div>
          <h1 style={{fontSize:"clamp(34px,6vw,58px)",fontWeight:800,lineHeight:1.1,margin:"0 0 20px",letterSpacing:-1.5,color:"#1c1208"}}>
            Gorjetas distribuídas<br/><span style={{color:ac}}>com transparência total</span>
          </h1>
          <p style={{color:"#8c7a5e",fontSize:"clamp(15px,2vw,18px)",lineHeight:1.7,marginBottom:40,maxWidth:520,margin:"0 auto 40px"}}>
            Esta plataforma automatiza o cálculo e distribuição de gorjetas, controla escala, horários e comunicados com IA — tudo pelo celular, sem app para instalar.
          </p>
          <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap"}}>
            <a href="#contato" style={{padding:"15px 32px",borderRadius:12,background:ac,color:"#fff",fontWeight:700,fontSize:16,textDecoration:"none",display:"inline-block",boxShadow:"0 4px 20px #d4a01744"}}>
              Quero conhecer →
            </a>
            <a href="#funcionalidades" style={{padding:"15px 32px",borderRadius:12,border:"1px solid #ede8df",color:"#5c4a2e",fontSize:16,textDecoration:"none",display:"inline-block",background:"#fff",fontWeight:500}}>
              Ver funcionalidades
            </a>
          </div>
          <p style={{color:"#b0996e",fontSize:13,marginTop:24}}>Sem taxa de adesão · Suporte incluso · Cancele quando quiser</p>
        </div>
      </section>

      {/* STATS */}
      <section style={{background:"#fff",padding:"48px 24px",borderTop:"1px solid #ede8df",borderBottom:"1px solid #ede8df"}}>
        <div style={{maxWidth:700,margin:"0 auto",display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:24,textAlign:"center"}}>
          {[["100%","Mobile first — sem app instalar"],["IA","Horários, FAQ e comunicados assistidos"],["LGPD","Conformidade e dados protegidos"]].map(([n,l])=>(
            <div key={l}>
              <div style={{fontSize:32,fontWeight:800,color:ac,marginBottom:6,letterSpacing:-1,fontFamily:"'DM Mono',monospace"}}>{n}</div>
              <div style={{color:"#8c7a5e",fontSize:13,fontWeight:500}}>{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FUNCIONALIDADES */}
      <section id="funcionalidades" style={{padding:"80px 24px",background:"#faf8f4"}}>
        <div style={{maxWidth:960,margin:"0 auto"}}>
          <div style={{textAlign:"center",marginBottom:56}}>
            <h2 style={{fontSize:"clamp(24px,4vw,38px)",fontWeight:800,margin:"0 0 12px",letterSpacing:-0.8,color:"#1c1208"}}>Tudo que sua equipe precisa</h2>
            <p style={{color:"#8c7a5e",fontSize:16}}>Gorjetas, escala, horários com IA, comunicados com IA e muito mais</p>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:20}}>
            {FEATURES.map(f=>(
              <div key={f.title} style={{padding:"28px",borderRadius:16,border:"1px solid #ede8df",background:"#fff",boxShadow:"0 2px 12px rgba(0,0,0,0.03)"}}>
                <div style={{fontSize:32,marginBottom:14}}>{f.icon}</div>
                <h3 style={{fontSize:16,fontWeight:700,margin:"0 0 8px",color:"#1c1208"}}>{f.title}</h3>
                <p style={{color:"#8c7a5e",fontSize:14,lineHeight:1.6,margin:0}}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMO FUNCIONA */}
      <section style={{padding:"80px 24px",background:"#f0ebe0"}}>
        <div style={{maxWidth:800,margin:"0 auto",textAlign:"center"}}>
          <h2 style={{color:"#1c1208",fontSize:"clamp(24px,4vw,38px)",fontWeight:800,margin:"0 0 12px",letterSpacing:-0.8}}>Como funciona</h2>
          <p style={{color:"#8c7a5e",fontSize:16,marginBottom:56}}>Do cadastro ao extrato do empregado em minutos</p>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:32}}>
            {[
              ["1","Configure o restaurante","Cadastre cargos, empregados, horários e regras fiscais. Defina dias de trabalho e folga — horários depois."],
              ["2","Lance as gorjetas","Informe o valor diário. O sistema distribui automaticamente por cargo, área e escala."],
              ["3","Comunique a equipe","Envie comunicados com IA, gerencie o FAQ automático e mantenha todos informados."],
              ["4","Equipe acompanha tudo","Cada empregado vê extrato, escala, horários e comunicados direto pelo celular."],
            ].map(([n,t,d])=>(
              <div key={n}>
                <div style={{width:52,height:52,borderRadius:"50%",background:ac,color:"#fff",fontSize:22,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px",fontFamily:"'DM Mono',monospace"}}>{n}</div>
                <h3 style={{color:"#1c1208",fontSize:16,fontWeight:700,margin:"0 0 8px"}}>{t}</h3>
                <p style={{color:"#8c7a5e",fontSize:14,lineHeight:1.6,margin:0}}>{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EARLY ADOPTER + PREÇOS */}
      <section id="precos" style={{padding:"0",background:"#faf8f4"}}>

        {/* Banner Early Adopter */}
        <div style={{background:"linear-gradient(135deg,#1c1208 0%,#2d1f0e 60%,#3a2a10 100%)",padding:"clamp(40px,6vw,72px) 24px",position:"relative",overflow:"hidden"}}>
          <div style={{position:"absolute",top:-60,right:-60,width:240,height:240,borderRadius:"50%",background:"radial-gradient(circle,#d4a01730 0%,transparent 70%)",pointerEvents:"none"}}/>
          <div style={{position:"absolute",bottom:-40,left:-40,width:180,height:180,borderRadius:"50%",background:"radial-gradient(circle,#d4a01718 0%,transparent 70%)",pointerEvents:"none"}}/>
          <div style={{maxWidth:760,margin:"0 auto",textAlign:"center",position:"relative",zIndex:1}}>
            <div style={{display:"inline-block",background:"linear-gradient(135deg,#d4a017,#e8b84a)",color:"#1c1208",fontSize:12,fontWeight:800,padding:"5px 18px",borderRadius:20,marginBottom:18,letterSpacing:0.8,textTransform:"uppercase"}}>🚀 Oferta limitada — Early Adopter</div>
            <h2 style={{fontSize:"clamp(24px,5vw,42px)",fontWeight:900,color:"#fff",margin:"0 0 10px",letterSpacing:-1,lineHeight:1.15}}>
              Primeiros <span style={{color:"#d4a017"}}>30 clientes</span> ganham
            </h2>
            <p style={{fontSize:"clamp(20px,4vw,32px)",fontWeight:800,color:"#d4a017",margin:"0 0 16px",lineHeight:1.2}}>
              30% de desconto permanente
            </p>
            <p style={{color:"#b0996e",fontSize:"clamp(13px,2vw,16px)",maxWidth:480,margin:"0 auto 28px",lineHeight:1.6}}>
              Assine agora e pague menos para sempre. O desconto vale enquanto sua assinatura estiver ativa — sem prazo, sem truques.
            </p>

            {/* Preços com desconto */}
            <div style={{display:"flex",justifyContent:"center",gap:12,flexWrap:"wrap",marginBottom:28}}>
              {[
                {plano:"Starter",       de:"R$97",  por:"R$67,90",  emp:"até 10"},
                {plano:"Básico",        de:"R$187", por:"R$130,90", emp:"até 20"},
                {plano:"Profissional",  de:"R$397", por:"R$277,90", emp:"até 50"},
              ].map(p=>(
                <div key={p.plano} style={{background:"rgba(255,255,255,0.06)",border:"1px solid #d4a01733",borderRadius:14,padding:"16px 22px",minWidth:150,backdropFilter:"blur(4px)"}}>
                  <div style={{color:"#e8d5a8",fontSize:12,fontWeight:700,marginBottom:2}}>{p.plano}</div>
                  <div style={{color:"#6b5a3e",fontSize:11,marginBottom:6}}>{p.emp} func.</div>
                  <div style={{color:"#8c7a5e",fontSize:13,textDecoration:"line-through",marginBottom:2}}>{p.de}/mês</div>
                  <div style={{color:"#d4a017",fontSize:22,fontWeight:800,fontFamily:"'DM Mono',monospace",lineHeight:1}}>{p.por}</div>
                  <div style={{color:"#a08060",fontSize:10,marginTop:2}}>/mês</div>
                </div>
              ))}
            </div>

            <a href="#contato" style={{display:"inline-block",padding:"16px 44px",borderRadius:14,background:"linear-gradient(135deg,#d4a017,#e8b84a)",color:"#1c1208",fontWeight:800,fontSize:"clamp(15px,2vw,18px)",textDecoration:"none",letterSpacing:-0.3,boxShadow:"0 6px 28px #d4a01755",transition:"transform 0.2s"}}>
              Garantir meu desconto →
            </a>
            <p style={{color:"#6b5a3e",fontSize:11,marginTop:14}}>Vagas limitadas · Desconto válido enquanto a assinatura estiver ativa</p>
          </div>
        </div>

        {/* Tabela de preços */}
        <div style={{padding:"48px 24px 80px"}}>
        <div style={{maxWidth:1000,margin:"0 auto"}}>
          <div style={{textAlign:"center",marginBottom:48}}>
            <h2 style={{fontSize:"clamp(24px,4vw,38px)",fontWeight:800,margin:"0 0 12px",letterSpacing:-0.8,color:"#1c1208"}}>Todos os planos</h2>
            <p style={{color:"#8c7a5e",fontSize:16}}>Plano anual com <strong style={{color:ac}}>10% de desconto</strong> · Máx. 1 unidade no Starter</p>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(170px,1fr))",gap:16}}>
            {PLANOS.map(p=>(
              <div key={p.nome} style={{borderRadius:16,border:p.destaque?`2px solid ${ac}`:"1px solid #ede8df",padding:"24px 18px",background:p.destaque?"#1c1208":"#fff",position:"relative",boxShadow:p.destaque?"0 8px 32px #d4a01733":"0 2px 12px rgba(0,0,0,0.04)",display:"flex",flexDirection:"column"}}>
                {p.destaque && <div style={{position:"absolute",top:-12,left:"50%",transform:"translateX(-50%)",background:ac,color:"#fff",fontSize:11,fontWeight:700,padding:"4px 14px",borderRadius:20,whiteSpace:"nowrap"}}>Mais popular</div>}
                <div style={{color:p.destaque?"#fff":"#1c1208",fontWeight:800,fontSize:16,marginBottom:4}}>{p.nome}</div>
                <div style={{color:p.destaque?"#d4c4a0":"#8c7a5e",fontSize:12,marginBottom:4}}>{p.emp} funcionários</div>
                <div style={{color:p.destaque?"#887a5e":"#b0996e",fontSize:10,marginBottom:14}}>{p.multi ? "🏢 Múltiplas unidades" : "🏢 Máx. 1 unidade"}</div>

                {/* Preço principal — anual */}
                {p.precoAnual ? (
                  <div style={{marginBottom:8,flex:1}}>
                    <div style={{display:"flex",alignItems:"baseline",gap:2,flexWrap:"wrap",marginBottom:2}}>
                      <span style={{color:p.destaque?ac:"#1c1208",fontSize:p.nome==="Enterprise"?20:26,fontWeight:800,fontFamily:"'DM Mono',monospace",letterSpacing:-0.5,lineHeight:1.1}}>{p.precoAnual}</span>
                      <span style={{color:p.destaque?"#d4c4a0":"#8c7a5e",fontSize:11,whiteSpace:"nowrap"}}>/mês</span>
                    </div>
                    <div style={{color:"#d4a017",fontSize:11,fontWeight:700,marginBottom:p.precoMensal?6:16}}>
                      {p.nome==="Enterprise" ? "por funcionário/mês" : "✦ no plano anual"}
                    </div>
                    {p.precoMensal && (
                      <div style={{color:p.destaque?"#6b5a3e":"#a08060",fontSize:11,marginBottom:16}}>
                        ou {p.precoMensal}/mês no mensal
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{marginBottom:16,flex:1}}>
                    <div style={{color:p.destaque?ac:"#1c1208",fontSize:18,fontWeight:800,marginBottom:4}}>Sob orçamento</div>
                    <div style={{color:p.destaque?"#d4c4a0":"#8c7a5e",fontSize:12}}>{p.mensalSub}</div>
                  </div>
                )}

                {p.multi && (
                  <div style={{color:p.destaque?"#6b5a3e":"#a08060",fontSize:10,marginBottom:12,lineHeight:1.5}}>
                    Distribua os funcionários entre suas unidades como quiser.
                  </div>
                )}

                <a href="#contato" style={{display:"block",textAlign:"center",padding:"11px",borderRadius:10,background:p.destaque?ac:"#f5f0e8",color:p.destaque?"#fff":"#5c4a2e",fontWeight:700,fontSize:14,textDecoration:"none",marginTop:"auto"}}>
                  {p.cta}
                </a>
              </div>
            ))}
          </div>
        </div>
        </div>
      </section>

      {/* CONTATO */}
      <section id="contato" style={{padding:"80px 24px",background:"#f0ebe0"}}>
        <div style={{maxWidth:540,margin:"0 auto"}}>
          <div style={{textAlign:"center",marginBottom:40}}>
            <h2 style={{fontSize:"clamp(24px,4vw,38px)",fontWeight:800,margin:"0 0 12px",letterSpacing:-0.8,color:"#1c1208"}}>Vamos conversar?</h2>
            <p style={{color:"#8c7a5e",fontSize:16}}>Solicite uma demonstração gratuita ou tire suas dúvidas</p>
          </div>

          {/* E-mail — único contato exposto */}
          <div style={{background:"#fff",borderRadius:14,padding:"20px",border:"1px solid #ede8df",textAlign:"center",marginBottom:28}}>
            <div style={{fontSize:28,marginBottom:8}}>📧</div>
            <div style={{fontWeight:700,fontSize:14,color:"#1c1208",marginBottom:6}}>E-mail</div>
            <a href="mailto:contato@apptip.app" style={{color:ac,fontSize:15,fontWeight:700,textDecoration:"none"}}>contato@apptip.app</a>
            <div style={{color:"#b0996e",fontSize:12,marginTop:6}}>Para dúvidas, parcerias e suporte</div>
          </div>

          {/* Formulário → envia via WhatsApp */}
          {formSent ? (
            <div style={{textAlign:"center",padding:"48px",background:"#fff",borderRadius:16,border:"1px solid #ede8df"}}>
              <div style={{fontSize:48,marginBottom:16}}>✅</div>
              <h3 style={{fontSize:20,fontWeight:700,margin:"0 0 8px",color:"#1c1208"}}>Mensagem enviada!</h3>
              <p style={{color:"#8c7a5e"}}>Entraremos em contato em breve. Obrigado pelo interesse no sistema!</p>
            </div>
          ) : (
            <div style={{background:"#fff",borderRadius:16,padding:"36px",border:"1px solid #ede8df",boxShadow:"0 4px 24px rgba(0,0,0,0.06)"}}>
              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                  <div>
                    <label style={{display:"block",fontSize:13,color:"#8c7a5e",marginBottom:6,fontWeight:600}}>Nome *</label>
                    <input value={formData.nome} onChange={e=>setFormData({...formData,nome:e.target.value})} placeholder="Seu nome" style={{width:"100%",padding:"11px 14px",borderRadius:10,border:"1px solid #ede8df",fontFamily:"'DM Sans',sans-serif",fontSize:14,outline:"none",boxSizing:"border-box",background:"#faf8f4"}}/>
                  </div>
                  <div>
                    <label style={{display:"block",fontSize:13,color:"#8c7a5e",marginBottom:6,fontWeight:600}}>Restaurante</label>
                    <input value={formData.restaurante} onChange={e=>setFormData({...formData,restaurante:e.target.value})} placeholder="Nome do restaurante" style={{width:"100%",padding:"11px 14px",borderRadius:10,border:"1px solid #ede8df",fontFamily:"'DM Sans',sans-serif",fontSize:14,outline:"none",boxSizing:"border-box",background:"#faf8f4"}}/>
                  </div>
                </div>
                <div>
                  <label style={{display:"block",fontSize:13,color:"#8c7a5e",marginBottom:6,fontWeight:600}}>Nº de empregados</label>
                  <input value={formData.empregados} onChange={e=>setFormData({...formData,empregados:e.target.value})} placeholder="Ex: 15" type="number" style={{width:"100%",padding:"11px 14px",borderRadius:10,border:"1px solid #ede8df",fontFamily:"'DM Sans',sans-serif",fontSize:14,outline:"none",boxSizing:"border-box",background:"#faf8f4"}}/>
                </div>
                <div>
                  <label style={{display:"block",fontSize:13,color:"#8c7a5e",marginBottom:6,fontWeight:600}}>Mensagem</label>
                  <textarea value={formData.mensagem} onChange={e=>setFormData({...formData,mensagem:e.target.value})} placeholder="Conte um pouco sobre seu restaurante e o que precisa..." rows={3} style={{width:"100%",padding:"11px 14px",borderRadius:10,border:"1px solid #ede8df",fontFamily:"'DM Sans',sans-serif",fontSize:14,outline:"none",resize:"vertical",boxSizing:"border-box",background:"#faf8f4"}}/>
                </div>
                <button onClick={sendForm} disabled={!formData.nome.trim()||formSending}
                  style={{padding:"14px",borderRadius:12,border:"none",background:!formData.nome.trim()?"#e8e0d0":"#25d366",color:"#fff",fontWeight:700,fontSize:16,cursor:!formData.nome.trim()?"not-allowed":"pointer",fontFamily:"'DM Sans',sans-serif",boxShadow:"0 4px 16px #25d36633",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                  <span style={{fontSize:18}}>💬</span>
                  {formSending?"Abrindo WhatsApp...":"Enviar pelo WhatsApp →"}
                </button>
                <p style={{color:"#b0996e",fontSize:12,textAlign:"center",margin:0}}>Você será redirecionado para o WhatsApp para enviar a mensagem</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{background:"#1c1208",padding:"48px 24px",textAlign:"center"}}>
        <div style={{marginBottom:12}}>
          <span style={{fontSize:20}}>🍽️</span>
          <span style={{fontWeight:800,fontSize:18,color:"#fff",marginLeft:8,letterSpacing:-0.5}}>App<span style={{color:ac}}>Tip</span></span>
        </div>
        <p style={{color:"#8c7a5e",fontSize:13,marginBottom:20}}>Transparência e eficiência para equipes de restaurantes</p>
        <div style={{display:"flex",gap:20,justifyContent:"center",flexWrap:"wrap",marginBottom:16}}>
          <button onClick={onLogin} style={{background:"none",border:"none",color:"#8c7a5e",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13}}>Área de acesso</button>
          <a href="mailto:contato@apptip.app" style={{color:"#8c7a5e",fontSize:13,textDecoration:"none"}}>contato@apptip.app</a>
          <button onClick={()=>window.__showPrivacy?.()} style={{background:"none",border:"none",color:"#8c7a5e",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13}}>Política de Privacidade</button>
        </div>
        <p style={{color:"#4a3a2a",fontSize:12}}>© {new Date().getFullYear()} Sistema. Todos os direitos reservados.</p>
      </footer>
    </div>
  );
}


//

//
// FATURA PAGE — página pública de cobrança
//
function FaturaPage({ faturaId, restaurants, onUpdate, loaded }) {
  const [confirmado, setConfirmado] = useState(false);

  if (!loaded) return (
    <div style={{minHeight:"100vh",background:"#faf8f4",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:16,fontFamily:"'DM Sans',sans-serif"}}>
      <div style={{fontSize:40}}>🍽️</div>
      <div style={{color:"#8c7a5e",fontSize:15,animation:"pulse 1.5s ease-in-out infinite"}}>Carregando fatura...</div>
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }`}</style>
    </div>
  );

  // Busca a cobrança pelo ID em todos os restaurantes
  let cobFound = null;
  let restFound = null;
  for (const r of restaurants) {
    const cob = (r.financeiro?.cobrancas??[]).find(c => c.id === faturaId);
    if (cob) { cobFound = cob; restFound = r; break; }
  }

  if (!cobFound || !restFound) return (
    <div style={{minHeight:"100vh",background:"#faf8f4",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Sans',sans-serif"}}>
      <div style={{textAlign:"center",padding:40}}>
        <div style={{fontSize:48,marginBottom:16}}>🔍</div>
        <h2 style={{color:"#1c1208",fontSize:20,fontWeight:700,margin:"0 0 8px"}}>Fatura não encontrada</h2>
        <p style={{color:"#8c7a5e",fontSize:14}}>O link pode ter expirado ou ser inválido.</p>
      </div>
    </div>
  );

  const ac = "#d4a017";
  const isPago = cobFound.status === "pago";
  const isClienteConfirmou = cobFound.clienteConfirmou;
  const vencLabel = cobFound.venc ? new Date(cobFound.venc+"T12:00:00").toLocaleDateString("pt-BR") : null;

  function clienteConfirmarPagamento() {
    const updated = restaurants.map(r => {
      if (r.id !== restFound.id) return r;
      const novasCobs = (r.financeiro?.cobrancas??[]).map(c =>
        c.id === faturaId ? {...c, clienteConfirmou:true, clienteConfirmouEm:new Date().toISOString(), status:"aguardando_confirmacao"} : c
      );
      // NÃO libera acesso — fica aguardando confirmação do admin
      return {...r, financeiro:{...r.financeiro, cobrancas:novasCobs}};
    });
    onUpdate("restaurants", updated);
    setConfirmado(true);
  }

  return (
    <div style={{minHeight:"100vh",background:"#faf8f4",fontFamily:"'DM Sans',sans-serif"}}>
      {/* Header */}
      <div style={{background:"#1c1208",padding:"18px 24px",display:"flex",alignItems:"center",gap:10}}>
        <span style={{fontSize:22}}>🍽️</span>
        <span style={{fontWeight:800,fontSize:18,color:"#fff",letterSpacing:-0.5}}>App<span style={{color:ac}}>Tip</span></span>
        <span style={{color:"#6b5a3e",fontSize:13,marginLeft:8}}>· Fatura</span>
      </div>

      <div style={{maxWidth:480,margin:"0 auto",padding:"32px 20px"}}>

        {/* Status */}
        {isPago && (
          <div style={{background:"#f0fdf4",border:"1px solid #86efac",borderRadius:12,padding:"14px 18px",marginBottom:20,display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:24}}>✅</span>
            <div>
              <div style={{color:"#166534",fontWeight:700,fontSize:15}}>Pagamento confirmado</div>
              <div style={{color:"#16a34a",fontSize:13}}>Esta fatura já foi paga. Obrigado!</div>
            </div>
          </div>
        )}
        {isClienteConfirmou && !isPago && (
          <div style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:12,padding:"14px 18px",marginBottom:20,display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:24}}>⏳</span>
            <div>
              <div style={{color:"#92400e",fontWeight:700,fontSize:15}}>Pagamento em verificação</div>
              <div style={{color:"#a16207",fontSize:13}}>Você confirmou o pagamento. Aguardando validação.</div>
            </div>
          </div>
        )}

        {/* Card da fatura */}
        <div style={{background:"#fff",borderRadius:16,border:"1px solid #ede8df",boxShadow:"0 4px 24px rgba(0,0,0,0.06)",overflow:"hidden",marginBottom:20}}>
          {/* Cabeçalho da fatura */}
          <div style={{background:"#1c1208",padding:"24px",textAlign:"center"}}>
            <div style={{color:"#8c7a5e",fontSize:12,fontWeight:600,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>Fatura</div>
            <div style={{color:"#fff",fontSize:22,fontWeight:800,marginBottom:4}}>{restFound.name}</div>
            <div style={{color:"#d4c4a0",fontSize:14}}>{cobFound.periodoLabel}</div>
          </div>

          {/* Detalhes */}
          <div style={{padding:"24px"}}>
            <div style={{display:"flex",flexDirection:"column",gap:14,marginBottom:24}}>
              {[
                ["Plano", cobFound.forma === "PIX" || cobFound.forma === "Link" ? (restFound.planoId === "pIsento"?"Isento":restFound.planoId === "p10"?"Starter":restFound.planoId === "p20"?"Básico":restFound.planoId === "p50"?"Profissional":restFound.planoId === "p999"?"Enterprise":"On Demand") : cobFound.forma],
                ["Valor", `R$ ${cobFound.valor?.toLocaleString("pt-BR",{minimumFractionDigits:2})}`],
                ...(vencLabel ? [["Vencimento", vencLabel]] : []),
              ].map(([k,v])=>(
                <div key={k} style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingBottom:14,borderBottom:"1px solid #f5f0e8"}}>
                  <span style={{color:"#8c7a5e",fontSize:14}}>{k}</span>
                  <span style={{color:"#1c1208",fontWeight:700,fontSize:15}}>{v}</span>
                </div>
              ))}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{color:"#1c1208",fontWeight:700,fontSize:16}}>Total</span>
                <span style={{color:ac,fontWeight:800,fontSize:22,fontFamily:"'DM Mono',monospace"}}>R$ {cobFound.valor?.toLocaleString("pt-BR",{minimumFractionDigits:2})}</span>
              </div>
            </div>

            {/* Dados de pagamento */}
            {cobFound.chave && (
              <div style={{background:"#faf8f4",borderRadius:12,padding:"16px",border:"1px solid #ede8df",marginBottom:20}}>
                <div style={{color:"#8c7a5e",fontSize:12,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5,marginBottom:10}}>
                  {cobFound.forma === "Link" ? "Link de pagamento" : "Pagamento via PIX"}
                </div>
                {cobFound.forma === "Link" ? (
                  <a href={cobFound.chave} target="_blank" rel="noreferrer"
                    style={{color:ac,fontWeight:700,fontSize:14,wordBreak:"break-all"}}>
                    {cobFound.chave}
                  </a>
                ) : (
                  <>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                      <span style={{color:"#8c7a5e",fontSize:13}}>Chave PIX</span>
                      <span style={{color:"#1c1208",fontWeight:700,fontSize:14,fontFamily:"'DM Mono',monospace"}}>{cobFound.chave}</span>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between"}}>
                      <span style={{color:"#8c7a5e",fontSize:13}}>Favorecido</span>
                      <span style={{color:"#1c1208",fontSize:13}}>Gustavo Rodrigues da Silva</span>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Botão confirmar */}
            {!isPago && !isClienteConfirmou && !confirmado && (
              <button onClick={clienteConfirmarPagamento}
                style={{width:"100%",padding:"16px",borderRadius:12,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:16,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",boxShadow:"0 4px 16px #d4a01744"}}>
                Já efetuei o pagamento ✓
              </button>
            )}
            {(confirmado || isClienteConfirmou) && !isPago && (
              <div style={{textAlign:"center",padding:"16px",borderRadius:12,background:"#f0fdf4",border:"1px solid #86efac"}}>
                <div style={{fontSize:32,marginBottom:8}}>✅</div>
                <div style={{color:"#166534",fontWeight:700,fontSize:15,marginBottom:4}}>Confirmação recebida!</div>
                <div style={{color:"#16a34a",fontSize:13}}>Aguarde a validação do pagamento. Seu acesso continua liberado.</div>
              </div>
            )}
            {isPago && (
              <div style={{textAlign:"center",padding:"16px",borderRadius:12,background:"#f0fdf4"}}>
                <div style={{color:"#166534",fontWeight:700,fontSize:15}}>Esta fatura foi paga e confirmada. Obrigado!</div>
              </div>
            )}
          </div>
        </div>

        <p style={{color:"#b0996e",fontSize:12,textAlign:"center"}}>Duvidas? Entre em contato via WhatsApp: (11) 98549-9821</p>
      </div>
    </div>
  );
}

//
// GUIA DO GESTOR
//
function GuiaGestor() {
  const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1.0"/>
<title>Guia do Gestor</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;800&family=DM+Mono:wght@400;500&display=swap');
:root{--ac:#d4a017;--ac-l:#fef9ee;--ac-b:#f0d080;--text:#1c1208;--t2:#4a3b1f;--t3:#8c7a5e;--bg:#faf8f4;--card:#fff;--border:#ede8df;--green:#16a34a;--gbg:#f0fdf4;--red:#dc2626;--rbg:#fef2f2;--blue:#2563eb;--bbg:#eff6ff;--sw:260px}
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:'DM Sans',sans-serif;background:var(--bg);color:var(--text);line-height:1.6}
.layout{display:flex;min-height:100vh}
.sidebar{width:var(--sw);background:var(--text);position:fixed;top:0;left:0;bottom:0;overflow-y:auto;padding-bottom:40px;z-index:100}
.logo{padding:20px 18px;border-bottom:1px solid #2e2010;display:flex;align-items:center;gap:10px}
.logo .nm{font-weight:800;font-size:18px;color:#fff;letter-spacing:-.5px}
.logo .nm span{color:var(--ac)}
.logo .badge{font-size:10px;background:var(--ac);color:#fff;padding:2px 7px;border-radius:20px;font-weight:700;margin-left:auto}
.sg{padding:16px 16px 4px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#6b5a3e}
.sidebar a{display:flex;align-items:center;gap:10px;padding:8px 14px;color:#c8b89a;text-decoration:none;font-size:13px;border-radius:8px;margin:2px 8px}
.sidebar a:hover,.sidebar a.active{background:#2a1e0e;color:#fff}
.sidebar a .ic{font-size:14px;width:18px;text-align:center}
.main{margin-left:var(--sw);flex:1}
.topbar{background:var(--card);border-bottom:1px solid var(--border);padding:18px 40px;display:flex;align-items:center;justify-content:space-between;position:sticky;top:0;z-index:50}
.topbar h1{font-size:22px;font-weight:800}
.topbar .sub{font-size:13px;color:var(--t3);margin-top:2px}
.ver{font-size:11px;background:var(--ac-l);color:var(--ac);border:1px solid var(--ac-b);padding:4px 12px;border-radius:20px;font-family:'DM Mono',monospace}
.content{padding:40px;max-width:820px}
.sec{margin-bottom:52px;scroll-margin-top:80px}
.sh{display:flex;align-items:center;gap:12px;margin-bottom:20px;padding-bottom:14px;border-bottom:2px solid var(--border)}
.iw{width:40px;height:40px;border-radius:10px;background:var(--ac-l);border:1px solid var(--ac-b);display:flex;align-items:center;justify-content:center;font-size:20px;flex-shrink:0}
.sh h2{font-size:20px;font-weight:800}
.sh p{font-size:13px;color:var(--t3);margin-top:2px}
.card{background:var(--card);border:1px solid var(--border);border-radius:14px;padding:20px 24px;margin-bottom:16px}
.card h3{font-size:15px;font-weight:700;margin-bottom:10px}
.card p,.card li{font-size:14px;color:var(--t2);line-height:1.7}
.card p+p{margin-top:8px}
.card ul{margin:10px 0 0 18px}
.card li{margin-bottom:4px}
.steps{display:flex;flex-direction:column;gap:12px;margin-top:12px}
.step{display:flex;gap:14px;align-items:flex-start}
.sn{width:26px;height:26px;border-radius:50%;background:var(--ac);color:#fff;font-size:12px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:1px}
.sc strong{font-size:14px;display:block;margin-bottom:2px}
.sc p{font-size:13px;color:var(--t2);margin:0}
.ib{border-radius:10px;padding:14px 16px;margin-top:14px;font-size:13px;display:flex;gap:10px;align-items:flex-start;line-height:1.6}
.ib.tip{background:var(--ac-l);border:1px solid var(--ac-b);color:var(--t2)}
.ib.warn{background:#fffbeb;border:1px solid #fde68a;color:#92400e}
.ib.green{background:var(--gbg);border:1px solid #86efac;color:#166534}
.ib.blue{background:var(--bbg);border:1px solid #bfdbfe;color:#1d4ed8}
.ib .ico{font-size:16px;flex-shrink:0;margin-top:1px}
.tag{display:inline-flex;padding:2px 9px;border-radius:20px;font-size:11px;font-weight:700;font-family:'DM Mono',monospace;border:1px solid}
.tag.am{background:var(--ac-l);color:var(--ac);border-color:var(--ac-b)}
.tag.gr{background:var(--gbg);color:var(--green);border-color:#86efac}
.tag.rd{background:var(--rbg);color:var(--red);border-color:#fca5a5}
.tag.gy{background:var(--bg);color:var(--t3);border-color:var(--border)}
.new{display:inline-block;background:var(--ac);color:#fff;font-size:9px;font-weight:700;padding:1px 6px;border-radius:20px;margin-left:6px;vertical-align:middle;font-family:'DM Mono',monospace}
table{width:100%;border-collapse:collapse;font-size:13px;margin-top:12px}
th{text-align:left;padding:10px 14px;background:var(--bg);color:var(--t3);font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;border-bottom:1px solid var(--border)}
td{padding:11px 14px;border-bottom:1px solid var(--border);color:var(--t2);vertical-align:top}
tr:last-child td{border-bottom:none}
tr:hover td{background:var(--bg)}
.hero{background:var(--text);padding:36px 40px;border-radius:16px;margin-bottom:40px;position:relative;overflow:hidden}
.hero::before{content:'';position:absolute;top:-40px;right:-40px;width:200px;height:200px;background:radial-gradient(circle,#d4a01733 0%,transparent 70%);border-radius:50%}
.hero h2{font-size:24px;font-weight:800;color:#fff;margin-bottom:8px}
.hero p{font-size:15px;color:#c8b89a;line-height:1.6;max-width:560px}
.chips{display:flex;flex-wrap:wrap;gap:8px;margin-top:20px}
.chip{padding:5px 14px;border-radius:20px;background:#2a1e0e;color:#c8b89a;font-size:12px;font-weight:600}
code{font-family:'DM Mono',monospace;font-size:12px;background:var(--bg);border:1px solid var(--border);padding:2px 7px;border-radius:5px;color:var(--t2)}
hr{border:none;border-top:1px solid var(--border);margin:24px 0}
@media(max-width:768px){.sidebar{display:none}.main{margin-left:0}.topbar,.content,.hero{padding-left:20px;padding-right:20px}}
</style>
</head>
<body>
<div class="layout">
<nav class="sidebar">
  <div class="logo"><span style="font-size:20px">🍽️</span><span class="nm">App<span>Tip</span></span><span class="badge">Gestor</span></div>
  <div class="sg">Visão Geral</div>
  <a href="#intro"><span class="ic">📖</span> O que é o sistema?</a>
  <a href="#acesso"><span class="ic">🔐</span> Acesso e Login</a>
  <a href="#restaurante"><span class="ic">🏢</span> Selecionar Restaurante</a>
  <a href="#dashboard"><span class="ic">📊</span> Dashboard</a>
  <div class="sg">Funcionalidades</div>
  <a href="#gorjetas"><span class="ic">💸</span> Gorjetas</a>
  <a href="#escala"><span class="ic">📅</span> Escala</a>
  <a href="#cargos"><span class="ic">🏷️</span> Cargos</a>
  <a href="#equipe"><span class="ic">👥</span> Equipe</a>
  <a href="#horarios"><span class="ic">🕐</span> Horários</a>
  <a href="#comunicados"><span class="ic">📢</span> Comunicados</a>
  <a href="#faq"><span class="ic">❓</span> FAQ com IA</a>
  <a href="#dp"><span class="ic">💬</span> Fale com DP</a>
  <a href="#caixa"><span class="ic">📬</span> Caixa</a>
  <div class="sg">Configurações</div>
  <a href="#config-abas"><span class="ic">📋</span> Abas Visíveis</a>
  <a href="#config-fiscal"><span class="ic">💰</span> Retenção Fiscal</a>
  <a href="#config-divisao"><span class="ic">⚖️</span> Divisão de Gorjeta</a>
  <div class="sg">Referência</div>
  <a href="#permissoes"><span class="ic">🔑</span> Permissões</a>
  <a href="#privacidade-admin"><span class="ic">🛡️</span> Privacidade</a>
  <a href="#mobile-gestor"><span class="ic">📱</span> Acesso Mobile</a>
  <a href="#bloqueio"><span class="ic">🔒</span> Acesso Suspenso</a>
</nav>
<div class="main">
  <div class="topbar">
    <div><h1>Guia do Gestor</h1><div class="sub">Manual completo de uso do sistema para gestores de restaurante</div></div>
    <span class="ver">v${APP_VERSION} · 2026</span>
  </div>
  <div class="content">

    <div class="hero">
      <h2>Bem-vindo 🍽️</h2>
      <p>A plataforma centraliza a gestão de gorjetas. Como gestor, você lança gorjetas, gerencia equipe, escala, horários com IA, comunicados, FAQ com IA e configura as regras do restaurante — inclusive pelo celular.</p>
      <div class="chips">
        <span class="chip">💸 Gorjetas automáticas</span><span class="chip">📅 Escala</span><span class="chip">👥 Equipe</span><span class="chip">🕐 Horários com IA</span><span class="chip">❓ FAQ com IA</span><span class="chip">📢 Comunicados com IA</span><span class="chip">📱 Mobile gestor</span><span class="chip">⚙️ Configurações</span>
      </div>
    </div>

    <div class="sec" id="intro">
      <div class="sh"><div class="iw">📖</div><div><h2>O que é o sistema?</h2><p>Visão geral do sistema</p></div></div>
      <div class="card"><h3>🎯 Para que serve?</h3><p>O gestor lança o valor total de gorjeta do dia. O sistema distribui automaticamente entre os empregados conforme os pontos de cada cargo e os dias na escala. A dedução fiscal é calculada e mostrada automaticamente para cada empregado.</p></div>
      <div class="card"><h3>📱 Como acessar?</h3><p>Pelo navegador em <code>apptip.app</code>. O botão <strong>❓ Ajuda</strong> no header abre este guia a qualquer momento. Adicione à tela inicial do celular para acesso rápido como app nativo.</p></div>
    </div>

    <div class="sec" id="acesso">
      <div class="sh"><div class="iw">🔐</div><div><h2>Acesso e Login</h2><p>Como entrar no sistema</p></div></div>
      <div class="card"><h3>Como fazer login</h3>
        <div class="steps">
          <div class="step"><div class="sn">1</div><div class="sc"><strong>Digite seu CPF</strong><p>Campo "CPF ou ID do empregado" na tela inicial.</p></div></div>
          <div class="step"><div class="sn">2</div><div class="sc"><strong>Digite seu PIN</strong><p>Código de 4 dígitos. Se também for empregado, pode usar o mesmo PIN.</p></div></div>
          <div class="step"><div class="sn">3</div><div class="sc"><strong>Selecione "Gestor"</strong><p>Se tiver mais de um perfil cadastrado, escolha o correto.</p></div></div>
        </div>
      </div>
      <div class="ib warn"><span class="ico">⚠️</span><span>Nunca compartilhe seu PIN. Em caso de comprometimento, solicite troca ao master.</span></div>
    </div>

    <div class="sec" id="restaurante">
      <div class="sh"><div class="iw">🏢</div><div><h2>Selecionar Restaurante</h2><p>Navegar entre os restaurantes que você gerencia</p></div></div>
      <div class="card"><h3>Tela de seleção <span class="new">MELHORADO</span></h3>
        <p>Se gerenciar mais de um restaurante, aparece a tela de seleção após o login. Cada card mostra dados reais do restaurante:</p>
        <ul><li><strong>👥 Ativos</strong> — número de empregados ativos</li><li><strong>💸 Dias lançados</strong> — gorjetas lançadas no mês atual</li><li><strong>💬 Novas</strong> — mensagens não lidas no Fale com DP</li><li><strong>📢 Pendentes</strong> — comunicados sem confirmação de leitura</li></ul>
        <div class="ib tip"><span class="ico">💡</span><span>Restaurantes com <span class="tag rd">🔒 Suspenso</span> têm pendência financeira e ficam bloqueados até regularização.</span></div>
      </div>
      <div class="card"><h3>Trocar de restaurante <span class="new">MELHORADO</span></h3><p>Com o restaurante aberto, o nome aparece no <strong>header</strong>. Para trocar, clique em <strong>‹ Trocar</strong> no canto superior esquerdo — sem precisar sair do sistema.</p></div>
    </div>

    <div class="sec" id="dashboard">
      <div class="sh"><div class="iw">📊</div><div><h2>Dashboard</h2><p>Visão geral e resumo do dia</p></div></div>
      <div class="card"><h3>📅 Resumo do dia <span class="new">NOVO</span></h3>
        <p>No topo do Dashboard (apenas no mês corrente), um card âmbar mostra o estado atual:</p>
        <ul><li><strong>👥 Trabalhando hoje</strong> — empregados sem marcação de folga ou falta</li><li><strong>💸 Gorjeta lançada</strong> — valor do pool de hoje, ou "—" se ainda não lançado</li><li><strong>Pendência gorjeta</strong> — dias úteis passados sem lançamento</li></ul>
        <p style="margin-top:10px">Se a gorjeta de hoje ainda não foi lançada, aparece um botão direto <strong>"💸 Lançar gorjeta de hoje"</strong> que leva à aba Gorjetas (visível apenas no computador).</p>
      </div>
      <div class="card"><h3>🔔 Alertas automáticos</h3>
        <table>
          <tr><th>Alerta</th><th>O que significa</th></tr>
          <tr><td>💸 Dias sem gorjeta</td><td>Dias úteis passados sem lançamento</td></tr>
          <tr><td>👤 Sem cargo</td><td>Empregados sem cargo definido</td></tr>
          <tr><td>🕐 Sem horário</td><td>Empregados sem horário cadastrado</td></tr>
          <tr><td>⚠️ Faltas injustificadas</td><td>Faltas não justificadas no mês</td></tr>
          <tr><td>💬 Mensagens não lidas</td><td>Fale com DP com mensagens pendentes</td></tr>
          <tr><td>📢 Sem ciência</td><td>Empregados que não confirmaram comunicados</td></tr>
        </table>
        <div class="ib tip"><span class="ico">💡</span><span>Clique em qualquer alerta para ir direto à aba correspondente.</span></div>
      </div>
    </div>

    <div class="sec" id="gorjetas">
      <div class="sh"><div class="iw">💸</div><div><h2>Gorjetas</h2><p>Lançamento e distribuição automática</p></div></div>
      <div class="card"><h3>Como funciona?</h3><p>O gestor lança o valor total do dia. O sistema distribui entre os empregados pelos pontos do cargo e dias na escala. A dedução fiscal (20% ou 33%) é calculada automaticamente.</p></div>
      <div class="card"><h3>Lançamento em tabela</h3>
        <div class="steps">
          <div class="step"><div class="sn">1</div><div class="sc"><strong>Navegue pelo mês</strong><p>Setas <code>‹ ›</code> para mudar de mês.</p></div></div>
          <div class="step"><div class="sn">2</div><div class="sc"><strong>Preencha o valor do dia</strong><p>Digite o total no campo "Valor (R$)". Adicione observação se necessário.</p></div></div>
          <div class="step"><div class="sn">3</div><div class="sc"><strong>Marque "Sem gorjeta" quando necessário</strong><p>Para dias sem operação. O dia fica marcado em roxo.</p></div></div>
          <div class="step"><div class="sn">4</div><div class="sc"><strong>Clique em Lançar</strong><p>Salva todos os dias preenchidos e distribui automaticamente.</p></div></div>
        </div>
      </div>
      <div class="card"><h3>Cores das linhas</h3>
        <table>
          <tr><th>Cor</th><th>Significado</th></tr>
          <tr><td><span class="tag gy">Branco</span></td><td>Sem valor preenchido</td></tr>
          <tr><td><span class="tag gr">Verde</span></td><td>Gorjeta lançada e confirmada</td></tr>
          <tr><td><span class="tag" style="background:#fffbeb;color:#92400e;border-color:#fde68a">Amarelo</span></td><td>Valor editado, ainda não relançado</td></tr>
          <tr><td><span class="tag" style="background:#f5f0ff;color:#6366f1;border-color:#c4b5fd">Roxo</span></td><td>Marcado como "sem gorjeta"</td></tr>
        </table>
      </div>
      <div class="card"><h3>📤 Exportar</h3><p>O botão "Exportar Gorjeta" baixa um Excel com todos os lançamentos do mês: valor por empregado, cargo, bruto, líquido e imposto.</p></div>
      <div class="ib warn"><span class="ico">⚠️</span><span>Apenas empregados com presença na escala do dia recebem gorjeta daquele dia. Mantenha a escala atualizada. Exceção: empregados de produção (🏭) recebem gorjeta todos os dias automaticamente.</span></div>
    </div>

    <div class="sec" id="escala">
      <div class="sh"><div class="iw">📅</div><div><h2>Escala</h2><p>Dias trabalhados por empregado</p></div></div>
      <div class="card"><h3>Por que é fundamental?</h3><p>A escala define quem trabalhou em cada dia e determina quem recebe gorjeta. Manter a escala atualizada garante a distribuição correta.</p></div>
      <div class="card"><h3>Como preencher</h3>
        <div class="steps">
          <div class="step"><div class="sn">1</div><div class="sc"><strong>Filtre por área</strong><p>Salão, Cozinha, Bar, Limpeza.</p></div></div>
          <div class="step"><div class="sn">2</div><div class="sc"><strong>Marque os dias</strong><p><span class="tag gr">✓</span> trabalhado · <strong>F</strong> folga · <strong>FL</strong> freela (presente sem gorjeta) · <strong>C</strong> compensação · <strong>FJ</strong> falta justificada · <strong>FI</strong> falta injustificada · <strong>V</strong> férias</p></div></div>
          <div class="step"><div class="sn">3</div><div class="sc"><strong>Salvo automaticamente</strong><p>Sem botão de confirmação — cada clique salva.</p></div></div>
        </div>
        <div class="ib tip"><span class="ico">💡</span><span>Empregados com horário contratual cadastrado em Horários têm a escala base gerada automaticamente. Você só ajusta as exceções.</span></div>
      </div>
    </div>

    <div class="sec" id="cargos">
      <div class="sh"><div class="iw">🏷️</div><div><h2>Cargos</h2><p>Pontos de gorjeta por cargo</p></div></div>
      <div class="card"><h3>O que são pontos?</h3><p>Cada cargo tem uma quantidade de pontos que define a proporção da gorjeta recebida. Mais pontos = maior fatia da gorjeta.</p>
        <div class="ib blue"><span class="ico">📐</span><span>Exemplo: gorjeta de R$1.000, 50 pontos totais → cada ponto vale R$20. Cargo com 6 pontos recebe R$120 bruto (antes da dedução fiscal).</span></div>
      </div>
      <div class="card"><h3>Gerenciar cargos</h3>
        <table>
          <tr><th>Ação</th><th>Como fazer</th></tr>
          <tr><td>Criar</td><td>Preencha nome, pontos e área → clique "+ Add"</td></tr>
          <tr><td>Editar</td><td>Edite diretamente na linha — alterações ficam em amarelo</td></tr>
          <tr><td>Salvar</td><td>Clique "💾 Salvar tudo" para confirmar todas as alterações de uma vez</td></tr>
          <tr><td>Sem gorjeta</td><td>Marque "Sem gorjeta" para cargos que não participam (sócios, admin)</td></tr>
          <tr><td>Inativar</td><td>Clique "Inativar" — cargo some da lista ativa, histórico preservado</td></tr>
          <tr><td>Apagar</td><td>Cargos inativos sem empregados vinculados podem ser apagados permanentemente</td></tr>
          <tr><td>IA</td><td>Use "✨ Gerenciar cargos com IA" para criar, renomear, ajustar pontos ou inativar com linguagem natural. Revise antes de confirmar.</td></tr>
        </table>
      </div>
    </div>

    <div class="sec" id="equipe">
      <div class="sh"><div class="iw">👥</div><div><h2>Equipe</h2><p>Cadastro e gestão dos empregados</p></div></div>
      <div class="card"><h3>Cadastrar empregado</h3>
        <div class="steps">
          <div class="step"><div class="sn">1</div><div class="sc"><strong>Preencha os dados</strong><p>Nome, CPF (opcional), data de admissão e PIN inicial.</p></div></div>
          <div class="step"><div class="sn">2</div><div class="sc"><strong>Selecione o cargo</strong><p>Define os pontos de gorjeta do empregado.</p></div></div>
          <div class="step"><div class="sn">3</div><div class="sc"><strong>Clique em Add</strong><p>O sistema gera um ID automático (ex: <code>LBZ0012</code>) que o empregado usa para fazer login.</p></div></div>
        </div>
      </div>
      <div class="card"><h3>🏭 Empregado de Produção <span class="new">NOVO</span></h3>
        <p>Empregados podem ser marcados como "Produção" usando o botão 🏭 ao lado do cargo na tabela de equipe. Esse flag é independente da área do cargo — qualquer empregado de qualquer área pode ser marcado como produção.</p>
        <table>
          <tr><th>Regra</th><th>Descrição</th></tr>
          <tr><td>Gorjeta</td><td>Recebe gorjeta <strong>todos os dias</strong> (trabalhando, folga, compensação, etc.), <strong>exceto férias</strong></td></tr>
          <tr><td>Distribuição</td><td>Segue os pontos do cargo normalmente, como qualquer empregado</td></tr>
          <tr><td>Penalidade injustificada</td><td>Falta injustificada: padrão <strong>6,66%</strong> do pool mensal por dia de falta</td></tr>
          <tr><td>Penalidade justificada</td><td>Falta justificada: padrão <strong>3,33%</strong> do pool mensal por dia de falta</td></tr>
          <tr><td>Configuração</td><td>Os percentuais de penalidade são configuráveis em <strong>Configurações → Penalidade por Falta</strong></td></tr>
        </table>
        <div class="ib tip"><span class="ico">💡</span><span>Use esse flag para empregados que participam da produção geral do restaurante e devem receber gorjeta diariamente, independente da escala individual.</span></div>
      </div>
      <div class="card"><h3>🎯 Empregado Freela <span class="new">NOVO</span></h3>
        <p>Empregados podem ser marcados como "Freela" usando o botão 🎯 ao lado do cargo na tabela de equipe. São colaboradores esporádicos que cobrem a equipe.</p>
        <table>
          <tr><th>Regra</th><th>Descrição</th></tr>
          <tr><td>Gorjeta</td><td><strong>Nunca</strong> participa do rateio de gorjeta, independente do status na escala</td></tr>
          <tr><td>Escala</td><td>Aparece normalmente na escala e pode ser escalado em qualquer dia</td></tr>
          <tr><td>Cargo</td><td>Cargo e área são atribuídos normalmente, mas os pontos não contam para gorjeta</td></tr>
        </table>
        <div class="ib blue"><span class="ico">📐</span><span>Além do flag no cadastro, existe o status "Freela" (FL) na escala: marca um empregado regular como presente sem gorjeta naquele dia — útil para quem estava de folga e resolveu cobrir.</span></div>
      </div>
      <div class="card"><h3>Inativar empregado</h3><p>Ao inativar, o empregado perde o acesso imediatamente. O histórico de gorjetas é preservado. É possível reativar na aba "Inativos" a qualquer momento.</p></div>
      <div class="card"><h3>Limite do plano</h3>
        <table>
          <tr><th>Plano</th><th>Limite</th></tr>
          <tr><td>Starter</td><td>10 empregados</td></tr>
          <tr><td>Básico</td><td>20 empregados</td></tr>
          <tr><td>Profissional</td><td>50 empregados</td></tr>
          <tr><td>Enterprise</td><td>51–100 empregados</td></tr>
        </table>
        <div class="ib warn"><span class="ico">⚠️</span><span>Ao atingir o limite, um botão "Solicitar upgrade" aparece para contato com o master.</span></div>
      </div>
    </div>

    <div class="sec" id="horarios">
      <div class="sh"><div class="iw">🕐</div><div><h2>Horários <span class="new">COM IA</span></h2><p>Horários contratuais dos empregados — com assistente de IA</p></div></div>
      <div class="card"><h3>Para que serve?</h3><p>Registra os dias de trabalho/folga e horários contratuais (entrada, saída, intervalo) de cada empregado. Usado para controle interno e geração automática da escala base. Disponível também no celular do gestor.</p></div>
      <div class="card"><h3>Fluxo simplificado</h3>
        <p>Você pode cadastrar de três formas:</p>
        <div class="steps">
          <div class="step"><div class="sn">1</div><div class="sc"><strong>Manual: dias de trabalho e folga</strong><p>Use o toggle de cada dia: ON = trabalha, OFF = folga. Clique em <strong>"Salvar Dias"</strong> para registrar sem precisar preencher horários.</p></div></div>
          <div class="step"><div class="sn">2</div><div class="sc"><strong>Manual: horários completos</strong><p>Preencha entrada/saída/intervalo e clique em <strong>"Validar e Salvar Horário"</strong>. O sistema valida carga semanal (43:55–44:00), interjornada (11h), máximo 10h/dia e intervalo mínimo.</p></div></div>
          <div class="step"><div class="sn">3</div><div class="sc"><strong>Com IA: descreva em linguagem natural</strong><p>Abra o <strong>Assistente de Horários</strong> e descreva o que deseja. Ex: <em>"folgue segunda e terça, trabalhe nos outros dias dividindo 44h semanais entrando às 10"</em>. A IA preenche tudo automaticamente — revise e salve.</p></div></div>
        </div>
        <div class="ib tip"><span class="ico">💡</span><span>A escala usa os dias de trabalho/folga mesmo sem horários preenchidos. Cadastre os dias primeiro para já gerar a escala base.</span></div>
      </div>
      <div class="card"><h3>📋 Copiar de outro empregado <span class="new">NOVO</span></h3>
        <p>Na tela de edição de horário, o card <strong>"📋 Copiar horário de outro empregado"</strong> permite selecionar qualquer colega com horário cadastrado e trazer os dias e horários para o empregado atual. Os dados ficam em edição até você confirmar com "Salvar Dias" ou "Validar e Salvar Horário" — nada é salvo automaticamente.</p>
        <div class="ib tip"><span class="ico">💡</span><span>Útil para novos empregados que seguem a mesma escala de um colega: copie o horário, ajuste o que for diferente e salve.</span></div>
      </div>
      <div class="card"><h3>🤖 Assistente de IA <span class="new">NOVO</span></h3>
        <p>Descreva os horários em linguagem natural — com typos, conjugações verbais ou abreviações. O assistente entende e preenche os campos automaticamente.</p>
        <ul>
          <li><strong>Folgas:</strong> "folgue segunda e terça", "folga dom e seg"</li>
          <li><strong>Carga horária:</strong> "divida 44 horas semanais", "44h em 5 dias"</li>
          <li><strong>Entrada fixa:</strong> "entrando às 10 todo dia", "entrada 09:00"</li>
          <li><strong>Frases compostas:</strong> "folgue seg e ter, trabalhe nos outros dias dividindo 44h entrando às 10"</li>
        </ul>
        <div class="ib warn"><span class="ico">⚠️</span><span>A IA aplica as mesmas regras de validação CLT (carga semanal, interjornada, intervalo mínimo). Se a sugestão violar alguma regra, os erros são mostrados antes de salvar.</span></div>
      </div>
      <div class="card"><h3>Status na lista de empregados</h3>
        <table>
          <tr><th>Status</th><th>Significado</th></tr>
          <tr><td><span class="tag am">HORÁRIOS PENDENTES</span></td><td>Dias definidos mas sem horários — escala funciona, validação pendente</td></tr>
          <tr><td><span class="tag gr">XX:XX/sem</span></td><td>Horário completo e validado — carga semanal exibida</td></tr>
          <tr><td><span class="tag gy">Sem horário</span></td><td>Nenhum cadastro ainda</td></tr>
        </table>
      </div>
      <div class="card"><h3>Regras de validação</h3>
        <ul>
          <li><strong>Carga semanal:</strong> entre 43h55 e 44h00 contratuais (rígido)</li>
          <li><strong>Interjornada:</strong> mínimo 11h entre saída e próxima entrada</li>
          <li><strong>Máximo diário:</strong> 10h contratuais por dia</li>
          <li><strong>Intervalo mínimo:</strong> 30 minutos em jornadas acima de 6h</li>
          <li><strong>Hora noturna:</strong> 22:00–05:00 com conversão ficta (52.5min real = 60min contratual)</li>
        </ul>
      </div>
    </div>

    <div class="sec" id="comunicados">
      <div class="sh"><div class="iw">📢</div><div><h2>Comunicados <span class="new">COM IA</span></h2><p>Avisos e informações para a equipe</p></div></div>
      <div class="card"><h3>Criar comunicado</h3>
        <div class="steps">
          <div class="step"><div class="sn">1</div><div class="sc"><strong>Descreva informalmente ou use a IA</strong><p>Escreva direto ou descreva o tema no assistente IA — ele gera título e texto profissional editável.</p></div></div>
          <div class="step"><div class="sn">2</div><div class="sc"><strong>Escolha o destinatário</strong><p>Toda a equipe, áreas específicas ou empregados individuais.</p></div></div>
          <div class="step"><div class="sn">3</div><div class="sc"><strong>Publique</strong><p>Aparece imediatamente. Acompanhe confirmações de leitura na lista de enviados.</p></div></div>
        </div>
        <div class="ib green"><span class="ico">✅</span><span>Você visualiza quem leu e quem ainda não confirmou diretamente na lista de comunicados enviados.</span></div>
      </div>
      <div class="card"><h3>🗑️ Exclusão de comunicados</h3>
        <p>Gestores podem mover comunicados para a lixeira. Na lixeira, é possível restaurar ou (admin) apagar permanentemente. Comunicados na lixeira não aparecem mais para os empregados.</p>
      </div>
    </div>

    <div class="sec" id="faq">
      <div class="sh"><div class="iw">❓</div><div><h2>FAQ <span class="new">COM IA</span></h2><p>Perguntas frequentes para a equipe, com assistente de IA</p></div></div>
      <div class="card"><h3>Duas seções no FAQ do empregado</h3>
        <p>O FAQ que o empregado vê está dividido em duas seções distintas:</p>
        <ul>
          <li><strong>📐 Regras do sistema</strong> — 7 perguntas geradas <em>automaticamente</em> pelo sistema com base nas regras reais do restaurante. Sempre atualizadas.</li>
          <li><strong>🏢 Sobre o restaurante</strong> — perguntas cadastradas por você, específicas do restaurante.</li>
        </ul>
      </div>
      <div class="card"><h3>📐 Perguntas automáticas do sistema</h3>
        <p>Visíveis para você na aba FAQ — aparece um card âmbar listando as 7 perguntas geradas automaticamente:</p>
        <ul>
          <li>💸 Como é calculada a minha gorjeta?</li>
          <li>📊 Como funciona a tabela de pontos? (ou por área)</li>
          <li>📅 Como funciona a escala e por que ela importa?</li>
                    <li>💬 Para que serve o Fale com DP?</li>
          <li>📢 Como funcionam os comunicados?</li>
          <li>🔐 O que é o PIN e como trocar?</li>
        </ul>
        <div class="ib tip"><span class="ico">💡</span><span>As perguntas sobre DP e comunicados só aparecem para o empregado se essas abas estiverem ativas nas suas configurações.</span></div>
      </div>
      <div class="card"><h3>✨ Assistente de IA <span class="new">NOVO</span></h3>
        <p>Ao criar uma nova pergunta, um campo de assistente IA aparece no formulário. Descreva informalmente o que quer comunicar e a IA redige uma versão profissional e editável.</p>
        <div class="steps">
          <div class="step"><div class="sn">1</div><div class="sc"><strong>Clique em "+ Nova Pergunta"</strong><p>O formulário abre com o assistente no topo.</p></div></div>
          <div class="step"><div class="sn">2</div><div class="sc"><strong>Descreva informalmente</strong><p>Ex: <em>"quando o empregado falta sem avisar ele perde a gorjeta do dia"</em></p></div></div>
          <div class="step"><div class="sn">3</div><div class="sc"><strong>Clique em "✨ Sugerir com IA"</strong><p>A IA gera pergunta e resposta profissional nos campos abaixo.</p></div></div>
          <div class="step"><div class="sn">4</div><div class="sc"><strong>Edite e salve</strong><p>Ajuste o texto como quiser antes de confirmar.</p></div></div>
        </div>
      </div>
      <div class="card"><h3>👁 Controle de visibilidade <span class="new">NOVO</span></h3>
        <p>Cada pergunta cadastrada tem um botão <span class="tag gr">👁 Exibindo</span> ou <span class="tag gy">🚫 Oculto</span>. Clique para alternar. Perguntas ocultas não aparecem para os empregados — útil para rascunhos ou perguntas temporariamente desativadas.</p>
      </div>
    </div>

    <div class="sec" id="dp">
      <div class="sh"><div class="iw">💬</div><div><h2>Fale com DP</h2><p>Canal com o departamento pessoal</p></div></div>
      <div class="card"><h3>Como funciona?</h3><p>Canal direto entre empregados e o gestor de DP — férias, atestados, dúvidas trabalhistas e documentos. Empregados podem enviar anonimamente.</p>
        <div class="ib blue"><span class="ico">ℹ️</span><span>Somente gestores marcados como "Gestor do DP" pelo master recebem essas mensagens.</span></div>
      </div>
      <div class="card"><h3>Gerenciar mensagens</h3><p>As mensagens aparecem na aba "Fale com DP". Selecione mensagens e mova para a lixeira. Na lixeira é possível restaurar ou (admin) apagar permanentemente.</p></div>
    </div>

    <div class="sec" id="caixa">
      <div class="sh"><div class="iw">📬</div><div><h2>Caixa</h2><p>Central de notificações</p></div></div>
      <div class="card"><h3>O que aparece na Caixa?</h3><p>Todas as notificações do restaurante: solicitações de alteração de horário, novas mensagens do Fale com DP e confirmações de leitura de comunicados. O número <code>📬 Caixa (3)</code> indica quantas não foram lidas.</p></div>
    </div>

    <div class="sec" id="config-abas">
      <div class="sh"><div class="iw">📋</div><div><h2>Abas Visíveis <span class="new">NOVO</span></h2><p>Escolha quais abas aparecem para você e seus empregados</p></div></div>
      <div class="card"><h3>Como acessar</h3><p>Clique em <strong>⚙️ Configurações</strong> no canto superior direito do painel do restaurante. O botão fica destacado em âmbar quando está ativo.</p></div>
      <div class="card"><h3>Sistema de dois níveis</h3>
        <table>
          <tr><th>Nível</th><th>Quem controla</th><th>O que faz</th></tr>
          <tr><td>1. Autorização</td><td>Master</td><td>Define quais abas o restaurante pode usar</td></tr>
          <tr><td>2. Visibilidade</td><td>Gestor (você)</td><td>Decide quais abas ativas aparecem para você e os empregados</td></tr>
        </table>
        <div class="ib tip"><span class="ico">💡</span><span>Você só pode ativar abas que o administrador autorizou. Abas bloqueadas aparecem acinzentadas com "(não autorizado pelo admin)".</span></div>
      </div>
      <div class="card"><h3>Efeito no FAQ automático</h3><p>As perguntas automáticas do FAQ se adaptam às abas ativas. Se você ocultar "Fale com DP", a pergunta sobre o DP some automaticamente do FAQ dos empregados.</p></div>
    </div>

    <div class="sec" id="config-fiscal">
      <div class="sh"><div class="iw">💰</div><div><h2>Retenção Fiscal <span class="new">ATUALIZADO</span></h2><p>Alíquota de dedução sobre gorjeta</p></div></div>
      <div class="card"><h3>Base legal — Lei 13.419/2017</h3>
        <p>A gorjeta é rendimento do trabalhador sujeito a encargos conforme a Lei 13.419/2017. A alíquota depende do regime tributário:</p>
        <table>
          <tr><th>Alíquota</th><th>Regime</th><th>Encargos</th></tr>
          <tr><td><strong>33%</strong></td><td>Lucro Real ou Presumido</td><td>INSS patronal (20%) + FGTS (8%) + RAT/terceiros (~5%). Art. 457 CLT e Lei 13.419/2017.</td></tr>
          <tr><td><strong>20%</strong></td><td>Simples Nacional (MEI, ME, EPP)</td><td>Encargos simplificados sobre folha. LC 123/2006 e Resolução CGSN 140/2018.</td></tr>
        </table>
        <div class="ib warn"><span class="ico">⚠️</span><span>Esta configuração não substitui orientação contábil. Consulte seu contador para confirmar a alíquota correta do seu estabelecimento.</span></div>
      </div>
    </div>

    <div class="sec" id="config-divisao">
      <div class="sh"><div class="iw">⚖️</div><div><h2>Divisão de Gorjeta</h2><p>Sistema de pontos global ou área + pontos</p></div></div>
      <div class="card"><h3>Dois modos disponíveis</h3>
        <table>
          <tr><th>Modo</th><th>Como funciona</th></tr>
          <tr><td><strong>Pontos Global</strong></td><td>Todos que trabalharam no dia dividem a gorjeta proporcionalmente aos pontos do cargo. Mais simples e direto.</td></tr>
          <tr><td><strong>Área + Pontos</strong></td><td>A gorjeta é dividida primeiro entre áreas (%) e depois internamente por pontos de cargo. Você define o percentual de cada área (Bar, Cozinha, Salão...).</td></tr>
        </table>
      </div>
      <div class="card"><h3>Penalidade por falta</h3>
        <p>Configure percentuais de desconto no pool mensal para empregados com faltas. A penalidade é cumulativa: cada dia de falta desconta o percentual configurado.</p>
        <table>
          <tr><th>Tipo</th><th>Como funciona</th></tr>
          <tr><td><strong>Por área</strong></td><td>Cada área (Bar, Cozinha, Salão, Limpeza) tem seu percentual de penalidade para faltas injustificadas. Padrão: 0%.</td></tr>
          <tr><td><strong>🏭 Produção (injustificada)</strong></td><td>Percentual próprio para faltas injustificadas (padrão: 6,66% por dia).</td></tr>
          <tr><td><strong>🏭 Produção (justificada)</strong></td><td>Percentual próprio para faltas justificadas (padrão: 3,33% por dia).</td></tr>
        </table>
        <div class="ib blue"><span class="ico">📐</span><span>Exemplo: pool mensal de R$10.000, empregado de produção com 2 faltas injustificadas (6,66%) + 1 justificada (3,33%) → desconto de 13,32% + 3,33% = 16,65% = R$1.665 do total de gorjetas dele no mês.</span></div>
        <div class="ib warn"><span class="ico">⚠️</span><span>Nenhum empregado recebe gorjeta durante férias, incluindo os de produção.</span></div>
      </div>
    </div>

    <div class="sec" id="permissoes">
      <div class="sh"><div class="iw">🔑</div><div><h2>Permissões</h2><p>O que cada gestor pode acessar</p></div></div>
      <div class="card"><h3>Perfis de gestor</h3>
        <p>O administrador define o perfil ao criar o gestor:</p>
        <table>
          <tr><th>Perfil</th><th>Descrição</th></tr>
          <tr><td>📬 Gestor Administrativo</td><td>Acesso completo: gorjetas, escala, cargos, equipe, comunicados, FAQ, DP e notificações. Pode criar outros gestores.</td></tr>
          <tr><td>👔 Líder Operacional</td><td>Acesso a escala e equipe apenas da(s) sua(s) área(s). Sem gorjetas, sem DP.</td></tr>
          <tr><td>⚙️ Personalizado</td><td>Permissões individuais escolhidas pelo admin.</td></tr>
        </table>
      </div>
      <div class="card"><h3>Permissões granulares (perfil personalizado)</h3>
        <table>
          <tr><th>Permissão</th><th>Dá acesso a</th></tr>
          <tr><td>💸 Gorjetas</td><td>Dashboard, aba Gorjetas, exportação</td></tr>
          <tr><td>📅 Escala</td><td>Aba Escala</td></tr>
          <tr><td>🕐 Horários</td><td>Aba Horários (se admin autorizou)</td></tr>
          <tr><td>🏷️ Cargos</td><td>Aba Cargos (se admin autorizou)</td></tr>
          <tr><td>👥 Equipe</td><td>Aba Equipe (se admin autorizou)</td></tr>
          <tr><td>📬 Gestor do DP</td><td>Aba Caixa, recebe mensagens do Fale com DP</td></tr>
        </table>
        <div class="ib tip"><span class="ico">💡</span><span>Se alguma aba não aparece, seu perfil pode não ter permissão ou o admin não autorizou. CPF é obrigatório para todos os gestores.</span></div>
      </div>
    </div>

    <div class="sec" id="privacidade-admin">
      <div class="sh"><div class="iw">🛡️</div><div><h2>Privacidade de Dados <span class="new">NOVO</span></h2><p>Controle o que a equipe de administradores pode ver</p></div></div>
      <div class="card"><h3>O que é o Modo Privacidade?</h3><p>Quando ativado, o modo privacidade oculta dados sensíveis do seu restaurante para a equipe de Masters. Os admins continuam tendo acesso à gestão do sistema, mas não visualizam informações confidenciais da sua operação.</p></div>
      <div class="card"><h3>O que fica oculto para o admin</h3>
        <table>
          <tr><th>Dado</th><th>Visão do admin</th></tr>
          <tr><td>💸 Valores de gorjeta</td><td>Aparece como <strong>••••,••</strong></td></tr>
          <tr><td>📋 CPFs de empregados e gestores</td><td>Aparece como <strong>•••.•••.•••-••</strong></td></tr>
          <tr><td>📢 Comunicados</td><td>Conteúdo completamente oculto</td></tr>
          <tr><td>💬 Mensagens do DP</td><td>Conteúdo completamente oculto</td></tr>
          <tr><td>🔔 Notificações</td><td>Conteúdo completamente oculto</td></tr>
        </table>
        <div class="ib tip"><span class="ico">💡</span><span>O admin vê um banner indicando que o modo privacidade está ativo, garantindo transparência de que existem dados mas que estão protegidos.</span></div>
      </div>
      <div class="card"><h3>Como ativar</h3>
        <div class="steps">
          <div class="st"><span class="sn">1</span><span>Acesse <strong>⚙️ Configurações</strong></span></div>
          <div class="st"><span class="sn">2</span><span>Encontre a seção <strong>🔒 Privacidade</strong></span></div>
          <div class="st"><span class="sn">3</span><span>Ative o toggle de privacidade</span></div>
          <div class="st"><span class="sn">4</span><span>Clique em <strong>Salvar Configurações</strong></span></div>
        </div>
        <div class="ib blue"><span class="ico">ℹ️</span><span>O modo privacidade afeta apenas a visão do admin. Gestores e empregados continuam vendo todos os dados normalmente em seus respectivos portais.</span></div>
      </div>
    </div>

    <div class="sec" id="mobile-gestor">
      <div class="sh"><div class="iw">📱</div><div><h2>Acesso Mobile <span class="new">NOVO</span></h2><p>O que o gestor consegue fazer pelo celular</p></div></div>
      <div class="card"><h3>Abas no celular</h3>
        <p>No celular, o gestor tem acesso a um conjunto otimizado de abas:</p>
        <table>
          <tr><th>Aba</th><th>Funcionalidade</th></tr>
          <tr><td>📊 Dashboard</td><td>Resumo do dia, alertas, widget de gorjetas (somente leitura)</td></tr>
          <tr><td>📅 Escala</td><td>Visão semanal com navegação ◀/▶, toque para alterar status</td></tr>
          <tr><td>🕐 Horários</td><td>Edição completa + assistente de IA para gerar horários</td></tr>
          <tr><td>👥 Empregados</td><td>Lista de empregados, detalhes e trilha individual</td></tr>
          <tr><td>📋 Reuniões</td><td>Planejamento e histórico de reuniões com a equipe</td></tr>
          <tr><td>📬 Caixa</td><td>Notificações e mensagens do Fale com DP</td></tr>
        </table>
        <div class="ib tip"><span class="ico">💡</span><span>As demais funcionalidades (Gorjetas, Cargos, VT, Comunicados, FAQ, DP e Configurações) estão disponíveis apenas pelo computador.</span></div>
      </div>
    </div>

    <div class="sec" id="bloqueio">
      <div class="sh"><div class="iw">🔒</div><div><h2>Acesso Suspenso</h2><p>O que fazer quando o acesso está bloqueado</p></div></div>
      <div class="card"><h3>Por que ocorre?</h3><p>O acesso é suspenso por pendência financeira. Você faz login normalmente, mas ao entrar no restaurante vê a tela de bloqueio. Na seleção de restaurantes, o card aparece com <span class="tag rd">🔒 Suspenso</span>.</p></div>
      <div class="card"><h3>O que fazer</h3>
        <div class="steps">
          <div class="step"><div class="sn">1</div><div class="sc"><strong>Entre em contato com o Master</strong><p>O número aparece na tela de bloqueio: <strong>(11) 98549-9821</strong></p></div></div>
          <div class="step"><div class="sn">2</div><div class="sc"><strong>Regularize a pendência</strong><p>Após confirmação do pagamento, o acesso é liberado automaticamente.</p></div></div>
        </div>
        <div class="ib warn"><span class="ico">⚠️</span><span>Durante a suspensão, os empregados também perdem acesso. Regularize o quanto antes para não impactar a operação.</span></div>
      </div>
    </div>

    <hr>
    <div style="text-align:center;padding:20px 0 40px;color:var(--t3);font-size:13px">
      <div style="font-size:28px;margin-bottom:8px">🍽️</div>
      <div>Guia do Gestor · v${APP_VERSION} · Abril 2026</div>
      <div style="margin-top:4px">Dúvidas? <a href="/cdn-cgi/l/email-protection#9bf8f4f5effaeff4dbfaebebeff2ebb5faebeb" style="color:var(--ac)"><span class="__cf_email__" data-cfemail="3a5955544e5b4e557a5b4a4a4e534a145b4a4a">[email&#160;protected]</span></a> · Clientes: <strong style="color:var(--t2)">(11) 98549-9821</strong></div>
    </div>

  </div>
</div>
</div>
<script>
const secs=document.querySelectorAll('.sec'),links=document.querySelectorAll('.sidebar a');
// Highlight active sidebar link on scroll
const obs=new IntersectionObserver(e=>{e.forEach(en=>{if(en.isIntersecting){links.forEach(l=>l.classList.remove('active'));const id=en.target.id;const match=[...links].find(l=>l.getAttribute('href')==='#'+id);if(match)match.classList.add('active');}});},{rootMargin:'-20% 0px -70% 0px'});
secs.forEach(s=>obs.observe(s));
// Smooth scroll for sidebar links
links.forEach(l=>{l.addEventListener('click',e=>{e.preventDefault();const id=l.getAttribute('href')?.slice(1);const el=id&&document.getElementById(id);if(el)el.scrollIntoView({behavior:'smooth',block:'start'});});});
</script>
`;
  return (
    <iframe
      srcDoc={html}
      style={{width:"100%", height:"100vh", border:"none"}}
      title="Guia do Master"
    />
  );
}

// v7.6 — Helper: ao trocar PIN, sincroniza nos 3 records linkados (pessoa, employee, manager)
// Garante que o PIN funciona em qualquer tela (Empregado, Líder, Gestor) sem disparar
// "primeiro acesso" de novo. Usa pessoa como fonte de verdade; sincroniza por linkedXId
// e por CPF (cobre legacy não migrado).
function syncPinAcrossLinkedRecords({ newPin, identityCpf, pessoa, pessoas, employees, managers, onUpdate }) {
  const cpfDigits = (identityCpf || pessoa?.cpf || "").replace(/\D/g, "");
  const update = { pin: newPin, mustChangePin: false };
  // Atualiza pessoa-alvo (se passada) + qualquer outra com mesmo CPF
  if ((pessoas || []).length > 0) {
    const nextPessoas = pessoas.map(p => {
      if (pessoa && p.id === pessoa.id) return { ...p, ...update };
      if (cpfDigits && (p.cpf || "").replace(/\D/g, "") === cpfDigits) return { ...p, ...update };
      return p;
    });
    onUpdate("pessoas", nextPessoas);
  }
  // Atualiza employee linked (por linkedEmployeeId OU CPF)
  const linkedEmpIds = new Set();
  if (pessoa?.linkedEmployeeId) linkedEmpIds.add(pessoa.linkedEmployeeId);
  (employees || []).forEach(e => {
    if (cpfDigits && (e.cpf || "").replace(/\D/g, "") === cpfDigits) linkedEmpIds.add(e.id);
  });
  if (linkedEmpIds.size > 0) {
    const nextEmployees = employees.map(e => linkedEmpIds.has(e.id) ? { ...e, ...update } : e);
    onUpdate("employees", nextEmployees);
  }
  // Atualiza manager linked (por linkedManagerId OU CPF)
  const linkedMgrIds = new Set();
  if (pessoa?.linkedManagerId) linkedMgrIds.add(pessoa.linkedManagerId);
  (managers || []).forEach(m => {
    if (cpfDigits && (m.cpf || "").replace(/\D/g, "") === cpfDigits) linkedMgrIds.add(m.id);
  });
  if (linkedMgrIds.size > 0) {
    const nextManagers = managers.map(m => linkedMgrIds.has(m.id) ? { ...m, ...update } : m);
    onUpdate("managers", nextManagers);
  }
}

//
// APP ROOT
//
export { ErrorBoundary as AppErrorBoundary };
export default function App() {
  const [theme, setTheme] = useState(() => localStorage.getItem("apptip_theme") || "light");
  const [showPrivacy, setShowPrivacy] = useState(false);

  useEffect(() => {
    document.body.classList.toggle("dark-mode", theme === "dark");
    localStorage.setItem("apptip_theme", theme);
  }, [theme]);

  function toggleTheme() { setTheme(t => t === "dark" ? "light" : "dark"); }

  // Global privacy modal toggle — avoids threading props through all portals
  useEffect(() => { window.__showPrivacy = () => setShowPrivacy(true); return () => { delete window.__showPrivacy; }; }, []);

  // Login unificado — uma única tela para todos
  const isSetupUrl = window.location.pathname.startsWith("/setup");
  const isFaturaUrl = window.location.pathname.startsWith("/fatura/");
  const isGuiaGestor = window.location.pathname.startsWith("/guia-gestor");
  const faturaId = isFaturaUrl ? window.location.pathname.split("/fatura/")[1] : null;

  const [view, setView] = useState(() => {
    if (isSetupUrl) return "setup";
    if (isFaturaUrl) return "fatura";
    if (isGuiaGestor) return "guia-gestor";
    const role = localStorage.getItem("apptip_role");
    // Se owner estava no shell, preserva (refresh mantém no shell em vez de voltar ao OwnerPortal)
    if (role === "super" && localStorage.getItem("apptip_shell_owner_mode") === "1") return "shell";
    if (role === "super") return "super";
    if (role === "shell") return "shell";
    if (role === "manager") return "manager";
    if (role === "operational") return "operational";
    if (role === "employee") return "employee";
    return "login";
  });
  // Flag pra controlar se o usuário quer usar o layout novo (shell) ou os portais antigos
  const [shellActiveRest, setShellActiveRest] = useState(() => localStorage.getItem("apptip_shell_rest") || null);
  useEffect(() => {
    if (shellActiveRest) localStorage.setItem("apptip_shell_rest", shellActiveRest);
    else localStorage.removeItem("apptip_shell_rest");
  }, [shellActiveRest]);
  // Impersonate: owner pode ver como outra pessoa (pra debug/suporte)
  const [impersonatingPessoaId, setImpersonatingPessoaId] = useState(() => localStorage.getItem("apptip_impersonate_pid") || null);
  useEffect(() => {
    if (impersonatingPessoaId) localStorage.setItem("apptip_impersonate_pid", impersonatingPessoaId);
    else localStorage.removeItem("apptip_impersonate_pid");
  }, [impersonatingPessoaId]);
  const [loaded, setLoaded] = useState(false);
  const [loadProgress, setLoadProgress] = useState(""); // feedback visual durante carregamento
  const [loadError, setLoadError] = useState(false);    // true se não conseguiu conectar
  const [toast, setToast] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [currentUserId] = useState(() => {
    try { return localStorage.getItem("apptip_userid") || null; } catch { return null; }
  });
  const [userRole, setUserRole] = useState(() => localStorage.getItem("apptip_role") || null);
  const sessionRestoredRef = React.useRef(false); // true após data-load verificar sessão

  // Persist session — só limpa localStorage DEPOIS que a restauração de sessão rodou
  useEffect(() => {
    if (currentUser) localStorage.setItem("apptip_userid", currentUser.id);
    else if (sessionRestoredRef.current && userRole !== "employee") localStorage.removeItem("apptip_userid");
    // empregado: apptip_userid salvo diretamente no login via apptip_empid
  }, [currentUser, userRole]);
  useEffect(() => {
    if (userRole) localStorage.setItem("apptip_role", userRole);
    else localStorage.removeItem("apptip_role");
  }, [userRole]);

  // Detectar online/offline para bloqueio de escrita (somente isMaster)
  useEffect(() => {
    const goOnline  = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener("online",  goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  // v8.11.1: Listener de versão — força recarregamento quando uma versão nova é deployada.
  // Doc /appdata/_appConfig com { value: { latestVersion: "X.Y.Z", releasedAt: ISO } }.
  // Self-updating: o primeiro user a abrir o build novo escreve o doc; demais clients vivos
  // recebem o snapshot e mostram o banner.
  const [latestRemoteVersion, setLatestRemoteVersion] = useState(null);
  const [updateBannerDismissed, setUpdateBannerDismissed] = useState(() => {
    try { return localStorage.getItem("apptip_update_dismissed") || ""; } catch { return ""; }
  });
  useEffect(() => {
    let unsub = null;
    try {
      unsub = onSnapshot(doc(db, "appdata", "_appConfig"), async (snap) => {
        const remote = snap.exists() ? snap.data().value : null;
        const remoteVer = remote?.latestVersion;
        // Primeiro a abrir build novo: grava no Firestore pra avisar os outros clients
        if (!remoteVer || compareVersions(APP_VERSION, remoteVer) > 0) {
          try {
            await setDoc(doc(db, "appdata", "_appConfig"), {
              value: {
                latestVersion: APP_VERSION,
                releasedAt: new Date().toISOString(),
              },
            });
          } catch (e) { /* ignora — outro client pode ter escrito antes */ }
          setLatestRemoteVersion(APP_VERSION);
          return;
        }
        setLatestRemoteVersion(remoteVer);
      }, (err) => {
        console.warn("[appConfig] listener falhou:", err);
      });
    } catch (e) {
      console.warn("[appConfig] não foi possível inscrever:", e);
    }
    return () => { if (unsub) unsub(); };
  }, []);
  const updateAvailable = latestRemoteVersion && compareVersions(latestRemoteVersion, APP_VERSION) > 0 && updateBannerDismissed !== latestRemoteVersion;

  const [owners, setOwners] = useState([]);
  const [managers,      setManagers]      = useState([]);
  const [restaurants,   setRestaurants]   = useState([]);
  const [employees,     setEmployees]     = useState([]);
  const [roles,         setRoles]         = useState([]);
  const [tips,          setTips]          = useState([]);
  const [splits,        setSplits]        = useState({});
  const [schedules,     setSchedules]     = useState({});
  const [communications,setCommunications]= useState([]);
  const [commAcks,      setCommAcks]      = useState({});
  const [faq,           setFaq]           = useState({});
  const [dpMessages,    setDpMessages]    = useState([]);
  const [workSchedules, setWorkSchedules] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [noTipDays,     setNoTipDays]     = useState({});
  const [trash,         setTrash]         = useState({ restaurants:[], managers:[], employees:[] });
  const [schedTemplates,setSchedTemplates]= useState({});
  const [schedDrafts,   setSchedDrafts]   = useState({});
  const [scheduleVersions, setScheduleVersions] = useState({});
  const [tipVersions,      setTipVersions]      = useState({});
  const [vtConfig,         setVtConfig]         = useState({});
  const [vtMonthly,        setVtMonthly]        = useState({});
  const [vtPayments,       setVtPayments]       = useState({});
  const [incidents,        setIncidents]        = useState([]);
  const [feedbacks,        setFeedbacks]        = useState([]);
  const [devChecklists,    setDevChecklists]    = useState({});
  const [scheduleAdjustments, setScheduleAdjustments] = useState({});
  const [scheduleStatus,      setScheduleStatus]      = useState({});
  const [schedulePrevista,    setSchedulePrevista]    = useState({});
  const [scheduleSwaps,       setScheduleSwaps]       = useState([]); // v8.11.0 — inversões informais
  const [dayInversions,       setDayInversions]       = useState([]); // inversões de dia inteiro (feriados)
  const [recursoFolders,      setRecursoFolders]      = useState([]); // v8.15.0 — pastas de Recursos
  const [recursos,            setRecursos]            = useState([]); // v8.15.0 — itens de Recursos
  const [recursosInitialized, setRecursosInitialized] = useState({}); // v8.15.0 — flag por restaurante
  const [employeeGoals,       setEmployeeGoals]       = useState({});
  const [delays,              setDelays]              = useState({});
  const [tipApprovals,        setTipApprovals]        = useState({});
  const [meetingPlans,        setMeetingPlans]        = useState([]);
  const [meetingIdeas,        setMeetingIdeas]        = useState([]);
  const [meetingAgendas,      setMeetingAgendas]      = useState([]);
  const [meetingActions,      setMeetingActions]      = useState([]);
  const [meetingOccurrences,  setMeetingOccurrences]  = useState([]);
  const [meetingPendencias,   setMeetingPendencias]   = useState([]);
  const [inbox,               setInbox]               = useState([]);
  const [inboxFolders,        setInboxFolders]        = useState({});
  // Operacional — Contagens
  const [miseCategories,  setMiseCategories]  = useState([]);
  const [miseStocks,      setMiseStocks]      = useState([]);
  const [miseAssignments, setMiseAssignments] = useState([]);
  const [miseItems,       setMiseItems]       = useState([]);
  const [miseCycles,      setMiseCycles]      = useState([]);
  const [miseCounts,      setMiseCounts]      = useState([]);
  const [miseSuppliers,       setMiseSuppliers]        = useState([]);
  const [miseProductSuppliers,setMiseProductSuppliers] = useState([]);
  const [miseSupplierOrders,  setMiseSupplierOrders]   = useState([]);
  const [miseChecklistTemplates, setMiseChecklistTemplates] = useState([]);
  const [miseChecklistRuns,      setMiseChecklistRuns]      = useState([]);
  const [pessoas,             setPessoas]              = useState([]);
  const [pessoasMigratedAt,   setPessoasMigratedAt]    = useState(null);
  const [permsV2MigratedAt,   setPermsV2MigratedAt]    = useState(null); // v8.17.0
  const [freelasFecharOpMigratedAt, setFreelasFecharOpMigratedAt] = useState(null); // v8.30.0
  // Operacional — Fichas Técnicas
  const [miseFtInsumos,      setMiseFtInsumos]      = useState([]);
  const [miseFtEquipamentos, setMiseFtEquipamentos] = useState({});
  const [miseFtDishes,       setMiseFtDishes]       = useState([]);
  // Operacional — Temperaturas (piloto: dados mockados até integração Tuya)
  const [tuyaLinks,          setTuyaLinks]          = useState({});
  const [tempSensors,        setTempSensors]        = useState([]);
  const [tempReadings,       setTempReadings]       = useState([]);
  const [tempAlerts,         setTempAlerts]         = useState([]);
  const [tempBackfillState,  setTempBackfillState]  = useState({});
  const [permProfiles,       setPermProfiles]       = useState({});
  const [freelaShifts,       setFreelaShifts]       = useState([]);
  const [freelaPagamentos,   setFreelaPagamentos]   = useState([]);
  const [reservasConfig,     setReservasConfig]     = useState({});
  const [reservasSalas,      setReservasSalas]      = useState([]);
  const [reservasTiposMesa,  setReservasTiposMesa]  = useState([]);
  const [reservasClientes,   setReservasClientes]   = useState([]);
  const [reservas,           setReservas]           = useState([]);

  useEffect(() => {
    const savedId = currentUserId;
    // Timeout: se demorar mais de 12s, mostra mensagem
    let slowTimer = setTimeout(() => setLoadProgress("Conexão lenta — tentando novamente..."), 6000);
    let verySlowTimer = setTimeout(() => setLoadProgress("Servidor demorando a responder... aguarde"), 12000);
    (async () => {
      setLoadProgress("Conectando ao servidor...");
      const allKeys = Object.values(K);
      const keyNames = Object.keys(K);

      // Carregar em paralelo com tracking
      let loadedCount = 0;
      const totalKeys = allKeys.length;
      const vals = await Promise.all(allKeys.map(async (k) => {
        const result = await load(k);
        loadedCount++;
        if (loadedCount <= 3) setLoadProgress("Conectando ao servidor...");
        else setLoadProgress(`Carregando dados... ${Math.round((loadedCount/totalKeys)*100)}%`);
        return result;
      }));

      clearTimeout(slowTimer);
      clearTimeout(verySlowTimer);
      setLoadProgress("Preparando o sistema...");

      const keys = keyNames;
      const map = { owners:setOwners, managers:setManagers, restaurants:setRestaurants, employees:setEmployees, roles:setRoles, tips:setTips, splits:setSplits, schedules:setSchedules, communications:setCommunications, commAcks:setCommAcks, faq:setFaq, dpMessages:setDpMessages, workSchedules:setWorkSchedules, notifications:setNotifications, noTipDays:setNoTipDays, trash:setTrash, schedTemplates:setSchedTemplates, schedDrafts:setSchedDrafts, scheduleVersions:setScheduleVersions, tipVersions:setTipVersions, vtConfig:setVtConfig, vtMonthly:setVtMonthly, vtPayments:setVtPayments, incidents:setIncidents, feedbacks:setFeedbacks, devChecklists:setDevChecklists, scheduleAdjustments:setScheduleAdjustments, scheduleStatus:setScheduleStatus, schedulePrevista:setSchedulePrevista, scheduleSwaps:setScheduleSwaps, dayInversions:setDayInversions, recursoFolders:setRecursoFolders, recursos:setRecursos, recursosInitialized:setRecursosInitialized, employeeGoals:setEmployeeGoals, delays:setDelays, tipApprovals:setTipApprovals, meetingPlans:setMeetingPlans, meetingIdeas:setMeetingIdeas, meetingAgendas:setMeetingAgendas, meetingActions:setMeetingActions, meetingOccurrences:setMeetingOccurrences, meetingPendencias:setMeetingPendencias, inbox:setInbox, inboxFolders:setInboxFolders, miseCategories:setMiseCategories, miseStocks:setMiseStocks, miseAssignments:setMiseAssignments, miseItems:setMiseItems, miseCycles:setMiseCycles, miseCounts:setMiseCounts, miseSuppliers:setMiseSuppliers, miseProductSuppliers:setMiseProductSuppliers, miseSupplierOrders:setMiseSupplierOrders, miseChecklistTemplates:setMiseChecklistTemplates, miseChecklistRuns:setMiseChecklistRuns, miseFtInsumos:setMiseFtInsumos, miseFtEquipamentos:setMiseFtEquipamentos, miseFtDishes:setMiseFtDishes, pessoas:setPessoas, pessoasMigratedAt:setPessoasMigratedAt, permsV2MigratedAt:setPermsV2MigratedAt, freelasFecharOpMigratedAt:setFreelasFecharOpMigratedAt, tuyaLinks:setTuyaLinks, tempSensors:setTempSensors, tempReadings:setTempReadings, tempAlerts:setTempAlerts, tempBackfillState:setTempBackfillState, permProfiles:setPermProfiles, freelaShifts:setFreelaShifts, freelaPagamentos:setFreelaPagamentos, reservasConfig:setReservasConfig, reservasSalas:setReservasSalas, reservasTiposMesa:setReservasTiposMesa, reservasClientes:setReservasClientes, reservas:setReservas };
      const loaded_data = {};
      let successCount = 0;
      keys.forEach((k, i) => {
        if (k !== "receipts" && vals[i]) { map[k]?.(vals[i]); loaded_data[k] = vals[i]; successCount++; }
      });

      // Se nenhuma key carregou com sucesso, marca como erro de conexão
      _loadSuccess = successCount > 0;
      if (!_loadSuccess) setLoadError(true);

      // Auto-purge: remover mensagens do inbox excluídas há mais de 90 dias
      if (loaded_data.inbox) {
        const purged = purgeExpiredInbox(loaded_data.inbox);
        if (purged) { setInbox(purged); save(K.inbox, purged); }
      }

      // Auto-cleanup de workSchedules: remove entries fantasma + dedupe (mesmo `days`, mantém o mais recente).
      // Saves antigos com bug de race condition deixaram entries vazios e duplicados.
      // Limpa silenciosamente e re-grava o objeto saneado.
      if (loaded_data.workSchedules && typeof loaded_data.workSchedules === "object") {
        const ws = loaded_data.workSchedules;
        let dirty = false;
        let removedFantasma = 0;
        let removedDuplicados = 0;
        const cleaned = {};
        const summary = []; // diagnóstico: o que sobrou

        // Helper: gera fingerprint do conteúdo dos days (ignora id/createdAt — só importa o "horário em si")
        function fingerprintDays(s) {
          if (!s || !s.days) return "__empty__";
          const keys = Object.keys(s.days).sort();
          return keys.map(k => {
            const d = s.days[k];
            if (!d) return `${k}=null`;
            return `${k}=${d.in||""}|${d.out||""}|${d.break||0}|${d.active?1:0}`;
          }).join("&") + `|hc=${s.hoursComplete!==false?1:0}|f=${s.freela?1:0}`;
        }

        // Helper: timestamp pra ordenação (mais recente primeiro)
        function entryTime(s) {
          return new Date(s.createdAt || s.validFrom || 0).getTime() || 0;
        }

        for (const [restId, byEmp] of Object.entries(ws)) {
          if (!byEmp || typeof byEmp !== "object") { dirty = true; removedFantasma++; continue; }
          const cleanedEmp = {};
          for (const [empId, scheds] of Object.entries(byEmp)) {
            if (!Array.isArray(scheds)) { dirty = true; removedFantasma++; continue; }
            // 1) Remove fantasmas (sem dia válido)
            const validos = scheds.filter(s =>
              s && s.days && typeof s.days === "object" &&
              Object.values(s.days).some(d => d && d.in && d.out)
            );
            const fantasmasAqui = scheds.length - validos.length;
            if (fantasmasAqui > 0) { dirty = true; removedFantasma += fantasmasAqui; }

            // 2) Dedupe — agrupa por fingerprint, mantém o mais recente
            const byFp = new Map();
            for (const s of validos) {
              const fp = fingerprintDays(s);
              const existing = byFp.get(fp);
              if (!existing || entryTime(s) > entryTime(existing)) {
                byFp.set(fp, s);
              }
            }
            const dedupados = [...byFp.values()].sort((a, b) => entryTime(a) - entryTime(b));
            const duplicadosAqui = validos.length - dedupados.length;
            if (duplicadosAqui > 0) { dirty = true; removedDuplicados += duplicadosAqui; }

            if (dedupados.length > 0) {
              cleanedEmp[empId] = dedupados;
              const diasResumo = dedupados.map(s => {
                const days = s.days || {};
                const ativos = Object.entries(days)
                  .filter(([,d]) => d && d.in && d.out)
                  .map(([i,d]) => `D${i}:${d.in}-${d.out}`).join(",");
                return `[${s.id?.slice(-6) || "?"}: ${ativos || "vazio"}]`;
              }).join(" ");
              summary.push(`rest=${restId.slice(-6)} emp=${empId.slice(-6)} → ${dedupados.length} entry(ies): ${diasResumo}`);
            } else if (scheds.length > 0) {
              dirty = true; // tinha conteúdo, agora vazio
            }
          }
          if (Object.keys(cleanedEmp).length > 0) cleaned[restId] = cleanedEmp;
          else if (Object.keys(byEmp).length > 0) dirty = true;
        }
        if (dirty) {
          const partes = [];
          if (removedFantasma > 0) partes.push(`${removedFantasma} fantasma(s)`);
          if (removedDuplicados > 0) partes.push(`${removedDuplicados} duplicado(s)`);
          console.log(`[cleanup] workSchedules: ${partes.join(" + ")} removido(s)`);
          setWorkSchedules(cleaned);
          save(K.workSchedules, cleaned);
        }
        if (summary.length > 0) {
          console.log("[diagnóstico] workSchedules — entries que SOBRARAM (geram badge ⏰):");
          summary.forEach(s => console.log("   " + s));
        }
      }

      // Migração automática: v4:superManagers → v4:owners (executa só uma vez)
      if (!loaded_data.owners || loaded_data.owners.length === 0) {
        const migrated = localStorage.getItem("apptip_migrated_owners");
        if (!migrated) {
          const oldData = await load("v4:superManagers");
          if (oldData && oldData.length > 0) {
            console.log("Migrando superManagers → owners...", oldData.length, "registros");
            const migratedData = oldData.map((o, i) => i === 0 ? {...o, isMaster: true} : o);
            await save(K.owners, migratedData);
            setOwners(migratedData);
            loaded_data.owners = migratedData;
            localStorage.setItem("apptip_migrated_owners", "1");
          }
        }
      }

      if (savedId || localStorage.getItem("apptip_empid")) {
        const role = localStorage.getItem("apptip_role");
        if (role === "super") {
          const u = (loaded_data.owners ?? []).find(s => s.id === savedId);
          if (u) setCurrentUser(u);
          else { localStorage.removeItem("apptip_userid"); localStorage.removeItem("apptip_role"); setView("login"); }
        } else if (role === "manager") {
          const u = (loaded_data.managers ?? []).find(m => m.id === savedId);
          if (u) setCurrentUser(u);
          else { localStorage.removeItem("apptip_userid"); localStorage.removeItem("apptip_role"); setView("login"); }
        } else if (role === "employee" || role === "operational") {
          const empIdSaved = localStorage.getItem("apptip_empid") || savedId;
          const u = (loaded_data.employees ?? []).find(e => e.id === empIdSaved);
          if (!u || (u.inactive && u.inactiveFrom && u.inactiveFrom <= today())) {
            // Sessão inválida — limpa tudo
            localStorage.removeItem("apptip_userid");
            localStorage.removeItem("apptip_role");
            localStorage.removeItem("apptip_empid");
            setView("login");
          } else if (role === "operational" && !hasAnyOperationalArea(u)) {
            // Role operational mas áreas revogadas — rebaixar para employee
            localStorage.setItem("apptip_role", "employee");
            localStorage.setItem("apptip_userid", empIdSaved);
            setUserRole("employee");
            setView("employee");
            // não setCurrentUser — EmployeePortal lê empId de localStorage
          } else {
            localStorage.setItem("apptip_userid", empIdSaved);
            if (role === "operational") setCurrentUser(u); // operational precisa de currentUser
            // sessão válida — view já está correta pelo useState inicial
          }
        }
      }
      sessionRestoredRef.current = true; // sessão verificada — agora persist effect e fallback podem agir
      // Limpeza automática da lixeira — itens com mais de 30 dias
      if (loaded_data.trash) {
        const cutoff = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
        const cleanTrash = {
          restaurants: (loaded_data.trash.restaurants??[]).filter(x => x.deletedAt > cutoff),
          managers:    (loaded_data.trash.managers??[]).filter(x => x.deletedAt > cutoff),
          employees:   (loaded_data.trash.employees??[]).filter(x => x.deletedAt > cutoff),
        };
        const totalOriginal = (loaded_data.trash.restaurants?.length??0)+(loaded_data.trash.managers?.length??0)+(loaded_data.trash.employees?.length??0);
        const totalClean = cleanTrash.restaurants.length+cleanTrash.managers.length+cleanTrash.employees.length;
        if (totalClean < totalOriginal) {
          await save(K.trash, cleanTrash);
          setTrash(cleanTrash);
        }
      }

      // Migração: cargos da área "Produção" → "Cozinha"
      const rolesWithProd = (loaded_data.roles ?? []).filter(r => r.area === "Produção");
      if (rolesWithProd.length > 0) {
        const migratedRoles = (loaded_data.roles ?? []).map(r => r.area === "Produção" ? {...r, area: "Cozinha"} : r);
        await save(K.roles, migratedRoles);
        setRoles(migratedRoles);
        console.log(`Migrados ${rolesWithProd.length} cargo(s) de Produção → Cozinha`);
      }

      // Migração: empregados sem empCode ou sem PIN
      {
        const allEmps = loaded_data.employees ?? [];
        const rests = loaded_data.restaurants ?? [];
        const needsFix = allEmps.filter(e => !e.empCode || !e.pin);
        if (needsFix.length > 0) {
          let fixed = [...allEmps];
          needsFix.forEach(emp => {
            const rest = rests.find(r => r.id === emp.restaurantId);
            const code = rest?.shortCode ?? "XXX";
            const idx = fixed.findIndex(e => e.id === emp.id);
            if (idx < 0) return;
            if (!fixed[idx].empCode) {
              const seq = nextEmpSeq(fixed, code);
              fixed[idx] = { ...fixed[idx], empCode: makeEmpCode(code, seq) };
            }
            if (!fixed[idx].pin) {
              const cpfDigits = (emp.cpf ?? "").replace(/\D/g, "");
              fixed[idx] = { ...fixed[idx], pin: cpfDigits.slice(0, 4).padEnd(4, "0") };
            }
          });
          await save(K.employees, fixed);
          setEmployees(fixed);
          console.log(`Migrados ${needsFix.length} empregado(s) sem empCode/PIN`);
        }
      }

      // Auto-inativar empregados demitidos cujo mês de demissão já passou
      {
        const allEmps = loaded_data.employees ?? [];
        const currentMonth = today().slice(0,7); // "YYYY-MM"
        const demitidosParaInativar = allEmps.filter(e => e.demitidoEm && !e.inactive && e.demitidoEm.slice(0,7) < currentMonth);
        if (demitidosParaInativar.length > 0) {
          const updated = allEmps.map(e =>
            e.demitidoEm && !e.inactive && e.demitidoEm.slice(0,7) < currentMonth
              ? { ...e, inactive: true, inactiveFrom: e.demitidoEm }
              : e
          );
          await save(K.employees, updated);
          setEmployees(updated);
          console.log(`Auto-inativados ${demitidosParaInativar.length} empregado(s) demitido(s)`);
        }
      }

      // Auto-podar tipVersions / scheduleVersions que cresceram acima do MAX_VERSIONS (legacy:
      // antes era 30, agora é 5). Sem isso o Firestore eventualmente recusa o save por exceder 1 MB.
      const trimVersionsDoc = (versions) => {
        if (!versions || typeof versions !== "object") return { trimmed: versions, changed: false };
        let changed = false;
        const out = {};
        Object.keys(versions).forEach(r => {
          const months = versions[r];
          if (!months || typeof months !== "object") { out[r] = months; return; }
          const trimmedMonths = {};
          Object.keys(months).forEach(m => {
            const list = months[m];
            if (Array.isArray(list) && list.length > MAX_VERSIONS) {
              trimmedMonths[m] = list.slice(0, MAX_VERSIONS);
              changed = true;
            } else {
              trimmedMonths[m] = list;
            }
          });
          out[r] = trimmedMonths;
        });
        return { trimmed: out, changed };
      };
      {
        const tv = trimVersionsDoc(loaded_data.tipVersions);
        if (tv.changed) {
          await save(K.tipVersions, tv.trimmed);
          setTipVersions(tv.trimmed);
          loaded_data.tipVersions = tv.trimmed;
          console.log("tipVersions podado pra MAX_VERSIONS=" + MAX_VERSIONS);
        }
        const sv = trimVersionsDoc(loaded_data.scheduleVersions);
        if (sv.changed) {
          await save(K.scheduleVersions, sv.trimmed);
          setScheduleVersions(sv.trimmed);
          loaded_data.scheduleVersions = sv.trimmed;
          console.log("scheduleVersions podado pra MAX_VERSIONS=" + MAX_VERSIONS);
        }
      }

      // Auto-aplicar promoções agendadas cuja data efetiva chegou
      {
        const allEmps = loaded_data.employees ?? [];
        const promos = allEmps.filter(e => e.pendingRoleChange && e.pendingRoleChange.effectiveDate <= today());
        if (promos.length > 0) {
          const updated = allEmps.map(e => {
            if (!e.pendingRoleChange || e.pendingRoleChange.effectiveDate > today()) return e;
            const prc = e.pendingRoleChange;
            const history = [...(e.roleHistory ?? []), { fromRoleId: e.roleId, toRoleId: prc.newRoleId, date: prc.effectiveDate, reason: prc.reason || "Promoção programada", changedBy: prc.changedBy }];
            const copy = { ...e, roleId: prc.newRoleId, roleHistory: history };
            delete copy.pendingRoleChange;
            return copy;
          });
          await save(K.employees, updated);
          setEmployees(updated);
          loaded_data.employees = updated;
          console.log(`Auto-aplicadas ${promos.length} promoção(ões) agendada(s)`);
        }
      }

      setLoadProgress("");
      setLoaded(true);
    })();
    return () => { clearTimeout(slowTimer); clearTimeout(verySlowTimer); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Migração automática: employees + managers → pessoas
  // Roda uma vez quando `loaded` vira true e `pessoas` está vazio
  useEffect(() => {
    if (!loaded) return;
    if (pessoas && pessoas.length > 0) return; // já migrou
    if ((employees || []).length === 0 && (managers || []).length === 0) return; // nada a migrar
    const migrated = pessoasMigrate(employees, managers);
    if (migrated.length > 0) {
      console.log(`[pessoas] Migração automática: ${migrated.length} pessoa(s) geradas a partir de ${(employees||[]).length} employee(s) + ${(managers||[]).length} manager(s)`);
      handleUpdate("pessoas", migrated);
      handleUpdate("pessoasMigratedAt", new Date().toISOString());
    }
  }, [loaded, employees.length, managers.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // v8.17.0 — Migração silenciosa: isDP=true → admin.dpCanal/atravessarPrivado/reabrirEscala/fecharEscala
  // Idempotente via permsV2MigratedAt. Roda 1x.
  useEffect(() => {
    if (!loaded) return;
    if (permsV2MigratedAt) return;
    if (!Array.isArray(pessoas) || pessoas.length === 0) return;
    let touched = 0;
    const next = pessoas.map(p => {
      const allPerms = p.permissions || {};
      let changed = false;
      const updatedPerms = {};
      Object.entries(allPerms).forEach(([rid, byGroup]) => {
        const isDPHere = byGroup?.special?.isDP === true;
        if (!isDPHere) {
          updatedPerms[rid] = byGroup;
          return;
        }
        const adm = { ...(byGroup.admin || {}) };
        const before = JSON.stringify(adm);
        adm.dpCanal = true;
        adm.atravessarPrivado = true;
        adm.reabrirEscala = true;
        adm.fecharEscala = true;
        const after = JSON.stringify(adm);
        if (before !== after) changed = true;
        updatedPerms[rid] = { ...byGroup, admin: adm };
      });
      if (!changed) return p;
      touched++;
      return { ...p, permissions: updatedPerms };
    });
    if (touched > 0) {
      console.log(`[permsV2] Migração silenciosa: ${touched} pessoa(s) com isDP ganharam dpCanal/atravessarPrivado/reabrirEscala/fecharEscala`);
      handleUpdate("pessoas", next);
    }
    handleUpdate("permsV2MigratedAt", new Date().toISOString());
  }, [loaded, pessoas?.length, permsV2MigratedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // v8.24.0 — Migração silenciosa idempotente: admin.freelas (lançar+fechar tudo junto)
  // → operational.freelasFechar + operational.freelasExec.
  // v8.30.0: ambos vão pra operational agora — agendar/fechar é tarefa de líder, não admin.
  // Sem flag: só atualiza pessoa que tem admin.freelas=true E ainda não tem operational.freelasFechar
  // OU operational.freelasExec — se já tem ambos, no-op.
  useEffect(() => {
    if (!loaded) return;
    if (!Array.isArray(pessoas) || pessoas.length === 0) return;
    let touched = 0;
    const next = pessoas.map(p => {
      const allPerms = p.permissions || {};
      let changed = false;
      const updatedPerms = {};
      Object.entries(allPerms).forEach(([rid, byGroup]) => {
        const hasLegacyFreelas = byGroup?.admin?.freelas === true;
        if (!hasLegacyFreelas) {
          updatedPerms[rid] = byGroup;
          return;
        }
        const adm = { ...(byGroup.admin || {}) };
        const op  = { ...(byGroup.operational || {}) };
        let localChange = false;
        if (op.freelasFechar !== true) { op.freelasFechar = true; localChange = true; } // v8.30.0: era admin.freelasFechar
        if (op.freelasExec   !== true) { op.freelasExec   = true; localChange = true; }
        if (!localChange) {
          updatedPerms[rid] = byGroup;
          return;
        }
        changed = true;
        updatedPerms[rid] = { ...byGroup, admin: adm, operational: op };
      });
      if (!changed) return p;
      touched++;
      return { ...p, permissions: updatedPerms };
    });
    if (touched > 0) {
      console.log(`[freelasSplit] ${touched} pessoa(s) com admin.freelas legado ganharam freelasFechar + freelasExec`);
      handleUpdate("pessoas", next);
    }
  }, [loaded, pessoas?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // v8.30.0 — Migra admin.freelasFechar → operational.freelasFechar.
  // Conceitualmente agendar/fechar lote de freelas é tarefa do líder de área (operacional),
  // não do admin. Estar em admin.X faz a pessoa virar "Gestor" em listas como
  // eligibleResponsaveis (Reuniões/Ações). Move pro operational e idempotente via flag.
  useEffect(() => {
    if (!loaded) return;
    if (freelasFecharOpMigratedAt) return;
    if (!Array.isArray(pessoas)) {
      handleUpdate("freelasFecharOpMigratedAt", new Date().toISOString());
      return;
    }
    let touched = 0;
    const next = pessoas.map(p => {
      const allPerms = p.permissions || {};
      let changed = false;
      const updated = {};
      Object.entries(allPerms).forEach(([rid, byGroup]) => {
        if (byGroup?.admin?.freelasFechar !== true) {
          updated[rid] = byGroup;
          return;
        }
        const adm = { ...(byGroup.admin || {}) };
        const op  = { ...(byGroup.operational || {}) };
        delete adm.freelasFechar;
        op.freelasFechar = true;
        changed = true;
        updated[rid] = { ...byGroup, admin: adm, operational: op };
      });
      if (!changed) return p;
      touched++;
      return { ...p, permissions: updated };
    });
    if (touched > 0) {
      console.log(`[freelasFecharOp] ${touched} pessoa(s) migradas: admin.freelasFechar → operational.freelasFechar`);
      handleUpdate("pessoas", next);
    }
    handleUpdate("freelasFecharOpMigratedAt", new Date().toISOString());
  }, [loaded, pessoas?.length, freelasFecharOpMigratedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // v7.3 — Migração auto-corretiva isTeam[rid]
  // Pessoas com linkedEmployeeId apontando pra employee ATIVO de um rid devem ter isTeam[rid]=true.
  // Isso conserta casos onde a pessoa ganhou employee record (via togglePerm operational ou outro)
  // mas isTeam[rid] ficou como false/undefined, escondendo "Meu Dia" no AppShell.
  // Idempotente: só faz update se algo mudou.
  useEffect(() => {
    if (!loaded) return;
    if (!pessoas || pessoas.length === 0) return;
    if (!employees || employees.length === 0) return;
    let changedCount = 0;
    const fixed = pessoas.map(p => {
      if (!p.linkedEmployeeId) return p;
      const emp = employees.find(e => e.id === p.linkedEmployeeId);
      if (!emp) return p;
      const isInactive = emp.inactive && emp.inactiveFrom && emp.inactiveFrom <= today();
      if (isInactive) return p;
      const rid = emp.restaurantId;
      if (!rid) return p;
      const currentIsTeam = !!p.isTeam?.[rid];
      if (currentIsTeam) return p;
      changedCount++;
      return { ...p, isTeam: { ...(p.isTeam || {}), [rid]: true } };
    });
    if (changedCount > 0) {
      console.log(`[pessoas] Auto-fix isTeam: ${changedCount} pessoa(s) marcada(s) como equipe (linkedEmployeeId apontando pra employee ativo)`);
      handleUpdate("pessoas", fixed);
    }
  }, [loaded, pessoas?.length, employees?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // v7.3 — Redirect view='employee' → view='shell' quando há pessoa correspondente.
  // Faz com que TODO acesso de empregado (login direto OU botão "👤 Empregado") use o
  // AppShell unificado em vez do EmployeePortal legado. Fallback: se não há pessoa pra
  // o currentUser, mantém a rota legada (pra empregados ainda não migrados).
  useEffect(() => {
    if (view !== "employee") return;
    if (!loaded) return;
    if (!currentUser) return;
    const cpfDigits = (currentUser.cpf || "").replace(/\D/g, "");
    let pessoa = null;
    if (currentUser.id) {
      pessoa = (pessoas || []).find(p => p.linkedEmployeeId === currentUser.id);
    }
    if (!pessoa && cpfDigits) {
      pessoa = (pessoas || []).find(p => (p.cpf || "").replace(/\D/g, "") === cpfDigits);
    }
    if (!pessoa) return; // fallback: continua no EmployeePortal legacy
    // Determina rid ativo: 1º o do employee atual, senão o primeiro restaurantId da pessoa
    const rid = currentUser.restaurantId || (pessoa.restaurantIds || [])[0] || null;
    if (!rid) return;
    console.log(`[v7.3] Redirect employee→shell: pessoa=${pessoa.name} rid=${rid}`);
    localStorage.setItem("apptip_role", "shell");
    setShellActiveRest(rid);
    setUserRole("shell");
    setView("shell");
  }, [view, loaded, currentUser?.id, pessoas?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // v7.5 — Redirect view='manager' → view='shell' quando há pessoa correspondente.
  // Toda gestora admin entra no shell unificado em vez do ManagerPortal legacy.
  useEffect(() => {
    if (view !== "manager") return;
    if (!loaded) return;
    if (!currentUser) return;
    if (currentUser.mustChangePin) return; // mantém ManagerPinChange screen
    const cpfDigits = (currentUser.cpf || "").replace(/\D/g, "");
    let pessoa = null;
    if (currentUser.id) {
      pessoa = (pessoas || []).find(p => p.linkedManagerId === currentUser.id);
    }
    if (!pessoa && cpfDigits) {
      pessoa = (pessoas || []).find(p => (p.cpf || "").replace(/\D/g, "") === cpfDigits);
    }
    if (!pessoa) return; // fallback: continua no ManagerPortal legacy
    const rid = (pessoa.restaurantIds || [])[0] || (currentUser.restaurantIds || [])[0] || null;
    if (!rid) return;
    console.log(`[v7.5] Redirect manager→shell: pessoa=${pessoa.name} rid=${rid}`);
    localStorage.setItem("apptip_role", "shell");
    setShellActiveRest(rid);
    setUserRole("shell");
    setView("shell");
  }, [view, loaded, currentUser?.id, pessoas?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // v7.5 — Redirect view='operational' → view='shell' quando há pessoa correspondente.
  // Gestor operacional entra no shell. As tabs operacionais (Mise) usam versões lean
  // (OperationalContagens etc.) por dispatching de kind=operational no renderContent.
  useEffect(() => {
    if (view !== "operational") return;
    if (!loaded) return;
    if (!currentUser) return;
    const cpfDigits = (currentUser.cpf || "").replace(/\D/g, "");
    let pessoa = null;
    if (currentUser.id) {
      pessoa = (pessoas || []).find(p => p.linkedEmployeeId === currentUser.id);
    }
    if (!pessoa && cpfDigits) {
      pessoa = (pessoas || []).find(p => (p.cpf || "").replace(/\D/g, "") === cpfDigits);
    }
    if (!pessoa) return; // fallback: continua no OperationalPortal legacy
    const rid = currentUser.restaurantId || (pessoa.restaurantIds || [])[0] || null;
    if (!rid) return;
    console.log(`[v7.5] Redirect operational→shell: pessoa=${pessoa.name} rid=${rid}`);
    localStorage.setItem("apptip_role", "shell");
    setShellActiveRest(rid);
    setUserRole("shell");
    setView("shell");
  }, [view, loaded, currentUser?.id, pessoas?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const data = { owners, managers, restaurants, employees, roles, tips, splits, schedules, communications, commAcks, faq, dpMessages, workSchedules, notifications, noTipDays, trash, schedTemplates, schedDrafts, scheduleVersions, tipVersions, vtConfig, vtMonthly, vtPayments, incidents, feedbacks, devChecklists, scheduleAdjustments, scheduleStatus, schedulePrevista, scheduleSwaps, dayInversions, recursoFolders, recursos, recursosInitialized, employeeGoals, delays, tipApprovals, meetingPlans, meetingIdeas, meetingAgendas, meetingActions, meetingOccurrences, meetingPendencias, inbox, inboxFolders, miseCategories, miseStocks, miseAssignments, miseItems, miseCycles, miseCounts, miseSuppliers, miseProductSuppliers, miseSupplierOrders, miseChecklistTemplates, miseChecklistRuns, miseFtInsumos, miseFtEquipamentos, miseFtDishes, pessoas, pessoasMigratedAt, permsV2MigratedAt, tuyaLinks, tempSensors, tempReadings, tempAlerts, tempBackfillState, permProfiles, freelaShifts, freelaPagamentos, reservasConfig, reservasSalas, reservasTiposMesa, reservasClientes, reservas };

  // v8.22.3: kick out automático — se o usuário logado for inativado durante a sessão,
  // a próxima rodada de data-refresh o desloga + mostra toast.
  useEffect(() => {
    if (!currentUser || !sessionRestoredRef.current) return;
    if (userRole === "super") return; // Owner nunca é deslogado por isso
    const cuId = currentUser.id;
    const cuCpf = (currentUser.cpf || "").replace(/\D/g, "");
    let kicked = false;
    let reason = "";
    // Acha pessoa correspondente (por id ou cpf)
    const pessoa = (pessoas || []).find(p => p.id === cuId)
      || (cuCpf ? (pessoas || []).find(p => (p.cpf || "").replace(/\D/g, "") === cuCpf) : null);
    if (pessoa && pessoa.inactive === true) { kicked = true; reason = "Sua conta foi inativada."; }
    if (!kicked && (userRole === "employee" || userRole === "operational")) {
      const emp = (employees || []).find(e => e.id === cuId);
      if (!emp || emp.inactive === true) { kicked = true; reason = "Seu vínculo foi encerrado."; }
    }
    if (!kicked && userRole === "manager") {
      const mgr = (managers || []).find(m => m.id === cuId);
      if (!mgr || mgr.inactive === true) { kicked = true; reason = "Seu acesso de gestor foi removido."; }
    }
    if (kicked) {
      setToast(`🔒 ${reason} Você foi desconectado.`);
      try { localStorage.removeItem("apptip_userid"); localStorage.removeItem("apptip_empid"); localStorage.removeItem("apptip_role"); } catch (e) {}
      setCurrentUser(null);
      setUserRole(null);
    }
  }, [currentUser, userRole, pessoas, employees, managers]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleUpdate(field, value) {
    if (field === "_toast") { setToast(value); return; }
    // Bloqueio de escrita offline — somente para master master
    if (!isOnline && currentUser?.isMaster === true && field !== "_toast") {
      setToast("⚠️ Você está offline — conecte à internet para salvar alterações");
      return;
    }
    const setters = { owners:setOwners, managers:setManagers, restaurants:setRestaurants, employees:setEmployees, roles:setRoles, tips:setTips, splits:setSplits, schedules:setSchedules, communications:setCommunications, commAcks:setCommAcks, faq:setFaq, dpMessages:setDpMessages, workSchedules:setWorkSchedules, notifications:setNotifications, noTipDays:setNoTipDays, trash:setTrash, schedTemplates:setSchedTemplates, schedDrafts:setSchedDrafts, scheduleVersions:setScheduleVersions, tipVersions:setTipVersions, vtConfig:setVtConfig, vtMonthly:setVtMonthly, vtPayments:setVtPayments, incidents:setIncidents, feedbacks:setFeedbacks, devChecklists:setDevChecklists, scheduleAdjustments:setScheduleAdjustments, scheduleStatus:setScheduleStatus, schedulePrevista:setSchedulePrevista, scheduleSwaps:setScheduleSwaps, dayInversions:setDayInversions, recursoFolders:setRecursoFolders, recursos:setRecursos, recursosInitialized:setRecursosInitialized, employeeGoals:setEmployeeGoals, delays:setDelays, tipApprovals:setTipApprovals, meetingPlans:setMeetingPlans, meetingIdeas:setMeetingIdeas, meetingAgendas:setMeetingAgendas, meetingActions:setMeetingActions, meetingOccurrences:setMeetingOccurrences, meetingPendencias:setMeetingPendencias, inbox:setInbox, inboxFolders:setInboxFolders, miseCategories:setMiseCategories, miseStocks:setMiseStocks, miseAssignments:setMiseAssignments, miseItems:setMiseItems, miseCycles:setMiseCycles, miseCounts:setMiseCounts, miseSuppliers:setMiseSuppliers, miseProductSuppliers:setMiseProductSuppliers, miseSupplierOrders:setMiseSupplierOrders, miseChecklistTemplates:setMiseChecklistTemplates, miseChecklistRuns:setMiseChecklistRuns, miseFtInsumos:setMiseFtInsumos, miseFtEquipamentos:setMiseFtEquipamentos, miseFtDishes:setMiseFtDishes, pessoas:setPessoas, pessoasMigratedAt:setPessoasMigratedAt, permsV2MigratedAt:setPermsV2MigratedAt, freelasFecharOpMigratedAt:setFreelasFecharOpMigratedAt, tuyaLinks:setTuyaLinks, tempSensors:setTempSensors, tempReadings:setTempReadings, tempAlerts:setTempAlerts, tempBackfillState:setTempBackfillState, permProfiles:setPermProfiles, freelaShifts:setFreelaShifts, freelaPagamentos:setFreelaPagamentos, reservasConfig:setReservasConfig, reservasSalas:setReservasSalas, reservasTiposMesa:setReservasTiposMesa, reservasClientes:setReservasClientes, reservas:setReservas };
    const keys    = { owners:K.owners, managers:K.managers, restaurants:K.restaurants, employees:K.employees, roles:K.roles, tips:K.tips, splits:K.splits, schedules:K.schedules, communications:K.communications, commAcks:K.commAcks, faq:K.faq, dpMessages:K.dpMessages, workSchedules:K.workSchedules, notifications:K.notifications, noTipDays:K.noTipDays, trash:K.trash, schedTemplates:K.schedTemplates, schedDrafts:K.schedDrafts, scheduleVersions:K.scheduleVersions, tipVersions:K.tipVersions, vtConfig:K.vtConfig, vtMonthly:K.vtMonthly, vtPayments:K.vtPayments, incidents:K.incidents, feedbacks:K.feedbacks, devChecklists:K.devChecklists, scheduleAdjustments:K.scheduleAdjustments, scheduleStatus:K.scheduleStatus, schedulePrevista:K.schedulePrevista, scheduleSwaps:K.scheduleSwaps, dayInversions:K.dayInversions, recursoFolders:K.recursoFolders, recursos:K.recursos, recursosInitialized:K.recursosInitialized, employeeGoals:K.employeeGoals, delays:K.delays, tipApprovals:K.tipApprovals, meetingPlans:K.meetingPlans, meetingIdeas:K.meetingIdeas, meetingAgendas:K.meetingAgendas, meetingActions:K.meetingActions, meetingOccurrences:K.meetingOccurrences, meetingPendencias:K.meetingPendencias, inbox:K.inbox, inboxFolders:K.inboxFolders, miseCategories:K.miseCategories, miseStocks:K.miseStocks, miseAssignments:K.miseAssignments, miseItems:K.miseItems, miseCycles:K.miseCycles, miseCounts:K.miseCounts, miseSuppliers:K.miseSuppliers, miseProductSuppliers:K.miseProductSuppliers, miseSupplierOrders:K.miseSupplierOrders, miseChecklistTemplates:K.miseChecklistTemplates, miseChecklistRuns:K.miseChecklistRuns, miseFtInsumos:K.miseFtInsumos, miseFtEquipamentos:K.miseFtEquipamentos, miseFtDishes:K.miseFtDishes, pessoas:K.pessoas, pessoasMigratedAt:K.pessoasMigratedAt, permsV2MigratedAt:K.permsV2MigratedAt, freelasFecharOpMigratedAt:K.freelasFecharOpMigratedAt, tuyaLinks:K.tuyaLinks, tempSensors:K.tempSensors, tempReadings:K.tempReadings, tempAlerts:K.tempAlerts, tempBackfillState:K.tempBackfillState, permProfiles:K.permProfiles, freelaShifts:K.freelaShifts, freelaPagamentos:K.freelaPagamentos, reservasConfig:K.reservasConfig, reservasSalas:K.reservasSalas, reservasTiposMesa:K.reservasTiposMesa, reservasClientes:K.reservasClientes, reservas:K.reservas };
    // Support functional updates to prevent stale-state race conditions:
    // When value is a function, it receives the latest state (like setState(prev => ...))
    // IMPORTANTE: o callback do setState pode rodar de forma assíncrona em React 18,
    // então usamos uma Promise para garantir que resolvedValue está populado antes do save.
    // Captura o valor anterior pra poder reverter caso o save falhe.
    let resolvedValue;
    let prevValue;
    if (typeof value === 'function') {
      resolvedValue = await new Promise(resolve => {
        setters[field]?.(prev => {
          prevValue = prev;
          const v = value(prev);
          resolve(v);
          return v;
        });
      });
    } else {
      // Pra capturar prevValue no caminho não-funcional, usamos setter com callback também
      await new Promise(resolve => {
        setters[field]?.(prev => {
          prevValue = prev;
          resolve();
          return value;
        });
      });
      resolvedValue = value;
    }
    if (keys[field]) {
      const ok = await save(keys[field], resolvedValue);
      if (!ok) {
        // Save falhou — reverter o state local pra refletir o que está realmente no Firestore.
        // Se não revertemos, a UI mostra dados "fantasmas" que somem no próximo refresh.
        if (prevValue !== undefined) {
          setters[field]?.(prevValue);
        }
        const detail = (typeof window !== "undefined" && window.__lastSaveError) || null;
        let causa = "";
        if (detail) {
          const sizeKB = (detail.bytes / 1024).toFixed(0);
          causa = `\n— Erro Firebase ——\n`
            + `Code: ${detail.code}\n`
            + `Tamanho: ${sizeKB} KB (limite 1024 KB)\n`
            + `Mensagem: ${detail.message}\n\n`;
        }
        const msg = `❌ Falha ao salvar no servidor (${field}).\n\n`
          + `Sua alteração foi REVERTIDA — a tela voltou pro estado anterior.\n`
          + causa
          + `Possíveis causas:\n`
          + `• Bloqueio de segurança (App Check / reCAPTCHA / Firestore rules)\n`
          + `• Internet instável\n`
          + `• Documento excedeu 1 MB (resource-exhausted)\n\n`
          + `Console (⌘⌥I) → procure "save error" pra mais detalhes.`;
        setToast(`❌ Falha ao salvar ${field} — alteração revertida`);
        if (typeof window !== "undefined") {
          try { window.alert(msg); } catch (_) {}
        }
        return;
      }
    }
    const labels = { owners:"Admins atualizados", managers:"Gestores atualizados", restaurants:"Restaurantes atualizados", employees:"Empregados atualizados", roles:"Cargos atualizados", tips:"Gorjetas atualizadas", splits:"Percentuais salvos", schedules:"Escala atualizada", communications:"Comunicados atualizados", commAcks:"Ciências atualizadas", faq:"FAQ atualizado", dpMessages:"Mensagem enviada", workSchedules:"Horários salvos", notifications:"Notificações atualizadas", schedTemplates:"Template salvo", schedDrafts:"Rascunho salvo", trash:"Lixeira atualizada", noTipDays:"Dias sem gorjeta atualizados", scheduleVersions:null, tipVersions:null, vtConfig:null, vtMonthly:null, vtPayments:"VT registrado", incidents:"Ocorrência registrada", feedbacks:"Feedback registrado", devChecklists:"Checklist atualizado", scheduleAdjustments:null, scheduleStatus:null, schedulePrevista:null, employeeGoals:"Objetivo atualizado", delays:"Atrasos atualizados", tipApprovals:null, inbox:null, inboxFolders:null };
    if (labels[field] === null) return; // silent save (e.g. version snapshots)
    setToast(labels[field] ?? (typeof value === "string" ? value : "Salvo!"));
  }

  function doLogout() {
    setCurrentUser(null);
    setUserRole(null);
    localStorage.removeItem("apptip_selrest");
    localStorage.removeItem("apptip_userid");
    localStorage.removeItem("apptip_role");
    localStorage.removeItem("apptip_empid");
    setView("login");
  }

  // Fallback: se carregou mas sessão ficou sem usuário, redireciona pro login
  // Só age DEPOIS que sessionRestoredRef = true (data-load já tentou restaurar a sessão)
  useEffect(() => {
    if (loaded && sessionRestoredRef.current && !currentUser && (view === "super" || view === "manager")) {
      console.warn("Sessão inválida: view="+view+" sem currentUser. Redirecionando para login.");
      doLogout();
    }
  }, [loaded, currentUser, view]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!loaded) return (
    <div style={{minHeight:"100vh",background:"var(--bg)",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:16,padding:24}}>
      <div style={{fontSize:40}}>🍽️</div>
      {loadError ? (
        <div style={{textAlign:"center",fontFamily:"'DM Sans',sans-serif",maxWidth:360}}>
          <div style={{color:"var(--red,#e74c3c)",fontSize:15,fontWeight:700,marginBottom:8}}>Erro de conexão</div>
          <div style={{color:"var(--text3,#888)",fontSize:13,marginBottom:16}}>Não foi possível conectar ao servidor. Verifique sua conexão com a internet e tente novamente.</div>
          <button onClick={()=>window.location.reload()}
            style={{padding:"10px 24px",borderRadius:10,background:"var(--ac,#d4a017)",border:"none",color:"#fff",fontWeight:700,cursor:"pointer",fontSize:14,fontFamily:"'DM Sans',sans-serif"}}>
            Tentar novamente
          </button>
        </div>
      ) : (
        <div style={{textAlign:"center",fontFamily:"'DM Sans',sans-serif"}}>
          <div style={{color:"var(--text3,#888)",fontSize:15,marginBottom:8,animation:"pulse 1.5s ease-in-out infinite"}}>
            {loadProgress || "Carregando…"}
          </div>
          {loadProgress && loadProgress.includes("lenta") && (
            <div style={{color:"var(--text3,#888)",fontSize:12,marginTop:4}}>
              Isso pode levar alguns segundos em conexões lentas
            </div>
          )}
        </div>
      )}
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }`}</style>
    </div>
  );

  return (
    <>
      {view === "login" && (
        <UnifiedLogin
          owners={owners} managers={managers} employees={employees} restaurants={restaurants}
          pessoas={pessoas}
          dataLoaded={_loadSuccess}
          onLoginOwner={u=>{setCurrentUser(u);setUserRole("super");setView("super");}}
          onLoginManager={u=>{setCurrentUser(u);setUserRole("manager");setView("manager");}}
          onLoginEmployee={u=>{
            localStorage.setItem("apptip_role", "employee");
            localStorage.setItem("apptip_userid", u.id);
            localStorage.setItem("apptip_empid", u.id);
            setUserRole("employee");
            setView("employee");
          }}
          onLoginOperational={emp=>{
            localStorage.setItem("apptip_role", "operational");
            localStorage.setItem("apptip_userid", emp.id);
            localStorage.setItem("apptip_empid", emp.id);
            setUserRole("operational");
            setCurrentUser(emp);
            setView("operational");
          }}
          onLoginShell={pessoa=>{
            localStorage.setItem("apptip_role", "shell");
            localStorage.setItem("apptip_userid", pessoa.linkedEmployeeId || pessoa.linkedManagerId || pessoa.id);
            if (pessoa.linkedEmployeeId) localStorage.setItem("apptip_empid", pessoa.linkedEmployeeId);
            setUserRole("shell");
            setShellActiveRest(pessoa.restaurantIds?.[0] || null);
            // Se tem manager, setCurrentUser pra ter nome etc
            const mgr = pessoa.linkedManagerId ? managers.find(m => m.id === pessoa.linkedManagerId) : null;
            if (mgr) setCurrentUser(mgr);
            setView("shell");
          }}
          onUpdatePessoas={next=>handleUpdate("pessoas", next)}
          onUpdate={handleUpdate}
          onGoHome={()=>setView("home")}
          toggleTheme={toggleTheme} theme={theme}
        />
      )}
      {/* Setup acessível apenas via /setup — protegido por senha de convite */}
      {view === "setup" && <FirstSetup onDone={sm=>{handleUpdate("owners",[...owners,sm]);setCurrentUser(sm);setUserRole("super");setView("super");}} />}
      {view === "super" && currentUser && <OwnerPortal data={data} onUpdate={handleUpdate} onBack={doLogout} currentUser={currentUser} toggleTheme={toggleTheme} theme={theme}
        onEnterOperational={(rid)=>{
          // Owner entra no AppShell como master do restaurante — UI unificada.
          const rest = restaurants.find(r => r.id === rid);
          if (!rest) return;
          localStorage.setItem("apptip_userid_owner", currentUser.id);
          localStorage.setItem("apptip_shell_rest", rid);
          localStorage.setItem("apptip_shell_owner_mode", "1"); // flag: owner no shell (pra refresh)
          setShellActiveRest(rid);
          setView("shell");
        }}
      />}
      {view === "manager" && currentUser && (currentUser.mustChangePin ? (
        <ManagerPinChange manager={currentUser} onDone={newPin=>{
          const updated = {...currentUser, pin:newPin, mustChangePin:false};
          // v7.6: sincroniza PIN nos 3 records (pessoa, employee, manager) pra evitar
          // pedir PIN novo de novo quando trocar de portal.
          const linkedPessoa = (pessoas || []).find(p =>
            p.linkedManagerId === currentUser.id ||
            ((p.cpf || "").replace(/\D/g,"") === (currentUser.cpf || "").replace(/\D/g,"") && (currentUser.cpf || "").length > 0)
          );
          syncPinAcrossLinkedRecords({
            newPin,
            identityCpf: currentUser.cpf,
            pessoa: linkedPessoa,
            pessoas: pessoas || [],
            employees: employees || [],
            managers: managers || [],
            onUpdate: handleUpdate,
          });
          setCurrentUser(updated);
        }} onBack={doLogout} />
      ) : (
        <ManagerPortal manager={currentUser} data={data} onUpdate={handleUpdate} onBack={doLogout} toggleTheme={toggleTheme} theme={theme}
          onSwitchToEmployee={(() => {
            const cpf = currentUser?.cpf?.replace(/\D/g,"");
            let emp = cpf ? employees.find(e => e.cpf?.replace(/\D/g,"") === cpf && !(e.inactive && e.inactiveFrom && e.inactiveFrom <= today())) : null;
            // Fallback: match por linkedEmpId do gestor
            if (!emp && currentUser?.linkedEmpId) emp = employees.find(e => e.id === currentUser.linkedEmpId && !(e.inactive && e.inactiveFrom && e.inactiveFrom <= today()));
            // Fallback: match por nome exato nos restaurantes do gestor
            if (!emp && currentUser?.name) {
              const mgrRids = currentUser.restaurantIds ?? (currentUser.restaurantId ? [currentUser.restaurantId] : []);
              emp = employees.find(e => e.name === currentUser.name && mgrRids.includes(e.restaurantId) && !(e.inactive && e.inactiveFrom && e.inactiveFrom <= today()));
            }
            if (!emp) return null;
            return () => { setCurrentUser(emp); setUserRole("employee"); localStorage.setItem("apptip_role","employee"); localStorage.setItem("apptip_userid",emp.id); localStorage.setItem("apptip_empid",emp.id); setView("employee"); };
          })()} />
      ))}
      {view === "employee" && (() => {
        // v8.32 — filtra dados por restaurante do empregado antes de passar (defesa em profundidade)
        const _empRid = currentUser?.restaurantId;
        const _f = filterDataForRestaurant({
          employees, roles, tips, restaurants, communications, dpMessages, incidents, feedbacks,
          meetingPlans, meetingIdeas: [], meetingAgendas: [], meetingActions: [], meetingOccurrences: [], meetingPendencias: [],
          notifications: [], inbox, scheduleSwaps: [], dayInversions,
          recursoFolders: [], recursos: [], reservas: [], reservasSalas: [], reservasTiposMesa: [], reservasClientes: [],
          freelaShifts: [], freelaPagamentos: [],
          pessoas, managers,
        }, _empRid);
        return <EmployeePortal employees={_f.employees} roles={_f.roles} tips={_f.tips} schedules={schedules} splits={splits} restaurants={_f.restaurants} communications={_f.communications} commAcks={commAcks} faq={faq} dpMessages={_f.dpMessages} workSchedules={workSchedules} incidents={_f.incidents} feedbacks={_f.feedbacks} devChecklists={devChecklists} employeeGoals={employeeGoals} tipApprovals={tipApprovals} delays={delays} meetingPlans={_f.meetingPlans} inbox={_f.inbox} inboxFolders={inboxFolders} pessoas={_f.pessoas} managers={_f.managers} dayInversions={_f.dayInversions} onBack={doLogout} onUpdateEmployee={emp=>{const next=employees.map(e=>e.id===emp.id?emp:e);handleUpdate("employees",next);}} onUpdate={handleUpdate} toggleTheme={toggleTheme} theme={theme}
        onSwitchToManager={(() => {
          const cpf = currentUser?.cpf?.replace(/\D/g,"");
          let mgr = cpf ? managers.find(m => m.cpf?.replace(/\D/g,"") === cpf) : null;
          // Fallback: match por linkedManagerId do empregado
          if (!mgr && currentUser?.linkedManagerId) mgr = managers.find(m => m.id === currentUser.linkedManagerId);
          // Fallback: match por nome exato no mesmo restaurante
          if (!mgr && currentUser?.name) {
            mgr = managers.find(m => m.name === currentUser.name && (m.restaurantIds ?? []).includes(currentUser.restaurantId));
          }
          if (!mgr) return null;
          return () => { setCurrentUser(mgr); setUserRole("manager"); localStorage.setItem("apptip_role","manager"); localStorage.setItem("apptip_userid",mgr.id); setView("manager"); };
        })()}
        onSwitchToOperational={(() => {
          const empIdLs = localStorage.getItem("apptip_empid");
          const emp = employees.find(e => e.id === empIdLs);
          if (!emp || !hasAnyOperationalArea(emp)) return null;
          return () => { setCurrentUser(emp); setUserRole("operational"); localStorage.setItem("apptip_role","operational"); localStorage.setItem("apptip_userid",emp.id); localStorage.setItem("apptip_empid",emp.id); setView("operational"); };
        })()} />;
      })()}
      {view === "shell" && !loaded && (
        <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:12,fontFamily:"'DM Sans',sans-serif"}}>
          <div style={{fontSize:40}}>🍽️</div>
          <div style={{fontSize:14,color:"var(--text2)"}}>Carregando…</div>
          <div style={{fontSize:11,color:"var(--text3)"}}>{loadProgress}</div>
        </div>
      )}
      {view === "shell" && loaded && (() => {
        // Descobre a pessoa logada
        let realPessoa = null;
        if (userRole === "employee" || userRole === "operational" || userRole === "shell") {
          const empId = localStorage.getItem("apptip_empid") || localStorage.getItem("apptip_userid");
          realPessoa = (pessoas || []).find(p => p.linkedEmployeeId === empId);
          // Fallback: tenta por linkedManagerId (pra pessoas que são gestores)
          if (!realPessoa) {
            const uid = localStorage.getItem("apptip_userid");
            realPessoa = (pessoas || []).find(p => p.linkedManagerId === uid);
          }
        } else if (userRole === "manager") {
          realPessoa = (pessoas || []).find(p => p.linkedManagerId === currentUser?.id);
        }
        // Fallback: por CPF
        if (!realPessoa && currentUser?.cpf) {
          const cpfD = currentUser.cpf.replace(/\D/g, "");
          realPessoa = (pessoas || []).find(p => (p.cpf || "").replace(/\D/g, "") === cpfD);
        }
        // Último fallback: se uid salvo é de uma pessoa direta
        if (!realPessoa) {
          const uid = localStorage.getItem("apptip_userid");
          realPessoa = (pessoas || []).find(p => p.id === uid);
        }
        const realIsOwner = userRole === "super" || currentUser?.isMaster === true;
        // Impersonate: se owner está vendo como outra pessoa
        const impersonatedPessoa = impersonatingPessoaId && realIsOwner
          ? (pessoas || []).find(p => p.id === impersonatingPessoaId)
          : null;
        // Durante impersonate: usa a pessoa impersonada como fonte de perms; owner NÃO é mais owner (simula pessoa)
        const pessoa = impersonatedPessoa || realPessoa;
        const isOwnerShell = realIsOwner && !impersonatedPessoa; // owner só é "full owner" quando não tá impersonando
        const defaultRid = shellActiveRest || pessoa?.restaurantIds?.[0] || (isOwnerShell ? restaurants[0]?.id : null);
        // Se não encontrou pessoa e não é owner, algo quebrou — manda pra tela de login
        if (!pessoa && !isOwnerShell) {
          return (
            <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:14,padding:24,fontFamily:"'DM Sans',sans-serif"}}>
              <div style={{fontSize:40}}>🔒</div>
              <div style={{fontSize:15,color:"var(--text)",fontWeight:700}}>Sessão inválida</div>
              <div style={{fontSize:13,color:"var(--text3)",textAlign:"center",maxWidth:360,lineHeight:1.5}}>Não foi possível recuperar seu perfil. Faça login de novo.</div>
              <button onClick={doLogout} style={{...S.btnPrimary,padding:"8px 20px"}}>Voltar ao login</button>
            </div>
          );
        }
        return (
          <AppShell
            pessoa={pessoa}
            data={data}
            activeRestaurantId={defaultRid}
            setActiveRestaurantId={(rid) => { setShellActiveRest(rid); }}
            userRole={userRole}
            currentUser={currentUser}
            onSwitchToEmployee={(tab) => { localStorage.setItem("apptip_role","employee"); setUserRole("employee"); setView("employee"); }}
            onSwitchToManager={(tab) => { localStorage.setItem("apptip_role","manager"); setUserRole("manager"); setView("manager"); }}
            onSwitchToOperational={(tab) => { localStorage.setItem("apptip_role","operational"); setUserRole("operational"); setView("operational"); }}
            onLogout={doLogout}
            onUpdate={handleUpdate}
            onExitShell={() => {
              // Volta pro portal tradicional baseado no role real (owner → super; senão tenta o que era antes do shell)
              const prev = localStorage.getItem("apptip_shell_prev_view");
              const prevRole = prev || (isOwnerShell ? "super" : "employee");
              localStorage.setItem("apptip_role", prevRole);
              setUserRole(prevRole);
              setView(prevRole);
            }}
            onReturnToOwnerHome={realIsOwner && !impersonatedPessoa ? () => {
              localStorage.removeItem("apptip_owner_virtual_return");
              localStorage.removeItem("apptip_shell_rest");
              localStorage.removeItem("apptip_shell_owner_mode");
              setShellActiveRest(null);
              setView("super");
            } : null}
            impersonatedPessoa={impersonatedPessoa}
            realIsOwner={realIsOwner}
            onStartImpersonate={realIsOwner ? (pid) => {
              setImpersonatingPessoaId(pid);
            } : null}
            onStopImpersonate={impersonatedPessoa ? () => {
              setImpersonatingPessoaId(null);
            } : null}
            toggleTheme={toggleTheme}
            theme={theme}
          />
        );
      })()}

      {view === "operational" && currentUser && (
        <OperationalPortal
          employee={currentUser}
          data={data}
          onUpdate={handleUpdate}
          onBack={doLogout}
          toggleTheme={toggleTheme}
          theme={theme}
          onSwitchToEmployee={() => {
            setUserRole("employee");
            setCurrentUser(null);
            localStorage.setItem("apptip_role","employee");
            localStorage.setItem("apptip_empid", currentUser.id);
            localStorage.setItem("apptip_userid", currentUser.id);
            setView("employee");
          }}
          onReturnToOwner={currentUser?._isOwnerVirtual ? () => {
            // Owner virtual retornando ao Portal Super
            const ownerRec = owners.find(o => o.isMaster || o.id === (localStorage.getItem("apptip_userid_owner") || ""));
            const owner = ownerRec || owners[0];
            if (owner) {
              localStorage.removeItem("apptip_owner_virtual_return");
              setCurrentUser(owner);
              setUserRole("super");
              localStorage.setItem("apptip_role", "super");
              localStorage.setItem("apptip_userid", owner.id);
              setView("super");
            } else {
              doLogout();
            }
          } : null}
        />
      )}
      {view === "fatura" && <FaturaPage faturaId={faturaId} restaurants={restaurants} onUpdate={handleUpdate} loaded={loaded} />}
      {view === "guia-gestor" && <GuiaGestor />}
      {view === "home" && <Home onLogin={()=>setView("login")} />}
      {/* Banner offline — somente master master */}
      {currentUser?.isMaster === true && !isOnline && (
        <div style={{position:"fixed",top:0,left:0,right:0,zIndex:9999,background:"#ef4444",color:"#fff",textAlign:"center",padding:"6px 16px",fontSize:13,fontFamily:"'DM Sans',sans-serif",fontWeight:600,letterSpacing:0.3}}>
          📡 Sem conexão — modo somente leitura
        </div>
      )}
      {/* v8.11.1: Banner de atualização disponível */}
      {updateAvailable && (
        <div style={{position:"fixed",top:0,left:0,right:0,zIndex:10000,background:"linear-gradient(90deg,#f59e0b,#fbbf24)",color:"#1f2937",padding:"10px 16px",fontSize:13,fontFamily:"'DM Sans',sans-serif",fontWeight:600,boxShadow:"0 2px 8px rgba(0,0,0,0.2)",display:"flex",alignItems:"center",justifyContent:"center",gap:14,flexWrap:"wrap"}}>
          <span>🔄 Nova versão <strong>v{latestRemoteVersion}</strong> disponível.</span>
          <button onClick={()=>{
            // Limpa caches antes do reload pra garantir
            try {
              if ("caches" in window) {
                window.caches.keys().then(keys => keys.forEach(k => window.caches.delete(k)));
              }
            } catch {}
            window.location.reload();
          }} style={{background:"#1f2937",color:"#fff",border:"none",borderRadius:6,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
            Recarregar agora
          </button>
          <button onClick={()=>{
            setUpdateBannerDismissed(latestRemoteVersion);
            try { localStorage.setItem("apptip_update_dismissed", latestRemoteVersion); } catch {}
          }} style={{background:"transparent",color:"#1f2937",border:"1px solid #1f293744",borderRadius:6,padding:"6px 12px",fontSize:11,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}} title="Esconder até a próxima versão">
            Mais tarde
          </button>
        </div>
      )}
      <Toast msg={toast} onClose={()=>setToast("")} />
      {/* Rodapé de versão + indicador online/offline (somente master) */}
      <div style={{position:"fixed",bottom:8,left:0,right:0,fontSize:10,color:"var(--text3)",fontFamily:"'DM Mono',monospace",opacity:0.45,pointerEvents:"none",zIndex:100,textAlign:"center"}}>
        v{APP_VERSION}
        {currentUser?.isMaster === true && (
          <span style={{marginLeft:8,color:isOnline?"#10b981":"#ef4444",opacity:1,fontWeight:600}}>
            {isOnline ? "● Online" : "● Offline"}
          </span>
        )}
      </div>

      <PrivacyModal open={showPrivacy} onClose={()=>setShowPrivacy(false)} />
      <ConfirmHost />
    </>
  );
}

